// React, ReactDOM e Supabase passam a vir DENTRO do nosso arquivo, em vez de serem
// baixados de tres servidores diferentes na hora que o professor abre o app. Cada
// servidor externo custa uma busca de DNS e um aperto de mao de seguranca — no 4G isso
// pesa mais que o tamanho do arquivo.
import React from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import { createClient } from '@supabase/supabase-js';

import { calcularCreditos, recalcularCreditos } from './regras/creditos.js';
import { comoPagar } from './regras/cobranca.js';
import { criarAlunoComContrato, completarPedidoDoAluno, carregarPadroes, descreverAgenda } from './regras/contratos.js';
import { interpretarContrato } from './regras/interpretar.js';
import { MOTIVOS, MOTIVO_POR_STATUS, MOTIVOS_HUMANOS, ROTULO_MOTIVO, categoriaDoMotivo, decidir as decidirReposicao, validadeDe } from './regras/reposicoes.js';
import { aoArLivre, chuvaLivre, chuvaPelaModalidade } from './regras/reposicoes.js';   // v148: a regra da chuva (ao ar livre)
import qrcode from 'qrcode-generator';
import { iniciarUso, uso, useFluxoUso } from './uso.js';
import { PainelCentral } from './central.jsx';

// Versao do app que vai junto de cada evento da central de uso. A cada deploy, subir AQUI
// e no sw.js (plen-vN) — os dois juntos.
const VERSAO_APP = 'plen-v157';

// v151 — link do "Esqueci minha senha" (/#access_token=...&type=recovery) ou link ja' usado/vencido
// (/#error_code=otp_expired...). Lido AQUI, antes de qualquer codigo mexer no endereco: o convite guardado
// limpava o endereco (replaceState) antes de o login ler o link, e o evento PASSWORD_RECOVERY podia chegar
// antes de o App escutar. Resultado (Cadu, 28/10): o link "entrava" no servidor e a tela de senha nova nao vinha.
const LINK_SENHA_SM = (() => {
  try {
    const h = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
    if (h.get('type') === 'recovery' && h.get('access_token')) return 'recuperar';
    if (h.get('error_code') === 'otp_expired' || /email link/i.test(h.get('error_description') || '')) return 'link_invalido';
  } catch {}
  return '';
})();
// De QUAL conta e' o link (o "sub" do token). A tela de senha nova so' grava se a conta aberta for essa: link velho
// num aparelho com outra conta aberta trocaria a senha da conta errada.
// O login apaga o "#" logo depois de ler o link: uma recarga antes de salvar (versao nova, iPhone descartando a aba)
// levava a pessoa para dentro do app sem senha nova. A marca 'plen_recuperacao' (conta + hora) guarda o modo por 1 h.
const LINK_SENHA_CONTA_SM = (() => {
  try {
    if (LINK_SENHA_SM === 'recuperar') {
      const t = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('access_token') || '';
      const conta = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub || '';
      if (conta) sessionStorage.setItem('plen_recuperacao', JSON.stringify({ conta, em: Date.now() }));
      return conta;
    }
    const m = JSON.parse(sessionStorage.getItem('plen_recuperacao') || 'null');
    if (m && m.conta && Date.now() - m.em < 60 * 60 * 1000) return m.conta;
    sessionStorage.removeItem('plen_recuperacao');
  } catch {}
  return '';
})();
function fimRecuperacaoSM() { try { sessionStorage.removeItem('plen_recuperacao'); } catch {} }
// Aviso de link vencido: uma vez so' (sem isto ele voltava a cada "Sair", sem a pessoa ter tocado em link nenhum).
let avisoLinkSenhaSM = LINK_SENHA_SM === 'link_invalido';

const SUPABASE_URL = 'https://jejgdfojbwdueppkuhgf.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImplamdkZm9qYndkdWVwcGt1aGdmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYzMTYzODIsImV4cCI6MjA5MTg5MjM4Mn0.oOZQz1Mztq2kWsedROxMMh3Tfvmiigaq6I1Nh1XjPbw';
// Sessao permanente. O professor abre o app varias vezes por dia, no meio da aula, com
// a mao suada — pedir senha de novo e' o jeito mais rapido de ele parar de usar. A sessao
// fica gravada no proprio celular e se renova sozinha antes de vencer; so' sai quem toca
// em Sair. Cada navegador guarda a sua (o navegador interno do WhatsApp e' um deles).
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
  },
});

// Central de uso (src/uso.js): liga uma vez so', no nivel do arquivo — o ErrorBoundary fica
// fora do App e precisa do `uso` pronto. Se falhar, o app segue normal.
try { iniciarUso({ supabase, url: SUPABASE_URL, anonKey: SUPABASE_ANON_KEY, versao: VERSAO_APP }); } catch (e) {}

// O navegador congela o temporizador de renovacao quando o app fica em segundo plano.
// Ao voltar, peco a sessao de volta na hora — sem isso o professor abre o app depois de
// horas, o token ja' venceu e ele cai no login achando que foi deslogado.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') supabase.auth.getSession();
  });
}

// ---- Icone da interface ----------------------------------------------------
// Substitui os emojis. Emoji tem desenho diferente em cada celular, cor propria que
// briga com a marca e tamanho que nao obedece — por isso todo app serio usa um
// conjunto de icones. Herda a cor do texto quando `c` nao e' passado.
// Verdadeiro quando o site esta' rodando dentro da embalagem nativa (App Store / Play).
const APP_NATIVO = !!(typeof window !== 'undefined' && window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
// v141: o Plen 1.0 esta' na App Store (aprovado em 19/10/2026). Link fixo da loja brasileira.
const APP_STORE_URL = 'https://apps.apple.com/br/app/plen/id6815676437';
// iPhone/iPad pelo NAVEGADOR (Safari, navegador de dentro do WhatsApp...), fora do app da loja.
// O iPad com iPadOS 13+ se apresenta como Mac: o toque (maxTouchPoints) e' que denuncia.
const IOS_NAVEGADOR = !APP_NATIVO && typeof navigator !== 'undefined' &&
  (/iPhone|iPad|iPod/.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
// Icone dos botoes "Baixar na App Store". Neutro de proposito: a Apple nao libera a maca (o
// brand-apple do tabler) fora do selo oficial. Trocar aqui se o Marcus decidir pelo selo.
const ICONE_APP_STORE = 'download';
// v141: no app da loja nao ha' "Continuar com Google" (so' e-mail e senha). Quem criou a conta pelo
// Google no navegador nao tem senha: "Email ou senha incorretos" sem explicacao o levaria a criar uma
// segunda conta, vazia. "Esqueci minha senha" manda o e-mail que cria uma senha para a mesma conta.
// v141: link de convite aberto (?c=) — o codigo fica guardado (pendingInviteCode) ANTES de o convite
// voltar do banco. A tela de entrada usa isto para nao mostrar "Baixar na App Store" nesse meio-tempo.
function conviteGuardadoSM() {
  try { return !!localStorage.getItem('pendingInviteCode') || /[?&]c=/.test(window.location.search || ''); } catch { return false; }
}
// v147 (21/10, aluno do Cadu e Iago): a Apple NAO entrega ao app o link tocado antes de instalar. Por isso:
//  * no iPhone SEM o app, o link abre a pagina "Baixar o Plen", que COPIA o link do convite (ConviteBaixarApp);
//  * dentro do app, "Colar convite" le' esse link e segue IGUAL ao toque no link (ColarConvite).
// Codigo do convite: o do link (?c=...), o "Codigo: *XXXX*" da mensagem, ou so' o codigo (6 letras dos
// antigos, 10 dos novos, no alfabeto do banco — sem I, L, O, 0 e 1).
function codigoConviteDoTextoSM(texto) {
  const t = String(texto || '').slice(0, 3000);
  const doBanco = x => /^([A-HJKMNP-Z2-9]{6}|[A-HJKMNP-Z2-9]{10})$/.test(x);
  const m = /[?&]c=([A-Za-z0-9]{4,16})(?![A-Za-z0-9])/i.exec(t);
  if (m) return m[1].toUpperCase();
  const m2 = /c[oó]digo:?\s*\*?\s*([A-Za-z0-9]{6,10})(?![A-Za-z0-9])/i.exec(t);
  if (m2 && doBanco(m2[1].toUpperCase())) return m2[1].toUpperCase();
  const so = t.replace(/[\s*"'.-]/g, '').toUpperCase();
  return doBanco(so) ? so : '';
}
// O mesmo caminho do link tocado (e do link universal, irPara): a pagina abre de novo com ?c=CODIGO, o App
// guarda em pendingInviteCode e mostra o convite — cadastro, entrada ou "Convite recebido!".
function abrirConviteColadoSM(code) {
  try { localStorage.setItem('pendingInviteCode', code); } catch {}
  window.location.href = window.location.pathname + '?c=' + encodeURIComponent(code);
}
// Copia DENTRO do toque, antes de a pagina sair para a App Store: o jeito antigo (sincrono) e o novo.
function copiarNoToqueSM(texto) {
  let ok = false;
  try {
    const ta = document.createElement('textarea');
    ta.value = texto; ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;';
    document.body.appendChild(ta);
    ta.focus(); ta.select(); ta.setSelectionRange(0, texto.length);
    ok = document.execCommand('copy');
    document.body.removeChild(ta);
  } catch {}
  try { if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(texto).then(() => {}, () => {}); } catch {}
  return ok;
}
const DICA_CONTA_GOOGLE_NATIVO = APP_NATIVO ? ' Criou a conta com o Google? Toque em "Esqueci minha senha" para criar uma senha.' : '';
// Uma frase so', no fim de todo convite (do professor para o aluno e do aluno para o professor).
// O link do convite e' universal: com o app instalado, tocar nele abre o app ja' no convite. Nao ha'
// "link adiado": quem instala depois precisa voltar ao WhatsApp e tocar no convite — por isso o
// "depois toque no link abaixo". v142c (21/10, Marcus: "link de cima" confundia): o link do convite vai
// SOZINHO NO FIM, depois do "No iPhone / No Android" — a ultima coisa da mensagem e' o que se toca. A
// previa do WhatsApp passa a ser a da App Store (o 1o link), que tambem e' do Plen. Sem parenteses em
// volta do link (o WhatsApp as vezes engole o ")" no endereco).
// 21/10 (Marcus e Cadu): UM LINK SO', sem "baixe na loja e depois volte aqui" — confundia, e quem ja' tem o app
// recebia instrucao de baixar. Com o app instalado o link abre o app (link universal); sem o app, abre o Plen no
// navegador, a pessoa entra e confirma ali e o proprio Plen sugere o app da loja depois (foi o caminho do Iago).
// A funcao fica por compatibilidade (outras rodadas a usam); devolve so' o link.
function blocoBaixarEConvite(url) {
  return url;
}

// ---------------------------------------------------------------------------
// U27 — Web Push. O aparelho se inscreve (uma vez, com a chave publica VAPID) e a
// inscricao vai para push_subscriptions. Quem envia e' a Edge Function enviar-push,
// chamada pelo banco a cada minuto. Funciona no Android e no iPhone com o app na tela
// de inicio (iOS 16.4+). Sem permissao ou sem suporte, nada quebra: o sininho continua.
// ---------------------------------------------------------------------------
// Telefone no padrao brasileiro enquanto digita: "+55 (11) 99999-9999". O +55 (Brasil)
// ja' vem fixo — a pessoa digita so' DDD e numero; se tiver o costume de digitar 55 de
// novo, o repetido e' descartado. Guardamos formatado; todo uso (WhatsApp, vincular por
// telefone) ja' tira o que nao e' digito e o 55 da frente. (Marcus, 25/09)
function mascaraTel(v) {
  const txt = String(v || '');
  let d = txt.replace(/\D/g, '');
  if (txt.trim().startsWith('+55')) d = d.slice(2);          // o +55 fixo do proprio campo
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2);   // 55 digitado por costume
  d = d.slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `+55 (${d}`;
  const ddd = d.slice(0, 2), r = d.slice(2);
  if (r.length <= 4) return `+55 (${ddd}) ${r}`;
  const corte = r.length === 9 ? 5 : 4;
  return `+55 (${ddd}) ${r.slice(0, corte)}-${r.slice(corte)}`;
}

// Inscricao de push do APARELHO (Fase 0, Etapa 1). O endpoint/token e' do aparelho, nao da
// conta: no mesmo celular, quem entrar por ultimo fica com ele (funcao registrar_push no banco).
// Antes, o upsert direto esbarrava na regra de acesso quando outra conta ja' tinha usado o
// aparelho, falhava calado, e o celular seguia recebendo os avisos da primeira pessoa.
// Banco sem a funcao ainda: o upsert de antes.
async function gravarInscricaoPush(linha) {
  try { localStorage.setItem('plenPushEndpoint', linha.endpoint); } catch {}
  const { error } = await supabase.rpc('registrar_push', {
    p_endpoint: linha.endpoint, p_p256dh: linha.p256dh || '', p_auth: linha.auth || '',
    p_plataforma: linha.plataforma || 'web', p_user_agent: linha.user_agent || null,
  });
  if (!error) return { error: null };
  if (!/registrar_push|function|PGRST202/i.test(error.message || '') && error.code !== 'PGRST202') return { error };
  return supabase.from('push_subscriptions').upsert({ ...linha, ultimo_uso: new Date().toISOString() }, { onConflict: 'endpoint' });
}
// Ao sair da conta, o aparelho deixa de receber os avisos dessa pessoa (antes continuava
// recebendo nomes de alunos e valores depois do logout). Chamar ANTES do signOut.
async function soltarPushDoAparelho() {
  let ep = null;
  try { ep = localStorage.getItem('plenPushEndpoint'); } catch {}
  if (!ep) return;
  try { await supabase.from('push_subscriptions').delete().eq('endpoint', ep); } catch {}
  try { localStorage.removeItem('plenPushEndpoint'); } catch {}
}
async function sairDaConta() {
  // a fila da central de uso sai ANTES do signOut, ainda com o token desta conta
  try { uso.evento('sessao.logout'); uso.enviarAgora(); } catch {}
  definirNumeroIconeSM(0);   // v148: o numero no icone era desta conta
  await soltarPushDoAparelho();
  await supabase.auth.signOut();
}

function b64UrlParaBytes(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, c => c.charCodeAt(0));
}
function pushSuportado() {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && typeof Notification !== 'undefined';
}
async function inscreverPush(userId) {
  if (!pushSuportado() || !userId) return { ok: false, motivo: 'sem suporte' };
  try {
    if (Notification.permission !== 'granted') return { ok: false, motivo: Notification.permission };
    const { data: cfg } = await supabase.from('config_publica').select('valor').eq('chave', 'vapid_public_key').maybeSingle();
    const chave = cfg?.valor;
    if (!chave || chave.startsWith('__')) return { ok: false, motivo: 'sem chave' };
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64UrlParaBytes(chave) });
    const j = sub.toJSON();
    const { error } = await gravarInscricaoPush({
      user_id: userId, endpoint: sub.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth,
      plataforma: 'web', user_agent: navigator.userAgent.slice(0, 200),
    });
    if (error) { console.warn('push upsert:', error.message); return { ok: false, motivo: error.message }; }
    avisarPushRegistradoSM();   // v148: o numero no icone confere este aparelho
    return { ok: true };
  } catch (e) { console.warn('push:', e?.message); return { ok: false, motivo: e?.message }; }
}
// Pede a permissao e, se vier, inscreve. Chamar SEMPRE a partir de um toque do usuario.
async function ativarPush(userId) {
  if (!pushSuportado()) return { ok: false, motivo: 'sem suporte' };
  try {
    const r = await Notification.requestPermission();
    if (r !== 'granted') return { ok: false, motivo: r };
    return await inscreverPush(userId);
  } catch (e) { return { ok: false, motivo: e?.message }; }
}

// ---------------------------------------------------------------------------
// Recursos do celular no app da loja (25/09), pela ponte do Capacitor.
//  * compartilharOuWhats: link de WhatsApp SEM numero (convite "para quem voce quiser")
//    vira o menu de compartilhar do proprio celular — WhatsApp, Mensagens, e-mail...
//    Com numero, continua indo direto para a conversa certa.
//  * link universal: convite https://meuplen.com.br/?c=... tocado no WhatsApp abre o app
//    instalado; aqui o app leva a tela para o endereco do convite.
// ---------------------------------------------------------------------------
function pluginNativo(nome) {
  const C = typeof window !== 'undefined' && window.Capacitor;
  return !!(APP_NATIVO && C && typeof C.nativePromise === 'function' &&
    (!C.PluginHeaders || C.PluginHeaders.some(h => h.name === nome)));
}
// Agenda do celular (29/09). Devolve [{nome, tel}] so' do(s) contato(s) escolhido(s) — o app
// nunca le' a agenda inteira. Dois caminhos:
//  * app da loja: plugin nativo Contacts (@capacitor-community/contacts, entra no build 6 do
//    iPhone). Abre o seletor de contatos do proprio sistema, um contato por vez.
//  * navegador: Contact Picker API — so' o Chrome do Android tem; permite marcar varios.
// No Safari do iPhone nao existe nenhum dos dois: temAgendaCelular() da' falso e o botao some.
function temAgendaCelular() {
  if (pluginNativo('SeletorContato') || pluginNativo('Contacts')) return true;
  return typeof navigator !== 'undefined' && 'contacts' in navigator && typeof navigator.contacts?.select === 'function';
}
// Numero da agenda vem de todo jeito: "+55 11 99324-5576", "011 99324 5576", "(0xx21) 11 9...".
// Deixa so' DDD + numero (ate' 11 digitos) e aplica a mascara +55 do app.
function telDaAgenda(t) {
  let d = String(t || '').replace(/\D/g, '');
  if (d.length >= 12 && d.startsWith('55')) d = d.slice(2);
  d = d.replace(/^0+/, '');
  if (d.length > 11) d = d.slice(-11);
  return d ? mascaraTel(d) : '';
}
// origem: de onde veio o pedido (central de uso conta quem usa a agenda do celular)
// textoErro (v142, revisao): o aviso quando a agenda falha. O "Novo aluno" e o "Adicionar professor" nao tem
// mais campo de WhatsApp — so' a turma ainda pede o numero de cada pessoa.
async function escolherContatos({ varios = false, origem = 'outro', textoErro = 'Não consegui abrir a agenda. Digite o nome.' } = {}) {
  const r = await escolherContatosCru({ varios, textoErro });
  try { uso.acao('agenda_celular.buscar', { origem, via: pluginNativo('SeletorContato') ? 'nativo_sem_permissao' : pluginNativo('Contacts') ? 'nativo' : 'web', n: r.length }); } catch {}
  return r;
}
async function escolherContatosCru({ varios = false, textoErro = 'Não consegui abrir a agenda. Digite o nome.' } = {}) {
  try {
    // Build 7+ (02/10): seletor proprio do iPhone, SEM pedir acesso a agenda (AppDelegate.swift,
    // SeletorContatoPlugin). Devolve {nome, telefones:[{numero, rotulo, tipo}]}; desistir = "cancelado".
    if (pluginNativo('SeletorContato')) {
      const r = await window.Capacitor.nativePromise('SeletorContato', 'escolher', {});
      if (!r) return [];
      const fones = r.telefones || [];
      const cel = fones.find(p => /mobile|iphone|cel|whats/i.test((p.tipo || '') + ' ' + (p.rotulo || ''))) || fones[0];
      return [{ nome: String(r.nome || '').trim(), tel: telDaAgenda(cel?.numero) }];
    }
    // Build 6: plugin da comunidade (pede acesso a agenda antes de abrir o seletor).
    if (pluginNativo('Contacts')) {
      const r = await window.Capacitor.nativePromise('Contacts', 'pickContact', { projection: { name: true, phones: true } });
      const c = r && r.contact;
      if (!c) return [];
      const nome = c.name?.display || [c.name?.given, c.name?.family].filter(Boolean).join(' ');
      const fones = c.phones || [];
      const cel = fones.find(p => /mobile|cel|iphone|whats/i.test((p.type || '') + (p.label || ''))) || fones[0];
      return [{ nome: (nome || '').trim(), tel: telDaAgenda(cel?.number) }];
    }
    const lista = await navigator.contacts.select(['name', 'tel'], { multiple: varios });
    return (lista || []).map(c => ({ nome: String(c.name?.[0] || '').trim(), tel: telDaAgenda(c.tel?.[0]) }));
  } catch (e) {
    // cancelar o seletor tambem cai aqui — so' avisa se for erro de verdade
    // Erro de verdade volta marcado (r.erro): quem chamou abre os campos para digitar, como o aviso pede.
    if (!/cancel/i.test(String(e?.message || e))) { toastSM(textoErro); uso.erro('erro.agenda_celular', e?.message || e); return Object.assign([], { erro: true }); }
    return [];
  }
}
// motivo: codigo para a central de uso ('convite', 'cobranca_turma'...) — nunca o texto da mensagem
function compartilharOuWhats(waUrl, motivo = 'outro') {
  const semNumero = /wa\.me\/\?text=/.test(waUrl);
  try { uso.acao('whatsapp.abrir', { motivo, com_numero: !semNumero, via: semNumero && pluginNativo('Share') ? 'compartilhar' : 'link' }); } catch {}
  if (semNumero && pluginNativo('Share')) {
    const texto = decodeURIComponent(waUrl.split('text=')[1] || '');
    window.Capacitor.nativePromise('Share', 'share', { text: texto, dialogTitle: 'Compartilhar' })
      .catch(e => { if (!/cancel/i.test(String(e?.message || e))) window.open(waUrl, '_blank'); });
    return;
  }
  window.open(waUrl, '_blank');
}
if (pluginNativo('App')) {
  // v151 (revisao): o iPhone devolve o MESMO link de abertura (getLaunchUrl) enquanto o app estiver aberto. Com a
  // recarga nova, o link do "Esqueci minha senha" reabria a tela de senha a cada recarga. Cada link vale uma vez.
  // No Android o getLaunchUrl devolve SEMPRE o primeiro link (o que abriu o app), mesmo depois de outro link
  // tocado com o app aberto — guardar so' o ultimo fazia a recarga voltar para o link antigo. Guarda a lista toda.
  const irPara = (url, deAbertura) => {
    try {
      let abertos = [];
      try { abertos = JSON.parse(sessionStorage.getItem('plen_links_abertos') || '[]'); } catch {}
      if (!Array.isArray(abertos)) abertos = [];
      if (deAbertura && abertos.includes(url)) return;
      if (!abertos.includes(url)) abertos = abertos.concat(url).slice(-20);
      sessionStorage.setItem('plen_links_abertos', JSON.stringify(abertos));
    } catch {}
    try {
      const u = new URL(url);
      if (!/(^|\.)meuplen\.com\.br$/.test(u.hostname)) return;
      const destino = u.pathname + u.search + u.hash;
      if (destino !== window.location.pathname + window.location.search + window.location.hash) {
        try { uso.evento('app.link_universal'); uso.enviarAgora(); } catch {}
        // v151: quando so' o "#..." muda (o link do "Esqueci minha senha" e' meuplen.com.br/#access_token=...), o
        // navegador NAO recarrega a pagina e o login nunca le' o link — o app ficava parado no "Bem-vindo de volta".
        const soOHash = u.pathname + u.search === window.location.pathname + window.location.search;
        window.location.href = destino;
        if (soOHash && u.hash) window.location.reload();
      }
    } catch {}
  };
  try {
    window.Capacitor.nativeCallback('App', 'addListener', { eventName: 'appUrlOpen' }, (r) => r?.url && irPara(r.url));
    window.Capacitor.nativePromise('App', 'getLaunchUrl', {}).then(r => r?.url && irPara(r.url, true)).catch(() => {});
  } catch {}
}

// ---------------------------------------------------------------------------
// Push NATIVO (app da loja). O site roda dentro da embalagem e fala com o plugin
// PushNotifications direto pela ponte do Capacitor (nativePromise / nativeCallback) —
// sem precisar empacotar @capacitor/core no site. O aparelho recebe um "token" da Apple;
// ele vai para push_subscriptions com plataforma 'ios' (endpoint = 'apns:<token>') e a
// Edge Function enviar-push manda pela Apple (APNs). Sem plugin (build antiga), nada quebra.
// Android (app da Play Store): o mesmo caminho, mas o token vem do Firebase (FCM) e vai com
// plataforma 'android' (endpoint = 'fcm:<token>'). O Chrome do Android sem o app segue no Web Push.
// ---------------------------------------------------------------------------
// Android sem google-services.json na build: o register() do plugin nao devolve erro, DERRUBA o
// app (o Firebase nao foi iniciado e a excecao sobe no Java). O site nao tem como perguntar antes,
// e marcar no localStorage antes do register nao pega a queda (o WebView grava em disco com atraso;
// o processo morre antes). Entao o Android so' entra pelo NUMERO DA BUILD (versionCode, via
// App.getInfo): so' builds a partir de BUILD_ANDROID_COM_FIREBASE. Enquanto o numero nao voltou,
// nada de register. 0 = nenhuma build com Firebase na loja ainda (Android fora, como antes).
// Ao publicar a build Android COM google-services.json: por aqui o versionCode dela.
// 29/10: a build 2 (1.1) e' a primeira com o Firebase (projeto plen-9ab1b); a 1 (28/09) nao tem.
const BUILD_ANDROID_COM_FIREBASE = 2;
function pushAndroidSM() {
  const C = typeof window !== 'undefined' && window.Capacitor;
  return !!(APP_NATIVO && C && C.getPlatform && C.getPlatform() === 'android');
}
let buildNativaSM = null; // Android: versionCode da build instalada (0 = nao deu para saber)
const buildNativaProntaSM = (() => {
  if (!pushAndroidSM() || !BUILD_ANDROID_COM_FIREBASE) return Promise.resolve();
  try {
    return window.Capacitor.nativePromise('App', 'getInfo', {})
      .then(i => { buildNativaSM = Number(i && i.build) || 0; })
      .catch(() => { buildNativaSM = 0; });
  } catch { buildNativaSM = 0; return Promise.resolve(); }
})();
function pushAndroidLiberadoSM() {
  return !!BUILD_ANDROID_COM_FIREBASE && buildNativaSM != null && buildNativaSM >= BUILD_ANDROID_COM_FIREBASE;
}
function pushNativoDisponivel() {
  const C = typeof window !== 'undefined' && window.Capacitor;
  if (pushAndroidSM() && !pushAndroidLiberadoSM()) return false;
  return !!(APP_NATIVO && C && typeof C.nativePromise === 'function' &&
    (!C.PluginHeaders || C.PluginHeaders.some(h => h.name === 'PushNotifications')));
}
// Android com build ainda por saber (getInfo nao voltou): pode vir a ter push — a faixa espera
function pushNativoTalvezSM() {
  return pushNativoDisponivel() || (pushAndroidSM() && !!BUILD_ANDROID_COM_FIREBASE && buildNativaSM == null);
}
function pushNativo(metodo, opcoes = {}) {
  return window.Capacitor.nativePromise('PushNotifications', metodo, opcoes);
}
// v143 (B): botoes no aviso (app da loja build 10). O toque chega com actionId: 'tap' (toque normal,
// e tambem o toque no aviso de confirmacao "Pronto: Cadu ve que voce vai") ou 'PLEN_NAO_VOU' ([Nao vou],
// que abre o app em data.url_nao_vou = "/?aula=<id>&acao=nao_vou"). [✓ Vou] e [✓ Foi dada] o proprio
// iPhone resolve com o banco, sem abrir o app — se um dia chegarem aqui, valem como toque normal.
// So' caminho do proprio app ("/...", nunca "//outro-site"), com menos de 300 letras.
function caminhoDoAppSM(u) {
  return typeof u === 'string' && u.length < 300 && /^\/(?!\/)/.test(u) ? u : null;
}
function destinoDoToquePushSM(ev) {
  const n = ev && ev.notification;
  const d = (n && n.data) || {};
  if (ev && ev.actionId === 'PLEN_NAO_VOU') {
    const u = caminhoDoAppSM(d.url_nao_vou);
    if (u) return u;
  }
  return caminhoDoAppSM(d.url);
}
// para a central de uso: 'tap' | 'nao_vou' | 'outra' (actionId que o site nao conhece)
function tipoDoToquePushSM(ev) {
  const a = ev && ev.actionId;
  if (a === 'PLEN_NAO_VOU') return 'nao_vou';
  return !a || a === 'tap' ? 'tap' : 'outra';
}
let pushNativoOuvindo = false;
async function registrarPushNativo(userId, { pedir = false } = {}) {
  if (!pushNativoDisponivel() || !userId) return { ok: false, motivo: 'sem suporte' };
  try {
    let perm = await pushNativo('checkPermissions');
    if (perm?.receive !== 'granted') {
      if (!pedir) return { ok: false, motivo: perm?.receive || 'prompt' };
      perm = await pushNativo('requestPermissions');
      if (perm?.receive !== 'granted') return { ok: false, motivo: 'denied' };
    }
    // Android so' chega aqui com build liberada (pushNativoDisponivel / BUILD_ANDROID_COM_FIREBASE)
    const plataforma = pushAndroidSM() ? 'android' : 'ios';
    const token = await new Promise((resolve) => {
      const t = setTimeout(() => resolve(null), 15000);
      window.Capacitor.nativeCallback('PushNotifications', 'addListener', { eventName: 'registration' }, (r) => { clearTimeout(t); resolve(r?.value || null); });
      window.Capacitor.nativeCallback('PushNotifications', 'addListener', { eventName: 'registrationError' }, (e) => { clearTimeout(t); console.warn('push nativo:', e?.error); resolve(null); });
      pushNativo('register').catch(() => { clearTimeout(t); resolve(null); });
    });
    if (!token) return { ok: false, motivo: 'sem token' };
    if (!pushNativoOuvindo) {
      pushNativoOuvindo = true;
      // tocou no aviso com o app em segundo plano: abre a tela certa. v131: o aviso traz o
      // endereco dela (Edge enviar-push: url "/?aula=<id>&l=<aviso>" nos avisos de aula — o
      // lembrete abre a aula nas opcoes). Sem endereco: recarrega para a tela inicial mostrar
      // o que chegou (a aula para confirmar, o pedido do aluno) ja' atualizado.
      // v143: o destino sai de destinoDoToquePushSM ([Nao vou] → url_nao_vou; o resto → url).
      window.Capacitor.nativeCallback('PushNotifications', 'addListener', { eventName: 'pushNotificationActionPerformed' }, (ev) => {
        let destino = null, tipo = 'tap';
        try {
          destino = destinoDoToquePushSM(ev);
          tipo = tipoDoToquePushSM(ev);
          // o mesmo toque nao abre duas vezes (a pagina recarrega e o aviso pode chegar de novo).
          // v143: a chave leva o botao — o [Nao vou] e o toque normal do mesmo aviso sao toques diferentes —
          // e o destino: no iPhone o id do aviso e' o apns-collapse-id (= o tipo), igual em todos os lembretes;
          // o endereco ("&l=<aviso>") separa um lembrete do outro
          const n = ev && ev.notification;
          const id = n && n.id != null ? String(n.id) : '';
          if (id) {
            const chave = id + ':' + String((ev && ev.actionId) || 'tap') + ':' + (destino || '');
            if (sessionStorage.getItem('plen_push_tocado') === chave) return;
            sessionStorage.setItem('plen_push_tocado', chave);
          }
        } catch {}
        try { uso.evento('push.tocou', { detalhes: { com_destino: !!destino, acao: tipo } }); uso.enviarAgora(); } catch {}
        setTimeout(() => { try { if (destino) window.location.href = destino; else window.location.reload(); } catch {} }, 300);
      });
    }
    const { error } = await gravarInscricaoPush({
      user_id: userId, endpoint: `${plataforma === 'android' ? 'fcm' : 'apns'}:${token}`, p256dh: '', auth: '',
      plataforma, user_agent: navigator.userAgent.slice(0, 200),
    });
    if (error) return { ok: false, motivo: error.message };
    avisarPushRegistradoSM();   // v148: o numero no icone confere este aparelho
    return { ok: true };
  } catch (e) { console.warn('push nativo:', e?.message); return { ok: false, motivo: e?.message }; }
}

// Central de uso: resposta do pedido de aviso em codigo (o motivo cru e' texto livre, nao sai).
function resultadoPush(r) {
  if (r && r.ok) return 'aceito';
  const m = String((r && r.motivo) || '');
  if (m === 'denied') return 'negado';
  if (m === 'default' || m === 'prompt') return 'ignorou';
  if (m === 'sem suporte') return 'sem_suporte';
  if (m === 'sem chave') return 'sem_chave';
  if (m === 'sem token') return 'sem_token';
  return 'erro';
}
// Central de uso: o motivo que a pessoa escolhe ao cancelar/faltar NAO sai como foi escolhido.
// "Estou doente" e' dado de saude (LGPD art. 11) — sai junto com "agenda" como 'pessoal', e
// assim o painel nunca sabe quem esteve doente. Os outros (viagem, feriado, clima) nao dizem nada
// da pessoa e continuam separados.
function motivoUso(m) { return m === 'doente' || m === 'agenda' ? 'pessoal' : (m || 'outro'); }
// Uma vez por sessao do navegador: como esta' a permissao de aviso deste aparelho.
function pushEstadoUmaVez(perm) {
  try {
    if (sessionStorage.getItem('plen_uso_push_estado')) return;
    sessionStorage.setItem('plen_uso_push_estado', '1');
    uso.evento('push.estado', { detalhes: { perm: String(perm || 'nada') } });
  } catch {}
}

// Faixa "Ativar avisos no celular" — aparece uma vez, some quando ativa ou quando a
// pessoa dispensa. No iPhone pelo navegador o push nao existe: a faixa mostra o caminho (v141: o
// app da App Store) em vez de pedir uma permissao que nao vai aparecer.
// texto (v131): a pergunta da faixa — o aluno ve' "Receber lembrete antes da aula e avisos do
// professor?" (Etapa 5); sem texto, a pergunta geral de antes.
// v141: a faixa da App Store (estado 'ios') tem a sua PROPRIA chave de "dispensada". Com a chave antiga,
// quem ja' tinha fechado a faixa "Adicionar a Tela de Inicio" nunca veria a da App Store — e esse e'
// justamente o publico a levar para o app da loja. A chave antiga continua valendo para 'pedir' e 'nativo'.
const CHAVE_FAIXA_APP_STORE = 'faixaAppStoreDispensada';
// v147 (21/10, Iago): no app da loja, quem NUNCA respondeu a permissao (estado "prompt") ficava sem aviso
// nenhum no celular e nao percebia — a faixa escura passava batido, abaixo do "primeiro aluno". Agora, no
// app, e' um CARTAO claro no topo do Inicio enquanto a pessoa nao decidir ("Agora nao" esconde por 1 dia).
// Negado: mostra como ligar em Ajustes → Plen → Notificacoes ate' a pessoa tocar em "Entendi". A chave
// antiga da faixa (pushFaixaDispensada) nao vale aqui. app-settings: abre o app Ajustes (testado no simulador).
const CHAVE_AVISO_ADIADO = 'plenAvisoAdiadoAte';
const CHAVE_AVISO_NEGADO_VISTO = 'plenAvisoNegadoVisto';
function avisoAdiadoSM() { try { return Number(localStorage.getItem(CHAVE_AVISO_ADIADO) || 0) > Date.now(); } catch { return false; } }
function adiarAvisoSM() { try { localStorage.setItem(CHAVE_AVISO_ADIADO, String(Date.now() + 24 * 3600 * 1000)); } catch {} }
function FaixaPush({ userId, texto, quem = 'professor' }) {
  const [estado, setEstado] = React.useState(() => {
    const dispensada = k => { try { return !!localStorage.getItem(k); } catch { return false; } };
    // Dentro do app da loja esta faixa nao faz sentido: o aviso pelo caminho do navegador
    // nao funciona no app do iPhone, e "coloque na tela de inicio" dentro de um app
    // instalado e' exatamente o que faz a Apple recusar por "e' so' um site". O aviso do
    // app vai ser o push nativo (plano das lojas, bloco 2).
    // No app da loja: aviso nativo. v147: decide depois de perguntar ao aparelho ('verificando'):
    // 'nativo' (nunca respondeu) = cartao; 'negado' = como ligar nos Ajustes; ja' ligado = renova em silencio.
    if (APP_NATIVO) return pushNativoTalvezSM() ? 'verificando' : 'oculta';
    // v141: iPhone/iPad pelo navegador, sem aviso pelo navegador -> o caminho agora e' o app da
    // App Store (antes: ensinava "Adicionar a Tela de Inicio"). Inclui o Plen ja' posto na tela de
    // inicio num iOS antigo (sem aviso por la'). O Android nao passa por aqui (tem aviso no navegador).
    if (!pushSuportado()) return IOS_NAVEGADOR && !dispensada(CHAVE_FAIXA_APP_STORE) ? 'ios' : 'oculta';
    if (dispensada('pushFaixaDispensada')) return 'oculta';
    return Notification.permission === 'default' ? 'pedir' : 'oculta';
  });
  const estadoRef = React.useRef(estado);
  estadoRef.current = estado;
  React.useEffect(() => {
    if (!userId) return;
    if (APP_NATIVO) {
      if (!pushNativoTalvezSM()) return;
      const negadoVisto = () => { try { return !!localStorage.getItem(CHAVE_AVISO_NEGADO_VISTO); } catch { return false; } };
      // Android: espera o numero da build; sem build liberada, a faixa some (nada de register)
      buildNativaProntaSM.then(() => {
        if (!pushNativoDisponivel()) { setEstado('oculta'); return; }
        return pushNativo('checkPermissions').then(p => {
          pushEstadoUmaVez(p?.receive);
          if (p?.receive === 'granted') { registrarPushNativo(userId); setEstado('oculta'); }
          else if (p?.receive === 'denied') setEstado(negadoVisto() ? 'oculta' : 'negado');
          else setEstado(avisoAdiadoSM() ? 'oculta' : 'nativo');
        });
      }).catch(() => setEstado('oculta'));
      // Voltou dos Ajustes com o aviso ligado: inscreve na hora e some o cartao.
      const aoVoltar = () => {
        if (document.visibilityState !== 'visible' || !pushNativoDisponivel() || (estadoRef.current !== 'negado' && estadoRef.current !== 'nativo')) return;
        pushNativo('checkPermissions').then(p => {
          if (p?.receive !== 'granted' || (estadoRef.current !== 'negado' && estadoRef.current !== 'nativo')) return;
          setEstado('oculta');
          registrarPushNativo(userId).then(r => { if (r && r.ok) { uso.acao('push.ligou_nos_ajustes', { quem }); toastSM('Avisos ligados neste celular.'); } });
        }).catch(() => {});
      };
      document.addEventListener('visibilitychange', aoVoltar);
      return () => document.removeEventListener('visibilitychange', aoVoltar);
    }
    pushEstadoUmaVez(pushSuportado() ? Notification.permission : 'sem_suporte');
    if (pushSuportado() && Notification.permission === 'granted') inscreverPush(userId);
  }, [userId]);
  // Central de uso: funil push_ativar fica ativo so' durante o pedido de permissao.
  const [pedindoPush, setPedindoPush] = React.useState(false);
  const fPush = useFluxoUso('push_ativar', 1, pedindoPush);
  React.useEffect(() => {
    if (estado !== 'ios' || !userId) return;
    try { if (!sessionStorage.getItem('plen_uso_push_ios')) { sessionStorage.setItem('plen_uso_push_ios', '1'); uso.evento('push.faixa_vista', { detalhes: { estado: 'ios' } }); } } catch {}
  }, [estado, userId]);
  // v147: central de uso — o cartao do app foi visto (uma vez por sessao e por estado)
  React.useEffect(() => {
    if ((estado !== 'nativo' && estado !== 'negado') || !userId) return;
    try { const k = 'plen_uso_push_passo_' + estado; if (!sessionStorage.getItem(k)) { sessionStorage.setItem(k, '1'); uso.acao('push.passo_visto', { quem, estado: estado === 'nativo' ? 'pedir' : 'negado' }); } } catch {}
  }, [estado, userId]);
  if (estado === 'oculta' || estado === 'verificando' || !userId) return null;
  const dispensar = () => { try { localStorage.setItem(estado === 'ios' ? CHAVE_FAIXA_APP_STORE : 'pushFaixaDispensada', '1'); } catch {} setEstado('oculta'); };
  const fimPush = (r, via, origem = 'faixa') => { try { const res = resultadoPush(r); fPush.fim(res, { via, origem }); if (res === 'erro') uso.erro('erro.push', r && r.motivo); } catch {} setPedindoPush(false); };
  if (estado === 'nativo' || estado === 'negado') {
    const ehProf = quem === 'professor';
    const ativarNativo = async () => {
      if (pedindoPush) return;
      setPedindoPush(true);
      const r = await registrarPushNativo(userId, { pedir: true });
      fimPush(r, 'nativo', 'cartao');
      if (r.ok) { toastSM('Pronto! Os avisos vão chegar neste celular.'); setEstado('oculta'); }
      else if (r.motivo === 'denied') setEstado('negado');
      else { toastSM('Não deu para ativar agora. Tente de novo mais tarde.'); adiarAvisoSM(); setEstado('oculta'); }
    };
    if (estado === 'nativo') return (
      <div style={{background:'white', border:'2px solid #C2D4FF', borderRadius:'18px', padding:'18px 16px 10px', marginBottom:'12px'}}>
        <div style={{display:'flex', gap:'12px', alignItems:'flex-start'}}>
          <span style={{width:'44px', height:'44px', borderRadius:'13px', background:'#E7EDFF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n="bell-ringing" s={22} c="#2F6BFF" /></span>
          <div style={{minWidth:0}}>
            <div style={{fontFamily:"'Sora',sans-serif", fontSize:'17px', fontWeight:'700', color:'#16181D', lineHeight:1.3}}>Receber avisos de aula e cancelamentos?</div>
            <div style={{fontSize:'13.5px', color:'#3D424C', lineHeight:1.5, marginTop:'5px'}}>
              {ehProf
                ? 'Quando um aluno cancelar, remarcar ou responder, o aviso aparece na tela do celular — mesmo com o Plen fechado.'
                : 'Quando o professor cancelar, remarcar ou marcar uma aula, o aviso aparece na tela do celular — mesmo com o Plen fechado.'}
            </div>
          </div>
        </div>
        <button onClick={ativarNativo} disabled={pedindoPush}
          style={{width:'100%', minHeight:'50px', marginTop:'14px', border:'none', borderRadius:'14px', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'15.5px', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', opacity: pedindoPush ? 0.7 : 1}}>
          <Ico n="bell" s={17} c="white" /> {pedindoPush ? 'Aguarde…' : 'Ativar avisos'}
        </button>
        <div style={{fontSize:'12px', color:'#6E7480', textAlign:'center', marginTop:'8px', lineHeight:1.45}}>{pushAndroidSM() ? 'O celular vai perguntar: toque em “Permitir”.' : 'O iPhone vai perguntar: toque em “Permitir” (ou “Allow”).'}</div>
        <button onClick={() => { uso.acao('push.passo_agora_nao', { quem }); adiarAvisoSM(); setEstado('oculta'); }}
          style={{width:'100%', minHeight:'44px', marginTop:'2px', border:'none', background:'transparent', color:'#6E7480', fontWeight:'700', fontSize:'13.5px', cursor:'pointer', fontFamily:'inherit'}}>Agora não</button>
      </div>
    );
    return (
      <div style={{background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'18px', padding:'16px', marginBottom:'12px'}}>
        <div style={{display:'flex', gap:'12px', alignItems:'flex-start'}}>
          <span style={{width:'40px', height:'40px', borderRadius:'12px', background:'#FEF3C7', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n="bell-off" s={20} c="#D97706" /></span>
          <div style={{minWidth:0}}>
            <div style={{fontSize:'15px', fontWeight:'800', color:'#16181D', lineHeight:1.3}}>Os avisos estão desligados neste celular</div>
            <div style={{fontSize:'13px', color:'#3D424C', lineHeight:1.5, marginTop:'4px'}}>
              {ehProf ? 'Cancelamentos e respostas dos alunos só aparecem no sininho, quando você abre o Plen.' : 'Cancelamentos e aulas novas do professor só aparecem quando você abre o Plen.'}
            </div>
          </div>
        </div>
        <div style={{fontSize:'13px', color:'#3D424C', lineHeight:1.6, marginTop:'10px', background:'white', border:'1px solid #FDE68A', borderRadius:'12px', padding:'10px 12px'}}>
          {pushAndroidSM()
            ? <><b>Para ligar:</b> abra as <b>Configurações</b> do celular → <b>Apps</b> → <b>Plen</b> → <b>Notificações</b> e ligue os avisos.</>
            : <><b>Para ligar:</b> abra os <b>Ajustes</b> do iPhone → <b>Plen</b> → <b>Notificações</b> e ligue <b>Permitir Notificações</b>.</>}
        </div>
        <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
          {/* app-settings: so' o iPhone conhece; no Android o toque nao faria nada — fica so' o texto */}
          {!pushAndroidSM() && (
            <button onClick={() => { uso.acao('push.ajustes_abrir', { quem }); uso.enviarAgora(); try { window.location.href = 'app-settings:'; } catch {} }}
              style={{flex:1, minHeight:'46px', border:'none', borderRadius:'12px', background:'#16181D', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer', fontFamily:'inherit'}}>Abrir Ajustes</button>
          )}
          <button onClick={() => { uso.acao('push.ajustes_entendi', { quem }); try { localStorage.setItem(CHAVE_AVISO_NEGADO_VISTO, '1'); } catch {} setEstado('oculta'); }}
            style={{flex:1, minHeight:'46px', border:'1.5px solid #E2E4E9', borderRadius:'12px', background:'white', color:'#3D424C', fontWeight:'700', fontSize:'14px', cursor:'pointer', fontFamily:'inherit'}}>Entendi</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{background:'#16181D', color:'white', borderRadius:'14px', padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px'}}>
      <Ico n="bell-ringing" s={20} c="#A9C2FF" />
      <div style={{flex:1, fontSize:'13px', lineHeight:1.4}}>
        {estado === 'ios'
          ? <><strong>Receber avisos no iPhone?</strong> Baixe o Plen na App Store e entre com a mesma conta.</>
          : texto
            ? <strong>{texto}</strong>
            : <><strong>Receber avisos no celular?</strong> Aula registrada para confirmar, cancelada, pedido do outro lado — na hora.</>}
      </div>
      {estado === 'ios' && (
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => uso.acao('app_store.abrir', { origem: 'faixa_push' })}
          style={{display:'inline-flex', alignItems:'center', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'12.5px', borderRadius:'10px', padding:'9px 12px', minHeight:'44px', boxSizing:'border-box', textDecoration:'none', fontFamily:'inherit', whiteSpace:'nowrap'}}>Baixar</a>
      )}
      {estado === 'pedir' && (
        <button onClick={async () => { setPedindoPush(true); const r = await ativarPush(userId); fimPush(r, 'web'); if (r.ok) { toastSM('Avisos ativados neste aparelho.'); setEstado('oculta'); } else { toastSM('Não deu para ativar: ' + (r.motivo || '')); dispensar(); } }}
          style={{border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'12.5px', borderRadius:'10px', padding:'9px 12px', minHeight:'44px', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap'}}>Ativar</button>
      )}
      <button onClick={() => { uso.acao('push.faixa_dispensar', { estado }); dispensar(); }} aria-label="Fechar" style={{border:'none', background:'transparent', color:'rgba(255,255,255,.5)', cursor:'pointer', padding:'4px', minWidth:'44px', minHeight:'44px', marginRight:'-10px', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n="x" s={16} /></button>
    </div>
  );
}

// "Falar com o Plen" (02/10): canal de duvida/ideia/problema do piloto. Abre o WhatsApp do Plen
// com a versao e a tela ja' escritas (sem dado pessoal alem do primeiro nome, que a pessoa ve antes
// de enviar). O numero mora no banco (config_publica 'suporte_whatsapp'): da' para trocar sem
// publicar versao nova, e o botao some enquanto nao houver numero.
let _suporteWhats;  // undefined = ainda nao leu; '' = sem numero
async function numeroSuporte() {
  if (_suporteWhats !== undefined) return _suporteWhats;
  try {
    const { data } = await supabase.from('config_publica').select('valor').eq('chave', 'suporte_whatsapp').maybeSingle();
    _suporteWhats = String(data?.valor || '').replace(/\D/g, '');
  } catch { _suporteWhats = ''; }
  return _suporteWhats;
}
function FalarComPlen({ quem = 'professor', nome = '', origem = 'config', compacto = false }) {
  const [numero, setNumero] = React.useState(_suporteWhats || '');
  React.useEffect(() => { let vivo = true; numeroSuporte().then(n => { if (vivo) setNumero(n); }); return () => { vivo = false; }; }, []);
  if (!/^55\d{10,11}$/.test(numero || '')) return null;   // 55 + DDD + numero (o banco tambem exige)
  function abrir() {
    const primeiro = String(nome || '').trim().split(' ')[0];
    const tela = (uso.telaAtual && uso.telaAtual()) || '';
    const texto = `Oi! ${primeiro ? 'Sou ' + primeiro + ', ' : ''}${quem === 'aluno' ? 'aluno' : 'professor'} no Plen.\n\n` +
      `(versão ${VERSAO_APP}${tela ? ' · tela ' + tela : ''})\n\n`;
    uso.acao('suporte.whatsapp', { origem, quem });
    compartilharOuWhats(`https://wa.me/${numero}?text=${encodeURIComponent(texto)}`, 'suporte');
  }
  if (compacto) return (
    <button type="button" onClick={abrir}
      style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', width:'100%', minHeight:'44px', border:'none', background:'none',
              color:'#2F6BFF', fontWeight:'700', fontSize:'14px', cursor:'pointer', fontFamily:'inherit'}}>
      <Ico n="brand-whatsapp" s={18} c="#0E9F6E" /> Dúvida ou ideia? Falar com o Plen
    </button>
  );
  return (
    <button type="button" onClick={abrir}
      style={{display:'flex', alignItems:'center', gap:'14px', width:'100%', textAlign:'left', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'16px',
              padding:'14px 16px', cursor:'pointer', fontFamily:'inherit', minHeight:'64px'}}>
      <Ico n="brand-whatsapp" s={26} c="#0E9F6E" />
      <span style={{flex:1, minWidth:0}}>
        <span style={{display:'block', fontSize:'14px', fontWeight:'800', color:'#16181D'}}>Falar com o Plen</span>
        <span style={{display:'block', fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>Dúvida, problema ou ideia? Mande pelo WhatsApp — a gente responde.</span>
      </span>
      <Ico n="chevron-right" s={18} c="#9AA0AB" />
    </button>
  );
}

// Central de uso — chave "Ajudar a melhorar o Plen" (Configuracoes do professor e perfil do
// aluno). Ligada por padrao (LGPD: legitimo interesse, com aviso na politica e opcao de
// desligar). Grava NA HORA no banco (uso_preferencia), sem depender de outro "Salvar". Ao
// desligar, o banco tambem apaga o que ja' tinha sido registrado desta conta.
function ChaveAjudarPlen() {
  const [ligado, setLigado] = React.useState(() => uso.ligado());
  const [salvando, setSalvando] = React.useState(false);
  React.useEffect(() => {
    let vivo = true;
    try { supabase.rpc('minha_preferencia_uso').then(r => { if (vivo && r && !r.error && typeof r.data === 'boolean') setLigado(r.data); }, () => {}); } catch {}
    return () => { vivo = false; };
  }, []);
  async function trocar() {
    if (salvando) return;
    const novo = !ligado;
    setSalvando(true);
    const { error } = await supabase.rpc('uso_preferencia', { p_permitir: novo });
    setSalvando(false);
    if (error) { toastSM('Não consegui salvar agora. Tente de novo em instantes.'); return; }
    if (novo) { uso.ligar(); uso.acao('uso.preferencia', { ligado: true }); } else uso.desligar();
    setLigado(novo);
  }
  return (
    <div style={{display:'flex', alignItems:'center', gap:'14px', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'16px', padding:'14px 16px'}}>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:'14px', fontWeight:'800', color:'#16181D'}}>Ajudar a melhorar o Plen</div>
        <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>
          Enviar como você usa o app (telas e erros, sem o conteúdo). <a href="/privacidade.html" target="_blank" rel="noopener" style={{color:'#2F6BFF', fontWeight:'700', textDecoration:'none'}}>Saiba mais</a>
        </div>
      </div>
      <button type="button" role="switch" aria-checked={ligado} aria-label="Ajudar a melhorar o Plen" onClick={trocar} disabled={salvando}
        style={{flexShrink:0, width:'50px', height:'30px', borderRadius:'15px', border:'none', padding:'3px', cursor: salvando ? 'wait' : 'pointer',
                background: ligado ? '#0E9F6E' : '#C7CAD1', display:'flex', justifyContent: ligado ? 'flex-end' : 'flex-start', transition:'background .15s', opacity: salvando ? .6 : 1}}>
        <span style={{width:'24px', height:'24px', borderRadius:'50%', background:'white', boxShadow:'0 1px 3px rgba(0,0,0,.25)', display:'block'}} />
      </button>
    </div>
  );
}

// Etapa 5 (v131) — chave "Lembrar meus alunos antes da aula" (professores.lembrete_aula_ativo).
// Ligada por padrao. O robo do banco manda ao aluno, 12h antes do fim do prazo para desmarcar,
// "Aula com <Prof> qui 01/10 às 07:00. Não vai poder? Avise pelo app até …". Grava NA HORA.
function ChaveLembreteAula({ professor }) {
  const [ligado, setLigado] = React.useState(() => professor?.lembrete_aula_ativo !== false);
  const [salvando, setSalvando] = React.useState(false);
  React.useEffect(() => {
    let vivo = true;
    if (professor?.id) {
      supabase.from('professores').select('lembrete_aula_ativo').eq('id', professor.id).maybeSingle()
        .then(({ data, error }) => { if (vivo && !error && data && typeof data.lembrete_aula_ativo === 'boolean') setLigado(data.lembrete_aula_ativo); }, () => {});
    }
    return () => { vivo = false; };
  }, [professor?.id]);
  async function trocar() {
    if (salvando || !professor?.id) return;
    const novo = !ligado;
    setSalvando(true);
    let error = null;
    try { ({ error } = await supabase.from('professores').update({ lembrete_aula_ativo: novo }).eq('id', professor.id)); } catch (e) { error = e; }
    setSalvando(false);
    if (error) { uso.erro('erro.lembrete_aula', error.message || String(error)); toastSM('Não consegui salvar agora. Tente de novo em instantes.'); return; }
    uso.acao('lembrete_aula.preferencia', { ligado: novo });
    setLigado(novo);
    toastSM(novo ? 'Seus alunos vão receber o lembrete antes da aula.' : 'Lembrete desligado: seus alunos não recebem mais o aviso antes da aula.');
  }
  return (
    <div style={{display:'flex', alignItems:'center', gap:'14px', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'16px', padding:'14px 16px'}}>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:'14px', fontWeight:'800', color:'#16181D'}}>Lembrar meus alunos antes da aula</div>
        <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>
          O aluno recebe no celular a hora da aula e até quando dá para desmarcar sem custo. Só aula individual, entre 8h e 21h.
        </div>
      </div>
      {/* area de toque de 44px; o desenho da chave e' o mesmo de "Ajudar a melhorar o Plen" */}
      <button type="button" role="switch" aria-checked={ligado} aria-label="Lembrar meus alunos antes da aula" onClick={trocar} disabled={salvando}
        style={{flexShrink:0, minWidth:'50px', minHeight:'44px', border:'none', background:'transparent', padding:0, display:'flex', alignItems:'center',
                cursor: salvando ? 'wait' : 'pointer', opacity: salvando ? .6 : 1}}>
        <span style={{width:'50px', height:'30px', borderRadius:'15px', padding:'3px', boxSizing:'border-box', display:'flex',
                      background: ligado ? '#0E9F6E' : '#C7CAD1', justifyContent: ligado ? 'flex-end' : 'flex-start', transition:'background .15s'}}>
          <span style={{width:'24px', height:'24px', borderRadius:'50%', background:'white', boxShadow:'0 1px 3px rgba(0,0,0,.25)', display:'block'}} />
        </span>
      </button>
    </div>
  );
}

// v140 (Tema 3, "o Cadu esqueceu a minha aula") — lembretes PARA O PROFESSOR: aviso X min antes de
// cada aula (professores.lembrete_prof_min: 30 | 60 | 120 | null = desligado; padrao 60) e resumo da
// agenda (professores.resumo_agenda: 'noite' = 20h da vespera | 'manha' = 7h do dia | null; padrao
// 'noite'). Quem manda e' o robo do banco (gerar_lembretes_professor). Grava NA HORA, como a chave
// acima. Banco sem as colunas (SQL nao rodado): as opcoes aparecem apagadas, sem quebrar a tela.
// v143 (A): "No meio da aula anterior" — o aviso chega no meio da aula que o professor esta' dando
// ("Proxima aula: Bia as 10:00."); sem aula logo antes, 30 min antes. Fica guardado em DUAS colunas:
// professores.lembrete_prof_modo ('antes' | 'meio_anterior', nova na v143) + lembrete_prof_min = 30 (a
// reserva — o app v142 mostra "30 min" e nao quebra). Este app grava SEMPRE as duas juntas; banco sem a
// coluna nova (SQL da v143 nao rodado ou desfeito): a opcao nova fica apagada e as outras gravam so' os minutos.
const OPCOES_LEMBRETE_PROF_SM = [
  { v: 30, t: '30 min' }, { v: 60, t: '1 hora' }, { v: 120, t: '2 horas' },
  { v: 'meio', t: 'No meio da aula anterior', sub: 'Sem aula logo antes, chega 30 min antes' },
  { v: null, t: 'Desligado' },
];
// a opcao marcada: null = desligado; 'meio' = no meio da aula anterior; 30/60/120 = minutos antes
function escolhaLembreteProfSM(min, modo) { return min == null ? null : (modo === 'meio_anterior' ? 'meio' : min); }
// o banco perdeu so' a coluna do modo (o resto da tela continua valendo)
const faltaColunaModoSM = e => ehColunaFaltandoSM(e) && /lembrete_prof_modo/.test(String((e && (e.message || e.details)) || ''));
const OPCOES_RESUMO_AGENDA_SM = [
  { v: 'noite', t: 'Na noite anterior (20h)' }, { v: 'manha', t: 'De manhã (7h)' }, { v: null, t: 'Desligado' },
];
function PreferenciasLembreteProfSM({ professor }) {
  const doProf = (k, padrao) => (professor && Object.prototype.hasOwnProperty.call(professor, k) ? professor[k] : padrao);
  const [min, setMin] = React.useState(() => doProf('lembrete_prof_min', 60));
  const [modo, setModo] = React.useState(() => (doProf('lembrete_prof_modo', 'antes') === 'meio_anterior' ? 'meio_anterior' : 'antes'));
  const [resumo, setResumo] = React.useState(() => doProf('resumo_agenda', 'noite'));
  const [estado, setEstado] = React.useState('carregando');   // carregando | ok | sem_coluna
  const [semModo, setSemModo] = React.useState(false);        // v143: banco sem lembrete_prof_modo
  const [salvando, setSalvando] = React.useState(null);       // 'min' | 'resumo'
  React.useEffect(() => {
    let vivo = true;
    if (!professor?.id) return undefined;
    const ler = cols => supabase.from('professores').select(cols).eq('id', professor.id).maybeSingle();
    (async () => {
      let r = null;
      try { r = await ler('lembrete_prof_min, resumo_agenda, lembrete_prof_modo'); } catch { r = null; }
      let comModo = true;
      // v143: SQL da v143 ainda nao rodou (ou foi desfeito) — le como a v142, sem o modo
      if (r && r.error && ehColunaFaltandoSM(r.error)) {
        comModo = false;
        try { r = await ler('lembrete_prof_min, resumo_agenda'); } catch { r = null; }
      }
      if (!vivo) return;
      if (!r) { setEstado('ok'); return; }
      const { data, error } = r;
      if (error) { setEstado(ehColunaFaltandoSM(error) ? 'sem_coluna' : 'ok'); return; }
      setSemModo(!comModo);
      if (data) {
        setMin(data.lembrete_prof_min == null ? null : Number(data.lembrete_prof_min));
        setModo(comModo && data.lembrete_prof_modo === 'meio_anterior' ? 'meio_anterior' : 'antes');
        setResumo(data.resumo_agenda || null);
      } else if (!comModo) setModo('antes');
      setEstado('ok');
    })();
    return () => { vivo = false; };
  }, [professor?.id]);
  const escolhido = escolhaLembreteProfSM(min, modo);
  async function salvar(campo, valor) {
    if (salvando || !professor?.id || estado === 'sem_coluna') return;
    if (campo === 'min') return salvarLembrete(valor);
    const antes = resumo;
    if (antes === valor) return;
    setSalvando(campo);
    setResumo(valor);
    let error = null;
    try {
      ({ error } = await supabase.from('professores').update({ resumo_agenda: valor }).eq('id', professor.id));
    } catch (e) { error = e; }
    setSalvando(null);
    if (error) {
      setResumo(antes);
      if (ehColunaFaltandoSM(error)) { setEstado('sem_coluna'); toastSM('Ainda não disponível. Atualize o app (feche e abra de novo) e tente de novo.'); }
      else toastSM('Não consegui salvar agora. Tente de novo em instantes.');
      uso.erro('erro.lembrete_prof', error.message || String(error));
      return;
    }
    uso.acao('resumo_agenda.preferencia', { quando: valor || 'desligado' });
    toastSM(valor === 'noite' ? 'Resumo da agenda: toda noite, às 20h, as aulas do dia seguinte.'
      : valor === 'manha' ? 'Resumo da agenda: todo dia, às 7h, as aulas do dia.' : 'Resumo da agenda desligado.');
  }
  // "Lembrar das minhas aulas": valor = 30 | 60 | 120 | 'meio' | null. Grava as DUAS colunas juntas
  // (o gatilho do banco tambem as mantem coerentes quando um app antigo troca so' os minutos).
  async function salvarLembrete(valor) {
    if (escolhido === valor) return;
    if (valor === 'meio' && semModo) return;
    const antesMin = min, antesModo = modo;
    const novoMin = valor === 'meio' ? 30 : valor;
    const novoModo = valor === 'meio' ? 'meio_anterior' : 'antes';
    setSalvando('min');
    setMin(novoMin); setModo(novoModo);
    let error = null;
    try {
      ({ error } = await supabase.from('professores')
        .update(semModo ? { lembrete_prof_min: novoMin } : { lembrete_prof_modo: novoModo, lembrete_prof_min: novoMin })
        .eq('id', professor.id));
    } catch (e) { error = e; }
    setSalvando(null);
    if (error) {
      setMin(antesMin); setModo(antesModo);
      // so' a coluna do modo sumiu (SQL da v143 desfeito com a tela aberta): a opcao nova apaga e as outras
      // voltam a gravar so' os minutos — o professor toca de novo
      if (faltaColunaModoSM(error)) { setSemModo(true); setModo('antes'); toastSM('Não consegui salvar agora. Toque de novo na opção.'); }
      else if (ehColunaFaltandoSM(error)) { setEstado('sem_coluna'); toastSM('Ainda não disponível. Atualize o app (feche e abra de novo) e tente de novo.'); }
      else toastSM('Não consegui salvar agora. Tente de novo em instantes.');
      uso.erro('erro.lembrete_prof', error.message || String(error));
      return;
    }
    uso.acao('lembrete_prof.preferencia', { min: novoMin == null ? 0 : novoMin, modo: valor === 'meio' ? 'meio' : 'antes' });
    toastSM(valor === 'meio' ? 'Pronto: o aviso chega no meio da aula anterior (sem aula logo antes, 30 min antes).'
      : valor == null ? 'Aviso antes da aula desligado.'
      : `Você vai receber um aviso ${valor === 60 ? '1 hora' : valor === 120 ? '2 horas' : valor + ' min'} antes de cada aula.`);
  }
  const off = estado === 'sem_coluna';
  const bt = (on, ocupado, apagado = off) => ({ minHeight: '44px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, fontFamily: 'inherit', padding: '0 8px',
    cursor: apagado ? 'not-allowed' : ocupado ? 'wait' : 'pointer', opacity: apagado ? 0.55 : 1,
    border: on ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: on ? '#E7EDFF' : 'white', color: on ? '#1C43AE' : '#3D424C' });
  const meioApagado = off || semModo;
  return (
    <div style={{ background: 'white', border: '1.5px solid #E2E4E9', borderRadius: '16px', padding: '14px 16px' }}>
      <div style={{ fontSize: '14px', fontWeight: 800, color: '#16181D' }}>Lembrar das minhas aulas</div>
      <div style={{ fontSize: '12.5px', color: '#6E7480', marginTop: '3px', lineHeight: 1.5 }}>
        Um aviso no seu celular antes de cada aula: "Daqui a 1h: aula com Marcus às 12:00."
      </div>
      {/* v143: 3 colunas fixas (30 min · 1 hora · 2 horas) e, embaixo, "No meio…" e "Desligado" em largura cheia —
          com a grade automatica, a 375px sobrava um buraco do lado de "2 horas" e "Desligado" ficava sozinho */}
      <div role="radiogroup" aria-label="Lembrar das minhas aulas" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '6px', marginTop: '10px' }}>
        {OPCOES_LEMBRETE_PROF_SM.map(o => {
          const on = escolhido === o.v;
          // v143: largura cheia, duas linhas (o nome e a reserva)
          if (o.v === 'meio') return (
            <button key="meio" type="button" role="radio" aria-checked={on} disabled={meioApagado || !!salvando}
              onClick={() => salvar('min', 'meio')}
              style={{ ...bt(on, salvando === 'min', meioApagado), gridColumn: '1 / -1', minHeight: '52px', textAlign: 'left', padding: '7px 14px',
                       display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', lineHeight: 1.3 }}>
              <span>{o.t}</span>{' '}
              <span style={{ fontSize: '11.5px', fontWeight: 600, color: on ? '#1C43AE' : '#6E7480', marginTop: '2px' }}>{o.sub}</span>
            </button>
          );
          return (
            <button key={String(o.v)} type="button" role="radio" aria-checked={on} disabled={off || !!salvando}
              onClick={() => salvar('min', o.v)} style={o.v == null ? { ...bt(on, salvando === 'min'), gridColumn: '1 / -1', textAlign: 'left', padding: '0 14px' } : bt(on, salvando === 'min')}>{o.t}</button>
          );
        })}
      </div>
      {escolhido === 'meio' && !meioApagado ? (
        <div style={{ fontSize: '12px', color: '#6E7480', marginTop: '8px', lineHeight: 1.5 }}>
          Chega no meio da aula que você estiver dando: "Próxima aula: Marcus às 10:00."
        </div>
      ) : null}
      <div style={{ fontSize: '14px', fontWeight: 800, color: '#16181D', marginTop: '18px' }}>Resumo da agenda</div>
      <div style={{ fontSize: '12.5px', color: '#6E7480', marginTop: '3px', lineHeight: 1.5 }}>
        As aulas do dia numa mensagem só: "Amanhã: 07:00 Ana · 12:00 Marcus." Só chega quando há aula.
      </div>
      <div role="radiogroup" aria-label="Resumo da agenda" style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
        {OPCOES_RESUMO_AGENDA_SM.map(o => (
          <button key={String(o.v)} type="button" role="radio" aria-checked={resumo === o.v} disabled={off || !!salvando}
            onClick={() => salvar('resumo', o.v)} style={{ ...bt(resumo === o.v, salvando === 'resumo'), textAlign: 'left', padding: '0 14px' }}>{o.t}</button>
        ))}
      </div>
      <div style={{ fontSize: '11.5px', color: '#9AA0AB', marginTop: '10px', lineHeight: 1.5 }}>
        {off ? 'Disponível depois da próxima atualização do Plen.'
          : 'Só chega com os avisos do Plen ativados neste celular. Salva sozinho, na hora.'}
        {!off && semModo ? <><br />No meio da aula anterior: disponível depois da próxima atualização do Plen.</> : null}
      </div>
    </div>
  );
}

// Largura de celular (a mesma regra do CSS: < 768px). Para trocar um desenho que so'
// cabe no desktop — a grade de 7 colunas da semana, por exemplo — por um de bolso.
function useEhMobile() {
  const [m, setM] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => { const f = () => setM(window.innerWidth < 768); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return m;
}

// Tela escura (entrada) x tela clara (app): o fundo do <body> e a cor do texto da barra
// de status do iPhone (hora, bateria) acompanham. No PWA o body basta; no app da loja o
// plugin StatusBar do Capacitor troca o texto para branco/preto.
function useTemaTela(escuro) {
  React.useEffect(() => {
    document.body.classList.toggle('tela-escura', !!escuro);
    try {
      const SB = window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.StatusBar;
      if (SB && SB.setStyle) SB.setStyle({ style: escuro ? 'DARK' : 'LIGHT' });
      const meta = document.querySelector('meta[name="theme-color"]');
      if (meta) meta.setAttribute('content', escuro ? '#16181D' : '#F7F8FA');
    } catch {}
    return () => { document.body.classList.remove('tela-escura'); };
  }, [escuro]);
}

// Campo de dia/hora com rotulo e texto quando vazio (21/10, Marcus em "Adicionar aula combinada"): no
// iPhone o campo de data/hora vazio aparece EM BRANCO, sem nada escrito, ate' tocar. O texto "Escolher o
// dia" fica por cima (sem pegar o toque) e o texto nativo do campo vazio ("dd/mm/aaaa" no computador)
// fica transparente para nao aparecer duas coisas. O seletor continua o do aparelho.
function CampoDataHoraSM({ tipo, rotulo, vazio, value, onChange, min, style }) {
  const temValor = !!value;
  return (
    <label style={{display:'flex', flexDirection:'column', gap:'4px', ...style}}>
      <span style={{fontSize:'11.5px', fontWeight:'700', color:'#3D424C'}}>{rotulo}</span>
      <span style={{position:'relative', display:'block'}}>
        <input type={tipo} value={value} min={min} onChange={e => onChange(e.target.value)} aria-label={rotulo}
          style={{width:'100%', minHeight:'44px', padding:'10px 12px', borderRadius:'10px', border:'1.5px solid ' + (temValor ? '#E2E4E9' : '#C2D4FF'),
                  fontSize:'14px', background:'white', boxSizing:'border-box', color: temValor ? '#16181D' : 'transparent'}} />
        {!temValor && (
          <span aria-hidden="true" style={{position:'absolute', left:'12px', right:'8px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none',
                                           display:'flex', alignItems:'center', gap:'6px', fontSize:'14px', fontWeight:'600', color:'#2F6BFF', whiteSpace:'nowrap', overflow:'hidden'}}>
            <Ico n={tipo === 'time' ? 'clock' : 'calendar'} s={15} c="#2F6BFF" /> {vazio}
          </span>
        )}
      </span>
    </label>
  );
}

// "?" ao lado do nome de uma funcionalidade. O professor (e mais ainda o aluno) nao le'
// manual: ele bate o olho num botao chamado "Adicionar aula combinada" e nao faz ideia do
// que acontece se tocar — e no fim nao toca. Em vez de encher a tela de texto explicativo,
// a explicacao fica dobrada aqui e abre com um toque, do lado da coisa que ela explica.
// Regra para o texto: diz O QUE acontece e QUEM decide, em uma ou duas frases, sem jargao.
function Ajuda({ texto, claro }) {
  const [aberto, setAberto] = React.useState(false);
  return (
    <>
      <button type="button" onClick={e => { e.stopPropagation(); setAberto(v => !v); }}
        aria-label={aberto ? 'Fechar explicação' : 'O que é isso?'}
        aria-expanded={aberto}
        style={{ width: '20px', height: '20px', borderRadius: '50%', border: 'none', padding: 0, flexShrink: 0,
                 background: aberto ? '#2F6BFF' : (claro ? 'rgba(255,255,255,0.18)' : '#E7EDFF'),
                 color: aberto ? 'white' : (claro ? 'white' : '#2455D6'),
                 fontSize: '12px', fontWeight: '800', lineHeight: '20px', cursor: 'pointer',
                 display: 'inline-flex', alignItems: 'center', justifyContent: 'center', verticalAlign: 'middle' }}>?</button>
      {aberto && (
        <div style={{ background: '#F4F6FB', border: '1px solid #DDE3F0', borderRadius: '12px',
                      padding: '10px 12px', marginTop: '8px', fontSize: '12.5px', color: '#3D424C',
                      lineHeight: 1.5, width: '100%' }}>
          {texto}
        </div>
      )}
    </>
  );
}

// AS BOLINHAS — o saldo do aluno do jeito que a landing promete: "a mesma conta, dos dois
// lados". Uma peca so', usada no cartao do professor e na tela do aluno, calculada pelo
// mesmo motor (calcularCreditos) — por isso os dois lados nunca veem numeros diferentes.
//
// PACOTE: cada bolinha e' uma aula comprada. Azul cheia = feita. Ambar = faltou e contou
// (usou a aula sem treinar — dizer isso com honestidade evita a briga "eu nao fiz 6!").
// Vazia = ainda tem. Com mais de 24 aulas as bolinhas viram poeira; vira barra + numero.
//
// MENSAL: nao existe saldo — o aluno paga o mes treinando ou nao. O que interessa e' o
// APROVEITAMENTO: das aulas do mes que ja' passaram, quantas ele fez. Azul = fez,
// vermelho = nao fez (faltou ou desmarcou), vazia = ainda vai acontecer, cinza = o
// professor cancelou ou foi imprevisto (nao conta contra o aluno). A porcentagem conta so'
// o que ja' passou — no dia 3 do mes, "1 de 12" seria mentira; "1 de 1" e' a verdade.
// Para o professor, aproveitamento baixo e' o primeiro sinal de que o aluno vai cancelar.
//
// PACOTE (v133, Marcus 06/10): a mesma ideia. Das aulas que ja' SAIRAM do pacote, quantas foram
// treino e quantas foram perdidas (falta que contou como aula). E' a conta das bolinhas — azul
// sobre azul + laranja. No lugar da "sequencia de semanas", que dizia "2a semana seguida
// treinando" logo depois de o aluno perder a aula da semana.
const corAproveitamentoSM = pct => pct === null ? '#16181D' : pct >= 80 ? '#0A6F4D' : pct >= 60 ? '#B45309' : '#DC2626';
function aproveitamentoPacoteSM(contrato, aulas) {
  if (!contrato || contrato.tipo_cobranca === 'mensal') return null;
  const doContrato = (aulas || []).filter(a => !a.contrato_id || a.contrato_id === contrato.id);
  const c = calcularCreditos(contrato, doContrato);
  if (!c.total || !c.usado) return null;
  const perdidas = Math.min(doContrato.filter(a => a.status === 'falta').length, c.usado);
  const treinos = c.usado - perdidas;
  const lista = doContrato.filter(a => a.status === 'realizada' || a.status === 'falta')
    .sort((x, y) => String(x.data).localeCompare(String(y.data)) || String(x.hora || '').localeCompare(String(y.hora || '')));
  return { treinos, perdidas, usadas: c.usado, pct: Math.round(treinos / c.usado * 100), lista };
}
function PontosAulas({ contrato, aulas, tamanho = 16, compacto = false, soPontos = false, comDatas = false, proximoInicio = null, semPrevisao = false }) {
  if (!contrato) return null;
  const mensal = contrato.tipo_cobranca === 'mensal';
  const cores = { feita: '#2F6BFF', falta: '#D97706', naoFez: '#DC2626', neutra: '#C7CAD1', pendente: '#A9C2FF' };
  const bola = (tipo, k) => (
    <span key={k} style={{
      width: tamanho + 'px', height: tamanho + 'px', borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
      background: tipo === 'vazia' ? 'transparent' : cores[tipo],
      border: tipo === 'vazia' ? `${Math.max(1.5, tamanho / 9)}px solid #2F6BFF` : 'none',
    }} />
  );
  const linha = (conteudo) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: Math.max(3, Math.round(tamanho * 0.35)) + 'px' }}>{conteudo}</div>
  );
  const txtP = { fontSize: compacto ? '11.5px' : '13px', color: '#6E7480' };
  const txtG = { fontSize: compacto ? '12.5px' : '17px', fontWeight: 800, color: '#16181D' };

  if (!mensal) {
    const doContrato = (aulas || []).filter(a => !a.contrato_id || a.contrato_id === contrato.id);
    const c = calcularCreditos(contrato, doContrato);
    if (!c.total) return null;
    // v136 (Marcus 09/10): cada bolinha e' UMA aula, na ORDEM DAS DATAS. Antes elas saiam agrupadas
    // (todas as azuis, depois as laranjas): perdeu a de 06/10 e fez a de 08/10, a laranja aparecia por
    // ultimo, como se a falta tivesse sido a aula mais recente. As "anteriores" (contadas antes do app,
    // sem data) vem primeiro; depois as que sairam do pacote, por data; as registradas esperando o outro
    // lado; e as que faltam, com a data marcada quando ja' existe na agenda.
    const ordemAula = (x, y) => String(x.data).localeCompare(String(y.data)) || String(x.hora || '').localeCompare(String(y.hora || ''));
    const anterioresN = Math.max(0, Math.min(c.usado, Number(contrato.aulas_realizadas_anterior) || 0));
    const usadasComData = doContrato.filter(a => a.status === 'realizada' || a.status === 'falta').sort(ordemAula)
      .slice(0, Math.max(0, c.usado - anterioresN));
    // registrada por um lado, esperando o outro confirmar: ainda nao saiu do saldo (consenso 24/09)
    const pendLista = doContrato.filter(a => a.status === 'agendada' && (a.desfecho_prof || a.confirmada_aluno_em)).sort(ordemAula).slice(0, c.restante);
    const marcadas = doContrato.filter(a => a.status === 'agendada' && !(a.desfecho_prof || a.confirmada_aluno_em)).sort(ordemAula);
    const posicoes = [
      ...Array.from({ length: anterioresN }, () => ({ tipo: 'feita', aula: null })),
      ...usadasComData.map(a => ({ tipo: a.status === 'falta' ? 'falta' : 'feita', aula: a })),
      ...Array.from({ length: Math.max(0, c.usado - anterioresN - usadasComData.length) }, () => ({ tipo: 'feita', aula: null })),
      ...pendLista.map(a => ({ tipo: 'pendente', aula: a })),
      ...Array.from({ length: Math.max(0, c.restante - pendLista.length) }, (_, i) => ({ tipo: 'vazia', aula: marcadas[i] || null })),
    ];
    const faltas = posicoes.filter(p => p.tipo === 'falta').length;
    const feitas = c.usado - faltas;
    const pend = pendLista.length;
    const fim = contrato.data_fim ? fmtDataBR(contrato.data_fim).slice(0, 5) : null;
    const listaPontos = posicoes.map((p, i) => bola(p.tipo, 'b' + i));
    // "Ver as datas" (Marcus 09/10: "tem que ter algum lugar para mostrar as datas das aulas feitas e
    // perdidas"): uma linha por bolinha, na mesma ordem, com a mesma cor. details nativo: sem estado.
    const quandoAulaSM = a => {
      const d = new Date(a.data + 'T12:00:00');
      return d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '') + ' ' + String(a.data).slice(8, 10) + '/' + String(a.data).slice(5, 7)
        + (a.hora ? ' · ' + String(a.hora).slice(0, 5) : '');
    };
    const rotuloPos = p => p.tipo === 'feita' ? (p.aula ? 'Treino' : 'Treino (antes do app)')
      : p.tipo === 'falta' ? 'Perdida — contou como aula'
      : p.tipo === 'pendente' ? 'Registrada, aguardando confirmação'
      : p.aula ? 'Marcada' : 'A marcar';
    const corRotulo = { feita: '#16181D', falta: '#B45309', pendente: '#1C43AE', vazia: '#6E7480' };
    const listaDatas = comDatas && !compacto && posicoes.length > 0 && posicoes.length <= 60 ? (
      <details style={{ marginTop: '10px' }} onToggle={e => { if (e.currentTarget.open) uso.acao('plano.datas'); }}>
        <summary style={{ cursor: 'pointer', listStyle: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 700, color: '#2F6BFF', minHeight: '32px' }}>
          <Ico n="calendar" s={14} c="#2F6BFF" /> Ver as datas <Ico n="chevron-down" s={13} c="#2F6BFF" />
        </summary>
        <div style={{ marginTop: '6px' }}>
          {posicoes.map((p, i) => (
            <div key={'d' + i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', borderTop: i ? '1px solid #F0F1F4' : 'none', fontSize: '13px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                             background: p.tipo === 'vazia' ? 'transparent' : cores[p.tipo], border: p.tipo === 'vazia' ? '1.5px solid #2F6BFF' : 'none' }} />
              <span className="num" style={{ color: '#9AA0AB', minWidth: '18px' }}>{i + 1}</span>
              <span className="num" style={{ color: '#3D424C', minWidth: '96px', whiteSpace: 'nowrap', flexShrink: 0 }}>{p.aula ? quandoAulaSM(p.aula) : '—'}</span>
              <span style={{ color: corRotulo[p.tipo], fontWeight: 600, minWidth: 0 }}>{rotuloPos(p)}</span>
            </div>
          ))}
        </div>
      </details>
    ) : null;
    const pontos = c.total <= 24 ? linha(listaPontos) : null;
    if (soPontos && pontos) {
      // Lista do professor: o aproveitamento so' aparece quando ja' houve aula perdida — e' o
      // sinal de aluno esfriando; com tudo azul, as bolinhas ja' dizem tudo.
      if (faltas === 0 || c.usado < 2) return pontos;
      const pctP = Math.round(feitas / c.usado * 100);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {pontos}
          <span style={{ fontSize: '11.5px', fontWeight: 800, color: corAproveitamentoSM(pctP) }}>aproveitamento {pctP}%</span>
        </div>
      );
    }
    return (
      <div>
        {c.total <= 24
          ? linha(listaPontos)
          : <div style={{ height: '8px', background: '#E7EDFF', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: Math.round(c.usado / c.total * 100) + '%', height: '100%', background: '#2F6BFF' }} />
            </div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: compacto ? '6px' : '12px', gap: '8px' }}>
          <span style={txtG}>{c.restante === 0 ? 'Pacote concluído' : `Faltam ${c.restante}`}
            {c.excedente > 0 && <span style={{ ...txtP, fontWeight: 700, color: '#B45309' }}> · +{c.excedente} além</span>}
          </span>
          <span style={txtP}>{c.usado} de {c.total}{proximoInicio ? ` · próximo pacote começa ${fmtDataBR(proximoInicio).slice(0, 5)}` : fim && c.restante > 0 && !semPrevisao ? ` · previsão de renovação ${fim}` : ''}</span>
        </div>
        {!compacto && faltas > 0 && (
          <div style={{ ...txtP, fontSize: '12px', marginTop: '6px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: cores.falta, marginRight: '5px' }} />
            {faltas === 1 ? '1 falta contou como aula' : `${faltas} faltas contaram como aula`}
          </div>
        )}
        {!compacto && pend > 0 && (
          <div style={{ ...txtP, fontSize: '12px', marginTop: '6px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: cores.pendente, marginRight: '5px' }} />
            {pend === 1 ? '1 aula aguardando confirmação (ainda não saiu do pacote)' : `${pend} aulas aguardando confirmação (ainda não saíram do pacote)`}
          </div>
        )}
        {listaDatas}
      </div>
    );
  }

  // mensal — aulas do mes corrente
  const hoje = hojeSP();
  const mes = hoje.slice(0, 7);
  // v131: sem a original de uma aula remarcada (a nova ja' esta' na lista)
  const doMes = (aulas || []).filter(a => a.data && a.data.startsWith(mes) && a.status !== 'aguardando_confirmacao' && a.status !== 'remarcada')
    .sort((a, b) => String(a.data).localeCompare(String(b.data)));
  if (!doMes.length) return null;
  const tipoDe = a => {
    if (a.status === 'realizada') return 'feita';
    if (rotuloEspecialAula(a)) return 'neutra';   // feriado / pausa: nao e' culpa do aluno
    if (a.status === 'falta' || a.status === 'cancelada_aluno') return 'naoFez';
    if (a.status === 'cancelada_professor' || a.status === 'cancelada_forca_maior') return 'neutra';
    return a.data < hoje ? 'neutra' : 'vazia';   // passou sem ninguem fechar: nao culpo o aluno
  };
  const tipos = doMes.map(tipoDe);
  const fez = tipos.filter(t => t === 'feita').length;
  const naoFez = tipos.filter(t => t === 'naoFez').length;
  const base = fez + naoFez;
  const pct = base ? Math.round(fez / base * 100) : null;
  const nomeMes = new Date().toLocaleDateString('pt-BR', { month: 'long' });
  const corPct = corAproveitamentoSM(pct);
  return (
    <div>
      {linha(tipos.map((t, i) => bola(t, i)))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: compacto ? '6px' : '12px', gap: '8px' }}>
        <span style={{ ...txtG, color: corPct }}>
          {pct === null ? `${doMes.length} aulas em ${nomeMes}` : `Aproveitamento ${pct}%`}
        </span>
        <span style={txtP}>{base ? `${fez} de ${base} até hoje` : 'o mês está começando'}</span>
      </div>
    </div>
  );
}

// Data do banco ('2026-11-06' ou ISO) em dd/mm/aaaa, sem o susto do fuso: sem a hora
// fixa do meio-dia, '2026-11-06' vira 05/11 em qualquer lugar a oeste de Greenwich.
function fmtDataBR(s) {
  if (!s) return '—';
  return new Date(String(s).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR');
}

// ---- Hora do Brasil (v131) --------------------------------------------------
// O banco decide prazo e "hoje" pela hora de Brasilia (_hoje_sp, _prazo_aula). O celular
// tem de usar a MESMA conta: toISOString() da' o dia de Londres (depois das 21h de Brasilia
// ja' seria amanha) e um celular em outro fuso erraria a hora da aula.
const FUSO_SP = 'America/Sao_Paulo';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// 'AAAA-MM-DD' de hoje em Brasilia (deslocDias: +1 = amanha).
function hojeSP(deslocDias = 0) {
  return diaSP(new Date(Date.now() + deslocDias * 86400000));
}
// Inicio da aula como instante: data + hora (sem hora: 08:00) em Brasilia (sem horario de
// verao desde 2019 = UTC-3) — a mesma conta de _prazo_de no banco.
function inicioAulaSP(aula) {
  if (!aula || !aula.data) return null;
  const h = String(aula.hora || '08:00').slice(0, 5);
  const t = new Date(`${String(aula.data).slice(0, 10)}T${h}:00-03:00`);
  return isNaN(t) ? null : t;
}
// 'qua 30/09 às 07:00' em Brasilia — o mesmo formato do banco (_quando_tz).
function quandoSP(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d)) return '';
  try {
    const dia = new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO_SP, weekday: 'short' }).format(d).replace('.', '').toLowerCase();
    const dm = new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO_SP, day: '2-digit', month: '2-digit' }).format(d);
    return `${dia} ${dm} às ${horaSP(d)}`;
  } catch { return d.toLocaleString('pt-BR'); }
}
// 'HH:MM' em Brasilia
function horaSP(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d)) return '';
  try { return new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO_SP, hour: '2-digit', minute: '2-digit' }).format(d); }
  catch { return d.toTimeString().slice(0, 5); }
}
// 'AAAA-MM-DD' (em Brasilia) de um instante
function diaSP(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d)) return '';
  try {
    // pelas partes (ano, mes, dia): o formato pronto de cada idioma muda entre navegadores
    const p = {};
    new Intl.DateTimeFormat('en-US', { timeZone: FUSO_SP, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(d).forEach(x => { p[x.type] = x.value; });
    if (p.year && p.month && p.day) return `${p.year}-${p.month}-${p.day}`;
  } catch {}
  return d.toLocaleDateString('sv-SE');
}

// Etapa 6 (v131): na mensalidade e na turma, remarcar NAO move a aula — a original fica com
// status 'remarcada' (so' historico) e nasce a nova. Toda lista de aulas esconde a original.
function semRemarcada(lista) { return (lista || []).filter(a => a && a.status !== 'remarcada'); }
// Aula cancelada que nao e' culpa de ninguem: o rotulo diz o que foi (nunca "forca maior").
function rotuloEspecialAula(a) {
  if (!a || !String(a.status || '').startsWith('cancelada')) return null;
  if (a.motivo_cancelamento === 'feriado') return 'Feriado';
  if (a.motivo_cancelamento === 'pausa') return 'Pausa';
  return null;
}
// Etapa 8 (v131): o aluno avisou pelo app (hora OFICIAL gravada pelo banco) e ninguem desfez depois.
function avisoVigenteAula(a) {
  if (!a || !a.aviso_aluno_em) return false;
  return !a.desfeita_em || new Date(a.aviso_aluno_em) > new Date(a.desfeita_em);
}
// "Você avisou às 22:00" (hoje) / "Você avisou em 30/09 às 22:00" (outro dia). v141: quando quem
// registrou foi o PROFESSOR ("o aluno avisou", canal professor_registrou), diz isso — antes o aluno lia
// "Você avisou" de um aviso que nao mandou. A hora e' a que o professor informou.
function quandoAvisoSM(a) {
  const hoje = diaSP(a.aviso_aluno_em) === hojeSP();
  const dm = diaSP(a.aviso_aluno_em).split('-').reverse().slice(0, 2).join('/');
  return hoje ? `às ${horaSP(a.aviso_aluno_em)}` : `em ${dm} às ${horaSP(a.aviso_aluno_em)}`;
}
function textoAvisouAluno(a, nomeProf = 'O professor') {
  if (!avisoVigenteAula(a)) return '';
  const quando = quandoAvisoSM(a);
  return a.aviso_canal === 'professor_registrou' ? `${nomeProf} registrou que você avisou ${quando}` : `Você avisou ${quando}`;
}
// v141 (B6): o PROFESSOR registrou "o aluno avisou" e nao foi a aula. Antes o aluno ficava sem como
// dizer "o professor nao veio" (o banco respondia avisou_falta) e perdia a aula. Com a regra da v141
// (sql da v141, aluno_responder_aula): o aviso registrado pelo professor cai e vale o caminho normal do
// "nao veio" — 24h para o professor responder; se ele registrar diferente, vira desacordo e a decisao
// final e' dele. O aviso feito pelo PROPRIO aluno continua travando o "nao veio", como antes.
// A regra so' vale se a parte 3 do SQL da v141 rodou. Quem diz e' o BANCO, nao uma constante: a parte 3
// grava config_publica 'nao_veio_contra_aviso_prof' = 'sim' (a VOLTA apaga). Assim app e banco nunca
// ficam diferentes — publicar o app antes do SQL, ou o Marcus dizer "nao" e a parte 3 ficar de fora, so'
// deixa a regra desligada (o app volta ao comportamento da v140), sem prometer o que o banco recusa.
// Sem a leitura (sem internet, banco sem a linha): desligada. Relida a cada 10 min (o aluno recarrega
// o contexto a cada minuto) — ligar a regra depois nao exige publicar o app de novo.
const _regraNaoVeio = { ligada: false, lidaEm: 0, lendo: null };
function naoVeioContraAvisoProfSM() { return _regraNaoVeio.ligada; }
function carregarRegraNaoVeioSM() {
  if (Date.now() - _regraNaoVeio.lidaEm < 10 * 60000) return Promise.resolve(_regraNaoVeio.ligada);
  if (!_regraNaoVeio.lendo) {
    _regraNaoVeio.lendo = (async () => {
      try {
        const { data, error } = await supabase.from('config_publica').select('valor').eq('chave', 'nao_veio_contra_aviso_prof').maybeSingle();
        if (!error) { _regraNaoVeio.ligada = String(data?.valor || '') === 'sim'; _regraNaoVeio.lidaEm = Date.now(); }
      } catch {}
      _regraNaoVeio.lendo = null;
      return _regraNaoVeio.ligada;
    })();
  }
  return _regraNaoVeio.lendo;
}
function avisoDoProfSM(a) { return !!a && avisoVigenteAula(a) && a.aviso_canal === 'professor_registrou'; }
// v142 (Desfazer, lado do aluno): o "Desfazer" discreto no cartao da aula (o do "Vou" e' o vouDesfazivelSM).
// Devolve o p_esperado de desfazer_minha_acao, ou null. E' so' o palpite pelas colunas que o app ja' tem —
// quem decide no fim e' o banco.
//  - 'aviso' (A2): o aviso FORA do prazo que o proprio aluno mandou, antes de a aula comecar (R2) e antes de
//    o professor decidir ou registrar;
//  - 'resposta' (A6/A7): a resposta dele sobre a aula ("Fiz a aula", "Nao aconteceu", "Cadu nao veio"),
//    enquanto o professor nao registrou — ou o desacordo que ela criou.
function desfazerCartaoAlunoSM(a, agora = Date.now()) {
  if (!a || a.status !== 'agendada') return null;
  if (avisoVigenteAula(a)) {
    const ini = inicioAulaSP(a);
    return (!avisoDoProfSM(a) && a.aviso_no_prazo === false && !a.aviso_decisao && !a.desfecho_prof && !!ini && ini.getTime() > agora) ? 'aviso' : null;
  }
  // Revisao v142: a mesma regra do banco — a resposta so' vale se for MAIS NOVA que o ultimo "reabrir" (desfeita_em).
  // O professor que reabre pela tela Aulas deixa as colunas do aluno; sem isto o cartao mostrava "Desfazer" e o
  // banco respondia "nada para desfazer".
  const depoisDe = (t, ref) => !!t && (!ref || new Date(t).getTime() > new Date(ref).getTime());
  const respondeu = depoisDe(a.confirmada_aluno_em, a.desfeita_em) || depoisDe(a.falta_declarada_aluno_em, a.desfeita_em);
  return respondeu && (!a.desfecho_prof || !!a.divergencia_em) ? 'resposta' : null;
}
// Revisao v142 (P1/P5): o "Desfazer" do registro do professor no Inicio. Depois de "Conta a aula" (aviso decidido) a
// ultima acao e' a DECISAO — com 'registro' o banco respondia "acao_mudou" e o mesmo botao voltava (beco).
function esperadoRegistroProfSM(a) { return a && a.aviso_decisao ? 'decisao_aviso' : 'registro'; }
// Revisao v142 (P3): o aluno respondeu sobre a aula ("Fiz a aula" / "Nao fui") DEPOIS do aviso que o professor
// registrou — o "Desfazer" do aviso sai (o banco responderia "outro_ja_respondeu").
function alunoRespondeuDepoisDoAvisoSM(a) {
  if (!a) return false;
  const ref = a.aviso_aluno_em ? new Date(a.aviso_aluno_em).getTime() : 0;
  return [a.confirmada_aluno_em, a.falta_declarada_aluno_em].some(t => !!t && new Date(t).getTime() > ref);
}
// A1: aula que o PROPRIO aluno desmarcou no prazo (ou por feriado) e que ainda nao comecou — o mesmo
// reconhecimento do banco (cancelada + aviso do app no prazo, ou feriado com cancelado_por 'aluno').
function cancelamentoDesfazivelAlunoSM(a, agora = Date.now()) {
  if (!a || !String(a.status || '').startsWith('cancelada') || a.aviso_decisao) return false;
  const doAluno = (a.aviso_canal === 'app' && a.aviso_no_prazo === true) || (a.motivo_cancelamento === 'feriado' && a.cancelado_por === 'aluno');
  const ini = inicioAulaSP(a);
  return doAluno && !!ini && ini.getTime() > agora;
}
// O aviso vigente impede o aluno de dizer "o professor nao veio"? (o banco recusa: avisou_falta)
function avisoTravaNaoVeioSM(a) { return avisoVigenteAula(a) && !(naoVeioContraAvisoProfSM() && avisoDoProfSM(a)); }
// Cartao "Hoje" e folha da aula: oferecer "Não avisei — Cadu não veio"? Aula individual, aberta, sem
// registro do professor, de 15 min depois do inicio ate' 24h depois do fim (as mesmas travas do banco).
function podeNaoVeioContraAvisoSM(a) {
  if (!naoVeioContraAvisoProfSM() || !avisoDoProfSM(a) || a.status !== 'agendada') return false;
  if (a.desfecho_prof || a.divergencia_em || a.confirmada_aluno_em || a.falta_declarada_aluno_em) return false;
  if (!aulaIndividualSM(a)) return false;
  const ini = inicioAulaSP(a);
  if (!ini || Date.now() < ini.getTime() + 15 * 60000) return false;
  return !naoVeioVencidoSM(a);
}

function Ico({ n, s = 16, c, style }) {
  return <i className={'ti ti-' + n} aria-hidden="true"
    style={{ fontSize: s + 'px', lineHeight: 1, color: c, verticalAlign: '-0.125em',
             display: 'inline-block', flexShrink: 0, ...style }} />;
}

// Mesmo desenho do esqueleto que esta' no index.html, para o app continuar de onde a
// pagina parou enquanto verifica a sessao. Usa as mesmas classes CSS — nao duplica estilo.
function EsqueletoCarregando() {
  const linha = (w, h, mt) => <div className="sk-linha" style={{ width: w, height: h, marginTop: mt }} />;
  const num = () => <div>{linha('26px', '23px')}{linha('46px', '9px', '7px')}</div>;
  return (
    <div className="plen-skel" aria-busy="true" aria-label="Carregando">
      <div className="sk-topo">
        <img src="/logo-branco.svg" alt="Plen" width="66" height="21" />
        {linha('38%', '11px', '22px')}
        {linha('62%', '21px', '9px')}
        <div className="sk-nums">{num()}{num()}{num()}</div>
      </div>
      <div className="sk-corpo">
        {[['45%', '70%'], ['55%', '35%'], ['40%', '60%']].map(([a, b], i) => (
          <div className="sk-cartao" key={i}>{linha(a, '13px')}{linha(b, '11px', '11px')}</div>
        ))}
      </div>
    </div>
  );
}

// ---- helpers globais (Acao 3) ----
// Estado real de uma aula agendada conforme o relogio.
// Faltava o estado do meio: a hora passou e ninguem disse o que aconteceu.
function estadoAulaSM(aula, agora = new Date(), folgaMin = 15) {
  if (!aula || aula.status !== 'agendada') return null;
  // Consenso (24/09): o professor ja' registrou e falta o aluno — ou os dois discordaram.
  // Vem antes do relogio: uma aula nesses estados nao esta' "sem resposta".
  if (aula.divergencia_em) return 'divergencia';
  if (aula.desfecho_prof) return 'esperando_aluno';
  const inicio = new Date(`${aula.data}T${(aula.hora || '00:00').slice(0, 5)}:00`);
  if (isNaN(inicio)) return null;
  // Duracao real do contrato quando disponivel (aula.duracao_min); 60 min como regra.
  // +15 min de folga: aula que acabou de terminar ainda esta' "agora", nao "pendente".
  const durMin = Number(aula.duracao_min) > 0 ? Number(aula.duracao_min) : 60;
  const fim = new Date(inicio.getTime() + (durMin + folgaMin) * 60 * 1000);
  if (agora < inicio) return 'futura';
  if (agora <= fim) return 'agora';
  return 'aguardando';   // passou e ninguem confirmou
}
// v156 (Marcus 29/10): o professor chegou mais cedo. "A aula comecou" (aluno) e "Aula dada / Nao houve"
// (professor, no Inicio) valem a partir de 30 MIN ANTES do horario marcado — nao antes: confirmar horas antes
// registraria presenca que nao houve. O banco usa a mesma janela (aluno_responder_aula, sql/v155_pente_fino.sql C1).
// true so' na meia hora ANTES do inicio (depois do inicio, estadoAulaSM ja' diz 'agora').
const JANELA_COMECOU_MIN = 30;
function quaseComecandoSM(aula, agora = new Date()) {
  if (!aula || aula.status !== 'agendada') return false;
  const ini = inicioAulaSP(aula);
  if (!ini) return false;
  return agora < ini && agora.getTime() >= ini.getTime() - JANELA_COMECOU_MIN * 60000;
}

// ---- v140: "Tudo certo para a aula?" e "o professor nao veio" (ajudantes globais) -----------
// Colunas novas de aulas (sql/v140_presenca.sql): presenca_pedida_em (o professor perguntou),
// presenca_vou_em (o aluno disse "Vou" — ou "Ok, combinado" numa remarcacao) e aluno_motivo_nao
// (junto de falta_declarada_aluno_em: 'eu_nao_fui' | 'prof_nao_veio'; nulo = 'eu_nao_fui').
// Quem grava e' sempre o banco; o app so' le e mostra.
// Aula individual: a pergunta e o "nao veio" nao valem para turma nesta versao.
function aulaIndividualSM(a) {
  return !!a && !a.sessao_id && !a.turma_id && !(a.duracao && a.duracao.turma_id) && !(a.acordo && a.acordo.turma_id);
}
// Duracao da aula em minutos: a do acordo carregado junto (duracao/acordo) ou 60.
function duracaoAulaSM(a) {
  const n = Number(a && a.duracao_min) || Number(a && a.duracao && a.duracao.duracao_min) || Number(a && a.acordo && a.acordo.duracao_min);
  return n > 0 ? n : 60;
}
// 'às 21:04' (hoje) / 'ontem às 21:04' / 'em 19/10 às 21:04' — a mesma conta de textoAvisoProfSM
function quandoRelSM(ts) {
  const dia = diaSP(ts), h = horaSP(ts);
  if (!dia) return '';
  return dia === hojeSP() ? `às ${h}` : dia === hojeSP(-1) ? `ontem às ${h}` : `em ${ddmmSM(dia)} às ${h}`;
}
// 'ter 20/10' (minusculo, para o meio da frase)
function diaCurtoSM(data) {
  const x = new Date(String(data || '').slice(0, 10) + 'T12:00:00');
  return isNaN(x) ? '' : `${['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][x.getDay()]} ${ddmmSM(data)}`;
}
// 'hoje' / 'amanhã' / '' (outro dia) — pelo dia de Brasilia
function hojeOuAmanhaSM(data) {
  const d = String(data || '').slice(0, 10);
  return d === hojeSP() ? 'hoje' : d === hojeSP(1) ? 'amanhã' : '';
}
// v143 (B): a folha do aviso de falta aberta pelo botao [Nao vou] do aviso no celular diz de que aula se
// trata — o aluno chega nela sem passar pelo cartao, e se a aula mudou de horario depois do aviso, ele ve
// o horario de agora. "Aula de amanhã, qui 22/10 às 18:00"
function LinhaAulaDoAvisoSM({ aula }) {
  const ini = inicioAulaSP(aula);
  if (!ini) return null;
  const qd = hojeOuAmanhaSM(aula.data);
  return (
    <p data-aula-do-aviso="" style={{ fontSize: '13px', color: '#6E7480', margin: '0 0 10px', lineHeight: 1.4 }}>
      Aula de {qd ? qd + ', ' : ''}{minuscSM(quandoSP(ini))}
    </p>
  );
}
// Linha de estado da pergunta na agenda do professor (so' aula agendada, futura, sem aviso nem registro):
// "✓ Bia vem" / "Perguntei às 21:04 · sem resposta" / null
function legendaPresencaProfSM(a, nomeAluno) {
  if (!a || a.status !== 'agendada' || avisoVigenteAula(a) || a.desfecho_prof || a.divergencia_em) return null;
  const ini = inicioAulaSP(a);
  if (!ini || ini <= new Date()) return null;
  const P = primeiroNomeSM(nomeAluno || a.nomeAluno || (a.alunos && a.alunos.nome), 'Aluno');
  if (a.presenca_vou_em) return { txt: `✓ ${P} vem`, cor: '#0E9F6E' };
  if (a.presenca_pedida_em) return { txt: `Perguntei ${quandoRelSM(a.presenca_pedida_em)} · sem resposta`, cor: '#6E7480', semResposta: true };
  return null;
}
// O aluno marcou "o professor nao veio" (e ninguem desfez depois). { em, voltaEm, pendente }:
// voltaEm = maior entre o fim da aula e a marcacao + 24h (o robo devolve ao pacote nessa hora);
// pendente = o professor ainda nao registrou nada (sem registro e sem divergencia).
function naoVeioSM(a) {
  if (!a || a.status !== 'agendada' || a.aluno_motivo_nao !== 'prof_nao_veio' || !a.falta_declarada_aluno_em || a.confirmada_aluno_em) return null;
  const em = new Date(a.falta_declarada_aluno_em);
  if (isNaN(em)) return null;
  if (a.desfeita_em && !(em > new Date(a.desfeita_em))) return null;
  const ini = inicioAulaSP(a);
  const fim = ini ? new Date(ini.getTime() + duracaoAulaSM(a) * 60000) : em;
  return { em, voltaEm: new Date(Math.max(fim.getTime(), em.getTime()) + 24 * 3600000), pendente: !a.desfecho_prof && !a.divergencia_em };
}
// "O professor nao veio" vale ate' 24 horas depois do fim da aula (Marcus, 16/10: o aluno marca quando aconteceu;
// o banco recusa depois: tarde_demais — sql/v140c_nao_veio_24h.sql). true = ja' passou das 24h (so' "Eu nao fui").
function naoVeioVencidoSM(a) {
  const ini = a ? inicioAulaSP(a) : null;
  if (!ini) return false;
  return Date.now() > ini.getTime() + (duracaoAulaSM(a) + 24 * 60) * 60000;
}
// Linha do prazo do "nao veio" para o professor (e para o aluno, com aluno=true). Prazo vencido: o robo
// (de hora em hora, no minuto 7) devolve na proxima hora — nao mostrar "ate'" uma hora que ja' passou.
function textoVoltaNaoVeioSM(voltaEm, { semSaldo = false, aluno = false } = {}) {
  const passou = !voltaEm || voltaEm <= new Date();
  if (aluno) {
    if (semSaldo) return 'Fica registrado que ele não veio. Combine a reposição com o professor.';
    return passou ? 'Ele não respondeu em 24h: a aula volta para o seu pacote na próxima hora.' : `Se ele não responder até ${quandoSP(voltaEm)}, a aula volta para o seu pacote.`;
  }
  if (semSaldo) return passou ? 'O prazo de 24h passou: fica registrado que você não veio.' : `Sem resposta até ${quandoSP(voltaEm)}, fica registrado que você não veio.`;
  return passou ? 'O prazo de 24h passou: a aula volta para o pacote na próxima hora.' : `Sem resposta até ${quandoSP(voltaEm)}, a aula volta para o pacote.`;
}
// O professor perguntou "vai?" e o aluno ainda pode responder (ate' o inicio da aula).
function perguntaAbertaSM(a) {
  if (!a || a.status !== 'agendada' || !a.presenca_pedida_em || a.presenca_vou_em || avisoVigenteAula(a) || a.desfecho_prof || a.divergencia_em) return false;
  const ini = inicioAulaSP(a);
  return !!ini && ini > new Date();
}
// v142 (B): o aluno diz "Vou" mesmo SEM a pergunta do professor — a mesma janela da pergunta: hoje ou
// amanha (dia de Brasilia), antes do inicio, aula individual agendada, sem aviso de falta e sem registro.
// O banco confere tudo de novo (aluno_confirmar_presenca, sql/v142b.sql). acordo = o acordo da aula
// (aula de turma nao entra). Quem chama ainda tira a aula com pedido aberto ou remarcacao esperando.
function podeDizerVouSM(a, acordo) {
  if (!a || a.status !== 'agendada' || a.presenca_vou_em || avisoVigenteAula(a) || a.desfecho_prof || a.divergencia_em) return false;
  if (!aulaIndividualSM(acordo && acordo.turma_id ? { ...a, acordo } : a)) return false;
  if (!hojeOuAmanhaSM(a.data)) return false;
  const ini = inicioAulaSP(a);
  return !!ini && ini > new Date();
}
// "Voce avisou que vai" e o "Desfazer" do proprio cartao (A3 da v142: ate' a aula comecar). O banco
// decide no fim (desfazer_minha_acao com esperado 'vou').
function vouDesfazivelSM(a) {
  if (!a || a.status !== 'agendada' || !a.presenca_vou_em || avisoVigenteAula(a) || a.desfecho_prof || a.divergencia_em) return false;
  const ini = inicioAulaSP(a);
  return !!ini && ini > new Date();
}
// Link do WhatsApp com texto pronto (com numero: direto na conversa; sem: escolher o contato).
// Abrir SEMPRE por compartilharOuWhats(url, motivo) (conta na central e usa o compartilhar do app).
function waLinkSM(fone, texto) {
  const t = encodeURIComponent(String(texto || ''));
  const n = String(fone || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  return n.length >= 10 ? `https://wa.me/55${n}?text=${t}` : `https://wa.me/?text=${t}`;
}
// Funcao que o banco ainda nao tem (SQL nao rodado ou VOLTA): PGRST202 do PostgREST
const semFuncaoBancoSM = t => /PGRST202|could not find the function/i.test(String(t || ''));

// ---- Professor e as funcoes de aula do banco (v131, Etapas 6 a 9) ---------------------------
// Toda mudanca de aula que mexe em saldo, fila do pacote ou agenda do aluno passa por uma funcao
// do banco, que decide pela regra do acordo e avisa o aluno. O app so' pergunta, mostra a
// consequencia e traduz a resposta. Sucesso so' com {ok} do banco; recusa vem como {erro:'codigo'}
// e vira frase de gente (nunca o codigo). Depois dessas funcoes o app NAO chama recalcularCreditos
// (o banco ja' recalculou e devolve `saldo`; null = acordo sem saldo de aulas).

// 'Zé Silva' -> 'Zé'; sem nome -> o padrao inteiro ("O aluno"), nunca so' "O"
function primeiroNomeSM(nome, padrao = 'O aluno') {
  const p = String(nome || '').trim().split(/\s+/)[0];
  return p || padrao;
}
// '2026-10-02' -> '02/10'
const ddmmSM = s => (s ? `${String(s).slice(8, 10)}/${String(s).slice(5, 7)}` : '');
// 'Qui 02/10 às 7h' / 'Qui 02/10 às 7h30'
function quandoCurtoSM(data, hora) {
  if (!data) return '';
  const d = new Date(String(data).slice(0, 10) + 'T12:00:00');
  const dia = isNaN(d) ? '' : ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.getDay()];
  const [h, m] = String(hora || '').slice(0, 5).split(':');
  const hh = h ? `${Number(h)}h${m && m !== '00' ? m : ''}` : '';
  return `${dia} ${ddmmSM(data)}${hh ? ' às ' + hh : ''}`.trim();
}

// Chamada de uma funcao de aula: {ok, data} | {ok:false, cod, data, rede, tecnico}.
// As funcoes antigas (professor_fechar_aula, responder_aula_proposta) recusam com frase sem
// acento; viram codigo aqui para a tela ter um texto so'.
const FRASES_ANTIGAS_SM = {
  'sem permissao': 'sem_permissao', 'aula nao encontrada': 'aula_nao_encontrada',
  'esta aula nao esta aguardando confirmacao': 'proposta_respondida', 'desfecho invalido': 'invalido',
};
// v133 (Cadu, 06/10, iPhone): "TypeError: Load failed" ao marcar aula — o iOS derruba a conexao
// quando o app volta do fundo e o primeiro pedido morre. Ele tocou de novo e foi. Agora o app tenta
// de novo sozinho UMA vez, so' em falha de REDE e so' nas funcoes que aguentam chegar duas vezes
// (fechar: mesmo desfecho = mesmo estado; desfazer: devolve ja_feito; previas com p_simular).
const RPC_AULA_REPETIVEL_SM = new Set(['professor_fechar_aula', 'desfazer_desfecho_aula', 'desfazer_minha_acao']);
// v155: trava do banco — o aluno ja' tem aula nesse dia e horario (nao e' falha de rede)
const ehAulaRepetidaSM = e => !!e && (e.hint === 'aula_mesmo_horario' || /já tem aula neste dia e horário/i.test(String(e.message || '')));
const TEXTO_AULA_REPETIDA_SM = 'O aluno já tem aula neste dia e horário. Escolha outro horário.';
const ehFalhaDeRedeSM = msg => /load failed|failed to fetch|networkerror|network request failed|internet connection|conexão|timed out|the network connection was lost/i.test(String(msg || ''));
async function rpcAulaSM(nome, params) {
  let data = null, error = null, repetiu = false;
  try { ({ data, error } = await supabase.rpc(nome, params)); } catch (e) { error = e; }
  if (error && ehFalhaDeRedeSM(error.message || error) && (RPC_AULA_REPETIVEL_SM.has(nome) || params?.p_simular === true)) {
    await new Promise(r => setTimeout(r, 1200));
    data = null; error = null; repetiu = true;
    try { ({ data, error } = await supabase.rpc(nome, params)); } catch (e) { error = e; }
    if (!error) uso.evento('aviso.rede_repetiu', { detalhes: { rpc: nome } });
  }
  if (error && ehAulaRepetidaSM(error)) return { ok: false, cod: 'aula_mesmo_horario', data: null };
  if (error) return { ok: false, rede: true, cod: 'rede', tecnico: error.message || String(error) };
  if (!data || data.erro || !data.ok) {
    const bruto = String((data && (data.codigo || data.erro)) || 'sem_resposta');
    return { ok: false, cod: FRASES_ANTIGAS_SM[bruto] || bruto, data };
  }
  // v134: mudou uma aula de verdade (previa nao conta) -> a aba Pendencias recarrega
  if (!(params && params.p_simular === true)) avisarPendenciasMudouSM();
  // repetiu: a 1a tentativa caiu na rede — pode ter gravado (so' a resposta se perdeu). Quem chama decide.
  return repetiu ? { ok: true, data, repetiu: true } : { ok: true, data };
}

// v132 (02/10): desfazer SEM avisar o aluno quando vem um passo seguinte que avisa o resultado final
// (antes o aluno recebia dois avisos e o primeiro contradizia o segundo). Banco ainda sem p_avisar
// (sql/avulsos_0210.sql nao rodado, ou VOLTA): repete sem o parametro — funciona nas duas ordens.
async function desfazerParaRefazerSM(aulaId) {
  let r = await rpcAulaSM('desfazer_desfecho_aula', { p_aula_id: aulaId, p_avisar: false });
  if (!r.ok && r.rede && /PGRST202|Could not find the function|p_avisar/i.test(String(r.tecnico || ''))) {
    r = await rpcAulaSM('desfazer_desfecho_aula', { p_aula_id: aulaId });
  }
  return r;
}

// Toque duplo nao chama o banco duas vezes para a mesma aula (vale para todas as telas).
const TRAVAS_AULA_SM = new Set();
async function comTravaAulaSM(id, fn) {
  if (!id || TRAVAS_AULA_SM.has(id)) return null;
  TRAVAS_AULA_SM.add(id);
  try { return await fn(); } finally { TRAVAS_AULA_SM.delete(id); }
}

// Recusa do banco -> frase de leigo. ctx: 'desfazer' | 'cancelar' | 'remarcar' | 'decidir' | 'aviso' | 'proposta'
function textoErroAulaProf(cod, d, ctx, nomeAluno) {
  const p = primeiroNomeSM(nomeAluno, 'o aluno');
  if (cod === 'hora_invalida') return 'Confira o dia e a hora do aviso: não pode ser no futuro nem mais de 7 dias antes da aula.';
  if (cod === 'aula_nao_agendada' && ctx === 'remarcar') return 'Só dá para remarcar aula que ainda está na agenda. Se ela já foi registrada, desfaça o registro antes.';
  if (cod === 'reposta_ja_usada') return `A aula que foi para o fim do pacote${d?.data ? ` (${ddmmSM(d.data)})` : ''} já foi dada ou registrada. Desfaça primeiro o registro dela.`;
  if (cod === 'fila_ja_usada') return ctx === 'desfazer'
    ? 'A última aula do pacote já foi usada, então não dá para tirar a aula que foi para o fim. Corrija pela ficha do aluno.'
    : 'Esta aula já teve a compensação dela (já foi para o fim do pacote antes).';
  // v155: trava de aula repetida — nunca "confira a internet"
  if (cod === 'aula_mesmo_horario') return `${p.charAt(0).toUpperCase() + p.slice(1)} já tem aula nesse dia e horário.`
    + (ctx === 'desfazer' ? ' É a aula que entrou no lugar desta: corrija aquela.' : ctx === 'remarcar' ? ' Escolha outro horário.' : ctx === 'proposta' ? ' Recuse esta proposta.' : '');
  if (cod === 'aluno_ja_avisou') return `${p.charAt(0).toUpperCase() + p.slice(1)} já tinha avisado${d?.aviso_em ? ` (${quandoSP(d.aviso_em)})` : ''}. Não precisa registrar de novo.`;
  return ({
    rede: 'Não foi possível salvar agora. Confira a internet e tente de novo.',
    sem_permissao: 'Não encontramos esta aula na sua agenda. Atualize a tela e tente de novo.',
    aula_nao_encontrada: 'Não encontramos esta aula na sua agenda. Atualize a tela e tente de novo.',
    aluno_desvinculado: 'O vínculo com este aluno foi encerrado — não dá para mexer nesta aula.',
    aula_proposta: 'Esta aula foi lançada pelo aluno e ainda espera você confirmar. Use Confirmar ou Recusar.',
    proposta_respondida: 'Esta aula já foi respondida. Atualize a tela.',
    aula_remarcada: 'Esta aula foi remarcada — a que vale é a nova. Atualize a tela.',
    aula_nao_agendada: 'Esta aula não está mais na agenda (já foi fechada, cancelada ou remarcada). Atualize a tela.',
    aula_com_registro: 'Você já registrou o que aconteceu nesta aula. Desfaça o registro antes.',
    professor_ja_registrou: 'Você já registrou o que aconteceu nesta aula. Desfaça o registro antes.',
    status_invalido: 'Esta aula não pode ser desfeita.',
    reposicao_ja_usada: 'A reposição desta aula já foi marcada. Desmarque a reposição antes de desfazer.',
    reposicao_existente: 'Esta aula já teve a compensação dela.',
    beneficio_ja_decidido: 'Esta aula já teve a compensação dela decidida — confira na ficha do aluno.',
    acordo_invalido: 'A aula está ligada a um acordo de outra ficha — corrija na ficha do aluno.',
    mensal_sem_devolucao: 'Na mensalidade não há o que devolver: a aula não gera crédito.',
    sem_data: 'Não há dia livre nos próximos 60 dias para pôr esta aula no fim do pacote. Libere um horário e tente de novo.',
    sem_aviso_fora_prazo: 'Não há aviso fora do prazo esperando você nesta aula. Atualize a tela.',
    data_invalida: 'Escolha uma data entre 90 dias atrás e um ano para a frente.',
    pedido_invalido: 'Este pedido não vale mais para esta aula. Atualize a tela.',
    pedido_fechado: 'Este pedido já foi respondido.',
    prazo_mudou: 'O prazo virou enquanto você preenchia. Confira o cartão e confirme de novo.',
    no_prazo_esperado_obrigatorio: 'Confira o cartão e confirme de novo.',
    motivo_invalido: 'Escolha o motivo de novo.',
    categoria_invalida: 'Escolha o que aconteceu de novo.',
    decisao_invalida: 'Escolha "Conta a aula" ou "Devolver".',
    conflito: 'Esse horário já está ocupado na sua agenda.',
    aula_mesmo_horario: TEXTO_AULA_REPETIDA_SM,
  })[cod] || 'Não foi possível concluir agora. Tente de novo em instantes.';
}
// Mostra a recusa e conta na central de uso (fluxo = trava solta, ex.: 'aula_desfazer').
function avisarErroAulaProf(res, fluxo, ctx, nomeAluno) {
  if (res.rede) { uso.erro('erro.' + fluxo, res.tecnico); toastSM(textoErroAulaProf('rede')); return; }
  uso.bloqueio(fluxo, null, null, /^[a-z_]{1,40}$/.test(res.cod) ? res.cod : 'outro');
  toastSM(textoErroAulaProf(res.cod, res.data, ctx, nomeAluno));
}

// O que a desmarcacao gerou (resposta de professor_cancelar_aula / _desmarcar_aula), em uma
// frase para o professor. previa=true: futuro ("vai para"), senao passado ("foi para").
function textoBeneficioProfSM(r, { previa = false } = {}) {
  if (!r) return '';
  const regras = r.regras || {};
  const semSaldo = regras.tipo_cobranca === 'mensal' || regras.turma;
  // v148: chuva no acordo ao ar livre — nao conta para ninguem (sem limite no mes)
  if (r.chuva && r.beneficio === 'fila') {
    const dm = r.fila?.nova_data ? `, em ${ddmmSM(r.fila.nova_data)}` : '';
    return previa ? `Chuva não conta para ninguém: a aula vai para o fim do pacote${dm}.` : `Chuva não conta para ninguém: a aula foi para o fim do pacote${dm}.`;
  }
  if (r.chuva && r.beneficio === 'reposicao') return previa ? 'Chuva não conta para ninguém: fica uma reposição a combinar.' : 'Chuva não conta para ninguém: ficou uma reposição a combinar.';
  if (r.beneficio === 'fila') {
    const dm = r.fila?.nova_data ? `, em ${ddmmSM(r.fila.nova_data)}` : '';
    // v155: com proximo pacote combinado e sem dia livre antes dele, a aula entra no proximo (+1 nele)
    if (r.fila?.no_proximo) return previa ? `Não desconta: a aula entra no próximo pacote${dm}.` : `Não desconta: a aula entrou no próximo pacote${dm}.`;
    return previa ? `Não desconta: a aula vai para o fim do pacote${dm}.` : `Não desconta: a aula foi para o fim do pacote${dm}.`;
  }
  if (r.beneficio === 'reposicao') return previa ? 'Não desconta: fica uma reposição a combinar.' : 'Não desconta: ficou uma reposição a combinar.';
  if (r.beneficio === 'desconto') return `Não desconta: ${fmtBRL(r.valor_credito)} vira desconto na próxima cobrança.`;
  if (r.beneficio === 'teto') return 'Não desconta, mas o limite de reposições por chuva ou imprevisto deste mês já foi usado — sem reposição.';
  if (semSaldo) return 'Na mensalidade a aula não gera crédito nem desconto.';
  if (!regras.tipo_cobranca) return 'Esta aula não tem acordo ligado: nada muda no saldo.';
  if (r.categoria === 'aluno_no_prazo' && regras.reposicao_permitida === false) return 'Não desconta. Este acordo não prevê reposição.';
  if (r.categoria === 'forca_maior' && regras.forca_maior_gera === 'nada') return 'Não desconta. Este acordo não repõe por chuva ou imprevisto.';
  return 'Não desconta do pacote.';
}

// "Desfeito." + o que saiu do fim do pacote (Etapa 7)
function textoDesfeitoSM(r) {
  if (!r) return '';
  if (r.ja_feito) return 'Nada para desfazer: a aula já está na agenda.';
  return 'Desfeito.'
    + (r.reposta_removida_data ? ` A aula que tinha ido para o fim do pacote (${ddmmSM(r.reposta_removida_data)}) saiu.` : '')
    + (r.aviso === 'fila_legada' ? ' Confira no fim do pacote se sobrou uma aula a mais.' : '');
}

// Desfaz o registro do professor (aula volta para a agenda; a aula que foi para o fim do pacote
// sai; a reposicao a combinar que nasceu dela some). desfazer_desfecho_aula, Etapa 7.
// v142 (etapa A): tenta PRIMEIRO "desfazer a minha acao" (desfazer_minha_acao, p_esperado null: o banco
// reconhece a ultima acao do professor e volta SO' ela, enquanto o aluno nao respondeu, avisando o aluno se
// ele tinha sido avisado). Se o banco disser que nao e' o caso (o aluno ja' respondeu, fechou sozinha,
// passou o prazo, nao foi ele, desacordo) — ou o banco ainda nao tem a v142 —, cai no caminho de hoje: o
// "corrigir" do professor (desfazer_desfecho_aula: reabre do zero e avisa o aluno). Spec 2.2, D5.
const CAI_NO_CAMINHO_DE_HOJE_SM = new Set(['nada_para_desfazer', 'outro_ja_respondeu', 'prazo_passou', 'nao_foi_voce', 'fechou_sozinha',
  'decisao_em_desacordo', 'acao_mudou', 'sem_funcao']);
async function desfazerAulaProf(aula, { origem = 'outro', nomeAluno } = {}) {
  if (!aula?.id) return null;
  const dz = await executarDesfazerSM({ ref_tipo: 'aula', ref_id: aula.id, esperado: null }, { papel: 'professor', nomeOutro: nomeAluno, origem: 'aulas', quieto: true });
  if (dz === null) return null;   // toque duplo, ou "Voltar" no horario ocupado
  if (dz.ok) {
    uso.acao('aula.marcar', { status: 'agendada', origem, desfeito: !dz.ja_feito, saiu_do_fim: !!dz.reposta_removida_data, via: 'minha_acao' });
    return dz;   // o banco ja' recalculou o pacote (como no desfazer_desfecho_aula)
  }
  if (!CAI_NO_CAMINHO_DE_HOJE_SM.has(dz.cod)) {
    if (dz.cod === 'rede') uso.erro('erro.desfazer', dz.tecnico); else uso.bloqueio('desfazer', null, null, dz.cod);
    toastSM(textoErroDesfazerSM(dz.cod, dz.data, 'professor', nomeAluno));
    return null;
  }
  return comTravaAulaSM(aula?.id, async () => {
    const res = await rpcAulaSM('desfazer_desfecho_aula', { p_aula_id: aula.id });
    if (!res.ok) { avisarErroAulaProf(res, 'aula_desfazer', 'desfazer', nomeAluno); return null; }
    const r = res.data;
    uso.acao('aula.marcar', { status: 'agendada', origem, desfeito: !r.ja_feito, saiu_do_fim: !!r.reposta_removida_data });
    toastSM(textoDesfeitoSM(r));
    return r;
  });
}

// O professor desmarca (regra unica do banco: professor_cancelar_aula, Etapa 9).
// categoria: 'professor' | 'forca_maior' | 'aluno_no_prazo'; motivo: agenda|doente|viagem|clima|outro.
// Aula com registro do professor (divergencia, por ex.): desfaz o registro e desmarca em seguida.
const CATEGORIA_POR_STATUS_SM = { cancelada_aluno: 'aluno_no_prazo', cancelada_professor: 'professor', cancelada_forca_maior: 'forca_maior' };
const MOTIVO_PADRAO_SM = { aluno_no_prazo: 'outro', professor: 'outro', forca_maior: 'clima' };
const MOTIVOS_AULA_SM = ['agenda', 'doente', 'viagem', 'clima', 'outro'];
// alunoSemApp (v140): o aluno nao usa o app — o banco grava o aviso, mas ele nao recebe; o toast manda
// avisar pelo WhatsApp (o painel "Nao vou poder dar" mostra o botao com o texto pronto).
async function cancelarAulaProf(aula, categoria, motivo, { obs = null, origem = 'outro', nomeAluno, acaoUso = true, alunoSemApp = false } = {}) {
  const primeiro = primeiroNomeSM(nomeAluno);
  const mot = MOTIVOS_AULA_SM.includes(motivo) ? motivo : (MOTIVO_PADRAO_SM[categoria] || 'outro');
  return comTravaAulaSM(aula?.id, async () => {
    const params = { p_aula_id: aula.id, p_categoria: categoria, p_motivo: mot, p_obs: obs || null, p_simular: false };
    let res = await rpcAulaSM('professor_cancelar_aula', params);
    let desfeito = false;
    if (!res.ok && res.cod === 'aula_com_registro') {
      const d = await desfazerParaRefazerSM(aula.id);
      if (!d.ok) { avisarErroAulaProf(d, 'aula_desfazer', 'desfazer', nomeAluno); return null; }
      desfeito = true;
      res = await rpcAulaSM('professor_cancelar_aula', params);
    }
    if (!res.ok) {
      if (desfeito) {   // o desfazer valeu (sem aviso) e o cancelar nao: o professor precisa saber
        if (res.rede) uso.erro('erro.aula_cancelar_prof', res.tecnico); else uso.bloqueio('aula_cancelar_prof', null, null, /^[a-z_]{1,40}$/.test(res.cod) ? res.cod : 'outro');
        toastSM(textoErroAulaProf(res.cod, res.data, 'cancelar', nomeAluno) + ' A aula ficou como agendada e o aluno ainda não foi avisado.');
        return null;
      }
      avisarErroAulaProf(res, 'aula_cancelar_prof', 'cancelar', nomeAluno); return null;
    }
    const r = res.data;
    if (acaoUso) uso.acao('aula.marcar', { status: r.status, origem, beneficio: r.beneficio || 'nenhum', motivo: categoria, ...(r.chuva ? { chuva: true } : {}) });   // motivo = categoria (como na v130); v148: chuva
    const msgCanc = `${textoBeneficioProfSM(r)} ${alunoSemApp ? `Avise ${primeiro} pelo WhatsApp.` : `${primeiro} recebe o aviso.`}`;
    // v142: "Feito · Desfazer" (P4) — a aula volta para a agenda; o banco confere o horario. Nao quando o
    // registro de antes foi desfeito para desmarcar (o desfazer nao traria o registro de volta).
    if (desfeito) toastSM(msgCanc);
    else toastDesfazerSM(msgCanc, { ref_tipo: 'aula', ref_id: aula.id, esperado: 'cancelamento' },
      { papel: 'professor', nomeOutro: nomeAluno, curto: 'desmarcação da aula' });
    return r;
  });
}

// Previa (p_simular) do "o professor desmarca": o texto do cartao antes de confirmar.
async function previaCancelarAulaProf(aulaId, categoria, motivo) {
  const mot = MOTIVOS_AULA_SM.includes(motivo) ? motivo : (MOTIVO_PADRAO_SM[categoria] || 'outro');
  const res = await rpcAulaSM('professor_cancelar_aula', { p_aula_id: aulaId, p_categoria: categoria, p_motivo: mot, p_obs: null, p_simular: true });
  // aula com registro (divergencia): o confirmar desfaz e desmarca — a tela usa o texto dela
  if (!res.ok) return { erro: res.cod, txt: res.rede || res.cod === 'aula_com_registro' ? null : textoErroAulaProf(res.cod, res.data, 'cancelar') };
  return { dados: res.data, txt: textoBeneficioProfSM(res.data, { previa: true }) };
}

// O que o professor diz sobre uma aula, num lugar so' (painel, tela Aulas, ficha, agenda).
//  - 'realizada' / 'falta' consomem o pacote: passam pelo CONSENSO (professor_fechar_aula).
//    Nao descontam na hora — o aluno confirma, ou vale o registro depois de 24h calado.
//  - 'cancelada_aluno' / 'cancelada_professor' / 'cancelada_forca_maior' sao a favor do aluno:
//    professor_cancelar_aula decide pelo acordo (fim do pacote, reposicao, desconto ou nada) e
//    avisa o aluno. `motivo` (opcional): agenda|doente|viagem|clima|outro.
//  - 'agendada' desfaz o registro (desfazer_desfecho_aula).
// Devolve o estado final ('aguardando', 'divergencia', o status gravado) ou null se falhou.
// origem: de qual tela veio (central de uso conta 'aula.marcar' por origem)
async function aplicarDesfechoProfessor(aula, novoStatus, { forcar = false, nomeAluno, origem = 'outro', motivo = null, obs = null, alunoSemApp = false } = {}) {
  const primeiro = primeiroNomeSM(nomeAluno);
  if (novoStatus === 'realizada' || novoStatus === 'falta') {
    return comTravaAulaSM(aula?.id, async () => {
      const res = await rpcAulaSM('professor_fechar_aula', { p_aula_id: aula.id, p_desfecho: novoStatus, p_forcar: !!forcar });
      if (!res.ok) { avisarErroAulaProf(res, 'aula_marcar', 'fechar', nomeAluno); return null; }
      const r = res.data;
      const msgReg = r.estado === 'aguardando'
        ? (alunoSemApp
            ? `Registrado. ${primeiro} ainda não entrou no app — o seu registro vale sozinho em 24h.`
            : `Registrado. ${primeiro} confirma no app — se não responder em 24h, vale o seu registro.`)
        : r.estado === 'divergencia'
          ? `${primeiro} tinha registrado diferente. Ficou para vocês acertarem — a aula não desconta até lá.`
          : r.estado === novoStatus ? 'Aula fechada.'
          : textoErroAulaProf('aula_nao_agendada');
      // v142: "Feito · Desfazer" — P1 (o aluno ainda nao confirmou: 'aguardando'/'divergencia') ou P2 (fechou
      // na hora: o aluno ja' tinha dito o mesmo). Forcado (corrigir por cima de um desacordo) nao: e' a
      // decisao final, e o banco responderia decisao_em_desacordo.
      const espReg = forcar ? null : (r.estado === 'aguardando' || r.estado === 'divergencia') ? 'registro' : r.estado === novoStatus ? 'registro_fechou' : null;
      if (espReg) toastDesfazerSM(msgReg, { ref_tipo: 'aula', ref_id: aula.id, esperado: espReg },
        { papel: 'professor', nomeOutro: nomeAluno, curto: novoStatus === 'realizada' ? 'registro de aula dada' : 'registro de falta' });
      else toastSM(msgReg);
      uso.acao('aula.marcar', { status: novoStatus, origem, estado: r.estado });
      if (aula.aluno_id) await recalcularCreditos(supabase, aula.aluno_id);
      return r.estado;
    });
  }
  if (novoStatus === 'agendada') {
    const r = await desfazerAulaProf(aula, { origem, nomeAluno });
    return r ? 'agendada' : null;
  }
  const categoria = CATEGORIA_POR_STATUS_SM[novoStatus];
  if (!categoria) return null;
  const r = await cancelarAulaProf(aula, categoria, motivo, { obs, origem, nomeAluno });
  return r ? r.status : null;
}

// ---- Remarcar (Etapa 6): remarcar_aula confere o horario por intervalo e devolve os nomes. O
// professor ve o alerta e pode marcar mesmo assim (decisao h). Na mensalidade e na turma a aula
// original vira 'remarcada' e nasce outra (modo 'nova', aula_id novo).
function textoConflitosSM(conflitos) {
  const lista = Array.isArray(conflitos) ? conflitos : [];
  if (!lista.length) return 'Esse horário já está ocupado na sua agenda.';
  const quem = c => c.turma ? `a turma ${c.nome || ''}`.trim() : String(c.nome || 'outro aluno').split(' ')[0];
  if (lista.length === 1) {
    const c = lista[0];
    if (c.mesmo_aluno && !c.turma) return `${quandoCurtoSM(c.data, c.hora)} ${quem(c)} já tem outra aula com você.`;
    return `${quandoCurtoSM(c.data, c.hora)} você já tem aula com ${quem(c)}.`;
  }
  const itens = lista.slice(0, 3).map(c => `${quem(c)} (${quandoCurtoSM(c.data, c.hora)})`);
  return `Nesse horário você já tem aula com ${itens.join(', ')}${lista.length > 3 ? ` e mais ${lista.length - 3}` : ''}.`;
}
function DialogoConflitoSM({ texto, rotuloVoltar, rotuloForcar, onVoltar, onForcar, titulo = 'Horário ocupado' }) {
  const bt = { flex: '1 1 140px', minHeight: '44px', borderRadius: '12px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', padding: '10px 12px' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 99990, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onVoltar}>
      <div role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}
        style={{ background: 'white', borderRadius: '20px', padding: '22px 20px', width: '100%', maxWidth: '380px', boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: "'Manrope', system-ui, sans-serif" }}>
        <div style={{ fontSize: '16px', fontWeight: '800', color: '#16181D', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Ico n="alert-triangle" s={18} c="#D97706" /> {titulo}
        </div>
        <div style={{ fontSize: '14px', color: '#3D424C', marginTop: '8px', lineHeight: 1.5 }}>{texto}</div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button type="button" onClick={onVoltar} style={{ ...bt, border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C' }}>{rotuloVoltar}</button>
          {onForcar && <button type="button" onClick={onForcar} style={{ ...bt, border: 'none', background: '#16181D', color: 'white' }}>{rotuloForcar}</button>}
        </div>
      </div>
    </div>
  );
}
// v155: o mesmo aluno com aula no MESMO dia e horario — o banco sempre recusa (trava de aula repetida),
// entao nao ha' "mesmo assim". alvo = { data, hora } da aula que vai para la'.
function conflitoMesmoHorarioSM(conflitos, alvo) {
  if (!alvo || !alvo.data || !alvo.hora || !Array.isArray(conflitos)) return null;
  return conflitos.find(c => c && c.mesmo_aluno && !c.turma && c.data === alvo.data
    && String(c.hora || '').slice(0, 5) === String(alvo.hora).slice(0, 5)) || null;
}
// Pergunta e devolve true (marcar mesmo assim) / false (voltar). Desenhada fora da arvore do app:
// serve para qualquer tela sem estado proprio.
function perguntarConflitoSM(conflitos, { rotuloVoltar = 'Escolher outro', rotuloForcar = 'Marcar mesmo assim', sufixo = '', alvo = null } = {}) {
  const igual = conflitoMesmoHorarioSM(conflitos, alvo);
  if (igual) {
    const nome = String(igual.nome || '').split(' ')[0] || 'O aluno';
    return perguntarSM(`${nome} já tem aula nesse dia e horário.`, { rotuloVoltar, semForcar: true, janela: 'conflito_mesmo_horario' });
  }
  return perguntarSM(textoConflitosSM(conflitos) + sufixo, { rotuloVoltar, rotuloForcar });
}
// Janela curta de duas opcoes (true = botao escuro). semForcar: so' o botao de voltar.
function perguntarSM(texto, { titulo, rotuloVoltar = 'Voltar', rotuloForcar = 'Confirmar', semForcar = false, janela = 'conflito_horario' } = {}) {
  return new Promise(resolve => {
    let el = null, raiz = null, feito = false;
    const fim = v => {
      if (feito) return; feito = true;
      try { raiz && raiz.unmount(); } catch {}
      try { el && el.remove(); } catch {}
      resolve(v);
    };
    try {
      uso.janela(janela);
      el = document.createElement('div');
      document.body.appendChild(el);
      raiz = createRoot(el);
      raiz.render(<DialogoConflitoSM texto={texto} rotuloVoltar={rotuloVoltar} rotuloForcar={rotuloForcar} titulo={titulo || 'Horário ocupado'}
        onVoltar={() => fim(false)} onForcar={semForcar ? null : () => fim(true)} />);
    } catch (e) { fim(false); }
  });
}

// ---- v142: DESFAZER ("quem fez desfaz, enquanto o outro lado nao respondeu") ------------------
// Uma funcao so' no banco (desfazer_minha_acao, sql/v142.sql). O app manda a referencia (aula,
// pagamento ou solicitacao) e o que acha que vai desfazer (p_esperado — so' uma trava: se o banco
// reconhecer outra coisa, devolve acao_mudou). Quem reconhece a acao, confere o autor, o prazo e se
// o outro lado ja' respondeu, volta tudo e avisa o outro (quando a acao original avisou) e' o BANCO.
// Dois jeitos na tela, iguais nos dois lados:
//   (a) faixa "Feito · Desfazer" por ~10 s logo depois da acao (toastDesfazerSM) — sem confirmacao,
//       porque a faixa ja' e' a confirmacao;
//   (b) "Desfazer" discreto no proprio item enquanto o outro nao respondeu (BotaoDesfazerSM), que
//       abre a confirmacao curta com o texto da previa (p_simular).
// Depois que o outro respondeu, o botao nao aparece; se a tela estava velha, o banco recusa e a frase
// abaixo explica o caminho de hoje (conversar, contestar, registrar).
// (Etapa B da v142: esta parte entrou junto com o "Vou" do aluno — A3. As outras acoes da spec ligam aqui.)
function textoErroDesfazerSM(cod, d, papel, nomeOutro) {
  const prof = papel === 'professor';
  // Sem nome (ou "Seu professor"/"O aluno" vindo de quem chama): "O professor ... fale com o professor."
  const generico = !String(nomeOutro || '').trim() || /^(seu|sua|o|a)\s/i.test(String(nomeOutro).trim());
  const O = generico ? (prof ? 'O aluno' : 'O professor') : primeiroNomeSM(nomeOutro);
  const o = generico ? (prof ? 'o aluno' : 'o professor') : O;   // no meio da frase
  if (cod === 'outro_ja_respondeu') return prof
    ? `${O} já respondeu depois de você, então não dá para desfazer sozinho. Combine com ${o} e registre o que aconteceu.`
    : `${O} já respondeu. Se algo está errado, fale com ${o}.`;
  if (cod === 'fechou_sozinha') return 'A aula já fechou sozinha pelo prazo de 24 horas.' + (prof ? '' : ' Se não concorda, toque em Contestar.');
  // A1 (cancelamento), A2 (aviso) e A3 ("Vou"): o prazo do aluno e' o inicio da aula (rel_sql v142, R2)
  if (cod === 'prazo_passou') return prof ? `O prazo para desfazer já passou. Para corrigir, fale com ${o}.`
    : (d && ['cancelamento', 'aviso', 'vou'].includes(d.desfeito)) ? `A aula já começou. Fale com ${o}.` : `O prazo para desfazer já passou. Fale com ${o}.`;
  if (cod === 'aula_mesmo_horario') return prof ? `${O} já tem aula nesse dia e horário. Não dá para desfazer.` : 'Você já tem aula nesse dia e horário. Não dá para desfazer.';
  if (cod === 'horario_ocupado') return prof ? textoConflitosSM(d && d.conflitos) : `Esse horário já foi ocupado na agenda de ${o}. Fale com ${o} para remarcar.`;
  if (cod === 'reposta_ja_usada' || cod === 'fila_ja_usada' || cod === 'reposicao_ja_usada') return prof
    ? textoErroAulaProf(cod, d, 'desfazer') : `A aula que foi para o fim do pacote (ou a reposição) já foi usada. Fale com ${o}.`;
  if (cod === 'fila_mexida') return 'A aula que foi para o fim do pacote já foi mudada. ' + (prof ? 'Desmarque-a antes.' : `Fale com ${o}.`);
  if (cod === 'aluno_desvinculado') return prof ? textoErroAulaProf('aluno_desvinculado') : `Seu vínculo com ${o} foi encerrado.`;
  if (cod === 'duplicado') return msgErroRpcPagamento('duplicado', d);
  // cobranca de um aluno que nao e' seu (R4 do rel_sql v142): a mesma frase do reabrir
  if (cod === 'sem_permissao' && prof && d && d.ref_tipo === 'pagamento') return msgErroRpcPagamento('sem_permissao', d);
  return ({
    nao_foi_voce: 'Só quem fez pode desfazer.',
    nada_para_desfazer: 'Não há nada para desfazer aqui. Atualize a tela.',
    acao_mudou: 'Isso mudou depois que a tela abriu. Atualize a tela.',
    decisao_em_desacordo: `Vocês dois registraram coisas diferentes nesta aula. Para corrigir, use o "desfazer" da tela Aulas.`,
    pago_pelo_plen: 'Este pagamento entrou pelo Plen e não pode ser desfeito aqui.',
    sem_permissao: 'Não encontramos isso na sua conta. Atualize a tela.',
    nao_encontrado: 'Não encontramos isso na sua conta. Atualize a tela.',
    pedido_invalido: 'Não encontramos isso na sua conta. Atualize a tela.',
    rede: 'Não foi possível desfazer agora. Confira a internet e tente de novo.',
  })[cod] || 'Não foi possível desfazer agora. Tente de novo em instantes.';
}
// Depois de um desfazer de verdade, as telas abertas (Inicio, Aulas, Agenda, Financeiro, app do aluno)
// recarregam — a faixa nao sabe de que tela veio a acao.
const EVENTO_DESFEITO_SM = 'plen:desfeito';
function avisarDesfeitoSM() { try { window.dispatchEvent(new Event(EVENTO_DESFEITO_SM)); } catch {} }
function useAoDesfazerSM(fn) {
  const ref = React.useRef(fn);
  ref.current = fn;
  React.useEffect(() => {
    const h = () => { try { ref.current && ref.current(); } catch {} };
    window.addEventListener(EVENTO_DESFEITO_SM, h);
    return () => window.removeEventListener(EVENTO_DESFEITO_SM, h);
  }, []);
}
// Chamada crua: {ok, data} | {ok:false, cod, data, rede, tecnico}. alvo = { ref_tipo, ref_id, esperado }.
async function desfazerMinhaAcaoSM(alvo, { simular = false, forcar = false } = {}) {
  if (!alvo || !alvo.ref_id || !alvo.ref_tipo) return { ok: false, cod: 'pedido_invalido' };
  return rpcAulaSM('desfazer_minha_acao', { p_ref_tipo: alvo.ref_tipo, p_ref_id: alvo.ref_id, p_esperado: alvo.esperado || null,
                                             p_simular: !!simular, p_forcar: !!forcar });
}
// Desfaz de verdade. Professor com horario ocupado na volta da aula: pergunta (Voltar / Desfazer mesmo
// assim); o aluno nao forca. Mostra o texto do banco. Devolve {ok:true, ...dados} | {ok:false, cod} |
// null (toque duplo ou o professor voltou). quieto: so' devolve o erro (quem chama decide o que fazer).
// alunoRecalc: desfazer de COBRANCA (P6/P7/P8/A9 — o banco nao mexe no saldo de aulas): o app recalcula
// o saldo do aluno antes de as telas relerem, como ja' faz depois de confirmar/reabrir/cancelar.
async function executarDesfazerSM(alvo, { papel = 'professor', nomeOutro, origem = 'faixa', quieto = false, alunoRecalc = null } = {}) {
  const r = await comTravaAulaSM('desfazer:' + (alvo && alvo.ref_id), async () => {
    let res = await desfazerMinhaAcaoSM(alvo);
    let forcou = false;
    // Faixa do "lancou ja' recebido" (P7): o banco so' cancela como "lancado por engano" a cobranca que NASCEU
    // daquela baixa (R3). Revisao v142: SEM segunda tentativa como "confirmacao_pagamento" — o app ja' manda
    // esse p_esperado quando a cobranca existia, entao a 2a tentativa so' pegava cobranca NOVA e a reabria
    // como divida que nunca existiu. No "acao_mudou" a frase leva a' lixeira do Financeiro.
    if (!res.ok && res.cod === 'acao_mudou' && alvo && alvo.esperado === 'lancamento' && !quieto) {
      uso.bloqueio('desfazer', null, null, 'lancamento_mudou');
      toastSM('Não deu para desfazer por aqui. Se foi engano, cancele este lançamento no Financeiro (ícone da lixeira ao lado dele).');
      avisarDesfeitoSM();
      return { ok: false, cod: 'acao_mudou', data: res.data };
    }
    if (!res.ok && res.cod === 'horario_ocupado' && papel === 'professor') {
      // v155: o dia e a hora da aula que volta (para ver se o conflito e' o proprio aluno no mesmo horario)
      let alvoAula = null;
      if (alvo.ref_tipo === 'aula' && (res.data?.conflitos || []).some(c => c && c.mesmo_aluno)) {
        try { const { data: au } = await supabase.from('aulas').select('data, hora').eq('id', alvo.ref_id).maybeSingle(); alvoAula = au || null; } catch {}
      }
      forcou = await perguntarConflitoSM(res.data && res.data.conflitos, { rotuloVoltar: 'Voltar', rotuloForcar: 'Desfazer mesmo assim', sufixo: ' Desfazer mesmo assim?', alvo: alvoAula });
      uso.acao('desfazer.conflito', { forcou, origem });
      if (!forcou) return null;
      res = await desfazerMinhaAcaoSM(alvo, { forcar: true });
    }
    if (!res.ok) {
      const semFuncao = !!res.rede && semFuncaoBancoSM(res.tecnico);
      const cod = semFuncao ? 'sem_funcao' : res.rede ? 'rede' : (/^[a-z_]{1,40}$/.test(res.cod) ? res.cod : 'outro');
      if (quieto) return { ok: false, cod, data: res.data, tecnico: res.tecnico };
      if (cod === 'rede') uso.erro('erro.desfazer', res.tecnico); else uso.bloqueio('desfazer', null, null, cod);
      toastSM(semFuncao ? 'Ainda não dá para desfazer por aqui. Atualize o app (feche e abra de novo).'
        : textoErroDesfazerSM(cod, { ...(res.data || {}), ref_tipo: alvo && alvo.ref_tipo }, papel, nomeOutro));
      // recusa do banco = a tela estava velha: as telas abertas releem (rede, nao: nada mudou)
      if (cod !== 'rede' && cod !== 'sem_funcao') avisarDesfeitoSM();
      return { ok: false, cod, data: res.data };
    }
    const d = res.data || {};
    // Revisao v142: "ja' feito" que veio da REPETICAO (a 1a tentativa gravou e so' a resposta caiu na rede) e'
    // o proprio desfazer deste toque — nao "Isso ja' tinha sido desfeito".
    const doRepetido = !!res.repetiu && !!d.ja_feito;
    uso.acao('desfazer.feito', { o_que: /^[a-z_]{1,40}$/.test(String(d.desfeito || '')) ? d.desfeito : 'outro', origem, ja_feito: !!d.ja_feito && !doRepetido, forcado: forcou, repetiu: !!res.repetiu });
    toastSM(doRepetido ? 'Desfeito.' : (d.texto || (d.ja_feito ? 'Isso já tinha sido desfeito.' : 'Desfeito.')));
    // Cobranca: o saldo de aulas e' do app — recalcula SEMPRE que deu certo (refazer a conta nao estraga nada;
    // antes o "ja' feito" da repeticao pulava o recalculo e o saldo ficava velho).
    if (alunoRecalc && alvo && alvo.ref_tipo === 'pagamento') { try { await recalcularCreditos(supabase, alunoRecalc); } catch {} }
    // desfeito por outro caminho (o "Desfazer" do proprio item): a faixa da mesma acao nao fica na tela
    if (FAIXA_DESFAZER_SM && FAIXA_DESFAZER_SM.ref === (alvo && alvo.ref_id)) fecharFaixaDesfazerSM();
    avisarDesfeitoSM();
    return { ok: true, ...d };
  });
  return r;
}

// (a) Faixa "Feito · Desfazer". Mesma faixa escura do toastSM, com o texto de sempre da acao e o
// botao "Desfazer" (branco, negrito, sublinhado, 44x44). Uma por vez: a nova substitui a anterior.
// Dura 10 s (ou o tempo de leitura do toastSM, se for maior), no maximo 12 s. O alvo e' fixado na
// hora da acao (trocar de tela nao muda). Fica acima do menu de baixo do celular.
// opcoes: { papel, nomeOutro, curto (para o aria-label), aoDesfazer(resultado) }
let FAIXA_DESFAZER_SM = null;
function fecharFaixaDesfazerSM() {
  const f = FAIXA_DESFAZER_SM; FAIXA_DESFAZER_SM = null;
  if (!f) return;
  try { clearTimeout(f.timer); } catch {}
  try { f.obs && f.obs.disconnect(); } catch {}
  // revisao v142: enquanto esmaece, a faixa ja' nao recebe toque (um toque no fim dos 10 s desfazia sem ver)
  try { f.el.style.pointerEvents = 'none'; f.el.style.opacity = '0'; setTimeout(() => { try { f.el.remove(); } catch {} }, 250); } catch {}
}
function toastDesfazerSM(msg, alvo, { segundos = 10, papel = 'professor', nomeOutro, curto = '', aoDesfazer, alunoRecalc = null } = {}) {
  if (!alvo || !alvo.ref_id) { toastSM(msg); return; }
  try { uso.evento('aviso.toast', { detalhes: { tipo: 'desfazer' } }); } catch {}
  fecharFaixaDesfazerSM();
  try {
    const el = document.createElement('div');
    el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite'); el.setAttribute('data-faixa-desfazer', '1');
    el.style.cssText = baseAvisoSM() + 'background:#16181D;color:white;padding:6px 6px 6px 18px;border-radius:12px;font-size:14px;font-weight:600;z-index:99999;box-shadow:0 8px 30px rgba(0,0,0,0.35);display:flex;align-items:center;gap:8px;opacity:0;transition:opacity .25s;';
    const txt = document.createElement('span');
    txt.textContent = String(msg);
    txt.style.cssText = 'flex:1;min-width:0;line-height:1.35;padding:6px 0';
    const bt = document.createElement('button');
    bt.type = 'button';
    bt.textContent = 'Desfazer';
    bt.setAttribute('aria-label', 'Desfazer: ' + (curto || String(msg)).slice(0, 80));
    bt.style.cssText = 'flex-shrink:0;min-width:44px;min-height:44px;padding:0 12px;border:none;background:transparent;color:white;font-weight:800;font-size:14px;text-decoration:underline;cursor:pointer;font-family:inherit';
    bt.onclick = async () => {
      if (bt.disabled) return;
      bt.disabled = true; bt.textContent = 'Desfazendo…'; bt.style.textDecoration = 'none';
      try { clearTimeout(FAIXA_DESFAZER_SM && FAIXA_DESFAZER_SM.timer); } catch {}
      const r = await executarDesfazerSM(alvo, { papel, nomeOutro, origem: 'faixa', alunoRecalc });
      if (FAIXA_DESFAZER_SM && FAIXA_DESFAZER_SM.el === el) fecharFaixaDesfazerSM(); else { try { el.remove(); } catch {} }
      try { aoDesfazer && aoDesfazer(r); } catch {}
    };
    el.appendChild(txt); el.appendChild(bt);
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    const leitura = Math.max(4000, Math.min(12000, String(msg).length * 70));
    const dur = Math.min(12000, Math.max(segundos * 1000, leitura));
    const reg = { el, timer: null, ref: alvo.ref_id, obs: null };
    reg.timer = setTimeout(() => { if (FAIXA_DESFAZER_SM === reg) fecharFaixaDesfazerSM(); }, dur);
    FAIXA_DESFAZER_SM = reg;
    // Revisao v142 (tela, achado 1): uma janela que abre por cima (ex.: a folha do "+") nao fica com a faixa em
    // cima das opcoes. A que abre JUNTO com a acao (ex.: o recibo, desenhado no mesmo instante) fica — 0,4 s e'
    // menos que o tempo de alguem ver a faixa e tocar em outra coisa (medido na tela: o recibo nasce em < 50 ms).
    try {
      const nasceu = Date.now();
      reg.obs = new MutationObserver(muts => {
        if (FAIXA_DESFAZER_SM !== reg || Date.now() - nasceu < 400) return;
        const abriu = muts.some(m => [...m.addedNodes].some(n => n && n.nodeType === 1 && n !== el && !n.hasAttribute('data-aviso-sm')
          && !n.hasAttribute('data-faixa-desfazer') && (n.classList.contains('plen-modal-wrap') || (getComputedStyle(n).position === 'fixed'))));
        if (abriu) fecharFaixaDesfazerSM();
      });
      reg.obs.observe(document.body, { childList: true });
    } catch {}
  } catch (e) { toastSM(msg); }
}

// (b) "Desfazer" discreto no proprio item. Toque: a previa do banco (nada e' gravado) abre a
// confirmacao curta DENTRO do cartao — "Desfazer?", o texto do banco, [Voltar] e [Desfazer]. Se a
// previa recusar, mostra a frase e [Ok]. onFeito(resultado) depois do desfazer de verdade (ou de uma
// recusa do banco, para a tela recarregar).
// previaSilenciosa (revisao v142): onde a tela nao sabe quem fez por ultimo (Pendencias: o desacordo que o
// professor criou depois, o aviso que o aluno ja' respondeu, o registro depois de "Conta a aula"), o botao
// pergunta ao banco ao aparecer (p_simular, nada e' gravado) e SOME se a resposta for uma recusa definitiva.
// Sem internet ou banco antigo: continua aparecendo (o toque faz a previa de sempre).
const RECUSA_DEFINITIVA_DESFAZER_SM = ['outro_ja_respondeu', 'nao_foi_voce', 'nada_para_desfazer', 'fechou_sozinha', 'prazo_passou'];
function BotaoDesfazerSM({ alvo, papel = 'professor', nomeOutro, onFeito, escuro = false, rotulo = 'Desfazer', origem = 'item', alunoRecalc = null, previaSilenciosa = false }) {
  const [fase, setFase] = React.useState(null);   // null | {carregando} | {previa} | {erro} | {desfazendo, previa}
  const [oculto, setOculto] = React.useState(false);
  const vivo = React.useRef(true);
  React.useEffect(() => () => { vivo.current = false; }, []);
  const chaveAlvo = alvo ? `${alvo.ref_tipo}|${alvo.ref_id}|${alvo.esperado || ''}` : '';
  React.useEffect(() => {
    if (!previaSilenciosa || !alvo || !alvo.ref_id) return;
    setOculto(false);
    let ativo = true;
    desfazerMinhaAcaoSM(alvo, { simular: true }).then(res => {
      if (!ativo || !vivo.current || !res || res.ok || res.rede) return;
      if (RECUSA_DEFINITIVA_DESFAZER_SM.includes(res.cod)) setOculto(true);
    }, () => {});
    return () => { ativo = false; };
  }, [previaSilenciosa, chaveAlvo]);
  async function abrir() {
    if (fase) return;
    setFase({ carregando: true });
    const res = await desfazerMinhaAcaoSM(alvo, { simular: true });
    if (!vivo.current) return;
    if (res.ok && res.data && res.data.ja_feito) { setFase(null); toastSM(res.data.texto || 'Isso já tinha sido desfeito.'); onFeito && onFeito({ ok: true, ...res.data }); return; }
    if (res.ok) { uso.acao('desfazer.previa', { o_que: /^[a-z_]{1,40}$/.test(String(res.data && res.data.desfeito || '')) ? res.data.desfeito : 'outro', origem }); setFase({ previa: res.data || {} }); return; }
    const semFuncao = !!res.rede && semFuncaoBancoSM(res.tecnico);
    const cod = semFuncao ? 'sem_funcao' : res.rede ? 'rede' : (/^[a-z_]{1,40}$/.test(res.cod) ? res.cod : 'outro');
    if (cod === 'rede') uso.erro('erro.desfazer', res.tecnico); else uso.bloqueio('desfazer', null, null, cod);
    setFase({ erro: semFuncao ? 'Ainda não dá para desfazer por aqui. Atualize o app (feche e abra de novo).'
      : textoErroDesfazerSM(cod, { ...(res.data || {}), ref_tipo: alvo && alvo.ref_tipo }, papel, nomeOutro), cod });
  }
  async function confirmar() {
    if (!fase || !fase.previa || fase.desfazendo) return;
    setFase(f => ({ ...f, desfazendo: true }));
    const r = await executarDesfazerSM(alvo, { papel, nomeOutro, origem, alunoRecalc });
    if (!vivo.current) return;
    if (r === null) { setFase(f => (f ? { ...f, desfazendo: false } : f)); return; }   // voltou do "horario ocupado"
    setFase(null);
    onFeito && onFeito(r);
  }
  function fechar() {
    const f = fase; setFase(null);
    if (f && f.erro && ['outro_ja_respondeu', 'fechou_sozinha', 'prazo_passou', 'nada_para_desfazer', 'acao_mudou', 'nao_foi_voce'].includes(f.cod)) onFeito && onFeito({ ok: false, cod: f.cod });
  }
  const corLink = escuro ? 'rgba(255,255,255,.9)' : '#6E7480';
  const bt = { flex: '1 1 90px', minHeight: '44px', borderRadius: '10px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', padding: '10px 12px' };
  if (oculto && !fase) return null;
  if (!fase || fase.carregando) return (
    <button type="button" onClick={abrir} disabled={!!fase} aria-label={rotulo}
      style={{ alignSelf: 'flex-start', minHeight: '44px', minWidth: '44px', border: 'none', background: 'none', padding: '0 2px', color: corLink,
               fontSize: '13px', fontWeight: 700, textDecoration: 'underline', cursor: fase ? 'default' : 'pointer', fontFamily: 'inherit', opacity: fase ? 0.6 : 1 }}>
      {fase ? 'Conferindo…' : rotulo}
    </button>
  );
  return (
    <div role="group" aria-label="Desfazer" style={{ marginTop: '8px', alignSelf: 'stretch', background: escuro ? 'rgba(255,255,255,.12)' : '#F7F8FA',
         border: '1px solid ' + (escuro ? 'rgba(255,255,255,.3)' : '#E2E4E9'), borderRadius: '12px', padding: '10px 12px', textAlign: 'left' }}>
      {fase.erro ? (
        <>
          <div style={{ fontSize: '13px', color: escuro ? 'white' : '#3D424C', lineHeight: 1.5 }}>{fase.erro}</div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button type="button" onClick={fechar} style={{ ...bt, border: '1.5px solid ' + (escuro ? 'rgba(255,255,255,.6)' : '#E2E4E9'), background: escuro ? 'transparent' : 'white', color: escuro ? 'white' : '#3D424C' }}>Ok</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: '14px', fontWeight: 800, color: escuro ? 'white' : '#16181D' }}>Desfazer?</div>
          <div style={{ fontSize: '13px', color: escuro ? 'rgba(255,255,255,.92)' : '#3D424C', lineHeight: 1.5, marginTop: '3px' }}>{fase.previa.texto_confirmacao || ''}</div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button type="button" disabled={!!fase.desfazendo} onClick={() => setFase(null)}
              style={{ ...bt, border: '1.5px solid ' + (escuro ? 'rgba(255,255,255,.6)' : '#E2E4E9'), background: escuro ? 'transparent' : 'white', color: escuro ? 'white' : '#3D424C' }}>Voltar</button>
            <button type="button" disabled={!!fase.desfazendo} onClick={confirmar}
              style={{ ...bt, border: 'none', background: escuro ? 'white' : '#16181D', color: escuro ? '#16181D' : 'white', opacity: fase.desfazendo ? 0.6 : 1 }}>
              {fase.desfazendo ? 'Desfazendo…' : 'Desfazer'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// v155: datas novas na troca do horario fixo (ficha do aluno e Agenda, a mesma conta). Cada aula vai para o
// novo dia da MESMA semana (nunca para o passado); se o aluno ja' tem aula nesse dia e horario (inclusive uma
// que acabou de mudar), vai para a semana seguinte livre. Antes a 2a caia em cima da 1a e o banco recusava.
// Devolve { [aula_id]: 'AAAA-MM-DD' }.
async function datasTrocaHorarioSM(alunoId, alvo, velhoDia, novoDia, novaHora, hojeStr) {
  const ids = new Set((alvo || []).map(a => a.id));
  let ja = [];
  try {
    const { data } = await supabase.from('aulas').select('id, data, hora').eq('aluno_id', alunoId).gte('data', hojeStr)
      .in('status', ['agendada', 'aguardando_confirmacao', 'realizada', 'falta']);
    ja = data || [];
  } catch {}
  const ocupadas = new Set(ja.filter(a => !ids.has(a.id) && String(a.hora || '').slice(0, 5) === String(novaHora).slice(0, 5)).map(a => a.data));
  const novas = {};
  for (const a of [...(alvo || [])].sort((x, y) => String(x.data).localeCompare(String(y.data)))) {
    const d = new Date(a.data + 'T12:00:00'); d.setDate(d.getDate() + (novoDia - velhoDia));
    if (d.toLocaleDateString('sv-SE') < hojeStr) d.setDate(d.getDate() + 7);
    for (let i = 0; i < 60 && ocupadas.has(d.toLocaleDateString('sv-SE')); i++) d.setDate(d.getDate() + 7);
    const s = d.toLocaleDateString('sv-SE');
    ocupadas.add(s); novas[a.id] = s;
  }
  return novas;
}

// Remarca uma aula pelo banco. Devolve a resposta (ok) ou null (recusou, deu erro ou o professor
// escolheu outro horario — quem chama deixa a tela de data aberta). solicitacaoId: quando vem de
// pedido do aluno (o banco fecha o pedido junto). Avisos ao aluno e a data de renovacao: o banco.
async function remarcarAulaProf(aula, data, hora, { solicitacaoId = null, origem = 'outro', nomeAluno, aoVoltar = null } = {}) {
  if (!aula?.id || !data) return null;
  if (!dataValidaSM(data)) { uso.bloqueio('aula_reagendar', null, null, 'data_invalida'); toastSM('Data inválida — confira o ano.'); return null; }
  return comTravaAulaSM(aula.id, async () => {
    const p = { p_aula_id: aula.id, p_data: data, p_hora: hora ? String(hora).slice(0, 5) : null, p_forcar: false, p_solicitacao_id: solicitacaoId };
    let res = await rpcAulaSM('remarcar_aula', p);
    let forcou = false;
    if (!res.ok && res.cod === 'conflito') {
      forcou = await perguntarConflitoSM(res.data?.conflitos, { alvo: { data, hora: hora || aula.hora } });
      uso.acao('aula.remarcar_conflito', { forcou, origem });
      if (!forcou) { aoVoltar && aoVoltar(); return null; }
      res = await rpcAulaSM('remarcar_aula', { ...p, p_forcar: true });
    }
    if (!res.ok) { avisarErroAulaProf(res, 'aula_reagendar', 'remarcar', nomeAluno); return null; }
    const r = res.data;
    uso.acao('aula.reagendar', { origem, modo: r.modo || (r.sem_mudanca ? 'sem_mudanca' : 'ja_feito'), forcado: forcou, pedido: !!solicitacaoId, aguarda_aluno: !!r.aguarda_aluno });
    const primeiro = primeiroNomeSM(nomeAluno);
    const renov = r.renovacao_depois && r.renovacao_antes && r.renovacao_depois !== r.renovacao_antes
      ? ` A renovação do pacote passou para ${fmtDataBR(r.renovacao_depois)}.` : '';
    // v136 (opcao B): remarcacao do professor vale na hora; aluno com conta pode dizer "nao consigo"
    // (o banco devolve aguarda_aluno). Banco ainda sem isso: a frase de antes.
    toastSM(r.ja_feito ? 'Esse pedido já tinha sido aceito.'
      : r.sem_mudanca ? (r.pedido === 'aceita' ? `Pedido aceito: a aula já estava nesse horário. ${primeiro} foi avisado.` : 'Nada mudou: a aula já está nesse dia e horário.')
      : r.aguarda_aluno && !solicitacaoId ? `Remarcado. ${primeiro} recebe o aviso e pode dizer se não consegue.${renov}`
      : `Aula remarcada. ${primeiro} foi avisado.${renov}`);
    return r;
  });
}

// Contraproposta do professor: o ALUNO aceita pelo banco (aluno_responder_contraproposta), que recusa
// horario ocupado (_aula_conflitos, por intervalo) — e o aluno nao tem "marcar mesmo assim". Entao o
// professor so' envia horario livre. Mesma conta, com as aulas do dia que o professor enxerga (as dele).
// Devolve a lista de conflitos ([] = livre) ou null se nao deu para conferir (o banco confere no aceite).
async function conflitosAgendaProfSM(professorId, alunoId, data, hora, { ignorar = null, durMin = 60 } = {}) {
  let lista = null;
  try {
    const { data: d, error } = await supabase.from('aulas')
      .select('id, data, hora, aluno_id, sessao_id, alunos(nome), contratos(duracao_min)')
      .eq('professor_id', professorId).eq('data', data).in('status', ['agendada', 'aguardando_confirmacao']);
    if (error) return null;
    lista = d || [];
  } catch { return null; }
  const min = h => { const [a, b] = String(h || '08:00').slice(0, 5).split(':').map(Number); return (a || 0) * 60 + (b || 0); };
  const ini = min(hora), fim = ini + (Number(durMin) || 60);
  const vistos = new Set();
  return lista.filter(x => x.id !== ignorar).filter(x => {
    const i = min(x.hora), f = i + (Number(x.contratos?.duracao_min) || 60);
    return i < fim && ini < f;
  }).filter(x => { const k = x.sessao_id || x.id; if (vistos.has(k)) return false; vistos.add(k); return true; })
    .map(x => ({ data: x.data, hora: x.hora, nome: x.sessao_id ? '' : (x.alunos?.nome || ''), turma: !!x.sessao_id, mesmo_aluno: !!alunoId && x.aluno_id === alunoId }));
}

// Aula que o aluno lancou ("combinei com o professor"): Confirmar/Recusar. Ao confirmar, o banco
// confere o horario (p_forcar:false) e o professor decide no alerta (decisao h).
async function responderPropostaProf(aula, aceita, { origem = 'outro', nomeAluno } = {}) {
  return comTravaAulaSM(aula?.id, async () => {
    const p = { p_aula_id: aula.id, p_aceita: !!aceita, p_forcar: false };
    let res = await rpcAulaSM('responder_aula_proposta', p);
    if (!res.ok && res.cod === 'conflito') {
      const forcar = await perguntarConflitoSM(res.data?.conflitos, { rotuloVoltar: 'Voltar', rotuloForcar: 'Confirmar mesmo assim', alvo: { data: aula.data, hora: aula.hora } });
      uso.acao('aula.remarcar_conflito', { forcou: forcar, origem: 'proposta_' + origem });
      if (!forcar) return null;
      res = await rpcAulaSM('responder_aula_proposta', { ...p, p_forcar: true });
    }
    if (!res.ok) { avisarErroAulaProf(res, 'proposta_aula', 'proposta', nomeAluno); return null; }
    uso.acao('proposta_aula.responder', { aceita: !!aceita, origem });
    const f = res.data.fila;
    toastSM(!aceita ? 'Aula retirada. O aluno foi avisado.'
      : f && f.consumiu ? `Aula confirmada. Ela usa uma aula do pacote: a última da agenda saiu${f.renovacao_depois ? ` e a renovação passou para ${fmtDataBR(f.renovacao_depois)}` : ''}. O aluno foi avisado.`
      : 'Aula confirmada. O aluno foi avisado.');
    return res.data;
  });
}

// ---- Aviso do aluno (Etapas 8 e 9) ----------------------------------------------------------
// Linha do professor: "Zé avisou pelo app às 22:00 · fora do prazo"
function textoAvisoProfSM(a, nomeAluno) {
  if (!avisoVigenteAula(a)) return '';
  const p = primeiroNomeSM(nomeAluno);
  const dia = diaSP(a.aviso_aluno_em), h = horaSP(a.aviso_aluno_em);
  const quando = dia === hojeSP() ? `às ${h}` : dia === hojeSP(-1) ? `ontem às ${h}` : `em ${ddmmSM(dia)} às ${h}`;
  const base = a.aviso_canal === 'professor_registrou' ? `${p} avisou ${quando} (você registrou)` : `${p} avisou pelo app ${quando}`;
  const prazo = a.aviso_no_prazo === true ? 'no prazo' : a.aviso_no_prazo === false ? 'fora do prazo' : '';
  const dec = a.aviso_decisao === 'conta' ? 'você manteve a regra' : a.aviso_decisao === 'devolver' ? 'você devolveu a aula' : '';
  return [base, prazo, dec].filter(Boolean).join(' · ');
}
// Aviso FORA do prazo esperando o professor: [Conta a aula] [Devolver]
function avisoPendenteSM(a) {
  return !!a && a.status === 'agendada' && avisoVigenteAula(a) && a.aviso_no_prazo === false && !a.aviso_decisao;
}
// 'conta' = vale o combinado (falta); 'devolver' = cortesia: a aula vai para o fim do pacote.
async function decidirAvisoProf(aula, decisao, { origem = 'outro', nomeAluno } = {}) {
  const primeiro = primeiroNomeSM(nomeAluno);
  return comTravaAulaSM(aula?.id, async () => {
    const res = await rpcAulaSM('professor_decidir_aviso', { p_aula_id: aula.id, p_decisao: decisao });
    if (!res.ok) { avisarErroAulaProf(res, 'aviso_aluno_decidir', 'decidir', nomeAluno); return null; }
    const r = res.data;
    uso.acao('aviso_aluno.decidir', { decisao, origem, ja_feito: !!r.ja_feito });
    // v142: "Feito · Desfazer" (P5) — o aviso volta a esperar a decisao (24 h).
    const alvoDec = { ref_tipo: 'aula', ref_id: aula.id, esperado: 'decisao_aviso' };
    const opDec = { papel: 'professor', nomeOutro: nomeAluno, curto: decisao === 'conta' ? 'Conta a aula' : 'Devolver' };
    if (r.ja_feito) toastSM('Este aviso já tinha sido decidido.');
    else if (decisao === 'conta') toastDesfazerSM(r.estado === 'aguardando'
      ? `Fica valendo a regra: a aula conta. ${primeiro} confirma no app — se não responder em 24h, vale o seu registro.`
      : `Fica valendo a regra: a aula conta. ${primeiro} foi avisado.`, alvoDec, opDec);
    else toastDesfazerSM(`Devolvida por cortesia: a aula foi para o fim do pacote${r.fila?.nova_data ? `, em ${ddmmSM(r.fila.nova_data)}` : ''}. ${primeiro} foi avisado.`, alvoDec, opDec);
    return r;
  });
}
function BotoesDecidirAviso({ aula, nomeAluno, podeDevolver = true, origem, onFeito }) {
  const [ocupado, setOcupado] = React.useState(null);
  const p = primeiroNomeSM(nomeAluno);
  // "Avisou, mas veio": depois que a aula comecou, o professor registra aula dada (o registro dele
  // vale — professor_fechar_aula com forcar; o aluno recebe o aviso). Antes, so' sobrava "Conta a
  // aula", que grava FALTA.
  const ini = inicioAulaSP(aula);
  const comecou = !!ini && ini.getTime() <= Date.now();
  async function decidir(dec) {
    if (ocupado) return;
    setOcupado(dec);
    const r = await decidirAvisoProf(aula, dec, { origem, nomeAluno });
    setOcupado(null);
    if (r) onFeito && onFeito(r, dec);
  }
  async function veio() {
    if (ocupado) return;
    setOcupado('veio');
    const est = await aplicarDesfechoProfessor(aula, 'realizada', { forcar: true, nomeAluno, origem: origem + '_veio' });
    setOcupado(null);
    if (est) onFeito && onFeito({ estado: est }, 'veio');
  }
  const bt = { flex: '1 1 110px', minHeight: '44px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', padding: '10px 12px' };
  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
        <button type="button" disabled={!!ocupado} onClick={() => decidir('conta')}
          style={{ ...bt, border: 'none', background: '#16181D', color: 'white', opacity: ocupado ? 0.6 : 1 }}>
          {ocupado === 'conta' ? 'Salvando…' : 'Conta a aula'}
        </button>
        {podeDevolver && (
          <button type="button" disabled={!!ocupado} onClick={() => decidir('devolver')}
            style={{ ...bt, border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C', opacity: ocupado ? 0.6 : 1 }}>
            {ocupado === 'devolver' ? 'Salvando…' : 'Devolver'}
          </button>
        )}
        {comecou && (
          <button type="button" disabled={!!ocupado} onClick={veio}
            style={{ ...bt, border: '1.5px solid #A7E3CB', background: '#EFFAF5', color: '#0A6F4D', opacity: ocupado ? 0.6 : 1 }}>
            {ocupado === 'veio' ? 'Salvando…' : `${p} veio — aula dada`}
          </button>
        )}
      </div>
      <div style={{ fontSize: '11.5px', color: '#6E7480', marginTop: '5px', lineHeight: 1.45 }}>
        {podeDevolver ? 'Conta a aula: vale o combinado. Devolver: cortesia — a aula vai para o fim do pacote.' : 'Conta a aula: vale o combinado (na mensalidade não há o que devolver).'}
        {comecou ? ` Se ${p} apareceu mesmo assim, registre a aula dada.` : ''}
      </div>
    </div>
  );
}

// ---- v140: o professor pergunta "vai?" (professor_pedir_presenca) --------------------------
// p_simular=true (abrir as Opcoes): o banco diz se da' para perguntar (bloqueio), se o aluno ainda
// esta' no prazo do acordo (no_prazo/prazo_texto) e se e' pacote. Nada e' gravado. Sem simular: grava
// presenca_pedida_em, manda o push "Tudo certo para a aula?" e registra. Uma pergunta por aula e
// horario (repetir devolve ja_feito). Devolve { ok, ...dados } | { ok:false, erro | rede | semFuncao }.
function textoErroPresencaProfSM(cod, nomeAluno) {
  const P = primeiroNomeSM(nomeAluno, 'O aluno');
  return ({
    aula_mudou: 'A aula mudou enquanto você perguntava. A tela foi atualizada.',
    ja_respondeu: `${P} já respondeu que vem.`,
    aviso_vigente: `${P} já avisou que não vem.`,
    perto_demais: 'Falta menos de 1 hora: melhor chamar no WhatsApp.',
    ja_comecou: 'A aula já começou: melhor chamar no WhatsApp.',
    tarde_da_noite: `Já é tarde para tocar o celular de ${P}. Pergunte a partir das 6h ou chame no WhatsApp.`,
    longe_demais: 'Dá para perguntar no dia da aula ou na véspera.',
    remarcacao_esperando: `${P} ainda não respondeu à remarcação. Quando tocar em "Ok, combinado", já vale como confirmação.`,
    pedido_aberto: `${P} tem um pedido aberto para esta aula. Responda o pedido primeiro.`,
    sem_app: `${P} ainda não usa o app. Pergunte pelo WhatsApp.`,
    turma: 'Em aula de turma ainda não dá para perguntar pelo app.',
  })[cod] || textoErroAulaProf(cod, null, 'presenca', nomeAluno);
}
async function pedirPresencaProfSM(aula, { simular = false, origem = 'outro', nomeAluno } = {}) {
  if (!aula || !aula.id) return { ok: false, erro: 'aula_nao_encontrada' };
  const chamar = () => rpcAulaSM('professor_pedir_presenca', { p_aula_id: aula.id, p_simular: !!simular });
  if (simular) {
    const res = await chamar();
    if (res.ok) return { ok: true, ...res.data };
    if (res.rede && semFuncaoBancoSM(res.tecnico)) return { ok: false, semFuncao: true };
    if (res.rede) return { ok: false, rede: true };
    return { ok: false, erro: res.cod };
  }
  const r = await comTravaAulaSM('presenca:' + aula.id, async () => {
    const res = await chamar();
    if (!res.ok) {
      if (res.rede && semFuncaoBancoSM(res.tecnico)) {
        uso.acao('presenca.pedir', { resultado: 'sem_funcao', origem });
        toastSM('Ainda não dá para perguntar por aqui. Atualize o app (feche e abra de novo) ou chame no WhatsApp.');
        return { ok: false, semFuncao: true };
      }
      if (res.rede) { uso.erro('erro.presenca_pedir', res.tecnico); toastSM(textoErroAulaProf('rede')); return { ok: false, rede: true }; }
      uso.acao('presenca.pedir', { resultado: /^[a-z_]{1,40}$/.test(res.cod) ? res.cod : 'outro', origem });
      toastSM(textoErroPresencaProfSM(res.cod, nomeAluno));
      return { ok: false, erro: res.cod };
    }
    const d = res.data || {};
    uso.acao('presenca.pedir', { resultado: d.ja_feito ? 'ja_feito' : 'ok', origem, no_prazo: d.no_prazo !== false });
    toastSM(d.ja_feito ? 'Você já tinha perguntado.' : 'Pergunta enviada. A resposta aparece aqui.');
    return { ok: true, ...d };
  });
  return r || { ok: false, ocupado: true };   // toque duplo: a trava devolve null
}
// Textos prontos do WhatsApp do professor (§4.4)
function textoWhatsPerguntaSM(aula, nomeAluno, nomeProf, { semResposta = false } = {}) {
  const P = primeiroNomeSM(nomeAluno, '');
  const quando = hojeOuAmanhaSM(aula.data);
  const h = hhmmSM(aula.hora);
  const assina = nomeProf ? ` — ${primeiroNomeSM(nomeProf, '')}` : '';
  if (semResposta) return `Oi${P ? ' ' + P : ''}! Te mandei uma pergunta no Plen: tudo certo para a nossa aula de ${quando || diaCurtoSM(aula.data)} às ${h}?${assina}`;
  return `Oi${P ? ' ' + P : ''}! Tudo certo para a nossa aula de ${quando ? `${quando} (${diaCurtoSM(aula.data)})` : diaCurtoSM(aula.data)} às ${h}?${assina}`;
}
const MOTIVO_WHATS_NAO_VOU_SM = { doente: 'doente', agenda: 'compromisso', clima: 'chuva/quadra fechada', outro: 'um imprevisto' };
function textoWhatsNaoVouSM(aula, nomeAluno, nomeProf, motivo, foiProFim) {
  const P = primeiroNomeSM(nomeAluno, '');
  const quando = hojeOuAmanhaSM(aula.data) || diaCurtoSM(aula.data);
  return `Oi${P ? ' ' + P : ''}! Não vou conseguir dar a nossa aula de ${quando} às ${hhmmSM(aula.hora)} (${MOTIVO_WHATS_NAO_VOU_SM[motivo] || 'um imprevisto'}).`
    + (foiProFim ? ' A aula vai para o fim do seu pacote.' : '') + ' Desculpe!' + (nomeProf ? ` — ${primeiroNomeSM(nomeProf, '')}` : '');
}

// ---- v140: o aluno responde "Vou" (aluno_confirmar_presenca) -------------------------------
// Molde de responderRemarcacaoAlunoSM. "Vou" nao mexe em credito nem avisa o professor por push (so' o
// ✓ na agenda dele). "Nao vou" e' o aviso de falta de sempre (folha cancelar/avisar).
// v142 (B): tambem SEM a pergunta (semPergunta: a aula nao tinha presenca_pedida_em) — o banco da v142b
// devolve sempre 'sem_pergunta' na resposta; sem essa marca num "ja_mudou" de aula sem pergunta, o banco
// ainda e' o da v140 (o SQL v142b nao rodou). Deu certo: faixa "Feito · Desfazer" (A3, esperado 'vou').
// aoDesfazer(resultado): a tela atualiza a aula na hora (o evento plen:desfeito recarrega o resto).
async function responderPresencaAlunoSM(aulaId, { origem = 'outro', nomeProf = 'Seu professor', semPergunta = false, aoDesfazer } = {}) {
  if (!aulaId) return { ok: false };
  const res = await comTravaAulaSM('vou:' + aulaId, async () => {
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('aluno_confirmar_presenca', { p_aula_id: aulaId })); } catch (e) { error = e; }
    if (error) {
      if (semFuncaoBancoSM([error.code, error.message].join(' '))) {
        uso.bloqueio('aluno_presenca', null, null, 'sem_funcao');
        toastSM(`Ainda não dá para responder por aqui. Combine com ${nomeProf}.`);
        return { ok: false };
      }
      uso.erro('erro.aluno_presenca', error.message || String(error));
      toastSM('Não foi possível responder agora. Confira a internet e tente de novo.');
      return { ok: false, rede: true };
    }
    if (!data || data.erro || !data.ok) {
      const cod = String((data && data.erro) || 'sem_resposta');
      uso.bloqueio('aluno_presenca', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      toastSM(cod === 'avisou_falta' ? `Você já avisou que não vai. Se mudou de ideia, fale com ${nomeProf}.`
        : cod === 'sem_permissao' ? 'Esta aula não está na sua ficha.'
        : cod === 'aluno_desvinculado' ? `Seu vínculo com ${nomeProf} foi encerrado.`
        : cod === 'aula_nao_encontrada' ? 'Não encontramos esta aula. A tela foi atualizada.'
        // v142 (B): recusas do "Vou" sem pergunta (sql/v142b.sql)
        : cod === 'longe_demais' ? 'Dá para avisar que vai no dia da aula ou na véspera.'
        : cod === 'turma' ? 'Em aula de turma ainda não dá para avisar por aqui.'
        : cod === 'remarcacao_esperando' ? `${nomeProf} remarcou esta aula. Responda à remarcação: o "Ok, combinado" já vale como "Vou".`
        : 'Não foi possível responder agora. Tente de novo em instantes.');
      avisarPendenciasMudouSM();
      return { ok: false, erro: cod };
    }
    // banco ainda sem a v142b: a aula sem pergunta volta como "ja_mudou" sem a marca 'sem_pergunta'
    if (semPergunta && data.ja_mudou && !('sem_pergunta' in data)) {
      uso.bloqueio('aluno_presenca', null, null, 'sem_funcao');
      toastSM(`Ainda não dá para avisar por aqui. Combine com ${nomeProf}.`);
      return { ok: false, semFuncao: true };
    }
    uso.acao('aluno.presenca_responder', { resposta: 'vou', origem, ja_feito: !!data.ja_feito, ja_mudou: !!data.ja_mudou, sem_pergunta: !!semPergunta });
    avisarPendenciasMudouSM();
    if (data.ja_feito || data.ja_mudou) {
      toastSM(data.ja_feito ? 'Você já tinha respondido.'
        : 'Não dá mais para responder: a aula mudou, foi desmarcada ou já começou. A tela foi atualizada.');
      return { ok: true, data };
    }
    // sem pergunta o professor nao recebe aviso no celular: ele ve' o "✓" na agenda dele
    toastDesfazerSM(semPergunta ? `Pronto! ${nomeProf} vê na agenda que você vai.` : `Pronto! ${nomeProf} já sabe que você vai.`,
      { ref_tipo: 'aula', ref_id: aulaId, esperado: 'vou' }, { papel: 'aluno', nomeOutro: nomeProf, curto: 'Vou', aoDesfazer });
    return { ok: true, data };
  });
  return res || { ok: false };   // toque duplo
}

// ---- Pendencias (v134) ----------------------------------------------------------------------
// Marcus, 06/10: "Pendencias" no lugar de "Notificacoes". Notificacao e' historico (o aviso "Cadu
// registrou falta, confirme no app" fica la' mesmo depois de confirmado); pendencia e' o que falta
// fazer, nos dois lados, com a meta "pendencia zero". Quem monta a lista e' o BANCO
// (minhas_pendencias), lendo o estado real das aulas, pagamentos, acordos e pedidos: resolveu, some
// sozinha. O banco manda fatos (tipo + detalhe); a frase e' montada aqui. Os botoes reusam o que
// ja' existe (fechar aula, decidir aviso, confirmar recebimento...) ou levam a tela onde se resolve.

// Toda acao que pode resolver uma pendencia dispara este evento (funcoes de aula e de pagamento do
// banco, respostas do aluno): quem mostra o numero da aba recarrega.
const EVENTO_PENDENCIAS_SM = 'plen:pendencias-mudou';
function avisarPendenciasMudouSM() { try { window.dispatchEvent(new Event(EVENTO_PENDENCIAS_SM)); } catch {} }
// v143 (B): [✓ Vou] / [✓ Foi dada] tocado no aviso com o app ABERTO — o iPhone respondeu ao banco sozinho
// e avisa a pagina (evento 'plen:acao-aviso', {acao}). As telas abertas recarregam como depois de um
// desfazer (o cartao passa a mostrar "Você avisou que vai ✓ · Desfazer") e as pendencias tambem.
const EVENTO_ACAO_AVISO_SM = 'plen:acao-aviso';
try { window.addEventListener(EVENTO_ACAO_AVISO_SM, () => { avisarDesfeitoSM(); avisarPendenciasMudouSM(); }); } catch {}
// Banco ainda sem a funcao (SQL nao rodado): o app segue como antes — so' o historico e o numero
// de nao lidos. Tem de funcionar antes e depois do SQL.
function semFuncaoPendenciasSM(e) {
  const t = e && typeof e === 'object' ? [e.code, e.message, e.details, e.hint].join(' ') : String(e || '');
  return /PGRST202|could not find the function|minhas_pendencias[\s\S]*does not exist/i.test(t);
}
let erroPendenciasContadoSM = false;   // erro de verdade vai a' central uma vez por abertura (o relogio repete)

// Lista do banco -> itens com detalhe sempre objeto, do mais antigo para o mais novo (desde).
function listaPendSM(l) {
  const ms = i => { const t = i.desde ? new Date(i.desde).getTime() : NaN; return isNaN(t) ? Infinity : t; };
  return (Array.isArray(l) ? l : [])
    .filter(i => i && typeof i === 'object' && i.tipo)
    .map(i => ({ ...i, detalhe: i.detalhe && typeof i.detalhe === 'object' && !Array.isArray(i.detalhe) ? i.detalhe : {} }))
    .sort((a, b) => { const x = ms(a), y = ms(b); return x === y ? 0 : x < y ? -1 : 1; });
}
function ladoPendSM(b) {
  if (!b || typeof b !== 'object') return null;
  return { sua_vez: listaPendSM(b.sua_vez), esperando: listaPendSM(b.esperando) };
}

// Carregador unico (professor e aluno usam o mesmo): chama minhas_pendencias ao abrir, a cada
// minuto com o app na frente, ao voltar o app para a frente e quando alguma acao avisa
// (EVENTO_PENDENCIAS_SM). estado: carregando | ok | sem_rpc (banco sem a funcao) | erro.
// Falha depois de uma lista boa: fica a ultima lista (nao some tudo por um soluco da rede).
function usePendenciasSM(ativo = true) {
  const [st, setSt] = React.useState({ estado: 'carregando', professor: null, aluno: null, contagem: null });
  const vivo = React.useRef(true);
  const correndo = React.useRef(false);
  const deNovo = React.useRef(false);
  const semRpc = React.useRef(false);
  const ultima = React.useRef('');   // a mesma lista de antes nao redesenha o app inteiro (o relogio roda a cada minuto)
  const carregar = React.useCallback(async () => {
    if (correndo.current) { deNovo.current = true; return; }   // pedido no meio de outro: roda mais uma vez no fim
    correndo.current = true;
    try {
      let data = null, error = null;
      try { ({ data, error } = await supabase.rpc('minhas_pendencias')); } catch (e) { error = e; }
      if (!vivo.current) return;
      const marcar = est => setSt(s => (s.estado === 'ok' || s.estado === est ? s : { ...s, estado: est }));
      const falhou = () => marcar('erro');
      if (error) {
        if (semFuncaoPendenciasSM(error)) { semRpc.current = true; marcar('sem_rpc'); return; }
        if (!erroPendenciasContadoSM && !ehFalhaDeRedeSM(error.message || error)) { erroPendenciasContadoSM = true; uso.erro('erro.pendencias', error.message || String(error)); }
        falhou();
        return;
      }
      if (!data || data.erro || !data.ok) {
        if (!erroPendenciasContadoSM) { erroPendenciasContadoSM = true; uso.erro('erro.pendencias', String((data && data.erro) || 'sem_resposta')); }
        falhou();
        return;
      }
      semRpc.current = false;
      let txt = '';
      try { txt = JSON.stringify(data); } catch {}
      if (txt && txt === ultima.current) return;
      ultima.current = txt;
      const c = Number(data.contagem && data.contagem.sua_vez);
      setSt({ estado: 'ok', professor: ladoPendSM(data.professor), aluno: ladoPendSM(data.aluno), contagem: Number.isFinite(c) ? c : null });
    } finally {
      correndo.current = false;
      if (deNovo.current && vivo.current) { deNovo.current = false; setTimeout(() => carregar(), 0); }
    }
  }, []);
  React.useEffect(() => {
    vivo.current = true;
    if (!ativo) return () => { vivo.current = false; };
    carregar();
    let espera = null;
    // varias acoes seguidas (ex.: presenca da turma fecha uma aula por aluno) = uma recarga so'
    const aoMudar = () => { clearTimeout(espera); espera = setTimeout(() => carregar(), 500); };
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregar(); };
    // sem a funcao no banco o relogio para (o 404 a cada minuto e' ruido); voltar o app tenta de novo
    const relogio = setInterval(() => { if (document.visibilityState === 'visible' && !semRpc.current) carregar(); }, 60000);
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener(EVENTO_PENDENCIAS_SM, aoMudar);
    return () => {
      vivo.current = false; clearTimeout(espera); clearInterval(relogio);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener(EVENTO_PENDENCIAS_SM, aoMudar);
    };
  }, [ativo, carregar]);
  return { ...st, recarregar: carregar };
}

// Numero vermelho da aba = quantas estao "Sua vez" do lado que a tela mostra (antes: avisos nao
// lidos). contagem.sua_vez do banco soma os dois lados (quem e' professor E aluno); com um lado
// so', vale o maior. null = sem lista (banco sem a funcao ou falha): a aba volta aos nao lidos.
// Carregando: 0 (sem numero) — evita piscar o numero antigo e trocar em seguida.
function numeroPendSM(p, lado) {
  if (!p || p.estado === 'carregando') return 0;
  if (p.estado !== 'ok') return null;
  const meu = p[lado], outro = p[lado === 'professor' ? 'aluno' : 'professor'];
  const n = meu ? meu.sua_vez.length : 0;
  return !outro && p.contagem != null ? Math.max(n, p.contagem) : n;
}

// ---------------------------------------------------------------------------
// v148 — NUMERO NO ICONE (Marcus, 21/10: "igual ao do e-mail"). O icone do Plen mostra o MESMO numero do sininho:
// o que esta' esperando a pessoa (numeroPendSM: o numero da aba Pendencias deste lado) + os avisos novos que
// chegaram com o app fechado (o banco conta do mesmo jeito: _numero_icone no sql/v148_icone.sql). Com o app aberto nao ha' aviso "novo": o app avisa o banco que a pessoa esta' vendo
// (marcar_icone_visto: ao abrir, ao voltar para a frente, ao ir para o fundo e a cada 10 min) e poe no icone o
// numero das Pendencias. Com o app fechado quem atualiza e' o proprio aviso (a Edge enviar-push manda o numero
// junto) — so' para o aparelho que avisou que sabe mostrar E limpar: o app da loja com o metodo nativo PlenIcone
// (build 10+) ou o navegador com navigator.setAppBadge. Apps 8 e 9 nao tem o metodo, nao avisam e nunca recebem
// numero. Banco sem a v148, ou aparelho que o banco nao marcou: o app so' APAGA o numero (um numero que ninguem
// atualiza com o app fechado ficaria parado e errado). Sair da conta apaga.
// ---------------------------------------------------------------------------
const EVENTO_PUSH_REGISTRADO_SM = 'plen:push-registrado';
function avisarPushRegistradoSM() { try { window.dispatchEvent(new Event(EVENTO_PUSH_REGISTRADO_SM)); } catch {} }
function iconeNativoSM() { return pluginNativo('PlenIcone'); }
function iconeNavegadorSM() {
  return !APP_NATIVO && typeof navigator !== 'undefined' && typeof navigator.setAppBadge === 'function';
}
// o aparelho deste celular/navegador que sabe mostrar e limpar o numero (o endereco do aviso que gravarInscricaoPush guardou)
function aparelhoDoIconeSM() {
  let ep = null;
  try { ep = localStorage.getItem('plenPushEndpoint'); } catch {}
  if (!ep) return null;
  if (iconeNativoSM()) return /^apns:/.test(ep) ? ep : null;
  if (iconeNavegadorSM()) return /^https:\/\//.test(ep) ? ep : null;
  return null;
}
function definirNumeroIconeSM(n) {
  const v = Math.max(0, Math.min(99999, Math.floor(Number(n) || 0)));
  try {
    if (iconeNativoSM()) { window.Capacitor.nativePromise('PlenIcone', 'definir', { numero: v }).catch(() => {}); return; }
    if (iconeNavegadorSM()) {
      const p = v > 0 ? navigator.setAppBadge(v) : (typeof navigator.clearAppBadge === 'function' ? navigator.clearAppBadge() : navigator.setAppBadge(0));
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  } catch {}
}
let erroIconeContadoSM = false;   // erro de verdade vai a' central uma vez por abertura
function useNumeroIconeSM(pend, lado, ativo) {
  const cuida = React.useRef(false);      // o banco confirmou: este aparelho recebe o numero com o app fechado
  const contagem = React.useRef(null);
  // o numero da aba (so' com a lista do banco na mao: carregando/erro nao mexe no icone)
  const n = pend && pend.estado === 'ok' ? numeroPendSM(pend, lado) : null;
  contagem.current = n;
  const aplicar = React.useCallback(() => {
    if (cuida.current && contagem.current != null) definirNumeroIconeSM(contagem.current);
  }, []);
  const ultimaMarca = React.useRef(0);
  const marcar = React.useCallback(async () => {
    ultimaMarca.current = Date.now();
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('marcar_icone_visto', { p_endpoint: aparelhoDoIconeSM() })); } catch (e) { error = e; }
    const msg = error ? [error.code, error.message, error.details].join(' ') : '';
    if (error && ehFalhaDeRedeSM(msg)) return;                       // sem rede agora: fica como esta'
    const semBanco = !!error && /PGRST202|could not find the function|marcar_icone_visto/i.test(msg);
    if (error && !semBanco && !erroIconeContadoSM) { erroIconeContadoSM = true; uso.erro('erro.icone', error.message || String(error)); }
    if (error && !semBanco) return;
    cuida.current = !error && !!data && data.ok === true && data.mostra_numero === true;
    try {
      if (!sessionStorage.getItem('plen_uso_icone')) {
        sessionStorage.setItem('plen_uso_icone', '1');
        uso.evento('push.icone', { detalhes: { estado: semBanco ? 'sem_banco' : cuida.current ? 'mostra' : 'nao_mostra',
          via: iconeNativoSM() ? 'nativo' : iconeNavegadorSM() ? 'navegador' : 'nenhum' } });
      }
    } catch {}
    if (cuida.current) aplicar(); else if (semBanco || (data && data.ok === true)) definirNumeroIconeSM(0);
  }, [aplicar]);
  // a contagem das Pendencias mudou (abriu, resolveu, chegou algo): o icone acompanha
  React.useEffect(() => { if (ativo) aplicar(); }, [ativo, n, aplicar]);
  React.useEffect(() => {
    if (!ativo) return undefined;
    marcar();
    // indo para o fundo: o icone fica com o numero do sininho na hora (o pedido ao banco pode nao terminar).
    // Trocar de aba no computador varias vezes seguidas = um pedido so' a cada 20 s.
    const aoMudar = () => {
      if (document.visibilityState === 'hidden') aplicar();
      if (Date.now() - ultimaMarca.current >= 20000) marcar();
    };
    const relogio = setInterval(() => { if (document.visibilityState === 'visible') marcar(); }, 10 * 60000);
    document.addEventListener('visibilitychange', aoMudar);
    window.addEventListener(EVENTO_PUSH_REGISTRADO_SM, marcar);   // aparelho novo: confere na hora
    return () => {
      clearInterval(relogio);
      document.removeEventListener('visibilitychange', aoMudar);
      window.removeEventListener(EVENTO_PUSH_REGISTRADO_SM, marcar);
    };
  }, [ativo, marcar, aplicar]);
}

// 'quando' do banco: ISO com hora (inicio da aula) ou so' a data ('AAAA-MM-DD', vencimento).
function partesQuandoSM(q) {
  if (!q) return { data: null, hora: null };
  const s = String(q);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { data: s, hora: null };
  const d = new Date(s);
  if (isNaN(d)) return { data: /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null, hora: null };
  return { data: diaSP(d), hora: horaSP(d) };
}
// 'Qui 02/10 às 7h' (hora de Brasilia)
function quandoPendSM(q) { const p = partesQuandoSM(q); return p.data ? quandoCurtoSM(p.data, p.hora) : ''; }
// 'hoje' / 'ontem' / 'em 02/10' (comHora: 'hoje às 22:00')
function desdeSM(ts, comHora = false) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const dia = diaSP(d);
  const txt = dia === hojeSP() ? 'hoje' : dia === hojeSP(-1) ? 'ontem' : 'em ' + ddmmSM(dia);
  return comHora ? `${txt} às ${horaSP(d)}` : txt;
}
// Periodo da pausa: 'De 10/10 a 20/10' / 'A partir de 10/10'
function periodoPendSM(ini, fim) {
  if (!ini && !fim) return '';
  if (ini && fim && ini !== fim) return `De ${ddmmSM(ini)} a ${ddmmSM(fim)}`;
  return `A partir de ${ddmmSM(ini || fim)}`;
}
// Motivo do aviso do aluno, contado pelo professor (o rotulo do seletor e' na voz do aluno: "Estou doente")
const MOTIVO_AVISO_PROF_SM = { agenda: 'compromisso', doente: 'doente', viagem: 'viagem', feriado: 'feriado', clima: 'chuva ou local fechado' };
// A aula do cartao no formato que as funcoes de aula do app usam (id, aluno, dia e hora).
function aulaDoItemSM(item) {
  const d = item.detalhe || {}, p = partesQuandoSM(item.quando);
  return { id: item.ref_id, aluno_id: item.aluno_id, status: 'agendada',
           data: d.data || p.data, hora: d.hora ? String(d.hora).slice(0, 5) : p.hora,
           divergencia_em: d.divergencia_em || null, motivo_cancelamento: d.motivo_cancelamento || null };
}
const tipoUsoPendSM = t => (/^[a-z0-9_.-]{1,40}$/.test(String(t || '')) ? t : 'outro');

// Historico de avisos: aviso "de acao" cuja pendencia ja' nao esta' aberta ganha "✓ resolvido" e
// perde o botao. Casa pelo ref (ref_tipo + ref_id) quando o aviso traz; muitos avisos antigos nao
// trazem (aula_para_confirmar, aula_divergencia, pedidos do app do aluno...) — ai' casa pela
// familia: resolvido = nao ha pendencia aberta daquele tipo para aquele aluno. So' os tipos abaixo
// contam como "de acao" (reajuste_refazer, por ex., nao tem estado no banco: fica como esta').
// A presenca da turma (turma_sem_fechar) nao tem aluno_id: casa pelos alunos da sessao (detalhe.aulas),
// senao uma turma aberta segurava o "resolvido" de todos os avisos de aula. Aviso sem aluno
// (aula_confirmada_aluno/falta_declarada_aluno sao gravados sem aluno_id e sem ref) nao tem como
// saber de quem e': so' fica "resolvido" quando nao sobra NENHUMA pendencia da familia.
const FAMILIA_AVISO_PROF_SM = {
  aulas_sem_confirmacao: ['aula_sem_fechar', 'turma_sem_fechar', 'aula_divergencia'],
  aula_confirmada_aluno: ['aula_sem_fechar', 'turma_sem_fechar', 'aula_divergencia'],
  falta_declarada_aluno: ['aula_sem_fechar', 'turma_sem_fechar', 'aula_divergencia', 'aviso_aluno_decidir'],
  aula_divergencia: ['aula_divergencia'],
  cancelamento_solicitado: ['aviso_aluno_decidir'],
  remarcacao_solicitada: ['pedido_aluno'],
  reagendamento: ['pedido_aluno'],
  pausa_solicitada: ['pedido_aluno'],
  ajuste_contrato: ['pedido_aluno'],
  contestacao: ['pedido_aluno', 'aula_sem_fechar'],
  aula_proposta: ['aula_proposta_aluno'],
  pagamento_declarado_aluno: ['pagamento_declarado'],
  plano_expirando: ['pacote_fim'],
  // v136: o aluno disse que nao consegue no horario novo (o aviso traz ref solicitacao)
  remarcacao_recusada: ['remarcacao_recusada'],
  // v140: o aluno disse que voce nao veio (o aviso traz ref aula: casa pela aula)
  prof_nao_veio: ['aula_sem_fechar', 'aula_divergencia'],
};
// Familia em forma de objeto ({ tipos, soRef }): o aviso so' e' "de acao" quando traz aquela ref.
// 'reagendamento' (o professor remarcou) era so' informativo ate' a v135 (ref aula); desde a v136
// o aviso novo traz ref solicitacao e o aluno pode responder "Ok" / "Nao consigo" — o antigo nao
// pode ganhar "resolvido" como se tivesse pedido alguma coisa.
const FAMILIA_AVISO_ALUNO_SM = {
  aula_para_confirmar: ['confirmar_aula'],
  aulas_sem_resposta_aluno: ['confirmar_aula', 'aula_sem_registro'],
  contrato: ['aceitar_acordo'],
  aceite_reajuste: ['responder_reajuste'],
  reagendamento_contraproposta: ['responder_contraproposta'],
  cobranca_lembrete: ['pagar'],
  reagendamento: { tipos: ['confirmar_remarcacao'], soRef: 'solicitacao' },
  // v140: "Tudo certo para a aula?" (o aviso traz ref aula: casa pela aula)
  presenca_pedida: ['confirmar_presenca'],
};
// v142: aviso que ganhou um "… desfez …" (tipo acao_desfeita) MAIS NOVO na mesma referencia (aula, cobranca
// ou pedido) -> marca cinza "desfeito" no lugar de "resolvido", sem botao de acao. Limite conhecido (spec 2.3):
// aviso original sem ref continua podendo mostrar "resolvido"; o "… desfez …" logo acima explica.
function avisoDesfeitoSM(n, lista) {
  if (!n || n.tipo === 'acao_desfeita' || !n.ref_tipo || !n.ref_id) return false;
  const t = new Date(n.created_at).getTime();
  return (lista || []).some(x => x && x.tipo === 'acao_desfeita' && x.ref_tipo === n.ref_tipo && String(x.ref_id) === String(n.ref_id)
    && new Date(x.created_at).getTime() > t);
}
// v152 (revisao): o pedido "ja' paguei o proximo pacote" e' o mesmo pedido de renovacao — o modal abre com "Ja' pagou"
function renovarJaPagouSM(n) { return !!(n && /já pagou o próximo pacote/.test(n.mensagem || '')); }
function avisoResolvidoSM(n, abertas, familias) {
  // v152: pedido de renovacao do aluno (ajuste_contrato + ref contrato): nao ha' pendencia no banco que case com ele;
  // quem marca e' o Historico de avisos (renovacao_resolvida = ja' tem proximo pacote ou o pacote virou)
  if (n && n.tipo === 'ajuste_contrato' && n.ref_tipo === 'contrato') return !!n.renovacao_resolvida;
  if (!n || !familias[n.tipo]) return false;
  const def = familias[n.tipo];
  if (def.soRef && n.ref_tipo !== def.soRef) return false;
  const lista = abertas || [];
  if (n.ref_tipo && n.ref_id) return !lista.some(i => i.ref_tipo === n.ref_tipo && String(i.ref_id) === String(n.ref_id));
  const fam = Array.isArray(def) ? def : (def.tipos || []);
  const doAluno = i => {
    if (!n.aluno_id) return true;                       // aviso sem aluno: qualquer uma da familia segura
    if (i.aluno_id) return i.aluno_id === n.aluno_id;
    const aulas = i.detalhe && Array.isArray(i.detalhe.aulas) ? i.detalhe.aulas : null;
    return aulas ? aulas.some(a => a && a.aluno_id === n.aluno_id) : true;
  };
  return !lista.some(i => fam.includes(i.tipo) && doAluno(i));
}

// ---- pecas da tela (mesmo visual do app: cartao branco, Manrope, toque de 44px) ----
const CORES_PEND_SM = {
  vermelho: ['#DC2626', '#FEE2E2'], ambar: ['#D97706', '#FEF3C7'], azul: ['#2F6BFF', '#E7EDFF'],
  verde: ['#0E9F6E', '#DCF5EC'], cinza: ['#6E7480', '#F0F1F4'],
};
function btPendSM(tipo = 'primario') {
  const base = { flex: '1 1 120px', minHeight: '44px', borderRadius: '12px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                 fontFamily: 'inherit', padding: '10px 14px', lineHeight: 1.2 };
  if (tipo === 'primario') return { ...base, border: 'none', background: '#2F6BFF', color: 'white' };
  if (tipo === 'escuro') return { ...base, border: 'none', background: '#16181D', color: 'white' };
  return { ...base, border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C' };
}
function BotoesPendSM({ children }) {
  return <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>{children}</div>;
}
function CartaoPendSM({ ic, cor = 'azul', titulo, linha, nota, notaCor = 'cinza', children }) {
  const [c, bg] = CORES_PEND_SM[cor] || CORES_PEND_SM.azul;
  const [nc, nbg] = CORES_PEND_SM[notaCor] || CORES_PEND_SM.cinza;
  return (
    <div style={{ background: 'white', border: '1px solid #E2E4E9', borderRadius: '16px', padding: '14px', boxShadow: '0 1px 2px rgba(22,24,29,.05)' }}>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <span style={{ width: '36px', height: '36px', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Ico n={ic} s={18} c={c} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, color: '#16181D', lineHeight: 1.35, overflowWrap: 'anywhere' }}>{titulo}</div>
          {linha ? <div className="num" style={{ fontSize: '12.5px', color: '#6E7480', marginTop: '3px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{linha}</div> : null}
          {nota ? <div style={{ display: 'inline-block', fontSize: '11.5px', fontWeight: 700, color: nc, background: nbg, borderRadius: '8px', padding: '4px 9px', marginTop: '7px', lineHeight: 1.4 }}>{nota}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}
function LinhaEsperaSM({ ic, titulo, linha, children }) {
  return (
    <div style={{ background: '#F7F8FA', border: '1px solid #EEF0F3', borderRadius: '14px', padding: '12px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <Ico n={ic} s={17} c="#9AA0AB" style={{ marginTop: '2px' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13.5px', fontWeight: 600, color: '#3D424C', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{titulo}</div>
        {linha ? <div className="num" style={{ fontSize: '12px', color: '#6E7480', marginTop: '2px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{linha}</div> : null}
        {children}
      </div>
    </div>
  );
}
function SecaoPendSM({ titulo, n, cinza = false, children }) {
  return (
    <section style={{ marginTop: '20px' }}>
      <h2 style={{ fontSize: '12px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: cinza ? '#6E7480' : '#16181D', margin: '0 2px 10px' }}>
        {titulo} · <span className="num">{n}</span>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>{children}</div>
    </section>
  );
}
function TudoEmDiaSM({ frase }) {
  return (
    <div style={{ background: 'white', border: '1px solid #E2E4E9', borderRadius: '16px', padding: '26px 16px', textAlign: 'center' }}>
      <Ico n="circle-check" s={40} c="#0E9F6E" />
      <div style={{ fontFamily: "'Sora', sans-serif", fontSize: '17px', fontWeight: 700, color: '#16181D', marginTop: '8px' }}>Tudo em dia</div>
      <div style={{ fontSize: '13px', color: '#6E7480', marginTop: '4px', lineHeight: 1.5 }}>{frase}</div>
    </div>
  );
}
function TopoPendSM({ subtitulo }) {
  return (
    <div style={{ marginBottom: '4px' }}>
      <h1 className="text-2xl font-bold text-gray-800">Pendências</h1>
      <p className="text-gray-500 mt-1" style={{ fontSize: '14px' }}>{subtitulo}</p>
    </div>
  );
}
// Banco ainda sem a funcao / falha sem lista: nao da' para dizer "tudo em dia" — mostra so' o historico.
function AvisoSemListaPendSM({ estado, onTentar }) {
  if (estado === 'carregando') return <div style={{ fontSize: '13px', color: '#6E7480', margin: '16px 2px' }}>Conferindo o que está pendente…</div>;
  if (estado !== 'erro') return null;
  return (
    <div style={{ fontSize: '13px', color: '#6E7480', margin: '16px 2px', lineHeight: 1.5 }}>
      Não deu para conferir as pendências agora.{' '}
      <button type="button" onClick={onTentar} style={{ border: 'none', background: 'none', color: '#2F6BFF', fontWeight: 700, cursor: 'pointer', minHeight: '44px', padding: '12px 4px', fontFamily: 'inherit', fontSize: '13px' }}>Tentar de novo</button>
    </div>
  );
}
// "Historico de avisos" recolhivel. Ao abrir a aba, comeca aberto quando nao ha nada "Sua vez" (ou
// sem lista) e fechado quando ha — decide UMA vez (a lista mudando depois nao abre/fecha sozinha
// enquanto a pessoa le). O conteudo monta JUNTO com a aba, mesmo fechado: a lista do professor marca
// os avisos como lidos ao montar e avisa o sininho do computador ('plen:avisos-lidos'), como na v133
// ao abrir a aba. Antes so' montava ao abrir o historico — com algo na "Sua vez" ele vinha fechado,
// nada era marcado como lido e o numero do sininho nao saia. O destaque "nova" continua (fica
// guardado na lista enquanto a aba esta' aberta).
function HistoricoAvisosSM({ decidido, abrir, children }) {
  const [aberto, setAberto] = React.useState(false);
  const decidiu = React.useRef(false);
  React.useEffect(() => {
    if (!decidido || decidiu.current) return;
    decidiu.current = true;
    setAberto(!!abrir);
  }, [decidido, abrir]);
  function alternar() {
    decidiu.current = true;
    const novo = !aberto;
    uso.acao('pendencias.historico', { aberto: novo });
    setAberto(novo);
  }
  return (
    <section style={{ marginTop: '24px' }}>
      <button type="button" onClick={alternar} aria-expanded={aberto}
        style={{ width: '100%', minHeight: '44px', display: 'flex', alignItems: 'center', gap: '8px', border: 'none', background: 'none', padding: '8px 2px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
        <Ico n="history" s={17} c="#6E7480" />
        <span style={{ flex: 1, fontSize: '12px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#6E7480' }}>Histórico de avisos</span>
        <Ico n={aberto ? 'chevron-up' : 'chevron-down'} s={16} c="#9AA0AB" />
      </button>
      <div style={{ display: aberto ? 'block' : 'none', marginTop: '6px' }}>{children}</div>
    </section>
  );
}

// ---- Remarcacao do professor (v136, opcao B) ------------------------------------------------
// Caso real (Marcus, 08/10): Cadu remarcou a aula dele (qui 12h -> 11h), valeu na hora e o aluno
// nao teve como dizer nada. Continua valendo na hora (o professor manda na agenda), mas o banco
// (remarcar_aula, sem pedido do aluno) grava em solicitacoes um registro tipo 'remarcacao_prof'
// esperando o aluno: [Ok, combinado] ou [Nao consigo] (+ mensagem opcional). A aula NUNCA volta
// sozinha para o horario antigo (ele pode nem existir mais): quem resolve a recusa e' o professor,
// remarcando de novo ou desmarcando.
// Campos, AO CONTRARIO do pedido do aluno: HORARIO NOVO em resposta_data/resposta_hora e o ANTIGO
// em data_sugerida/hora_sugerida. status: pendente | aceita | recusada | substituida (o professor
// remarcou de novo, ou a aula mudou antes da resposta).
// NAO e' pedido do aluno: toda lista de pedidos (Inicio do professor, "Meus pedidos", selo
// "Aguardando o professor" da aula, contraproposta) ignora este tipo.
const TIPO_REMARC_PROF_SM = 'remarcacao_prof';
const ehPedidoDoAlunoSM = s => !!s && s.tipo !== TIPO_REMARC_PROF_SM;
const hhmmSM = h => String(h || '').slice(0, 5);
// 'Qui 08/10 às 11h' no meio da frase: 'qui 08/10 às 11h'
const minuscSM = t => (t ? t.charAt(0).toLowerCase() + t.slice(1) : '');
// A remarcacao ainda vale: a aula esta' agendada, sem registro do professor, EXATAMENTE no horario
// novo (a mesma conta do banco em minhas_pendencias). 'pendente' (o aluno ainda pode responder): so'
// ate' a aula comecar. 'recusada' (o aluno disse "nao consigo"): MESMO depois do horario — senao a
// aula viraria so' "sem registro" e o professor poderia lancar falta de quem avisou antes; vale ate'
// ele remarcar, desmarcar ou registrar. Sem a aula em maos, nao mostra.
function remarcacaoValendoSM(s, aula, agora = new Date()) {
  if (!s || s.tipo !== TIPO_REMARC_PROF_SM || !aula || aula.status !== 'agendada') return false;
  if (aula.desfecho_prof || aula.divergencia_em) return false;
  if (String(aula.data || '').slice(0, 10) !== String(s.resposta_data || '').slice(0, 10)) return false;
  if (hhmmSM(aula.hora) !== hhmmSM(s.resposta_hora)) return false;
  if (s.status === 'recusada') return true;
  const ini = inicioAulaSP(aula);
  return !!ini && ini > agora;
}
// A aula ja' comecou (o "nao consigo" passou da hora: o caminho e' registrar "Eu desmarquei")
const aulaComecouSM = (aula, agora = new Date()) => { const ini = inicioAulaSP(aula); return !!ini && ini <= agora; };
// 'qui 08/10 · 12:00 → 11:00' (mesmo dia) / 'qui 08/10 · 12:00 → sex 09/10 · 11:00'
function deParaRemarcacaoSM(deData, deHora, paraData, paraHora) {
  const dia = d => {
    const x = new Date(String(d).slice(0, 10) + 'T12:00:00');
    return isNaN(x) ? '' : `${['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'][x.getDay()]} ${ddmmSM(d)}`;
  };
  if (!paraData) return '';
  const para = `${dia(paraData)}${paraHora ? ' · ' + hhmmSM(paraHora) : ''}`;
  if (!deData) return para;
  if (String(deData).slice(0, 10) === String(paraData).slice(0, 10) && deHora && paraHora) {
    return `${dia(deData)} · ${hhmmSM(deHora)} → ${hhmmSM(paraHora)}`;
  }
  return `${dia(deData)}${deHora ? ' · ' + hhmmSM(deHora) : ''} → ${para}`;
}

// Resposta do aluno pelo banco (aluno_responder_remarcacao: confere o dono, o tipo e se a aula
// ainda esta' no horario novo). O app so' traduz. Devolve { ok, data } | { ok:false, rede }.
// Antes do SQL nao ha' registro nenhum (os botoes nem aparecem); se mesmo assim chamar e a funcao
// nao existir (PGRST202), so' um aviso simples.
async function responderRemarcacaoAlunoSM(solicitacaoId, ok, mensagem, { origem = 'outro', nomeProf = 'Seu professor' } = {}) {
  if (!solicitacaoId) return { ok: false };
  const res = await comTravaAulaSM('remarc:' + solicitacaoId, async () => {
    const msg = ok ? null : (String(mensagem || '').trim().slice(0, 200) || null);
    let data = null, error = null;
    try {
      ({ data, error } = await supabase.rpc('aluno_responder_remarcacao', { p_solicitacao_id: solicitacaoId, p_ok: !!ok, p_mensagem: msg }));
    } catch (e) { error = e; }
    if (error) {
      if (/PGRST202|could not find the function/i.test([error.code, error.message].join(' '))) {
        uso.bloqueio('aluno_remarcacao', null, null, 'sem_funcao');
        toastSM(`Ainda não dá para responder por aqui. Combine com ${nomeProf}.`);
        return { ok: false };
      }
      uso.erro('erro.aluno_remarcacao', error.message || String(error));
      toastSM('Não foi possível responder agora. Confira a internet e tente de novo.');
      return { ok: false, rede: true };
    }
    if (!data || data.erro || !data.ok) {
      const cod = String((data && data.erro) || 'sem_resposta');
      uso.bloqueio('aluno_remarcacao', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      toastSM(cod === 'sem_permissao' ? 'Esta aula não está na sua ficha.'
        : cod === 'aluno_desvinculado' ? `Seu vínculo com ${nomeProf} foi encerrado.`
        : /nao_encontrad|invalid|tipo|fechad/.test(cod) ? 'Essa mudança não vale mais. A tela foi atualizada.'
        : 'Não foi possível responder agora. Tente de novo em instantes.');
      avisarPendenciasMudouSM();
      return { ok: false };
    }
    uso.acao('aluno.remarcacao_responder', { resposta: ok ? 'ok' : 'nao_consigo', origem, com_msg: !!msg,
      ja_feito: !!data.ja_feito, ja_mudou: !!data.ja_mudou });
    avisarPendenciasMudouSM();
    // ja_mudou: o banco nao diz qual (remarcou de novo, desmarcou, registrou ou a aula comecou) —
    // a frase vale para todos. Sem genero para o professor (o app nao sabe se e' professora)
    if (data.ja_feito || data.ja_mudou) toastSM(data.ja_feito ? 'Você já tinha respondido essa mudança.'
      : 'Não dá mais para responder: a aula mudou, foi desmarcada ou já começou. A tela foi atualizada.');
    // v142: "Feito · Desfazer" (A4 / A5) — a pergunta "Consegue?" volta (ate' a aula comecar). Ref = o pedido.
    else toastDesfazerSM(ok ? 'Combinado.' : `Avisamos ${nomeProf}, que vai remarcar ou desmarcar.`,
      { ref_tipo: 'solicitacao', ref_id: solicitacaoId, esperado: ok ? 'ok_remarcacao' : 'nao_consigo' },
      { papel: 'aluno', nomeOutro: nomeProf, curto: ok ? '"Ok, combinado"' : '"Não consigo"' });
    return { ok: true, data };
  });
  return res || { ok: false };   // toque duplo: a trava devolve null
}

// [Ok, combinado] [Nao consigo] — os mesmos no Inicio do aluno e em Pendencias. "Nao consigo" abre
// um campo opcional antes de enviar (vai junto do aviso ao professor). onFeito(res): quem mostra
// tira da lista e recarrega (falha de rede nao chama: o cartao fica para tentar de novo).
function BotoesRemarcacaoSM({ solicitacaoId, nomeProf, origem, onFeito }) {
  const [aberto, setAberto] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [busy, setBusy] = React.useState(null);   // 'ok' | 'nao' enquanto o banco responde
  async function responder(ok) {
    if (busy) return;
    setBusy(ok ? 'ok' : 'nao');
    let res = null;
    try { res = await responderRemarcacaoAlunoSM(solicitacaoId, ok, msg, { origem, nomeProf }); } finally { setBusy(null); }
    if (res && res.ok) { setAberto(false); setMsg(''); }
    if (res && !res.rede && onFeito) onFeito(res);
  }
  const off = !!busy;
  if (aberto) {
    return (
      <div style={{ marginTop: '10px' }}>
        <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#3D424C', marginBottom: '6px' }}>
          Quer dizer algo? (opcional)
          <textarea value={msg} maxLength={200} rows={2} onChange={e => setMsg(e.target.value)} placeholder="Ex.: nesse horário eu trabalho"
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '6px', border: '1.5px solid #E2E4E9', borderRadius: '10px',
                     padding: '10px 12px', fontSize: '16px', fontWeight: 400, fontFamily: 'inherit', resize: 'none', outline: 'none', background: 'white', color: '#16181D' }} />
        </label>
        <BotoesPendSM>
          <button type="button" disabled={off} style={btPendSM('secundario')} onClick={() => { setAberto(false); setMsg(''); }}>Voltar</button>
          <button type="button" disabled={off} style={{ ...btPendSM(), opacity: off ? 0.6 : 1 }} onClick={() => responder(false)}>
            {busy === 'nao' ? 'Enviando…' : 'Enviar'}
          </button>
        </BotoesPendSM>
      </div>
    );
  }
  return (
    <BotoesPendSM>
      <button type="button" disabled={off} style={{ ...btPendSM(), opacity: off ? 0.6 : 1 }} onClick={() => responder(true)}>
        {busy === 'ok' ? 'Salvando…' : <><Ico n="check" s={15} /> Ok, combinado</>}
      </button>
      <button type="button" disabled={off} style={btPendSM('secundario')} onClick={() => setAberto(true)}>Não consigo</button>
    </BotoesPendSM>
  );
}

// Inicio do aluno: "Cadu mudou sua aula", acima do cartao "Hoje" — mesmo visual da faixa "Confirme
// 1 aula". itens: as remarcacoes da ficha aberta que ainda valem (remarcacaoValendoSM).
function FaixaRemarcacaoAlunoSM({ itens, nomeProf, onFeito }) {
  if (!itens || !itens.length) return null;
  const n = itens.length;
  return (
    <div style={{ background: '#E7EDFF', border: '2px solid #C2D4FF', borderRadius: '16px', padding: '14px 16px' }}>
      <p style={{ fontSize: '13px', fontWeight: 800, color: '#1C43AE', marginBottom: '4px' }}>
        <Ico n="calendar-event" s={15} c="#2F6BFF" /> {n === 1 ? `${nomeProf} mudou sua aula` : `${nomeProf} mudou ${n} das suas aulas`}
      </p>
      <p style={{ fontSize: '12px', color: '#3D5AC4', marginBottom: '10px', lineHeight: 1.5 }}>
        {n === 1 ? 'O horário novo já vale.' : 'Os horários novos já valem.'} Se não puder, diga aqui — {nomeProf} remarca ou desmarca.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {itens.map(s => (
          <div key={s.id} style={{ background: 'white', border: '1px solid #C2D4FF', borderRadius: '12px', padding: '10px 12px' }}>
            <div className="num" style={{ fontSize: '13px', fontWeight: 700, color: '#16181D' }}>
              {deParaRemarcacaoSM(s.data_sugerida, s.hora_sugerida, s.resposta_data, s.resposta_hora)}
            </div>
            <BotoesRemarcacaoSM solicitacaoId={s.id} nomeProf={nomeProf} origem="inicio" onFeito={res => onFeito && onFeito(s, res)} />
          </div>
        ))}
      </div>
    </div>
  );
}

// v140: "O que aconteceu?" — o "Nao aconteceu" do aluno separa as duas coisas que antes eram uma so':
// [Eu nao fui] (falta dele, o professor fecha com o motivo) e [Cadu nao veio] (aluno_motivo_nao =
// 'prof_nao_veio': o professor e' avisado e, calado 24h, a aula volta ao pacote). O "nao veio" so'
// libera 15 min depois do inicio (atraso acontece). opcoes: quais botoes ('fiz' | 'eu_nao_fui' |
// 'nao_veio'), na ordem. escuro: dentro do cartao azul "Hoje".
// aulaRef (opcional): a aula — passadas 24h do fim, "{Prof} nao veio" sai (o banco recusa: tarde_demais).
function EscolhaNaoAconteceuSM({ nomeProf = 'O professor', liberaNaoVeioEm = null, onEscolher, onVoltar, disabled = false, escuro = false,
                                 opcoes: opcoesPedidas = ['eu_nao_fui', 'nao_veio'], titulo = 'O que aconteceu?', aulaRef = null }) {
  const libera = liberaNaoVeioEm ? new Date(liberaNaoVeioEm) : null;
  const cedo = !!libera && !isNaN(libera) && libera > new Date();
  const vencido = !!aulaRef && opcoesPedidas.includes('nao_veio') && naoVeioVencidoSM(aulaRef);
  const opcoes = vencido ? opcoesPedidas.filter(k => k !== 'nao_veio') : opcoesPedidas;
  const rotulo = { fiz: 'Fiz a aula', eu_nao_fui: 'Eu não fui', nao_veio: cedo ? `${nomeProf} não veio — a partir das ${horaSP(libera)}` : `${nomeProf} não veio` };
  const st = (i, off) => (escuro
    ? { flex: '1 1 120px', minHeight: '44px', borderRadius: '12px', fontSize: '14px', fontWeight: 800, cursor: off ? 'not-allowed' : 'pointer', fontFamily: 'inherit', padding: '10px 12px', lineHeight: 1.2,
        border: i === 0 ? 'none' : '1.5px solid rgba(255,255,255,0.6)', background: i === 0 ? 'white' : 'transparent', color: i === 0 ? '#1C43AE' : 'white', opacity: off ? 0.55 : 1 }
    : { ...btPendSM(i === 0 ? 'primario' : 'secundario'), opacity: off ? 0.55 : 1, cursor: off ? 'not-allowed' : 'pointer' });
  return (
    <div style={{ marginTop: '10px' }}>
      {titulo ? <div style={{ fontSize: '13px', fontWeight: 800, color: escuro ? 'white' : '#16181D', marginBottom: '2px' }}>{titulo}</div> : null}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '6px' }}>
        {opcoes.map((k, i) => {
          const off = disabled || (k === 'nao_veio' && cedo);
          return (
            <button key={k} type="button" disabled={off} onClick={() => !off && onEscolher && onEscolher(k)} style={st(i, off)}>{rotulo[k]}</button>
          );
        })}
      </div>
      {vencido ? (
        <div style={{ fontSize: '11.5px', color: escuro ? 'rgba(255,255,255,.85)' : '#6E7480', marginTop: '6px', lineHeight: 1.45 }}>
          Já passou mais de 1 dia desta aula: para dizer que {nomeProf} não veio, fale com {nomeProf}.
        </div>
      ) : null}
      {onVoltar ? (
        <button type="button" onClick={onVoltar} disabled={disabled}
          style={{ marginTop: '4px', minHeight: '44px', border: 'none', background: 'none', padding: '0 2px', color: escuro ? 'rgba(255,255,255,.85)' : '#6E7480', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
          Voltar
        </button>
      ) : null}
    </div>
  );
}

// v140: Inicio do aluno — "Cadu perguntou se você vai" (a pergunta "vai?" das aulas que nao sao de
// hoje; a de hoje aparece no proprio cartao "Hoje"). [Vou] responde aqui; [Nao vou conseguir] abre o
// aviso de falta de sempre (no prazo: cancelar; fora: avisar). prazoTexto(a): a consequencia ANTES.
function FaixaPresencaAlunoSM({ itens, nomeProf, prazoTexto, onFeito, onNaoVou }) {
  const [busy, setBusy] = React.useState(null);
  if (!itens || !itens.length) return null;
  const n = itens.length;
  async function vou(a) {
    if (busy) return;
    setBusy(a.id);
    let r = null;
    try { r = await responderPresencaAlunoSM(a.id, { origem: 'faixa', nomeProf }); } finally { setBusy(null); }
    if (r && !r.rede && onFeito) onFeito(a, r);
  }
  return (
    <div style={{ background: '#E7EDFF', border: '2px solid #C2D4FF', borderRadius: '16px', padding: '14px 16px' }}>
      <p style={{ fontSize: '13px', fontWeight: 800, color: '#1C43AE', marginBottom: '4px' }}>
        <Ico n="calendar-question" s={15} c="#2F6BFF" /> {n === 1 ? `${nomeProf} perguntou se você vai` : `${nomeProf} perguntou se você vai a ${n} aulas`}
      </p>
      <p style={{ fontSize: '12px', color: '#3D5AC4', marginBottom: '10px', lineHeight: 1.5 }}>Tudo certo para a aula? Responda com um toque.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {itens.map(a => {
          const pz = prazoTexto ? prazoTexto(a) : '';
          return (
            <div key={a.id} style={{ background: 'white', border: '1px solid #C2D4FF', borderRadius: '12px', padding: '10px 12px' }}>
              <div className="num" style={{ fontSize: '13px', fontWeight: 700, color: '#16181D' }}>{diaCurtoSM(a.data)} · {hhmmSM(a.hora)}</div>
              {pz ? <div style={{ fontSize: '12px', color: '#6E7480', marginTop: '3px', lineHeight: 1.45 }}>{pz}</div> : null}
              <BotoesPendSM>
                <button type="button" disabled={!!busy} style={{ ...btPendSM(), opacity: busy ? 0.6 : 1 }} onClick={() => vou(a)}>
                  {busy === a.id ? 'Salvando…' : <><Ico n="check" s={15} /> Vou</>}
                </button>
                <button type="button" disabled={!!busy} style={btPendSM('secundario')} onClick={() => onNaoVou && onNaoVou(a)}>Não vou</button>
              </BotoesPendSM>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// v142 (B): o "Vou / Nao vou" no cartao da aula do aluno (cartoes "Hoje" e "Proxima aula", fundo azul).
// mostrar: [✓ Vou] em destaque e [Nao vou] secundario — com ou sem a pergunta do professor (hoje ou
// amanha, antes do inicio, aula individual). consequencia: so' quando o professor perguntou (como na
// v140); sem pergunta o prazo aparece no [Nao vou], na folha do aviso de falta, antes de confirmar.
// feito: depois do "Vou" — so' o "Desfazer" discreto (o "Voce avisou que vai ✓" fica na linha de cima).
function BlocoVouAlunoSM({ aula, nomeProf, mostrar, feito, consequencia, busy, onVou, onNaoVou, onDesfeito }) {
  if (feito) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: '4px' }}>
      <BotaoDesfazerSM escuro alvo={{ ref_tipo: 'aula', ref_id: aula.id, esperado: 'vou' }} papel="aluno" nomeOutro={nomeProf}
        origem="cartao" onFeito={onDesfeito} />
    </div>
  );
  if (!mostrar) return null;
  return (
    <div style={{ marginTop: '12px' }}>
      {consequencia ? <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,.9)', lineHeight: 1.45, marginBottom: '8px' }}>{consequencia}</div> : null}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button type="button" disabled={!!busy} onClick={onVou}
          style={{ flex: '1 1 120px', minHeight: '44px', borderRadius: '12px', border: 'none', background: 'white', color: '#1C43AE', fontSize: '14px', fontWeight: 800,
                   cursor: 'pointer', opacity: busy ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontFamily: 'inherit' }}>
          {busy === aula.id ? 'Salvando…' : <><Ico n="check" s={16} c="#1C43AE" /> Vou</>}
        </button>
        <button type="button" disabled={!!busy} onClick={onNaoVou}
          style={{ flex: '1 1 120px', minHeight: '44px', borderRadius: '12px', border: '1.5px solid rgba(255,255,255,0.6)', background: 'transparent', color: 'white',
                   fontSize: '14px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
          Não vou
        </button>
      </div>
    </div>
  );
}

// Onde se resolve, quando o tipo e' novo para o app: pelo que o item aponta.
function destinoPadraoProfSM(item) {
  switch (item.ref_tipo) {
    case 'aula': return ['aulas', { aula_id: item.ref_id }];
    case 'pagamento': return ['financeiro', item.aluno_id ? { aluno_id: item.aluno_id } : {}];
    case 'contrato': case 'aluno': return ['alunos', item.aluno_id ? { aluno_id: item.aluno_id } : {}];
    case 'solicitacao': return ['dashboard', { solicitacao_id: item.ref_id }];
    default: return ['dashboard', {}];
  }
}

// ---- Professor ----
function PendenciasProfessor({ professor, onNavigate, pend }) {
  React.useEffect(() => { pend.recarregar && pend.recarregar(); }, []);
  const [confirmar, setConfirmar] = React.useState(null);   // cartao com o "Confirmar que a aula foi concluida?" aberto
  const [ocupado, setOcupado] = React.useState(null);
  const [presenca, setPresenca] = React.useState(null);     // sessao da turma aberta na janela de presenca
  const ok = pend.estado === 'ok';
  const lado = (ok && pend.professor) || { sua_vez: [], esperando: [] };
  const chave = i => `${i.tipo}:${i.ref_tipo}:${i.ref_id}`;
  const ir = (item, tela, params = {}) => {
    uso.acao('pendencias.resolver', { tipo: tipoUsoPendSM(item.tipo), via: 'navegar' });
    onNavigate && onNavigate(tela, params);
  };
  const feito = item => { uso.acao('pendencias.resolver', { tipo: tipoUsoPendSM(item.tipo), via: 'inline' }); pend.recarregar(); };

  // v155 (teste de 29/10): "Quero renovar" / "Ja' paguei o proximo" do aluno ficava so' no Historico de avisos, fechado
  // no fim da tela (no celular nao ha' sininho) — o professor nao via. Agora vem no topo da "Sua vez" enquanto o pacote
  // de onde o aluno pediu continua o atual e nao ha' proximo combinado; combinou (ou o pacote mudou), some sozinho.
  const [pedidosRenovar, setPedidosRenovar] = React.useState([]);
  React.useEffect(() => {
    let vivo = true;
    (async () => {
      const desde = new Date(Date.now() - 45 * 86400000).toISOString();
      const { data: ns } = await supabase.from('notificacoes').select('id, aluno_id, ref_id, mensagem, created_at')
        .eq('professor_id', professor.id).eq('tipo', 'ajuste_contrato').eq('ref_tipo', 'contrato')
        .gte('created_at', desde).order('created_at', { ascending: false }).limit(50);
      const porAluno = {};
      (ns || []).forEach(n => { if (n.aluno_id && n.ref_id && !porAluno[n.aluno_id]) porAluno[n.aluno_id] = n; });
      const ids = Object.keys(porAluno);
      if (!ids.length) { if (vivo) setPedidosRenovar([]); return; }
      const [{ data: cts }, { data: als }] = await Promise.all([
        supabase.from('contratos').select('id, aluno_id, status').in('aluno_id', ids).in('status', ['ativo', 'proximo']),
        supabase.from('alunos').select('id, nome').in('id', ids),
      ]);
      const nomes = Object.fromEntries((als || []).map(a => [a.id, a.nome]));
      const lista = ids.map(id => porAluno[id]).filter(n => {
        const dele = (cts || []).filter(k => k.aluno_id === n.aluno_id);
        return !dele.some(k => k.status === 'proximo') && dele.some(k => k.id === n.ref_id && k.status === 'ativo');
      }).map(n => ({ ...n, nome: nomes[n.aluno_id] || '' }));
      if (vivo) setPedidosRenovar(lista);
    })().catch(e => uso.erro('erro.pend_renovar', e?.message || String(e)));
    return () => { vivo = false; };
  }, [pend.professor]);
  function cartaoRenovar(n) {
    const P = primeiroNomeSM(n.nome, 'O aluno');
    const pagou = renovarJaPagouSM(n);
    return (
      <CartaoPendSM key={'renovar:' + n.id} ic="refresh" cor="azul" titulo={pagou ? `${P} disse que já pagou o próximo pacote` : `${P} quer renovar o pacote`}
        linha={`Pediu ${desdeSM(n.created_at)}`} nota={pagou ? 'Combine o próximo pacote: o "Já pagou" vem marcado' : 'Combine o próximo pacote — começa quando o atual terminar'} notaCor="cinza">
        <BotoesPendSM>
          <button type="button" style={btPendSM()} onClick={() => { uso.acao('pendencias.resolver', { tipo: 'pedido_renovacao', via: 'navegar' });
            onNavigate && onNavigate('alunos', { renovar_aluno_id: n.aluno_id, renovar_ja_pagou: pagou, t: Date.now() }); }}>Combinar próximo pacote</button>
        </BotoesPendSM>
      </CartaoPendSM>
    );
  }

  async function concluir(item) {
    if (ocupado) return;
    setOcupado(chave(item));
    const est = await aplicarDesfechoProfessor(aulaDoItemSM(item), 'realizada',
      { forcar: false, nomeAluno: item.nome, origem: 'pendencias', alunoSemApp: item.detalhe.aluno_usa_app === false });
    setOcupado(null); setConfirmar(null);
    if (est) feito(item);
  }
  async function responderProposta(item, aceita) {
    if (ocupado) return;
    setOcupado(chave(item));
    const r = await responderPropostaProf(aulaDoItemSM(item), aceita, { origem: 'pendencias', nomeAluno: item.nome });
    setOcupado(null);
    if (r) feito(item);
  }
  // Presenca da turma: a MESMA janela do Inicio, com as aulas da sessao que o banco mandou. Sem a
  // lista de alunos no detalhe, leva ao Inicio (o cartao "Aulas da turma" esta' la').
  function abrirPresenca(item) {
    const d = item.detalhe;
    const aulas = (Array.isArray(d.aulas) ? d.aulas : []).filter(a => a && a.id && a.aluno_id);
    if (!aulas.some(a => a.status === 'agendada')) { ir(item, 'dashboard'); return; }
    const p = partesQuandoSM(item.quando);
    setPresenca({ item, sessao: { sessao_id: item.ref_id, turma_id: d.turma_id || null, turma_nome: item.nome || 'Turma',
      data: d.data || p.data, hora: d.hora || p.hora || '', aulas: aulas.map(a => ({ ...a, nome: a.nome || 'Aluno' })) } });
  }

  function cartao(item) {
    const d = item.detalhe, k = chave(item), P = primeiroNomeSM(item.nome, 'Aluno');
    const quando = quandoPendSM(item.quando);
    const valor = item.valor != null && Number(item.valor) > 0 ? fmtBRL(item.valor) : '';
    const busy = ocupado === k;
    const resolver = (rotulo = 'Resolver') => {
      const [tela, params] = destinoPadraoProfSM(item);
      return <BotoesPendSM><button type="button" style={btPendSM()} onClick={() => ir(item, tela, params)}>{rotulo}</button></BotoesPendSM>;
    };
    switch (item.tipo) {
      case 'aula_sem_fechar': {
        // aula reaberta porque o aluno contestou o fechamento: o texto dele vem no detalhe (o pedido
        // 'outro' nao aparece como pedido — quem aparece e' a propria aula)
        const contestou = d.contestacao ? String(d.contestacao).replace(/^Contestação do fechamento automático:\s*/i, '').trim() : '';
        // v140: o aluno disse que VOCE nao veio (aluno_motivo; aluno_disse continua 'nao_foi' para o app antigo)
        const naoVeio = !d.contestacao && d.aluno_motivo === 'prof_nao_veio';
        const volta = naoVeio && d.volta_ao_pacote_em ? new Date(d.volta_ao_pacote_em) : null;
        const disse = d.contestacao ? `${P} contestou${contestou ? `: "${contestou.slice(0, 90)}${contestou.length > 90 ? '…' : ''}"` : ' o registro'}`
          : naoVeio ? `${P} diz que você não veio${volta && !isNaN(volta) ? ` · ${minuscSM(textoVoltaNaoVeioSM(volta, { semSaldo: !!d.sem_saldo })).replace(/\.$/, '')}` : ''}`
          : d.aluno_disse === 'aconteceu' ? `${P} confirmou que aconteceu` : d.aluno_disse === 'nao_foi' ? `${P} disse que não foi` : null;
        const abriu = confirmar === k;
        // o aluno disse que nao foi: "Concluida" nao fecha — vira divergencia (professor_fechar_aula),
        // entao o caminho principal e' o "Nao houve"
        const naoFoi = d.aluno_disse === 'nao_foi' || naoVeio;
        // a consequencia ANTES de confirmar (mesmo texto do Inicio): em aula e em reais
        const vAula = Number(d.valor_aula) > 0 ? ` (${fmtBRL(d.valor_aula)})` : '';
        const efeito = naoVeio ? 'Fica registrado diferente e a aula não desconta até vocês acertarem.'
          : d.sem_saldo ? 'Na mensalidade nada muda no saldo.'
          : naoFoi ? `${P} disse que não foi. Se confirmar como dada, fica registrado diferente e a aula não desconta até vocês acertarem.`
          : d.contestacao ? `${P} contestou o fechamento. Confirmando como dada, a aula volta a abater 1 aula do pacote${vAula} — e fecha em seguida, porque o prazo de 24h já passou.`
          : d.aluno_disse === 'aconteceu' ? `${P} já confirmou: a aula fecha agora e abate 1 aula do pacote${vAula}.`
          : d.aluno_usa_app === false ? `Vai abater 1 aula do pacote de ${P}${vAula}. ${P} ainda não entrou no app — vale sozinha em 24h.`
          : `Vai abater 1 aula do pacote de ${P}${vAula} quando ${P} confirmar no app — ou em 24h, se não responder.`;
        // "Nao houve": o Inicio ja' com os motivos DESTA aula abertos (furou, avisou, eu desmarquei, imprevisto).
        // Contestada: vai para a tela Aulas, no dia dela. Ate' a v140 o registro antigo do professor continuava
        // gravado e o Inicio nao listava a aula ("Desfazer" la' limpava o registro). Com o SQL da v141 a
        // contestacao ja' apaga o registro; a tela Aulas continua servindo nos dois casos (e se a VOLTA rodar).
        const naoHouve = () => d.contestacao ? ir(item, 'aulas', { aula_id: item.ref_id, t: Date.now() })
          : ir(item, 'dashboard', { aula_id: item.ref_id, nao_houve: true, t: Date.now() });
        return (
          <CartaoPendSM key={k} ic={naoVeio ? 'user-exclamation' : 'clock-exclamation'} cor="vermelho" titulo={`Fechar a aula de ${P}`}
            linha={`${quando} · ${d.contestacao ? `reaberta: ${P} contestou o registro` : 'terminou sem registro'}`}
            nota={disse} notaCor={naoVeio ? 'vermelho' : !d.contestacao && d.aluno_disse === 'aconteceu' ? 'verde' : 'ambar'}>
            {abriu ? (
              <div style={{ marginTop: '12px', borderTop: '1px solid #F0F1F4', paddingTop: '10px' }}>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#16181D' }}>Confirmar que a aula foi concluída?</div>
                <div style={{ fontSize: '12.5px', color: '#6E7480', marginTop: '3px', lineHeight: 1.5 }}>{efeito}</div>
                <BotoesPendSM>
                  <button type="button" disabled={busy} style={btPendSM('secundario')} onClick={() => setConfirmar(null)}>Voltar</button>
                  <button type="button" disabled={busy} style={{ ...btPendSM(), opacity: busy ? 0.6 : 1 }} onClick={() => concluir(item)}>{busy ? 'Salvando…' : 'Confirmar'}</button>
                </BotoesPendSM>
              </div>
            ) : naoFoi ? (
              <BotoesPendSM>
                <button type="button" style={btPendSM()} onClick={naoHouve}>Não houve</button>
                <button type="button" style={btPendSM('secundario')} onClick={() => setConfirmar(k)}><Ico n="check" s={15} /> Concluída</button>
              </BotoesPendSM>
            ) : (
              <BotoesPendSM>
                <button type="button" style={btPendSM()} onClick={() => setConfirmar(k)}><Ico n="check" s={15} /> Concluída</button>
                <button type="button" style={btPendSM('secundario')} onClick={naoHouve}>Não houve</button>
              </BotoesPendSM>
            )}
          </CartaoPendSM>
        );
      }
      case 'turma_sem_fechar': {
        const nAbertas = Number(d.abertas) > 0 ? ` · ${d.abertas} sem presença` : '';
        return (
          <CartaoPendSM key={k} ic="users-group" cor="vermelho" titulo={`Presença da turma ${item.nome || ''}`.trim()} linha={`${quando}${nAbertas}`}>
            <BotoesPendSM><button type="button" style={btPendSM()} onClick={() => abrirPresenca(item)}>Fazer a presença</button></BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'aula_divergencia': {
        const voce = d.desfecho_prof === 'falta' ? 'falta' : 'aula dada';
        // v140: aluno_motivo = 'prof_nao_veio' (quando o banco manda)
        const ele = d.aluno_disse === 'aconteceu' ? 'aconteceu' : d.aluno_motivo === 'prof_nao_veio' ? 'você não veio' : 'não foi';
        return (
          <CartaoPendSM key={k} ic="arrows-diff" cor="vermelho" titulo={`Vocês registraram diferente — ${P}`}
            linha={`${quando} · você: ${voce} · ${P}: ${ele}`} nota="A aula não desconta até você decidir" notaCor="ambar">
            {/* aula de turma nao aparece na faixa do Inicio (e a presenca nao resolve divergencia): vai a' tela Aulas, no dia dela */}
            <BotoesPendSM>
              <button type="button" style={btPendSM()} onClick={() => d.sessao_id ? ir(item, 'aulas', { aula_id: item.ref_id }) : ir(item, 'dashboard')}>Decidir</button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'aviso_aluno_decidir': {
        const canal = d.aviso_canal === 'professor_registrou' ? ' (você registrou)' : '';
        const motivo = MOTIVO_AVISO_PROF_SM[d.motivo_cancelamento] ? ` · ${MOTIVO_AVISO_PROF_SM[d.motivo_cancelamento]}` : '';
        return (
          <CartaoPendSM key={k} ic="bell-ringing" cor="ambar" titulo={`${P} avisou que não vem — fora do prazo`}
            linha={`Aula de ${quando} · avisou ${desdeSM(item.desde, true)}${canal}${motivo}`}>
            <BotoesDecidirAviso aula={aulaDoItemSM(item)} nomeAluno={item.nome} podeDevolver={d.pode_devolver !== false}
              origem="pendencias" onFeito={() => feito(item)} />
            {/* v142 (P3): so' o aviso que VOCE registrou (o do aluno pelo app, nao) — enquanto o aluno nao responde */}
            {d.aviso_canal === 'professor_registrou' ? (
              // previaSilenciosa (revisao v142): some se o aluno ja' respondeu sobre a aula depois do aviso
              <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: item.ref_id, esperado: 'aviso_registrado' }} papel="professor" nomeOutro={item.nome}
                previaSilenciosa onFeito={() => pend.recarregar()} />
            ) : null}
          </CartaoPendSM>
        );
      }
      case 'pedido_aluno': {
        const t = d.tipo_pedido || d.tipo;   // o banco manda tipo_pedido (o 'tipo' do item e' o da pendencia)
        const de = d.aula_data ? quandoCurtoSM(d.aula_data, d.aula_hora) : quando;
        const titulo = t === 'reagendamento' ? `${P} pediu para remarcar`
          : t === 'pausa' ? `${P} pediu uma pausa`
          : t === 'ajuste_contrato' ? `${P} pediu correção no acordo`
          : t === 'cancelamento' ? `${P} pediu para cancelar uma aula`
          : `${P} mandou um pedido`;
        const msg = d.mensagem ? `"${String(d.mensagem).slice(0, 90)}${String(d.mensagem).length > 90 ? '…' : ''}"` : '';
        const linha = t === 'reagendamento' && d.data_sugerida ? `${de} → ${quandoCurtoSM(d.data_sugerida, d.hora_sugerida || d.aula_hora)}`
          : t === 'pausa' && (d.periodo_inicio || d.periodo_fim) ? periodoPendSM(d.periodo_inicio, d.periodo_fim)
          : [de, msg].filter(Boolean).join(' · ');
        return (
          <CartaoPendSM key={k} ic={t === 'pausa' ? 'player-pause' : t === 'ajuste_contrato' ? 'pencil' : t === 'reagendamento' ? 'calendar-repeat' : 'message-report'}
            cor="ambar" titulo={titulo} linha={linha} nota={item.desde ? `Pediu ${desdeSM(item.desde)}` : null}>
            <BotoesPendSM><button type="button" style={btPendSM()} onClick={() => ir(item, 'dashboard', { solicitacao_id: item.ref_id })}>Responder</button></BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'remarcacao_recusada': {
        // v136: voce remarcou e o aluno disse que nao consegue. A aula continua no horario novo ate'
        // voce remarcar de novo ou desmarcar (os dois levam a' tela Aulas, no dia dela, com a janela
        // certa aberta). Remarcou/desmarcou/registrou: some sozinha. Passou do horario sem nada disso
        // (comecou): o cartao CONTINUA — e o caminho e' "Eu desmarquei" (nao desconta), nunca "falta"
        // de quem avisou antes. para_data/para_hora vao junto: se a aula ja' mudou, a tela Aulas so'
        // mostra o dia (nao abre a janela para o horario errado).
        const pq = partesQuandoSM(item.quando);
        const pd = d.para_data || pq.data, ph = d.para_hora || pq.hora;
        const txt = d.mensagem ? String(d.mensagem) : '';
        const recado = txt ? `"${txt.slice(0, 120)}${txt.length > 120 ? '…' : ''}"` : `${P} respondeu no app, sem mensagem.`;
        const quandoTxt = minuscSM(quandoCurtoSM(pd, ph));
        const irAula = acao => ir(item, 'aulas', d.aula_id
          ? { aula_id: d.aula_id, [acao]: true, para_data: pd || null, para_hora: ph || null, t: Date.now() } : {});
        if (d.comecou) {
          return (
            <CartaoPendSM key={k} ic="calendar-x" cor="ambar" titulo={`${P} avisou que não conseguia ${quandoTxt || 'no novo horário'}`}
              linha={recado} nota={`A aula ficou sem registro. Se não aconteceu, registre "Eu desmarquei" — não desconta de ${P}.`} notaCor="ambar">
              <BotoesPendSM>
                <button type="button" style={btPendSM()} onClick={() => irAula('desmarcar')}>Eu desmarquei</button>
                <button type="button" style={btPendSM('secundario')} onClick={() => irAula('remarcar')}>Remarcar</button>
              </BotoesPendSM>
            </CartaoPendSM>
          );
        }
        return (
          <CartaoPendSM key={k} ic="calendar-x" cor="ambar" titulo={`${P} não consegue ${quandoTxt || 'no novo horário'}`}
            linha={recado} nota="A aula continua nesse horário até você remarcar ou desmarcar" notaCor="ambar">
            <BotoesPendSM>
              <button type="button" style={btPendSM()} onClick={() => irAula('remarcar')}>Remarcar</button>
              <button type="button" style={btPendSM('secundario')} onClick={() => irAula('desmarcar')}>Desmarcar</button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'aula_proposta_aluno':
        return (
          <CartaoPendSM key={k} ic="calendar-plus" cor="azul" titulo={`${P} lançou uma aula combinada`}
            linha={`${quando}${d.observacao ? ` · "${String(d.observacao).slice(0, 60)}"` : ''}`}
            nota={d.passou ? 'O dia já passou' : null} notaCor="ambar">
            <BotoesPendSM>
              <button type="button" disabled={busy} style={{ ...btPendSM(), opacity: busy ? 0.6 : 1 }} onClick={() => responderProposta(item, true)}>{busy ? '…' : 'Confirmar'}</button>
              <button type="button" disabled={busy} style={btPendSM('secundario')} onClick={() => responderProposta(item, false)}>Não combinamos isso</button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      case 'pagamento_declarado':
        return (
          <CartaoPendSM key={k} ic="coin" cor="verde" titulo={`${P} disse que pagou`} linha={[valor, item.desde ? `avisou ${desdeSM(item.desde)}` : ''].filter(Boolean).join(' · ')}
            nota={!d.forma_recebimento || d.forma_recebimento === 'pix_direto' ? 'Confira se o Pix caiu' : 'Confira se recebeu'} notaCor="cinza">
            <BotoesPendSM>
              <button type="button" style={btPendSM()} onClick={() => ir(item, 'financeiro', { aluno_id: item.aluno_id, confirmar_pagamento_id: item.ref_id })}>Confirmar recebimento</button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      case 'cobranca_vencida': {
        const venc = d.vencimento || partesQuandoSM(item.quando).data;
        const dias = Number(d.dias_atraso) > 0 ? ` (há ${d.dias_atraso} dia${Number(d.dias_atraso) === 1 ? '' : 's'})` : '';
        return (
          <CartaoPendSM key={k} ic="alert-circle" cor="vermelho" titulo={`Cobrança de ${P} vencida`}
            linha={[valor, venc ? `venceu ${ddmmSM(venc)}${dias}` : ''].filter(Boolean).join(' · ')}
            nota={d.acordo_aceito === false ? `O acordo de ${P} ainda não foi aceito` : null} notaCor="ambar">
            <BotoesPendSM>
              <button type="button" style={btPendSM()} onClick={() => ir(item, 'financeiro', { aluno_id: item.aluno_id })}>Cobrar</button>
              <button type="button" style={btPendSM('secundario')} onClick={() => ir(item, 'financeiro', { aluno_id: item.aluno_id, confirmar_pagamento_id: item.ref_id })}>Já recebi</button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'pacote_fim': {
        const passou = d.motivo === 'passou_a_data';
        const usou = Number(d.total) === 1 ? 'Usou a aula do pacote' : `Usou as ${d.total} aulas`;
        const linha = !passou && Number(d.total) > 0 ? usou : (d.data_fim || item.quando) ? `Terminou em ${ddmmSM(d.data_fim || partesQuandoSM(item.quando).data)}` : '';
        // "Nao vai renovar": abre o "Encerrar com este aluno" da tela Alunos (a consequencia aparece
        // antes de confirmar). Sem isso, pacote que nao vai ser renovado ficava na vez para sempre.
        return (
          <CartaoPendSM key={k} ic="refresh" cor="ambar" titulo={passou ? `O acordo de ${P} venceu` : `O pacote de ${P} acabou`} linha={[linha, valor].filter(Boolean).join(' · ')}>
            <BotoesPendSM>
              <button type="button" style={btPendSM()} onClick={() => ir(item, 'alunos', { renovar_aluno_id: item.aluno_id, t: Date.now() })}>Renovar</button>
              <button type="button" style={btPendSM('secundario')} onClick={() => ir(item, 'alunos', { encerrar_aluno_id: item.aluno_id, t: Date.now() })}>Não vai renovar</button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'acordo_corrigir': {
        // o aluno nao consegue aceitar: as cobrancas lancadas passam do total do acordo (o banco recusa
        // o aceite). Corrige no Financeiro (cancelar a cobranca a mais) — o aluno ve "Acordo em revisao".
        const cobrado = Number(d.total_cobrado) > 0 ? fmtBRL(d.total_cobrado) : '';
        const total = Number(d.total_do_acordo) > 0 ? fmtBRL(d.total_do_acordo) : valor;
        return (
          <CartaoPendSM key={k} ic="file-alert" cor="vermelho" titulo={`Corrija o acordo de ${P}`}
            linha={cobrado && total ? `Cobranças lançadas: ${cobrado} · total do acordo: ${total}` : 'As cobranças lançadas passam do total do acordo'}
            nota={`${P} não consegue aceitar enquanto as cobranças passarem do total`} notaCor="ambar">
            <BotoesPendSM><button type="button" style={btPendSM()} onClick={() => ir(item, 'financeiro', { aluno_id: item.aluno_id })}>Ver as cobranças</button></BotoesPendSM>
          </CartaoPendSM>
        );
      }
      default:
        return (
          <CartaoPendSM key={k} ic="point" cor="cinza" titulo={item.nome ? `Pendência com ${P}` : 'Pendência'} linha={[quando, valor].filter(Boolean).join(' · ')}>
            {resolver()}
          </CartaoPendSM>
        );
    }
  }

  function linhaEspera(item) {
    const d = item.detalhe, k = chave(item), P = primeiroNomeSM(item.nome, 'O aluno');
    const quando = quandoPendSM(item.quando);
    switch (item.tipo) {
      case 'aula_aguardando_aluno': {
        // prazo ja' passou (ex.: fechou de novo uma aula contestada): o robo fecha na proxima volta
        const vale = d.fecha_sozinha_em ? (new Date(d.fecha_sozinha_em) > new Date() ? ` · vale sozinha ${quandoSP(d.fecha_sozinha_em)}` : ' · vale sozinha em seguida') : '';
        // v142 (P1): "Desfazer" enquanto o aluno nao confirma (o banco confere)
        return <LinhaEsperaSM key={k} ic="hourglass" titulo={`${P} confirma a aula de ${quando}`}
          linha={`Você registrou ${d.desfecho_prof === 'falta' ? 'falta' : 'aula dada'}${vale}`}>
          {/* revisao v142: sem p_esperado — depois de "Conta a aula" a ultima acao e' a decisao do aviso, nao o registro
              (o detalhe da pendencia nao diz qual); o banco escolhe e a previa mostra o texto certo */}
          <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: item.ref_id, esperado: null }} papel="professor" nomeOutro={item.nome}
            previaSilenciosa onFeito={() => pend.recarregar()} />
        </LinhaEsperaSM>;
      }
      case 'acordo_aguardando_aceite':
        // inconsistente: o banco manda como acordo_corrigir (na vez do professor); aqui so' por garantia
        if (d.inconsistente) return <LinhaEsperaSM key={k} ic="file-alert" titulo={`O acordo de ${P} tem cobranças acima do total`}
          linha={`Corrija para ${P} conseguir aceitar`} />;
        if (d.proximo) return <LinhaEsperaSM key={k} ic="file-text" titulo={`${P} ainda não confirmou o próximo pacote`}
          linha={[item.valor ? fmtBRL(item.valor) : '', item.desde ? `mandado ${desdeSM(item.desde)}` : ''].filter(Boolean).join(' · ')} />;
        return <LinhaEsperaSM key={k} ic="file-text" titulo={`${P} ainda não aceitou o acordo`}
          linha={[item.valor ? fmtBRL(item.valor) : '', item.desde ? `mandado ${desdeSM(item.desde)}` : ''].filter(Boolean).join(' · ')} />;
      case 'reajuste_aguardando':
        return <LinhaEsperaSM key={k} ic="trending-up" titulo={`${P} ainda não respondeu o reajuste`}
          linha={d.valor_atual ? `${fmtBRL(d.valor_atual)} → ${fmtBRL(item.valor)}` : (item.valor ? `Novo valor ${fmtBRL(item.valor)}` : '')} />;
      case 'contraproposta_aguardando':
        return <LinhaEsperaSM key={k} ic="calendar-event" titulo={`${P} ainda não respondeu o novo horário`}
          linha={`${quando || 'Outro horário'}${d.vencida ? ' · o dia já passou' : ''}`} />;
      case 'remarcacao_aguardando': {
        // v136: voce remarcou; ja' vale. Se o aluno nao puder, vira "nao consegue" na sua vez.
        const pq = partesQuandoSM(item.quando);
        const para = quandoCurtoSM(d.para_data || pq.data, d.para_hora || pq.hora);
        const de = d.de_data ? quandoCurtoSM(d.de_data, d.de_hora) : '';
        return <LinhaEsperaSM key={k} ic="calendar-event" titulo={`${P} confirma o novo horário${para ? ` (${para})` : ''}`}
          linha={`${de ? `Era ${minuscSM(de)}. ` : ''}Já vale. Se não puder, ${P} avisa pelo app.`} />;
      }
      case 'convite_nao_aberto': {
        // nunca convidado: quem precisa agir e' o professor (o titulo nao pode culpar o aluno)
        const enviado = d.enviado_em || null;
        return (
          <LinhaEsperaSM key={k} ic="send" titulo={enviado ? `${P} ainda não entrou no app` : `Convide ${P} para o app`}
            linha={enviado ? `Convite enviado ${desdeSM(enviado)}` : `Com o app, ${P} confirma as aulas e avisa quando paga`}>
            <button type="button" onClick={() => ir(item, 'alunos', { convidar_aluno_id: item.aluno_id, t: Date.now() })}
              style={{ ...btPendSM('secundario'), flex: 'none', marginTop: '8px', fontSize: '13px' }}>
              {enviado ? 'Reenviar convite' : 'Mandar convite'}
            </button>
          </LinhaEsperaSM>
        );
      }
      default:
        return <LinhaEsperaSM key={k} ic="hourglass" titulo={`Esperando ${P}`} linha={quando} />;
    }
  }

  const suaVez = lado.sua_vez, esperando = lado.esperando;
  return (
    <div className="module-page">
      <div style={{ maxWidth: '720px' }}>
        <TopoPendSM subtitulo={pend.estado === 'sem_rpc' ? 'Seus avisos recentes.' : 'O que está com você e o que espera o aluno. Resolveu, some daqui.'} />
        {ok ? (
          <>
            <SecaoPendSM titulo="Sua vez" n={suaVez.length + pedidosRenovar.length}>
              {pedidosRenovar.map(cartaoRenovar)}
              {suaVez.length ? suaVez.map(cartao) : !pedidosRenovar.length && <TudoEmDiaSM frase="Nenhuma aula para fechar, nenhum pedido ou pagamento esperando você." />}
            </SecaoPendSM>
            {esperando.length > 0 && (
              <SecaoPendSM titulo="Esperando o aluno" n={esperando.length} cinza>
                {esperando.map(linhaEspera)}
              </SecaoPendSM>
            )}
          </>
        ) : <AvisoSemListaPendSM estado={pend.estado} onTentar={() => pend.recarregar()} />}
        <HistoricoAvisosSM decidido={pend.estado !== 'carregando'} abrir={!ok || suaVez.length === 0}>
          <Notificacoes professor={professor} onNavigate={(k, p) => onNavigate && onNavigate(k, p)} embutido
            avisoResolvido={ok ? n => avisoResolvidoSM(n, suaVez, FAMILIA_AVISO_PROF_SM) : null} />
        </HistoricoAvisosSM>
      </div>
      {presenca && <ModalPresencaTurma professor={professor} sessao={presenca.sessao} onClose={() => setPresenca(null)}
        onFechou={() => feito(presenca.item)} />}
    </div>
  );
}

// ---- Aluno ----
// onIr(item, tela): troca para a ficha do professor do item e abre a tela onde se resolve.
// onResponderAula(item, aconteceu): a mesma resposta do Inicio (aluno_responder_aula).
// onRespondeuRemarcacao(): o Inicio recarrega (a faixa "mudou sua aula" tambem some de la').
// v140: onResponderAula(item, aconteceu, motivo) — motivo 'prof_nao_veio' no "Nao aconteceu";
// onAbrirAula(item, modo): abre a aula no Inicio ('naovou' = o aviso de falta de sempre).
function PendenciasAluno({ pend, onIr, onResponderAula, onRespondeuRemarcacao, onAbrirAula, historico }) {
  React.useEffect(() => { pend.recarregar && pend.recarregar(); }, []);
  const [ocupado, setOcupado] = React.useState(null);
  const [escolha, setEscolha] = React.useState(null);   // v140: item com o "O que aconteceu?" aberto
  const ok = pend.estado === 'ok';
  const lado = (ok && pend.aluno) || { sua_vez: [], esperando: [] };
  const chave = i => `${i.tipo}:${i.ref_tipo}:${i.ref_id}`;
  async function responder(item, aconteceu, motivo = null) {
    if (ocupado) return;
    setOcupado(chave(item));
    try { await onResponderAula(item, aconteceu, motivo); } finally { setOcupado(null); setEscolha(null); }
    pend.recarregar();
  }
  // v140: "Vou" da pergunta do professor (aluno_confirmar_presenca)
  async function vou(item) {
    if (ocupado) return;
    setOcupado(chave(item));
    let r = null;
    try { r = await responderPresencaAlunoSM(item.detalhe.aula_id || item.ref_id, { origem: 'pendencias', nomeProf: primeiroNomeSM(item.nome, 'Seu professor') }); }
    finally { setOcupado(null); }
    if (r && r.ok) uso.acao('pendencias.resolver', { tipo: tipoUsoPendSM(item.tipo), via: 'inline' });
    pend.recarregar();
    onRespondeuRemarcacao && onRespondeuRemarcacao();
  }
  // v136: [Ok, combinado] / [Nao consigo] da remarcacao do professor (BotoesRemarcacaoSM ja' chamou o banco)
  function remarcacaoRespondida(item, res) {
    if (res && res.ok) uso.acao('pendencias.resolver', { tipo: tipoUsoPendSM(item.tipo), via: 'inline' });
    pend.recarregar();
    onRespondeuRemarcacao && onRespondeuRemarcacao();
  }
  const botaoIr = (item, tela, rotulo) => (
    <BotoesPendSM><button type="button" style={btPendSM()} onClick={() => onIr(item, tela)}>{rotulo}</button></BotoesPendSM>
  );

  function cartao(item) {
    const d = item.detalhe, k = chave(item), N = primeiroNomeSM(item.nome, 'Seu professor');
    const quando = quandoPendSM(item.quando);
    const busy = ocupado === k;
    switch (item.tipo) {
      case 'confirmar_aula': {
        // o banco manda desfecho_prof e vale_sozinha_em (os nomes antigos ficam so' por garantia)
        const falta = (d.desfecho_prof || d.desfecho) === 'falta';
        const valeEm = d.vale_sozinha_em || d.vale_sozinho_em;
        // v140: turma nao tem "nao veio" (o banco recusa: turma); individual so' a partir de inicio + 15 min
        const turmaC = !!(d.sessao_id || d.turma_id);
        const aulaC = aulaDoItemSM(item);
        const iniC = inicioAulaSP(aulaC);
        const nota = !valeEm ? null
          : new Date(valeEm) > new Date() ? `Se você não responder até ${quandoSP(valeEm)}, vale o registro de ${N}`
          : `O prazo de 24h passou: se você não responder, vale o registro de ${N}`;
        return (
          <CartaoPendSM key={k} ic="checks" cor="azul" titulo={`Confirme a aula de ${quando}`}
            linha={`${N} registrou ${falta ? 'falta (conta como aula)' : 'aula dada'}. Está certo?`}
            nota={nota}>
            {escolha === k ? (
              // v140: discordar abre "O que aconteceu?" — com falta: [Fiz a aula] [Cadu nao veio];
              // com aula dada: [Eu nao fui] [Cadu nao veio]. Nunca fecha como falta sem os dois concordarem.
              <EscolhaNaoAconteceuSM nomeProf={N} disabled={busy} aulaRef={aulaC}
                opcoes={falta ? (turmaC ? ['fiz'] : ['fiz', 'nao_veio']) : (turmaC ? ['eu_nao_fui'] : ['eu_nao_fui', 'nao_veio'])}
                liberaNaoVeioEm={iniC ? new Date(iniC.getTime() + 15 * 60000) : null}
                onEscolher={op => responder(item, op === 'fiz', op === 'nao_veio' ? 'prof_nao_veio' : null)} onVoltar={() => setEscolha(null)} />
            ) : (
            <BotoesPendSM>
              <button type="button" disabled={busy} style={{ ...btPendSM(), opacity: busy ? 0.6 : 1 }} onClick={() => responder(item, !falta)}>
                {busy ? 'Salvando…' : falta ? 'Sim, faltei' : 'Sim, fiz a aula'}
              </button>
              <button type="button" disabled={busy} style={btPendSM('secundario')} onClick={() => setEscolha(k)}>
                {falta ? 'Não, eu fui' : 'Não aconteceu'}
              </button>
            </BotoesPendSM>
            )}
          </CartaoPendSM>
        );
      }
      case 'aceitar_acordo':
        // v155: o proximo pacote (na fila) tambem pede o aceite — confirma no bloco Proximo pacote do Financeiro
        if (d.proximo) return (
          <CartaoPendSM key={k} ic="file-check" cor="azul" titulo={`Confirme o próximo pacote com ${N}`}
            linha={item.valor ? fmtBRL(item.valor) : 'Confira as condições'}
            nota="Confirme antes de pagar">
            {botaoIr(item, 'financeiro', 'Ver e confirmar')}
          </CartaoPendSM>
        );
        return (
          <CartaoPendSM key={k} ic="file-check" cor="azul" titulo={`Aceite o acordo com ${N}`}
            linha={item.valor ? `${fmtBRL(item.valor)}${d.tipo_cobranca === 'mensal' ? ' por mês' : ''}` : 'Confira as condições'}
            nota="Enquanto você não aceitar, nada é cobrado">
            {botaoIr(item, 'inicio', 'Ver o acordo')}
          </CartaoPendSM>
        );
      case 'responder_reajuste':
        return (
          <CartaoPendSM key={k} ic="trending-up" cor="ambar" titulo={`${N} propôs um novo valor`}
            linha={d.valor_atual ? `${fmtBRL(d.valor_atual)} → ${fmtBRL(item.valor)}${d.tipo_cobranca === 'mensal' ? ' por mês' : ''}` : (item.valor ? `Novo valor ${fmtBRL(item.valor)}` : '')}>
            {botaoIr(item, 'inicio', 'Responder')}
          </CartaoPendSM>
        );
      case 'confirmar_presenca': {
        // v140: o professor perguntou "vai?". [Vou] responde aqui; [Nao vou conseguir] abre a aula no
        // Inicio, no aviso de falta de sempre — com o prazo do acordo ANTES de confirmar.
        const prazo = d.prazo_ate ? new Date(d.prazo_ate) : null;
        const nota = !prazo || isNaN(prazo) ? null
          : d.no_prazo === false || prazo <= new Date()
            ? `O prazo para desmarcar sem custo acabou ${quandoSP(prazo)}. ${d.tipo_cobranca === 'mensal' ? 'Se você não for, fica como falta.' : `Se você não for, a aula conta — ${N} pode decidir devolver.`}`
          : `Se não puder, avise até ${quandoSP(prazo)}.`;
        const quandoP = d.data ? quandoCurtoSM(d.data, d.hora) : quando;
        return (
          <CartaoPendSM key={k} ic="calendar-question" cor="azul" titulo={`${N} quer saber se você vai`} linha={quandoP}
            nota={nota} notaCor={d.no_prazo === false ? 'ambar' : 'cinza'}>
            <BotoesPendSM>
              <button type="button" disabled={busy} style={{ ...btPendSM(), opacity: busy ? 0.6 : 1 }} onClick={() => vou(item)}>
                {busy ? 'Salvando…' : <><Ico n="check" s={15} /> Vou</>}
              </button>
              <button type="button" disabled={busy} style={btPendSM('secundario')}
                onClick={() => { uso.acao('aluno.presenca_responder', { resposta: 'nao_vou', origem: 'pendencias' }); onAbrirAula && onAbrirAula(item, 'naovou'); }}>
                Não vou
              </button>
            </BotoesPendSM>
          </CartaoPendSM>
        );
      }
      case 'confirmar_remarcacao': {
        // v136: o professor remarcou e ja' vale; o aluno diz se consegue (de_* = antigo, para_* = novo)
        const pq = partesQuandoSM(item.quando);
        const linha = deParaRemarcacaoSM(d.de_data, d.de_hora, d.para_data || pq.data, d.para_hora || pq.hora) || quando;
        return (
          <CartaoPendSM key={k} ic="calendar-event" cor="azul" titulo={`${N} mudou sua aula`} linha={linha}
            nota={`O horário novo já vale. Se não puder, ${N} remarca ou desmarca.`}>
            <BotoesRemarcacaoSM solicitacaoId={d.solicitacao_id || item.ref_id} nomeProf={N} origem="pendencias"
              onFeito={res => remarcacaoRespondida(item, res)} />
          </CartaoPendSM>
        );
      }
      case 'responder_contraproposta': {
        // a aula de origem: o banco manda aula_data/aula_hora
        const deD = d.de_data || d.aula_data, deH = d.de_hora || d.aula_hora;
        const de = deD ? quandoCurtoSM(deD, deH) : '';
        const para = d.para_data ? quandoCurtoSM(d.para_data, d.para_hora) : quando;
        return (
          <CartaoPendSM key={k} ic="calendar-event" cor="azul" titulo={`${N} propôs outro horário`} linha={de ? `${de} → ${para}` : para}>
            {botaoIr(item, 'inicio', 'Responder')}
          </CartaoPendSM>
        );
      }
      case 'pagar': {
        const vencida = !!d.vencida;
        const venc = d.vencimento || partesQuandoSM(item.quando).data;
        return (
          <CartaoPendSM key={k} ic="credit-card" cor={vencida ? 'vermelho' : 'ambar'} titulo={`${vencida ? 'Pagamento vencido' : 'Pagamento em aberto'} — ${N}`}
            linha={[item.valor ? fmtBRL(item.valor) : '', venc ? `${vencida ? 'venceu' : 'vence'} ${ddmmSM(venc)}` : ''].filter(Boolean).join(' · ')}>
            {botaoIr(item, 'financeiro', 'Pagar')}
          </CartaoPendSM>
        );
      }
      default:
        return (
          <CartaoPendSM key={k} ic="point" cor="cinza" titulo={`Pendência com ${N}`} linha={quando}>
            {botaoIr(item, 'inicio', 'Ver')}
          </CartaoPendSM>
        );
    }
  }

  function linhaEspera(item) {
    const d = item.detalhe, k = chave(item), N = primeiroNomeSM(item.nome, 'seu professor');
    const quando = quandoPendSM(item.quando);
    const busy = ocupado === k;
    // v142: "Desfazer" discreto enquanto o professor nao respondeu (A2, A5, A6, A7, A9). O app mostra por
    // um palpite; quem decide e' o banco (desfazer_minha_acao). O que vale ate' a aula comecar (A2, A5)
    // some depois do inicio (rel_sql v142, R2).
    const iniItem = inicioAulaSP(aulaDoItemSM(item));
    const antesDoInicio = !iniItem || iniItem.getTime() > Date.now();
    const desfazer = (esperado, silencioso = false) => item.ref_id ? (
      <BotaoDesfazerSM alvo={{ ref_tipo: item.ref_tipo || 'aula', ref_id: item.ref_id, esperado }} papel="aluno" nomeOutro={item.nome}
        previaSilenciosa={silencioso} onFeito={() => pend.recarregar()} />
    ) : null;
    switch (item.tipo) {
      case 'pagamento_declarado':
        return <LinhaEsperaSM key={k} ic="coin" titulo={`Você avisou que pagou${item.valor ? ' ' + fmtBRL(item.valor) : ''}`} linha={`${N} confere e confirma o recebimento`}>{desfazer('ja_paguei')}</LinhaEsperaSM>;
      case 'pedido_enviado': {
        const t = d.tipo_pedido;
        const titulo = t === 'reagendamento' ? `Pedido para remarcar a aula de ${d.aula_data ? quandoCurtoSM(d.aula_data, d.aula_hora) : quando}`
          : t === 'pausa' ? `Pedido de pausa${d.periodo_inicio || d.periodo_fim ? ` (${periodoPendSM(d.periodo_inicio, d.periodo_fim).toLowerCase()})` : ''}`
          : t === 'ajuste_contrato' ? 'Pedido de correção no acordo'
          : t === 'cancelamento' ? `Pedido para cancelar a aula de ${d.aula_data ? quandoCurtoSM(d.aula_data, d.aula_hora) : quando}`
          : 'Seu pedido';
        return <LinhaEsperaSM key={k} ic="send" titulo={titulo} linha={`${N} ainda não respondeu${item.desde ? ` · enviado ${desdeSM(item.desde)}` : ''}`} />;
      }
      case 'nao_consigo_enviado': {
        // v136: o aluno disse "Nao consigo" na remarcacao do professor e a aula segue nesse horario.
        // Sem isto a tela ficava "Tudo em dia" e a aula seguia normal — parecia que o "Nao consigo" nao
        // tinha valido. Depois que a aula termina quem aparece e' "Aula ... sem registro".
        const pq = partesQuandoSM(item.quando);
        const q = quandoCurtoSM(d.para_data || pq.data, d.para_hora || pq.hora) || quando;
        return <LinhaEsperaSM key={k} ic="calendar-x" titulo={`Você disse que não consegue ${minuscSM(q)}`}
          linha={`${N} remarca ou desmarca. Até lá, a aula continua nesse horário.`}>{!d.comecou && antesDoInicio ? desfazer('nao_consigo') : null}</LinhaEsperaSM>;
      }
      case 'aviso_falta_enviado':
        // revisao v142: o banco tambem manda este item quando foi o PROFESSOR que registrou o aviso — ai' o titulo
        // e' dele e nao ha' "Desfazer" (o banco responderia "So' quem fez pode desfazer")
        if (d.aviso_canal === 'professor_registrou') return <LinhaEsperaSM key={k} ic="bell-ringing" titulo={`${N} registrou que você não vai à aula de ${quando}`} linha={`${N} decide se a aula conta`} />;
        return <LinhaEsperaSM key={k} ic="bell-ringing" titulo={`Você avisou que não vai à aula de ${quando}`} linha={`${N} decide se a aula conta`}>{antesDoInicio ? desfazer('aviso') : null}</LinhaEsperaSM>;
      case 'aula_divergencia':
        return <LinhaEsperaSM key={k} ic="arrows-diff" titulo={`Aula de ${quando}: vocês registraram diferente`}
          linha={d.aluno_motivo === 'prof_nao_veio'   // v140
            ? `Você disse que ${N} não veio e ${N} registrou diferente. A aula não sai do seu pacote até ${N} decidir — conversem.`
            : `A aula não sai do seu pacote até ${N} decidir — conversem.`}>{desfazer('resposta', true)}</LinhaEsperaSM>;
      case 'aula_resposta_enviada':
        return <LinhaEsperaSM key={k} ic="hourglass" titulo={`Você respondeu sobre a aula de ${quando}`} linha={`Falta ${N} fechar`}>{desfazer('resposta')}</LinhaEsperaSM>;
      case 'aula_sem_registro': {
        // ja' respondeu (ou contestou): sem botoes — tocar de novo regravava a resposta e mandava outro
        // aviso ao professor. vale_sozinha_em: so' quando disse que fez a aula (o robo fecha como dada).
        const fez = !d.vale_sozinha_em ? `Você disse que fez a aula. Falta ${N} fechar.`
          : new Date(d.vale_sozinha_em) > new Date() ? `Você disse que fez a aula. Se ${N} não fechar até ${quandoSP(d.vale_sozinha_em)}, ela vale como dada.`
          : 'Você disse que fez a aula. Ela vale como dada em seguida.';
        // v140: "o professor nao veio" (aluno_motivo; aluno_disse continua 'nao_foi')
        // mensalidade (o banco manda tipo_cobranca): fica registrado, sem "volta para o pacote" (decisao 4 = A)
        const volta = d.volta_ao_pacote_em ? new Date(d.volta_ao_pacote_em) : null;
        const semPacoteNV = d.tipo_cobranca === 'mensal' || !!(d.sessao_id || d.turma_id);
        const naoVeio = d.aluno_motivo !== 'prof_nao_veio' ? null
          : semPacoteNV ? `Você disse que ${N} não veio. Fica registrado — combine a reposição com ${N}.`
          : `Você disse que ${N} não veio.${volta && !isNaN(volta) ? (volta > new Date() ? ` Se ${N} não responder até ${quandoSP(volta)}, a aula volta para o seu pacote.` : ' A aula volta para o seu pacote na próxima hora.') : ''}`;
        const linha = d.contestada ? `Você contestou o registro. Falta ${N} fechar de novo.`
          : d.aluno_disse === 'aconteceu' ? fez
          : naoVeio ? naoVeio
          : d.aluno_disse === 'nao_foi' ? `Você disse que não foi. Falta ${N} fechar.`
          // v136: tinha dito "Nao consigo" nesse horario (remarcacao do professor) e ninguem remarcou
          : d.nao_consigo ? `Você tinha avisado que não conseguia nesse horário. ${N} ainda não fechou — se não aconteceu, diga aqui.`
          : `${N} ainda não fechou. Se quiser, diga o que aconteceu.`;
        const pode = d.pode_responder !== false && !d.aluno_disse && !d.contestada;
        // v140: "Nao aconteceu" pergunta "Eu nao fui" / "Cadu nao veio" (aula de turma: so' "Eu nao fui")
        const turma = !!(d.sessao_id || d.turma_id);
        const ini = inicioAulaSP(aulaDoItemSM(item));
        return (
          <LinhaEsperaSM key={k} ic={d.aluno_motivo === 'prof_nao_veio' ? 'user-exclamation' : 'clock-exclamation'} titulo={d.contestada ? `Aula de ${quando} reaberta` : `Aula de ${quando} sem registro`} linha={linha}>
            {pode && escolha === k ? (
              <EscolhaNaoAconteceuSM nomeProf={N} disabled={busy} opcoes={turma ? ['eu_nao_fui'] : ['eu_nao_fui', 'nao_veio']}
                liberaNaoVeioEm={ini ? new Date(ini.getTime() + 15 * 60000) : null} aulaRef={aulaDoItemSM(item)}
                onEscolher={op => responder(item, false, op === 'nao_veio' ? 'prof_nao_veio' : null)} onVoltar={() => setEscolha(null)} />
            ) : pode ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                <button type="button" disabled={busy} style={{ ...btPendSM('secundario'), fontSize: '13px' }} onClick={() => responder(item, true)}>{busy ? '…' : 'Fiz a aula'}</button>
                <button type="button" disabled={busy} style={{ ...btPendSM('secundario'), fontSize: '13px' }} onClick={() => setEscolha(k)}>Não aconteceu</button>
              </div>
            ) : (!d.contestada && (d.aluno_disse || d.aluno_motivo)) ? desfazer('resposta') : null}
          </LinhaEsperaSM>
        );
      }
      case 'aula_proposta_enviada':
        return <LinhaEsperaSM key={k} ic="calendar-plus" titulo={`Aula combinada de ${quando}`} linha={`Esperando ${N} confirmar`} />;
      case 'acordo_em_revisao':
        return <LinhaEsperaSM key={k} ic="file-text" titulo="Acordo em revisão" linha={`As cobranças passam do total do acordo — ${N} precisa corrigir o valor.`} />;
      default:
        return <LinhaEsperaSM key={k} ic="hourglass" titulo={`Esperando ${N}`} linha={quando} />;
    }
  }

  // "Esperando Cadu" — com mais de um professor, um bloco para cada (na ordem do mais antigo). Sem
  // artigo: "Esperando o Mariana" saia errado (o app nao sabe se e' professor ou professora).
  const grupos = [];
  lado.esperando.forEach(i => {
    const kk = i.professor_id || i.nome || '?';
    let g = grupos.find(x => x.k === kk);
    if (!g) { g = { k: kk, nome: i.nome, itens: [] }; grupos.push(g); }
    g.itens.push(i);
  });
  const suaVez = lado.sua_vez;
  return (
    <div className="flex flex-col">
      <TopoPendSM subtitulo={pend.estado === 'sem_rpc' ? 'Seus avisos recentes.' : 'O que está com você e o que espera o professor. Resolveu, some daqui.'} />
      {ok ? (
        <>
          <SecaoPendSM titulo="Sua vez" n={suaVez.length}>
            {suaVez.length ? suaVez.map(cartao) : <TudoEmDiaSM frase="Nada esperando por você agora." />}
          </SecaoPendSM>
          {grupos.map(g => (
            <SecaoPendSM key={g.k} titulo={g.nome ? `Esperando ${primeiroNomeSM(g.nome, 'o professor')}` : 'Esperando o professor'} n={g.itens.length} cinza>
              {g.itens.map(linhaEspera)}
            </SecaoPendSM>
          ))}
        </>
      ) : <AvisoSemListaPendSM estado={pend.estado} onTentar={() => pend.recarregar()} />}
      <HistoricoAvisosSM decidido={pend.estado !== 'carregando'} abrir={!ok || suaVez.length === 0}>
        {historico}
      </HistoricoAvisosSM>
    </div>
  );
}

// Dias padrao por frequencia — o professor edita se quiser, mas ja nasce FIXO (nunca "flexivel").
// 1x Seg · 2x Seg/Qua · 3x Seg/Qua/Sex · 4x Seg/Ter/Qui/Sex · 5x Seg a Sex
function diasPadraoSM(n) {
  const mapa = { 1: [1], 2: [1, 3], 3: [1, 3, 5], 4: [1, 2, 4, 5], 5: [1, 2, 3, 4, 5] };
  return mapa[n] || [1];
}

// Dinheiro digitado (Marcus, 29/09): o professor digita "1700" ou "1700,5" e, ao sair do
// campo, ve "1.700,00" — do jeito que a gente escreve. Enquanto digita, so' numeros e uma
// virgula. numDinheiro() le' qualquer um dos dois formatos (com ponto de milhar ou sem).
function numDinheiro(v) {
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  const s = String(v ?? '').replace(/[^\d,.-]/g, '');
  if (!s) return 0;
  // "2.500,00" / "2.500" (padrao BR, ponto de milhar) ou "2500.5" (numero puro vindo do banco)
  const limpo = s.includes(',') ? s.replace(/\./g, '').replace(',', '.')
    : /^\d{1,3}(\.\d{3})+$/.test(s) ? s.replace(/\./g, '') : s;
  return Number(limpo) || 0;
}
// Enquanto digita (30/09 — Marcus: "tudo que a gente digita em dinheiro padronizado"):
// 2500 vira "2.500" na hora; a virgula e os centavos o professor digita se quiser, ate' 2 casas.
// Ao sair do campo, formatarDinheiro completa o ",00". Todo ponto e' de milhar (o teclado do
// iPhone em portugues so' tem virgula).
function limparDinheiro(v) {
  let s = String(v ?? '').replace(/[^\d,]/g, '');
  const i = s.indexOf(',');
  let inteiro = i >= 0 ? s.slice(0, i) : s;
  const dec = i >= 0 ? s.slice(i + 1).replace(/,/g, '').slice(0, 2) : null;
  inteiro = inteiro.replace(/^0+(?=\d)/, '');
  if (!inteiro && dec !== null) inteiro = '0';
  inteiro = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return dec !== null ? inteiro + ',' + dec : inteiro;
}
function formatarDinheiro(v) {
  const n = numDinheiro(v);
  return n > 0 ? n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
}
// Custos fixos do professor por mes (30/09 — Marcus: Smart Fit e a academia da Vila Madalena
// cobram MENSALIDADE do professor; a Politec cobra por aula). Mensalidade de academia e' do
// professor, nao do aluno: fica UMA vez em professores.custos_mensais
// ([{nome, valor, desde:'YYYY-MM'}]) e sai do liquido uma vez por mes — se ficasse em cada
// aluno, 10 alunos na Smart Fit descontariam a mensalidade 10 vezes. Custo por aula continua
// no acordo do aluno (taxa_academia).
async function carregarCustosMensais(profId) {
  const { data, error } = await supabase.from('professores').select('custos_mensais').eq('id', profId).maybeSingle();
  if (error) return [];                                   // coluna ainda nao criada: sem custos
  return Array.isArray(data?.custos_mensais) ? data.custos_mensais : [];
}
function custosMensaisDoMes(lista, mesKey) {
  return (lista || []).filter(c => !c.desde || c.desde <= mesKey).reduce((t, c) => t + (Number(c.valor) || 0), 0);
}
async function salvarCustosMensais(profId, lista) {
  const { error } = await supabase.from('professores').update({ custos_mensais: lista }).eq('id', profId);
  return !error;
}
const mesAtualSM = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const semAcentoSM = t => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
function dataValidaSM(s) {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(String(s))) return false;
  const y = Number(String(s).slice(0, 4));
  return y >= 2020 && y <= 2035; // fora disso e' erro de digitacao — protege contra loops de milhares de anos
}
// v142: onde o aviso escuro fica — acima do menu de baixo do celular, quando ele esta' na tela (antes o aviso
// ficava por cima do menu), e com a largura do texto ate' a tela menos 16 px de cada lado (antes, com left:50%,
// a largura maxima era meia tela e o texto longo virava uma coluna estreita). Vale para o aviso de sempre e
// para a faixa "Feito · Desfazer".
// Revisao v142 (tela, achado 1): com o botao redondo "+" do professor na tela (.plen-fab, 54 px a 80 px do fim),
// o aviso fica ACIMA dele (80 + 54 + 12 de folga) — antes o "Desfazer" da faixa caia bem em cima do "+" e um toque
// para cadastrar alguem desfazia a acao, sem perguntar.
function baseAvisoSM() {
  let temNav = false, temFab = false;
  const visivel = el => !!el && getComputedStyle(el).display !== 'none' && getComputedStyle(el).visibility !== 'hidden';
  try { temNav = visivel(document.querySelector('.app-bottom-nav')); } catch {}
  try { temFab = visivel(document.querySelector('.plen-fab')); } catch {}
  const fundo = temFab ? 'calc(146px + env(safe-area-inset-bottom, 0px))' : temNav ? 'calc(84px + env(safe-area-inset-bottom, 0px))' : '28px';
  return 'position:fixed;left:50%;transform:translateX(-50%);width:max-content;max-width:calc(100vw - 32px);box-sizing:border-box;'
    + 'bottom:' + fundo + ';';
}
function toastSM(msg) {
  // Central de uso: so' o FATO de ter aparecido um aviso (e se parece falha) — nunca o texto.
  try { uso.evento('aviso.toast', { detalhes: { tipo: /^(Erro|Não foi possível|Não deu|Não consegui|Falha)/i.test(String(msg)) ? 'falha' : 'info' } }); } catch {}
  try {
    const el = document.createElement('div');
    el.textContent = String(msg);
    el.setAttribute('data-aviso-sm', '1');
    // pointer-events:none (revisao v142): o aviso comum nao tem botao — nao engole o toque no que esta' embaixo
    el.style.cssText = baseAvisoSM() + 'background:#16181D;color:white;padding:12px 20px;border-radius:12px;font-size:14px;font-weight:600;z-index:99999;box-shadow:0 8px 30px rgba(0,0,0,0.35);text-align:center;opacity:0;transition:opacity .25s;pointer-events:none';
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    // Mensagem longa fica mais tempo na tela (dá tempo de ler).
    const dur = Math.max(4000, Math.min(12000, String(msg).length * 70));
    setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 300); }, dur);
  } catch (e) { console.log('toast:', msg); }
}
// v141 (erro.promessa): copiar para a area de transferencia SEMPRE com o "e se falhar". No iPhone a
// copia e' recusada (NotAllowedError) quando o app perdeu o foco ou o toque nao conta como gesto;
// sem o tratamento a recusa virava "erro.promessa" na central e a pessoa achava que tinha copiado.
// falha: o que dizer quando a copia e' recusada — "segure o dedo sobre o link" so' onde o link aparece na tela.
function copiarTextoSM(texto, ok = 'Copiado!', falha = 'Não consegui copiar. Segure o dedo sobre o link para copiar.') {
  let p;
  try { p = navigator.clipboard && navigator.clipboard.writeText(String(texto || '')); } catch (e) { p = Promise.reject(e); }
  if (!p || typeof p.then !== 'function') p = Promise.reject(new Error('sem_area_transferencia'));
  return p.then(() => { if (ok) toastSM(ok); return true; }, (e) => {
    try { uso.erro('erro.copiar', (e && (e.name && e.name !== 'Error' ? e.name + ': ' : '') + (e.message || e)) || 'falhou'); } catch {}
    toastSM(falha);
    return false;
  });
}
// Erro vindo da trava do banco (sql/pagamento_unico.sql) vira frase de gente.
function msgErroPagamento(err) {
  const m = String(err?.message || err || '');
  if (m.includes('PAGAMENTO_DUPLICADO')) {
    return 'Não foi possível: este aluno já tem um pagamento ou cobrança para este mês. ' +
      'O Plen não aceita dois pagamentos do mesmo aluno no mesmo mês — confira em Financeiro → Pagamentos.';
  }
  // O texto tecnico (ex.: "TypeError: Load failed", "JWT expired") vai so' para a central de uso
  // (uso.erro, em quem chama) — na tela, frase de gente.
  return 'Não foi possível concluir agora. Confira sua internet e tente de novo em instantes.';
}

// ---- Pagamentos: COMO foi pago (meio) x DE ONDE veio a cobranca (tipo) — Fase 0, Etapa 3 ----
// Ate' a v129 o app gravava o meio ("pix") por cima do tipo ("mensalidade") e a origem sumia.
// Agora: tipo = origem (mensalidade, pacote, migracao, aguardando...); meio = como foi pago.
// O app NUNCA grava meio plen_* (so' o servidor, na Fase 3). "Virou pago" passa sempre pela
// funcao confirmar_pagamento; cancelar e reabrir tambem (sql/pagamento_tomadas.sql).
const MEIOS_PAGAMENTO = [
  { v: 'pix_direto', l: 'Pix' },
  { v: 'dinheiro', l: 'Dinheiro' },
  { v: 'cartao_credito', l: 'Cartão de crédito' },
  { v: 'cartao_debito', l: 'Cartão de débito' },
  { v: 'transferencia', l: 'Transferência' },
  { v: 'outro', l: 'Outro' },
];
const ROTULO_MEIO = {
  pix_direto: 'Pix', dinheiro: 'Dinheiro', cartao_credito: 'Cartão de crédito', cartao_debito: 'Cartão de débito',
  transferencia: 'Transferência', outro: 'Outro', plen_pix: 'Pix pelo Plen', plen_cartao: 'Cartão pelo Plen', plen_boleto: 'Boleto pelo Plen',
};
// tipo antigo que era, na verdade, o meio (gravado pelo app ate' a v129)
const MEIO_DO_TIPO_ANTIGO = { pix: 'pix_direto', pix_direto: 'pix_direto', dinheiro: 'dinheiro', cartao_credito: 'cartao_credito', cartao_debito: 'cartao_debito', transferencia: 'transferencia', outro: 'outro' };
// Rotulo de "como foi pago": le' o meio; so' cai para o tipo quando o meio esta' vazio.
function formaDoPagamento(p) {
  if (!p) return '—';
  if (p.meio && ROTULO_MEIO[p.meio]) return ROTULO_MEIO[p.meio];
  const m = MEIO_DO_TIPO_ANTIGO[p.tipo];
  if (m) return ROTULO_MEIO[m];
  if (p.tipo === 'migracao' || p.confirmado_via === 'migracao') return 'pago antes do cadastro';
  return '—';
}
// "Parcelas ja' pagas" do cadastro de acordo: pago antes de entrar no app, sem dia nem meio.
const ehPagoAntesDoCadastro = p => p?.confirmado_via === 'migracao' || p?.tipo === 'migracao';
// Meio ja' conhecido de um pagamento (para vir marcado no "Confirmar"). Nunca plen_*.
function meioConhecido(p) {
  if (p?.meio && MEIOS_PAGAMENTO.some(m => m.v === p.meio)) return p.meio;
  return MEIO_DO_TIPO_ANTIGO[p?.tipo] || '';
}
// Lista FECHADA de motivos de cancelamento (o banco so' aceita codigo: ^[a-z_]{1,40}$).
const MOTIVOS_CANCELAR_PAGAMENTO = [
  { v: 'lancado_por_engano', l: 'Lançado por engano (ex.: registrado duas vezes)' },
  { v: 'valor_errado', l: 'Valor errado — vou lançar de novo' },
  { v: 'aluno_desistiu', l: 'O aluno desistiu' },
  { v: 'combinado_outro_jeito', l: 'Combinamos de outro jeito' },
  { v: 'outro', l: 'Outro motivo' },
];
const ROTULO_MOTIVO_CANCEL = {
  lancado_por_engano: 'lançado por engano', valor_errado: 'valor errado', aluno_desistiu: 'aluno desistiu',
  combinado_outro_jeito: 'combinado de outro jeito', outro: 'outro motivo', duplicado: 'em duplicidade',
};
// "Hoje" no fuso de Sao Paulo (o banco confere a data do recebimento nesse fuso): hojeSP(), la' em
// cima junto das outras contas de hora do Brasil (v131).
const dataSP = v => v ? new Date(v).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }) : null;
const nomeMesRefSM = ref => /^\d{4}-\d{2}$/.test(ref || '') ? new Date(ref + '-15T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : 'este mês';
// Codigos que as funcoes de pagamento do banco devolvem ({erro:'...'}) virando frase de gente.
function msgErroRpcPagamento(codigo, r) {
  switch (codigo) {
    case 'duplicado': return `Não foi possível: este aluno já tem outro pagamento ou cobrança para ${nomeMesRefSM(r?.referencia_mes)}. O Plen não aceita dois do mesmo aluno no mesmo mês — confira em Financeiro → Pagamentos.`;
    case 'cancelado': return 'Esta cobrança está cancelada. Toque em "Reabrir cobrança" antes de confirmar.';
    case 'pago_pelo_plen': return 'Este pagamento foi confirmado pelo Plen e não pode ser mudado aqui.';
    case 'data_futura': return 'A data do recebimento não pode ser no futuro. Escolha hoje ou um dia que já passou.';
    case 'data_invalida': return 'Data inválida — confira o ano.';
    case 'meio_obrigatorio': return 'Escolha como o aluno pagou (Pix, dinheiro, cartão…).';
    case 'meio_invalido': return 'Forma de pagamento inválida. Escolha uma das opções da lista.';
    case 'referencia_invalida': return 'O mês de referência está inválido. Escolha o mês de novo.';
    case 'motivo_obrigatorio': return 'Este lançamento já está pago: escolha o motivo do cancelamento.';
    case 'motivo_invalido': return 'Motivo inválido. Escolha uma das opções da lista.';
    case 'status_invalido': return 'Esta cobrança não pode voltar para "em aberto".';
    case 'sem_permissao': return 'Esta cobrança não é de um aluno seu — não foi possível mudar.';
    case 'nao_encontrado': return 'Não encontrei esta cobrança. Atualize a tela e tente de novo.';
    default: return 'Não foi possível salvar o pagamento agora. Tente de novo em instantes.';
  }
}
// Chama uma funcao de pagamento do banco e devolve { ok, r } ou { ok:false, codigo, msg }.
// Confere os DOIS erros: o da chamada (rede, permissao) e o {erro} que a funcao devolve.
// Sucesso so' com {ok:true} do banco — nunca mostrar "pronto" sem o banco confirmar.
async function rpcPagamento(nome, args) {
  try {
    const { data, error } = await supabase.rpc(nome, args);
    if (error) {
      const dup = String(error.message || '').includes('PAGAMENTO_DUPLICADO');
      return { ok: false, codigo: dup ? 'duplicado' : 'falha', msg: msgErroPagamento(error), tecnico: error.message };
    }
    if (!data || data.erro || !data.ok) {
      const codigo = data?.erro || 'sem_resposta';
      return { ok: false, codigo: /^[a-z_]{1,40}$/.test(codigo) ? codigo : 'outro', r: data, msg: msgErroRpcPagamento(codigo, data) };
    }
    avisarPendenciasMudouSM();   // v134: cobranca mudou -> a aba Pendencias recarrega
    return { ok: true, r: data };
  } catch (e) {
    return { ok: false, codigo: 'falha', msg: msgErroPagamento(e), tecnico: e?.message || String(e) };
  }
}

// Reajuste (Etapa 4): numa MENSALIDADE a base e' o valor_mensal (o que a rotina cobra) e o texto
// diz "/mes". Antes o reajuste partia do preco_negociado, que podia estar velho — um "+10%" chegou
// a BAIXAR a mensalidade. O acordo e' relido do banco antes de propor.
const ehMensalAcordo = c => c?.tipo_cobranca === 'mensal';
function baseReajuste(c) {
  if (!c) return 0;
  if (ehMensalAcordo(c) && Number(c.valor_mensal) > 0) return Number(c.valor_mensal);
  return Number(c.preco_negociado || 0);
}
const sufixoPrecoAcordo = c => ehMensalAcordo(c) ? '/mês' : '/pacote';
async function carregarAcordoReajuste(id) {
  if (!id) return null;
  const { data, error } = await supabase.from('contratos')
    .select('id, aluno_id, status, data_inicio, tipo_cobranca, valor_mensal, preco_negociado, preco_reajuste_pendente')
    .eq('id', id).maybeSingle();
  return error ? null : data;
}

// Cancelar um lancamento — no lugar de APAGAR (Fase 0, Etapa 3). Nada some: a linha fica na lista
// como "Cancelado", com o motivo. Motivo de uma lista fechada; cobranca ja' PAGA exige motivo e a
// confirmacao explicita (dinheiro que entrou nao some "por engano" calado). O saldo de aulas do
// aluno e' recalculado. origem: 'financeiro' | 'lixeira' | 'ficha' (so' para a central de uso).
function ModalCancelarLancamento({ pagamento, nomeAluno, origem, onFechar, onCancelado }) {
  const [motivo, setMotivo] = React.useState('');
  const [entendi, setEntendi] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);
  const [pagoNoBanco, setPagoNoBanco] = React.useState(false);   // a lista dizia "em aberto", o banco diz "pago"
  React.useEffect(() => { uso.janela('pagamento_cancelar'); }, []);
  if (!pagamento) return null;
  const pago = pagamento.status === 'pago' || pagoNoBanco;
  const pode = !!motivo && (!pago || entendi) && !salvando;
  async function confirmar() {
    if (!motivo) { uso.bloqueio('pagamento_cancelar', null, null, 'sem_motivo'); toastSM('Escolha o motivo.'); return; }
    if (pago && !entendi) { uso.bloqueio('pagamento_cancelar', null, null, 'sem_confirmacao'); toastSM('Marque que entendeu antes de cancelar um pagamento já recebido.'); return; }
    setSalvando(true);
    // A lista pode estar velha (ex.: recebimento confirmado no celular com o computador aberto).
    // Antes de cancelar algo "em aberto", confere no banco: se ja' esta' pago, mostra o aviso de
    // dinheiro ja' recebido e pede a confirmacao — nada e' gravado nesta volta.
    if (!pago) {
      const { data: atual } = await supabase.from('pagamentos').select('status').eq('id', pagamento.id).maybeSingle();
      if (atual?.status === 'pago') {
        setPagoNoBanco(true); setSalvando(false);
        uso.bloqueio('pagamento_cancelar', null, null, 'virou_pago');
        toastSM('Este pagamento já foi confirmado como recebido. Leia o aviso em vermelho, marque que entendeu e toque de novo.');
        return;
      }
    }
    const res = await rpcPagamento('cancelar_pagamento', { p_id: pagamento.id, p_motivo: motivo });
    if (!res.ok) {
      setSalvando(false);
      if (res.codigo === 'falha') uso.erro('erro.pagamento', res.tecnico);
      uso.bloqueio('pagamento_cancelar', null, null, res.codigo);
      toastSM(res.msg);
      return;
    }
    if (pagamento.aluno_id) await recalcularCreditos(supabase, pagamento.aluno_id);
    setSalvando(false);
    uso.acao(origem === 'ficha' ? 'ficha.pagamento_cancelar' : origem === 'lixeira' ? 'pagamento.excluir' : 'pagamento.cancelar',
      { motivo, estava_pago: res.r.estava_pago ?? pago, ja_feito: !!res.r.ja_feito });
    const msgCanc = res.r.ja_feito ? 'Este lançamento já estava cancelado.'
      : (res.r.estava_pago && !pago) ? `Lançamento cancelado. Atenção: ele já estava como recebido — ${fmtBRL(pagamento.valor)} saiu do que você recebeu. Se foi engano, toque em "Desfazer".`
      : 'Lançamento cancelado. Ele continua na lista como "Cancelado".';
    // v142: "Feito · Desfazer" (P8) — a cobranca volta como estava: em aberto, ou paga (R1), por 24 h.
    if (res.r.ja_feito) toastSM(msgCanc);
    else toastDesfazerSM(msgCanc, { ref_tipo: 'pagamento', ref_id: pagamento.id, esperado: 'cancelamento_cobranca' },
      { papel: 'professor', nomeOutro: nomeAluno, curto: 'cancelamento da cobrança', alunoRecalc: pagamento.aluno_id || null });
    onCancelado && onCancelado(res.r, motivo);
  }
  const fechar = () => { if (!salvando) onFechar && onFechar(); };
  return (
    <Portal>
    <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:90, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px'}}
      onClick={e => { e.stopPropagation(); fechar(); }}>
      <div onClick={e => e.stopPropagation()} onChange={e => e.stopPropagation()} onInput={e => e.stopPropagation()}
        style={{background:'white', borderRadius:'20px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)', width:'100%', maxWidth:'400px', maxHeight:'90vh', overflowY:'auto', padding:'22px'}}>
        <div style={{fontSize:'17px', fontWeight:'800', color:'#16181D'}}>Cancelar lançamento</div>
        <div style={{fontSize:'13px', color:'#6E7480', marginTop:'4px'}}>
          {fmtBRL(pagamento.valor)}{nomeAluno ? ' · ' + nomeAluno : ''} · {pago ? 'já recebido' : 'em aberto'}
        </div>
        <div style={{fontSize:'12.5px', color:'#3D424C', margin:'12px 0 4px', lineHeight:1.5}}>
          Nada é apagado: o lançamento fica na lista como <strong>Cancelado</strong>, com o motivo, e deixa de contar no que você {pago ? 'recebeu' : 'tem a receber'}.
        </div>
        <div style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', margin:'12px 0 6px'}}>Por que cancelar?</div>
        <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
          {MOTIVOS_CANCELAR_PAGAMENTO.map(m => (
            <button key={m.v} type="button" onClick={() => setMotivo(m.v)}
              style={{textAlign:'left', minHeight:'44px', padding:'10px 12px', borderRadius:'10px', border: motivo === m.v ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9',
                      background: motivo === m.v ? '#E7EDFF' : 'white', color: motivo === m.v ? '#1C43AE' : '#16181D', fontSize:'14px',
                      fontWeight: motivo === m.v ? 700 : 500, cursor:'pointer', fontFamily:'inherit'}}>
              {m.l}
            </button>
          ))}
        </div>
        {pago && (
          <label style={{display:'flex', gap:'10px', alignItems:'flex-start', marginTop:'12px', background:'#FEF2F2', border:'1.5px solid #FECACA', borderRadius:'12px', padding:'12px', fontSize:'13px', color:'#991B1B', lineHeight:1.5, cursor:'pointer'}}>
            <input type="checkbox" checked={entendi} onChange={e => setEntendi(e.target.checked)} style={{width:'20px', height:'20px', marginTop:'1px', flexShrink:0}} />
            <span>Este pagamento já está como <strong>recebido</strong>. Cancelar tira {fmtBRL(pagamento.valor)} do que você recebeu. Use só se o dinheiro não entrou ou foi devolvido.</span>
          </label>
        )}
        <div style={{display:'flex', gap:'10px', marginTop:'16px'}}>
          <button type="button" onClick={confirmar} disabled={!pode}
            style={{flex:1, minHeight:'46px', borderRadius:'12px', border:'none', background:'#DC2626', color:'white', fontSize:'14px', fontWeight:'800', cursor: pode ? 'pointer' : 'not-allowed', opacity: pode ? 1 : 0.5, fontFamily:'inherit'}}>
            {salvando ? 'Cancelando…' : 'Cancelar lançamento'}
          </button>
          <button type="button" onClick={fechar} disabled={salvando}
            style={{minHeight:'46px', padding:'0 18px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'14px', fontWeight:'700', cursor:'pointer', fontFamily:'inherit'}}>
            Voltar
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

// Texto do acordo montado pelo BANCO (termos_do_contrato_json, Etapa 4) — e' exatamente o texto
// que fica guardado no aceite. Cada linha "Rotulo: valor" vira uma linha da tabela; nada e'
// reescrito aqui (o app so' arruma na tela).
function TextoDoAcordo({ texto, escuro = false }) {
  const linhas = String(texto || '').split('\n').filter(l => l.trim());
  return (
    <>
      {linhas.map((l, i) => {
        const k = l.indexOf(': ');
        const rot = k > 0 ? l.slice(0, k) : '';
        const val = k > 0 ? l.slice(k + 2) : l;
        const longo = val.length > 30 || !rot;
        return (
          <div key={i} style={{display:'flex', flexDirection: longo ? 'column' : 'row', justifyContent:'space-between', gap: longo ? '2px' : '12px',
                               padding:'10px 0', borderBottom: i === linhas.length - 1 ? 'none' : (escuro ? '1px solid rgba(255,255,255,0.15)' : '1px solid #E7EDFF')}}>
            {rot && <span style={{color: escuro ? '#C2D4FF' : '#6E7480', fontSize:'13px'}}>{rot}</span>}
            <span style={{color: escuro ? 'white' : '#16181D', fontSize:'13px', fontWeight:'700', textAlign: longo ? 'left' : 'right', lineHeight:1.45}}>{val}</span>
          </div>
        );
      })}
    </>
  );
}
// garantirCreditosSM: substituido por recalcularCreditos (src/regras/creditos.js)

const AppContext = React.createContext(null);
function useApp() { return React.useContext(AppContext); }

const Icons = {
  Home: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  Users: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Calendar: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  DollarSign: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  Settings: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  Bell: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  Plus: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  X: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Check: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><polyline points="20 6 9 17 4 12"/></svg>,
  Edit: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  Trash: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>,
  CreditCard: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  LogOut: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  Package: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  ClipboardList: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/></svg>,
  Eye: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  AlertTriangle: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  CheckCircle: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  Link: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  Receipt: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1z"/><line x1="8" y1="9" x2="16" y2="9"/><line x1="8" y1="13" x2="14" y2="13"/></svg>,
  FileText: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  Send: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
  BarChart2: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

// Dinheiro sempre do mesmo jeito: R$ 2.000,00. Antes cada tela formatava de um modo
// ("R$ 2.000", "R$ 2000.00") e o mesmo numero parecia dois numeros diferentes.
function fmtBRL(v, { semCentavos = false } = {}) {
  const n = Number(v) || 0;
  return 'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: semCentavos ? 0 : 2, maximumFractionDigits: semCentavos ? 0 : 2 });
}

// No celular a janela sobe de baixo (bottom sheet) — o CSS .plen-modal cuida disso.
// Janela padrao. Duas regras que valem para TODAS as janelas do app (Marcus, 29/09):
//  1. Se a pessoa mexeu em algum campo e toca no X ou fora da janela, o app pergunta antes
//     de jogar fora. A janela percebe sozinha (qualquer campo dentro dela que mude marca
//     "mexeu"); quem salva e fecha por codigo nao passa pela pergunta.
//  2. `footer`: rodape preso embaixo, com o botao principal sempre a vista — o meio rola.
// Toda janela e' desenhada direto no <body>, fora do painel que rola (.app-main). No
// celular o painel e' position:fixed, o que cria um "andar" proprio de pintura: uma janela
// desenhada dentro dele ficava presa nesse andar e o menu de baixo (irmao do painel)
// pintava por cima do botao de confirmar de todo cadastro (Marcus, 29/09, dois prints).
// Fora do painel, a janela cobre tudo — topo, menu, "+" — como num app nativo.
function Portal({ children }) {
  return createPortal(children, document.body);
}

// `uso`: nome da janela para a central de uso (janela.abrir). Sem nome, conta como 'outra'.
function Modal({ open, onClose, title, children, wide = false, footer = null, sujo = false, uso: nomeUso }) {
  const mexeu = React.useRef(false);
  React.useEffect(() => { if (open) uso.janela(nomeUso || 'outra'); }, [open]);
  if (!open) return null;
  function fecharPeloUsuario() {
    const preenchido = mexeu.current || sujo;
    if (preenchido && !window.confirm('Sair sem salvar? O que você preencheu será perdido.')) return;
    if (preenchido) uso.evento('janela.descartar', { detalhes: { nome: nomeUso || 'outra' } });
    onClose && onClose();
  }
  return (
    <Portal>
    <div className="plen-modal-wrap fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(22,24,29,.55)'}} onClick={fecharPeloUsuario}>
      <div className={`plen-modal bg-white w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`} style={{borderRadius:'20px', boxShadow:'0 24px 60px rgba(22,24,29,.25)'}} onClick={e => e.stopPropagation()}
        onInput={() => { mexeu.current = true; }} onChange={() => { mexeu.current = true; }}>
        <div className="plen-modal-cabecalho" style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 20px 12px'}}>
          <h2 style={{fontSize:'17px', fontWeight:'800', color:'#16181D', margin:0}}>{title}</h2>
          <button onClick={fecharPeloUsuario} aria-label="Fechar" style={{width:'36px', height:'36px', borderRadius:'10px', border:'none', background:'#F0F1F4', color:'#3D424C', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center'}}><Ico n="x" s={18} /></button>
        </div>
        <div className="plen-modal-corpo" style={{padding:'4px 20px 20px'}}>{children}</div>
        {footer && <div className="plen-modal-rodape">{footer}</div>}
      </div>
    </div>
    </Portal>
  );
}

function Card({ children, className = '', style }) {
  return <div className={`plen-card p-5 ${className}`} style={style}>{children}</div>;
}

function Badge({ children, color = 'blue' }) {
  const colors = { blue: 'bg-blue-100 text-blue-700', green: 'bg-green-100 text-green-700', red: 'bg-red-100 text-red-700', yellow: 'bg-yellow-100 text-yellow-700', gray: 'bg-gray-100 text-gray-600', purple: 'bg-purple-100 text-purple-700', orange: 'bg-orange-100 text-orange-700' };
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${colors[color] || colors.gray}`} style={{lineHeight:1.2}}>{children}</span>;
}

function Button({ children, onClick, variant = 'primary', className = '', disabled = false, type = 'button', style }) {
  // Um botao so' para o app inteiro: 44px de altura (dedo no sol), 700, raio 12.
  // Primario = azul da marca. Nada de gradiente aqui — gradiente e' so' para o herói.
  const base = { height:'44px', padding:'0 18px', borderRadius:'12px', fontWeight:'700', fontSize:'14px', border:'1.5px solid transparent',
                 display:'inline-flex', alignItems:'center', justifyContent:'center', gap:'8px', cursor: disabled ? 'not-allowed' : 'pointer',
                 opacity: disabled ? 0.55 : 1, transition:'background .12s, transform .06s', whiteSpace:'nowrap', lineHeight:1 };
  const v = {
    primary:   { background:'#2F6BFF', color:'white' },
    secondary: { background:'white', color:'#3D424C', borderColor:'#E2E4E9' },
    danger:    { background:'#FEE2E2', color:'#B91C1C' },
    success:   { background:'#0E9F6E', color:'white' },
    ghost:     { background:'transparent', color:'#6E7480', padding:'0 10px', height:'40px' },
  }[variant] || {};
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={className} style={{...base, ...v, ...style}}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = 'scale(.98)'; }}
      onMouseUp={e => { e.currentTarget.style.transform = 'none'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}>
      {children}
    </button>
  );
}

// Campo de dinheiro do app inteiro (30/09). Mostra "2.500" enquanto digita e "2.500,00" ao sair;
// entrega para quem usa (onValor) o numero puro em texto ("2500" / "2500.5"), para as contas e o
// banco continuarem iguais. Sem onValor funciona solto: quem salva le' o campo pelo id e passa
// por numDinheiro. Quando o valor muda por fora (ex.: reajuste por %), o campo acompanha.
function CampoDinheiro({ value, onValor, onBlur, ...props }) {
  const fmt = v => (v === '' || v === null || v === undefined || numDinheiro(v) === 0 && String(v).trim() === '') ? '' : formatarDinheiro(v);
  const [txt, setTxt] = React.useState(() => fmt(value));
  React.useEffect(() => {
    if (value === undefined) return;
    if (numDinheiro(value) !== numDinheiro(txt)) setTxt(fmt(value));
  }, [value]);
  return (
    <input type="text" inputMode="decimal" autoComplete="off" {...props} value={txt}
      onChange={e => { const t = limparDinheiro(e.target.value); setTxt(t); onValor && onValor(t ? String(numDinheiro(t)) : ''); }}
      onBlur={e => { setTxt(t => formatarDinheiro(t)); onBlur && onBlur(e); }} />
  );
}

function Input({ label, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      <input className="rounded-xl text-sm" style={{border:'1.5px solid #E2E4E9', padding:'11px 14px', fontSize:'15px', background:'white', color:'#16181D'}} {...props} />
    </div>
  );
}

function calcProximaAulaInicio(horarios) {
  const agora = new Date();
  const slots = (horarios || []).filter(h => h.dia_semana !== null && h.dia_semana !== undefined && h.hora);
  if (!slots.length) return null;
  for (let d = 0; d <= 14; d++) {
    const data = new Date(agora);
    data.setDate(data.getDate() + d);
    data.setHours(0, 0, 0, 0);
    for (const h of slots) {
      if (data.getDay() === h.dia_semana) {
        const [hh, mm] = h.hora.split(':').map(Number);
        const aulaTime = new Date(data);
        aulaTime.setHours(hh, mm, 0, 0);
        if (aulaTime > agora) {
          const p = n => String(n).padStart(2, '0');
          return `${data.getFullYear()}-${p(data.getMonth()+1)}-${p(data.getDate())}`;
        }
      }
    }
  }
  return null;
}

function calcDataFimPorAulas(dataInicioStr, diasSemana, totalAulas) {
  const dias = (diasSemana || []).filter(d => d !== null && d !== undefined);
  if (!dataInicioStr || !dias.length || !totalAulas) return '';
  let data = new Date(dataInicioStr + 'T12:00:00');
  let count = 0, ultima = null, maxIter = 2000;
  while (count < totalAulas && maxIter-- > 0) {
    if (dias.includes(data.getDay())) { count++; ultima = new Date(data); }
    if (count < totalAulas) data.setDate(data.getDate() + 1);
  }
  if (!ultima) return '';
  const p = n => String(n).padStart(2, '0');
  return `${ultima.getFullYear()}-${p(ultima.getMonth()+1)}-${p(ultima.getDate())}`;
}

function Select({ label, children, ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      <select className="rounded-xl text-sm bg-white" style={{border:'1.5px solid #E2E4E9', padding:'11px 14px', fontSize:'15px', color:'#16181D'}} {...props}>{children}</select>
    </div>
  );
}

function StatCard({ label, value, color = 'indigo', icon, sub }) {
  // Cartao de numero no padrao Nubank: fundo branco, icone com a cor do estado, numero
  // grande. Os gradientes verde/azul/laranja saíram — cor forte e' so' para estado.
  const tom = { indigo:['#2F6BFF','#E7EDFF'], blue:['#2F6BFF','#E7EDFF'], purple:['#2F6BFF','#E7EDFF'], green:['#0E9F6E','#DCF5EC'],
                red:['#DC2626','#FEE2E2'], orange:['#D97706','#FEF3C7'], yellow:['#D97706','#FEF3C7'], gray:['#6E7480','#F0F1F4'] }[color] || ['#2F6BFF','#E7EDFF'];
  const valStr = String(value);
  const tamanho = valStr.length > 11 ? '18px' : valStr.length > 7 ? '22px' : '26px';
  return (
    <div className="plen-card" style={{padding:'16px 18px', display:'flex', flexDirection:'column', gap:'8px', minWidth:0}}>
      <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
        <span style={{width:'28px', height:'28px', borderRadius:'8px', background:tom[1], color:tom[0], display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>{icon}</span>
        <span style={{fontSize:'12.5px', fontWeight:'600', color:'#6E7480', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{label}</span>
      </div>
      <div className="num" style={{fontSize:tamanho, fontWeight:'800', color:'#16181D', letterSpacing:'-0.02em', lineHeight:1.1}}>{value}</div>
      {sub && <div style={{fontSize:'12px', color:'#6E7480'}}>{sub}</div>}
    </div>
  );
}

function getStatusAluno(aluno) {
  const contrato = aluno.contratos?.find(c => c.status === 'ativo');
  if (!contrato) return { label: 'Sem plano', color: 'gray' };
  // Aluno ainda nao aceitou o convite (nao tem conta vinculada): isso vale MAIS do que
  // qualquer coisa de pagamento — nao faz sentido "confirmar pagamento" de quem nem
  // entrou no app ainda. Antes essa checagem vinha depois da de pagamento pendente e o
  // status pulava direto para "Confirmar pagamento" (relato do Cadu, 23/09).
  if (!aluno.user_id) return { label: 'Convite enviado', color: 'blue' };
  const now = new Date(); const pad2 = n => String(n).padStart(2,'0');
  const hoje = `${now.getFullYear()}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())}`;
  // v149: cobranca do pacote da fila que ainda nao venceu (e nao foi avisada) nao e' pendencia.
  // v155: so' a do contrato 'proximo' (pacote novo/mensal/turma com vencimento futuro continua pendente)
  const ehProx = p => !!p.contrato_id && (aluno.contratos || []).some(c => c.id === p.contrato_id && c.status === 'proximo');
  const pagPendente = aluno.pagamentos?.find(p => p.status === 'pendente'
    && !(ehProx(p) && p.data_vencimento && String(p.data_vencimento).slice(0, 10) > hoje && !p.declarado_pago_em));
  // v155: divida vem antes de "Plano expirado" (senao a lista mostra "Renovar" e esconde a cobranca)
  if (!pagPendente && contrato.data_fim < hoje) return { label: 'Plano expirado', color: 'orange' };
  if (pagPendente) {
    // O aluno tem um botao "Ja' paguei" no Financeiro dele (avisa o professor, mas quem
    // da' baixa de verdade continua sendo o professor, conferindo se o Pix caiu). Enquanto
    // ele nao avisa, e' "Pagamento pendente" (nada a conferir ainda); depois que ele avisa,
    // vira "Aguardando sua confirmação" (agora sim tem algo pro professor conferir).
    if (pagPendente.declarado_pago_em) return { label: 'Aguardando sua confirmação', color: 'red', pagPendenteId: pagPendente.aluno_id };
    return { label: 'Pagamento pendente', color: 'red', pagPendenteId: pagPendente.aluno_id };
  }
  const credito = aluno.creditos?.[0];
  const restante = credito ? credito.total - credito.usado : 0;
  if (credito && credito.total > 0 && restante <= 2 && restante >= 0) return { label: 'Poucos créditos', color: 'yellow' };
  return { label: 'Ativo', color: 'green' };
}

function AuthInput({ label, required, type, ...props }) {
  // Campo da tela escura de entrada: fundo um tom acima do preto da marca, borda fina,
  // texto branco. No foco a borda vira o azul da marca.
  const [focused, setFocused] = React.useState(false);
  // Senha com olhinho: quem digita no celular erra e nao ve o que errou — e a saida
  // hoje e' apagar tudo e tentar de novo. O padrao (Google, banco, Apple) e' deixar
  // espiar. Fica escondido por padrao; so' mostra enquanto a pessoa segura a decisao.
  const ehSenha = type === 'password';
  const [vendo, setVendo] = React.useState(false);
  const tipoReal = ehSenha ? (vendo ? 'text' : 'password') : type;
  return (
    <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
      <label style={{fontSize:'12.5px', fontWeight:'600', color:'rgba(255,255,255,0.72)', letterSpacing:'0.01em'}}>
        {label}{required && <span style={{color:'#6E9BFF', marginLeft:'2px'}}>*</span>}
      </label>
      <div style={{position:'relative', display:'flex'}}>
        <input
          className="auth-input"
          type={tipoReal}
          style={{border: focused ? '1.5px solid #2F6BFF' : '1.5px solid rgba(255,255,255,0.12)', borderRadius:'14px', padding: ehSenha ? '14px 52px 14px 16px' : '14px 16px', fontSize:'16px', color:'white', background:'rgba(255,255,255,0.06)', outline:'none', transition:'border-color 0.15s', width:'100%'}}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          required={required}
          {...props}
        />
        {ehSenha && (
          <button type="button" onClick={() => setVendo(v => !v)}
            aria-label={vendo ? 'Ocultar senha' : 'Mostrar senha'}
            title={vendo ? 'Ocultar senha' : 'Mostrar senha'}
            style={{position:'absolute', right:'4px', top:'50%', transform:'translateY(-50%)', width:'44px', height:'44px',
                    display:'flex', alignItems:'center', justifyContent:'center', border:'none', background:'transparent',
                    color: vendo ? '#6E9BFF' : 'rgba(255,255,255,0.45)', cursor:'pointer', padding:0, borderRadius:'12px'}}>
            <Ico n={vendo ? 'eye-off' : 'eye'} s={19} />
          </button>
        )}
      </div>
    </div>
  );
}

// Central de uso — funis da entrada. Atravessam AuthPage -> TipoUsuario -> app e recargas
// (volta do Google, reload depois de assumir a ficha), por isso ficam fora de componente.
const funilCadastro = uso.fluxo('cadastro');
const funilConvite = uso.fluxo('entrada_convite');
const naRotaPainel = () => { try { return window.location.pathname.replace(/\/$/, '') === '/painel'; } catch { return false; } };
// Erro do signUp do Supabase em codigo (a mensagem e' texto livre em ingles).
function motivoSignup(msg) {
  const m = String(msg || '');
  if (/password/i.test(m)) return 'senha_fraca';
  if (/rate|too many|security purposes/i.test(m)) return 'muitas_tentativas';
  if (/email/i.test(m)) return 'email_invalido';
  return 'signup_falhou';
}

// v147 — "Recebeu um link de convite no WhatsApp? [Colar convite]", no app da loja: na entrada sem convite
// guardado (quem = 'entrada'), no aluno sem professor ('aluno') e no Inicio do professor sem alunos
// ('professor'). Le' a area de transferencia — no iPhone aparece o balaozinho "Colar"/"Paste" do sistema e a
// pessoa toca nele (testado no simulador, 21/10; ignorar o balao = NotAllowedError). Sem convite ali, abre o
// campo para colar ou digitar o codigo. Convite valido segue IGUAL ao toque no link (abrirConviteColadoSM).
// O aluno com conta e sem ficha cai no "Convite recebido!" de sempre: o link pessoal assume a ficha que o
// professor fez (assumir_ficha_aluno) — nao nasce ficha nova.
// Fecho v147 (revisao, achado 1): onPedido(code) — o professor que ja' abriu o pedido de um aluno (no navegador do
// WhatsApp, por exemplo) e cola o MESMO link no app: o banco diz "ja' usado", mas o pedido esta' na lista dele para
// completar. Em vez de "peca um link novo" (falso, e nascia um pedido repetido), abre direto nesse pedido.
function ColarConvite({ quem = 'entrada', escuro = false, onde = '', onPedido = null, compacto = false }) {
  const [campo, setCampo] = React.useState(false);
  const [ocupado, setOcupado] = React.useState('');   // '' | 'lendo' | 'conferindo'
  const [valor, setValor] = React.useState('');
  const [erro, setErro] = React.useState('');
  const iosApp = APP_NATIVO && !!(window.Capacitor && window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'ios');
  const origem = onde || (quem === 'entrada' ? 'entrada' : quem === 'aluno' ? 'sem_professor' : 'inicio_prof');
  // Sem login o banco so' aceita acoes entrada.* (registrar_uso)
  const conta = (resultado, via) => { try { uso.acao(quem === 'entrada' ? 'entrada.colar_convite' : 'convite.colar', { resultado, via, origem }); } catch {} };
  // Cada toque (colar / continuar) e' uma tentativa: a leitura atrasada do balao so' vale se ainda for a ultima.
  const tentativa = React.useRef(0);
  const vivo = React.useRef(true);
  React.useEffect(() => () => { vivo.current = false; }, []);

  async function seguir(code, via) {
    setErro(''); setOcupado('conferindo');
    let c = null;
    resolverConvite.motivo = '';
    try { c = await resolverConvite(code); } catch {}
    if (!vivo.current) return;
    if (!c) {
      // motivo vazio = o banco nao respondeu (sem internet): nao e' "convite vencido". O codigo fica no campo.
      const m = resolverConvite.motivo;
      const bloq = m === 'convite_bloqueado';
      if (quem === 'professor' && m && !bloq && onPedido) {
        let lista = null;
        try { const { data, error } = await supabase.rpc('professor_pedidos_a_completar'); if (!error && Array.isArray(data)) lista = data; } catch {}
        if (!vivo.current) return;
        const pd = lista ? lista.find(x => String((x && x.code) || '').toUpperCase() === code) : null;
        if (pd) { conta('pedido_aberto', via); setOcupado(''); onPedido(pd.code); return; }
      }
      conta(bloq ? 'bloqueado' : m ? 'invalido' : 'sem_rede', via);
      setErro(bloq ? 'Muitas tentativas seguidas. Espere alguns minutos e tente de novo.'
        : m && quem === 'entrada' ? 'Esse convite já foi aberto ou venceu. Se você já abriu este convite antes, entre na sua conta: ele está lá. Se não, peça um link novo para quem te convidou.'
        : m ? 'Esse convite não vale mais (já foi usado ou venceu). Peça um link novo para quem te convidou.'
        : 'Não deu para conferir o convite agora. Confira a internet e toque em “Continuar” de novo.');
      if (!m) setValor(v => v.trim() ? v : code);
      setOcupado(''); setCampo(true); return;
    }
    const deAluno = c.tipo === 'aluno_prof';
    // v147 (juncao com a v144ind): o professor pode colar o convite de OUTRO professor (indicacao) — o App segue
    // igual ao toque no link: conta nova (24h) fica anotada como indicada; conta antiga, nada muda.
    const deProf = c.tipo === 'prof_prof';
    if ((quem === 'aluno' && (deAluno || !c.professor_id)) || (quem === 'professor' && !deAluno && !deProf)) {
      conta('tipo_errado', via);
      setErro(quem === 'aluno' ? 'Esse link é de um aluno chamando um professor — não é um convite para você entrar como aluno.'
        : 'Esse link é de um professor chamando um aluno — não é para a conta de professor.');
      setOcupado(''); setCampo(true); return;
    }
    conta('ok', via);
    try { uso.enviarAgora(); } catch {}
    abrirConviteColadoSM(code);
  }
  async function colar() {
    if (ocupado) return;
    setErro('');
    const minha = ++tentativa.current;
    let texto = null, semResposta = false;
    if (navigator.clipboard && navigator.clipboard.readText) {
      setOcupado('lendo');
      // Se o balao "Colar" do iPhone sumir sem resposta, a leitura pode ficar parada: depois de 10 s abre o
      // campo. Se a pessoa ainda tocar em "Colar" e vier um convite, segue (se nao tentou outra coisa).
      let leitura = null;
      try { leitura = Promise.resolve(navigator.clipboard.readText()); } catch (e) { leitura = Promise.reject(e); }
      const ESPERA = {};
      let r = null;
      try { r = await Promise.race([leitura, new Promise(res => setTimeout(() => res(ESPERA), 10000))]); } catch (e) { r = null; }
      if (r === ESPERA) {
        semResposta = true;
        leitura.then(t => { const cd = codigoConviteDoTextoSM(t); if (cd && vivo.current && tentativa.current === minha) seguir(cd, 'area'); }, () => {});
      } else texto = typeof r === 'string' ? r : null;
      if (!vivo.current) return;
      setOcupado('');
    }
    const code = codigoConviteDoTextoSM(texto);
    if (code) return seguir(code, 'area');
    conta(semResposta ? 'sem_resposta' : texto == null ? 'sem_permissao' : 'sem_convite', 'area');
    if (texto != null) setErro('Não achei um convite no que está copiado. Cole o link abaixo ou digite o código.');
    setCampo(true);
  }
  function continuar(e) {
    if (e) e.preventDefault();
    if (ocupado) return;
    tentativa.current++;
    const code = codigoConviteDoTextoSM(valor);
    if (!code) { conta('codigo_invalido', 'campo'); setErro('Cole o link inteiro do convite ou digite o código (6 ou 10 letras e números).'); return; }
    seguir(code, 'campo');
  }

  const cor = escuro
    ? { fundo:'rgba(255,255,255,0.06)', borda:'rgba(255,255,255,0.16)', titulo:'white', texto:'rgba(255,255,255,0.68)', icoFundo:'rgba(47,107,255,0.25)', ico:'#A9C2FF', erro:'#FCA5A5', campo:'rgba(255,255,255,0.08)', sec:'#A9C2FF' }
    : { fundo:'white', borda:'#E2E4E9', titulo:'#16181D', texto:'#6E7480', icoFundo:'#E7EDFF', ico:'#2F6BFF', erro:'#DC2626', campo:'white', sec:'#2F6BFF' };
  const sub = quem === 'aluno' ? 'Se o seu professor já te cadastrou, use o link dele: assim você não fica com dois cadastros.'
    : quem === 'professor' ? 'Se um aluno ou outro professor te mandou um link do Plen, cole aqui — o Plen abre o convite.'
    : 'Copie o link na conversa e toque em “Colar convite”: o Plen abre o seu convite.';
  const btnPrim = { width:'100%', minHeight:'48px', border:'none', borderRadius:'14px', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'15px', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', padding:'10px 14px', boxSizing:'border-box' };
  // v157: linha discreta de rodape' (tela de entrada). O quadro completo so' aparece quando o toque nao resolveu
  // sozinho (abriu o campo ou deu erro) — ai' a pessoa precisa ver o campo e a explicacao.
  if (compacto && !campo && !erro) return (
    <div style={{textAlign:'center', fontSize:'12.5px', color:cor.texto, lineHeight:1.6}}>
      Recebeu um convite no WhatsApp?{' '}
      <button type="button" onClick={colar} disabled={!!ocupado}
        style={{background:'none', border:'none', padding:'6px 4px', color:cor.sec, fontWeight:'800', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', minHeight:'32px'}}>
        {ocupado === 'lendo' ? 'Toque em “Colar” no balãozinho' : ocupado === 'conferindo' ? 'Abrindo o convite…' : 'Colar convite'}
      </button>
    </div>
  );
  return (
    <div style={{background:cor.fundo, border:'1.5px solid ' + cor.borda, borderRadius:'16px', padding:'14px 16px', textAlign:'left', boxSizing:'border-box', width:'100%'}}>
      <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'12px'}}>
        <span style={{width:'38px', height:'38px', borderRadius:'11px', background:cor.icoFundo, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n="link" s={19} c={cor.ico} /></span>
        <div style={{minWidth:0}}>
          <div style={{fontSize:'14.5px', fontWeight:'800', color:cor.titulo, lineHeight:1.3}}>Recebeu um link de convite no WhatsApp?</div>
          <div style={{fontSize:'12.5px', color:cor.texto, marginTop:'3px', lineHeight:1.45}}>{sub}</div>
        </div>
      </div>
      {!campo ? (
        <>
          <button type="button" onClick={colar} disabled={!!ocupado} style={{...btnPrim, opacity: ocupado === 'conferindo' ? 0.7 : 1}}>
            <Ico n="clipboard-text" s={18} c="white" />
            {ocupado === 'lendo' ? 'Toque em “Colar” no balãozinho' : ocupado === 'conferindo' ? 'Abrindo o convite…' : 'Colar convite'}
          </button>
          {iosApp && <div style={{fontSize:'12px', color:cor.texto, marginTop:'7px', lineHeight:1.45, textAlign:'center'}}>O iPhone mostra um balãozinho “Colar” (ou “Paste”) — é só tocar nele.</div>}
        </>
      ) : (
        <form onSubmit={continuar}>
          <label style={{display:'block', fontSize:'12.5px', fontWeight:'800', color:cor.titulo, marginBottom:'6px'}}>Cole o link ou digite o código do convite</label>
          <input value={valor} onChange={e => { setValor(e.target.value.slice(0, 400)); setErro(''); }}
            placeholder="Ex.: K7MPQ2XRTA" autoCapitalize="characters" autoCorrect="off" autoComplete="off" spellCheck={false}
            style={{width:'100%', boxSizing:'border-box', minHeight:'48px', padding:'12px 14px', borderRadius:'12px', border:'1.5px solid ' + (erro ? cor.erro : cor.borda), fontSize:'16px', background:cor.campo, color:cor.titulo, outline:'none', fontFamily:'inherit'}} />
          {erro && <div role="alert" style={{fontSize:'12.5px', fontWeight:'700', color:cor.erro, marginTop:'7px', lineHeight:1.45}}>{erro}</div>}
          <div style={{fontSize:'12px', color:cor.texto, marginTop:'6px', lineHeight:1.45}}>Para colar: segure o dedo no campo e toque em “Colar”.</div>
          <button type="submit" disabled={!!ocupado || !valor.trim()} style={{...btnPrim, marginTop:'10px', opacity: (ocupado || !valor.trim()) ? 0.55 : 1}}>
            {ocupado === 'conferindo' ? 'Abrindo o convite…' : 'Continuar'}
          </button>
          <div style={{fontSize:'12.5px', color:cor.texto, marginTop:'10px', lineHeight:1.5}}>
            Ou toque no link que você recebeu no WhatsApp — com o Plen instalado, ele abre aqui direto.
          </div>
        </form>
      )}
    </div>
  );
}

// v147 — PAGINA DO LINK NO iPHONE SEM O APP. O link do convite aberto no Safari (ou no navegador do WhatsApp)
// mostra primeiro quem convidou e o caminho do app: "Baixar o Plen" COPIA o link (no proprio toque) e abre a
// App Store; dentro do app, "Colar convite" continua dali. O combinado (dias, valor) NAO aparece: sem login o
// banco (ver_convite) so' devolve os nomes — e a pagina nao mostra nada alem do que o convite ja' mostrava.
// "Continuar no navegador" = o caminho de antes (vale ate' fechar a aba). Android e computador nao passam aqui.
function ConviteBaixarApp({ convite, code, onNavegador }) {
  useTemaTela(true);
  const [copiou, setCopiou] = React.useState(null);   // null = ainda nao tocou em "Baixar o Plen"
  React.useEffect(() => { uso.area('entrada'); uso.tela('entrada.convite_ios'); }, []);
  const c = convite || null;
  const deAluno = c?.tipo === 'aluno_prof';
  // v147 (juncao com a v144ind): o link de um professor chamando OUTRO professor (indicacao) tambem passa aqui.
  const deProf = c?.tipo === 'prof_prof';
  const nomeOk = x => { const s = String(x || '').trim(); return s && !s.includes('@') ? s : ''; };
  const primeiro = (deAluno ? nomeOk(c?.anome) : nomeOk(c?.pnome)).split(' ')[0];
  const oi = c && !deAluno && !deProf && c.aid ? nomeOk(c.anome).split(' ')[0] : '';
  const titulo = !c ? 'Você recebeu um convite para o Plen'
    : deAluno ? `${primeiro || 'Um aluno'} quer organizar as aulas com você no Plen`
    : deProf ? `${primeiro || 'Um professor'} convidou você para dar suas aulas pelo Plen`
    : c.aid ? `${primeiro || 'Seu professor'} cadastrou você no Plen`
    : `${primeiro || 'Um professor'} convidou você para o Plen`;
  const sub = !c ? 'Aulas, saldo e pagamentos entre professor e aluno, num lugar só.'
    : deAluno ? 'Crie sua conta de professor: o pedido aparece lá para você conferir.'
    : deProf ? 'Crie sua conta de professor: agenda, pacotes e alunos num lugar só.'
    : c.aid ? `Suas aulas com ${primeiro || 'seu professor'} já estão lá: agenda, saldo de aulas e pagamentos.`
    : 'Agenda, saldo de aulas e pagamentos num lugar só.';
  const link = `${window.location.origin}${window.location.pathname}?c=${code}`;
  // Funil do convite (entrada_convite): quem vai para a App Store sai daqui como "foi_para_app" (o app abre
  // outra passada quando ele colar). Se voltar e seguir no navegador, abre de novo. O pedido do aluno nao usa,
  // nem o convite de professor para professor (o App tambem nao o conta nesse funil: entrada.link_indicacao).
  const noFunil = c && !deAluno && !deProf;
  function baixar() {
    const ok = copiarNoToqueSM(link);
    setCopiou(ok);
    try { uso.acao('entrada.convite_ios_baixar', { copiou: ok }); if (noFunil) funilConvite.sair('foi_para_app'); uso.enviarAgora(); } catch {}
  }
  function navegador() {
    try { sessionStorage.setItem('plen_convite_navegador', '1'); } catch {}
    try { uso.acao('entrada.convite_ios_navegador'); if (noFunil && copiou !== null) funilConvite.passo(1, { tipo: c.aid ? 'pessoal' : 'geral' }); } catch {}
    onNavegador && onNavegador();
  }
  const passos = [
    <>Toque em <b>Baixar o Plen</b> — o convite fica copiado.</>,
    <>Instale o Plen pela App Store.</>,
    <>Abra o Plen e toque em <b>Colar convite</b>.</>,
  ];
  return (
    <div style={{minHeight:'100vh', background:'linear-gradient(150deg,#16181D,#1C43AE,#2455D6)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px 16px', boxSizing:'border-box', fontFamily:"'Manrope',-apple-system,BlinkMacSystemFont,sans-serif"}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.35)', padding:'26px 20px 20px', width:'100%', maxWidth:'420px', boxSizing:'border-box'}}>
        <img src="/logo-cor.svg" alt="Plen" style={{height:'24px', display:'block', marginBottom:'18px'}} onError={e => { e.currentTarget.style.display = 'none'; }} />
        {oi && <div style={{fontSize:'14px', fontWeight:'700', color:'#2F6BFF', marginBottom:'4px'}}>Oi, {oi}!</div>}
        <h1 style={{fontFamily:"'Sora',sans-serif", fontSize:'22px', fontWeight:'700', color:'#16181D', lineHeight:1.28, margin:'0 0 8px', letterSpacing:'-0.3px'}}>{titulo}</h1>
        <p style={{fontSize:'14.5px', color:'#3D424C', lineHeight:1.55, margin:'0 0 16px'}}>{sub}</p>
        <div style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'14px', padding:'12px 14px', marginBottom:'14px', display:'flex', flexDirection:'column', gap:'9px'}}>
          {passos.map((p, i) => (
            <div key={i} style={{display:'flex', gap:'10px', alignItems:'flex-start', fontSize:'13.5px', color:'#16181D', lineHeight:1.45}}>
              <span style={{width:'22px', height:'22px', borderRadius:'50%', background:'#2F6BFF', color:'white', fontSize:'12px', fontWeight:'800', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>{i + 1}</span>
              <span>{p}</span>
            </div>
          ))}
        </div>
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={baixar}
          style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', minHeight:'52px', boxSizing:'border-box', padding:'12px 16px', borderRadius:'14px', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'16px', textDecoration:'none', boxShadow:'0 8px 24px rgba(47,107,255,0.3)'}}>
          <Ico n={ICONE_APP_STORE} s={18} c="white" /> Baixar o Plen
        </a>
        {copiou !== null && (copiou ? (
          <div role="status" style={{marginTop:'10px', background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'10px 12px', fontSize:'13px', color:'#0A6F4D', lineHeight:1.5, fontWeight:'700'}}>
            <Ico n="circle-check" s={14} c="#0E9F6E" /> Convite copiado. Depois de instalar, abra o Plen e toque em “Colar convite”.
          </div>
        ) : (
          <div role="status" style={{marginTop:'10px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'12px', padding:'10px 12px', fontSize:'13px', color:'#92400E', lineHeight:1.5, fontWeight:'700'}}>
            Depois de instalar, volte ao WhatsApp e toque no link de novo — ele abre direto no Plen.
          </div>
        ))}
        <button type="button" onClick={navegador}
          style={{width:'100%', minHeight:'48px', marginTop:'10px', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'14px', color:'#3D424C', fontWeight:'700', fontSize:'14.5px', cursor:'pointer', fontFamily:'inherit'}}>
          Continuar no navegador
        </button>
        <p style={{fontSize:'12px', color:'#6E7480', lineHeight:1.5, margin:'12px 0 0', textAlign:'center'}}>
          Já tem o Plen no celular? Volte ao WhatsApp e toque no link de novo — ele abre direto no app.
        </p>
      </div>
    </div>
  );
}

// v157 — ABERTURA DO APP. No celular, a primeira tela monta o logo peca por peca (pontinhos azuis
// convergem, o anel se desenha, a barra sobe, as letras acendem) e so' depois o formulario entra. Os
// tracos sao os do kit da marca (Plen_Marca/01_logo, versao para fundo escuro). O anel do simbolo e'
// desenhado como um circulo de traco grosso (r 16,5 + traco 15 = a coroa de 9 a 24 do logo) para poder
// "nascer" por stroke-dashoffset. Sem `animar`, e' o logo parado — igual ao <img> que ficava aqui.
// Quem desliga animacoes no celular (prefers-reduced-motion) ve' o logo ja' montado (CSS no index.html).
const LETRAS_PLEN_SM = [
  'M66 -200V552H210V314H194Q197 395 226.5 452.0Q256 509 307.0 538.5Q358 568 424 568Q481 568 527.0 547.5Q573 527 606.0 489.5Q639 452 656.5 401.0Q674 350 674 289V265Q674 204 658.0 152.5Q642 101 610.0 62.5Q578 24 531.5 3.0Q485 -18 425 -18Q361 -18 312.0 7.5Q263 33 234.5 84.5Q206 136 204 214L248 160V-200ZM370 132Q406 132 433.5 150.5Q461 169 476.5 202.0Q492 235 492 277Q492 320 476.5 351.5Q461 383 434.0 400.5Q407 418 370 418Q337 418 308.5 403.0Q280 388 262.0 359.0Q244 330 244 289V255Q244 216 263.0 188.5Q282 161 311.0 146.5Q340 132 370 132Z',
  'M722.0 0V730H904.0V0ZM660.0 598V730H904.0V598Z',
  'M1237.0 -20Q1167.0 -20 1113.0 4.0Q1059.0 28 1022.0 69.0Q985.0 110 965.5 161.0Q946.0 212 946.0 266V286Q946.0 342 965.5 393.5Q985.0 445 1021.5 485.0Q1058.0 525 1111.0 548.5Q1164.0 572 1231.0 572Q1320.0 572 1382.0 532.0Q1444.0 492 1477.0 426.5Q1510.0 361 1510.0 284V226H1022.0V324H1405.0L1342.0 280Q1342.0 327 1329.5 359.5Q1317.0 392 1292.5 409.0Q1268.0 426 1231.0 426Q1195.0 426 1168.5 409.5Q1142.0 393 1128.0 359.5Q1114.0 326 1114.0 275Q1114.0 229 1127.0 195.5Q1140.0 162 1167.0 144.0Q1194.0 126 1237.0 126Q1275.0 126 1299.5 139.0Q1324.0 152 1334.0 174H1500.0Q1488.0 118 1452.5 74.0Q1417.0 30 1362.5 5.0Q1308.0 -20 1237.0 -20Z',
  'M1546.0 0V552H1690.0V315H1682.0Q1682.0 401 1704.0 457.5Q1726.0 514 1769.5 542.0Q1813.0 570 1875.0 570H1883.0Q1977.0 570 2028.5 507.5Q2080.0 445 2080.0 316V0H1898.0V321Q1898.0 359 1875.5 383.5Q1853.0 408 1814.0 408Q1775.0 408 1751.5 383.5Q1728.0 359 1728.0 319V0Z',
];
function LogoAbertura({ animar = false }) {
  const pontos = animar ? [[-120,-180,0],[110,-150,.1],[-130,90,.05],[120,140,.2],[-40,-230,.15],[60,210,.25],[-140,-30,.3],[135,10,.12],[20,-200,.35],[-80,190,.22]] : [];
  return (
    <div className={'plen-abre' + (animar ? ' plen-abre-anima' : '')} style={{position:'relative', display:'inline-block'}} aria-label="Plen" role="img">
      {pontos.map(([x, y, d], i) => <span key={i} className="pa-ponto" aria-hidden="true" style={{'--x': x + 'px', '--y': y + 'px', animationDelay: d + 's'}} />)}
      <svg viewBox="0 0 256.3 96" style={{height:'46px', width:'auto', display:'block'}} aria-hidden="true">
        <circle className="pa-anel" cx="54" cy="34" r="16.5" fill="none" stroke="#2F6BFF" strokeWidth="15" strokeLinecap="round" transform="rotate(-90 54 34)" />
        <path className="pa-barra" fill="#2F6BFF" d="M16 16a6 6 0 0 1 12 0v64a6 6 0 0 1-12 0z" />
        <g transform="translate(122.0,17.0) scale(0.06667)"><g fill="#F4F4F2" transform="translate(-66,730) scale(1,-1)">
          {LETRAS_PLEN_SM.map((d, i) => <path key={i} className="pa-letra" d={d} />)}
        </g></g>
      </svg>
    </div>
  );
}

function AuthPage() {
  const { setUser, setPerfil, setProfessor, conviteData, setConviteData } = useApp();
  useTemaTela(true);

  const [mode, setMode] = React.useState('login');
  // Quem ja' entrou neste aparelho tem o e-mail lembrado — e so' nesse caso a tela
  // diz "bem-vindo de volta". Para quem abre o app pela primeira vez, essa frase e'
  // a primeira coisa que ele le' e ja' sai errada.
  const emailLembrado = (() => { try { return localStorage.getItem('plenUltimoEmail') || ''; } catch { return ''; } })();
  // v157: a abertura (logo se formando, ~3,3 s) so' para quem abre o app no celular pela primeira vez: sem e-mail
  // lembrado, sem convite (na tela, guardado ou na URL), sem link de senha. Decidido no primeiro render, sem
  // piscar. Um toque em qualquer lugar pula. Se o convite chegar no meio (vem do banco), a abertura sai na hora.
  const [abertura, setAbertura] = React.useState(() => {
    try {
      if (naRotaPainel() || conviteData || conviteGuardadoSM() || emailLembrado || avisoLinkSenhaSM) return false;
      if (!APP_NATIVO && window.innerWidth >= 768) return false;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
      if (/[?&](c|i|aula|code|type)=/.test(window.location.search || '') || /access_token|type=recovery/.test(window.location.hash || '')) return false;
      return true;
    } catch { return false; }
  });
  const [aberturaRodou] = React.useState(abertura);   // o logo anima so' quando a abertura comecou nesta tela
  React.useEffect(() => {
    if (!abertura) return;
    const t = setTimeout(() => { setAbertura(false); uso.acao('entrada.abertura', { fim: 'tempo' }); }, 3300);
    return () => clearTimeout(t);
  }, [abertura]);
  React.useEffect(() => { if (abertura && conviteData) { setAbertura(false); uso.acao('entrada.abertura', { fim: 'convite' }); } }, [conviteData]);
  const pularAbertura = () => { if (abertura) { setAbertura(false); uso.acao('entrada.abertura', { fim: 'toque' }); } };
  const [form, setForm] = React.useState({ nome: '', email: emailLembrado, senha: '', telefone: '' });
  const [loading, setLoading] = React.useState(false);
  // v151: chegou por um link de senha ja' usado ou vencido — antes caia aqui calado, sem saber o que houve
  const [erro, setErro] = React.useState(avisoLinkSenhaSM ? 'Esse link de troca de senha já foi usado ou venceu. Toque em "Esqueci minha senha" para receber outro e abra só o e-mail mais novo.' : '');
  const [newUser, setNewUser] = React.useState(null);
  const [recuperacaoEnviada, setRecuperacaoEnviada] = React.useState('');   // v151: o e-mail para onde foi
  React.useEffect(() => { if (avisoLinkSenhaSM) { avisoLinkSenhaSM = false; uso.bloqueio('senha_link', null, null, 'link_invalido'); } }, []);

  // U23 — quem chega pelo link do professor e' aluno, e quase sempre aluno NOVO. O link
  // abre direto em "criar conta", com o nome que o professor ja' cadastrou preenchido;
  // "ja' tenho conta" fica pequeno embaixo (padrao Calendly/ClassPass). O convite chega
  // depois do primeiro render (vem da URL), por isso o efeito.
  const ehConviteAluno = !!(conviteData && conviteData.pid && conviteData.tipo !== 'aluno_prof');
  // v144: link de OUTRO PROFESSOR (indicacao) — a conta nasce de professor, sem a pergunta "professor ou aluno".
  const ehIndicacao = conviteData?.tipo === 'prof_prof';
  const [mexeuNoModo, setMexeuNoModo] = React.useState(false);
  React.useEffect(() => {
    if (!conviteData || mexeuNoModo) return;
    setMode('register');
    if (ehConviteAluno) setForm(f => ({ ...f, nome: f.nome || conviteData.anome || '', telefone: f.telefone || conviteData.telefone || '' }));
  }, [conviteData]);
  // v144: o aluno saiu da conta dele para criar a de professor (IndicacaoParaAlunoSM). O e-mail lembrado e' o da
  // conta de aluno: o campo comeca vazio e a caixa do convite pede outro e-mail.
  const [indicacaoOutroEmail] = React.useState(() => { try { const v = sessionStorage.getItem('plenIndicacaoOutroEmail') === '1'; sessionStorage.removeItem('plenIndicacaoOutroEmail'); return v; } catch { return false; } });
  React.useEffect(() => { if (indicacaoOutroEmail && ehIndicacao) setForm(f => ({ ...f, email: '' })); }, []);
  // v144: veio pelo convite de outro professor — criou a conta, ja' vira professor (sem a pergunta).
  const indicacaoFeita = React.useRef(false);
  React.useEffect(() => {
    if (mode !== 'tipo' || !ehIndicacao || !newUser || indicacaoFeita.current) return;
    indicacaoFeita.current = true;
    handleTipo('professor');
  }, [mode, ehIndicacao, newUser]);
  // Central de uso: tela da entrada e o funil de cadastro (no /painel e' so' o login do Marcus)
  const ultimoInvalido = React.useRef(0);
  React.useEffect(() => {
    if (naRotaPainel()) { uso.area('painel'); uso.tela('painel.login'); return; }
    uso.tela(mode === 'tipo' ? (ehIndicacao ? 'entrada.tipo_indicacao' : ehConviteAluno ? 'entrada.tipo_convite' : 'entrada.tipo') : 'entrada.' + mode);
    if (mode === 'register') funilCadastro.passo(1);
    if (mode === 'register' && ehConviteAluno) funilConvite.passo(2, null, true);
  }, [mode, ehConviteAluno, ehIndicacao]);
  const primeiroNomeProf = (conviteData?.pnome || '').split(' ')[0] || 'seu professor';

  async function handleGoogleLogin() {
    // Central de uso: a pagina sai para o Google e volta recarregada — a marca no
    // sessionStorage diz, na volta, que foi login/cadastro pelo Google (App, carregarSessao).
    try { sessionStorage.setItem('plen_uso_google', mode); uso.acao('entrada.google', { modo: mode }); if (mode === 'register') funilCadastro.passo(2, { metodo: 'google' }); uso.enviarAgora(); } catch {}
    // window.location.origin = o endereco de onde o app esta' rodando agora. Assim o
    // retorno do login funciona em meuplen.com.br, no endereco da Netlify e em qualquer
    // dominio futuro, sem ninguem precisar lembrar de trocar isto aqui.
    await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: window.location.origin + '/' } });
  }

  async function handleRecuperarSenha() {
    uso.acao('entrada.esqueci_senha');
    if (!form.email) { uso.bloqueio('senha_recuperar', null, null, 'sem_email'); setErro('Digite seu email para recuperar a senha.'); return; }
    setLoading(true); setErro('');
    const { error } = await supabase.auth.resetPasswordForEmail(form.email, {
      redirectTo: window.location.origin + '/',
    });
    setLoading(false);
    if (error) {
      // v151: o limite de e-mails por hora aparecia como "verifique o endereço" — a pessoa achava que o e-mail estava errado
      const limite = error.status === 429 || /rate|limit|seconds/i.test(error.message || '');
      uso.bloqueio('senha_recuperar', null, null, limite ? 'limite_envio' : 'envio_falhou');
      setErro(limite ? 'Já mandamos um e-mail de troca de senha há pouco. Procure o mais novo na caixa de entrada (e no spam), ou espere alguns minutos para pedir outro.'
        : 'Não deu para enviar o e-mail. Confira o endereço e tente de novo.');
    }
    else { uso.acao('entrada.recuperacao_enviada'); setRecuperacaoEnviada(form.email.trim()); }
  }

  async function handleLogin(e) {
    e.preventDefault(); setLoading(true); setErro('');
    const { data, error } = await supabase.auth.signInWithPassword({ email: form.email, password: form.senha });
    if (error) { uso.bloqueio('login', null, null, 'senha_errada'); if (conviteData) funilConvite.barrar('senha_errada'); setErro('Email ou senha incorretos.' + DICA_CONTA_GOOGLE_NATIVO); setLoading(false); return; }
    try { localStorage.setItem('plenUltimoEmail', form.email); } catch {}
    uso.evento('sessao.login', { detalhes: { metodo: 'senha', convite: !!conviteData } });
    if (conviteData) funilConvite.passo(3, { conta: 'entrou' }, true);
    const { data: perfil } = await supabase.from('perfis').select('*').eq('id', data.user.id).single();
    if (perfil?.tipo === 'professor') {
      const { data: prof } = await supabase.from('professores').select('*').eq('user_id', data.user.id).single();
      setProfessor(prof);
    }
    setPerfil(perfil); setUser(data.user); setLoading(false);
  }

  async function handleRegister(e) {
    e.preventDefault(); setLoading(true); setErro('');
    funilCadastro.passo(2, { metodo: 'email' });
    const { data, error } = await supabase.auth.signUp({ email: form.email, password: form.senha });
    if (error) {
      // Padrao Google/Apple: e-mail que ja' existe troca a tela para "Entrar", sem
      // mensagem tecnica. Com confirmacao desligada o Supabase devolve este erro.
      if (/already|registered|exists/i.test(error.message)) { funilCadastro.barrar('email_ja_existe'); funilCadastro.sair('ja_tinha_conta'); if (conviteData) funilConvite.barrar('email_ja_existe'); setMode('login'); setMexeuNoModo(true); setErro('Você já tem conta com este e-mail. Entre com a sua senha.' + DICA_CONTA_GOOGLE_NATIVO); setLoading(false); return; }
      funilCadastro.barrar(motivoSignup(error.message));
      setErro(error.message); setLoading(false); return;
    }
    // Conta repetida com confirmacao ligada: o Supabase nao da' erro, devolve um usuario sem identidade.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      funilCadastro.barrar('email_ja_existe'); funilCadastro.sair('ja_tinha_conta'); if (conviteData) funilConvite.barrar('email_ja_existe');
      setMode('login'); setMexeuNoModo(true); setErro('Você já tem conta com este e-mail. Entre com a sua senha.' + DICA_CONTA_GOOGLE_NATIVO); setLoading(false); return;
    }
    uso.evento('sessao.cadastro', { detalhes: { metodo: 'email', convite: !!conviteData } });
    funilCadastro.passo(3);
    if (conviteData) funilConvite.passo(3, { conta: 'criou' }, true);
    setNewUser(data.user); setMode('tipo'); setLoading(false);
  }

  // U23b — veio pelo convite do professor: nao pergunta o papel. Cria o perfil de aluno e
  // ja' assume a ficha que o professor preencheu, no mesmo toque.
  async function handleEntrarComoAlunoConvidado() {
    setLoading(true); setErro('');
    try {
      await supabase.from('perfis').insert({ id: newUser.id, tipo: 'aluno', nome: form.nome || conviteData.anome || '' });
      let assumiu = false;
      if (conviteData._code && conviteData.aid) {
        const { error } = await rpcAssumirFicha(conviteData._code, conviteData.aid);
        if (error) { console.warn('assumir ficha no cadastro:', error.message); funilConvite.barrar('assumir_falhou'); }
        else { localStorage.removeItem('pendingInvite'); localStorage.removeItem('pendingInviteCode'); assumiu = true; funilConvite.passo(4, null, true); }
      }
      funilCadastro.passo(4, { papel: 'aluno' }, true);
      const { data: perfil } = await supabase.from('perfis').select('*').eq('id', newUser.id).single();
      // Seguranca convites (01/10): a ficha ja' esta' ligada — sem limpar o convite, o app do
      // aluno abria "Convite recebido!" e o "Aceitar" dava erro (o convite ja' foi usado).
      // Limpa junto com a troca de tela, para nao piscar a tela "Como voce vai usar o Plen?".
      if (assumiu) setConviteData(null);
      setPerfil(perfil); setUser(newUser);
    } catch (e) { funilCadastro.barrar('entrar_convite_falhou'); uso.erro('erro.entrar_convite', e?.message || e); setErro('Não deu para entrar: ' + (e?.message || e)); }
    setLoading(false);
  }

  async function handleTipo(tipo) {
    setLoading(true);
    funilCadastro.passo(4, { papel: tipo }, true);
    await supabase.from('perfis').insert({ id: newUser.id, tipo, nome: form.nome });
    if (tipo === 'professor') {
      await supabase.from('professores').insert({ user_id: newUser.id, nome: form.nome, email: form.email, telefone: form.telefone });
      const { data: prof } = await supabase.from('professores').select('*').eq('user_id', newUser.id).single();
      if (prof) await supabase.from('config_notificacoes').insert({ professor_id: prof.id });
    }
    const { data: perfil } = await supabase.from('perfis').select('*').eq('id', newUser.id).single();
    if (!perfil) funilCadastro.barrar('perfil_falhou');
    if (tipo === 'professor') { const { data: prof } = await supabase.from('professores').select('*').eq('user_id', newUser.id).single(); setProfessor(prof); }
    setPerfil(perfil); setUser(newUser); setLoading(false);
  }

  const googleBtn = (
    <button type="button" onClick={handleGoogleLogin}
      style={{width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', border:'1.5px solid rgba(255,255,255,0.16)', borderRadius:'14px', padding:'14px', background:'rgba(255,255,255,0.06)', cursor:'pointer', fontSize:'14px', fontWeight:'700', color:'white', transition:'background 0.15s', marginBottom:'16px', height:'52px'}}
      onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.11)'}
      onMouseOut={e => e.currentTarget.style.background='rgba(255,255,255,0.06)'}>
      <svg viewBox="0 0 24 24" style={{width:'18px',height:'18px',flexShrink:0}}><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
      Continuar com Google
    </button>
  );

  // v144: a conta de professor esta' sendo criada (o efeito acima chama handleTipo). Se falhar, o App cai na
  // TipoUsuario, que mostra o convite com o botao "Começar como professor".
  if (mode === 'tipo' && ehIndicacao) return (
    <div style={{minHeight:'100vh', background:'linear-gradient(150deg,#16181D,#1C43AE,#2455D6)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
      <div style={{color:'white', textAlign:'center', fontSize:'15px', fontWeight:'700'}}>Preparando sua conta de professor…</div>
    </div>
  );

  if (mode === 'tipo' && ehConviteAluno) return (
    <div style={{minHeight:'100vh', background:'linear-gradient(150deg,#16181D,#1C43AE,#2455D6)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.35)', padding:'40px 32px', width:'100%', maxWidth:'440px'}}>
        <div style={{width:'56px', height:'56px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'16px', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'18px'}}><Ico n="trophy" s={26} c="white" /></div>
        <h1 style={{fontSize:'26px', fontWeight:'800', color:'#16181D', marginBottom:'10px', letterSpacing:'-0.5px', lineHeight:1.2}}>Bem-vindo, {(form.nome || conviteData.anome || '').split(' ')[0] || 'atleta'}!</h1>
        <p style={{color:'#3D424C', fontSize:'15px', lineHeight:'1.6', marginBottom:'18px'}}>
          {primeiroNomeProf} já deixou suas aulas organizadas aqui. A partir de agora você:
        </p>
        <div style={{display:'flex', flexDirection:'column', gap:'10px', marginBottom:'24px'}}>
          {[['calendar-check','vê e confirma as aulas feitas'],['wallet','acompanha pagamentos e saldo de aulas'],['clock','controla sua agenda — avisa, remarca, pausa'],['message-circle','resolve tudo com ' + primeiroNomeProf + ' sem ficar caçando mensagem']].map(([ic, t]) => (
            <div key={t} style={{display:'flex', alignItems:'center', gap:'12px', fontSize:'14px', color:'#16181D'}}>
              <span style={{width:'34px', height:'34px', borderRadius:'10px', background:'#E7EDFF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n={ic} s={17} c="#2F6BFF" /></span>{t}
            </div>
          ))}
        </div>
        <button onClick={handleEntrarComoAlunoConvidado} disabled={loading}
          style={{width:'100%', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', color:'white', border:'none', borderRadius:'14px', padding:'16px', fontSize:'16px', fontWeight:'800', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1}}>
          {loading ? 'Preparando…' : 'Vamos lá'}
        </button>
        {erro && <div style={{marginTop:14, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', fontSize:13, color:'#DC2626'}}>{erro}</div>}
      </div>
    </div>
  );

  if (mode === 'tipo') return (
    <div style={{minHeight:'100vh', background:'#F7F8FA', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 20px 60px rgba(0,0,0,0.1)', padding:'48px', width:'100%', maxWidth:'480px', textAlign:'center'}}>
        <div style={{width:'56px', height:'56px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'16px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'26px', margin:'0 auto 20px'}}><Ico n="trophy" /></div>
        <h1 style={{fontSize:'24px', fontWeight:'800', color:'#16181D', marginBottom:'8px', letterSpacing:'-0.5px'}}>Bem-vindo, {form.nome?.split(' ')[0] || 'atleta'}!</h1>
        <p style={{color:'#6E7480', fontSize:'14px', marginBottom:'32px', lineHeight:'1.5'}}>Como você vai usar o Plen?</p>
        {/* v140: professor chegando pelo convite de um ALUNO (conta por e-mail cai aqui, nao no
            cartao "Seu aluno te espera!"). Diz quem convidou e qual botao tocar. */}
        {conviteData?.tipo === 'aluno_prof' && (
          <div style={{background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'12px 14px', margin:'-16px 0 20px', fontSize:'13.5px', color:'#0A6F4D', lineHeight:1.5, textAlign:'left'}}>
            <b>{(conviteData.anome && !conviteData.anome.includes('@')) ? conviteData.anome.split(' ')[0] : 'Seu aluno'}</b> te convidou para organizar as aulas dele. Toque em <b>Professor</b>.
          </div>
        )}
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px'}}>
          <button onClick={() => handleTipo('professor')} disabled={loading}
            style={{background:'linear-gradient(135deg, #2F6BFF, #2F6BFF)', color:'white', border:'none', borderRadius:'16px', padding:'24px 16px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'10px', transition:'transform 0.15s', opacity: loading ? 0.7 : 1}}
            onMouseOver={e => e.currentTarget.style.transform='translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform='translateY(0)'}>
            <span style={{fontSize:'32px'}}><Ico n="user"/><Ico n="building"/></span>
            <span style={{fontWeight:'700', fontSize:'16px'}}>Professor</span>
            <span style={{fontSize:'11px', opacity:0.8, lineHeight:'1.4'}}>Gerencio alunos, aulas e cobranças</span>
          </button>
          <button onClick={() => handleTipo('aluno')} disabled={loading}
            style={{background:'linear-gradient(135deg, #2455D6, #2455D6)', color:'white', border:'none', borderRadius:'16px', padding:'24px 16px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'10px', transition:'transform 0.15s', opacity: loading ? 0.7 : 1}}
            onMouseOver={e => e.currentTarget.style.transform='translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform='translateY(0)'}>
            <span style={{fontSize:'32px'}}><Ico n="ball-tennis" /></span>
            <span style={{fontWeight:'700', fontSize:'16px'}}>Aluno</span>
            <span style={{fontSize:'11px', opacity:0.8, lineHeight:'1.4'}}>Acompanho minhas aulas e créditos</span>
          </button>
        </div>
        {loading && <p style={{color:'#2F6BFF', fontSize:'13px', marginTop:'20px'}}>Configurando sua conta...</p>}
      </div>
    </div>
  );

  return (
    <div className={abertura ? 'auth-intro' : undefined} onClickCapture={pularAbertura} style={{minHeight:'100vh', display:'flex', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter','Segoe UI',sans-serif"}}>
      {}
      <div className="auth-hero" style={{flex:'0 0 48%', background:'linear-gradient(150deg, #16181D 0%, #16181D 45%, #16181D 100%)', flexDirection:'column', justifyContent:'center', padding:'64px 56px', position:'relative', overflow:'hidden'}}>
        {}
        <div style={{position:'absolute', top:'-120px', right:'-80px', width:'400px', height:'400px', borderRadius:'50%', background:'rgba(99,102,241,0.12)', pointerEvents:'none'}}/>
        <div style={{position:'absolute', bottom:'-100px', left:'-80px', width:'350px', height:'350px', borderRadius:'50%', background:'rgba(139,92,246,0.1)', pointerEvents:'none'}}/>
        <div style={{position:'absolute', top:'40%', right:'10%', width:'160px', height:'160px', borderRadius:'50%', background:'rgba(168,85,247,0.07)', pointerEvents:'none'}}/>
        <div style={{position:'relative', zIndex:1}}>
          {}
          <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'52px'}}>
            <div style={{maxWidth:'46px',width:'100%', height:'46px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'13px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px', boxShadow:'0 8px 24px rgba(99,102,241,0.4)'}}><Ico n="trophy" /></div>
            <img src="/logo-escuro.svg" alt="Plen" style={{height:'30px', display:'block'}} />
          </div>
          {}
          {ehConviteAluno ? (
            <h1 style={{fontSize:'38px', fontWeight:'800', color:'white', lineHeight:'1.18', marginBottom:'18px', letterSpacing:'-1px'}}>
              Suas aulas com<br/>{primeiroNomeProf},<br/>
              <span style={{background:'linear-gradient(90deg, #6E9BFF, #A9C2FF)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text'}}>organizadas.</span>
            </h1>
          ) : (
            <h1 style={{fontSize:'38px', fontWeight:'800', color:'white', lineHeight:'1.18', marginBottom:'18px', letterSpacing:'-1px'}}>
              Gerencie alunos,<br/>aulas e pagamentos<br/>
              <span style={{background:'linear-gradient(90deg, #6E9BFF, #A9C2FF)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text'}}>com inteligência.</span>
            </h1>
          )}
          <p style={{fontSize:'15px', color:'rgba(255,255,255,0.55)', marginBottom:'40px', lineHeight:'1.7'}}>
            {ehConviteAluno ? 'Agenda, saldo de aulas e pagamentos num lugar só — e a conversa com o professor fica simples.' : 'Tudo que um professor de esporte precisa — sem planilha, sem papel, sem dor de cabeça.'}
          </p>
          {}
          <div style={{display:'flex', flexDirection:'column', gap:'18px', marginBottom:'44px'}}>
            {(ehConviteAluno ? [
              ['calendar-event','Sua agenda no bolso','Próximas aulas, prazo para avisar, remarcação com um toque'],
              ['wallet','Saldo sempre certo','Quantas aulas faltam, o que já foi pago, o que vence'],
              ['message-circle','Sem caçar mensagem','Cancelou, remarcou, pagou — tudo registrado nos dois lados'],
            ] : [
              ['user-plus','Alunos em segundos','Escolha na agenda do celular — nome e WhatsApp já vêm prontos'],
              ['wallet','Financeiro em dia','Cobranças, saldo de aulas e recibos — sem planilha'],
              ['device-mobile','App para seus alunos','Eles veem aulas, saldo e avisos no celular'],
            ]).map(([icon, title, sub]) => (
              <div key={title} style={{display:'flex', gap:'14px', alignItems:'flex-start'}}>
                <div style={{width:'38px', height:'38px', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'11px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0, backdropFilter:'blur(4px)'}}>
                  <Ico n={icon} s={18} c="rgba(255,255,255,0.85)" />
                </div>
                <div>
                  <div style={{color:'rgba(255,255,255,0.95)', fontWeight:'600', fontSize:'14px'}}>{title}</div>
                  <div style={{color:'rgba(255,255,255,0.45)', fontSize:'12px', marginTop:'3px', lineHeight:'1.5'}}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
          {}
          <div style={{background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'16px', padding:'20px 22px', backdropFilter:'blur(8px)'}}>
            {/* Antes havia um depoimento de exemplo (pessoa inventada) — saiu em 14/10: a Apple
                trata depoimento fictício como informação enganosa. Volta quando o piloto der um real. */}
            <div style={{display:'flex', gap:'12px', alignItems:'flex-start'}}>
              <Ico n="shield-check" s={20} c="#6E9BFF" style={{marginTop:'2px'}} />
              <div>
                <div style={{color:'rgba(255,255,255,0.95)', fontWeight:'600', fontSize:'14px'}}>O dinheiro não passa pelo Plen</div>
                <div style={{color:'rgba(255,255,255,0.55)', fontSize:'12.5px', marginTop:'4px', lineHeight:'1.55'}}>{ehConviteAluno ? 'Você paga direto no Pix do seu professor.' : 'O aluno paga direto no seu Pix. O Plen organiza, avisa e registra.'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="auth-form-panel">
        <div style={{width:'100%', maxWidth:'400px'}}>
          {}
          {/* No celular esta e' a primeira tela do app — e antes ela tinha um quadradinho
              azul com um trofeu generico brigando com o logo de verdade, os dois pequenos.
              Agora e' um so': o logo da marca, grande, num cartao escuro que traz a cor do
              Plen para a tela. O cartao nao encosta no topo de proposito, para nao brigar
              com a hora e a bateria do iPhone. */}
          {/* v157: o logo e' o mesmo componente da abertura — anima so' quando a abertura rodou nesta tela */}
          <div className="auth-mobile-header">
            <LogoAbertura animar={aberturaRodou} />
          </div>
          <div className="auth-titulo" style={{marginBottom:'26px'}}>
            <h2 style={{fontSize:'28px', fontWeight:'800', color:'white', marginBottom:'6px', letterSpacing:'-0.6px'}}>
 {mode==='login' ? (emailLembrado ? 'Bem-vindo de volta' : conviteData ? 'Entrar' : 'Bem-vindo') : ehConviteAluno ? `Crie sua conta${(form.nome || conviteData.anome) ? ', ' + (form.nome || conviteData.anome).split(' ')[0] : ''}` : ehIndicacao ? 'Criar conta de professor' : 'Criar conta'}
            </h2>
            <p style={{fontSize:'14px', color:'rgba(255,255,255,0.55)', lineHeight:'1.5'}}>
              {mode==='login'
                ? (ehIndicacao ? 'Entre com a sua conta de professor'
                  : conviteData && conviteData.tipo !== 'aluno_prof'
                    ? 'Entre para aceitar o convite do seu professor'
                    : conviteData && conviteData.tipo === 'aluno_prof'
                      ? 'Entre para gerenciar as aulas do seu aluno'
                      : emailLembrado ? 'Acesse sua conta para continuar'
                      : 'Agenda, pacotes e confirmações de aula num lugar só.')
                : (ehConviteAluno ? `${primeiroNomeProf} já organizou suas aulas. Crie sua conta para ver agenda, saldo e pagamentos — leva 1 minuto.`
                   : ehIndicacao ? 'Agenda, pacotes e alunos num lugar só — leva 1 minuto.'
                   : conviteData ? 'Crie sua conta para aceitar o convite' : 'Comece agora — é gratuito')}
            </p>
          </div>

          {conviteData && conviteData.tipo === 'aluno_prof' && (
            <div style={{background:'rgba(14,159,110,0.14)', border:'1px solid rgba(14,159,110,0.4)', borderRadius:'14px', padding:'14px 16px', marginBottom:'20px'}}>
              <div style={{fontWeight:'700', color:'#7EE2BF', fontSize:'14px'}}>Seu aluno te convidou</div>
              <div style={{color:'rgba(255,255,255,0.7)', fontSize:'13px', marginTop:'4px'}}><strong style={{color:'white'}}>{conviteData.anome || 'Um aluno'}</strong> quer que você organize as aulas dele — crie sua conta abaixo.</div>
            </div>
          )}
          {/* v144: convite de outro professor (indicacao) */}
          {ehIndicacao && (
            <div style={{background:'rgba(47,107,255,0.14)', border:'1px solid rgba(47,107,255,0.45)', borderRadius:'14px', padding:'14px 16px', marginBottom:'20px'}}>
              <div style={{fontWeight:'700', color:'#A9C2FF', fontSize:'14px'}}>{conviteData.pnome || 'Um professor'} te convidou para o Plen</div>
              <div style={{color:'rgba(255,255,255,0.7)', fontSize:'13px', marginTop:'4px'}}>
                {mode === 'register' ? 'Crie sua conta de professor abaixo — é grátis. Depois é só cadastrar seus alunos.' : 'Entre com a sua conta de professor.'}
                {mode === 'register' && indicacaoOutroEmail ? ' Use um e-mail diferente do da sua conta de aluno.' : ''}
              </div>
            </div>
          )}
          {conviteData && conviteData.tipo !== 'aluno_prof' && !ehIndicacao && (
            <div style={{background:'rgba(47,107,255,0.14)', border:'1px solid rgba(47,107,255,0.45)', borderRadius:'14px', padding:'14px 16px', marginBottom:'20px'}}>
              <div style={{fontWeight:'700', color:'#A9C2FF', fontSize:'14px'}}>{conviteData.pnome || 'Seu professor'} cadastrou você como aluno</div>
              <div style={{color:'rgba(255,255,255,0.7)', fontSize:'13px', marginTop:'4px'}}>
                {mode === 'register' ? 'É só criar sua conta abaixo. Já tem conta no Plen? Entre com ela e o vínculo aparece na tela de início.' : 'Entre com a sua conta e o vínculo aparece na tela de início.'}
              </div>
            </div>
          )}

          {/* v147→v157: o quadro "Colar convite" saiu do topo; virou uma linha discreta no rodape' (abaixo). */}

          {/* Dentro do app da loja (Capacitor) o login do Google nao funciona na janela
              embutida (o Google bloqueia) e a Apple exigiria "Entrar com Apple" junto.
              Entao no nativo fica so' e-mail e senha — simples e aprovado. */}
          {!APP_NATIVO && googleBtn}

          {!APP_NATIVO && (
            <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px'}}>
              <div style={{flex:1, height:'1px', background:'rgba(255,255,255,0.1)'}}/>
              <span style={{fontSize:'11px', color:'rgba(255,255,255,0.4)', fontWeight:'700', letterSpacing:'0.08em'}}>OU COM E-MAIL</span>
              <div style={{flex:1, height:'1px', background:'rgba(255,255,255,0.1)'}}/>
            </div>
          )}

          {erro && <div style={{background:'rgba(220,38,38,0.16)', border:'1px solid rgba(220,38,38,0.45)', borderRadius:'12px', padding:'11px 14px', marginBottom:'16px', fontSize:'13px', color:'#FCA5A5', display:'flex', alignItems:'center', gap:'8px'}}><Ico n="alert-triangle" c="#D97706" /> {erro}</div>}

          <form onSubmit={mode==='login' ? handleLogin : handleRegister} style={{display:'flex', flexDirection:'column', gap:'14px'}}
            onInvalidCapture={e => { try { if (Date.now() - ultimoInvalido.current < 800) return; ultimoInvalido.current = Date.now(); const t = (e.target && e.target.type) || 'campo'; const motivo = t === 'email' && e.target.value ? 'email_formato' : 'campo_vazio_' + t; if (mode === 'register') funilCadastro.barrar(motivo); else uso.bloqueio('login', null, null, motivo); } catch {} }}>
            {mode==='register' && (<>
              <AuthInput label="Nome completo" type="text" placeholder="Seu nome completo" value={form.nome} onChange={e => setForm(f=>({...f, nome:e.target.value}))} required />
              {/* Apple (revisao 11/10, guideline 5.1.1v): telefone nao pode ser obrigatorio no cadastro. */}
              <AuthInput label="Telefone / WhatsApp (opcional)" type="tel" placeholder="+55 (11) 99999-9999" value={form.telefone} onChange={e => setForm(f=>({...f, telefone:mascaraTel(e.target.value)}))} />
            </>)}
            <AuthInput label="Email" type="email" placeholder="seu@email.com" value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} required />
            <AuthInput label="Senha" type="password" placeholder={mode==='login' ? 'Sua senha' : 'Mínimo 6 caracteres'} value={form.senha} onChange={e => setForm(f=>({...f, senha:e.target.value}))} required />
            {mode==='login' && (
              <div style={{textAlign:'right', marginTop:'-6px'}}>
                <button type="button" onClick={handleRecuperarSenha} disabled={loading}
                  style={{background:'none', border:'none', color:'#A9C2FF', fontSize:'13px', fontWeight:'600', cursor:'pointer', padding:0}}>
                  Esqueci minha senha
                </button>
              </div>
            )}
            {recuperacaoEnviada && (
              <div style={{background:'rgba(14,159,110,0.14)', border:'1px solid rgba(14,159,110,0.4)', borderRadius:'12px', padding:'12px 14px', fontSize:'13px', color:'#7EE2BF'}}>
                <Ico n="circle-check" c="#7EE2BF" /> E-mail enviado para {recuperacaoEnviada}. Abra o e-mail mais novo (veja também o spam) e toque no link uma vez só: abre a tela para criar a senha nova. Se não chegar em 5 minutos, confira se é o e-mail com que você criou a conta.
              </div>
            )}
            <button type="submit" disabled={loading}
              style={{background:'#2F6BFF', color:'white', border:'none', borderRadius:'14px', height:'52px', fontSize:'16px', fontWeight:'800', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1, letterSpacing:'-0.2px', marginTop:'4px', boxShadow:'0 8px 24px rgba(47,107,255,0.35)'}}
              onMouseOver={e => { if(!loading) e.currentTarget.style.transform='translateY(-1px)'; }}
              onMouseOut={e => e.currentTarget.style.transform='translateY(0)'}>
 {loading ? 'Aguarde…' : mode==='login' ? 'Entrar' : 'Criar conta'}
            </button>
          </form>

          {/* v157: "Criar minha conta" virou botao de verdade (quem chega pela primeira vez nao precisa cacar um link
              pequeno); em "criar conta", o caminho de volta continua em texto. */}
          {mode==='login' ? (
            <>
              <div style={{display:'flex', alignItems:'center', gap:'12px', margin:'18px 0'}}>
                <div style={{flex:1, height:'1px', background:'rgba(255,255,255,0.1)'}}/>
                <span style={{fontSize:'12px', color:'rgba(255,255,255,0.4)'}}>ou</span>
                <div style={{flex:1, height:'1px', background:'rgba(255,255,255,0.1)'}}/>
              </div>
              <button type="button" onClick={()=>{ uso.acao('entrada.ir_cadastro'); setMode('register'); setMexeuNoModo(true); }}
                style={{width:'100%', minHeight:'52px', background:'transparent', color:'white', border:'1.5px solid rgba(255,255,255,0.28)', borderRadius:'14px', fontSize:'15px', fontWeight:'800', cursor:'pointer', fontFamily:'inherit'}}>
                Criar minha conta
              </button>
            </>
          ) : (
            <p style={{textAlign:'center', fontSize:'14px', color:'rgba(255,255,255,0.55)', marginTop:'22px'}}>
              Já tem conta? <button onClick={()=>{ uso.acao('entrada.ir_login'); if (funilCadastro.passoAtual() < 3) funilCadastro.sair('foi_para_login'); setMode('login'); setMexeuNoModo(true); }} style={{color:'#A9C2FF', fontWeight:'700', background:'none', border:'none', cursor:'pointer', padding:0, fontSize:'14px'}}>Entrar</button>
            </p>
          )}
          {/* v157: no app da loja, sem convite na tela nem guardado — linha discreta para colar o convite do WhatsApp.
              O texto "Colar convite" tem de continuar existindo: a pagina de baixar o app (ConviteBaixarApp) manda
              "toque em Colar convite". Em modo compacto, o quadro completo so' abre se o toque precisar do campo. */}
          {APP_NATIVO && !conviteData && !conviteGuardadoSM() && (
            <div style={{marginTop:'22px'}}><ColarConvite quem="entrada" escuro compacto /></div>
          )}
          {/* v141: no iPhone/iPad pelo navegador, um link discreto para o app da App Store. Some no
              app da loja, no Android e no computador, e tambem quando a pessoa chegou por um convite
              (ela ja' esta' no meio do cadastro; trocar de app ali perderia o convite). Botao de texto
              com icone neutro — nada do selo nem da maca da Apple. O convite (conviteData) so' chega
              depois da consulta ao banco; o codigo guardado ja' esconde o link antes, sem piscar. */}
          {IOS_NAVEGADOR && !conviteData && !conviteGuardadoSM() && (
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => uso.acao('entrada.app_store')}
              style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', margin:'18px auto 0', width:'fit-content', minHeight:'44px', boxSizing:'border-box', padding:'10px 16px', borderRadius:'12px', border:'1px solid rgba(255,255,255,0.25)', color:'white', fontSize:'13.5px', fontWeight:'700', textDecoration:'none'}}>
              <Ico n={ICONE_APP_STORE} s={16} /> Baixar na App Store
            </a>
          )}
          <p style={{textAlign:'center', fontSize:'11px', color:'rgba(255,255,255,0.3)', marginTop:'18px', lineHeight:'1.5'}}>
            Ao continuar, você concorda com os <a href="/termos" target="_blank" rel="noopener" style={{color:'rgba(255,255,255,0.45)'}}>Termos de Uso</a> e a <a href="/privacidade" target="_blank" rel="noopener" style={{color:'rgba(255,255,255,0.45)'}}>Política de Privacidade</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

function TipoUsuario({ user }) {
  const { setPerfil, setProfessor, conviteData, setConviteData } = useApp();
  const [loading, setLoading] = React.useState(false);
  const nome = user.user_metadata?.full_name || user.email?.split('@')[0] || '';

  const [erroTipo, setErroTipo] = React.useState('');
  // Central de uso: qual das 3 variantes desta tela apareceu
  const varianteTipo = conviteData?.tipo === 'aluno_prof' ? 'entrada.tipo_pedido_prof' : conviteData?.tipo === 'prof_prof' ? 'entrada.tipo_indicacao' : conviteData?.pid ? 'entrada.tipo_convite' : 'entrada.tipo';
  React.useEffect(() => {
    uso.area('entrada'); uso.tela(varianteTipo);
    if (varianteTipo === 'entrada.tipo_convite') funilConvite.passo(2, null, true);
  }, [varianteTipo]);

  async function handleTipo(tipo) {
    setLoading(true); setErroTipo('');
    funilCadastro.passo(4, { papel: tipo }, true);

    const { error: perfilErr } = await supabase
      .from('perfis')
      .upsert({ id: user.id, tipo, nome }, { onConflict: 'id' });
    if (perfilErr) { funilCadastro.barrar('perfil_falhou'); uso.erro('erro.perfil', perfilErr.message); setErroTipo('Erro ao salvar perfil: ' + perfilErr.message); setLoading(false); return; }

    if (tipo === 'professor') {
      const jaTemProf = await supabase.from('professores').select('id').eq('user_id', user.id).single();
      if (!jaTemProf.data) {
        const { data: prof, error: profErr } = await supabase
          .from('professores').insert({ user_id: user.id, nome, email: user.email, telefone: '' }).select().single();
        if (profErr) { funilCadastro.barrar('prof_falhou'); uso.erro('erro.perfil', profErr.message); setErroTipo('Erro ao criar perfil de professor: ' + profErr.message); setLoading(false); return; }
        if (prof) { await supabase.from('config_notificacoes').insert({ professor_id: prof.id }); setProfessor(prof); }
      } else {
        setProfessor(jaTemProf.data);
      }
    }

    if (tipo === 'aluno') {
      if (conviteData?.pid) {
        // NAO cria ficha aqui. Se o convite ja' aponta a ficha que o professor preencheu,
        // assume ela agora (U23b: um toque a menos). Sem ficha apontada, a tela de aceite
        // do app do aluno pergunta "qual desses e' voce?". Criar ficha aqui gerava a
        // "ficha fantasma".
        if (conviteData._code && conviteData.aid) {
          const { error: eAss } = await rpcAssumirFicha(conviteData._code, conviteData.aid);
          if (!eAss) { funilConvite.passo(4, null, true); localStorage.removeItem('pendingInvite'); localStorage.removeItem('pendingInviteCode'); setConviteData(null); }
          else { console.warn('assumir ficha:', eAss.message); funilConvite.barrar('assumir_falhou'); }
        }
      } else {

        // FIX: busca registro criado pelo professor via e-mail antes de criar um novo
        // limit(1) em vez de maybeSingle: o mesmo e-mail pode ter sido convidado por
        // dois professores. maybeSingle erra com 2 linhas, devolve null, e o app criava
        // uma ficha orfa — o aluno entrava sem professor e a ficha do professor ficava
        // abandonada. Vale para toda busca de aluno por user_id/e-mail: o modelo permite
        // um vinculo POR PROFESSOR.
        // Seguranca convites (01/10): quem liga a ficha pelo e-mail agora e' o banco (funcao
        // vincular_ficha_por_email, que confere o e-mail da propria conta). A regra antiga que
        // deixava o app fazer isso direto na tabela tambem deixava qualquer um pegar ficha alheia.
        // VOLTA: se a funcao ainda nao existe, o caminho antigo (busca + update direto).
        let porEmail = null;
        const { data: idPorEmail, error: eVinc } = await supabase.rpc('vincular_ficha_por_email');
        if (!eVinc) {
          porEmail = idPorEmail ? { id: idPorEmail } : null;
        } else {
          const { data: porEmailL } = await supabase.from('alunos').select('id')
            .eq('email', user.email).is('user_id', null).order('created_at').limit(1);
          porEmail = porEmailL && porEmailL[0];
          if (porEmail) await supabase.from('alunos').update({ user_id: user.id }).eq('id', porEmail.id);
        }
        if (!porEmail) {
          const { data: jaExisteL } = await supabase.from('alunos').select('id').eq('user_id', user.id).limit(1);
          if (!(jaExisteL && jaExisteL.length)) {
            const { error: e } = await supabase.from('alunos').insert({ professor_id: null, user_id: user.id, nome, email: user.email, telefone: '' });
            if (e) { funilCadastro.barrar('aluno_falhou'); uso.erro('erro.perfil', e.message); setErroTipo('Erro ao criar conta de aluno: ' + e.message); setLoading(false); return; }
          }
        }
      }
    }

    const { data: p } = await supabase.from('perfis').select('*').eq('id', user.id).single();
    setPerfil(p);
    setLoading(false);
  }

  if (conviteData?.tipo === 'aluno_prof') {
    const nomeAluno = conviteData.anome || 'seu aluno';
    async function handleConfirmarProfessor() {
      setLoading(true); setErroTipo('');
      funilCadastro.passo(4, { papel: 'professor', convite: true }, true);

      const { error: perfilErr } = await supabase.from('perfis').upsert({ id: user.id, tipo: 'professor', nome }, { onConflict: 'id' });
      if (perfilErr) { funilCadastro.barrar('perfil_falhou'); uso.erro('erro.perfil', perfilErr.message); setErroTipo(perfilErr.message); setLoading(false); return; }

      let profId;
      const { data: jaTemProf } = await supabase.from('professores').select('id').eq('user_id', user.id).maybeSingle();
      if (jaTemProf) {
        profId = jaTemProf.id;
      } else {
        const { data: novoProf, error: profErr } = await supabase.from('professores').insert({ user_id: user.id, nome, email: user.email, telefone: '' }).select().single();
        if (profErr) { funilCadastro.barrar('prof_falhou'); uso.erro('erro.perfil', profErr.message); setErroTipo(profErr.message); setLoading(false); return; }
        profId = novoProf.id;
        await supabase.from('config_notificacoes').insert({ professor_id: profId });
        setProfessor(novoProf);
      }

      const alvoAluno = conviteData.aid || conviteData.aluno_id;
      if (alvoAluno) {
        await supabase.from('alunos').update({ professor_id: profId }).eq('id', alvoAluno);
      }

      // O aluno descreveu como sao as aulas e isso veio dentro do convite. Guardo aqui
      // para o app abrir o cadastro JA' PREENCHIDO assim que o professor entrar — ele
      // confere e ajusta. Nenhum numero vira contrato sem ele olhar: o dinheiro e' dele.
      // Seguranca convites (01/10): aceite + proposta + codigo do pedido numa funcao so'
      // (aceitarPedidoDoAluno), a mesma usada pelo app do professor.
      if (conviteData._code) await aceitarPedidoDoAluno(conviteData);
      localStorage.removeItem('pendingInviteCode');
      setConviteData(null);

      if (!jaTemProf) {
        const { data: profCompleto } = await supabase.from('professores').select('*').eq('id', profId).single();
        if (profCompleto) setProfessor(profCompleto);
      } else {
        const { data: profCompleto } = await supabase.from('professores').select('*').eq('id', profId).single();
        if (profCompleto) setProfessor(profCompleto);
      }
      const { data: p } = await supabase.from('perfis').select('*').eq('id', user.id).single();
      setPerfil(p);
      setLoading(false);
    }
    return (
      <div style={{minHeight:'100vh', background:'linear-gradient(150deg, #16181D, #16181D, #0A5B40)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
        <div style={{background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', padding:'48px 40px', width:'100%', maxWidth:'420px', textAlign:'center'}}>
          <div style={{maxWidth:'64px',width:'100%', height:'64px', background:'linear-gradient(135deg, #0E9F6E, #0C8A5F)', borderRadius:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'30px', margin:'0 auto 20px', boxShadow:'0 8px 24px rgba(22,163,74,0.4)'}}><Ico n="trophy" /></div>
          <h1 style={{fontSize:'26px', fontWeight:'800', color:'#16181D', marginBottom:'10px', letterSpacing:'-0.5px'}}>Seu aluno te espera!</h1>
          <p style={{color:'#6E7480', fontSize:'14px', marginBottom:'8px', lineHeight:'1.6'}}>Você foi convidado por</p>
          <div style={{background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'12px 20px', marginBottom:'16px', display:'inline-block'}}>
            <span style={{fontWeight:'800', color:'#0A6F4D', fontSize:'16px'}}>{nomeAluno}</span>
          </div>
          {(() => {
            const prop = conviteData.proposta || conviteData.dados?.proposta;
            if (!prop) return (
              <p style={{color:'#9AA0AB', fontSize:'13px', marginBottom:'28px', lineHeight:'1.5'}}>
                Confirme abaixo para criar sua conta como professor e já ter {nomeAluno.split(' ')[0]} na sua lista de alunos.
              </p>
            );
            // v143: as mesmas linhas do WhatsApp do aluno, na voz de quem le' (linhasDaProposta):
            // agenda com a duracao, valor com a quantidade, quantas ja' fizeram, se ja' pagou.
            const L = linhasDaProposta(prop, 'ele');
            const linhasCartao = [L.agenda, L.valor, L.andamento, L.pagamento].filter(Boolean);
            return (
              <>
                <p style={{color:'#9AA0AB', fontSize:'13px', marginBottom:'12px', lineHeight:'1.5'}}>
                  {nomeAluno.split(' ')[0]} já deixou o combinado preenchido:
                </p>
                <div style={{background:'#F7F8FA', border:'1.5px solid #E2E4E9', borderRadius:'14px', padding:'14px', marginBottom:'14px', textAlign:'left'}}>
                  {L.modalidade ? <div style={{fontSize:'13px', color:'#16181D', fontWeight:'700'}}>{L.modalidade}</div> : null}
                  {linhasCartao.map((t, i) => <div key={i} style={{fontSize:'13px', color:'#3D424C', marginTop:'3px'}}>{t}</div>)}
                  {L.enviadoEm ? <div style={{fontSize:'12px', color:'#9AA0AB', marginTop:'8px'}}>{'Enviado em ' + fmtDiaMes(L.enviadoEm)}</div> : null}
                </div>
                <p style={{color:'#9AA0AB', fontSize:'12.5px', marginBottom:'24px', lineHeight:'1.55'}}>
                  Você confere e ajusta o que quiser antes de valer. É grátis, e o pagamento
                  continua por Pix direto na sua conta — o Plen não fica com o dinheiro.
                </p>
              </>
            );
          })()}
          {erroTipo && <div style={{background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'10px', padding:'10px', marginBottom:'16px', fontSize:'13px', color:'#DC2626'}}><Ico n="alert-triangle" c="#D97706" /> {erroTipo}</div>}
          <button onClick={handleConfirmarProfessor} disabled={loading}
            style={{width:'100%', background:'linear-gradient(135deg, #0E9F6E, #0C8A5F)', color:'white', border:'none', borderRadius:'14px', padding:'16px', fontSize:'16px', fontWeight:'800', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1, boxShadow:'0 8px 24px rgba(22,163,74,0.4)', letterSpacing:'-0.3px'}}>
 {loading ? 'Configurando...': 'Entrar como Professor'}
          </button>
        </div>
      </div>
    );
  }

  // v144: convite de outro professor (indicacao): a conta vira de professor com um toque, sem a pergunta.
  // "Nao e' a minha conta" sai sem apagar o convite guardado (a pessoa entra com a conta certa e segue).
  if (conviteData?.tipo === 'prof_prof') return (
    <div style={{minHeight:'100vh', background:'#16181D', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', padding:'40px 28px', width:'100%', maxWidth:'420px', textAlign:'center', boxSizing:'border-box'}}>
        <div style={{width:'64px', height:'64px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'20px', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px'}}><Ico n="user-plus" s={30} c="white" /></div>
        <h1 style={{fontSize:'24px', fontWeight:'800', color:'#16181D', marginBottom:'10px', letterSpacing:'-0.5px'}}>Bem-vindo, {nome.split(' ')[0] || 'professor'}!</h1>
        <p style={{color:'#3D424C', fontSize:'15px', marginBottom:'24px', lineHeight:'1.6'}}><b>{conviteData.pnome || 'Um professor'}</b> te convidou para organizar suas aulas no Plen: agenda, pacotes e alunos num lugar só.</p>
        <button onClick={() => handleTipo('professor')} disabled={loading}
          style={{width:'100%', minHeight:'52px', background:'#2F6BFF', color:'white', border:'none', borderRadius:'14px', padding:'15px', fontSize:'16px', fontWeight:'800', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1}}>
          {loading ? 'Preparando…' : 'Começar como professor'}
        </button>
        {erroTipo && <div style={{marginTop:16, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', fontSize:13, color:'#DC2626', textAlign:'left'}}><Ico n="alert-triangle" c="#D97706" /> {erroTipo}</div>}
        <button onClick={async () => { uso.acao('entrada.tipo_sair'); await sairDaConta(); window.location.reload(); }}
          style={{marginTop:'18px', minHeight:'44px', background:'none', border:'none', color:'#9AA0AB', fontSize:'13px', cursor:'pointer', textDecoration:'underline'}}>
          Não é a minha conta — sair
        </button>
      </div>
    </div>
  );

  if (conviteData?.pid) return (
    <div style={{minHeight:'100vh', background:'linear-gradient(150deg, #16181D, #16181D, #16181D)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', padding:'48px 40px', width:'100%', maxWidth:'420px', textAlign:'center'}}>
        <div style={{maxWidth:'64px',width:'100%', height:'64px', background:'linear-gradient(135deg, #2455D6, #2455D6)', borderRadius:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'30px', margin:'0 auto 20px', boxShadow:'0 8px 24px rgba(124,58,237,0.4)'}}><Ico n="ball-tennis" /></div>
        <h1 style={{fontSize:'26px', fontWeight:'800', color:'#16181D', marginBottom:'10px', letterSpacing:'-0.5px'}}>Bem-vindo, {(conviteData.anome || nome).split(' ')[0]}!</h1>
        <p style={{color:'#6E7480', fontSize:'14px', marginBottom:'8px', lineHeight:'1.6'}}>Suas aulas já estão organizadas aqui por</p>
        <div style={{background:'#E7EDFF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'12px 20px', marginBottom:'28px', display:'inline-block'}}>
          <span style={{fontWeight:'800', color:'#1C43AE', fontSize:'16px'}}>{conviteData.pnome}</span>
        </div>
        <p style={{color:'#9AA0AB', fontSize:'13px', marginBottom:'28px', lineHeight:'1.5'}}>Aqui você vê e confirma as aulas feitas, acompanha pagamentos e controla sua agenda.</p>
        <button onClick={() => handleTipo('aluno')} disabled={loading}
          style={{width:'100%', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', color:'white', border:'none', borderRadius:'14px', padding:'16px', fontSize:'16px', fontWeight:'800', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1, boxShadow:'0 8px 24px rgba(99,102,241,0.4)', letterSpacing:'-0.3px'}}>
 {loading ? 'Preparando…': 'Vamos lá'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh', background:'#F7F8FA', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 20px 60px rgba(0,0,0,0.1)', padding:'48px 40px', width:'100%', maxWidth:'480px', textAlign:'center'}}>
        <div style={{maxWidth:'56px',width:'100%', height:'56px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'16px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'26px', margin:'0 auto 20px', boxShadow:'0 8px 24px rgba(99,102,241,0.35)'}}><Ico n="trophy" /></div>
        <h1 style={{fontSize:'26px', fontWeight:'800', color:'#16181D', marginBottom:'8px', letterSpacing:'-0.5px'}}>Bem-vindo, {nome.split(' ')[0]}!</h1>
        <p style={{color:'#6E7480', fontSize:'14px', marginBottom:'32px', lineHeight:'1.5'}}>Como você vai usar o Plen?</p>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px'}}>
          <button onClick={() => handleTipo('professor')} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl p-6 flex flex-col items-center gap-3 transition-colors"><span className="text-3xl"><Ico n="user"/><Ico n="building"/></span><span className="font-bold">Professor</span><span className="text-xs opacity-80">Gerencio alunos e aulas</span></button>
          <button onClick={() => handleTipo('aluno')} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white rounded-2xl p-6 flex flex-col items-center gap-3 transition-colors"><span className="text-3xl"><Ico n="ball-tennis" /></span><span className="font-bold">Aluno</span><span className="text-xs opacity-80">Acompanho minhas aulas</span></button>
        </div>
        {loading && <p className="text-gray-400 text-sm mt-4">Configurando sua conta...</p>}
        {erroTipo && <div style={{marginTop:16, background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:10, padding:'12px 16px', fontSize:13, color:'#DC2626'}}><Ico n="alert-triangle" c="#D97706" /> {erroTipo}</div>}
        {/* Esta tela era um beco sem saida: quem entrou na conta errada, ou numa conta
            que perdeu o perfil, ficava preso sem botao de sair. Toda tela que segura a
            pessoa antes do app precisa de uma porta para fora. */}
        <button onClick={async () => { uso.acao('entrada.tipo_sair'); await sairDaConta(); localStorage.clear(); window.location.reload(); }}
          style={{marginTop:'22px', background:'none', border:'none', color:'#9AA0AB', fontSize:'13px', cursor:'pointer', textDecoration:'underline'}}>
          Não é a minha conta — sair
        </button>
      </div>
    </div>
  );
}

// v141 (B3): `pendencias` = o mesmo numero da aba Pendencias e do menu (minhas_pendencias, "sua vez").
// Com ele, o numero vermelho do sino passa a ser esse — antes contava os avisos nao lidos da lista
// propria do sino e o computador mostrava dois numeros diferentes na mesma tela. null (banco sem a
// funcao ou erro) volta a contagem antiga, igual ao menu (numeroPendSM).
// pendCarregando (v141): as Pendencias ainda estao chegando — sem "Tudo em dia!" nem "Nada na sua vez"
// nesse meio-tempo (o numero chegaria logo depois dizendo o contrario).
function NotificationBell({ professor, onNavigate, pendencias = null, pendCarregando = false }) {
  const [aberto, setAberto] = React.useState(false);
  const [itens, setItens] = React.useState([]);
  const [carregando, setCarregando] = React.useState(true);
  const [lidas, setLidas] = React.useState(() => {
    try { return JSON.parse(sessionStorage.getItem('notif_lidas') || '[]'); } catch { return []; }
  });

  // Aviso do banco (notif_<id>) tambem fica lido no banco (Fase 0, Etapa 1) — antes o lido do
  // professor vivia so' nesta sessao do navegador e voltava a aparecer no dia seguinte.
  function lidoNoBanco(chaves) {
    const ids = chaves.filter(c => String(c).startsWith('notif_')).map(c => String(c).slice(6));
    if (ids.length) supabase.from('notificacoes').update({ lida: true }).in('id', ids).then(() => window.dispatchEvent(new Event('plen:avisos-lidos')));
  }

  function marcarLida(chave) {
    lidoNoBanco([chave]);
    setLidas(prev => {
      const novo = [...new Set([...prev, chave])];
      try { sessionStorage.setItem('notif_lidas', JSON.stringify(novo)); } catch {}
      return novo;
    });
  }

  function marcarTodasLidas() {
    const chaves = itens.map(i => i.chave);
    lidoNoBanco(chaves);
    setLidas(prev => {
      const novo = [...new Set([...prev, ...chaves])];
      try { sessionStorage.setItem('notif_lidas', JSON.stringify(novo)); } catch {}
      return novo;
    });
  }

  async function carregar() {
    setCarregando(true);
    const agora = new Date();
    const hoje = agora.toISOString().split('T')[0];
    const pad2 = n => String(n).padStart(2,'0');
    const horaAtual = `${pad2(agora.getHours())}:${pad2(agora.getMinutes())}`;

    const { data: cfgNotif } = await supabase.from('config_notificacoes').select('aviso_renovacao_mensal_dias').eq('professor_id', professor.id).maybeSingle();
    const diasAlertaRenovacao = cfgNotif?.aviso_renovacao_mensal_dias || 7;
    const emXdias = new Date(agora.getTime() + diasAlertaRenovacao*24*60*60*1000).toISOString().split('T')[0];
    const h48atras = new Date(agora.getTime() - 48*60*60*1000).toISOString();

    const d11m = new Date(agora); d11m.setMonth(d11m.getMonth() - 11); const em11m = d11m.toISOString().split('T')[0];
    const d14m = new Date(agora); d14m.setMonth(d14m.getMonth() - 14); const em14m = d14m.toISOString().split('T')[0];

    const [
      { data: aulasPassadas },
      { data: aulasHojeAtrasadas },
      { data: pagsPendentes },
      { data: pagosRecentes },
      { data: contratos },
      { data: notifs },
      { data: contratosReajuste },
    ] = await Promise.all([
      supabase.from('aulas').select('id, data, hora, aluno_id, turma_id, sessao_id, alunos(id, nome)').eq('professor_id', professor.id).eq('status', 'agendada').lt('data', hoje),
      supabase.from('aulas').select('id, data, hora, aluno_id, turma_id, sessao_id, alunos(id, nome)').eq('professor_id', professor.id).eq('status', 'agendada').eq('data', hoje).lt('hora', horaAtual),
      // v152 (revisao): o status do acordo diz se a cobranca e' do proximo pacote (so' essa espera o vencimento)
      (async () => {
        const r = await supabase.from('pagamentos').select('id, valor, aluno_id, data_vencimento, contrato_id, alunos(id, nome), contratos(status)').eq('professor_id', professor.id).eq('status', 'pendente');
        if (!r.error) return r;
        return supabase.from('pagamentos').select('id, valor, aluno_id, data_vencimento, alunos(id, nome)').eq('professor_id', professor.id).eq('status', 'pendente');
      })(),
      // pagamentos nao tem updated_at (a consulta voltava 400 em silencio desde sempre —
      // achado no teste de 29/09). Usa a data do pagamento.
      supabase.from('pagamentos').select('id, valor, aluno_id, alunos(id, nome), data_pagamento').eq('professor_id', professor.id).eq('status', 'pago').gte('data_pagamento', h48atras.slice(0, 10)),
      // v155: quem ja' combinou o proximo pacote nao entra em "Pacote vencendo" (igual a v152 parte b)
      (async () => {
        const [r, rp] = await Promise.all([
          supabase.from('contratos').select('id, data_fim, aluno_id, alunos(id, nome)').eq('professor_id', professor.id).eq('status', 'ativo').gte('data_fim', hoje).lte('data_fim', emXdias),
          supabase.from('contratos').select('aluno_id').eq('professor_id', professor.id).eq('status', 'proximo'),
        ]);
        if (r.error || rp.error) return r;
        const comProx = new Set((rp.data || []).map(c => c.aluno_id));
        return { ...r, data: (r.data || []).filter(c => !comProx.has(c.aluno_id)) };
      })(),
      // Avisos PARA o professor (coluna destinatario). Sem a coluna (SQL da Etapa 1 nao rodou),
      // volta a lista fixa de tipos de antes. 'aulas_sem_confirmacao' fica de fora: o sino ja'
      // monta esse item a partir das aulas. v140: os lembretes do professor (antes da aula e resumo da
      // agenda) tambem ficam de fora — ja' chegam por push e ficam no Historico (o banco grava como lidos).
      (async () => {
        const r = await supabase.from('notificacoes').select('id, tipo, mensagem, lida, created_at, aluno_id, ref_tipo, ref_id').eq('professor_id', professor.id).eq('lida', false).eq('destinatario', 'professor').not('tipo', 'in', '(aulas_sem_confirmacao,lembrete_aula_prof,agenda_resumo_prof)').order('created_at', { ascending: false }).limit(20);
        if (!r.error) return r;
        return supabase.from('notificacoes').select('id, tipo, mensagem, lida, created_at, aluno_id').eq('professor_id', professor.id).eq('lida', false).in('tipo', ['cancelamento_solicitado','remarcacao_solicitada','cancelamento_aluno','reajuste_aceito','reajuste_recusado','reajuste_refazer','contrato_assinado','pagamento_declarado_aluno']).order('created_at', { ascending: false }).limit(20);
      })(),
      supabase.from('contratos').select('id, data_inicio, preco_negociado, tipo_cobranca, valor_mensal, aluno_id, alunos(id, nome)').eq('professor_id', professor.id).eq('status', 'ativo').lte('data_inicio', em11m).gte('data_inicio', em14m),
    ]);

    const lista = [];

    (notifs||[]).forEach(n => {
      const tipos = {
        cancelamento_solicitado: { emoji:'ban', cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tela:'agenda', label:'Solicitou cancelamento'},
        remarcacao_solicitada:   { emoji:'refresh', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'agenda', label:'Solicitou remarcação'},
        mensagem_aluno:          { emoji:'message-circle', cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tela:'alunos', label:'Mensagem'},
        cancelamento_aluno:      { emoji:'circle-x', cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tela:'agenda', label:'Aula desmarcada'},
        pausa_solicitada:        { emoji:'player-pause', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'dashboard', label:'Pedido de pausa'},
        ajuste_contrato:         { emoji:'pencil', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'alunos', label:'Correção no acordo'},
        aula_proposta:           { emoji:'calendar-plus', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'dashboard', label:'Aula proposta'},
        contestacao:             { emoji:'message-report', cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tela:'dashboard', label:'Aula contestada'},
        pagamento_declarado_aluno:{ emoji:'coin', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'financeiro', label:'Pagamento informado'},
        contrato_assinado:       { emoji:'file-check', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'alunos', label:'Acordo confirmado'},
        reajuste_aceito:         { emoji:'trending-up', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'alunos', label:'Reajuste aceito'},
        reajuste_recusado:       { emoji:'trending-up', cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tela:'alunos', label:'Reajuste recusado'},
        // Etapa 4: proposta calculada sobre um valor velho foi cancelada pelo banco — propor de novo
        reajuste_refazer:        { emoji:'trending-up', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'financeiro', label:'Refazer reajuste'},
        reagendamento_aceito:    { emoji:'check', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'agenda', label:'Remarcação aceita'},
        // v136: o aluno nao consegue no horario que o professor remarcou (o Inicio tem [Remarcar] [Desmarcar])
        remarcacao_recusada:     { emoji:'calendar-x', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'dashboard', label:'Não consegue no novo horário'},
        falta_declarada_aluno:   { emoji:'user-x', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'dashboard', label:'Aluno disse que não foi'},
        aula_confirmada_aluno:   { emoji:'check', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'dashboard', label:'Aluno confirmou'},
        aula_divergencia:        { emoji:'arrows-diff', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'dashboard', label:'Registro diferente'},
        resumo_semanal:          { emoji:'chart-bar', cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tela:'dashboard', label:'Sua semana'},
        recompensa:              { emoji:'gift', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'dashboard', label:'Recompensa'},
        // v140: o aluno esta' esperando no local / disse que voce nao veio (abre a aula no Inicio) e o robo das 24h
        aluno_esperando:         { emoji:'user-exclamation', cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tela:'dashboard', label:'Aluno esperando'},
        prof_nao_veio:           { emoji:'user-exclamation', cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tela:'dashboard', label:'Aluno disse que você não veio'},
        aula_devolvida:          { emoji:'arrow-back-up', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'alunos', label:'Aula fechada sem resposta'},
        // v140 (Tema 3): lembretes do proprio professor — so' aviso
        lembrete_aula_prof:      { emoji:'alarm', cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tela:'dashboard', label:'Sua próxima aula'},
        agenda_resumo_prof:      { emoji:'calendar', cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tela:'agenda', label:'Sua agenda'},
        // v142: o aluno desfez o que tinha feito (abre onde a coisa mora)
        acao_desfeita:           { emoji:'arrow-back-up', cor:'#3D424C', bg:'#F7F8FA', borda:'#E2E4E9',
                                   tela: n.ref_tipo === 'pagamento' ? 'financeiro' : 'dashboard', label:'Ação desfeita'},
        // v144: o aluno cancelou o pedido que tinha mandado / o acordo passou a "desmarcar livre"
        pedido_cancelado:        { emoji:'circle-x', cor:'#3D424C', bg:'#F7F8FA', borda:'#E2E4E9', tela:'alunos', label:'Pedido cancelado'},
        acordo_desmarcar_livre:  { emoji:'file-text', cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tela:'alunos', label:'Acordo'},
        // v142 (revisao): entrou pelo link "Cadastrar varios de uma vez" (abre Alunos, onde esta' o "Completar")
        aluno_entrou_link:       { emoji:'user-plus', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tela:'alunos', label:'Entrou pelo seu link'},
        // v144: um professor entrou pelo seu convite (link de indicacao) — so' aviso
        prof_entrou_indicacao:   { emoji:'user-plus', cor:'#067A52', bg:'#E6F7F0', borda:'#8FDFC4', tela:'dashboard', label:'Professor pelo seu convite'},
      };
      // v152: 'ajuste_contrato' com ref contrato e' o pedido de renovacao do aluno — abre "Combinar proximo pacote" dele
      const pedeRenovar = n.tipo === 'ajuste_contrato' && n.ref_tipo === 'contrato' && n.aluno_id;
      const t = pedeRenovar ? { emoji:'refresh', cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tela:'alunos', label:'Quer renovar'}
        : tipos[n.tipo] || { emoji:'bell', cor:'#3D424C', bg:'#F7F8FA', borda:'#E2E4E9', tela:'alunos', label:'Aviso'};
      // 'reajuste_refazer' foi o sistema que mandou (nao o aluno): vai para o grupo Reajustes e abre o reajuste daquele aluno.
      const refazer = n.tipo === 'reajuste_refazer';
      // v140: aviso de uma aula que pede resposta (aluno esperando / "nao veio"): abre a aula no Inicio
      const abreAula = (n.tipo === 'aluno_esperando' || n.tipo === 'prof_nao_veio' || n.tipo === 'lembrete_aula_prof') && n.ref_tipo === 'aula' && n.ref_id;
      lista.push({ chave:`notif_${n.id}`, emoji:t.emoji, titulo:t.label, texto:n.mensagem || '—', tela:t.tela, aluno_id:n.aluno_id, cor:t.cor, bg:t.bg, borda:t.borda, tipo: refazer ? 'reajuste' : 'notificacao',
                   params: refazer && n.aluno_id ? { reajuste_aluno_id: n.aluno_id } : abreAula ? { aula_id: n.ref_id, abrir_aula: true, t: Date.now() }
                     : pedeRenovar ? { renovar_aluno_id: n.aluno_id, renovar_ja_pagou: renovarJaPagouSM(n), t: Date.now() } : null });
    });

    // Aula de turma e' UMA pendencia por sessao (nao uma por pessoa) e fecha na tela Turmas.
    const porSessao = {};
    const porAluno = {};
    [...(aulasPassadas||[]), ...(aulasHojeAtrasadas||[])].forEach(a => {
      if (a.sessao_id) { const sx = porSessao[a.sessao_id] || (porSessao[a.sessao_id] = { data: a.data, hora: a.hora, n: 0 }); sx.n++; return; }
      const id = a.aluno_id || a.alunos?.id || '';
      const nome = a.alunos?.nome?.split(' ')[0] || 'Aluno';
      if (!porAluno[id]) porAluno[id] = { nome, count: 0, aluno_id: id };
      porAluno[id].count++;
    });
    Object.entries(porSessao).forEach(([sid, sx]) => {
      lista.push({ chave:`turma_pend_${sid}`, emoji:'users', titulo:'Presença da turma', texto:`${fmtDataBR(sx.data)} ${String(sx.hora).slice(0,5)} — ${sx.n} pessoa${sx.n!==1?'s':''}, falta fechar`, tela:'turmas', cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tipo:'aula'});
    });
    Object.values(porAluno).forEach(({ nome, count, aluno_id }) => {
      lista.push({ chave:`aula_pend_${aluno_id}`, emoji:'clipboard-list', titulo:'Confirmar aula', texto:`${nome} — ${count} aula${count!==1?'s':''} aguardando confirmação`, tela:'alunos', aluno_id, cor:'#92400E', bg:'#FFFBEB', borda:'#FDE68A', tipo:'aula'});
    });

    (pagsPendentes||[]).forEach(p => {
      // v152: a cobranca do proximo pacote (combinado antes) que ainda nao venceu nao e' pendencia. As outras
      // continuam como antes, mesmo com vencimento futuro (revisao v152).
      if (p.contratos?.status === 'proximo' && p.data_vencimento && String(p.data_vencimento).slice(0, 10) > hojeSP()) return;
      const nome = p.alunos?.nome?.split(' ')[0] || 'Aluno';
      lista.push({ chave:`pag_pend_${p.id}`, emoji:'credit-card', titulo:'Pagamento pendente', texto:`${nome} — ${fmtBRL(p.valor)} a receber`, tela:'financeiro', aluno_id: p.aluno_id, cor:'#991B1B', bg:'#FEF2F2', borda:'#FECACA', tipo:'pagamento'});
    });

    (pagosRecentes||[]).forEach(p => {
      const nome = p.alunos?.nome?.split(' ')[0] || 'Aluno';
      lista.push({ chave:`pag_ok_${p.id}`, emoji:'circle-check', titulo:'Pagamento recebido', texto:`${nome} — ${fmtBRL(p.valor)} confirmado`, tela:'financeiro', aluno_id: p.aluno_id, cor:'#0A6F4D', bg:'#DCF5EC', borda:'#8FDFC4', tipo:'pagamento_ok'});
    });

    (contratos||[]).forEach(c => {
      const dias = Math.ceil((new Date(c.data_fim+'T12:00:00') - agora) / (1000*60*60*24));
      const nome = c.alunos?.nome?.split(' ')[0] || 'Aluno';
      lista.push({ chave:`contrato_${c.id}`, emoji:'file-text', titulo:'Pacote vencendo', texto:`${nome} — vence em ${dias} dia${dias!==1?'s':''}`, tela:'contratos', aluno_id: c.aluno_id, cor:'#1C43AE', bg:'#E7EDFF', borda:'#C2D4FF', tipo:'contrato'});
    });

    (contratosReajuste||[]).forEach(c => {
      const meses = Math.floor((agora - new Date(c.data_inicio+'T12:00:00')) / (1000*60*60*24*30.44));
      const nome = c.alunos?.nome?.split(' ')[0] || 'Aluno';
      lista.push({ chave:`reajuste_${c.id}`, emoji:'trending-up', titulo:'Reajuste sugerido', texto:`${nome} — ${meses} meses de acordo · ${fmtBRL(baseReajuste(c))}${sufixoPrecoAcordo(c)}`, tela:'financeiro', aluno_id: c.aluno_id, cor:'#B45309', bg:'#FFFBEB', borda:'#FDE68A', tipo:'reajuste'});
    });

    setItens(lista);
    setCarregando(false);
  }

  React.useEffect(() => {
    carregar();
    const t = setInterval(carregar, 5*60*1000);
    // a tela Notificacoes marcou avisos como lidos: o sino recarrega na hora
    window.addEventListener('plen:avisos-lidos', carregar);
    return () => { clearInterval(t); window.removeEventListener('plen:avisos-lidos', carregar); };
  }, [professor.id]);

  const naoLidas = itens.filter(i => !lidas.includes(i.chave));
  const badge = pendencias != null ? pendencias : naoLidas.length;

  const grupos = [
    { label:'Do aluno', tipos:['notificacao'], cor:'#1C43AE' },
    { label:'Aulas', tipos:['aula'], cor:'#92400E' },
    { label:'Financeiro', tipos:['pagamento','pagamento_ok'], cor:'#0A6F4D' },
    { label:'Contratos', tipos:['contrato'], cor:'#1C43AE' },
    { label:'Reajustes', tipos:['reajuste'], cor:'#B45309' },
  ];

  return (
    <>
      {}
      <button
        onClick={() => { if (!aberto) uso.janela('sino', badge); setAberto(a => !a); }}
        aria-label="Notificações"
        style={{
          position:'fixed', top:'14px', right:'16px', zIndex:200,
          maxWidth:'42px',width:'100%', height:'42px', borderRadius:'50%',
          background: badge > 0 ? '#16181D' : '#3D424C',
          border:'none', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 2px 12px rgba(0,0,0,0.28)',
          transition:'background 0.2s',
        }}>
        {}
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {badge > 0 && (
          <span style={{
            position:'absolute', top:'-2px', right:'-2px',
            minWidth:'18px', height:'18px', borderRadius:'9px',
            background:'#EF4444', border:'2px solid white',
            fontSize:'10px', fontWeight:'800', color:'white',
            display:'flex', alignItems:'center', justifyContent:'center',
            lineHeight:1, padding:'0 3px',
          }}>
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>

      {}
      {aberto && (
        <Portal>
        <div style={{position:'fixed', inset:0, zIndex:199}} onClick={() => setAberto(false)}>
          <div
            style={{
              position:'fixed', top:'64px', right:'12px',
              width:'min(340px, calc(100vw - 24px))',
              background:'white', borderRadius:'18px',
              boxShadow:'0 20px 60px rgba(0,0,0,0.18)', border:'1px solid #E2E4E9',
              overflow:'hidden', zIndex:200,
            }}
            onClick={e => e.stopPropagation()}>

            {}
            <div style={{padding:'14px 18px', background:'#16181D', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="16" height="16">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                <span style={{fontSize:'14px', fontWeight:'700', color:'white'}}>Notificações</span>
                {badge > 0 && <span style={{background:'#EF4444', color:'white', fontSize:'10px', fontWeight:'800', borderRadius:'8px', padding:'1px 6px'}}>{pendencias != null ? `${badge} na sua vez` : `${badge} novas`}</span>}
              </div>
              <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                {naoLidas.length > 0 && <button onClick={() => { uso.acao('sino.marcar_lidas', { n: naoLidas.length }); marcarTodasLidas(); }} style={{background:'rgba(255,255,255,0.12)', border:'none', color:'white', fontSize:'11px', fontWeight:'600', cursor:'pointer', borderRadius:'6px', padding:'4px 8px'}}>Marcar lidas</button>}
                <button onClick={carregar} style={{background:'none', border:'none', color:'rgba(255,255,255,0.6)', fontSize:'16px', cursor:'pointer', lineHeight:1}}><Ico n="refresh" /></button>
              </div>
            </div>

            {pendencias != null && !pendCarregando && (
              <button onClick={() => { uso.acao('sino.ver_pendencias', { n: badge }); onNavigate('notificacoes'); setAberto(false); }}
                style={{width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'12px 18px', background: badge > 0 ? '#FEF2F2' : '#F7F8FA', border:'none', borderBottom:'1px solid #F0F1F4', cursor:'pointer', textAlign:'left', fontFamily:'inherit'}}>
                <Ico n="checklist" s={17} c={badge > 0 ? '#DC2626' : '#6E7480'} />
                <span style={{flex:1, fontSize:'13px', fontWeight:'700', color: badge > 0 ? '#991B1B' : '#3D424C'}}>
                  {badge > 0 ? `${badge} ${badge === 1 ? 'pendência sua' : 'pendências suas'}` : 'Nada na sua vez'}
                </span>
                <span style={{fontSize:'12px', fontWeight:'700', color:'#2F6BFF', whiteSpace:'nowrap'}}>Ver pendências</span>
              </button>
            )}
            <div style={{maxHeight:'70vh', overflowY:'auto'}}>
              {carregando ? (
                <div style={{padding:'32px', textAlign:'center', color:'#9AA0AB', fontSize:'13px'}}>Carregando...</div>
              ) : itens.length === 0 ? (
                <div style={{padding:'40px 24px', textAlign:'center'}}>
                  <div style={{fontSize:'32px', marginBottom:'8px'}}><Ico n={pendencias > 0 || pendCarregando ? 'bell' : 'confetti'} /></div>
                  <div style={{fontSize:'14px', fontWeight:'700', color: pendencias > 0 || pendCarregando ? '#3D424C' : '#0A6F4D'}}>{pendencias > 0 || pendCarregando ? 'Nenhum aviso novo' : 'Tudo em dia!'}</div>
                  <div style={{fontSize:'12px', color:'#9AA0AB', marginTop:'4px'}}>{pendencias > 0 || pendCarregando ? 'O que está na sua vez fica em Pendências.' : 'Nenhuma pendência no momento.'}</div>
                </div>
              ) : (
                grupos.map(grupo => {
                  const gr = itens.filter(i => grupo.tipos.includes(i.tipo));
                  if (gr.length === 0) return null;
                  return (
                    <div key={grupo.label}>
                      <div style={{padding:'8px 18px 4px', fontSize:'10px', fontWeight:'800', letterSpacing:'0.06em', color:'#9AA0AB', textTransform:'uppercase', background:'#F7F8FA', borderBottom:'1px solid #F0F1F4'}}>
                        {grupo.label}
                      </div>
                      {gr.map((item) => {
                        const jáLida = lidas.includes(item.chave);
                        const isContrato = item.tipo === 'contrato';
                        return (
                          <div key={item.chave} style={{borderBottom:'1px solid #F0F1F4', background: jáLida ? 'white' : '#F5F7FF', opacity: jáLida ? 0.6 : 1}}>
                            <button
                              onClick={() => { uso.acao('sino.abrir_aviso', { tipo: item.tipo, destino: item.tela }); marcarLida(item.chave); onNavigate(item.tela, { aluno_id: item.aluno_id, ...(item.params || {}) }); setAberto(false); }}
                              style={{
                                width:'100%', display:'flex', alignItems:'flex-start', gap:'12px',
                                padding: isContrato ? '10px 18px 6px' : '12px 18px',
                                background:'transparent',
                                border:'none',
                                cursor:'pointer', textAlign:'left',
                              }}
                              onMouseOver={e => e.currentTarget.style.background='#E7EDFF'}
                              onMouseOut={e => e.currentTarget.style.background='transparent'}>
                              <span style={{
                                width:'34px', height:'34px', borderRadius:'10px',
                                background: jáLida ? '#F0F1F4' : item.bg,
                                border:`1.5px solid ${jáLida ? '#E2E4E9' : item.borda}`,
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize:'16px', flexShrink:0,
                              }}><Ico n={item.emoji} s={17} /></span>
                              <div style={{flex:1, minWidth:0}}>
                                <div style={{display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px'}}>
                                  <span style={{fontSize:'12px', fontWeight: jáLida ? '500' : '700', color: jáLida ? '#9AA0AB' : '#16181D'}}>{item.titulo}</span>
                                  {!jáLida && <span style={{width:'6px', height:'6px', borderRadius:'50%', background:'#EF4444', flexShrink:0}}/>}
                                </div>
                                <div style={{fontSize:'12px', color: jáLida ? '#9AA0AB' : '#3D424C', lineHeight:'1.4', whiteSpace:'normal'}}>{item.texto}</div>
                              </div>
                              {!jáLida && !isContrato && <svg viewBox="0 0 24 24" fill="none" stroke="#9AA0AB" strokeWidth="2" width="14" height="14" style={{flexShrink:0,marginTop:'2px'}}>
                                <polyline points="9 18 15 12 9 6"/>
                              </svg>}
                            </button>
                            {}
                            {isContrato && (
                              <div style={{padding:'0 18px 10px 62px', display:'flex', gap:'6px'}}>
                                <button onClick={() => { uso.acao('sino.renovar'); marcarLida(item.chave); onNavigate('alunos', { aluno_id: item.aluno_id, renovar: true }); setAberto(false); }}
                                  style={{fontSize:'11px', fontWeight:'700', padding:'5px 12px', borderRadius:'8px', border:'none', background:'#2F6BFF', color:'white', cursor:'pointer'}}>
                                  <Ico n="refresh" /> Renovar plano <Ico n="arrow-right" />
                                </button>
                                <button onClick={() => { uso.acao('sino.ver_acordo'); marcarLida(item.chave); onNavigate('contratos', { aluno_id: item.aluno_id }); setAberto(false); }}
                                  style={{fontSize:'11px', fontWeight:'600', padding:'5px 10px', borderRadius:'8px', border:'1px solid #C2D4FF', background:'white', color:'#2F6BFF', cursor:'pointer'}}>
                                  Ver contrato
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {}
            {itens.length > 0 && (
              <div style={{padding:'10px 18px', borderTop:'1px solid #F0F1F4', textAlign:'center'}}>
                <button onClick={() => { uso.acao('sino.ver_todas'); onNavigate('notificacoes'); setAberto(false); }}
                  style={{background:'none', border:'none', fontSize:'12px', color:'#2F6BFF', fontWeight:'600', cursor:'pointer'}}>
                  Ver todas as notificações <Ico n="arrow-right" />
                </button>
              </div>
            )}
          </div>
        </div>
        </Portal>
      )}
    </>
  );
}

function MobileTopBarProf({ professor, onLogout, onPerfil }) {
  const iniciais = professor?.nome ? professor.nome.split(' ').slice(0,2).map(n=>n[0]).join('').toUpperCase() : '?';
  return (
    <div className="mobile-top-bar">
      <div onClick={() => onPerfil && onPerfil()} role="button" title="Abrir configurações" style={{display:'flex', alignItems:'center', gap:'10px', cursor:'pointer'}}>
        <div style={{width:'34px', height:'34px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'15px', fontWeight:'800', color:'white', flexShrink:0}}><Ico n="trophy" /></div>
        <div>
          <div style={{fontSize:'11px', color:'#9AA0AB', fontWeight:'600', letterSpacing:'0.05em'}}>PROFESSOR</div>
          <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D', lineHeight:'1.2'}}>{professor?.nome?.split(' ')[0] || 'Perfil'}</div>
        </div>
      </div>
      <button onClick={onLogout} aria-label="Sair da conta"
        style={{display:'flex', alignItems:'center', gap:'6px', padding:'8px 12px', borderRadius:'12px', background:'#F0F1F4', border:'none', color:'#6E7480', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>
        <Icons.LogOut /> Sair
      </button>
    </div>
  );
}

function BottomNavProf({ ativa, setAtiva, professorId, pendencias = null }) {
  // v134: a aba virou "Pendencias" e o numero vermelho e' o que esta' "Sua vez" (pendencias, do
  // banco). Sem a lista (banco sem minhas_pendencias, ou falha): volta ao de antes, avisos nao lidos.
  // Numero de avisos nao lidos PARA o professor (coluna destinatario, Fase 0 Etapa 1). Atualiza
  // ao abrir, ao voltar o app para a frente e quando a tela Notificacoes marca como lidos.
  // Banco sem a coluna nova: a contagem falha e o numero simplesmente nao aparece.
  const [naoLidos, setNaoLidos] = React.useState(0);
  React.useEffect(() => {
    if (!professorId) return;
    let vivo = true;
    const contar = async () => {
      const { count, error } = await supabase.from('notificacoes').select('id', { count: 'exact', head: true })
        .eq('professor_id', professorId).eq('destinatario', 'professor').eq('lida', false);
      if (vivo) setNaoLidos(error ? 0 : (count || 0));
    };
    contar();
    const relogio = setInterval(contar, 60000);
    const aoVoltar = () => { if (document.visibilityState === 'visible') contar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('plen:avisos-lidos', contar);
    return () => { vivo = false; clearInterval(relogio); document.removeEventListener('visibilitychange', aoVoltar); window.removeEventListener('plen:avisos-lidos', contar); };
  }, [professorId]);
  const tabs = [
    { key:'dashboard',    label:'Início',      ic:'home' },
    { key:'alunos',       label:'Alunos',      ic:'users' },
    { key:'aulas',        label:'Aulas',       ic:'calendar' },
    { key:'financeiro',   label:'Financeiro',  ic:'wallet' },
    { key:'notificacoes', label:'Pendências', ic:'checklist' },   // a chave continua 'notificacoes' (links e navegacao antiga)
  ];
  const numAba = pendencias == null ? naoLidos : pendencias;
  const rotuloNum = pendencias == null ? ' avisos novos' : (numAba === 1 ? ' pendência sua' : ' pendências suas');
  return (
    <div className="app-bottom-nav">
      {tabs.map(t => {
        const ativo = ativa === t.key;
        return (
          <button key={t.key} className="bnav-btn" onClick={() => setAtiva(t.key)}>
            <span className="bnav-icon" style={{position:'relative'}}><Ico n={t.ic} s={21} c={ativo ? '#6E9BFF' : 'rgba(255,255,255,0.62)'} />
              {t.key === 'notificacoes' && numAba > 0 && (
                <span aria-label={numAba + rotuloNum} style={{position:'absolute', top:'-5px', right:'-9px', minWidth:'17px', height:'17px', padding:'0 4px', borderRadius:'9px', background:'#DC2626', color:'white', fontSize:'10.5px', fontWeight:'800', lineHeight:'17px', textAlign:'center', boxSizing:'border-box'}}>{numAba > 9 ? '9+' : numAba}</span>
              )}
            </span>
            <span className="bnav-label" style={{color: ativo ? '#6E9BFF' : 'rgba(255,255,255,0.65)'}}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Sidebar({ ativa, setAtiva, professor, onLogout, pendencias = null }) {
  // Quatro destinos na frente (os que o professor abre todo dia) e o resto num grupo
  // secundario — o mesmo desenho da barra inferior do celular. Dez itens iguais era
  // um painel de sistema, nao um app.
  // v134: "Pendencias" (antes "Avisos", no grupo Mais) sobe para a frente, como na barra do
  // celular, com o numero do que esta' "Sua vez".
  const menus = [
    { key: 'dashboard', label: 'Início', icon: <Icons.Home /> },
    { key: 'alunos', label: 'Alunos', icon: <Icons.Users /> },
    { key: 'aulas', label: 'Aulas', icon: <Icons.ClipboardList /> },
    { key: 'financeiro', label: 'Financeiro', icon: <Icons.DollarSign /> },
    { key: 'notificacoes', label: 'Pendências', icon: <Ico n="checklist" s={20} />, badge: pendencias || 0 },
    { grupo: 'Mais' },
    { key: 'turmas', label: 'Turmas de treino', icon: <Icons.Users /> },
    { key: 'agenda', label: 'Calendário', icon: <Icons.Calendar /> },
    { key: 'contratos', label: 'Acordos', icon: <Icons.FileText /> },
    { key: 'planos', label: 'Meus Planos', icon: <Icons.Package /> },
    // 'Relatórios' saiu do menu em 14/10: era tela "Em breve" (a Apple recusa conteúdo de espera).
    { key: 'configuracoes', label: 'Configurações', icon: <Icons.Settings /> },
  ];
  const iniciais = professor?.nome ? professor.nome.split(' ').slice(0,2).map(n=>n[0]).join('').toUpperCase() : '?';
  return (
    <div className="app-sidebar" style={{width:'232px', background:'#16181D', minHeight:'100vh', flexDirection:'column', flexShrink:0}}>
      <div style={{padding:'22px 18px 18px', borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
          <div style={{maxWidth:'34px',width:'100%', height:'34px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'17px', flexShrink:0, boxShadow:'0 4px 10px rgba(99,102,241,0.4)'}}><Ico n="trophy" /></div>
          <img src="/logo-branco.svg" alt="Plen" style={{height:'22px', display:'block'}} />
        </div>
      </div>
      <nav style={{flex:1, padding:'14px 10px', display:'flex', flexDirection:'column', gap:'1px'}}>
        {menus.map(m => {
          if (m.grupo) return <div key={'g-'+m.grupo} style={{fontSize:'10.5px', fontWeight:'800', letterSpacing:'.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.28)', padding:'16px 12px 6px'}}>{m.grupo}</div>;
          const isAtiva = ativa === m.key;
          return (
            <button key={m.key} onClick={() => setAtiva(m.key)}
              style={{width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'10px', border:'none', cursor:'pointer', textAlign:'left', fontSize:'13px', fontWeight: isAtiva ? '700' : '500', background: isAtiva ? 'rgba(99,102,241,0.18)' : 'transparent', color: isAtiva ? '#6E9BFF' : 'rgba(255,255,255,0.48)', transition:'all 0.12s'}}
              onMouseOver={e => { if(!isAtiva){e.currentTarget.style.background='rgba(255,255,255,0.05)';e.currentTarget.style.color='rgba(255,255,255,0.75)';}}}
              onMouseOut={e => { if(!isAtiva){e.currentTarget.style.background='transparent';e.currentTarget.style.color='rgba(255,255,255,0.48)';}}} >
              <span style={{color:isAtiva?'#6E9BFF':'rgba(255,255,255,0.32)',flexShrink:0,display:'flex'}}>{m.icon}</span>
              <span style={{flex:1}}>{m.label}</span>
              {m.badge > 0 && <span className="num" style={{background:'#DC2626',color:'white',borderRadius:'10px',fontSize:'10px',fontWeight:'800',padding:'1px 6px',minWidth:'18px',textAlign:'center'}}>{m.badge > 99 ? '99+' : m.badge}</span>}
            </button>
          );
        })}
      </nav>
      <div style={{padding:'10px', borderTop:'1px solid rgba(255,255,255,0.06)'}}>
        <button onClick={() => setAtiva('configuracoes')}
          style={{width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'10px', background: ativa==='configuracoes' ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.04)', marginBottom:'4px', border:'none', cursor:'pointer', textAlign:'left', transition:'all 0.12s'}}
          onMouseOver={e=>{if(ativa!=='configuracoes'){e.currentTarget.style.background='rgba(255,255,255,0.09)';}}}
          onMouseOut={e=>{if(ativa!=='configuracoes'){e.currentTarget.style.background='rgba(255,255,255,0.04)';}}} >
          <div style={{width:'30px', height:'30px', borderRadius:'8px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'800', color:'white', flexShrink:0}}>{iniciais}</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:'12px', fontWeight:'700', color:'rgba(255,255,255,0.85)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{professor?.nome?.split(' ')[0]}</div>
            <div style={{fontSize:'10px', color:'rgba(255,255,255,0.32)'}}>Meu perfil ›</div>
          </div>
        </button>
        <button onClick={onLogout}
          style={{width:'100%', display:'flex', alignItems:'center', gap:'8px', padding:'9px 12px', borderRadius:'8px', border:'none', background:'transparent', cursor:'pointer', fontSize:'12px', color:'rgba(255,255,255,0.32)', transition:'all 0.12s'}}
          onMouseOver={e=>{e.currentTarget.style.background='rgba(239,68,68,0.1)';e.currentTarget.style.color='#EF4444';}}
          onMouseOut={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='rgba(255,255,255,0.32)';}}>
          <Icons.LogOut /> Sair da conta
        </button>
      </div>
    </div>
  );
}

// U21 — seletor de motivo usado nos dois lados (aluno e professor). O motivo e' para a
// relacao e a estatistica; quem decide o dinheiro continua sendo quem cancelou e quando.
// Lista curta, "Outro" no fim, escolhido DEPOIS de ver a consequencia — padrao Uber/Airbnb.
function MotivoPicker({ valor, onChange, obs, onObs, excluir = [] }) {
  const lista = MOTIVOS_HUMANOS.filter(m => !excluir.includes(m.id));
  return (
    <div>
      <div style={{fontSize:'12px', fontWeight:'700', color:'#6E7480', marginBottom:'8px'}}>Qual o motivo?</div>
      <div style={{display:'flex', flexWrap:'wrap', gap:'6px'}}>
        {lista.map(m => (
          <button key={m.id} type="button" onClick={() => onChange(m.id)}
            style={{border: valor === m.id ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: valor === m.id ? '#E7EDFF' : 'white',
                    color: valor === m.id ? '#1C43AE' : '#3D424C', borderRadius:'20px', padding:'7px 12px', minHeight:'44px', fontSize:'12.5px',
                    fontWeight:'700', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'6px'}}>
            <Ico n={m.icone} s={14} /> {m.rotulo}
          </button>
        ))}
      </div>
      {valor === 'outro' && onObs && (
        <input value={obs || ''} onChange={e => onObs(e.target.value)} placeholder="Conte em poucas palavras"
          style={{width:'100%', marginTop:'8px', border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'9px 12px', fontSize:'13px', boxSizing:'border-box', fontFamily:'inherit'}} />
      )}
    </div>
  );
}

// Etapa 9 (v131) — "Aluno avisou" pelo WhatsApp ou por ligacao. O professor diz QUANDO o aluno
// avisou ([Hoje]/[Ontem] + hora) e o motivo; o BANCO decide se foi no prazo, pelo acordo da aula,
// na hora de Brasilia (professor_registrar_aviso). O cartao mostra antes, em reais, o que vai
// acontecer (previa com p_simular). Ao confirmar, manda o no_prazo que o cartao MOSTROU: se o
// prazo virou enquanto o professor preenchia, nada e' gravado e o cartao novo aparece.
function PainelAvisoAluno({ aula, nomeAluno, onFeito, onFechar }) {
  const primeiro = primeiroNomeSM(nomeAluno);
  const [dia, setDia] = React.useState('hoje');                       // 'hoje' | 'ontem'
  const [hora, setHora] = React.useState(() => horaSP(new Date()));
  const [motivo, setMotivo] = React.useState(null);
  const [obs, setObs] = React.useState('');
  const [previa, setPrevia] = React.useState(null);                  // { carregando, dados, erro, extra }
  const [salvando, setSalvando] = React.useState(false);
  const [tentativa, setTentativa] = React.useState(0);               // [Tentar de novo] refaz a previa
  const trava = React.useRef(false);
  const req = React.useRef(0);
  React.useEffect(() => { uso.janela('aviso_aluno_registrar'); }, []);
  const diaISO = dia === 'ontem' ? hojeSP(-1) : hojeSP();
  const horaOk = /^\d{2}:\d{2}$/.test(String(hora || ''));
  const params = (simular, esperado) => ({
    p_aula_id: aula.id, p_avisado_dia: diaISO, p_avisado_hora: hora, p_motivo: motivo,
    p_obs: motivo === 'outro' && obs.trim() ? obs.trim() : null, p_simular: simular, p_no_prazo_esperado: esperado,
  });
  React.useEffect(() => {
    if (!motivo || !horaOk) { req.current++; setPrevia(null); return; }
    const n = ++req.current;
    setPrevia(p => ({ carregando: true, dados: p && p.dados, erro: null }));
    const t = setTimeout(async () => {
      const res = await rpcAulaSM('professor_registrar_aviso', params(true, null));
      if (n !== req.current) return;
      setPrevia(res.ok ? { carregando: false, dados: res.data, erro: null }
                       : { carregando: false, dados: null, erro: res.cod, extra: res.data });
    }, 300);
    return () => clearTimeout(t);
  }, [motivo, dia, hora, tentativa]);

  async function registrar() {
    if (!motivo) { uso.bloqueio('aviso_aluno_registrar', null, null, 'sem_motivo'); toastSM('Escolha o motivo.'); return; }
    const d = previa && previa.dados;
    if (!d || previa.carregando || trava.current) return;
    trava.current = true; setSalvando(true);
    const res = await rpcAulaSM('professor_registrar_aviso', params(false, !!d.no_prazo));
    trava.current = false; setSalvando(false);
    if (!res.ok) {
      if (res.cod === 'prazo_mudou' && res.data && res.data.previa) {
        setPrevia({ carregando: false, dados: res.data.previa, erro: null });
        uso.bloqueio('aviso_aluno_registrar', null, null, 'prazo_mudou');
        toastSM(textoErroAulaProf('prazo_mudou'));
        return;
      }
      avisarErroAulaProf(res, 'aviso_aluno_registrar', 'aviso', nomeAluno);
      if (!res.rede) setPrevia({ carregando: false, dados: null, erro: res.cod, extra: res.data });
      return;
    }
    const r = res.data;
    uso.acao('aviso_aluno.registrar', { no_prazo: !!r.no_prazo, beneficio: r.beneficio || 'nenhum', dia, motivo: motivoUso(motivo) });   // LGPD: "doente" sai como 'pessoal'
    // v142: "Feito · Desfazer" — no prazo virou desmarcacao (P4); fora do prazo e' o aviso registrado (P3,
    // o exemplo do Marcus: registrou "avisou que nao vem" por engano).
    if (r.ja_feito) toastSM(`${primeiro} já tinha avisado. Nada mudou.`);
    else toastDesfazerSM(r.no_prazo ? `Registrado: ${primeiro} avisou ${r.chuva ? 'por chuva' : 'no prazo'}. ${textoBeneficioProfSM(r)} ${primeiro} recebe o aviso.`
      : `Registrado: aviso fora do prazo. ${r.pode_devolver ? 'Decida agora ou depois: Conta a aula ou Devolver.' : 'Pela regra fica como falta.'}`,
      { ref_tipo: 'aula', ref_id: aula.id, esperado: r.no_prazo ? 'cancelamento' : 'aviso_registrado' },
      { papel: 'professor', nomeOutro: nomeAluno, curto: `registro do aviso de ${primeiro}` });
    onFeito && onFeito(r);
  }

  const d = previa && previa.dados;
  const seg = ativo => ({ flex: '1 1 0', minWidth: 0, minHeight: '44px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
                          border: ativo ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: ativo ? '#E7EDFF' : 'white', color: ativo ? '#1C43AE' : '#3D424C' });
  let cartao = null;
  if (previa && previa.carregando && !d) cartao = <div style={{ fontSize: '12.5px', color: '#6E7480' }}>Conferindo o prazo…</div>;
  else if (previa && previa.erro === 'rede') cartao = (
    <div style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
      Não consegui conferir o prazo agora. Confira a internet.
      <button type="button" onClick={() => setTentativa(n => n + 1)}
        style={{ display: 'block', marginTop: '8px', minHeight: '44px', width: '100%', borderRadius: '10px', border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>
        Tentar de novo
      </button>
    </div>
  );
  else if (previa && previa.erro) cartao = (
    <div style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>{textoErroAulaProf(previa.erro, previa.extra, 'aviso', nomeAluno)}</div>
  );
  else if (d && d.no_prazo) cartao = (
    <div style={{ fontSize: '13px', color: '#0A5B40', lineHeight: 1.5 }}>
      <b>{d.chuva ? 'Chuva' : 'No prazo'}</b> ({d.chuva ? 'vale até o início da aula:' : 'o limite era'} {d.prazo_texto}). {textoBeneficioProfSM(d, { previa: true })}
    </div>
  );
  else if (d) cartao = (
    <div style={{ fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
      <b>Fora do prazo</b> (o limite era {d.prazo_texto}).{' '}
      {d.consome ? `Pela regra a aula conta: sai 1 aula do pacote de ${primeiro}${Number(d.valor_aula) > 0 ? ` (${fmtBRL(d.valor_aula)})` : ''}.` : 'Pela regra fica como falta.'}
      {d.pode_devolver ? ' Depois você decide: Conta a aula ou Devolver, por cortesia.' : ''}
    </div>
  );
  const podeConfirmar = !!d && !(previa && previa.carregando) && !salvando;
  return (
    <div style={{ marginTop: '10px', background: '#F7F9FF', border: '1px solid #C2D4FF', borderRadius: '14px', padding: '12px' }}>
      <div style={{ fontSize: '13px', fontWeight: '800', color: '#16181D' }}>Quando {primeiro} avisou que não vem?</div>
      <div style={{ fontSize: '11.5px', color: '#6E7480', marginTop: '2px' }}>Hora no horário de Brasília.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1.2fr)', gap: '6px', marginTop: '8px' }}>
        <button type="button" onClick={() => setDia('hoje')} style={seg(dia === 'hoje')}>Hoje</button>
        <button type="button" onClick={() => setDia('ontem')} style={seg(dia === 'ontem')}>Ontem</button>
        <input type="time" value={hora} onChange={e => setHora(e.target.value)} aria-label="Hora do aviso"
          style={{ minWidth: 0, width: '100%', minHeight: '44px', boxSizing: 'border-box', border: '1.5px solid #E2E4E9', borderRadius: '10px', padding: '6px 8px', fontSize: '14px', fontFamily: 'inherit', background: 'white' }} />
      </div>
      <div style={{ marginTop: '10px' }}>
        <MotivoPicker valor={motivo} onChange={setMotivo} obs={obs} onObs={setObs} excluir={['feriado']} />
      </div>
      {cartao && <div style={{ marginTop: '10px', background: 'white', borderRadius: '10px', padding: '10px 12px', border: '1px solid #E2E4E9' }}>{cartao}</div>}
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button type="button" onClick={onFechar} disabled={salvando}
          style={{ flex: 1, minHeight: '44px', borderRadius: '10px', border: '1.5px solid #E2E4E9', background: 'white', color: '#6E7480', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}>
          Voltar
        </button>
        <button type="button" onClick={registrar} disabled={!podeConfirmar}
          style={{ flex: 2, minHeight: '44px', borderRadius: '10px', border: 'none', background: podeConfirmar ? '#16181D' : '#E2E4E9', color: podeConfirmar ? 'white' : '#9AA0AB', fontSize: '13px', fontWeight: '800', cursor: podeConfirmar ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
          {salvando ? 'Registrando…' : 'Registrar aviso'}
        </button>
      </div>
    </div>
  );
}

// ---- v140: [Opcoes] da aula de hoje (antes do inicio) e de amanha --------------------------
// Tres caminhos num painel so' (no lugar do antigo [Avisou falta]):
//  1. "Perguntar se Bia vem" — professor_pedir_presenca. Ao abrir, a previa (p_simular) diz se da'
//     e por que nao (bloqueio). Aluno sem app: a linha vira "Perguntar pelo WhatsApp" (nada gravado).
//  2. "Bia avisou que nao vem" — o PainelAvisoAluno de sempre (aviso por WhatsApp/ligacao).
//  3. "Nao vou poder dar esta aula" — PainelNaoVouDarSM (professor_cancelar_aula com o motivo).
// onFeito(patch): patch = campos para atualizar a linha na hora (pergunta enviada) ou null (recarregar).
function LinhaOpcaoSM({ ic, cor = '#2F6BFF', titulo, sub, nota, onClick, off = false, busy = false, children }) {
  const ativo = !!onClick && !off && !busy;
  return (
    <div style={{ background: off ? '#F7F8FA' : 'white', border: '1px solid #E2E4E9', borderRadius: '12px' }}>
      <button type="button" onClick={ativo ? onClick : undefined} disabled={!ativo} aria-disabled={!ativo}
        style={{ width: '100%', minHeight: '44px', textAlign: 'left', border: 'none', background: 'transparent', padding: '10px 12px',
                 cursor: ativo ? 'pointer' : 'default', fontFamily: 'inherit', display: 'flex', gap: '10px', alignItems: 'flex-start', opacity: busy ? 0.6 : 1 }}>
        <Ico n={ic} s={17} c={off ? '#9AA0AB' : cor} style={{ marginTop: '1px' }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: off ? '#6E7480' : '#16181D', lineHeight: 1.35 }}>{busy ? 'Enviando…' : titulo}</span>
          {sub ? <span style={{ display: 'block', fontSize: '11.5px', color: '#6E7480', marginTop: '2px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{sub}</span> : null}
          {nota ? <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: '#92400E', background: '#FFFBEB', borderRadius: '8px', padding: '5px 8px', marginTop: '6px', lineHeight: 1.45 }}>{nota}</span> : null}
        </span>
      </button>
      {children ? <div style={{ padding: '0 12px 10px 39px' }}>{children}</div> : null}
    </div>
  );
}
function BotaoWhatsSM({ onClick, rotulo = 'Chamar no WhatsApp' }) {
  return (
    <button type="button" onClick={onClick}
      style={{ minHeight: '44px', border: '1.5px solid #A7E3CB', background: '#EFFAF5', color: '#0A6F4D', borderRadius: '10px', padding: '0 12px',
               fontSize: '12.5px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <Ico n="brand-whatsapp" s={15} c="#0E9F6E" /> {rotulo}
    </button>
  );
}
function OpcoesAulaProfSM({ aula, nomeAluno, alunoUsaApp, telefone, nomeProf, origem = 'outro', onFeito, onFechar, abrir = null }) {
  const P = primeiroNomeSM(nomeAluno, 'Aluno');
  const individual = aulaIndividualSM(aula);
  const [sim, setSim] = React.useState(() => (individual ? { carregando: true } : null));
  const [sub, setSub] = React.useState(abrir);     // null | 'aviso' | 'naovou'
  const [busy, setBusy] = React.useState(false);
  const [tentativa, setTentativa] = React.useState(0);
  React.useEffect(() => { uso.janela('aula_opcoes_prof'); }, []);
  React.useEffect(() => {
    if (!individual || !aula?.id) return undefined;
    let vivo = true;
    setSim({ carregando: true });
    pedirPresencaProfSM(aula, { simular: true, origem, nomeAluno }).then(r => { if (vivo) setSim(r || { ok: false, rede: true }); }, () => { if (vivo) setSim({ ok: false, rede: true }); });
    return () => { vivo = false; };
  }, [aula?.id, tentativa]);
  const mensal = sim && sim.ok && typeof sim.pacote === 'boolean' ? !sim.pacote
    : !!(aula.duracao && (aula.duracao.tipo_cobranca === 'mensal' || aula.duracao.turma_id));
  function whats(motivo) {
    uso.acao('presenca.whats', { origem, motivo });
    compartilharOuWhats(waLinkSM(telefone, textoWhatsPerguntaSM(aula, nomeAluno, nomeProf)), 'presenca');
  }
  async function perguntar() {
    if (busy) return;
    setBusy(true);
    const r = await pedirPresencaProfSM(aula, { origem, nomeAluno });
    setBusy(false);
    if (r.ok) { onFeito && onFeito({ presenca_pedida_em: r.pedida_em || new Date().toISOString() }); return; }
    if (r.erro === 'aula_mudou') { onFeito && onFeito(null); return; }
    if (r.erro) setTentativa(n => n + 1);   // o estado mudou: confere de novo o que da' para fazer
  }

  if (sub === 'aviso') return (
    <PainelAvisoAluno aula={aula} nomeAluno={nomeAluno} onFechar={() => (abrir ? onFechar && onFechar() : setSub(null))}
      onFeito={() => onFeito && onFeito(null)} />
  );
  if (sub === 'naovou') return (
    <PainelNaoVouDarSM aula={aula} nomeAluno={nomeAluno} alunoUsaApp={alunoUsaApp} telefone={telefone} nomeProf={nomeProf} origem={origem}
      onFechar={() => (abrir ? onFechar && onFechar() : setSub(null))} onFeito={() => onFeito && onFeito(null)} />
  );

  // Linha 1 — a pergunta (so' aula individual), pelo bloqueio que o banco mandou (§4.4)
  let l1 = null;
  const tituloPerg = `Perguntar se ${P} vem`;
  // v142 (B): o aluno ja' disse "Vou" (com ou sem a pergunta): nada a perguntar
  const linhaVem = <LinhaOpcaoSM ic="circle-check" cor="#0E9F6E" titulo={`✓ ${P} vem`} sub={`${P} avisou pelo app que vai. Não precisa perguntar.`} off />;
  // revisao v142: com a previa NOVA do banco dizendo que da' para perguntar (o aluno desfez o "Vou" depois de a tela
  // carregar), vale a previa — antes o "✓ vem" velho da memoria escondia o "Perguntar".
  if (individual && aula.presenca_vou_em && !(sim && sim.ok && sim.bloqueio !== 'ja_respondeu')) l1 = linhaVem;
  else if (individual && sim) {
    const b = sim.ok ? (sim.bloqueio || null) : null;
    if (sim.carregando) l1 = <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} sub="Conferindo…" off />;
    else if (sim.semFuncao) l1 = (
      <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} sub="Ainda não disponível nesta versão. Atualize o app (feche e abra de novo) — ou chame no WhatsApp." off>
        <BotaoWhatsSM onClick={() => whats('sem_funcao')} />
      </LinhaOpcaoSM>
    );
    else if (sim.rede) l1 = (
      <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} sub="Não consegui conferir agora. Confira a internet." off>
        <button type="button" onClick={() => setTentativa(n => n + 1)}
          style={{ minHeight: '44px', border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C', borderRadius: '10px', padding: '0 12px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
          Tentar de novo
        </button>
      </LinhaOpcaoSM>
    );
    else if (!sim.ok) l1 = ['turma', 'aula_nao_agendada', 'aula_com_registro', 'aula_proposta', 'aluno_desvinculado'].includes(sim.erro) ? null
      : <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} sub={textoErroPresencaProfSM(sim.erro, nomeAluno)} off />;
    else if (['aviso_vigente', 'aula_com_registro', 'aula_nao_agendada', 'aula_proposta', 'turma', 'aluno_desvinculado'].includes(b)) l1 = null;
    else if (!b) l1 = (
      <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} busy={busy} onClick={perguntar}
        sub={`${P} recebe um aviso no celular e responde Vou ou Não vou.`}
        nota={sim.no_prazo === false
          ? `O prazo de ${P} para desmarcar sem custo já passou${sim.prazo_texto ? ` (${sim.prazo_texto})` : ''}. ${mensal ? 'Se disser que não vai, fica como falta.' : 'Se disser que não vai, a aula conta — você pode devolver.'}`
          : null} />
    );
    else if (b === 'ja_perguntou') l1 = <LinhaOpcaoSM ic="clock" titulo={`Perguntado ${sim.pedida_em ? quandoRelSM(sim.pedida_em) : ''}`.trim()} sub="A resposta aparece aqui." off />;
    else if (b === 'ja_respondeu') l1 = linhaVem;
    else if (b === 'sem_app') l1 = (
      <LinhaOpcaoSM ic="brand-whatsapp" cor="#0E9F6E" titulo="Perguntar pelo WhatsApp" onClick={() => whats('sem_app')}
        sub={`${P} ainda não usa o app. Abre o WhatsApp com a pergunta pronta.`} />
    );
    else if (b === 'perto_demais' || b === 'ja_comecou' || b === 'tarde_da_noite') l1 = (
      <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} off
        sub={b === 'tarde_da_noite' ? `Já é tarde para tocar o celular de ${P}. Pergunte a partir das 6h ou chame no WhatsApp.` : 'Falta menos de 1 hora: melhor chamar no WhatsApp.'}>
        <BotaoWhatsSM onClick={() => whats(b === 'tarde_da_noite' ? 'noite' : 'perto')} />
      </LinhaOpcaoSM>
    );
    else l1 = <LinhaOpcaoSM ic="help-circle" titulo={tituloPerg} sub={textoErroPresencaProfSM(b, nomeAluno)} off />;
  }
  const subNaoVou = alunoUsaApp === false
    ? (mensal ? `Depois você avisa ${P} pelo WhatsApp.` : `A aula não sai do pacote. Depois você avisa ${P} pelo WhatsApp.`)
    : (mensal ? `${P} recebe o aviso na hora.` : `${P} recebe o aviso na hora e a aula não sai do pacote.`);
  return (
    <div style={{ marginTop: '10px', background: '#F7F9FF', border: '1px solid #C2D4FF', borderRadius: '14px', padding: '12px' }}>
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#16181D', marginBottom: '8px' }}>O que fazer com esta aula?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {l1}
        {!avisoVigenteAula(aula) && (
          <LinhaOpcaoSM ic="message-off" cor="#92400E" titulo={`${P} avisou que não vem`} onClick={() => setSub('aviso')}
            sub="Avisou por WhatsApp ou ligação? Registre aqui — o Plen confere o prazo." />
        )}
        <LinhaOpcaoSM ic="calendar-x" cor="#B91C1C" titulo="Não vou poder dar esta aula" onClick={() => setSub('naovou')} sub={subNaoVou} />
      </div>
      <button type="button" onClick={onFechar}
        style={{ marginTop: '8px', width: '100%', minHeight: '44px', borderRadius: '10px', border: '1.5px solid #E2E4E9', background: 'white', color: '#6E7480', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
        Fechar
      </button>
    </div>
  );
}

// "Nao vou poder dar esta aula": o motivo, a previa do banco (o que acontece com a aula, em reais e
// data) e o aviso ao aluno — tudo por professor_cancelar_aula (regra unica). Chuva/quadra fechada
// segue a regra de imprevisto do acordo. Aluno sem app: depois de gravar, o texto pronto do WhatsApp.
const CHIPS_NAO_VOU_SM = [
  { k: 'doente', t: 'Estou doente', ic: 'mood-sick', categoria: 'professor', motivo: 'doente' },
  { k: 'agenda', t: 'Compromisso', ic: 'calendar-event', categoria: 'professor', motivo: 'agenda' },
  { k: 'clima', t: 'Chuva / quadra fechada', ic: 'cloud-rain', categoria: 'forca_maior', motivo: 'clima' },
  { k: 'outro', t: 'Outro motivo', ic: 'dots', categoria: 'professor', motivo: 'outro' },
];
function PainelNaoVouDarSM({ aula, nomeAluno, alunoUsaApp, telefone, nomeProf, origem = 'outro', onFeito, onFechar }) {
  const P = primeiroNomeSM(nomeAluno, 'o aluno');
  const [esc, setEsc] = React.useState(null);
  const [previa, setPrevia] = React.useState(null);     // { carregando, txt, dados, erro }
  const [salvando, setSalvando] = React.useState(false);
  const [feito, setFeito] = React.useState(null);       // resposta do banco (aluno sem app: falta o WhatsApp)
  const req = React.useRef(0);
  React.useEffect(() => { uso.janela('aula_nao_vou_dar'); }, []);
  async function escolher(c) {
    if (salvando) return;
    setEsc(c);
    const n = ++req.current;
    setPrevia({ carregando: true });
    let pv = null;
    try { pv = await previaCancelarAulaProf(aula.id, c.categoria, c.motivo); } catch { pv = { erro: 'rede', txt: null }; }
    if (n !== req.current) return;
    setPrevia({ carregando: false, txt: pv && pv.txt, dados: (pv && pv.dados) || null, erro: (pv && pv.erro) || null });
  }
  async function confirmar() {
    if (!esc || salvando) return;
    setSalvando(true);
    const r = await cancelarAulaProf(aula, esc.categoria, esc.motivo, { origem: 'opcoes', nomeAluno, alunoSemApp: alunoUsaApp === false });
    setSalvando(false);
    if (!r) return;
    if (alunoUsaApp === false) { setFeito(r); return; }
    onFeito && onFeito(r);
  }
  const caixa = { marginTop: '10px', background: '#F7F9FF', border: '1px solid #C2D4FF', borderRadius: '14px', padding: '12px' };
  const btSec = { flex: 1, minHeight: '44px', borderRadius: '10px', border: '1.5px solid #E2E4E9', background: 'white', color: '#6E7480', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  if (feito) return (
    <div style={caixa}>
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#16181D' }}>Aula desmarcada.</div>
      <div style={{ fontSize: '12.5px', color: '#6E7480', marginTop: '3px', lineHeight: 1.5 }}>{P} ainda não usa o app — avise pelo WhatsApp. O texto já vai pronto.</div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        <BotaoWhatsSM rotulo={`Avisar ${P} no WhatsApp`}
          onClick={() => compartilharOuWhats(waLinkSM(telefone, textoWhatsNaoVouSM(aula, nomeAluno, nomeProf, esc && esc.motivo, feito.beneficio === 'fila')), 'nao_vou_dar')} />
        <button type="button" onClick={() => onFeito && onFeito(feito)} style={{ ...btSec, flex: '1 1 90px' }}>Pronto</button>
      </div>
    </div>
  );
  const d = previa && previa.dados;
  const recusa = previa && !previa.carregando && previa.erro && previa.txt;   // o banco recusou (texto proprio)
  const podeConfirmar = !!esc && !(previa && previa.carregando) && !recusa && !salvando;
  return (
    <div style={caixa}>
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#16181D' }}>Por que você não vai poder?</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
        {CHIPS_NAO_VOU_SM.map(c => {
          const on = esc && esc.k === c.k;
          return (
            <button key={c.k} type="button" onClick={() => escolher(c)}
              style={{ border: on ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: on ? '#E7EDFF' : 'white', color: on ? '#1C43AE' : '#3D424C',
                       borderRadius: '20px', padding: '7px 12px', minHeight: '44px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                       display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Ico n={c.ic} s={14} /> {c.t}
            </button>
          );
        })}
      </div>
      {previa && (
        <div style={{ marginTop: '10px', background: 'white', borderRadius: '10px', padding: '10px 12px', border: '1px solid #E2E4E9', fontSize: '13px', lineHeight: 1.5,
                      color: recusa ? '#92400E' : '#3D424C' }}>
          {previa.carregando ? <span style={{ color: '#6E7480' }}>Conferindo a regra do acordo…</span>
            : recusa ? previa.txt
            : (
              <>
                {d && d.virou_professor ? <>{`${primeiroNomeSM(nomeAluno)} disse que você não veio: conta como desmarcação sua — a aula volta para o pacote, sem limite.`}{' '}</> : null}
                {previa.txt || 'Não consegui conferir agora o que acontece com a aula — o Plen confere de novo quando você confirmar.'}
                {alunoUsaApp !== false ? ` ${primeiroNomeSM(nomeAluno)} recebe o aviso agora.` : ''}
              </>
            )}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
        <button type="button" onClick={onFechar} disabled={salvando} style={btSec}>Voltar</button>
        <button type="button" onClick={confirmar} disabled={!podeConfirmar}
          style={{ flex: 2, minHeight: '44px', borderRadius: '10px', border: 'none', background: podeConfirmar ? '#16181D' : '#E2E4E9', color: podeConfirmar ? 'white' : '#9AA0AB',
                   fontSize: '13px', fontWeight: 800, cursor: podeConfirmar ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
          {salvando ? 'Desmarcando…' : alunoUsaApp === false ? 'Desmarcar' : `Desmarcar e avisar ${primeiroNomeSM(nomeAluno)}`}
        </button>
      </div>
    </div>
  );
}

// v140: colunas novas de aulas nas consultas do Inicio. O SQL vai ao ar antes do app; se faltar
// (VOLTA do SQL), a consulta cai na lista antiga em vez de deixar o Inicio vazio.
const COLS_PRESENCA_SM = 'presenca_pedida_em, presenca_vou_em, aluno_motivo_nao';
const ehColunaFaltandoSM = e => !!e && (e.code === '42703' || e.code === 'PGRST204' || /column .* does not exist|could not find the .* column/i.test(String(e.message || '')));
// v148: o 2o parametro de montar = a coluna da regra da chuva no acordo (', chuva_livre'). Banco sem a v148 (ou
// com a VOLTA): pede de novo sem ela, mantendo as da v140.
const COLS_CHUVA_SM = ', chuva_livre';
async function consultaComColunasSM(montar) {
  let r = await montar(', ' + COLS_PRESENCA_SM, COLS_CHUVA_SM);
  if (r && r.error && ehColunaFaltandoSM(r.error) && /chuva_livre/.test(String(r.error.message || ''))) r = await montar(', ' + COLS_PRESENCA_SM, '');
  if (r && r.error && ehColunaFaltandoSM(r.error)) {
    uso.erro('erro.v140_colunas', r.error.message || 'coluna');
    r = await montar('');
  }
  return r;
}

function Dashboard({ professor, onNavigate, params }) {
  const [stats, setStats] = React.useState({ aulasSemanais: 0, aulasRealizadas: 0, aulasCanceladas: 0, receita: 0, receitaPendente: 0, alunosAtivos: 0, expirandoEmBreve: 0, taxaPresenca: 0 });
  const [chartData, setChartData] = React.useState([]);
  const [modoFat, setModoFat] = React.useState('bruto');
  const [aulasPendentes, setAulasPendentes] = React.useState([]);
  const [naoHouveId, setNaoHouveId] = React.useState(null);   // pendencia expandida no "por que nao houve?"
  // Confirmar antes de mexer no saldo. Mudar o estado de uma aula mexe no credito do
  // aluno — e portanto no dinheiro. A confirmacao existe para dizer a consequencia ANTES,
  // em aula e em reais, nao para proteger de toque errado. { id, motivo|null }
  const [confirmando, setConfirmando] = React.useState(null);
  const [promo, setPromo] = React.useState(null);              // { promocao, progresso, ganhas, total_ganho }
  const [aulasHoje, setAulasHoje] = React.useState([]);
  const [aulasAmanha, setAulasAmanha] = React.useState([]);     // v131: bloco "Amanhã"
  const [avisoAbertoId, setAvisoAbertoId] = React.useState(null); // v131: PainelAvisoAluno aberto nesta aula
  const [previaConf, setPreviaConf] = React.useState(null);     // v131: { id, motivo, carregando, txt } — previa do "Não houve"
  const [sessoesTurma, setSessoesTurma] = React.useState([]);   // aulas em grupo de hoje/atrasadas
  const [presencaTurma, setPresencaTurma] = React.useState(null);
  const [hojeNaoHouveId, setHojeNaoHouveId] = React.useState(null);  // aula de hoje com "Não houve" aberto
  // v134: "Nao houve" de Pendencias (params.aula_id + nao_houve): abre os motivos DESTA aula aqui e
  // rola ate' ela — antes so' trocava de tela e o professor tinha de achar a aula e tocar de novo.
  // Dias anteriores: lista "para fechar" (vai para o topo, mesmo se fosse a 7a); hoje: lista "Hoje".
  // Uma vez por toque (params.t).
  const alvoNaoHouve = params?.nao_houve && params?.aula_id ? params.aula_id + ':' + (params.t || '') : null;
  const alvoNHFeito = React.useRef(null);
  React.useEffect(() => {
    if (!alvoNaoHouve || alvoNHFeito.current === alvoNaoHouve) return;
    const id = params.aula_id;
    const naLista = aulasPendentes.some(a => a.id === id), deHoje = aulasHoje.some(a => a.id === id);
    if (!naLista && !deHoje) return;
    alvoNHFeito.current = alvoNaoHouve;
    if (naLista) {
      setAulasPendentes(prev => { const a = prev.find(x => x.id === id); return a ? [a, ...prev.filter(x => x.id !== id)] : prev; });
      setConfirmando(null); setNaoHouveId(id);
    } else setHojeNaoHouveId(id);
    setTimeout(() => { try { const el = document.getElementById('aula-' + id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {} }, 120);
  }, [alvoNaoHouve, aulasPendentes, aulasHoje]);
  // v140: tocou no push de uma aula ("Seu aluno está esperando", "Aluno disse que você não veio",
  // "Sua próxima aula"...): o ProfessorApp manda params.abrir_aula. Depois que as listas chegam, rola
  // ate' a aula; se o aluno marcou "nao veio", ja' abre o "Nao houve" (com "Eu nao fui / desmarquei"
  // primeiro). Aula que nao esta' no Inicio (outro dia, ja' fechada): vai para a tela Aulas, no dia dela.
  const [carregouDash, setCarregouDash] = React.useState(0);
  const alvoAbrir = params?.abrir_aula && params?.aula_id ? params.aula_id + ':' + (params.t || '') : null;
  const alvoAbrirFeito = React.useRef(null);
  React.useEffect(() => {
    if (!alvoAbrir || !carregouDash || alvoAbrirFeito.current === alvoAbrir) return;
    alvoAbrirFeito.current = alvoAbrir;
    const id = params.aula_id;
    const pend = aulasPendentes.find(a => a.id === id), deHoje = aulasHoje.find(a => a.id === id), deAmanha = aulasAmanha.find(a => a.id === id);
    const a = pend || deHoje || deAmanha;
    uso.acao('inicio.aula_push', { achou: !!a, nao_veio: !!(a && naoVeioSM(a)) });
    if (!a) { onNavigate && onNavigate('aulas', { aula_id: id, t: Date.now() }); return; }
    if (naoVeioSM(a) && !a.divergencia_em) {
      if (pend) { setAulasPendentes(prev => [pend, ...prev.filter(x => x.id !== id)]); setConfirmando(null); setNaoHouveId(id); }
      else if (deHoje) setHojeNaoHouveId(id);
    }
    setTimeout(() => { try { const el = document.getElementById('aula-' + id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {} }, 150);
  }, [alvoAbrir, carregouDash]);
  const [atualizandoId, setAtualizandoId] = React.useState(null);
  const hora = new Date().getHours();
  const saudacao = hora < 12 ? 'Bom dia' : hora < 18 ? 'Boa tarde' : 'Boa noite';
  // So' a primeira letra maiuscula. O `capitalize` do CSS escrevia "Quarta-Feira, 2 De Setembro".
  const hojeLabel = (d => d.charAt(0).toUpperCase() + d.slice(1))(
    new Date().toLocaleDateString('pt-BR', { weekday:'long', day:'numeric', month:'long' }));

  const [recargaDash, setRecargaDash] = React.useState(0); // incrementa para recarregar o painel
  useAoDesfazerSM(() => setRecargaDash(n => n + 1));   // v142: desfez (faixa ou item) — o painel relê
  const hojeStrRender = new Date().toLocaleDateString('sv-SE');
  React.useEffect(() => {
    async function load() {
      const now = new Date();
      const pad2 = n => String(n).padStart(2,'0');
      const hojeStr = `${now.getFullYear()}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())}`;
      const amanhaD = new Date(now); amanhaD.setDate(now.getDate() + 1);
      const amanhaStr = `${amanhaD.getFullYear()}-${pad2(amanhaD.getMonth()+1)}-${pad2(amanhaD.getDate())}`;
      const em7dias = new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0];
      const inicioMes = `${now.getFullYear()}-${pad2(now.getMonth()+1)}-01`;

      const dow = now.getDay();
      const diffSeg = (dow === 0 ? -6 : 1 - dow);
      const seg = new Date(now); seg.setDate(now.getDate() + diffSeg); seg.setHours(0,0,0,0);
      const dom = new Date(seg); dom.setDate(seg.getDate() + 6);
      const inicioSemana = `${seg.getFullYear()}-${pad2(seg.getMonth()+1)}-${pad2(seg.getDate())}`;
      const fimSemana    = `${dom.getFullYear()}-${pad2(dom.getMonth()+1)}-${pad2(dom.getDate())}`;
      const seisAtras = new Date(); seisAtras.setMonth(seisAtras.getMonth() - 5); seisAtras.setDate(1);
      const seisAtrasStr = `${seisAtras.getFullYear()}-${pad2(seisAtras.getMonth()+1)}-01`;

      const [
        { data: aulasSemanais },
        { data: aulasMes },
        { data: pagsPagos },
        { data: pagsPendentes },
        { data: contratos },
        { data: alunosList },
        { data: pendentesData },
        { data: aulasHojeData },
        { data: aulasHistorico },
        { data: pagsMensais },
        { data: aulasAmanhaData },
      ] = await Promise.all([
        supabase.from('aulas').select('id, status').eq('professor_id', professor.id).gte('data', inicioSemana).lte('data', fimSemana),
        supabase.from('aulas').select('id, status').eq('professor_id', professor.id).gte('data', inicioMes),
        // v155: pago sem data (renovou com "Ja' pagou" e deixou a data em branco) conta pelo mes de referencia
        supabase.from('pagamentos').select('valor').eq('professor_id', professor.id).eq('status', 'pago')
          .or(`data_pagamento.gte.${inicioMes},and(data_pagamento.is.null,referencia_mes.eq.${String(inicioMes).slice(0, 7)})`),
        // v155: + vencimento e status do contrato (a cobranca do proximo pacote que nao venceu e' "a vencer", nao "em aberto")
        supabase.from('pagamentos').select('valor, data_vencimento, declarado_pago_em, contratos(status)').eq('professor_id', professor.id).eq('status', 'pendente'),
        // v155: + o 'proximo' (quem ja' combinou o proximo pacote nao conta como expirando)
        supabase.from('contratos').select('aluno_id, data_fim, status').eq('professor_id', professor.id).in('status', ['ativo', 'proximo']),
        // v140: + telefone (WhatsApp com a pergunta pronta, aluno sem app ou sem resposta)
        supabase.from('alunos').select('id, nome, modalidade, user_id, telefone').eq('professor_id', professor.id),
        // v131: + status (a presenca da turma precisa) e os campos do aviso do aluno (Etapas 8/9)
        // v140: + pergunta "vai?" e o motivo do "nao aconteceu" (consultaComColunasSM)
        consultaComColunasSM((extra, chuva = '') => supabase.from('aulas').select('id, status, aluno_id, contrato_id, turma_id, sessao_id, data, hora, confirmada_aluno_em, falta_declarada_aluno_em, desfecho_prof, divergencia_em, motivo_cancelamento, cancelado_por, observacao, aviso_aluno_em, aviso_no_prazo, aviso_prazo_ate, aviso_canal, aviso_decisao, desfeita_em' + extra + ', duracao:contratos(id, duracao_min, tipo_cobranca, turma_id, preco_negociado, total_aulas, valor_mensal, aulas_por_semana, reposicao_permitida, reposicao_validade_dias, forca_maior_gera, forca_maior_teto_mes' + chuva + ')')
          .eq('professor_id', professor.id).in('status', ['agendada','aguardando_confirmacao']).lt('data', hojeStr)
          .order('data').order('hora').limit(40)),
        // A DATA faltava aqui: sem ela a conta de "esta' acontecendo agora?" falhava, e a
        // aula do horario aparecia como "Confirmada", sem botao — foi o que o Cadu viu
        // durante a aula em 24/09.
        consultaComColunasSM(extra => supabase.from('aulas').select('id, status, data, hora, aluno_id, contrato_id, turma_id, sessao_id, confirmada_aluno_em, falta_declarada_aluno_em, desfecho_prof, divergencia_em, motivo_cancelamento, aviso_aluno_em, aviso_no_prazo, aviso_prazo_ate, aviso_canal, aviso_decisao, desfeita_em' + extra + ', duracao:contratos(duracao_min, tipo_cobranca, turma_id, reposicao_permitida)')
          .eq('professor_id', professor.id).eq('data', hojeStr).order('hora')),
        supabase.from('aulas').select('data, status').eq('professor_id', professor.id).gte('data', seisAtrasStr),
        supabase.from('pagamentos').select('valor, referencia_mes, status, contratos(taxa_academia)').eq('professor_id', professor.id).eq('status', 'pago').gte('referencia_mes', seisAtrasStr.slice(0,7)),
        // v131 (Etapa 9): o dia seguinte — quem avisou que nao vem, e o "Aluno avisou" pelo WhatsApp
        // v140: + o que o aluno respondeu (a pergunta "vai?" e o "Vou")
        consultaComColunasSM(extra => supabase.from('aulas').select('id, status, data, hora, aluno_id, contrato_id, turma_id, sessao_id, confirmada_aluno_em, falta_declarada_aluno_em, desfecho_prof, divergencia_em, motivo_cancelamento, aviso_aluno_em, aviso_no_prazo, aviso_prazo_ate, aviso_canal, aviso_decisao, desfeita_em' + extra + ', duracao:contratos(duracao_min, tipo_cobranca, turma_id, reposicao_permitida)')
          .eq('professor_id', professor.id).eq('data', amanhaStr).order('hora')),
      ]);

      const aulasSemanaisCount = (aulasSemanais || []).filter(a => a.status === 'agendada').length;
      const realizadasSemana = (aulasSemanais || []).filter(a => a.status === 'realizada').length;
      const canceladasSemana = (aulasSemanais || []).filter(a => ['cancelada_professor','cancelada_aluno','cancelada_forca_maior','falta'].includes(a.status)).length;
      const totalEncerradas = realizadasSemana + canceladasSemana;
      const taxaPresenca = totalEncerradas > 0 ? Math.round((realizadasSemana / totalEncerradas) * 100) : 0;
      const receita = (pagsPagos || []).reduce((s, p) => s + Number(p.valor), 0);
      // v155: mesma regra do Financeiro (naoVenceuAinda)
      const aVencerProx = p => p.contratos?.status === 'proximo' && !p.declarado_pago_em && !!p.data_vencimento && String(p.data_vencimento).slice(0, 10) > hojeStr;
      const receitaPendente = (pagsPendentes || []).filter(p => !aVencerProx(p)).reduce((s, p) => s + Number(p.valor), 0);
      const comProximo = new Set((contratos || []).filter(c => c.status === 'proximo').map(c => c.aluno_id));
      const ativos = (contratos || []).filter(c => c.status === 'ativo');
      const expirandoEmBreve = ativos.filter(c => c.data_fim >= hojeStr && c.data_fim <= em7dias && !comProximo.has(c.aluno_id)).length;
      const alunosAtivos = ativos.length;

      setStats({ aulasSemanais: aulasSemanaisCount, aulasRealizadas: realizadasSemana, aulasCanceladas: canceladasSemana, receita, receitaPendente, alunosAtivos, expirandoEmBreve, taxaPresenca });

      // Pendencia = aula de DIAS ANTERIORES que ainda precisa do professor. As de hoje ficam
      // na lista "Hoje", logo abaixo, com os botoes na propria linha — antes a mesma aula
      // aparecia nos dois lugares. Aula que o professor ja' registrou e so' espera o aluno
      // confirmar nao e' pendencia dele; divergencia e', porque ele precisa decidir.
      // Aula de turma: uma sessao por linha (com a lista de presenca), fora da lista individual.
      const nomeDe = id => (alunosList || []).find(x => x.id === id)?.nome || 'Aluno';
      // v131: a sessao leva tambem quem cancelou (aparece riscado na presenca), mas so' entra a
      // sessao que ainda tem alguem para fechar
      // 02/10: aula de turma que o professor JA' fechou (desfecho_prof) espera so' o robo do consenso
      // (24h) — nao e' mais pendencia dele, salvo divergencia. Antes a sessao continuava vermelha e
      // reabria com as bolinhas logo depois do "Aula da turma fechada" (achado no teste da demo).
      const abertaTurma = a => ['agendada','aguardando_confirmacao','em_andamento'].includes(a.status || 'agendada')
        && (!a.desfecho_prof || !!a.divergencia_em);
      const deTurma = [...(pendentesData || []), ...semRemarcada(aulasHojeData)].filter(a => a.sessao_id);
      if (deTurma.some(abertaTurma)) {
        const ids = [...new Set(deTurma.map(a => a.turma_id).filter(Boolean))];
        const { data: ts } = await supabase.from('turmas').select('id, nome').in('id', ids);
        const porId = {}; (ts || []).forEach(t => porId[t.id] = t);
        setSessoesTurma(agruparSessoes(deTurma.map(a => ({ ...a, nome: nomeDe(a.aluno_id) })), porId).filter(sx => sx.aulas.some(abertaTurma)));
      } else setSessoesTurma([]);

      // v131: aula lancada pelo aluno (aguardando_confirmacao) fica no bloco "aulas combinadas"
      const pendentes = (pendentesData || []).filter(a => !a.sessao_id && a.status !== 'aguardando_confirmacao').filter(a => !(a.desfecho_prof && !a.divergencia_em)).map(a => ({
        ...a,
        nome: nomeDe(a.aluno_id),
        modalidade: (alunosList || []).find(x => x.id === a.aluno_id)?.modalidade || '',   // v148: a regra da chuva
        duracao_min: a.duracao?.duracao_min,
        alunoUsaApp: !!(alunosList || []).find(x => x.id === a.aluno_id)?.user_id,
        telefone: (alunosList || []).find(x => x.id === a.aluno_id)?.telefone || '',
      }));
      setAulasPendentes(pendentes);

      // v131: a original de uma aula remarcada (mensal/turma) nao aparece — a nova e' que vale
      const hojeEnriquecidas = semRemarcada(aulasHojeData).filter(a => !a.sessao_id).map(a => ({
        ...a,
        duracao_min: a.duracao?.duracao_min,
        nomeAluno: (alunosList || []).find(x => x.id === a.aluno_id)?.nome || 'Aluno',
        modalidade: (alunosList || []).find(x => x.id === a.aluno_id)?.modalidade || '',
        // v133: aluno sem conta no app nao tem como confirmar — a tela nao pode dizer "aguardando"
        alunoUsaApp: !!(alunosList || []).find(x => x.id === a.aluno_id)?.user_id,
        telefone: (alunosList || []).find(x => x.id === a.aluno_id)?.telefone || '',
      }));
      setAulasHoje(hojeEnriquecidas);
      setAulasAmanha(semRemarcada(aulasAmanhaData).filter(a => !a.sessao_id && a.status !== 'aguardando_confirmacao').map(a => ({
        ...a,
        duracao_min: a.duracao?.duracao_min,
        nomeAluno: (alunosList || []).find(x => x.id === a.aluno_id)?.nome || 'Aluno',
        alunoUsaApp: !!(alunosList || []).find(x => x.id === a.aluno_id)?.user_id,
        telefone: (alunosList || []).find(x => x.id === a.aluno_id)?.telefone || '',
      })));
      setCarregouDash(n => n + 1);   // v140: o toque no push (abrir a aula) espera a lista chegar

      const nomesMes = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
      const custosFixosProf = await carregarCustosMensais(professor.id);
      const meses = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(); d.setMonth(d.getMonth() - i); d.setDate(1);
        const key = `${d.getFullYear()}-${pad2(d.getMonth()+1)}`;
        const realizadas = (aulasHistorico || []).filter(a => a.data?.startsWith(key) && a.status === 'realizada').length;
        const canceladas = (aulasHistorico || []).filter(a => a.data?.startsWith(key) && ['cancelada_professor','cancelada_aluno','cancelada_forca_maior','falta'].includes(a.status)).length;

        const pagsMes = (pagsMensais || []).filter(p => p.referencia_mes === key);
        const bruto = pagsMes.reduce((s, p) => s + Number(p.valor || 0), 0);

        const taxaTotal = pagsMes.reduce((s, p) => s + Number(p.contratos?.taxa_academia || 0), 0);
        const liquido = Math.max(0, bruto - taxaTotal * realizadas - custosMensaisDoMes(custosFixosProf, key));
        meses.push({ mes: nomesMes[d.getMonth()], key, realizadas, canceladas, bruto, liquido });
      }
      setChartData(meses);

      // Promocao vigente + progresso deste professor. Se nao houver promocao ativa,
      // o cartao simplesmente nao aparece — sem espaco vazio.
      const { data: promos } = await supabase.from('promocoes').select('*')
        .eq('ativa', true).lte('vigencia_inicio', hojeStr)
        .or(`vigencia_fim.is.null,vigencia_fim.gte.${hojeStr}`)
        .order('vigencia_inicio', { ascending: false }).limit(3);
      const vigente = (promos || []).find(p => !p.professores || p.professores.includes(professor.id));
      if (vigente) {
        const [{ data: prog }, { data: recs }] = await Promise.all([
          supabase.from('promocao_progresso').select('aulas_contadas, valor_contado')
            .eq('promocao_id', vigente.id).eq('professor_id', professor.id).maybeSingle(),
          supabase.from('recompensas').select('valor, status, ganha_em')
            .eq('professor_id', professor.id).neq('status', 'cancelada'),
        ]);
        setPromo({
          promocao: vigente,
          contadas: prog?.aulas_contadas || 0,
          valorContado: Number(prog?.valor_contado || 0),
          ganhas: (recs || []).length,
          totalGanho: (recs || []).reduce((s, r) => s + Number(r.valor || 0), 0),
          aPagar: (recs || []).filter(r => r.status === 'ganha').reduce((s, r) => s + Number(r.valor || 0), 0),
        });
      } else {
        setPromo(null);
      }
    }
    load();
  }, [professor.id, recargaDash]);

  const statusAulaCor = { agendada: { bg:'#E7EDFF', color:'#2F6BFF', label:'Agendada' }, aguardando_confirmacao: { bg:'#F0F1F4', color:'#6E7480', label:'A confirmar' }, realizada: { bg:'#DCF5EC', color:'#0A6F4D', label:'Realizada' }, cancelada_professor: { bg:'#FEE2E2', color:'#991B1B', label:'Cancelada' }, cancelada_aluno: { bg:'#FEF3C7', color:'#92400E', label:'Canc. aluno' }, cancelada_forca_maior: { bg:'#F0F1F4', color:'#6E7480', label:'Imprevisto' }, falta: { bg:'#F0F1F4', color:'#3D424C', label:'Falta' } };

  // Reflete na tela o que o banco decidiu (consenso): fechou, espera o aluno ou divergiu.
  const aplicarLocal = (a, novoStatus, estado) => {
    if (estado === 'aguardando') return { ...a, desfecho_prof: novoStatus, divergencia_em: null };
    if (estado === 'divergencia') return { ...a, desfecho_prof: novoStatus, divergencia_em: new Date().toISOString() };
    // desfazer (estado 'agendada'): o banco tambem zera a decisao do aviso ("Conta a aula") — o
    // aviso fora do prazo volta a esperar [Conta a aula]/[Devolver]
    if (estado === 'agendada') return { ...a, status: 'agendada', desfecho_prof: null, divergencia_em: null, aviso_decisao: null };
    return { ...a, status: estado || novoStatus, desfecho_prof: null, divergencia_em: null };
  };

  async function atualizarStatusHoje(aulaId, novoStatus) {
    const aula = aulasHoje.find(a => a.id === aulaId);
    if (!aula) return;
    setAtualizandoId(aulaId);
    const estado = await aplicarDesfechoProfessor(aula, novoStatus, { forcar: !!aula.divergencia_em, nomeAluno: aula.nomeAluno, origem: 'inicio_hoje', alunoSemApp: aula.alunoUsaApp === false });
    if (estado) setAulasHoje(prev => prev.map(a => a.id === aulaId ? aplicarLocal(a, novoStatus, estado) : a));
    setHojeNaoHouveId(null);
    setAtualizandoId(null);
  }

  // 1 toque na faixa vermelha: registra e tira da lista (o aluno confirma do lado dele).
  async function resolverPendencia(ap, novoStatus) {
    setAtualizandoId(ap.id);
    const estado = await aplicarDesfechoProfessor(ap, novoStatus, { forcar: !!ap.divergencia_em, nomeAluno: ap.nome, origem: 'inicio_pendente', alunoSemApp: ap.alunoUsaApp === false });
    if (estado) {
      setAulasPendentes(prev => prev.filter(x => x.id !== ap.id));
      setAulasHoje(prev => prev.map(a => a.id === ap.id ? aplicarLocal(a, novoStatus, estado) : a));
    }
    setAtualizandoId(null);
  }

  // "Nao houve" com motivo. Falta que consome passa pelo consenso (como a aula dada). O resto
  // (aluno avisou no prazo, eu desmarquei, chuva/imprevisto) e' professor_cancelar_aula (v131): o
  // BANCO decide pelo acordo — fim do pacote, reposicao, desconto ou nada —, conta o limite de
  // imprevistos do mes e avisa o aluno. O app nao conta mais o limite nem grava reposicao.
  async function resolverNaoHouve(ap, motivo) {
    setAtualizandoId(ap.id);
    if (motivo === 'aluno_fora_prazo') {
      const estado = await aplicarDesfechoProfessor(ap, 'falta', { forcar: !!ap.divergencia_em, nomeAluno: ap.nome, origem: 'inicio_nao_houve' });
      if (estado) {
        setAulasPendentes(prev => prev.filter(x => x.id !== ap.id));
        setAulasHoje(prev => prev.map(a => a.id === ap.id ? aplicarLocal(a, 'falta', estado) : a));
      }
      setNaoHouveId(null); setAtualizandoId(null);
      return;
    }
    // motivo da aula: o que o aluno disse (se disse), senao chuva para imprevisto e "outro"
    const motivoAula = motivo === 'forca_maior' ? 'clima'
      : (motivo === 'aluno_no_prazo' && MOTIVOS_AULA_SM.includes(ap.motivo_cancelamento) ? ap.motivo_cancelamento : 'outro');
    const r = await cancelarAulaProf(ap, motivo, motivoAula, { origem: 'inicio_nao_houve', nomeAluno: ap.nome });
    if (r) {
      setAulasPendentes(prev => prev.filter(x => x.id !== ap.id));
      setAulasHoje(prev => prev.map(a => a.id === ap.id ? { ...a, status: r.status, desfecho_prof: null, divergencia_em: null } : a));
      if (r.beneficio === 'reposicao') carregarReposicoes();
    }
    setNaoHouveId(null);
    setAtualizandoId(null);
  }
  // Previa do "Não houve" (o texto do cartao vem do banco, com a data do fim do pacote e o valor)
  async function abrirConfirmacao(ap, motivo) {
    setConfirmando({ id: ap.id, motivo }); setNaoHouveId(null);
    if (!motivo || motivo === 'aluno_fora_prazo') { setPreviaConf(null); return; }
    const motivoAula = motivo === 'forca_maior' ? 'clima'
      : (motivo === 'aluno_no_prazo' && MOTIVOS_AULA_SM.includes(ap.motivo_cancelamento) ? ap.motivo_cancelamento : 'outro');
    setPreviaConf({ id: ap.id, motivo, carregando: true, txt: null });
    const pv = await previaCancelarAulaProf(ap.id, motivo, motivoAula);
    setPreviaConf(p => (p && p.id === ap.id && p.motivo === motivo) ? { id: ap.id, motivo, carregando: false, txt: pv.txt } : p);
  }

  // Reposicoes a combinar — o professor ve' o que deve e marca a data quando fechar no WhatsApp.
  const [reposicoes, setReposicoes] = React.useState([]);
  const [marcandoId, setMarcandoId] = React.useState(null);
  const [marcar, setMarcar] = React.useState({ data: '', hora: '' });
  async function carregarReposicoes() {
    const { data } = await supabase.from('reposicoes')
      .select('id, aluno_id, contrato_id, motivo, validade, status, alunos(nome, telefone)')
      .eq('professor_id', professor.id).eq('status', 'devida').order('validade');
    setReposicoes(data || []);
  }
  React.useEffect(() => { carregarReposicoes(); }, [professor.id]);

  // -------------------------------------------------------------------------
  // Pedidos dos alunos. O aluno pede pelo app; o professor decide aqui, num toque.
  // O WhatsApp continua sendo o carteiro (a mensagem leva um link pra ca'), mas
  // quem manda no calendario e' o app — texto livre no WhatsApp nao move aula.
  // -------------------------------------------------------------------------
  const [pedidos, setPedidos] = React.useState([]);
  const [respondendoId, setRespondendoId] = React.useState(null);

  // -------------------------------------------------------------------------
  // Feriado se aproximando. Academia nao "cancela" aula em feriado — o dia nao existe
  // na grade. Mas personal trainer da' aula em feriado, entao a decisao e' do
  // professor, e vem no momento certo: 14 dias antes do primeiro feriado que cruza a
  // agenda dele. Um toque resolve todas as aulas do dia e vira o padrao dele.
  // -------------------------------------------------------------------------
  const [feriadoAlerta, setFeriadoAlerta] = React.useState(null);   // { data, nome, aulas:[{id,nome,hora}] }
  const [resolvendoFeriado, setResolvendoFeriado] = React.useState(false);

  async function carregarFeriado() {
    const pad = n => String(n).padStart(2, '0');
    const hoje = new Date();
    const ate = new Date(); ate.setDate(hoje.getDate() + 14);
    const iso = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    const { data: fs } = await supabase.from('feriados').select('data, nome, abrangencia, cidade, professor_id')
      .gte('data', iso(hoje)).lte('data', iso(ate)).order('data');
    const valem = (fs || []).filter(f => f.abrangencia === 'nacional' || f.professor_id === professor.id
      || (f.cidade && professor.cidade && f.cidade === professor.cidade));
    for (const f of valem) {
      // ja' respondeu "vou dar aula" para este feriado: nao pergunta de novo
      try { if (localStorage.getItem(`feriadoOk:${professor.id}:${f.data}`)) continue; } catch {}
      const { data: aus } = await supabase.from('aulas').select('id, hora, aluno_id, alunos(nome), contratos(tipo_cobranca)')
        .eq('professor_id', professor.id).eq('data', f.data).eq('status', 'agendada').order('hora');
      if (aus && aus.length) {
        setFeriadoAlerta({ data: f.data, nome: f.nome, aulas: aus.map(a => ({ id: a.id, hora: a.hora, aluno_id: a.aluno_id, nome: a.alunos?.nome || 'Aluno', mensal: a.contratos?.tipo_cobranca === 'mensal' })) });
        return;
      }
    }
    setFeriadoAlerta(null);
  }
  React.useEffect(() => { carregarFeriado(); }, [professor.id]);

  // A decisao e' deste feriado, nao um padrao para sempre (Marcus, 25/09): o professor
  // escolhe caso a caso, e o aluno fica sabendo nos dois casos.
  async function resolverFeriado(pular) {
    if (!feriadoAlerta) return;
    uso.acao('feriado.resolver', { pular: !!pular, n: feriadoAlerta.aulas.length });
    setResolvendoFeriado(true);
    if (pular) {
      let movidas = 0;
      const resultadoPorAluno = {};
      for (const a of feriadoAlerta.aulas) {
        const { data } = await supabase.rpc('empurrar_aula_feriado', { p_aula_id: a.id });
        if (data && data.ok) { movidas++; if (a.aluno_id && !resultadoPorAluno[a.aluno_id]) resultadoPorAluno[a.aluno_id] = data; }
      }
      const quando = new Date(feriadoAlerta.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
      const nomeProf = professor?.nome?.split(' ')[0] || 'Seu professor';
      // Cada aluno fica sabendo o que DE FATO aconteceu com a aula dele (02/10): antes o texto dizia
      // "foi para o fim do pacote" tambem para quem paga mensalidade, onde a aula so' nao acontece.
      const consequencia = r => {
        if (r?.empurrada) return r.nova_data
          ? `A sua aula foi para o fim do pacote, em ${new Date(r.nova_data + 'T12:00:00').toLocaleDateString('pt-BR')} — você não perde nenhuma.`
          : 'A sua aula foi para o fim do pacote — você não perde nenhuma.';
        if (r?.desconto) return 'O valor dessa aula vira desconto na sua próxima cobrança.';
        return 'Sua mensalidade não muda.';
      };
      for (const alunoId of Object.keys(resultadoPorAluno)) {
        await supabase.from('notificacoes').insert({
          aluno_id: alunoId, professor_id: professor.id, tipo: 'feriado',
          mensagem: `${quando} é feriado (${feriadoAlerta.nome}). ${nomeProf} não dá aula nesse dia. ${consequencia(resultadoPorAluno[alunoId])}`,
        });
      }
      toastSM(`${movidas} aula${movidas === 1 ? '' : 's'} empurrada${movidas === 1 ? '' : 's'} para depois do feriado.`);
    } else {
      try { localStorage.setItem(`feriadoOk:${professor.id}:${feriadoAlerta.data}`, '1'); } catch {}
      const quando = new Date(feriadoAlerta.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' });
      const nomeProf = professor?.nome?.split(' ')[0] || 'Seu professor';
      const alunosIds = [...new Set(feriadoAlerta.aulas.map(a => a.aluno_id).filter(Boolean))];
      for (const alunoId of alunosIds) {
        await supabase.from('notificacoes').insert({
          aluno_id: alunoId, professor_id: professor.id, tipo: 'feriado',
          mensagem: `${quando} é feriado (${feriadoAlerta.nome}), mas ${nomeProf} vai dar aula normalmente. Sua aula está mantida.`,
        });
      }
      toastSM('Combinado — aulas mantidas. Os alunos foram avisados.');
    }
    setResolvendoFeriado(false);
    setFeriadoAlerta(null);
  }
  const [contra, setContra] = React.useState({ data: '', hora: '' });

  // Sem embutir alunos(...) nem aulas(...): a tabela solicitacoes nasceu sem chave
  // estrangeira, e o Supabase recusa o embed quando nao enxerga a relacao — a consulta
  // voltava vazia e o painel ficava em branco mesmo com pedido na fila. Busco os nomes
  // e as aulas em seguida, o que tambem funciona se a chave for adicionada depois.
  // U26 — aulas que o aluno lancou ("combinei com o professor fora do app"). O professor
  // confirma com um toque; ate' la' a aula nao conta em saldo nenhum.
  const [aulasPropostas, setAulasPropostas] = React.useState([]);
  const [respondendoProposta, setRespondendoProposta] = React.useState(null);
  async function carregarPropostas() {
    const { data } = await supabase.from('aulas').select('id, aluno_id, data, hora, observacao, created_at')
      .eq('professor_id', professor.id).eq('status', 'aguardando_confirmacao').order('data').limit(20);
    const lista = data || [];
    if (!lista.length) { setAulasPropostas([]); return; }
    const ids = [...new Set(lista.map(a => a.aluno_id))];
    const { data: als } = await supabase.from('alunos').select('id, nome').in('id', ids);
    const mapA = Object.fromEntries((als || []).map(a => [a.id, a.nome]));
    setAulasPropostas(lista.map(a => ({ ...a, nome: mapA[a.aluno_id] || 'Aluno' })));
  }
  // v131 (Etapa 6): ao confirmar, o banco confere o horario (p_forcar:false); se bater com outra
  // aula, o professor ve quem e decide no alerta (2o toque = p_forcar:true).
  async function responderProposta(a, aceita) {
    setRespondendoProposta(a.id);
    const r = await responderPropostaProf(a, aceita, { origem: 'inicio', nomeAluno: a.nome });
    setRespondendoProposta(null);
    if (!r) return;
    setAulasPropostas(prev => prev.filter(x => x.id !== a.id));
    if (aceita) setRecargaDash(n => n + 1);
  }
  React.useEffect(() => { carregarPropostas(); }, [professor.id]);

  async function carregarPedidos() {
    // v136: sem a remarcacao do PROFESSOR (tipo remarcacao_prof, 'pendente' = esperando o aluno) —
    // nao e' pedido do aluno e nao tem o que aceitar aqui. O filtro no app e' a garantia.
    const { data, error } = await supabase.from('solicitacoes')
      .select('*').eq('professor_id', professor.id).eq('status', 'pendente').neq('tipo', TIPO_REMARC_PROF_SM)
      .order('criado_em', { ascending: false });
    if (error) { console.warn('pedidos:', error.message); setPedidos([]); return; }
    const lista = (data || []).filter(ehPedidoDoAlunoSM);
    if (!lista.length) { setPedidos([]); return; }

    const idsAluno = [...new Set(lista.map(s => s.aluno_id).filter(Boolean))];
    const idsAula  = [...new Set(lista.map(s => s.aula_id).filter(Boolean))];
    const idsPausa = [...new Set(lista.filter(s => s.tipo === 'pausa').map(s => s.aluno_id).filter(Boolean))];
    const [{ data: als }, { data: aus }, { data: cps }] = await Promise.all([
      idsAluno.length ? supabase.from('alunos').select('id, nome, telefone').in('id', idsAluno) : Promise.resolve({ data: [] }),
      idsAula.length  ? supabase.from('aulas').select('id, data, hora, status').in('id', idsAula) : Promise.resolve({ data: [] }),
      // v131: pausa — o acordo que o banco vai usar (pausar_pacote: sem turma primeiro, depois o do
      // pedido, depois o mais novo). Na mensalidade a pausa CANCELA as aulas; o cartao diz isso antes.
      idsPausa.length ? supabase.from('contratos').select('id, aluno_id, professor_id, tipo_cobranca, turma_id, data_inicio').in('aluno_id', idsPausa).eq('status', 'ativo') : Promise.resolve({ data: [] }),
    ]);
    const mapA = Object.fromEntries((als || []).map(a => [a.id, a]));
    const mapU = Object.fromEntries((aus || []).map(a => [a.id, a]));
    const acordoPausa = s => (cps || [])
      .filter(c => c.aluno_id === s.aluno_id && (!c.professor_id || c.professor_id === professor.id))
      .sort((x, y) => (x.turma_id ? 1 : 0) - (y.turma_id ? 1 : 0) || (y.id === s.contrato_id) - (x.id === s.contrato_id)
        || String(y.data_inicio || '').localeCompare(String(x.data_inicio || '')))[0] || null;
    setPedidos(lista.map(s => ({ ...s, alunos: mapA[s.aluno_id] || null, aulas: mapU[s.aula_id] || null,
      acordoPausa: s.tipo === 'pausa' ? acordoPausa(s) : null })));
  }
  React.useEffect(() => { carregarPedidos(); }, [professor.id]);

  // Revisao v142 (achado 3 da ida e volta): quem entrou pelo link "Cadastrar varios de uma vez" e ainda nao tem
  // acordo aparece aqui tambem — antes o professor so' via se abrisse a tela Alunos.
  const [esperandoLink, setEsperandoLink] = React.useState([]);
  React.useEffect(() => {
    supabase.from('alunos').select('id, nome, contratos(id)').eq('professor_id', professor.id).not('user_id', 'is', null).is('desvinculado_em', null).limit(200)
      .then(({ data, error }) => setEsperandoLink(error ? [] : (data || []).filter(a => a && Array.isArray(a.contratos) && a.contratos.length === 0)), () => setEsperandoLink([]));
  }, [professor.id]);
  // v144: pedido de aluno aceito e ainda nao completado (vem do banco: sobrevive a fechar a janela e trocar de aparelho)
  const [pedidosAbertos, setPedidosAbertos] = React.useState([]);
  React.useEffect(() => {
    supabase.rpc('professor_pedidos_a_completar')
      .then(({ data, error }) => setPedidosAbertos(!error && Array.isArray(data) ? data : []), () => setPedidosAbertos([]));
  }, [professor.id]);
  // Fechamento v144 (teste de tela): no professor sem alunos (o caso do Iago) o cartao vai para o TOPO do painel vazio —
  // embaixo do "Cadastrar meu primeiro aluno" ele ficava fora da tela e o caminho natural era o cadastro em branco.
  const cartaoPedidos = pedidosAbertos.length > 0 ? (
    <button type="button" data-pedidos-abertos="1" onClick={() => { uso.acao('inicio.pedido_completar', { n: pedidosAbertos.length }); onNavigate && onNavigate('alunos', { pedido: pedidosAbertos[0].code, t: Date.now() }); }}
      style={{display:'flex', alignItems:'center', gap:'10px', width:'100%', textAlign:'left', background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'16px',
              padding:'12px 14px', minHeight:'44px', cursor:'pointer', fontFamily:'inherit'}}>
      <Ico n="message-2" s={17} c="#2F6BFF" />
      <span style={{flex:1, minWidth:0}}>
        <span style={{display:'block', fontSize:'13px', fontWeight:'800', color:'#16181D'}}>
          {pedidosAbertos.slice(0, 3).map(x => primeiroNomeSM(x.aluno_nome, 'Aluno')).join(', ')}{pedidosAbertos.length > 3 ? ' e outros' : ''} {pedidosAbertos.length === 1 ? 'mandou' : 'mandaram'} o combinado
        </span>
        <span style={{display:'block', fontSize:'12px', color:'#6E7480', marginTop:'2px', lineHeight:1.4}}>
          Toque para conferir e criar — {pedidosAbertos.length === 1 ? 'ele vê' : 'eles veem'} no app na hora.
        </span>
      </span>
      <Ico n="chevron-right" s={18} c="#2F6BFF" />
    </button>
  ) : null;

  // v136: o aluno disse que nao consegue no horario que voce remarcou (remarcacao_prof 'recusada') e
  // a aula ainda esta' nesse horario, sem registro: aparece junto dos pedidos, com [Remarcar]
  // [Desmarcar] — os dois levam a' tela Aulas, no dia dela, com a janela certa aberta. Remarcou,
  // desmarcou ou registrou: some. Passou do horario: CONTINUA (o caminho vira "Eu desmarquei").
  // A lista muda fora daqui (aceitar pedido, Pendencias, tela Aulas, o aluno): recarrega quando o app
  // avisa (EVENTO_PENDENCIAS_SM) e quando volta para a frente — senao mostrava aula ja' remarcada.
  const [remarcRecusadas, setRemarcRecusadas] = React.useState([]);
  async function carregarRemarcRecusadas() {
    let data = null;
    try {
      ({ data } = await supabase.from('solicitacoes')
        .select('id, tipo, status, aluno_id, aula_id, resposta, resposta_data, resposta_hora, respondida_em')
        .eq('professor_id', professor.id).eq('tipo', TIPO_REMARC_PROF_SM).eq('status', 'recusada')
        .order('respondida_em', { ascending: false }).limit(20));
    } catch { data = null; }
    const lista = data || [];
    if (!lista.length) { setRemarcRecusadas([]); return; }
    const idsAula = [...new Set(lista.map(s => s.aula_id).filter(Boolean))];
    const idsAluno = [...new Set(lista.map(s => s.aluno_id).filter(Boolean))];
    const [{ data: aus }, { data: als }] = await Promise.all([
      idsAula.length ? supabase.from('aulas').select('id, data, hora, status, desfecho_prof, divergencia_em').in('id', idsAula) : Promise.resolve({ data: [] }),
      idsAluno.length ? supabase.from('alunos').select('id, nome').in('id', idsAluno) : Promise.resolve({ data: [] }),
    ]);
    const mapU = Object.fromEntries((aus || []).map(a => [a.id, a]));
    const mapA = Object.fromEntries((als || []).map(a => [a.id, a.nome]));
    // a aula vai junto: "ja' comecou" e' conferido na hora de desenhar, nao so' aqui
    setRemarcRecusadas(lista.filter(s => remarcacaoValendoSM(s, mapU[s.aula_id]))
      .map(s => ({ ...s, aula: mapU[s.aula_id], nome: mapA[s.aluno_id] || 'Aluno' })));
  }
  React.useEffect(() => {
    carregarRemarcRecusadas();
    let espera = null;
    const aoMudar = () => { clearTimeout(espera); espera = setTimeout(() => carregarRemarcRecusadas(), 500); };
    const aoVoltar = () => { if (document.visibilityState === 'visible') carregarRemarcRecusadas(); };
    window.addEventListener(EVENTO_PENDENCIAS_SM, aoMudar);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      clearTimeout(espera);
      window.removeEventListener(EVENTO_PENDENCIAS_SM, aoMudar);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [professor.id]);
  function abrirRemarcRecusada(s, acao) {
    uso.acao('remarcacao_recusada.abrir', { acao, origem: 'inicio' });
    onNavigate && onNavigate('aulas', { aula_id: s.aula_id, [acao]: true, para_data: s.resposta_data || null,
                                        para_hora: s.resposta_hora || null, t: Date.now() });
  }

  // Link do WhatsApp: o navegador do WhatsApp nao tem a sessao do app, entao ele cai no
  // login. Guardo o destino antes de logar e, quando a lista chega, abro o pedido certo
  // — senao o professor entra, ve' a tela de sempre e acha que o link nao funcionou.
  const [pedidoDestacado, setPedidoDestacado] = React.useState(null);
  const [pedidoSumido, setPedidoSumido] = React.useState(false);
  React.useEffect(() => {
    const alvo = localStorage.getItem('pendingSolicitacao');
    if (!alvo || !pedidos.length) return;
    if (pedidos.some(p => p.id === alvo)) {
      uso.acao('entrada.link_pedido', { achou: true });
      setPedidoDestacado(alvo);
      setTimeout(() => document.getElementById('bloco-pedidos')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
    }
    localStorage.removeItem('pendingSolicitacao');
  }, [pedidos]);
  React.useEffect(() => {
    const alvo = localStorage.getItem('pendingSolicitacao');
    if (!alvo) return;
    const t = setTimeout(async () => {
      if (!localStorage.getItem('pendingSolicitacao')) return;
      const { data } = await supabase.from('solicitacoes').select('status').eq('id', alvo).maybeSingle();
      if (data && data.status !== 'pendente') { uso.acao('entrada.link_pedido', { resolvido: true }); setPedidoSumido(true); }
      localStorage.removeItem('pendingSolicitacao');
    }, 2500);
    return () => clearTimeout(t);
  }, []);
  // v134: veio de Pendencias ("Responder" num pedido): destaca e rola ate' o pedido, como o link do
  // WhatsApp — mas sem passar pelo localStorage (nao conta como entrada.link_pedido). Uma vez por pedido.
  const alvoPend = params?.solicitacao_id || null;
  const alvoPendFeito = React.useRef(null);
  React.useEffect(() => {
    if (!alvoPend || alvoPendFeito.current === alvoPend || !pedidos.some(p => p.id === alvoPend)) return;
    alvoPendFeito.current = alvoPend;
    setPedidoDestacado(alvoPend);
    setTimeout(() => document.getElementById('bloco-pedidos')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
  }, [pedidos, alvoPend]);

  // v131: toque duplo no mesmo pedido nao responde duas vezes
  async function responderPedido(s, decisao) {
    return comTravaAulaSM('pedido:' + s.id, () => responderPedido0(s, decisao));
  }
  async function responderPedido0(s, decisao) {
    setRespondendoId(s.id);
    const nomeProf = professor?.nome?.split(' ')[0] || 'Seu professor';
    const fmt = (d, h) => `${new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})}${h ? ' às ' + String(h).slice(0,5) : ''}`;

    if (decisao === 'aceita') {
      const novaData = s.data_sugerida;
      const novaHora = s.hora_sugerida || s.aulas?.hora || null;
      if (!novaData) { uso.bloqueio('pedido_responder', null, null, 'pedido_sem_data'); setRespondendoId(null); toastSM('Esse pedido não trouxe uma data. Use "Propor outro horário".'); return; }
      if (s.aula_id) {
        // v131 (Etapa 6): remarcar_aula move a aula, confere o horario, fecha o pedido e avisa o
        // aluno — tudo no banco. Horario ocupado: o professor ve quem e decide.
        let voltou = false;
        const r = await remarcarAulaProf({ id: s.aula_id, data: s.aulas?.data, hora: s.aulas?.hora }, novaData, novaHora,
          { solicitacaoId: s.id, origem: 'pedido', nomeAluno: s.alunos?.nome, aoVoltar: () => { voltou = true; } });
        if (!r) {
          // "Escolher outro" no alerta: abre o "Propor outro" deste pedido (respondendoId = s.id) com o
          // dia pedido e a hora em branco, em vez de fechar tudo
          if (voltou) setContra({ data: novaData, hora: '' });
          else setRespondendoId(null);
          return;
        }
      } else {
        // pedido sem aula ligada: como antes, o professor so' fecha o pedido
        const { error } = await supabase.from('solicitacoes').update({
          status: 'aceita', resposta: 'Combinado.', resposta_data: novaData, resposta_hora: novaHora,
          respondida_em: new Date().toISOString(),
        }).eq('id', s.id);
        if (error) { setRespondendoId(null); uso.erro('erro.pedido_responder', error.message); toastSM('Não foi possível responder agora. Tente de novo.'); return; }
        await supabase.from('notificacoes').insert({
          aluno_id: s.aluno_id, professor_id: professor.id, tipo: 'reagendamento_aceito',
          mensagem: `${nomeProf} aceitou. Sua aula agora é ${fmt(novaData, novaHora)}.`,
        });
        toastSM('Pedido aceito e aluno avisado.');
      }
    }

    if (decisao === 'recusada') {
      const ehAjuste = s.tipo === 'ajuste_contrato';
      const ehPausa = s.tipo === 'pausa';
      await supabase.from('solicitacoes').update({
        status: 'recusada',
        resposta: ehAjuste ? 'Conferi e está correto.' : ehPausa ? 'Vamos combinar pelo WhatsApp.' : 'Não consigo nessa data.',
        respondida_em: new Date().toISOString(),
      }).eq('id', s.id);
      await supabase.from('notificacoes').insert({
        aluno_id: s.aluno_id, professor_id: professor.id,
        tipo: ehAjuste ? 'ajuste_contrato' : ehPausa ? 'pausa_recusada' : 'reagendamento_recusado',
        mensagem: ehAjuste
          ? `${nomeProf} conferiu e diz que o acordo está correto. Fale com ele se ainda não bater.`
          : ehPausa
          ? `${nomeProf} prefere combinar a sua ausência direto com você. Suas aulas seguem como estão por enquanto.`
          : `${nomeProf} não conseguiu remarcar para essa data. Sua aula segue no horário de sempre.`,
      });
      toastSM('Aluno avisado.');
    }

    if (decisao === 'contraproposta') {
      if (!contra.data) { uso.bloqueio('pedido_responder', null, null, 'contra_sem_data'); setRespondendoId(null); toastSM('Escolha o dia que serve para você.'); return; }
      // v131 (Etapa 6): sempre com hora — sem hora escolhida, a da aula (o aluno aceita pelo banco,
      // que le' dia e hora daqui)
      const horaC = contra.hora || (s.aulas?.hora ? String(s.aulas.hora).slice(0, 5) : null) || (s.hora_sugerida ? String(s.hora_sugerida).slice(0, 5) : null);
      if (!horaC) { uso.bloqueio('pedido_responder', null, null, 'contra_sem_hora'); setRespondendoId(null); toastSM('Escolha também o horário.'); return; }
      if (s.aula_id) {
        // horario ocupado: o aluno nao conseguiria aceitar — o formulario fica aberto para outro horario
        const conf = await conflitosAgendaProfSM(professor.id, s.aluno_id, contra.data, horaC, { ignorar: s.aula_id });
        if (conf && conf.length) {
          uso.bloqueio('pedido_responder', null, null, 'contra_ocupado');
          toastSM(`${textoConflitosSM(conf)} Escolha outro horário para propor.`);
          return;
        }
      }
      await supabase.from('solicitacoes').update({
        status: 'contraproposta', resposta: `Consigo ${fmt(contra.data, horaC)}.`,
        resposta_data: contra.data, resposta_hora: horaC,
        respondida_em: new Date().toISOString(),
      }).eq('id', s.id);
      await supabase.from('notificacoes').insert({
        aluno_id: s.aluno_id, professor_id: professor.id, tipo: 'reagendamento_contraproposta',
        mensagem: `${nomeProf} não consegue no dia que você pediu, mas propôs ${fmt(contra.data, horaC)}. Confirme no app.`,
      });
      setContra({ data: '', hora: '' });
      toastSM('Proposta enviada ao aluno.');
    }

    uso.acao('pedido.responder', { decisao, tipo: s.tipo });
    avisarPendenciasMudouSM();   // v134: o pedido saiu da lista de Pendencias (ou virou "esperando o aluno")
    setRespondendoId(null);
    setPedidos(prev => prev.filter(p => p.id !== s.id));
  }

  async function marcarReposicao(r) {
    if (!marcar.data || !marcar.hora) { uso.bloqueio('reposicao_marcar', null, null, 'sem_data_hora'); toastSM('Escolha dia e horário'); return; }
    const { data: aula, error } = await supabase.from('aulas').insert({
      aluno_id: r.aluno_id, professor_id: professor.id, contrato_id: r.contrato_id,
      data: marcar.data, hora: marcar.hora, status: 'agendada', consome_credito: false,
    }).select().single();
    if (error) { toastSM(ehAulaRepetidaSM(error) ? TEXTO_AULA_REPETIDA_SM : 'Não foi possível marcar: ' + error.message); return; }
    await supabase.from('reposicoes').update({ status: 'marcada', aula_reposicao_id: aula.id, atualizado_em: new Date().toISOString() }).eq('id', r.id);
    await supabase.from('notificacoes').insert({ aluno_id: r.aluno_id, professor_id: professor.id, tipo: 'reposicao_marcada',
      mensagem: `Reposição marcada para ${new Date(marcar.data + 'T12:00:00').toLocaleDateString('pt-BR')} às ${marcar.hora}.` });
    uso.acao('reposicao.marcar');
    toastSM('Reposição marcada — entrou na agenda');
    setMarcandoId(null); setMarcar({ data: '', hora: '' });
    carregarReposicoes();
  }

  const CirculoPresenca = ({ pct }) => {
    const r = 26; const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;
    const cor = pct >= 80 ? '#0E9F6E' : pct >= 60 ? '#D97706' : '#DC2626';
    return (
      <svg width="64" height="64" viewBox="0 0 64 64" style={{flexShrink:0}}>
        <circle cx="32" cy="32" r={r} fill="none" stroke="#E2E4E9" strokeWidth="6"/>
        <circle cx="32" cy="32" r={r} fill="none" stroke={cor} strokeWidth="6"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 32 32)"/>
        <text x="32" y="37" textAnchor="middle" fontSize="13" fontWeight="700" fill={cor}>{pct}%</text>
      </svg>
    );
  };

  const treinadasHoje  = aulasHoje.filter(a => a.status === 'realizada').length;
  const totalHoje = aulasHoje.length;
  return (
    <div className="p-4 max-w-2xl mx-auto flex flex-col gap-4">

      {/* Cabecalho escuro: o professor abre o app e sabe o dia em 2 segundos.
          Os tres numeros sao o dia dele — nao metrica de vaidade. */}
      <div style={{background:'#16181D', borderRadius:'24px', padding:'22px 22px 24px', color:'white',
                   marginBottom:'2px'}}>
        <div style={{fontSize:'12px', color:'#6E7480'}}>{hojeLabel}</div>
        <div style={{fontFamily:"'Sora',sans-serif", fontSize:'23px', fontWeight:'600',
                     marginTop:'3px', letterSpacing:'-0.02em'}}>
          {saudacao}, {professor.nome?.split(' ')[0]}
        </div>

        <div style={{display:'flex', gap:'20px', marginTop:'20px', alignItems:'flex-start'}}>
          {/* Tres numeros, nao quatro: "ja confirmadas" repetia "aulas hoje" e no celular
              os rotulos quebravam em duas linhas. */}
          {[
            { v: totalHoje,               r: totalHoje === 1 ? 'aula hoje' : 'aulas hoje', cor: 'white', ir: 'agenda' },
            { v: aulasPendentes.length,   r: aulasPendentes.length === 1 ? 'pendente' : 'pendentes',
              cor: aulasPendentes.length ? '#FF6B6B' : '#6E7480', ir: null },
            { v: stats.alunosAtivos,      r: stats.alunosAtivos === 1 ? 'aluno' : 'alunos', cor: 'white', ir: 'alunos' },
          ].map((c, i) => (
            <React.Fragment key={c.r}>
              {i > 0 && <div style={{width:'1px', alignSelf:'stretch', background:'#2A2E36'}} />}
              <div onClick={() => { if (c.ir) uso.acao('inicio.numero', { destino: c.ir }); c.ir && onNavigate && onNavigate(c.ir); }} style={{cursor: c.ir ? 'pointer' : 'default'}}>
                <div style={{fontFamily:"'Sora',sans-serif", fontSize:'26px', fontWeight:'700',
                             lineHeight:1, color:c.cor}}>{c.v}</div>
                <div style={{fontSize:'11.5px', color:'#9AA0AB', marginTop:'5px', whiteSpace:'nowrap'}}>{c.r}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* v140 (Tema 3): o aviso antes da aula e o resumo da agenda chegam por aqui. v147: logo abaixo do
          cabecalho (antes ficava depois do painel vazio e o professor novo nao via); no app, e' o cartao claro. */}
      <FaixaPush userId={professor?.user_id} quem="professor" texto="Receber aviso antes das suas aulas e quando um aluno responder?" />

      {/* Painel vazio. Em vez de uma lista de tarefas de configuracao — que e' pedir
          trabalho antes de entregar valor —, mostro em cinza como o painel dele vai
          ficar. Ele entende o produto olhando, nao lendo, e a unica acao viva e' a
          que importa: colocar o primeiro aluno. */}
      {stats.alunosAtivos === 0 && aulasPendentes.length === 0 && (
        <div style={{display:'flex', flexDirection:'column', gap:'14px'}}>
          {cartaoPedidos}
          <div style={{position:'relative', borderRadius:'18px', overflow:'hidden', border:'1px solid #E2E4E9', background:'white'}}>
            <div style={{padding:'16px 18px', filter:'grayscale(1)', opacity:.45, pointerEvents:'none', userSelect:'none'}}>
              <div style={{fontSize:'13px', fontWeight:'800', color:'#991B1B', marginBottom:'10px'}}>
                <Ico n="point-filled" c="#DC2626" /> 2 aulas sem confirmação
              </div>
              {[['João Pedro','ter · 07:00'], ['Marina Alves','ter · 18:00']].map(([n, q]) => (
                <div key={n} style={{background:'white', border:'1px solid #FECACA', borderRadius:'12px', padding:'11px 12px', marginBottom:'8px'}}>
                  <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D'}}>{n}</div>
                  <div style={{fontSize:'12px', color:'#6E7480', marginTop:'1px'}}>{q}</div>
                  <div style={{display:'flex', gap:'7px', marginTop:'10px'}}>
                    <div style={{flex:1, background:'#16181D', color:'white', fontWeight:'600', fontSize:'13px', borderRadius:'10px', padding:'11px 8px', textAlign:'center'}}>Concluída</div>
                    <div style={{border:'0.5px solid #E2E4E9', color:'#6E7480', fontWeight:'600', fontSize:'13px', borderRadius:'10px', padding:'11px 14px'}}>Não houve</div>
                  </div>
                </div>
              ))}
              <div style={{display:'flex', gap:'18px', marginTop:'14px', paddingTop:'14px', borderTop:'1px solid #F0F1F4'}}>
                {[['R$ 4.800','recebido no mês'], ['R$ 1.600','em aberto'], ['12','alunos']].map(([v, r]) => (
                  <div key={r}>
                    <div style={{fontFamily:"'Sora',sans-serif", fontSize:'20px', fontWeight:'700', color:'#16181D'}}>{v}</div>
                    <div style={{fontSize:'11px', color:'#6E7480', marginTop:'3px'}}>{r}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center',
                         background:'linear-gradient(180deg, rgba(255,255,255,.35), rgba(255,255,255,.92))'}}>
              <span style={{fontSize:'12px', fontWeight:'700', color:'#6E7480', background:'white', border:'1px solid #E2E4E9',
                            borderRadius:'999px', padding:'7px 16px'}}>
                É assim que seu painel vai ficar
              </span>
            </div>
          </div>

          <div style={{background:'linear-gradient(135deg,#2F6BFF,#2455D6)', borderRadius:'20px', padding:'24px', color:'white'}}>
            <div style={{fontFamily:"'Sora',sans-serif", fontSize:'20px', fontWeight:'700', marginBottom:'6px'}}>
              Vamos cadastrar o seu primeiro aluno?
            </div>
            <p style={{fontSize:'13px', opacity:.88, marginBottom:'18px', lineHeight:1.55}}>
              Seis perguntas curtas. No fim, todas as aulas dele já entram na sua agenda
              sozinhas — e o painel acima ganha vida com os seus números.
            </p>
            <button onClick={() => { uso.acao('inicio.primeiro_aluno'); onNavigate && onNavigate('alunos'); }}
              style={{width:'100%', background:'white', color:'#1C43AE', border:'none', borderRadius:'14px',
                      padding:'15px', fontSize:'15px', fontWeight:'800', cursor:'pointer'}}>
              <Ico n="user-plus" s={16} c="#1C43AE" /> Cadastrar meu primeiro aluno
            </button>
          </div>
          {APP_NATIVO && <ColarConvite quem="professor" onPedido={code => onNavigate && onNavigate('alunos', { pedido: code, origem: 'colar', t: Date.now() })} />}
        </div>
      )}

      {false && (
        <div style={{background:'linear-gradient(135deg,#2F6BFF,#2455D6)', borderRadius:'20px', padding:'24px', color:'white'}}>
          <div style={{fontSize:'22px', marginBottom:'6px'}}><Ico n="mood-smile" /> Bem-vindo ao Plen!</div>
          <p style={{fontSize:'13px', opacity:0.85, marginBottom:'20px', lineHeight:1.5}}>
            Siga os passos abaixo para configurar tudo e começar a gerenciar seus alunos.
          </p>
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            {[
 { n:1, icon:'user', titulo:'Configure seu perfil e PIX', sub:'Adicione seu nome, telefone e chave PIX para receber pagamentos', tela:'configuracoes'},
 { n:2, icon:'clipboard-list', titulo:'Crie seus planos', sub:'Defina os planos de treino que você oferece (mensal, semestral, etc.)', tela:'planos'},
 { n:3, icon:'user-plus', titulo:'Adicione seu primeiro aluno', sub:'Cadastre um aluno e envie o convite de acesso para ele', tela:'alunos'},
            ].map(step => (
              <button key={step.n} onClick={() => onNavigate && onNavigate(step.tela)}
                style={{background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'14px', cursor:'pointer', textAlign:'left', transition:'all 0.15s'}}
                onMouseOver={e => e.currentTarget.style.background='rgba(255,255,255,0.2)'}
                onMouseOut={e => e.currentTarget.style.background='rgba(255,255,255,0.12)'}>
                <div style={{width:'36px', height:'36px', borderRadius:'10px', background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n={step.icon} s={18} c="white" /></div>
                <div style={{flex:1}}>
                  <div style={{fontSize:'13px', fontWeight:'700', color:'white'}}>{step.n}. {step.titulo}</div>
                  <div style={{fontSize:'11px', color:'rgba(255,255,255,0.65)', marginTop:'2px'}}>{step.sub}</div>
                </div>
                <span style={{color:'rgba(255,255,255,0.5)', fontSize:'16px'}}>›</span>
              </button>
            ))}
          </div>
          <p style={{fontSize:'11px', opacity:0.5, marginTop:'14px', textAlign:'center'}}>Este guia some automaticamente quando você adicionar seu primeiro aluno.</p>
        </div>
      )}

      {}
      {/* Confirmar aula e' 1 toque, aqui mesmo — sem abrir tela. Aula sem status estraga
          creditos, financeiro e as metricas do piloto; por isso a faixa e' vermelha e
          fica acima de tudo. */}
      {sessoesTurma.length > 0 && (
        <div style={{background:'#F7F9FF', border:'2px solid #C2D4FF', borderRadius:'16px', padding:'14px 16px'}}>
          <p style={{fontSize:'14px', fontWeight:'800', color:'#1C43AE', margin:'0 0 10px'}}><Ico n="users" c="#2F6BFF" /> Aulas da turma</p>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {sessoesTurma.map(sx => {
              const passou = sx.data < hojeStrRender || (sx.data === hojeStrRender && String(sx.hora).slice(0,5) < new Date().toTimeString().slice(0,5));
              return (
                <div key={sx.sessao_id} style={{background:'white', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'11px 12px', display:'flex', alignItems:'center', gap:'10px'}}>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontWeight:'800', fontSize:'14px'}}>{sx.turma_nome}</div>
                    {/* v131: quem cancelou nao conta (aparece riscado na presenca) */}
                    {(() => { const nP = sx.aulas.filter(x => !String(x.status || '').startsWith('cancelada')).length; return (
                    <div style={{fontSize:'12.5px', color: passou ? '#B91C1C' : '#6E7480'}}>{fmtDataBR(sx.data)} · {String(sx.hora).slice(0,5)} · {nP} pessoa{nP !== 1 ? 's' : ''}{passou ? ' · falta fechar' : ''}</div>
                    ); })()}
                  </div>
                  <Button onClick={() => setPresencaTurma(sx)} style={{padding:'8px 14px'}}>Presença</Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {presencaTurma && <ModalPresencaTurma professor={professor} sessao={presencaTurma} onClose={() => setPresencaTurma(null)} onFechou={() => setRecargaDash(n => n + 1)} />}
      {aulasPendentes.length > 0 && (
        <div style={{background:'#FEF2F2', border:'2px solid #FECACA', borderRadius:'16px', padding:'14px 16px'}}>
          <p style={{fontSize:'14px', fontWeight:'800', color:'#991B1B', margin:'0 0 10px'}}>
            <Ico n="point-filled" c="#DC2626" /> {aulasPendentes.length === 1 ? '1 aula de dias anteriores para fechar' : aulasPendentes.length + ' aulas de dias anteriores para fechar'}
          </p>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {aulasPendentes.slice(0, 6).map(ap => {
              const dt = new Date(ap.data + 'T12:00:00');
              const quando = dt.toLocaleDateString('pt-BR', { weekday:'short', day:'numeric' }).replace('.', '');
              const aberto = naoHouveId === ap.id;
              // v140: o aluno marcou "o professor nao veio" (e o professor ainda nao registrou nada)
              const nvAp = naoVeioSM(ap);
              const nvPend = nvAp && nvAp.pendente ? nvAp : null;
              const semSaldoAp = !!(ap.duracao && (ap.duracao.tipo_cobranca === 'mensal' || ap.duracao.turma_id));
              return (
                <div key={ap.id} id={'aula-' + ap.id} style={{background:'white', border:'1px solid #FECACA', borderRadius:'12px', padding:'11px 12px'}}>
                  {/* No celular nome + data + 2 botoes nao cabem numa linha: o nome quebrava
                      no meio. Nome em cima, botoes embaixo com area de toque decente. */}
                  <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D'}}>{ap.nome}</div>
                  <div style={{fontSize:'12px', color:'#6E7480', marginTop:'1px'}}>
                    {quando} · {String(ap.hora||'').slice(0,5)}
                  </div>
                  {/* O aluno ja' disse que aconteceu. Isso nao fecha a aula sozinho — quem
                      fecha e' o professor —, mas tira a duvida de quem esta' decidindo. */}
                  {ap.divergencia_em ? (
                    <div style={{fontSize:'11px', fontWeight:'700', color:'#991B1B', background:'#FEF2F2',
                                 borderRadius:'8px', padding:'4px 9px', marginTop:'6px', display:'inline-block'}}>
                      <Ico n="alert-triangle" s={12} /> Vocês registraram diferente
                      ({ap.desfecho_prof === 'realizada' ? 'você: aula dada' : 'você: falta'} · {ap.confirmada_aluno_em ? 'aluno: aconteceu' : nvAp ? 'aluno: você não veio' : 'aluno: não foi'}).
                      Decida abaixo — o aluno é avisado.
                    </div>
                  ) : nvPend ? (
                    <div style={{fontSize:'11.5px', fontWeight:'700', color:'#991B1B', background:'#FEF2F2',
                                 borderRadius:'8px', padding:'5px 9px', marginTop:'6px', lineHeight:1.45}}>
                      <Ico n="user-exclamation" s={12} /> {ap.nome.split(' ')[0]} diz que você não veio · avisou {quandoRelSM(nvPend.em)}
                      <div style={{fontWeight:'600', marginTop:'2px'}}>
                        {textoVoltaNaoVeioSM(nvPend.voltaEm, { semSaldo: semSaldoAp })}
                        {' '}Se foi isso, use "Não houve" → "Eu não fui / desmarquei".
                      </div>
                    </div>
                  ) : avisoPendenteSM(ap) ? (
                    // v131 (Etapa 9): aviso FORA do prazo esperando o professor
                    <div style={{fontSize:'11.5px', fontWeight:'700', color:'#92400E', background:'#FFFBEB',
                                 borderRadius:'8px', padding:'5px 9px', marginTop:'6px', lineHeight:1.45}}>
                      <Ico n="bell-ringing" s={12} /> {textoAvisoProfSM(ap, ap.nome)}{ap.motivo_cancelamento && ROTULO_MOTIVO[ap.motivo_cancelamento] ? ` (${ROTULO_MOTIVO[ap.motivo_cancelamento].toLowerCase()})` : ''}.
                      {' '}{ap.duracao && (ap.duracao.tipo_cobranca === 'mensal' || ap.duracao.turma_id) ? 'Na mensalidade fica como falta — confirme.' : 'Pela regra a aula conta — você decide.'}
                    </div>
                  ) : ap.confirmada_aluno_em ? (
                    <div style={{fontSize:'11px', fontWeight:'700', color:'#0A6F4D', background:'#EFFAF5',
                                 borderRadius:'8px', padding:'4px 9px', marginTop:'6px', display:'inline-block'}}>
                      <Ico n="circle-check" s={12} /> O aluno confirmou que aconteceu
                    </div>
                  ) : ap.falta_declarada_aluno_em ? (
                    <div style={{fontSize:'11px', fontWeight:'700', color:'#92400E', background:'#FFFBEB',
                                 borderRadius:'8px', padding:'4px 9px', marginTop:'6px', display:'inline-block'}}>
                      <Ico n="user-off" s={12} /> O aluno disse que não foi{ap.motivo_cancelamento ? ` (${(ap.motivo_cancelamento === 'outro' && ap.observacao) ? ap.observacao : (ROTULO_MOTIVO[ap.motivo_cancelamento] || '').toLowerCase()})` : ''} — escolha o que fazer em "Não houve"
                    </div>
                  ) : remarcRecusadas.some(s => s.aula_id === ap.id) ? (
                    <div style={{fontSize:'11.5px', fontWeight:'700', color:'#92400E', background:'#FFFBEB',
                                 borderRadius:'8px', padding:'5px 9px', marginTop:'6px', lineHeight:1.45}}>
                      <Ico n="calendar-x" s={12} /> {ap.nome.split(' ')[0]} avisou antes que não conseguia neste horário (você tinha remarcado). Se não aconteceu, use "Não houve" → "Eu que desmarquei" — não desconta.
                    </div>
                  ) : null}
                  {avisoPendenteSM(ap) ? (
                    <BotoesDecidirAviso aula={ap} nomeAluno={ap.nome} origem="inicio_pendente"
                      podeDevolver={!(ap.duracao && (ap.duracao.tipo_cobranca === 'mensal' || ap.duracao.turma_id))}
                      onFeito={() => { setAulasPendentes(prev => prev.filter(x => x.id !== ap.id)); setRecargaDash(n => n + 1); }} />
                  ) : (
                  <div style={{display:'flex', gap:'7px', marginTop:'10px'}}>
                    <button disabled={atualizandoId === ap.id} onClick={() => abrirConfirmacao(ap, null)}
                      style={{flex:1, minHeight:'44px', border:'none', background:'#16181D', color:'white', fontWeight:'600', fontSize:'13px',
                              borderRadius:'10px', padding:'11px 8px', cursor:'pointer', fontFamily:'inherit'}}>
                      <Ico n="check" s={14} /> Concluída
                    </button>
                    <button disabled={atualizandoId === ap.id} onClick={() => { setNaoHouveId(aberto ? null : ap.id); setConfirmando(null); }}
                      style={{minHeight:'44px', border: aberto ? '1.5px solid #9AA0AB' : '0.5px solid #E2E4E9', background:'white',
                              color:'#6E7480', fontWeight:'600', fontSize:'13px', borderRadius:'10px',
                              padding:'11px 14px', cursor:'pointer', whiteSpace:'nowrap', fontFamily:'inherit'}}>
                      Não houve
                    </button>
                  </div>
                  )}

                  {/* O "por que" decide o dinheiro: furou = consome credito do pacote;
                      avisou no prazo ou o professor desmarcou = credito intacto. */}
                  {aberto && (
                    <div style={{display:'flex', flexDirection:'column', gap:'6px', marginTop:'8px',
                                 borderTop:'1px solid #FEE2E2', paddingTop:'8px'}}>
                      {/* v140: o aluno disse que voce nao veio -> "Eu nao fui / desmarquei" vem primeiro */}
                      {(nvAp ? ['professor', 'aluno_fora_prazo', 'aluno_no_prazo', 'forca_maior'].filter(k => MOTIVOS[k]).map(k => [k, MOTIVOS[k]]) : Object.entries(MOTIVOS)).map(([motivo, rotulo]) => {
                        const c = ap.duracao || {};
                        const mensal0 = c.tipo_cobranca === 'mensal';
                        // v131: no pacote, quem decide e' o banco — a aula vai para o fim do pacote
                        const mensal = mensal0 || !!c.turma_id;
                        const pAp = ap.nome.split(' ')[0];
                        const chuvaAp = motivo === 'forca_maior' && chuvaLivre(c, ap.modalidade, parseMods(professor.modalidades));   // v148
                        const dica = nvAp && motivo === 'professor' ? (mensal ? 'mensalidade fixa — fica registrado' : `não desconta — a aula volta para o fim do pacote de ${pAp}`)
                                   : nvAp && motivo === 'aluno_fora_prazo' ? `${pAp} diz que você não veio — fica registrado diferente e a aula não desconta até vocês acertarem`
                                   : nvAp && motivo === 'forca_maior' ? (mensal ? 'mensalidade fixa — nada muda' : 'sem aviso antes, conta como desmarcação sua — a aula volta para o pacote')
                                   : nvAp && motivo === 'aluno_no_prazo' ? `não consome — ${pAp} diz que você não veio: fica registrado "em desacordo"`
                                   : motivo === 'aluno_fora_prazo' ? (mensal ? 'a mensalidade corre' : 'consome 1 aula do pacote')
                                   : motivo === 'aluno_no_prazo'   ? (mensal ? 'a mensalidade corre' : (c.reposicao_permitida === false ? 'não consome — sem reposição neste acordo' : 'não consome — vai para o fim do pacote'))
                                   : motivo === 'professor'        ? (mensal ? 'mensalidade fixa — nada muda' : 'não consome — vai para o fim do pacote')
                                   : chuvaAp ? (mensal ? 'chuva não conta para ninguém — vira uma reposição a combinar' : 'chuva não conta para ninguém — vai para o fim do pacote')
                                   : (mensal ? 'mensalidade fixa — nada muda' : c.forca_maior_gera === 'nada' ? 'não consome' : c.forca_maior_gera === 'desconto' ? 'vira desconto na próxima cobrança' : `vai para o fim do pacote (máx. ${c.forca_maior_teto_mes ?? 1}/mês)`);
                        return (
                          <button key={motivo} disabled={atualizandoId === ap.id}
                            onClick={() => abrirConfirmacao(ap, motivo)}
                            style={{textAlign:'left', border:'1px solid #E2E4E9', background:'#F7F8FA',
                                    borderRadius:'10px', padding:'9px 11px', cursor:'pointer'}}>
                            <div style={{fontSize:'12.5px', fontWeight:'700', color:'#16181D'}}>
                              {motivo === 'aluno_fora_prazo' ? ap.nome.split(' ')[0] + ' furou ou desmarcou em cima da hora'
                               : motivo === 'professor' && nvAp ? 'Eu não fui / desmarquei'
                               : motivo === 'aluno_no_prazo' && ap.motivo_cancelamento && ap.cancelado_por === 'aluno' ? 'Devolver a aula desta vez (cortesia)'
                               : rotulo}
                            </div>
                            <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'2px'}}>{dica}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {confirmando && confirmando.id === ap.id && (() => {
                    const c = ap.duracao || {};
                    const mensal = c.tipo_cobranca === 'mensal';
                    const primeiro = ap.nome.split(' ')[0];
                    const m = confirmando.motivo;
                    const valorAula = mensal ? 0 : (Number(c.total_aulas) > 0 ? Number(c.preco_negociado || 0) / Number(c.total_aulas) : 0);
                    const emReais = v => v > 0 ? ` (${fmtBRL(v)})` : '';
                    // v131: para "nao houve" a favor do aluno, o texto vem da previa do banco
                    const pv = previaConf && previaConf.id === ap.id && previaConf.motivo === m ? previaConf : null;
                    const consome = m === null || m === 'aluno_fora_prazo';
                    const titulo = m === null ? 'Confirmar que a aula foi concluída?'
                      : m === 'aluno_fora_prazo' ? `Confirmar que ${primeiro} furou?`
                      : m === 'aluno_no_prazo'   ? `Confirmar que ${primeiro} avisou dentro do prazo?`
                      : m === 'professor'        ? (nvAp ? 'Confirmar que você não foi?' : 'Confirmar que você desmarcou?')
                      : 'Confirmar que foi chuva ou imprevisto?';
                    // v140: o aluno disse que voce nao veio — "dada" ou "faltou" viram divergencia (nao descontam)
                    const chuvaC = m === 'forca_maior' && !nvPend && chuvaLivre(c, ap.modalidade, parseMods(professor.modalidades));   // v148
                    const efeito = nvPend && consome
                      ? `${primeiro} diz que você não veio: fica registrado diferente e a aula não desconta até vocês acertarem.`
                      : chuvaC
                      ? (mensal ? 'Não abate aula. Chuva não conta para ninguém: vira uma reposição a combinar.' : 'Não abate aula. Chuva não conta para ninguém: vai para o fim do pacote.')
                      : mensal
                      ? 'A mensalidade corre igual — nada muda no saldo.'
                      : consome
                        ? (ap.divergencia_em
                            ? `Isto encerra a divergência e abate 1 aula do pacote de ${primeiro}${emReais(valorAula)} agora. Ele é avisado.`
                            : `Vai abater 1 aula do pacote de ${primeiro}${emReais(valorAula)} quando ele confirmar no app — ou em 24h, se ele não responder.`)
                        : m === 'forca_maior'
                          ? (c.forca_maior_gera === 'nada' ? 'Não abate aula nenhuma.'
                             : c.forca_maior_gera === 'desconto' ? `Não abate aula. Vira desconto${emReais(valorAula)} na próxima cobrança.`
                             : `Não abate aula. Vira reposição (máx. ${c.forca_maior_teto_mes ?? 1} por mês).`)
                          : (c.reposicao_permitida === false ? 'Não abate aula. Este acordo não prevê reposição.'
                             : 'Não abate aula. Vai para o fim do pacote.');
                    const efeitoTela = consome ? efeito : pv ? (pv.carregando ? 'Conferindo a regra do acordo…' : (pv.txt || efeito)) : efeito;
                    return (
                      <div style={{marginTop:'10px', borderTop:'1px solid #FEE2E2', paddingTop:'10px'}}>
                        <div style={{fontSize:'13px', fontWeight:'800', color:'#16181D'}}>{titulo}</div>
                        <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>{efeitoTela}</div>
                        <div style={{display:'flex', gap:'7px', marginTop:'10px'}}>
                          <button disabled={atualizandoId === ap.id} onClick={() => setConfirmando(null)}
                            style={{minHeight:'44px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontWeight:'700',
                                    fontSize:'13px', borderRadius:'10px', padding:'11px 16px', cursor:'pointer', fontFamily:'inherit'}}>
                            Cancelar
                          </button>
                          <button disabled={atualizandoId === ap.id}
                            onClick={() => { const alvo = confirmando; setConfirmando(null);
                                             alvo.motivo === null ? resolverPendencia(ap, 'realizada') : resolverNaoHouve(ap, alvo.motivo); }}
                            style={{flex:1, minHeight:'44px', border:'none', background:'#16181D', color:'white', fontWeight:'700',
                                    fontSize:'13px', borderRadius:'10px', padding:'11px 8px', cursor:'pointer', fontFamily:'inherit'}}>
                            {atualizandoId === ap.id ? 'Salvando…' : 'Confirmar'}
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
          {aulasPendentes.length > 6 && (
            <p style={{fontSize:'12px', color:'#B91C1C', margin:'10px 0 0'}}>
              …e mais {aulasPendentes.length - 6}. Elas aparecem aqui conforme você resolve estas.
            </p>
          )}
        </div>
      )}

      {/* 24/09: "Hoje" subiu para logo abaixo do cabecalho — e' a primeira coisa que o
          professor precisa ao abrir o app: quem tem aula, quem esta' em aula agora, o que fechar. */}
      {/* Linha do tempo do dia. Substitui o donut: o professor nao quer uma pizza,
          quer saber quem vem, a que horas e o que ja' resolveu. */}
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', margin:'6px 2px 10px'}}>
        <span style={{fontFamily:"'Sora',sans-serif", fontSize:'15px', fontWeight:'600', color:'#16181D'}}>Hoje</span>
        <button onClick={() => { uso.acao('inicio.ver_agenda'); onNavigate && onNavigate('agenda'); }}
          style={{border:'none', background:'none', color:'#2F6BFF', fontSize:'12px', fontWeight:'600',
                  cursor:'pointer', padding:0, fontFamily:'inherit'}}>Ver agenda</button>
      </div>

      <div style={{background:'white', borderRadius:'16px', padding:'2px 14px',
                   boxShadow:'0 1px 2px rgba(22,24,29,.05)'}}>
        {aulasHoje.length === 0 && (
          <div style={{padding:'26px 4px', textAlign:'center'}}>
            <Ico n="calendar-off" s={26} c="#C7CAD1" />
            <div style={{fontSize:'13.5px', color:'#6E7480', marginTop:'8px'}}>Nenhuma aula hoje</div>
          </div>
        )}
        {aulasHoje.map((a, i) => {
          const estado = estadoAulaSM(a);
          const st = statusAulaCor[a.status] || {};
          const primeiroAl = (a.nomeAluno || 'o aluno').split(' ')[0];
          // v140: a pergunta "vai?" ("✓ Bia vem" / "Perguntei às 21:04 · sem resposta") e o "nao veio"
          const legP = legendaPresencaProfSM(a, a.nomeAluno);
          const nvH = naoVeioSM(a);
          const nvHPend = nvH && nvH.pendente ? nvH : null;
          const cor = a.status === 'realizada' ? '#0E9F6E'
                    : ['cancelada_professor','cancelada_aluno','cancelada_forca_maior','falta'].includes(a.status) ? '#C7CAD1'
                    : nvHPend ? '#DC2626'
                    : estado === 'agora' ? '#2F6BFF'
                    : estado === 'esperando_aluno' ? '#1C43AE'
                    : estado === 'divergencia' ? '#DC2626'
                    : estado === 'aguardando' ? '#DC2626' : '#2F6BFF';
          const legenda = a.status === 'realizada' ? 'Aula dada'
                        : a.status === 'falta' ? 'Faltou'
                        : a.status?.startsWith('cancelada') ? (rotuloEspecialAula(a) ? `Não houve · ${rotuloEspecialAula(a)}` : 'Não houve')
                        : estado === 'agora' ? '● Em andamento'
                        : estado === 'esperando_aluno' ? (a.alunoUsaApp === false
                            ? `Registrada · vale sozinha em 24h (${primeiroAl} ainda não entrou no app)`
                            : `Registrada · aguardando ${primeiroAl} confirmar`)
                        : estado === 'divergencia' ? `${primeiroAl} registrou diferente — decida`
                        : estado === 'aguardando' ? 'Terminou · falta fechar'
                        : legP ? legP.txt
                        : 'Agendada';
          const corLegenda = estado === 'futura' && legP && a.status === 'agendada' ? legP.cor : cor;
          const futura = estado === 'futura';
          const salvando = atualizandoId === a.id;
          // Botoes aparecem DURANTE a aula tambem: o professor fecha na hora que acabou,
          // sem esperar a aula "vencer". Some quando ja' registrou e so' falta o aluno.
          // v131 (Etapa 9): aviso fora do prazo esperando o professor -> [Conta a aula] [Devolver]
          const pendAviso = avisoPendenteSM(a);
          // v156: tambem na meia hora antes do horario (o aluno chegou e a aula ja' comecou)
          const mostraBotoes = !pendAviso && (estado === 'agora' || estado === 'aguardando' || estado === 'divergencia'
            || (estado === 'futura' && quaseComecandoSM(a)));
          const abertoNH = hojeNaoHouveId === a.id;
          const linhaAviso = textoAvisoProfSM(a, a.nomeAluno);
          // "Aluno avisou" (WhatsApp/ligacao): aula de hoje que ainda nao comecou, sem aviso nem registro
          const podeRegistrarAviso = futura && a.status === 'agendada' && !avisoVigenteAula(a) && !a.desfecho_prof;
          const abertoAviso = avisoAbertoId === a.id;
          const cH = a.duracao || {};
          const semSaldoH = cH.tipo_cobranca === 'mensal' || !!cH.turma_id;
          return (
            <div key={a.id} id={'aula-' + a.id} style={{padding:'13px 0', borderTop: i ? '0.5px solid #F0F1F4' : 'none'}}>
            <div style={{display:'flex', gap:'13px'}}>
              <div style={{width:'42px', flexShrink:0}}>
                <div style={{fontSize:'13px', fontWeight:'700', color: futura ? '#16181D' : '#6E7480'}}>
                  {(a.hora || '').slice(0,5)}
                </div>
                <div style={{fontSize:'11px', color:'#9AA0AB'}}>
                  {(Number(a.duracao?.duracao_min) || 60) === 60 ? '1h' : (Number(a.duracao?.duracao_min) || 60) + ' min'}
                </div>
              </div>
              <div style={{width:'2px', borderRadius:'2px', background:cor, flexShrink:0}} />
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:'14px', fontWeight:'600', color:'#16181D',
                             overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.nomeAluno}</div>
                <div style={{fontSize:'12px', color:corLegenda, fontWeight: legP && futura ? '700' : '500'}}>{legenda}</div>
                {futura && legP && legP.semResposta && a.telefone && (
                  <button type="button" onClick={() => { uso.acao('presenca.whats', { origem: 'inicio_hoje', motivo: 'sem_resposta' });
                      compartilharOuWhats(waLinkSM(a.telefone, textoWhatsPerguntaSM(a, a.nomeAluno, professor?.nome, { semResposta: true })), 'presenca'); }}
                    style={{border:'none', background:'none', color:'#0A6F4D', fontSize:'12px', fontWeight:'700', padding:'0', minHeight:'44px', cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:'4px'}}>
                    <Ico n="brand-whatsapp" s={13} c="#0E9F6E" /> Chamar no WhatsApp
                  </button>
                )}
                {nvHPend && (
                  <div style={{fontSize:'11.5px', fontWeight:'700', color:'#991B1B', background:'#FEF2F2', borderRadius:'8px', padding:'5px 9px', marginTop:'4px', lineHeight:1.45}}>
                    <Ico n="user-exclamation" s={12} /> {primeiroAl} diz que você não veio · avisou {quandoRelSM(nvHPend.em)}
                    <div style={{fontWeight:'600', marginTop:'2px'}}>
                      {textoVoltaNaoVeioSM(nvHPend.voltaEm, { semSaldo: semSaldoH })}
                    </div>
                  </div>
                )}
                {linhaAviso && (
                  <div style={{fontSize:'11.5px', fontWeight:'600', color: a.aviso_no_prazo === false ? '#92400E' : '#6E7480', marginTop:'2px', lineHeight:1.4}}>
                    <Ico n="bell-ringing" s={12} /> {linhaAviso}
                  </div>
                )}
                {pendAviso && (
                  <BotoesDecidirAviso aula={a} nomeAluno={a.nomeAluno} origem="inicio_hoje" podeDevolver={!semSaldoH}
                    onFeito={() => setRecargaDash(n => n + 1)} />
                )}
                {/* v142 (P3): o aviso que EU registrei (fora do prazo, sem decisao) — desfazer enquanto o aluno nao responde */}
                {a.status === 'agendada' && avisoVigenteAula(a) && a.aviso_canal === 'professor_registrou' && !a.aviso_decisao && !alunoRespondeuDepoisDoAvisoSM(a) && (
                  <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: a.id, esperado: 'aviso_registrado' }} papel="professor" nomeOutro={a.nomeAluno}
                    onFeito={() => setRecargaDash(n => n + 1)} />
                )}
                {mostraBotoes && (
                  <>
                    <div style={{display:'flex', gap:'7px', marginTop:'9px'}}>
                      <button disabled={salvando} onClick={() => atualizarStatusHoje(a.id, 'realizada')}
                        style={{minHeight:'44px', border:'none', background:'#16181D', color:'white', fontSize:'12.5px', fontWeight:'600',
                                padding:'9px 14px', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit',
                                opacity: salvando ? 0.5 : 1}}>
                        <Ico n="check" s={14} /> Aula dada
                      </button>
                      <button disabled={salvando} onClick={() => setHojeNaoHouveId(abertoNH ? null : a.id)}
                        style={{minHeight:'44px', border: abertoNH ? '1.5px solid #9AA0AB' : '0.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'12.5px',
                                fontWeight:'600', padding:'9px 14px', borderRadius:'10px', cursor:'pointer',
                                fontFamily:'inherit', opacity: salvando ? 0.5 : 1}}>
                        Não houve
                      </button>
                    </div>
                    {abertoNH && (
                      <div style={{display:'flex', flexDirection:'column', gap:'6px', marginTop:'8px'}}>
                        {(nvH ? [
                          // v140: o aluno disse que voce nao veio — admitir vem primeiro (a aula volta ao pacote)
                          ['cancelada_professor', 'Eu não fui / desmarquei', semSaldoH ? 'mensalidade fixa — fica registrado' : `não desconta — a aula volta para o fim do pacote de ${primeiroAl}`],
                          ['falta', `${primeiroAl} faltou`, `${primeiroAl} diz que você não veio — fica registrado diferente e a aula não desconta até vocês acertarem`],
                          ['cancelada_aluno', `${primeiroAl} avisou antes`, `${primeiroAl} diz que você não veio — fica registrado "em desacordo"; ${semSaldoH ? 'mensalidade fixa' : cH.reposicao_permitida === false ? 'não desconta, sem reposição neste acordo' : 'não desconta — vai para o fim do pacote'}`],
                        ] : [
                          ['falta', `${primeiroAl} faltou`, semSaldoH ? 'a mensalidade corre — fica como falta' : a.alunoUsaApp === false ? 'conta como aula — vale sozinha em 24h' : `conta como aula — ${primeiroAl} confirma no app`],
                          ['cancelada_aluno', `${primeiroAl} avisou antes`, semSaldoH ? 'mensalidade fixa — não gera crédito' : cH.reposicao_permitida === false ? 'não desconta — este acordo não prevê reposição' : 'não desconta — a aula vai para o fim do pacote'],
                          ['cancelada_professor', 'Eu desmarquei', semSaldoH ? 'mensalidade fixa — nada muda' : 'não desconta — a aula vai para o fim do pacote'],
                        // v156: antes do horario marcado (meia hora antes) nao da' para dizer "faltou" — o aluno ainda pode
                        // chegar. Espelha o aluno: o "aconteceu" vale 30 min antes; o no-show, so' depois do inicio.
                        ]).filter(([st]) => !(futura && st === 'falta')).map(([st, t, d]) => (
                          <button key={st} disabled={salvando} onClick={() => atualizarStatusHoje(a.id, st)}
                            style={{textAlign:'left', border:'1px solid #E2E4E9', background:'#F7F8FA', borderRadius:'10px',
                                    padding:'9px 11px', cursor:'pointer', fontFamily:'inherit'}}>
                            <div style={{fontSize:'12.5px', fontWeight:'700', color:'#16181D'}}>{t}</div>
                            <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'1px'}}>{d}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {/* v142 (P1): no lugar do "Desfazer meu registro" (que reabria do zero pelo desfazer_desfecho_aula) —
                    o aluno ainda nao confirmou: desfaz so' o seu registro, com a confirmacao curta da previa */}
                {estado === 'esperando_aluno' && (
                  // revisao v142: depois de "Conta a aula" (aviso decidido) a ultima acao e' a decisao, nao o registro
                  <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: a.id, esperado: esperadoRegistroProfSM(a) }} papel="professor" nomeOutro={a.nomeAluno}
                    onFeito={() => setRecargaDash(n => n + 1)} />
                )}
              </div>
              {podeRegistrarAviso && (
                // v140: [Avisou falta] virou [Opcoes] — perguntar se vem, registrar o aviso, "nao vou poder"
                <button type="button" onClick={() => { if (!abertoAviso) uso.acao('opcoes_aula.abrir', { origem: 'inicio_hoje' }); setAvisoAbertoId(abertoAviso ? null : a.id); }}
                  aria-expanded={abertoAviso}
                  style={{flexShrink:0, alignSelf:'center', minHeight:'44px', border: abertoAviso ? '1.5px solid #9AA0AB' : '1px solid #E2E4E9', background:'white', color:'#3D424C',
                          fontSize:'12px', fontWeight:'700', borderRadius:'10px', padding:'0 10px', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap'}}>
                  <Ico n="dots" s={13} /> Opções
                </button>
              )}
            </div>
            {abertoAviso && podeRegistrarAviso && (
              <OpcoesAulaProfSM aula={a} nomeAluno={a.nomeAluno} alunoUsaApp={a.alunoUsaApp} telefone={a.telefone} nomeProf={professor?.nome}
                origem="inicio_hoje" onFechar={() => setAvisoAbertoId(null)}
                onFeito={patch => { setAvisoAbertoId(null);
                  if (patch) setAulasHoje(prev => prev.map(x => x.id === a.id ? { ...x, ...patch } : x));
                  else setRecargaDash(n => n + 1); }} />
            )}
            </div>
          );
        })}
      </div>

      {/* v131 (Etapa 9) — Amanhã: quem já avisou que não vem e, para o aviso que chegou pelo
          WhatsApp, o "Avisou falta" (o banco decide se foi no prazo). Só aula individual. */}
      {aulasAmanha.length > 0 && (
        <>
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', margin:'6px 2px 10px'}}>
            <span style={{fontFamily:"'Sora',sans-serif", fontSize:'15px', fontWeight:'600', color:'#16181D'}}>Amanhã</span>
            <span style={{fontSize:'12px', color:'#9AA0AB'}}>{aulasAmanha.filter(x => x.status === 'agendada').length} na agenda</span>
          </div>
          <div style={{background:'white', borderRadius:'16px', padding:'2px 14px', boxShadow:'0 1px 2px rgba(22,24,29,.05)'}}>
            {aulasAmanha.map((a, i) => {
              const cancelada = String(a.status || '').startsWith('cancelada');
              const pendAviso = avisoPendenteSM(a);
              const linhaAviso = textoAvisoProfSM(a, a.nomeAluno);
              const podeRegistrarAviso = a.status === 'agendada' && !avisoVigenteAula(a) && !a.desfecho_prof;
              const abertoAviso = avisoAbertoId === a.id;
              const cA = a.duracao || {};
              // v140: "✓ Bia vem" / "Perguntei às 21:04 · sem resposta" no lugar de "Agendada"
              const legP = !cancelada && a.status === 'agendada' ? legendaPresencaProfSM(a, a.nomeAluno) : null;
              const legenda = cancelada ? (rotuloEspecialAula(a) ? `Não vai ter · ${rotuloEspecialAula(a)}` : 'Não vai ter')
                            : a.status === 'realizada' ? 'Aula dada' : a.status === 'falta' ? 'Faltou' : legP ? legP.txt : 'Agendada';
              const cor = cancelada ? '#9AA0AB' : pendAviso ? '#D97706' : '#2F6BFF';
              return (
                <div key={a.id} id={'aula-' + a.id} style={{padding:'12px 0', borderTop: i ? '0.5px solid #F0F1F4' : 'none'}}>
                  <div style={{display:'flex', gap:'13px', alignItems:'center'}}>
                    <div style={{width:'42px', flexShrink:0, fontSize:'13px', fontWeight:'700', color: cancelada ? '#9AA0AB' : '#16181D'}}>{(a.hora || '').slice(0,5)}</div>
                    <div style={{width:'2px', alignSelf:'stretch', borderRadius:'2px', background:cor, flexShrink:0}} />
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:'14px', fontWeight:'600', color: cancelada ? '#6E7480' : '#16181D', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                                   textDecoration: cancelada ? 'line-through' : 'none'}}>{a.nomeAluno}</div>
                      <div style={{fontSize:'12px', color: legP ? legP.cor : cor, fontWeight: legP ? '700' : '500'}}>{legenda}</div>
                      {legP && legP.semResposta && a.telefone && (
                        <button type="button" onClick={() => { uso.acao('presenca.whats', { origem: 'inicio_amanha', motivo: 'sem_resposta' });
                            compartilharOuWhats(waLinkSM(a.telefone, textoWhatsPerguntaSM(a, a.nomeAluno, professor?.nome, { semResposta: true })), 'presenca'); }}
                          style={{border:'none', background:'none', color:'#0A6F4D', fontSize:'12px', fontWeight:'700', padding:'0', minHeight:'44px', cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:'4px'}}>
                          <Ico n="brand-whatsapp" s={13} c="#0E9F6E" /> Chamar no WhatsApp
                        </button>
                      )}
                      {linhaAviso && (
                        <div style={{fontSize:'11.5px', fontWeight:'600', color: a.aviso_no_prazo === false ? '#92400E' : '#6E7480', marginTop:'2px', lineHeight:1.4}}>
                          <Ico n="bell-ringing" s={12} /> {linhaAviso}
                        </div>
                      )}
                    </div>
                    {podeRegistrarAviso && (
                      // v140: [Avisou falta] virou [Opcoes]
                      <button type="button" onClick={() => { if (!abertoAviso) uso.acao('opcoes_aula.abrir', { origem: 'inicio_amanha' }); setAvisoAbertoId(abertoAviso ? null : a.id); }}
                        aria-expanded={abertoAviso}
                        style={{flexShrink:0, minHeight:'44px', border: abertoAviso ? '1.5px solid #9AA0AB' : '1px solid #E2E4E9', background:'white', color:'#3D424C',
                                fontSize:'12px', fontWeight:'700', borderRadius:'10px', padding:'0 10px', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap'}}>
                        <Ico n="dots" s={13} /> Opções
                      </button>
                    )}
                  </div>
                  {pendAviso && (
                    <BotoesDecidirAviso aula={a} nomeAluno={a.nomeAluno} origem="inicio_amanha"
                      podeDevolver={!(cA.tipo_cobranca === 'mensal' || cA.turma_id)} onFeito={() => setRecargaDash(n => n + 1)} />
                  )}
                  {/* v142 (P3): o mesmo do cartao de hoje */}
                  {a.status === 'agendada' && avisoVigenteAula(a) && a.aviso_canal === 'professor_registrou' && !a.aviso_decisao && !alunoRespondeuDepoisDoAvisoSM(a) && (
                    <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: a.id, esperado: 'aviso_registrado' }} papel="professor" nomeOutro={a.nomeAluno}
                      onFeito={() => setRecargaDash(n => n + 1)} />
                  )}
                  {abertoAviso && podeRegistrarAviso && (
                    <OpcoesAulaProfSM aula={a} nomeAluno={a.nomeAluno} alunoUsaApp={a.alunoUsaApp} telefone={a.telefone} nomeProf={professor?.nome}
                      origem="inicio_amanha" onFechar={() => setAvisoAbertoId(null)}
                      onFeito={patch => { setAvisoAbertoId(null);
                        if (patch) setAulasAmanha(prev => prev.map(x => x.id === a.id ? { ...x, ...patch } : x));
                        else setRecargaDash(n => n + 1); }} />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {feriadoAlerta && (() => {
        const d = new Date(feriadoAlerta.data + 'T12:00:00');
        const quando = d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
        const n = feriadoAlerta.aulas.length;
        return (
          <div style={{background:'#FFFBEB', border:'2px solid #FDE68A', borderRadius:'16px', padding:'14px 16px'}}>
            <p style={{fontSize:'11px', fontWeight:'800', color:'#92400E', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'6px'}}>
              <Ico n="calendar-off" s={13} c="#D97706" /> Feriado chegando
            </p>
            <p style={{fontSize:'15px', fontWeight:'700', color:'#16181D', lineHeight:1.4}}>
              {quando.charAt(0).toUpperCase() + quando.slice(1)} é {feriadoAlerta.nome}.
              {' '}{n === 1 ? '1 aula cai' : n + ' aulas caem'} nesse dia.
            </p>
            <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'6px'}}>
              {feriadoAlerta.aulas.map(a => `${String(a.hora || '').slice(0,5)} ${a.nome.split(' ')[0]}`).join(' · ')}
            </div>
            <p style={{fontSize:'12px', color:'#92400E', marginTop:'8px', lineHeight:1.5}}>
              Você decide só para este feriado, e os alunos são avisados.{' '}
              {/* 02/10: o texto dizia "fim do pacote" tambem para mensalidade, onde a aula so' nao acontece. */}
              {feriadoAlerta.aulas.every(a => a.mensal) ? 'Se não der aula, a aula não acontece e a mensalidade não muda.'
                : feriadoAlerta.aulas.some(a => a.mensal) ? 'Se não der aula: no pacote, a aula vai para o fim — ninguém perde aula; na mensalidade, o valor não muda.'
                : 'Se não der aula, cada uma vai para o fim do pacote — ninguém perde aula.'}
            </p>
            <div style={{display:'flex', gap:'7px', marginTop:'10px', flexWrap:'wrap'}}>
              <button disabled={resolvendoFeriado} onClick={() => resolverFeriado(false)}
                style={{flex:1, padding:'11px 12px', borderRadius:'10px', border:'none', background:'#16181D', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                Vou dar aula
              </button>
              <button disabled={resolvendoFeriado} onClick={() => resolverFeriado(true)}
                style={{flex:1, padding:'11px 12px', borderRadius:'10px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                {resolvendoFeriado ? 'Movendo…' : 'Não vou dar aula'}
              </button>
            </div>
          </div>
        );
      })()}

      {pedidoSumido && (
        <div style={{background:'#F4F5F7', border:'1px solid #E2E4E9', borderRadius:'14px', padding:'12px 14px', fontSize:'13px', color:'#3D424C'}}>
          Esse pedido já foi respondido. Nada a fazer aqui.
        </div>
      )}

      {/* Pedidos dos alunos. Aceitar move a aula de verdade — nao e' so' um recado. */}
      {aulasPropostas.length > 0 && (
        <div style={{background:'white', borderRadius:'16px', padding:'14px 16px', boxShadow:'0 1px 2px rgba(22,24,29,.05)', border:'1px solid #C2D4FF'}}>
          <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px'}}>
            <Ico n="calendar-plus" s={17} c="#2F6BFF" />
            <span style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>
              {aulasPropostas.length === 1 ? '1 aula combinada para confirmar' : aulasPropostas.length + ' aulas combinadas para confirmar'}
            </span>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {aulasPropostas.map(a => {
              const ocupado = respondendoProposta === a.id;
              const quando = new Date(a.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'long', day:'numeric', month:'long' });
              return (
                <div key={a.id} style={{background:'#F7F9FF', border:'1px dashed #C2D4FF', borderRadius:'12px', padding:'10px 12px'}}>
                  <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D'}}>{a.nome} adicionou uma aula</div>
                  <div style={{fontSize:'12.5px', color:'#3D424C', marginTop:'2px'}}>{quando} às {String(a.hora||'').slice(0,5)}{a.observacao ? ` · "${a.observacao}"` : ''}</div>
                  <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'2px'}}>Combinaram fora do app? Confirme e ela entra na agenda dos dois.</div>
                  <div style={{display:'flex', gap:'7px', marginTop:'9px'}}>
                    <button disabled={ocupado} onClick={() => responderProposta(a, true)}
                      style={{flex:1, minHeight:'44px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'13px', borderRadius:'10px', padding:'11px', cursor:'pointer', fontFamily:'inherit'}}>
                      {ocupado ? '…' : 'Confirmar'}
                    </button>
                    <button disabled={ocupado} onClick={() => responderProposta(a, false)}
                      style={{flex:1, minHeight:'44px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontWeight:'700', fontSize:'13px', borderRadius:'10px', padding:'11px', cursor:'pointer', fontFamily:'inherit'}}>
                      Não combinamos isso
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!(stats.alunosAtivos === 0 && aulasPendentes.length === 0) && cartaoPedidos}

      {esperandoLink.length > 0 && (
        <button type="button" data-esperando-link="1" onClick={() => { uso.acao('inicio.esperando_completar', { n: esperandoLink.length }); onNavigate && onNavigate('alunos'); }}
          style={{display:'flex', alignItems:'center', gap:'10px', width:'100%', textAlign:'left', background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'16px',
                  padding:'12px 14px', minHeight:'44px', cursor:'pointer', fontFamily:'inherit'}}>
          <Ico n="hourglass" s={17} c="#D97706" />
          <span style={{flex:1, minWidth:0}}>
            <span style={{display:'block', fontSize:'13px', fontWeight:'800', color:'#16181D'}}>Esperando você completar ({esperandoLink.length})</span>
            <span style={{display:'block', fontSize:'12px', color:'#6E7480', marginTop:'2px', lineHeight:1.4}}>
              {esperandoLink.slice(0, 3).map(a => primeiroNomeSM(a.nome, 'Aluno')).join(', ')}{esperandoLink.length > 3 ? ' e outros' : ''}
              {esperandoLink.length === 1 ? ' entrou' : ' entraram'} pelo seu link. Toque para escolher o pacote e os dias.
            </span>
          </span>
          <Ico n="chevron-right" s={18} c="#2F6BFF" />
        </button>
      )}

      {pedidos.length > 0 && (
        <div id="bloco-pedidos" style={{background:'white', borderRadius:'16px', padding:'14px 16px',
                     boxShadow:'0 1px 2px rgba(22,24,29,.05)', border:'1px solid #C2D4FF'}}>
          <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px'}}>
            <Ico n="message-2" s={17} c="#2F6BFF" />
            <span style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>
              {pedidos.length === 1 ? '1 pedido de aluno' : pedidos.length + ' pedidos de alunos'}
            </span>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {pedidos.map(s => {
              const nome = s.alunos?.nome || 'Aluno';
              // Pedido de correcao no acordo nao tem De -> Para: e' o aluno dizendo que um
              // numero esta' errado. Aceitar/recusar nao resolve — o professor tem de abrir
              // a ficha e corrigir. Entao este cartao tem outra cara e outro botao.
              // Pausa: um pedido cobre varias aulas. "Pausar o pacote" e' o trancamento das
              // academias — as aulas do periodo saem, o saldo nao anda, a renovacao desloca.
              if (s.tipo === 'pausa') {
                const ocupadoP = !!respondendoId;   // v131: o proprio pedido tambem trava (toque duplo)
                const fmt = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }) : '?';
                const mensalP = s.acordoPausa?.tipo_cobranca === 'mensal';
                return (
                  <div key={s.id} style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'10px 12px'}}>
                    <div style={{fontSize:'11px', fontWeight:'800', color:'#1C43AE', textTransform:'uppercase', letterSpacing:'.04em'}}>
                      <Ico n="plane-departure" s={12} c="#2F6BFF" /> Vai se ausentar
                    </div>
                    <div style={{fontSize:'13px', fontWeight:'700', color:'#16181D', marginTop:'4px'}}>{nome}</div>
                    <div style={{fontSize:'13px', color:'#3D424C', marginTop:'3px'}}>
                      De <strong>{fmt(s.periodo_inicio)}</strong> a <strong>{fmt(s.periodo_fim)}</strong>{s.mensagem ? ` · ${s.mensagem.replace(/^Vou pausar de .*? — /, '')}` : ''}
                    </div>
                    <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'6px', lineHeight:1.5}}>
                      {mensalP
                        ? 'Na mensalidade, pausar cancela as aulas do período (a mensalidade não muda). Esse horário fica livre para outro aluno.'
                        : 'Pausar tira as aulas do período e põe as mesmas no fim — o saldo não anda, a renovação desloca. Esse horário fica livre para outro aluno.'}
                    </div>
                    <div style={{display:'flex', gap:'6px', marginTop:'9px', flexWrap:'wrap'}}>
                      <button disabled={ocupadoP} onClick={() => comTravaAulaSM('pausa:' + s.id, async () => {
                          setRespondendoId(s.id);
                          let data = null, error = null;
                          try { ({ data, error } = await supabase.rpc('pausar_pacote', { p_solicitacao_id: s.id })); } catch (e) { error = e; }
                          if (error) { uso.erro('erro.pausa_pacote', error.message || String(error)); toastSM('Não foi possível pausar agora. Confira a internet e tente de novo.'); setRespondendoId(null); return; }
                          if (!data || data.erro || !data.ok) {
                            const e0 = String((data && data.erro) || '');
                            uso.bloqueio('pausa_pacote', null, null, e0 === 'sem contrato ativo' ? 'sem_acordo' : e0 === 'sem permissao' ? 'sem_permissao' : e0 === 'pedido nao encontrado' ? 'pedido_sumiu' : 'outro');
                            toastSM(e0 === 'sem contrato ativo' ? 'Este aluno não tem acordo ativo — não há aulas para pausar.'
                              : e0 === 'pedido nao encontrado' ? 'Este pedido não existe mais. Atualize a tela.'
                              : e0 === 'sem permissao' ? 'Este pedido não é de um aluno seu. Atualize a tela.'
                              : 'Não foi possível pausar agora. Tente de novo em instantes.');
                            setRespondendoId(null); return;
                          }
                          uso.acao('pedido.responder', { decisao: 'pausou', tipo: 'pausa' });
                          avisarPendenciasMudouSM();
                          // v131: na mensalidade o banco CANCELA as aulas do periodo (nao vao para o fim)
                          const nP = (data?.aulas_movidas || 0) + (data?.mensal ? (data?.dias_futuros_cancelados || 0) : 0);
                          const nPtxt = `${nP} aula${nP === 1 ? '' : 's'}`;
                          await supabase.from('notificacoes').insert({
                            aluno_id: s.aluno_id, professor_id: professor.id, tipo: 'pausa_aceita',
                            mensagem: data?.mensal
                              ? `${professor?.nome?.split(' ')[0] || 'Seu professor'} pausou suas aulas de ${fmt(s.periodo_inicio)} a ${fmt(s.periodo_fim)}. ${nPtxt} do período ${nP === 1 ? 'ficou cancelada' : 'ficaram canceladas'} (a mensalidade não muda).`
                              : `${professor?.nome?.split(' ')[0] || 'Seu professor'} pausou seu pacote de ${fmt(s.periodo_inicio)} a ${fmt(s.periodo_fim)}. ${data?.aulas_movidas || 0} aula${data?.aulas_movidas === 1 ? '' : 's'} foram para o fim — você não perde nenhuma.`,
                          });
                          toastSM(data?.mensal
                            ? `Pausado. ${nPtxt} do período ${nP === 1 ? 'ficou cancelada' : 'ficaram canceladas'} (a mensalidade não muda).`
                            : `Pausado. ${data?.aulas_movidas || 0} aulas movidas para o fim do pacote.`);
                          setRespondendoId(null); setPedidos(prev => prev.filter(p => p.id !== s.id));
                        })}
                        style={{minHeight:'44px', padding:'8px 14px', borderRadius:'9px', border:'none', background:'#2F6BFF', color:'white',
                                fontSize:'12px', fontWeight:'800', cursor:'pointer', opacity: ocupadoP && respondendoId !== s.id ? .5 : 1}}>
                        {respondendoId === s.id ? 'Pausando…' : mensalP ? 'Pausar as aulas' : 'Pausar o pacote'}
                      </button>
                      <button disabled={ocupadoP} onClick={() => responderPedido(s, 'recusada')}
                        style={{minHeight:'44px', padding:'8px 14px', borderRadius:'9px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480',
                                fontSize:'12px', fontWeight:'700', cursor:'pointer', opacity: ocupadoP ? .5 : 1}}>
                        Vamos combinar
                      </button>
                    </div>
                  </div>
                );
              }
              if (s.tipo === 'ajuste_contrato') {
                const ocupadoA = respondendoId && respondendoId !== s.id;
                return (
                  <div key={s.id} style={{background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'12px', padding:'10px 12px'}}>
                    <div style={{fontSize:'11px', fontWeight:'800', color:'#92400E', textTransform:'uppercase', letterSpacing:'.04em'}}>
                      <Ico n="alert-triangle" s={12} c="#D97706" /> Correção no acordo
                    </div>
                    <div style={{fontSize:'13px', fontWeight:'700', color:'#16181D', marginTop:'4px'}}>{nome}</div>
                    <div style={{fontSize:'13px', color:'#3D424C', marginTop:'3px', fontStyle:'italic'}}>"{s.mensagem}"</div>
                    <div style={{fontSize:'11.5px', color:'#92400E', marginTop:'6px'}}>
                      O acordo dele está parado até você corrigir.
                    </div>
                    <div style={{display:'flex', gap:'6px', marginTop:'9px', flexWrap:'wrap'}}>
                      <button disabled={ocupadoA} onClick={() => { uso.acao('ajuste.abrir_ficha'); onNavigate && onNavigate('alunos'); }}
                        style={{padding:'8px 14px', borderRadius:'9px', border:'none', background:'#D97706', color:'white',
                                fontSize:'12px', fontWeight:'800', cursor:'pointer', opacity: ocupadoA ? .5 : 1}}>
                        Abrir ficha e corrigir
                      </button>
                      <button disabled={ocupadoA} onClick={() => responderPedido(s, 'recusada')}
                        style={{padding:'8px 14px', borderRadius:'9px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480',
                                fontSize:'12px', fontWeight:'700', cursor:'pointer', opacity: ocupadoA ? .5 : 1}}>
                        Está certo como está
                      </button>
                    </div>
                  </div>
                );
              }
              const de = s.aulas?.data
                ? `${new Date(s.aulas.data+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'short',day:'numeric',month:'short'})} às ${String(s.aulas.hora||'').slice(0,5)}`
                : '—';
              const para = s.data_sugerida
                ? `${new Date(s.data_sugerida+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'short',day:'numeric',month:'short'})}${s.hora_sugerida ? ' às ' + String(s.hora_sugerida).slice(0,5) : ''}`
                : 'sem dia definido';
              const aberto = respondendoId === s.id;
              const ocupado = respondendoId && respondendoId !== s.id;
              return (
                <div key={s.id} style={{background: pedidoDestacado === s.id ? '#E7EDFF' : '#F7F9FF',
                                        border: pedidoDestacado === s.id ? '2px solid #2F6BFF' : '2px solid transparent',
                                        borderRadius:'12px', padding:'10px 12px'}}>
                  {pedidoDestacado === s.id ? (
                    <div style={{fontSize:'11px', fontWeight:'800', color:'#2F6BFF', marginBottom:'4px'}}>
                      <Ico n="arrow-down" s={12} /> É este o pedido do link
                    </div>
                  ) : null}
                  <div style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>{nome}</div>
                  <div style={{fontSize:'12px', color:'#3D424C', marginTop:'3px'}}>
                    {de} <Ico n="arrow-right" s={12} c="#2F6BFF" /> <strong style={{color:'#2F6BFF'}}>{para}</strong>
                  </div>
                  {s.mensagem ? <div style={{fontSize:'12px', color:'#6E7480', marginTop:'3px', fontStyle:'italic'}}>"{s.mensagem}"</div> : null}
                  <div style={{display:'flex', gap:'6px', marginTop:'8px', flexWrap:'wrap'}}>
                    <button disabled={ocupado} onClick={() => responderPedido(s, 'aceita')}
                      style={{minHeight:'44px', padding:'7px 12px', borderRadius:'9px', border:'none', background:'#0E9F6E', color:'white',
                              fontSize:'12px', fontWeight:'700', cursor:'pointer', opacity: ocupado ? .5 : 1}}>
                      <Ico n="check" s={13} /> Aceitar
                    </button>
                    <button disabled={ocupado} onClick={() => { setRespondendoId(aberto ? null : s.id); setContra({ data: s.data_sugerida || '', hora: String(s.hora_sugerida || s.aulas?.hora || '').slice(0, 5) }); }}
                      style={{minHeight:'44px', padding:'7px 12px', borderRadius:'9px', border:'1.5px solid #C2D4FF', background:'white', color:'#1C43AE',
                              fontSize:'12px', fontWeight:'700', cursor:'pointer', opacity: ocupado ? .5 : 1}}>
                      Propor outro
                    </button>
                    <button disabled={ocupado} onClick={() => responderPedido(s, 'recusada')}
                      style={{minHeight:'44px', padding:'7px 12px', borderRadius:'9px', border:'1.5px solid #FECACA', background:'white', color:'#991B1B',
                              fontSize:'12px', fontWeight:'700', cursor:'pointer', opacity: ocupado ? .5 : 1}}>
                      Não consigo
                    </button>
                  </div>
                  {aberto ? (
                    <div style={{display:'flex', gap:'6px', marginTop:'8px', flexWrap:'wrap', alignItems:'center'}}>
                      <input type="date" value={contra.data} onChange={e => setContra(c => ({ ...c, data: e.target.value }))}
                        style={{padding:'7px 10px', borderRadius:'9px', border:'1.5px solid #E2E4E9', fontSize:'13px'}} />
                      <input type="time" value={contra.hora} onChange={e => setContra(c => ({ ...c, hora: e.target.value }))}
                        style={{padding:'7px 10px', borderRadius:'9px', border:'1.5px solid #E2E4E9', fontSize:'13px'}} />
                      <button onClick={() => responderPedido(s, 'contraproposta')}
                        style={{minHeight:'44px', padding:'7px 12px', borderRadius:'9px', border:'none', background:'#2F6BFF', color:'white',
                                fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>Enviar proposta</button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* v136: o aluno nao consegue no horario que voce remarcou — junto dos pedidos, mesmo desenho */}
      {remarcRecusadas.length > 0 && (
        <div id="bloco-remarc-recusadas" style={{background:'white', borderRadius:'16px', padding:'14px 16px',
                     boxShadow:'0 1px 2px rgba(22,24,29,.05)', border:'1px solid #FDE68A'}}>
          <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px'}}>
            <Ico n="calendar-x" s={17} c="#D97706" />
            <span style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>
              {remarcRecusadas.length === 1 ? '1 aluno não consegue no novo horário' : remarcRecusadas.length + ' alunos não conseguem no novo horário'}
            </span>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {remarcRecusadas.map(s => {
              // passou do horario sem registro: o caminho e' "Eu desmarquei" (nao "falta" de quem avisou)
              const passou = aulaComecouSM(s.aula);
              const quandoTxt = minuscSM(quandoCurtoSM(s.resposta_data, s.resposta_hora));
              const btPrim = {minHeight:'44px', padding:'7px 12px', borderRadius:'9px', border:'none', background:'#2F6BFF', color:'white',
                              fontSize:'12px', fontWeight:'700', cursor:'pointer'};
              const btSec = {minHeight:'44px', padding:'7px 12px', borderRadius:'9px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C',
                             fontSize:'12px', fontWeight:'700', cursor:'pointer'};
              return (
                <div key={s.id} style={{background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'12px', padding:'10px 12px'}}>
                  <div style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>{s.nome}</div>
                  <div className="num" style={{fontSize:'12px', color:'#3D424C', marginTop:'3px'}}>
                    {passou ? `Avisou que não conseguia ${quandoTxt}` : `Não consegue ${quandoTxt}`}
                  </div>
                  {s.resposta ? <div style={{fontSize:'12px', color:'#6E7480', marginTop:'3px', fontStyle:'italic', overflowWrap:'anywhere'}}>"{s.resposta}"</div> : null}
                  <div style={{fontSize:'11.5px', color:'#92400E', marginTop:'6px'}}>
                    {passou ? 'A aula ficou sem registro. Se não aconteceu, registre "Eu desmarquei" — não desconta.'
                            : 'A aula continua nesse horário até você remarcar ou desmarcar.'}
                  </div>
                  {passou ? (
                    <div style={{display:'flex', gap:'6px', marginTop:'8px', flexWrap:'wrap'}}>
                      <button onClick={() => abrirRemarcRecusada(s, 'desmarcar')} style={btPrim}>Eu desmarquei</button>
                      <button onClick={() => abrirRemarcRecusada(s, 'remarcar')} style={btSec}><Ico n="calendar-event" s={13} /> Remarcar</button>
                    </div>
                  ) : (
                    <div style={{display:'flex', gap:'6px', marginTop:'8px', flexWrap:'wrap'}}>
                      <button onClick={() => abrirRemarcRecusada(s, 'remarcar')} style={btPrim}><Ico n="calendar-event" s={13} /> Remarcar</button>
                      <button onClick={() => abrirRemarcRecusada(s, 'desmarcar')} style={btSec}>Desmarcar</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reposicoes devidas: a conversa acontece no WhatsApp; o app guarda o compromisso.
          Cada uma tem validade — passou, vira desconto ou expira, conforme o contrato. */}
      {reposicoes.length > 0 && (
        <div style={{background:'white', borderRadius:'16px', padding:'14px 16px',
                     boxShadow:'0 1px 2px rgba(22,24,29,.05)', border:'1px solid #FDE68A'}}>
          <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px'}}>
            <Ico n="calendar-plus" s={17} c="#D97706" />
            <span style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>
              {reposicoes.length === 1 ? '1 reposição a combinar' : reposicoes.length + ' reposições a combinar'}
            </span>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
            {reposicoes.map(r => {
              const nome = r.alunos?.nome || 'Aluno';
              const fone = (r.alunos?.telefone || '').replace(/\D/g, '');
              const venc = new Date(r.validade + 'T12:00:00').toLocaleDateString('pt-BR', {day:'numeric', month:'short'});
              const motivoTxt = r.motivo === 'forca_maior' ? 'chuva/imprevisto' : r.motivo === 'professor' ? 'você desmarcou' : 'avisou no prazo';
              const msg = `Oi, ${nome.split(' ')[0]}! Ficou uma aula pra repor (${motivoTxt}). Que dia fica bom pra você? Tenho até ${venc} pra encaixar.`;
              const wa = `https://wa.me/${fone ? '55' + fone.replace(/^55/, '') : ''}?text=${encodeURIComponent(msg)}`;
              const aberto = marcandoId === r.id;
              return (
                <div key={r.id} style={{background:'#FFFBEB', borderRadius:'12px', padding:'10px 12px'}}>
                  <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:'13.5px', fontWeight:'700', color:'#16181D'}}>{nome}</div>
                      <div style={{fontSize:'12px', color:'#92400E'}}>{motivoTxt} · repor até {venc}</div>
                    </div>
                    <a href={wa} target="_blank" rel="noopener" onClick={() => uso.acao('whatsapp.abrir', { motivo: 'reposicao', com_numero: !!fone, via: 'link' })}
                       style={{border:'none', background:'#25D366', color:'white', fontWeight:'700', fontSize:'12px',
                               borderRadius:'9px', padding:'8px 10px', textDecoration:'none', whiteSpace:'nowrap'}}>
                      <Ico n="brand-whatsapp" s={14} /> Combinar
                    </a>
                    <button onClick={() => { setMarcandoId(aberto ? null : r.id); setMarcar({ data:'', hora:'' }); }}
                      style={{border:'none', background:'#16181D', color:'white', fontWeight:'700', fontSize:'12px',
                              borderRadius:'9px', padding:'8px 10px', cursor:'pointer', whiteSpace:'nowrap'}}>
                      Marcar
                    </button>
                  </div>
                  {aberto && (
                    <div style={{display:'flex', gap:'8px', marginTop:'10px', alignItems:'center', flexWrap:'wrap'}}>
                      <input type="date" value={marcar.data} onChange={e => setMarcar({...marcar, data: e.target.value})}
                        style={{padding:'9px 10px', borderRadius:'9px', border:'1px solid #E2E4E9', fontSize:'14px', fontFamily:'inherit'}} />
                      <input type="time" value={marcar.hora} onChange={e => setMarcar({...marcar, hora: e.target.value})}
                        style={{padding:'9px 10px', borderRadius:'9px', border:'1px solid #E2E4E9', fontSize:'14px', fontFamily:'inherit'}} />
                      <button onClick={() => marcarReposicao(r)}
                        style={{border:'none', background:'#0E9F6E', color:'white', fontWeight:'700', fontSize:'12.5px',
                                borderRadius:'9px', padding:'9px 12px', cursor:'pointer'}}>
                        <Ico n="check" s={13} /> Confirmar
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Promocao: a barra que anda toda vez que o professor confirma uma aula e marca
          um pagamento. Recompensa invisivel nao muda comportamento; barra muda. */}
      {/* U12: a promocao e' preparacao para o lancamento — nao vale no piloto. Mostrar
          "faltam 10 aulas para uma de volta" cria expectativa que ninguem vai honrar. O
          motor e as tabelas continuam; a tela cala ate' o admin ligar no Painel do Piloto. */}
      {promo && promo.promocao && promo.promocao.visivel_no_app === true && (() => {
        const p = promo.promocao;
        const porAulas = p.regra === 'a_cada_aulas';
        const meta = Number(p.gatilho);
        const atual = porAulas ? promo.contadas : promo.valorContado;
        const pct = meta > 0 ? Math.min(100, Math.round((atual / meta) * 100)) : 0;
        const falta = Math.max(0, meta - atual);
        const fmtR = v => 'R$ ' + Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
        const premio = p.recompensa_tipo === 'valor_fixo' ? fmtR(p.recompensa_valor)
                     : p.recompensa_tipo === 'percentual' ? p.recompensa_valor + '% de volta'
                     : 'uma aula de volta';
        return (
          <div style={{background:'white', borderRadius:'16px', padding:'14px 16px',
                       boxShadow:'0 1px 2px rgba(22,24,29,.05)', border:'1px solid #C2D4FF'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px'}}>
              <div style={{display:'flex', alignItems:'center', gap:'8px', minWidth:0}}>
                <Ico n="gift" s={18} c="#2F6BFF" />
                <span style={{fontSize:'13px', fontWeight:'700', color:'#16181D', overflow:'hidden',
                              textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{p.nome}</span>
              </div>
              <span style={{fontSize:'12px', fontWeight:'700', color:'#2F6BFF', whiteSpace:'nowrap'}}>
                {porAulas ? `${atual} de ${meta}` : `${fmtR(atual)} de ${fmtR(meta)}`}
              </span>
            </div>
            <div style={{height:'8px', background:'#E7EDFF', borderRadius:'8px', marginTop:'10px', overflow:'hidden'}}>
              <div style={{height:'100%', width: pct + '%', background:'#2F6BFF', borderRadius:'8px',
                           transition:'width .4s ease'}} />
            </div>
            <div style={{display:'flex', justifyContent:'space-between', gap:'10px', marginTop:'8px'}}>
              <span style={{fontSize:'12px', color:'#6E7480'}}>
                {falta > 0
                  ? (porAulas ? `Falta${falta === 1 ? '' : 'm'} ${falta} aula${falta === 1 ? '' : 's'} para ${premio}`
                              : `Faltam ${fmtR(falta)} para ${premio}`)
                  : 'Meta batida — a recompensa entra na próxima virada'}
              </span>
              {promo.totalGanho > 0 && (
                <span style={{fontSize:'12px', fontWeight:'700', color:'#0E9F6E', whiteSpace:'nowrap'}}>
                  {fmtR(promo.totalGanho)} ganhos
                </span>
              )}
            </div>
            {promo.aPagar > 0 && (
              <div style={{fontSize:'11.5px', color:'#0A6F4D', background:'#EFFAF5', borderRadius:'8px',
                           padding:'7px 10px', marginTop:'9px'}}>
                {fmtR(promo.aPagar)} a caminho da sua chave Pix
              </div>
            )}
          </div>
        );
      })()}

      {/* Dinheiro: dois numeros, sem gradiente. Numero grande fala mais alto que cor. */}
      <div style={{display:'flex', gap:'10px', marginTop:'14px'}}>
        <div onClick={() => { uso.acao('inicio.ir_financeiro'); onNavigate && onNavigate('financeiro'); }}
             style={{flex:1, background:'white', borderRadius:'16px', padding:'14px', cursor:'pointer',
                     boxShadow:'0 1px 2px rgba(22,24,29,.05)'}}>
          <Ico n="arrow-down-left" s={17} c="#0E9F6E" />
          <div style={{fontFamily:"'Sora',sans-serif", fontSize:'18px', fontWeight:'700', color:'#16181D', marginTop:'7px'}}>
            R$ {stats.receita.toLocaleString('pt-BR', {maximumFractionDigits:0})}
          </div>
          <div style={{fontSize:'11px', color:'#6E7480', marginTop:'2px'}}>recebido no mês</div>
        </div>
        <div onClick={() => { uso.acao('inicio.ir_financeiro'); onNavigate && onNavigate('financeiro'); }}
             style={{flex:1, background:'white', borderRadius:'16px', padding:'14px', cursor:'pointer',
                     boxShadow:'0 1px 2px rgba(22,24,29,.05)'}}>
          <Ico n="clock" s={17} c="#D97706" />
          <div style={{fontFamily:"'Sora',sans-serif", fontSize:'18px', fontWeight:'700', color:'#16181D', marginTop:'7px'}}>
            R$ {stats.receitaPendente.toLocaleString('pt-BR', {maximumFractionDigits:0})}
          </div>
          <div style={{fontSize:'11px', color:'#6E7480', marginTop:'2px'}}>em aberto</div>
        </div>
      </div>

      {}
      {/* Quatro mini-cartoes de 10px viraram uma linha de texto: e' contexto, nao acao. */}
      <div className="plen-card num" style={{padding:'12px 16px', display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', fontSize:'13px', color:'#3D424C'}}>
        <span style={{fontWeight:'800', color:'#6E7480', fontSize:'11px', textTransform:'uppercase', letterSpacing:'.06em', width:'100%'}}>Esta semana</span>
        <span><strong style={{color:'#2F6BFF'}}>{stats.aulasSemanais}</strong> agendada{stats.aulasSemanais === 1 ? '' : 's'}</span>
        <span style={{color:'#C7CAD1'}}>·</span>
        <span><strong style={{color:'#0E9F6E'}}>{stats.aulasRealizadas}</strong> feita{stats.aulasRealizadas === 1 ? '' : 's'}</span>
        <span style={{color:'#C7CAD1'}}>·</span>
        <span><strong style={{color: stats.aulasCanceladas ? '#DC2626' : '#3D424C'}}>{stats.aulasCanceladas}</strong> cancelada{stats.aulasCanceladas === 1 ? '' : 's'}</span>
        {stats.aulasRealizadas + stats.aulasCanceladas > 0 && (<>
          <span style={{color:'#C7CAD1'}}>·</span>
          <span>presença <strong style={{color: stats.taxaPresenca>=80?'#0E9F6E':stats.taxaPresenca>=60?'#D97706':'#DC2626'}}>{stats.taxaPresenca}%</strong></span>
        </>)}
      </div>

      {}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-start justify-between mb-4 gap-3" style={{flexWrap:'wrap'}}>
          <div style={{minWidth:'160px'}}>
            <p className="text-sm font-bold text-gray-800"><Ico n="coin" /> Faturamento · 6 meses</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {modoFat === 'bruto' ? 'Bruto: total pago pelos alunos' : 'Líquido: descontada taxa da academia por aula'}
            </p>
          </div>
          {}
          <div style={{display:'flex', gap:'2px', background:'#F0F1F4', borderRadius:'8px', padding:'3px'}}>
            {[{v:'bruto',l:'Bruto'},{v:'liquido',l:'Líquido'}].map(o => (
              <button key={o.v} onClick={() => { uso.acao('inicio.faturamento', { modo: o.v }); setModoFat(o.v); }}
                style={{padding:'4px 12px', borderRadius:'6px', border:'none', fontSize:'11px', fontWeight:'700', cursor:'pointer',
                  background: modoFat===o.v ? 'white' : 'transparent',
                  color: modoFat===o.v ? '#2F6BFF' : '#9AA0AB',
                  boxShadow: modoFat===o.v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition:'all 0.15s'}}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-6">Sem dados</p>
        ) : (() => {
          const vals = chartData.map(d => modoFat === 'bruto' ? d.bruto : d.liquido);
          const maxVal = Math.max(...vals, 1);
          const totalAtual = vals[vals.length - 1] || 0;
          const totalAnterior = vals[vals.length - 2] || 0;
          const delta = totalAnterior > 0 ? ((totalAtual - totalAnterior) / totalAnterior * 100).toFixed(0) : null;
          const W=480, H=100, padX=36, padY=8, barW=40;
          const slotW = (W-padX*2)/chartData.length;
          return (
            <div>
              {}
              <div className="flex items-baseline gap-2 mb-4">
                <span style={{fontSize:'28px', fontWeight:'800', color:'#2F6BFF', lineHeight:1}}>
                  R$ {totalAtual.toLocaleString('pt-BR', {minimumFractionDigits:0, maximumFractionDigits:0})}
                </span>
                <span className="text-xs text-gray-400">mês atual</span>
                {delta !== null && (
                  <span style={{fontSize:'12px', fontWeight:'700', color: Number(delta) >= 0 ? '#0E9F6E' : '#DC2626',
                    background: Number(delta) >= 0 ? '#DCF5EC' : '#FEE2E2', padding:'2px 8px', borderRadius:'20px'}}>
 {Number(delta) >= 0 ? '': ''} {Math.abs(delta)}% vs mês ant.
                  </span>
                )}
              </div>
              <svg viewBox={`0 0 ${W} ${H+28}`} style={{width:'100%', overflow:'visible'}}>
                {[0, 0.5, 1].map((t, i) => {
                  const y = padY + (1-t)*H;
                  const label = t === 0 ? '' : `R$${Math.round(t*maxVal/1000)}k`;
                  return <g key={i}>
                    <line x1={padX} y1={y} x2={W-padX} y2={y} stroke="#F0F1F4" strokeWidth="1"/>
                    {maxVal >= 1000 && <text x={padX-4} y={y+4} textAnchor="end" fill="#C7CAD1" fontSize="8">{label}</text>}
                  </g>;
                })}
                {chartData.map((d, i) => {
                  const val = modoFat === 'bruto' ? d.bruto : d.liquido;
                  const h = val > 0 ? Math.max((val/maxVal)*H, 4) : 2;
                  const cx = padX + i*slotW + slotW/2;
                  const cur = i === chartData.length - 1;
                  const cor = cur ? '#2F6BFF' : '#C2D4FF';
                  return <g key={i}>
                    <rect x={cx - barW/2} y={padY+H-h} width={barW} height={h} rx="6"
                      fill={cor} opacity={cur ? 1 : 0.7}/>
                    {cur && val > 0 && (
                      <text x={cx} y={padY+H-h-5} textAnchor="middle" fill="#2F6BFF" fontSize="9" fontWeight="700">
                        {val >= 1000 ? `${(val/1000).toFixed(1)}k` : val.toFixed(0)}
                      </text>
                    )}
                    <text x={cx} y={padY+H+16} textAnchor="middle"
                      fill={cur ? '#16181D' : '#9AA0AB'} fontSize="11" fontWeight={cur ? '700' : '400'}>
                      {d.mes}
                    </text>
                  </g>;
                })}
                <line x1={padX} y1={padY+H} x2={W-padX} y2={padY+H} stroke="#E2E4E9" strokeWidth="1"/>
              </svg>
            </div>
          );
        })()}
      </div>

      {}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <p className="text-sm font-bold text-gray-800 mb-1"><Ico n="calendar" /> Aulas realizadas — últimos 6 meses</p>
        <p className="text-xs text-gray-400 mb-4">Comparativo mês a mês</p>
        {chartData.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-4">Sem dados</p>
        ) : (() => {
          const maxVal = Math.max(...chartData.map(d => d.realizadas), 1);
          const W=480, H=70, padX=24, padY=6, barW=36;
          const slotW = (W-padX*2)/chartData.length;
          return (
            <svg viewBox={`0 0 ${W} ${H+24}`} style={{width:'100%', overflow:'visible'}}>
              {chartData.map((d, i) => {
                const h = d.realizadas > 0 ? Math.max((d.realizadas/maxVal)*H, 4) : 2;
                const cx = padX + i*slotW + slotW/2;
                const cur = i === chartData.length - 1;
                return <g key={i}>
                  <rect x={cx - barW/2} y={padY+H-h} width={barW} height={h} rx="5"
                    fill={cur ? '#2F6BFF' : '#E7EDFF'} opacity={cur ? 1 : 0.8}/>
                  {d.realizadas > 0 && (
                    <text x={cx} y={padY+H-h-4} textAnchor="middle"
                      fill={cur ? '#2F6BFF' : '#9AA0AB'} fontSize="10" fontWeight="700">
                      {d.realizadas}
                    </text>
                  )}
                  <text x={cx} y={padY+H+16} textAnchor="middle"
                    fill={cur ? '#16181D' : '#9AA0AB'} fontSize="11" fontWeight={cur ? '700' : '400'}>
                    {d.mes}
                  </text>
                </g>;
              })}
              <line x1={padX} y1={padY+H} x2={W-padX} y2={padY+H} stroke="#E2E4E9" strokeWidth="1"/>
            </svg>
          );
        })()}
      </div>

      {}
      {stats.expirandoEmBreve > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => { uso.acao('inicio.vencendo'); onNavigate && onNavigate('alunos'); }}>
          <span className="text-xl"><Ico n="bolt" /></span>
          <div className="flex-1">
            <p className="text-sm font-bold text-orange-800">{stats.expirandoEmBreve} contrato{stats.expirandoEmBreve!==1?'s':''} expirando em 7 dias</p>
            <p className="text-xs text-orange-600 mt-0.5">Toque para ver os alunos <Ico n="arrow-right" /></p>
          </div>
        </div>
      )}

      {/* v144: convidar outro professor — so' para quem ja' tem aluno (o Inicio vazio tem uma acao so': o 1o aluno) */}
      {stats.alunosAtivos > 0 && <ConvidarProfessorSM origem="inicio" />}

      {/* Piloto (02/10): canal de duvida/ideia no fim do Inicio — some se nao houver numero no banco. */}
      <FalarComPlen quem="professor" nome={professor?.nome} origem="inicio" compacto />
    </div>
  );
}

function PlanConfig({ planKey, label, emoji, duracaoLabel, metodo }) {
  const headerColors = {
    mensal:    'linear-gradient(135deg,#2F6BFF,#2F6BFF)',
    semestral: 'linear-gradient(135deg,#2F6BFF,#2F6BFF)',
    anual:     'linear-gradient(135deg,#2455D6,#2455D6)',
  };
  const itens = [
 { icon:'calendar', text: duracaoLabel },
 { icon:'credit-card', text: metodo === 'pix_cartao'? 'Pix ou Cartão': 'Cartão de Crédito · Pagamento Mensal'},
 { icon:'user', text:'Valor por aula definido por aluno'},
 { icon:'chart-bar', text:'Frequência definida por aluno'},
  ];
  return (
    <div style={{background:'white', borderRadius:'20px', border:'1px solid #E2E4E9', overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', display:'flex', flexDirection:'column'}}>
      <div style={{background: headerColors[planKey] || headerColors.mensal, padding:'20px'}}>
        <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px'}}>
          <span style={{display:'flex'}}><Ico n={emoji} s={24} c="white" /></span>
          <div>
            <div style={{fontSize:'16px', fontWeight:'800', color:'white', letterSpacing:'-0.3px'}}>Plano {label}</div>
            <div style={{fontSize:'11px', color:'rgba(255,255,255,0.68)', marginTop:'1px'}}>{duracaoLabel}</div>
          </div>
        </div>
        <div style={{background:'rgba(255,255,255,0.18)', borderRadius:'20px', padding:'4px 10px', display:'inline-block'}}>
          <span style={{fontSize:'11px', fontWeight:'600', color:'white'}}>
 {metodo === 'pix_cartao'? 'Pix ou Cartão': 'Cartão de Crédito · Pagamento Mensal'}
          </span>
        </div>
      </div>
      <div style={{padding:'18px 20px', flex:1, display:'flex', flexDirection:'column', gap:'10px'}}>
        <div style={{fontSize:'11px', fontWeight:'700', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'2px'}}>Características</div>
        {itens.map((item, i) => (
          <div key={i} style={{display:'flex', alignItems:'center', gap:'9px'}}>
            <span style={{flexShrink:0}}><Ico n={item.icon} s={15} /></span>
            <span style={{fontSize:'13px', color:'#3D424C', lineHeight:'1.4'}}>{item.text}</span>
          </div>
        ))}
        <div style={{marginTop:'8px', padding:'9px 12px', background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'10px'}}>
          <div style={{fontSize:'11px', color:'#0A6F4D', fontWeight:'600'}}><Ico n="circle-check" c="#0E9F6E" /> Disponível para vincular com alunos</div>
        </div>
      </div>
    </div>
  );
}

function Planos({ professor }) {
  const [planos, setPlanos] = React.useState([]);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [showCustomForm, setShowCustomForm] = React.useState(false);
  const [salvandoCustom, setSalvandoCustom] = React.useState(false);

  const defaultCustomForm = { nome: '', duracao_meses: 3, tipo_pagamento: 'pix_cartao' };
  const [customForm, setCustomForm] = React.useState(defaultCustomForm);

  React.useEffect(() => { loadPlanos(); }, []);
  async function loadPlanos() {
    const { data } = await supabase.from('planos').select('*').eq('professor_id', professor.id).order('created_at');
    setPlanos(data || []);
  }

  async function salvarPlanoCustom() {
    if (!customForm.nome.trim()) { toastSM('Informe o nome do plano'); return; }
    setSalvandoCustom(true);
    const payload = {
      professor_id: professor.id,
      nome: customForm.nome,
      duracao_meses: Number(customForm.duracao_meses),
      preco: 0,
      aulas_por_semana: 0,
      tipo: customForm.tipo_pagamento === 'pix_cartao' ? 'fixo' : 'anual',
      antecedencia_cancelamento_horas: 24,
      limite_reposicao: 0,
    };
    const { error: ePlano } = await supabase.from('planos').insert(payload);
    if (ePlano) uso.erro('erro.plano_salvar', ePlano.message); else uso.acao('plano.criar');
    setSalvandoCustom(false);
    setCustomForm(defaultCustomForm);
    setShowCustomForm(false);
    loadPlanos();
  }

  async function excluir(id) {
    setPlanos(prev => prev.filter(p => p.id !== id));
    setConfirmDelete(null);
    await supabase.from('planos').delete().eq('id', id);
  }

  const planDefs = [
 { key:'mensal', label:'Mensal', emoji:'calendar', duracaoLabel:'Mensalidade · 1 mês', metodo:'pix_cartao'},
 { key:'semestral', label:'Semestral', emoji:'calendar-event', duracaoLabel:'Plano longo · 6 meses', metodo:'cartao_debito'},
 { key:'anual', label:'Anual', emoji:'trophy', duracaoLabel:'Plano anual · 12 meses', metodo:'cartao_debito'},
  ];

  return (
    <div className="module-page" style={{maxWidth:'960px', margin:'0 auto'}}>
      <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'28px', gap:'16px'}}>
        <div>
          <div style={{fontSize:'22px', fontWeight:'800', color:'#16181D', letterSpacing:'-0.5px'}}>Cardápio de Planos</div>
          <div style={{fontSize:'13px', color:'#9AA0AB', marginTop:'4px'}}>Planos disponíveis para vincular com alunos. Valor e frequência são definidos em cada acordo.</div>
        </div>
        <button onClick={() => { uso.janela('plano_novo'); setShowCustomForm(true); }}
          style={{display:'flex', alignItems:'center', gap:'7px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', borderRadius:'12px', padding:'11px 18px', fontSize:'13px', fontWeight:'700', cursor:'pointer', boxShadow:'0 4px 14px rgba(99,102,241,0.3)', flexShrink:0, whiteSpace:'nowrap'}}>
          <span style={{fontSize:'18px', lineHeight:1}}>＋</span> Criar Plano
        </button>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'16px', marginBottom:'28px'}}>
        {planDefs.map(pd => (
          <PlanConfig key={pd.key} planKey={pd.key} label={pd.label} emoji={pd.emoji} duracaoLabel={pd.duracaoLabel} metodo={pd.metodo} />
        ))}
      </div>

      {(() => { const planosCustom = planos.filter(p => !['Plano Mensal','Plano Semestral','Plano Anual'].includes(p.nome)); return planosCustom.length > 0 && (
        <div>
          <div style={{fontSize:'14px', fontWeight:'700', color:'#3D424C', marginBottom:'14px'}}>Planos personalizados ({planosCustom.length})</div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'14px'}}>
            {planosCustom.map(p => {
              const metodoLabel = p.tipo === 'fixo'? 'Pix / Cartão': 'Cartão de Crédito · Pagamento Mensal';
              const duracaoLabel = p.duracao_meses === 1 ? '1 mês' : p.duracao_meses === 6 ? '6 meses' : `${p.duracao_meses} meses`;
              return (
                <div key={p.id} style={{background:'white', borderRadius:'16px', border:'1px solid #E2E4E9', padding:'18px 20px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
                  <div style={{marginBottom:'8px'}}>
                    <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D', marginBottom:'6px'}}>{p.nome}</div>
                    <div style={{display:'flex', gap:'5px', flexWrap:'wrap'}}>
                      <span style={{background:'#E7EDFF', color:'#2455D6', padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:'600'}}>{duracaoLabel}</span>
                      <span style={{background:'#E7EDFF', color:'#1C43AE', padding:'2px 8px', borderRadius:'20px', fontSize:'11px', fontWeight:'600'}}>{metodoLabel}</span>
                    </div>
                  </div>
                  <div style={{fontSize:'11px', color:'#9AA0AB', marginBottom:'12px'}}>Valor e frequência definidos em cada acordo</div>
                  {confirmDelete === p.id ? (
                    <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                      <span style={{fontSize:'12px', color:'#6E7480', marginRight:'2px'}}>Confirmar exclusão?</span>
                      <button onClick={() => excluir(p.id)} style={{padding:'5px 12px', borderRadius:'8px', border:'none', background:'#DC2626', color:'white', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>Sim</button>
                      <button onClick={() => setConfirmDelete(null)} style={{padding:'5px 12px', borderRadius:'8px', border:'1px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'12px', fontWeight:'600', cursor:'pointer'}}>Não</button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(p.id)} style={{padding:'5px 12px', borderRadius:'8px', border:'1px solid #FECACA', background:'#FEF2F2', color:'#DC2626', fontSize:'12px', fontWeight:'600', cursor:'pointer'}}>
                      Excluir
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ); })()}

      {}
      {showCustomForm && (
        <Portal>
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
          <div style={{background:'white', borderRadius:'24px', boxShadow:'0 20px 60px rgba(0,0,0,0.15)', width:'100%', maxWidth:'440px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'22px 24px', borderBottom:'1px solid #F0F1F4'}}>
              <div>
                <div style={{fontSize:'16px', fontWeight:'800', color:'#16181D'}}><Ico n="sparkles" /> Criar Plano Personalizado</div>
                <div style={{fontSize:'12px', color:'#9AA0AB', marginTop:'2px'}}>Ex: Plano Trimestral, Pacote Intensivo…</div>
              </div>
              <button onClick={() => { setShowCustomForm(false); setCustomForm(defaultCustomForm); }}
                style={{background:'#F0F1F4', border:'none', borderRadius:'10px', width:'32px', height:'32px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:'16px', color:'#6E7480'}}><Ico n="x" /></button>
            </div>
            <div style={{padding:'22px 24px', display:'flex', flexDirection:'column', gap:'16px'}}>

              <div>
                <label style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'6px'}}>Nome do plano *</label>
                <input value={customForm.nome} onChange={e => setCustomForm(f => ({...f, nome: e.target.value}))}
                  placeholder="Ex: Plano Trimestral"
                  style={{border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'10px 12px', fontSize:'14px', color:'#3D424C', width:'100%', outline:'none', boxSizing:'border-box'}}
                  onFocus={e => e.target.style.border='1.5px solid #2F6BFF'}
                  onBlur={e => e.target.style.border='1.5px solid #E2E4E9'} />
              </div>

              <div>
                <label style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'7px'}}>Duração</label>
                <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                  {[{v:1,l:'1 mês'},{v:2,l:'2 meses'},{v:3,l:'3 meses'},{v:6,l:'6 meses'},{v:12,l:'12 meses'}].map(({v,l}) => (
                    <button key={v} onClick={() => setCustomForm(f => ({...f, duracao_meses: v}))}
                      style={{padding:'8px 14px', borderRadius:'9px', border: customForm.duracao_meses === v ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: customForm.duracao_meses === v ? '#E7EDFF' : 'white', color: customForm.duracao_meses === v ? '#2F6BFF' : '#6E7480', fontWeight:'700', fontSize:'13px', cursor:'pointer'}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'7px'}}>Forma de pagamento</label>
                <div style={{display:'flex', gap:'8px'}}>
 {[{v:'pix_cartao',l:'Pix ou Cartão'},{v:'cartao_debito',l:'Cartão de Crédito · Pagamento Mensal'}].map(({v,l}) => (
                    <button key={v} onClick={() => setCustomForm(f => ({...f, tipo_pagamento: v}))}
                      style={{flex:1, padding:'10px 12px', borderRadius:'10px', border: customForm.tipo_pagamento === v ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: customForm.tipo_pagamento === v ? '#E7EDFF' : 'white', color: customForm.tipo_pagamento === v ? '#2F6BFF' : '#6E7480', fontWeight:'700', fontSize:'12px', cursor:'pointer', textAlign:'center'}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{display:'flex', gap:'10px', marginTop:'4px'}}>
                <button onClick={() => { setShowCustomForm(false); setCustomForm(defaultCustomForm); }}
                  style={{flex:1, padding:'13px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'14px', fontWeight:'600', cursor:'pointer'}}>
                  Cancelar
                </button>
                <button onClick={salvarPlanoCustom} disabled={salvandoCustom || !customForm.nome.trim()}
                  style={{flex:2, background: !customForm.nome.trim() ? '#F0F1F4' : 'linear-gradient(135deg,#2F6BFF,#2455D6)', color: !customForm.nome.trim() ? '#9AA0AB' : 'white', border:'none', borderRadius:'12px', padding:'13px', fontSize:'14px', fontWeight:'700', cursor: !customForm.nome.trim() ? 'not-allowed' : 'pointer', boxShadow: customForm.nome.trim() ? '0 4px 14px rgba(99,102,241,0.25)' : 'none'}}>
 {salvandoCustom ? 'Salvando...': 'Criar Plano'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

function AlunoHistoricoModal({ aluno, professor, onClose }) {
  const [aulas, setAulas] = React.useState([]);
  const [pagamentos, setPagamentos] = React.useState([]);
  const [aba, setAba] = React.useState('aulas');
  // Aulas mes a mes (Marcus, 25/09): a lista comecava em dezembro — a agenda do mensal e'
  // gerada 90 dias para frente — e o professor precisava rolar ate' hoje. Abre no mes atual.
  const [mesVer, setMesVer] = React.useState(() => new Date().toISOString().slice(0, 7));
  React.useEffect(() => { uso.janela('aluno_historico'); }, []);

  React.useEffect(() => {
    async function load() {
      const [{ data: a }, { data: p }] = await Promise.all([
        supabase.from('aulas').select('*').eq('aluno_id', aluno.id).order('data', { ascending: true }).limit(500),
        supabase.from('pagamentos').select('*').eq('aluno_id', aluno.id).order('created_at', { ascending: false }).limit(30),
      ]);
      setAulas(semRemarcada(a)); setPagamentos(p || []);   // v131: sem a original de aula remarcada
    }
    load();
  }, [aluno.id]);

  const statusAulaInfo = { agendada: { label: 'Agendada', color: 'blue' }, aguardando_confirmacao: { label: 'A confirmar', color: 'gray' }, realizada: { label: 'Realizada', color: 'green' }, cancelada_professor: { label: 'Canc. Professor', color: 'yellow' }, cancelada_aluno: { label: 'Canc. Aluno', color: 'orange' }, cancelada_forca_maior: { label: 'Imprevisto', color: 'gray' }, falta: { label: 'Falta', color: 'red' } };
  const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{aluno.nome}</h2>
            <p className="text-sm text-gray-500">{aluno.modalidade || 'Esporte não definido'} · {aluno.telefone || aluno.email || ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><Icons.X /></button>
        </div>
        <div className="flex border-b">
          {[{k:'aulas',l:'Aulas'},{k:'pagamentos',l:'Pagamentos'}].map(t => (
            <button key={t.k} onClick={() => { uso.acao('historico.aba', { aba: t.k }); setAba(t.k); }} className={`flex-1 py-3 text-sm font-medium ${aba===t.k?'text-indigo-600 border-b-2 border-indigo-600':'text-gray-500'}`}>{t.l}</button>
          ))}
        </div>
        <div className="p-4">
          {aba === 'aulas' && (() => {
            const doMes = aulas.filter(a => String(a.data).slice(0, 7) === mesVer);
            const [yy, mm] = mesVer.split('-').map(Number);
            const nomeMes = new Date(yy, mm - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
            const mover = k => { const d = new Date(yy, mm - 1 + k, 1); setMesVer(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
            const feitas = doMes.filter(a => a.status === 'realizada').length;
            return (
            <div className="flex flex-col gap-2">
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px'}}>
                <button onClick={() => mover(-1)} aria-label="Mês anterior" style={{border:'1px solid #E2E4E9', background:'white', borderRadius:'10px', width:'36px', height:'36px', cursor:'pointer', fontSize:'18px', color:'#6E7480'}}>‹</button>
                <div style={{textAlign:'center'}}>
                  <div style={{fontSize:'15px', fontWeight:'800', color:'#16181D', textTransform:'capitalize'}}>{nomeMes}</div>
                  <div style={{fontSize:'12px', color:'#6E7480'}}>{doMes.length} aula{doMes.length !== 1 ? 's' : ''} no mês{feitas ? ` · ${feitas} dada${feitas !== 1 ? 's' : ''}` : ''}</div>
                </div>
                <button onClick={() => mover(1)} aria-label="Próximo mês" style={{border:'1px solid #E2E4E9', background:'white', borderRadius:'10px', width:'36px', height:'36px', cursor:'pointer', fontSize:'18px', color:'#6E7480'}}>›</button>
              </div>
              {doMes.length === 0 ? <p className="text-gray-400 text-center py-8">Nenhuma aula neste mês</p> : doMes.map(a => {
                const info0 = statusAulaInfo[a.status] || { label: a.status, color: 'gray' };
                const info = rotuloEspecialAula(a) ? { ...info0, label: rotuloEspecialAula(a), color: 'gray' } : info0;
                const d = new Date(a.data + 'T12:00');
                return (
                  <div key={a.id} className="flex items-center gap-3 py-2 border-b border-gray-50">
                    <div className="text-center min-w-[48px]">
                      <div className="text-xs text-gray-400">{diasSemana[d.getDay()]}</div>
                      <div className="font-bold text-gray-700">{d.getDate()}/{d.getMonth()+1}</div>
                    </div>
                    <div className="flex-1 text-sm text-gray-600">{a.hora?.slice(0,5)}</div>
                    <Badge color={info.color}>{info.label}</Badge>
                  </div>
                );
              })}
            </div>
            );
          })()}
          {aba === 'pagamentos' && (
            <div className="flex flex-col gap-2">
              {pagamentos.length === 0 ? <p className="text-gray-400 text-center py-8">Nenhum pagamento registrado</p> : pagamentos.map(p => (
                <div key={p.id} className="flex items-center gap-3 py-2 border-b border-gray-50">
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{fmtBRL(p.valor)}</div>
                    <div className="text-xs text-gray-400">{p.status === 'pago' && formaDoPagamento(p) !== '—' ? formaDoPagamento(p) + ' · ' : ''}{p.status === 'cancelado' && p.cancelado_motivo ? (ROTULO_MOTIVO_CANCEL[p.cancelado_motivo] || 'outro motivo') + ' · ' : ''}{p.referencia_mes || '-'}</div>
                  </div>
                  <Badge color={p.status==='pago'?'green':p.status==='pendente'?'yellow':'red'}>{p.status==='pago'?'Pago':p.status==='pendente'?'Pendente':'Cancelado'}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}

function ModoRapido({ professor, planos, onClose, onSalvar }) {
  const DIAS_MAP = {'segunda':1,'segunda-feira':1,'seg':1,'terça':2,'terca':2,'terça-feira':2,'ter':2,'quarta':3,'quarta-feira':3,'qua':3,'quinta':4,'quinta-feira':4,'qui':4,'sexta':5,'sexta-feira':5,'sex':5,'sábado':6,'sabado':6,'sab':6,'domingo':0,'dom':0};
  const DIAS_LABEL = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  const [msgs, setMsgs] = React.useState([
    { role:'bot', text:'👋 Olá! Me diga seus alunos — o nome, quando treinam e o valor. Pode ser um de cada vez ou vários juntos.\n\n📝 Exemplo: "Marcos treina segunda e quarta às 12h, paga R$ 400. Rodrigo treina terça e quinta às 14h, paga R$ 350."' }
  ]);
  const [input, setInput] = React.useState('');
  const [parsed, setParsed] = React.useState([]);
  const [step, setStep] = React.useState('chat');
  const chatRef = React.useRef(null);

  React.useEffect(() => { if(chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, [msgs]);

  function parseHora(txt) {
    if (/meio.?dia/i.test(txt)) return '12:00';
    const m = txt.match(/(\d{1,2})[h:](\d{2})?/i);
    if (m) return `${String(m[1]).padStart(2,'0')}:${m[2]||'00'}`;
    return null;
  }

  function parseTexto(texto) {
    const resultados = [];

    const partes = texto.split(/(?<=[.;])\s*|\s*,\s*(?=[A-ZÁÉÍÓÚÃÕ])/);

    partes.forEach(seg => {
      seg = seg.trim();
      if (!seg) return;

      const nomeM = seg.match(/^([A-ZÁÉÍÓÚÃÕÀÂÊÇ][a-záéíóúãõàâêç]+(?:\s+[A-ZÁÉÍÓÚÃÕÀÂÊÇ][a-záéíóúãõàâêç]+)*)/);
      if (!nomeM) return;
      const nome = nomeM[1];
      if (nome.toLowerCase() === 'o' || nome.toLowerCase() === 'a') return;

      const dias = [];
      Object.entries(DIAS_MAP).forEach(([key, val]) => {
        const re = new RegExp(`\\b${key}\\b`, 'i');
        if (re.test(seg) && !dias.includes(val)) dias.push(val);
      });

      const hora = parseHora(seg) || '08:00';
      const valorM = seg.match(/R?\$?\s*(\d+(?:[.,]\d+)?)/i);
      const valor = valorM ? parseFloat(valorM[1].replace(',','.')) : 0;

      if (nome.length > 1 && (dias.length > 0 || valor > 0)) {
        resultados.push({ nome, dias: dias.sort((a,b)=>a-b), hora, valor });
      }
    });
    return resultados;
  }

  function enviar() {
    if (!input.trim() || step !== 'chat') return;
    const texto = input.trim();
    setInput('');
    setMsgs(m => [...m, { role:'user', text:texto }]);

    const novos = parseTexto(texto);
    if (novos.length === 0) {
      setMsgs(m => [...m, { role:'bot', text:'Não consegui identificar nenhum aluno. Tente algo como:\n"João treina seg/qua às 10h, paga R$ 400"'}]);
      return;
    }
    setParsed(p => [...p, ...novos]);
    const preview = novos.map(a =>
      ` ${a.nome}\n    ${a.dias.map(d=>DIAS_LABEL[d]).join('· ')} às ${a.hora}${a.valor>0 ? `\n   <Ico n="coin"/> R$ ${a.valor.toFixed(0)}/pacote` : ''}`
    ).join('\n\n');
    setMsgs(m => [...m, {
      role:'bot',
      text:`Perfeito! ${novos.length > 1 ? 'Encontrei esses alunos' : 'Encontrei'}:\n\n${preview}\n\n${novos.length > 1 ? 'Quer adicionar mais ou confirmar o cadastro?' : 'Quer adicionar mais alunos ou confirmar?'}`,
    }]);
  }

  async function confirmar() {
    if (!parsed.length || step !== 'chat') return;
    // Sem o try/catch, uma falha de rede ou de permissao deixava o modal preso em
    // 'saving' — estado que nao tem tela nem botao. O professor so' podia fechar e
    // perdia tudo que tinha digitado.
    setStep('saving');
    setMsgs(m => [...m, { role:'bot', text:` Salvando ${parsed.length} aluno${parsed.length>1?'s':''}...` }]);

    try {
    for (const a of parsed) {
      const { data: aluno } = await supabase.from('alunos').insert({
        professor_id: professor.id, nome: a.nome, telefone: '', email: '', modalidade: '', observacoes: '',
      }).select().single();

      if (aluno) {
        for (const dia of a.dias) {
          await supabase.from('agenda_semanal').insert({ aluno_id: aluno.id, professor_id: professor.id, dia_semana: dia, hora: a.hora });
        }
        if (a.valor > 0 && planos.length > 0) {
          const plano = planos[0];
          const dataInicio = new Date().toISOString().split('T')[0];
          const dataFimD = new Date(); dataFimD.setMonth(dataFimD.getMonth() + (plano.duracao_meses||1));
          const dataFim = dataFimD.toISOString().split('T')[0];
          const { data: contrato } = await supabase.from('contratos').insert({
            aluno_id: aluno.id, plano_id: plano.id, professor_id: professor.id,
            preco_negociado: a.valor, data_inicio: dataInicio, data_fim: dataFim, status: 'ativo',
          }).select().single();
          if (contrato) {
 { const rc = await recalcularCreditos(supabase, aluno.id); if (rc.error) toastSM('Créditos: '+ rc.error.message); }
            await supabase.from('pagamentos').insert({ aluno_id: aluno.id, professor_id: professor.id, valor: a.valor, tipo: 'pendente', status: 'pendente', referencia_mes: dataInicio.slice(0,7), data_pagamento: null });
          }
        }
      }
    }
    } catch (e) {
      setStep('chat');
      setMsgs(m => [...m, { role:'bot', text: 'Não consegui salvar: ' + (e?.message || 'erro inesperado') + '. Confira e tente de novo — o que você escreveu continua aqui.' }]);
      return;
    }
    setStep('done');
    setMsgs(m => [...m, { role:'bot', text:` Pronto! ${parsed.length} aluno${parsed.length>1?'s':''} cadastrado${parsed.length>1?'s':''} com agenda${planos.length>0?'e cobrança gerada':''} . Você pode editar os detalhes no módulo Alunos.` }]);
    setTimeout(onSalvar, 1800);
  }

  return (
    <Portal>
    <div style={{position:'fixed', inset:0, background:'rgba(15,23,42,0.75)', backdropFilter:'blur(6px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'20px'}}>
      <div style={{background:'white', borderRadius:'24px', width:'100%', maxWidth:'560px', height:'88vh', maxHeight:'640px', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 30px 80px rgba(0,0,0,0.35)'}}>
        {}
        <div style={{background:'linear-gradient(135deg, #16181D, #16181D)', padding:'20px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0}}>
          <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
            <div style={{width:'38px', height:'38px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px'}}><Ico n="bolt" /></div>
            <div>
              <div style={{color:'white', fontWeight:'800', fontSize:'15px', letterSpacing:'-0.3px'}}>Modo Rápido</div>
              <div style={{color:'rgba(255,255,255,0.5)', fontSize:'11px', marginTop:'1px'}}>Fale seus alunos — eu cadastro tudo</div>
            </div>
          </div>
          <button onClick={onClose} style={{color:'rgba(255,255,255,0.5)', background:'rgba(255,255,255,0.08)', border:'none', borderRadius:'8px', padding:'6px 12px', cursor:'pointer', fontSize:'12px', fontWeight:'600', transition:'all 0.15s'}}
            onMouseOver={e => e.currentTarget.style.color='white'}
            onMouseOut={e => e.currentTarget.style.color='rgba(255,255,255,0.5)'}><Ico n="x" /> Fechar</button>
        </div>

        {}
        <div ref={chatRef} style={{flex:1, overflowY:'auto', padding:'20px', display:'flex', flexDirection:'column', gap:'14px', background:'#F7F8FA'}}>
          {msgs.map((m, i) => (
            <div key={i} style={{display:'flex', justifyContent: m.role==='user' ? 'flex-end' : 'flex-start', alignItems:'flex-end', gap:'8px'}}>
              {m.role==='bot' && (
                <div style={{width:'30px', height:'30px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'14px', flexShrink:0}}><Ico n="trophy" /></div>
              )}
              <div style={{
                maxWidth:'76%', padding:'12px 16px', fontSize:'13px', lineHeight:'1.65', whiteSpace:'pre-line',
                background: m.role==='user' ? 'linear-gradient(135deg, #2F6BFF, #2455D6)' : 'white',
                color: m.role==='user' ? 'white' : '#1F2229',
                borderRadius: m.role==='user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                boxShadow: m.role==='bot' ? '0 2px 10px rgba(0,0,0,0.06)' : '0 4px 14px rgba(99,102,241,0.25)',
                border: m.role==='bot' ? '1px solid #E2E4E9' : 'none',
              }}>{m.text}</div>
            </div>
          ))}
          {step==='done' && (
            <div style={{textAlign:'center', padding:'10px'}}>
              <div style={{display:'inline-flex', alignItems:'center', gap:'8px', background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'10px 20px', fontSize:'13px', color:'#0C8A5F', fontWeight:'600'}}>
                <Ico n="circle-check" c="#0E9F6E" /> Cadastro concluído! Fechando...
              </div>
            </div>
          )}
        </div>

        {}
        {step === 'chat' && (
          <div style={{padding:'16px 20px', borderTop:'1px solid #F0F1F4', background:'white', flexShrink:0}}>
            {parsed.length > 0 && (
              <button onClick={confirmar}
                style={{width:'100%', background:'linear-gradient(135deg, #0E9F6E, #0E9F6E)', color:'white', border:'none', borderRadius:'12px', padding:'12px', fontWeight:'700', fontSize:'14px', cursor:'pointer', marginBottom:'12px', boxShadow:'0 4px 14px rgba(5,150,105,0.3)', transition:'all 0.15s'}}
                onMouseOver={e => e.currentTarget.style.transform='translateY(-1px)'}
                onMouseOut={e => e.currentTarget.style.transform='translateY(0)'}>
                <Ico n="circle-check" c="#0E9F6E" /> Confirmar e cadastrar {parsed.length} aluno{parsed.length>1?'s':''}
              </button>
            )}
            <div style={{display:'flex', gap:'8px'}}>
              <input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); enviar(); }}}
                placeholder='Ex: "Ana treina seg/qua às 9h, paga R$ 380"'
                style={{flex:1, border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'11px 14px', fontSize:'13px', outline:'none', background:'#F7F8FA', transition:'border-color 0.15s'}}
                onFocus={e => e.target.style.borderColor='#2F6BFF'}
                onBlur={e => e.target.style.borderColor='#E2E4E9'}
              />
              <button onClick={enviar}
                style={{background:'linear-gradient(135deg, #2F6BFF, #2455D6)', color:'white', border:'none', borderRadius:'10px', padding:'11px 18px', cursor:'pointer', fontWeight:'700', fontSize:'13px', flexShrink:0, boxShadow:'0 4px 12px rgba(99,102,241,0.3)'}}>
                Enviar
              </button>
            </div>
            <div style={{textAlign:'center', fontSize:'11px', color:'#9AA0AB', marginTop:'8px'}}>
              Adicione quantos alunos quiser antes de confirmar · Enter para enviar
            </div>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}

const MODALIDADES = ['Personal Trainer','Fisioterapia','Musculação','Pilates','Yoga','Natação','Tênis','Padel','Futsal','Vôlei','Basquete','Crossfit','Outro'];
// Duas listas, as duas em ordem alfabetica. As oito primeiras resolvem a maioria dos
// professores de um relance; o resto fica atras de um toque, para a tela nao virar uma
// parede de 39 botoes. A busca varre as duas ao mesmo tempo.
const MODALIDADES_COMUNS = [
  'Beach Tennis', 'Dança', 'Jiu-jitsu', 'Musculação',
  'Natação', 'Personal Trainer', 'Pilates', 'Tênis',
];
const MODALIDADES_OUTRAS = [
  'Alongamento', 'Badminton', 'Balé', 'Basquete', 'Bike / Ciclismo', 'Boxe', 'Corrida',
  'Crossfit', 'Fisioterapia', 'Funcional', 'Futebol', 'Futevôlei', 'Ginástica',
  'Handebol', 'Hidroginástica', 'Judô', 'Karatê', 'Muay Thai', 'Nutrição esportiva',
  'Padel', 'Patinação', 'Preparação física', 'Reabilitação', 'Skate', 'Spinning',
  'Squash', 'Stand Up Paddle', 'Surf', 'Tênis de mesa', 'Triathlon', 'Vôlei', 'Yoga',
];
const MODALIDADES_PROF = [...MODALIDADES_COMUNS, ...MODALIDADES_OUTRAS];
function parseMods(str) { if (Array.isArray(str)) return str; try { const v = JSON.parse(str || '[]'); return Array.isArray(v) ? v : []; } catch { return []; } }

function ModalAulasDetalhes({ professor, aluno, aulas: aulasIniciais, onClose, onAtualizou }) {
  const [aulas, setAulas] = React.useState(() => semRemarcada(aulasIniciais));   // v131: sem a original de aula remarcada
  // Aulas feitas ANTES de entrar no app ("ja' comecou, 8 feitas") nao viram linhas na
  // agenda — mas contam. Sem isto, um pacote 8/8 aparecia como "0 realizadas".
  const contratoAtivoM = (aluno?.contratos || []).find(c => c.status === 'ativo');
  const anterioresN = Number(contratoAtivoM?.aulas_realizadas_anterior) || 0;
  const totalPacoteN = Number(contratoAtivoM?.total_aulas) || 0;
  const [reagendando, setReagendando] = React.useState(null);
  // U21 — professor cancela uma aula futura com motivo. Pacote: nao consome e nasce uma
  // reposicao devida. Mensal: nada (mensalidade fixa). Feriado: a aula e' empurrada.
  const [cancelando, setCancelando] = React.useState(null); // { id, motivo, obs }
  React.useEffect(() => { uso.janela('aulas_aluno'); }, []);
  // v131: o texto do cartao vem da previa do banco (p_simular) — data do fim do pacote, valor
  // do desconto, limite do mes. Feriado fica como antes (fora desta rodada).
  const [previaCanc, setPreviaCanc] = React.useState(null);   // { id, motivo, carregando, txt }
  React.useEffect(() => {
    const id = cancelando?.id, m = cancelando?.motivo;
    if (!id || !m || m === 'feriado') { setPreviaCanc(null); return; }
    let vivo = true;
    setPreviaCanc({ id, motivo: m, carregando: true, txt: null });
    previaCancelarAulaProf(id, categoriaDoMotivo({ motivoHumano: m, quem: 'professor' }), m)
      .then(pv => { if (vivo) setPreviaCanc({ id, motivo: m, carregando: false, txt: pv.txt }); });
    return () => { vivo = false; };
  }, [cancelando?.id, cancelando?.motivo]);
  async function cancelarComoProfessor(au) {
    const c0 = cancelando || {};
    if (!c0.motivo) { uso.bloqueio('aula_cancelar_prof', null, null, 'sem_motivo'); toastSM('Escolha o motivo.'); return; }
    setAtualizandoStatus(au.id);
    try {
      const quando = new Date(au.data+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'}) + ' às ' + (au.hora||'').slice(0,5);
      if (c0.motivo === 'feriado') {
        const { data, error } = await supabase.rpc('empurrar_aula_feriado', { p_aula_id: au.id });
        if (error || data?.erro) throw new Error(error?.message || data?.erro);
        setAulas(prev => prev.filter(a => a.id !== au.id));
        await supabase.from('notificacoes').insert({ aluno_id: aluno.id, professor_id: professor.id, tipo: 'aula_empurrada',
          mensagem: `A aula de ${quando} caiu num feriado. ${data?.empurrada ? 'Ela foi para o fim do pacote — você não perde nada.' : 'Ela foi retirada da agenda.'}` });
        toastSM(data?.empurrada ? 'Aula empurrada para o fim do pacote.' : 'Aula retirada.');
      } else {
        // v131 (Etapa 9): a regra unica do banco (professor_cancelar_aula) decide pelo acordo DA
        // AULA — fim do pacote, reposicao, desconto ou nada —, conta o limite de imprevistos do mes
        // e avisa o aluno. Sairam daqui: o update direto, o insert em reposicoes, o aviso montado no
        // app, a conta do limite e o recalculo de creditos.
        const categoria = categoriaDoMotivo({ motivoHumano: c0.motivo, quem: 'professor' });
        const r = await cancelarAulaProf({ ...au, aluno_id: au.aluno_id || aluno.id }, categoria, c0.motivo,
          { obs: c0.motivo === 'outro' ? (c0.obs || '').trim() || null : null, origem: 'aulas_aluno', nomeAluno: aluno.nome, acaoUso: false });
        if (!r) { setAtualizandoStatus(null); return; }
        setAulas(prev => prev.map(a => a.id === au.id ? {...a, status: r.status, motivo_cancelamento: c0.motivo} : a));
      }
      uso.acao('aula.cancelar_prof', { motivo: motivoUso(c0.motivo) });
      setCancelando(null);
      onAtualizou?.();
    } catch (e) { uso.erro('erro.aula_cancelar', e?.message || e); toastSM('Não foi possível cancelar: ' + (e?.message || e)); }
    setAtualizandoStatus(null);
  }
  const [novaData, setNovaData] = React.useState('');
  const [novaHora, setNovaHora] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [verTodasAgendadas, setVerTodasAgendadas] = React.useState(false);
  const [atualizandoStatus, setAtualizandoStatus] = React.useState(null);

  const [perguntandoReposicao, setPerguntandoReposicao] = React.useState(null);
  const [editandoSala, setEditandoSala] = React.useState(null);
  const [valorSala, setValorSala] = React.useState('');

  const now = new Date();
  const pad2 = n => String(n).padStart(2,'0');
  const hoje = `${now.getFullYear()}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())}`;

  const em30dias = new Date(now); em30dias.setDate(em30dias.getDate()+30);
  const limite30 = `${em30dias.getFullYear()}-${pad2(em30dias.getMonth()+1)}-${pad2(em30dias.getDate())}`;

  const pendentes = [...aulas]
    .filter(a => a.status === 'agendada' && a.data < hoje)
    .sort((a,b) => a.data > b.data ? 1 : -1)
    .filter((a,i,arr) => arr.findIndex(x => x.data === a.data && (x.hora||'').slice(0,5) === (a.hora||'').slice(0,5)) === i);

  const realizadasTodas = [...aulas].filter(a=>a.status==='realizada').sort((a,b)=>b.data>a.data?1:-1);
  const realizadas = realizadasTodas.filter((a,i,arr)=>arr.findIndex(x=>x.data===a.data&&(x.hora||'').slice(0,5)===(a.hora||'').slice(0,5))===i);

  const agendadasTodas = [...aulas]
    .filter(a=>a.status==='agendada' && a.data >= hoje)
    .sort((a,b)=>a.data>b.data?1:-1)
    .filter((a,i,arr)=>arr.findIndex(x=>x.data===a.data&&(x.hora||'').slice(0,5)===(a.hora||'').slice(0,5))===i);
  const agendadasVisiveis = verTodasAgendadas ? agendadasTodas : agendadasTodas.filter(a=>a.data<=limite30);
  const agendadasOcultas = agendadasTodas.length - agendadasVisiveis.length;

  const fmtDate = d => new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'short',day:'2-digit',month:'2-digit',year:'2-digit'});

  async function atualizarStatus(au, novoStatus) {
    setAtualizandoStatus(au.id);
    // passa pelo consenso: "dada"/"falta" so' descontam quando o aluno concorda (ou 24h de silencio)
    const estado = await aplicarDesfechoProfessor({ ...au, aluno_id: au.aluno_id || aluno.id }, novoStatus, { nomeAluno: aluno.nome, origem: 'aulas_aluno' });
    setAtualizandoStatus(null);
    if (!estado) return;
    const agoraIso = new Date().toISOString();
    setAulas(prev => prev.map(a => a.id !== au.id ? a
      : (estado === 'aguardando' ? { ...a, desfecho_prof: novoStatus, confirmada_prof_em: agoraIso }
        : estado === 'divergencia' ? { ...a, desfecho_prof: novoStatus, divergencia_em: agoraIso }
        : { ...a, status: estado })));
    onAtualizou?.();
    // v131: "Aluno cancelou" / "Eu cancelei" passam por professor_cancelar_aula — o banco aplica a
    // regra do acordo (fim do pacote, reposicao...) e avisa o aluno. Sairam a pergunta "Esta aula
    // sera' reposta?" (contradizia a regra ja' aplicada) e o aviso 'reposicao_disponivel'.
  }

  async function registrarReposicao(au, seraReposta) {

    const { data: contrato } = await supabase.from('contratos')
      .select('id, limite_reposicao, reposicoes_usadas')
      .eq('aluno_id', aluno.id).eq('status', 'ativo')
      .order('data_inicio', { ascending: false }).limit(1).maybeSingle();

    if (!seraReposta && contrato) {
      const usadas = (contrato.reposicoes_usadas || 0) + 1;
      await supabase.from('contratos').update({ reposicoes_usadas: usadas }).eq('id', contrato.id);
      const limite = contrato.limite_reposicao || 0;
      if (usadas > limite && limite > 0) {

        toastSM(` Atenção: ${aluno.nome} usou ${usadas}/${limite} reposições permitidas no plano.`);
      }
    }
    setPerguntandoReposicao(null);
    onAtualizou?.();
  }

  async function salvarCustoSala() {
    const val = numDinheiro(valorSala);
    await supabase.from('aulas').update({ custo_sala: val }).eq('id', editandoSala.id);
    setAulas(prev => prev.map(a => a.id === editandoSala.id ? {...a, custo_sala: val} : a));
    setEditandoSala(null); setValorSala('');
    onAtualizou?.();
  }

  // v131 (Etapa 6): remarcar_aula confere o horario (alerta com nomes; o professor pode marcar
  // mesmo assim), acerta a renovacao do pacote e avisa o aluno. Mensal/turma: nasce uma aula nova
  // (modo 'nova', id novo) e a antiga fica so' no historico.
  async function salvarReagendar() {
    if (!novaData || novaData.length < 10) return;
    setSalvando(true);
    const alvo = reagendando;
    const r = await remarcarAulaProf(alvo, novaData, novaHora || alvo.hora, { origem: 'aulas_aluno', nomeAluno: aluno.nome });
    setSalvando(false);
    if (!r) return;   // recusou ou "Escolher outro": a data continua aberta
    const novaH = novaHora || alvo.hora;
    setAulas(prev => prev.map(a => a.id === alvo.id ? {...a, id: r.aula_id || a.id, data: novaData, hora: novaH} : a));
    setReagendando(null); setNovaData(''); setNovaHora('');
    if (r.modo === 'nova' || r.renovacao_depois !== r.renovacao_antes) onAtualizou?.();
  }

  const AulaRow = ({au, permitirReagendar}) => {
    const editando = reagendando?.id === au.id;
    const editandoSalaEsta = editandoSala?.id === au.id;
    const temSala = au.custo_sala && Number(au.custo_sala) > 0;
    return (
      <div key={au.id} style={{borderRadius:'10px',marginBottom:'6px',border:'1px solid #E2E4E9',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 12px',background: editando?'#E7EDFF':editandoSalaEsta?'#EFFAF5':'#F7F8FA'}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <span style={{fontSize:'13px',fontWeight:'700',color:'#1F2229'}}>{fmtDate(au.data)}</span>
            <span style={{fontSize:'12px',color:'#9AA0AB',fontWeight:'600'}}>{(au.hora||'').slice(0,5)}</span>
            {temSala && !editandoSalaEsta && (
              <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 7px',borderRadius:'10px',background:'#DCF5EC',color:'#0A6F4D'}}>
                <Ico n="coin" /> Sala: R${Number(au.custo_sala).toFixed(2)}
              </span>
            )}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            {permitirReagendar && !editando && !editandoSalaEsta && (
              <button onClick={()=>{setReagendando(au);setNovaData(au.data);setNovaHora((au.hora||'').slice(0,5));}}
                style={{fontSize:'11px',padding:'3px 9px',borderRadius:'6px',border:'1px solid #C2D4FF',background:'white',color:'#2F6BFF',cursor:'pointer',fontWeight:'600'}}>
                <Ico n="pencil" /> Reagendar
              </button>
            )}
            {permitirReagendar && !editando && !editandoSalaEsta && au.status === 'agendada' && (
              <button onClick={()=>setCancelando(cancelando?.id === au.id ? null : { id: au.id, motivo: null, obs: '' })}
                style={{fontSize:'11px',padding:'3px 9px',borderRadius:'6px',border:'1px solid #FCA5A5',background:'white',color:'#DC2626',cursor:'pointer',fontWeight:'600'}}>
                <Ico n="circle-x" c="#DC2626" /> Cancelar
              </button>
            )}
            {!editando && !editandoSalaEsta && (
              <button onClick={()=>{setEditandoSala(au);setValorSala(au.custo_sala ? String(Number(au.custo_sala).toFixed(2)) : '');}}
                style={{fontSize:'11px',padding:'3px 9px',borderRadius:'6px',border:'1px solid #B7E9D6',background:'white',color:'#0E9F6E',cursor:'pointer',fontWeight:'600'}}>
                <Ico n="coin" /> Sala
              </button>
            )}
            <span style={{fontSize:'11px',fontWeight:'700',padding:'3px 9px',borderRadius:'20px',
              background: au.status==='realizada'?'#DCF5EC':'#C2D4FF',
              color: au.status==='realizada'?'#0A6F4D':'#1C43AE'}}>
 {au.status==='realizada'?'Realizada':'Agendada'}
            </span>
          </div>
        </div>
        {cancelando?.id === au.id && (
          <div style={{padding:'12px',background:'#FEF2F2',borderTop:'1px solid #FECACA'}}>
            <div style={{fontSize:'13px',fontWeight:'800',color:'#991B1B',marginBottom:'8px'}}>Cancelar a aula de {fmtDate(au.data)}?</div>
            <MotivoPicker valor={cancelando.motivo} onChange={m => setCancelando({...cancelando, motivo: m})} obs={cancelando.obs} onObs={o => setCancelando({...cancelando, obs: o})} />
            <div style={{fontSize:'12px',color:'#6E7480',marginTop:'8px',lineHeight:1.5}}>
              {cancelando.motivo === 'feriado' ? 'A aula sai do feriado e vai para o fim do pacote. Ninguém perde nada.'
               : previaCanc && previaCanc.id === au.id && previaCanc.motivo === cancelando.motivo
                 ? (previaCanc.carregando ? 'Conferindo a regra do acordo…' : (previaCanc.txt || 'O aluno recebe o aviso com o motivo e o que acontece com a aula dele.'))
               : cancelando.motivo === 'clima' ? 'Chuva ou imprevisto: não consome a aula do aluno — o que acontece com ela segue a regra do acordo.'
               : cancelando.motivo ? 'Você desmarcou: não consome a aula do aluno. No pacote, a aula vai para o fim; na mensalidade fixa, não gera crédito.'
               : 'O aluno recebe o aviso com o motivo e o que acontece com a aula dele.'}
            </div>
            <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
              <button onClick={()=>setCancelando(null)} style={{flex:1,minHeight:'44px',padding:'9px',borderRadius:'9px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>Voltar</button>
              <button disabled={!cancelando.motivo || atualizandoStatus === au.id} onClick={()=>cancelarComoProfessor(au)}
                style={{flex:1,minHeight:'44px',padding:'9px',borderRadius:'9px',border:'none',background:cancelando.motivo?'#DC2626':'#E2E4E9',color:'white',fontSize:'12px',fontWeight:'700',cursor:cancelando.motivo?'pointer':'not-allowed'}}>
                {atualizandoStatus === au.id ? 'Cancelando…' : 'Confirmar e avisar'}
              </button>
            </div>
          </div>
        )}
        {editandoSalaEsta && (
          <div style={{padding:'12px',background:'#EFFAF5',borderTop:'1px solid #B7E9D6',display:'flex',gap:'8px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div>
              <div style={{fontSize:'10px',fontWeight:'700',color:'#0E9F6E',marginBottom:'4px'}}><Ico n="coin" /> Custo de sala para esta aula</div>
              <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                <span style={{fontSize:'13px',color:'#3D424C'}}>R$</span>
                <CampoDinheiro value={valorSala} onValor={setValorSala}
                  onFocus={e=>e.target.select()}
                  autoFocus
                  placeholder="0,00"
                  style={{border:'1.5px solid #8FDFC4',borderRadius:'8px',padding:'6px 10px',fontSize:'13px',outline:'none',width:'100px'}} />
              </div>
              <div style={{fontSize:'10px',color:'#6E7480',marginTop:'4px'}}>Use 0 para remover o custo</div>
            </div>
            <button onClick={salvarCustoSala}
              style={{padding:'7px 14px',borderRadius:'8px',border:'none',background:'#0E9F6E',color:'white',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
              Salvar
            </button>
            <button onClick={()=>setEditandoSala(null)}
              style={{padding:'7px 12px',borderRadius:'8px',border:'1px solid #B7E9D6',background:'white',color:'#0E9F6E',fontSize:'12px',cursor:'pointer'}}>
              Cancelar
            </button>
          </div>
        )}
        {editando && (
          <div style={{padding:'12px',background:'#E7EDFF',borderTop:'1px solid #C2D4FF',display:'flex',gap:'8px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div>
              <div style={{fontSize:'10px',fontWeight:'700',color:'#2F6BFF',marginBottom:'4px'}}>Nova data</div>
              <input type="date" value={novaData} onChange={e=>setNovaData(e.target.value)}
                style={{border:'1.5px solid #C2D4FF',borderRadius:'8px',padding:'6px 10px',fontSize:'13px',outline:'none'}} />
            </div>
            <div>
              <div style={{fontSize:'10px',fontWeight:'700',color:'#2F6BFF',marginBottom:'4px'}}>Horário</div>
              <input type="time" value={novaHora} onChange={e=>setNovaHora(e.target.value)}
                style={{border:'1.5px solid #C2D4FF',borderRadius:'8px',padding:'6px 10px',fontSize:'13px',outline:'none'}} />
            </div>
            <button onClick={salvarReagendar} disabled={salvando}
              style={{padding:'7px 14px',borderRadius:'8px',border:'none',background:'#2F6BFF',color:'white',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
              {salvando?'Salvando…':'Confirmar'}
            </button>
            <button onClick={()=>setReagendando(null)}
              style={{padding:'7px 12px',borderRadius:'8px',border:'1px solid #C2D4FF',background:'white',color:'#2F6BFF',fontSize:'12px',cursor:'pointer'}}>
              Cancelar
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <Portal>
    <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={onClose}>
      <div style={{background:'white',borderRadius:'20px',boxShadow:'0 24px 80px rgba(0,0,0,0.2)',width:'100%',maxWidth:'460px',maxHeight:'88vh',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
        {}
        <div style={{padding:'18px 22px',borderBottom:'1px solid #E2E4E9',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <div style={{fontSize:'16px',fontWeight:'800',color:'#16181D'}}><Ico n="clipboard-list" /> Aulas — {aluno.nome}</div>
                {pendentes.length > 0 && (
                  <span style={{background:'#FEF3C7',border:'1px solid #FDE68A',color:'#92400E',fontSize:'11px',fontWeight:'700',padding:'2px 8px',borderRadius:'20px'}}>
                    <Ico n="alert-triangle" c="#D97706" /> {pendentes.length} pendente{pendentes.length!==1?'s':''}
                  </span>
                )}
              </div>
              <div style={{fontSize:'12px',color:'#6E7480',marginTop:'3px',display:'flex',gap:'14px',flexWrap:'wrap'}}>
                <span><Ico n="circle-check" c="#0E9F6E" /> {realizadas.length + anterioresN} realizadas{anterioresN > 0 ? ` (${anterioresN} antes do app)` : ''}</span>
                <span><Ico n="calendar" /> {agendadasTodas.length} agendadas</span>
                {(() => {
                  const totalFin = aulas.filter(a => a.status !== 'agendada' && !rotuloEspecialAula(a)).length;
                  // pausa / feriado (v131) nao sao cancelamento do aluno
                  const cancAluno = aulas.filter(a => (a.status === 'cancelada_aluno' || a.status === 'falta') && !rotuloEspecialAula(a)).length;
                  const pct = totalFin > 0 ? Math.round(cancAluno / totalFin * 100) : 0;
                  return cancAluno > 0 ? (
                    <span style={{color:'#DC2626'}}><Ico n="circle-x" c="#DC2626" /> {cancAluno} pelo aluno ({pct}%)</span>
                  ) : null;
                })()}
              </div>
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0AB',fontSize:'22px',lineHeight:1}}><Ico n="x" /></button>
          </div>
        </div>
        {}
        <div style={{flex:1,overflowY:'auto',padding:'18px 22px'}}>

          {}
          {pendentes.length > 0 && (
            <div style={{marginBottom:'20px',background:'#FFFBEB',border:'1.5px solid #FDE68A',borderRadius:'14px',padding:'14px 16px'}}>
              <div style={{fontSize:'12px',fontWeight:'800',color:'#92400E',marginBottom:'10px',display:'flex',alignItems:'center',gap:'6px'}}>
                <span style={{fontSize:'16px'}}><Ico n="alert-triangle" c="#D97706" /></span>
                {pendentes.length === 1 ? 'Aula sem confirmação' : `${pendentes.length} aulas sem confirmação`}
                <span style={{fontSize:'10px',fontWeight:'500',color:'#B45309',marginLeft:'4px'}}>— o que aconteceu?</span>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                {pendentes.map(au => {
                  const carregando = atualizandoStatus === au.id;
                  return (
                    <div key={au.id} style={{background:'white',borderRadius:'10px',border:'1px solid #FDE68A',overflow:'hidden'}}>
                      <div style={{padding:'8px 12px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'8px'}}>
                        <div>
                          <span style={{fontSize:'13px',fontWeight:'700',color:'#92400E'}}>{fmtDate(au.data)}</span>
                          <span style={{fontSize:'12px',color:'#B45309',marginLeft:'8px'}}>{(au.hora||'').slice(0,5)}</span>
                        </div>
                        <div style={{display:'flex',gap:'5px',flexWrap:'wrap'}}>
                          <button disabled={carregando} onClick={()=>atualizarStatus(au,'realizada')}
                            style={{fontSize:'11px',padding:'4px 10px',borderRadius:'7px',border:'none',background:'#DCF5EC',color:'#0A6F4D',cursor:carregando?'not-allowed':'pointer',fontWeight:'700',opacity:carregando?0.6:1}}>
                            <Ico n="circle-check" c="#0E9F6E" /> Realizada
                          </button>
                          <button disabled={carregando} onClick={()=>atualizarStatus(au,'falta')}
                            style={{fontSize:'11px',padding:'4px 10px',borderRadius:'7px',border:'none',background:'#FEF3C7',color:'#92400E',cursor:carregando?'not-allowed':'pointer',fontWeight:'700',opacity:carregando?0.6:1}}>
                            <Ico n="user-off" /> Aluno faltou
                          </button>
                          <button disabled={carregando} onClick={()=>atualizarStatus(au,'cancelada_aluno')}
                            style={{fontSize:'11px',padding:'4px 10px',borderRadius:'7px',border:'none',background:'#C2D4FF',color:'#1C43AE',cursor:carregando?'not-allowed':'pointer',fontWeight:'700',opacity:carregando?0.6:1}}>
                            <Ico n="phone" /> Aluno cancelou
                          </button>
                          <button disabled={carregando} onClick={()=>atualizarStatus(au,'cancelada_professor')}
                            style={{fontSize:'11px',padding:'4px 10px',borderRadius:'7px',border:'none',background:'#FEE2E2',color:'#991B1B',cursor:carregando?'not-allowed':'pointer',fontWeight:'700',opacity:carregando?0.6:1}}>
                            <Ico n="refresh" /> Eu cancelei
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {}
          {agendadasTodas.length > 0 && (
            <div style={{marginBottom:'20px'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:'#2F6BFF',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px'}}>
                <Ico n="calendar" /> Próximas aulas {!verTodasAgendadas && agendadasOcultas > 0 ? '(próximos 30 dias)' : 'agendadas'}
              </div>
              {agendadasVisiveis.length === 0
                ? <div style={{fontSize:'12px',color:'#9AA0AB',padding:'8px 0'}}>Nenhuma aula nos próximos 30 dias.</div>
                : agendadasVisiveis.map(au => <AulaRow key={au.id} au={au} permitirReagendar={true} />)
              }
              {agendadasOcultas > 0 && !verTodasAgendadas && (
                <button onClick={()=>setVerTodasAgendadas(true)}
                  style={{width:'100%',marginTop:'6px',padding:'9px',borderRadius:'10px',border:'1.5px dashed #C2D4FF',background:'#F7F8FA',color:'#2F6BFF',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
                  Ver mais {agendadasOcultas} aulas do plano <Ico n="arrow-right" />
                </button>
              )}
              {verTodasAgendadas && agendadasOcultas > 0 && (
                <button onClick={()=>setVerTodasAgendadas(false)}
                  style={{width:'100%',marginTop:'6px',padding:'9px',borderRadius:'10px',border:'1.5px dashed #E2E4E9',background:'#F7F8FA',color:'#9AA0AB',fontSize:'12px',cursor:'pointer'}}>
                  Mostrar menos <Ico n="arrow-up" />
                </button>
              )}
            </div>
          )}
          {}
          {realizadas.length > 0 && (
            <div>
              <div style={{fontSize:'11px',fontWeight:'700',color:'#0E9F6E',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'8px'}}><Ico n="circle-check" c="#0E9F6E" /> Histórico de aulas realizadas</div>
              {realizadas.map(au => <AulaRow key={au.id} au={au} permitirReagendar={false} />)}
            </div>
          )}
          {aulas.length === 0 && (
            <div style={{textAlign:'center',color:'#6E7480',padding:'28px 12px',fontSize:'13px',lineHeight:1.5}}>
              {anterioresN > 0
                ? <>As <strong>{anterioresN} aulas</strong> deste pacote foram feitas antes de entrar no Plen, por isso não aparecem uma a uma. {anterioresN >= totalPacoteN && totalPacoteN > 0 ? 'O pacote terminou — é hora de renovar.' : ''}</>
                : 'Nenhuma aula registrada.'}
            </div>
          )}
        </div>
      </div>
    </div>
    </Portal>

    {}
    {perguntandoReposicao && (
      <Portal>
      <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.7)',zIndex:70,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
        <div style={{background:'white',borderRadius:'20px',padding:'28px',maxWidth:'360px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
          <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="clipboard-list" /> Reposição de aula</div>
          <div style={{fontSize:'13px',color:'#6E7480',marginBottom:'6px'}}>
            Aula de <strong>{aluno.nome}</strong> cancelada pelo aluno
          </div>
          <div style={{fontSize:'12px',color:'#9AA0AB',marginBottom:'20px'}}>
            {new Date((perguntandoReposicao.data||'')+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})}
            {' às '}{(perguntandoReposicao.hora||'').slice(0,5)}
          </div>
          <div style={{fontSize:'14px',fontWeight:'700',color:'#3D424C',marginBottom:'14px'}}>Esta aula será reposta?</div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <button onClick={()=>registrarReposicao(perguntandoReposicao, true)}
              style={{padding:'14px',borderRadius:'12px',border:'none',background:'#DCF5EC',color:'#0A6F4D',fontSize:'14px',fontWeight:'800',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
              <span style={{fontSize:'20px'}}><Ico n="circle-check" c="#0E9F6E" /></span>
              <div>
                <div style={{fontWeight:'800'}}>Sim, será reposta</div>
                <div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>Crédito mantido — aula será reagendada</div>
              </div>
            </button>
            <button onClick={()=>registrarReposicao(perguntandoReposicao, false)}
              style={{padding:'14px',borderRadius:'12px',border:'none',background:'#FEF2F2',color:'#991B1B',fontSize:'14px',fontWeight:'800',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
              <span style={{fontSize:'20px'}}><Ico n="circle-x" c="#DC2626" /></span>
              <div>
                <div style={{fontWeight:'800'}}>Não, sem reposição</div>
                <div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>Consome uma ficha de reposição do aluno</div>
              </div>
            </button>
          </div>
        </div>
      </div>
      </Portal>
    )}
    </>
  );
}

// Seguranca convites (01/10): sorteio com o gerador seguro do navegador (Math.random e'
// previsivel). Continua com 6 letras porque, com o SQL novo, quem decide o codigo e' o BANCO
// (10 letras, sorteadas la'); antes do SQL a coluna so' aceita 6.
function gerarCodigo() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const n = new Uint32Array(6);
  try { crypto.getRandomValues(n); } catch { for (let i = 0; i < 6; i++) n[i] = Math.floor(Math.random() * 4294967296); }
  return Array.from(n, x => chars[x % chars.length]).join('');
}

async function criarConvite({ tipo, professor_id = null, aluno_id = null, dados = {} }) {

  for (let i = 0; i < 3; i++) {
    const code = gerarCodigo();
    // Seguranca convites (01/10): o banco troca o codigo por um sorteado por ele — o link tem
    // de usar o que voltou gravado. VOLTA: sem o SQL novo, volta o mesmo codigo enviado.
    const { data, error } = await supabase.from('convites').insert({
      code, tipo, professor_id, aluno_id, dados,
      expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    }).select('code').single();
    if (!error) return (data && data.code) || code;
  }
  throw new Error('Não foi possível gerar código de convite');
}

// Seguranca convites (01/10): a tabela de convites deixou de ser publica (qualquer pessoa,
// ate' sem login, lia todos os codigos com nome e telefone dos alunos). Quem abre o link le
// o convite pela funcao ver_convite do banco, que devolve so' o que a tela de entrada usa
// (tipo, professor, ficha, nome do professor e do aluno). _seguro marca que o banco ja' esta'
// com as regras novas. VOLTA: se a funcao ainda nao existe (SQL nao rodou), leitura antiga.
async function resolverConvite(code) {
  if (!code) return null;
  const { data: seguro, error: eSeg } = await supabase.rpc('ver_convite', { p_code: code });
  if (!eSeg) {
    // Muitas tentativas erradas: o banco segura por 10 minutos. Guarda o codigo e tenta depois.
    // (resolverConvite.motivo: so' para a central de uso distinguir os dois "null")
    if (seguro && seguro.bloqueado) { resolverConvite.motivo = 'convite_bloqueado'; return null; }
    // Codigo ja' usado, vencido ou que nao existe: esquece, senao toda abertura do app pergunta de novo.
    if (!seguro) { resolverConvite.motivo = 'convite_invalido'; try { localStorage.removeItem('pendingInviteCode'); } catch {} return null; }
    return { ...seguro, _seguro: true };
  }
  const { data } = await supabase
    .from('convites')
    .select('*')
    .eq('code', code.toUpperCase())
    .gt('expires_at', new Date().toISOString())
    .is('usado_em', null)
    .maybeSingle();
  return data;
}

// Seguranca convites (01/10): o professor confirmando o convite que o ALUNO mandou. Antes
// marcava "usado" direto na tabela (so' funcionava porque qualquer um alterava qualquer
// convite). Agora o banco marca e guarda qual professor aceitou. VOLTA: sem a funcao, update antigo.
// Devolve true se o pedido ficou aceito por este professor.
async function marcarConviteUsado(code) {
  if (!code) return false;
  const { data, error } = await supabase.rpc('professor_aceitar_convite', { p_code: code });
  if (!error) return !!(data && data.ok);
  await supabase.from('convites').update({ usado_em: new Date().toISOString() }).eq('code', code.toUpperCase());
  return true;
}

// Seguranca convites (01/10): o professor aceita o pedido do aluno e guarda a proposta (dias,
// horario, valor) para o cadastro abrir JA' PREENCHIDO. Vai junto o codigo do PEDIDO: o
// cadastro grava esse codigo no convite que nasce com o aluno, e e' por ele que o banco mostra o
// aviso "professor te convidou" ao aluno certo (nome e telefone nao servem mais para isso).
// Usado no cadastro pelo Google (TipoUsuario) e no app do professor (conta por e-mail ou ja'
// existente) — antes so' o primeiro aceitava, e nos outros o pedido ficava esquecido.
async function aceitarPedidoDoAluno(cd) {
  const code = String(cd?._code || '').trim().toUpperCase();
  if (!code) return { ok: false };
  let prop = cd.proposta || cd.dados?.proposta || null;
  let alvo = cd.aid || cd.aluno_id || null;
  // O convite lido antes do login vem sem a proposta (ela tem telefone e valor). Le de novo,
  // ja' logado, ANTES de aceitar — depois de aceito ele nao abre mais.
  if (!prop) {
    const c = await resolverConvite(code);
    if (c) { prop = c.dados?.proposta || null; alvo = alvo || c.aluno_id || null; }
  }
  const ok = await marcarConviteUsado(code);
  try { localStorage.removeItem('pendingInviteCode'); } catch {}
  if (!ok) return { ok: false };
  // Revisao v142 (achado 7 da ida e volta): o link SEM combinado ("Ou so' gere o link" do aluno) tambem guarda
  // o pedido e a ficha — o "Novo aluno" abre com "Seu WhatsApp" e o convite que nasce leva o codigo do pedido
  // (e' assim que o aviso automatico chega ao aluno). O nome vai se nao for e-mail (lerProposta descarta).
  if (alvo) {
    try {
      const base = prop || { aluno_nome: String(cd.anome || cd.dados?.anome || '').slice(0, 80) };
      localStorage.setItem('propostaDoAluno', JSON.stringify({ ...base, aluno_id: alvo, pedido: code, quando: Date.now(), ...(prop ? {} : { so_pedido: true }) }));
    } catch (e) {}
    return { ok: true, temProposta: true };
  }
  return { ok: true };
}

// Seguranca convites (01/10): assumir_ficha_aluno no banco novo devolve {ok:false} quando o
// codigo nao existe (para a tentativa ficar anotada); no antigo, so' dava erro. Aqui vira um
// {error} so', igual para os dois.
async function rpcAssumirFicha(code, alunoId) {
  const { data, error } = await supabase.rpc('assumir_ficha_aluno', { p_code: code, p_aluno_id: alunoId });
  if (error) return { error };
  if (data && data.ok === false) return { error: { message: data.error || 'Convite inválido, vencido ou já usado.' } };
  return { error: null, data };
}

// ---------------------------------------------------------------------------
// Pecas do cadastro guiado que os DOIS lados usam iguais (v140, Marcus 16/10: "o aluno tem
// que poder fazer esse trabalho da outra ponta, da mesma maneira"):
//  * professor — "Novo aluno" (ModalNovoContrato);
//  * aluno — "Adicionar professor" (ModalAdicionarProfessor).
// Mudou aqui, muda nos dois. Quem guarda o estado e' a tela; aqui so' o desenho e os toques.
// ---------------------------------------------------------------------------

// Passo "Quem e'" dos DOIS lados (v142, Marcus 20/10): so' o NOME, com o teclado ja' aberto. O
// numero saiu do caminho de quem cadastra — contato salvo so' no WhatsApp aparece no iPhone como "A
// Siri encontrou em apps" e nenhum app consegue ler. A agenda vira atalho pequeno: se trouxer o
// numero, ele fica guardado (sem campo na tela) e o convite vai direto para a pessoa; sem numero, o
// WhatsApp abre para escolher a pessoa la'. Quem RECEBE o convite confirma o proprio WhatsApp.
// (Antes havia uma primeira tela "Escolher da agenda / Digitar o nome" — saiu.)
const TEXTO_SEM_NUMERO = 'Não precisa do número: no fim você manda o convite pelo WhatsApp.';
// tel: o numero guardado (veio da agenda ou do pedido do aluno). Revisao da v142 (20/10): ele SEMPRE
// aparece numa linha pequena, com "Tirar" — escondido, ele ficava preso ao trocar de contato (o convite de
// "Carla" ia para o WhatsApp da Bia) e travava o cadastro por "WhatsApp repetido" sem nada na tela.
// onLimpar: apagou o nome inteiro — o numero sai junto (venha de onde vier), senao o convite iria para o
// contato antigo com o nome de outra pessoa. onTirarTel: o "Tirar" da linha do numero.
function CampoSoONome({ valor, onValor, placeholder, inputRef, onEnter, entrada, tel, rotuloTel = 'WhatsApp da agenda', textoAgenda, temAgenda, onAgenda, onLimpar, onTirarTel, estilo, notaTel }) {
  const temNumero = !!digitosWhatsSM(tel);
  return (
    <>
      {entrada === 'agenda' && <AvisoVeioDaAgenda>{textoAgenda}</AvisoVeioDaAgenda>}
      {entrada === 'vazia' && <AvisoAgendaVazia>Não veio da agenda? Tudo bem: digite o nome.</AvisoAgendaVazia>}
      <input ref={inputRef} value={valor} placeholder={placeholder}
        onChange={e => { const v = e.target.value; onValor(v); if (!v.trim() && temNumero && onLimpar) onLimpar(); }}
        autoFocus={entrada !== 'agenda'} autoCapitalize="words" enterKeyHint="next" style={estilo || INPUT_ACORDO}
        onKeyDown={e => { if (e.key === 'Enter' && onEnter) onEnter(); }} />
      {temNumero ? (
        <div data-tel-guardado="1" style={{display:'flex', alignItems:'center', gap:'8px', marginTop:'6px', fontSize:'13px', color:'#3D424C', lineHeight:1.45}}>
          <Ico n="brand-whatsapp" s={15} c="#0E9F6E" />
          <span style={{flex:1, minWidth:0}}>
            {rotuloTel}: <b style={{whiteSpace:'nowrap'}}>{tel}</b>
            {notaTel || (whatsValidoSM(tel) ? ' — o convite vai direto para ele.' : ' — sem DDD: no fim, o WhatsApp abre para você escolher a pessoa.')}
          </span>
          {onTirarTel && (
            <button type="button" onClick={onTirarTel} aria-label="Tirar o WhatsApp"
              style={{flexShrink:0, minHeight:'44px', minWidth:'44px', border:'none', background:'none', color:'#2F6BFF', fontWeight:'700', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', padding:'0 4px'}}>
              Tirar
            </button>
          )}
        </div>
      ) : (
        <div style={{fontSize:'13px', color:'#6E7480', marginTop:'8px', lineHeight:1.5}}>{TEXTO_SEM_NUMERO}</div>
      )}
      {temAgenda && <LinkAgendaDepois onClick={onAgenda} outro={entrada === 'agenda'} />}
    </>
  );
}

// WhatsApp de quem RECEBE o convite (v142, item D): ele mesmo confirma o numero ao aceitar. Obrigatorio
// so' quando a ficha (ou o cadastro do professor) ainda nao tem numero; com numero, vem preenchido.
// So' digitos: 10 ou 11 (DDD + numero), aceitando o +55 na frente.
function digitosWhatsSM(t) { let d = String(t || '').replace(/\D/g, ''); if (d.length >= 12 && d.startsWith('55')) d = d.slice(2); return d; }
function whatsValidoSM(t) { const d = digitosWhatsSM(t); return d.length === 10 || d.length === 11; }
function mesmoWhatsSM(a, b) { return digitosWhatsSM(a) === digitosWhatsSM(b); }
// Link do WhatsApp: com numero vai direto para a pessoa; sem numero abre para escolher la'.
// Numero incompleto (contato da agenda salvo sem DDD) tambem abre para escolher — wa.me com 8
// digitos cai em "este numero nao esta' no WhatsApp".
function linkWhatsConviteSM(fone, texto) {
  const d = whatsValidoSM(fone) ? digitosWhatsSM(fone) : '';
  return (d ? `https://wa.me/55${d}` : 'https://wa.me/') + '?text=' + encodeURIComponent(texto || '');
}
function CampoSeuWhats({ valor, onValor, obrigatorio, quem, erro, escuro = false, inputRef, dica }) {
  const cor = escuro ? 'rgba(255,255,255,.75)' : '#6E7480';
  return (
    <div style={{marginTop:'14px', textAlign:'left'}}>
      <label style={{display:'block', fontSize:'12px', fontWeight:'800', color: escuro ? 'white' : '#3D424C', marginBottom:'6px', letterSpacing:'.02em'}}>
        Seu WhatsApp{obrigatorio ? '' : ' (confira)'}
      </label>
      <input ref={inputRef} value={valor} onChange={e => onValor(mascaraTel(e.target.value))} placeholder="+55 (11) 99999-9999"
        inputMode="tel" autoComplete="tel" aria-invalid={!!erro}
        style={{width:'100%', padding:'13px 14px', borderRadius:'12px', border:'1.5px solid ' + (erro ? '#DC2626' : '#E2E4E9'), fontSize:'16px', outline:'none', boxSizing:'border-box', background:'white', color:'#16181D'}} />
      <div style={{fontSize:'12px', color: erro ? '#DC2626' : cor, marginTop:'6px', lineHeight:1.45, fontWeight: erro ? 700 : 400}}>
        {erro || dica || `${quem} usa este número para falar com você.`}
      </div>
    </div>
  );
}

// Faixa verde depois de escolher da agenda (texto muda de um lado para o outro).
function AvisoVeioDaAgenda({ children }) {
  return (
    <div style={{fontSize:'12.5px', color:'#0A6F4D', background:'#DCF5EC', borderRadius:'10px', padding:'8px 12px', marginBottom:'10px'}}>
      <Ico n="check" s={14} c="#0A6F4D" /> {children}
    </div>
  );
}

// Fechou a agenda sem trazer ninguem (desistiu, ou tocou num cartao que o iPhone nao devolve — ex.: sugestao
// da Siri "a Siri encontrou em WhatsApp", Marcus 19/10): vai direto para os campos, com uma dica curta.
function AvisoAgendaVazia({ children }) {
  return (
    <div style={{fontSize:'12.5px', color:'#3D424C', background:'#F7F8FA', border:'1px solid #E2E4E9', borderRadius:'10px', padding:'8px 12px', marginBottom:'10px', lineHeight:1.45}}>
      <Ico n="info-circle" s={14} c="#6E7480" /> {children}
    </div>
  );
}

// Atalho pequeno para a agenda do celular (embaixo do nome). Depois de escolher, vira "Escolher
// outro da agenda" (pegou o contato errado: troca sem apagar nada a mao).
function LinkAgendaDepois({ onClick, outro = false }) {
  return (
    <button type="button" onClick={onClick}
      style={{marginTop:'4px', minHeight:'44px', border:'none', background:'none', color:'#2F6BFF', fontWeight:'700', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'6px', padding:'0'}}>
      <Ico n="address-book" s={16} c="#2F6BFF" /> {outro ? 'Escolher outro da agenda' : 'Escolher da agenda do celular'}
    </button>
  );
}

// Horario do treino depois de marcar os dias: o MESMO em todos OU um por dia.
//   horaDe(d) = horario daquele dia (o proprio, se separou por dia; senao o unico)
//   onSeparar = "treina em horarios diferentes?" (a tela copia o horario unico para cada dia)
//   onJuntar  = "usar o mesmo horario em todos"
function HorariosDosDias({ dias, rotulos, hora, onHora, horaDe, onHoraDia, porDia, onSeparar, onJuntar, textoSeparar }) {
  const horaCss = { padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #E2E4E9',
                    fontSize: '17px', fontWeight: '700', outline: 'none', fontFamily: 'inherit' };
  // Link com 44px de altura (area de toque do dedo) — com 19px o "horarios diferentes" escapava.
  const linkCss = { marginTop: '6px', minHeight: '44px', display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: 'none', border: 'none', color: '#2455D6', fontSize: '14px', fontWeight: '700',
                    cursor: 'pointer', padding: 0, textAlign: 'left', fontFamily: 'inherit' };
  const sub = t => <div style={{fontSize: '13px', color: '#6E7480', margin: '16px 0 6px'}}>{t}</div>;
  if (!porDia) return (
    <>
      {sub(dias.length > 1 ? `Horário — o mesmo nos ${dias.length} dias` : 'Horário')}
      <input type="time" value={hora} onChange={e => onHora(e.target.value)} style={horaCss} />
      {dias.length > 1 && (
        <div><button type="button" onClick={onSeparar} style={linkCss}><Ico n="clock" /> {textoSeparar}</button></div>
      )}
    </>
  );
  // Separou por dia e depois desmarcou todos: nada de titulo sem linha embaixo.
  if (!dias.length) return null;
  return (
    <>
      {sub('Horário de cada dia')}
      <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
        {dias.map(d => (
          <div key={d} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                               gap: '12px', background: '#F7F8FA', borderRadius: '12px', padding: '8px 8px 8px 14px'}}>
            <span style={{fontSize: '15px', fontWeight: '700', color: '#3D424C'}}>{rotulos[d]}</span>
            <input type="time" value={horaDe(d)} onChange={e => onHoraDia(d, e.target.value)}
              style={{...horaCss, background: 'white'}} />
          </div>
        ))}
      </div>
      <div><button type="button" onClick={onJuntar} style={linkCss}><Ico n="arrow-back-up" /> Usar o mesmo horário em todos</button></div>
    </>
  );
}

// Horarios por dia que vieram de fora (proposta do aluno, JSON com chave texto "1"/"3"):
// devolve {1:'07:00', 3:'18:00'} so' com os dias marcados e horario valido, ou null.
function horariosDaProposta(dias, horarios) {
  if (!horarios || typeof horarios !== 'object' || !Array.isArray(dias)) return null;
  const out = {};
  for (const d of dias) {
    const h = horarios[d] ?? horarios[String(d)];
    if (/^\d{1,2}:\d{2}/.test(String(h || ''))) out[Number(d)] = String(h).slice(0, 5).padStart(5, '0');
  }
  return Object.keys(out).length ? out : null;
}

// ---------------------------------------------------------------------------
// O COMBINADO (v143, Marcus 20/10: "tem que ser a MESMA LOGICA de cadastro do lado do aluno. A
// diferenca e' que o aluno tem a opcao de deixar em branco e o professor preenche").
// As perguntas Quanto -> Dias e duracao -> Ja' comecou? -> Ja' pagou? -> Confira sao as MESMAS
// pecas nos dois lados: "Novo aluno" (professor) e "Adicionar professor" (aluno). Cada tela
// guarda o proprio estado e monta o objeto `a` com os mesmos nomes; `c = contaDoAcordo(a)`
// faz as contas; `validarPassoAcordo` decide o que trava. Mudou aqui, muda nos dois.
// `voz` = 'professor' | 'aluno' (so' muda o texto). Tudo aqui e' de nivel de modulo: componente
// declarado dentro do wizard troca de tipo a cada render e o campo perde o foco a cada letra.
// ---------------------------------------------------------------------------
const MAX_AULAS = 200;
const DURACOES = [30, 45, 60, 90];
const DIAS_ACORDO = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const rotuloDuracao = m => m === 60 ? '1h' : m === 90 ? '1h30' : m + ' min';
const fmtDiaMes = iso => iso ? String(iso).slice(8, 10) + '/' + String(iso).slice(5, 7) : '';   // '2026-10-20' -> '20/10'
// v149 (proximo pacote): 'qui 29/10' e o 1o dia de aula (dos dias da semana do acordo) a partir de uma data.
const DIA_SEMANA_CURTO_SM = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const diaEDataSM = iso => iso ? DIA_SEMANA_CURTO_SM[new Date(String(iso).slice(0, 10) + 'T12:00:00').getDay()] + ' ' + fmtDiaMes(iso) : '';
function somarDiaSM(iso, n) { const d = new Date(String(iso).slice(0, 10) + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toLocaleDateString('sv-SE'); }
function primeiraAulaDesdeSM(dias, iso) {
  if (!iso || !dias || !dias.length) return iso || '';
  for (let k = 0; k < 14; k++) { const x = somarDiaSM(iso, k); if (dias.includes(new Date(x + 'T12:00:00').getDay())) return x; }
  return iso;
}
// Dinheiro: inteiro sem centavos ("R$ 200"); quebrado sempre com 2 casas ("R$ 187,50", nunca "187,5").
const numAcordoBR = n => { const v = Math.round(Number(n || 0) * 100) / 100;
  return v.toLocaleString('pt-BR', Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
const brlAcordo = n => 'R$ ' + numAcordoBR(n);
// Modalidade que o aluno digitou ("balé", "tenis"): vira a grafia da lista do professor, senao a do
// app, senao o texto com a 1a letra maiuscula. Assim a ficha e os chips reconhecem o valor.
function modalidadeDoPedido(texto, modsProf) {
  const x = String(texto || '').trim().slice(0, 40);
  if (!x) return '';
  const k = semAcentoSM(x);
  return (modsProf || []).find(m => semAcentoSM(m) === k) || MODALIDADES_PROF.find(m => semAcentoSM(m) === k)
    || x.charAt(0).toUpperCase() + x.slice(1);
}

// Estilos (eram locais do ModalNovoContrato; mesmos valores).
function chipAcordo(ativo) {
  return {
    padding: '11px 16px', borderRadius: '12px', fontSize: '15px', cursor: 'pointer', fontWeight: ativo ? '800' : '600',
    border: ativo ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9',
    background: ativo ? '#E7EDFF' : 'white', color: ativo ? '#2F6BFF' : '#3D424C',
  };
}
const GRADE_ACORDO = { display: 'flex', flexWrap: 'wrap', gap: '8px' };
const INPUT_ACORDO = { width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #E2E4E9', fontSize: '16px', outline: 'none' };
const NUM_ACORDO = { ...INPUT_ACORDO, fontSize: '22px', fontWeight: '800', textAlign: 'center' };
function SubAcordo({ children }) {
  return <div style={{fontSize: '13px', color: '#6E7480', margin: '16px 0 6px'}}>{children}</div>;
}

// v144 (Marcus, 21/10): sem prazo combinado = desmarcar LIVRE — avisou antes do horario da aula, a aula nao conta.
// A regra de desmarcar so' existe se alguem escolher (antes o "Novo aluno" gravava 24h sem ninguem escolher).
// valor: 0 = livre; N = avisar com N horas. Mesma pergunta no "Novo aluno" e no "Adicionar professor".
const PRAZOS_DESMARCAR = [2, 6, 12, 24, 48];
function textoPrazoDesmarcar(h) { return Number(h) > 0 ? `avisar com ${Number(h)}h de antecedência` : 'livre — sem prazo combinado'; }
function PerguntaPrazoDesmarcar({ valor, onValor, padrao }) {
  const h = Number(valor) > 0 ? Number(valor) : 0;
  const opcoes = h && !PRAZOS_DESMARCAR.includes(h) ? [...PRAZOS_DESMARCAR, h].sort((x, y) => x - y) : PRAZOS_DESMARCAR;
  const mini = on => ({ ...chipAcordo(on), fontSize: '12.5px', padding: '9px 13px' });
  return (
    <div data-prazo-desmarcar={h ? String(h) : 'livre'} style={{marginTop: '14px'}}>
      <div style={{fontSize: '12px', fontWeight: '700', color: '#9AA0AB', marginBottom: '7px'}}>COMBINARAM PRAZO PARA DESMARCAR?</div>
      <div style={{display: 'flex', gap: '7px', flexWrap: 'wrap'}}>
        <button type="button" onClick={() => onValor(0)} style={mini(!h)}>Não — livre</button>
        <button type="button" onClick={() => { if (!h) onValor(Number(padrao) > 0 ? Number(padrao) : 24); }} style={mini(!!h)}>Sim — com prazo</button>
      </div>
      {!!h && (
        <div style={{display: 'flex', gap: '7px', flexWrap: 'wrap', marginTop: '8px'}}>
          {opcoes.map(x => <button key={x} type="button" onClick={() => onValor(x)} style={mini(h === x)}>{x}h antes</button>)}
        </div>
      )}
      <div style={{fontSize: '11.5px', color: '#9AA0AB', marginTop: '7px', lineHeight: 1.45}}>
        {h ? `Avisou com menos de ${h}h, a aula conta.` : 'Avisou antes do horário da aula, a aula não conta.'}
      </div>
    </div>
  );
}

// Horario de um dia. Separado por dia, o dia apagado fica VAZIO e trava o Continuar (regra do
// aluno desde a v140; o professor voltava escondido para o horario unico).
function horaDoDiaAcordo(a, d) { return a.porDia ? (a.horarios[d] ?? a.hora) : a.hora; }

// As contas do combinado (eram soltas no ModalNovoContrato; mesmas formulas).
function contaDoAcordo(a) {
  const num = numDinheiro;
  const ehMensal = a.modo === 'mes';
  // "Por aula" nao fala em pacote (Marcus, 30/09). Aluno que ainda nao escolheu (null) fala em "aulas".
  const porAula = a.modo !== 'pacote' && !ehMensal;
  const pacoteN = Math.max(0, Math.round(num(a.qtdAulas)));
  const totalPacote = a.modo === 'aula' ? num(a.valorAula) * pacoteN : a.modo === 'pacote' ? num(a.valorPacote) : 0;
  const valorAulaEfetivo = a.modo === 'aula' ? num(a.valorAula) : (a.modo === 'pacote' && pacoteN > 0 ? num(a.valorPacote) / pacoteN : 0);
  const valorDoModo = a.modo === 'aula' ? num(a.valorAula) : a.modo === 'pacote' ? num(a.valorPacote) : a.modo === 'mes' ? num(a.valorMes) : 0;
  const mensalN = num(a.valorMes);
  // Quantas ja' aconteceram — nunca passa de N (a trava feitas_demais mostra o motivo).
  const feitasN = a.novoPacote === true ? 0 : Math.min(Math.max(0, Math.round(num(a.feitas))), pacoteN);
  const restantes = Math.max(0, pacoteN - feitasN);
  const horarios = {};
  a.dias.forEach(d => { horarios[d] = horaDoDiaAcordo(a, d); });
  const agendaTxt = descreverAgenda({ dias: a.dias, hora: a.dias.length ? horarios[a.dias[0]] : a.hora, horarios });
  const aulasMes = ehMensal && a.dias.length ? { min: a.dias.length * 4, max: a.dias.length * 5, porSemana: a.dias.length } : null;
  return { ehMensal, porAula, pacoteN, totalPacote, valorAulaEfetivo, valorDoModo, mensalN, feitasN, restantes, horarios, agendaTxt, aulasMes };
}

// Titulo e ajuda de cada pergunta, nas duas vozes.
function textosPassoAcordo(passo, voz, c) {
  const aluno = voz === 'aluno';
  if (passo === 2) return aluno
    ? { titulo: 'Quanto você paga a ele?', ajuda: 'Do jeito que vocês combinaram' }
    : { titulo: 'Quanto você cobra?', ajuda: 'Do jeito que você fala com o aluno' };
  if (passo === 4) return { titulo: aluno ? 'Que dias você treina com ele?' : 'Que dias ele treina?', ajuda: 'Marque os dias e confira o horário' };
  if (passo === 5) return {
    titulo: c.porAula ? 'Essas aulas começam agora?' : 'Esse pacote começa agora?',
    ajuda: aluno ? 'Ou vocês já vêm treinando e já fizeram algumas aulas' : 'Ou ele já vem treinando e você está passando pro app',
  };
  if (passo === 6) return {
    titulo: aluno ? (c.porAula ? 'Você já pagou essas aulas?' : 'Você já pagou esse pacote?')
                  : (c.porAula ? 'Essas aulas já estão pagas?' : 'Esse pacote já está pago?'),
    ajuda: aluno ? 'Ou você já pagou tudo, ou ainda vai pagar tudo'
      : c.porAula ? 'É tudo junto: ou está pago, ou está todo em aberto' : 'Pacote é inteiro: ou está pago, ou está todo em aberto',
  };
  return { titulo: '', ajuda: '' };
}

// O que trava cada pergunta (passos 2, 4, 5 e 6, nos dois lados). Devolve null ou { codigo, msg }.
// parcial = "Nao sei — ele preenche": nao cobra resposta que FALTA, mas continua barrando
// resposta ERRADA (quantidade demais, feitas demais, data impossivel).
function validarPassoAcordo(passo, a, c, opcoes) {
  const o = opcoes || {};
  const aluno = o.voz === 'aluno';
  const falta = !o.parcial;
  const num = numDinheiro;
  const erro = (codigo, msg) => ({ codigo, msg });
  if (passo === 2) {
    if (!a.modo) return falta ? erro('sem_forma', 'Escolha como você paga: por aula, pelo pacote ou por mês.') : null;
    if (a.modo === 'mes') {
      if (falta && c.mensalN <= 0) return erro('sem_mensalidade', 'Digite o valor da mensalidade');
      return null;
    }
    if (falta && a.modo === 'aula' && num(a.valorAula) <= 0) return erro('sem_valor_aula', 'Digite o valor da aula');
    if (falta && a.modo === 'pacote' && num(a.valorPacote) <= 0) return erro('sem_valor_pacote', 'Digite o valor do pacote');
    if (falta && c.pacoteN <= 0) return erro('sem_qtd_aulas', c.porAula ? 'Digite quantas aulas vocês combinaram' : 'Digite quantas aulas vêm no pacote');
    // v133: numero gigante (telefone colado) fazia a data de fim "estourar". Pacote de verdade nao passa de 200.
    if (c.pacoteN > MAX_AULAS) return erro('qtd_aulas_demais', `${c.pacoteN} aulas? Confira a quantidade — o máximo é ${MAX_AULAS} ${c.porAula ? 'de uma vez' : 'por pacote'}.`);
    return null;
  }
  if (passo === 4) {
    if (a.dias.length === 0) return erro('sem_dias', 'Marque pelo menos um dia da semana');
    if (!a.porDia && !a.hora) return erro('sem_horario', 'Escolha o horário do treino');
    if (a.porDia && a.dias.some(d => !horaDoDiaAcordo(a, d))) return erro('falta_horario_dia', 'Falta o horário de algum dia');
    // v143: o aluno pode deixar a duracao em branco ("Nao sei"); ai' o professor escolhe (nao vem
    // 1h marcado como se fosse resposta do aluno). Sem proposta o professor ja' comeca com 1h.
    if (!aluno && !(Number(a.duracao) > 0)) return erro('sem_duracao', 'Escolha quanto tempo dura a aula');
    return null;
  }
  if (passo === 5) {
    if (a.fila && a.fila.ativa && a.inicioFila && a.inicioFila < a.fila.inicio) return erro('fila_antes_da_ultima',
      `O pacote atual ainda tem aula até ${fmtDiaMes(a.fila.ultima)}. O novo começa a partir de ${fmtDiaMes(a.fila.inicio)}.`);
    if (falta && a.novoPacote === null) return erro('em_curso_sem_resposta', c.porAula ? 'Essas aulas começam agora ou já estavam rolando?' : 'Esse pacote começa agora ou já estava rolando?');
    if (a.novoPacote === false) {
      if (falta && a.feitas === '') return erro('sem_aulas_feitas', aluno
        ? (c.porAula ? 'Quantas dessas aulas vocês já fizeram?' : 'Quantas vocês já fizeram desse pacote?')
        : (c.porAula ? 'Quantas dessas aulas ele já fez?' : 'Quantas aulas ele já fez desse pacote?'));
      // Antes "12 feitas" num pacote de 10 virava "10 de 10" calado.
      if (a.feitas !== '' && c.pacoteN > 0 && Math.round(num(a.feitas)) > c.pacoteN) return erro('feitas_demais', c.porAula
        ? `Vocês combinaram ${c.pacoteN} aulas — confira quantas já foram feitas.`
        : `O pacote tem ${c.pacoteN} aulas — confira quantas já foram feitas.`);
      if (a.inicioCiclo && !dataValidaSM(a.inicioCiclo)) return erro('data_inicio_invalida', 'Data de início inválida — confira o ano.');
    }
    return null;
  }
  if (passo === 6) {
    if (a.pago === true && a.dataPgto && !dataValidaSM(a.dataPgto)) return erro('data_pagamento_invalida', 'Data de pagamento inválida — confira o ano.');
    if (falta && a.pago === null) return erro('pago_sem_resposta', aluno
      ? (c.porAula ? 'Me diz se você já pagou essas aulas' : 'Me diz se você já pagou esse pacote')
      : (c.porAula ? 'Me diz se essas aulas já foram pagas' : 'Me diz se esse pacote já foi pago'));
    return null;
  }
  return null;
}

// Linhas do resumo do professor e da tela Confira do aluno: [{ k, v, passo, vazio, pre }].
// passo = para onde vai o toque (null: nao se toca); vazio = a linha inteira e' "ele preenche";
// pre = so' o fim da linha e' "ele preenche" (o comeco e' o que o aluno respondeu, em preto).
// extras = linhas so' do professor (Seu liquido, Academia), logo depois das de dinheiro.
// detalhar (professor com o pedido do aluno): mostra tambem o "comeca agora", a data de inicio e
// a do pagamento — com o passo 5/6 pulado, o resumo e' o unico lugar onde o professor ve' isso.
function linhasDoAcordo(a, c, opcoes) {
  const o = opcoes || {};
  const aluno = o.voz === 'aluno';
  const num = numDinheiro;
  const L = [];
  const add = (k, v, passo, vazio, pre) => L.push({ k, v, passo: passo == null ? null : passo, vazio: !!vazio, pre: pre || '' });
  const PRE = 'ele preenche';
  const COB = aluno ? 'Pagamento' : 'Cobrança';   // "cobranca" e' palavra de quem recebe
  const hojeDia = new Date().getDate();
  const dataOk = d => d && dataValidaSM(d);
  L.push({ k: o.titulo || (aluno ? 'Professor' : 'Aluno'),
           v: (o.modalidade ? o.modalidade + ' · ' : '') + c.agendaTxt + (a.duracao ? ' · ' + rotuloDuracao(a.duracao) : ''),
           passo: 4, vazio: false, pre: '' });
  if (aluno && !a.duracao) add('Duração da aula', PRE, 4, true);
  if (c.ehMensal) {
    if (aluno && c.mensalN <= 0) add('Mensalidade', PRE, 2, true);
    else add('Mensalidade', brlAcordo(c.mensalN) + ' por mês', 2);
    add('Aulas por mês', c.aulasMes ? `${c.aulasMes.min} a ${c.aulasMes.max} (${c.aulasMes.porSemana}× por semana)` : '—', 4);
    const dia = aluno ? (Number(a.diaVenc) || 0) : (Number(a.diaVenc) || hojeDia);
    const VENC = aluno ? 'Dia de pagar' : 'Vencimento';
    if (dia) add(VENC, 'todo dia ' + dia, null);
    else add(VENC, PRE, null, true);
    // fecho v148: acordo ao ar livre (o.chuva) — a chuva vira reposição, como no texto do acordo que o aluno recebe
    if (o.chuva) { add('Chuva', 'não conta para ninguém e vira uma reposição a combinar', null); add('Reposição', 'só a da chuva — fora isso, horário fixo', null); }
    else add('Reposição', 'não — horário fixo', null);
    (o.extras || []).forEach(x => L.push({ passo: null, vazio: false, pre: '', ...x }));
    if (!aluno && a.mesPago === null) add(COB, 'este mês: escolha abaixo', null);   // pedido do aluno sem a resposta
    else if (!aluno) {
      add(COB, a.mesPago ? 'Mensalidade deste mês já paga' + (o.detalhar && dataOk(a.dataPgto) ? ' em ' + fmtDiaMes(a.dataPgto) : '') + ' · próxima no dia ' + dia
        : '1ª mensalidade de ' + brlAcordo(c.mensalN) + ' vence dia ' + dia, null);
    } else if (a.mesPago === null) add(COB, PRE, null, true);
    else if (a.mesPago) add(COB, 'Mensalidade deste mês já paga' + (dataOk(a.dataPgto) ? ' em ' + fmtDiaMes(a.dataPgto) : '') + (dia ? ' · próxima no dia ' + dia : ''), null);
    else add(COB, '1ª mensalidade' + (c.mensalN > 0 ? ' de ' + brlAcordo(c.mensalN) : '') + (dia ? ' vence dia ' + dia : ' em aberto'), null);
  } else {
    const temN = c.pacoteN > 0, temV = c.valorDoModo > 0, completo = !aluno || (temN && temV);
    if (aluno && !a.modo) add('Quanto você paga', PRE, 2, true);
    else {
      const rot = c.porAula ? 'Aulas combinadas' : 'Pacote';
      if (completo) add(rot, c.pacoteN + ' aulas · ' + brlAcordo(c.valorAulaEfetivo) + ' cada', 2);
      else if (temN) add(rot, c.pacoteN + ' aulas · valor: ', 2, false, PRE);
      else if (temV) add(rot, (a.modo === 'aula' ? brlAcordo(c.valorDoModo) + ' cada' : brlAcordo(c.valorDoModo)) + ' · quantidade: ', 2, false, PRE);
      else add(rot, PRE, 2, true);
      if (completo) add('Total', brlAcordo(c.totalPacote), 2);
    }
    (o.extras || []).forEach(x => L.push({ passo: null, vazio: false, pre: '', ...x }));
    const desde = dataOk(a.inicioCiclo) ? ' · desde ' + fmtDiaMes(a.inicioCiclo) : '';
    if (!aluno && a.fila && a.fila.ativa) {
      // v149: pacote na fila — comeca depois da ultima aula do atual. v152: a cobranca vence no dia da ultima aula do ATUAL
      const ini = a.inicioFila || a.fila.inicio, prim = primeiraAulaDesdeSM(a.dias, ini);
      add('Começa', diaEDataSM(prim) + ' · o atual termina ' + diaEDataSM(a.fila.ultima), 5);
      add(COB, a.pago === true ? 'Pacote já pago' + (dataOk(a.dataPgto) ? ' em ' + fmtDiaMes(a.dataPgto) : '')
        : brlAcordo(c.totalPacote) + ' · vence em ' + fmtDiaMes(a.fila.ultima || prim), 6);
    } else if (!aluno) {
      if (o.detalhar && a.novoPacote === true) add('Já feitas', 'nenhuma — começa agora', 5);
      else if (o.detalhar && a.novoPacote === false) add('Já feitas', c.feitasN + ' de ' + c.pacoteN + ' · ' + c.restantes + ' restantes' + desde, 5);
      else if (c.feitasN > 0) add('Já feitas', c.feitasN + ' de ' + c.pacoteN + ' · ' + c.restantes + ' restantes', 5);
      add(COB, a.pago === true ? 'Pacote já pago' + (o.detalhar && dataOk(a.dataPgto) ? ' em ' + fmtDiaMes(a.dataPgto) : '')
        : brlAcordo(c.totalPacote) + ' em aberto', 6);
    } else {
      if (a.novoPacote === true) add('Já feitas', 'nenhuma — começa agora', 5);
      else if (a.novoPacote === false && a.feitas !== '' && temN) add('Já feitas', c.feitasN + ' de ' + c.pacoteN + ' · ' + c.restantes + ' restantes' + desde, 5);
      else if (a.novoPacote === false && a.feitas !== '') add('Já feitas', String(Math.round(num(a.feitas))) + desde, 5);
      else if (a.novoPacote === false) add('Já feitas', 'já começou · quantas: ', 5, false, PRE);
      else add('Já feitas', PRE, 5, true);
      if (a.pago === true) add(COB, (c.porAula ? 'Aulas já pagas' : 'Pacote já pago') + (dataOk(a.dataPgto) ? ' em ' + fmtDiaMes(a.dataPgto) : ''), 6);
      else if (a.pago === false) add(COB, c.totalPacote > 0 && temN ? brlAcordo(c.totalPacote) + ' em aberto' : 'em aberto', 6);
      else add(COB, PRE, 6, true);
    }
    if (o.chuva) add('Chuva', 'não conta para ninguém e vai para o fim do pacote', null);   // fecho v148: acordo ao ar livre
  }
  // Regras do professor (desmarcar, reajuste): o aluno nao escreve termo pelo professor.
  // v144: o prazo para desmarcar o aluno responde (pergunta logo abaixo da tabela); o reajuste segue com o professor.
  if (aluno) add('Reajuste', 'ele define', null);
  return L;
}

// Proposta v2 que viaja no convite (convites.dados.proposta). O que nao se sabe NAO vai (nunca
// null — o ver_convite tira os nulls). `hora` e' sempre a do 1o dia: o app antigo so' le' isso.
function propostaDoAcordo(a, c, opcoes) {
  const o = opcoes || {};
  const num = numDinheiro;
  const p = { v: 2, modalidade: String(o.modalidade || '').trim(), dias: [...a.dias] };
  p.hora = a.dias.length ? c.horarios[a.dias[0]] : a.hora;
  if (new Set(Object.values(c.horarios)).size > 1) p.horarios = { ...c.horarios };
  p.como_paga = a.modo || 'nao_sei';
  p.valor = c.valorDoModo;
  if (a.modo !== 'mes' && c.pacoteN >= 1 && c.pacoteN <= MAX_AULAS) p.qtd_aulas = c.pacoteN;
  if (DURACOES.includes(a.duracao)) p.duracao = a.duracao;
  if (!c.ehMensal) {
    if (a.novoPacote !== null) p.novo_pacote = a.novoPacote;
    if (a.novoPacote === false) {
      const f = Math.round(num(a.feitas));
      if (a.feitas !== '' && f >= 0 && f <= MAX_AULAS) p.aulas_feitas = f;
      if (dataValidaSM(a.inicioCiclo)) p.data_inicio = a.inicioCiclo;
    }
    if (a.pago !== null) p.pago = a.pago;
    if (a.pago === true && dataValidaSM(a.dataPgto)) p.data_pagamento = a.dataPgto;
  } else {
    const dia = Number(a.diaVenc);
    if (Number.isInteger(dia) && dia >= 1 && dia <= 31) p.dia_vencimento = dia;
    if (a.mesPago !== null) p.mes_pago = a.mesPago;
    if (a.mesPago === true && dataValidaSM(a.dataPgto)) p.data_pagamento = a.dataPgto;
  }
  p.aluno_nome = o.aluno_nome || '';
  p.aluno_telefone = o.aluno_telefone || '';
  // v142 (revisao): o WhatsApp do professor que o aluno pegou da agenda — so' quando e' um numero completo
  if (whatsValidoSM(o.prof_telefone)) p.prof_telefone = String(o.prof_telefone).slice(0, 20);
  // v144: prazo para desmarcar que o aluno marcou (0 = livre). Convite antigo, sem a chave: livre (o padrao).
  if (Number.isInteger(o.desmarcar_h) && o.desmarcar_h >= 0 && o.desmarcar_h <= 168) p.desmarcar_h = o.desmarcar_h;
  p.enviado_em = hojeSP();
  return p;
}

// Proposta lida pelo professor. O aluno escreve esse JSON direto no banco: tudo aqui e' dado
// dele e passa por esta peneira (tipo, faixa, data ate' hoje). Chave faltando = "nao sabe".
function lerProposta(p) {
  p = p || {};
  const hoje = hojeSP();
  const data = s => (dataValidaSM(s) && String(s) <= hoje) ? String(s) : '';
  const int = (x, min, max) => { const n = Number(x); return x !== null && x !== '' && x !== true && Number.isInteger(n) && n >= min && n <= max ? n : null; };
  const modo = ['aula', 'pacote', 'mes'].includes(p.como_paga) ? p.como_paga : null;
  const naoMes = modo !== 'mes';
  const valor = Number(p.valor) > 0 && Number(p.valor) < 1000000 ? Math.round(Number(p.valor) * 100) / 100 : 0;
  const qtd = naoMes ? int(p.qtd_aulas, 1, MAX_AULAS) : null;
  const dias = Array.isArray(p.dias) ? [...new Set(p.dias.map(Number))].filter(d => Number.isInteger(d) && d >= 0 && d <= 6).sort() : [];
  const hora = /^\d{1,2}:\d{2}/.test(String(p.hora || '')) ? String(p.hora).slice(0, 5).padStart(5, '0') : '';
  const hs = horariosDaProposta(dias, p.horarios);
  const porDia = !!hs && new Set(Object.values(hs)).size > 1;
  const duracao = DURACOES.includes(Number(p.duracao)) ? Number(p.duracao) : null;
  const novoPacote = naoMes && typeof p.novo_pacote === 'boolean' ? p.novo_pacote : null;
  const feitas = novoPacote === false ? int(p.aulas_feitas, 0, MAX_AULAS) : null;
  const inicioCiclo = novoPacote === false ? data(p.data_inicio) : '';
  const pago = naoMes && typeof p.pago === 'boolean' ? p.pago : null;
  const mesPago = modo === 'mes' && typeof p.mes_pago === 'boolean' ? p.mes_pago : null;
  const dataPgto = (pago === true || mesPago === true) ? data(p.data_pagamento) : '';
  const diaVenc = modo === 'mes' ? int(p.dia_vencimento, 1, 31) : null;
  const nomeBruto = String(p.aluno_nome || '').trim().slice(0, 80);
  const r = {
    nome: nomeBruto.includes('@') ? '' : nomeBruto,              // sem nome no perfil o app mandava o e-mail
    tel: String(p.aluno_telefone || '').slice(0, 20),
    profTel: whatsValidoSM(p.prof_telefone) ? String(p.prof_telefone).slice(0, 20) : '',   // v142: o do professor (agenda do aluno)
    modalidade: String(p.modalidade || '').trim().slice(0, 40),
    modo, valor, qtd, dias, hora, horarios: porDia ? hs : null, porDia, duracao,
    novoPacote, feitas, inicioCiclo, pago, dataPgto, diaVenc, mesPago,
    enviadoEm: data(p.enviado_em),
    desmarcarH: int(p.desmarcar_h, 0, 168),                       // v144: 0 = livre; null = nao disse (livre)
  };
  const horasOk = dias.length > 0 && (porDia ? dias.every(d => hs[d]) : !!hora);
  r.completos = {          // o aluno respondeu o passo INTEIRO (o professor pula)
    2: (modo === 'mes' && valor > 0) || ((modo === 'aula' || modo === 'pacote') && valor > 0 && qtd !== null),
    4: horasOk && duracao !== null,
    5: naoMes && (novoPacote === true || (novoPacote === false && feitas !== null && qtd !== null && feitas <= qtd)),
    6: naoMes && pago !== null,
  };
  r.doAluno = {            // o aluno disse ALGO nesse passo (mostra o "Veio preenchido por...")
    2: !!modo || valor > 0 || qtd !== null, 4: dias.length > 0, 5: novoPacote !== null, 6: pago !== null,
  };
  return r;
}
const passosCompletosDaProposta = p => { const r = lerProposta(p); return [2, 4, 5, 6].filter(k => r.completos[k]); };

// Textos que viajam: o WhatsApp do aluno (quem = 'eu'), o cartao que o professor le' ('ele') e, desde
// 21/10, o WhatsApp do professor para o aluno ('prof': "Já fizemos", "Você já pagou").
function linhasDaProposta(p, quem) {
  const r = lerProposta(p);
  const voce = quem === 'prof';
  const eu = quem === 'eu' || voce;
  const mensal = r.modo === 'mes';
  const N = r.qtd, V = r.valor;
  const agenda = (r.dias.length ? descreverAgenda({ dias: r.dias, hora: r.hora, horarios: r.horarios }) : 'dias a combinar')
    + (r.duracao ? ' · aulas de ' + rotuloDuracao(r.duracao) : '');
  let valor = 'valor a combinar';
  if (r.modo === 'aula') {
    valor = N && V ? `${N} aulas · ${brlAcordo(V)} cada = ${brlAcordo(V * N)}` : V ? `${brlAcordo(V)} por aula` : N ? `${N} aulas · valor a combinar` : valor;
  } else if (r.modo === 'pacote') {
    valor = N && V ? `Pacote de ${N} aulas: ${brlAcordo(V)} (${brlAcordo(V / N)} por aula)` : V ? `Pacote: ${brlAcordo(V)}` : N ? `Pacote de ${N} aulas · valor a combinar` : valor;
  } else if (mensal) {
    valor = V ? `${brlAcordo(V)} por mês` + (r.dias.length ? ` (${r.dias.length * 4} a ${r.dias.length * 5} aulas)` : '') + (r.diaVenc ? ` · vence todo dia ${r.diaVenc}` : '')
      : 'Mensalidade · valor a combinar';
  }
  let andamento = '';
  if (!mensal && r.novoPacote === true) andamento = eu ? 'Começamos agora' : 'Começa agora';
  else if (!mensal && r.novoPacote === false) {
    if (r.feitas !== null && N) andamento = `${eu ? 'Já fizemos' : 'Já fizeram'} ${r.feitas} · faltam ${Math.max(0, N - r.feitas)}`;
    else if (r.feitas !== null) andamento = `${eu ? 'Já fizemos' : 'Já fizeram'} ${r.feitas}`;
    else andamento = eu ? 'Já começamos' : 'Já começaram';
    if (r.inicioCiclo) andamento += ` (${eu ? 'começamos' : 'começaram'} em ${fmtDiaMes(r.inicioCiclo)})`;
  }
  let pagamento = '';
  if (!mensal && r.pago === true) pagamento = (voce ? 'Você já pagou ' : eu ? 'Já paguei ' : 'Já pagou ') + (r.modo === 'pacote' ? 'o pacote' : 'essas aulas') + (r.dataPgto ? ` (em ${fmtDiaMes(r.dataPgto)})` : '');
  else if (!mensal && r.pago === false) pagamento = voce ? 'Pagamento em aberto' : eu ? 'Ainda vou pagar' : 'Ainda vai pagar';
  else if (mensal && r.mesPago === true) pagamento = 'A mensalidade deste mês já está paga';
  else if (mensal && r.mesPago === false) pagamento = voce ? 'A mensalidade deste mês está em aberto' : eu ? 'Ainda vou pagar a deste mês' : 'Ainda vai pagar a deste mês';
  // Um "Nao sei" na forma de pagar conta 1 (valor e quantidade nem chegaram a ser perguntados).
  const brancos = [
    !r.modo, !!r.modo && !V, !!r.modo && !mensal && N === null, r.duracao === null,
    !mensal && (r.novoPacote === null || (r.novoPacote === false && r.feitas === null)),
    !mensal && r.pago === null,
    mensal && r.diaVenc === null, mensal && r.mesPago === null,
  ].filter(Boolean).length;
  const desmarcar = r.desmarcarH === null ? '' : 'Desmarcar: ' + textoPrazoDesmarcar(r.desmarcarH);   // v144
  return { modalidade: r.modalidade, agenda, valor, andamento, pagamento, desmarcar, emBranco: brancos > 0, nEmBranco: brancos, enviadoEm: r.enviadoEm };
}

// Convite do PROFESSOR para o aluno (o mesmo texto no fim do "Novo aluno" e na janela "Convidar").
// Quem fala e' o professor (sai do WhatsApp dele): o que muda pro aluno, os dados do treino (prova de
// que nao e' spam) e uma acao so'.
// 21/10 (Marcus): o MESMO formato da mensagem do aluno para o professor (textoWhatsConviteProf) — modalidade,
// dias e horarios, valor, aulas feitas e pagamento, linha a linha. Sem a proposta (dados nao carregaram), cai
// no resumo de uma linha.
function textoWhatsConviteAluno({ nomeAluno, proposta, resumo, url }) {
  const nome = String(nomeAluno || '').trim().split(' ')[0] || 'aluno';
  let combinado;
  if (proposta) {
    const L = linhasDaProposta(proposta, 'prof');
    combinado = `\n\nJá deixei preenchido o nosso combinado:\n` +
      `${L.modalidade ? L.modalidade + ' · ' : ''}${L.agenda}\n` +
      `${L.valor}\n` +
      (L.andamento ? L.andamento + '\n' : '') +
      (L.pagamento ? L.pagamento + '\n' : '');
  } else {
    combinado = resumo ? `\n\nSeu acordo já está lá:\n*${resumo}*\n` : '\n';
  }
  return `Oi, ${nome}! Comecei a usar o Plen pra organizar nossas aulas — agenda, quantas aulas faltam e pagamento, tudo num lugar só.`
    + combinado
    + `\nÉ grátis pra você. Toque no link, confere se está tudo certo e confirma:`
    + `\n${blocoBaixarEConvite(url)}`;
}

// O combinado de um aluno que JA' esta' na lista (janela "Convidar"), lido do banco no formato da proposta.
async function propostaDoBancoSM(alunoId) {
  const [{ data: al }, { data: c }, { data: ag }] = await Promise.all([
    supabase.from('alunos').select('modalidade').eq('id', alunoId).maybeSingle(),
    supabase.from('contratos').select('id, tipo_cobranca, valor_mensal, preco_negociado, total_aulas, duracao_min, dia_vencimento')
      .eq('aluno_id', alunoId).eq('status', 'ativo').order('created_at', { ascending: false }).limit(1).maybeSingle(),
    supabase.from('agenda_semanal').select('dia_semana, hora').eq('aluno_id', alunoId).eq('ativo', true).order('dia_semana'),
  ]);
  if (!c) return null;
  const [{ data: cr }, { data: pg }] = await Promise.all([
    supabase.from('creditos').select('usado').eq('contrato_id', c.id).maybeSingle(),
    supabase.from('pagamentos').select('status, data_pagamento, referencia_mes').eq('contrato_id', c.id).order('created_at', { ascending: false }).limit(6),
  ]);
  const mensal = c.tipo_cobranca === 'mensal';
  const dias = [...new Set((ag || []).map(x => Number(x.dia_semana)))].sort();
  const horarios = {};
  (ag || []).forEach(x => { horarios[Number(x.dia_semana)] = String(x.hora || '').slice(0, 5); });
  const p = { v: 2, modalidade: al?.modalidade || '', dias, hora: dias.length ? horarios[dias[0]] : '' };
  if (new Set(Object.values(horarios)).size > 1) p.horarios = horarios;
  p.como_paga = mensal ? 'mes' : 'pacote';
  p.valor = Number(mensal ? c.valor_mensal : c.preco_negociado) || 0;
  if (!mensal && c.total_aulas) p.qtd_aulas = Number(c.total_aulas);
  if (c.duracao_min) p.duracao = Number(c.duracao_min);
  if (!mensal) {
    const usado = Number(cr?.usado || 0);
    p.novo_pacote = usado === 0;
    if (usado > 0) p.aulas_feitas = usado;
    const pago = (pg || []).find(x => x.status === 'pago');
    p.pago = !!pago;
    if (pago?.data_pagamento) p.data_pagamento = String(pago.data_pagamento).slice(0, 10);
  } else {
    if (c.dia_vencimento) p.dia_vencimento = Number(c.dia_vencimento);
    const doMes = (pg || []).find(x => x.referencia_mes === hojeSP().slice(0, 7));
    if (doMes) {
      p.mes_pago = doMes.status === 'pago';
      if (p.mes_pago && doMes.data_pagamento) p.data_pagamento = String(doMes.data_pagamento).slice(0, 10);
    }
  }
  return p;
}
// "Cadastrar varios de uma vez" (v142, item E): o link geral, para um aluno ou para o grupo. Quem
// entra coloca o nome e o WhatsApp; o professor completa o combinado depois.
function textoWhatsCadastroGeral(url) {
  return `Oi! Vou organizar nossas aulas pelo Plen — agenda, aulas e pagamento num lugar só. É grátis pra você.`
    + `\n\nToque no link e coloca seu nome e seu WhatsApp:\n${blocoBaixarEConvite(url)}`
    + `\n\nDepois eu completo o nosso combinado e você confere lá.`;
}

// v144 — "Convidar outro professor" (Inicio e Configuracoes). O link de indicacao DESTE professor: um codigo por
// professor, de varios usos, que o banco gera ou reaproveita (meu_link_indicacao, sql/v144ind.sql). Quem toca no
// link cria a conta ja' como professor; o banco anota quem indicou e avisa aqui ("Fulano entrou no Plen pelo seu
// convite."). Sem Pix e sem dinheiro na mensagem (Marcus, 21/10: nada que pareca cobranca no teste). A mensagem
// fica pronta numa janela antes do WhatsApp: abrir o WhatsApp depois de esperar o banco perde o toque da pessoa
// e o iPhone bloqueia a janela nova.
// v147 (juncao com o "um link so'" de 21/10): o mesmo formato dos outros convites — uma frase dizendo o que o
// toque faz e o link sozinho no fim. Sem "baixe na loja e volte aqui": com o app, o link abre o app; sem o app,
// no iPhone abre a pagina "Baixar o Plen" (que guarda o convite) e no Android/computador o cadastro direto.
function textoWhatsConviteProfessor(url) {
  return 'Oi! Tô testando o Plen, um app pra organizar minhas aulas: agenda, pacotes, confirmações e cancelamentos num toque, e os alunos veem tudo pelo celular. Tô chamando alguns professores pra testar junto comigo nesse começo. É grátis.'
    + `\n\nToque no link para criar a sua conta de professor:\n${blocoBaixarEConvite(url)}`;
}
function ConvidarProfessorSM({ origem = 'config' }) {
  const [aberto, setAberto] = React.useState(false);
  const [link, setLink] = React.useState('');
  const [estado, setEstado] = React.useState('');   // carregando | pronto | sem_banco | erro
  async function gerar() {
    setEstado('carregando');
    let r = null, err = null;
    try { const res = await supabase.rpc('meu_link_indicacao'); r = res.data; err = res.error; } catch (e) { err = e; }
    if (!err && r && r.ok && r.code) {
      uso.acao('convite_prof.gerar', { origem, novo: !!r.novo });
      setLink(`${window.location.origin}/?c=${r.code}`);
      setEstado('pronto');
      return;
    }
    const semBanco = !!err && (err.code === 'PGRST202' || /meu_link_indicacao/.test(String(err.message || '')));
    if (err) uso.erro('erro.convite_prof', err.message || err); else uso.bloqueio('convite_prof', null, null, (r && r.erro) || 'sem_resposta');
    setEstado(semBanco ? 'sem_banco' : 'erro');
  }
  function abrir() { setAberto(true); if (!link && estado !== 'carregando') gerar(); }
  const msg = link ? textoWhatsConviteProfessor(link) : '';
  return (
    <>
      <button type="button" onClick={abrir}
        style={{display:'flex', alignItems:'center', gap:'14px', width:'100%', textAlign:'left', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'16px',
                padding:'14px 16px', cursor:'pointer', fontFamily:'inherit', minHeight:'64px', boxSizing:'border-box'}}>
        <span style={{width:'40px', height:'40px', borderRadius:'12px', background:'#E7EDFF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
          <Ico n="user-plus" s={21} c="#2F6BFF" />
        </span>
        <span style={{flex:1, minWidth:0}}>
          <span style={{display:'block', fontSize:'14px', fontWeight:'800', color:'#16181D'}}>Convidar outro professor</span>
          <span style={{display:'block', fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>Chame um colega para testar o Plen com você.</span>
        </span>
        <Ico n="chevron-right" s={18} c="#9AA0AB" />
      </button>
      <Modal open={aberto} onClose={() => setAberto(false)} title="Convidar outro professor" uso="convite_prof">
        {estado === 'pronto' ? (
          <div className="flex flex-col gap-4">
            <div style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'12px 14px', fontSize:'13.5px', color:'#16181D', lineHeight:1.55}}>
              Mande para os professores que vão testar o Plen com você — <strong>um por um ou num grupo</strong>. Cada um cria a própria conta de professor: os alunos e as aulas de cada um ficam separados.
              <div style={{fontSize:'12px', color:'#6E7480', marginTop:'6px'}}>O mesmo link serve para quantos professores você quiser. Quando alguém entrar, você recebe um aviso.</div>
            </div>
            <div>
              <div style={{fontSize:'12px', color:'#6E7480', marginBottom:'6px'}}>A mensagem que vai sair do seu WhatsApp:</div>
              <div style={{background:'#DCF8C6', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'12px 14px',
                           fontSize:'13px', color:'#0A5B40', whiteSpace:'pre-wrap', overflowWrap:'anywhere', lineHeight:1.55}}>
                {msg}
              </div>
            </div>
            <button onClick={() => { uso.acao('convite_prof.whats', { origem }); compartilharOuWhats(`https://wa.me/?text=${encodeURIComponent(msg)}`, 'convite_prof'); }}
              style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', minHeight:'48px', background:'linear-gradient(135deg,#25D366,#128C7E)', color:'white', border:'none', borderRadius:'12px', padding:'13px', fontSize:'15px', fontWeight:'800', cursor:'pointer'}}>
              <Ico n="brand-whatsapp" c="white" /> Mandar no WhatsApp
            </button>
            <button onClick={() => { uso.acao('convite_prof.copiar', { origem, o_que: 'mensagem' }); copiarTextoSM(msg, 'Mensagem copiada. Cole no grupo ou na conversa.', 'Não consegui copiar. Toque em "Mandar no WhatsApp".'); }}
              style={{minHeight:'44px', background:'white', color:'#3D424C', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'12px', fontSize:'14px', fontWeight:'700', cursor:'pointer'}}>
              Copiar a mensagem
            </button>
            <button onClick={() => { uso.acao('convite_prof.copiar', { origem, o_que: 'link' }); copiarTextoSM(link, 'Link copiado!'); }}
              style={{minHeight:'44px', background:'transparent', color:'#2F6BFF', border:'none', padding:'8px', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
              Copiar só o link
            </button>
          </div>
        ) : estado === 'sem_banco' || estado === 'erro' ? (
          <div className="flex flex-col gap-4">
            <div style={{background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'12px', padding:'12px 14px', fontSize:'13.5px', color:'#92400E', lineHeight:1.55}}>
              {estado === 'sem_banco' ? 'Ainda não dá para convidar professores por aqui. Tente de novo mais tarde.' : 'Não consegui preparar o link agora. Confira a internet e tente de novo.'}
            </div>
            {estado === 'erro' && <Button onClick={gerar} className="w-full justify-center">Tentar de novo</Button>}
          </div>
        ) : (
          <div style={{padding:'18px 0', textAlign:'center', fontSize:'14px', color:'#6E7480'}}>Preparando o seu link…</div>
        )}
      </Modal>
    </>
  );
}

// v144: aluno LOGADO tocou no link de convite de um PROFESSOR (indicacao). Cada conta do Plen e' de aluno ou de
// professor: para dar aula pelo Plen, cria outra conta (outro e-mail). A conta de aluno fica como esta'.
// "Sair e criar conta de professor" sai sem apagar o convite guardado: a tela de entrada abre em "Criar conta de
// professor", com o campo de e-mail vazio. "Continuar como aluno" esquece o convite.
function IndicacaoParaAlunoSM() {
  const { conviteData, setConviteData } = useApp();
  const [saindo, setSaindo] = React.useState(false);
  React.useEffect(() => { uso.tela('aluno.indicacao'); }, []);
  const quem = conviteData?.pnome || 'Um professor';
  async function sairECriar() {
    if (saindo) return;
    setSaindo(true);
    uso.acao('indicacao.aluno_sair');
    try { sessionStorage.setItem('plenIndicacaoOutroEmail', '1'); } catch {}
    await sairDaConta();
  }
  function continuarComoAluno() {
    uso.acao('indicacao.aluno_continuar');
    try { localStorage.removeItem('pendingInviteCode'); } catch {}
    setConviteData(null);
  }
  return (
    <div style={{minHeight:'100vh', background:'#F7F8FA', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px 16px', boxSizing:'border-box'}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 20px 60px rgba(0,0,0,0.1)', padding:'32px 22px', width:'100%', maxWidth:'440px', boxSizing:'border-box'}}>
        <div style={{width:'56px', height:'56px', background:'linear-gradient(135deg, #2F6BFF, #2455D6)', borderRadius:'16px', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'18px'}}><Ico n="user-plus" s={26} c="white" /></div>
        <h1 style={{fontSize:'21px', fontWeight:'800', color:'#16181D', marginBottom:'10px', letterSpacing:'-0.4px', lineHeight:1.25}}>{quem} te convidou para usar o Plen como professor</h1>
        <p style={{color:'#3D424C', fontSize:'14.5px', lineHeight:1.6, marginBottom:'10px'}}>
          Você está na sua conta de aluno. Cada conta do Plen é de aluno ou de professor — para dar as suas aulas pelo Plen, crie uma conta de professor com outro e-mail.
        </p>
        <p style={{color:'#6E7480', fontSize:'13px', lineHeight:1.55, marginBottom:'22px'}}>Sua conta de aluno continua do jeito que está, com suas aulas e pagamentos.</p>
        <button onClick={sairECriar} disabled={saindo}
          style={{width:'100%', minHeight:'50px', background:'#2F6BFF', color:'white', border:'none', borderRadius:'14px', padding:'14px', fontSize:'15px', fontWeight:'800', cursor:saindo?'not-allowed':'pointer', opacity:saindo?0.7:1}}>
          {saindo ? 'Saindo…' : 'Sair e criar conta de professor'}
        </button>
        <button onClick={continuarComoAluno} disabled={saindo}
          style={{width:'100%', minHeight:'48px', marginTop:'10px', background:'white', color:'#3D424C', border:'1.5px solid #E2E4E9', borderRadius:'14px', padding:'12px', fontSize:'14px', fontWeight:'700', cursor:'pointer'}}>
          Continuar como aluno
        </button>
      </div>
    </div>
  );
}

// Mensagem do aluno para o professor (WhatsApp). O comeco e o fim sao os de sempre.
function textoWhatsConviteProf({ nomeProf, proposta, url }) {
  const primeiro = String(nomeProf || '').trim().split(' ')[0];
  const L = linhasDaProposta(proposta, 'eu');
  return `Oi${primeiro ? ' ' + primeiro : ''}! Comecei a usar o Plen pra organizar minhas aulas — agenda, ` +
    `quantas aulas faltam e pagamento, tudo num lugar só.\n\n` +
    `Já deixei preenchido o nosso combinado:\n` +
    `${L.modalidade ? L.modalidade + ' · ' : ''}${L.agenda}\n` +
    `${L.valor}\n` +
    (L.andamento ? L.andamento + '\n' : '') +
    (L.pagamento ? L.pagamento + '\n' : '') +
    (L.emBranco ? 'O que deixei em branco você completa lá.\n' : '') +
    `\nÉ grátis pra você. Toque no link, confere se está certo e ajusta o que precisar:` +
    `\n${blocoBaixarEConvite(url)}`;
}

// ---- Pecas de tela ----------------------------------------------------------

function SeletorCobranca({ modo, onModo }) {
  return (
    <div style={{display: 'flex', border: '1.5px solid #E2E4E9', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px'}}>
      {[['aula', 'Por aula'], ['pacote', 'Pelo pacote'], ['mes', 'Por mês']].map(([m, l]) => (
        <button key={m} type="button" onClick={() => onModo(m)}
          style={{flex: 1, padding: '12px 6px', fontSize: '13.5px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  fontWeight: modo === m ? '800' : '600',
                  background: modo === m ? '#E7EDFF' : 'white', color: modo === m ? '#2F6BFF' : '#6E7480'}}>
          {l}
        </button>
      ))}
    </div>
  );
}

// Dinheiro: "2.000" enquanto digita, "2.000,00" ao sair. onValor e' o setState da tela.
function CampoDinheiroAcordo({ valor, onValor, placeholder, autoFocus }) {
  return (
    <input value={valor} onChange={e => onValor(limparDinheiro(e.target.value))} onBlur={() => onValor(v => formatarDinheiro(v))}
      placeholder={placeholder} inputMode="decimal" style={NUM_ACORDO} autoFocus={!!autoFocus} />
  );
}

// So' digitos. slice(0, 12): um telefone colado aqui dispara a mensagem em vez de ser cortado.
function CampoInteiroAcordo({ valor, onValor, placeholder, max = 12, estreito, autoFocus }) {
  return (
    <input value={valor} onChange={e => onValor(e.target.value.replace(/[^\d]/g, '').slice(0, max))}
      placeholder={placeholder} inputMode="numeric" autoComplete="off"
      style={estreito ? { ...NUM_ACORDO, maxWidth: '120px' } : NUM_ACORDO} autoFocus={!!autoFocus} />
  );
}

function ContaAoVivo({ a, c }) {
  if (!(a.modo === 'aula' || a.modo === 'pacote') || !(c.totalPacote > 0) || c.pacoteN < 1 || c.pacoteN > MAX_AULAS) return null;
  const num = numDinheiro;
  return (
    <div style={{marginTop: '16px', padding: '12px 14px', background: '#E7EDFF', borderRadius: '12px',
                 fontSize: '14px', fontWeight: '700', color: '#1C43AE'}}>
      {a.modo === 'aula'
        ? `${brlAcordo(num(a.valorAula))} × ${c.pacoteN} aulas = ${brlAcordo(c.totalPacote)} no total`
        : `${brlAcordo(num(a.valorPacote))} ÷ ${c.pacoteN} aulas = ${brlAcordo(c.valorAulaEfetivo)} por aula`}
    </div>
  );
}

// P2 — Quanto. Mensalidade nao tem "pacote": as aulas do mes vem dos dias (P4). Perguntar
// "quantas aulas no pacote" no mensal confundia (o Cadu digitava 12 e via "R$ 1.600 ÷ 12").
function CorpoQuanto({ voz, a, c }) {
  const aluno = voz === 'aluno';
  // Trocar a forma apaga o aviso de erro que ja' nao vale (ex.: "Escolha como voce paga").
  const onModo = m => { a.setModo(m); a.setErro?.(''); };
  if (!a.modo) return <SeletorCobranca modo={a.modo} onModo={onModo} />;
  if (a.modo === 'mes') return (
    <>
      <SeletorCobranca modo={a.modo} onModo={onModo} />
      <SubAcordo>Valor da mensalidade (R$)</SubAcordo>
      <CampoDinheiroAcordo valor={a.valorMes} onValor={a.setValorMes} placeholder="400,00" autoFocus={!a.valorMes} />
      <div style={{marginTop: '16px', padding: '12px 14px', background: '#E7EDFF', borderRadius: '12px',
                   fontSize: '13px', color: '#1C43AE', lineHeight: 1.55}}>
        <strong>Mensalidade fixa:</strong>{aluno
          ? ' horário fixo, sem reposição. Você paga todo mês no mesmo dia, tendo treinado ou não.'
            + (c.aulasMes ? ` Com ${c.aulasMes.porSemana}× por semana, dá ${c.aulasMes.min} a ${c.aulasMes.max} aulas por mês.` : '')
          : ' horário fixo, sem reposição. Todo mês, no mesmo dia em que ele entrou, o app abre a cobrança sozinho — tendo ele treinado ou não.'}
      </div>
    </>
  );
  const porAula = a.modo === 'aula';
  const valor = porAula ? a.valorAula : a.valorPacote;
  return (
    <>
      <SeletorCobranca modo={a.modo} onModo={onModo} />
      <SubAcordo>{porAula ? 'Valor de cada aula (R$)' : 'Valor do pacote fechado (R$)'}</SubAcordo>
      <CampoDinheiroAcordo key={a.modo} valor={valor} onValor={porAula ? a.setValorAula : a.setValorPacote}
        placeholder={porAula ? '150,00' : '1.200,00'} autoFocus={!valor} />
      <SubAcordo>{porAula ? 'Quantas aulas vocês combinaram agora?' : 'Quantas aulas vêm no pacote?'}</SubAcordo>
      <CampoInteiroAcordo valor={a.qtdAulas} onValor={a.setQtdAulas} placeholder="8" autoFocus={!!valor && !a.qtdAulas} />
      {porAula && <div style={{fontSize: '12.5px', color: '#6E7480', marginTop: '6px'}}>
        {aluno ? 'Quando acabarem, vocês combinam as próximas.' : 'Quando acabarem, o app avisa para combinar as próximas.'}
      </div>}
      <ContaAoVivo a={a} c={c} />
    </>
  );
}

// Aviso de horario ja' ocupado por outro aluno (so' o professor ve': le' a agenda dele).
function AvisoHorarioOcupado({ conflitos }) {
  return (
    <div style={{marginTop: '12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '10px 12px', fontSize: '12.5px', color: '#92400E', lineHeight: 1.5}}>
      <b>Horário já ocupado:</b> {conflitos.join(' · ')}. Se for treino em grupo, use <b>Turmas de treino</b>; se não, ajuste o horário.
    </div>
  );
}

// P4 — Dias, horario e duracao.
function CorpoDias({ voz, a, c, aviso, onSeparou, onJuntou }) {
  const aluno = voz === 'aluno';
  function toggleDia(d) {
    a.setDias(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
    a.setHorarios(prev => prev[d] ? prev : { ...prev, [d]: a.hora });   // dia novo nasce com o horario unico
  }
  function separarPorDia() {
    const base = {};
    a.dias.forEach(d => { base[d] = horaDoDiaAcordo(a, d); });
    a.setHorarios(base); a.setPorDia(true);
  }
  // "Usar o mesmo horario em todos" fica com o horario do 1o dia (o unico podia estar velho).
  function juntar() {
    if (a.dias.length) a.setHora(horaDoDiaAcordo(a, a.dias[0]));
    a.setPorDia(false);
  }
  return (
    <>
      <div style={GRADE_ACORDO}>
        {DIAS_ACORDO.map((d, i) => (
          <button key={i} type="button" onClick={() => toggleDia(i)} style={{...chipAcordo(a.dias.includes(i)), padding: '11px 13px'}}>{d}</button>
        ))}
      </div>
      <HorariosDosDias dias={a.dias} rotulos={DIAS_ACORDO} hora={a.hora} onHora={a.setHora} horaDe={d => horaDoDiaAcordo(a, d)}
        onHoraDia={(d, v) => a.setHorarios(prev => ({ ...prev, [d]: v }))} porDia={a.porDia}
        onSeparar={() => { onSeparou?.(); separarPorDia(); }}
        onJuntar={() => { onJuntou?.(); juntar(); }}
        textoSeparar={aluno ? 'Treino em horários diferentes?' : 'Ele treina em horários diferentes?'} />
      {aviso || null}
      {aluno && (
        <div style={{fontSize: '12px', color: '#9AA0AB', marginTop: '12px', lineHeight: 1.5}}>
          Se mudar de uma semana para outra, não tem problema — ele ajusta quando entrar.
        </div>
      )}
      {c.aulasMes && (
        <div style={{marginTop: '12px', padding: '10px 12px', background: '#E7EDFF', borderRadius: '10px', fontSize: '13px', color: '#1C43AE', lineHeight: 1.5}}>
          {`Dá ${c.aulasMes.min} a ${c.aulasMes.max} aulas por mês (${c.aulasMes.porSemana}× por semana).`}
        </div>
      )}
      <SubAcordo>A aula dura quanto tempo?</SubAcordo>
      <div style={GRADE_ACORDO}>
        {DURACOES.map(m => (
          <button key={m} type="button" onClick={() => { a.setDuracao(m); a.setErro?.(''); }} style={chipAcordo(a.duracao === m)}>{rotuloDuracao(m)}</button>
        ))}
        {aluno && <button type="button" onClick={() => a.setDuracao(null)} style={chipAcordo(a.duracao === null)}>Não sei</button>}
      </div>
    </>
  );
}

// P5 — Ja' comecou? O professor NAO lembra a data em que o pacote comecou — lembra do saldo.
// Entao "quantas ja' fez" vem primeiro e a data e' opcional (antes era o contrario, e travava).
function CorpoComecou({ voz, a, c, onSeguir }) {
  const aluno = voz === 'aluno';
  const feitasNum = a.feitas === '' ? null : Math.round(numDinheiro(a.feitas));
  return (
    <>
      <div style={GRADE_ACORDO}>
        <button type="button" onClick={() => { a.setNovoPacote(true); a.setFeitas(''); a.setInicioCiclo(''); a.setErro?.(''); onSeguir?.(); }}
          style={chipAcordo(a.novoPacote === true)}>Começa agora</button>
        <button type="button" onClick={() => { a.setNovoPacote(false); a.setErro?.(''); }} style={chipAcordo(a.novoPacote === false)}>Já começou</button>
      </div>
      {a.novoPacote === false && (
        <>
          <SubAcordo>{aluno
            ? (c.porAula ? 'Quantas dessas aulas vocês já fizeram?' : 'Quantas vocês já fizeram desse pacote?')
            : (c.porAula ? 'Quantas dessas aulas ele já fez?' : 'Quantas aulas desse pacote ele já fez?')}</SubAcordo>
          <CampoInteiroAcordo valor={a.feitas} onValor={v => { a.setFeitas(v); a.setErro?.(''); }} estreito autoFocus={a.feitas === ''} />
          {feitasNum !== null && c.pacoteN > 0 && feitasNum <= c.pacoteN && (
            <div style={{fontSize: '13px', color: '#0A6F4D', background: '#EFFAF5', borderRadius: '10px', padding: '10px 12px', marginTop: '12px'}}>
              Faltam <strong>{c.restantes}</strong>{` de ${c.pacoteN} aulas.${aluno ? '' : ' Elas entram na agenda a partir de hoje.'}`}
            </div>
          )}
          <SubAcordo>{c.porAula ? 'Quando essas aulas começaram? (opcional)' : 'Quando esse pacote começou? (opcional)'}</SubAcordo>
          <input type="date" value={a.inicioCiclo} onChange={e => { a.setInicioCiclo(e.target.value); a.setErro?.(''); }}
            max={hojeSP()} style={{...INPUT_ACORDO, maxWidth: '220px'}} />
          <div style={{fontSize: '12px', color: '#9AA0AB', marginTop: '8px', lineHeight: 1.5}}>
            {aluno ? 'Se não lembrar, deixe em branco — serve só para o histórico.'
              : 'Se não lembrar, deixe em branco — serve só para o histórico. A data de renovação sai das aulas que faltam e dos dias que ele treina.'}
          </div>
        </>
      )}
    </>
  );
}

// P5 na renovacao com aula do pacote atual ainda marcada (v149): o pacote novo entra NA FILA. Nao existe
// "comeca agora" (seria aula em dobro e a aula que falta sumia — caso Marcus/Cadu, 28/10) nem "ja' comecou".
function CorpoFila({ a, nome }) {
  const f = a.fila;
  const outra = !!a.inicioFila && a.inicioFila !== f.inicio;
  const ini = a.inicioFila || f.inicio;
  const prim = primeiraAulaDesdeSM(a.dias, ini);
  return (
    <>
      <div style={{fontSize: '13px', color: '#3D424C', background: '#F4F6FA', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', lineHeight: 1.5}}>
        {`O pacote atual de ${primeiroNomeSM(nome, 'o aluno')} termina `}<strong>{diaEDataSM(f.ultima)}{f.horaUltima ? ', ' + f.horaUltima : ''}</strong>
        {` (falta${f.abertas.length === 1 ? '' : 'm'} ${f.abertas.length} aula${f.abertas.length === 1 ? '' : 's'}).`}
      </div>
      <div style={GRADE_ACORDO}>
        <button type="button" onClick={() => { a.setNovoPacote(true); a.setFeitas(''); a.setInicioCiclo(''); a.setInicioFila(''); a.setErro?.(''); }}
          style={chipAcordo(a.novoPacote === true && !outra)}>Quando o atual terminar</button>
        <button type="button" onClick={() => { a.setNovoPacote(true); a.setFeitas(''); a.setInicioCiclo(''); a.setInicioFila(somarDiaSM(f.inicio, 7)); a.setErro?.(''); }}
          style={chipAcordo(outra)}>Escolher outra data</button>
      </div>
      {outra && (
        <>
          <SubAcordo>A partir de quando?</SubAcordo>
          <input type="date" value={a.inicioFila} min={f.inicio} onChange={e => { a.setInicioFila(e.target.value || f.inicio); a.setErro?.(''); }}
            style={{...INPUT_ACORDO, maxWidth: '220px'}} />
        </>
      )}
      {a.novoPacote === true && (
        <div style={{fontSize: '13px', color: '#0A6F4D', background: '#EFFAF5', borderRadius: '10px', padding: '10px 12px', marginTop: '12px', lineHeight: 1.5}}>
          {`O pacote novo começa ${diaEDataSM(prim)}, na 1ª aula dele. Até lá, a contagem continua sendo a do pacote atual.`}
        </div>
      )}
    </>
  );
}

// P6 — Ja' pagou? Se ja' disse quando o pacote comecou, a data do pagamento vem com a mesma
// (quase sempre e' o mesmo dia — Marcus, 29/09). Da' para trocar.
function CorpoPagou({ voz, a, c }) {
  const aluno = voz === 'aluno';
  const caixaAmarela = { fontSize: '12.5px', color: '#92400E', background: '#FFFBEB', borderRadius: '10px', padding: '10px 12px', marginTop: '14px' };
  const opcoes = [['Hoje', hojeSP(0)], ['Semana passada', hojeSP(-7)], ['Mês passado', hojeSP(-30)]];
  if (a.inicioCiclo) opcoes.unshift([c.porAula ? 'No início das aulas' : 'No início do pacote', a.inicioCiclo]);
  const mesmoDia = a.inicioCiclo && a.dataPgto === a.inicioCiclo;
  const totalOk = c.totalPacote > 0 && c.pacoteN > 0;
  return (
    <>
      <div style={GRADE_ACORDO}>
        <button type="button" onClick={() => { a.setPago(true); if (!a.dataPgto && a.inicioCiclo) a.setDataPgto(a.inicioCiclo); a.setErro?.(''); }}
          style={chipAcordo(a.pago === true)}>{aluno ? 'Já paguei' : 'Já pagou'}</button>
        <button type="button" onClick={() => { a.setPago(false); a.setErro?.(''); }} style={chipAcordo(a.pago === false)}>{aluno ? 'Vou pagar' : 'Vai pagar'}</button>
      </div>
      {a.pago === false && !aluno && c.totalPacote > 0 && (
        <div style={caixaAmarela}>{a.fila && a.fila.ativa
          ? `A cobrança de ${brlAcordo(c.totalPacote)} vence em ${fmtDiaMes(a.fila.ultima || primeiraAulaDesdeSM(a.dias, a.inicioFila || a.fila.inicio))}, no dia da última aula do pacote atual. Se ele quiser, pode pagar antes pelo app.`
          : `Vou deixar uma cobrança de ${brlAcordo(c.totalPacote)} em aberto — ele paga por Pix pelo app.`}</div>
      )}
      {a.pago === false && aluno && (
        <div style={caixaAmarela}>{totalOk
          ? `Quando ele confirmar, fica uma cobrança de ${brlAcordo(c.totalPacote)} em aberto — você paga por Pix, direto para ele.`
          : 'Quando ele confirmar, a cobrança fica em aberto — você paga por Pix, direto para ele.'}</div>
      )}
      {a.pago === true && (
        <>
          <SubAcordo>{mesmoDia
            ? (aluno ? (c.porAula ? 'Você pagou no dia em que as aulas começaram — confere?' : 'Você pagou no dia em que o pacote começou — confere?')
                     : (c.porAula ? 'Pagou no dia em que as aulas começaram — confere?' : 'Pagou no dia em que o pacote começou — confere?'))
            : (aluno ? 'Quando você pagou? (opcional)' : 'Quando ele pagou? (opcional)')}</SubAcordo>
          <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px'}}>
            {opcoes.map(([l, v]) => (
              <button key={l} type="button" onClick={() => { a.setDataPgto(v); a.setErro?.(''); }} style={chipAcordo(a.dataPgto === v)}>{l}</button>
            ))}
          </div>
          <input type="date" value={a.dataPgto} onChange={e => { a.setDataPgto(e.target.value); a.setErro?.(''); }}
            max={hojeSP()} style={{...INPUT_ACORDO, maxWidth: '220px'}} />
          <div style={{fontSize: '12.5px', color: '#0A6F4D', background: '#EFFAF5', borderRadius: '10px', padding: '10px 12px', marginTop: '14px'}}>
            {aluno ? 'Ele confirma que recebeu quando aceitar. Se não souber a data, deixe em branco.'
              : 'Nada em aberto. Se não souber a data, deixe em branco — o que importa é que você confirmou o pagamento.'}
          </div>
        </>
      )}
    </>
  );
}

// Mensal, no resumo/Confira: dia do vencimento e se a mensalidade deste mes ja' foi paga.
// Professor: vazio vira o dia de hoje. Aluno: pode ficar vazio (ele preenche).
function BlocoMensalResumo({ voz, a }) {
  const aluno = voz === 'aluno';
  const rotulo = { fontSize: '12px', fontWeight: '700', color: '#9AA0AB', marginBottom: '7px' };
  const chipP = v => ({ ...chipAcordo(a.mesPago === v), fontSize: '12.5px', padding: '9px 13px', minHeight: '44px' });
  return (
    <div style={{marginTop: '14px'}}>
      <div style={rotulo}>{aluno ? 'QUE DIA DO MÊS VOCÊ PAGA?' : 'VENCIMENTO'}</div>
      <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#16181D'}}>
        todo dia
        <input value={a.diaVenc} onChange={e => a.setDiaVenc(e.target.value.replace(/\D/g, '').slice(0, 2))}
          onBlur={() => a.setDiaVenc(v => aluno
            ? (v === '' ? '' : String(Math.min(31, Math.max(1, Number(v) || 1))))
            : String(Math.min(31, Math.max(1, Number(v) || new Date().getDate()))))}
          inputMode="numeric" aria-label="Dia do mês" style={{...NUM_ACORDO, maxWidth: '70px', textAlign: 'center'}} />
        do mês
      </div>
      {aluno && <div style={{fontSize: '12px', color: '#9AA0AB', marginTop: '6px'}}>Se não souber, deixe em branco — ele preenche.</div>}
      <div style={{...rotulo, margin: '14px 0 7px'}}>A MENSALIDADE DESTE MÊS</div>
      <div style={{display: 'flex', gap: '7px', flexWrap: 'wrap'}}>
        {[[false, aluno ? 'Ainda vou pagar' : 'Ainda vai pagar'], [true, aluno ? 'Já paguei' : 'Já está paga']].map(([v, l]) => (
          <button key={String(v)} type="button" onClick={() => { a.setMesPago(v); a.setErro?.(''); }} style={chipP(v)}>{l}</button>
        ))}
      </div>
      {a.mesPago === true && (
        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '14px', color: '#16181D'}}>
          {aluno ? 'paguei em' : 'pago em'}
          <input type="date" value={a.dataPgto} max={hojeSP()}
            onChange={e => a.setDataPgto(e.target.value)} style={{...INPUT_ACORDO, maxWidth: '180px', padding: '9px 10px'}} />
        </div>
      )}
      {a.mesPago === true && !a.dataPgto && (
        <div style={{fontSize: '11.5px', color: '#B45309', marginTop: '6px'}}>
          {aluno ? 'Não sabe a data? Pode deixar em branco.' : 'Não sabe a data? Pode deixar em branco — o pagamento fica marcado para você confirmar depois.'}
        </div>
      )}
    </div>
  );
}

// Tabela do resumo/Confira. Linha com passo vira botao (44px) com "›" no fim: toque leva aquela
// pergunta. marcados = passos que vieram inteiros do aluno e ninguem abriu: rotulo com "• " azul.
// "ele preenche" sai em cinza escuro e italico (o cinza claro de antes ficava abaixo do contraste).
function TabelaAcordo({ linhas, marcados = [], onTocar }) {
  const cinzaPre = { color: '#6E7480', fontWeight: '600', fontStyle: 'italic' };
  return (
    <div style={{background: '#F7F8FA', borderRadius: '12px', padding: '4px 14px'}}>
      {linhas.map((l, i) => {
        const marcado = l.passo != null && marcados.includes(l.passo);
        const tocavel = l.passo != null && !!onTocar;
        const linha = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '9px 0',
                        borderTop: i ? '1px solid #E2E4E9' : 'none' };
        const miolo = (
          <>
            <span style={{fontSize: '13px', color: '#6E7480', textAlign: 'left'}}>
              {marcado && <span style={{color: '#2F6BFF', fontWeight: '800'}}>• </span>}{l.k}
            </span>
            <span style={{fontSize: '13px', fontWeight: '700', color: '#16181D', textAlign: 'right', marginLeft: 'auto', ...(l.vazio ? cinzaPre : {})}}>
              {l.v}{l.pre ? <span style={cinzaPre}>{l.pre}</span> : null}
            </span>
            {tocavel && <span aria-hidden="true" style={{fontSize: '18px', color: '#9AA0AB', marginLeft: '-4px', lineHeight: 1}}>›</span>}
          </>
        );
        if (tocavel) return (
          <button key={l.k + i} type="button" onClick={() => onTocar(l)} aria-label={`${l.k}: ${l.v}${l.pre || ''}. Toque para mudar`}
            style={{...linha, justifyContent: 'flex-start', width: '100%', minHeight: '44px', background: 'none', borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
                    cursor: 'pointer', fontFamily: 'inherit', margin: 0}}>
            {miolo}
          </button>
        );
        return <div key={l.k + i} style={linha}>{miolo}</div>;
      })}
    </div>
  );
}

// "Nao sei — ele preenche" (so' no aluno): segue deixando em branco o que nao respondeu.
function LinkNaoSei({ onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{marginTop: '10px', minHeight: '44px', border: 'none', background: 'none', color: '#2F6BFF', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', padding: '0', display: 'block'}}>
      Não sei — ele preenche
    </button>
  );
}

// No professor, em cima da pergunta que veio respondida (no todo ou em parte) pelo aluno.
function MarcaDoAluno({ nome, data, extra, texto }) {
  return (
    <div style={{fontSize: '12.5px', color: '#6E7480', background: '#F7F9FF', borderRadius: '10px', padding: '8px 12px', marginBottom: '12px', lineHeight: 1.45}}>
      {texto || `Veio preenchido por ${nome || 'seu aluno'}${data ? ' em ' + data : ''} — confira.${extra || ''}`}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modo Migração — onboarding da carteira: um aluno por tela, 3 perguntas.
// Só o NOME é digitado (a referência que o professor usa); o resto é toque.
// Planos em curso entram com aulas já feitas e sem gerar cobrança se pagos.
// ---------------------------------------------------------------------------
function ModalNovoContrato({ professor, onClose, onSalvou, onConvidar, proposta, alunoExistente, onReativar, completar, onAbrirFicha, pedidosAbertos, onAbrirPedido, jaPagouProximo }) {
  const appCtx = useApp();   // v142 (item D): atualiza o telefone do professor na memoria do app
  // v142 (item E): `completar` = ficha de quem entrou pelo link "Cadastrar varios de uma vez" (ja' tem
  // conta, nome e WhatsApp; falta o combinado). Abre o mesmo cadastro, com o nome preenchido, e o
  // acordo nasce NA ficha dele (aluno_id_existente) — sem criar outra pessoa e sem convite novo.
  // Cadastro GUIADO v2 (ordem e formato definidos pelo Marcus):
  // nome → quanto cobra (por aula OU pelo pacote, numeros digitados, conversao ao vivo)
  // → quadra/sala (pula se nao paga) → dias+horario → ja fez quantas + ja pagou → resumo.
  // Uma pergunta por tela, sempre no cabecalho. Nada de pergunta grande em cima e
  // pergunta pequena embaixo — o professor se perde. E nada de numero ja' digitado:
  // campo preenchido por nos vira dado errado que ninguem confere.
  const DIAS_FULL = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  const [planos, setPlanos] = React.useState([]);
  // Ja' nasce no passo certo (renovacao: 2; pedido do aluno: 1, 2 ou 3) — os efeitos abaixo
  // repetem o mesmo valor. Assim o funil da central de uso nao conta um "passo 1" falso.
  // v143: o aluno responde as mesmas perguntas do professor (lerProposta). Sem nome: 1. Faltou
  // forma, valor ou quantidade: 2. Senao a sala (3), que e' sempre do professor. Dali para a
  // frente pula o que o aluno respondeu INTEIRO (pulados) e cai no resumo, onde essas linhas
  // aparecem com "•" e se tocam para mudar.
  const passoDaProposta = p => { const r = lerProposta(p); return !r.nome ? 1 : !r.completos[2] ? 2 : 3; };
  const [passo, setPasso] = React.useState(() => alunoExistente ? 2 : proposta ? passoDaProposta(proposta) : 1);
  const prop = React.useMemo(() => proposta ? lerProposta(proposta) : null, []);
  // v144 (Marcus 21/10): pedido de um aluno que JA' USA o app — o 1o aluno salvo desta janela e' ele: o banco
  // adota a ficha dele (sem ficha nova e sem convite de volta pelo WhatsApp). Ver completarPedidoDoAluno.
  // Fechamento v144 (revisao, achado 1): o codigo do pedido fica guardado ao ABRIR a janela — o formulario e o pedido
  // nunca podem ser de alunos diferentes (a janela tambem remonta quando o pedido muda: key em Alunos).
  const [pedidoCode] = React.useState(() => proposta && proposta.pedido && proposta.aluno_id ? String(proposta.pedido) : null);
  const pedidoDoAluno = !!pedidoCode && !alunoExistente && !completar;
  // "E' o Marcus — juntar": a ficha SEM conta que o professor ja' tinha recebe a conta do aluno e o acordo
  const [fichaJuntar, setFichaJuntar] = React.useState(null);   // { id, nome }
  const [pulados] = React.useState(() => proposta && !alunoExistente ? passosCompletosDaProposta(proposta) : []);
  const [vistos, setVistos] = React.useState([]);              // passos que o professor ja' passou (Continuar)
  const [voltarResumo, setVoltarResumo] = React.useState(false); // veio de um toque numa linha do resumo
  const PASSO_RESUMO = 9, PASSO_FIM = 10;
  // Central de uso: funil do cadastro (novo_aluno) ou da renovacao (renovar_pacote). O passo 10
  // (pronto) fecha a passada; "cadastrar outro" volta ao 1 e abre uma passada nova.
  const fNovo = useFluxoUso(alunoExistente ? 'renovar_pacote' : 'novo_aluno', passo, passo !== 10);
  React.useEffect(() => { uso.janela(alunoExistente ? 'pacote_renovar' : 'novo_aluno'); }, []);
  const [salvos, setSalvos] = React.useState(0);
  const [salvando, setSalvando] = React.useState(false);
  const [erro, setErro] = React.useState('');

  const [nome, setNome] = React.useState('');
  const [tel, setTel] = React.useState('');
  // Como o nome entra (Marcus, 02/10): primeiro a ESCOLHA — agenda do celular ou digitar —, campo so' depois.
  // null = escolhendo; 'agenda' = veio da agenda (campos preenchidos, sem teclado); 'digitar' = campos com teclado.
  const [entrada, setEntrada] = React.useState(null);
  const [modo, setModo] = React.useState('aula');           // 'aula' | 'pacote' | 'mes'
  const [valorAula, setValorAula] = React.useState('');     // digitado livre
  const [valorPacote, setValorPacote] = React.useState('');
  const [valorMes, setValorMes] = React.useState('');       // mensalidade fixa
  const [qtdAulas, setQtdAulas] = React.useState('');       // digitado livre
  const [sala, setSala] = React.useState('');               // '' = ainda nao respondeu
  const [pagaSala, setPagaSala] = React.useState(null);     // null | false | true
  const [salaTipo, setSalaTipo] = React.useState('aula');   // 'aula' (no acordo) | 'mes' (custo fixo do professor)
  const [salaMesNome, setSalaMesNome] = React.useState('');  // "Smart Fit"
  const [custosMensais, setCustosMensais] = React.useState([]);
  React.useEffect(() => { carregarCustosMensais(professor.id).then(setCustosMensais); }, []);
  const [dias, setDias] = React.useState([]);
  const [hora, setHora] = React.useState('');               // horario unico (caso comum)
  const [horarios, setHorarios] = React.useState({});       // {dia: 'HH:MM'} quando cada dia difere
  const [porDia, setPorDia] = React.useState(false);
  const [duracao, setDuracao] = React.useState(60);          // minutos por aula — 60 e' a regra do mercado
  const [novoPacote, setNovoPacote] = React.useState(null); // null | true (comeca agora) | false (ja rolando)
  const [feitas, setFeitas] = React.useState('');
  const [pago, setPago] = React.useState(null);             // null | true | false
  // Duas datas, independentes. O ciclo pode comecar ANTES do pagamento: o aluno faz duas
  // aulas do pacote novo na terca e na quinta e so' paga na sexta. Uma manda no calendario
  // e nos creditos; a outra manda no financeiro e na renovacao.
  const [inicioCiclo, setInicioCiclo] = React.useState('');   // quando este pacote comecou
  const [dataPgto, setDataPgto] = React.useState('');         // quando ele pagou
  // Mensal: dia do vencimento (padrao: hoje) e se a mensalidade deste mes ja' foi paga.
  const [diaVenc, setDiaVenc] = React.useState(String(new Date().getDate()));
  const [mesPago, setMesPago] = React.useState(false);
  // v155 (teste de 29/10): o aviso vermelho sai assim que o professor corrige o campo (antes "Marque pelo menos um
  // dia" continuava na tela com os dias ja' marcados, ate' o proximo Continuar)
  React.useEffect(() => { setErro(''); }, [valorAula, valorPacote, valorMes, qtdAulas, dias, hora, horarios]);

  // Regras do professor: perguntadas UMA vez, na primeira vez que ele monta um contrato.
  // Antes eu assumia 24h/com reposicao/sem reajuste sem perguntar — assumir termo de
  // contrato pelo professor e' justamente o que nao se pode fazer.
  const [regras, setRegras] = React.useState({ antecedencia: 24, reposicao: true, reajuste: 0 });
  const [precisaRegras, setPrecisaRegras] = React.useState(false);
  const [cancelResp, setCancelResp] = React.useState(null); // '24' | '12' | 'perde' | 'depois'
  const [prazoH, setPrazoH] = React.useState(0);            // v144: prazo para desmarcar (0 = livre, o padrao)
  const [reajResp, setReajResp] = React.useState(null);     // '0' | '5' | '10' | 'outro' | 'depois'
  const [reajOutro, setReajOutro] = React.useState('');

  // Aluno repetido (regras do Marcus, 29/09, depois do caso Cadu x Marcus duplicado):
  //  - procura por TELEFONE alem do nome — telefone igual e' a mesma pessoa;
  //  - se o encontrado teve o vinculo encerrado, o caminho principal e' REATIVAR (historico
  //    continua, cria so' o pacote novo); "criar outro" fica como segunda opcao, com aviso;
  //  - se esta' ativo, nao cria outro: abre a ficha dele.
  const [jaExiste, setJaExiste] = React.useState(null);   // { id, nome, telefone, desvinculado_em, porTelefone }
  const [ignorarDuplicado, setIgnorarDuplicado] = React.useState(false);
  React.useEffect(() => {
    if (alunoExistente) return;
    const busca = nome.trim();
    const fone = (tel || '').replace(/\D/g, '');
    const fone8 = fone.slice(-8);
    setJaExiste(null); setIgnorarDuplicado(false);
    if (busca.length < 3 && fone8.length < 8) return;
    const t = setTimeout(async () => {
      const { data } = await supabase.from('alunos').select('id, nome, telefone, desvinculado_em, user_id' + (pedidoDoAluno ? ', contratos(id, status)' : ''))
        .eq('professor_id', professor.id).limit(200);
      // v143: a ficha provisoria do proprio pedido (o aceite pelo Google poe nela o professor_id)
      // nao e' "aluno repetido" — senao o professor voltava ao passo 1 com "Ana ja' esta' na sua lista".
      // Fechamento v144: nem a ficha que o aluno do pedido ja' tem com voce (mesma conta — o acordo entra nela).
      const lista = (data || []).filter(x => (!proposta?.aluno_id || x.id !== proposta.aluno_id) && (!proposta?.ficha_sua || x.id !== proposta.ficha_sua) && (!completar || x.id !== completar.id));
      const porFone = fone8.length === 8 ? lista.find(a => (a.telefone || '').replace(/\D/g, '').slice(-8) === fone8) : null;
      const porNome = busca.length >= 3 ? lista.find(a => semAcentoSM(a.nome).includes(semAcentoSM(busca))) : null;
      const achado = porFone || porNome;
      setJaExiste(achado ? { ...achado, porTelefone: !!porFone } : null);
    }, 400);
    return () => clearTimeout(t);
  }, [nome, tel, professor.id]);
  // Fechamento v144 (revisao, achado 2): "Novo aluno" EM BRANCO de alguem que ja' mandou o combinado pelo app (o caminho
  // do 21/10: o Iago redigitou de memoria e nasceu ficha nova + convite de volta). Mesmo primeiro nome ou mesmo WhatsApp
  // de um pedido aberto: oferece abrir o pedido (vem preenchido; o aluno ve' no app). Nao impede — pode ser outra pessoa.
  const pedidoParecido = (() => {
    if (proposta || alunoExistente || completar || passo !== 1 || !onAbrirPedido || !Array.isArray(pedidosAbertos) || !pedidosAbertos.length) return null;
    const p1 = semAcentoSM(nome.split(/\s+/).filter(Boolean)[0] || '');
    const f8 = (tel || '').replace(/\D/g, '').slice(-8);
    for (const pd of pedidosAbertos) {
      const n1 = semAcentoSM(String(pd.aluno_nome || '').split(/\s+/).filter(Boolean)[0] || '');
      const t8 = String((pd.proposta && pd.proposta.aluno_telefone) || '').replace(/\D/g, '').slice(-8);
      if (f8.length === 8 && t8 === f8) return { ...pd, _por: 'whatsapp' };
      if (p1.length >= 3 && n1.length >= 3 && (p1 === n1 || p1.startsWith(n1) || n1.startsWith(p1))) return { ...pd, _por: 'nome' };
    }
    return null;
  })();
  // Pedido de aluno que JA' esta' na lista (v140): o cadastro da proposta comeca no passo 2 ou 3 e
  // o aviso de repetido so' existe no passo 1 — o professor criava uma segunda ficha sem ver nada.
  // Volta ao passo 1, onde aparecem o aviso, o bloqueio por WhatsApp ativo e o "Reativar".
  React.useEffect(() => {
    if (proposta && jaExiste && !ignorarDuplicado && !(fichaJuntar && fichaJuntar.id === jaExiste.id) && salvos === 0 && passo > 1 && passo < PASSO_FIM) setPasso(1);
  }, [jaExiste]);

  // Revisao v142 (achado 2 da ida e volta, GRAVE): o "Completar" de quem entrou pelo link geral pode ser de
  // alguem que o professor JA' tinha cadastrado (ficha sem conta, com acordo, aulas e cobrancas). Sem isto
  // nasciam duas fichas da mesma pessoa (aulas e Financeiro em dobro). Aqui aparecem as fichas sem conta do
  // professor — as parecidas primeiro (mesmo WhatsApp, mesmo primeiro nome) — para JUNTAR numa so'
  // (professor_juntar_fichas, sql/v142b.sql): a conta e o WhatsApp vao para a ficha que ele ja' tinha.
  const [semConta, setSemConta] = React.useState(null);    // null = carregando | [{ id, nome, telefone, temAcordo, acordoPendente }]
  const [verTodosSemConta, setVerTodosSemConta] = React.useState(false);
  const [juntarCom, setJuntarCom] = React.useState(null);  // a ficha escolhida (confirmacao dentro da caixa)
  const [juntando, setJuntando] = React.useState(false);
  const juntandoRef = React.useRef(false);
  React.useEffect(() => {
    if (!completar) return;
    supabase.from('alunos').select('id, nome, telefone, contratos(id, status, status_aceite)')
      .eq('professor_id', professor.id).is('user_id', null).is('desvinculado_em', null).order('nome').limit(300)
      .then(({ data, error }) => setSemConta(error ? [] : (data || []).filter(x => x && x.id !== completar.id).map(x => {
        const ativos = (x.contratos || []).filter(k => k.status === 'ativo');
        return { id: x.id, nome: x.nome || 'Aluno', telefone: x.telefone || '', temAcordo: ativos.length > 0, acordoPendente: ativos.some(k => k.status_aceite === 'pendente') };
      })), () => setSemConta([]));
  }, []);
  const candidatosJuntar = (() => {
    if (!completar || !semConta) return { parecidos: [], outros: [] };
    const f8 = digitosWhatsSM(completar.telefone).slice(-8);
    const prims = [completar.nome, nome].map(t => semAcentoSM(String(t || '').trim().split(/\s+/)[0])).filter(t => t.length >= 2);
    // mesmo primeiro nome, ou um comeca o outro ("Dani" x "Daniela"), com 3 letras ou mais
    const mesmoPrimeiro = xp => prims.some(pp => pp === xp || (Math.min(pp.length, xp.length) >= 3 && (pp.startsWith(xp) || xp.startsWith(pp))));
    const pontua = x => (f8.length === 8 && digitosWhatsSM(x.telefone).slice(-8) === f8 ? 2 : 0)
      + (mesmoPrimeiro(semAcentoSM(String(x.nome || '').trim().split(/\s+/)[0])) ? 1 : 0);
    const com = semConta.map(x => ({ ...x, p: pontua(x) }));
    return { parecidos: com.filter(x => x.p > 0).sort((a, b) => b.p - a.p), outros: com.filter(x => x.p === 0) };
  })();
  function textoErroJuntarSM(cod, nomeFicha) {
    const X = nomeFicha || 'essa ficha', X1 = primeiroNomeSM(nomeFicha, 'a pessoa');
    return ({
      sem_funcao: 'Ainda não dá para juntar por aqui. Por enquanto: abra a ficha que você já tinha e mande o link pessoal dela.',
      ficha_com_conta: `${X} já usa o app com outra conta — não dá para juntar. Fale com ${X1}.`,
      ficha_com_acordo: 'Quem entrou pelo link já tem acordo ou aulas nesta ficha — não dá para juntar.',
      ja_tem_outra_ficha: 'Essa pessoa já está ligada a outra ficha sua. Feche e confira a lista.',
      ficha_encerrada: `A ficha de ${X} está encerrada. Reative ${X1} antes de juntar.`,
      sem_conta: 'Essa pessoa ainda não entrou no app. Feche e confira a lista.',
      rede: 'Não foi possível juntar agora. Confira a internet e tente de novo.',
    })[cod] || (['nao_encontrado', 'pedido_invalido', 'sem_permissao'].includes(cod)
      ? 'Não encontramos uma das fichas. Feche e confira a lista.' : 'Não foi possível juntar agora. Tente de novo em instantes.');
  }
  async function juntar() {
    if (!juntarCom || !completar || juntandoRef.current) return;
    juntandoRef.current = true; setJuntando(true); setErro('');
    const alvo = juntarCom;
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('professor_juntar_fichas', { p_ficha_app: completar.id, p_ficha_cadastrada: alvo.id })); } catch (e) { error = e; }
    juntandoRef.current = false; setJuntando(false);
    if (error || !data || data.ok === false) {
      const cod = error ? (semFuncaoBancoSM(error.message || error) ? 'sem_funcao' : 'rede') : String((data && data.erro) || 'sem_resposta');
      if (cod === 'rede') uso.erro('erro.juntar_fichas', (error && error.message) || String(error));
      else uso.bloqueio('juntar_fichas', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      setErro(textoErroJuntarSM(cod, alvo.nome));
      return;
    }
    uso.acao('novo_aluno.completar_juntou', { tinha_acordo: !!alvo.temAcordo, pendente: !!alvo.acordoPendente, ja_feito: !!data.ja_feito, parecido: (candidatosJuntar.parecidos || []).some(x => x.id === alvo.id) });
    const P = primeiroNomeSM(professor.nome, 'Seu professor');
    if (!data.ja_feito) {
      // aviso no app do aluno (insert de sempre do professor; o banco entrega ao aluno)
      const { error: eN } = await supabase.from('notificacoes').insert({ aluno_id: alvo.id, professor_id: professor.id, tipo: alvo.acordoPendente ? 'contrato' : 'vinculo',
        mensagem: alvo.acordoPendente ? `${P} ligou você à sua ficha. Seu acordo está pronto para você conferir e confirmar no app.`
          : alvo.temAcordo ? `${P} ligou você à sua ficha: suas aulas e pagamentos já aparecem aqui.`
          : `${P} ligou você à sua ficha e vai completar o combinado de vocês. Você confere aqui.` });
      if (eN) uso.erro('erro.juntar_aviso', eN.message);
    }
    const A1 = primeiroNomeSM(alvo.nome, 'Seu aluno');
    toastSM(alvo.acordoPendente ? `Pronto! ${A1} agora está ligado ao app — o acordo aparece para ele conferir.`
      : alvo.temAcordo ? `Pronto! ${A1} agora vê as aulas e os pagamentos no app.`
      : `Pronto! ${A1} agora está ligado ao app. Toque em Completar de novo para combinar o pacote.`);
    onSalvou?.();
    onClose();
  }

  // A voz saiu do cadastro: e' aqui que nasce o contrato, e erro de interpretacao em
  // valor ou tamanho de pacote so' aparece semanas depois, como saldo errado. Ela volta
  // no lancamento de aula ("acabei de dar aula para o Marcus"), onde o erro e' barato e
  // visivel na hora. O modulo interpretar.js continua no projeto para esse dia.
  const [criado, setCriado] = React.useState(null);
  // v149 (proximo pacote): renovacao com aula do pacote atual ainda marcada. fila = { ativa, abertas, ultima,
  // horaUltima, inicio (dia seguinte a' ultima, nunca antes de hoje), proximo (ja' existe um na fila) }.
  const [fila, setFila] = React.useState(null);
  const [inicioFila, setInicioFila] = React.useState('');
  const [mandou, setMandou] = React.useState(false);        // ja' abriu o convite no WhatsApp (tela "Pronto!")
  // Item D (v142): no pedido do aluno, o professor confere o proprio WhatsApp no resumo — o aluno
  // cadastrou so' o nome dele. Vem preenchido com o do cadastro; obrigatorio so' se nao houver.
  // Revisao v142 (achado 4 da ida e volta): sem numero valido no cadastro do professor, o campo comeca com o
  // WhatsApp que o aluno pegou da agenda dele (prop.profTel) — marcado "(confira)". Antes esse numero so' abria
  // o WhatsApp do aluno e se perdia; o professor que entrou pelo Google via o campo vazio e obrigatorio.
  const [meuWhats, setMeuWhats] = React.useState(() => whatsValidoSM(professor?.telefone) ? professor.telefone : (prop?.profTel || professor?.telefone || ''));
  const [erroWhats, setErroWhats] = React.useState('');
  const nomeRef = React.useRef(null);
  // Cada pergunta abre no topo (antes o resumo abria rolado, com o titulo fora da vista).
  const folhaRef = React.useRef(null);
  React.useEffect(() => { if (folhaRef.current) folhaRef.current.scrollTop = 0; }, [passo]);

  // Proposta vinda do aluno que convidou este professor. Preenche o que ele ja' disse e
  // para na primeira pergunta que falta. Nada vira contrato sem o professor passar pelo
  // resumo e tocar em criar — o dinheiro e' dele, a conferencia tem de ser dele.
  // v143: tudo passa por lerProposta (o JSON e' escrito pelo aluno) e entra cada resposta dele:
  // forma, valor (formatado "2.000,00"), quantidade (agora tambem no "por aula"), duracao, ja'
  // comecou/quantas/desde quando, ja' pagou/quando e, no mensal, vencimento e mes pago.
  React.useEffect(() => {
    if (!prop) return;
    if (prop.nome) setNome(prop.nome);
    if (prop.tel) setTel(prop.tel);
    if (prop.dias.length) setDias(prop.dias);
    if (prop.hora) setHora(prop.hora);
    // v140: o aluno pode mandar um horario por dia (so' quando diferem). Abre o passo dos dias
    // ja' separado por dia — senao a agenda nasceria toda no horario do primeiro dia.
    if (prop.porDia && prop.horarios) {
      const base = {};
      prop.dias.forEach(d => { base[d] = prop.horarios[d] || prop.hora || ''; });
      setHorarios(base); setPorDia(true);
    }
    // Duracao em branco ("Nao sei", ou convite antigo, que nao perguntava): o professor escolhe —
    // 1h ja' marcado parecia resposta do aluno (o passo 4 mostra "Veio preenchido por...").
    if (prop.duracao) setDuracao(prop.duracao);
    else if (prop.doAluno[4]) setDuracao(null);
    if (prop.modo) setModo(prop.modo);
    if (prop.valor > 0) {
      if (prop.modo === 'aula') setValorAula(formatarDinheiro(prop.valor));
      else if (prop.modo === 'pacote') setValorPacote(formatarDinheiro(prop.valor));
      else if (prop.modo === 'mes') setValorMes(formatarDinheiro(prop.valor));
    }
    if (prop.qtd) setQtdAulas(String(prop.qtd));
    setNovoPacote(prop.novoPacote);
    if (prop.feitas !== null) setFeitas(String(prop.feitas));
    setInicioCiclo(prop.inicioCiclo);
    setPago(prop.pago);
    setDataPgto(prop.dataPgto);
    if (prop.modo === 'mes') {
      if (prop.diaVenc) setDiaVenc(String(prop.diaVenc));
      setMesPago(prop.mesPago);   // null = o aluno nao sabia: nada marcado, o professor escolhe no resumo
    }
    if (prop.desmarcarH !== null) setPrazoH(prop.desmarcarH);   // v144: o prazo que o aluno marcou (0 = livre)
    setPasso(passoDaProposta(proposta));
  }, []);

  // Pacote novo para aluno que ja' existe ("Pacote terminou — renovar" etc.): o nome ja'
  // esta' definido, entao a primeira pergunta (que e' sobre o aluno, nao sobre o pacote)
  // nao faz sentido — comeca direto em "quanto voce cobra".
  React.useEffect(() => {
    if (!completar) return;
    setNome(completar.nome || '');
    setTel(completar.telefone || '');
  }, []);
  React.useEffect(() => {
    if (!alunoExistente) return;
    setNome(alunoExistente.nome || '');
    setTel(alunoExistente.telefone || '');
    setPasso(2);
    // v144: a renovacao continua a regra de desmarcar do acordo anterior (livre segue livre; 24h segue 24h)
    supabase.from('contratos').select('*').eq('aluno_id', alunoExistente.id).order('data_inicio', { ascending: false }).limit(1)
      .then(({ data }) => {
        const k = data && data[0]; if (!k) return;
        setPrazoH(k.cancelamento_livre ? 0 : (Number(k.antecedencia_cancelamento_horas) || 24));
        // v155 (teste de 29/10): renovar e' quase sempre "o mesmo de novo" — valor, aulas, quadra e duracao ja' vem
        // do pacote atual para o professor so' conferir (antes os campos vinham vazios com exemplo "150,00" e "8"
        // em cinza, que pareciam preenchidos: Continuar dava "Digite o valor da aula")
        const din = n => Number(n) > 0 ? Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        if (k.tipo_cobranca === 'mensal') { setModo('mes'); setValorMes(din(k.valor_mensal || k.preco_negociado)); }
        else if (Number(k.total_aulas) > 0 && Number(k.preco_negociado) > 0) {
          setModo('aula'); setValorAula(din(Number(k.preco_negociado) / Number(k.total_aulas))); setQtdAulas(String(k.total_aulas));
        }
        if (Number(k.duracao_min) > 0) setDuracao(Number(k.duracao_min));
        if (Number(k.taxa_academia) > 0) { setPagaSala(true); setSalaTipo('aula'); setSala(din(k.taxa_academia)); }
        else if (k.taxa_academia != null) setPagaSala(false);
      }, () => {});
    // ...e os dias e horarios da agenda dele
    supabase.from('agenda_semanal').select('dia_semana, hora').eq('aluno_id', alunoExistente.id).eq('professor_id', professor.id).eq('ativo', true)
      .then(({ data }) => {
        const ag = (data || []).filter(x => x.hora != null && x.dia_semana >= 0 && x.dia_semana <= 6);
        if (!ag.length) return;
        const ds = [...new Set(ag.map(x => Number(x.dia_semana)))].sort();
        const hs = {}; ag.forEach(x => { hs[Number(x.dia_semana)] = String(x.hora).slice(0, 5); });
        const unicas = [...new Set(Object.values(hs))];
        setDias(ds);
        if (unicas.length === 1) setHora(unicas[0]); else { setHorarios(hs); setPorDia(true); }
      }, () => {});
    // O aluno avisou que ja' pagou o proximo pacote (pedido dele): o "Ja' pagou" vem marcado.
    // (sem chutar a data: o professor informa quando recebeu — regra de 16/09, "Ja' esta' pago" nao assume hoje)
    if (jaPagouProximo) setPago(true);
    carregarFila();
  }, []);
  // v149: o que falta do pacote atual decide se o novo entra na fila. v155: consulta que falhou NAO vira "Comeca
  // agora" calado (fila.erro: o passo 5 mostra "Tentar de novo" e o criar() trava).
  function carregarFila() {
    if (!alunoExistente) return;
    setFila(null);
    (async () => {
      const { data: cts, error: eC } = await supabase.from('contratos').select('id, status, tipo_cobranca, turma_id, data_inicio')
        .eq('aluno_id', alunoExistente.id).eq('professor_id', professor.id).in('status', ['ativo', 'proximo']);
      if (eC) throw eC;
      const lista = cts || [];
      const proximo = lista.find(k => k.status === 'proximo') || null;
      const atual = lista.filter(k => k.status === 'ativo' && k.tipo_cobranca !== 'mensal' && !k.turma_id)
        .sort((x, y) => String(y.data_inicio).localeCompare(String(x.data_inicio)))[0];
      let abertas = [], registradas = [];
      if (atual) {
        // So' as de hoje em diante: aula passada sem registro nao segura o pacote novo (a virada leva para ele no dia de inicio)
        const { data: au, error: eA } = await supabase.from('aulas').select('data, hora, desfecho_prof, divergencia_em').eq('contrato_id', atual.id)
          .in('status', ['agendada', 'aguardando_confirmacao']).gte('data', hojeSP()).order('data').order('hora');
        if (eA) throw eA;
        // v155: aula que o professor ja' registrou (esperando o aluno) ou em divergencia nao e' "aula que falta"
        abertas = (au || []).filter(x => !x.desfecho_prof && !x.divergencia_em);
        registradas = (au || []).filter(x => x.desfecho_prof || x.divergencia_em);
      }
      const ult = abertas.length ? abertas[abertas.length - 1] : null;
      const hoje = hojeSP();
      const inicio = ult ? (somarDiaSM(ult.data, 1) > hoje ? somarDiaSM(ult.data, 1) : hoje) : hoje;
      setFila({ ativa: abertas.length > 0 && !proximo, abertas, registradas, ultima: ult ? ult.data : null,
                horaUltima: ult && ult.hora ? String(ult.hora).slice(0, 5) : '', inicio, proximo });
      if (abertas.length > 0 && !proximo) setNovoPacote(true);   // "Depois da ultima aula" ja' vem marcado
    })().catch(e => { uso.erro('erro.renovar_fila', e?.message || String(e)); setFila({ ativa: false, abertas: [], registradas: [], proximo: null, erro: true }); });
  }

  React.useEffect(() => {
    supabase.from('planos').select('id').eq('professor_id', professor.id).limit(1).then(({data}) => setPlanos(data||[]));
    carregarPadroes(supabase, professor.id).then(setRegras);
    // O wizard abre sempre na primeira pergunta. Havia uma tela 0 de boas-vindas aqui:
    // alem de repetir um convite que a pessoa ja' aceitou ao tocar no botao, o passo 0
    // nao existia em passosAtivos() — entao 'Vamos la'' caia direto no resumo, com o
    // cadastro vazio. Duas razoes para tirar, uma de UX e uma de bug.
    // Prazo de cancelamento e reajuste NAO se perguntam no cadastro. Um professor que
    // acabou de chegar nunca parou para pensar nisso — perguntar aqui nao e' coletar um
    // dado, e' ensinar que existe uma decisao dificil que ele ainda nao tomou, e trava-lo
    // nela. O app assume o padrao do mercado (aviso de 24h, com reposicao, sem reajuste
    // automatico) e pergunta na hora em que a regra importa de verdade: na primeira vez
    // que alguem desmarca. Ate' la', da' para mudar na ficha de cada aluno.
    setPrecisaRegras(false);
  }, []);

  // Item D: no pedido do aluno o professor confere o proprio WhatsApp. Revisao v142 (achado 5): tambem no
  // "Novo aluno" e no "Completar" quando o cadastro dele ainda nao tem numero valido (entrou pelo Google, ou
  // deixou em branco) — senao o "Estou no local"/"Chamar no WhatsApp" do aluno abria sem destinatario.
  const whatsProfObrigatorio = !whatsValidoSM(professor?.telefone);
  const pedeWhatsProf = salvos === 0 && !alunoExistente && (!!prop || whatsProfObrigatorio);
  const whatsVeioDoAluno = !!prop && whatsProfObrigatorio && whatsValidoSM(prop.profTel) && mesmoWhatsSM(meuWhats, prop.profTel);
  // Uma linha do combinado para o convite ("Seg · Qua às 07:00 · pacote de 10 aulas · R$ 2.000").
  const resumoLocalDoAcordo = () => {
    const partes = [];
    if (dias.length) partes.push(c.agendaTxt);
    if (c.ehMensal) { if (c.mensalN > 0) partes.push(brlAcordo(c.mensalN) + ' por mês'); }
    else {
      if (c.pacoteN > 0) partes.push(c.porAula ? `${c.pacoteN} aulas` : `pacote de ${c.pacoteN} aulas`);
      if (c.totalPacote > 0) partes.push(brlAcordo(c.totalPacote));
    }
    return partes.join(' · ');
  };
  const num = numDinheiro;
  // v143: as contas e as regras de cada pergunta sao as do bloco "O COMBINADO" (contaDoAcordo,
  // validarPassoAcordo), as mesmas do "Adicionar professor" do aluno. Mesmos nomes de antes.
  const a = { modo, setModo, valorAula, setValorAula, valorPacote, setValorPacote, valorMes, setValorMes,
              qtdAulas, setQtdAulas, dias, setDias, hora, setHora, horarios, setHorarios, porDia, setPorDia,
              duracao, setDuracao, novoPacote, setNovoPacote, feitas, setFeitas, inicioCiclo, setInicioCiclo,
              pago, setPago, dataPgto, setDataPgto, diaVenc, setDiaVenc, mesPago, setMesPago, setErro,
              fila: alunoExistente && fila && fila.ativa && modo !== 'mes' ? fila : null, inicioFila, setInicioFila };
  const c = contaDoAcordo(a);
  const naFila = !!a.fila;   // v149: o pacote novo entra na fila (ver CorpoFila)
  // "Por aula" nao fala em pacote (Marcus, 30/09): quem cobra 200 por aula combina "8 aulas",
  // nao "um pacote de 8". A palavra pacote fica so' para quem escolheu "Pelo pacote".
  // Mensalidade fixa: agenda fixa, sem reposicao, sem saldo de aulas — somem "quantas ja' fez"
  // e "ja' esta' pago" (nao existe pacote para ter sido pago).
  const { pacoteN, totalPacote, valorAulaEfetivo, porAula, ehMensal, mensalN, feitasN, restantes } = c;
  const salaN = pagaSala && salaTipo === 'aula' ? num(sala) : 0;
  const salaMesN = pagaSala && salaTipo === 'mes' ? num(sala) : 0;
  const salaMesJaExiste = custosMensais.some(x => semAcentoSM(x.nome) === semAcentoSM(salaMesNome));
  const liquido = totalPacote - salaN * pacoteN;

  // Perguntas que existem nesta combinacao (passosAtivos), sem o resumo e o "pronto".
  // Antes era um numero fixo (6 / 4) e a renovacao mostrava "de 6" com 5 perguntas.
  const TOTAL = passosAtivos().filter(x => x < PASSO_RESUMO).length;
  // As regras agora sao escolhidas no resumo, por aluno — nao mais num passo separado.
  // `precisaRegras` ficou desligado (ver comentario no useEffect), entao esta conta passa
  // a valer sempre que o professor tiver mexido nos chips.
  const regrasEscolhidas = (cancelResp || reajResp) ? {
    antecedencia: cancelResp === '12' ? 12 : 24,
    reposicao: cancelResp !== 'perde',
    reajuste: reajResp === '5' ? 5 : reajResp === '10' ? 10 : reajResp === 'outro' ? num(reajOutro) : 0,
  } : regras;
  // v144 (Marcus 21/10): o prazo para desmarcar vem SO' da pergunta do resumo — sem escolha, LIVRE (antes gravava
  // 24h sem ninguem escolher). Livre guarda o padrao em antecedencia so' para o dia em que combinarem um prazo.
  const regrasFinais = { ...regrasEscolhidas, antecedencia: prazoH > 0 ? prazoH : (Number(regras.antecedencia) || 24), livre: !(prazoH > 0) };

  // Horario daquele dia (v143: separado por dia, o dia apagado fica vazio e trava — regra do aluno).
  const horaDe = d => horaDoDiaAcordo(a, d);
  const descreverDias = () => c.agendaTxt;

  function aplicarDitado(texto) {
    const r = interpretarContrato(texto);
    const d = r.dados;
    if (d.nome) setNome(d.nome);
    if (d.valor_aula) { setModo('aula'); setValorAula(String(d.valor_aula)); }
    if (d.pacote) setQtdAulas(String(d.pacote));
    if (d.custo_sala !== undefined) { setPagaSala(d.custo_sala > 0); setSala(d.custo_sala > 0 ? String(d.custo_sala) : ''); }
    if (d.dias && d.dias.length) setDias(d.dias);
    if (d.hora) setHora(d.hora);
    if (d.horarios) { setHorarios(d.horarios); setPorDia(true); }   // "terça ao meio-dia e quinta às 19h"
    if (d.aulas_feitas) { setNovoPacote(false); setFeitas(String(d.aulas_feitas)); }
    if (d.pacote_pago !== undefined) setPago(d.pacote_pago);
    if (r.entendido.length === 0) { toastSM('Não peguei nada da frase — responde pergunta por pergunta que é rapidinho'); return; }
    toastSM('Entendi: ' + r.entendido.join(' · '));
    // Cai direto no primeiro buraco que a frase nao preencheu, em vez de repetir tudo.
    const temValor = (d.valor_aula > 0) || num(valorAula) > 0 || num(valorPacote) > 0;
    const temPacote = (d.pacote > 0) || pacoteN > 0;
    const temDias = (d.dias && d.dias.length) || dias.length;
    const temHora = d.hora || d.horarios || hora;
    // O alvo tem de existir em passosAtivos(). Na mensalidade os passos 5 e 6 nao
    // existem, e cair neles dava "Pergunta 0 de 4" com o Continuar pulando para o fim.
    const lista = passosAtivos();
    const alvo = (
      !d.nome && !nome.trim() ? 1
      : (!temValor || !temPacote) ? 2
      : (d.custo_sala === undefined && pagaSala === null) ? 3
      : (!temDias || !temHora) ? 4
      : (d.aulas_feitas === undefined && novoPacote === null) ? 5
      : (d.pacote_pago === undefined && pago === null) ? 6
      : PASSO_RESUMO
    );
    setPasso(lista.includes(alvo) ? alvo : (lista.find(x => x > alvo) ?? PASSO_RESUMO));
  }

  // Quais passos existem nesta combinacao (mensal x precisa perguntar as regras).
  // Manter uma lista e' mais seguro que somar e subtrair 1 com excecoes espalhadas.
  function passosAtivos() {
    const p = alunoExistente ? [2, 3, 4] : [1, 2, 3, 4];
    if (!ehMensal) p.push(5, 6);
    if (precisaRegras) { if (!ehMensal) p.push(7); p.push(8); }
    p.push(PASSO_RESUMO, PASSO_FIM);
    // v143: com a proposta do aluno, some o que ele respondeu INTEIRO (menos o passo aberto agora,
    // que o professor pode ter tocado no resumo). O 1 nunca some: o aviso de repetido mora nele.
    return salvos === 0 && pulados.length ? p.filter(x => !pulados.includes(x) || x === passo) : p;
  }
  // Para onde vai o Continuar. Voltando de uma linha do resumo: a primeira pergunta (entre 2 e 8)
  // que ainda nao foi vista — ex.: trocou "Por mes" por "Pelo pacote" e faltam 5 e 6 —, senao o resumo.
  // Tambem e' pendente a pergunta ja' vista (ou pulada) que ficou ERRADA com a mudanca — ex.: o
  // pacote caiu de 10 para 5 e as "8 feitas" do aluno passaram do total: vai direto ao passo 5.
  function destino(p) {
    if (!voltarResumo) return proximoPasso(p);
    const vs = [...vistos, p];
    const ativos = passosAtivos();
    const errado = x => [2, 4, 5, 6].includes(x) && !(ehMensal && x >= 5) && !!validarPassoAcordo(x, a, c, { voz: 'professor' });
    const pendente = [2, 3, 4, 5, 6, 7, 8].find(x => x !== p && ((ativos.includes(x) && !vs.includes(x)) || errado(x)));
    return pendente ?? PASSO_RESUMO;
  }
  function seguir(p) {
    const d = destino(p);
    setVistos(v => v.includes(p) ? v : [...v, p]);
    setErro('');
    if (d === PASSO_RESUMO) setVoltarResumo(false);
    setPasso(d);
  }
  function proximoPasso(p) {
    const lista = passosAtivos();
    const i = lista.indexOf(p);
    return i >= 0 && i < lista.length - 1 ? lista[i + 1] : PASSO_RESUMO;
  }
  function passoAnterior(p) {
    const lista = passosAtivos();
    const i = lista.indexOf(p);
    return i > 0 ? lista[i - 1] : lista[0];
  }
  // "Pergunta 3 de 5" — a posicao dentro dos passos que realmente existem.
  const iPasso = passosAtivos().indexOf(passo);
  const numPergunta = iPasso >= 0 ? iPasso + 1 : 1;

  function avancar() {
    setErro('');
    if (passo === 1 && !nome.trim()) { fNovo.barrar('sem_nome'); setErro('Me diz o nome dele primeiro'); nomeRef.current?.focus(); return; }
    // Avisa, mas nao impede: o professor pode ter dois alunos com o mesmo primeiro nome.
    // Toca Continuar de novo e segue. So' o duplo toque acidental e' bloqueado, la' no criar().
    if (passo === 1 && jaExiste && !ignorarDuplicado && !completar && !(fichaJuntar && fichaJuntar.id === jaExiste.id)) {
      if (jaExiste.porTelefone && !jaExiste.desvinculado_em) {
        // mesmo telefone, aluno ativo. Revisao v142 (20/10): o numero agora aparece (com "Tirar") e o 2o toque
        // segue — irmaos com o WhatsApp da mae sao o caso comum (antes era um beco: o numero escondido da agenda
        // travava o cadastro sem nada na tela). Se for a mesma pessoa: "Abrir a ficha" no aviso de cima.
        fNovo.barrar('telefone_ja_ativo');
        setErro(`Esse WhatsApp já é de "${jaExiste.nome}". Se for outra pessoa (ex.: irmão), toque em Continuar de novo. Se for ${primeiroNomeSM(jaExiste.nome, 'ele')}, ${onAbrirFicha ? 'toque em "Abrir a ficha" acima' : 'feche e abra a ficha dele'}.`);
        setIgnorarDuplicado(true);
        return;
      }
      fNovo.barrar(jaExiste.desvinculado_em ? 'ex_aluno' : 'nome_repetido');
      setErro(jaExiste.desvinculado_em
        ? `Você já teve "${jaExiste.nome}" (vínculo encerrado). Toque em Reativar acima, ou em Continuar de novo para criar outra pessoa.`
        : `Você já tem "${jaExiste.nome}" na lista. Se for outra pessoa, toque em Continuar de novo.`);
      setIgnorarDuplicado(true);
      return;
    }
    // Passos 2, 4, 5 e 6: as mesmas regras do aluno (validarPassoAcordo). 1, 3, 7 e 8 ficam aqui.
    // Obrigatorio e' so' o que o app nao funciona sem: valor, quantidade, dias, quantas ja' fez e
    // pago ou nao. Datas sao opcionais — travar numa data que o professor nao tem na cabeca e'
    // onde ele desiste no meio.
    if ([2, 4, 5, 6].includes(passo)) {
      const v = validarPassoAcordo(passo, a, c, { voz: 'professor' });
      if (v) { fNovo.barrar(v.codigo); setErro(v.msg); return; }
    }
    // v155: pacote com aula ainda marcada nao vira mensalidade no meio (as aulas pagas ficavam presas no acordo encerrado)
    if (passo === 2 && modo === 'mes' && avisoMensalComAulas()) { fNovo.barrar('mensal_com_aulas'); setErro(avisoMensalComAulas()); return; }
    if (passo === 3 && pagaSala === null) { fNovo.barrar('sala_sem_resposta'); setErro('Responde essa rapidinho: paga ou não paga?'); return; }
    if (passo === 3 && pagaSala === true && salaTipo === 'mes' && !salaMesNome.trim()) { fNovo.barrar('sala_sem_academia'); setErro('Qual é a academia? Ex.: Smart Fit'); return; }
    if (passo === 3 && pagaSala === true && num(sala) <= 0) { fNovo.barrar('sala_sem_valor'); setErro(salaTipo === 'mes' ? 'Digite quanto você paga por mês' : 'Digite quanto você paga por aula, ou toque em "Não pago"'); return; }
    if (passo === 7 && cancelResp === null) { fNovo.barrar('regra_sem_resposta'); setErro('Escolha uma opção — dá pra deixar pra depois'); return; }
    if (passo === 8 && reajResp === null) { fNovo.barrar('reajuste_sem_resposta'); setErro('Escolha uma opção — dá pra deixar pra depois'); return; }
    if (passo === 8 && reajResp === 'outro' && num(reajOutro) <= 0) { fNovo.barrar('reajuste_sem_pct'); setErro('Digite o percentual do reajuste'); return; }
    if (passo === PASSO_FIM) { if (alunoExistente || completar || criado?.adotado) { onClose(); } else { proximoAluno(); } return; }
    seguir(passo);
  }

  // v155: o texto de quando o professor troca para "Por mes" com aulas do pacote atual ainda marcadas (ou '' se pode)
  function avisoMensalComAulas() {
    if (!alunoExistente || !fila || !fila.ativa) return '';
    const n = fila.abertas.length;
    return `O pacote atual ainda tem ${n} aula${n === 1 ? '' : 's'} marcada${n === 1 ? '' : 's'}, até ${fmtDiaMes(fila.ultima)}. Para passar para mensalidade, termine essas aulas (ou desmarque as que não vão acontecer) e depois renove.`;
  }

  function proximoAluno() {
    // Mantem valor e quadra — sao os mesmos na maioria dos alunos. Dias e horario NAO:
    // cada aluno tem o seu, e manter os do anterior fazia o segundo aluno nascer no
    // mesmo horario do primeiro sem ninguem perceber (teste de 29/09).
    setNome(''); setTel(''); setEntrada(null); setNovoPacote(null); setFeitas(''); setPago(null);
    setInicioCiclo(''); setDataPgto('');
    setDias([]); setHora(''); setHorarios({}); setPorDia(false);
    setMesPago(false); setDiaVenc(String(new Date().getDate()));
    setCriado(null);
    setVistos([]); setVoltarResumo(false);
    setPasso(1);
    setTimeout(() => nomeRef.current?.focus(), 80);
  }

  // Trava de duplo toque. Em 15/09 dois contratos nasceram com 0,66s de diferenca porque
  // o dedo encostou duas vezes no botao. `salvando` sozinho nao segura: entre o clique e o
  // React redesenhar a tela passa tempo suficiente para o segundo toque entrar. Esta marca
  // e' sincrona — vale no mesmo instante do clique.
  const criandoRef = React.useRef(false);
  // Fechamento v144 (revisao, achado 3): "e' o Marcus — juntar" com a ficha que voce ja' tinha. Se ela ja' tem acordo,
  // junta na hora (o banco liga a conta do aluno a ela e resolve o pedido; nenhum acordo novo). Sem acordo, segue o
  // formulario e o acordo nasce nela.
  async function juntarComFicha(alvo) {
    if (!alvo) return;
    if (!alvo.temAcordo) {
      uso.acao('novo_aluno.pedido_juntar', { tinha_acordo: false });
      setFichaJuntar({ id: alvo.id, nome: alvo.nome }); setIgnorarDuplicado(true); setErro('');
      return;
    }
    if (criandoRef.current) return;
    criandoRef.current = true; setSalvando(true); setErro('');
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('professor_completar_pedido', { p_pedido: pedidoCode, p_dados: { ficha_id: alvo.id } })); } catch (e) { error = e; }
    criandoRef.current = false; setSalvando(false);
    if (error || !data || data.ok === false) {
      const cod = error ? 'rede' : String((data && data.erro) || 'sem_resposta');
      fNovo.barrar(/^[a-z_]{1,30}$/.test(cod) ? 'pedido_juntar_' + cod : 'pedido_juntar_outro');
      if (cod === 'rede') uso.erro('erro.pedido_juntar', (error && error.message) || String(error));
      setErro(cod === 'rede' ? 'Não foi possível juntar agora. Confira a internet e tente de novo.'
        : cod === 'cancelado' ? `${primeiroNomeSM(nome, 'O aluno')} cancelou este pedido. Não precisa completar.`
        : 'Não deu para juntar com essa ficha. Feche e confira a lista de alunos.');
      return;
    }
    uso.acao('novo_aluno.pedido_juntar', { tinha_acordo: true, ja_feito: !!data.ja_feito });
    setCriado({ id: data.aluno_id || alvo.id, nome: alvo.nome || nome.trim(), telefone: '', aulas: 0, codigo: null,
                adotado: { aceito: !!data.aceito, mudancas: [], ficha: 'juntou_com_acordo', semProposta: false } });
    setMandou(false);
    setSalvos(n => n + 1);
    onSalvou?.();
    fNovo.fim('salvou', { modo, proposta: true, adotou: 'juntou' });
    setPasso(PASSO_FIM);
  }

  async function criar() {
    if (criandoRef.current) return;      // segundo toque: ignora
    // v155: renovacao sem saber o que falta do pacote atual (a consulta falhou ou nao voltou) nao grava no escuro
    if (alunoExistente && (!fila || fila.erro)) {
      fNovo.barrar('fila_sem_resposta');
      setErro('Não consegui conferir as aulas que faltam do pacote atual. Confira a internet e toque de novo.');
      if (fila && fila.erro) carregarFila();
      return;
    }
    if (ehMensal && avisoMensalComAulas()) { fNovo.barrar('mensal_com_aulas'); setErro(avisoMensalComAulas()); return; }
    // Rede de seguranca para qualquer caminho que pule uma pergunta (proposta do aluno, toque numa
    // linha do resumo, "Por mes" trocado por "Pelo pacote" sem passar por 5 e 6). Sem quantidade o
    // banco inventaria 8 aulas (dias x 4) que o professor nunca viu no resumo.
    for (const p of [2, 4, 5, 6]) {
      if (ehMensal && (p === 5 || p === 6)) continue;
      const v = validarPassoAcordo(p, a, c, { voz: 'professor' });
      if (v) { fNovo.barrar(v.codigo); setVoltarResumo(true); setPasso(p); setErro(v.msg); return; }
    }
    // v142 (item D): pedido do aluno — o professor confirma o PROPRIO WhatsApp (o aluno cadastrou so'
    // o nome dele). Obrigatorio so' quando o cadastro do professor ainda nao tem numero.
    if (pedeWhatsProf && (whatsProfObrigatorio || meuWhats.trim()) && !whatsValidoSM(meuWhats)) {
      fNovo.barrar(meuWhats.trim() ? 'whats_prof_invalido' : 'sem_whats_prof');
      setErroWhats(meuWhats.trim() ? 'Confira o número: DDD + número, ex.: (11) 99999-9999.' : `Digite o seu WhatsApp — ${primeiroAluno || primeiroNomeSM(nome, 'seu aluno')} usa para falar com você.`);
      // so' o aviso do proprio campo (a janela rola ate' ele) — dois avisos vermelhos juntos era repeticao
      setTimeout(() => { if (folhaRef.current) folhaRef.current.scrollTop = folhaRef.current.scrollHeight; }, 60);
      return;
    }
    // Mensal do pedido em que o aluno nao sabia se ja' pagou o mes: o professor responde no resumo.
    if (ehMensal && mesPago === null) {
      fNovo.barrar('mes_pago_sem_resposta'); setErro('Me diz se a mensalidade deste mês já foi paga');
      // a pergunta e o aviso ficam no fim do resumo: rola ate' eles
      setTimeout(() => { if (folhaRef.current) folhaRef.current.scrollTop = folhaRef.current.scrollHeight; }, 60);
      return;
    }
    criandoRef.current = true;
    setSalvando(true); setErro('');

    // Seguranca extra: se este aluno ja' tem contrato ativo identico criado agora ha' pouco,
    // e' a mesma acao chegando duas vezes. Nao cria de novo.
    const { data: recente } = await supabase.from('contratos')
      .select('id, created_at, alunos!inner(nome)')
      .eq('professor_id', professor.id).eq('status', 'ativo')
      .ilike('alunos.nome', nome.trim())
      .gte('created_at', new Date(Date.now() - 60000).toISOString()).limit(1);
    if (recente && recente.length) {
      criandoRef.current = false; setSalvando(false);
      fNovo.barrar('criado_agora');
      setErro('Esse aluno acabou de ser criado. Confira a lista antes de criar de novo.');
      return;
    }

    // Regra respondida vira padrao do professor — nunca mais perguntamos.
    // "Decido depois" nao grava nada: da' proxima vez a pergunta volta.
    if (precisaRegras) {
      const patch = {};
      if (cancelResp && cancelResp !== 'depois') {
        patch.padrao_antecedencia_horas = regrasFinais.antecedencia;
        patch.padrao_reposicao = regrasFinais.reposicao;
      }
      if (reajResp && reajResp !== 'depois') patch.padrao_reajuste_pct = regrasFinais.reajuste;
      if (Object.keys(patch).length) {
        await supabase.from('professores').update(patch).eq('id', professor.id);
        if (patch.padrao_antecedencia_horas != null && patch.padrao_reajuste_pct != null) setPrecisaRegras(false);
      }
    }

    if (salaMesN > 0 && salaMesNome.trim() && !salaMesJaExiste) {
      const nova = [...custosMensais, { nome: salaMesNome.trim(), valor: salaMesN, desde: mesAtualSM() }];
      if (await salvarCustosMensais(professor.id, nova)) setCustosMensais(nova);
      else toastSM('Não consegui guardar a mensalidade da academia — dá para colocar depois em Configurações.');
    }
    const entradaAcordo = {
      professor_id: professor.id,
      plano_id: planos[0]?.id || null,
      nome: nome.trim(), telefone: tel.trim(),
      // v143: a modalidade que o aluno disse (so' no aluno do pedido; os seguintes sao outras pessoas)
      modalidade: modalidadePedido || (parseMods(professor.modalidades)[0]) || '',
      tipo_cobranca: ehMensal ? 'mensal' : 'pacote',
      valor_mensal: mensalN,
      dia_vencimento: ehMensal ? (Number(diaVenc) || undefined) : undefined,
      mensal_mes_pago: ehMensal ? mesPago === true : undefined,
      valor_aula: valorAulaEfetivo,
      preco_total: totalPacote,
      custo_sala: salaN,
      dias, hora, horarios: porDia ? horarios : null, duracao, pacote: pacoteN,
      antecedencia: regrasFinais.antecedencia, reposicao: regrasFinais.reposicao, reajuste: regrasFinais.reajuste,
      cancelamento_livre: regrasFinais.livre,
      em_curso: feitasN > 0 || pago === true, aulas_feitas: feitasN, pacote_pago: pago === true,
      data_inicio: naFila ? primeiraAulaDesdeSM(dias, inicioFila || fila.inicio) : novoPacote === false && inicioCiclo ? inicioCiclo : undefined,
      depois_do_atual: naFila || undefined,
      vencimento_fila: naFila ? fila.ultima || undefined : undefined,   // v152: vence no dia da ultima aula do atual
      data_pagamento: (pago === true || (ehMensal && mesPago)) && dataPgto ? dataPgto : undefined,
      aluno_id_existente: (alunoExistente || completar)?.id || undefined,
      renovacao: !!alunoExistente || undefined,   // v155: pago aqui e' pagamento do pacote novo, nao "antes do cadastro"
    };
    // v144: pedido do aluno que ja' usa o app — a ficha dele vira a deste professor, com o acordo, numa funcao so'
    // do banco. Sem a funcao (o SQL ainda nao rodou) ou ficha sem conta: o caminho de sempre, logo abaixo.
    let r = null, adotado = null;
    if (pedidoDoAluno && salvos === 0) {
      const ra = await completarPedidoDoAluno(supabase, pedidoCode, entradaAcordo, { ficha_id: fichaJuntar ? fichaJuntar.id : null });
      if (ra.ok) { adotado = ra; r = { ok: true, aluno_id: ra.aluno_id, aulas_criadas: ra.aulas_criadas, problemas: [] }; }
      else if (ra.semFuncao || ra.codigo === 'ficha_sem_conta') uso.acao('novo_aluno.pedido_caminho_antigo', { motivo: ra.semFuncao ? 'sem_funcao' : 'ficha_sem_conta' });
      else {
        setSalvando(false); criandoRef.current = false;
        const cod = ra.codigo || '';
        if (cod) fNovo.barrar(/^[a-z_]{1,40}$/.test(cod) ? 'pedido_' + cod : 'pedido_outro');
        else uso.erro('erro.completar_pedido', ra.erro);
        if (cod === 'dados_invalidos') uso.erro('erro.completar_pedido_dados', ra.motivo || '?');   // o que o banco recusou
        const A1 = primeiroNomeSM(nome, 'Esse aluno');
        const mot = String(ra.motivo || '');
        setErro(({
          ja_e_seu_aluno: `${A1} já é seu aluno, com acordo. Feche e abra a ficha dele — o pacote novo se faz por lá. O pedido saiu da sua lista.`,
          pedido_invalido: 'Este pedido não está mais valendo. Feche e confira a lista de alunos.',
          nao_encontrado: 'Este pedido não está mais valendo. Feche e confira a lista de alunos.',
          sem_permissao: 'Só o professor que aceitou o pedido consegue completar.',
          cancelado: `${A1} cancelou este pedido. Não precisa completar.`,
          ja_cadastrado: `Você já cadastrou ${A1} por este pedido. Feche e confira a lista de alunos.`,
          juntar_invalido: 'Não deu para juntar com essa ficha (ela já tem conta no app ou foi encerrada). Volte ao começo e toque em Continuar.',
          dados_invalidos: /aula/.test(mot) ? 'Alguma aula ficou fora dos dias da agenda ou antes de hoje. Confira o início e os dias no resumo.'
            : /pagamento/.test(mot) ? 'O pagamento não fechou com o valor ou a data. Confira o pagamento no resumo.'
            : /agenda|semana/.test(mot) ? 'Confira os dias e os horários no resumo.'
            : 'Algo no acordo não fechou (dias, horários ou datas). Confira o resumo e tente de novo.',
        })[cod] || (ra.rede === false && ra.erro) || 'Não foi possível criar agora. Confira a internet e tente de novo.');
        return;
      }
    }
    if (!r) r = await criarAlunoComContrato(supabase, entradaAcordo, { modo: 'aplicar' });
    setSalvando(false);
    criandoRef.current = false;
    if (!r.ok) { fNovo.barrar('falha_criar'); uso.erro('erro.novo_aluno', r.erro); setErro(r.erro || 'Não foi possível criar'); return; }
    if (r.problemas && r.problemas.length) toastSM(''+ r.problemas.join('· '));
    setSalvos(n => n + 1);
    onSalvou?.();
    // Completar (item E): o nome que o professor usa vale na ficha (a do aluno veio do cadastro dele);
    // a modalidade, se a ficha nao tem, e' a do professor. O professor da ficha altera tudo.
    if (completar) {
      const patch = {};
      if (nome.trim() && nome.trim() !== (completar.nome || '')) patch.nome = nome.trim();
      if (!completar.modalidade && parseMods(professor.modalidades)[0]) patch.modalidade = parseMods(professor.modalidades)[0];
      if (Object.keys(patch).length) {
        const { error: eF } = await supabase.from('alunos').update(patch).eq('id', completar.id);
        if (eF) uso.erro('erro.completar_ficha', eF.message);
      }
    }
    // Item D: o WhatsApp do professor (pedido do aluno) — so' grava se mudou. O cadastro do professor
    // na memoria do app tambem muda: senao Configuracoes abria com o numero velho (vazio) e, ao salvar
    // o perfil, apagava o que acabou de ser gravado.
    if (pedeWhatsProf && whatsValidoSM(meuWhats) && !mesmoWhatsSM(meuWhats, professor.telefone)) {
      const telNovo = meuWhats.trim();
      const { error: eW } = await supabase.from('professores').update({ telefone: telNovo }).eq('id', professor.id);
      if (eW) { uso.erro('erro.whats_prof', eW.message); toastSM('Não consegui guardar o seu WhatsApp — dá para colocar depois em Configurações.'); }
      else {
        uso.acao('novo_aluno.whats_prof', { tinha: !!digitosWhatsSM(professor.telefone), origem: prop ? 'pedido' : completar ? 'completar' : 'novo', do_aluno: whatsVeioDoAluno });
        if (appCtx && appCtx.setProfessor) appCtx.setProfessor(prev => prev ? { ...prev, telefone: telNovo } : prev);
      }
    }
    // Completar (item E): ele ja' tem o app — o aviso no sininho dele e' o mesmo de quando o professor
    // manda o acordo pela ficha. O acordo so' vale depois que ele conferir e aceitar.
    if (completar) {
      const { error: eN } = await supabase.from('notificacoes').insert({ aluno_id: completar.id, professor_id: professor.id, tipo: 'contrato',
        mensagem: 'Seu acordo está pronto para você conferir e confirmar no app.' });
      if (eN) uso.erro('erro.completar_aviso', eN.message);
    }

    // O convite nasce JUNTO com o aluno, mesmo que o professor nunca toque em "convidar".
    // E' ele que faz o aviso aparecer sozinho no app de quem ja' usa o Plen (funcao
    // convites_para_mim). O botao do WhatsApp so' compartilha um codigo que ja' existe.
    // Na renovacao o aluno ja' existe e ja' tem convite/conta — nao manda outro.
    // v144: pedido adotado — o aluno ja' ve' no app (aviso no celular dele): sem convite de volta.
    let codigoConvite = null;
    if (!alunoExistente && !completar && !adotado) {
      // Seguranca convites (01/10): cadastro aberto pelo PEDIDO de um aluno — grava o codigo do
      // pedido no convite. E' so' assim que o aviso aparece para o aluno que convidou (o banco
      // nao confia mais em nome/telefone). So' no primeiro aluno salvo nesta janela: os
      // seguintes ("cadastrar mais um") sao outras pessoas.
      const pedido = salvos === 0 && proposta?.pedido ? String(proposta.pedido) : null;
      // v142: o codigo fica guardado — o "Mandar convite no WhatsApp" do fim usa este mesmo convite.
      try {
        codigoConvite = await criarConvite({
          tipo: 'prof_aluno', professor_id: professor.id, aluno_id: r.aluno_id,
          dados: { pnome: professor.nome, anome: nome.trim(), aid: r.aluno_id, ...(pedido ? { pedido } : {}) },
        });
      } catch (e) { console.warn('convite automatico:', e?.message); uso.erro('erro.convite_auto', e?.message || e); }
    }

    // v149: pacote na fila — o aluno recebe um aviso (o acordo novo aparece como cartao no Inicio dele, sem travar a tela)
    let filaFeita = null;
    if (naFila) {
      const ini = inicioFila || fila.inicio, prim = primeiraAulaDesdeSM(dias, ini);
      filaFeita = { prim, ultima: fila.ultima, n: fila.abertas.length };
      const { error: eN } = await supabase.from('notificacoes').insert({ aluno_id: alunoExistente.id, professor_id: professor.id, tipo: 'contrato',
        mensagem: `${primeiroNomeSM(professor.nome, 'Seu professor')} combinou seu próximo pacote: ${pacoteN} aulas, começa ${diaEDataSM(prim)}.${pago === true || !fila.ultima ? '' : ` O pagamento vence ${fmtDiaMes(fila.ultima)}, no dia da última aula do pacote atual.`} Confira e confirme no app.` });
      if (eN) uso.erro('erro.fila_aviso', eN.message);
      uso.acao('renovar.fila', { abertas: fila.abertas.length, outra_data: !!inicioFila && inicioFila !== fila.inicio, pago: pago === true });
    }
    setCriado({ id: r.aluno_id, nome: nome.trim(), telefone: tel.trim(), aulas: r.aulas_criadas, codigo: codigoConvite, fila: filaFeita,
                adotado: adotado ? { aceito: !!adotado.aceito, mudancas: Array.isArray(adotado.mudancas) ? adotado.mudancas : [],
                                     ficha: adotado.ficha || '', semProposta: !!(adotado.sem_proposta || (proposta && proposta.so_pedido)) } : null });
    setMandou(false);
    // v155: aula do pedido que caia no mesmo dia e horario de uma que a ficha ja' tinha nao foi marcada
    if (adotado && adotado.aulas_puladas > 0) {
      const np = adotado.aulas_puladas;
      toastSM(`${np} aula${np !== 1 ? 's' : ''} do pedido não ${np !== 1 ? 'foram marcadas' : 'foi marcada'}: já tinha aula nesse dia e horário. Confira na agenda.`);
    }
    fNovo.fim('salvou', { modo, em_curso: feitasN > 0 || pago === true, com_problema: !!(r.problemas && r.problemas.length), proposta: !!proposta, pulados: pulados.length, completar: !!completar,
                          ...(adotado ? { adotou: adotado.aceito ? 'aceito' : 'mudou' } : {}) });   // a peneira da central guarda 8 chaves
    setPasso(PASSO_FIM);
  }

  // Estilos: os do bloco "O COMBINADO" (apelidos para os passos 1, 3 e o resumo nao mudarem).
  const chip = chipAcordo;
  const grade = GRADE_ACORDO;
  const inputCss = INPUT_ACORDO;
  const numCss = NUM_ACORDO;
  const sub = t => <SubAcordo>{t}</SubAcordo>;

  // Horario ja' ocupado por outro aluno: avisa, nao trava (turma e' outra tela; aqui
  // duas pessoas no mesmo horario quase sempre e' engano — achado no teste de 29/09).
  // v143: carrega tambem no resumo — com o passo 4 pulado (veio do aluno), e' ali que o
  // professor ve' o choque de horario.
  const [ocupados, setOcupados] = React.useState([]);
  React.useEffect(() => {
    if (passo !== 4 && passo !== PASSO_RESUMO) return;
    supabase.from('agenda_semanal').select('dia_semana, hora, aluno_id, alunos(nome)')
      .eq('professor_id', professor.id).eq('ativo', true)
      .then(({ data }) => setOcupados((data || []).filter(x => !alunoExistente || x.aluno_id !== alunoExistente.id)));
  }, [passo]);
  const conflitos = dias.map(d => {
    const h = horaDe(d); if (!h) return null;
    const o = ocupados.find(x => Number(x.dia_semana) === d && String(x.hora).slice(0, 5) === h.slice(0, 5));
    return o ? `${DIAS_FULL[d]} ${h} — ${o.alunos?.nome?.split(' ')[0] || 'outro aluno'}` : null;
  }).filter(Boolean);

  // Marca "Veio preenchido por Ana em 20/10 — confira." nas perguntas que o aluno respondeu.
  const primeiroAluno = prop ? (prop.nome || '').split(' ')[0] : '';
  // v143: diz tambem o que o aluno deixou em branco naquela pergunta (o professor completa).
  const emBrancoNoPasso = p => {
    if (!prop) return '';
    const f = [];
    if (p === 2) {
      if (!prop.modo) f.push('a forma de pagar');
      else { if (!(prop.valor > 0)) f.push('o valor'); if (prop.modo !== 'mes' && prop.qtd === null) f.push('a quantidade de aulas'); }
    }
    if (p === 4 && prop.duracao === null) f.push('a duração da aula');
    if (p === 5 && prop.novoPacote === false && prop.feitas === null) f.push('quantas aulas já foram feitas');
    return f.length ? ` Ficou em branco: ${f.join(' e ')} — complete.` : '';
  };
  const marcaAluno = (p, extra) => (prop && salvos === 0 && prop.doAluno[p])
    ? <MarcaDoAluno nome={primeiroAluno} data={fmtDiaMes(prop.enviadoEm)} extra={emBrancoNoPasso(p) + (extra || '')} /> : null;
  // Modalidade do pedido, na grafia da lista do professor ("balé" -> "Balé"). So' no 1o aluno salvo.
  const modalidadePedido = prop && salvos === 0 ? modalidadeDoPedido(prop.modalidade, parseMods(professor.modalidades)) : '';

  let titulo = '', ajuda = '', conteudo = null, rotuloBotao = 'Continuar', semRodape = false;

  // U24 — escolher da agenda do celular. A Contact Picker API abre o seletor nativo e
  // devolve so' o contato escolhido (nome + telefone), sem o app ler a agenda inteira —
  // o mesmo padrao de privacidade do WhatsApp/Nubank. Existe no Chrome Android (e no
  // app instalado). No iPhone/Safari nao existe: o botao simplesmente nao aparece.
  // 29/09: passa por escolherContatos — no app do iPhone (build 6) usa o seletor nativo.
  const temAgenda = temAgendaCelular();
  async function escolherDaAgenda() {
    const r = await escolherContatos({ origem: 'novo_aluno' });
    // Revisao v142: cancelar (ou erro) depois de ja' ter escolhido alguem nao muda nada — o contato de
    // antes continua, com o numero na tela. Escolher outro troca o numero SEMPRE (sem numero = sem numero).
    const jaEscolheu = entrada === 'agenda';
    if (r.erro) { if (!jaEscolheu) setEntrada('digitar'); return; }   // agenda falhou: o campo do nome continua
    const [c] = r;
    if (!c) { if (!jaEscolheu) setEntrada('vazia'); return; }   // fechou sem trazer ninguem: campo + dica (AvisoAgendaVazia)
    if (c.nome) setNome(c.nome);
    setTel(c.tel || '');
    setEntrada('agenda');
  }

  if (passo === 1) {
    titulo = completar ? 'Como você chama este aluno?' : 'Quem é o aluno?';
    // Completar: o nome ja' vem (o do cadastro dele) e o WhatsApp foi ele quem deixou — sem teclado
    // aberto: na maioria das vezes e' so' tocar em Continuar.
    ajuda = completar ? `Entrou pelo seu link${completar.telefone ? ` · WhatsApp ${completar.telefone}` : ''}. Pode trocar pelo apelido que você usa — só você vê.`
      : 'Pode ser o apelido que você usa — só você vê';
    conteudo = (
      <>
        {completar ? (
          <>
            <input ref={nomeRef} value={nome} onChange={e => setNome(e.target.value)} placeholder="Zé da manhã" style={inputCss}
              autoCapitalize="words" onKeyDown={e => { if (e.key === 'Enter') avancar(); }} />
            {semConta && semConta.length > 0 && (() => {
              const P1 = primeiroNomeSM(nome || completar.nome, 'Ele');
              const linhaFicha = x => (
                <button key={x.id} type="button" onClick={() => { uso.acao('novo_aluno.completar_escolheu', { parecido: x.p > 0 }); setErro(''); setJuntarCom(x); }}
                  style={{display: 'block', width: '100%', textAlign: 'left', minHeight: '44px', marginTop: '8px', padding: '9px 12px', borderRadius: '10px',
                          border: '1.5px solid ' + (x.p > 0 ? '#2F6BFF' : '#E2E4E9'), background: 'white', cursor: 'pointer', fontFamily: 'inherit'}}>
                  <div style={{fontSize: '14px', fontWeight: '700', color: '#16181D'}}>É {x.nome}</div>
                  <div style={{fontSize: '12px', color: '#6E7480', marginTop: '1px'}}>
                    {x.telefone ? x.telefone : 'sem WhatsApp'} · {x.acordoPendente ? 'acordo esperando aceite' : x.temAcordo ? 'com acordo' : 'sem acordo'}
                  </div>
                </button>
              );
              return (
                <div data-caixa-juntar="1" style={{marginTop: '14px', border: '1.5px solid #C2D4FF', background: '#F7F9FF', borderRadius: '12px', padding: '12px 14px'}}>
                  {juntarCom ? (
                    <>
                      <div style={{fontSize: '14px', fontWeight: '800', color: '#16181D'}}>Juntar com {juntarCom.nome}?</div>
                      <div style={{fontSize: '12.5px', color: '#3D424C', lineHeight: 1.5, marginTop: '3px'}}>
                        {P1} passa a usar a ficha de {juntarCom.nome}{juntarCom.temAcordo ? ', com o acordo, as aulas e as cobranças que você já tem' : ''}. A ficha que veio do link sai da lista.
                        {juntarCom.acordoPendente ? ' O acordo aparece no app para conferir.' : juntarCom.temAcordo ? '' : ' Depois você combina o pacote em "Completar".'}
                      </div>
                      <div style={{display: 'flex', gap: '8px', marginTop: '10px'}}>
                        <button type="button" disabled={juntando} onClick={() => { setJuntarCom(null); setErro(''); }}
                          style={{flex: 1, minHeight: '44px', borderRadius: '10px', border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'}}>
                          Voltar
                        </button>
                        <button type="button" disabled={juntando} onClick={juntar}
                          style={{flex: 1, minHeight: '44px', borderRadius: '10px', border: 'none', background: '#2F6BFF', color: 'white', fontWeight: '800', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', opacity: juntando ? 0.6 : 1}}>
                          {juntando ? 'Juntando…' : 'Juntar'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{fontSize: '14px', fontWeight: '800', color: '#16181D'}}>É alguém que você já cadastrou?</div>
                      <div style={{fontSize: '12.5px', color: '#3D424C', lineHeight: 1.5, marginTop: '3px'}}>
                        Se {P1} já estava na sua lista, junte as duas fichas: o que você já combinou passa a aparecer no app dele.
                      </div>
                      {candidatosJuntar.parecidos.map(linhaFicha)}
                      {candidatosJuntar.outros.length > 0 && (verTodosSemConta ? (
                        <div style={{maxHeight: '240px', overflowY: 'auto', marginTop: '4px'}}>{candidatosJuntar.outros.map(linhaFicha)}</div>
                      ) : (
                        <button type="button" onClick={() => { uso.acao('novo_aluno.completar_ver_todos'); setVerTodosSemConta(true); }}
                          style={{display: 'block', marginTop: '6px', minHeight: '44px', border: 'none', background: 'none', padding: 0, color: '#2F6BFF', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit'}}>
                          {candidatosJuntar.parecidos.length ? 'Outro nome? ' : ''}Ver quem ainda não usa o app ({candidatosJuntar.outros.length})
                        </button>
                      ))}
                      <div style={{fontSize: '12px', color: '#6E7480', lineHeight: 1.45, marginTop: '8px'}}>É gente nova? Toque em Continuar.</div>
                    </>
                  )}
                </div>
              );
            })()}
          </>
        ) : (
          <CampoSoONome valor={nome} onValor={setNome} placeholder="Zé da manhã" inputRef={nomeRef} onEnter={avancar} estilo={inputCss}
            entrada={entrada} tel={tel} textoAgenda="Veio da agenda. Se quiser, troque pelo apelido que você usa."
            rotuloTel={prop && salvos === 0 && entrada !== 'agenda' ? `WhatsApp que ${primeiroAluno || 'o aluno'} mandou` : 'WhatsApp da agenda'}
            notaTel={pedidoDoAluno && salvos === 0 && entrada !== 'agenda' ? ` — ${primeiroAluno || 'ele'} já usa o app: nada de convite pelo WhatsApp.` : undefined}
            onLimpar={() => { setTel(''); if (entrada === 'agenda') setEntrada('digitar'); }}
            onTirarTel={() => { uso.acao('novo_aluno.tirar_whats', { via: entrada === 'agenda' ? 'agenda' : 'pedido' }); setTel(''); setIgnorarDuplicado(false); if (erro) setErro(''); }}
            temAgenda={temAgenda} onAgenda={() => { uso.acao('novo_aluno.entrada', { via: entrada === 'agenda' ? 'agenda_outro' : 'agenda_depois' }); escolherDaAgenda(); }} />
        )}
        {fichaJuntar && salvos === 0 && (
          <div data-juntar-escolhido="1" style={{marginTop: '12px', background: '#F7F9FF', border: '1px solid #C2D4FF', borderRadius: '12px', padding: '12px 14px', fontSize: '13px', color: '#16181D', lineHeight: 1.5}}>
            Vai juntar com a ficha de <b>{fichaJuntar.nome}</b> que você já tinha — o acordo nasce nela. Toque em Continuar.
            <button type="button" onClick={() => { uso.acao('novo_aluno.pedido_juntar_desfez'); setFichaJuntar(null); setIgnorarDuplicado(false); }}
              style={{display: 'block', marginTop: '6px', minHeight: '44px', border: 'none', background: 'none', padding: 0, color: '#2F6BFF', fontWeight: '700', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit'}}>
              Não é {primeiroNomeSM(fichaJuntar.nome, 'ele')} — desfazer
            </button>
          </div>
        )}
        {jaExiste && !(completar && !jaExiste.user_id && !jaExiste.desvinculado_em) && !(fichaJuntar && fichaJuntar.id === jaExiste.id) && (
          <div style={{marginTop: '12px', background: jaExiste.desvinculado_em ? '#F7F9FF' : '#FFFBEB', border: '1px solid ' + (jaExiste.desvinculado_em ? '#C2D4FF' : '#FDE68A'),
                       borderRadius: '12px', padding: '12px 14px', fontSize: '13px', color: '#16181D', lineHeight: 1.5}}>
            {completar ? (
              // Completar: esta ficha e' a que esta' ligada ao app dele. Revisao v142: a ficha repetida SEM conta
              // aparece no "E' alguem que voce ja' cadastrou?" (logo abaixo), para juntar — aqui so' a que ja' tem
              // outra conta do app ou foi encerrada. Avisa e deixa seguir, sem prometer que "fica tudo junto".
              <><b>{jaExiste.nome}</b> {jaExiste.desvinculado_em ? 'já foi seu aluno' : 'também está na sua lista, com outra conta do app'}{jaExiste.porTelefone ? ' (mesmo WhatsApp)' : ''}. Se for a mesma pessoa, a ficha de {primeiroNomeSM(jaExiste.nome, 'lá')} continua com as aulas e as cobranças dela: o acordo que você criar aqui fica separado.</>
            ) : jaExiste.desvinculado_em ? (
              <>
                <b>{jaExiste.nome}</b> já foi seu aluno{jaExiste.porTelefone ? ' (mesmo WhatsApp)' : ''} — vínculo encerrado em {new Date(jaExiste.desvinculado_em).toLocaleDateString('pt-BR')}.
                Reativar mantém o histórico e cria só o pacote novo.
                {onReativar && (
                  <button type="button" onClick={() => { fNovo.sair('reativou'); onReativar(jaExiste); }}
                    style={{display: 'block', marginTop: '10px', width: '100%', padding: '12px', borderRadius: '12px', border: 'none',
                            background: '#2F6BFF', color: 'white', fontWeight: '800', fontSize: '14px', cursor: 'pointer'}}>
                    <Ico n="user-check" s={15} c="white" /> Reativar {jaExiste.nome.split(' ')[0]}
                  </button>
                )}
              </>
            ) : (
              <>
                <b>{jaExiste.nome}</b> já está na sua lista{jaExiste.porTelefone ? ' com o mesmo WhatsApp' : ''}. Se for outra pessoa{jaExiste.porTelefone ? ' (ex.: irmão)' : ' com o mesmo nome'}, toque em Continuar.
                {/* Fechamento v144 (revisao, achado 3): no pedido de um aluno que ja' usa o app, a ficha SEM conta que voce
                    ja' tinha pode ser dele — juntar (a conta do aluno vai para ela; o acordo nasce nela ou continua o que ela tem) */}
                {pedidoDoAluno && salvos === 0 && !jaExiste.user_id && (() => {
                  const temAcordo = Array.isArray(jaExiste.contratos) && jaExiste.contratos.some(k => k.status === 'ativo');
                  const X1 = primeiroNomeSM(jaExiste.nome, 'ele');
                  return (
                    <>
                      <div style={{marginTop: '8px', fontSize: '12.5px', color: '#3D424C'}}>
                        É {primeiroAluno || X1}? Junte as fichas: {primeiroAluno || X1} passa a usar a ficha que você já tinha{temAcordo ? ', com o acordo, as aulas e as cobranças dela' : ', e o acordo nasce nela'}.
                      </div>
                      <button type="button" disabled={salvando} data-juntar-pedido="1" onClick={() => juntarComFicha({ id: jaExiste.id, nome: jaExiste.nome, temAcordo })}
                        style={{display: 'block', marginTop: '10px', width: '100%', minHeight: '44px', padding: '10px', borderRadius: '12px', border: 'none',
                                background: '#2F6BFF', color: 'white', fontWeight: '800', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', opacity: salvando ? 0.6 : 1}}>
                        {salvando ? 'Juntando…' : `É ${X1} — juntar as fichas`}
                      </button>
                    </>
                  );
                })()}
                {onAbrirFicha && (
                  <button type="button" onClick={() => { uso.acao('novo_aluno.abrir_ficha_repetida', { por_telefone: !!jaExiste.porTelefone, pedido: !!prop }); fNovo.sair('abriu_ficha'); onAbrirFicha(jaExiste); }}
                    style={{display: 'block', marginTop: '10px', width: '100%', minHeight: '44px', padding: '10px', borderRadius: '12px', border: '1.5px solid #FDE68A',
                            background: 'white', color: '#92400E', fontWeight: '800', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'}}>
                    <Ico n="id" s={15} c="#92400E" /> Abrir a ficha de {primeiroNomeSM(jaExiste.nome, 'dele')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        {pedidoParecido && (
          <div data-pedido-parecido="1" style={{marginTop: '12px', background: '#F7F9FF', border: '1.5px solid #C2D4FF', borderRadius: '12px', padding: '12px 14px', fontSize: '13px', color: '#16181D', lineHeight: 1.5}}>
            <b>{primeiroNomeSM(pedidoParecido.aluno_nome, 'Esse aluno')}</b> já mandou o combinado pelo app. Abra o pedido: vem tudo preenchido e {primeiroNomeSM(pedidoParecido.aluno_nome, 'ele')} vê no app na hora, sem mensagem de volta.
            <button type="button" onClick={() => { uso.acao('novo_aluno.abriu_pedido_parecido', { por: pedidoParecido._por }); fNovo.sair('abriu_pedido'); onAbrirPedido(pedidoParecido); }}
              style={{display: 'block', marginTop: '10px', width: '100%', minHeight: '44px', padding: '10px', borderRadius: '12px', border: 'none',
                      background: '#2F6BFF', color: 'white', fontWeight: '800', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'}}>
              Abrir o pedido de {primeiroNomeSM(pedidoParecido.aluno_nome, 'dele')}
            </button>
            <div style={{fontSize: '12px', color: '#6E7480', marginTop: '6px'}}>Se for outra pessoa, toque em Continuar.</div>
          </div>
        )}
      </>
    );
  }

  if (passo === 2) {
    ({ titulo, ajuda } = textosPassoAcordo(2, 'professor', c));
    conteudo = (
      <>
        {marcaAluno(2)}
        <CorpoQuanto voz="professor" a={a} c={c} />
      </>
    );
  }

  if (passo === 3) {
    titulo = 'Paga quadra ou sala pra dar essa aula?'; ajuda = 'Pra calcular quanto sobra pra você — o aluno não vê';
    conteudo = (
      <>
        <div style={grade}>
          <button onClick={() => { setPagaSala(false); setSala(''); setErro(''); seguir(3); }} style={chip(pagaSala === false)}>Não pago</button>
          <button onClick={() => setPagaSala(true)} style={chip(pagaSala === true)}>Pago</button>
        </div>
        {pagaSala === true && (
          <>
            {sub('Como a academia cobra de você?')}
            <div style={grade}>
              <button onClick={() => { if (salaTipo !== 'aula') { setSalaTipo('aula'); setSala(''); } setErro(''); }} style={chip(salaTipo === 'aula')}>Por aula</button>
              <button onClick={() => { if (salaTipo !== 'mes') { setSalaTipo('mes'); setSala(''); } setErro(''); }} style={chip(salaTipo === 'mes')}>Por mês</button>
            </div>
            {salaTipo === 'aula' ? (
              <>
                {sub('Valor por aula (R$)')}
                <input value={sala} onChange={e => setSala(limparDinheiro(e.target.value))} onBlur={() => setSala(v => formatarDinheiro(v))}
                  placeholder="30,00" inputMode="decimal" style={numCss} />
                {num(sala) > 0 && pacoteN > 0 && (
                  <div style={{fontSize: '13px', color: '#6E7480', marginTop: '12px'}}>
                    Seu líquido no pacote: <strong style={{color: '#0A6F4D'}}>R$ {liquido.toLocaleString('pt-BR')}</strong>
                  </div>
                )}
              </>
            ) : (
              <>
                {custosMensais.length > 0 && (
                  <>
                    {sub('Já registradas — toque se for uma delas')}
                    <div style={grade}>
                      {custosMensais.map(c => (
                        <button key={c.nome} onClick={() => { setSalaMesNome(c.nome); setSala(formatarDinheiro(c.valor)); setErro(''); }}
                          style={chip(semAcentoSM(c.nome) === semAcentoSM(salaMesNome))}>{c.nome} · R$ {Number(c.valor).toLocaleString('pt-BR')}</button>
                      ))}
                    </div>
                  </>
                )}
                {sub('Academia')}
                <input value={salaMesNome} onChange={e => setSalaMesNome(e.target.value)} placeholder="Ex.: Smart Fit Pinheiros" style={{...numCss, textAlign: 'left'}} />
                {sub('Mensalidade da academia (R$ por mês)')}
                <input value={sala} onChange={e => setSala(limparDinheiro(e.target.value))} onBlur={() => setSala(v => formatarDinheiro(v))}
                  placeholder="600,00" inputMode="decimal" style={numCss} disabled={salaMesJaExiste} />
                <div style={{fontSize: '13px', color: '#6E7480', marginTop: '12px', lineHeight: 1.5}}>
                  {salaMesJaExiste
                    ? 'Essa academia já está nos seus custos do mês — não conta de novo.'
                    : 'É um custo seu, não do aluno: entra uma vez por mês no seu Financeiro, mesmo que vários alunos treinem lá.'}
                </div>
              </>
            )}
          </>
        )}
      </>
    );
  }

  if (passo === 4) {
    ({ titulo, ajuda } = textosPassoAcordo(4, 'professor', c));
    conteudo = (
      <>
        {marcaAluno(4)}
        <CorpoDias voz="professor" a={a} c={c}
          aviso={conflitos.length > 0 ? <AvisoHorarioOcupado conflitos={conflitos} /> : null}
          onSeparou={() => uso.acao('novo_aluno.horario_por_dia')} />
      </>
    );
  }

  if (passo === 5 && naFila) {
    titulo = 'Quando começa o pacote novo?'; ajuda = 'O pacote atual termina as aulas dele — nenhuma some, nenhuma fica em dobro';
    conteudo = <CorpoFila a={a} nome={nome} />;
  } else if (passo === 5) {
    ({ titulo, ajuda } = textosPassoAcordo(5, 'professor', c));
    // "Ja' fizemos 3" envelhece: o convite vale 30 dias.
    const velha = prop && prop.enviadoEm && prop.enviadoEm < hojeSP() ? ' Se fizeram aulas depois disso, ajuste.' : '';
    // v155: a consulta do pacote atual falhou — nao segue no escuro; aula ja' registrada aparece a' parte
    const regs = alunoExistente && fila && fila.registradas ? fila.registradas : [];
    conteudo = (
      <>
        {marcaAluno(5, velha)}
        {alunoExistente && fila && fila.erro && (
          <div style={{fontSize: '13px', color: '#B91C1C', background: '#FEF2F2', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', lineHeight: 1.5}}>
            Não consegui conferir as aulas que faltam do pacote atual.
            <button type="button" onClick={carregarFila} style={{display: 'block', marginTop: '8px', padding: '8px 12px', borderRadius: '10px', border: '1.5px solid #FCA5A5', background: 'white', color: '#B91C1C', fontWeight: '700', fontSize: '13px', cursor: 'pointer'}}>
              Tentar de novo
            </button>
          </div>
        )}
        {regs.length > 0 && (
          <div style={{fontSize: '13px', color: '#3D424C', background: '#F4F6FA', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', lineHeight: 1.5}}>
            {regs.length === 1
              ? `A aula de ${diaEDataSM(regs[0].data)} já foi registrada e espera ${primeiroNomeSM(nome, 'o aluno')} confirmar. Ela conta no pacote atual.`
              : `${regs.length} aulas já foram registradas e esperam ${primeiroNomeSM(nome, 'o aluno')} confirmar. Elas contam no pacote atual.`}
          </div>
        )}
        <CorpoComecou voz="professor" a={a} c={c} onSeguir={() => seguir(5)} />
      </>
    );
  }

  if (passo === 6) {
    ({ titulo, ajuda } = textosPassoAcordo(6, 'professor', c));
    conteudo = (
      <>
        {marcaAluno(6)}
        <CorpoPagou voz="professor" a={a} c={c} />
      </>
    );
  }

  // "Contrato" e' palavra de cartorio. O que existe aqui e' o combinado entre duas pessoas que
  // se conhecem — e o professor le' melhor assim. Vale em qualquer pergunta cujo proximo passo
  // seja o resumo (P6; ou P3/P4 quando o resto veio do aluno; ou voltando de uma linha).
  if (passo >= 2 && passo <= 8 && destino(passo) === PASSO_RESUMO) rotuloBotao = alunoExistente ? 'Ver resumo' : 'Ver acordo com o aluno';

  if (passo === 7) {
    titulo = 'Se o aluno desmarcar, o que vale?'; ajuda = 'Sua regra, igual pra todos os alunos — pergunto só desta vez';
    const opcoes = [
      ['24', 'Avisou 24h antes  pode repor'],
      ['12', 'Avisou 12h antes  pode repor'],
      ['perde', 'Desmarcou, perdeu a aula'],
      ['depois', 'Decido depois'],
    ];
    conteudo = (
      <>
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          {opcoes.map(([v, l]) => (
            <button key={v} onClick={() => { setCancelResp(v); setErro(''); }}
              style={{...chip(cancelResp === v), textAlign: 'left', width: '100%'}}>{l}</button>
          ))}
        </div>
        {cancelResp === 'depois' && (
          <div style={{fontSize: '12.5px', color: '#6E7480', marginTop: '14px'}}>
            Vale 24h com reposição neste contrato. Volto a perguntar no próximo aluno.
          </div>
        )}
      </>
    );
  }

  if (passo === 8) {
    titulo = 'Quer deixar um reajuste combinado?'; ajuda = 'Fica no acordo que o aluno confirma';
    const opcoes = [['0', 'Sem reajuste'], ['5', '5% ao ano'], ['10', '10% ao ano'], ['outro', 'Outro valor'], ['depois', 'Decido depois']];
    conteudo = (
      <>
        <div style={grade}>
          {opcoes.map(([v, l]) => (
            <button key={v} onClick={() => { setReajResp(v); setErro(''); }} style={chip(reajResp === v)}>{l}</button>
          ))}
        </div>
        {reajResp === 'outro' && (
          <>
            {sub('Percentual ao ano (%)')}
            <input value={reajOutro} onChange={e => setReajOutro(e.target.value.replace(/[^\d.,]/g, ''))}
              inputMode="decimal" style={{...numCss, maxWidth: '120px'}} autoFocus />
          </>
        )}
        {reajResp === 'depois' && (
          <div style={{fontSize: '12.5px', color: '#6E7480', marginTop: '14px'}}>
            O contrato sai sem reajuste previsto. Volto a perguntar no próximo aluno.
          </div>
        )}
      </>
    );
    rotuloBotao = 'Ver acordo';
  }

  if (passo === PASSO_RESUMO) {
    titulo = 'O acordo com o aluno';
    ajuda = pedidoDoAluno && salvos === 0 ? `Confira — ${primeiroAluno || 'seu aluno'} vê no app assim que você criar` : 'Confira — é isto que ele vai receber pra aceitar';
    const extras = [];
    if (salaN > 0) extras.push({ k: 'Seu líquido', v: 'R$ ' + liquido.toLocaleString('pt-BR') + ' (quadra R$ ' + salaN.toLocaleString('pt-BR') + '/aula)', passo: 3 });
    if (salaMesN > 0) extras.push({ k: 'Academia', v: salaMesNome.trim() + ' · R$ ' + salaMesN.toLocaleString('pt-BR') + '/mês' + (salaMesJaExiste ? ' (já registrada)' : ' — entra uma vez no Financeiro'), passo: 3 });
    // fecho v148: o acordo novo segue a regra da chuva? Automático pela modalidade que a ficha vai ter (a do pedido ou a
    // 1a do professor; renovar/completar: a da ficha) — a mesma conta do banco (_chuva_livre). O aluno recebe essa linha.
    const modsProfC = parseMods(professor.modalidades);
    const fichaC = alunoExistente || completar;
    const chuvaC = chuvaPelaModalidade(fichaC ? (fichaC.modalidade || (completar ? modsProfC[0] : '') || '') : (modalidadePedido || modsProfC[0] || ''), modsProfC);
    const linhas = linhasDoAcordo(a, c, { voz: 'professor', titulo: nome.trim() || 'Aluno', extras,
                                          modalidade: modalidadePedido, detalhar: !!prop && salvos === 0, chuva: chuvaC });
    // "•" = veio inteiro do aluno e o professor ainda nao abriu. Toque numa linha: volta aquela pergunta.
    const marcados = salvos === 0 ? pulados.filter(x => !vistos.includes(x)) : [];
    const temMarca = !!prop && linhas.some(l => l.passo != null && marcados.includes(l.passo));
    // Mensal: vencimento e "mes pago" ficam no bloco de baixo (sem linha que se toca, sem "•").
    // Se vieram do aluno — ou ele nao sabia —, avisa em cima do bloco (R2: "ja' paga" e' palavra dele).
    let marcaMensal = null;
    if (prop && salvos === 0 && prop.modo === 'mes') {
      const semDia = !prop.diaVenc, semPago = prop.mesPago === null;
      const quem = primeiroAluno || 'Seu aluno';
      marcaMensal = (
        <div style={{marginTop: '14px'}}>
          <MarcaDoAluno nome={primeiroAluno} data={fmtDiaMes(prop.enviadoEm)}
            texto={semDia && semPago ? `${quem} deixou em branco o dia do vencimento (deixei o de hoje) e se a mensalidade deste mês já foi paga — escolha abaixo.` : ''}
            extra={semDia ? ' Ficou em branco: o dia do vencimento (deixei o de hoje) — confira abaixo.'
              : semPago ? ' Ficou em branco: se a mensalidade deste mês já foi paga — escolha abaixo.' : ''} />
        </div>
      );
    }

    conteudo = (
      <>
        <TabelaAcordo linhas={linhas} marcados={marcados}
          onTocar={l => { uso.acao('novo_aluno.mudar_do_resumo', { passo: l.passo }); setErro(''); setVoltarResumo(true); setPasso(l.passo); }} />
        {temMarca && (
          <div style={{fontSize: '12px', color: '#6E7480', marginTop: '8px', lineHeight: 1.45}}>
            {`As linhas com • vieram de ${primeiroAluno || 'seu aluno'}${prop.enviadoEm ? ' em ' + fmtDiaMes(prop.enviadoEm) : ''}. Toque numa linha para mudar.`}
          </div>
        )}
        {/* Com o passo 4 pulado (veio do aluno), e' aqui que o professor ve' o choque de horario. */}
        {conflitos.length > 0 && pulados.includes(4) && <AvisoHorarioOcupado conflitos={conflitos} />}
        {ehMensal && marcaMensal}
        {ehMensal && <BlocoMensalResumo voz="professor" a={a} />}
        {/* Cancelamento e reajuste nao sao mais texto morto. O app vinha AFIRMANDO no
            acordo do aluno duas regras que ninguem combinou — os padroes que ele assumiu
            sozinho. Aqui o professor le' a frase que o aluno vai receber e corrige na
            hora, ou deixa explicitamente para combinar depois. E' o momento em que a
            resposta e' obvia para ele, diferente de perguntar em abstrato no comeco. */}
        {/* v144: "Combinaram prazo para desmarcar?" — Nao (livre, o padrao) / Sim (avisar com X horas). */}
        <PerguntaPrazoDesmarcar valor={prazoH} padrao={regras.antecedencia}
          onValor={v => { uso.acao('novo_aluno.prazo_desmarcar', { h: v }); setPrazoH(v); }} />
        <div style={{marginTop: '14px'}}>
          <div style={{fontSize: '12px', fontWeight: '700', color: '#9AA0AB', marginBottom: '7px'}}>
            REAJUSTE ANUAL
          </div>
          <div style={{display: 'flex', gap: '7px', flexWrap: 'wrap'}}>
            {[['0', 'Não previsto'], ['5', '5% ao ano'], ['10', '10% ao ano'], ['outro', 'Outro']].map(([v, l]) => (
              <button key={v} onClick={() => setReajResp(v)}
                style={{...chip((reajResp || '0') === v), fontSize: '12.5px', padding: '9px 13px'}}>{l}</button>
            ))}
          </div>
          {reajResp === 'outro' && (
            <input value={reajOutro} onChange={e => setReajOutro(e.target.value.replace(/[^\d.,]/g, ''))}
              inputMode="decimal" placeholder="%" style={{...numCss, maxWidth: '110px', marginTop: '10px'}} />
          )}
        </div>
        <div style={{fontSize: '11.5px', color: '#9AA0AB', marginTop: '12px'}}>
          Valem só para este aluno. Dá para mudar depois na ficha dele.
        </div>
        {/* v144 (item d): o WhatsApp que o aluno mandou no pedido — antes viajava escondido (o passo 1 e' pulado).
            O professor confere e pode trocar. Vazio = fica o que a ficha do aluno ja' tem. */}
        {prop && salvos === 0 && (
          <div style={{marginTop: '14px'}}>
            <div style={{fontSize: '12px', fontWeight: '700', color: '#9AA0AB', marginBottom: '7px'}}>
              WHATSAPP DE {(primeiroAluno || 'seu aluno').toUpperCase()}
            </div>
            <input value={tel} onChange={e => setTel(mascaraTel(e.target.value))} placeholder="(11) 99999-9999"
              inputMode="tel" aria-label="WhatsApp do aluno" style={{...inputCss, padding: '12px 14px'}} />
            <div style={{fontSize: '11.5px', color: '#9AA0AB', marginTop: '6px'}}>
              {digitosWhatsSM(prop.tel) ? `Veio de ${primeiroAluno || 'seu aluno'} — confira.` : `${primeiroAluno || 'Seu aluno'} não mandou o número — pode deixar em branco.`}
            </div>
          </div>
        )}
        {pedeWhatsProf && (
          <div style={{borderTop: '1px solid #F0F1F4', marginTop: '16px', paddingTop: '2px'}}>
            <CampoSeuWhats valor={meuWhats} onValor={v => { setMeuWhats(v); setErroWhats(''); if (erro) setErro(''); }}
              obrigatorio={whatsProfObrigatorio && !whatsVeioDoAluno} quem={primeiroAluno || primeiroNomeSM(nome, 'Seu aluno')} erro={erroWhats}
              dica={whatsVeioDoAluno ? `${primeiroAluno || 'Seu aluno'} anotou este número da agenda — confira. É por ele que vocês se falam.` : undefined} />
          </div>
        )}
      </>
    );
    rotuloBotao = salvando ? 'Criando…' : (alunoExistente ? (naFila ? 'Combinar próximo pacote' : 'Renovar pacote') : completar || (pedidoDoAluno && salvos === 0) ? 'Criar o acordo' : 'Criar e convidar');
  }

  if (passo === PASSO_FIM) {
    const u = criado || {};
    // Mensal renova mes a mes: mostrar a janela de 90 dias da agenda ("26 aulas") confunde.
    const aulasDoMes = (() => {
      if (!ehMensal) return null;
      const hoje = new Date(); const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      let n = 0; for (let d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()); d <= fim; d.setDate(d.getDate() + 1)) if (dias.includes(d.getDay())) n++;
      return n;
    })();
    const txtAulas = ehMensal
      ? `${aulasDoMes} aula${aulasDoMes !== 1 ? 's' : ''} até o fim de ${new Date().toLocaleDateString('pt-BR', { month: 'long' })}`
      : `${u.aulas || 0} aulas na agenda`;
    const txtSub = ehMensal ? descreverDias() + ' · a agenda se renova todo mês' : descreverDias();
    if (alunoExistente && u.fila) {
      // v149: na fila — o atual segue; o novo comeca sozinho depois da ultima aula dele
      const n1 = primeiroNomeSM(u.nome, 'O aluno');
      titulo = 'Próximo pacote combinado!';
      ajuda = `O pacote atual de ${n1} termina ${diaEDataSM(u.fila.ultima)}. O novo começa ${diaEDataSM(u.fila.prim)}, sozinho.`;
      conteudo = (
        <div style={{background: '#DCF5EC', border: '1.5px solid #8FDFC4', borderRadius: '12px', padding: '14px'}}>
          <div style={{fontSize: '14px', fontWeight: '800', color: '#0A6F4D'}}><Ico n="circle-check" c="#0E9F6E" /> {u.aulas || 0} aulas a partir de {diaEDataSM(u.fila.prim)}</div>
          <div style={{fontSize: '13px', color: '#0C8A5F', marginTop: '4px'}}>{txtSub}</div>
          <div style={{fontSize: '13px', color: '#0C8A5F', marginTop: '4px'}}>{pago === true ? 'Pacote já pago' : `Cobrança de ${brlAcordo(totalPacote)} vence em ${fmtDiaMes(u.fila.ultima || u.fila.prim)}, no dia da última aula do pacote atual`}</div>
          <div style={{fontSize: '12.5px', color: '#3D424C', marginTop: '10px'}}>{alunoExistente.user_id ? `${n1} recebe o aviso no app para conferir e confirmar.` : `${n1} ainda não usa o app — avise pelo WhatsApp.`}</div>
        </div>
      );
      rotuloBotao = 'Fechar';
    } else if (alunoExistente) {
      // Renovacao: o aluno ja' existe, ja' tem conta e ja' sabe o combinado — nao faz
      // sentido mandar de novo a mensagem de "primeiro acordo" pelo WhatsApp.
      titulo = 'Pacote renovado!'; ajuda = 'O pacote novo de ' + (u.nome || 'o aluno') + ' já está valendo';
      conteudo = (
        <div style={{background: '#DCF5EC', border: '1.5px solid #8FDFC4', borderRadius: '12px', padding: '14px'}}>
          <div style={{fontSize: '14px', fontWeight: '800', color: '#0A6F4D'}}><Ico n="circle-check" c="#0E9F6E" /> {txtAulas}</div>
          <div style={{fontSize: '13px', color: '#0C8A5F', marginTop: '4px'}}>{txtSub}</div>
        </div>
      );
      rotuloBotao = 'Fechar';
    } else if (completar) {
      // Item E: ele ja' tem conta — o acordo aparece no app dele para conferir. O WhatsApp e' so' um aviso.
      const primeiro = (u.nome || 'ele').split(' ')[0];
      const textoAviso = `Oi, ${primeiro}! Completei o nosso combinado no Plen. Abre o app e confere: ${window.location.origin}`;
      titulo = 'Pronto!'; ajuda = `${primeiro} recebe o acordo no app para conferir e aceitar`;
      conteudo = (
        <>
          <div style={{background: '#DCF5EC', border: '1.5px solid #8FDFC4', borderRadius: '12px', padding: '14px'}}>
            <div style={{fontSize: '14px', fontWeight: '800', color: '#0A6F4D'}}><Ico n="circle-check" c="#0E9F6E" /> {txtAulas}</div>
            <div style={{fontSize: '13px', color: '#0C8A5F', marginTop: '4px'}}>{txtSub}</div>
          </div>
          <a href={linkWhatsConviteSM(u.telefone, textoAviso)} target="_blank" rel="noopener noreferrer"
            onClick={() => { uso.acao('novo_aluno.completar_avisar', { com_numero: whatsValidoSM(u.telefone) }); uso.acao('whatsapp.abrir', { motivo: 'completar', com_numero: whatsValidoSM(u.telefone), via: 'link' }); }}
            style={{display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '14px', width: '100%', padding: '14px', borderRadius: '13px',
                    border: '1.5px solid #B7E9D6', background: 'white', color: '#0A6F4D', fontWeight: '800', fontSize: '14px', boxSizing: 'border-box'}}>
            <Ico n="message-circle" /> Avisar {primeiro} no WhatsApp
          </a>
        </>
      );
      rotuloBotao = 'Fechar';
    } else if (u.adotado) {
      // v144: pedido do aluno que ja' usa o app — nada de convite de volta. Ele ve' no app (e no celular, se deixou avisos).
      const A1 = primeiroNomeSM(u.nome, 'Seu aluno');
      const muds = u.adotado.mudancas;
      const soJuntou = u.adotado.ficha === 'juntou_com_acordo';   // juntou com a ficha que ja' tinha acordo: nada novo
      titulo = 'Pronto!'; ajuda = `${A1} já vê no app.`;
      conteudo = (
        <>
          {!soJuntou && (
            <div style={{background: '#DCF5EC', border: '1.5px solid #8FDFC4', borderRadius: '12px', padding: '14px'}}>
              <div style={{fontSize: '14px', fontWeight: '800', color: '#0A6F4D'}}><Ico n="circle-check" c="#0E9F6E" /> {txtAulas}</div>
              <div style={{fontSize: '13px', color: '#0C8A5F', marginTop: '4px'}}>{txtSub}</div>
            </div>
          )}
          <div data-pedido-fim={soJuntou ? 'juntou' : u.adotado.aceito ? 'aceito' : 'pendente'} style={{marginTop: soJuntou ? 0 : '12px', background: 'white', border: '1.5px solid #E2E4E9', borderRadius: '12px', padding: '12px 14px', fontSize: '13.5px', color: '#3D424C', lineHeight: 1.5}}>
            {soJuntou
              ? <><strong>Fichas juntas.</strong> {A1} passa a usar a ficha que você já tinha, com o acordo, as aulas e as cobranças dela.{u.adotado.aceito ? '' : ` O acordo aparece no app de ${A1} para conferir.`}</>
              : u.adotado.aceito
              ? <><strong>Acordo confirmado.</strong> É o mesmo combinado que {A1} mandou — já vale para os dois.</>
              : u.adotado.semProposta
              ? <><strong>{A1} confere o acordo</strong> e confirma no app. Até lá, o acordo fica pendente na sua lista.</>
              : <><strong>{A1} confere o que você mudou</strong>{muds.length ? ` (${muds.map(m => String(m.r || '').toLowerCase()).filter(Boolean).join(', ')})` : ''} e confirma no app. Até lá, o acordo fica pendente na sua lista.</>}
          </div>
          <div style={{fontSize: '12px', color: '#6E7480', marginTop: '9px', lineHeight: 1.5}}>
            Não precisa mandar nada pelo WhatsApp: o aviso chega no app de {A1}.
          </div>
        </>
      );
      rotuloBotao = 'Fechar';
    } else {
      titulo = 'Pronto!'; ajuda = (u.nome || 'O aluno') + ' já está na sua lista';
      // v142 (item C): o convite vai DAQUI para o WhatsApp — com o numero (veio da agenda ou do pedido),
      // direto para a pessoa; sem numero, o WhatsApp abre para escolher a pessoa la'. O convite e' o
      // que nasceu junto com o aluno (codigo guardado no criar()).
      const urlConv = u.codigo ? `${window.location.origin}${window.location.pathname}?c=${u.codigo}` : '';
      const textoConv = urlConv ? textoWhatsConviteAluno({ nomeAluno: u.nome, resumo: resumoLocalDoAcordo(), url: urlConv,
        proposta: (() => { try { return propostaDoAcordo(a, c, { modalidade: modalidadePedido || parseMods(professor.modalidades)[0] || '' }); } catch { return null; } })() }) : '';
      const comNumero = whatsValidoSM(u.telefone);
      const marcarEnviado = () => { if (u.id) supabase.from('alunos').update({ convite_enviado_em: new Date().toISOString() }).eq('id', u.id).then(() => onSalvou?.(), () => {}); };
      conteudo = (
        <>
          <div style={{background: '#DCF5EC', border: '1.5px solid #8FDFC4', borderRadius: '12px', padding: '14px'}}>
            <div style={{fontSize: '14px', fontWeight: '800', color: '#0A6F4D'}}><Ico n="circle-check" c="#0E9F6E" /> {txtAulas}</div>
            <div style={{fontSize: '13px', color: '#0C8A5F', marginTop: '4px'}}>{txtSub}</div>
          </div>

          {/* O contrato so' vale quando o aluno aceita — entao mandar agora e' parte do trabalho,
              nao um extra. Botao cheio e a saida discreta logo abaixo (igual ao "Convite pronto" do aluno). */}
          {urlConv ? (
            <a href={linkWhatsConviteSM(u.telefone, textoConv)} target="_blank" rel="noopener noreferrer"
              onClick={() => { uso.acao('novo_aluno.mandar_acordo', { com_numero: comNumero, via: 'fim' }); uso.acao('whatsapp.abrir', { motivo: 'convite', com_numero: comNumero, via: 'link' }); marcarEnviado(); setMandou(true); }}
              style={{display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '14px', width: '100%', padding: '15px', borderRadius: '13px',
                      background: 'linear-gradient(135deg,#25D366,#128C7E)', color: 'white', fontWeight: '800', fontSize: '15px', boxSizing: 'border-box'}}>
              <Ico n="message-circle" c="white" /> Mandar convite no WhatsApp
            </a>
          ) : (
            <button onClick={() => { uso.acao('novo_aluno.mandar_acordo', { com_numero: comNumero, via: 'janela' }); onClose(); if (u.id) onConvidar?.(u); }}
              style={{marginTop: '14px', width: '100%', padding: '15px', borderRadius: '13px', border: 'none',
                      background: 'linear-gradient(135deg,#25D366,#128C7E)', color: 'white', fontWeight: '800',
                      fontSize: '15px', cursor: 'pointer'}}>
              <Ico n="message-circle" /> Mandar convite no WhatsApp
            </button>
          )}
          {urlConv && (
            <button onClick={() => { uso.acao('novo_aluno.copiar_convite'); marcarEnviado(); copiarTextoSM(urlConv, 'Link copiado.', 'Não consegui copiar. Toque em "Mandar convite no WhatsApp".'); }}
              style={{width: '100%', marginTop: '10px', padding: '13px', borderRadius: '12px', border: '1.5px solid #E2E4E9', background: 'white', color: '#3D424C', fontWeight: '700', fontSize: '14px', cursor: 'pointer'}}>
              Copiar o link
            </button>
          )}
          <div style={{fontSize: '12px', color: '#6E7480', marginTop: '9px', lineHeight: 1.5}}>
            {mandou ? '✓ Convite aberto no WhatsApp. ' : comNumero ? 'Vai uma mensagem pronta para o WhatsApp dele, com os dias, o pacote e o link. ' : 'O WhatsApp abre com a mensagem pronta — é só escolher ' + ((u.nome || 'ele').split(' ')[0]) + ' lá. '}
            Enquanto ele não aceitar, o acordo fica como <strong>pendente</strong> na sua lista.
          </div>
        </>
      );
      rotuloBotao = mandou ? 'Cadastrar outro aluno' : 'Deixar pra depois — cadastrar outro';
    }
  }

  // Fechar no meio do cadastro joga tudo fora — entao pergunta, a nao ser que esteja na
  // v149: ja' existe um pacote na fila — nao abre outro (seria um terceiro pacote). Mostra o que ja' esta' combinado.
  if (alunoExistente && fila && fila.proximo && passo !== PASSO_FIM) {
    titulo = 'O próximo pacote já está combinado';
    ajuda = `${primeiroNomeSM(alunoExistente.nome, 'O aluno')} já tem o próximo pacote: começa ${diaEDataSM(fila.proximo.data_inicio)}, quando o atual termina.`;
    conteudo = (
      <>
        <button type="button" onClick={onClose} style={{width: '100%', padding: '14px', borderRadius: '12px', border: 'none', marginTop: '6px',
          background: 'linear-gradient(135deg,#2F6BFF,#2455D6)', color: 'white', fontWeight: '800', fontSize: '15px', cursor: 'pointer'}}>Fechar</button>
        <button type="button" disabled={salvando} onClick={desfazerFila} style={{width: '100%', padding: '12px', borderRadius: '12px', marginTop: '10px',
          border: '1.5px solid #FCA5A5', background: 'white', color: '#B91C1C', fontWeight: '700', fontSize: '14px', cursor: 'pointer'}}>
          {salvando ? 'Desfazendo…' : 'Desfazer o próximo pacote'}
        </button>
        <div style={{fontSize: '12px', color: '#9AA0AB', marginTop: '8px', lineHeight: 1.5}}>
          Desfazer tira as aulas dele da agenda e cancela a cobrança. Só dá enquanto o pagamento não foi feito nem avisado. Depois, é só combinar de novo.
        </div>
      </>
    );
    semRodape = true;
  }

  // v149: desfaz o pacote da fila (errou o valor/dias) — so' sem pagamento feito nem avisado.
  async function desfazerFila() {
    const k = fila && fila.proximo;
    if (!k || salvando) return;
    if (!window.confirm('Desfazer o próximo pacote? As aulas dele saem da agenda e a cobrança é cancelada.')) return;
    setSalvando(true);
    // v155: o robo pode ter virado o pacote depois que esta tela abriu. Ja' virou (ativo): nao apaga nada.
    const jaComecou = () => { setSalvando(false); uso.acao('renovar.fila_desfazer_ja_virou'); toastSM('Esse pacote já começou — as aulas dele agora são as do pacote atual. Nada foi desfeito.'); onSalvou?.(); onClose(); };
    const { data: agora, error: eS } = await supabase.from('contratos').select('status').eq('id', k.id).maybeSingle();
    if (eS) { setSalvando(false); uso.erro('erro.fila_desfazer', eS.message); toastSM('Não consegui conferir o pacote agora. Confira a internet e tente de novo.'); return; }
    if (!agora || agora.status !== 'proximo') return jaComecou();
    // v155: sem conferir a cobranca (internet ruim), nao desfaz nada
    const { data: pgs, error: eP } = await supabase.from('pagamentos').select('id, status, declarado_pago_em').eq('contrato_id', k.id);
    if (eP) { setSalvando(false); uso.erro('erro.fila_desfazer', eP.message); toastSM('Não consegui conferir a cobrança agora. Confira a internet e tente de novo.'); return; }
    // v155 (teste de 29/10): cobranca CANCELADA nao segura (era beco: a tela mandava cancelar no Financeiro e, cancelada,
    // o "Ja' paguei" antigo dela continuava travando o Desfazer)
    const pagoOuAvisado = x => x.status !== 'cancelado' && (x.status === 'pago' || !!x.declarado_pago_em);
    if ((pgs || []).some(pagoOuAvisado)) {
      setSalvando(false); toastSM('Esse pacote já foi pago (ou o aluno avisou que pagou). Para mudar, fale com o aluno e cancele a cobrança no Financeiro primeiro.'); return;
    }
    // v155: pacote da fila com aula ja' dada ou registrada nao se desfaz (a aula ficaria presa num acordo encerrado)
    const { data: dadas, error: eD } = await supabase.from('aulas').select('id').eq('contrato_id', k.id)
      .or('status.in.(realizada,falta),desfecho_prof.not.is.null,divergencia_em.not.is.null').limit(1);
    if (eD) { setSalvando(false); uso.erro('erro.fila_desfazer', eD.message); toastSM('Não consegui conferir as aulas agora. Confira a internet e tente de novo.'); return; }
    if ((dadas || []).length > 0) {
      setSalvando(false); uso.bloqueio('fila_desfazer', null, null, 'aula_dada');
      toastSM('Esse pacote já tem aula dada. Não dá para desfazer: ajuste pela ficha do aluno.'); return;
    }
    // Encerra primeiro, so' se ainda estiver na fila (a virada nao pega mais um pacote encerrado). Nenhuma linha: ja' virou.
    const { data: enc, error: eC } = await supabase.from('contratos').update({ status: 'encerrado' }).eq('id', k.id).eq('status', 'proximo').select('id');
    if (eC) { setSalvando(false); uso.erro('erro.fila_desfazer', eC.message); toastSM('Não consegui desfazer agora: ' + eC.message); return; }
    if (!enc || !enc.length) return jaComecou();
    // v155: o aluno pode ter tocado em "Ja' paguei" entre a conferencia e agora — confere de novo antes de cancelar
    const { data: pgs2, error: eP2 } = await supabase.from('pagamentos').select('id, status, declarado_pago_em').eq('contrato_id', k.id);
    if (eP2 || (pgs2 || []).some(pagoOuAvisado)) {
      await supabase.from('contratos').update({ status: 'proximo' }).eq('id', k.id).eq('status', 'encerrado');
      setSalvando(false);
      toastSM(eP2 ? 'Não consegui conferir a cobrança agora. Nada foi desfeito: tente de novo.' : 'O aluno acabou de avisar que pagou esse pacote. Nada foi desfeito.');
      onSalvou?.(); onClose(); return;
    }
    for (const x of (pgs2 || []).filter(x => x.status === 'pendente')) {
      const res = await rpcPagamento('cancelar_pagamento', { p_id: x.id, p_motivo: 'combinado_outro_jeito' });
      if (!res.ok) { setSalvando(false); uso.erro('erro.fila_desfazer_pag', res.tecnico || res.codigo); toastSM('O pacote foi desfeito, mas a cobrança dele ficou em aberto. Cancele no Financeiro.'); onSalvou?.(); onClose(); return; }
    }
    const { error: eA } = await supabase.from('aulas').delete().eq('contrato_id', k.id).eq('status', 'agendada');
    setSalvando(false);
    if (eA) { uso.erro('erro.fila_desfazer_aulas', eA.message); toastSM('O pacote foi desfeito, mas as aulas dele ficaram na agenda. Tente desmarcar pela agenda.'); onSalvou?.(); onClose(); return; }
    uso.acao('renovar.fila_desfazer');
    toastSM('Próximo pacote desfeito.');
    onSalvou?.();
    onClose();
  }

  // tela final (ja' salvou) ou ainda na primeira pergunta sem nada digitado.
  function fecharPeloUsuario() {
    // Completar: o nome (e o WhatsApp) ja' vieram da ficha — fechar no passo 1 sem mexer nao perde nada.
    const soOQueVeio = !!completar && passo === 1 && nome.trim() === String(completar.nome || '').trim();
    const temAlgo = passo !== PASSO_FIM && !soOQueVeio && (nome.trim() || tel || passo > 1);
    if (temAlgo && !window.confirm('Sair sem salvar? O cadastro será perdido.')) return;
    onClose();
  }

  return (
    <Portal>
    <div className="plen-modal-wrap" style={{position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px'}} onClick={fecharPeloUsuario}>
      {/* Sem padding embaixo quando ha' rodape preso: o navegador desconta o padding da janela do
          "sticky" e ~20px do conteudo apareciam rolando por baixo do botao (igual ao do aluno). */}
      <div ref={folhaRef} className="plen-modal" style={{position: 'relative', background: 'white', borderRadius: '20px', width: '100%', maxWidth: '440px',
                   maxHeight: '88dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: semRodape ? '20px' : '20px 20px 0', display: 'block'}}
        onClick={e => e.stopPropagation()}>

        <button onClick={fecharPeloUsuario} aria-label="Fechar"
          style={{position: 'absolute', top: '14px', right: '16px', width: '32px', height: '32px', borderRadius: '50%',
                  border: 'none', background: '#F0F1F4', color: '#6E7480', fontSize: '17px', cursor: 'pointer', lineHeight: 1}}><Ico n="x" /></button>

        <div style={{fontSize: '17px', fontWeight: '800', color: '#16181D', marginBottom: '12px', paddingRight: '40px'}}>
          {alunoExistente ? `Novo pacote — ${alunoExistente.nome}` : completar ? `Completar — ${completar.nome || 'aluno'}` : 'Novo aluno' + (salvos > 0 ? ' · ' + salvos + ' criado' + (salvos > 1 ? 's' : '') : '')}
        </div>

        <div style={{display: 'flex', gap: '5px', marginBottom: '18px'}}>
          {Array.from({length: TOTAL}, (_, k) => k + 1).map(i => (
            <div key={i} style={{height: '4px', flex: 1, borderRadius: '2px', background: i <= Math.max(numPergunta, passo >= PASSO_RESUMO ? TOTAL : 0) ? '#2F6BFF' : '#E2E4E9'}} />
          ))}
        </div>

        <div style={{fontSize: '12px', color: '#9AA0AB', marginBottom: '4px'}}>
          {passo === PASSO_RESUMO ? 'Confira antes de criar'
            : passo === PASSO_FIM ? 'Tudo pronto'
            : 'Pergunta ' + numPergunta + ' de ' + TOTAL}
        </div>
        <div style={{fontSize: '19px', fontWeight: '800', color: '#16181D', marginBottom: '4px', lineHeight: 1.3}}>{titulo}</div>
        <div style={{fontSize: '13px', color: '#6E7480', margin: '0 0 16px'}}>{ajuda}</div>

        {conteudo}

        {erro && <div style={{fontSize: '13px', color: '#DC2626', marginTop: '10px', fontWeight: '600'}}>{erro}</div>}

        {/* Rodape preso embaixo da janela: o botao de seguir/criar nunca fica escondido
            quando a pergunta e' comprida (Marcus, 29/09). */}
        {!semRodape && <div style={{display: 'flex', gap: '8px', position: 'sticky', bottom: 0, background: 'white',
                     padding: '12px 20px 20px', margin: '22px -20px 0', borderTop: '1px solid #F0F1F4'}}>
          {passo > 1 && passo < PASSO_FIM && (
            <button onClick={() => {
                setErro('');
                // Veio de uma linha do resumo: volta direto para ele.
                if (voltarResumo && passo !== PASSO_RESUMO) { setVoltarResumo(false); setPasso(PASSO_RESUMO); return; }
                setPasso(passoAnterior(passo));
              }}
              style={{padding: '14px 18px', borderRadius: '12px', border: '1.5px solid #E2E4E9', background: 'white',
                      color: '#3D424C', fontWeight: '700', fontSize: '14px', cursor: 'pointer'}}>
              {voltarResumo && passo !== PASSO_RESUMO ? 'Voltar ao resumo' : 'Voltar'}
            </button>
          )}
          <button disabled={salvando} onClick={() => { if (passo === PASSO_RESUMO) criar(); else avancar(); }}
            style={{flex: 1, padding: '14px', borderRadius: '12px', border: 'none',
                    background: 'linear-gradient(135deg,#2F6BFF,#2455D6)', color: 'white', fontWeight: '800',
                    fontSize: '15px', cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.6 : 1}}>
            {rotuloBotao}
          </button>
        </div>}
      </div>
    </div>
    </Portal>
  );
}

// ---- FICHA COMPLETA DO ALUNO ------------------------------------------------------
// Um lugar so' onde o professor corrige QUALQUER coisa: dados, contrato, agenda (inclusive
// um dia extra fora da rotina), historico de aulas passadas e pagamentos (inclusive adiar
// um vencimento quando o aluno pede prazo).
//
// Regra que vale para a ficha inteira: toda alteracao AVISA O ALUNO. Sem isso o professor
// muda o combinado e o aluno descobre pelo susto — que e' o oposto do que o contrato serve.
// v148: "no app: <nome>" — o nome que o aluno usa no app (o do perfil, que ele mesmo escolhe), logo abaixo do
// apelido que o professor deu na ficha. So' ficha ligada a uma conta (nao encerrada) e nome diferente do apelido
// (maiusculas, acentos e espacos nao contam); nunca algo com "@" ou cara de telefone. Os nomes vem do banco
// (professor_nomes_no_app, sql/v148_nome.sql); sem o SQL, nada aparece. O check verde e' o do selo "vinculado":
// na lista, quando esta linha aparece ela faz as vezes do selo (a 375 px o selo + o botao Aula deixavam o
// apelido em "C…" — sem o selo, o apelido cabe inteiro).
function nomeNoAppSM(aluno, nomes) {
  if (!aluno || !aluno.user_id || aluno.desvinculado_em || !nomes) return null;
  const n = String(nomes[aluno.id] || '').replace(/\s+/g, ' ').trim();
  if (!n || n.includes('@') || /\d{6}/.test(n)) return null;
  const chave = t => semAcentoSM(String(t || '').replace(/\s+/g, ' '));
  return chave(n) === chave(aluno.nome) ? null : n;
}
function NomeNoAppSM({ aluno, nomes, style }) {
  const n = nomeNoAppSM(aluno, nomes);
  if (!n) return null;
  return (
    <div title="Aluno com conta no app, vinculado a você — este é o nome que ele usa no app" style={{ fontSize: '12.5px', color: '#6E7480', lineHeight: 1.35, overflowWrap: 'anywhere', ...style }}>
      <Ico n="circle-check" s={11} c="#0E9F6E" style={{ marginRight: '3px' }} />no app: <span style={{ fontWeight: '700', color: '#3D424C' }}>{n}</span>
    </div>
  );
}
function FichaCompleta({ professor, aluno, onFechar, onMudou, nomesNoApp }) {
  useAoDesfazerSM(() => { carregar(); onMudou && onMudou(); });   // v142: desfez (faixa ou item) — a ficha relê
  const [aba, setAba] = React.useState('contrato');
  const [contrato, setContrato] = React.useState(null);
  const [agenda, setAgenda] = React.useState([]);
  const [aulas, setAulas] = React.useState([]);
  const [pagamentos, setPagamentos] = React.useState([]);
  const [dados, setDados] = React.useState({ nome: aluno.nome || '', telefone: aluno.telefone || '' });
  const [carregando, setCarregando] = React.useState(true);
  const [salvando, setSalvando] = React.useState(false);
  const [novoHorario, setNovoHorario] = React.useState({ dia: '', hora: '' });
  const [editandoAula, setEditandoAula] = React.useState(null);
  const [editandoPag, setEditandoPag] = React.useState(null);
  const [cancelarPagFicha, setCancelarPagFicha] = React.useState(null);   // janela "Cancelar lançamento"
  const [verCanceladosFicha, setVerCanceladosFicha] = React.useState(true);
  const [chuvaSel, setChuvaSel] = React.useState(null);   // v148: a regra da chuva escolhida agora (null = a gravada)

  const DIAS = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
  const fmtD = d => d ? new Date(String(d).slice(0,10) + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

  async function carregar() {
    setCarregando(true);
    const [{ data: c }, { data: ag }, { data: au }, { data: pg }] = await Promise.all([
      supabase.from('contratos').select('*').eq('aluno_id', aluno.id).eq('status', 'ativo')
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('agenda_semanal').select('*').eq('aluno_id', aluno.id).eq('ativo', true).order('dia_semana'),
      supabase.from('aulas').select('*').eq('aluno_id', aluno.id).order('data', { ascending: false }).limit(60),
      supabase.from('pagamentos').select('*').eq('aluno_id', aluno.id).order('data_vencimento', { ascending: false }),
    ]);
    // A busca vem da aula mais distante no futuro pra mais perto (pra nao cortar historico
    // se o aluno tiver muita coisa marcada). Pra MOSTRAR na aba "Aulas", inverte: as mais
    // proximas de hoje primeiro. Sem isso, um aluno mensal (que tem a agenda sempre
    // preenchida 90 dias a frente) aparecia com a aula de daqui a 3 meses no topo, dando a
    // impressao errada de que ele ja tinha um plano anual fechado.
    const auOrdenadas = semRemarcada(au).sort((a, b) => (a.data > b.data ? 1 : a.data < b.data ? -1 : 0));   // v131: sem a original de aula remarcada
    setContrato(c || null); setAgenda(ag || []); setAulas(auOrdenadas); setPagamentos(pg || []);
    setCarregando(false);
  }
  React.useEffect(() => { carregar(); }, [aluno.id]);
  // Central de uso: cada aba da ficha conta como uma tela (prof.ficha.<aba>); ao fechar a ficha,
  // volta a valer a tela de antes (a lista de alunos, a agenda...).
  React.useEffect(() => { const antes = uso.telaAtual(); return () => { if (antes) uso.tela(antes); }; }, []);
  React.useEffect(() => { uso.tela('prof.ficha.' + aba); }, [aba]);

  // A ponte: toda edicao vira aviso para o aluno. Um lugar so', para nao esquecer nenhum caso.
  async function avisarAluno(tipo, mensagem) {
    await supabase.from('notificacoes').insert({
      aluno_id: aluno.id, professor_id: professor.id, tipo, mensagem,
    });
  }

  async function salvarDados() {
    setSalvando(true);
    const mudouNome = dados.nome.trim() !== (aluno.nome || '');
    await supabase.from('alunos').update({ nome: dados.nome.trim(), telefone: dados.telefone.trim() }).eq('id', aluno.id);
    if (mudouNome) await avisarAluno('cadastro_alterado', `Seu professor atualizou seu cadastro.`);
    setSalvando(false); toastSM('Dados salvos'); onMudou?.();
  }

  async function salvarContrato(campos, aviso) {
    if (!contrato) return;
    setSalvando(true);
    const { error } = await supabase.from('contratos').update(campos).eq('id', contrato.id);
    if (error) { toastSM('Não foi possível salvar: ' + error.message); setSalvando(false); return; }
    if (aviso) await avisarAluno('contrato', aviso);
    await recalcularCreditos(supabase, aluno.id);
    setSalvando(false); toastSM('Acordo atualizado'); carregar(); onMudou?.();
  }

  async function addHorario() {
    if (novoHorario.dia === '' || !novoHorario.hora) { toastSM('Escolha o dia e o horário'); return; }
    const jaTem = agenda.some(h => Number(h.dia_semana) === Number(novoHorario.dia) && String(h.hora).slice(0,5) === novoHorario.hora);
    if (jaTem) { toastSM('Esse dia e horário já existem'); return; }
    await supabase.from('agenda_semanal').insert({
      aluno_id: aluno.id, professor_id: professor.id,
      dia_semana: Number(novoHorario.dia), hora: novoHorario.hora, ativo: true,
    });
    await avisarAluno('cadastro_alterado',
      `Novo horário na sua agenda: ${DIAS[Number(novoHorario.dia)]} às ${novoHorario.hora}.`);
    setNovoHorario({ dia: '', hora: '' }); toastSM('Horário adicionado'); carregar(); onMudou?.();
  }

  // Alterar um horario fixo (Marcus, 27/09): muda a agenda semanal E as aulas futuras
  // daquele horario — cada uma vai para o novo dia da MESMA semana (nunca para o passado).
  // No pacote, a previsao de renovacao acompanha a ultima aula.
  const [editHor, setEditHor] = React.useState(null);   // { id, dia, hora }
  async function salvarAlteracaoHorario(h) {
    const novoDia = Number(editHor.dia), novaHora = editHor.hora;
    if (!novaHora) { toastSM('Escolha o horário'); return; }
    const velhoDia = Number(h.dia_semana), velhaHora = String(h.hora).slice(0, 5);
    if (novoDia === velhoDia && novaHora === velhaHora) { setEditHor(null); return; }
    setSalvando(true);
    await supabase.from('agenda_semanal').update({ dia_semana: novoDia, hora: novaHora }).eq('id', h.id);
    const hojeStr = new Date().toLocaleDateString('sv-SE');
    // v152: as aulas do proximo pacote (renovou antecipado) ficam como foram combinadas. Revisao v152: o resto
    // muda como antes da v152 — qualquer acordo ativo (pacote + turma) e aula antiga sem contrato_id.
    const [{ data: futuras }, { data: acordosAl }] = await Promise.all([
      supabase.from('aulas').select('id, data, hora, contrato_id, turma_id, sessao_id, desfecho_prof, divergencia_em, aviso_aluno_em, desfeita_em')
        .eq('aluno_id', aluno.id).eq('status', 'agendada').gte('data', hojeStr),
      supabase.from('contratos').select('id, status, tipo_cobranca').eq('aluno_id', aluno.id).in('status', ['ativo', 'proximo']),
    ]);
    const idsProx = (acordosAl || []).filter(k => k.status === 'proximo').map(k => k.id);
    const doSlotH = (futuras || []).filter(a => !idsProx.includes(a.contrato_id) && (h.turma_id ? a.turma_id === h.turma_id : !a.turma_id) && !a.sessao_id
      && new Date(a.data + 'T12:00:00').getDay() === velhoDia && String(a.hora).slice(0, 5) === velhaHora);
    // v155: o mesmo filtro da Agenda — aula ja' registrada pelo professor, em divergencia ou com aviso do aluno fica
    const alvo = doSlotH.filter(a => !a.desfecho_prof && !a.divergencia_em && !avisoVigenteAula(a));
    const ficaramH = doSlotH.length - alvo.length;
    const txtFicaram = ficaramH > 0 ? ` ${ficaramH} aula${ficaramH !== 1 ? 's' : ''} com aviso ou registro ${ficaramH !== 1 ? 'ficaram' : 'ficou'} no horário antigo.` : '';
    // v155: le o erro de cada aula (o banco recusa aula repetida no mesmo dia) e so' conta as que mudaram
    const movidasL = [];
    const novasDatas = await datasTrocaHorarioSM(aluno.id, alvo, velhoDia, novoDia, novaHora, hojeStr);
    for (const a of alvo) {
      const { error } = await supabase.from('aulas').update({ data: novasDatas[a.id], hora: novaHora }).eq('id', a.id);
      if (!error) movidasL.push(a);
    }
    const movidas = movidasL.length, falhou = alvo.length - movidas;
    // v155: aulas do proximo pacote no horario antigo continuam la' — o professor fica sabendo
    const proxNoAntigo = (futuras || []).some(a => idsProx.includes(a.contrato_id)
      && new Date(a.data + 'T12:00:00').getDay() === velhoDia && String(a.hora).slice(0, 5) === velhaHora);
    const txtProx = proxNoAntigo ? ` O próximo pacote continua ${DIAS[velhoDia]} às ${velhaHora}.` : '';
    // O fim de cada pacote ativo que teve aula movida acompanha a ultima aula dele
    const pacotesMovidos = (acordosAl || []).filter(k => k.status === 'ativo' && k.tipo_cobranca !== 'mensal' && movidasL.some(a => a.contrato_id === k.id));
    for (const k of pacotesMovidos) {
      const { data: ult } = await supabase.from('aulas').select('data').eq('contrato_id', k.id)
        .in('status', ['agendada', 'aguardando_confirmacao']).order('data', { ascending: false }).limit(1).maybeSingle();
      if (ult?.data) await supabase.from('contratos').update({ data_fim: ult.data }).eq('id', k.id);
    }
    await avisarAluno('cadastro_alterado',
      `Seu horário mudou: ${DIAS[velhoDia]} às ${velhaHora} → ${DIAS[novoDia]} às ${novaHora}. ${movidas} aula${movidas !== 1 ? 's' : ''} já ajustada${movidas !== 1 ? 's' : ''} na agenda.${txtProx}`);
    setSalvando(false); setEditHor(null);
    toastSM(`Horário alterado — ${movidas} aula${movidas !== 1 ? 's' : ''} ajustada${movidas !== 1 ? 's' : ''}. Aluno avisado.${txtProx}${txtFicaram}`
      + (falhou > 0 ? ` ${falhou} não ${falhou !== 1 ? 'puderam' : 'pôde'} mudar (já tem aula nesse dia) — confira na agenda.` : ''));
    carregar(); onMudou?.();
  }

  async function removerHorario(h) {
    await supabase.from('agenda_semanal').update({ ativo: false }).eq('id', h.id);
    await avisarAluno('cadastro_alterado',
      `Horário removido da sua agenda: ${DIAS[Number(h.dia_semana)]} às ${String(h.hora).slice(0,5)}.`);
    toastSM('Horário removido'); carregar(); onMudou?.();
  }

  // Corrigir o passado (ex.: o professor marcou falta e o aluno mostra que avisou no prazo).
  // v131 (Etapa 7): o que ja' foi registrado e' DESFEITO primeiro no banco (desfazer_desfecho_aula
  // tira a aula que tinha ido para o fim do pacote e a reposicao que nasceu dela) e o desfecho novo
  // segue o caminho de sempre: dada/falta -> professor_fechar_aula com p_forcar (vale na hora);
  // a favor do aluno -> professor_cancelar_aula (regra do acordo). O banco avisa o aluno. Sairam o
  // update direto, o delete/insert em reposicoes e o aviso montado no app.
  async function corrigirAula(au, novoStatus) {
    if (!au || novoStatus === au.status) return;
    setSalvando(true);
    const res = await comTravaAulaSM(au.id, async () => {
      const temRegistro = au.status !== 'agendada' || !!au.desfecho_prof || !!au.divergencia_em;
      let saiu = '';
      if (temRegistro) {
        // So' desfazer (volta para a agenda) avisa o aluno; com um desfecho novo em seguida, quem avisa
        // e' o passo seguinte (v132).
        const d = novoStatus === 'agendada' ? await rpcAulaSM('desfazer_desfecho_aula', { p_aula_id: au.id }) : await desfazerParaRefazerSM(au.id);
        if (!d.ok) { avisarErroAulaProf(d, 'aula_desfazer', 'desfazer', aluno.nome); return { ok: false }; }
        if (novoStatus === 'agendada') return { ok: true, txt: textoDesfeitoSM(d.data) };
        saiu = d.data.reposta_removida_data ? ` A aula que tinha ido para o fim do pacote (${ddmmSM(d.data.reposta_removida_data)}) saiu.` : '';
      } else if (novoStatus === 'agendada') {
        return { ok: true, txt: 'Nada para desfazer: a aula já está na agenda.' };
      }
      const ficou = temRegistro ? ' A aula ficou como agendada e o aluno ainda não foi avisado — escolha de novo.' : '';
      if (novoStatus === 'realizada' || novoStatus === 'falta') {
        // v140: aula SEM registro ainda passa pelo consenso (o aluno confirma, ou vale em 24h; se ele disse
        // "o professor não veio", vira desacordo) — nunca fecha como falta sem os dois concordarem. Forçar
        // (vale na hora) e' so' para CORRIGIR uma aula que ja' tinha registro (decisao 2 = A).
        const f = await rpcAulaSM('professor_fechar_aula', { p_aula_id: au.id, p_desfecho: novoStatus, p_forcar: temRegistro });
        if (!f.ok) {
          if (f.rede) uso.erro('erro.aula_corrigir', f.tecnico); else uso.bloqueio('aula_marcar', null, null, /^[a-z_]{1,40}$/.test(f.cod) ? f.cod : 'outro');
          toastSM(textoErroAulaProf(f.cod, f.data, 'fechar', aluno.nome) + ficou); return { ok: false, mudou: temRegistro };
        }
        await recalcularCreditos(supabase, aluno.id);
        const P1f = aluno.nome.split(' ')[0];
        const est = f.data && f.data.estado;
        if (!temRegistro && est === 'aguardando') {
          return { ok: true, txt: aluno.user_id !== null
            ? `Registrado. ${P1f} confirma no app — se não responder em 24h, vale o seu registro.`
            : `Registrado. ${P1f} ainda não entrou no app — o seu registro vale sozinho em 24h.` };
        }
        if (!temRegistro && est === 'divergencia') {
          return { ok: true, txt: `${P1f} tinha registrado diferente${naoVeioSM(au) ? ' (disse que você não veio)' : ''}. Ficou para vocês acertarem — a aula não desconta até lá.` };
        }
        return { ok: true, txt: `Corrigida para ${novoStatus === 'realizada' ? 'aula dada' : 'falta (conta como aula)'}.${saiu} ${P1f} foi avisado.` };
      }
      const categoria = CATEGORIA_POR_STATUS_SM[novoStatus];
      if (!categoria) return { ok: false, mudou: temRegistro };
      const motivo = MOTIVOS_AULA_SM.includes(au.motivo_cancelamento) ? au.motivo_cancelamento : MOTIVO_PADRAO_SM[categoria];
      const c = await rpcAulaSM('professor_cancelar_aula', { p_aula_id: au.id, p_categoria: categoria, p_motivo: motivo, p_obs: null, p_simular: false });
      if (!c.ok) {
        if (c.rede) uso.erro('erro.aula_corrigir', c.tecnico); else uso.bloqueio('aula_cancelar_prof', null, null, /^[a-z_]{1,40}$/.test(c.cod) ? c.cod : 'outro');
        toastSM(textoErroAulaProf(c.cod, c.data, 'cancelar', aluno.nome) + ficou); return { ok: false, mudou: temRegistro };
      }
      return { ok: true, txt: `Corrigida. ${textoBeneficioProfSM(c.data)} ${aluno.nome.split(' ')[0]} foi avisado.` };
    });
    setSalvando(false);
    if (!res) return;   // outro toque ja' estava salvando esta aula
    if (res.ok) {
      uso.acao('aula.corrigir', { de: au.status, para: novoStatus });
      toastSM(res.txt);
      setEditandoAula(null);
    }
    if (res.ok || res.mudou) { carregar(); onMudou?.(); }
  }

  // v131 (Etapa 6): remarcar_aula confere o horario (alerta com nomes), acerta a renovacao do
  // pacote e avisa o aluno. Sairam o update direto e o aviso montado no app.
  async function salvarDataAula(au, novaData, novaHora) {
    if (!novaData) { toastSM('Escolha a data'); return; }
    setSalvando(true);
    const r = await remarcarAulaProf(au, novaData, novaHora || au.hora, { origem: 'ficha', nomeAluno: aluno.nome });
    setSalvando(false);
    if (!r) return;
    setEditandoAula(null); carregar(); onMudou?.();
  }

  // Dar prazo: "me paga semana que vem" vira uma data nova, registrada e avisada.
  // Etapa 3: valor e vencimento continuam gravados direto; "virou pago" (e corrigir a data de um
  // pago) vai pela funcao confirmar_pagamento, com o MEIO que o professor escolhe aqui. O aviso
  // de "pagamento confirmado" ao aluno quem manda e' a funcao (aqui saiu, para nao ir em dobro).
  async function salvarPagamento(p, { valor, venc, quit, meio }) {
    const pago = p.status === 'pago';
    const virouPago = !!quit && !pago;
    if (!pago && !quit && meio) { uso.bloqueio('ficha_pagamento', null, null, 'meio_sem_data'); toastSM('Para dar baixa, preencha também "Pago em" com o dia em que o aluno pagou.'); return; }
    if (virouPago && !meio) { uso.bloqueio('ficha_pagamento', null, null, 'sem_meio'); toastSM('Escolha como o aluno pagou (Pix, dinheiro, cartão…).'); return; }
    if (quit && quit > hojeSP()) { uso.bloqueio('ficha_pagamento', null, null, 'data_futura'); toastSM(msgErroRpcPagamento('data_futura')); return; }
    setSalvando(true);
    const vencAntes = p.data_vencimento ? String(p.data_vencimento).slice(0, 10) : null;
    const dataAntes = dataSP(p.data_pagamento);
    const base = {};
    if (Number(valor) !== Number(p.valor)) base.valor = valor;
    if (!pago && (venc || null) !== vencAntes) base.data_vencimento = venc || null;
    // ja' pago continua pago mesmo sem data (a data e' que falta confirmar) — como antes
    if (pago && !quit && dataAntes) base.data_pagamento = null;
    if (Object.keys(base).length) {
      const { error } = await supabase.from('pagamentos').update(base).eq('id', p.id);
      if (error) { setSalvando(false); uso.erro('erro.pagamento', error.message); toastSM(msgErroPagamento(error)); return; }
    }
    let r = null;
    if (virouPago || (pago && quit && quit !== dataAntes)) {
      const res = await rpcPagamento('confirmar_pagamento', { p_pagamento_id: p.id, p_meio: virouPago ? meio : null, p_data: quit, p_referencia_mes: null });
      if (!res.ok) {
        setSalvando(false);
        if (res.codigo === 'falha') uso.erro('erro.pagamento', res.tecnico);
        uso.bloqueio('ficha_pagamento', null, null, res.codigo);
        toastSM(res.msg + (Object.keys(base).length ? ' (O valor e o vencimento foram salvos.)' : ''));
        carregar(); onMudou?.();
        return;
      }
      r = res.r;
      uso.acao('ficha.pagamento_confirmar', { meio: r.meio || 'sem_meio', correcao: !!r.correcao });
      await recalcularCreditos(supabase, aluno.id);
    }
    const mudouVenc = !pago && !!venc && venc !== vencAntes;
    if (mudouVenc && !virouPago) await avisarAluno('cobranca_lembrete', `Seu professor adiou o vencimento de ${fmtBRL(valor)} para ${fmtD(venc)}.`);
    setEditandoPag(null); setSalvando(false);
    // v142: "Feito · Desfazer" (P6) — so' na baixa de verdade (nao na correcao da data): a cobranca volta a
    // ficar em aberto, com o "Ja' paguei" do aluno, se havia.
    if (virouPago && r && !r.ja_feito && !r.correcao) toastDesfazerSM(r.aluno_avisado ? 'Pagamento confirmado — o aluno foi avisado no app.' : 'Pagamento confirmado.',
      { ref_tipo: 'pagamento', ref_id: p.id, esperado: 'confirmacao_pagamento' },
      { papel: 'professor', nomeOutro: aluno.nome, curto: 'confirmação do pagamento', alunoRecalc: aluno.id });
    else toastSM(virouPago ? (r?.aluno_avisado ? 'Pagamento confirmado — o aluno foi avisado no app.' : 'Pagamento confirmado.') : 'Pagamento atualizado');
    carregar(); onMudou?.();
  }

  // Lancamento feito por engano (ex.: o mesmo pagamento registrado duas vezes — Cadu,
  // Rogerio, 25/09). Etapa 3: nao apaga mais — CANCELA com motivo (janela ModalCancelarLancamento).
  function apagarPagamento(p) { setCancelarPagFicha(p); }

  const inp = { width: '100%', padding: '11px 12px', borderRadius: '10px', border: '1.5px solid #E2E4E9', fontSize: '15px', outline: 'none', fontFamily: 'inherit' };
  const lab = t => <div style={{ fontSize: '11.5px', fontWeight: '700', color: '#6E7480', textTransform: 'uppercase', margin: '14px 0 5px' }}>{t}</div>;
  const btn = (bg, cor) => ({ border: 'none', background: bg, color: cor, borderRadius: '10px', padding: '10px 14px', fontSize: '13.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' });
  const caixa = { background: 'white', border: '1px solid #E2E4E9', borderRadius: '12px', padding: '12px', marginBottom: '8px' };
  // v148: a regra da chuva do acordo. Vazia no banco = automatica pela modalidade da ficha (ou a do professor, se ele da'
  // uma so'). Turma e banco sem a coluna: nao aparece.
  const temChuva = !!contrato && 'chuva_livre' in contrato && !contrato.turma_id;
  const modsProfF = parseMods(professor.modalidades);
  const chuvaAuto = temChuva && chuvaPelaModalidade(aluno.modalidade, modsProfF);   // fecho v148: ficha vazia = todas as do professor
  const chuvaGravada = temChuva && (typeof contrato.chuva_livre === 'boolean' ? contrato.chuva_livre : chuvaAuto);
  const chuvaAtual = temChuva && (chuvaSel === null ? chuvaGravada : chuvaSel);

  const ABAS = [['contrato','Acordo'], ['agenda','Agenda'], ['aulas','Aulas'], ['pagamentos','Pagamentos'], ['dados','Dados']];
  const mensal = contrato?.tipo_cobranca === 'mensal';

  return (
    <Modal open={true} onClose={onFechar} title={`Ficha de ${aluno.nome}`} wide uso="ficha_aluno">
      <NomeNoAppSM aluno={aluno} nomes={nomesNoApp} style={{ fontSize: '13px', margin: '-2px 0 10px' }} />
      <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', borderBottom: '1px solid #E2E4E9', marginBottom: '14px', paddingBottom: '2px' }}>
        {ABAS.map(([k, l]) => (
          <button key={k} onClick={() => { if (k !== aba) uso.acao('ficha.aba', { aba: k }); setAba(k); }}
            style={{ border: 'none', background: 'none', padding: '9px 12px', fontSize: '14px', cursor: 'pointer',
                     fontWeight: aba === k ? '800' : '600', color: aba === k ? '#2F6BFF' : '#6E7480',
                     borderBottom: aba === k ? '2px solid #2F6BFF' : '2px solid transparent', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
            {l}
          </button>
        ))}
      </div>

      {carregando && <div style={{ padding: '30px', textAlign: 'center', color: '#9AA0AB' }}>Carregando…</div>}

      {!carregando && aba === 'contrato' && (contrato ? (
        <div>
          <div style={{ fontSize: '12.5px', color: '#6E7480', marginBottom: '6px' }}>
            {mensal ? 'Mensalidade fixa' : `Pacote de ${contrato.total_aulas} aulas`} · início {fmtD(contrato.data_inicio)}
          </div>
          {mensal ? (
            <>
              {lab('Valor da mensalidade (R$)')}
              <CampoDinheiro value={contrato.valor_mensal} id="fc_vm" style={inp} />
              {lab('Dia do vencimento')}
              <input defaultValue={contrato.dia_vencimento} id="fc_dv" style={inp} inputMode="numeric" />
            </>
          ) : (
            <>
              {lab('Valor total do pacote (R$)')}
              <CampoDinheiro value={contrato.preco_negociado} id="fc_pn" style={inp} />
              {lab('Total de aulas do pacote')}
              <input defaultValue={contrato.total_aulas} id="fc_ta" style={inp} inputMode="numeric" />
              {lab('Aulas já feitas antes de entrar no app')}
              <input defaultValue={contrato.aulas_realizadas_anterior || 0} id="fc_ar" style={inp} inputMode="numeric" />
            </>
          )}
          {lab('Duração da aula (minutos)')}
          <input defaultValue={contrato.duracao_min || 60} id="fc_dm" style={inp} inputMode="numeric" />
          {/* v144: com a coluna do "desmarcar livre" no banco, em branco = livre (sem prazo combinado) */}
          {lab('cancelamento_livre' in contrato ? 'Prazo para desmarcar (horas antes)' : 'Cancelamento: horas de antecedência')}
          <input defaultValue={contrato.cancelamento_livre === true ? '' : (contrato.antecedencia_cancelamento_horas || 24)} id="fc_ac" style={inp} inputMode="numeric"
            placeholder={'cancelamento_livre' in contrato ? 'Em branco = livre' : undefined} />
          {'cancelamento_livre' in contrato && (
            <div style={{ fontSize: '11.5px', color: '#9AA0AB', marginTop: '4px' }}>Em branco = livre: avisou antes do horário da aula, a aula não conta.</div>
          )}
          {lab('Reajuste ao ano (%)')}
          <input defaultValue={contrato.reajuste_percentual || 0} id="fc_rj" style={inp} inputMode="decimal" />
          {temChuva && (
            <>
              {lab('Chuva')}
              <select value={chuvaAtual ? 'sim' : 'nao'} onChange={e => setChuvaSel(e.target.value === 'sim')} id="fc_ch" style={inp}>
                <option value="sim">Não conta para ninguém</option>
                <option value="nao">Conta como imprevisto</option>
              </select>
              <div style={{ fontSize: '11.5px', color: '#9AA0AB', marginTop: '4px', lineHeight: 1.45 }}>
                {chuvaAtual ? `Cancelou por chuva até o início da aula (você ou o aluno): não desconta, ${mensal ? 'vira reposição a combinar' : 'vai para o fim do pacote'} e não tem limite no mês.`
                            : 'Chuva vale como imprevisto: prazo combinado e a regra abaixo.'}
                {typeof contrato.chuva_livre !== 'boolean' && chuvaSel === null
                  ? (chuvaAuto ? ` Automático: ${aluno.modalidade ? aluno.modalidade + ' é' : modsProfF.length > 1 ? 'suas modalidades são' : 'a modalidade é'} ao ar livre.`
                     : !aluno.modalidade && modsProfF.length > 1 ? ' Automático: a ficha não diz a modalidade — se a aula for ao ar livre, escolha aqui.'
                     : ' Automático: a modalidade não é ao ar livre.') : ''}
              </div>
            </>
          )}
          {lab(chuvaAtual ? 'Imprevisto gera' : 'Chuva / imprevisto gera')}
          <select defaultValue={contrato.forca_maior_gera || 'reposicao'} id="fc_fm" style={inp}>
            <option value="reposicao">Reposição a combinar</option>
            <option value="desconto">Desconto na próxima cobrança</option>
            <option value="nada">Nada</option>
          </select>
          {lab('Máximo de reposições por imprevisto no mês')}
          <input defaultValue={contrato.forca_maior_teto_mes ?? 1} id="fc_ft" style={inp} inputMode="numeric" />
          {chuvaAtual && (
            <div style={{ fontSize: '11.5px', color: '#9AA0AB', marginTop: '4px', lineHeight: 1.45 }}>
              Enquanto a chuva não conta para ninguém, estas duas regras ficam guardadas: só valem se você mudar a chuva para “Conta como imprevisto”.
            </div>
          )}

          <button disabled={salvando} style={{ ...btn('#16181D', 'white'), width: '100%', marginTop: '16px', padding: '13px' }}
            onClick={() => {
              const g = id => document.getElementById(id)?.value;
              const n = v => Number(String(v).replace(',', '.')) || 0;
              // Mensal: o "preco" do acordo acompanha a mensalidade (o banco tambem espelha — Etapa 4).
              const campos = mensal
                ? { valor_mensal: numDinheiro(g('fc_vm')), preco_negociado: numDinheiro(g('fc_vm')), dia_vencimento: Math.min(31, Math.max(1, n(g('fc_dv')) || 1)) }
                : { preco_negociado: numDinheiro(g('fc_pn')), total_aulas: n(g('fc_ta')), aulas_realizadas_anterior: n(g('fc_ar')) };
              campos.duracao_min = n(g('fc_dm')) || 60;
              if ('cancelamento_livre' in contrato) {          // v144: em branco (ou 0) = livre
                campos.cancelamento_livre = !(n(g('fc_ac')) > 0);
                if (!campos.cancelamento_livre) campos.antecedencia_cancelamento_horas = n(g('fc_ac'));
              } else campos.antecedencia_cancelamento_horas = n(g('fc_ac')) || 24;
              // Fechamento v144 (revisao, achado 6 do banco): acordo ja' ACEITO — prazo novo contra o aluno (era livre e
              // passa a ter prazo, ou mais horas) nao muda por aqui: vai pelo "Mandar acordo", e o aluno confere (regra 1).
              if (contrato.status_aceite === 'aceito' && 'cancelamento_livre' in contrato) {
                const hAntes = contrato.cancelamento_livre === true ? 0 : (Number(contrato.antecedencia_cancelamento_horas) || 24);
                const hDepois = campos.cancelamento_livre ? 0 : Number(campos.antecedencia_cancelamento_horas) || 0;
                if (hDepois > hAntes) {
                  uso.bloqueio('ficha_prazo_contra_aluno', null, null, hAntes ? 'mais_horas' : 'livre_para_prazo');
                  toastSM(`${primeiroNomeSM(aluno?.nome, 'O aluno')} já aceitou o acordo ${hAntes ? `com ${hAntes}h para desmarcar` : 'sem prazo para desmarcar'}. Para combinar um prazo maior, use "Mandar acordo" na lista de alunos — ele confere antes de valer.`);
                  return;
                }
              }
              campos.reajuste_percentual = n(g('fc_rj'));
              campos.forca_maior_gera = g('fc_fm');
              campos.forca_maior_teto_mes = n(g('fc_ft'));
              // v148: so' grava a regra da chuva quando o professor mudou (vazia continua automatica pela modalidade)
              if (temChuva && chuvaSel !== null && chuvaSel !== chuvaGravada) {
                campos.chuva_livre = chuvaSel;
                uso.acao('ficha.chuva', { valor: chuvaSel ? 'sim' : 'nao', automatico: chuvaAuto ? 'sim' : 'nao' });
              }
              salvarContrato(campos, 'chuva_livre' in campos
                ? `Seu professor mudou a regra da chuva no seu acordo: agora a chuva ${campos.chuva_livre ? 'não conta para ninguém' : 'conta como imprevisto'}. Confira na tela do acordo.`
                : 'Seu professor atualizou as condições do seu plano. Confira na tela do acordo.');
            }}>
            {salvando ? 'Salvando…' : 'Salvar acordo e avisar aluno'}
          </button>
        </div>
      ) : <div style={{ color: '#9AA0AB', fontSize: '14px' }}>Este aluno não tem acordo em vigor.</div>)}

      {!carregando && aba === 'agenda' && (
        <div>
          {agenda.length === 0 && <div style={{ color: '#9AA0AB', fontSize: '14px', marginBottom: '10px' }}>Nenhum horário fixo.</div>}
          {agenda.map(h => editHor?.id === h.id ? (
            <div key={h.id} style={{ ...caixa }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#16181D', marginBottom: '8px' }}>
                Alterar {DIAS[Number(h.dia_semana)]} às {String(h.hora).slice(0, 5)}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <select value={editHor.dia} onChange={e => setEditHor({ ...editHor, dia: e.target.value })} style={{ ...inp, flex: 1, minWidth: '130px' }}>
                  {DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
                <input type="time" value={editHor.hora} onChange={e => setEditHor({ ...editHor, hora: e.target.value })} style={{ ...inp, width: 'auto' }} />
              </div>
              <div style={{ fontSize: '11.5px', color: '#6E7480', marginTop: '8px' }}>
                As próximas aulas desse horário mudam junto. O aluno é avisado.
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button disabled={salvando} onClick={() => salvarAlteracaoHorario(h)} style={{ ...btn('#2F6BFF', 'white'), flex: 1 }}>{salvando ? 'Salvando…' : 'Salvar'}</button>
                <button onClick={() => setEditHor(null)} style={btn('#F0F1F4', '#3D424C')}>Voltar</button>
              </div>
            </div>
          ) : (
            <div key={h.id} style={{ ...caixa, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#16181D' }}>{DIAS[Number(h.dia_semana)]}</div>
                <div style={{ fontSize: '13px', color: '#6E7480' }}>{String(h.hora).slice(0, 5)}</div>
              </div>
              <button onClick={() => setEditHor({ id: h.id, dia: String(h.dia_semana), hora: String(h.hora).slice(0, 5) })} style={btn('#E7EDFF', '#2F6BFF')}>Alterar</button>
              <button onClick={() => { if (window.confirm(`Remover ${DIAS[Number(h.dia_semana)]} às ${String(h.hora).slice(0, 5)} da agenda fixa?`)) removerHorario(h); }} style={btn('#FEF2F2', '#DC2626')}>Remover</button>
            </div>
          ))}
          <div style={{ background: '#F7F8FA', borderRadius: '12px', padding: '12px', marginTop: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#16181D', marginBottom: '8px' }}>Adicionar horário</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <select value={novoHorario.dia} onChange={e => setNovoHorario({ ...novoHorario, dia: e.target.value })} style={{ ...inp, flex: 1, minWidth: '130px' }}>
                <option value="">Dia da semana</option>
                {DIAS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
              <input type="time" value={novoHorario.hora} onChange={e => setNovoHorario({ ...novoHorario, hora: e.target.value })} style={{ ...inp, width: 'auto' }} />
              <button onClick={addHorario} style={btn('#2F6BFF', 'white')}>Adicionar</button>
            </div>
            <div style={{ fontSize: '11.5px', color: '#6E7480', marginTop: '8px' }}>
              Serve para um terceiro dia, ou um horário diferente dos demais. O aluno é avisado.
            </div>
          </div>
        </div>
      )}

      {!carregando && aba === 'aulas' && (
        <div>
          <div style={{ fontSize: '12.5px', color: '#6E7480', marginBottom: '10px' }}>
            Toque numa aula para corrigir o que aconteceu ou mudar a data. O saldo é recalculado e o aluno avisado.
          </div>
          {aulas.map(au => {
            const aberta = editandoAula === au.id;
            const cor = au.status === 'realizada' ? '#0E9F6E' : au.status === 'falta' ? '#DC2626'
                      : au.status === 'agendada' ? '#2F6BFF' : '#9AA0AB';
            const rotulo = rotuloEspecialAula(au) || { realizada: 'Realizada', falta: 'Faltou (consumiu aula)', agendada: 'Agendada',
                             cancelada_aluno: 'Cancelou no prazo', cancelada_professor: 'Professor cancelou',
                             cancelada_forca_maior: 'Imprevisto / chuva', aguardando_confirmacao: 'Lançada pelo aluno — confirme no Início' }[au.status] || au.status;
            // v140: o aluno disse que VOCE nao veio — linha vermelha e "Eu que desmarquei" primeiro
            const nvF = naoVeioSM({ ...au, duracao_min: au.duracao_min || contrato?.duracao_min });
            const opcoesFicha = [['realizada','Concluída'],
                        ['falta','Furou ou desmarcou em cima da hora'],
                        ['cancelada_aluno','Avisou dentro do prazo'],
                        ['cancelada_professor', nvF ? 'Eu não fui / desmarquei' : 'Eu que desmarquei'],
                        ['cancelada_forca_maior','Chuva, quadra fechada, feriado'],
                        ['agendada','Ainda vai acontecer']];
            if (nvF) opcoesFicha.unshift(...opcoesFicha.splice(3, 1));
            return (
              <div key={au.id} style={caixa}>
                <div onClick={() => setEditandoAula(aberta ? null : au.id)} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#16181D' }}>{fmtD(au.data)} às {String(au.hora).slice(0, 5)}</div>
                    <div style={{ fontSize: '12.5px', color: cor, fontWeight: '600' }}>{rotulo}</div>
                    {nvF ? (
                      <div style={{ fontSize: '12px', color: '#991B1B', fontWeight: '600', marginTop: '2px', lineHeight: 1.45 }}>
                        <Ico n="user-exclamation" s={12} /> {aluno.nome.split(' ')[0]} diz que você não veio · avisou {quandoRelSM(nvF.em)}
                      </div>
                    ) : null}
                  </div>
                  <Ico n={aberta ? 'chevron-down' : 'chevron-right'} s={16} c="#9AA0AB" />
                </div>
                {aberta && (
                  <div style={{ marginTop: '10px', borderTop: '1px solid #F0F1F4', paddingTop: '10px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#6E7480', marginBottom: '6px' }}>O que aconteceu de verdade?</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {opcoesFicha.map(([st, txt]) => (
                        <button key={st} disabled={salvando || st === au.status} onClick={() => corrigirAula(au, st)}
                          style={{ textAlign: 'left', border: '1px solid #E2E4E9', borderRadius: '9px', padding: '9px 11px',
                                   background: st === au.status ? '#E7EDFF' : '#F7F8FA', cursor: st === au.status ? 'default' : 'pointer',
                                   fontSize: '13px', fontWeight: st === au.status ? '700' : '500',
                                   color: st === au.status ? '#2F6BFF' : '#16181D', fontFamily: 'inherit' }}>
                          {txt}{st === au.status ? ' · atual' : ''}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#6E7480', margin: '12px 0 6px' }}>Ou mude a data</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <input type="date" defaultValue={String(au.data).slice(0, 10)} id={'fc_d_' + au.id} style={{ ...inp, width: 'auto' }} />
                      <input type="time" defaultValue={String(au.hora).slice(0, 5)} id={'fc_h_' + au.id} style={{ ...inp, width: 'auto' }} />
                      <button style={btn('#2F6BFF', 'white')}
                        onClick={() => salvarDataAula(au, document.getElementById('fc_d_' + au.id)?.value, document.getElementById('fc_h_' + au.id)?.value)}>
                        Remarcar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!carregando && aba === 'pagamentos' && (
        <div>
          <div style={{ fontSize: '12.5px', color: '#6E7480', marginBottom: '10px' }}>
            Dá para adiar um vencimento, registrar a data em que o aluno pagou, ou corrigir o valor.
          </div>
          {pagamentos.length === 0 && <div style={{ color: '#9AA0AB', fontSize: '14px' }}>Nenhuma cobrança ainda.</div>}
          {pagamentos.some(p => p.status === 'cancelado') && (
            <button onClick={() => setVerCanceladosFicha(v => !v)}
              style={{ ...btn('#F0F1F4', '#3D424C'), fontSize: '12.5px', padding: '8px 12px', marginBottom: '10px' }}>
              {verCanceladosFicha ? 'Esconder cancelados' : 'Mostrar cancelados'} ({pagamentos.filter(p => p.status === 'cancelado').length})
            </button>
          )}
          {pagamentos.map((p, idx) => {
            if (p.status === 'cancelado') {
              // Cancelado nunca some (Etapa 3): fica na lista, cinza, com o motivo.
              if (!verCanceladosFicha) return null;
              return (
                <div key={p.id} style={{ ...caixa, background: '#F7F8FA' }}>
                  <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#9AA0AB', textDecoration: 'line-through' }}>{fmtBRL(p.valor)}</div>
                  <div style={{ fontSize: '12.5px', color: '#6E7480', fontWeight: '600' }}>
                    Cancelado{p.cancelado_motivo ? ' · ' + (ROTULO_MOTIVO_CANCEL[p.cancelado_motivo] || 'outro motivo') : ''}
                  </div>
                </div>
              );
            }
            const aberta = editandoPag === p.id;
            const pago = p.status === 'pago';
            // mesmo valor, pago, mesmo mes de referencia que outro lancamento: provavel duplicidade
            const duplicado = pago && pagamentos.some((q, j) => j !== idx && q.status === 'pago'
              && Number(q.valor) === Number(p.valor) && (q.referencia_mes || '') === (p.referencia_mes || ''));
            return (
              <div key={p.id} style={caixa}>
                <div onClick={() => setEditandoPag(aberta ? null : p.id)} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#16181D' }}>{fmtBRL(p.valor)}</div>
                    <div style={{ fontSize: '12.5px', color: pago ? '#0E9F6E' : '#D97706', fontWeight: '600' }}>
                      {!pago ? `Vence em ${fmtD(p.data_vencimento)}`
                        : (!p.data_pagamento && !p.meio && ehPagoAntesDoCadastro(p)) ? 'Pago antes do cadastro'
                        : (p.data_pagamento ? `Pago em ${fmtD(p.data_pagamento)}` : 'Pago') + (formaDoPagamento(p) !== '—' ? ' · ' + formaDoPagamento(p) : '')}
                    </div>
                    {pago && !p.data_pagamento && !ehPagoAntesDoCadastro(p) && (
                      <div style={{ fontSize: '11.5px', color: '#B45309', fontWeight: '700', marginTop: '3px' }}>
                        Data do pagamento não informada — toque para confirmar
                      </div>
                    )}
                    {duplicado && (
                      <div style={{ fontSize: '11.5px', color: '#B45309', fontWeight: '700', marginTop: '3px' }}>
                        Parece lançado duas vezes — abra e cancele o que sobrou
                      </div>
                    )}
                  </div>
                  <Ico n={aberta ? 'chevron-down' : 'chevron-right'} s={16} c="#9AA0AB" />
                </div>
                {aberta && (
                  <div style={{ marginTop: '10px', borderTop: '1px solid #F0F1F4', paddingTop: '10px' }}>
                    {lab('Valor (R$)')}
                    <CampoDinheiro value={p.valor} id={'fc_pv_' + p.id} style={inp} />
                    {/* Ja' pago: vencimento nao importa mais — so' valor e data em que pagou (Marcus, 25/09) */}
                    {!pago && lab('Vence em')}
                    <input type="date" defaultValue={p.data_vencimento ? String(p.data_vencimento).slice(0, 10) : ''} id={'fc_pd_' + p.id}
                      style={pago ? { display: 'none' } : inp} />
                    {lab(pago ? 'Pago em' : 'Pago em (deixe vazio se ainda não pagou)')}
                    <input type="date" defaultValue={dataSP(p.data_pagamento) || ''} max={hojeSP()} id={'fc_pp_' + p.id} style={inp} />
                    {/* Ao virar "pago", o meio e' obrigatorio (vai para confirmar_pagamento). Ja' pago: so' mostra. */}
                    {!pago && lab('Como pagou (se já pagou)')}
                    {!pago && (
                      <select id={'fc_pm_' + p.id} defaultValue="" style={inp}>
                        <option value="">Escolha…</option>
                        {MEIOS_PAGAMENTO.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                      </select>
                    )}
                    <button disabled={salvando} style={{ ...btn('#16181D', 'white'), width: '100%', marginTop: '14px', padding: '12px' }}
                      onClick={() => {
                        salvarPagamento(p, {
                          valor: numDinheiro(document.getElementById('fc_pv_' + p.id)?.value),
                          venc: document.getElementById('fc_pd_' + p.id)?.value || null,
                          quit: document.getElementById('fc_pp_' + p.id)?.value || null,
                          meio: document.getElementById('fc_pm_' + p.id)?.value || '',
                        });
                      }}>
                      {salvando ? 'Salvando…' : 'Salvar e avisar aluno'}
                    </button>
                    <button disabled={salvando} onClick={() => apagarPagamento(p)}
                      style={{ ...btn('transparent', '#DC2626'), width: '100%', marginTop: '6px', padding: '10px', fontSize: '13px' }}>
                      Cancelar lançamento
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {cancelarPagFicha && (
        <ModalCancelarLancamento pagamento={cancelarPagFicha} nomeAluno={aluno.nome} origem="ficha"
          onFechar={() => setCancelarPagFicha(null)}
          onCancelado={() => { setCancelarPagFicha(null); setEditandoPag(null); carregar(); onMudou?.(); }} />
      )}

      {!carregando && aba === 'dados' && (
        <div>
          {lab('Nome')}
          <input value={dados.nome} onChange={e => setDados({ ...dados, nome: e.target.value })} style={inp} />
          {lab('WhatsApp')}
          <input value={dados.telefone} onChange={e => setDados({ ...dados, telefone: mascaraTel(e.target.value) })} style={inp} inputMode="tel" />
          <button disabled={salvando} onClick={salvarDados}
            style={{ ...btn('#16181D', 'white'), width: '100%', marginTop: '16px', padding: '13px' }}>
            {salvando ? 'Salvando…' : 'Salvar dados'}
          </button>
        </div>
      )}
    </Modal>
  );
}

function Alunos({ professor, onIrParaFinanceiro, onIrParaTurmas, params }) {
  const [alunos, setAlunos] = React.useState([]);
  const [planos, setPlanos] = React.useState([]);
  const [modal, setModal] = React.useState(false);
  const [modoEdicao, setModoEdicao] = React.useState(false);
  const [alunoEditandoId, setAlunoEditandoId] = React.useState(null);
  const [modalContrato, setModalContrato] = React.useState(false);
  const [modalConfirmaDelete, setModalConfirmaDelete] = React.useState(false);
  const [alunoParaDeletar, setAlunoParaDeletar] = React.useState(null);
  const [alunoSelecionado, setAlunoSelecionado] = React.useState(null);
  const [fichaAluno, setFichaAluno] = React.useState(null);   // ficha completa editavel
  const [menuAluno, setMenuAluno] = React.useState(null);     // id do aluno com o "···" aberto
  const [alunoHistorico, setAlunoHistorico] = React.useState(null);
  const [modalAulasAluno, setModalAulasAluno] = React.useState(null);
  const [modalReajusteAluno, setModalReajusteAluno] = React.useState(null);
  const [reajusteAlunoForm, setReajusteAlunoForm] = React.useState({ novoPreco: '', percentual: '' });
  const [salvandoReajusteAluno, setSalvandoReajusteAluno] = React.useState(false);
  const enviandoReajusteAlunoRef = React.useRef(false);   // trava o toque duplo no "Enviar para aceite"
  const [filtroStatus, setFiltroStatus] = React.useState('todos');
  const [modalModoRapido, setModalModoRapido] = React.useState(false);
  const [modalMigracao, setModalMigracao] = React.useState(false);
  const [alunoParaRenovar, setAlunoParaRenovar] = React.useState(null);
  const [renovarJaPagou, setRenovarJaPagou] = React.useState(false); // v152: veio do aviso "ja' paguei o proximo"
  const [fichaCompletar, setFichaCompletar] = React.useState(null);   // v142 (item E): "Completar" 
  // O professor chegou por convite de aluno: o combinado veio junto, entao o cadastro
  // abre ja' preenchido em vez de ele redigitar o que o aluno acabou de escrever.
  const [propostaAluno, setPropostaAluno] = React.useState(null);
  // params.proposta: o app do professor acabou de aceitar um pedido de aluno (ver ProfessorApp)
  // com esta tela ja' aberta — le de novo.
  React.useEffect(() => {
    // Fechamento v144 (revisao, achado 1): veio do Inicio com um pedido do banco — abre ESSE (logo abaixo), nunca o que
    // ficou guardado no aparelho (podia ser de outro aluno: o formulario de um e o pedido de outro).
    if (params?.pedido) return;
    try {
      const bruto = localStorage.getItem('propostaDoAluno');
      if (!bruto) return;
      const pr = JSON.parse(bruto);
      if (!pr || (Date.now() - (pr.quando || 0)) > 86400000) { localStorage.removeItem('propostaDoAluno'); return; }
      setPropostaAluno(pr);
      uso.acao('alunos.novo_proposta');
      setModalMigracao(true);
    } catch { try { localStorage.removeItem('propostaDoAluno'); } catch {} }
  }, [params?.proposta]);
  // v144: os pedidos que este professor aceitou e ainda nao completou vem do BANCO (antes so' do aparelho onde
  // o pedido foi aceito: fechar a janela ou abrir em outro aparelho perdia o combinado — teste de 21/10).
  const [pedidosAbertos, setPedidosAbertos] = React.useState([]);
  async function carregarPedidosAbertos() {
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('professor_pedidos_a_completar')); } catch (e) { error = e; }
    const lista = !error && Array.isArray(data) ? data : [];
    setPedidosAbertos(lista);
    return lista;
  }
  function abrirPedido(pd, origem) {
    uso.acao('alunos.pedido_completar', { origem });
    // aluno_nome: o do banco (nunca o e-mail). ficha_sua: a ficha que ele ja' tem com voce (nao e' "repetido").
    setPropostaAluno({ ...(pd.proposta || {}), aluno_nome: pd.aluno_nome || (pd.proposta && pd.proposta.aluno_nome) || '',
                       aluno_id: pd.aluno_id, pedido: pd.code, quando: Date.now(), ficha_sua: pd.ficha_sua || null,
                       ...(pd.proposta ? {} : { so_pedido: true }) });
    setModalMigracao(true);
  }
  React.useEffect(() => {
    carregarPedidosAbertos().then(lista => {
      const pd = params?.pedido ? lista.find(x => x.code === params.pedido) : null;   // veio do Inicio
      if (pd) abrirPedido(pd, params?.origem === 'colar' ? 'colar' : 'inicio');       // 'colar': colou o link (fecho v147)
    });
  }, [professor.id, params?.pedido]);
  // v148: o nome que cada aluno usa no app ({id da ficha: nome}), para o "no app: ..." da lista e da ficha. O banco
  // devolve so' os alunos deste professor e so' o nome. Pede ao abrir a tela e quando muda quem esta' ligado a uma
  // conta. Sem o SQL v148_nome (PGRST202) fica vazio, calado — a lista fica como antes.
  const [nomesNoApp, setNomesNoApp] = React.useState({});
  const ligadosNoApp = alunos.filter(a => a.user_id && !a.desvinculado_em).map(a => a.id + ':' + a.user_id).sort().join(',');
  React.useEffect(() => {
    if (!ligadosNoApp) { setNomesNoApp({}); return undefined; }
    let vivo = true;
    supabase.rpc('professor_nomes_no_app').then(({ data, error }) => {
      if (!vivo) return;
      if (error) { if (error.code !== 'PGRST202') uso.erro('erro.nomes_no_app', error.message || error.code); return; }
      setNomesNoApp(data && data.ok && data.nomes && typeof data.nomes === 'object' ? data.nomes : {});
    }, () => {});
    return () => { vivo = false; };
  }, [professor.id, ligadosNoApp]);
  const [modalConvite, setModalConvite] = React.useState(false);
  // Central de uso: funil convite_enviar — 1 pediu o link, 2 janela aberta; fecha com
  // copiar/WhatsApp/e-mail (fim) ou ao fechar a janela (saiu).
  const [convPasso, setConvPasso] = React.useState(0);
  const fConv = useFluxoUso('convite_enviar', convPasso, convPasso > 0);
  const [resumoConvite, setResumoConvite] = React.useState('');
  const [propostaConvite, setPropostaConvite] = React.useState(null);
  const [linkConvite, setLinkConvite] = React.useState('');
  const [conviteAluno, setConviteAluno] = React.useState(null);
  const [wizardStep, setWizardStep] = React.useState(1);
  const [wizardPreenchido, setWizardPreenchido] = React.useState(false);
  const [agendaLivre, setAgendaLivre] = React.useState(false);
  const [agendaJaDefinida, setAgendaJaDefinida] = React.useState(false);
  const [modalRevisaoContrato, setModalRevisaoContrato] = React.useState(false);
  const [revisaoContrato, setRevisaoContrato] = React.useState(null);
  const [salvandoRevisao, setSalvandoRevisao] = React.useState(false);
  const [modalAulaAvulsa, setModalAulaAvulsa] = React.useState(false);
  const [formAulaAvulsa, setFormAulaAvulsa] = React.useState({ aluno_id: '', aluno_nome: '', data: '', hora: '10:00', nota: '' });
  const [salvandoAvulsa, setSalvandoAvulsa] = React.useState(false);
  // Central de uso: janelas desta tela que nao usam <Modal> (as outras se registram sozinhas)
  React.useEffect(() => { if (modalRevisaoContrato) uso.janela('acordo_revisao'); }, [modalRevisaoContrato]);
  React.useEffect(() => { if (modalContrato) uso.janela('acordo_wizard'); }, [modalContrato]);
  React.useEffect(() => { if (modalAulaAvulsa) uso.janela('aula_avulsa'); }, [modalAulaAvulsa]);
  React.useEffect(() => { if (modalReajusteAluno) uso.janela('reajuste_aluno'); }, [!!modalReajusteAluno]);

  const [ignoradosContrato, setIgnoradosContrato] = React.useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(`ignorar_contrato_${professor?.id}`) || '[]')); }
    catch { return new Set(); }
  });
  function ignorarContrato(alunoId) {
    const novos = new Set(ignoradosContrato); novos.add(alunoId);
    setIgnoradosContrato(novos);
    localStorage.setItem(`ignorar_contrato_${professor?.id}`, JSON.stringify([...novos]));
  }
  const [form, setForm] = React.useState({ nome: '', telefone: '', email: '', modalidade: '', observacoes: '', tipo_vinculo: 'vinculado' });
  const [buscaStep, setBuscaStep] = React.useState(0);
  const [buscandoAluno, setBuscandoAluno] = React.useState(false);
  const [alunoEncontrado, setAlunoEncontrado] = React.useState(null);
  const [fotoContato, setFotoContato] = React.useState(null);
  const defaultFormContrato = { plano_id: '', duracao_meses: 1, valor_aula: '', taxa_academia: 0, aulas_por_semana: 2, reposicao_permitida: false, limite_reposicao: 1, antecedencia_cancelamento_horas: 24, data_inicio: (() => { const n=new Date(); const p=v=>String(v).padStart(2,'0'); return `${n.getFullYear()}-${p(n.getMonth()+1)}-${p(n.getDate())}`; })(), data_fim: '', horarios: [{dia_semana: null, hora: '08:00'}, {dia_semana: null, hora: '08:00'}], tipo_cobranca: 'mensal', dia_vencimento: 5, em_andamento: false, aulas_ja_realizadas: 0, aulas_canceladas: 0, parcelas_ja_pagas: 0, modo: 'interno', multa_cancelamento_pct: 10 };
  const [formContrato, setFormContrato] = React.useState(defaultFormContrato);
  const [conflitosWizard, setConflitosWizard] = React.useState({});

  React.useEffect(() => {
    if (!modalContrato || wizardStep !== 2) { setConflitosWizard({}); return; }
    const timer = setTimeout(async () => {
      const novos = {};
      for (let i = 0; i < (formContrato.horarios||[]).length; i++) {
        const h = formContrato.horarios[i];
        if (h.dia_semana === null || !h.hora) continue;
        const { data } = await supabase.from('agenda_semanal')
          .select('*, alunos(nome)')
          .eq('professor_id', professor.id)
          .eq('dia_semana', h.dia_semana)
          .eq('hora', h.hora)
          .eq('ativo', true)
          .neq('aluno_id', alunoSelecionado?.id || 'x');
        if (data && data.length > 0) novos[i] = data;
      }
      setConflitosWizard(novos);
    }, 400);
    return () => clearTimeout(timer);
  }, [formContrato.horarios, modalContrato, wizardStep]);

  React.useEffect(() => {
    if (!modalContrato || agendaLivre) return;
    const proxData = calcProximaAulaInicio(formContrato.horarios);
    if (proxData && proxData !== formContrato.data_inicio) {
      setFormContrato(f => ({ ...f, data_inicio: proxData }));
    }
  }, [formContrato.horarios, agendaLivre, modalContrato]);

  React.useEffect(() => {
    if (!modalContrato || agendaLivre) return;
    const diasSemana = (formContrato.horarios || []).map(h => h.dia_semana).filter(d => d !== null && d !== undefined);
    const totalAulas = (formContrato.aulas_por_semana || 0) * (formContrato.duracao_meses || 1) * 4;

    if (diasSemana.length < (formContrato.aulas_por_semana || 1) || !totalAulas || !formContrato.data_inicio) return;
    const novaDataFim = calcDataFimPorAulas(formContrato.data_inicio, diasSemana, totalAulas);
    if (novaDataFim && novaDataFim !== formContrato.data_fim) {
      setFormContrato(f => ({ ...f, data_fim: novaDataFim }));
    }
  }, [formContrato.horarios, formContrato.data_inicio, formContrato.aulas_por_semana, formContrato.duracao_meses, modalContrato, agendaLivre]);

  // Abre a janela com o acordo RELIDO do banco (mensal: a base e' o valor_mensal).
  async function abrirReajusteAluno(a, contrato) {
    const fresco = await carregarAcordoReajuste(contrato.id);
    const c = { ...contrato, ...(fresco || {}) };
    setModalReajusteAluno({ aluno: a, contrato: c });
    setReajusteAlunoForm({ novoPreco: baseReajuste(c).toFixed(2), percentual: '' });
  }

  async function aplicarReajusteAluno() {
    if (!modalReajusteAluno) return;
    // Trava ANTES de reler o acordo: sem isso, um toque duplo mandava a proposta (e o aviso
    // ao aluno) duas vezes enquanto a releitura ainda estava a caminho.
    if (enviandoReajusteAlunoRef.current) return;
    enviandoReajusteAlunoRef.current = true;
    setSalvandoReajusteAluno(true);
    try {
      const { contrato } = modalReajusteAluno;
      // Relê o acordo antes de propor: se o valor mudou enquanto a janela estava aberta, mostra o novo.
      const fresco = await carregarAcordoReajuste(contrato.id);
      if (!fresco) { uso.bloqueio('reajuste_aluno', null, null, 'falha_leitura'); toastSM('Não consegui ler o acordo agora. Tente de novo em instantes.'); return; }
      if (fresco.status && fresco.status !== 'ativo') { uso.bloqueio('reajuste_aluno', null, null, 'acordo_inativo'); toastSM('Este acordo não está mais ativo.'); setModalReajusteAluno(null); loadAlunos(); return; }
      const precoAtual = baseReajuste(fresco);
      if (Math.round(precoAtual * 100) !== Math.round(baseReajuste(contrato) * 100) || fresco.tipo_cobranca !== contrato.tipo_cobranca) {
        setModalReajusteAluno(m => ({ ...m, contrato: { ...m.contrato, ...fresco } }));
        setReajusteAlunoForm({ novoPreco: precoAtual.toFixed(2), percentual: '' });
        uso.bloqueio('reajuste_aluno', null, null, 'valor_mudou');
        toastSM(`O valor atual deste acordo é ${fmtBRL(precoAtual)}${sufixoPrecoAcordo(fresco)}. Confira o novo preço e envie de novo.`);
        return;
      }
      const novoPreco = reajusteAlunoForm.percentual
        ? +(precoAtual * (1 + Number(reajusteAlunoForm.percentual) / 100)).toFixed(2)
        : Number(reajusteAlunoForm.novoPreco);
      if (!novoPreco || novoPreco <= 0) { uso.bloqueio('reajuste_aluno', null, null, 'sem_preco'); toastSM('Informe o novo preço'); return; }
      uso.acao('reajuste.propor', { origem: 'aluno', mensal: ehMensalAcordo(fresco) });
      const { error: eRj } = await supabase.from('contratos').update({
        preco_reajuste_pendente: novoPreco,
        reajuste_solicitado_em: new Date().toISOString()
      }).eq('id', fresco.id);
      if (eRj) { uso.erro('erro.reajuste_propor', eRj.message); toastSM('Não foi possível enviar o reajuste agora. Confira sua internet e tente de novo.'); return; }
      await supabase.from('notificacoes').insert({
        aluno_id: modalReajusteAluno.aluno.id, professor_id: professor.id, tipo: 'aceite_reajuste',
        mensagem: `Seu professor propôs um reajuste de ${fmtBRL(precoAtual)} para ${fmtBRL(novoPreco)}${sufixoPrecoAcordo(fresco)}. Toque para aceitar ou recusar.`
      });
      setModalReajusteAluno(null);
      toastSM('Proposta enviada. O aluno aceita ou recusa no app.');
      loadAlunos();
    } finally {
      enviandoReajusteAlunoRef.current = false;
      setSalvandoReajusteAluno(false);
    }
  }

  React.useEffect(() => { loadAlunos(); loadPlanos(); }, []);
  // Veio pelo atalho "+" do menu de baixo: ja' abre o cadastro.
  React.useEffect(() => { if (params?.novo) setModalMigracao(true); }, [params?.novo]);

  React.useEffect(() => {
    if (!params?.aluno_id || alunos.length === 0) return;
    const a = alunos.find(x => x.id === params.aluno_id);
    if (!a) return;
    if (params?.renovar) {

      abrirContrato(a);
    } else {
      setModalAulasAluno({ aluno: a, aulas: a.aulas || [] });
    }
  }, [params?.aluno_id, alunos.length]);
  // v134: veio de Pendencias. "Renovar" abre o MESMO cadastro de renovacao do chip da lista
  // (params.renovar, do sininho, abre o assistente antigo — fica como esta'); "Mandar/Reenviar
  // convite" gera o convite como o chip "Convidar para o app"; "Nao vai renovar" abre o "Encerrar
  // com este aluno" (a consequencia aparece antes de confirmar). Uma vez por toque (params.t).
  const pendAlunosFeito = React.useRef(null);
  React.useEffect(() => {
    const id = params?.renovar_aluno_id || params?.convidar_aluno_id || params?.encerrar_aluno_id;
    if (!id || alunos.length === 0) return;
    const marca = id + ':' + (params?.t || '');
    if (pendAlunosFeito.current === marca) return;
    const a = alunos.find(x => x.id === id);
    if (!a) return;
    pendAlunosFeito.current = marca;
    if (params.renovar_aluno_id) { setRenovarJaPagou(!!params.renovar_ja_pagou); setAlunoParaRenovar(a); }
    else if (params.encerrar_aluno_id) confirmarDelete(a);
    else gerarLinkConviteAluno(a);
  }, [params?.renovar_aluno_id, params?.convidar_aluno_id, params?.encerrar_aluno_id, params?.t, alunos.length]);

  async function loadAlunos() {
    let { data, error } = await supabase.from('alunos')
      .select('*, contratos(id, status, plano_id, preco_negociado, preco_reajuste_pendente, reajuste_solicitado_em, data_fim, data_inicio, modo, total_aulas, aulas_realizadas_anterior, tipo_cobranca, valor_mensal, dia_vencimento, aulas_por_semana, status_aceite), creditos(id, total, usado), pagamentos(id, status, valor, declarado_pago_em, data_vencimento, contrato_id), aulas(id, status, data, contrato_id)')
      .eq('professor_id', professor.id).order('nome');
    if (error && error.message && error.message.includes('declarado_pago_em')) {
      // Coluna nova (aluno avisando "ja' paguei") ainda nao existe neste banco — busca sem
      // ela em vez de quebrar a tela inteira de Alunos. Ver sql/declarado_pago_em.sql.
      ({ data, error } = await supabase.from('alunos')
        .select('*, contratos(id, status, plano_id, preco_negociado, preco_reajuste_pendente, reajuste_solicitado_em, data_fim, data_inicio, modo, total_aulas, aulas_realizadas_anterior, tipo_cobranca, valor_mensal, dia_vencimento, aulas_por_semana, status_aceite), creditos(id, total, usado), pagamentos(id, status, valor, data_vencimento, contrato_id), aulas(id, status, data, contrato_id)')
        .eq('professor_id', professor.id).order('nome'));
      if (!error) console.warn('Coluna declarado_pago_em nao existe ainda. Execute sql/declarado_pago_em.sql no Supabase.');
    }
    if (error) console.error('loadAlunos:', error.message);
    // v131: a original de uma aula remarcada (mensal/turma) nao conta em nenhuma lista
    const alunos = (data || []).map(x => Array.isArray(x.aulas) ? { ...x, aulas: semRemarcada(x.aulas) } : x);

    // (auto-conserto removido: o motor de creditos recalcula apos cada mutacao)
    setAlunos(alunos);
  }

  async function loadPlanos() {
    const { data } = await supabase.from('planos').select('*').eq('professor_id', professor.id).order('duracao_meses');
    let lista = data || [];
    if (lista.length === 0) {
      const padrao = [
        { professor_id: professor.id, nome: 'Plano Mensal', duracao_meses: 1, preco: 0, aulas_por_semana: 0, tipo: 'fixo', antecedencia_cancelamento_horas: 24, limite_reposicao: 0 },
        { professor_id: professor.id, nome: 'Plano Semestral', duracao_meses: 6, preco: 0, aulas_por_semana: 0, tipo: 'anual', antecedencia_cancelamento_horas: 48, limite_reposicao: 0 },
        { professor_id: professor.id, nome: 'Plano Anual', duracao_meses: 12, preco: 0, aulas_por_semana: 0, tipo: 'anual', antecedencia_cancelamento_horas: 48, limite_reposicao: 0 },
      ];
      const { data: inserted } = await supabase.from('planos').insert(padrao).select();
      lista = inserted || [];
    }
    setPlanos(lista);
  }

  async function gerarLinkConvite() {
    setConvPasso(1);
    let code;
    try { code = await criarConvite({ tipo: 'prof_aluno', professor_id: professor.id, dados: { pnome: professor.nome } }); }
    catch (e) { fConv.barrar('falha_gerar_codigo'); uso.erro('erro.convite', e?.message || e); setConvPasso(0); toastSM('Não consegui gerar o link agora. Tente de novo.'); return; }
    const url = `${window.location.origin}${window.location.pathname}?c=${code}`;
    setConviteAluno(null); setLinkConvite(url); setModalConvite(true); setConvPasso(2);
  }

  // Uma linha com o treino do aluno — "Ter e Qui às 7h · pacote de 8 aulas · R$ 1.200".
  // Vai no meio da mensagem de convite: e' o que prova pro aluno que aquilo nao e' spam.
  async function resumoDoContrato(aluno) {
    const [{ data: c }, { data: ag }] = await Promise.all([
      supabase.from('contratos').select('total_aulas, preco_negociado')
        .eq('aluno_id', aluno.id).eq('status', 'ativo')
        .order('created_at', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('agenda_semanal').select('dia_semana, hora')
        .eq('aluno_id', aluno.id).eq('ativo', true).order('dia_semana'),
    ]);
    const partes = [];
    if (ag && ag.length) {
      const dias = [...new Set(ag.map(x => Number(x.dia_semana)))].sort();
      const horarios = {};
      ag.forEach(x => { horarios[Number(x.dia_semana)] = String(x.hora || '').slice(0, 5); });
      partes.push(descreverAgenda({ dias, horarios, hora: horarios[dias[0]] || '' }));
    }
    if (c?.total_aulas) partes.push(`pacote de ${c.total_aulas} aulas`);
    if (c?.preco_negociado) partes.push('R$ ' + Number(c.preco_negociado).toLocaleString('pt-BR'));
    return partes.join(' · ');
  }

  async function gerarLinkConviteAluno(aluno) {
    setConvPasso(1);
    let code;
    try { code = await criarConvite({ tipo: 'prof_aluno', professor_id: professor.id, aluno_id: aluno.id, dados: { pnome: professor.nome, anome: aluno.nome, telefone: aluno.telefone } }); }
    catch (e) { fConv.barrar('falha_gerar_codigo'); uso.erro('erro.convite', e?.message || e); setConvPasso(0); toastSM('Não consegui gerar o convite agora. Tente de novo.'); return; }
    const url = `${window.location.origin}${window.location.pathname}?c=${code}`;
    await supabase.from('alunos').update({ convite_enviado_em: new Date().toISOString() }).eq('id', aluno.id);
    setResumoConvite(await resumoDoContrato(aluno));
    setPropostaConvite(await propostaDoBancoSM(aluno.id).catch(() => null));
    setConviteAluno(aluno); setLinkConvite(url); setModalConvite(true); setConvPasso(2);
    loadAlunos();
  }
  function abrirNovoAluno() {
    const profMods = parseMods(professor.modalidades);
    const modalidadeAuto = profMods.length === 1 ? profMods[0] : '';
    setModoEdicao(false); setAlunoEditandoId(null);
    setForm({ nome: '', telefone: '', email: '', modalidade: modalidadeAuto, observacoes: '', tipo_vinculo: 'vinculado' });
    setBuscaStep(1); setAlunoEncontrado(null); setFotoContato(null);
    setModal(true);
  }

  async function executarBuscaPorTelefone(foneRaw, nomeContato, emailContato) {
    const fone = (foneRaw || '').replace(/\D/g, '');
    if (fone.length < 8) { toastSM('Número inválido. Verifique o telefone.'); return; }
    setBuscandoAluno(true);
    try {

      // O telefone pode estar salvo formatado ("+55 (11) 98765-4321"): busca pelos 4 ultimos
      // digitos (nunca cortados por hifen) e confere os 8 ultimos so' com digitos.
      const { data: candidatos } = await supabase
        .from('alunos')
        .select('id, user_id, nome, email, telefone, modalidade')
        .ilike('telefone', '%' + fone.slice(-4))
        .limit(50);
      const data = (candidatos || []).find(a => (a.telefone || '').replace(/\D/g, '').endsWith(fone.slice(-8))) || null;
      if (data) {
        setAlunoEncontrado(data);
        setForm(f => ({
          ...f,
          nome: data.nome || nomeContato || f.nome,
          email: data.email || emailContato || f.email,
          telefone: data.telefone || foneRaw || f.telefone,
        }));
      } else {
        setAlunoEncontrado(null);

        setForm(f => ({
          ...f,
          nome: nomeContato || f.nome,
          email: emailContato || f.email,
          telefone: foneRaw || f.telefone,
        }));
      }
    } catch(e) { setAlunoEncontrado(null); }
    setBuscandoAluno(false);
    setBuscaStep(1);
  }

  function buscarPorTelefone() {
    executarBuscaPorTelefone(form.telefone.trim(), '');
  }

  async function abrirContatos() {
    if (!navigator.contacts) {
      toastSM('Seu navegador não suporta acesso a contatos. Disponível no Safari iOS e Chrome Android.');
      return;
    }
    try {

      const camposDesejados = ['name', 'tel', 'email', 'icon'];
      const suportados = navigator.contacts.getProperties
        ? await navigator.contacts.getProperties()
        : camposDesejados;
      const camposDisponiveis = camposDesejados.filter(c => suportados.includes(c));

      const selecionados = await navigator.contacts.select(camposDisponiveis, { multiple: false });
      if (!selecionados || selecionados.length === 0) return;
      const contato = selecionados[0];

      const nome = contato.name?.[0] || '';
      const tel = (contato.tel?.[0] || '').replace(/\s/g, '').replace(/[^\d+]/g, '');
      const emailC = contato.email?.[0] || '';

      const iconBlob = contato.icon?.[0];
      if (iconBlob) {
        try {
          const url = URL.createObjectURL(iconBlob);
          setFotoContato(url);
        } catch(_) { setFotoContato(null); }
      } else {
        setFotoContato(null);
      }

      if (!tel) { toastSM('Este contato não tem telefone cadastrado.'); return; }
      setForm(f => ({ ...f, nome, telefone: tel, email: emailC }));
      await executarBuscaPorTelefone(tel, nome, emailC);
    } catch(e) {

    }
  }
  function abrirEditarAluno(aluno) {
    const profMods = parseMods(professor.modalidades);
    const modalidadeAluno = aluno.modalidade || (profMods.length === 1 ? profMods[0] : '');
    setModoEdicao(true); setAlunoEditandoId(aluno.id);
    setForm({ nome: aluno.nome, telefone: aluno.telefone || '', email: aluno.email || '', modalidade: modalidadeAluno, observacoes: aluno.observacoes || '', tipo_vinculo: aluno.tipo_vinculo || 'interno' });
    setModal(true);
  }

  async function salvarAluno() {
    if (!form.nome.trim()) { toastSM('Nome é obrigatório'); return; }

    const profMods = parseMods(professor.modalidades);
    if (profMods.length > 1 && !form.modalidade) { toastSM('Selecione a modalidade do aluno'); return; }

    const modalidadeFinal = form.modalidade || (profMods.length === 1 ? profMods[0] : '');
    if (modoEdicao) {
      const { error } = await supabase.from('alunos').update({ nome: form.nome, telefone: form.telefone, email: form.email, modalidade: modalidadeFinal, observacoes: form.observacoes, tipo_vinculo: form.tipo_vinculo }).eq('id', alunoEditandoId);
      if (error) { toastSM('Erro ao salvar: ' + error.message); return; }
      setModal(false); loadAlunos();
    } else {
      const { data: novoAluno, error } = await supabase.from('alunos').insert({ professor_id: professor.id, nome: form.nome, telefone: form.telefone, email: form.email, modalidade: modalidadeFinal, observacoes: form.observacoes, tipo_vinculo: form.tipo_vinculo || 'vinculado' }).select().single();
      if (error) { toastSM('Erro ao salvar: ' + error.message); return; }
      setModal(false);

      setAlunoSelecionado(novoAluno);
      setFormContrato(defaultFormContrato);
      setWizardStep(1);
      setModalContrato(true);
      loadAlunos();
    }
  }

  function abrirAulaAvulsa(aluno) {
    const hoje = new Date();
    const p = v => String(v).padStart(2, '0');
    const dataHoje = `${hoje.getFullYear()}-${p(hoje.getMonth()+1)}-${p(hoje.getDate())}`;
    setFormAulaAvulsa({ aluno_id: aluno?.id || '', aluno_nome: aluno?.nome || '', data: dataHoje, hora: '10:00', nota: '' });
    setModalAulaAvulsa(true);
  }

  async function salvarAulaAvulsa() {
    if (!formAulaAvulsa.aluno_id) { uso.bloqueio('aula_avulsa', null, null, 'sem_aluno'); toastSM('Selecione o aluno'); return; }
    if (!formAulaAvulsa.data) { uso.bloqueio('aula_avulsa', null, null, 'sem_data'); toastSM('Informe a data da aula'); return; }
    setSalvandoAvulsa(true);
    try {

      const { data: contratosAv } = await supabase
        .from('contratos').select('id').eq('aluno_id', formAulaAvulsa.aluno_id)
        .eq('professor_id', professor.id).eq('status', 'ativo')
        .order('data_inicio', { ascending: false }).limit(1);
      const contrato = contratosAv && contratosAv[0];
      // v152: o aluno nao pode ter duas aulas no mesmo dia e horario (caso Adriana, 29/10). O banco tambem barra.
      const { data: mesmoHorario } = await supabase.from('aulas').select('id, aluno_id, data, hora, status, desfecho_prof, divergencia_em')
        .eq('aluno_id', formAulaAvulsa.aluno_id).eq('data', formAulaAvulsa.data).eq('hora', formAulaAvulsa.hora)
        .in('status', ['agendada', 'aguardando_confirmacao', 'realizada', 'falta']).limit(1);
      if (mesmoHorario && mesmoHorario.length) {
        // v155: so' "ja' conta" se ela ja' foi registrada; agendada sem registro ou proposta do aluno: oferece fechar ESSA
        const ex = mesmoHorario[0];
        const nomeAv = formAulaAvulsa.aluno_nome || '';
        const P1av = nomeAv.split(' ')[0] || 'O aluno';
        uso.bloqueio('aula_avulsa', null, null, 'mesmo_horario');
        // v155: so' aula que ja' comecou (data e hora em Brasilia) — a de hoje mais tarde ainda nao aconteceu
        const jaComecouAv = (inicioAulaSP(ex) || new Date(8.64e15)) <= new Date();
        if (ex.status === 'agendada' && !ex.desfecho_prof && !ex.divergencia_em && jaComecouAv) {
          const sim = await perguntarSM(`${P1av} já tem essa aula na agenda, ainda sem registro. Registrar ela como dada?`,
            { titulo: 'Aula já na agenda', rotuloVoltar: 'Voltar', rotuloForcar: 'Registrar como dada', janela: 'avulsa_ja_agendada' });
          if (!sim) return;
          const est = await aplicarDesfechoProfessor(ex, 'realizada', { nomeAluno: nomeAv, origem: 'avulsa' });
          if (est) { setModalAulaAvulsa(false); loadAlunos(); }
          return;
        }
        if (ex.status === 'aguardando_confirmacao') {
          const sim = await perguntarSM(`${P1av} já lançou essa aula e ela espera você confirmar. Confirmar agora?`,
            { titulo: 'Aula já lançada', rotuloVoltar: 'Voltar', rotuloForcar: 'Confirmar a aula', janela: 'avulsa_proposta' });
          if (!sim) return;
          const r = await responderPropostaProf(ex, true, { origem: 'avulsa', nomeAluno: nomeAv });
          if (r) { await recalcularCreditos(supabase, formAulaAvulsa.aluno_id); setModalAulaAvulsa(false); loadAlunos(); }
          return;
        }
        toastSM(ex.status === 'agendada' && ex.divergencia_em
          ? `${P1av} registrou diferente nessa aula. Acertem nela — não precisa lançar de novo.`
          : ex.status === 'agendada' && ex.desfecho_prof
          ? `Você já registrou essa aula. Falta ${P1av} confirmar no app — não precisa lançar de novo.`
          : ex.status === 'agendada' ? `${P1av} já tem essa aula na agenda. Registre nela quando ela acontecer.`
          : `${P1av} já tem aula nesse dia e horário. Ela já está registrada — não precisa lançar de novo.`);
        return;
      }

      const baseInsert = {
        aluno_id: formAulaAvulsa.aluno_id,
        professor_id: professor.id,
        contrato_id: contrato?.id || null,
        data: formAulaAvulsa.data,
        hora: formAulaAvulsa.hora,
        // nasce agendada e passa pelo consenso (o aluno confirma; 24h de silencio = vale)
        status: 'agendada',
        observacao: formAulaAvulsa.nota || null,
        consome_credito: true,
      };
      let { data: novaAv, error } = await supabase.from('aulas').insert({ ...baseInsert, tipo: 'avulsa' }).select('id').single();
      if (error && error.message && error.message.includes('tipo')) {

        ({ data: novaAv, error } = await supabase.from('aulas').insert(baseInsert).select('id').single());
        if (!error) console.warn('Coluna tipo não existe. Execute: ALTER TABLE aulas ADD COLUMN IF NOT EXISTS tipo VARCHAR(20) DEFAULT \'regular\';');
      }
      if (error) { toastSM(ehAulaRepetidaSM(error) ? `${(formAulaAvulsa.aluno_nome || '').split(' ')[0] || 'O aluno'} já tem aula nesse dia e horário.` : 'Erro: ' + error.message); return; }

      if (novaAv?.id) {
        uso.acao('aula.avulsa');
        await supabase.rpc('professor_fechar_aula', { p_aula_id: novaAv.id, p_desfecho: 'realizada', p_forcar: false });
        toastSM('Aula registrada. O aluno confirma no app — se não responder em 24h, vale o seu registro.');
      }
      await recalcularCreditos(supabase, formAulaAvulsa.aluno_id);

      setModalAulaAvulsa(false);
      loadAlunos();
    } catch(e) { uso.erro('erro.aula_avulsa', e?.message || e); toastSM('Erro inesperado: ' + e.message); }
    finally { setSalvandoAvulsa(false); }
  }

  function confirmarDelete(aluno) { setAlunoParaDeletar(aluno); setModalConfirmaDelete(true); }
  // Tirar da lista NAO e' apagar. O professor e' dono do vinculo, nao da historia do
  // aluno — e entre o que era apagado estavam os pagamentos, que sao comprovante dele.
  // Desvincular: encerra contrato, cancela aulas futuras, avisa o aluno. Nada some.
  async function deletarAluno() {
    const id = alunoParaDeletar.id;
    const { data, error } = await supabase.rpc('desvincular_aluno', { p_aluno_id: id });
    if (error) { toastSM('Não foi possível desvincular: ' + error.message); return; }
    if (data && data.erro) { toastSM('Não foi possível desvincular: ' + data.erro); return; }
    setModalConfirmaDelete(false); setAlunoParaDeletar(null); loadAlunos();
    const aberto = Number(data?.valor_em_aberto || 0);
    uso.acao('alunos.desvincular', { em_aberto: aberto > 0 });
    toastSM(aberto > 0
      ? `Aluno desvinculado. Atenção: ficou R$ ${aberto.toFixed(0)} em aberto no Financeiro.`
      : 'Aluno desvinculado. O histórico dele foi preservado.');
  }

  async function revincularAluno(aluno) {
    const { error } = await supabase.rpc('revincular_aluno', { p_aluno_id: aluno.id });
    if (error) { toastSM('Não foi possível reativar: ' + error.message); return; }
    uso.acao('alunos.reativar', { origem: 'lista' });
    loadAlunos();
    toastSM(`${aluno.nome.split(' ')[0]} voltou para a sua lista. Crie um pacote novo para ele.`);
  }

  async function abrirContrato(aluno) {
    setAlunoSelecionado(aluno);

    const contratoRef = aluno.contratos?.find(c => c.status === 'ativo')
      || [...(aluno.contratos || [])].sort((a, b) => (b.data_inicio || '') > (a.data_inicio || '') ? 1 : -1)[0];

    let form = { ...defaultFormContrato };

    if (contratoRef) {
      const plano = contratoRef.planos;
      const aulasPorSemana = contratoRef.aulas_por_semana || plano?.aulas_por_semana || 2;
      const duracaoMeses = plano?.duracao_meses || 1;

      // Valor por aula = total do pacote / aulas do pacote (a mesma conta do texto do acordo).
      const valorAula = contratoRef.preco_negociado && (Number(contratoRef.total_aulas) > 0 || aulasPorSemana)
        ? String(Math.round(Number(contratoRef.preco_negociado) / (Number(contratoRef.total_aulas) > 0 ? Number(contratoRef.total_aulas) : aulasPorSemana * 4)))
        : '';

      const fim = new Date();
      fim.setMonth(fim.getMonth() + duracaoMeses);
      const p = n => String(n).padStart(2, '0');
      const dataFim = `${fim.getFullYear()}-${p(fim.getMonth()+1)}-${p(fim.getDate())}`;

      form = {
        ...form,
        plano_id: contratoRef.plano_id || '',
        duracao_meses: duracaoMeses,
        valor_aula: valorAula,
        taxa_academia: contratoRef.taxa_academia || 0,
        aulas_por_semana: aulasPorSemana,
        reposicao_permitida: contratoRef.reposicao_permitida || false,
        limite_reposicao: contratoRef.limite_reposicao || 1,
        antecedencia_cancelamento_horas: contratoRef.cancelamento_livre === true ? 0 : (contratoRef.antecedencia_cancelamento_horas || 24),   // v144: 0 = livre
        data_fim: dataFim,
        modo: contratoRef.modo || 'interno',
        multa_cancelamento_pct: contratoRef.multa_cancelamento_pct || 10,
        horarios: diasPadraoSM(aulasPorSemana).map(d => ({ dia_semana: d, hora: '08:00' })),
      };
    }

    const { data: agenda } = await supabase
      .from('agenda_semanal')
      .select('dia_semana, hora')
      .eq('aluno_id', aluno.id)
      .eq('professor_id', professor.id)
      .eq('ativo', true)
      .order('dia_semana');

    if (agenda && agenda.length > 0) {
      const n = Math.max(form.aulas_por_semana, agenda.length);
      const horarios = agenda.slice(0, n).map(slot => ({
        dia_semana: slot.dia_semana,
        hora: (slot.hora || '08:00').slice(0, 5),
      }));
      while (horarios.length < form.aulas_por_semana) {
        horarios.push({ dia_semana: null, hora: '08:00' });
      }
      form = { ...form, aulas_por_semana: horarios.length, horarios };
    }

    const agendaExiste = !!(agenda && agenda.length > 0);
    const planoId = contratoRef?.plano_id || form.plano_id;
    const temDados = agendaExiste && !!planoId && !!contratoRef?.id;

    if (temDados) {

      const { data: cf } = await supabase.from('contratos').select('*').eq('id', contratoRef.id).maybeSingle();
      const planoObj = planos.find(p => p.id === planoId);
      const aulasSemana = form.aulas_por_semana || 1;
      const precoMensal = Number(cf?.preco_negociado || 0) || (Number(form.valor_aula || 0) * aulasSemana * 4);
      const valorAula = Number(cf?.total_aulas) > 0 ? Math.round(precoMensal / Number(cf.total_aulas))
        : aulasSemana > 0 ? Math.round(precoMensal / (aulasSemana * 4)) : 0;
      setRevisaoContrato({
        contratoId: contratoRef.id,
        alunoId: aluno.id,
        alunoNome: aluno.nome,
        plano: planoObj,
        agenda,
        valorAula,
        precoMensal,
        aulasSemana,
        dataInicio: cf?.data_inicio || form.data_inicio,
        dataFim: cf?.data_fim || form.data_fim,
        antecedencia: cf?.cancelamento_livre === true ? 0 : (cf?.antecedencia_cancelamento_horas || 24),   // v144: 0 = livre
        temLivre: !!cf && 'cancelamento_livre' in cf,
        reposicao: cf?.reposicao_permitida ?? false,
        limiteReposicao: cf?.limite_reposicao || 1,
        formParaWizard: form,
      });
      setModalRevisaoContrato(true);
      return;
    }

    setFormContrato(form);
    setWizardPreenchido(false);
    setAgendaJaDefinida(agendaExiste);
    setWizardStep(1);
    setModalContrato(true);
  }

  async function confirmarContratoFormal() {
    if (!revisaoContrato) return;
    setSalvandoRevisao(true);
    try {
      const { contratoId, alunoId, antecedencia, reposicao, limiteReposicao, temLivre } = revisaoContrato;

      let { error } = await supabase.from('contratos').update({
        modo: 'formal', status_aceite: 'pendente',
        ...(antecedencia > 0 ? { antecedencia_cancelamento_horas: antecedencia } : {}),
        ...(temLivre ? { cancelamento_livre: !(antecedencia > 0) } : {}),   // v144: 0 = livre (sem prazo combinado)
        reposicao_permitida: reposicao,
        limite_reposicao: limiteReposicao,
      }).eq('id', contratoId);

      if (error && (error.message.includes('antecedencia') || error.message.includes('reposicao') || error.message.includes('schema cache'))) {

        ({ error } = await supabase.from('contratos').update({ modo: 'formal', status_aceite: 'pendente' }).eq('id', contratoId));
        if (!error) console.warn('Colunas antecedencia_cancelamento_horas / reposicao_permitida / limite_reposicao não existem. Execute as migrations necessárias.');
      }

      if (error) { toastSM('Erro ao confirmar acordo: ' + error.message); return; }

      await supabase.from('notificacoes').insert({
        aluno_id: alunoId, professor_id: professor.id, tipo: 'contrato',
        mensagem: 'Seu professor atualizou o acordo. Abra o app para conferir e confirmar.',
      });
      setModalRevisaoContrato(false); setRevisaoContrato(null);
      loadAlunos();
    } finally { setSalvandoRevisao(false); }
  }

  function abrirWizardParaAlterar() {
    if (!revisaoContrato) return;
    setModalRevisaoContrato(false);
    setAlunoSelecionado({ id: revisaoContrato.alunoId, nome: revisaoContrato.alunoNome });
    setFormContrato(revisaoContrato.formParaWizard || defaultFormContrato);
    setWizardPreenchido(false);
    setAgendaJaDefinida(!!(revisaoContrato.agenda?.length > 0));
    setWizardStep(1);
    setModalContrato(true);
  }

  function onPlanoChange(plano_id) {
    const p = planos.find(x => x.id === plano_id);
    if (p) {
      const fim = new Date(); fim.setMonth(fim.getMonth() + p.duracao_meses);
      setFormContrato(f => ({ ...f, plano_id, duracao_meses: p.duracao_meses, data_fim: fim.toISOString().split('T')[0] }));
    } else { setFormContrato(f => ({ ...f, plano_id: '', duracao_meses: 1, data_fim: '' })); }
  }

  function onAulasPorSemanaChange(n) {
    setFormContrato(f => {
      // mantem o que o professor ja ajustou; o resto nasce fixo, no mesmo horario da primeira aula
      const horaBase = (f.horarios && f.horarios[0] && f.horarios[0].hora) || '08:00';
      const padrao = diasPadraoSM(n);
      const novos = Array.from({ length: n }, (_, i) => f.horarios[i] || { dia_semana: padrao[i], hora: horaBase });
      return { ...f, aulas_por_semana: n, horarios: novos };
    });
  }

  async function salvarContrato() {
    if (!formContrato.plano_id) { toastSM('Selecione um plano'); return; }
    if (!formContrato.valor_aula || Number(formContrato.valor_aula) <= 0) { toastSM('Informe o valor por aula'); return; }
    if (!formContrato.data_fim) { toastSM('Data de término inválida'); return; }
    if (!dataValidaSM(formContrato.data_inicio) || !dataValidaSM(formContrato.data_fim)) { toastSM('Datas do acordo inválidas — confira o ano.'); return; }
    if (!agendaLivre) {
      const temSlot = (formContrato.horarios || []).some(h => h.dia_semana !== null && h.hora);
      if (!temSlot && !confirm('Agenda fixa selecionada, mas nenhum dia foi definido nos slots (todos estão como Flexível). As aulas NÃO serão geradas no calendário — continuar mesmo assim?')) return;
    }
    const jaRealizadasAnterior = formContrato.em_andamento ? (Number(formContrato.aulas_ja_realizadas) || 0) : 0;
    const precoMensal = Number(formContrato.valor_aula) * Number(formContrato.aulas_por_semana) * 4;
    const totalAulasContrato = (formContrato.aulas_por_semana || 0) * (formContrato.duracao_meses || 1) * 4;
    // Etapa 4 (02/10): este assistente cria um PACOTE de N meses (total_aulas = N meses de aulas) e
    // lança N parcelas de um mês cada (ou uma cobrança do total). O acordo tem de dizer o TOTAL real
    // (valor do mês × meses) e o tipo 'pacote'; antes gravava só um mês e o texto dizia R$ 800 para
    // um aluno que pagaria 6 × R$ 800 — o banco recusa esse aceite (acordo_inconsistente).
    const mesesContrato = Math.max(1, Number(formContrato.duracao_meses) || 1);
    const totalContrato = +(precoMensal * mesesContrato).toFixed(2);
    // v149: com um pacote na fila, este assistente antigo criaria um terceiro pacote (e a fila viraria dois ativos)
    const { data: filaK } = await supabase.from('contratos').select('id').eq('aluno_id', alunoSelecionado.id).eq('status', 'proximo').limit(1);
    if (filaK && filaK.length) { toastSM('Este aluno já tem um próximo pacote combinado. Ele começa sozinho depois da última aula do atual.'); return; }
    await supabase.from('contratos').update({ status: 'encerrado' }).eq('aluno_id', alunoSelecionado.id).eq('status', 'ativo');
    const insertBase = {
      aluno_id: alunoSelecionado.id, plano_id: formContrato.plano_id, professor_id: professor.id,
      preco_negociado: totalContrato, tipo_cobranca: 'pacote', data_inicio: formContrato.data_inicio, data_fim: formContrato.data_fim,
      status: 'ativo', total_aulas: totalAulasContrato,
      aulas_por_semana: Number(formContrato.aulas_por_semana) || 0,
      reposicao_permitida: !!formContrato.reposicao_permitida,
      limite_reposicao: Number(formContrato.limite_reposicao) || 0,
      antecedencia_cancelamento_horas: Number(formContrato.antecedencia_cancelamento_horas) || 24,
      // v144: "Livre (sem prazo)" — avisou antes do horario da aula, nao conta (so' com o SQL da v144; sem ele, 24h)
      ...(Number(formContrato.antecedencia_cancelamento_horas) === 0 ? { cancelamento_livre: true } : {}),
    };
    const taxaAcad = Number(formContrato.taxa_academia) || 0;
    const modoContrato = formContrato.modo || 'interno';
    const multaPct = modoContrato === 'formal' && formContrato.duracao_meses > 1 ? (Number(formContrato.multa_cancelamento_pct) || 0) : 0;
    let { data: contrato, error } = await supabase.from('contratos').insert({ ...insertBase, taxa_academia: taxaAcad, aulas_realizadas_anterior: jaRealizadasAnterior, modo: modoContrato, multa_cancelamento_pct: multaPct }).select().single();
    if (error && /cancelamento_livre/.test(String(error.message || '')) && 'cancelamento_livre' in insertBase) {
      delete insertBase.cancelamento_livre;   // banco ainda sem a coluna da v144: grava como antes
      ({ data: contrato, error } = await supabase.from('contratos').insert({ ...insertBase, taxa_academia: taxaAcad, aulas_realizadas_anterior: jaRealizadasAnterior, modo: modoContrato, multa_cancelamento_pct: multaPct }).select().single());
    }
    if (error && error.message && error.message.includes('aulas_por_semana')) {
      const { aulas_por_semana: _aps, ...baseSemAPS } = insertBase;
      ({ data: contrato, error } = await supabase.from('contratos').insert({ ...baseSemAPS, taxa_academia: taxaAcad, aulas_realizadas_anterior: jaRealizadasAnterior, modo: modoContrato, multa_cancelamento_pct: multaPct }).select().single());
      if (!error) console.warn('\u26a0\ufe0f Coluna aulas_por_semana nao existe. Execute: ALTER TABLE contratos ADD COLUMN IF NOT EXISTS aulas_por_semana integer DEFAULT 0;');
    }
    if (error && error.message && (error.message.includes('modo') || error.message.includes('multa_cancelamento_pct'))) {

      ({ data: contrato, error } = await supabase.from('contratos').insert({ ...insertBase, taxa_academia: taxaAcad, aulas_realizadas_anterior: jaRealizadasAnterior }).select().single());
      if (!error) console.warn('Colunas modo/multa_cancelamento_pct não existem. Execute: ALTER TABLE contratos ADD COLUMN IF NOT EXISTS modo text DEFAULT \'interno\'; ALTER TABLE contratos ADD COLUMN IF NOT EXISTS multa_cancelamento_pct numeric DEFAULT 0;');
    }
    if (error && error.message && error.message.includes('aulas_realizadas_anterior')) {

      ({ data: contrato, error } = await supabase.from('contratos').insert({ ...insertBase, taxa_academia: taxaAcad }).select().single());
      if (!error) console.warn('Coluna aulas_realizadas_anterior não existe. Execute: ALTER TABLE contratos ADD COLUMN IF NOT EXISTS aulas_realizadas_anterior integer DEFAULT 0;');
    }
    if (error && error.message && error.message.includes('taxa_academia')) {

      ({ data: contrato, error } = await supabase.from('contratos').insert(insertBase).select().single());
      if (!error) console.warn('Coluna taxa_academia não existe ainda. Execute: ALTER TABLE contratos ADD COLUMN taxa_academia numeric DEFAULT 0;');
    }
    if (error) { toastSM('Erro ao salvar acordo: ' + error.message); return; }
    if (contrato) {
 { const rc = await recalcularCreditos(supabase, alunoSelecionado.id); if (rc.error) toastSM('Créditos: '+ rc.error.message); }
      const tipoCobranca = formContrato.tipo_cobranca || 'mensal';
      if (tipoCobranca === 'mensal') {
        for (let i = 0; i < formContrato.duracao_meses; i++) {
          const mesBase = new Date(formContrato.data_inicio+'T12:00:00'); mesBase.setMonth(mesBase.getMonth()+i);
          const p = n => String(n).padStart(2,'0');
          const refMes = `${mesBase.getFullYear()}-${p(mesBase.getMonth()+1)}`;
          const { data: pagEx } = await supabase.from('pagamentos').select('id').eq('aluno_id', alunoSelecionado.id).eq('referencia_mes', refMes).eq('status','pendente').limit(1).maybeSingle();
          if (!pagEx) await supabase.from('pagamentos').insert({ aluno_id: alunoSelecionado.id, professor_id: professor.id, contrato_id: contrato.id, valor: precoMensal, tipo: 'aguardando', status: 'pendente', referencia_mes: refMes, data_pagamento: null });
        }
        await supabase.from('notificacoes').insert({ aluno_id: alunoSelecionado.id, professor_id: professor.id, tipo: 'renovacao', mensagem: `Cobrança do pacote de ${fmtBRL(totalContrato)} configurada — ${mesesContrato} parcela(s) de ${fmtBRL(precoMensal)}.` });
      } else {
        const totalValor = totalContrato;
        const refMes = formContrato.data_inicio.slice(0,7);
        const { data: pagEx } = await supabase.from('pagamentos').select('id').eq('aluno_id', alunoSelecionado.id).eq('referencia_mes', refMes).eq('status','pendente').limit(1).maybeSingle();
        if (!pagEx) await supabase.from('pagamentos').insert({ aluno_id: alunoSelecionado.id, professor_id: professor.id, contrato_id: contrato.id, valor: totalValor, tipo: 'aguardando', status: 'pendente', referencia_mes: refMes, data_pagamento: null });
        await supabase.from('notificacoes').insert({ aluno_id: alunoSelecionado.id, professor_id: professor.id, tipo: 'renovacao', mensagem: `Cobrança total de ${fmtBRL(totalValor)} gerada — confirme quando receber.` });
      }

      const horariosValidos = agendaLivre ? [] : (formContrato.horarios || [])
        .filter(h => h.dia_semana !== null && h.hora)
        .filter((h, idx, arr) => arr.findIndex(x => x.dia_semana === h.dia_semana && x.hora === h.hora) === idx);
      if (agendaLivre || horariosValidos.length === 0) {
        // agenda livre OU agenda fixa sem nenhum dia definido: nao gera aulas,
        // mas precisa creditar o total do contrato (antes caia no vazio e ficava 0/0)
        await recalcularCreditos(supabase, alunoSelecionado.id);
      } else {
        await supabase.from('agenda_semanal').update({ ativo: false }).eq('aluno_id', alunoSelecionado.id);
        await supabase.from('agenda_semanal').insert(horariosValidos.map(h => ({ aluno_id: alunoSelecionado.id, professor_id: professor.id, dia_semana: h.dia_semana, hora: h.hora, ativo: true })));
        const aulas = [];

        const inicio = jaRealizadasAnterior > 0
          ? (() => { const d = new Date(); d.setHours(12,0,0,0); return d; })()
          : new Date(formContrato.data_inicio + 'T12:00:00');
        const fim = new Date(formContrato.data_fim + 'T12:00:00');
        window.__loopSM = 0; const atual = new Date(inicio);
        // O pacote nao pode gerar mais aulas do que foi contratado. Antes o laco ia ate'
        // a data_fim sem teto, e ainda marcava como "realizada" tudo que caia no passado —
        // isso somava com as aulas_realizadas_anterior e o aluno aparecia com 13 de 8.
        // Aula so' vira realizada quando o professor confirma na faixa vermelha.
        const tetoAulas = Math.max(0, (Number(totalAulasContrato) || 0) - jaRealizadasAnterior);
        while (atual <= fim && aulas.length < tetoAulas && (window.__loopSM = (window.__loopSM || 0) + 1) < 800) {
          for (const h of horariosValidos) {
            if (atual.getDay() === h.dia_semana && aulas.length < tetoAulas) {
              const dataStr = atual.toISOString().split('T')[0];
              aulas.push({ aluno_id: alunoSelecionado.id, professor_id: professor.id, contrato_id: contrato.id, data: dataStr, hora: h.hora, status: 'agendada', consome_credito: false });
            }
          }
          atual.setDate(atual.getDate() + 1);
        }
        if (aulas.length > 0) {
          // NAO destrutivo: so insere o que ainda nao existe (mesma data+hora).
          // O delete anterior apagava remarcacoes e aulas ja lancadas.
          const chaveAula = a => `${a.data}|${(a.hora || '').slice(0,5)}`;
          const { data: existentes } = await supabase.from('aulas').select('data,hora').eq('aluno_id', alunoSelecionado.id);
          const jaTem = new Set((existentes || []).map(chaveAula));
          const novas = aulas.filter(a => !jaTem.has(chaveAula(a)));
          if (novas.length > 0) await supabase.from('aulas').insert(novas);
          await recalcularCreditos(supabase, alunoSelecionado.id);
        }

        const jaPageas = Math.min(formContrato.parcelas_ja_pagas || 0, formContrato.duracao_meses);
        if (jaPageas > 0) {
          const { data: pagsParaMarcar } = await supabase.from('pagamentos').select('id').eq('aluno_id', alunoSelecionado.id).eq('status','pendente').order('referencia_mes').limit(jaPageas);
          if (pagsParaMarcar?.length > 0) {
            // Pago ANTES do cadastro (migracao): sem meio (ninguem disse como foi pago — antes
            // gravava "dinheiro") e sem data (o Financeiro pede para confirmar). confirmado_via
            // 'migracao' e' o caminho que o banco aceita do app (sql/pagamento_tomadas.sql).
            await supabase.from('pagamentos').update({ status: 'pago', tipo: 'migracao', confirmado_via: 'migracao', data_pagamento: null }).in('id', pagsParaMarcar.map(p => p.id));
          }
        }
      }

      if (modoContrato === 'formal') {
        await supabase.from('contratos').update({ status_aceite: 'pendente' }).eq('id', contrato.id);
        await supabase.from('notificacoes').insert({ aluno_id: alunoSelecionado.id, professor_id: professor.id, tipo: 'contrato', mensagem: `Seu acordo está pronto para você conferir e confirmar no app.` });
      }
    }
    setModalContrato(false); setWizardStep(1); setWizardPreenchido(false); setAgendaLivre(false); setAgendaJaDefinida(false); loadAlunos();
  }

  // Desvinculado nao aparece na lista normal — e' o efeito que o professor queria ao
  // tocar em encerrar. Mas continua existindo, e da' pra encontrar pelo filtro.
  const desvinculados = alunos.filter(a => a.desvinculado_em);
  // v142 (item E): entrou pelo link "Cadastrar varios de uma vez" (tem conta) e ainda nao tem acordo
  // nenhum — fica numa secao propria em cima, com "Completar", e sai da lista de baixo.
  const esperandoCompletar = alunos.filter(a => a.user_id && !a.desvinculado_em && !(a.contratos || []).length);
  const idsEsperando = new Set(esperandoCompletar.map(a => a.id));
  const alunosFiltrados = alunos.filter(a => {
    if (filtroStatus === 'desvinculados') return !!a.desvinculado_em;
    if (a.desvinculado_em) return false;
    if (idsEsperando.has(a.id)) return false;
    if (filtroStatus === 'todos') return true;
    const s = getStatusAluno(a);
    if (filtroStatus === 'ativo') return s.color === 'green';
    if (filtroStatus === 'alerta') return s.color === 'yellow' || s.color === 'orange' || s.color === 'red' || s.color === 'blue';
    if (filtroStatus === 'sem_plano') return s.color === 'gray';
    return true;
  });

  return (
    <div className="module-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div><h1 className="text-2xl font-bold text-gray-800">Alunos</h1><p className="text-gray-500 text-sm">{alunos.length} aluno{alunos.length !== 1 ? 's' : ''} cadastrado{alunos.length !== 1 ? 's' : ''}</p></div>
        <div className="flex flex-wrap gap-2">
          {/* UMA PORTA SO'. Com a lista vazia e' literalmente uma: o link de convite
              generico nao aparece, porque nao ha' ninguem para convidar — ele vive no
              fim do cadastro e na ficha de quem ainda nao entrou. O microfone saiu: o
              ditado por voz ainda nao existe, e botao que promete e nao cumpre custa
              mais confianca do que entrega. */}
          {alunos.length > 0 && <button onClick={() => { uso.acao('alunos.novo_botao'); setModalMigracao(true); }}
            style={{display:'flex', alignItems:'center', gap:'8px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', borderRadius:'12px', padding:'12px 20px', fontWeight:'800', fontSize:'14px', cursor:'pointer', boxShadow:'0 6px 16px rgba(99,102,241,0.35)', whiteSpace:'nowrap'}}>
            <Ico n="user-plus" s={16} c="white" /> Novo aluno
          </button>}
          {alunos.length > 0 && (
            <button onClick={() => { uso.acao('alunos.link_geral', { origem: 'topo' }); gerarLinkConvite(); }}
              style={{border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', borderRadius:'12px', padding:'12px 16px', fontWeight:'700', fontSize:'13px', cursor:'pointer', whiteSpace:'nowrap', minHeight:'44px'}}>
              {/* revisao v142: rotulo curto — o longo nao cabia ao lado do "Novo aluno" a 375 px (3 linhas de botao) */}
              <Ico n="users-plus" /> Cadastrar vários
            </button>
          )}
          {/* Aula em grupo mora na tela Turmas; no celular ela so' e' alcancavel por aqui. */}
          {onIrParaTurmas && (
            <button onClick={() => { uso.acao('alunos.ir_turmas'); onIrParaTurmas(); }}
              style={{border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', borderRadius:'12px', padding:'12px 16px', fontWeight:'700', fontSize:'13px', cursor:'pointer', whiteSpace:'nowrap'}}>
              <Ico n="users" /> Turmas de treino
            </button>
          )}
        </div>
      </div>

      {alunos.length > 0 && <div className="flex gap-2 mb-6 flex-wrap">
        {[{v:'todos',l:'Todos'},{v:'ativo',l:'Ativos'},{v:'alerta',l:'Atenção'},{v:'sem_plano',l:'Sem plano'}]
          .concat(desvinculados.length ? [{v:'desvinculados', l:`Desvinculados (${desvinculados.length})`}] : [])
          .map(f => (
          <button key={f.v} onClick={() => { uso.acao('alunos.filtro', { filtro: f.v }); setFiltroStatus(f.v); }} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filtroStatus===f.v?'bg-indigo-600 text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f.l}</button>
        ))}
      </div>}

      {pedidosAbertos.length > 0 && filtroStatus !== 'desvinculados' && (
        <div data-pedidos-abertos="1" style={{background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'16px', padding:'14px 16px', marginBottom:'16px'}}>
          <div style={{fontSize:'14px', fontWeight:'800', color:'#16181D'}}>
            <Ico n="message-2" s={15} c="#2F6BFF" /> {pedidosAbertos.length === 1 ? 'Pedido de aluno para completar' : `Pedidos de alunos para completar (${pedidosAbertos.length})`}
          </div>
          <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>
            O aluno já mandou o combinado. Toque em Completar, confira e crie — ele vê no app na hora.
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'8px', marginTop:'10px'}}>
            {pedidosAbertos.map(pd => {
              const L = linhasDaProposta(pd.proposta || {}, 'ele');
              return (
                <div key={pd.code} style={{display:'flex', alignItems:'center', gap:'10px', background:'white', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'10px 12px'}}>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{pd.aluno_nome || 'Aluno'}</div>
                    <div style={{fontSize:'12px', color:'#6E7480'}}>{(L.modalidade ? L.modalidade + ' · ' : '') + L.agenda}</div>
                  </div>
                  <button onClick={() => abrirPedido(pd, 'alunos')}
                    style={{minHeight:'44px', padding:'10px 16px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer', flexShrink:0}}>
                    Completar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {esperandoCompletar.length > 0 && filtroStatus !== 'desvinculados' && (
        <div style={{background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'16px', padding:'14px 16px', marginBottom:'16px'}}>
          <div style={{fontSize:'14px', fontWeight:'800', color:'#16181D'}}>
            <Ico n="hourglass" s={15} c="#D97706" /> Esperando você completar ({esperandoCompletar.length})
          </div>
          <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'3px', lineHeight:1.5}}>
            Entraram pelo seu link de cadastro. Toque em Completar e escolha o pacote e os dias de cada um — se já tinha cadastrado a pessoa, o Completar junta as duas fichas.
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:'8px', marginTop:'10px'}}>
            {esperandoCompletar.map(a => (
              <div key={a.id} style={{display:'flex', alignItems:'center', gap:'10px', background:'white', border:'1px solid #FDE68A', borderRadius:'12px', padding:'10px 12px'}}>
                {/* Tocar no nome abre a ficha (ex.: alguem que nao e' seu aluno entrou pelo link — la' da' para encerrar). */}
                <div onClick={() => { uso.acao('alunos.completar_ficha'); setFichaAluno(a); }} role="button" style={{flex:1, minWidth:0, cursor:'pointer', minHeight:'44px', display:'flex', flexDirection:'column', justifyContent:'center'}}>
                  <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.nome || 'Aluno'}</div>
                  <NomeNoAppSM aluno={a} nomes={nomesNoApp} style={{fontSize:'12px'}} />
                  <div style={{fontSize:'12px', color:'#6E7480'}}>{a.telefone ? <><Ico n="brand-whatsapp" s={12} c="#0E9F6E" /> {a.telefone}</> : 'sem WhatsApp'}</div>
                </div>
                <button onClick={() => { uso.acao('alunos.completar_abrir'); setFichaCompletar({ id: a.id, nome: a.nome, telefone: a.telefone, modalidade: a.modalidade }); }}
                  style={{minHeight:'44px', padding:'10px 16px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer', flexShrink:0}}>
                  Completar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {filtroStatus === 'desvinculados' && (
        <div style={{background:'#F4F5F7', border:'1px solid #E2E4E9', borderRadius:'12px', padding:'12px 14px', marginBottom:'16px', fontSize:'13px', color:'#3D424C'}}>
          Estes alunos não treinam mais com você. O histórico foi preservado — se algum voltar, toque em <strong>Reativar</strong> e crie um pacote novo.
        </div>
      )}

      {/* Lista vazia = primeiro acesso do professor. E' a porta mais importante do app:
          tem que levar ao wizard de contrato, nunca ao formulario antigo sem contrato. */}
      {/* So' tem gente esperando completar (todos entraram pelo link): a secao amarela ja' e' a lista. */}
      {alunosFiltrados.length === 0 && esperandoCompletar.length > 0 && filtroStatus === 'todos' ? null
      : alunosFiltrados.length === 0 ? (
        <Card className="text-center py-12">
          <div className="mb-3"><Ico n="users" s={34} c="#C7CAD1" /></div>
          <h3 className="font-bold text-gray-700 mb-1">{alunos.length === 0 ? 'Nenhum aluno ainda' : 'Nenhum aluno neste filtro'}</h3>
          {alunos.length === 0 && <p className="text-sm text-gray-500 mb-4">Cadastre o primeiro — leva menos de um minuto.</p>}
          {alunos.length === 0 ? <Button onClick={() => { uso.acao('alunos.novo_vazio'); setModalMigracao(true); }}><Ico n="user-plus" s={15} /> Cadastrar primeiro aluno</Button>
            : <Button variant="secondary" onClick={() => setFiltroStatus('todos')}>Ver todos</Button>}
          {alunos.length === 0 && (
            <div style={{marginTop:'14px'}}>
              <button onClick={() => { uso.acao('alunos.link_geral', { origem: 'lista_vazia' }); gerarLinkConvite(); }}
                style={{minHeight:'44px', border:'none', background:'none', color:'#2F6BFF', fontWeight:'700', fontSize:'14px', cursor:'pointer'}}>
                <Ico n="users-plus" /> Tem vários alunos? Cadastrar vários de uma vez
              </button>
            </div>
          )}
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {alunosFiltrados.map(a => {
            const contrato = a.contratos?.find(c => c.status === 'ativo');
            const credito = a.creditos?.[0];

            const aulasAluno = a.aulas || [];

            const now = new Date(); const pad2m = n => String(n).padStart(2,'0');
            const mesAtual = `${now.getFullYear()}-${pad2m(now.getMonth()+1)}`;
            const hojeStr = `${now.getFullYear()}-${pad2m(now.getMonth()+1)}-${pad2m(now.getDate())}`;
            const aulasMes = aulasAluno.filter(au => au.data && au.data.startsWith(mesAtual));
            const totalMes = aulasMes.length;
            const realizadasMes = aulasMes.filter(au => au.status === 'realizada').length;

            const realizadasTotal = aulasAluno.filter(au => au.status === 'realizada').length;
            const totalPlano = aulasAluno.filter(au => au.status === 'realizada' || au.status === 'agendada').length;

            const aulasAtrasadas = aulasAluno.filter(au => au.status === 'agendada' && au.data < hojeStr).length;
            const status = getStatusAluno(a);
            return (
              <Card key={a.id} style={{padding:'14px 16px', position:'relative'}}>
                {/* Linha do aluno no padrao Trainerize: nome, uma linha de contexto, barra de
                    saldo e UM botao. O resto vai para o "···". Chip so' quando ha' algo a fazer. */}
                {(() => {
                  const mensal = contrato?.tipo_cobranca === 'mensal';
                  const credCalc = contrato ? calcularCreditos(contrato, aulasAluno.filter(x => x.contrato_id === contrato.id)) : null;
                  const totalC = credCalc ? (credCalc.total || totalPlano) : 0;
                  const usadoC = credCalc ? credCalc.usado : 0;
                  const restC = Math.max(0, totalC - usadoC);
                  const pct = totalC > 0 ? Math.min(100, Math.round((usadoC / totalC) * 100)) : 0;
                  const corBarra = restC <= 1 ? '#DC2626' : restC <= 2 ? '#D97706' : '#2F6BFF';
                  const aberto = menuAluno === a.id;
                  const modoFormal = (contrato?.modo || 'interno') === 'formal';
                  const acordoPendente = contrato && modoFormal && contrato.status_aceite !== 'aceito';
                  const proximoK = (a.contratos || []).find(k => k.status === 'proximo') || null;
                  // Um chip de acao, em ordem de urgencia. Aluno em dia e no app: nenhum.
                  // Ordem por urgencia — mas ANTES de tudo, "ainda nao aceitou o convite":
                  // nao faz sentido pedir pra confirmar pagamento ou renovar pacote de
                  // quem nem entrou no app. Sem isso, um aluno recem-convidado (relato do
                  // Cadu em 23/09) ja' aparecia com "Confirmar pagamento" — confuso, porque
                  // nao ha' nada a confirmar ainda.
                  let chip = null;
                  if (!contrato) chip = { k: 'configurar_plano', t: 'Configurar plano', ic: 'clipboard-list', cor: '#DC2626', bg: '#FEE2E2', on: () => setAlunoParaRenovar(a) };
                  else if (!a.user_id) chip = { k: 'convidar', t: a.convite_enviado_em ? 'Reenviar convite do app' : 'Convidar para o app', ic: 'send', cor: '#1C43AE', bg: '#E7EDFF', on: () => gerarLinkConviteAluno(a) };
                  else if (acordoPendente) chip = { k: 'acordo_pendente', t: 'Acordo aguardando o aluno', ic: 'hourglass', cor: '#B45309', bg: '#FEF3C7', on: () => gerarLinkConviteAluno(a) };
                  // O aluno tem um botao "Ja' paguei" do lado dele — so' vira urgente (vermelho,
                  // "Confirmar pagamento") depois que ele avisa. Antes disso e' so' uma cobranca
                  // em aberto esperando o Pix cair; o professor pode registrar na mao se quiser,
                  // mas nao ha' nada pra "confirmar" ainda.
                  else if (status.label === 'Aguardando sua confirmação') chip = { k: 'confirmar_pagamento', t: 'Confirmar pagamento', ic: 'credit-card', cor: '#B91C1C', bg: '#FEE2E2', on: () => onIrParaFinanceiro && onIrParaFinanceiro(a.id) };
                  // v149: pacote na fila — no lugar de "combinar renovacao"/"aguardando pagamento" (que e' do pacote da fila
                  // e ainda nao venceu). Tocar abre a ficha. Sem isto o professor renovava de novo (terceiro pacote).
                  // v155: qualquer cobranca que conta (de outro contrato, ou a do proximo ja' vencida) passa na frente
                  else if (proximoK && status.label !== 'Pagamento pendente') chip = proximoK.status_aceite !== 'aceito'
                    // v155: aluno com conta ainda nao confirmou o pacote da fila
                    ? { k: 'proximo_pacote', t: 'Próximo pacote: esperando o aluno confirmar', ic: 'hourglass', cor: '#B45309', bg: '#FEF3C7', on: () => setAlunoParaRenovar(a) }
                    : { k: 'proximo_pacote', t: `Próximo pacote: começa ${fmtDiaMes(proximoK.data_inicio)}`, ic: 'calendar-check', cor: '#0A6F4D', bg: '#EFFAF5', on: () => setAlunoParaRenovar(a) };
                  else if (status.label === 'Pagamento pendente') chip = { k: 'aguardando_pagamento', t: 'Aguardando pagamento', ic: 'clock', cor: '#3D424C', bg: '#F0F1F4', on: () => onIrParaFinanceiro && onIrParaFinanceiro(a.id) };
                  else if (status.label === 'Plano expirado') chip = { k: 'renovar', t: 'Renovar pacote', ic: 'refresh', cor: '#B45309', bg: '#FEF3C7', on: () => setAlunoParaRenovar(a) };
                  else if (!mensal && totalC > 0 && restC === 0) chip = { k: 'renovar', t: 'Pacote terminou — renovar', ic: 'refresh', cor: '#B45309', bg: '#FEF3C7', on: () => setAlunoParaRenovar(a) };
                  else if (status.label === 'Poucos créditos') chip = { k: 'poucos_creditos', t: `Só ${restC} aula${restC === 1 ? '' : 's'} — combinar próximo pacote`, ic: 'alert-triangle', cor: '#B45309', bg: '#FEF3C7', on: () => setAlunoParaRenovar(a) };
                  // Mensalidade: o professor pensa por MES ("12 aulas", 3x por semana) — nao no
                  // total da agenda gerada (37). Mostra feitas/previstas do mes corrente.
                  const mesRef = new Date().toLocaleDateString('pt-BR', { month: 'long' });
                  const aulasDoMes = aulasAluno.filter(x => x.data && x.data.startsWith(mesAtual) && x.status !== 'cancelada_professor' && x.status !== 'cancelada_aluno' && x.status !== 'cancelada_forca_maior');
                  const feitasDoMes = aulasDoMes.filter(x => x.status === 'realizada' || x.status === 'falta').length;
                  const linha2 = !contrato ? (a.modalidade || 'Sem plano')
                    : mensal ? `${a.modalidade ? a.modalidade + ' · ' : ''}${fmtBRL(contrato.valor_mensal || contrato.preco_negociado, {semCentavos:true})}/mês · ${feitasDoMes} de ${aulasDoMes.length} aulas em ${mesRef}`
                    : `${a.modalidade ? a.modalidade + ' · ' : ''}${usadoC} de ${totalC} aulas${credCalc?.excedente > 0 ? ` (+${credCalc.excedente} além)` : ''}`;
                  return (
                    <>
                      <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                        <div onClick={() => setFichaAluno(a)} role="button" style={{width:'44px', height:'44px', background:'#E7EDFF', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', color:'#2455D6', fontWeight:'800', flexShrink:0, fontSize:'17px', cursor:'pointer'}}>
                          {(a.nome || '?').trim().charAt(0).toUpperCase()}
                        </div>
                        <div onClick={() => setFichaAluno(a)} role="button" style={{flex:1, minWidth:0, cursor:'pointer'}}>
                          <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                            <span style={{fontWeight:'800', color:'#16181D', fontSize:'15px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.nome}</span>
                            {/* Selo discreto e permanente — diferente do chip de acao (que so' mostra
                                UMA coisa por vez). Fica sempre visivel: "esse aluno ja' esta' no app",
                                independente do que mais estiver pendente (pagamento, renovacao etc).
                                v148: com a linha "no app: <nome>" logo abaixo (check verde), ela faz as vezes do selo. */}
                            {a.user_id && !a.desvinculado_em && !nomeNoAppSM(a, nomesNoApp) && <span title="Aluno com conta no app, vinculado a você" style={{display:'inline-flex', alignItems:'center', gap:'3px', fontSize:'10.5px', fontWeight:'700', color:'#0A6F4D', background:'#DCF5EC', borderRadius:'20px', padding:'2px 7px', flexShrink:0}}><Ico n="circle-check" s={10} /> vinculado</span>}
                            {a.desvinculado_em && <span style={{fontSize:'10.5px', fontWeight:'700', color:'#6E7480', background:'#F0F1F4', borderRadius:'20px', padding:'2px 7px'}}>encerrado</span>}
                            {aulasAtrasadas > 0 && (
                              <button onClick={e => { e.stopPropagation(); setModalAulasAluno({aluno:a, aulas:aulasAluno}); }} title="Aulas passadas sem resposta"
                                style={{display:'inline-flex', alignItems:'center', gap:'3px', background:'#FEE2E2', border:'none', color:'#B91C1C', fontSize:'11px', fontWeight:'800', padding:'2px 7px', borderRadius:'20px', cursor:'pointer'}}>
                                <Ico n="point-filled" s={10} /> {aulasAtrasadas}
                              </button>
                            )}
                          </div>
                          <NomeNoAppSM aluno={a} nomes={nomesNoApp} style={{marginTop:'1px'}} />
                          <div className="num" style={{fontSize:'12.5px', color:'#6E7480', marginTop:'2px', lineHeight:1.35}}>{linha2}</div>
                          {contrato && ((!mensal && totalC > 0) || (mensal && aulasDoMes.length > 0)) && (
                            // As mesmas bolinhas que o aluno ve na tela dele (antes era uma
                            // barrinha so' do professor). No pacote a linha de cima ja' diz
                            // "6 de 8"; aqui vao so' os pontos. No mensal entra o aproveitamento,
                            // que nao aparece em lugar nenhum e e' o sinal de aluno esfriando.
                            <div style={{marginTop:'8px'}}>
                              <PontosAulas contrato={contrato} aulas={aulasAluno} tamanho={9} compacto soPontos={!mensal} />
                            </div>
                          )}
                        </div>
                        {!a.desvinculado_em && (
                          <button onClick={() => abrirAulaAvulsa(a)} title="Registrar uma aula agora"
                            style={{height:'38px', padding:'0 14px', background:'#2F6BFF', color:'white', border:'none', borderRadius:'11px', fontSize:'13px', fontWeight:'800', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'6px', flexShrink:0}}>
                            <Ico n="bolt" s={15} /> Aula
                          </button>
                        )}
                        <button onClick={() => { if (!aberto) uso.acao('alunos.menu'); setMenuAluno(aberto ? null : a.id); }} aria-label="Mais opções"
                          style={{width:'38px', height:'38px', borderRadius:'11px', border:'none', background: aberto ? '#E7EDFF' : '#F0F1F4', color: aberto ? '#2F6BFF' : '#3D424C', cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                          <Ico n="dots" s={18} />
                        </button>
                      </div>

                      {chip && (
                        <button onClick={() => { uso.acao('alunos.chip', { tipo: chip.k }); chip.on(); }}
                          style={{marginTop:'10px', display:'inline-flex', alignItems:'center', gap:'6px', padding:'7px 12px', borderRadius:'20px', border:'none', background:chip.bg, color:chip.cor, fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>
                          <Ico n={chip.ic} s={14} /> {chip.t}
                        </button>
                      )}

                      {aberto && (
                        <div style={{marginTop:'10px', borderTop:'1px solid #F0F1F4', paddingTop:'8px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px'}}>
                          {[
                            { k:'historico', t:'Ver histórico', ic:'history', on: () => setAlunoHistorico(a) },
                            { k:'ficha', t:'Editar ficha', ic:'pencil', on: () => setFichaAluno(a) },
                            { k:'aulas', t:'Aulas e saldo', ic:'calendar-stats', on: () => setModalAulasAluno({aluno:a, aulas:aulasAluno}) },
                            !a.desvinculado_em && !mensal ? (proximoK
                              ? { k:'proximo_pacote_ver', t:`Próximo pacote: começa ${fmtDiaMes(proximoK.data_inicio)}`, ic:'calendar-check', on: () => setAlunoParaRenovar(a) }
                              : { k:'proximo_pacote', t:'Combinar próximo pacote', ic:'calendar-plus', on: () => setAlunoParaRenovar(a) }) : null,
                            contrato && !contrato.preco_reajuste_pendente ? { k:'reajuste', t:'Reajustar valor', ic:'cash-banknote', on: () => abrirReajusteAluno(a, contrato) } : null,
                            contrato && !modoFormal && !ignoradosContrato.has(a.id) ? { k:'mandar_acordo', t:'Mandar acordo', ic:'send', on: () => abrirContrato(a) } : null,
                            a.user_id ? null : { k:'convite', t:'Convite do app', ic:'link', on: () => gerarLinkConviteAluno(a) },
                            a.desvinculado_em ? { k:'reativar', t:'Reativar aluno', ic:'user-check', on: () => revincularAluno(a) } : { k:'encerrar', t:'Encerrar vínculo', ic:'user-off', on: () => confirmarDelete(a), perigo: true },
                          ].filter(Boolean).map(op => (
                            <button key={op.t} onClick={() => { uso.acao('alunos.menu_opcao', { opcao: op.k }); setMenuAluno(null); op.on(); }}
                              style={{display:'flex', alignItems:'center', gap:'8px', padding:'10px 12px', borderRadius:'10px', border:'1px solid #EEF0F4', background:'white', color: op.perigo ? '#B91C1C' : '#3D424C', fontSize:'13px', fontWeight:'600', cursor:'pointer', textAlign:'left'}}>
                              <Ico n={op.ic} s={15} c={op.perigo ? '#DC2626' : '#6E7480'} /> {op.t}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </Card>
            );
          })}
        </div>
      )}

      {modalModoRapido && <ModoRapido professor={professor} planos={planos} onClose={() => setModalModoRapido(false)} onSalvar={() => { setModalModoRapido(false); loadAlunos(); }} />}
      {fichaAluno && <FichaCompleta professor={professor} aluno={fichaAluno} nomesNoApp={nomesNoApp}
         onFechar={() => setFichaAluno(null)} onMudou={() => loadAlunos()} />}
      {modalMigracao && <ModalNovoContrato key={propostaAluno ? String(propostaAluno.pedido || propostaAluno.quando || 'proposta') : 'novo'}
        professor={professor} proposta={propostaAluno}
        onClose={() => { setModalMigracao(false); setPropostaAluno(null); try { localStorage.removeItem('propostaDoAluno'); } catch {} }}
        onSalvou={() => { loadAlunos(); carregarPedidosAbertos(); }} onConvidar={(a) => gerarLinkConviteAluno(a)}
        pedidosAbertos={pedidosAbertos} onAbrirPedido={pd => abrirPedido(pd, 'novo_aluno')}
        onAbrirFicha={(a) => {
          // Revisao v142: o WhatsApp (ou o nome) ja' e' de alguem ativo — abre a ficha dele (la' esta' o link pessoal).
          setModalMigracao(false); setPropostaAluno(null); try { localStorage.removeItem('propostaDoAluno'); } catch {}
          setFichaAluno(alunos.find(x => x.id === a.id) || a);
        }}
        onReativar={async (a) => {
          // Aluno com vinculo encerrado digitado de novo: reativa (historico fica) e ja' abre
          // o pacote novo para ele, em vez de criar uma segunda pessoa (caso Cadu x Marcus, 29/09).
          const { error } = await supabase.rpc('revincular_aluno', { p_aluno_id: a.id });
          if (error) { toastSM('Não foi possível reativar: ' + error.message); return; }
          uso.acao('alunos.reativar', { origem: 'cadastro' });
          setModalMigracao(false);
          await loadAlunos();
          setAlunoParaRenovar({ id: a.id, nome: a.nome, telefone: a.telefone });
        }} />}
      {alunoParaRenovar && <ModalNovoContrato professor={professor} alunoExistente={alunoParaRenovar} jaPagouProximo={renovarJaPagou}
        onClose={() => { setAlunoParaRenovar(null); setRenovarJaPagou(false); }}
        onSalvou={() => loadAlunos()} />}
      {fichaCompletar && <ModalNovoContrato professor={professor} completar={fichaCompletar}
        onClose={() => setFichaCompletar(null)}
        onSalvou={() => loadAlunos()} />}
      {alunoHistorico && <AlunoHistoricoModal aluno={alunoHistorico} professor={professor} onClose={() => setAlunoHistorico(null)} />}

      {}
      {modalRevisaoContrato && revisaoContrato && (() => {
        const R = revisaoContrato;
        const DIAS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
        const fmtData = s => s ? new Date(s+'T12:00').toLocaleDateString('pt-BR',{day:'numeric',month:'short',year:'numeric'}) : '—';
        const agendaStr = (R.agenda||[]).map(s => s.dia_semana !== null && s.dia_semana !== undefined ? `${DIAS[s.dia_semana]} ${(s.hora||'').slice(0,5)}` : 'Livre').join('· ');
        const Row = ({icon, label, children}) => (
          <div style={{display:'flex',alignItems:'flex-start',gap:'12px',padding:'12px 0',borderBottom:'1px solid #F0F1F4'}}>
            <span style={{fontSize:'16px',flexShrink:0,marginTop:'1px'}}>{icon}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:'10px',fontWeight:'700',color:'#9AA0AB',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'4px'}}>{label}</div>
              {children}
            </div>
          </div>
        );
        return (
          <Portal>
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}}>
            <div style={{background:'white',borderRadius:'22px',boxShadow:'0 24px 64px rgba(0,0,0,0.22)',width:'100%',maxWidth:'440px',maxHeight:'92vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
              {}
              <div style={{background:'linear-gradient(135deg,#16181D,#1C43AE)',padding:'22px 24px',flexShrink:0}}>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#A9C2FF',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'6px'}}>Revisão do acordo</div>
                <div style={{fontSize:'20px',fontWeight:'800',color:'white'}}>{R.alunoNome}</div>
                <div style={{fontSize:'12px',color:'#6E9BFF',marginTop:'2px'}}>{R.plano?.nome || 'Plano'} · {R.aulasSemana}×/semana</div>
              </div>

              {}
              <div style={{overflowY:'auto',flex:1,padding:'0 24px'}}>
                <Row icon=""label="Agenda">
                  <div style={{fontSize:'14px',fontWeight:'700',color:'#16181D'}}>{agendaStr || '—'}</div>
                </Row>
                <Row icon=""label="Valor">
                  <div style={{fontSize:'14px',fontWeight:'700',color:'#16181D'}}>
                    R$ {R.valorAula}/aula
                    <span style={{fontWeight:'400',color:'#6E7480',fontSize:'12px'}}> = {fmtBRL(R.precoMensal)}/pacote</span>
                  </div>
                </Row>
                <Row icon=""label="Vigência">
                  <div style={{fontSize:'13px',fontWeight:'600',color:'#16181D'}}>{fmtData(R.dataInicio)} <Ico n="arrow-right" /> {fmtData(R.dataFim)}</div>
                </Row>

                {}
                <Row icon=""label="Aula de reposição">
                  <div style={{display:'flex',alignItems:'center',gap:'10px',marginTop:'2px'}}>
                    <button type="button" onClick={() => setRevisaoContrato(r => ({...r, reposicao: !r.reposicao}))}
                      style={{padding:'6px 14px',borderRadius:'8px',fontSize:'12px',fontWeight:'700',cursor:'pointer',border:R.reposicao?'2px solid #0E9F6E':'1.5px solid #E2E4E9',background:R.reposicao?'#DCF5EC':'white',color:R.reposicao?'#0A5B40':'#6E7480'}}>
 {R.reposicao ? 'Permitida': 'Não permitida'}
                    </button>
                    {R.reposicao && (
                      <div style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',color:'#3D424C'}}>
                        até
                        <select value={R.limiteReposicao} onChange={e => setRevisaoContrato(r => ({...r, limiteReposicao: Number(e.target.value)}))}
                          style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'4px 8px',fontSize:'12px',fontWeight:'700',color:'#16181D',outline:'none'}}>
                          {[1,2,3,4].map(n => <option key={n} value={n}>{n} no plano</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </Row>

                {}
                {R.reposicao && (
                  <Row icon=""label="Aviso mínimo para remarcar">
                    <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginTop:'2px'}}>
                      {[...(R.temLivre ? [{v:0,l:'Livre'}] : []), {v:12,l:'12h'},{v:24,l:'24h'},{v:48,l:'48h'},{v:72,l:'72h'}].map(op => (
                        <button key={op.v} type="button" onClick={() => setRevisaoContrato(r => ({...r, antecedencia: op.v}))}
                          style={{padding:'5px 12px',borderRadius:'8px',fontSize:'12px',fontWeight:'700',cursor:'pointer',border:R.antecedencia===op.v?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:R.antecedencia===op.v?'#E7EDFF':'white',color:R.antecedencia===op.v?'#2F6BFF':'#6E7480'}}>
                          {op.l}
                        </button>
                      ))}
                    </div>
                  </Row>
                )}
                <div style={{paddingBottom:'8px'}} />
              </div>

              {}
              <div style={{padding:'16px 24px',borderTop:'1px solid #F0F1F4',display:'flex',gap:'10px',flexShrink:0}}>
                <button onClick={() => { setModalRevisaoContrato(false); setRevisaoContrato(null); }}
                  style={{padding:'12px 16px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',fontWeight:'600',cursor:'pointer',flexShrink:0}}>
                  Cancelar
                </button>
                <button onClick={abrirWizardParaAlterar}
                  style={{flex:1,padding:'12px',borderRadius:'12px',border:'1.5px solid #C2D4FF',background:'#E7EDFF',color:'#2F6BFF',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="pencil" /> Alterar
                </button>
                <button onClick={confirmarContratoFormal} disabled={salvandoRevisao}
                  style={{flex:2,padding:'12px',borderRadius:'12px',border:'none',background:'linear-gradient(135deg,#0E9F6E,#0E9F6E)',color:'white',fontSize:'13px',fontWeight:'800',cursor:'pointer',opacity:salvandoRevisao?0.7:1}}>
 {salvandoRevisao ? 'Confirmando...': 'Confirmar acordo'}
                </button>
              </div>
            </div>
          </div>
          </Portal>
        );
      })()}

      {}
      {modalAulasAluno && <ModalAulasDetalhes professor={professor} aluno={modalAulasAluno.aluno} aulas={modalAulasAluno.aulas} onClose={()=>{ setModalAulasAluno(null); loadAlunos(); }} onAtualizou={loadAlunos} />}

      {}
      {modalAulaAvulsa && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'20px',boxShadow:'0 20px 60px rgba(0,0,0,0.2)',width:'100%',maxWidth:'400px',padding:'28px'}}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'20px'}}>
              <div style={{width:'42px',height:'42px',borderRadius:'12px',background:'linear-gradient(135deg,#2F6BFF,#2455D6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'22px',flexShrink:0}}><Ico n="bolt" /></div>
              <div>
                <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D'}}>Registrar Aula</div>
                <div style={{fontSize:'12px',color:'#9AA0AB'}}>{formAulaAvulsa.aluno_nome || 'Selecione o aluno'}</div>
              </div>
            </div>
            {!formAulaAvulsa.aluno_id && (
              <div style={{marginBottom:'16px'}}>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Aluno</label>
                <select value={formAulaAvulsa.aluno_id}
                  onChange={e => { const al = alunos.find(a => a.id === e.target.value); setFormAulaAvulsa(f => ({...f, aluno_id: e.target.value, aluno_nome: al?.nome || ''})); }}
                  style={{width:'100%',border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'11px 14px',fontSize:'13px',color:'#16181D',outline:'none',background:'white',boxSizing:'border-box'}}>
                  <option value="">— selecione —</option>
                  {alunos.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                </select>
              </div>
            )}
            <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Data da aula</label>
                <input type="date" value={formAulaAvulsa.data} onChange={e => setFormAulaAvulsa(f => ({...f, data: e.target.value}))}
                  style={{width:'100%',border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'11px 14px',fontSize:'14px',color:'#16181D',outline:'none',boxSizing:'border-box'}} />
              </div>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Horário</label>
                <input type="time" value={formAulaAvulsa.hora} onChange={e => setFormAulaAvulsa(f => ({...f, hora: e.target.value}))}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'11px 14px',fontSize:'14px',color:'#16181D',outline:'none'}} />
              </div>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Observação <span style={{fontWeight:'400',color:'#9AA0AB'}}>(opcional)</span></label>
                <input type="text" placeholder="Ex: treino extra, reposição..." value={formAulaAvulsa.nota} onChange={e => setFormAulaAvulsa(f => ({...f, nota: e.target.value}))}
                  style={{width:'100%',border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'11px 14px',fontSize:'13px',color:'#16181D',outline:'none',boxSizing:'border-box'}} />
              </div>
              <div style={{display:'flex',gap:'10px',marginTop:'4px'}}>
                <button onClick={() => setModalAulaAvulsa(false)}
                  style={{flex:1,padding:'13px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>
                  Cancelar
                </button>
                <button onClick={salvarAulaAvulsa} disabled={salvandoAvulsa}
                  style={{flex:2,padding:'13px',borderRadius:'12px',border:'none',background:'linear-gradient(135deg,#2F6BFF,#2455D6)',color:'white',fontSize:'14px',fontWeight:'800',cursor:'pointer',opacity:salvandoAvulsa?0.7:1}}>
 {salvandoAvulsa ? 'Salvando...': 'Confirmar Aula'}
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {}
      {modalReajusteAluno && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'20px',boxShadow:'0 20px 60px rgba(0,0,0,0.2)',width:'100%',maxWidth:'420px',padding:'28px'}}>
            <div style={{fontSize:'18px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="cash-banknote" /> Reajuste de Preço</div>
            <div style={{fontSize:'13px',color:'#9AA0AB',marginBottom:'20px'}}>{modalReajusteAluno.aluno.nome}</div>
            <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:'12px',padding:'14px 16px',marginBottom:'20px'}}>
              <div style={{fontSize:'12px',color:'#92400E',fontWeight:'700',marginBottom:'8px'}}><Ico n="clipboard-list" /> Acordo atual</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                <div><div style={{fontSize:'10px',color:'#B45309',fontWeight:'600'}}>DESDE</div><div style={{fontSize:'13px',fontWeight:'700',color:'#16181D'}}>{modalReajusteAluno.contrato.data_inicio ? new Date(modalReajusteAluno.contrato.data_inicio+'T12:00:00').toLocaleDateString('pt-BR') : '—'}</div></div>
                <div><div style={{fontSize:'10px',color:'#B45309',fontWeight:'600'}}>{ehMensalAcordo(modalReajusteAluno.contrato) ? 'MENSALIDADE ATUAL' : 'PREÇO ATUAL'}</div><div style={{fontSize:'16px',fontWeight:'800',color:'#B45309'}}>{fmtBRL(baseReajuste(modalReajusteAluno.contrato))}<span style={{fontSize:'11px',fontWeight:'600'}}>{sufixoPrecoAcordo(modalReajusteAluno.contrato)}</span></div></div>
                {reajusteAlunoForm.novoPreco && Number(reajusteAlunoForm.novoPreco) > 0 && (
                  <div><div style={{fontSize:'10px',color:'#0E9F6E',fontWeight:'600'}}>NOVO PREÇO</div>
                    <div style={{fontSize:'16px',fontWeight:'800',color:'#0E9F6E'}}>{fmtBRL(reajusteAlunoForm.percentual ? (baseReajuste(modalReajusteAluno.contrato)*(1+Number(reajusteAlunoForm.percentual)/100)) : reajusteAlunoForm.novoPreco)}<span style={{fontSize:'11px',fontWeight:'600'}}>{sufixoPrecoAcordo(modalReajusteAluno.contrato)}</span></div>
                  </div>
                )}
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Reajuste por percentual (%)</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {[5,10,15,20].map(pct => (
                    <button key={pct} onClick={() => setReajusteAlunoForm({ percentual: String(pct), novoPreco: (baseReajuste(modalReajusteAluno.contrato)*(1+pct/100)).toFixed(2) })}
                      style={{padding:'7px 12px',borderRadius:'8px',fontSize:'12px',fontWeight:'700',cursor:'pointer',border:reajusteAlunoForm.percentual===String(pct)?'2px solid #B45309':'1.5px solid #E2E4E9',background:reajusteAlunoForm.percentual===String(pct)?'#FEF3C7':'white',color:reajusteAlunoForm.percentual===String(pct)?'#B45309':'#6E7480'}}>
                      +{pct}%
                    </button>
                  ))}
                  <input type="number" placeholder="%" value={reajusteAlunoForm.percentual} min="0" max="100" step="0.1"
                    onChange={e => setReajusteAlunoForm({ percentual: e.target.value, novoPreco: e.target.value ? (baseReajuste(modalReajusteAluno.contrato)*(1+Number(e.target.value)/100)).toFixed(2) : reajusteAlunoForm.novoPreco })}
                    style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#16181D',outline:'none',width:'65px'}} />
                </div>
              </div>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Ou informe o valor direto (R$)</label>
                <CampoDinheiro placeholder="0,00" value={reajusteAlunoForm.novoPreco}
                  onValor={v => setReajusteAlunoForm({ novoPreco: v, percentual: '' })}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 14px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
              <div style={{background:'#E7EDFF',border:'1px solid #C2D4FF',borderRadius:'10px',padding:'10px 14px',fontSize:'12px',color:'#1C43AE'}}>
                <Ico n="device-mobile" /> O aluno receberá uma notificação para aceitar ou recusar o novo valor. O preço atual permanece até o aceite.{ehMensalAcordo(modalReajusteAluno.contrato) ? ' Na mensalidade, o valor novo vale a partir da próxima cobrança.' : ''}
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={aplicarReajusteAluno} disabled={salvandoReajusteAluno}
                  style={{flex:1,background:'linear-gradient(135deg,#B45309,#D97706)',color:'white',border:'none',borderRadius:'12px',padding:'13px',fontSize:'14px',fontWeight:'800',cursor:'pointer',opacity:salvandoReajusteAluno?0.7:1}}>
 {salvandoReajusteAluno ? 'Enviando...': 'Enviar para aceite'}
                </button>
                <button onClick={() => setModalReajusteAluno(null)}
                  style={{padding:'13px 20px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {}
      <Modal open={modal} onClose={() => setModal(false)} title={modoEdicao ? 'Editar Aluno' : 'Novo Aluno'}>
        <div className="flex flex-col gap-4">

          {}
          {!modoEdicao && buscaStep === 0 && (
            <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>

              {}
              {'contacts' in navigator && (
                <button onClick={abrirContatos} disabled={buscandoAluno}
                  style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',padding:'16px',borderRadius:'16px',background:'linear-gradient(135deg,#2F6BFF,#2455D6)',color:'white',border:'none',fontSize:'15px',fontWeight:'700',cursor:'pointer',boxShadow:'0 4px 14px rgba(99,102,241,0.35)',width:'100%',opacity:buscandoAluno?0.6:1}}>
                  <span style={{fontSize:'22px'}}><Ico n="id" /></span>
                  {buscandoAluno ? 'Buscando...' : 'Escolher da lista de contatos'}
                </button>
              )}

              {}
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <div style={{flex:1,height:'1px',background:'#E2E4E9'}} />
                <span style={{fontSize:'11px',color:'#9AA0AB',fontWeight:'600'}}>OU DIGITE O NÚMERO</span>
                <div style={{flex:1,height:'1px',background:'#E2E4E9'}} />
              </div>

              {}
              <div style={{display:'flex',gap:'8px',alignItems:'flex-end'}}>
                <div style={{flex:1}}>
                  <Input label="WhatsApp do aluno" placeholder="+55 (11) 99999-9999" value={form.telefone}
                    onChange={e => setForm(f => ({...f, telefone: mascaraTel(e.target.value)}))}
                    onKeyDown={e => { if (e.key === 'Enter') buscarPorTelefone(); }} />
                </div>
                <button onClick={buscarPorTelefone} disabled={buscandoAluno}
                  style={{padding:'10px 18px',borderRadius:'12px',background:'#2F6BFF',color:'white',border:'none',fontWeight:'700',fontSize:'13px',cursor:'pointer',whiteSpace:'nowrap',height:'42px',flexShrink:0,opacity:buscandoAluno?0.6:1}}>
                  {buscandoAluno ? '...' : 'Buscar'}
                </button>
              </div>

              {}
              {!('contacts' in navigator) && (
                <div style={{fontSize:'11px',color:'#9AA0AB',textAlign:'center'}}>
                  <Ico n="device-desktop" /> No celular, você poderá escolher direto da sua lista de contatos.
                </div>
              )}

              {}
              <button onClick={() => { setBuscaStep(1); setAlunoEncontrado(null); }}
                style={{background:'none',border:'none',color:'#2F6BFF',fontSize:'12px',fontWeight:'600',cursor:'pointer',padding:'4px 0',textDecoration:'underline',textUnderlineOffset:'3px'}}>
                Cadastrar sem WhatsApp <Ico n="arrow-right" />
              </button>
            </div>
          )}

          {}
          {(modoEdicao || buscaStep === 1) && (<>
            {}
            {alunoEncontrado && (
              <div style={{borderRadius:'14px',padding:'12px 14px',fontSize:'12px',display:'flex',alignItems:'center',gap:'10px',
                background: alunoEncontrado.user_id ? '#EFFAF5' : '#E7EDFF',
                border: alunoEncontrado.user_id ? '1.5px solid #8FDFC4' : '1.5px solid #C2D4FF',
                color: alunoEncontrado.user_id ? '#0A6F4D' : '#1C43AE'}}>
                {fotoContato
                  ? <img src={fotoContato} alt="" style={{width:'36px',height:'36px',borderRadius:'50%',objectFit:'cover',flexShrink:0,border:'2px solid white',boxShadow:'0 1px 4px rgba(0,0,0,0.15)'}} />
                  : <span style={{fontSize:'20px'}}>{alunoEncontrado.user_id ? '': ''}</span>}
                <div>
                  {alunoEncontrado.user_id
                    ? <><strong>Aluno já tem conta na plataforma!</strong><br/>Dados preenchidos. Cadastre e envie o convite — ele aceitará e ficará vinculado a você.</>
                    : <><strong>Aluno encontrado nos registros.</strong><br/>Dados preenchidos automaticamente. Você pode editar o nome se quiser.</>}
                </div>
              </div>
            )}
            {}
            {!alunoEncontrado && fotoContato && (
              <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',background:'#F7F8FA',borderRadius:'12px',border:'1px solid #E2E4E9'}}>
                <img src={fotoContato} alt="" style={{width:'36px',height:'36px',borderRadius:'50%',objectFit:'cover',border:'2px solid #E2E4E9'}} />
                <span style={{fontSize:'12px',color:'#6E7480'}}>Foto importada dos contatos</span>
              </div>
            )}
            <div style={{display:'flex',gap:'8px',alignItems:'flex-end'}}>
              <div style={{flex:1}}>
                <Input label="Telefone / WhatsApp (opcional)" placeholder="+55 (11) 99999-9999" value={form.telefone}
                  onChange={e => setForm(f => ({...f, telefone: mascaraTel(e.target.value)}))}
                  onKeyDown={e => { if (e.key === 'Enter') buscarPorTelefone(); }} />
              </div>
              {!modoEdicao && (
                <button onClick={buscarPorTelefone} disabled={buscandoAluno || !form.telefone.trim()}
                  style={{padding:'9px 14px',borderRadius:'12px',background:'#2F6BFF',color:'white',border:'none',fontWeight:'700',fontSize:'12px',cursor:'pointer',whiteSpace:'nowrap',height:'40px',flexShrink:0,opacity:(buscandoAluno||!form.telefone.trim())?0.4:1}}>
                  {buscandoAluno ? '...' : 'Buscar'}
                </button>
              )}
            </div>
            <Input label="Nome completo *" placeholder="Nome do aluno" value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} />
          <Input label="Email (opcional)" type="email" placeholder="aluno@email.com" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} />
          {(() => {
            const profMods = parseMods(professor.modalidades);
            if (profMods.length === 1) {

              return (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">Modalidade</label>
                  <div style={{display:'flex',alignItems:'center',gap:'8px',border:'1.5px solid #C2D4FF',background:'#E7EDFF',borderRadius:'12px',padding:'10px 14px'}}>
                    <span style={{fontSize:'16px'}}><Ico n="medal" /></span>
                    <span style={{fontSize:'14px',fontWeight:'700',color:'#2455D6'}}>{profMods[0]}</span>
                    <span style={{fontSize:'11px',color:'#6E9BFF',marginLeft:'auto'}}>preenchida automaticamente</span>
                  </div>
                </div>
              );
            }
            if (profMods.length > 1) {

              return (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">Modalidade deste aluno *</label>
                  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                    {profMods.map(m => (
                      <button key={m} onClick={() => setForm(f => ({...f, modalidade: m}))}
                        style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 14px',borderRadius:'10px',border:form.modalidade===m?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:form.modalidade===m?'#E7EDFF':'#F7F8FA',cursor:'pointer',textAlign:'left'}}>
                        <span style={{fontSize:'14px'}}><Ico n="medal" /></span>
                        <span style={{fontSize:'13px',fontWeight:'700',color:form.modalidade===m?'#2F6BFF':'#3D424C'}}>{m}</span>
                        {form.modalidade===m && <span style={{marginLeft:'auto',width:'18px',height:'18px',borderRadius:'50%',background:'#2F6BFF',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><span style={{color:'white',fontSize:'11px'}}><Ico n="check" /></span></span>}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:'12px',padding:'12px 14px',fontSize:'12px',color:'#92400E'}}>
                <Ico n="alert-triangle" c="#D97706" /> Você ainda não configurou suas modalidades. Acesse <strong>Configurações <Ico n="arrow-right" /> Perfil</strong> para definir quais serviços você oferece.
              </div>
            );
          })()}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Observações</label>
            <textarea className="border border-gray-200 rounded-xl px-3 py-2 text-sm" rows="2" placeholder="Lesões, preferências, etc." value={form.observacoes} onChange={e => setForm(f => ({...f, observacoes: e.target.value}))} />
          </div>
          {}
          <div style={{background:'#F7F8FA',border:'1px solid #E2E4E9',borderRadius:'14px',padding:'14px 16px'}}>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Tipo de vínculo</div>
            <div style={{display:'flex',gap:'8px'}}>
 {[{v:'vinculado',l:'Vinculado à plataforma',desc:'Aluno terá acesso e confirma o acordo no app'},{v:'interno',l:'Controle interno',desc:'Apenas registro do professor, sem acesso do aluno'}].map(op=>(
                <button key={op.v} onClick={()=>setForm(f=>({...f,tipo_vinculo:op.v}))}
                  style={{flex:1,padding:'10px 12px',borderRadius:'10px',border:form.tipo_vinculo===op.v?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:form.tipo_vinculo===op.v?'#E7EDFF':'white',cursor:'pointer',textAlign:'left',transition:'all 0.15s'}}>
                  <div style={{fontSize:'12px',fontWeight:'700',color:form.tipo_vinculo===op.v?'#2F6BFF':'#3D424C'}}>{op.l}</div>
                  <div style={{fontSize:'10px',color:'#9AA0AB',marginTop:'3px',lineHeight:'1.3'}}>{op.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2"><Button onClick={salvarAluno} className="flex-1 justify-center">{modoEdicao ? 'Salvar Alterações' : 'Salvar Aluno'}</Button><Button variant="secondary" onClick={() => setModal(false)}>Cancelar</Button></div>
          </>)}

          {}
          {!modoEdicao && buscaStep === 0 && (
            <Button variant="secondary" onClick={() => setModal(false)}>Cancelar</Button>
          )}
        </div>
      </Modal>

      {}
      <Modal open={modalConfirmaDelete} onClose={() => setModalConfirmaDelete(false)} title="Encerrar com este aluno" uso="aluno_encerrar">
        <div className="flex flex-col gap-4">
          <div style={{background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'12px', padding:'14px', color:'#92400E', fontSize:'14px'}}>
            <strong>{alunoParaDeletar?.nome}</strong> sai da sua lista, o pacote é encerrado e as aulas futuras são canceladas.
          </div>
          <div style={{fontSize:'13px', color:'#3D424C', lineHeight:'1.55'}}>
            O histórico dele <strong>não é apagado</strong> — aulas e pagamentos continuam registrados, para os dois lados.
            Ele recebe um aviso de que o vínculo foi encerrado, e você pode reativá-lo depois pelo filtro “Desvinculados”.
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={deletarAluno} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-xl transition-colors">Sim, encerrar</button>
            <Button variant="secondary" onClick={() => setModalConfirmaDelete(false)}>Cancelar</Button>
          </div>
        </div>
      </Modal>

      {}
      {modalContrato && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'24px',boxShadow:'0 24px 80px rgba(0,0,0,0.18)',width:'100%',maxWidth:'520px',display:'flex',flexDirection:'column',maxHeight:'90vh'}} onClick={e=>e.stopPropagation()}>

            {}
            <div style={{padding:'24px 28px 0',flexShrink:0}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'6px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#2F6BFF',textTransform:'uppercase',letterSpacing:'0.08em'}}>
                  {alunoSelecionado?.nome}
                </div>
                <button onClick={() => setModalContrato(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0AB',fontSize:'20px',lineHeight:1,padding:'2px'}}><Ico n="x" /></button>
              </div>
              <div style={{fontSize:'19px',fontWeight:'800',color:'#16181D',marginBottom:'16px'}}>
 {wizardPreenchido ? 'Confirmar acordo': wizardStep===1 ? 'Escolha o plano': wizardStep===2 ? 'Dias e horários': 'Valor e confirmação'}
              </div>
              {}
              {!wizardPreenchido && (
                <div style={{display:'flex',gap:'6px',marginBottom:'24px'}}>
                  {[1,2,3].map(s => (
                    <div key={s} style={{flex:1,height:'4px',borderRadius:'2px',background:s<=wizardStep?'#2F6BFF':'#E2E4E9',transition:'background 0.3s'}}></div>
                  ))}
                </div>
              )}
            </div>

            {}
            {wizardStep===1 && (
              <div style={{padding:'0 28px 28px',display:'flex',flexDirection:'column',gap:'12px',overflowY:'auto',flex:1}}>
                {agendaJaDefinida && (
                  <div style={{background:'#EFFAF5',border:'1px solid #8FDFC4',borderRadius:'12px',padding:'10px 14px',display:'flex',alignItems:'center',gap:'10px'}}>
                    <span style={{fontSize:'16px',flexShrink:0}}><Ico n="calendar" /></span>
                    <div style={{fontSize:'12px',color:'#0A6F4D'}}>
                      <strong>Agenda já configurada.</strong> Ao escolher o plano você vai direto para a etapa de valor — sem precisar refazer os horários.
                    </div>
                  </div>
                )}
                <div style={{fontSize:'13px',color:'#6E7480',marginBottom:'4px'}}>Selecione o tipo de acordo:</div>
                <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
                  {planos.filter(p=>['Plano Mensal','Plano Semestral','Plano Anual'].includes(p.nome)).map(p => {
                    const sel = formContrato.plano_id===p.id;
                    const info = p.duracao_meses===1?{emoji:'calendar',desc:'Renovação mensal',dur:'1 mês'}:p.duracao_meses===6?{emoji:'calendar-event',desc:'Comprometimento semestral',dur:'6 meses'}:{emoji:'trophy',desc:'Melhor custo-benefício',dur:'12 meses'};
                    return (
                      <button key={p.id} onClick={() => onPlanoChange(p.id)}
                        style={{display:'flex',alignItems:'center',gap:'14px',padding:'14px 16px',borderRadius:'14px',border:sel?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:sel?'#E7EDFF':'#F7F8FA',cursor:'pointer',textAlign:'left',transition:'all 0.15s'}}>
                        <div style={{lineHeight:1}}><Ico n={info.emoji} s={24} /></div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:'800',fontSize:'14px',color:sel?'#2F6BFF':'#16181D'}}>{p.nome.replace('Plano ','Plano ')}</div>
                          <div style={{fontSize:'12px',color:sel?'#6E9BFF':'#9AA0AB',marginTop:'2px'}}>{info.desc} · {info.dur}</div>
                        </div>
                        {sel && <div style={{width:'20px',height:'20px',borderRadius:'50%',background:'#2F6BFF',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><span style={{color:'white',fontSize:'12px'}}><Ico n="check" /></span></div>}
                      </button>
                    );
                  })}
                  {planos.filter(p=>!['Plano Mensal','Plano Semestral','Plano Anual'].includes(p.nome)).map(p => {
                    const sel = formContrato.plano_id===p.id;
                    return (
                      <button key={p.id} onClick={() => onPlanoChange(p.id)}
                        style={{display:'flex',alignItems:'center',gap:'14px',padding:'14px 16px',borderRadius:'14px',border:sel?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:sel?'#E7EDFF':'#F7F8FA',cursor:'pointer',textAlign:'left'}}>
                        <div style={{fontSize:'24px'}}><Ico n="star" /></div>
                        <div style={{flex:1}}>
                          <div style={{fontWeight:'800',fontSize:'14px',color:sel?'#2F6BFF':'#16181D'}}>{p.nome}</div>
                          <div style={{fontSize:'12px',color:'#9AA0AB'}}>{p.duracao_meses} meses</div>
                        </div>
                        {sel && <div style={{width:'20px',height:'20px',borderRadius:'50%',background:'#2F6BFF',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{color:'white',fontSize:'12px'}}><Ico n="check" /></span></div>}
                      </button>
                    );
                  })}
                </div>
                <div style={{display:'flex',gap:'10px',marginTop:'8px'}}>
                  <button onClick={() => { setModalContrato(false); setAgendaJaDefinida(false); }} style={{flex:1,padding:'13px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>Cancelar</button>
                  <button onClick={() => setWizardStep(agendaJaDefinida ? 3 : 2)} disabled={!formContrato.plano_id}
                    style={{flex:2,padding:'13px',borderRadius:'12px',border:'none',background:formContrato.plano_id?'linear-gradient(135deg,#2F6BFF,#2F6BFF)':'#E2E4E9',color:formContrato.plano_id?'white':'#9AA0AB',fontSize:'14px',fontWeight:'800',cursor:formContrato.plano_id?'pointer':'not-allowed'}}>
 {agendaJaDefinida ? 'Próximo — Valor ': 'Próximo — Horários '}
                  </button>
                </div>
              </div>
            )}

            {}
            {wizardStep===2 && (
              <div style={{padding:'0 28px 28px',display:'flex',flexDirection:'column',gap:'16px',overflowY:'auto',flex:1}}>
                {}
                <div style={{display:'flex',gap:'8px'}}>
                  <button type="button" onClick={() => setAgendaLivre(false)}
                    style={{flex:1,padding:'10px',borderRadius:'10px',border:!agendaLivre?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:!agendaLivre?'#E7EDFF':'white',color:!agendaLivre?'#2F6BFF':'#6E7480',fontWeight:'700',fontSize:'13px',cursor:'pointer'}}>
                    <Ico n="calendar" /> Agenda fixa
                  </button>
                  <button type="button" onClick={() => setAgendaLivre(true)}
                    style={{flex:1,padding:'10px',borderRadius:'10px',border:agendaLivre?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:agendaLivre?'#E7EDFF':'white',color:agendaLivre?'#2F6BFF':'#6E7480',fontWeight:'700',fontSize:'13px',cursor:'pointer'}}>
                    <Ico n="bolt" /> Agenda flexível
                  </button>
                </div>
                {agendaLivre && (
                  <div style={{background:'#E7EDFF',border:'1.5px solid #C2D4FF',borderRadius:'12px',padding:'12px 14px',fontSize:'12px',color:'#2455D6'}}>
                    <strong>Sem horários fixos.</strong> As aulas são registradas avulsas conforme acontecem — professor ou aluno lança quando a aula for feita. O total do pacote ainda define quantos créditos o aluno tem.
                  </div>
                )}
                <div>
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Quantas aulas por semana?</div>
                  <div style={{display:'flex',gap:'8px'}}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} onClick={() => onAulasPorSemanaChange(n)}
                        style={{flex:1,padding:'12px 0',borderRadius:'12px',border:formContrato.aulas_por_semana===n?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:formContrato.aulas_por_semana===n?'#E7EDFF':'white',color:formContrato.aulas_por_semana===n?'#2F6BFF':'#6E7480',fontWeight:'800',fontSize:'16px',cursor:'pointer'}}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                {!agendaLivre && <div>
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Dias e horários de cada aula</div>
                  <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                    {(formContrato.horarios||[]).map((h,i) => {
                      const conflito = conflitosWizard[i];
                      const isLivre = h.dia_semana === null || h.dia_semana === undefined;
                      return (
                      <div key={i} style={{background: isLivre ? '#E7EDFF' : conflito ? '#FFFBEB' : '#F7F8FA', borderRadius:'12px', padding:'12px 14px', border: isLivre ? '1.5px solid #C4B5FD' : conflito ? '1.5px solid #FDE68A' : '1px solid transparent'}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
                          <div style={{fontSize:'11px',fontWeight:'700',color: isLivre ? '#2455D6' : conflito ? '#92400E' : '#2F6BFF',display:'flex',alignItems:'center',gap:'6px'}}>
                            AULA {i+1}
                            {conflito && <span style={{fontSize:'10px',fontWeight:'600',color:'#92400E',background:'#FEF3C7',padding:'1px 7px',borderRadius:'10px'}}><Ico n="alert-triangle" c="#D97706" /> {conflito.map(c=>c.alunos?.nome?.split(' ')[0]).join(', ')} já usa este horário</span>}
                          </div>
                          {}
                          <div style={{display:'flex',gap:'4px'}}>
                            <button type="button" onClick={() => { const novos=[...formContrato.horarios]; novos[i]={...novos[i],dia_semana:isLivre?1:null}; setFormContrato(f=>({...f,horarios:novos})); }}
                              style={{padding:'3px 9px',borderRadius:'8px',fontSize:'10px',fontWeight:'700',cursor:'pointer',border:!isLivre?'2px solid #2F6BFF':'1px solid #E2E4E9',background:!isLivre?'#E7EDFF':'white',color:!isLivre?'#2F6BFF':'#9AA0AB'}}>
                              <Ico n="calendar" /> Fixo
                            </button>
                            <button type="button" onClick={() => { const novos=[...formContrato.horarios]; novos[i]={...novos[i],dia_semana:null}; setFormContrato(f=>({...f,horarios:novos})); }}
                              style={{padding:'3px 9px',borderRadius:'8px',fontSize:'10px',fontWeight:'700',cursor:'pointer',border:isLivre?'2px solid #2455D6':'1px solid #E2E4E9',background:isLivre?'#C2D4FF':'white',color:isLivre?'#2455D6':'#9AA0AB'}}>
                              <Ico n="bolt" /> Flexível
                            </button>
                          </div>
                        </div>
                        {isLivre ? (
                          <div style={{fontSize:'12px',color:'#1C43AE',background:'#C2D4FF',borderRadius:'8px',padding:'8px 10px'}}>
                            A data e hora serão combinadas na hora — use o botão <strong><Ico n="bolt" /> Aula</strong> no card do aluno para registrar quando acontecer.
                          </div>
                        ) : (
                          <>
                            <div style={{display:'flex',gap:'4px',flexWrap:'wrap',marginBottom:'8px'}}>
                              {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map((d,di) => (
                                <button key={di} onClick={() => { const novos=[...formContrato.horarios]; novos[i]={...novos[i],dia_semana:di}; setFormContrato(f=>({...f,horarios:novos})); }}
                                  style={{padding:'6px 8px',borderRadius:'8px',fontSize:'11px',fontWeight:'700',cursor:'pointer',border:h.dia_semana===di?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:h.dia_semana===di?'#E7EDFF':'white',color:h.dia_semana===di?'#2F6BFF':'#9AA0AB'}}>
                                  {d}
                                </button>
                              ))}
                            </div>
                            <input type="time" value={h.hora||'08:00'} onChange={e => { const novos=[...formContrato.horarios]; novos[i]={...novos[i],hora:e.target.value}; setFormContrato(f=>({...f,horarios:novos})); }}
                              style={{border: conflito ? '1.5px solid #FDE68A' : '1.5px solid #E2E4E9',borderRadius:'8px',padding:'7px 12px',fontSize:'14px',fontWeight:'700',color:'#16181D',outline:'none',background:'white'}} />
                          </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <div style={{fontSize:'11px',color:'#9AA0AB',marginTop:'8px'}}><Ico n="bulb" c="#D97706" /> Slots fixos geram calendário automático · Slots livres são registrados avulsos</div>
                </div>}
                <div style={{display:'flex',gap:'10px'}}>
                  <button onClick={() => setWizardStep(1)} style={{flex:1,padding:'13px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}><Ico n="arrow-right" /> Voltar</button>
                  <button onClick={() => setWizardStep(3)}
                    style={{flex:2,padding:'13px',borderRadius:'12px',border:'none',background:'linear-gradient(135deg,#2F6BFF,#2F6BFF)',color:'white',fontSize:'14px',fontWeight:'800',cursor:'pointer'}}>
                    Próximo — Valor <Ico n="arrow-right" />
                  </button>
                </div>
              </div>
            )}

            {}
            {wizardStep===3 && (
              <div style={{padding:'0 28px 28px',display:'flex',flexDirection:'column',gap:'16px',overflowY:'auto',flex:1}}>
                {}
                {wizardPreenchido && (
                  <div style={{background:'#E7EDFF',border:'1px solid #C2D4FF',borderRadius:'12px',padding:'11px 14px',display:'flex',alignItems:'flex-start',gap:'10px'}}>
                    <span style={{fontSize:'18px',lineHeight:1,flexShrink:0}}><Ico n="bolt" /></span>
                    <div>
                      <div style={{fontSize:'12px',fontWeight:'800',color:'#2F6BFF',marginBottom:'2px'}}>Dados pré-preenchidos do plano atual</div>
                      <div style={{fontSize:'11px',color:'#2F6BFF'}}>Revise e ajuste se necessário — os horários e o plano foram carregados automaticamente.</div>
                    </div>
                    <button onClick={() => { setWizardPreenchido(false); setWizardStep(1); }}
                      style={{marginLeft:'auto',fontSize:'10px',color:'#2F6BFF',background:'none',border:'1px solid #C2D4FF',borderRadius:'6px',padding:'3px 8px',cursor:'pointer',flexShrink:0,fontWeight:'700',whiteSpace:'nowrap'}}>
                      Editar tudo
                    </button>
                  </div>
                )}
                <div>
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Valor por aula (R$)</div>
                  <div style={{display:'flex',alignItems:'center',gap:'4px',border:'1.5px solid #E2E4E9',borderRadius:'12px',padding:'0 16px',background:'#F7F8FA'}}>
                    <span style={{fontSize:'16px',fontWeight:'700',color:'#9AA0AB'}}>R$</span>
                    <CampoDinheiro placeholder="0,00" value={formContrato.valor_aula}
                      onValor={v => setFormContrato(f => ({...f, valor_aula: v}))}
                      style={{border:'none',background:'transparent',padding:'14px 8px',fontSize:'28px',fontWeight:'800',color:'#16181D',width:'120px',outline:'none'}} />
                    <span style={{fontSize:'13px',color:'#9AA0AB'}}>por aula</span>
                  </div>
                </div>
                <div>
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Taxa academia por aula (R$) <span style={{fontWeight:'400',color:'#9AA0AB'}}>(opcional)</span></div>
                  <div style={{display:'flex',alignItems:'center',gap:'4px',border:'1.5px solid #E2E4E9',borderRadius:'12px',padding:'0 16px',background:'#F7F8FA'}}>
                    <span style={{fontSize:'16px',fontWeight:'700',color:'#9AA0AB'}}>R$</span>
                    <CampoDinheiro placeholder="0,00" value={formContrato.taxa_academia || ''}
                      onValor={v => setFormContrato(f => ({...f, taxa_academia: v}))}
                      style={{border:'none',background:'transparent',padding:'14px 8px',fontSize:'28px',fontWeight:'800',color:'#16181D',width:'120px',outline:'none'}} />
                    <span style={{fontSize:'13px',color:'#9AA0AB'}}>por aula (academia)</span>
                  </div>
                </div>
                {formContrato.valor_aula && Number(formContrato.valor_aula) > 0 && (() => {
                  const mensal = Number(formContrato.valor_aula) * formContrato.aulas_por_semana * 4;
                  const total = mensal * formContrato.duracao_meses;
                  const taxaAcad = Number(formContrato.taxa_academia) || 0;
                  const custoMensal = taxaAcad * formContrato.aulas_por_semana * 4;
                  const liquidoMensal = mensal - custoMensal;
                  return (
                    <div style={{background:'linear-gradient(135deg,#EFFAF5,#DCF5EC)',border:'1px solid #B7E9D6',borderRadius:'14px',padding:'14px 18px'}}>
                      <div style={{fontSize:'22px',fontWeight:'800',color:'#0A6F4D'}}>R$ {mensal.toFixed(0)}<span style={{fontSize:'14px',fontWeight:'600',color:'#8FDFC4'}}>/pacote</span></div>
                      <div style={{fontSize:'12px',color:'#0E9F6E',marginTop:'3px'}}>{formContrato.aulas_por_semana}x/sem × 4 sem × R${Number(formContrato.valor_aula).toFixed(0)}{formContrato.duracao_meses>1?` = R$${total.toFixed(0)} total em ${formContrato.duracao_meses} meses`:''}</div>
                      {taxaAcad > 0 && (
                        <div style={{marginTop:'8px',paddingTop:'8px',borderTop:'1px solid #B7E9D6',display:'flex',flexDirection:'column',gap:'2px'}}>
                          <div style={{fontSize:'12px',color:'#DC2626'}}><Ico n="building" /> Custo academia: −R$ {custoMensal.toFixed(0)}/pacote</div>
                          <div style={{fontSize:'14px',fontWeight:'800',color:'#0A6F4D'}}><Ico n="coin" /> Receita líquida: R$ {liquidoMensal.toFixed(0)}/pacote</div>
                        </div>
                      )}
                    </div>
                  );
                })()}
                {}
                {(() => {
                  const diasSemana = (formContrato.horarios||[]).map(h=>h.dia_semana).filter(d=>d!==null&&d!==undefined);
                  const totalAulas = (formContrato.aulas_por_semana||0) * (formContrato.duracao_meses||1) * 4;
                  if (!diasSemana.length || !totalAulas || !formContrato.data_inicio) return null;
                  const ultimaData = calcDataFimPorAulas(formContrato.data_inicio, diasSemana, totalAulas);
                  const fmtData = str => str ? new Date(str+'T12:00').toLocaleDateString('pt-BR',{day:'numeric',month:'short',year:'numeric'}) : '—';
                  const DIAS_NOME = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
                  const diasNomes = diasSemana.map(d=>DIAS_NOME[d]).join(' e ');
                  return (
                    <div style={{background:'linear-gradient(135deg,#E7EDFF,#E7EDFF)',border:'1px solid #C2D4FF',borderRadius:'14px',padding:'14px 18px'}}>
                      <div style={{fontSize:'11px',fontWeight:'800',color:'#2F6BFF',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}><Ico n="clipboard-list" /> Resumo do plano</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                        <div style={{background:'white',borderRadius:'10px',padding:'10px 12px'}}>
                          <div style={{fontSize:'10px',color:'#9AA0AB',fontWeight:'600',marginBottom:'2px'}}>AULAS NO PLANO</div>
                          <div style={{fontSize:'22px',fontWeight:'800',color:'#2F6BFF'}}>{totalAulas}</div>
                          <div style={{fontSize:'10px',color:'#2F6BFF'}}>{formContrato.aulas_por_semana}×/sem × {formContrato.duracao_meses} {formContrato.duracao_meses===1?'mês':'meses'}</div>
                        </div>
                        <div style={{background:'white',borderRadius:'10px',padding:'10px 12px'}}>
                          <div style={{fontSize:'10px',color:'#9AA0AB',fontWeight:'600',marginBottom:'2px'}}>DIAS DA SEMANA</div>
                          <div style={{fontSize:'14px',fontWeight:'800',color:'#2F6BFF'}}>{diasNomes}</div>
                          <div style={{fontSize:'10px',color:'#2F6BFF'}}>recorrente</div>
                        </div>
                        <div style={{background:'white',borderRadius:'10px',padding:'10px 12px',gridColumn:'1/-1'}}>
                          <div style={{fontSize:'10px',color:'#9AA0AB',fontWeight:'600',marginBottom:'4px'}}>INÍCIO <Ico n="arrow-right" /> ÚLTIMA AULA</div>
                          <div style={{fontSize:'13px',fontWeight:'700',color:'#1F2229'}}>
                            {fmtData(formContrato.data_inicio)} <span style={{color:'#9AA0AB',fontWeight:'400'}}><Ico n="arrow-right" /></span> {fmtData(ultimaData)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {}
                {(() => { try {
                  const hvs = (formContrato.horarios||[]).filter(h=>h.dia_semana!==null&&h.hora);
                  if (!hvs.length || !formContrato.data_inicio || formContrato.data_inicio.length < 10) return null;
                  const ini = new Date(formContrato.data_inicio+'T12:00:00');
                  if (isNaN(ini.getTime())) return null;
                  const fim = new Date(formContrato.data_inicio+'T12:00:00'); fim.setMonth(fim.getMonth()+formContrato.duracao_meses);
                  let prim=null,ult=null; const at=new Date(ini);
                  while(at<=fim){for(const h of hvs){if(at.getDay()===h.dia_semana){const ds=at.toISOString().split('T')[0];if(!prim)prim=ds;ult=ds;}}at.setDate(at.getDate()+1);}
                  if(!prim) return null;
                  const fmt = d => new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'numeric'});
                  return (
                    <div style={{background:'#E7EDFF',border:'1px solid #C2D4FF',borderRadius:'12px',padding:'12px 14px',fontSize:'12px',color:'#1C43AE',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}>
                      <span><Ico n="calendar" /> <strong>1ª aula:</strong> {fmt(prim)} &nbsp;·&nbsp; <strong>Última:</strong> {fmt(ult)}</span>
                      <button onClick={()=>setFormContrato(f=>({...f,data_inicio:prim,data_fim:ult}))}
                        style={{fontSize:'11px',padding:'4px 10px',borderRadius:'6px',border:'1px solid #2F6BFF',background:'white',color:'#2F6BFF',cursor:'pointer',fontWeight:'700',whiteSpace:'nowrap'}}>
                        Usar estas datas
                      </button>
                    </div>
                  );
                } catch(e) { return null; } })()}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div>
                    <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>Início</div>
                    <input type="date" value={formContrato.data_inicio} onChange={e => {
                      const ini = e.target.value;
                      if (!ini || ini.length < 10) { setFormContrato(f=>({...f,data_inicio:ini})); return; }
                      const d = new Date(ini+'T12:00:00');
                      if (isNaN(d.getTime())) { setFormContrato(f=>({...f,data_inicio:ini})); return; }
                      const fim = new Date(ini+'T12:00:00'); fim.setMonth(fim.getMonth()+formContrato.duracao_meses);
                      setFormContrato(f=>({...f,data_inicio:ini,data_fim:fim.toISOString().split('T')[0]}));
                    }} style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'9px 12px',fontSize:'13px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
                  </div>
                  <div>
                    <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>Término</div>
                    <input type="date" value={formContrato.data_fim} onChange={e => setFormContrato(f=>({...f,data_fim:e.target.value}))}
                      style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'9px 12px',fontSize:'13px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
                  </div>
                </div>
                {}
                {formContrato.duracao_meses > 1 && (
                  <div>
                    <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Forma de cobrança</div>
                    <div style={{display:'flex',gap:'8px',marginBottom:'10px'}}>
 {[{v:'mensal',l:'Mensal (recorrente)'},{v:'avista',l:'À vista (total)'}].map(op=>(
                        <button key={op.v} onClick={()=>setFormContrato(f=>({...f,tipo_cobranca:op.v}))}
                          style={{flex:1,padding:'10px',borderRadius:'10px',border:formContrato.tipo_cobranca===op.v?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:formContrato.tipo_cobranca===op.v?'#E7EDFF':'white',color:formContrato.tipo_cobranca===op.v?'#2F6BFF':'#6E7480',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
                          {op.l}
                        </button>
                      ))}
                    </div>
                    {formContrato.tipo_cobranca==='mensal' && (
                      <div style={{display:'flex',alignItems:'center',gap:'10px',fontSize:'13px',color:'#3D424C',background:'#F7F8FA',borderRadius:'10px',padding:'10px 12px'}}>
                        <span>Vencimento todo dia</span>
                        <input type="number" min="1" max="28" value={formContrato.dia_vencimento}
                          onChange={e=>setFormContrato(f=>({...f,dia_vencimento:Number(e.target.value)}))}
                          style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'6px 10px',fontSize:'13px',width:'60px',outline:'none',textAlign:'center'}} />
                        <span>de cada mês</span>
                      </div>
                    )}
                    {formContrato.tipo_cobranca==='avista' && formContrato.valor_aula && (
                      <div style={{fontSize:'12px',color:'#2F6BFF',background:'#E7EDFF',borderRadius:'10px',padding:'8px 12px',fontWeight:'600'}}>
                        Total à vista: R$ {(Number(formContrato.valor_aula)*formContrato.aulas_por_semana*4*formContrato.duracao_meses).toFixed(0)}
                      </div>
                    )}
                  </div>
                )}
                <div style={{background:'#F7F8FA',border:'1px solid #E2E4E9',borderRadius:'12px',padding:'12px 14px'}}>
                  <label style={{display:'flex',alignItems:'center',gap:'10px',cursor:'pointer'}}>
                    <input type="checkbox" checked={formContrato.reposicao_permitida} onChange={e => setFormContrato(f => ({...f, reposicao_permitida: e.target.checked}))}
                      style={{width:'16px',height:'16px',accentColor:'#2F6BFF',cursor:'pointer',flexShrink:0}} />
                    <div>
                      <div style={{fontSize:'13px',fontWeight:'700',color:'#3D424C'}}>Permitir reposição de aula</div>
                      <div style={{fontSize:'11px',color:'#9AA0AB'}}>Reagendamento com aviso antecipado</div>
                    </div>
                  </label>
                  {formContrato.reposicao_permitida && (
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginTop:'12px',paddingTop:'12px',borderTop:'1px solid #E2E4E9'}}>
                      <div>
                        <div style={{fontSize:'10px',fontWeight:'700',color:'#6E7480',textTransform:'uppercase',marginBottom:'5px'}}>Reposições no plano</div>
                        <select value={formContrato.limite_reposicao} onChange={e => setFormContrato(f=>({...f,limite_reposicao:Number(e.target.value)}))}
                          style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'8px 10px',fontSize:'13px',color:'#16181D',background:'white',width:'100%',outline:'none'}}>
                          {[1,2,3,4,5].map(n=><option key={n} value={n}>{n}x</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{fontSize:'10px',fontWeight:'700',color:'#6E7480',textTransform:'uppercase',marginBottom:'5px'}}>Aviso mínimo</div>
                        <select value={formContrato.antecedencia_cancelamento_horas} onChange={e => setFormContrato(f=>({...f,antecedencia_cancelamento_horas:Number(e.target.value)}))}
                          style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'8px 10px',fontSize:'13px',color:'#16181D',background:'white',width:'100%',outline:'none'}}>
                          {[0,6,12,24,48,72].map(h=><option key={h} value={h}>{h ? `${h}h antes` : 'Livre (sem prazo)'}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                {}
                {(() => {
                  const totalAulas = formContrato.aulas_por_semana * formContrato.duracao_meses * 4;
                  const realizadas = Math.min(Number(formContrato.aulas_ja_realizadas) || 0, totalAulas);
                  const restantes = Math.max(0, totalAulas - realizadas);
                  const calcDataFim = () => {
                    try {
                      if (restantes === 0) return null;
                      const dias = [...new Set((formContrato.horarios||[]).filter(h=>h.dia_semana!==null).map(h=>Number(h.dia_semana)))];
                      if (!dias.length) return null;
                      let count = 0; const at = new Date(); at.setHours(12,0,0,0);
                      for (let i = 0; i < 730; i++) {
                        if (dias.includes(at.getDay())) { count++; if (count === restantes) { const p=v=>String(v).padStart(2,'0'); return `${at.getFullYear()}-${p(at.getMonth()+1)}-${p(at.getDate())}`; } }
                        at.setDate(at.getDate()+1);
                      }
                      return null;
                    } catch(e) { return null; }
                  };
                  const sugerida = calcDataFim();
                  const fmtD = d => d ? new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—';
                  const aplicada = sugerida && formContrato.data_fim === sugerida;
                  return (
                    <div style={{border:'1.5px solid #E2E4E9',borderRadius:'12px',overflow:'hidden'}}>
                      {}
                      <button onClick={()=>setFormContrato(f=>({...f,em_andamento:!f.em_andamento,aulas_ja_realizadas:0}))}
                        style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 16px',background:formContrato.em_andamento?'#EFFAF5':'#F7F8FA',border:'none',cursor:'pointer',textAlign:'left'}}>
                        <div>
                          <div style={{fontSize:'13px',fontWeight:'700',color:formContrato.em_andamento?'#0A6F4D':'#3D424C'}}>
                            <Ico n="clipboard-list" /> Este plano já está em andamento?
                          </div>
                          <div style={{fontSize:'11px',color:'#9AA0AB',marginTop:'2px'}}>
                            {formContrato.em_andamento ? 'Sim — informe quantas aulas já foram feitas' : 'Não — plano começa do zero'}
                          </div>
                        </div>
                        <div style={{width:'40px',height:'22px',borderRadius:'11px',background:formContrato.em_andamento?'#0E9F6E':'#C7CAD1',position:'relative',flexShrink:0,transition:'background 0.2s'}}>
                          <div style={{maxWidth:'18px',width:'100%',height:'18px',borderRadius:'50%',background:'white',position:'absolute',top:'2px',left:formContrato.em_andamento?'20px':'2px',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}></div>
                        </div>
                      </button>
                      {}
                      {formContrato.em_andamento && (
                        <div style={{padding:'14px 16px',background:'#EFFAF5',borderTop:'1px solid #B7E9D6',display:'flex',flexDirection:'column',gap:'12px'}}>
                          <div style={{fontSize:'11px',color:'#0A6F4D'}}>Total do plano: <strong>{totalAulas} aulas</strong> ({formContrato.aulas_por_semana}x/sem × {formContrato.duracao_meses} {formContrato.duracao_meses===1?'mês':'meses'})</div>
                          {}
                          <div>
                            <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'6px'}}>Aulas já realizadas até hoje</div>
                            <input type="number" min="0" max={totalAulas} value={formContrato.aulas_ja_realizadas}
                              onChange={e=>setFormContrato(f=>({...f,aulas_ja_realizadas:Math.max(0,Math.min(totalAulas,Number(e.target.value)))}))}
                              style={{border:'1.5px solid #B7E9D6',borderRadius:'8px',padding:'10px 12px',fontSize:'26px',fontWeight:'800',color:'#16181D',width:'100%',boxSizing:'border-box',outline:'none',textAlign:'center',background:'white'}} />
                            <div style={{fontSize:'10px',color:'#9AA0AB',marginTop:'4px',textAlign:'center'}}>Restam <strong style={{color:'#0A6F4D'}}>{restantes}</strong> aulas a agendar a partir de hoje</div>
                          </div>
                          {}
                          {sugerida && (
                            <div style={{background:'white',border:'1px solid #B7E9D6',borderRadius:'10px',padding:'12px 14px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px'}}>
                              <div>
                                <div style={{fontSize:'10px',color:'#6E7480',marginBottom:'2px'}}>Previsão de término (pela agenda)</div>
                                <div style={{fontSize:'15px',fontWeight:'800',color:'#0A6F4D'}}>{fmtD(sugerida)}</div>
                              </div>
                              <button onClick={()=>setFormContrato(f=>({...f,data_fim:sugerida}))}
                                style={{padding:'8px 14px',borderRadius:'8px',border:'none',background:aplicada?'#0E9F6E':'#2F6BFF',color:'white',fontSize:'12px',fontWeight:'700',cursor:'pointer',whiteSpace:'nowrap',flexShrink:0}}>
 {aplicada ? 'Aplicado': 'Usar esta data '}
                              </button>
                            </div>
                          )}
                          {!sugerida && restantes > 0 && <div style={{fontSize:'11px',color:'#E8971A'}}><Ico n="alert-triangle" c="#D97706" /> Defina os dias e horários (passo 2) para calcular o término.</div>}
                          {restantes === 0 && <div style={{fontSize:'11px',color:'#0E9F6E'}}><Ico n="circle-check" c="#0E9F6E" /> Todas as aulas já foram realizadas!</div>}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {}
                {formContrato.duracao_meses === 1 ? (
                        <div>
                          <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'8px'}}>Este período já foi pago?</div>
                          <div style={{display:'flex',gap:'8px'}}>
 {[{v:0,l:'Não — gerar cobrança pendente'},{v:1,l:'Sim — já foi pago'}].map(op=>(
                              <button key={op.v} onClick={()=>setFormContrato(f=>({...f,parcelas_ja_pagas:op.v}))}
                                style={{flex:1,padding:'10px 8px',borderRadius:'10px',border:formContrato.parcelas_ja_pagas===op.v?'2px solid #0E9F6E':'1.5px solid #E2E4E9',background:formContrato.parcelas_ja_pagas===op.v?'#DCF5EC':'white',color:formContrato.parcelas_ja_pagas===op.v?'#0A6F4D':'#3D424C',fontSize:'12px',fontWeight:'700',cursor:'pointer',textAlign:'center'}}>
                                {op.l}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'5px'}}>Parcelas já pagas (de {formContrato.duracao_meses})</div>
                          <input type="number" min="0" max={formContrato.duracao_meses} value={formContrato.parcelas_ja_pagas}
                            onChange={e=>setFormContrato(f=>({...f,parcelas_ja_pagas:Math.min(formContrato.duracao_meses,Math.max(0,Number(e.target.value)))}))}
                            style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'8px 12px',fontSize:'15px',fontWeight:'700',color:'#16181D',width:'100%',boxSizing:'border-box',outline:'none',textAlign:'center'}} />
                          <div style={{fontSize:'10px',color:'#9AA0AB',marginTop:'4px',textAlign:'center'}}>ex: 2 de 6 meses já pagos</div>
                        </div>
                      )}
                <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'12px',padding:'10px 14px',fontSize:'12px',color:'#92400E'}}>
                  <Ico n="credit-card" /> {formContrato.parcelas_ja_pagas >= formContrato.duracao_meses ? 'Todas as parcelas já pagas — nenhuma cobrança pendente será gerada.' : formContrato.duracao_meses === 1 && formContrato.parcelas_ja_pagas === 0 ? 'Uma cobrança pendente será gerada para este mês — confirme quando receber.' : 'As parcelas restantes serão geradas como pendentes — confirme quando receber.'}
                </div>
                {}
                <div style={{border:'1.5px solid #E2E4E9',borderRadius:'12px',overflow:'hidden'}}>
                  <div style={{padding:'12px 16px',background:'#F7F8FA',borderBottom:'1px solid #E2E4E9'}}>
                    <div style={{fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Tipo de acordo</div>
                    <div style={{display:'flex',gap:'8px'}}>
 {[{v:'interno',l:'Apenas registro',desc:'Só para controle interno, sem documento formal'},{v:'formal',l:'Acordo formal',desc:'O aluno confirma as condições no app'}].map(op=>(
                        <button key={op.v} onClick={()=>setFormContrato(f=>({...f,modo:op.v}))}
                          style={{flex:1,padding:'10px 8px',borderRadius:'10px',border:formContrato.modo===op.v?`2px solid ${op.v==='formal'?'#2F6BFF':'#2F6BFF'}`:'1.5px solid #E2E4E9',background:formContrato.modo===op.v?(op.v==='formal'?'#E7EDFF':'#E7EDFF'):'white',color:formContrato.modo===op.v?(op.v==='formal'?'#2F6BFF':'#2F6BFF'):'#6E7480',fontSize:'12px',fontWeight:'700',cursor:'pointer',textAlign:'center',transition:'all 0.15s'}}>
                          <div>{op.l}</div>
                          <div style={{fontSize:'10px',fontWeight:'400',marginTop:'3px',color:formContrato.modo===op.v?(op.v==='formal'?'#2F6BFF':'#2F6BFF'):'#9AA0AB'}}>{op.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {formContrato.modo === 'formal' && (
                    <div style={{padding:'14px 16px',background:'#E7EDFF',display:'flex',flexDirection:'column',gap:'10px'}}>
                      <div style={{fontSize:'12px',color:'#1C43AE'}}><Ico n="bulb" c="#D97706" /> O aluno receberá um aviso para confirmar o acordo no app.</div>
                      {formContrato.duracao_meses > 1 && (
                        <div>
                          <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:'6px'}}>Multa por cancelamento antecipado (%)</div>
                          <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                            {[0,5,10,20].map(pct=>(
                              <button key={pct} onClick={()=>setFormContrato(f=>({...f,multa_cancelamento_pct:pct}))}
                                style={{flex:1,padding:'8px 4px',borderRadius:'8px',border:formContrato.multa_cancelamento_pct===pct?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:formContrato.multa_cancelamento_pct===pct?'#2F6BFF':'white',color:formContrato.multa_cancelamento_pct===pct?'white':'#6E7480',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                                {pct===0?'Sem multa':`${pct}%`}
                              </button>
                            ))}
                          </div>
                          {formContrato.multa_cancelamento_pct > 0 && (
                            <div style={{fontSize:'10px',color:'#2F6BFF',marginTop:'5px'}}>
                              <Ico n="info-circle" /> {formContrato.multa_cancelamento_pct <= 10 ? 'Dentro do limite do CDC (máx. 10%)' : 'Acima do limite CDC — verifique a legislação vigente'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{display:'flex',gap:'10px'}}>
                  <button onClick={() => wizardPreenchido ? setModalContrato(false) : setWizardStep(2)} style={{flex:1,padding:'13px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>{wizardPreenchido ? 'Cancelar': 'Voltar'}</button>
                  <button onClick={salvarContrato} disabled={!formContrato.valor_aula || Number(formContrato.valor_aula)<=0 || !formContrato.data_fim}
                    style={{flex:2,padding:'13px',borderRadius:'12px',border:'none',background:(!formContrato.valor_aula||Number(formContrato.valor_aula)<=0||!formContrato.data_fim)?'#E2E4E9':'linear-gradient(135deg,#0E9F6E,#0E9F6E)',color:(!formContrato.valor_aula||Number(formContrato.valor_aula)<=0||!formContrato.data_fim)?'#9AA0AB':'white',fontSize:'14px',fontWeight:'800',cursor:(!formContrato.valor_aula||Number(formContrato.valor_aula)<=0||!formContrato.data_fim)?'not-allowed':'pointer'}}>
                    <Ico n="circle-check" c="#0E9F6E" /> Confirmar contrato
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
        </Portal>
      )}

      {}
      <Modal open={modalConvite} onClose={() => { setModalConvite(false); setConviteAluno(null); setConvPasso(0); }} title={conviteAluno ? `Convidar ${conviteAluno.nome.split(' ')[0]}` : 'Cadastrar vários de uma vez'} uso="convite">
        {(() => {
          const nome     = conviteAluno?.nome?.split(' ')[0] || 'aluno';
          const fone     = (conviteAluno?.telefone || '').replace(/\D/g, '');
          const email    = conviteAluno?.email || '';
          // A mensagem vai no WhatsApp do professor, entao quem fala e' ele — nao a plataforma.
          // Ordem: o que muda pro aluno → os dados do treino dele (prova de que nao e' spam)
          // → uma acao so'. v141: no iPhone o caminho e' o app da App Store (TEXTO_BAIXAR_APP);
          // no Android o convite segue abrindo direto no navegador.
          const msgTexto = conviteAluno
            ? textoWhatsConviteAluno({ nomeAluno: conviteAluno.nome, resumo: resumoConvite, proposta: propostaConvite, url: linkConvite })
            : textoWhatsCadastroGeral(linkConvite);
          const waUrl    = fone
            ? `https://wa.me/55${fone.replace(/^55(?=\d{10,11}$)/, '')}?text=${encodeURIComponent(msgTexto)}`
            : `https://wa.me/?text=${encodeURIComponent(msgTexto)}`;
          const emailAssunto = encodeURIComponent(`Seu acesso ao Plen — ${professor.nome}`);
          const emailCorpo   = encodeURIComponent(msgTexto);
          const mailtoUrl    = email ? `mailto:${email}?subject=${emailAssunto}&body=${emailCorpo}` : '';
          // v142 (item E): o link GERAL e' o "Cadastrar varios de uma vez" — vai para um aluno ou para o
          // grupo. Cada um coloca o nome e o WhatsApp e cai em "Esperando voce completar" (lista abaixo).
          if (!conviteAluno) return (
            <div className="flex flex-col gap-4">
              <div style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'12px 14px', fontSize:'13.5px', color:'#16181D', lineHeight:1.55}}>
                Mande este link para os seus alunos — <strong>um por um ou no grupo do WhatsApp</strong>. Cada um coloca o nome e o WhatsApp e aparece aqui em <strong>Esperando você completar</strong>. Aí você só escolhe o pacote e os dias.
                <div style={{fontSize:'12px', color:'#6E7480', marginTop:'6px'}}>O link vale por 30 dias e serve para quantos alunos você quiser.</div>
              </div>
              <div>
                <div style={{fontSize:'12px',color:'#6E7480',marginBottom:'6px'}}>A mensagem que vai sair do seu WhatsApp:</div>
                <div style={{background:'#DCF8C6',border:'1px solid #B7E9D6',borderRadius:'12px',padding:'12px 14px',
                             fontSize:'13px',color:'#0A5B40',whiteSpace:'pre-wrap',overflowWrap:'anywhere',lineHeight:1.55}}>
                  {msgTexto}
                </div>
              </div>
              <button onClick={() => { fConv.fim('whatsapp', { tem_fone: false, pessoal: false }); compartilharOuWhats(waUrl, 'convite_geral'); }}
                style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'8px',minHeight:'48px',background:'linear-gradient(135deg,#25D366,#128C7E)',color:'white',border:'none',borderRadius:'12px',padding:'13px',fontSize:'15px',fontWeight:'800',cursor:'pointer'}}>
                <Ico n="message-circle" c="white" /> Mandar no WhatsApp
              </button>
              <button onClick={() => { uso.acao('convite.copiar', { pessoal: false, o_que: 'mensagem' }); fConv.fim('copiou'); copiarTextoSM(msgTexto, 'Mensagem copiada. Cole no grupo ou na conversa.', 'Não consegui copiar. Toque em "Mandar no WhatsApp".'); }}
                style={{minHeight:'44px',background:'white',color:'#3D424C',border:'1.5px solid #E2E4E9',borderRadius:'12px',padding:'12px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>
                Copiar a mensagem
              </button>
              <button onClick={() => { uso.acao('convite.copiar', { pessoal: false, o_que: 'link' }); fConv.fim('copiou'); copiarTextoSM(linkConvite, 'Link copiado!'); }}
                style={{minHeight:'44px',background:'transparent',color:'#2F6BFF',border:'none',padding:'8px',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                Copiar só o link
              </button>
            </div>
          );
          return (
            <div className="flex flex-col gap-4">
              {conviteAluno ? (
                <div style={{background:'#E7EDFF',border:'1px solid #C2D4FF',borderRadius:'12px',padding:'12px 16px',fontSize:'13px',color:'#1C43AE'}}>
                  <div style={{fontWeight:'700',marginBottom:'4px'}}><Ico n="user" /> {conviteAluno.nome}</div>
                  <div style={{display:'flex',flexWrap:'wrap',gap:'10px'}}>
                    {fone  && <span style={{fontSize:'12px',color:'#2F6BFF'}}><Ico n="phone" /> {conviteAluno.telefone}</span>}
                    {email && <span style={{fontSize:'12px',color:'#2F6BFF'}}><Ico n="mail" /> {email}</span>}
                  </div>
                  <div style={{fontSize:'12px',color:'#6E9BFF',marginTop:'6px'}}>Ao clicar no link, ele cria a conta e acessa automaticamente. <strong>Válido por 30 dias.</strong></div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-blue-700">
                  <Ico n="mail" /> Convite geral — o aluno será vinculado a você ao criar a conta. <strong>Válido por 30 dias.</strong>
                </div>
              )}

              {conviteAluno && (
                <div>
                  <div style={{fontSize:'12px',color:'#6E7480',marginBottom:'6px'}}>A mensagem que vai sair do seu WhatsApp:</div>
                  <div style={{background:'#DCF8C6',border:'1px solid #B7E9D6',borderRadius:'12px',padding:'12px 14px',
                               fontSize:'13px',color:'#0A5B40',whiteSpace:'pre-wrap',overflowWrap:'anywhere',lineHeight:1.55}}>
                    {msgTexto.replace(/\*/g, '')}
                  </div>
                </div>
              )}

              {}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs font-mono text-gray-600 break-all select-all">{linkConvite}</div>

              {}
              <Button onClick={() => { navigator.clipboard.writeText(linkConvite).then(() => { uso.acao('convite.copiar', { pessoal: !!conviteAluno }); fConv.fim('copiou'); toastSM('Link copiado!'); }, (e) => { fConv.barrar('falha_copiar'); uso.erro('erro.copiar', e?.message || e); toastSM('Não consegui copiar. Segure o dedo sobre o link para copiar.'); }); }} className="w-full justify-center">
                <Ico n="clipboard-list" /> Copiar link
              </Button>

              {}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                {}
                <button onClick={() => { fConv.fim('whatsapp', { tem_fone: !!fone, pessoal: !!conviteAluno }); compartilharOuWhats(waUrl, conviteAluno ? 'convite' : 'convite_geral'); }}
                  style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',background:'linear-gradient(135deg,#25D366,#128C7E)',color:'white',border:'none',borderRadius:'12px',padding:'11px 8px',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  <svg viewBox="0 0 24 24" fill="currentColor" style={{width:'17px',height:'17px',flexShrink:0}}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {fone ? 'WhatsApp' : 'WhatsApp'}
                </button>

                {}
                <button
                  onClick={() => { if (mailtoUrl) { fConv.fim('email'); window.location.href = mailtoUrl; } }}
                  disabled={!email}
                  title={!email ? 'Adicione o e-mail do aluno para usar este canal' : `Enviar para ${email}`}
                  style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'7px',
                    background: email ? 'linear-gradient(135deg,#2F6BFF,#2455D6)' : '#F0F1F4',
                    color: email ? 'white' : '#9AA0AB',
                    border: email ? 'none' : '1.5px solid #E2E4E9',
                    borderRadius:'12px',padding:'11px 8px',fontSize:'13px',fontWeight:'700',
                    cursor: email ? 'pointer' : 'not-allowed',opacity: email ? 1 : 0.7}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:'17px',height:'17px',flexShrink:0}}>
                    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  E-mail
                </button>
              </div>

              {}
              {conviteAluno && (!fone || !email) && (
                <div style={{fontSize:'11px',color:'#9AA0AB',textAlign:'center',lineHeight:'1.6'}}>
                  {!fone  && <span><Ico n="phone" /> Sem telefone — WhatsApp abre sem destino fixo. </span>}
                  {!email && <span><Ico n="mail" /> Sem e-mail cadastrado — adicione no perfil do aluno para habilitar envio por e-mail.</span>}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

function Agenda({ professor }) {
  useAoDesfazerSM(() => { loadAulasMonth(); });   // v142: desfez (faixa ou item) — a agenda relê
  const [alunos, setAlunos] = React.useState([]);
  const [alunoId, setAlunoId] = React.useState('');
  const [agendas, setAgendas] = React.useState([]);
  const [aulasMonth, setAulasMonth] = React.useState([]);
  const [currentMonth, setCurrentMonth] = React.useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [modal, setModal] = React.useState(false);
  const [form, setForm] = React.useState({ dia_semana: 1, hora: '08:00' });
  const [reagendandoCal, setReagendandoCal] = React.useState(null);
  const [novaDataCal, setNovaDataCal] = React.useState('');
  const [novaHoraCal, setNovaHoraCal] = React.useState('');
  const dias = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
  const diasAbbr = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
  const mesesNomes = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  const cores = ['#2F6BFF','#0E9F6E','#D97706','#DC2626','#2455D6','#2F6BFF','#65A30D','#E11D48'];
  const corAluno = React.useMemo(() => {
    const map = {};
    alunos.forEach((a, i) => { map[a.id] = cores[i % cores.length]; });
    return map;
  }, [alunos]);

  function calcHoraFim(hora, minutos = 60) {
    if (!hora) return '';
    const [h, m] = hora.slice(0,5).split(':').map(Number);
    const total = h * 60 + m + minutos;
    return `${String(Math.floor(total/60)%24).padStart(2,'0')}:${String(total%60).padStart(2,'0')}`;
  }

  const [conflitoReagendar, setConflitoReagendar] = React.useState([]);
  React.useEffect(() => {
    if (!reagendandoCal || !novaDataCal) { setConflitoReagendar([]); return; }
    const hora = novaHoraCal || reagendandoCal.hora?.slice(0,5) || '08:00';
    const timer = setTimeout(async () => {
      const { data } = await supabase.from('aulas')
        .select('id, aluno_id, alunos(nome)')
        .eq('professor_id', professor.id)
        .eq('data', novaDataCal)
        .eq('hora', hora)
        .eq('status', 'agendada')
        .neq('aluno_id', reagendandoCal.aluno_id || reagendandoCal.alunos?.id || 'x');
      setConflitoReagendar(data || []);
    }, 350);
    return () => clearTimeout(timer);
  }, [novaDataCal, novaHoraCal, reagendandoCal]);

  const [diasExpandidos, setDiasExpandidos] = React.useState({});
  const MAX_PILLS = 5;
  // Celular: o mes vira mapa (dia + pontinhos) e o dia tocado aparece embaixo em lista —
  // o mesmo desenho da tela Aulas (Cadu, 23/09: "06:00 Cris" em 50px vira borrao).
  const ehMobile = useEhMobile();
  const [diaSel, setDiaSel] = React.useState(new Date().toISOString().split('T')[0]);

  const [modalConflitos, setModalConflitos] = React.useState(false);
  const [conflitosExistentes, setConflitosExistentes] = React.useState([]);
  const [carregandoConflitos, setCarregandoConflitos] = React.useState(false);

  const [confirmandoPassada, setConfirmandoPassada] = React.useState(null);

  const [acaoAula, setAcaoAula] = React.useState(null);
  // v140: dentro do "O que deseja fazer?": null | 'opcoes' (perguntar se vem...) | 'naovou'
  const [opcoesAgenda, setOpcoesAgenda] = React.useState(null);
  React.useEffect(() => { setOpcoesAgenda(null); }, [acaoAula?.id]);
  // aula de hoje (antes do inicio) ou de amanha, individual, agendada e sem registro
  const podeOpcoesAgenda = a => {
    if (!a || a.status !== 'agendada' || a.desfecho_prof || !aulaIndividualSM(a)) return false;
    const ini = inicioAulaSP(a), d = String(a.data || '').slice(0, 10);
    return !!ini && ini > new Date() && (d === hojeSP() || d === hojeSP(1));
  };

  const [alterandoFixo, setAlterandoFixo] = React.useState(null);
  const [novoDiaFixo, setNovoDiaFixo] = React.useState(1);
  const [novaHoraFixa, setNovaHoraFixa] = React.useState('08:00');
  const [salvandoFixo, setSalvandoFixo] = React.useState(false);
  // Central de uso: as janelas desta tela (todas sem <Modal>, menos "Adicionar horario")
  React.useEffect(() => { if (modalConflitos) uso.janela('agenda_conflitos'); }, [modalConflitos]);
  React.useEffect(() => { if (confirmandoPassada) uso.janela('agenda_aula_passada'); }, [!!confirmandoPassada]);
  React.useEffect(() => { if (acaoAula) uso.janela('agenda_aula_acao'); }, [!!acaoAula]);
  React.useEffect(() => { if (alterandoFixo) uso.janela('agenda_horario_fixo'); }, [!!alterandoFixo]);
  React.useEffect(() => { if (reagendandoCal) uso.janela('aula_reagendar'); }, [!!reagendandoCal]);

  async function auditarConflitos() {
    setCarregandoConflitos(true);
    setModalConflitos(true);
    const hoje = new Date().toISOString().split('T')[0];

    const { data } = await supabase.from('aulas')
      .select('id, data, hora, aluno_id, alunos(id, nome)')
      .eq('professor_id', professor.id)
      .eq('status', 'agendada')
      .gte('data', hoje)
      .order('data').order('hora');

    const grupos = {};
    (data || []).forEach(a => {
      const key = `${a.data}|${(a.hora||'').slice(0,5)}`;
      if (!grupos[key]) grupos[key] = [];
      grupos[key].push(a);
    });
    const conflitos = Object.entries(grupos)
      .filter(([, aulas]) => aulas.length > 1)
      .map(([key, aulas]) => {
        const [data, hora] = key.split('|');
        return { data, hora, aulas };
      })
      .sort((a, b) => a.data.localeCompare(b.data));
    setConflitosExistentes(conflitos);
    setCarregandoConflitos(false);
  }

  React.useEffect(() => { loadAlunos(); }, []);
  React.useEffect(() => { if (alunoId) loadAgenda(); else loadAulasMonth(); }, [alunoId, currentMonth]);

  async function loadAlunos() {
    const { data } = await supabase.from('alunos').select('id, nome').eq('professor_id', professor.id).order('nome');
    setAlunos(data || []);
  }
  async function loadAgenda() {
    const { data } = await supabase.from('agenda_semanal').select('*').eq('aluno_id', alunoId).eq('ativo', true).order('dia_semana');
    setAgendas(data || []);
  }
  async function loadAulasMonth() {
    const y = currentMonth.getFullYear();
    const m = String(currentMonth.getMonth()+1).padStart(2,'0');
    const inicio = `${y}-${m}-01`;
    const fim = new Date(y, currentMonth.getMonth()+1, 0).toISOString().split('T')[0];
    // v140: + user_id e telefone do aluno (as Opcoes da aula: perguntar se vem / nao vou poder dar)
    const { data } = await supabase.from('aulas').select('*, alunos(id, nome, user_id, telefone)').eq('professor_id', professor.id).gte('data', inicio).lte('data', fim).in('status', ['agendada','realizada']).order('hora');
    setAulasMonth(data || []);
  }

  async function adicionarHorario() { uso.acao('agenda.horario_add'); await supabase.from('agenda_semanal').insert({ aluno_id: alunoId, professor_id: professor.id, dia_semana: Number(form.dia_semana), hora: form.hora }); setModal(false); loadAgenda(); }
  async function removerHorario(id) { await supabase.from('agenda_semanal').update({ ativo: false }).eq('id', id); loadAgenda(); }

  // v131 (Etapa 6): remarcar_aula confere o horario por intervalo (alerta com nomes; o professor
  // pode marcar mesmo assim), acerta a renovacao do pacote e avisa o aluno. Sairam o update direto,
  // o ajuste manual de data_fim e o aviso 'renovacao' montado no app.
  async function executarReagendarCal() {
    if (!reagendandoCal || !novaDataCal) return;
    const novaHora = novaHoraCal || reagendandoCal.hora?.slice(0,5) || '08:00';
    const r = await remarcarAulaProf(reagendandoCal, novaDataCal, novaHora, { origem: 'agenda', nomeAluno: reagendandoCal.alunos?.nome });
    if (!r) return;   // recusou ou "Escolher outro": a janela continua aberta
    setReagendandoCal(null); setNovaDataCal(''); setNovaHoraCal('');
    loadAulasMonth();
  }

  async function executarConfirmarPassada(aula, status) {
    // passa pelo consenso: "dada" so' desconta quando o aluno concorda (ou 24h de silencio)
    await aplicarDesfechoProfessor(aula, status, { nomeAluno: aula.alunos?.nome, origem: 'agenda' });
    setConfirmandoPassada(null);
    loadAulasMonth();
  }

  // v131 (Etapa 7): mudar o horario fixo MOVE as aulas futuras daquele horario (data e hora), em
  // vez de apagar e recriar — apagar perdia o vinculo "esta e' a aula que foi para o fim do pacote"
  // (reposta_de) e deixava o desfazer sem saber o que tirar. Cada aula vai para o novo dia da MESMA
  // semana (nunca para o passado), a mesma conta da ficha do aluno (salvarAlteracaoHorario). Aula
  // com registro ou com aviso do aluno fica onde esta'. No pacote, a renovacao acompanha a ultima aula.
  async function executarAlterarFixo() {
    if (!alterandoFixo || salvandoFixo) return;
    const alunoIdFixo = alterandoFixo.aluno_id;
    const horaAntiga = alterandoFixo.hora?.slice(0, 5) || '08:00';
    const diaAntigo  = new Date(alterandoFixo.data + 'T12:00:00').getDay();
    const novoDia = Number(novoDiaFixo), novaHora = String(novaHoraFixa || '').slice(0, 5);
    if (!novaHora) { uso.bloqueio('agenda_horario_fixo', null, null, 'sem_hora'); toastSM('Escolha o horário'); return; }
    if (novoDia === diaAntigo && novaHora === horaAntiga) { setAlterandoFixo(null); return; }
    setSalvandoFixo(true);
    const hojeStr = new Date().toLocaleDateString('sv-SE');

    const { data: contratosL } = await supabase.from('contratos').select('id, data_fim, tipo_cobranca, turma_id')
      .eq('aluno_id', alunoIdFixo).eq('status', 'ativo')
      .order('data_inicio', { ascending: false }).limit(1);
    const contrato = contratosL && contratosL[0];
    if (!contrato) { setSalvandoFixo(false); uso.bloqueio('agenda_horario_fixo', null, null, 'sem_acordo'); toastSM('Este aluno não tem acordo em vigor — não dá para mudar o horário fixo.'); return; }

    const { data: agItems } = await supabase.from('agenda_semanal')
      .select('id').eq('aluno_id', alunoIdFixo).eq('professor_id', professor.id)
      .eq('dia_semana', diaAntigo).eq('hora', horaAntiga).eq('ativo', true);
    const { error: eAg } = agItems && agItems.length > 0
      ? await supabase.from('agenda_semanal').update({ dia_semana: novoDia, hora: novaHora }).eq('id', agItems[0].id)
      : await supabase.from('agenda_semanal').insert({ aluno_id: alunoIdFixo, professor_id: professor.id, dia_semana: novoDia, hora: novaHora, ativo: true });
    if (eAg) { setSalvandoFixo(false); uso.erro('erro.agenda_horario_fixo', eAg.message); toastSM('Não foi possível mudar o horário agora. Tente de novo.'); return; }

    // v155: + contrato_id — as aulas do proximo pacote ficam como foram combinadas (igual a ficha do aluno)
    const [{ data: futuras }, { data: proxL }] = await Promise.all([
      supabase.from('aulas')
        .select('id, data, hora, turma_id, sessao_id, contrato_id, desfecho_prof, aviso_aluno_em, desfeita_em')
        .eq('aluno_id', alunoIdFixo).eq('professor_id', professor.id)
        .eq('status', 'agendada').gte('data', hojeStr),
      supabase.from('contratos').select('id').eq('aluno_id', alunoIdFixo).eq('status', 'proximo'),
    ]);
    const idsProxF = (proxL || []).map(k => k.id);
    const noSlot = (futuras || []).filter(au => !au.turma_id && !au.sessao_id
      && new Date(au.data + 'T12:00:00').getDay() === diaAntigo && String(au.hora || '').slice(0, 5) === horaAntiga);
    const doSlot = noSlot.filter(au => !idsProxF.includes(au.contrato_id));
    const txtProxF = noSlot.length > doSlot.length ? ` O próximo pacote continua ${dias[diaAntigo]} às ${horaAntiga}.` : '';
    const alvo = doSlot.filter(au => !au.desfecho_prof && !avisoVigenteAula(au));
    let movidas = 0;
    const novasDatas = await datasTrocaHorarioSM(alunoIdFixo, alvo, diaAntigo, novoDia, novaHora, hojeStr);
    for (const au of alvo) {
      const { error } = await supabase.from('aulas').update({ data: novasDatas[au.id], hora: novaHora }).eq('id', au.id);
      if (!error) movidas++;
    }
    if (contrato.tipo_cobranca !== 'mensal' && !contrato.turma_id) {
      const { data: ult } = await supabase.from('aulas').select('data').eq('contrato_id', contrato.id)
        .in('status', ['agendada', 'aguardando_confirmacao']).order('data', { ascending: false }).limit(1).maybeSingle();
      if (ult?.data && ult.data !== contrato.data_fim) await supabase.from('contratos').update({ data_fim: ult.data }).eq('id', contrato.id);
    }
    await supabase.from('notificacoes').insert({
      aluno_id: alunoIdFixo, professor_id: professor.id, tipo: 'reagendamento_fixo',
      mensagem: `Seu horário fixo foi alterado para ${dias[novoDia]} às ${novaHora}. ${movidas} aula${movidas !== 1 ? 's' : ''} ajustada${movidas !== 1 ? 's' : ''} na agenda.${txtProxF}`,
    });

    setSalvandoFixo(false);
    setAlterandoFixo(null);
    loadAulasMonth();
    uso.acao('agenda.horario_fixo', { n: movidas });
    const ficaram = doSlot.length - alvo.length;
    toastSM(`Horário fixo atualizado — ${movidas} aula${movidas !== 1 ? 's' : ''} ajustada${movidas !== 1 ? 's' : ''}. Aluno avisado.${txtProxF}`
      + (ficaram > 0 ? ` ${ficaram} aula${ficaram !== 1 ? 's' : ''} com aviso ou registro ${ficaram !== 1 ? 'ficaram' : 'ficou'} no horário antigo.` : '')
      + (movidas < alvo.length ? ` ${alvo.length - movidas} não ${alvo.length - movidas !== 1 ? 'puderam' : 'pôde'} ser movida${alvo.length - movidas !== 1 ? 's' : ''} — confira na agenda.` : ''));
  }

  async function gerarAulas() {
    if (!alunoId) return;
    const { data: contrato } = await supabase.from('contratos').select('*, planos(*)').eq('aluno_id', alunoId).eq('status', 'ativo').order('data_inicio', { ascending: false }).limit(1).maybeSingle();
    if (!contrato) { uso.bloqueio('agenda_gerar', null, null, 'sem_acordo'); toastSM('Este aluno não tem acordo em vigor'); return; }
    const aulas = [];
    const inicio = new Date(contrato.data_inicio + 'T12:00:00');
    const fim = new Date(contrato.data_fim + 'T12:00:00');
    window.__loopSM = 0; const atual = new Date(inicio);
    while (atual <= fim && (window.__loopSM = (window.__loopSM || 0) + 1) < 800) {
      for (const ag of agendas) { if (atual.getDay() === ag.dia_semana) { aulas.push({ aluno_id: alunoId, professor_id: professor.id, contrato_id: contrato.id, data: atual.toISOString().split('T')[0], hora: ag.hora, status: 'agendada', consome_credito: false }); } }
      atual.setDate(atual.getDate() + 1);
    }
    if (aulas.length === 0) { uso.bloqueio('agenda_gerar', null, null, 'nada_gerado'); toastSM('Nenhuma aula gerada. Verifique a agenda e as datas do acordo.'); return; }
    // NAO destrutivo: preserva aulas ja realizadas, canceladas e remarcadas
    const chaveAula = a => `${a.data}|${(a.hora || '').slice(0,5)}`;
    const { data: existentes } = await supabase.from('aulas').select('data,hora').eq('aluno_id', alunoId);
    const jaTem = new Set((existentes || []).map(chaveAula));
    // v155: horario repetido na agenda semanal gerava a mesma aula duas vezes — o banco recusava o lote inteiro
    const novas = aulas.filter(a => { const k = chaveAula(a); if (jaTem.has(k)) return false; jaTem.add(k); return true; });
    if (novas.length > 0) {
      const { error: eIns } = await supabase.from('aulas').insert(novas);
      if (eIns) {
        if (ehAulaRepetidaSM(eIns)) uso.bloqueio('agenda_gerar', null, null, 'aula_mesmo_horario'); else uso.erro('erro.agenda_gerar', eIns.message);
        toastSM(ehAulaRepetidaSM(eIns) ? 'Nenhuma aula criada: o aluno já tem aula em um desses dias e horários. Confira a agenda.'
          : 'Não foi possível criar as aulas agora. Tente de novo.');
        return;
      }
    }
    uso.acao('agenda.gerar_aulas', { n: novas.length });
    await recalcularCreditos(supabase, alunoId);
    const jaEx = aulas.length - novas.length;
    toastSM(novas.length > 0
      ? `${novas.length} aula${novas.length !== 1 ? 's' : ''} criada${novas.length !== 1 ? 's' : ''}.`
        + (jaEx > 0 ? ` ${jaEx} já existia${jaEx !== 1 ? 'm' : ''} e ${jaEx !== 1 ? 'ficaram' : 'ficou'} como estava${jaEx !== 1 ? 'm' : ''}.` : '')
      : 'Nenhuma aula nova. As aulas do período já existem.');
  }

  const agendaByDia = dias.map((d, i) => ({ dia: d, abbr: diasAbbr[i], index: i, horarios: agendas.filter(a => a.dia_semana === i) }));

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth()+1, 0).getDate();
  const firstDayOfWeek = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const hoje = new Date().toISOString().split('T')[0];
  const calCells = [];
  for (let i = 0; i < firstDayOfWeek; i++) calCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calCells.push(d);
  const weeks = [];
  for (let i = 0; i < calCells.length; i += 7) weeks.push(calCells.slice(i, i+7));

  function aulasParaDia(day) {
    if (!day) return [];
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return aulasMonth.filter(a => a.data === dateStr);
  }

  return (
    <div className="module-page" style={{maxWidth:'1100px', margin:'0 auto'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'24px', gap:'16px', flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:'22px', fontWeight:'800', color:'#16181D', letterSpacing:'-0.5px'}}>Calendário</div>
          <div style={{fontSize:'13px', color:'#9AA0AB', marginTop:'4px'}}>{alunoId ? 'Horários fixos do aluno' : 'Visão geral do mês — todos os alunos'}</div>
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap'}}>
          <button onClick={auditarConflitos}
            style={{display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'10px', border:'1.5px solid #FDE68A', background:'#FFFBEB', color:'#92400E', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
            <Ico n="alert-triangle" c="#D97706" /> Verificar conflitos
          </button>
          <select value={alunoId} onChange={e => { uso.acao('agenda.filtro_aluno', { todos: !e.target.value }); setAlunoId(e.target.value); }}
            style={{border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'9px 14px', fontSize:'13px', color:'#16181D', background:'white', outline:'none', cursor:'pointer'}}>
            <option value=""><Ico n="calendar" /> Visão geral (todos)</option>
            {alunos.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </select>
        </div>
      </div>

      {}
      {modalConflitos && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setModalConflitos(false)}>
          <div style={{background:'white',borderRadius:'20px',boxShadow:'0 24px 80px rgba(0,0,0,0.2)',width:'100%',maxWidth:'520px',maxHeight:'88vh',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid #E2E4E9',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
              <div>
                <div style={{fontSize:'16px',fontWeight:'800',color:'#16181D'}}><Ico n="alert-triangle" c="#D97706" /> Conflitos de horário</div>
                <div style={{fontSize:'12px',color:'#9AA0AB',marginTop:'2px'}}>Aulas futuras com mesmo dia e horário</div>
              </div>
              <button onClick={()=>setModalConflitos(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0AB',fontSize:'22px',lineHeight:1}}><Ico n="x" /></button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>
              {carregandoConflitos ? (
                <div style={{textAlign:'center',padding:'40px 0',color:'#9AA0AB',fontSize:'13px'}}>Verificando conflitos...</div>
              ) : conflitosExistentes.length === 0 ? (
                <div style={{textAlign:'center',padding:'40px 0'}}>
                  <div style={{fontSize:'40px',marginBottom:'12px'}}><Ico n="circle-check" c="#0E9F6E" /></div>
                  <div style={{fontSize:'15px',fontWeight:'700',color:'#0A6F4D'}}>Nenhum conflito encontrado</div>
                  <div style={{fontSize:'13px',color:'#9AA0AB',marginTop:'6px'}}>Todas as aulas futuras estão em horários exclusivos</div>
                </div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                  <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:'10px',padding:'10px 14px',fontSize:'12px',color:'#92400E',marginBottom:'4px'}}>
                    {conflitosExistentes.length} conflito{conflitosExistentes.length!==1?'s':''} encontrado{conflitosExistentes.length!==1?'s':''}. Reagende um dos alunos em cada caso.
                  </div>
                  {conflitosExistentes.map((c, ci) => {
                    const fmtData = new Date(c.data+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'short',day:'2-digit',month:'2-digit'});
                    const hFim = (() => { const [h,m]=c.hora.split(':').map(Number); const t=h*60+m+60; return `${String(Math.floor(t/60)%24).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`; })();
                    return (
                      <div key={ci} style={{border:'1.5px solid #FDE68A',borderRadius:'12px',overflow:'hidden'}}>
                        <div style={{background:'#FFFBEB',padding:'10px 14px',display:'flex',alignItems:'center',gap:'10px'}}>
                          <div style={{flex:1}}>
                            <div style={{fontSize:'13px',fontWeight:'800',color:'#92400E'}}>{fmtData}</div>
                            <div style={{fontSize:'12px',color:'#B45309',fontWeight:'600'}}>{c.hora}–{hFim}</div>
                          </div>
                          <span style={{background:'#FEF3C7',color:'#92400E',fontSize:'11px',fontWeight:'700',padding:'3px 8px',borderRadius:'20px'}}>
                            {c.aulas.length} alunos
                          </span>
                        </div>
                        <div style={{padding:'10px 14px',display:'flex',flexDirection:'column',gap:'6px',background:'white'}}>
                          {c.aulas.map((a, ai) => (
                            <div key={a.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'}}>
                              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                                <div style={{width:'28px',height:'28px',borderRadius:'50%',background:'#E7EDFF',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',color:'#2F6BFF',flexShrink:0}}>
                                  {(a.alunos?.nome||'?')[0].toUpperCase()}
                                </div>
                                <span style={{fontSize:'13px',fontWeight:'600',color:'#16181D'}}>{a.alunos?.nome}</span>
                              </div>
                              {ai > 0 && (
                                <button onClick={()=>{ setReagendandoCal({...a, aluno_id: a.aluno_id}); setNovaDataCal(a.data); setNovaHoraCal(a.hora?.slice(0,5)||''); setConflitoReagendar([]); setModalConflitos(false); }}
                                  style={{fontSize:'11px',padding:'5px 12px',borderRadius:'8px',border:'1.5px solid #C2D4FF',background:'white',color:'#2F6BFF',cursor:'pointer',fontWeight:'700',whiteSpace:'nowrap'}}>
                                  <Ico n="pencil" /> Reagendar
                                </button>
                              )}
                              {ai === 0 && <span style={{fontSize:'11px',color:'#9AA0AB',fontStyle:'italic'}}>mantém horário</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        </Portal>
      )}

      {!alunoId && (
        <div>
          {}
          <div style={{display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px'}}>
            <button onClick={() => { uso.acao('agenda.mes', { delta: -1 }); setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth()-1, 1)); }}
              style={{width:'32px', height:'32px', borderRadius:'8px', border:'1.5px solid #E2E4E9', background:'white', cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center', justifyContent:'center', color:'#6E7480'}}>‹</button>
            <div style={{fontSize:'15px', fontWeight:'800', color:'#16181D', minWidth:'160px', textAlign:'center'}}>
              {mesesNomes[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            <button onClick={() => { uso.acao('agenda.mes', { delta: 1 }); setCurrentMonth(m => new Date(m.getFullYear(), m.getMonth()+1, 1)); }}
              style={{width:'32px', height:'32px', borderRadius:'8px', border:'1.5px solid #E2E4E9', background:'white', cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center', justifyContent:'center', color:'#6E7480'}}>›</button>
            <div style={{fontSize:'12px', color:'#9AA0AB', marginLeft:'8px'}}>{aulasMonth.length} aulas no mês</div>
          </div>
          {}
          <div style={{background:'white', borderRadius:'16px', border:'1px solid #E2E4E9', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
            {}
            <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', background:'#F7F8FA', borderBottom:'1px solid #E2E4E9'}}>
              {diasAbbr.map(d => (
                <div key={d} style={{padding:'8px', textAlign:'center', fontSize:'11px', fontWeight:'700', color:'#6E7480', letterSpacing:'0.05em'}}>{d}</div>
              ))}
            </div>
            {}
            {weeks.map((week, wi) => (
              <div key={wi} style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom: wi < weeks.length-1 ? '1px solid #F0F1F4' : 'none'}}>
                {week.map((day, di) => {
                  const dateStr = day ? `${currentMonth.getFullYear()}-${String(currentMonth.getMonth()+1).padStart(2,'0')}-${String(day).padStart(2,'0')}` : null;
                  const isHoje = dateStr === hoje;
                  const aulas = day ? aulasParaDia(day) : [];
                  return (
                    <div key={di} onClick={() => { if (day && ehMobile) setDiaSel(dateStr); }}
                      style={{minHeight: ehMobile ? '44px' : '80px', padding: ehMobile ? '3px 2px' : '5px 4px', borderLeft: di > 0 ? '1px solid #F0F1F4' : 'none',
                        background: ehMobile && dateStr === diaSel ? '#E7EDFF' : isHoje && !ehMobile ? '#E7EDFF' : day ? 'white' : '#F7F8FA', position:'relative', cursor: day && ehMobile ? 'pointer' : 'default'}}>
                      {day && ehMobile && (
                        <>
                          <div style={{fontSize:'12px', textAlign:'center', fontWeight: (isHoje || dateStr === diaSel) ? '800' : '600', color: (isHoje || dateStr === diaSel) ? '#2F6BFF' : '#3D424C', marginBottom:'3px'}}>{day}</div>
                          <div style={{display:'flex', justifyContent:'center', gap:'2px', flexWrap:'wrap'}}>
                            {aulas.slice(0, 3).map(a => (
                              <span key={a.id} style={{width:'5px', height:'5px', borderRadius:'50%',
                                background: (a.status === 'agendada' && a.data < hoje) ? '#E8971A' : (corAluno[a.alunos?.id] || '#2F6BFF'),
                                opacity: a.status === 'realizada' ? 0.45 : 1}} />
                            ))}
                            {aulas.length > 3 && <span style={{fontSize:'8px', color:'#9AA0AB', lineHeight:'5px'}}>+</span>}
                          </div>
                        </>
                      )}
                      {day && !ehMobile && (
                        <>
                          <div style={{fontSize:'11px', fontWeight: isHoje ? '800' : '600', color: isHoje ? '#2F6BFF' : '#3D424C', marginBottom:'3px', display:'flex', alignItems:'center', gap:'3px'}}>
                            {day}{isHoje && <span style={{width:'4px', height:'4px', borderRadius:'50%', background:'#2F6BFF', display:'inline-block'}}></span>}
                          </div>
                          {(() => {
                            const visiveis = diasExpandidos[dateStr] ? aulas : aulas.slice(0, MAX_PILLS);
                            const ocultas = aulas.length - visiveis.length;
                            return (
                              <div style={{display:'flex', flexDirection:'column', gap:'2px'}}>
                                {visiveis.map(a => {
                                  const cor = corAluno[a.alunos?.id] || '#2F6BFF';
                                  const realizada = a.status === 'realizada';
                                  const passadaNaoConf = a.status === 'agendada' && a.data < hoje;
                                  const nome = a.alunos?.nome?.split(' ')[0] || '';
                                  const hFim = calcHoraFim(a.hora);
                                  const bgCor = passadaNaoConf ? '#6E7480' : realizada ? cor : cor;
                                  return (
                                    <div key={a.id}
                                      onClick={() => {
                                        if (passadaNaoConf) { setConfirmandoPassada(a); }
                                        else if (a.status === 'agendada') { setAcaoAula(a); }
                                      }}
                                      title={passadaNaoConf ? `${nome} ·  sem confirmação` : realizada ? `${nome} · realizada` : `${nome} · clique para opções`}
                                      style={{
                                        background: bgCor, color: 'white',
                                        borderRadius:'4px', padding:'2px 4px', fontSize:'9px', fontWeight:'700', lineHeight:'1.35',
                                        opacity: realizada ? 0.65 : 1,
                                        cursor: (passadaNaoConf || a.status === 'agendada') ? 'pointer' : 'default',
                                        overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis',
                                        border: passadaNaoConf ? '1px solid #E8971A' : 'none',
                                      }}>
                                      {passadaNaoConf && <span style={{marginRight:'2px'}}><Ico n="alert-triangle" c="#D97706" /></span>}
                                      <span style={{opacity:0.85}}>{a.hora?.slice(0,5)}–{hFim}</span>
                                      <span style={{marginLeft:'3px'}}>{nome}</span>
                                      {realizada && <span style={{opacity:0.9, marginLeft:'2px'}}><Ico n="check" /></span>}
                                    </div>
                                  );
                                })}
                                {ocultas > 0 && (
                                  <button onClick={() => setDiasExpandidos(prev => ({...prev, [dateStr]: true}))}
                                    style={{fontSize:'9px', fontWeight:'700', color:'#2F6BFF', background:'#E7EDFF', border:'none', borderRadius:'4px', padding:'2px 4px', cursor:'pointer', textAlign:'left'}}>
                                    +{ocultas} mais
                                  </button>
                                )}
                                {diasExpandidos[dateStr] && aulas.length > MAX_PILLS && (
                                  <button onClick={() => setDiasExpandidos(prev => ({...prev, [dateStr]: false}))}
                                    style={{fontSize:'9px', color:'#9AA0AB', background:'none', border:'none', cursor:'pointer', textAlign:'left', padding:'1px 0'}}>
                                    ver menos <Ico n="arrow-up" />
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          {ehMobile && (() => {
            const doDia = aulasMonth.filter(a => a.data === diaSel).sort((a, b) => String(a.hora).localeCompare(String(b.hora)));
            return (
              <div style={{marginTop:'14px'}}>
                <div style={{fontSize:'12px', fontWeight:'800', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'8px'}}>
                  {new Date(diaSel + 'T12:00:00').toLocaleDateString('pt-BR', {weekday:'long', day:'numeric', month:'long'})}
                </div>
                {doDia.length === 0
                  ? <div style={{background:'white', border:'1px solid #E2E4E9', borderRadius:'14px', padding:'18px', textAlign:'center', color:'#9AA0AB', fontSize:'13px'}}>Nenhuma aula neste dia.</div>
                  : <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                      {doDia.map(a => {
                        const passadaNaoConf = a.status === 'agendada' && a.data < hoje;
                        const realizada = a.status === 'realizada';
                        const cor = corAluno[a.alunos?.id] || '#2F6BFF';
                        return (
                          <button key={a.id}
                            onClick={() => { if (passadaNaoConf) setConfirmandoPassada(a); else if (a.status === 'agendada') setAcaoAula(a); }}
                            style={{display:'flex', alignItems:'center', gap:'12px', width:'100%', textAlign:'left', background:'white', border:'1px solid #E2E4E9', borderLeft:`4px solid ${passadaNaoConf ? '#E8971A' : cor}`, borderRadius:'12px', padding:'12px 14px', cursor: a.status === 'agendada' ? 'pointer' : 'default'}}>
                            <div style={{fontSize:'15px', fontWeight:'800', color:'#16181D', minWidth:'48px'}}>{a.hora?.slice(0,5)}</div>
                            <div style={{flex:1, minWidth:0}}>
                              <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.alunos?.nome}</div>
                              <div style={{fontSize:'12px', color: passadaNaoConf ? '#B45309' : realizada ? '#0A6F4D' : '#6E7480', fontWeight:'600'}}>
                                {passadaNaoConf ? 'Falta fechar — toque para registrar' : realizada ? 'Aula dada' : `até ${calcHoraFim(a.hora)} · toque para reagendar`}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>}
              </div>
            );
          })()}
          {alunos.length > 0 && (
            <div style={{display:'flex', gap:'12px', flexWrap:'wrap', marginTop:'12px'}}>
              {alunos.filter(a => aulasMonth.some(au => au.alunos?.id === a.id)).map(a => (
                <div key={a.id} style={{display:'flex', alignItems:'center', gap:'5px'}}>
                  <div style={{width:'10px', height:'10px', borderRadius:'3px', background: corAluno[a.id]}}></div>
                  <span style={{fontSize:'11px', color:'#6E7480'}}>{a.nome}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {alunoId && (
        <>
          {ehMobile ? (
            <div style={{display:'flex', flexDirection:'column', gap:'8px', marginBottom:'20px'}}>
              {agendaByDia.filter(d => d.index !== 0 || d.horarios.length).map(({ dia, index, horarios }) => (
                <div key={index} style={{display:'flex', alignItems:'center', gap:'10px', background: horarios.length ? '#E7EDFF' : 'white', border:'1px solid ' + (horarios.length ? '#C2D4FF' : '#E2E4E9'), borderRadius:'12px', padding:'10px 14px'}}>
                  <div style={{width:'72px', fontSize:'13px', fontWeight:'700', color: horarios.length ? '#1C43AE' : '#9AA0AB'}}>{dia}</div>
                  <div style={{flex:1, display:'flex', gap:'6px', flexWrap:'wrap'}}>
                    {horarios.length === 0 && <span style={{fontSize:'12px', color:'#C7CAD1'}}>—</span>}
                    {horarios.map(h => (
                      <span key={h.id} style={{display:'inline-flex', alignItems:'center', gap:'6px', background:'#2F6BFF', color:'white', borderRadius:'8px', padding:'5px 10px', fontSize:'13px', fontWeight:'700'}}>
                        {h.hora?.slice(0,5)}
                        <button onClick={() => removerHorario(h.id)} aria-label="Remover horário" style={{background:'none', border:'none', color:'#C2D4FF', cursor:'pointer', padding:0, display:'flex'}}><Ico n="x" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-7 gap-2 mb-6">
            {agendaByDia.map(({ dia, abbr, index, horarios }) => (
              <div key={index} className={`rounded-xl border-2 p-3 text-center ${horarios.length > 0 ? 'border-indigo-300 bg-indigo-50' : 'border-gray-100 bg-white'}`}>
                <div className="text-xs font-bold text-gray-500 mb-2">{abbr}</div>
                {horarios.map(h => (
                  <div key={h.id} className="flex items-center justify-between bg-indigo-600 text-white rounded-lg px-2 py-1 mb-1">
                    <span className="text-xs font-medium">{h.hora?.slice(0,5)}</span>
                    <button onClick={() => removerHorario(h.id)} className="text-indigo-200 hover:text-white text-xs ml-1"><Ico n="x" /></button>
                  </div>
                ))}
              </div>
            ))}
          </div>
          )}
          <div className="flex gap-3" style={{flexWrap:'wrap'}}>
            <Button onClick={() => setModal(true)}><Icons.Plus /> Adicionar Horário</Button>
            <Button variant="success" onClick={gerarAulas}><Icons.Check /> Gerar Aulas</Button>
          </div>
        </>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Adicionar Horário" uso="agenda_horario_add">
        <div className="flex flex-col gap-4">
          <Select label="Dia da semana" value={form.dia_semana} onChange={e => setForm(f => ({...f, dia_semana: e.target.value}))}>
            {dias.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </Select>
          <Input label="Horário" type="time" value={form.hora} onChange={e => setForm(f => ({...f, hora: e.target.value}))} />
          <div className="flex gap-3 pt-2"><Button onClick={adicionarHorario} className="flex-1 justify-center">Adicionar</Button><Button variant="secondary" onClick={() => setModal(false)}>Cancelar</Button></div>
        </div>
      </Modal>

      {}
      {confirmandoPassada && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setConfirmandoPassada(null)}>
          <div style={{background:'white',borderRadius:'20px',padding:'28px',maxWidth:'380px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="alert-triangle" c="#D97706" /> Aula sem confirmação</div>
            <div style={{fontSize:'13px',color:'#6E7480',marginBottom:'6px'}}>
              <strong>{confirmandoPassada.alunos?.nome}</strong>
            </div>
            <div style={{fontSize:'13px',color:'#9AA0AB',marginBottom:'22px'}}>
              {new Date(confirmandoPassada.data+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})} às {confirmandoPassada.hora?.slice(0,5)}
            </div>
            <div style={{fontSize:'13px',fontWeight:'600',color:'#3D424C',marginBottom:'12px'}}>O que aconteceu nesta aula?</div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              <button onClick={()=>executarConfirmarPassada(confirmandoPassada,'realizada')}
                style={{padding:'14px',borderRadius:'12px',border:'none',background:'#DCF5EC',color:'#0A6F4D',fontSize:'14px',fontWeight:'800',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontSize:'20px'}}><Ico n="circle-check" c="#0E9F6E" /></span>
                <div><div style={{fontWeight:'800'}}>Aula dada</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>O aluno confirma no app (24h de silêncio = vale)</div></div>
              </button>
              <button onClick={()=>executarConfirmarPassada(confirmandoPassada,'cancelada_professor')}
                style={{padding:'14px',borderRadius:'12px',border:'none',background:'#FEE2E2',color:'#991B1B',fontSize:'14px',fontWeight:'800',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontSize:'20px'}}><Ico n="circle-x" c="#DC2626" /></span>
                <div><div style={{fontWeight:'800'}}>Cancelada por mim</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>O professor cancelou</div></div>
              </button>
              <button onClick={()=>executarConfirmarPassada(confirmandoPassada,'cancelada_aluno')}
                style={{padding:'14px',borderRadius:'12px',border:'none',background:'#FFFBEB',color:'#92400E',fontSize:'14px',fontWeight:'800',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontSize:'20px'}}><Ico n="user" /></span>
                <div><div style={{fontWeight:'800'}}>Cancelada pelo aluno</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>O aluno avisou que não viria</div></div>
              </button>
            </div>
            <button onClick={()=>setConfirmandoPassada(null)} style={{marginTop:'16px',width:'100%',padding:'10px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}>Fechar</button>
          </div>
        </div>
        </Portal>
      )}

      {}
      {acaoAula && !reagendandoCal && !alterandoFixo && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setAcaoAula(null)}>
          <div style={{background:'white',borderRadius:'20px',padding:'28px',maxWidth:'360px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)',maxHeight:'90vh',overflowY:'auto',WebkitOverflowScrolling:'touch'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="calendar" /> O que deseja fazer?</div>
            <div style={{fontSize:'13px',color:'#6E7480',marginBottom: legendaPresencaProfSM(acaoAula, acaoAula.alunos?.nome) ? '4px' : '20px'}}>
              <strong>{acaoAula.alunos?.nome}</strong> · {new Date(acaoAula.data+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'short',day:'numeric',month:'short'})} às {acaoAula.hora?.slice(0,5)}
            </div>
            {/* v140: "✓ Bia vem" / "Perguntei às 21:04 · sem resposta" */}
            {(() => { const lp = legendaPresencaProfSM(acaoAula, acaoAula.alunos?.nome); return lp ? (
              <div style={{fontSize:'12.5px',fontWeight:'700',color:lp.cor,marginBottom:'16px'}}>{lp.txt}</div>
            ) : null; })()}
            {opcoesAgenda ? (
              <OpcoesAulaProfSM aula={acaoAula} nomeAluno={acaoAula.alunos?.nome} alunoUsaApp={acaoAula.alunos ? !!acaoAula.alunos.user_id : undefined}
                telefone={acaoAula.alunos?.telefone} nomeProf={professor?.nome} origem="agenda" abrir={opcoesAgenda === 'naovou' ? 'naovou' : null}
                onFechar={() => setOpcoesAgenda(null)}
                onFeito={() => { setOpcoesAgenda(null); setAcaoAula(null); loadAulasMonth(); }} />
            ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {podeOpcoesAgenda(acaoAula) && (
                <>
                  {/* com aviso de falta vigente (ou divergencia) o painel nao tem a pergunta nem o "avisou": so' o "Nao vou poder dar" */}
                  {!avisoVigenteAula(acaoAula) && !acaoAula.divergencia_em && (
                  <button onClick={()=>{ uso.acao('opcoes_aula.abrir', { origem: 'agenda' }); setOpcoesAgenda('opcoes'); }}
                    style={{padding:'14px',borderRadius:'12px',border:'1.5px solid #C2D4FF',background:'white',color:'#1C43AE',fontSize:'14px',fontWeight:'700',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px',minHeight:'44px'}}>
                    <span style={{fontSize:'20px'}}><Ico n={acaoAula.presenca_vou_em ? 'message-off' : 'help-circle'} /></span>
                    {/* v142 (B): o aluno ja' disse "Vou" (com ou sem pergunta) — nao ha' o que perguntar; o painel
                        mostra "✓ Bia vem" e o registro do aviso, se ele mudou de ideia pelo WhatsApp */}
                    {acaoAula.presenca_vou_em
                      ? <div><div style={{fontWeight:'800'}}>{primeiroNomeSM(acaoAula.alunos?.nome, 'O aluno')} avisou que não vem?</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>Mudou de ideia pelo WhatsApp ou ligação? Registre aqui</div></div>
                      : <div><div style={{fontWeight:'800'}}>Perguntar se {primeiroNomeSM(acaoAula.alunos?.nome, 'o aluno')} vem</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>Ou registrar que avisou que não vem</div></div>}
                  </button>
                  )}
                  <button onClick={()=>{ uso.acao('opcoes_aula.abrir', { origem: 'agenda_nao_vou' }); setOpcoesAgenda('naovou'); }}
                    style={{padding:'14px',borderRadius:'12px',border:'1.5px solid #FECACA',background:'#FEF2F2',color:'#991B1B',fontSize:'14px',fontWeight:'700',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px',minHeight:'44px'}}>
                    <span style={{fontSize:'20px'}}><Ico n="calendar-x" /></span>
                    <div><div style={{fontWeight:'800'}}>Não vou poder dar esta aula</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.8}}>{acaoAula.alunos && !acaoAula.alunos.user_id ? `Depois você avisa ${primeiroNomeSM(acaoAula.alunos?.nome)} pelo WhatsApp` : `${primeiroNomeSM(acaoAula.alunos?.nome)} recebe o aviso na hora`}</div></div>
                  </button>
                </>
              )}
              <button onClick={()=>{ setReagendandoCal(acaoAula); setNovaDataCal(acaoAula.data); setNovaHoraCal(acaoAula.hora?.slice(0,5)||''); setConflitoReagendar([]); setAcaoAula(null); }}
                style={{padding:'14px',borderRadius:'12px',border:'1.5px solid #C2D4FF',background:'#E7EDFF',color:'#2455D6',fontSize:'14px',fontWeight:'700',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontSize:'20px'}}><Ico n="calendar-event" /></span>
                <div><div style={{fontWeight:'800'}}>Reagendar só esta aula</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>Move apenas esta ocorrência</div></div>
              </button>
              <button onClick={()=>{ setNovoDiaFixo(new Date(acaoAula.data+'T12:00:00').getDay()); setNovaHoraFixa(acaoAula.hora?.slice(0,5)||'08:00'); setAlterandoFixo(acaoAula); setAcaoAula(null); }}
                style={{padding:'14px',borderRadius:'12px',border:'1.5px solid #B7E9D6',background:'#EFFAF5',color:'#0A5B40',fontSize:'14px',fontWeight:'700',cursor:'pointer',textAlign:'left',display:'flex',alignItems:'center',gap:'10px'}}>
                <span style={{fontSize:'20px'}}><Ico n="refresh" /></span>
                <div><div style={{fontWeight:'800'}}>Alterar horário fixo</div><div style={{fontSize:'11px',fontWeight:'500',opacity:0.7}}>Muda o horário semanal permanentemente</div></div>
              </button>
            </div>
            )}
            <button onClick={()=>setAcaoAula(null)} style={{marginTop:'16px',width:'100%',padding:'10px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}>Cancelar</button>
          </div>
        </div>
        </Portal>
      )}

      {}
      {alterandoFixo && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setAlterandoFixo(null)}>
          <div style={{background:'white',borderRadius:'20px',padding:'28px',maxWidth:'420px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="refresh" /> Alterar horário fixo</div>
            <div style={{fontSize:'13px',color:'#6E7480',marginBottom:'4px'}}><strong>{alterandoFixo.alunos?.nome}</strong></div>
            <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:'10px',padding:'10px 14px',fontSize:'12px',color:'#92400E',marginBottom:'20px'}}>
              <Ico n="alert-triangle" c="#D97706" /> Esta ação altera o horário semanal permanente. As aulas futuras desse horário passam para o novo dia e horário, na mesma semana. O aluno é avisado.
            </div>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#6E7480',marginBottom:'6px'}}>Horário atual: <span style={{color:'#16181D'}}>{dias[new Date(alterandoFixo.data+'T12:00:00').getDay()]}s às {alterandoFixo.hora?.slice(0,5)}</span></div>
            <div style={{fontSize:'13px',fontWeight:'700',color:'#16181D',marginBottom:'14px'}}>Novo horário fixo:</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'20px'}}>
              <div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'7px'}}>Dia da semana</div>
                <select value={novoDiaFixo} onChange={e=>setNovoDiaFixo(Number(e.target.value))}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',background:'white'}}>
                  {dias.map((d,i)=><option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'7px'}}>Horário</div>
                <input type="time" value={novaHoraFixa} onChange={e=>setNovaHoraFixa(e.target.value)}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
            </div>
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={()=>setAlterandoFixo(null)} style={{flex:1,padding:'12px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>Cancelar</button>
              <button onClick={executarAlterarFixo} disabled={salvandoFixo}
                style={{flex:2,padding:'12px',borderRadius:'12px',border:'none',background:salvandoFixo?'#E2E4E9':'linear-gradient(135deg,#0E9F6E,#0E9F6E)',color:salvandoFixo?'#9AA0AB':'white',fontSize:'14px',fontWeight:'800',cursor:salvandoFixo?'not-allowed':'pointer'}}>
 {salvandoFixo ? 'Salvando...': 'Confirmar alteração'}
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {}
      {reagendandoCal && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'20px',padding:'28px',maxWidth:'400px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}}>
            <div style={{fontSize:'18px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="calendar" /> Reagendar aula</div>
            <div style={{fontSize:'13px',color:'#9AA0AB',marginBottom:'22px'}}>{reagendandoCal.alunos?.nome} · original: {new Date(reagendandoCal.data+'T12:00:00').toLocaleDateString('pt-BR')} às {reagendandoCal.hora?.slice(0,5)}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'8px'}}>
              <div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'7px'}}>Nova data</div>
                <input type="date" value={novaDataCal} onChange={e=>setNovaDataCal(e.target.value)}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
              <div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'7px'}}>Novo horário</div>
                <input type="time" value={novaHoraCal} onChange={e=>setNovaHoraCal(e.target.value)}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
            </div>
            {conflitoReagendar.length > 0 && (
              <div style={{background:'#FEF3C7',border:'1.5px solid #FDE68A',borderRadius:'10px',padding:'10px 14px',marginBottom:'12px',fontSize:'12px',color:'#92400E'}}>
                <Ico n="alert-triangle" c="#D97706" /> <strong>Horário ocupado:</strong> {conflitoReagendar.map(c => c.alunos?.nome?.split(' ')[0]).join(', ')} já tem aula neste horário. Escolha outro.
              </div>
            )}
            <div style={{fontSize:'11px',color:'#9AA0AB',marginBottom:'20px'}}><Ico n="bulb" c="#D97706" /> Se a nova data ultrapassar o término do plano, ele será estendido automaticamente.</div>
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={()=>{setReagendandoCal(null);setNovaDataCal('');setNovaHoraCal('');}}
                style={{flex:1,padding:'12px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>Cancelar</button>
              <button onClick={executarReagendarCal} disabled={!novaDataCal}
                style={{flex:2,padding:'12px',borderRadius:'12px',border:'none',background:!novaDataCal?'#E2E4E9':'linear-gradient(135deg,#2F6BFF,#2F6BFF)',color:!novaDataCal?'#9AA0AB':'white',fontSize:'14px',fontWeight:'800',cursor:!novaDataCal?'not-allowed':'pointer'}}>
                <Ico n="circle-check" c="#0E9F6E" /> Confirmar reagendamento
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

function Aulas({ professor, params }) {
  useAoDesfazerSM(() => { loadAulas(); });   // v142: desfez (faixa ou item) — a lista relê
  const [aulas, setAulas] = React.useState([]);
  const [alunos, setAlunos] = React.useState([]);
  const [filtroAluno, setFiltroAluno] = React.useState('');
  const [viewMode, setViewMode] = React.useState('semana');
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [loading, setLoading] = React.useState(false);

  const cores = ['#2F6BFF','#0E9F6E','#D97706','#DC2626','#2455D6','#2F6BFF','#65A30D','#E11D48'];
  const corAluno = React.useMemo(() => { const m={}; alunos.forEach((a,i)=>{m[a.id]=cores[i%cores.length];}); return m; }, [alunos]);

  const { dataInicio, dataFim } = React.useMemo(() => {
    const pad = n => String(n).padStart(2,'0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (viewMode === 'dia') { const s=fmt(currentDate); return {dataInicio:s,dataFim:s}; }
    if (viewMode === 'semana') {
      const s = new Date(currentDate); s.setDate(currentDate.getDate()-currentDate.getDay());
      const e = new Date(s); e.setDate(s.getDate()+6);
      return { dataInicio: fmt(s), dataFim: fmt(e) };
    }
    const y=currentDate.getFullYear(), m=currentDate.getMonth();
    return { dataInicio: `${y}-${pad(m+1)}-01`, dataFim: fmt(new Date(y,m+1,0)) };
  }, [viewMode, currentDate]);

  React.useEffect(() => { supabase.from('alunos').select('id,nome').eq('professor_id',professor.id).order('nome').then(({data})=>setAlunos(data||[])); }, []);
  React.useEffect(() => { loadAulas(); }, [dataInicio, dataFim, filtroAluno]);

  async function loadAulas() {
    setLoading(true);
    // acordo: o tipo de cobranca decide se o [Devolver] aparece (mensalidade/turma nao tem o que devolver)
    // v140: + duracao_min do acordo (o prazo de 24h do "o professor nao veio" conta do fim da aula)
    let q = supabase.from('aulas').select('*, alunos(id,nome,modalidade,user_id), acordo:contratos(tipo_cobranca, turma_id, duracao_min)').eq('professor_id',professor.id).gte('data',dataInicio).lte('data',dataFim).order('data').order('hora');
    if (filtroAluno) q = q.eq('aluno_id', filtroAluno);
    const { data } = await q; setAulas(semRemarcada(data)); setLoading(false);   // v131: sem a original de aula remarcada
    carregarNaoConsegue(data || []);
  }
  // v136: aula em que o aluno disse "Nao consigo" na remarcacao do professor e que segue nesse horario,
  // sem registro: o cartao avisa (e o "Nao houve" poe "Eu desmarquei" primeiro) — para ninguem lancar
  // "falta" de quem avisou antes. Pelo dia (resposta_data = dia da aula), nao por lista de ids (o mes
  // inteiro passaria do tamanho da URL).
  const [naoConsegue, setNaoConsegue] = React.useState({});   // aula_id -> remarcacao 'recusada'
  async function carregarNaoConsegue(lista) {
    let data = null;
    try {
      ({ data } = await supabase.from('solicitacoes')
        .select('id, tipo, status, aula_id, resposta, resposta_data, resposta_hora')
        .eq('professor_id', professor.id).eq('tipo', TIPO_REMARC_PROF_SM).eq('status', 'recusada')
        .gte('resposta_data', dataInicio).lte('resposta_data', dataFim));
    } catch { data = null; }
    const m = {};
    (data || []).forEach(s => { if (remarcacaoValendoSM(s, lista.find(x => x.id === s.aula_id))) m[s.aula_id] = s; });
    setNaoConsegue(m);
  }
  // "Desmarcar" / "Eu desmarquei" do "nao consegue" (Pendencias e Inicio): confirma ANTES, com o que o
  // banco vai fazer (previa). Antes abria o "Nao houve" generico, cuja 1a opcao (um toque, sem
  // confirmar) era "Aluno furou — consome 1 aula".
  const [desmarcando, setDesmarcando] = React.useState(null);   // { aula, carregando, txt, busy }
  function abrirDesmarcar(a) {
    uso.janela('aula_desmarcar_confirmar');
    setNaoHouveAulaId(null);
    setDesmarcando({ aula: a, carregando: true, txt: null, busy: false });
    previaCancelarAulaProf(a.id, 'professor', 'outro')
      .then(pv => setDesmarcando(d => (d && d.aula.id === a.id ? { ...d, carregando: false, txt: (pv && pv.txt) || null } : d)))
      .catch(() => setDesmarcando(d => (d && d.aula.id === a.id ? { ...d, carregando: false } : d)));
  }
  async function confirmarDesmarcar() {
    const d = desmarcando;
    if (!d || d.busy) return;
    setDesmarcando({ ...d, busy: true });
    const r = await cancelarAulaProf(d.aula, 'professor', 'outro', { origem: 'remarcacao_recusada', nomeAluno: d.aula.alunos?.nome });
    if (!r) { setDesmarcando(x => (x ? { ...x, busy: false } : x)); return; }
    setDesmarcando(null);
    loadAulas();
  }

  // Tudo passa por aplicarDesfechoProfessor: aula dada/falta vao para o aluno confirmar
  // (consenso); cancelamentos voltam a aula para o fim da fila. Antes, "professor
  // desmarcou" esticava a data do acordo por uma conta antiga (7 dias / aulas por semana)
  // que nao batia com a fila do pacote.
  // v131: cancelar = professor_cancelar_aula (o banco avisa o aluno com o que aconteceu de fato);
  // desfazer = desfazer_desfecho_aula. Saiu o aviso montado aqui ("foi para o fim do seu pacote"),
  // que valia so' para pacote.
  async function marcarStatus(aulaId, novoStatus, alunoId) {
    const aula = aulas.find(x => x.id === aulaId) || { id: aulaId, aluno_id: alunoId };
    const estado = await aplicarDesfechoProfessor(aula, novoStatus,
      { forcar: !!aula.divergencia_em, nomeAluno: aula.alunos?.nome, origem: 'aulas', alunoSemApp: !!aula.alunos && !aula.alunos.user_id });
    if (estado) loadAulas();
  }
  // Aula lancada pelo aluno (aguardando_confirmacao): Confirmar/Recusar daqui tambem (Etapa 7)
  async function responderPropostaAulas(a, aceita) {
    const r = await responderPropostaProf(a, aceita, { origem: 'aulas', nomeAluno: a.alunos?.nome });
    if (r) loadAulas();
  }

  function navigate(delta) {
    setCurrentDate(d => {
      const nd = new Date(d);
      if (viewMode==='dia') nd.setDate(d.getDate()+delta);
      else if (viewMode==='semana') nd.setDate(d.getDate()+delta*7);
      else nd.setMonth(d.getMonth()+delta);
      return nd;
    });
  }

  const mesesAbr = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const mesesFull = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
  const diasAbbr = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
  const hoje = new Date().toISOString().split('T')[0];

  const periodLabel = React.useMemo(() => {
    if (viewMode==='dia') return currentDate.toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'});
    if (viewMode==='semana') {
      const s=new Date(currentDate); s.setDate(currentDate.getDate()-currentDate.getDay());
      const e=new Date(s); e.setDate(s.getDate()+6);
      return `${s.getDate()} ${mesesAbr[s.getMonth()]} – ${e.getDate()} ${mesesAbr[e.getMonth()]} ${e.getFullYear()}`;
    }
    return `${mesesFull[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  }, [viewMode, currentDate]);

  const statusInfo = {
    agendada:          { label:'Agendada',        dot:'#2F6BFF', bg:'#E7EDFF', tx:'#1C43AE' },
    aguardando_confirmacao: { label:'A confirmar', dot:'#9AA0AB', bg:'#F0F1F4', tx:'#6E7480' },
    realizada:         { label:'Realizada',        dot:'#0E9F6E', bg:'#EFFAF5', tx:'#0A5B40' },
    cancelada_professor:{ label:'Canc. Professor', dot:'#E8971A', bg:'#FFFBEB', tx:'#92400E' },
    cancelada_aluno:   { label:'Canc. Aluno',      dot:'#F97316', bg:'#FFF7ED', tx:'#9A3412' },
    cancelada_forca_maior:{ label:'Imprevisto',    dot:'#9AA0AB', bg:'#F7F8FA', tx:'#6E7480' },
    falta:             { label:'Falta',            dot:'#EF4444', bg:'#FEF2F2', tx:'#991B1B' },
  };
  // feriado / pausa: o rotulo diz o que foi (v131)
  const infoStatus = a => { const b = statusInfo[a.status]; const r = rotuloEspecialAula(a); return b && r ? { ...b, label: r } : b; };

  const [reagendando, setReagendando] = React.useState(null);
  const [novaDataReagendar, setNovaDataReagendar] = React.useState('');
  const [novaHoraReagendar, setNovaHoraReagendar] = React.useState('');

  function abrirReagendar(a) { uso.janela('aula_reagendar'); setReagendando(a); setNovaDataReagendar(a.data); setNovaHoraReagendar(a.hora?.slice(0,5)||''); }

  // v131 (Etapa 6): remarcar_aula (horario conferido no banco, renovacao e aviso ao aluno la').
  async function executarReagendar() {
    if (!reagendando || !novaDataReagendar) return;
    const novaHora = novaHoraReagendar || reagendando.hora?.slice(0,5) || '08:00';
    const r = await remarcarAulaProf(reagendando, novaDataReagendar, novaHora, { origem: 'aulas', nomeAluno: reagendando.alunos?.nome });
    if (!r) return;   // recusou ou "Escolher outro": a janela continua aberta
    setReagendando(null); setNovaDataReagendar(''); setNovaHoraReagendar('');
    loadAulas();
  }
  // Botoes da aula. Antes eram cinco quadradinhos com siglas (CP, CA, F) que ninguem
  // decifra em pe' na quadra. Agora: "Concluída" e "Não houve" (que abre os motivos em
  // palavras), mais "Remarcar". Aula que ja' fechou mostra "desfazer".
  const [naoHouveAulaId, setNaoHouveAulaId] = React.useState(null);
  function AcoesBtns({ a }) {
    // v131: aula que o aluno lancou ("combinei com o professor") — Confirmar/Recusar, como no Inicio
    if (a.status === 'aguardando_confirmacao') return (
      <div style={{display:'flex', gap:'6px', flexWrap:'wrap', alignItems:'center'}}>
        <span style={{fontSize:'12px', color:'#6E7480', fontWeight:'600', flexBasis:'100%'}}>O aluno lançou esta aula. Vocês combinaram?</span>
        <button onClick={()=>responderPropostaAulas(a, true)} style={{minHeight:'44px', padding:'0 14px', borderRadius:'9px', border:'none', background:'#2F6BFF', color:'white', cursor:'pointer', fontSize:'12.5px', fontWeight:'800', fontFamily:'inherit'}}><Ico n="check" s={14} /> Confirmar</button>
        <button onClick={()=>responderPropostaAulas(a, false)} style={{minHeight:'44px', padding:'0 14px', borderRadius:'9px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', cursor:'pointer', fontSize:'12.5px', fontWeight:'700', fontFamily:'inherit'}}>Recusar</button>
      </div>
    );
    // v131 (Etapa 9): o aluno avisou FORA do prazo — o professor decide [Conta a aula] [Devolver]
    if (avisoPendenteSM(a)) return (
      <div style={{minWidth:0}}>
        <div style={{fontSize:'11.5px', fontWeight:'600', color:'#92400E', lineHeight:1.4}}><Ico n="bell-ringing" s={12} /> {textoAvisoProfSM(a, a.alunos?.nome)}</div>
        <BotoesDecidirAviso aula={a} nomeAluno={a.alunos?.nome} origem="aulas" onFeito={() => loadAulas()}
          podeDevolver={!(a.acordo?.tipo_cobranca === 'mensal' || a.acordo?.turma_id || a.turma_id)} />
        <button onClick={()=>abrirReagendar(a)} title="Remarcar"
          style={{marginTop:'6px', minHeight:'44px', padding:'0 12px', borderRadius:'9px', background:'#E7EDFF', border:'none', color:'#2F6BFF', cursor:'pointer', fontSize:'12.5px', fontWeight:'700', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:'5px'}}>
          <Ico n="calendar-event" s={14} /> Remarcar
        </button>
      </div>
    );
    if (a.status !== 'agendada') return (
      <button onClick={()=>marcarStatus(a.id,'agendada',a.aluno_id)} style={{fontSize:'11.5px',color:'#6E7480',padding:'5px 9px',borderRadius:'8px',border:'1px solid #E2E4E9',background:'white',cursor:'pointer',fontWeight:'600'}} title="Desfazer"><Ico n="arrow-back-up" s={13} /> desfazer</button>
    );
    // Ja' registrada pelo professor, esperando o aluno confirmar: nada a fazer aqui alem
    // de desfazer. (Divergencia volta a mostrar os botoes — o professor decide.)
    if (a.desfecho_prof && !a.divergencia_em) return (
      <div style={{display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap'}}>
        <span style={{fontSize:'12px', color:'#1C43AE', fontWeight:'600'}}>
          <Ico n="clock" s={13} /> Você registrou como {a.desfecho_prof === 'realizada' ? 'dada' : 'falta'} · aguardando o aluno
        </span>
        <button onClick={()=>marcarStatus(a.id,'agendada',a.aluno_id)} style={{fontSize:'11.5px',color:'#6E7480',padding:'5px 9px',borderRadius:'8px',border:'1px solid #E2E4E9',background:'white',cursor:'pointer',fontWeight:'600'}}><Ico n="arrow-back-up" s={13} /> desfazer</button>
      </div>
    );
    const aberto = naoHouveAulaId === a.id;
    const bt = (bg, cor) => ({ padding:'8px 11px', borderRadius:'9px', background:bg, border:'none', color:cor, cursor:'pointer', fontSize:'12.5px', fontWeight:'700', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:'5px' });
    const nc = naoConsegue[a.id] || null;   // v136: o aluno disse "Nao consigo" neste horario
    const nvA = naoVeioSM(a);                 // v140: o aluno disse que voce nao veio
    const opcoesNaoHouve = [
      { st:'falta', t:'Aluno furou ou avisou em cima da hora', d: nvA ? 'o aluno diz que você não veio — fica registrado diferente e não desconta até vocês acertarem' : 'consome 1 aula', ic:'user-off', cor:'#B91C1C' },
      { st:'cancelada_aluno', t:'Aluno avisou dentro do prazo', d: nvA ? 'não consome — o aluno diz que você não veio: fica registrado "em desacordo"' : 'não consome', ic:'phone', cor:'#1C43AE' },
      { st:'cancelada_professor', t: nvA ? 'Eu não fui / desmarquei' : 'Eu desmarquei', d: nvA ? 'não consome — a aula volta para o fim do pacote' : 'não consome — fico devendo', ic:'refresh', cor:'#B45309' },
    ];
    return (
      <div>
        {a.divergencia_em && (
          <div style={{fontSize:'11.5px', fontWeight:'700', color:'#991B1B', background:'#FEF2F2', borderRadius:'8px', padding:'5px 9px', marginBottom:'7px'}}>
            <Ico n="alert-triangle" s={12} /> O aluno registrou diferente. Decida — ele é avisado.
          </div>
        )}
        {nc && (
          <div style={{fontSize:'11.5px', fontWeight:'700', color:'#92400E', background:'#FFFBEB', borderRadius:'8px', padding:'5px 9px', marginBottom:'7px', overflowWrap:'anywhere'}}>
            <Ico n="calendar-x" s={12} /> {primeiroNomeSM(a.alunos?.nome, 'O aluno')} disse que não consegue neste horário{nc.resposta ? `: "${String(nc.resposta).slice(0, 80)}${String(nc.resposta).length > 80 ? '…' : ''}"` : ''}. Remarque ou desmarque — "Eu desmarquei" não desconta.
          </div>
        )}
        <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
          <button onClick={()=>{ setNaoHouveAulaId(null); marcarStatus(a.id,'realizada',a.aluno_id); }} style={bt('#DCF5EC','#0A6F4D')}><Ico n="check" s={14} /> Aula dada</button>
          <button onClick={()=>setNaoHouveAulaId(aberto ? null : a.id)} style={bt(aberto ? '#FEE2E2' : '#F0F1F4', aberto ? '#B91C1C' : '#3D424C')}><Ico n="x" s={14} /> Não houve</button>
          <button onClick={()=>abrirReagendar(a)} title="Remarcar" style={bt('#E7EDFF','#2F6BFF')}><Ico n="calendar-event" s={14} /> Remarcar</button>
        </div>
        {aberto && (
          <div style={{display:'flex', flexDirection:'column', gap:'5px', marginTop:'8px', paddingTop:'8px', borderTop:'1px solid #F0F1F4'}}>
            {(nc ? [opcoesNaoHouve[2], opcoesNaoHouve[1], opcoesNaoHouve[0]] : nvA ? [opcoesNaoHouve[2], opcoesNaoHouve[0], opcoesNaoHouve[1]] : opcoesNaoHouve).map(o => (
              <button key={o.st} onClick={()=>{ setNaoHouveAulaId(null); marcarStatus(a.id,o.st,a.aluno_id); }}
                style={{textAlign:'left', border:'1px solid #E9EBEF', background:'white', borderRadius:'10px', padding:'9px 11px', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:'9px'}}>
                <Ico n={o.ic} s={15} c={o.cor} />
                <span style={{flex:1}}><span style={{fontSize:'12.5px', fontWeight:'700', color:'#16181D', display:'block'}}>{o.t}</span><span style={{fontSize:'11px', color:'#6E7480'}}>{o.d}</span></span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  function isAtrasada(a) {
    if (a.status !== 'agendada') return false;
    if (a.desfecho_prof && !a.divergencia_em) return false;   // ja' registrada, so' falta o aluno
    const now = new Date();
    const pad2 = n => String(n).padStart(2,'0');
    const hojeStr = `${now.getFullYear()}-${pad2(now.getMonth()+1)}-${pad2(now.getDate())}`;
    if (a.data < hojeStr) return true;
    if (a.data === hojeStr) {
      const [h, min] = (a.hora || '00:00').split(':').map(Number);
      return now.getHours() * 60 + now.getMinutes() > h * 60 + min;
    }
    return false;
  }

  const [filtroStatus, setFiltroStatus] = React.useState(null);
  const ehMobile = useEhMobile();
  const [diaSemanaSel, setDiaSemanaSel] = React.useState(null);
  // v131: o aviso "Aviso de falta" abre a semana da aula, ja' no dia dela — o cartao tem [Conta a aula]
  // [Devolver]. (No Inicio so' aparecem hoje e amanha; com acordo de 48h/72h o aviso pode ser de depois.)
  // v134: params.t (Pendencias, aula contestada) deixa tocar de novo na mesma aula.
  // v136: [Remarcar] / [Desmarcar] do "nao consegue no novo horario" (Pendencias e Inicio) abrem a
  // janela de remarcar ou a confirmacao do "Eu desmarquei" DESTA aula, assim que ela aparece na lista
  // (e a lista rola ate' ela). Uma vez por toque (params.t). para_data/para_hora = o horario que o
  // aluno recusou: se a aula ja' esta' em outro (o Inicio ainda mostrava, ou aceitou um pedido nesse
  // meio tempo), so' mostra o dia — remarcar de novo mandaria outro aviso ao aluno sem motivo.
  const alvoAcaoAulas = React.useRef(null);
  React.useEffect(() => {
    const id = params?.aula_id;
    if (!id) return;
    const acao = params.remarcar ? 'remarcar' : params.desmarcar ? 'desmarcar' : null;
    alvoAcaoAulas.current = { id, acao, paraData: params.para_data || null, paraHora: params.para_hora || null };
    let vivo = true;
    (async () => {
      try {
        const { data } = await supabase.from('aulas').select('data').eq('id', id).maybeSingle();
        if (!vivo || !data?.data) return;
        setViewMode('semana'); setCurrentDate(new Date(String(data.data).slice(0, 10) + 'T12:00:00')); setDiaSemanaSel(String(data.data).slice(0, 10));
      } catch {}
    })();
    return () => { vivo = false; };
  }, [params?.aula_id, params?.t]);
  React.useEffect(() => {
    const alvo = alvoAcaoAulas.current;
    if (!alvo) return;
    const a = aulas.find(x => x.id === alvo.id);
    if (!a) return;
    alvoAcaoAulas.current = null;
    setTimeout(() => { try { document.getElementById('aula-' + a.id)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {} }, 250);
    if (!alvo.acao) return;
    if (a.status !== 'agendada' || a.desfecho_prof) return;   // mudou nesse meio tempo: fica so' o dia aberto
    if (alvo.paraData && (String(a.data || '').slice(0, 10) !== String(alvo.paraData).slice(0, 10)
                          || (alvo.paraHora && hhmmSM(a.hora) !== hhmmSM(alvo.paraHora)))) {
      uso.bloqueio('remarcacao_recusada', null, null, 'aula_mudou');
      toastSM('Essa aula já está em outro horário. Confira aqui na agenda.');
      return;
    }
    if (alvo.acao === 'remarcar') abrirReagendar(a); else abrirDesmarcar(a);
  }, [aulas]);

  const realizadas=aulas.filter(a=>a.status==='realizada').length;
  const agendadas=aulas.filter(a=>a.status==='agendada'&&!isAtrasada(a)).length;
  const canceladas=aulas.filter(a=>a.status==='cancelada_professor'||a.status==='cancelada_aluno').length;
  const atrasadas=aulas.filter(a=>isAtrasada(a)).length;

  // O cartao de uma aula na lista — o mesmo na semana e no mes, para as duas telas nao
  // divergirem com o tempo (era markup copiado; a do mes nem existia).
  function CartaoAula({ a }) {
    const atrasada = isAtrasada(a);
    const est = estadoAulaSM(a);
    const legP = legendaPresencaProfSM(a, a.alunos?.nome);   // v140
    const nvC = naoVeioSM(a);
    // "Agendada" durante a aula fazia parecer que nada estava acontecendo (Cadu, 24/09).
    const si = est === 'agora' ? {label:'● Em andamento', bg:'#E7EDFF', tx:'#1C43AE'}
      : est === 'esperando_aluno' ? (a.alunos && !a.alunos.user_id
          ? {label:'Vale em 24h · aluno sem app', bg:'#E7EDFF', tx:'#1C43AE'}   // v133: ninguem vai confirmar
          : {label:'Aguardando aluno', bg:'#E7EDFF', tx:'#1C43AE'})
      : est === 'divergencia' ? {label:'Divergência', bg:'#FEF2F2', tx:'#991B1B'}
      : atrasada ? {label:'Sem resposta', bg:'#FFFBEB', tx:'#92400E'} : (infoStatus(a) || {label:a.status, bg:'#F7F8FA', tx:'#6E7480'});
    const cor = corAluno[a.alunos?.id] || '#2F6BFF';
    return (
      <div id={'aula-' + a.id} className="plen-card" style={{padding:'12px 14px', borderLeft:`4px solid ${atrasada ? '#D97706' : cor}`}}>
        <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
          <div className="num" style={{fontSize:'16px', fontWeight:'800', color:'#16181D', minWidth:'46px'}}>{a.hora?.slice(0,5)}</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontWeight:'700', color:'#16181D', fontSize:'14px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{a.alunos?.nome}</div>
            {a.alunos?.modalidade && <div style={{fontSize:'11.5px', color:'#6E7480'}}>{a.alunos.modalidade}</div>}
            {!avisoPendenteSM(a) && textoAvisoProfSM(a, a.alunos?.nome) && (
              <div style={{fontSize:'11.5px', color:'#6E7480', fontWeight:'600'}}><Ico n="bell-ringing" s={12} /> {textoAvisoProfSM(a, a.alunos?.nome)}</div>
            )}
            {/* v140: a pergunta "vai?" e o "o professor nao veio" */}
            {legP && <div style={{fontSize:'11.5px', color:legP.cor, fontWeight:'700'}}>{legP.txt}</div>}
            {nvC && (
              <div style={{fontSize:'11.5px', color:'#991B1B', fontWeight:'700', lineHeight:1.45}}>
                <Ico n="user-exclamation" s={12} /> {primeiroNomeSM(a.alunos?.nome)} diz que você não veio · avisou {quandoRelSM(nvC.em)}
                {nvC.pendente ? <span style={{fontWeight:'600', display:'block'}}>{textoVoltaNaoVeioSM(nvC.voltaEm, { semSaldo: !!(a.acordo && (a.acordo.tipo_cobranca === 'mensal' || a.acordo.turma_id)) })}</span> : null}
              </div>
            )}
          </div>
          <span style={{padding:'3px 9px', borderRadius:'20px', background:si.bg, color:si.tx, fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap'}}>{si.label}</span>
        </div>
        <div style={{marginTop:'10px'}}><AcoesBtns a={a} /></div>
      </div>
    );
  }

  function DiaView() {
    const aulasHoje = aulas.filter(a=>a.data===dataInicio).sort((a,b)=>a.hora.localeCompare(b.hora));
    if (aulasHoje.length===0) return <div style={{textAlign:'center',padding:'60px 0',color:'#9AA0AB',fontSize:'14px'}}><Ico n="calendar" /> Nenhuma aula neste dia</div>;
    return (
      <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>
        {aulasHoje.map(a => {
          const atrasada = isAtrasada(a);
          const si = atrasada ? {label:'Não realizada',bg:'#FFFBEB',tx:'#92400E'} : (infoStatus(a)||{label:a.status,dot:'#9AA0AB',bg:'#F7F8FA',tx:'#6E7480'});
          const cor = corAluno[a.alunos?.id]||'#2F6BFF';
          return (
            <div key={a.id} style={{background:'white',border:`1px solid ${atrasada?'#FCD34D':'#E2E4E9'}`,borderRadius:'14px',padding:'14px 18px',display:'flex',alignItems:'center',gap:'14px',boxShadow:'0 1px 3px rgba(0,0,0,0.04)',borderLeft:`4px solid ${atrasada?'#E8971A':cor}`}}>
              <div style={{minWidth:'44px',textAlign:'center'}}>
                <div style={{fontSize:'16px',fontWeight:'800',color:'#16181D'}}>{a.hora?.slice(0,5)}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{fontWeight:'700',color:'#16181D',fontSize:'14px'}}>{a.alunos?.nome}</div>
                {a.alunos?.modalidade && <div style={{fontSize:'11px',color:'#9AA0AB',marginTop:'1px'}}>{a.alunos.modalidade}</div>}
              </div>
              <div style={{padding:'3px 10px',borderRadius:'20px',background:si.bg,color:si.tx,fontSize:'11px',fontWeight:'700'}}>{atrasada?'':''}{si.label}</div>
              <AcoesBtns a={a} />
            </div>
          );
        })}
      </div>
    );
  }

  function SemanaView() {
    const weekStart = new Date(currentDate); weekStart.setDate(currentDate.getDate()-currentDate.getDay());
    const weekDays = Array.from({length:7},(_,i)=>{ const d=new Date(weekStart); d.setDate(weekStart.getDate()+i); return d; });
    const pad2 = n => String(n).padStart(2,'0');
    const fmt = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
    // No celular a grade de 7 colunas nao cabe (44px por dia). Vira o desenho do Google
    // Agenda no celular: fileira de dias em cima, lista do dia escolhido embaixo.
    if (ehMobile) {
      const dias = weekDays.map(fmt);
      // Abre com a SEMANA INTEIRA, dia a dia (Cadu, 25/09: trocou de semana e "so' veio o
      // Rogerio" — era so' a segunda selecionada). Tocar num dia filtra; tocar de novo volta.
      const sel = dias.includes(diaSemanaSel) ? diaSemanaSel : null;
      const ordenar = (x, y) => String(x.hora || '').padStart(8, '0').localeCompare(String(y.hora || '').padStart(8, '0'));
      const grupos = (sel ? [sel] : dias).map(d => ({ data: d, itens: aulas.filter(a => a.data === d).sort(ordenar) })).filter(g => g.itens.length);
      const tituloDia = d => { const t = new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }); return t.charAt(0).toUpperCase() + t.slice(1); };
      return (
        <div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'4px', marginBottom:'12px'}}>
            {weekDays.map((wd, i) => {
              const ds = dias[i]; const on = ds === sel; const tem = aulas.some(a => a.data === ds); const isHoje = ds === hoje;
              return (
                <button key={ds} onClick={() => { if (!on) uso.acao('aulas.dia_semana'); setDiaSemanaSel(on ? null : ds); }}
                  style={{border:'none', borderRadius:'12px', padding:'8px 0 6px', background: on ? '#2F6BFF' : 'white', color: on ? 'white' : '#3D424C',
                          boxShadow: on ? 'none' : 'inset 0 0 0 1px #E9EBEF', cursor:'pointer', fontFamily:'inherit'}}>
                  <div style={{fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', color: on ? 'rgba(255,255,255,.75)' : (isHoje ? '#2F6BFF' : '#9AA0AB')}}>{diasAbbr[wd.getDay()]}</div>
                  <div style={{fontSize:'16px', fontWeight:'800', marginTop:'1px'}}>{wd.getDate()}</div>
                  <div style={{width:'5px', height:'5px', borderRadius:'50%', margin:'4px auto 0', background: tem ? (on ? 'white' : '#2F6BFF') : 'transparent'}} />
                </button>
              );
            })}
          </div>
          {sel && (
            <button onClick={() => setDiaSemanaSel(null)}
              style={{border:'none', background:'transparent', color:'#2F6BFF', fontSize:'13px', fontWeight:'700', cursor:'pointer', padding:'0 2px 10px', fontFamily:'inherit'}}>
              ← Ver a semana toda
            </button>
          )}
          {grupos.length === 0 ? (
            <div style={{textAlign:'center', padding:'36px 0', color:'#9AA0AB', fontSize:'14px'}}>{sel ? 'Nenhuma aula neste dia' : 'Nenhuma aula nesta semana'}</div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:'16px'}}>
              {grupos.map(g => (
                <div key={g.data}>
                  <div style={{fontSize:'13px', fontWeight:'800', color: g.data === hoje ? '#2F6BFF' : '#16181D', textTransform:'uppercase', letterSpacing:'.04em', margin:'6px 2px 8px', paddingTop:'10px', borderTop:'2px solid ' + (g.data === hoje ? '#2F6BFF' : '#E2E4E9')}}>
                    {g.data === hoje ? 'Hoje · ' : ''}{tituloDia(g.data)} · {g.itens.length} aula{g.itens.length !== 1 ? 's' : ''}
                  </div>
                  <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                    {g.itens.map(a => <CartaoAula key={a.id} a={a} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return (
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:'6px'}}>
        {weekDays.map((wd,i) => {
          const ds = fmt(wd);
          const dayAulas = aulas.filter(a=>a.data===ds).sort((a,b)=>a.hora.localeCompare(b.hora));
          const isHoje = ds===hoje;
          return (
            <div key={i} style={{background:'white',border:`1.5px solid ${isHoje?'#2F6BFF':'#E2E4E9'}`,borderRadius:'12px',overflow:'hidden',minHeight:'120px'}}>
              <div style={{padding:'8px 8px 6px',background:isHoje?'#E7EDFF':'#F7F8FA',borderBottom:'1px solid #E2E4E9',textAlign:'center'}}>
                <div style={{fontSize:'10px',fontWeight:'700',color:isHoje?'#2F6BFF':'#9AA0AB',textTransform:'uppercase',letterSpacing:'0.04em'}}>{diasAbbr[wd.getDay()]}</div>
                <div style={{fontSize:'15px',fontWeight:'800',color:isHoje?'#2F6BFF':'#3D424C',marginTop:'2px'}}>{wd.getDate()}</div>
              </div>
              <div style={{padding:'6px',display:'flex',flexDirection:'column',gap:'4px'}}>
                {dayAulas.length===0 && <div style={{fontSize:'10px',color:'#C7CAD1',textAlign:'center',paddingTop:'8px'}}>—</div>}
                {dayAulas.map(a => {
                  const atrasada=isAtrasada(a);
                  const si=atrasada?{bg:'#FFFBEB',tx:'#92400E'}:(statusInfo[a.status]||{bg:'#F7F8FA',tx:'#6E7480'});
                  const cor=corAluno[a.alunos?.id]||'#2F6BFF';
                  return (
                    <div key={a.id} style={{background:si.bg,borderRadius:'7px',padding:'5px 7px',borderLeft:`3px solid ${atrasada?'#E8971A':cor}`}}>
                      <div style={{fontSize:'11px',fontWeight:'800',color:'#16181D'}}>{a.hora?.slice(0,5)}{atrasada&&<span style={{color:'#E8971A',marginLeft:'3px'}}><Ico n="alert-triangle" c="#D97706" /></span>}</div>
                      <div style={{fontSize:'10px',color:'#3D424C',fontWeight:'600',marginTop:'1px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{a.alunos?.nome?.split(' ')[0]}</div>
                      <div style={{marginTop:'4px'}}><AcoesBtns a={a} /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function MesView() {
    const y=currentDate.getFullYear(), m=currentDate.getMonth();
    const daysInMonth=new Date(y,m+1,0).getDate();
    const firstDow=new Date(y,m,1).getDay();
    const pad2=n=>String(n).padStart(2,'0');
    const cells=[]; for(let i=0;i<firstDow;i++) cells.push(null); for(let d=1;d<=daysInMonth;d++) cells.push(d);
    const weeks=[]; for(let i=0;i<cells.length;i+=7) weeks.push(cells.slice(i,i+7));
    const dsSelecionado=`${y}-${pad2(m+1)}-${pad2(currentDate.getDate())}`;
    const aulasDoDia=aulas.filter(a=>a.data===dsSelecionado).sort((a,b)=>String(a.hora).localeCompare(String(b.hora)));

    // No celular a grade de 7 colunas da' 50px por dia: "06:00 Cris" dentro disso vira
    // um borrao que quebra linha (apontado pelo Cadu em 23/09). O padrao de calendario
    // de bolso — Apple, Google — e' o mes como MAPA (dia + pontinhos) e o detalhe do dia
    // escolhido embaixo, em lista. O nome do aluno precisa de largura; o ponto nao.
    const cel = ehMobile ? { minHeight:'44px', padding:'3px 2px' } : { minHeight:'80px', padding:'5px' };

    return (
      <>
        <div style={{background:'white',borderRadius:'16px',border:'1px solid #E2E4E9',overflow:'hidden'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',background:'#F7F8FA',borderBottom:'1px solid #E2E4E9'}}>
            {diasAbbr.map(d=><div key={d} style={{padding:'8px 2px',textAlign:'center',fontSize:'11px',fontWeight:'700',color:'#6E7480'}}>{ehMobile?d.charAt(0):d}</div>)}
          </div>
          {weeks.map((wk,wi)=>(
            <div key={wi} style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:wi<weeks.length-1?'1px solid #F0F1F4':'none'}}>
              {wk.map((day,di)=>{
                const ds=day?`${y}-${pad2(m+1)}-${pad2(day)}`:null;
                const isHoje=ds===hoje;
                const isSel=ds===dsSelecionado;
                const dayAulas=ds?aulas.filter(a=>a.data===ds).sort((a,b)=>String(a.hora).localeCompare(String(b.hora))):[];
                return (
                  <div key={di} onClick={()=>{ if(day) setCurrentDate(new Date(y,m,day)); }}
                    style={{...cel,borderLeft:di>0?'1px solid #F0F1F4':'none',cursor:day?'pointer':'default',
                            background:isSel&&day?'#E7EDFF':day?'white':'#F7F8FA'}}>
                    {day&&(
                      <>
                        <div style={{fontSize:'12px',textAlign:ehMobile?'center':'left',
                                     fontWeight:(isHoje||isSel)?'800':'600',
                                     color:isSel?'#2F6BFF':isHoje?'#2F6BFF':'#3D424C',marginBottom:'3px'}}>
                          {day}{isHoje&&!ehMobile?' · hoje':''}
                        </div>
                        {ehMobile ? (
                          <div style={{display:'flex',justifyContent:'center',gap:'2px',flexWrap:'wrap'}}>
                            {dayAulas.slice(0,3).map(a=>(
                              <span key={a.id} style={{width:'5px',height:'5px',borderRadius:'50%',
                                background:isAtrasada(a)?'#E8971A':(corAluno[a.alunos?.id]||'#2F6BFF'),
                                opacity:a.status==='realizada'?0.45:1}} />
                            ))}
                            {dayAulas.length>3&&<span style={{fontSize:'8px',color:'#9AA0AB',lineHeight:'5px'}}>+</span>}
                          </div>
                        ) : dayAulas.map(a=>{
                          const atrasada=isAtrasada(a);
                          const cor=corAluno[a.alunos?.id]||'#2F6BFF';
                          return (
                            <div key={a.id} style={{background:atrasada?'#E8971A':cor,color:'white',borderRadius:'4px',padding:'2px 5px',fontSize:'9px',fontWeight:'700',marginBottom:'2px',opacity:a.status==='realizada'?0.6:1,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                              {a.hora?.slice(0,5)} {a.alunos?.nome?.split(' ')[0]}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* O detalhe do dia que o professor tocou. No desktop a grade ja' mostra tudo,
            mas manter a lista tambem ali da' o horario e o status por extenso. */}
        <div style={{marginTop:'14px'}}>
          <div style={{fontSize:'12px',fontWeight:'800',color:'#9AA0AB',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:'8px'}}>
            {new Date(y,m,currentDate.getDate()).toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})}
          </div>
          {aulasDoDia.length===0
            ? <div style={{background:'white',border:'1px solid #E2E4E9',borderRadius:'14px',padding:'18px',textAlign:'center',color:'#9AA0AB',fontSize:'13px'}}>Nenhuma aula neste dia.</div>
            : <div style={{display:'flex',flexDirection:'column',gap:'8px'}}>{aulasDoDia.map(a=><CartaoAula key={a.id} a={a} />)}</div>}
        </div>
      </>
    );
  }

  function ListaView() {
    const fmtData = d => { try { return new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{weekday:'short',day:'numeric',month:'short'}); } catch { return d; } };
    const aulasFiltered = aulas.filter(a => {
      if (filtroStatus === 'realizada')  return a.status === 'realizada';
      if (filtroStatus === 'agendada')   return a.status === 'agendada' && !isAtrasada(a);
      if (filtroStatus === 'cancelada')  return a.status === 'cancelada_professor' || a.status === 'cancelada_aluno' || a.status === 'falta';
      if (filtroStatus === 'atrasada')   return isAtrasada(a);
      return true;
    }).sort((a,b) => (a.data + String(a.hora || '').padStart(8,'0')).localeCompare(b.data + String(b.hora || '').padStart(8,'0')));

    const labelFiltro = {
      realizada:  { titulo:'Aulas Realizadas',        emoji:'circle-check', cor:'#0A5B40', bg:'#EFFAF5'},
      agendada:   { titulo:'Aulas Agendadas',          emoji:'calendar', cor:'#1C43AE', bg:'#E7EDFF'},
      cancelada:  { titulo:'Aulas Canceladas',         emoji:'circle-x', cor:'#991B1B', bg:'#FEF2F2'},
      atrasada:   { titulo:'Aguardando Confirmação',   emoji:'hourglass', cor:'#92400E', bg:'#FFFBEB'},
    }[filtroStatus] || {};

    if (aulasFiltered.length === 0) return (
      <div style={{textAlign:'center',padding:'48px',color:'#9AA0AB',fontSize:'14px'}}>
        Nenhuma aula encontrada para este filtro no período selecionado.
      </div>
    );

    // Agrupada por dia e, dentro do dia, pelo horario (5:00, 6:00...) — antes era uma
    // lista corrida com cartao apertado: nome e data quebravam em varias linhas (Cadu, 25/09).
    const porDia = [];
    aulasFiltered.forEach(a => {
      const ult = porDia[porDia.length - 1];
      if (ult && ult.data === a.data) ult.itens.push(a); else porDia.push({ data: a.data, itens: [a] });
    });
    const tituloDia = d => { const t = new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }); return t.charAt(0).toUpperCase() + t.slice(1); };
    return (
      <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
        {porDia.map(g => (
          <div key={g.data}>
            <div style={{fontSize:'13px', fontWeight:'800', color: g.data === hoje ? '#2F6BFF' : '#16181D', textTransform:'uppercase', letterSpacing:'.04em', margin:'6px 2px 8px', paddingTop:'10px', borderTop:'2px solid ' + (g.data === hoje ? '#2F6BFF' : '#E2E4E9')}}>
              {g.data === hoje ? 'Hoje · ' : ''}{tituloDia(g.data)} · {g.itens.length} aula{g.itens.length !== 1 ? 's' : ''}
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
              {g.itens.map(a => <CartaoAula key={a.id} a={a} />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="module-page" style={{maxWidth:'1100px', margin:'0 auto'}}>
      {}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'20px',gap:'12px',flexWrap:'wrap'}}>
        <div>
          <div style={{fontSize:'22px',fontWeight:'800',color:'#16181D',letterSpacing:'-0.5px'}}>Aulas</div>
          <div style={{fontSize:'13px',color:'#9AA0AB',marginTop:'4px'}}>Gerencie presença e acompanhe o progresso</div>
        </div>
        <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
          <select value={filtroAluno} onChange={e=>{ uso.acao('aulas.filtro_aluno', { todos: !e.target.value }); setFiltroAluno(e.target.value); }}
            style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'8px 12px',fontSize:'13px',color:'#16181D',background:'white',outline:'none',cursor:'pointer'}}>
            <option value="">Todos os alunos</option>
            {alunos.map(a=><option key={a.id} value={a.id}>{a.nome}</option>)}
          </select>
          <div style={{display:'flex',background:'#F0F1F4',borderRadius:'10px',padding:'3px',gap:'2px'}}>
            {[['dia','Dia'],['semana','Semana'],['mes','Mês']].map(([v,l])=>(
              <button key={v} onClick={()=>{ if (v !== viewMode) uso.acao('aulas.modo', { modo: v }); setViewMode(v); }}
                style={{padding:'7px 14px',borderRadius:'8px',fontSize:'12px',fontWeight:'700',border:'none',cursor:'pointer',background:viewMode===v?'white':'transparent',color:viewMode===v?'#2F6BFF':'#6E7480',boxShadow:viewMode===v?'0 1px 3px rgba(0,0,0,0.1)':'none',transition:'all 0.15s'}}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {}
      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'16px'}}>
        <button onClick={()=>{ uso.acao('aulas.periodo', { delta: -1 }); navigate(-1); }} style={{width:'32px',height:'32px',borderRadius:'8px',border:'1.5px solid #E2E4E9',background:'white',cursor:'pointer',fontSize:'18px',display:'flex',alignItems:'center',justifyContent:'center',color:'#6E7480'}}>‹</button>
        <div style={{fontSize:'14px',fontWeight:'700',color:'#16181D',minWidth:'240px',textAlign:'center',textTransform:'capitalize'}}>{periodLabel}</div>
        <button onClick={()=>{ uso.acao('aulas.periodo', { delta: 1 }); navigate(1); }} style={{width:'32px',height:'32px',borderRadius:'8px',border:'1.5px solid #E2E4E9',background:'white',cursor:'pointer',fontSize:'18px',display:'flex',alignItems:'center',justifyContent:'center',color:'#6E7480'}}>›</button>
        <button onClick={()=>{ uso.acao('aulas.hoje'); setCurrentDate(new Date()); }} style={{padding:'6px 12px',borderRadius:'8px',border:'1.5px solid #E2E4E9',background:'white',cursor:'pointer',fontSize:'12px',fontWeight:'600',color:'#2F6BFF',marginLeft:'4px'}}>Hoje</button>
      </div>

      {}
      <div style={{display:'flex',gap:'8px',marginBottom:'18px',flexWrap:'wrap'}}>
        {[
 { key:'realizada', label:` ${realizadas} realizadas`, bg: filtroStatus==='realizada'?'#B7E9D6':'#EFFAF5', border: filtroStatus==='realizada'?'#0E9F6E':'#B7E9D6', cor:'#0A5B40', show: true },
 { key:'agendada', label:` ${agendadas} agendadas`, bg: filtroStatus==='agendada'?'#C2D4FF':'#E7EDFF', border: filtroStatus==='agendada'?'#2F6BFF':'#C2D4FF', cor:'#1C43AE', show: true },
 { key:'cancelada', label:` ${canceladas} canceladas`, bg: filtroStatus==='cancelada'?'#FECACA':'#FFFBEB', border: filtroStatus==='cancelada'?'#EF4444':'#FDE68A', cor:'#991B1B', show: canceladas > 0 },
 { key:'atrasada', label:` ${atrasadas} sem confirmar`,bg: filtroStatus==='atrasada'?'#FDBA74':'#FFF7ED', border: filtroStatus==='atrasada'?'#F97316':'#FDBA74', cor:'#9A3412', show: atrasadas > 0 },
        ].filter(s => s.show).map(s => (
          <button key={s.key}
            onClick={() => { uso.acao('aulas.filtro_status', { status: s.key }); setFiltroStatus(prev => prev === s.key ? null : s.key); }}
            style={{
              padding:'6px 14px', borderRadius:'20px', fontSize:'12px', fontWeight:'700',
              color: s.cor, background: s.bg, border:`1.5px solid ${s.border}`,
              cursor:'pointer', transition:'all 0.15s',
              boxShadow: filtroStatus===s.key ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
              transform: filtroStatus===s.key ? 'scale(1.04)' : 'scale(1)',
            }}>
            {s.label}
          </button>
        ))}
        {filtroStatus && (
          <button onClick={() => setFiltroStatus(null)}
            style={{padding:'6px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:'600',color:'#6E7480',background:'#F0F1F4',border:'1.5px solid #E2E4E9',cursor:'pointer'}}>
            <Ico n="x" /> Ver todos
          </button>
        )}
      </div>

      {}
      {alunos.filter(a=>aulas.some(au=>au.aluno_id===a.id)).length>0&&(
        <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'14px'}}>
          {alunos.filter(a=>aulas.some(au=>au.aluno_id===a.id)).map(a=>(
            <div key={a.id} style={{display:'flex',alignItems:'center',gap:'5px'}}>
              <div style={{width:'10px',height:'10px',borderRadius:'3px',background:corAluno[a.id]}}></div>
              <span style={{fontSize:'11px',color:'#6E7480'}}>{a.nome}</span>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{textAlign:'center',padding:'60px 0',color:'#9AA0AB',fontSize:'14px'}}>Carregando aulas...</div>
      ) : filtroStatus ? (
        <ListaView />
      ) : (
        viewMode==='dia' ? <DiaView /> : viewMode==='semana' ? <SemanaView /> : <MesView />
      )}

      {}
      {desmarcando && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'20px',padding:'24px',maxWidth:'400px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}}>
            {(() => {
              // v136: confirmacao do "Eu desmarquei" (vindo do "nao consegue"). Antes do horario:
              // "Desmarcar a aula"; depois: registrar como "Eu desmarquei" — o mesmo no banco
              const a = desmarcando.aula, P = primeiroNomeSM(a.alunos?.nome, 'o aluno');
              const quandoTxt = minuscSM(quandoCurtoSM(a.data, a.hora));
              const passou = aulaComecouSM(a);
              return (
                <>
                  <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D',marginBottom:'8px',lineHeight:1.35}}>
                    <Ico n="calendar-x" c="#D97706" /> {passou ? `Registrar a aula de ${P} (${quandoTxt}) como "Eu desmarquei"?` : `Desmarcar a aula de ${P} ${quandoTxt}?`}
                  </div>
                  <div style={{fontSize:'13px',color:'#3D424C',lineHeight:1.5,marginBottom:'18px'}}>
                    {desmarcando.carregando ? 'Conferindo o acordo…' : (desmarcando.txt || 'Não conta como falta.')} {P} recebe o aviso.
                  </div>
                  <div style={{display:'flex',gap:'10px'}}>
                    <button type="button" disabled={desmarcando.busy} onClick={() => setDesmarcando(null)}
                      style={{flex:1,minHeight:'44px',padding:'12px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>Voltar</button>
                    <button type="button" disabled={desmarcando.busy} onClick={confirmarDesmarcar}
                      style={{flex:2,minHeight:'44px',padding:'12px',borderRadius:'12px',border:'none',background:'#D97706',color:'white',fontSize:'14px',fontWeight:'800',cursor:desmarcando.busy?'wait':'pointer',opacity:desmarcando.busy?0.7:1}}>
                      {desmarcando.busy ? 'Salvando…' : passou ? 'Registrar' : 'Desmarcar'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
        </Portal>
      )}
      {reagendando && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'20px',padding:'28px',maxWidth:'400px',width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}}>
            <div style={{fontSize:'18px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="calendar" /> Reagendar aula</div>
            <div style={{fontSize:'13px',color:'#9AA0AB',marginBottom:'22px'}}>{reagendando.alunos?.nome} · original: {new Date(reagendando.data+'T12:00:00').toLocaleDateString('pt-BR')} às {reagendando.hora?.slice(0,5)}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'8px'}}>
              <div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'7px'}}>Nova data</div>
                <input type="date" value={novaDataReagendar} onChange={e=>setNovaDataReagendar(e.target.value)}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
              <div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'7px'}}>Novo horário</div>
                <input type="time" value={novaHoraReagendar} onChange={e=>setNovaHoraReagendar(e.target.value)}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
            </div>
            <div style={{fontSize:'11px',color:'#9AA0AB',marginBottom:'20px'}}><Ico n="alert-triangle" c="#D97706" /> Se a nova data ultrapassar o término do plano, o plano será estendido e o aluno será notificado.</div>
            <div style={{display:'flex',gap:'10px'}}>
              <button onClick={()=>{setReagendando(null);setNovaDataReagendar('');setNovaHoraReagendar('');}}
                style={{flex:1,padding:'12px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>Cancelar</button>
              <button onClick={executarReagendar} disabled={!novaDataReagendar}
                style={{flex:2,padding:'12px',borderRadius:'12px',border:'none',background:!novaDataReagendar?'#E2E4E9':'linear-gradient(135deg,#2F6BFF,#2F6BFF)',color:!novaDataReagendar?'#9AA0AB':'white',fontSize:'14px',fontWeight:'800',cursor:!novaDataReagendar?'not-allowed':'pointer'}}>
                <Ico n="circle-check" c="#0E9F6E" /> Confirmar reagendamento
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

function Financeiro({ professor, filtroInicialAluno, params }) {
  useAoDesfazerSM(() => { loadPagamentos(); });   // v142: desfez (faixa ou item) — a lista relê
  const [pagamentos, setPagamentos] = React.useState([]);
  const [alunos, setAlunos] = React.useState([]);
  const [modal, setModal] = React.useState(false);
  const mesAtual = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}`;
  const [custosFixos, setCustosFixos] = React.useState([]);   // mensalidade de academia etc.
  React.useEffect(() => { carregarCustosMensais(professor.id).then(setCustosFixos); }, []);
  const pad2 = n => String(n).padStart(2,'0');
  const fmtISO = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
  // meio = COMO foi pago (so' vale quando ja' nasce paga). O tipo da cobranca e' a origem.
  const [form, setForm] = React.useState({ aluno_id: '', valor: '', meio: 'pix_direto', periodo_inicio: '', periodo_fim: '', status_pagamento: 'pendente' });
  const registrandoPagRef = React.useRef(false);
  const [registrandoPag, setRegistrandoPag] = React.useState(false);   // "Registrando…" (ate' 3 idas ao banco)
  const confirmandoPagRef = React.useRef(false);
  const [confirmandoPag, setConfirmandoPag] = React.useState(false);
  const [cancelarPag, setCancelarPag] = React.useState(null);   // { pag, origem } — janela "Cancelar lançamento"
  const [sugestaoOrigem, setSugestaoOrigem] = React.useState('');
  const [filtroStatus, setFiltroStatus] = React.useState('');
  const [filtroAluno, setFiltroAluno] = React.useState(filtroInicialAluno || '');
  const [reciboData, setReciboData] = React.useState(null);
  const [modalConfirmar, setModalConfirmar] = React.useState(null);
  // competencia = mes de referencia da cobranca (p_referencia_mes). So' vai ao banco se o
  // professor mudar: antes vinha "mes que vem" por padrao e era ignorado.
  const [confirmarForm, setConfirmarForm] = React.useState(() => ({ meio: 'pix_direto', data_pagamento: hojeSP(), competencia: hojeSP().slice(0, 7) }));

  const [contratosParaReajuste, setContratosParaReajuste] = React.useState([]);
  const [modalReajuste, setModalReajuste] = React.useState(null);
  const [reajusteForm, setReajusteForm] = React.useState({ novoPreco: '', percentual: '' });
  const [salvandoReajuste, setSalvandoReajuste] = React.useState(false);
  const enviandoReajusteRef = React.useRef(false);   // trava o toque duplo no "Confirmar reajuste"
  const [reajusteManualAlunoId, setReajusteManualAlunoId] = React.useState('');
  const [abaFin, setAbaFin] = React.useState('pagamentos');
  const [mesRelatorio, setMesRelatorio] = React.useState(mesAtual);
  // Central de uso: a tela do Financeiro e' a aba (o ProfessorApp nao emite 'financeiro').
  React.useEffect(() => { uso.tela('prof.financeiro.' + abaFin); }, [abaFin]);
  // Funis: registrar pagamento (1 aluno, 2 valor, 3 pronto) e confirmar recebimento.
  const fPag = useFluxoUso('pagamento_registrar', !form.aluno_id ? 1 : !form.valor ? 2 : 3, modal);
  const fConf = useFluxoUso('pagamento_confirmar', 1, !!modalConfirmar);
  React.useEffect(() => { if (modalConfirmar) uso.janela('pagamento_confirmar'); }, [!!modalConfirmar]);
  React.useEffect(() => { if (reciboData) uso.janela('recibo'); }, [!!reciboData]);
  React.useEffect(() => { if (modalReajuste) uso.janela('reajuste_financeiro'); }, [!!modalReajuste]);

  React.useEffect(() => { loadAlunos(); loadPagamentos(); loadContratosReajuste(); }, []);
  React.useEffect(() => { if (filtroInicialAluno) setFiltroAluno(filtroInicialAluno); }, [filtroInicialAluno]);

  React.useEffect(() => { if (params?.aluno_id) setFiltroAluno(params.aluno_id); }, [params?.aluno_id]);
  // v134: veio de Pendencias ("Confirmar recebimento" / "Ja' recebi"): abre a MESMA janela de
  // confirmar recebimento desta cobranca. Fora das 100 da lista: busca so' ela. Ja' paga ou
  // cancelada: so' avisa (a lista ja' mostra como ficou).
  const confPendFeito = React.useRef(null);
  React.useEffect(() => {
    const id = params?.confirmar_pagamento_id;
    if (!id || confPendFeito.current === id || !pagamentos.length) return;
    confPendFeito.current = id;
    (async () => {
      let pag = pagamentos.find(p => p.id === id) || null;
      if (!pag) {
        const { data } = await supabase.from('pagamentos').select('*, alunos(id, nome)').eq('id', id).eq('professor_id', professor.id).maybeSingle();
        pag = data || null;
      }
      if (pag && pag.status === 'pendente') confirmarPagamento(pag);
      else toastSM(pag && pag.status === 'pago' ? 'Este pagamento já foi confirmado.' : 'Esta cobrança não está mais em aberto.');
    })();
  }, [params?.confirmar_pagamento_id, pagamentos]);
  // Aviso "Refazer reajuste" (Etapa 4): ja' abre Reajustes com o aluno escolhido — falta so' "Reajustar".
  React.useEffect(() => {
    if (!params?.reajuste_aluno_id) return;
    setAbaFin('pagamentos'); setFiltroStatus('reajuste'); setReajusteManualAlunoId(params.reajuste_aluno_id);
  }, [params?.reajuste_aluno_id]);
  async function loadAlunos() { const { data } = await supabase.from('alunos').select('id, nome, telefone, contratos(id, plano_id, preco_negociado, taxa_academia, status, data_inicio, data_fim, reposicao_permitida, limite_reposicao, antecedencia_cancelamento_horas, modo, multa_cancelamento_pct, tipo_cobranca, valor_mensal, planos(aulas_por_semana, duracao_meses)), aulas(id, status, custo_sala)').eq('professor_id', professor.id).order('nome'); setAlunos((data || []).map(x => Array.isArray(x.aulas) ? { ...x, aulas: semRemarcada(x.aulas) } : x)); /* v131: a original de aula remarcada nao soma sala */ }
  async function loadPagamentos() {
    // forma_recebimento (Etapa 3) alimenta o "como pagar" do Cobrar no WhatsApp. Sem a coluna: consulta de antes.
    let { data, error } = await supabase.from('pagamentos').select('*, alunos(id, nome, contratos(id, status, data_inicio, data_fim, tipo_cobranca, valor_mensal, preco_negociado, forma_recebimento))').eq('professor_id', professor.id).order('created_at', { ascending: false }).limit(100);
    if (error) ({ data } = await supabase.from('pagamentos').select('*, alunos(id, nome, contratos(id, status, data_inicio, data_fim, tipo_cobranca))').eq('professor_id', professor.id).order('created_at', { ascending: false }).limit(100));
    setPagamentos(data || []);
  }
  // v141 (B1): tocar em "Cobrar" registra o dia da cobranca (pagamentos.cobrado_em, gravado pelo banco
  // em professor_marcar_cobrado). Vai sem esperar: o WhatsApp abre na hora. Sem a funcao no banco (SQL
  // da v141 ainda nao rodado) nao acontece nada — o Cobrar segue igual ao de antes.
  async function marcarCobrado(ids) {
    const lista = (ids || []).filter(Boolean);
    if (!lista.length) return;
    try {
      const { data, error } = await supabase.rpc('professor_marcar_cobrado', { p_pagamento_ids: lista });
      if (error) { if (!semFuncaoBancoSM((error.code || '') + ' ' + (error.message || ''))) uso.erro('erro.cobrado', error.message); return; }
      if (data && data.ok) {
        const em = data.cobrado_em || new Date().toISOString();
        setPagamentos(prev => prev.map(x => lista.includes(x.id) && x.status === 'pendente' ? { ...x, cobrado_em: em } : x));
      }
    } catch (e) { uso.erro('erro.cobrado', e?.message || e); }
  }
  // "cobrado em 19/10" (data de Brasilia)
  const cobradoEmTxt = ts => (ts ? 'cobrado em ' + ddmmSM(diaSP(ts)) : '');

  function proximaAulaAgenda(agendaSlots, hoje) {
    if (!agendaSlots?.length) return null;
    let earliest = null, minDiff = 8;
    for (const slot of agendaSlots) {
      let diff = slot.dia_semana - hoje.getDay();
      if (diff <= 0) diff += 7;
      if (diff < minDiff) { minDiff = diff; const d = new Date(hoje); d.setDate(d.getDate() + diff); earliest = d; }
    }
    return earliest ? fmtISO(earliest) : null;
  }

  async function onAlunoChange(aluno_id) {
    const a = alunos.find(x => x.id === aluno_id);
    const contrato = (a?.contratos || []).find(c => c.status === 'ativo') || a?.contratos?.[0];
    const preco = contrato?.preco_negociado || '';
    // v152: renovou antecipado = existe um acordo 'proximo' com a cobranca dele na fila.
    const contratoProx = (a?.contratos || []).find(c => c.status === 'proximo');

    const [{ data: pagsList }, { data: agendaSlots }] = await Promise.all([
      supabase.from('pagamentos')
        .select('id, valor, status, contrato_id, periodo_inicio, periodo_fim, referencia_mes, created_at')
        .eq('aluno_id', aluno_id).eq('professor_id', professor.id)
        .not('status', 'eq', 'cancelado')
        .order('created_at', { ascending: false }).limit(10),
      supabase.from('agenda_semanal')
        .select('dia_semana, hora').eq('aluno_id', aluno_id)
        .eq('professor_id', professor.id).eq('ativo', true)
    ]);

    // A sugestao de periodo ignora a cobranca da fila (e' do proximo pacote, nao a "ultima" do atual).
    const ehDaFila = p => !!contratoProx && p.contrato_id === contratoProx.id;
    const ultimoPag = (pagsList || []).find(p => !ehDaFila(p));
    // Cobranca da fila em aberto e nada em aberto no atual: o pagamento e' dela — sugere dar baixa nela.
    const pagFila = (pagsList || []).find(p => ehDaFila(p) && p.status === 'pendente');
    const pendenteAtual = (pagsList || []).some(p => !ehDaFila(p) && p.status === 'pendente');
    if (pagFila && !pendenteAtual) {
      setForm(f => ({ ...f, aluno_id, valor: pagFila.valor, periodo_inicio: pagFila.periodo_inicio || '', periodo_fim: pagFila.periodo_fim || '', pag_fila_id: pagFila.id }));
      setSugestaoOrigem('proximo_pacote');
      return;
    }
    // v155: proximo pacote ja' pago e nada em aberto no atual — nao sugere "continuacao" (viraria uma 2a cobranca do mesmo pacote)
    const filaPaga = (pagsList || []).find(p => ehDaFila(p) && p.status === 'pago');
    if (filaPaga && !pendenteAtual) {
      setForm(f => ({ ...f, aluno_id, valor: '', periodo_inicio: '', periodo_fim: '', pag_fila_id: null }));
      setSugestaoOrigem('proximo_pago');
      return;
    }
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const duracaoMeses = contrato?.planos?.duracao_meses || 1;
    let periodoInicio = '';
    let origem = '';

    const fimVigenteStr = ultimoPag?.periodo_fim || (ultimoPag && contrato?.data_fim ? contrato.data_fim : null);
    const fimVigente = fimVigenteStr ? new Date(fimVigenteStr + 'T12:00:00') : null;

    if (fimVigente && fimVigente >= hoje) {

      const dStart = new Date(fimVigente); dStart.setDate(dStart.getDate() + 1);
      periodoInicio = fmtISO(dStart); origem = 'continuacao';
    } else if (fimVigente && fimVigente < hoje) {

      const proxima = proximaAulaAgenda(agendaSlots, hoje);
      if (proxima) { periodoInicio = proxima; origem = 'agenda'; }
      else { periodoInicio = fmtISO(hoje); origem = 'hoje'; }
    } else {

      const proxima = proximaAulaAgenda(agendaSlots, hoje);
      if (proxima) { periodoInicio = proxima; origem = 'agenda_novo'; }
      else if (!agendaSlots?.length) { origem = 'sem_agenda'; }
      else { periodoInicio = fmtISO(hoje); origem = 'hoje'; }
    }

    let periodoFim = '';
    if (periodoInicio) {
      const dIni = new Date(periodoInicio + 'T12:00:00');
      const dFimCalc = new Date(dIni);
      dFimCalc.setMonth(dFimCalc.getMonth() + duracaoMeses);
      dFimCalc.setDate(dFimCalc.getDate() - 1);
      periodoFim = fmtISO(dFimCalc);
    }

    setForm(f => ({ ...f, aluno_id, valor: preco, periodo_inicio: periodoInicio, periodo_fim: periodoFim, pag_fila_id: null }));
    setSugestaoOrigem(origem);
  }

  async function registrarPagamento() {
    if (!form.aluno_id || !form.valor) { fPag.barrar('sem_aluno_ou_valor'); toastSM('Selecione o aluno e informe o valor'); return; }
    // Trava contra duplo toque: sem isso, dois cliques rápidos no "Registrar" criavam
    // duas cobranças idênticas (foi o que aconteceu com o Adolfo em 23/09 — duas linhas
    // pix/pago de R$2000, 2 segundos de diferença). Usa ref porque setState é assíncrono
    // e não bloquearia a tempo um segundo clique rápido.
    if (registrandoPagRef.current) return;
    registrandoPagRef.current = true;
    setRegistrandoPag(true);

    try {
      const refMes = form.periodo_inicio ? form.periodo_inicio.slice(0,7) : mesAtual;

      // Se já existe uma cobrança EM ABERTO desse aluno pra esse mês (ex.: a mensalidade
      // que o robô gera sozinho todo dia — ver sql/mensalidade.sql), dar baixa nela em vez
      // de criar uma linha nova. Sem isso, o pagamento manual convivia com a cobrança
      // automática pendente e parecia "duplicado" no Financeiro.
      // Regra (28/09): um pagamento por aluno, por acordo, por mês. Cobrança em aberto
      // daquele mês recebe a baixa; se já está paga, NÃO registra de novo (sem "mesmo
      // assim"). O banco tem a mesma trava (sql/pagamento_unico.sql) para qualquer caminho.
      // v152: o acordo atual E o proximo (renovou antecipado). A cobranca do proximo pacote ja' existe na
      // fila: a baixa vai NELA — antes nascia uma cobranca nova no acordo atual (duplicada, virava divida).
      const { data: acordosAbertos } = await supabase.from('contratos').select('id, tipo_cobranca, status')
        .eq('aluno_id', form.aluno_id).in('status', ['ativo', 'proximo'])
        .order('data_inicio', { ascending: false });
      const contratoAtivo = (acordosAbertos || []).find(c => c.status === 'ativo') || null;
      const idsAbertos = (acordosAbertos || []).map(c => c.id);
      // v155: + as cobrancas do proximo pacote em qualquer mes (a da fila nasce no mes de inicio dele)
      const idsFilaQ = (acordosAbertos || []).filter(c => c.status === 'proximo').map(c => c.id);
      const { data: existentes } = await supabase.from('pagamentos')
        .select('id, status, valor, data_pagamento, contrato_id, referencia_mes')
        .eq('aluno_id', form.aluno_id).neq('status', 'cancelado')
        .or([`referencia_mes.eq.${refMes}`, form.pag_fila_id ? `id.eq.${form.pag_fila_id}` : '',
             idsFilaQ.length ? `contrato_id.in.(${idsFilaQ.join(',')})` : ''].filter(Boolean).join(','))
        .order('created_at');
      const doAcordo = (existentes || []).filter(p => !idsAbertos.length || !p.contrato_id || idsAbertos.includes(p.contrato_id));
      // A cobranca da fila so' recebe a baixa quando veio escolhida (pag_fila_id); senao, a em aberto do mes do acordo
      // ATUAL (revisao v152: antes achava a da fila pelo mes e gravava nela valor e periodo do pacote atual).
      const idsProximo = (acordosAbertos || []).filter(c => c.status === 'proximo').map(c => c.id);
      const pendenteExistente = (form.pag_fila_id && doAcordo.find(p => p.id === form.pag_fila_id && p.status === 'pendente'))
        || doAcordo.find(p => p.status === 'pendente' && p.referencia_mes === refMes && !idsProximo.includes(p.contrato_id));
      // v155: + a cobranca paga do proximo pacote no mesmo mes (antes nascia outra no acordo atual e virava divida)
      // v155: a paga do proximo pacote barra em qualquer mes (ela fica no mes de inicio do pacote novo)
      const jaPago = doAcordo.find(p => p.status === 'pago' && idsProximo.includes(p.contrato_id))
        || doAcordo.find(p => p.status === 'pago' && p.referencia_mes === refMes
          && (!contratoAtivo || !p.contrato_id || p.contrato_id === contratoAtivo.id));
      const nomeMes = new Date(refMes + '-15T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

      if (!pendenteExistente && jaPago) {
        fPag.barrar('ja_pago_no_mes');
        if (idsProximo.includes(jaPago.contrato_id)) { toastSM(`O próximo pacote deste aluno já está pago (${fmtBRL(jaPago.valor)}). Não é preciso registrar de novo.`); return; }
        toastSM(`Este aluno já tem pagamento registrado para ${nomeMes}: ${fmtBRL(jaPago.valor)}` +
          (jaPago.data_pagamento ? `, pago em ${new Date(jaPago.data_pagamento).toLocaleDateString('pt-BR')}` : '') +
          `. Não é possível registrar outro. Se é um pacote novo, use "Renovar" na ficha do aluno.`);
        return;
      }

      // Etapa 3: a cobranca nasce (ou fica) EM ABERTO com a origem no tipo; se o professor ja'
      // recebeu, a baixa vai pela funcao confirmar_pagamento (grava o meio, a data e avisa o
      // aluno). Por isso saiu o aviso 'credito_atualizado' daqui: o aluno recebia dois.
      const jaPagoAgora = form.status_pagamento === 'pago';
      if (jaPagoAgora && !form.meio) { fPag.barrar('sem_meio'); toastSM('Escolha como o aluno pagou (Pix, dinheiro, cartão…).'); return; }
      let pag;
      if (pendenteExistente) {
        const { data, error: eUp } = await supabase.from('pagamentos').update({
          valor: Number(form.valor),
          periodo_inicio: form.periodo_inicio || null, periodo_fim: form.periodo_fim || null,
        }).eq('id', pendenteExistente.id).select().single();
        if (eUp) { const dup = String(eUp.message || '').includes('PAGAMENTO_DUPLICADO'); fPag.barrar(dup ? 'pagamento_duplicado' : 'falha_salvar'); if (!dup) uso.erro('erro.pagamento', eUp.message); toastSM(msgErroPagamento(eUp)); return; }
        pag = data;
      } else {
        const { data, error: eIns } = await supabase.from('pagamentos').insert({
          aluno_id: form.aluno_id, professor_id: professor.id, valor: Number(form.valor),
          contrato_id: contratoAtivo?.id || null,
          tipo: contratoAtivo?.tipo_cobranca === 'mensal' ? 'mensalidade' : 'pacote',   // DE ONDE veio
          status: 'pendente',
          referencia_mes: refMes,
          periodo_inicio: form.periodo_inicio || null,
          periodo_fim: form.periodo_fim || null,
          data_pagamento: null
        }).select().single();
        if (eIns) { const dup = String(eIns.message || '').includes('PAGAMENTO_DUPLICADO'); fPag.barrar(dup ? 'pagamento_duplicado' : 'falha_salvar'); if (!dup) uso.erro('erro.pagamento', eIns.message); toastSM(msgErroPagamento(eIns)); return; }
        pag = data;
      }

      let rConf = null;
      if (pag && jaPagoAgora) {
        const res = await rpcPagamento('confirmar_pagamento', { p_pagamento_id: pag.id, p_meio: form.meio, p_data: null, p_referencia_mes: null });
        if (!res.ok) {
          if (res.codigo === 'falha') uso.erro('erro.pagamento', res.tecnico);
          fPag.barrar(res.codigo === 'duplicado' ? 'pagamento_duplicado' : 'falha_confirmar');
          toastSM('A cobrança ficou lançada em aberto, mas a baixa não entrou. ' + res.msg + ' Depois, toque em "Confirmar recebimento" na lista.');
          setModal(false); loadPagamentos();
          setForm({ aluno_id: '', valor: '', meio: 'pix_direto', periodo_inicio: '', periodo_fim: '', status_pagamento: 'pendente' });
          return;
        }
        rConf = res.r;
      }
      await recalcularCreditos(supabase, form.aluno_id);
      fPag.fim('registrou', { status: form.status_pagamento, tipo: jaPagoAgora ? form.meio : 'pendente', meio: jaPagoAgora ? form.meio : 'nenhum' });
      const msgReg = jaPagoAgora
        ? (rConf?.aluno_avisado ? 'Pagamento registrado e confirmado. O aluno foi avisado no app.' : 'Pagamento registrado e confirmado.')
        : 'Cobrança lançada em aberto.';
      // v142: "Feito · Desfazer" — lancamento novo ja' recebido (P7: o banco cancela como "lancado por engano",
      // ate' 10 min) ou baixa numa cobranca em aberto que ja' existia (P6: volta a ficar em aberto). R3 do
      // rel_sql v142: com a cobranca que ja' existia o app manda 'confirmacao_pagamento'.
      if (jaPagoAgora && pag && rConf && !rConf.ja_feito) toastDesfazerSM(msgReg,
        { ref_tipo: 'pagamento', ref_id: pag.id, esperado: pendenteExistente ? 'confirmacao_pagamento' : 'lancamento' },
        { papel: 'professor', nomeOutro: (alunos.find(a => a.id === form.aluno_id) || {}).nome, curto: 'registro do pagamento', alunoRecalc: form.aluno_id });
      else toastSM(msgReg);
      setModal(false); loadPagamentos();
      setForm({ aluno_id: '', valor: '', meio: 'pix_direto', periodo_inicio: '', periodo_fim: '', status_pagamento: 'pendente' });
    } finally {
      registrandoPagRef.current = false;
      setRegistrandoPag(false);
    }
  }

  function confirmarPagamento(pag) {
    // Serve tambem para corrigir a data (ou o meio) de um pagamento ja' registrado (vem preenchida).
    // Pago sem meio (ex.: pago antes do cadastro) vem SEM meio marcado: "Confirmar data" nao vira Pix.
    const jaPago = pag.status === 'pago';
    setConfirmarForm({
      meio: meioConhecido(pag) || (jaPago ? '' : 'pix_direto'),
      // Pago SEM data (ex.: pago antes do cadastro): o campo vem vazio — um toque nao pode gravar
      // "hoje" para algo que foi pago antes. Cobranca em aberto: hoje, como antes.
      data_pagamento: dataSP(pag.data_pagamento) || (jaPago ? '' : hojeSP()),
      competencia: /^\d{4}-\d{2}$/.test(pag.referencia_mes || '') ? pag.referencia_mes : hojeSP().slice(0, 7),
    });
    setConfirmarForm(f => ({ ...f, competencia_orig: f.competencia }));   // so' manda o mes se o professor mudar
    setModalConfirmar(pag);
  }

  // Etapa 3: "virou pago" so' pela funcao confirmar_pagamento (grava status, meio, data e quem deu
  // a baixa; avisa o aluno; registra). Recibo SO' depois do {ok} do banco.
  async function executarConfirmarPagamento() {
    if (confirmandoPagRef.current) return;
    confirmandoPagRef.current = true;
    setConfirmandoPag(true);
    try {
      const pag = modalConfirmar;
      const jaPago = pag.status === 'pago';
      if (!confirmarForm.meio && !jaPago) { fConf.barrar('sem_meio'); toastSM('Escolha como o aluno pagou (Pix, dinheiro, cartão…).'); return; }
      if (!confirmarForm.data_pagamento) { fConf.barrar('sem_data'); toastSM(jaPago && !pag.data_pagamento ? 'Escolha o dia em que o aluno pagou. Se não lembrar, toque em Voltar — fica como pago, sem data.' : 'Escolha a data do recebimento.'); return; }
      if (confirmarForm.data_pagamento > hojeSP()) { fConf.barrar('data_futura'); toastSM(msgErroRpcPagamento('data_futura')); return; }
      const mudouMes = !!confirmarForm.competencia && confirmarForm.competencia !== confirmarForm.competencia_orig
        && confirmarForm.competencia !== (pag.referencia_mes || '');

      // Cobranca cancelada ("Marcar como pago"): primeiro volta a ficar em aberto, depois confirma.
      let reabriuAgora = false;
      if (pag.status === 'cancelado') {
        const re = await rpcPagamento('reabrir_pagamento', { p_id: pag.id });
        if (!re.ok) {
          if (re.codigo === 'falha') uso.erro('erro.pagamento', re.tecnico);
          fConf.barrar(re.codigo === 'duplicado' ? 'pagamento_duplicado' : 'falha_reabrir');
          toastSM(re.msg);
          return;
        }
        reabriuAgora = !re.r.ja_feito;
      }
      const res = await rpcPagamento('confirmar_pagamento', {
        p_pagamento_id: pag.id,
        p_meio: confirmarForm.meio || null,
        p_data: confirmarForm.data_pagamento,
        p_referencia_mes: mudouMes ? confirmarForm.competencia : null,
      });
      if (!res.ok) {
        if (res.codigo === 'falha') uso.erro('erro.pagamento', res.tecnico);
        fConf.barrar(res.codigo === 'duplicado' ? 'pagamento_duplicado' : res.codigo === 'falha' ? 'falha_salvar' : res.codigo);
        if (reabriuAgora) {
          // Nao deixa a cobranca "em aberto" pela metade (viraria divida para o aluno): desfaz
          // o reabrir, com o motivo de antes.
          const volta = await rpcPagamento('cancelar_pagamento', { p_id: pag.id, p_motivo: pag.cancelado_motivo || 'outro' });
          if (!volta.ok && volta.codigo === 'falha') uso.erro('erro.pagamento', volta.tecnico);
          toastSM(res.msg + (volta.ok ? ' Nada mudou: a cobrança continua cancelada.' : ' Atenção: a cobrança voltou a ficar em aberto — cancele de novo se precisar.'));
          loadPagamentos();
          return;
        }
        toastSM(res.msg);
        if (pag.status === 'cancelado') loadPagamentos();
        return;
      }
      const r = res.r;
      // v152: o recibo leva o plano do acordo DESTA cobranca (pode ser o proximo pacote); sem contrato_id, o ativo.
      const { data: contrato } = pag.contrato_id
        ? await supabase.from('contratos').select('*, planos(*)').eq('id', pag.contrato_id).maybeSingle()
        : await supabase.from('contratos').select('*, planos(*)').eq('aluno_id', pag.aluno_id).eq('status', 'ativo').order('data_inicio', { ascending: false }).limit(1).maybeSingle();

      await recalcularCreditos(supabase, pag.aluno_id);
      fConf.fim('confirmou', { tipo: r.meio || 'sem_meio', meio: r.meio || 'sem_meio', ja_pago: jaPago, ja_feito: !!r.ja_feito });
      uso.acao('pagamento.confirmar', { tipo: r.meio || 'sem_meio', meio: r.meio || 'sem_meio', correcao: !!r.correcao });
      setModalConfirmar(null);
      setReciboData({ ...pag, status: 'pago', meio: r.meio, confirmado_via: r.confirmado_via, data_pagamento: r.data_pagamento,
                      referencia_mes: r.referencia_mes, plano_nome: contrato?.planos?.nome || '—' });
      // v142: "Feito · Desfazer" (P6) — so' na baixa de verdade de uma cobranca em aberto (nao na correcao de
      // data/meio, nem na cancelada que foi reaberta e paga no mesmo toque). Desfeito: o recibo fecha.
      if (r.ja_feito) toastSM('Nada mudou — o pagamento já estava assim.');
      else if (!jaPago && !reabriuAgora && !r.correcao) toastDesfazerSM(r.aluno_avisado ? 'Recebimento confirmado. O aluno foi avisado no app.' : 'Recebimento confirmado.',
        { ref_tipo: 'pagamento', ref_id: pag.id, esperado: 'confirmacao_pagamento' },
        { papel: 'professor', nomeOutro: pag.alunos?.nome, curto: 'confirmação do recebimento', alunoRecalc: pag.aluno_id,
          aoDesfazer: res2 => { if (res2 && res2.ok) setReciboData(null); } });
      else if (r.aluno_avisado) toastSM('Recebimento confirmado. O aluno foi avisado no app.');
      loadPagamentos();
    } finally {
      confirmandoPagRef.current = false;
      setConfirmandoPag(false);
    }
  }

  // Todo clique errado tem volta (Marcus, 27/09): pago -> "nao foi pago" volta a cobrar;
  // cancelado -> "reabrir cobranca". Etapa 3: pela funcao reabrir_pagamento (limpa data, meio,
  // motivo e o "Ja' paguei" do aluno). O saldo de aulas do aluno e' recalculado.
  async function voltarParaPendente(p, pergunta) {
    if (pergunta && !window.confirm(pergunta)) return;
    // "Reabrir cobrança" (sem pergunta) com a lista velha: se no banco ja' esta' PAGO, reabrir
    // tiraria o dinheiro recebido sem o professor saber. Confere antes e nao mexe.
    if (p.status === 'cancelado') {
      const { data: atual } = await supabase.from('pagamentos').select('status').eq('id', p.id).maybeSingle();
      if (atual?.status === 'pago') {
        uso.bloqueio('pagamento_reabrir', null, null, 'virou_pago');
        toastSM('Este lançamento já está como pago (a lista estava desatualizada). Nada mudou.');
        loadPagamentos();
        return;
      }
    }
    // v142: primeiro "desfazer a minha acao" — "Nao foi pago" (P6: volta em aberto com o "Ja' paguei" do aluno e
    // o avisa, se ele tinha sido avisado) e "Reabrir cobranca" (P8: volta como estava — em aberto, ou PAGA se
    // estava paga antes do cancelamento, R1). Se o banco disser que nao e' o caso (passou de 24 h, foi o
    // aluno/o Plen, nada para desfazer), o caminho de hoje (reabrir_pagamento).
    const espP = p.status === 'pago' ? 'confirmacao_pagamento' : p.status === 'cancelado' ? 'cancelamento_cobranca' : null;
    if (espP) {
      const dz = await executarDesfazerSM({ ref_tipo: 'pagamento', ref_id: p.id, esperado: espP },
        { papel: 'professor', nomeOutro: p.alunos?.nome, origem: 'financeiro', quieto: true, alunoRecalc: p.aluno_id || null });
      if (dz === null) return;   // toque duplo
      if (dz.ok) { uso.acao('pagamento.reabrir', { de: p.status, via: 'desfazer' }); return; }   // a lista relê pelo aviso de "desfeito"
      if (!CAI_NO_CAMINHO_DE_HOJE_SM.has(dz.cod)) {
        if (dz.cod === 'rede') uso.erro('erro.desfazer', dz.tecnico); else uso.bloqueio('desfazer', null, null, dz.cod);
        toastSM(dz.cod === 'duplicado'
          ? `Não dá para reabrir: este aluno já tem outra cobrança ou pagamento para ${nomeMesRefSM((dz.data && dz.data.referencia_mes) || p.referencia_mes)}. Cancele a outra antes.`
          : textoErroDesfazerSM(dz.cod, { ...(dz.data || {}), ref_tipo: 'pagamento' }, 'professor', p.alunos?.nome));
        if (dz.cod !== 'rede') loadPagamentos();
        return;
      }
    }
    const res = await rpcPagamento('reabrir_pagamento', { p_id: p.id });
    if (!res.ok) {
      if (res.codigo === 'falha') uso.erro('erro.pagamento', res.tecnico);
      toastSM(res.codigo === 'duplicado'
        ? `Não dá para reabrir: este aluno já tem outra cobrança ou pagamento para ${nomeMesRefSM(res.r?.referencia_mes || p.referencia_mes)}. Cancele a outra antes.`
        : res.msg);
      return;
    }
    uso.acao('pagamento.reabrir', { de: p.status });
    if (p.aluno_id) await recalcularCreditos(supabase, p.aluno_id);
    toastSM(res.r.ja_feito ? 'Esta cobrança já estava em aberto.' : 'Pronto — voltou a ficar em aberto.');
    loadPagamentos();
  }

  async function loadContratosReajuste() {
    const agora = new Date();
    const d11m = new Date(agora); d11m.setMonth(d11m.getMonth() - 11);
    const d14m = new Date(agora); d14m.setMonth(d14m.getMonth() - 14);
    const { data } = await supabase.from('contratos')
      .select('id, data_inicio, preco_negociado, tipo_cobranca, valor_mensal, aluno_id, alunos(id, nome)')
      .eq('professor_id', professor.id).eq('status', 'ativo')
      .lte('data_inicio', d11m.toISOString().split('T')[0])
      .gte('data_inicio', d14m.toISOString().split('T')[0]);
    setContratosParaReajuste(data || []);
  }

  // Abre a janela com o acordo RELIDO do banco (a lista pode estar velha).
  async function abrirReajusteFin(c) {
    const fresco = await carregarAcordoReajuste(c.id);
    const ac = { ...c, ...(fresco || {}) };
    setModalReajuste(ac);
    setReajusteForm({ novoPreco: baseReajuste(ac).toFixed(2), percentual: '' });
  }

  async function aplicarReajuste() {
    if (!modalReajuste) return;
    // Trava ANTES de reler o acordo (toque duplo mandava a proposta e o aviso ao aluno duas vezes).
    if (enviandoReajusteRef.current) return;
    enviandoReajusteRef.current = true;
    setSalvandoReajuste(true);
    try {
      // Relê o acordo antes de propor: se o valor mudou enquanto a janela estava aberta, mostra o novo.
      const fresco = await carregarAcordoReajuste(modalReajuste.id);
      if (!fresco) { uso.bloqueio('reajuste_financeiro', null, null, 'falha_leitura'); toastSM('Não consegui ler o acordo agora. Tente de novo em instantes.'); return; }
      if (fresco.status && fresco.status !== 'ativo') { uso.bloqueio('reajuste_financeiro', null, null, 'acordo_inativo'); toastSM('Este acordo não está mais ativo.'); setModalReajuste(null); loadContratosReajuste(); return; }
      const precoAtual = baseReajuste(fresco);
      if (Math.round(precoAtual * 100) !== Math.round(baseReajuste(modalReajuste) * 100) || fresco.tipo_cobranca !== modalReajuste.tipo_cobranca) {
        setModalReajuste(m => ({ ...m, ...fresco }));
        setReajusteForm({ novoPreco: precoAtual.toFixed(2), percentual: '' });
        uso.bloqueio('reajuste_financeiro', null, null, 'valor_mudou');
        toastSM(`O valor atual deste acordo é ${fmtBRL(precoAtual)}${sufixoPrecoAcordo(fresco)}. Confira o novo preço e envie de novo.`);
        return;
      }
      const novoPreco = reajusteForm.percentual
        ? +(precoAtual * (1 + Number(reajusteForm.percentual) / 100)).toFixed(2)
        : Number(reajusteForm.novoPreco);
      if (!novoPreco || novoPreco <= 0) { uso.bloqueio('reajuste_financeiro', null, null, 'sem_preco'); toastSM('Informe o novo preço'); return; }
      uso.acao('reajuste.propor', { origem: 'financeiro', mensal: ehMensalAcordo(fresco) });

      const { error: eRj } = await supabase.from('contratos').update({
        preco_reajuste_pendente: novoPreco,
        reajuste_solicitado_em: new Date().toISOString()
      }).eq('id', fresco.id);
      if (eRj) { uso.erro('erro.reajuste_propor', eRj.message); toastSM('Não foi possível enviar o reajuste agora. Confira sua internet e tente de novo.'); return; }

      await supabase.from('notificacoes').insert({
        aluno_id: fresco.aluno_id || modalReajuste.aluno_id, professor_id: professor.id, tipo: 'aceite_reajuste',
        mensagem: `Seu professor propôs um reajuste de ${fmtBRL(precoAtual)} para ${fmtBRL(novoPreco)}${sufixoPrecoAcordo(fresco)}. Toque para aceitar ou recusar.`
      });
      setModalReajuste(null);
      toastSM('Proposta enviada. O aluno aceita ou recusa no app.');
      loadAlunos(); loadContratosReajuste();
    } finally {
      enviandoReajusteRef.current = false;
      setSalvandoReajuste(false);
    }
  }

  // v155: "Recebido em <mes>" e' o dinheiro que entrou no mes (data do pagamento, como no Inicio) — pago
  // adiantado do proximo pacote conta no mes em que caiu. referencia_mes fica para o relatorio por competencia.
  // v155: pago sem data (renovou com "Ja' pagou" sem a data) entra pelo mes de referencia, ate' confirmar a data
  const pagosMes = pagamentos.filter(p => p.status === 'pago'
    && (p.data_pagamento ? diaSP(p.data_pagamento).slice(0, 7) === mesAtual : String(p.referencia_mes || '').slice(0, 7) === mesAtual));
  // v152: a cobranca do proximo pacote (renovou antecipado) vence no fim do pacote atual: enquanto nao vence entra
  // como "a vencer", a parte, e nao como divida. So' ela — as outras cobrancas seguem em "Em aberto" como antes.
  const hojeFin = hojeSP();
  const ehDoProximoFin = p => !!p.contrato_id && (p.alunos?.contratos || []).some(c => c.id === p.contrato_id && c.status === 'proximo');
  // v155: se o aluno ja' avisou que pagou, sai do "a vencer" e vai para as cobrancas esperando confirmacao
  const naoVenceuAinda = p => ehDoProximoFin(p) && !p.declarado_pago_em && !!p.data_vencimento && String(p.data_vencimento).slice(0, 10) > hojeFin;
  const pendentes = pagamentos.filter(p => p.status === 'pendente' && !naoVenceuAinda(p));
  const aVencer = pagamentos.filter(p => p.status === 'pendente' && naoVenceuAinda(p));
  const receitaBruta = pagosMes.reduce((s, p) => s + Number(p.valor), 0);
  const totalPendente = pendentes.reduce((s, p) => s + Number(p.valor), 0);
  const totalAVencer = aVencer.reduce((s, p) => s + Number(p.valor), 0);

  const custosAcademia = alunos.reduce((total, a) => {
    const contrato = (a.contratos || []).find(c => c.status === 'ativo');
    if (!contrato || !contrato.taxa_academia || Number(contrato.taxa_academia) === 0) return total;
    const realizadas = (a.aulas || []).filter(au => au.status === 'realizada').length;
    return total + Number(contrato.taxa_academia) * realizadas;
  }, 0);

  const custosSala = alunos.reduce((total, a) => {
    return total + (a.aulas || []).reduce((s, au) => s + Number(au.custo_sala || 0), 0);
  }, 0);
  const custosFixosMes = custosMensaisDoMes(custosFixos, mesAtual);
  const receitaLiquida = receitaBruta - custosAcademia - custosSala - custosFixosMes;
  const receitaMes = receitaBruta;

  const pagsFiltrados = pagamentos.filter(p => {
    if (filtroStatus && p.status !== filtroStatus) return false;
    if (filtroAluno && p.aluno_id !== filtroAluno) return false;
    return true;
  });

  // Inadimplente e' quem passou do VENCIMENTO — nao quem acabou de receber a cobranca.
  // Sem vencimento gravado, usa a criacao + prazo padrao de 5 dias.
  const PRAZO_PADRAO_DIAS = 5;
  // v134: o dia de Brasilia (como o banco e a aba Pendencias). Antes cortava o ISO em UTC: entre 21h e
  // meia-noite o "hoje" e a criacao da cobranca ja' eram o dia seguinte, e as duas telas discordavam.
  // data_vencimento/periodo_inicio sao datas (AAAA-MM-DD): so' o corte; created_at e' instante: diaSP.
  const refDate = d => new Date(String(d || hojeSP()).slice(0, 10) + 'T12:00:00');
  const vencimentoDe = p => {
    if (p.data_vencimento) return refDate(p.data_vencimento);
    const base = refDate(p.periodo_inicio || (p.created_at ? diaSP(p.created_at) : null));
    base.setDate(base.getDate() + PRAZO_PADRAO_DIAS);
    return base;
  };
  const hojeRef = refDate(hojeSP());

  const inadimplentes = alunos.map(a => {
    const pVencidos = pagamentos.filter(p =>
      p.aluno_id === a.id && p.status === 'pendente' && vencimentoDe(p) < hojeRef
    );
    if (!pVencidos.length) return null;
    const maisVelho = pVencidos.reduce((min, p) => {
      const v = vencimentoDe(p);
      return v < min ? v : min;
    }, vencimentoDe(pVencidos[0]));
    const dias = Math.floor((hojeRef - maisVelho) / (1000*60*60*24));
    const total = pVencidos.reduce((s,p) => s + Number(p.valor||0), 0);
    return { aluno: a, pagamentos: pVencidos, diasAtraso: dias, total };
  }).filter(Boolean).sort((a,b) => b.diasAtraso - a.diasAtraso);

  const fmtNomeMes = iso => { const [y,m] = iso.split('-'); const ns=['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']; return `${ns[Number(m)-1]} ${y}`; };
  const mesesDisponiveis = (() => {
    const set = new Set(); const hoje = new Date();
    for (let i=0; i<12; i++) { const d = new Date(hoje); d.setMonth(d.getMonth()-i); set.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }
    pagamentos.forEach(p => { if (p.referencia_mes) set.add(p.referencia_mes.slice(0,7)); });
    return [...set].sort((a,b) => b.localeCompare(a)).slice(0,18);
  })();
  const pagsRelatorio = pagamentos.filter(p => p.status==='pago' && (p.referencia_mes||'').startsWith(mesRelatorio));
  const brutoRel = pagsRelatorio.reduce((s,p) => s+Number(p.valor||0), 0);
  const aulasRealizadasRel = alunos.reduce((s,a) => s + (a.aulas||[]).filter(au => au.status==='realizada').length, 0);
  const custosRel = alunos.reduce((total, a) => {
    const c = (a.contratos||[]).find(ct => ct.status==='ativo');
    if (!c || !c.taxa_academia) return total;
    const realizadas = (a.aulas||[]).filter(au => au.status==='realizada').length;
    return total + Number(c.taxa_academia)*realizadas;
  }, 0);
  const custosSalaRel = alunos.reduce((t,a) => t + (a.aulas||[]).reduce((s,au) => s + Number(au.custo_sala||0), 0), 0);
  const liquidoRel = Math.max(0, brutoRel - custosRel - custosSalaRel - custosMensaisDoMes(custosFixos, mesRelatorio));
  const alunosQuePageramRel = new Set(pagsRelatorio.map(p => p.aluno_id)).size;

  function exportarRelatorio() {
    const linhas = pagsRelatorio.map(p => {
      const nome = p.alunos?.nome || '—';
      const val = fmtBRL(p.valor);
      const data = p.data_pagamento ? new Date(p.data_pagamento).toLocaleDateString('pt-BR') : '—';
      const tipo = formaDoPagamento(p);   // COMO foi pago (meio); o tipo so' quando o meio esta' vazio
      return `<tr><td>${nome}</td><td>${val}</td><td>${tipo}</td><td>${data}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Relatório ${fmtNomeMes(mesRelatorio)}</title>
    <style>body{font-family:Arial,sans-serif;padding:32px;color:#16181D} h1{font-size:22px;margin-bottom:4px} .sub{color:#6E7480;font-size:13px;margin-bottom:24px}
    .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:28px} .stat{background:#F7F8FA;border:1px solid #E2E4E9;border-radius:10px;padding:14px}
    .stat-val{font-size:22px;font-weight:800;color:#2F6BFF} .stat-lbl{font-size:11px;color:#6E7480;margin-top:3px}
    table{width:100%;border-collapse:collapse;font-size:13px} th{background:#2F6BFF;color:white;padding:10px 14px;text-align:left;font-size:12px}
    td{padding:9px 14px;border-bottom:1px solid #F0F1F4} tr:nth-child(even) td{background:#F7F8FA}
    .total{font-weight:800;text-align:right;padding:12px 14px;font-size:14px;color:#0A6F4D}</style></head>
    <body><h1> Relatório Financeiro — ${fmtNomeMes(mesRelatorio)}</h1>
    <div class="sub">Professor: ${professor.nome} · Gerado em ${new Date().toLocaleDateString('pt-BR')}</div>
    <div class="stats">
      <div class="stat"><div class="stat-val">${fmtBRL(brutoRel)}</div><div class="stat-lbl">Receita Bruta</div></div>
      <div class="stat"><div class="stat-val">${fmtBRL(liquidoRel)}</div><div class="stat-lbl">Receita Líquida</div></div>
      <div class="stat"><div class="stat-val">${alunosQuePageramRel}</div><div class="stat-lbl">Alunos pagantes</div></div>
      <div class="stat"><div class="stat-val">${pagsRelatorio.length}</div><div class="stat-lbl">Pagamentos</div></div>
    </div>
    <table><thead><tr><th>Aluno</th><th>Valor</th><th>Método</th><th>Data confirmação</th></tr></thead>
    <tbody>${linhas || '<tr><td colspan="4" style="text-align:center;color:#9AA0AB;padding:20px">Nenhum pagamento neste período</td></tr>'}</tbody>
    <tfoot><tr><td colspan="3" class="total">TOTAL</td><td class="total">${fmtBRL(brutoRel)}</td></tr></tfoot></table>
    </body></html>`;
    uso.acao('financeiro.exportar_pdf', { n: pagsRelatorio.length });
    const w = window.open('','_blank');
    // app da loja / pop-up bloqueado: window.open devolve null (antes dava erro calado)
    if (!w) { uso.bloqueio('financeiro_exportar', null, null, 'janela_bloqueada'); toastSM('Não consegui abrir o relatório neste aparelho. Tente pelo navegador do computador.'); return; }
    w.document.write(html); w.document.close(); setTimeout(()=>w.print(),400);
  }

  return (
    <div className="module-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div><h1 className="text-2xl font-bold text-gray-800">Financeiro</h1><p className="text-gray-500 text-sm">Controle de pagamentos e cobranças</p></div>
        <Button onClick={() => { uso.acao('financeiro.registrar_botao'); setModal(true); }}><Icons.Plus /> Registrar Pagamento</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label={'Recebido em ' + new Date().toLocaleDateString('pt-BR', { month: 'long' })} value={fmtBRL(receitaBruta)} color="green" icon={<Ico n="arrow-down-left" s={16} />} sub={custosAcademia > 0 ? `− ${fmtBRL(custosAcademia)} de academia` : 'total pago pelos alunos'} />
        <StatCard label="Em aberto" value={fmtBRL(totalPendente)} color={pendentes.length ? 'orange' : 'gray'} icon={<Ico n="clock" s={16} />} sub={(pendentes.length ? `${pendentes.length} cobrança${pendentes.length > 1 ? 's' : ''} esperando` : 'ninguém devendo') + (aVencer.length ? ` · ${fmtBRL(totalAVencer)} a vencer` : '')} />
        <StatCard label="Líquido no mês" value={fmtBRL(receitaLiquida)} color={receitaLiquida >= 0 ? 'blue' : 'red'} icon={<Ico n="wallet" s={16} />} sub={(custosAcademia > 0 || custosSala > 0 || custosFixosMes > 0) ? [custosAcademia > 0 && `academia ${fmtBRL(custosAcademia, {semCentavos:true})}`, custosSala > 0 && `sala ${fmtBRL(custosSala, {semCentavos:true})}`, custosFixosMes > 0 && `mensalidades ${fmtBRL(custosFixosMes, {semCentavos:true})}`].filter(Boolean).join(' · ') : 'sem custos de espaço'} />
      </div>

      {}
      <div style={{display:'flex',gap:'6px',marginBottom:'20px',borderBottom:'2px solid #F0F1F4',paddingBottom:'0',overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
        {[
 {v:'pagamentos', l:'Pagamentos'},
 {v:'inadimplentes', l:` Inadimplentes${inadimplentes.length > 0 ? ` (${inadimplentes.length})` : ''}`},
 {v:'relatorio', l:'Relatório'},
        ].map(t => (
          <button key={t.v} onClick={() => setAbaFin(t.v)}
            style={{padding:'10px 18px',border:'none',borderBottom: abaFin===t.v ? '2px solid #2F6BFF' : '2px solid transparent',background:'none',fontWeight:abaFin===t.v?'700':'500',fontSize:'13px',color:abaFin===t.v?'#2F6BFF':'#6E7480',cursor:'pointer',marginBottom:'-2px',transition:'all 0.15s',
              ...(t.v==='inadimplentes' && inadimplentes.length > 0 ? {color: abaFin===t.v ? '#DC2626' : '#B91C1C', borderBottomColor: abaFin===t.v ? '#DC2626' : 'transparent'} : {})}}>
            {t.l}
          </button>
        ))}
      </div>

      {}
      {abaFin === 'inadimplentes' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {inadimplentes.length === 0 ? (
            <div style={{background:'#EFFAF5',border:'1.5px solid #8FDFC4',borderRadius:'16px',padding:'32px',textAlign:'center'}}>
              <div style={{fontSize:'32px',marginBottom:'8px'}}><Ico n="circle-check" c="#0E9F6E" /></div>
              <div style={{fontWeight:'700',color:'#0A6F4D',fontSize:'15px'}}>Nenhum pagamento em atraso</div>
              <div style={{color:'#8FDFC4',fontSize:'13px',marginTop:'4px'}}>Todos os alunos estão em dia!</div>
            </div>
          ) : (
            <>
              <div style={{background:'#FEF2F2',border:'1.5px solid #FECACA',borderRadius:'12px',padding:'14px 18px',display:'flex',alignItems:'center',gap:'12px'}}>
                <div style={{fontSize:'24px'}}><Ico n="alert-triangle" c="#D97706" /></div>
                <div>
                  <div style={{fontWeight:'800',color:'#991B1B',fontSize:'14px'}}>{inadimplentes.length} aluno{inadimplentes.length!==1?'s':''} com pagamento em aberto</div>
                  <div style={{fontSize:'12px',color:'#DC2626',marginTop:'2px'}}>Total: {fmtBRL(inadimplentes.reduce((s,i)=>s+i.total,0))} a receber</div>
                </div>
              </div>
              {inadimplentes.map(({ aluno, pagamentos: pags, diasAtraso, total }) => {
                const foneFormatado = (aluno.telefone||'').replace(/\D/g,'');
                const pgInad = comoPagar(pags, professor, (aluno.contratos || []).find(c => c.status === 'ativo'));
                const pixInad = pgInad.forma === 'pix_direto' && pgInad.chave
                  ? `\n\nMinha chave Pix: ${pgInad.chave}` + (pgInad.brcode ? `\n\nOu use o Pix copia e cola (já vai com o valor):\n${pgInad.brcode}` : '')
                  : '';
                const wppMsg = encodeURIComponent(`Olá ${aluno.nome.split(' ')[0]}! Passando para lembrar que há um pagamento pendente de ${fmtBRL(total)}. Qualquer dúvida me avisa!${pixInad}`);
                const wppLink = `https://wa.me/55${foneFormatado.replace(/^55(?=\d{10,11}$)/, '')}?text=${wppMsg}`;
                return (
                  <div key={aluno.id} style={{background:'white',border: diasAtraso > 15 ? '1.5px solid #FECACA' : '1.5px solid #FEF3C7',borderRadius:'14px',padding:'16px 18px',boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'10px'}}>
                      <div style={{width:'40px',height:'40px',borderRadius:'50%',background: diasAtraso > 15 ? '#FEF2F2' : '#FFFBEB',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px',flexShrink:0}}>
 {diasAtraso > 15 ? '': ''}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:'700',color:'#16181D',fontSize:'14px'}}>{aluno.nome}</div>
                        <div style={{fontSize:'11px',color: diasAtraso > 15 ? '#DC2626' : '#B45309',fontWeight:'600',marginTop:'1px'}}>
                          {diasAtraso > 0 ? `${diasAtraso} dia${diasAtraso!==1?'s':''} em atraso` : 'Vence hoje'}
                          {' · '}{pags.length} cobrança{pags.length!==1?'s':''}
                          {(() => { const ult = pags.map(x => x.cobrado_em).filter(Boolean).sort().pop(); return ult ? ' · ' + cobradoEmTxt(ult) : null; })()}
                        </div>
                      </div>
                      <div style={{fontWeight:'800',color:'#16181D',fontSize:'18px',flexShrink:0}}>{fmtBRL(total)}</div>
                    </div>
                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      {pags.map(p => (
                        <button key={p.id} onClick={() => confirmarPagamento(p)}
                          style={{flex:1,minWidth:'140px',display:'flex',alignItems:'center',justifyContent:'center',gap:'5px',background:'linear-gradient(135deg,#0E9F6E,#0E9F6E)',color:'white',border:'none',borderRadius:'9px',padding:'8px 12px',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
                          <Icons.Check /> Confirmar {fmtBRL(p.valor)}
                        </button>
                      ))}
                      {foneFormatado.length >= 10 && (
                        <a href={wppLink} target="_blank" rel="noreferrer" onClick={() => { uso.acao('whatsapp.abrir', { motivo: 'cobranca_atraso', com_numero: true, via: 'link', tem_pix: !!pgInad.chave, com_codigo: !!pgInad.brcode }); marcarCobrado(pags.map(x => x.id)); }}
                          style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'5px',background:'#25D366',color:'white',borderRadius:'9px',padding:'8px 14px',fontSize:'12px',fontWeight:'700',textDecoration:'none',flexShrink:0}}>
                          <Ico n="device-mobile" /> WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {}
      {abaFin === 'relatorio' && (
        <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
          {}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'12px',flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
              <span style={{fontSize:'13px',fontWeight:'600',color:'#3D424C'}}>Período:</span>
              <select value={mesRelatorio} onChange={e => { uso.acao('financeiro.relatorio_mes'); setMesRelatorio(e.target.value); }}
                style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'7px 12px',fontSize:'13px',fontWeight:'700',color:'#16181D',outline:'none',background:'white',cursor:'pointer'}}>
                {mesesDisponiveis.map(m => <option key={m} value={m}>{fmtNomeMes(m)}</option>)}
              </select>
            </div>
            <button onClick={exportarRelatorio}
              style={{display:'flex',alignItems:'center',gap:'6px',background:'linear-gradient(135deg,#2F6BFF,#2F6BFF)',color:'white',border:'none',borderRadius:'10px',padding:'9px 18px',fontSize:'13px',fontWeight:'700',cursor:'pointer',boxShadow:'0 4px 12px rgba(99,102,241,0.3)'}}>
              <Ico n="printer"/> Exportar PDF
            </button>
          </div>
          {}
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'12px'}}>
            {[
              {label:'Receita Bruta',    val:fmtBRL(brutoRel),        cor:'#2F6BFF', bg:'#E7EDFF'},
              {label:'Receita Líquida',  val:fmtBRL(liquidoRel),      cor:'#0E9F6E', bg:'#EFFAF5'},
              {label:'Alunos pagantes',  val:String(alunosQuePageramRel),          cor:'#0EA5E9', bg:'#F0F9FF'},
              {label:'Pagamentos',       val:String(pagsRelatorio.length),         cor:'#B45309', bg:'#FFFBEB'},
            ].map(c => (
              <div key={c.label} style={{background:c.bg,borderRadius:'14px',padding:'16px',textAlign:'center'}}>
                <div style={{fontSize:'22px',fontWeight:'800',color:c.cor}}>{c.val}</div>
                <div style={{fontSize:'11px',color:'#6E7480',marginTop:'4px',fontWeight:'600'}}>{c.label}</div>
              </div>
            ))}
          </div>
          {}
          <div style={{background:'white',border:'1.5px solid #E2E4E9',borderRadius:'16px',overflow:'hidden'}}>
            <div style={{padding:'14px 18px',borderBottom:'1px solid #F0F1F4',fontSize:'12px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.06em'}}>
              Pagamentos confirmados — {fmtNomeMes(mesRelatorio)}
            </div>
            {pagsRelatorio.length === 0 ? (
              <div style={{padding:'28px',textAlign:'center',color:'#9AA0AB',fontSize:'13px'}}>Nenhum pagamento confirmado neste período</div>
            ) : (
              <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                <thead>
                  <tr style={{background:'#F7F8FA'}}>
                    {['Aluno','Valor','Método','Data'].map(h => (
                      <th key={h} style={{padding:'10px 16px',textAlign:'left',fontWeight:'700',color:'#6E7480',fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.05em'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagsRelatorio.map((p,i) => {
                    const tipo = formaDoPagamento(p);
                    return (
                      <tr key={p.id} style={{borderTop:'1px solid #F0F1F4',background:i%2===1?'#F7F8FA':'white'}}>
                        <td style={{padding:'10px 16px',fontWeight:'600',color:'#16181D'}}>{p.alunos?.nome||'—'}</td>
                        <td style={{padding:'10px 16px',fontWeight:'800',color:'#0E9F6E'}}>{fmtBRL(p.valor)}</td>
                        <td style={{padding:'10px 16px',color:'#3D424C'}}>{tipo}</td>
                        <td style={{padding:'10px 16px',color:'#6E7480'}}>{p.data_pagamento ? new Date(p.data_pagamento).toLocaleDateString('pt-BR') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:'2px solid #E2E4E9',background:'#F7F8FA'}}>
                    <td style={{padding:'12px 16px',fontWeight:'800',color:'#16181D'}}>Total</td>
                    <td style={{padding:'12px 16px',fontWeight:'800',color:'#0E9F6E',fontSize:'15px'}}>{fmtBRL(brutoRel)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {}
      {abaFin === 'pagamentos' && <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {[{v:'',l:'Todos'},{v:'pago',l:'Pagos'},{v:'pendente',l:'Pendentes'},{v:'cancelado',l:'Cancelados'}].map(f => (
            <button key={f.v} onClick={() => { uso.acao('financeiro.filtro', { status: f.v || 'todos' }); setFiltroStatus(f.v); }} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filtroStatus===f.v?'bg-indigo-600 text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f.l}</button>
          ))}
          <button onClick={() => { uso.acao('financeiro.filtro', { status: 'reajuste' }); setFiltroStatus('reajuste'); }}
            style={{padding:'6px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'700', cursor:'pointer', border: filtroStatus==='reajuste' ? '2px solid #B45309' : '1.5px solid #FDE68A', background: filtroStatus==='reajuste' ? '#B45309' : '#FFFBEB', color: filtroStatus==='reajuste' ? 'white' : '#B45309', display:'flex', alignItems:'center', gap:'5px'}}>
            <Ico n="alarm" /> Reajustes {contratosParaReajuste.length > 0 && <span style={{background: filtroStatus==='reajuste' ? 'rgba(255,255,255,0.3)' : '#FCD34D', color: filtroStatus==='reajuste' ? 'white' : '#78350F', borderRadius:'10px', padding:'0 6px', fontSize:'11px', fontWeight:'800'}}>{contratosParaReajuste.length}</span>}
          </button>
        </div>
        <select value={filtroAluno} onChange={e => { uso.acao('financeiro.filtro_aluno', { todos: !e.target.value }); setFiltroAluno(e.target.value); }}
          style={{border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'6px 12px', fontSize:'13px', color: filtroAluno ? '#2F6BFF' : '#6E7480', fontWeight: filtroAluno ? '700' : '400', background: filtroAluno ? '#E7EDFF' : 'white', outline:'none', cursor:'pointer'}}>
          <option value="">Todos os alunos</option>
          {alunos.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
        </select>
        {filtroAluno && (
          <button onClick={() => { uso.acao('financeiro.limpar_filtro'); setFiltroAluno(''); }} style={{fontSize:'11px', color:'#2F6BFF', background:'none', border:'none', cursor:'pointer', fontWeight:'600'}}><Ico n="x" /> limpar filtro</button>
        )}
      </div>}

      {}
      {abaFin === 'pagamentos' && filtroStatus === 'reajuste' && (
        <div className="flex flex-col gap-4">

          {}
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#92400E',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:'10px',display:'flex',alignItems:'center',gap:'6px'}}>
              <span style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:'20px',padding:'2px 8px'}}><Ico n="bolt" /> Alertas automáticos — 11 a 14 meses</span>
            </div>
            <div className="flex flex-col gap-2">
              {contratosParaReajuste.length === 0 ? (
                <div style={{background:'#F7F8FA',border:'1.5px dashed #C7CAD1',borderRadius:'12px',padding:'20px',textAlign:'center',color:'#9AA0AB',fontSize:'13px'}}>
                  <Ico n="circle-check" c="#0E9F6E" /> Nenhum aluno na janela de reajuste (11–14 meses)
                </div>
              ) : contratosParaReajuste.map(c => {
                const fmtDR = d => d ? new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—';
                const meses = Math.floor((new Date() - new Date(c.data_inicio+'T12:00:00')) / (1000*60*60*24*30.44));
                const precoAtual = baseReajuste(c);
                return (
                  <div key={c.id} style={{background:'white',border:'1.5px solid #FDE68A',borderRadius:'14px',padding:'14px 18px',display:'flex',alignItems:'center',gap:'12px',boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
                    <div style={{width:'38px',height:'38px',borderRadius:'50%',background:'#FEF3C7',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'18px',flexShrink:0}}><Ico n="cash-banknote" /></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:'700',color:'#16181D',fontSize:'14px'}}>{c.alunos?.nome}</div>
                      <div style={{fontSize:'11px',color:'#92400E',fontWeight:'600',marginTop:'1px'}}>{meses} meses · desde {fmtDR(c.data_inicio)}</div>
                      <div style={{fontSize:'11px',color:'#6E7480'}}>{ehMensalAcordo(c) ? 'Mensalidade atual' : 'Preço atual'}: <strong>{fmtBRL(precoAtual)}</strong>{sufixoPrecoAcordo(c)}</div>
                    </div>
                    <button onClick={() => abrirReajusteFin(c)}
                      style={{background:'linear-gradient(135deg,#B45309,#D97706)',color:'white',border:'none',borderRadius:'10px',padding:'8px 14px',fontSize:'12px',fontWeight:'700',cursor:'pointer',flexShrink:0}}>
                      Reajustar
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {}
          <div style={{borderTop:'1.5px solid #F0F1F4',margin:'4px 0'}} />

          {}
          <div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:'10px',display:'flex',alignItems:'center',gap:'6px'}}>
              <span style={{background:'#F0F1F4',border:'1px solid #E2E4E9',borderRadius:'20px',padding:'2px 8px'}}><Ico n="settings" /> Reajuste manual — qualquer aluno</span>
            </div>
            <div style={{background:'white',border:'1.5px solid #E2E4E9',borderRadius:'14px',padding:'16px 18px',display:'flex',alignItems:'center',gap:'12px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'}}>
              <select value={reajusteManualAlunoId} onChange={e => setReajusteManualAlunoId(e.target.value)}
                style={{flex:1,border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'9px 12px',fontSize:'13px',color: reajusteManualAlunoId ? '#16181D' : '#9AA0AB',outline:'none',background:'white',cursor:'pointer'}}>
                <option value="">Selecione o aluno...</option>
                {alunos.filter(a => (a.contratos||[]).some(c => c.status === 'ativo')).map(a => {
                  const c = (a.contratos||[]).find(c => c.status === 'ativo');
                  return <option key={a.id} value={a.id}>{a.nome} — {fmtBRL(baseReajuste(c))}{sufixoPrecoAcordo(c)}</option>;
                })}
              </select>
              <button
                disabled={!reajusteManualAlunoId}
                onClick={() => {
                  const a = alunos.find(x => x.id === reajusteManualAlunoId);
                  const c = (a?.contratos||[]).find(c => c.status === 'ativo');
                  if (!c) return;
                  abrirReajusteFin({ id: c.id, preco_negociado: c.preco_negociado, tipo_cobranca: c.tipo_cobranca, valor_mensal: c.valor_mensal, aluno_id: a.id, alunos: { nome: a.nome }, data_inicio: c.data_inicio });
                }}
                style={{background: reajusteManualAlunoId ? 'linear-gradient(135deg,#2F6BFF,#2F6BFF)' : '#E2E4E9',color: reajusteManualAlunoId ? 'white' : '#9AA0AB',border:'none',borderRadius:'10px',padding:'9px 18px',fontSize:'13px',fontWeight:'700',cursor: reajusteManualAlunoId ? 'pointer' : 'not-allowed',flexShrink:0,transition:'all 0.15s'}}>
                <Ico n="cash-banknote" /> Reajustar
              </button>
            </div>
          </div>

        </div>
      )}

      {}
      {abaFin === 'pagamentos' && filtroStatus !== 'reajuste' && <div className="flex flex-col gap-2">
        {pagsFiltrados.length === 0 ? (
          <Card className="text-center py-10"><div className="text-gray-500">Nenhum pagamento encontrado</div></Card>
        ) : pagsFiltrados.map(p => {
          // COMO foi pago: le' o meio (Etapa 3); o tipo antigo so' quando o meio esta' vazio.
          const tipoLabel = formaDoPagamento(p);
          // v152: o acordo DA PROPRIA cobranca (p.contrato_id). Renovou antecipado = a cobranca do proximo
          // pacote mostra o proximo, nao o atual. Sem contrato_id (lancamento antigo), cai no ativo.
          const contratosDoAluno = p.alunos?.contratos || [];
          const contratoAtivo = (p.contrato_id && contratosDoAluno.find(c => c.id === p.contrato_id)) || contratosDoAluno.find(c => c.status === 'ativo');
          const ehProximoPag = contratoAtivo?.status === 'proximo';
          const fmtD = d => d ? new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'2-digit'}) : '—';
          const periodoContrato = contratoAtivo ? `${fmtD(contratoAtivo.data_inicio)} a ${fmtD(contratoAtivo.data_fim)}` : null;
          const periodoPag = (p.periodo_inicio && p.periodo_fim) ? `${fmtD(p.periodo_inicio)} a ${fmtD(p.periodo_fim)}` : null;

          const pIni = p.periodo_inicio || contratoAtivo?.data_inicio;
          const pFim = p.periodo_fim || contratoAtivo?.data_fim;
          // Comparacao por data (AAAA-MM-DD): antes comparava meio-dia com meia-noite e um
          // pacote que comecava HOJE aparecia como "Nao iniciado" (25/09).
          const hjStr = new Date().toLocaleDateString('sv-SE');
          const ehMensalPag = contratoAtivo?.tipo_cobranca === 'mensal' || p.tipo === 'mensalidade';
          const periodoStatusBadge = (() => {
            if (ehMensalPag || !pIni || !pFim) return null;
            const d1 = String(pIni).slice(0, 10), d2 = String(pFim).slice(0, 10);
            if (d1 > hjStr) return { label:'Não iniciado', color:'#2455D6', bg:'#E7EDFF' };
            if (d2 < hjStr) return { label:'Concluído',    color:'#6E7480', bg:'#F0F1F4' };
            return              { label:'Ativo',         color:'#0E9F6E', bg:'#EFFAF5' };
          })();
          const nomeMesRef = p.referencia_mes ? new Date(p.referencia_mes + '-15T12:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : null;
          return (
          <div key={p.id} style={{background:'white',border:'1px solid #F0F1F4',borderRadius:'14px',padding:'12px 14px',boxShadow:'0 1px 4px rgba(0,0,0,0.05)'}}>
            {}
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px'}}>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontWeight:'700', color:'#16181D', fontSize:'14px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{p.alunos?.nome}</div>
                <div style={{fontSize:'12px', color:'#6E7480', marginTop:'2px', lineHeight:1.35}}>
                  {p.status === 'pago' ? (p.data_pagamento ? 'Pago em ' + new Date(p.data_pagamento).toLocaleDateString('pt-BR')
                      : ehPagoAntesDoCadastro(p) ? 'Pago antes do cadastro'
                      : <span style={{color:'#B45309', fontWeight:700}}>Pago · confirme a data</span>)
                    : p.status === 'cancelado' ? ('Cancelado' + (p.cancelado_motivo ? ' · ' + (ROTULO_MOTIVO_CANCEL[p.cancelado_motivo] || 'outro motivo') : ''))
                    : p.status === 'pendente' && p.declarado_pago_em   // v155: aluno tocou em "Ja' paguei"
                      ? <span style={{color:'#B45309', fontWeight:700}}>Aluno avisou que pagou em {fmtDiaMes(diaSP(p.declarado_pago_em))} · confira o Pix e confirme</span>
                    : p.status === 'pendente' && p.data_vencimento && String(p.data_vencimento).slice(0, 10) > hjStr
                      ? 'Vence em ' + fmtDiaMes(p.data_vencimento) + (p.cobrado_em ? ' · ' + cobradoEmTxt(p.cobrado_em) : '')   // v149: proximo pacote, ainda nao venceu
                    : 'Aguardando pagamento' + (p.status === 'pendente' && p.cobrado_em ? ' · ' + cobradoEmTxt(p.cobrado_em) : '')}
                  {p.status === 'pago' && tipoLabel !== '—' && !(ehPagoAntesDoCadastro(p) && !p.data_pagamento && !p.meio) ? ` · ${tipoLabel}` : ''}
                  {ehMensalPag
                    ? (nomeMesRef && <span style={{display:'block', color:'#9AA0AB'}}>Mensalidade de {nomeMesRef}</span>)
                    : ehProximoPag
                      ? <span style={{display:'block', color:'#2455D6', fontWeight:600}}>Próximo pacote{(periodoPag || periodoContrato) ? ' · ' + (periodoPag || periodoContrato) : ''}</span>
                    : ((periodoPag || periodoContrato) && <span style={{display:'block', color:'#9AA0AB'}}>Pacote de {periodoPag ? periodoPag : periodoContrato}</span>)}
                </div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'6px', flexShrink:0}}>
                <span className="num" style={{fontWeight:'800', color:'#16181D', fontSize:'15px'}}>{fmtBRL(p.valor)}</span>
                {/* Lixeira = "Cancelar lançamento" (Etapa 3): nada some, a linha fica como Cancelado. */}
                {p.status !== 'cancelado' && (
                  <button onClick={() => setCancelarPag({ pag: p, origem: 'lixeira' })} aria-label="Cancelar lançamento"
                    style={{padding:'4px', borderRadius:'6px', border:'none', background:'transparent', cursor:'pointer', color:'#C7CAD1'}} title="Cancelar lançamento">
                    <Icons.Trash />
                  </button>
                )}
              </div>
            </div>
            {}
            <div style={{marginTop:'8px', display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap'}}>
              <Badge color={p.status==='pago'?'green':p.status==='pendente'?'yellow':'red'}>
                {p.status==='pago'?'Pago':p.status==='pendente'?'Pendente':'Cancelado'}
              </Badge>
              {periodoStatusBadge && (
                <span style={{fontSize:'10px',fontWeight:'700',padding:'2px 7px',borderRadius:'10px',color:periodoStatusBadge.color,background:periodoStatusBadge.bg,whiteSpace:'nowrap'}}>
                  {periodoStatusBadge.label}
                </span>
              )}
              {p.status === 'pendente' && (
                <button onClick={() => confirmarPagamento(p)}
                  style={{display:'flex', alignItems:'center', gap:'4px', background:'linear-gradient(135deg,#0E9F6E,#0E9F6E)', color:'white', border:'none', borderRadius:'8px', padding:'5px 10px', fontSize:'11px', fontWeight:'700', cursor:'pointer'}}>
                  <Ico n="circle-check" c="#0E9F6E" /> Confirmar recebimento
                </button>
              )}
              {p.status === 'pendente' && (() => {
                // Cobrar onde o aluno de fato le'. A regua do banco avisa dentro do app;
                // aqui o professor manda o empurrao pelo WhatsApp, com a chave Pix junto.
                // "Como pagar" vem de comoPagar (src/regras/cobranca.js): valor em aberto desta
                // cobranca, chave e o Pix copia e cola com o codigo da cobranca.
                const aluno = alunos.find(x => x.id === p.aluno_id);
                const fone = (aluno?.telefone || '').replace(/\D/g, '');
                const primeiro = (aluno?.nome || '').split(' ')[0] || 'Oi';
                const pg = comoPagar([p], professor, contratoAtivo);
                const chave = pg.forma === 'pix_direto' && pg.chave
                  ? `\n\nMinha chave Pix: ${pg.chave}` + (pg.brcode ? `\n\nOu use o Pix copia e cola (já vai com o valor):\n${pg.brcode}` : '')
                  : '';
                const doQue = ehMensalPag ? `da mensalidade${nomeMesRef ? ' de ' + nomeMesRef : ''}` : ehProximoPag ? 'do próximo pacote de aulas' : 'do pacote de aulas';
                const texto = `Oi ${primeiro}! Passando pra lembrar ${doQue} — ${fmtBRL(pg.valor || p.valor)} em aberto.${chave}`;
                const url = `https://wa.me/${fone ? '55' + fone.replace(/^55/, '') : ''}?text=${encodeURIComponent(texto)}`;
                return (
                  <a href={url} target="_blank" rel="noopener noreferrer" onClick={() => { uso.acao('whatsapp.abrir', { motivo: 'cobranca_pix', com_numero: !!fone, tem_pix: !!pg.chave, via: 'link', com_codigo: !!pg.brcode }); marcarCobrado([p.id]); }}
                    style={{display:'flex', alignItems:'center', gap:'4px', background:'#0E9F6E', color:'white', borderRadius:'8px', padding:'5px 10px', fontSize:'11px', fontWeight:'700', textDecoration:'none'}}>
                    <Ico n="message-circle" /> Cobrar no WhatsApp
                  </a>
                );
              })()}
              {p.status === 'pago' && (
                <button onClick={() => { uso.acao('financeiro.ver_recibo'); setReciboData({ ...p, plano_nome: '—' }); }} style={{display:'flex',alignItems:'center',gap:'4px',padding:'4px 8px',borderRadius:'8px',border:'1px solid #E2E4E9',background:'white',color:'#2F6BFF',fontSize:'11px',fontWeight:'600',cursor:'pointer'}} title="Ver Recibo"><Ico n="receipt" /> Recibo</button>
              )}
              {p.status === 'pago' && (() => {
                // Pago antes do cadastro, sem data: botao neutro (nao e' pendencia), so' se o professor quiser informar o dia.
                const neutro = !!p.data_pagamento || ehPagoAntesDoCadastro(p);
                return (
                  <button onClick={() => confirmarPagamento(p)}
                    style={{display:'flex',alignItems:'center',gap:'4px',padding:'4px 8px',borderRadius:'8px',border: neutro ? '1px solid #E2E4E9' : 'none',background: neutro ? 'white' : '#D97706',color: neutro ? '#3D424C' : 'white',fontSize:'11px',fontWeight:'700',cursor:'pointer'}}>
                    <Ico n="calendar" /> {p.data_pagamento ? 'Corrigir' : ehPagoAntesDoCadastro(p) ? 'Informar data' : 'Confirmar data'}
                  </button>
                );
              })()}
              {p.status === 'pago' && (
                <button onClick={() => voltarParaPendente(p, `Marcar o pagamento de ${p.alunos?.nome || 'este aluno'} como NÃO pago? Ele volta para "em aberto" (se o aluno tinha avisado "Já paguei", ele pode avisar de novo).`)}
                  style={{fontSize:'11px',color:'#6E7480',background:'none',border:'none',cursor:'pointer',padding:'2px 4px',fontWeight:'600'}}>Não foi pago</button>
              )}
              {p.status === 'pendente' && (
                <button onClick={() => setCancelarPag({ pag: p, origem: 'financeiro' })}
                  style={{fontSize:'11px',color:'#9AA0AB',background:'none',border:'none',cursor:'pointer',padding:'2px 4px'}}>Cancelar cobrança</button>
              )}
              {/* Pago: um caminho visivel alem da lixeira (icone pequeno e claro, dificil no sol). */}
              {p.status === 'pago' && (
                <button onClick={() => setCancelarPag({ pag: p, origem: 'financeiro' })}
                  style={{fontSize:'11px',color:'#9AA0AB',background:'none',border:'none',cursor:'pointer',padding:'2px 4px',minHeight:'32px'}}>Cancelar lançamento</button>
              )}
              {p.status === 'cancelado' && (
                <button onClick={() => confirmarPagamento(p)}
                  style={{display:'flex',alignItems:'center',gap:'4px',background:'#0E9F6E',color:'white',border:'none',borderRadius:'8px',padding:'5px 10px',fontSize:'11px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="circle-check" /> Marcar como pago
                </button>
              )}
              {p.status === 'cancelado' && (
                <button onClick={() => voltarParaPendente(p)}
                  style={{display:'flex',alignItems:'center',gap:'4px',padding:'4px 8px',borderRadius:'8px',border:'1px solid #E2E4E9',background:'white',color:'#3D424C',fontSize:'11px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="arrow-back-up" /> Reabrir cobrança
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>}

      {}
      {modalReajuste && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:60,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'white',borderRadius:'20px',boxShadow:'0 20px 60px rgba(0,0,0,0.2)',width:'100%',maxWidth:'420px',padding:'28px'}}>
            <div style={{fontSize:'18px',fontWeight:'800',color:'#16181D',marginBottom:'4px'}}><Ico n="cash-banknote" /> Reajuste de Preço</div>
            <div style={{fontSize:'13px',color:'#9AA0AB',marginBottom:'20px'}}>{modalReajuste.alunos?.nome}</div>
            {}
            <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:'12px',padding:'14px 16px',marginBottom:'20px'}}>
              <div style={{fontSize:'12px',color:'#92400E',fontWeight:'700',marginBottom:'6px'}}><Ico n="clipboard-list" /> Acordo atual</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                <div><div style={{fontSize:'10px',color:'#B45309',fontWeight:'600'}}>DESDE</div><div style={{fontSize:'13px',fontWeight:'700',color:'#16181D'}}>{modalReajuste.data_inicio ? new Date(modalReajuste.data_inicio+'T12:00:00').toLocaleDateString('pt-BR') : '—'}</div></div>
                <div><div style={{fontSize:'10px',color:'#B45309',fontWeight:'600'}}>DURAÇÃO</div><div style={{fontSize:'13px',fontWeight:'700',color:'#16181D'}}>{Math.floor((new Date()-new Date(modalReajuste.data_inicio+'T12:00:00'))/(1000*60*60*24*30.44))} meses</div></div>
                <div><div style={{fontSize:'10px',color:'#B45309',fontWeight:'600'}}>{ehMensalAcordo(modalReajuste) ? 'MENSALIDADE ATUAL' : 'PREÇO ATUAL'}</div><div style={{fontSize:'16px',fontWeight:'800',color:'#B45309'}}>{fmtBRL(baseReajuste(modalReajuste))}<span style={{fontSize:'11px',fontWeight:'600'}}>{sufixoPrecoAcordo(modalReajuste)}</span></div></div>
                {reajusteForm.novoPreco && Number(reajusteForm.novoPreco) > 0 && Number(reajusteForm.novoPreco) !== baseReajuste(modalReajuste) && (
                  <div><div style={{fontSize:'10px',color:'#0E9F6E',fontWeight:'600'}}>NOVO PREÇO</div><div style={{fontSize:'16px',fontWeight:'800',color:'#0E9F6E'}}>{fmtBRL(reajusteForm.percentual ? (baseReajuste(modalReajuste)*(1+Number(reajusteForm.percentual)/100)) : reajusteForm.novoPreco)}<span style={{fontSize:'11px',fontWeight:'600'}}>{sufixoPrecoAcordo(modalReajuste)}</span></div></div>
                )}
              </div>
            </div>
            {}
            <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Reajuste por percentual (%)</label>
                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                  {[5,10,15,20].map(pct => (
                    <button key={pct} onClick={() => { setReajusteForm(f => ({ percentual: String(pct), novoPreco: (baseReajuste(modalReajuste)*(1+pct/100)).toFixed(2) })); }}
                      style={{padding:'7px 12px',borderRadius:'8px',fontSize:'12px',fontWeight:'700',cursor:'pointer',border:reajusteForm.percentual===String(pct)?'2px solid #B45309':'1.5px solid #E2E4E9',background:reajusteForm.percentual===String(pct)?'#FEF3C7':'white',color:reajusteForm.percentual===String(pct)?'#B45309':'#6E7480'}}>
                      +{pct}%
                    </button>
                  ))}
                  <input type="number" placeholder="Outro %" value={reajusteForm.percentual} min="0" max="100" step="0.1"
                    onChange={e => setReajusteForm(f => ({ percentual: e.target.value, novoPreco: e.target.value ? (baseReajuste(modalReajuste)*(1+Number(e.target.value)/100)).toFixed(2) : f.novoPreco }))}
                    style={{border:'1.5px solid #E2E4E9',borderRadius:'8px',padding:'7px 10px',fontSize:'13px',color:'#16181D',outline:'none',width:'70px'}} />
                </div>
              </div>
              <div>
                <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'7px'}}>Ou informe o valor direto (R$)</label>
                <CampoDinheiro placeholder="0,00" value={reajusteForm.novoPreco}
                  onValor={v => setReajusteForm(f => ({ novoPreco: v, percentual: '' }))}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'10px 14px',fontSize:'14px',color:'#16181D',outline:'none',width:'100%',boxSizing:'border-box'}} />
              </div>
              <div style={{display:'flex',gap:'10px',paddingTop:'4px'}}>
                <button onClick={aplicarReajuste} disabled={salvandoReajuste}
                  style={{flex:1,background:'linear-gradient(135deg,#B45309,#D97706)',color:'white',border:'none',borderRadius:'12px',padding:'13px',fontSize:'14px',fontWeight:'800',cursor:'pointer',opacity:salvandoReajuste?0.7:1}}>
 {salvandoReajuste ? 'Salvando...': 'Confirmar reajuste'}
                </button>
                <button onClick={() => setModalReajuste(null)}
                  style={{padding:'13px 20px',borderRadius:'12px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'14px',fontWeight:'600',cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Registrar Pagamento" uso="pagamento_registrar">
        <div className="flex flex-col gap-4">
          <Select label="Aluno" value={form.aluno_id} onChange={e => onAlunoChange(e.target.value)}>
            <option value="">Selecione o aluno...</option>
            {alunos.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
          </Select>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Valor (R$)</label>
            <CampoDinheiro className="rounded-xl text-sm" style={{border:'1.5px solid #E2E4E9', padding:'11px 14px', fontSize:'16px', background:'white', color:'#16181D'}}
              placeholder="0,00" value={form.valor} onValor={v => setForm(f => ({...f, valor: v}))} />
          </div>

          {}
          <div>
            <label style={{fontSize:'11px',fontWeight:'700',color:'#3D424C',textTransform:'uppercase',letterSpacing:'0.05em',display:'block',marginBottom:'8px'}}>Período coberto</label>
            {form.aluno_id && (
              sugestaoOrigem === 'proximo_pago' ? (
                <div style={{background:'#EFFAF5',border:'1px solid #8FDFC4',borderRadius:'8px',padding:'8px 12px',fontSize:'12px',color:'#0A6F4D',marginBottom:'8px',fontWeight:'600'}}>
                  <Ico n="circle-check" c="#0E9F6E" /> O próximo pacote deste aluno já está pago. Não há nada a registrar agora.
                </div>
              ) : sugestaoOrigem === 'sem_agenda' ? (
                <div style={{background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:'8px',padding:'8px 12px',fontSize:'12px',color:'#92400E',marginBottom:'8px',fontWeight:'600'}}>
                  <Ico n="alert-triangle" c="#D97706" /> Configure a agenda semanal deste aluno para sugestão automática do início do período
                </div>
              ) : sugestaoOrigem && form.periodo_inicio ? (
                <div style={{background:'#E7EDFF',border:'1px solid #C2D4FF',borderRadius:'8px',padding:'8px 12px',fontSize:'12px',color:'#1C43AE',marginBottom:'8px',fontWeight:'600'}}>
 {sugestaoOrigem === 'proximo_pacote'&& 'Cobrança do próximo pacote, já combinada — o pagamento dá baixa nela'}
 {sugestaoOrigem === 'continuacao'&& 'Continuação — início sugerido a partir do término do plano atual'}
 {(sugestaoOrigem === 'agenda'|| sugestaoOrigem === 'agenda_novo') && 'Início sugerido na próxima aula da agenda regular'}
 {sugestaoOrigem === 'hoje'&& 'Início sugerido a partir de hoje'}
                </div>
              ) : null
            )}
            <div style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) minmax(0,1fr)',gap:'10px'}}>
              <div style={{minWidth:0}}>
                <div style={{fontSize:'11px',fontWeight:'600',color:'#6E7480',marginBottom:'5px'}}>Início</div>
                <input type="date" value={form.periodo_inicio}
                  onChange={e => {
                    const ini = e.target.value;

                    let fim = form.periodo_fim;
                    if (ini) {
                      const d = new Date(ini+'T12:00:00');
                      d.setMonth(d.getMonth()+1); d.setDate(d.getDate()-1);
                      fim = fmtISO(d);
                    }
                    setForm(f => ({...f, periodo_inicio: ini, periodo_fim: fim}));
                  }}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'9px 8px',fontSize:'13px',color:'#16181D',outline:'none',width:'100%',minWidth:0,boxSizing:'border-box'}} />
              </div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:'11px',fontWeight:'600',color:'#6E7480',marginBottom:'5px'}}>Fim</div>
                <input type="date" value={form.periodo_fim}
                  onChange={e => setForm(f => ({...f, periodo_fim: e.target.value}))}
                  min={form.periodo_inicio}
                  style={{border:'1.5px solid #E2E4E9',borderRadius:'10px',padding:'9px 8px',fontSize:'13px',color:'#16181D',outline:'none',width:'100%',minWidth:0,boxSizing:'border-box'}} />
              </div>
            </div>
            {form.periodo_inicio && form.periodo_fim && (
              <div style={{fontSize:'11px',color:'#6E7480',marginTop:'5px',textAlign:'center'}}>
                {(() => {
                  const d1 = new Date(form.periodo_inicio+'T12:00:00');
                  const d2 = new Date(form.periodo_fim+'T12:00:00');
                  const dias = Math.round((d2-d1)/(1000*60*60*24))+1;
                  return `${dias} dia${dias!==1?'s':''} de cobertura`;
                })()}
              </div>
            )}
          </div>

          <Select label="Status do pagamento" value={form.status_pagamento} onChange={e => setForm(f => ({...f, status_pagamento: e.target.value}))}>
            <option value="pendente">Em aberto — o aluno ainda vai pagar</option>
            <option value="pago">Já recebi — dar baixa agora</option>
          </Select>

          {form.status_pagamento === 'pago' && (
            <Select label="Como o aluno pagou" value={form.meio} onChange={e => setForm(f => ({...f, meio: e.target.value}))}>
              {MEIOS_PAGAMENTO.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </Select>
          )}
          {form.status_pagamento === 'pendente'
            ? <div className="bg-yellow-50 text-yellow-700 rounded-xl p-3 text-sm"><Ico n="bulb" c="#D97706" /> Vai aparecer como cobrança em aberto para o aluno. Confirme quando o Pix cair.</div>
            : <div className="bg-green-50 text-green-700 rounded-xl p-3 text-sm"><Ico n="circle-check" c="#0E9F6E" /> Só use se o dinheiro <strong>já entrou</strong> na sua conta — isso dá baixa na cobrança.</div>}
          <div className="flex gap-3 pt-2"><Button onClick={registrarPagamento} disabled={registrandoPag} className="flex-1 justify-center">{registrandoPag ? 'Registrando…' : 'Registrar'}</Button><Button variant="secondary" disabled={registrandoPag} onClick={() => setModal(false)}>Voltar</Button></div>
        </div>
      </Modal>

      {}
      {modalConfirmar && (
        <Portal>
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:60, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
          <div style={{background:'white', borderRadius:'20px', boxShadow:'0 20px 60px rgba(0,0,0,0.2)', width:'100%', maxWidth:'380px', padding:'28px'}}>
            <div style={{fontSize:'18px', fontWeight:'800', color:'#16181D', marginBottom:'4px'}}><Ico n="circle-check" c="#0E9F6E" /> {modalConfirmar.status === 'pago' ? (modalConfirmar.data_pagamento ? 'Corrigir pagamento' : 'Confirmar data do pagamento') : 'Confirmar recebimento'}</div>
            <div style={{fontSize:'13px', color:'#9AA0AB', marginBottom:'22px'}}>
              {fmtBRL(modalConfirmar.valor)} · {modalConfirmar.alunos?.nome || '—'}
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:'16px'}}>
              <div>
                <label style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'8px'}}>Como o aluno pagou</label>
                <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                  {MEIOS_PAGAMENTO.map(op => (
                    <button key={op.v} onClick={() => setConfirmarForm(f => ({...f, meio: op.v}))}
                      style={{padding:'8px 14px', minHeight:'40px', borderRadius:'10px', fontSize:'13px', fontWeight:'700', cursor:'pointer', border: confirmarForm.meio === op.v ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: confirmarForm.meio === op.v ? '#E7EDFF' : 'white', color: confirmarForm.meio === op.v ? '#2F6BFF' : '#6E7480'}}>
                      {op.l}
                    </button>
                  ))}
                </div>
                {modalConfirmar.status === 'pago' && !confirmarForm.meio && (
                  <div style={{fontSize:'11px', color:'#9AA0AB', marginTop:'6px'}}>Sem forma informada (ex.: pago antes do cadastro). Escolha só se souber.</div>
                )}
              </div>
              {/* Um embaixo do outro no celular, lado a lado no computador: o campo de mes do navegador
                  tem largura minima propria e passava da borda (ou cortava "setembro de 2026") em 375 px. 02/10. */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))',gap:'12px'}}>
                <div style={{minWidth:0}}>
                  <label style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'7px'}}>Data do recebimento</label>
                  <input type="date" value={confirmarForm.data_pagamento} max={hojeSP()} onChange={e => setConfirmarForm(f => ({...f, data_pagamento: e.target.value}))}
                    style={{border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'10px 10px', fontSize:'14px', color:'#16181D', outline:'none', width:'100%', minWidth:0, boxSizing:'border-box'}} />
                </div>
                <div style={{minWidth:0}}>
                  <label style={{fontSize:'11px', fontWeight:'700', color:'#3D424C', textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'7px'}}>Mês de referência</label>
                  <input type="month" value={confirmarForm.competencia} onChange={e => setConfirmarForm(f => ({...f, competencia: e.target.value}))}
                    style={{border:'1.5px solid #E2E4E9', borderRadius:'10px', padding:'10px 10px', fontSize:'14px', color:'#16181D', outline:'none', width:'100%', minWidth:0, boxSizing:'border-box'}} />
                </div>
              </div>
              <div style={{fontSize:'11px',color:'#9AA0AB',marginTop:'-8px'}}><Ico n="bulb" c="#D97706" /> Mude o "Mês de referência" só se este pagamento for de outro mês (ex.: pagou em janeiro a mensalidade de fevereiro).</div>
              {modalConfirmar.status === 'pago' && !modalConfirmar.data_pagamento && (
                <div style={{fontSize:'12px', color:'#3D424C', background:'#F7F8FA', border:'1px solid #E2E4E9', borderRadius:'10px', padding:'8px 12px'}}>Não lembra o dia? Toque em <strong>Voltar</strong> — ele continua como pago, sem data.</div>
              )}
              {modalConfirmar.status === 'cancelado' && (
                <div style={{fontSize:'12px', color:'#92400E', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'10px', padding:'8px 12px'}}>Esta cobrança estava cancelada: ela volta a valer e já entra como paga.</div>
              )}
              <div style={{display:'flex', gap:'10px', paddingTop:'6px'}}>
                <button onClick={executarConfirmarPagamento} disabled={confirmandoPag}
                  style={{flex:1, background:'linear-gradient(135deg,#0E9F6E,#0E9F6E)', color:'white', border:'none', borderRadius:'12px', padding:'13px', fontSize:'14px', fontWeight:'800', cursor: confirmandoPag ? 'not-allowed' : 'pointer', opacity: confirmandoPag ? 0.7 : 1}}>
                  <Ico n="circle-check" c="white" /> {confirmandoPag ? 'Confirmando…' : 'Confirmar'}
                </button>
                <button onClick={() => { if (!confirmandoPag) setModalConfirmar(null); }} disabled={confirmandoPag}
                  style={{padding:'13px 20px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'14px', fontWeight:'600', cursor: confirmandoPag ? 'not-allowed' : 'pointer', opacity: confirmandoPag ? 0.6 : 1}}>
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {}
      {reciboData && (
        <Portal>
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setReciboData(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icons.CheckCircle />
                  <span className="text-3xl ml-1"><Ico n="check" /></span>
                </div>
                <h2 className="text-xl font-bold text-gray-800">Recibo de Pagamento</h2>
                <div className="text-xs text-gray-400 mt-1 font-mono">#{String(reciboData.id || '').slice(0,8).toUpperCase()}</div>
              </div>
              <div className="flex flex-col gap-0 divide-y divide-gray-100 text-sm mb-6">
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Aluno</span>
                  <span className="font-semibold text-gray-800">{reciboData.alunos?.nome || '—'}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Plano</span>
                  <span className="font-medium text-gray-700">{reciboData.plano_nome || '—'}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Referência</span>
                  <span className="font-medium text-gray-700">{/^\d{4}-\d{2}$/.test(reciboData.referencia_mes || '') ? nomeMesRefSM(reciboData.referencia_mes) : (reciboData.referencia_mes || '—')}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Forma de pagamento</span>
                  <span className="font-medium text-gray-700">{formaDoPagamento(reciboData)}</span>
                </div>
                <div className="flex justify-between py-2.5">
                  <span className="text-gray-500">Data</span>
                  <span className="font-medium text-gray-700">{reciboData.data_pagamento ? new Date(reciboData.data_pagamento).toLocaleDateString('pt-BR') : '—'}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="font-bold text-gray-800 text-base">Valor pago</span>
                  <span className="text-2xl font-bold text-green-600">{fmtBRL(reciboData.valor)}</span>
                </div>
              </div>
              <div className="text-center text-xs text-gray-400 mb-5">Pagamento confirmado <Ico n="check" /> · Plen</div>
              <div className="flex gap-3">
                <Button onClick={() => { uso.acao('financeiro.imprimir_recibo'); window.print(); }} className="flex-1 justify-center"><Ico n="printer"/> Imprimir</Button>
                <Button variant="secondary" onClick={() => setReciboData(null)} className="flex-1 justify-center">Fechar</Button>
              </div>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {cancelarPag && (
        <ModalCancelarLancamento pagamento={cancelarPag.pag} nomeAluno={cancelarPag.pag.alunos?.nome} origem={cancelarPag.origem}
          onFechar={() => setCancelarPag(null)}
          onCancelado={() => { setCancelarPag(null); loadPagamentos(); }} />
      )}
    </div>
  );
}

// ---- Administracao de promocoes (so' admin) --------------------------------------
// Aqui a Plen cria a regra do incentivo e paga o que foi ganho. Tudo que muda o modelo
// (8 → 10 aulas, valor fixo → percentual, publico, vigencia) e' feito aqui, sem deploy.
function AdminPromocoes() {
  const [promos, setPromos] = React.useState([]);
  const [recs, setRecs] = React.useState([]);
  const [profs, setProfs] = React.useState({});
  const [aberto, setAberto] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);
  const [f, setF] = React.useState({ nome:'', regra:'a_cada_aulas', gatilho:'10',
    recompensa_tipo:'valor_aula_media', recompensa_valor:'', vigencia_inicio:'', vigencia_fim:'' });

  async function carregar() {
    const [{ data: p }, { data: r }, { data: pr }] = await Promise.all([
      supabase.from('promocoes').select('*').order('vigencia_inicio', { ascending: false }),
      supabase.from('recompensas').select('*').neq('status', 'cancelada').order('ganha_em', { ascending: false }).limit(100),
      supabase.from('professores').select('id, nome, pix_chave'),
    ]);
    setPromos(p || []); setRecs(r || []);
    const m = {}; (pr || []).forEach(x => { m[x.id] = x; }); setProfs(m);
  }
  React.useEffect(() => { carregar(); }, []);

  const fmtR = v => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtD = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '—';

  async function criar() {
    if (!f.nome.trim() || !Number(f.gatilho)) { toastSM('Nome e regra são obrigatórios'); return; }
    setSalvando(true);
    const { error } = await supabase.from('promocoes').insert({
      nome: f.nome.trim(), regra: f.regra, gatilho: Number(f.gatilho),
      recompensa_tipo: f.recompensa_tipo,
      recompensa_valor: f.recompensa_tipo === 'valor_aula_media' ? 0 : Number(String(f.recompensa_valor).replace(',', '.')) || 0,
      vigencia_inicio: f.vigencia_inicio || new Date().toISOString().slice(0, 10),
      vigencia_fim: f.vigencia_fim || null, ativa: true,
    });
    setSalvando(false);
    if (error) { toastSM('Não foi possível criar: ' + error.message); return; }
    toastSM('Promoção criada');
    setAberto(false); setF({ ...f, nome: '' }); carregar();
  }

  async function alternar(p) {
    await supabase.from('promocoes').update({ ativa: !p.ativa }).eq('id', p.id);
    carregar();
  }

  async function marcarPaga(r) {
    const { error } = await supabase.from('recompensas')
      .update({ status: 'paga', paga_em: new Date().toISOString(), pago_via: 'pix_manual' }).eq('id', r.id);
    if (error) { toastSM('Não foi possível marcar: ' + error.message); return; }
    carregar();
  }

  const aPagar = recs.filter(r => r.status === 'ganha');
  const inp = { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1.5px solid #E2E4E9', fontSize: '14px', outline: 'none' };
  const lab = t => <div style={{ fontSize: '11px', fontWeight: '700', color: '#6E7480', textTransform: 'uppercase', margin: '10px 0 4px' }}>{t}</div>;
  const regraTxt = p => (p.regra === 'a_cada_aulas' ? `a cada ${Number(p.gatilho)} aulas` : `a cada ${fmtR(p.gatilho)}`);
  const premioTxt = p => p.recompensa_tipo === 'valor_fixo' ? fmtR(p.recompensa_valor)
                       : p.recompensa_tipo === 'percentual' ? `${Number(p.recompensa_valor)}%`
                       : 'valor médio da aula';

  return (
    <div style={{ background: 'white', border: '1px solid #E2E4E9', borderRadius: '16px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '800', color: '#16181D' }}><Ico n="gift" c="#2F6BFF" /> Promoções e prêmios</div>
          <div style={{ fontSize: '12px', color: '#6E7480' }}>O Plen confere os prêmios toda madrugada, às 4h. Aula conta se foi dada e o aluno está em dia.</div>
        </div>
        <button onClick={() => setAberto(!aberto)}
          style={{ border: 'none', background: '#2F6BFF', color: 'white', borderRadius: '10px', padding: '9px 14px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <Ico n="plus" s={14} /> Nova promoção
        </button>
      </div>

      {aberto && (
        <div style={{ marginTop: '14px', background: '#F7F8FA', borderRadius: '12px', padding: '14px' }}>
          {lab('Nome')}
          <input value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} placeholder="Lançamento" style={inp} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              {lab('Regra')}
              <select value={f.regra} onChange={e => setF({ ...f, regra: e.target.value })} style={inp}>
                <option value="a_cada_aulas">A cada N aulas</option>
                <option value="a_cada_valor">A cada R$ X recebidos</option>
              </select>
            </div>
            <div>
              {lab(f.regra === 'a_cada_aulas' ? 'N (aulas)' : 'X (R$)')}
              <input value={f.gatilho} onChange={e => setF({ ...f, gatilho: e.target.value.replace(/[^\d.,]/g, '') })} inputMode="decimal" style={inp} />
            </div>
            <div>
              {lab('Recompensa')}
              <select value={f.recompensa_tipo} onChange={e => setF({ ...f, recompensa_tipo: e.target.value })} style={inp}>
                <option value="valor_aula_media">Uma aula de volta (valor médio)</option>
                <option value="valor_fixo">Valor fixo (R$)</option>
                <option value="percentual">Percentual (%)</option>
              </select>
            </div>
            <div>
              {lab(f.recompensa_tipo === 'percentual' ? '%' : 'R$')}
              <input value={f.recompensa_valor} disabled={f.recompensa_tipo === 'valor_aula_media'}
                onChange={e => setF({ ...f, recompensa_valor: e.target.value.replace(/[^\d.,]/g, '') })}
                inputMode="decimal" style={{ ...inp, opacity: f.recompensa_tipo === 'valor_aula_media' ? 0.4 : 1 }} />
            </div>
            <div>
              {lab('Começa em')}
              <input type="date" value={f.vigencia_inicio} onChange={e => setF({ ...f, vigencia_inicio: e.target.value })} style={inp} />
            </div>
            <div>
              {lab('Termina em (opcional)')}
              <input type="date" value={f.vigencia_fim} onChange={e => setF({ ...f, vigencia_fim: e.target.value })} style={inp} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <button disabled={salvando} onClick={criar}
              style={{ flex: 1, border: 'none', background: '#16181D', color: 'white', borderRadius: '10px', padding: '11px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
              {salvando ? 'Criando…' : 'Criar promoção'}
            </button>
            <button onClick={() => setAberto(false)}
              style={{ border: '1.5px solid #E2E4E9', background: 'white', color: '#6E7480', borderRadius: '10px', padding: '11px 14px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* lista de promocoes */}
      <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {promos.length === 0 && <div style={{ fontSize: '13px', color: '#9AA0AB' }}>Nenhuma promoção ainda.</div>}
        {promos.map(p => (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', border: '1px solid #E2E4E9', borderRadius: '12px', padding: '10px 12px', opacity: p.ativa ? 1 : 0.55 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#16181D' }}>{p.nome}</div>
              <div style={{ fontSize: '12px', color: '#6E7480' }}>
                {regraTxt(p)} → {premioTxt(p)} · {fmtD(p.vigencia_inicio)}{p.vigencia_fim ? ' a ' + fmtD(p.vigencia_fim) : ' em diante'}
                {p.professores ? ` · ${p.professores.length} professor(es)` : ' · todos'}
              </div>
            </div>
            <button onClick={() => alternar(p)}
              style={{ border: '1.5px solid #E2E4E9', background: 'white', borderRadius: '8px', padding: '6px 10px', fontSize: '12px', fontWeight: '700', color: p.ativa ? '#DC2626' : '#0E9F6E', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {p.ativa ? 'Encerrar' : 'Reativar'}
            </button>
          </div>
        ))}
      </div>

      {/* fila de pagamento */}
      <div style={{ marginTop: '16px', borderTop: '1px solid #F0F1F4', paddingTop: '14px' }}>
        <div style={{ fontSize: '13px', fontWeight: '800', color: '#16181D', marginBottom: '8px' }}>
          A pagar {aPagar.length > 0 && <span style={{ color: '#DC2626' }}>· {aPagar.length} · {fmtR(aPagar.reduce((s, r) => s + Number(r.valor), 0))}</span>}
        </div>
        {aPagar.length === 0 && <div style={{ fontSize: '13px', color: '#9AA0AB' }}>Nada para pagar. Os prêmios aparecem aqui depois da conferência da madrugada.</div>}
        {aPagar.map(r => {
          const pf = profs[r.professor_id] || {};
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '10px 12px', marginBottom: '6px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#16181D' }}>{pf.nome || 'Professor'} · {fmtR(r.valor)}</div>
                <div style={{ fontSize: '12px', color: '#6E7480' }}>
                  {new Date(r.ganha_em).toLocaleDateString('pt-BR')} · {Number(r.base_qtd)} aulas · Pix: <span style={{ userSelect: 'all', fontFamily: 'monospace' }}>{pf.pix_chave || 'sem chave cadastrada'}</span>
                </div>
              </div>
              <button onClick={() => marcarPaga(r)}
                style={{ border: 'none', background: '#0E9F6E', color: 'white', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <Ico n="check" s={13} /> Paguei
              </button>
            </div>
          );
        })}
        {recs.filter(r => r.status === 'paga').length > 0 && (
          <div style={{ fontSize: '12px', color: '#9AA0AB', marginTop: '8px' }}>
            {recs.filter(r => r.status === 'paga').length} pagas · {fmtR(recs.filter(r => r.status === 'paga').reduce((s, r) => s + Number(r.valor), 0))} no total
          </div>
        )}
      </div>
    </div>
  );
}

// Painel do administrador (Configuracoes -> Painel e a pagina /painel). Desde a v139 e' so'
// uma casca do cockpit (src/central.jsx): Inicio + Detalhes, com as promocoes dentro de
// Detalhes › Promocoes e premios. Quem decide o acesso continua o BANCO.
function PainelPiloto() {
  const app = useApp();
  return <PainelCentral supabase={supabase} versaoNoAr={VERSAO_APP} email={app && app.user ? app.user.email : ''}
    promocoes={<AdminPromocoes />} onAba={k => { try { if (naRotaPainel()) uso.tela('painel.' + k); } catch (e) {} }} />;
}

// Mensalidades que o professor paga para dar aula (academia, clube). Salva na hora — nao
// depende do "Salvar perfil" la' de baixo. 30/09.
function CardCustosMensais({ professor }) {
  const [lista, setLista] = React.useState([]);
  const [nome, setNome] = React.useState('');
  const [valor, setValor] = React.useState('');
  React.useEffect(() => { carregarCustosMensais(professor.id).then(setLista); }, []);
  async function gravar(nova, msg) {
    if (await salvarCustosMensais(professor.id, nova)) { setLista(nova); toastSM(msg); }
    else toastSM('Não consegui salvar. Tente de novo em instantes.');
  }
  function adicionar() {
    const n = nome.trim(), v = numDinheiro(valor);
    if (!n || v <= 0) { uso.bloqueio('custos_mensais', null, null, 'sem_dados'); toastSM('Digite a academia e quanto você paga por mês.'); return; }
    if (lista.some(c => semAcentoSM(c.nome) === semAcentoSM(n))) { uso.bloqueio('custos_mensais', null, null, 'repetida'); toastSM('Essa academia já está na lista.'); return; }
    uso.acao('custos.adicionar');
    gravar([...lista, { nome: n, valor: v, desde: mesAtualSM() }], n + ' adicionada.');
    setNome(''); setValor('');
  }
  function remover(c) {
    if (!window.confirm(`Tirar ${c.nome} dos seus custos do mês?`)) return;
    uso.acao('custos.remover');
    gravar(lista.filter(x => x !== c), c.nome + ' removida.');
  }
  const inp = { border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'10px 12px', fontSize:'15px', outline:'none', boxSizing:'border-box', minWidth:0 };
  return (
    <Card>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl"><Ico n="building" /></span>
        <h2 className="font-bold text-gray-700">Academias que cobram por mês</h2>
      </div>
      <p className="text-xs text-gray-400 mb-4">Sai uma vez por mês do seu líquido no Financeiro, não importa quantos alunos treinem lá. Academia que cobra por aula fica no acordo de cada aluno.</p>
      {lista.map(c => (
        <div key={c.nome} style={{display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', border:'1.5px solid #E2E4E9', borderRadius:'12px', marginBottom:'8px'}}>
          <span style={{flex:1, fontWeight:'700', fontSize:'14px'}}>{c.nome}</span>
          <span style={{fontSize:'14px', color:'#3D424C'}}>R$ {Number(c.valor).toLocaleString('pt-BR', {minimumFractionDigits: 2})}/mês</span>
          <button type="button" onClick={() => remover(c)} aria-label={'Tirar ' + c.nome} style={{border:'none', background:'none', cursor:'pointer', color:'#9AA0AB', padding:'4px'}}><Ico n="x" s={16} /></button>
        </div>
      ))}
      <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
        <input style={{...inp, flex:'2 1 140px'}} value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex.: Smart Fit Pinheiros" />
        <input style={{...inp, flex:'1 1 90px'}} inputMode="decimal" value={valor} onChange={e => setValor(limparDinheiro(e.target.value))} onBlur={() => setValor(v => formatarDinheiro(v))} placeholder="R$ por mês" />
        <button type="button" onClick={adicionar} style={{flex:'0 0 auto', border:'none', background:'#2F6BFF', color:'white', fontWeight:'700', borderRadius:'12px', padding:'10px 16px', cursor:'pointer'}}>Adicionar</button>
      </div>
    </Card>
  );
}

function Configuracoes({ professor }) {
  const { setProfessor, setPerfil: setCtxPerfil, setUser: setCtxUser } = useApp();
  const [tab, setTab] = React.useState('conta');
  // Central de uso: a tela de Configuracoes e' a aba (o ProfessorApp nao emite 'configuracoes').
  React.useEffect(() => { uso.tela('prof.config.' + tab); }, [tab]);
  const [adminOk, setAdminOk] = React.useState(false);
  React.useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.user?.email === 'mvprianti21@gmail.com') setAdminOk(true);
    });
  }, []);
  const [confirmaExclusaoProf, setConfirmaExclusaoProf] = React.useState(false);
  const [excluindoConta, setExcluindoConta] = React.useState(false);

  async function excluirContaProfessor() {
    setExcluindoConta(true);
    // a fila da central de uso sai ANTES: depois da exclusao o login deixa de valer
    try { uso.evento('sessao.conta_excluida'); uso.enviarAgora(); } catch {}
    try {
      // Caminho certo (28/09): a função do banco apaga os dados E o login (e-mail).
      // Antes o app apagava os dados mas o e-mail continuava cadastrado — as lojas exigem
      // que a conta suma de verdade. Os logins dos alunos NÃO são apagados (são contas deles).
      const { error: eRpc } = await supabase.rpc('excluir_minha_conta');
      if (!eRpc) {
        try { localStorage.clear(); } catch (_) {}
        await supabase.auth.signOut();
        setCtxUser(null); setCtxPerfil(null); setProfessor(null);
        return;
      }
      console.warn('excluir_minha_conta indisponível, usando caminho antigo:', eRpc.message);
      // Buscar todos os alunos deste professor
      const { data: alunos } = await supabase.from('alunos').select('id, user_id').eq('professor_id', professor.id);
      const ids = (alunos || []).map(a => a.id);
      const uids = (alunos || []).map(a => a.user_id).filter(Boolean);
      // Apagar dados dos alunos
      if (ids.length > 0) {
        await supabase.from('notificacoes').delete().in('aluno_id', ids);
        await supabase.from('aulas').delete().in('aluno_id', ids);
        await supabase.from('creditos').delete().in('aluno_id', ids);
        await supabase.from('pagamentos').delete().in('aluno_id', ids);
        await supabase.from('contratos').delete().in('aluno_id', ids);
        await supabase.from('alunos').delete().eq('professor_id', professor.id);
        // Os perfis/logins dos alunos NÃO são apagados: são contas deles, não do professor.
      }
      // Apagar dados do professor
      await supabase.from('config_notificacoes').delete().eq('professor_id', professor.id);
      await supabase.from('convites').delete().eq('professor_id', professor.id);
      await supabase.from('professores').delete().eq('id', professor.id);
      await supabase.from('perfis').delete().eq('id', professor.user_id);
      localStorage.clear();
      await supabase.auth.signOut();
      setCtxUser(null); setCtxPerfil(null); setProfessor(null);
    } catch(e) {
      uso.erro('erro.excluir_conta', e?.message || e);
      toastSM('Erro ao excluir conta: ' + e.message);
      setExcluindoConta(false);
    }
  }

  const [config, setConfig] = React.useState({ aviso_creditos_restantes: 2, aviso_renovacao_mensal_dias: 7, aviso_renovacao_anual_meses: 2, desconto_renovacao_antecipada: 0 });

  const [perfil, setPerfil] = React.useState({
    nome: professor.nome || '',
    telefone: professor.telefone || '',
    pix_tipo: professor.pix_tipo || 'cpf',
    pix_chave: professor.pix_chave || '',
    bio: professor.bio || '',
    modalidades: parseMods(professor.modalidades),
    padrao_antecedencia_horas: professor.padrao_antecedencia_horas ?? 24,
    padrao_reposicao: professor.padrao_reposicao ?? true,
    padrao_reajuste_pct: professor.padrao_reajuste_pct ?? 0,
  });

  function toggleMod(m) {
    setPerfil(p => {
      const cur = p.modalidades;
      return { ...p, modalidades: cur.includes(m) ? cur.filter(x => x !== m) : [...cur, m] };
    });
  }
  const [salvandoPerfil, setSalvandoPerfil] = React.useState(false);
  const [msgPerfil, setMsgPerfil] = React.useState('');

  React.useEffect(() => {
    async function load() {
      const { data: c } = await supabase.from('config_notificacoes').select('*').eq('professor_id', professor.id).single();
      if (c) setConfig(c);
    }
    load();
  }, [professor.id]);

  async function salvarAlertas() {
    await supabase.from('config_notificacoes').upsert({ professor_id: professor.id, ...config });
    uso.acao('alertas.salvar');
    toastSM('Configurações de alertas salvas!');
  }

  async function salvarPerfil() {
    if (!perfil.nome.trim()) { uso.bloqueio('perfil_salvar', null, null, 'sem_nome'); setMsgPerfil('Nome é obrigatório.'); return; }
    setSalvandoPerfil(true); setMsgPerfil('');
    const { error } = await supabase.from('professores').update({
      nome: perfil.nome.trim(),
      telefone: perfil.telefone.trim(),
      pix_tipo: perfil.pix_tipo,
      pix_chave: perfil.pix_chave.trim(),
      bio: perfil.bio.trim(),
      modalidades: JSON.stringify(perfil.modalidades),
      padrao_antecedencia_horas: Number(perfil.padrao_antecedencia_horas) || 24,
      padrao_reposicao: !!perfil.padrao_reposicao,
      padrao_reajuste_pct: Number(perfil.padrao_reajuste_pct) || 0,
    }).eq('id', professor.id);
    if (error) { uso.erro('erro.perfil_salvar', error.message); setMsgPerfil('Erro ao salvar: '+ error.message); }
    else {
      uso.acao('perfil.salvar', { tem_pix: !!perfil.pix_chave.trim(), tem_bio: !!perfil.bio.trim() });

      setProfessor(prev => ({ ...prev, ...perfil, modalidades: JSON.stringify(perfil.modalidades) }));
      setMsgPerfil('Perfil atualizado com sucesso!');
      setTimeout(() => setMsgPerfil(''), 3000);
    }
    setSalvandoPerfil(false);
  }

  const tabs = [
 { k: 'conta', l: 'Meu Perfil'},
 { k: 'alertas', l: 'Alertas'},
    ...(adminOk ? [{ k: 'piloto', l: 'Painel'}] : []),
  ];

  const pixTipos = [
    { v: 'cpf',      l: 'CPF' },
    { v: 'cnpj',     l: 'CNPJ' },
    { v: 'telefone', l: 'Telefone' },
    { v: 'email',    l: 'E-mail' },
    { v: 'aleatoria',l: 'Chave aleatória' },
  ];

  return (
    <div className="module-page" style={{maxWidth:'800px', margin:'0 auto'}}>
      <div style={{fontSize:'22px',fontWeight:'800',color:'#16181D',letterSpacing:'-0.5px',marginBottom:'4px'}}>Configurações</div>
      <div style={{fontSize:'13px',color:'#9AA0AB',marginBottom:'24px'}}>Perfil, alertas e regras do sistema</div>

      {}
      <div style={{display:'flex',gap:'4px',background:'#F0F1F4',borderRadius:'12px',padding:'4px',marginBottom:'28px',overflowX:'auto',maxWidth:'100%'}}>
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{padding:'8px 20px',borderRadius:'8px',border:'none',fontWeight:'700',fontSize:'13px',cursor:'pointer',
              background:tab===t.k?'white':'transparent',color:tab===t.k?'#2F6BFF':'#6E7480',
              boxShadow:tab===t.k?'0 1px 4px rgba(0,0,0,0.1)':'none',transition:'all 0.15s'}}>
            {t.l}
          </button>
        ))}
      </div>

      {}
      {tab === 'piloto' && adminOk && <PainelPiloto />}

      {tab === 'conta' && (
        <div className="flex flex-col gap-5">
          {}
          <Card>
            <h2 className="font-bold text-gray-700 mb-1">Dados pessoais</h2>
            <p className="text-xs text-gray-400 mb-4">Nome e telefone visíveis para seus alunos</p>
            <div className="flex flex-col gap-4">
              <Input label="Nome completo" value={perfil.nome}
                onChange={e => setPerfil(p => ({...p, nome: e.target.value}))}
                placeholder="Seu nome como professor" />
              <Input label="Telefone / WhatsApp" value={perfil.telefone}
                onChange={e => setPerfil(p => ({...p, telefone: mascaraTel(e.target.value)}))}
                placeholder="+55 (11) 99999-9999" />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">E-mail da conta</label>
                <div className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-400">
                  {professor.email} <span className="text-xs">(não editável)</span>
                </div>
              </div>
            </div>
          </Card>

          {/* v151: logo abaixo do e-mail da conta — achavel, longe da "Zona de Perigo" */}
          <TrocarSenhaSM quem="professor" />

          {}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl"><Ico n="bolt" /></span>
              <h2 className="font-bold text-gray-700">Chave PIX para recebimento</h2>
            </div>
            <p className="text-xs text-gray-400 mb-4">Seus alunos verão essa chave na tela de pagamento para te pagar via Pix</p>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">Tipo de chave</label>
                <select value={perfil.pix_tipo}
                  onChange={e => setPerfil(p => ({...p, pix_tipo: e.target.value}))}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 bg-white">
                  {pixTipos.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <Input label="Chave PIX" value={perfil.pix_chave}
                onChange={e => setPerfil(p => ({...p, pix_chave: e.target.value}))}
                placeholder={
                  perfil.pix_tipo === 'cpf'       ? '000.000.000-00' :
                  perfil.pix_tipo === 'cnpj'      ? '00.000.000/0001-00' :
                  perfil.pix_tipo === 'telefone'  ? '+55 11 99999-9999' :
                  perfil.pix_tipo === 'email'     ? 'seu@email.com' :
                  'Chave aleatória (UUID)'
                } />
              {perfil.pix_chave && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                  <span><Ico n="eye"/></span>
                  <p className="text-xs text-green-700">
                    O aluno verá: <strong>{pixTipos.find(t=>t.v===perfil.pix_tipo)?.l}</strong> — <strong>{perfil.pix_chave}</strong>
                  </p>
                </div>
              )}
            </div>
          </Card>

          {}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl"><Ico n="barbell" /></span>
              <h2 className="font-bold text-gray-700">Modalidades que você oferece</h2>
            </div>
            <p className="text-xs text-gray-400 mb-4">Selecione todas que se aplicam — aparecerá no cadastro de cada aluno</p>
            {(() => {
              const chip = m => {
                const ativo = perfil.modalidades.includes(m);
                return (
                  <button key={m} type="button" onClick={() => toggleMod(m)}
                    style={{padding:'7px 14px', borderRadius:'20px', border: ativo ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9',
                      background: ativo ? '#E7EDFF' : 'white', color: ativo ? '#2F6BFF' : '#6E7480',
                      fontSize:'13px', fontWeight: ativo ? '700' : '500', cursor:'pointer', transition:'all 0.15s'}}>
                    {m}
                  </button>
                );
              };
              // Aqui nao ha' busca: quem chega em Configuracoes ja' sabe o que procura e
              // esta' sentado, nao entre duas aulas. Mostro as duas listas abertas.
              return (
                <>
                  <div className="flex flex-wrap gap-2">{MODALIDADES_COMUNS.map(chip)}</div>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', margin:'16px 0 12px'}}>
                    <div style={{height:'1px', flex:1, background:'#E2E4E9'}} />
                    <span style={{fontSize:'11px', fontWeight:'700', color:'#9AA0AB', whiteSpace:'nowrap'}}>outras</span>
                    <div style={{height:'1px', flex:1, background:'#E2E4E9'}} />
                  </div>
                  <div className="flex flex-wrap gap-2">{MODALIDADES_OUTRAS.map(chip)}</div>
                </>
              );
            })()}
            {perfil.modalidades.length === 0 && (
              <p className="text-xs text-amber-600 mt-3"><Ico n="alert-triangle" c="#D97706" /> Selecione ao menos uma modalidade</p>
            )}
          </Card>

          {/* Regras da casa: perguntadas uma vez, reaproveitadas em todo contrato novo */}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl"><Ico n="note" /></span>
              <h2 className="font-bold text-gray-700">Regras padrão dos seus acordos</h2>
            </div>
            <p className="text-xs text-gray-400 mb-4">Todo acordo novo já nasce com estas regras — você pode mudar caso a caso na hora de criar</p>

            {/* v144: sem prazo combinado, desmarcar e' livre — este numero so' vale quando o professor escolhe "Sim — com prazo" */}
            <label className="text-xs font-bold text-gray-600 block mb-1">SE COMBINAR PRAZO PARA DESMARCAR, QUANTAS HORAS?</label>
            <p className="text-xs text-gray-400 mb-2">Sem prazo combinado, desmarcar é livre: avisou antes do horário da aula, não conta.</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[12,24,48].map(h => {
                const ativo = Number(perfil.padrao_antecedencia_horas) === h;
                return <button key={h} type="button" onClick={() => setPerfil(f => ({...f, padrao_antecedencia_horas: h}))}
                  style={{...{padding:'8px 16px', borderRadius:'20px', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
                    border: ativo?'2px solid #2F6BFF':'1.5px solid #E2E4E9', background: ativo?'#E7EDFF':'white', color: ativo?'#2F6BFF':'#6E7480'}}>{h}h</button>;
              })}
            </div>

            <label className="text-xs font-bold text-gray-600 block mb-2">CANCELANDO NO PRAZO, O ALUNO TEM REPOSIÇÃO?</label>
            <div className="flex gap-2 mb-4">
              {[['Sim', true], ['Não', false]].map(([lab, val]) => {
                const ativo = !!perfil.padrao_reposicao === val;
                return <button key={lab} type="button" onClick={() => setPerfil(f => ({...f, padrao_reposicao: val}))}
                  style={{...{padding:'8px 16px', borderRadius:'20px', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
                    border: ativo?'2px solid #2F6BFF':'1.5px solid #E2E4E9', background: ativo?'#E7EDFF':'white', color: ativo?'#2F6BFF':'#6E7480'}}>{lab}</button>;
              })}
            </div>

            <label className="text-xs font-bold text-gray-600 block mb-2">REAJUSTE ANUAL</label>
            <div className="flex flex-wrap gap-2">
              {[0,5,8,10,15].map(r => {
                const ativo = Number(perfil.padrao_reajuste_pct) === r;
                return <button key={r} type="button" onClick={() => setPerfil(f => ({...f, padrao_reajuste_pct: r}))}
                  style={{...{padding:'8px 16px', borderRadius:'20px', fontSize:'13px', fontWeight:'700', cursor:'pointer'},
                    border: ativo?'2px solid #2F6BFF':'1.5px solid #E2E4E9', background: ativo?'#E7EDFF':'white', color: ativo?'#2F6BFF':'#6E7480'}}>{r === 0 ? 'Sem reajuste' : r + '%'}</button>;
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">Ao completar 12 meses de acordo, o app te lembra de aplicar o reajuste.</p>
          </Card>

          <CardCustosMensais professor={professor} />

          {}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl"><Ico n="note" /></span>
              <h2 className="font-bold text-gray-700">Apresentação</h2>
            </div>
            <p className="text-xs text-gray-400 mb-4">Opcional — visível para seus alunos</p>
            <div className="flex flex-col gap-1">
              <textarea
                rows={4}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none"
                value={perfil.bio}
                onChange={e => setPerfil(p => ({...p, bio: e.target.value}))}
                placeholder="Experiência, metodologia, diferenciais..." />
              <span className="text-xs text-gray-400 text-right">{perfil.bio.length}/300</span>
            </div>
          </Card>

          {msgPerfil && (
            <div className={`rounded-xl p-3 text-sm font-medium ${msgPerfil.startsWith('') ? 'bg-green-50 text-green-700': 'bg-red-50 text-red-700'}`}>
              {msgPerfil}
            </div>
          )}
          <Button onClick={salvarPerfil} disabled={salvandoPerfil} className="justify-center">
 {salvandoPerfil ? 'Salvando...': 'Salvar perfil'}
          </Button>

          {/* Central de uso: chave de recusa (grava sozinha, nao depende do "Salvar perfil") */}
          <ChaveAjudarPlen />

          {/* v144: convidar outro professor (link de indicacao) */}
          <ConvidarProfessorSM origem="config" />

          <FalarComPlen quem="professor" nome={professor?.nome} origem="config" />

          {}
          <Card style={{borderColor:'#FECACA',background:'#FEF2F2'}}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl"><Ico n="alert-triangle" c="#D97706" /></span>
              <h2 style={{fontWeight:'800',color:'#DC2626',fontSize:'15px'}}>Zona de Perigo — LGPD</h2>
            </div>
            <p style={{fontSize:'12px',color:'#7F1D1D',marginBottom:'14px',lineHeight:'1.5'}}>
              Excluir sua conta apaga para sempre o seu login e todos os seus dados, inclusive os cadastros dos seus alunos com aulas, pagamentos e acordos. A conta de acesso de cada aluno continua existindo (é dele). Esta ação não pode ser desfeita.
            </p>
            {!confirmaExclusaoProf ? (
              <button onClick={() => { uso.acao('conta.excluir_pedir'); setConfirmaExclusaoProf(true); }}
                style={{width:'100%',padding:'11px',borderRadius:'10px',border:'1.5px solid #FECACA',background:'white',color:'#DC2626',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                <Ico n="trash"/> Excluir minha conta e todos os dados
              </button>
            ) : (
              <div>
                <p style={{fontSize:'13px',fontWeight:'700',color:'#DC2626',marginBottom:'10px'}}>Tem certeza? Isso é irreversível.</p>
                <div className="flex gap-3">
                  <button onClick={excluirContaProfessor} disabled={excluindoConta}
                    style={{flex:1,padding:'10px',borderRadius:'10px',border:'none',background:'#DC2626',color:'white',fontSize:'13px',fontWeight:'800',cursor:excluindoConta?'not-allowed':'pointer',opacity:excluindoConta?0.7:1}}>
 {excluindoConta ? 'Excluindo...': 'Sim, excluir tudo'}
                  </button>
                  <button onClick={() => setConfirmaExclusaoProf(false)}
                    style={{flex:1,padding:'10px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {}
      {tab === 'alertas' && (
        <>
          {/* v131: grava sozinha, nao depende do "Salvar Alertas" */}
          <div style={{marginBottom:'20px'}}><ChaveLembreteAula professor={professor} /></div>
          {/* v140 (Tema 3): os lembretes do proprio professor — tambem gravam sozinhos */}
          <div style={{marginBottom:'20px'}}><PreferenciasLembreteProfSM professor={professor} /></div>
          <Card className="mb-5">
            <h2 className="font-bold text-gray-700 mb-4">Alertas de crédito e renovação</h2>
            <div className="flex flex-col gap-4">
              <Input label="Avisar aluno quando restar X créditos" type="number" min="1" value={config.aviso_creditos_restantes} onChange={e => setConfig(c => ({...c, aviso_creditos_restantes: Number(e.target.value)}))} />
              <Input label="Avisar renovação mensal X dias antes" type="number" min="1" value={config.aviso_renovacao_mensal_dias} onChange={e => setConfig(c => ({...c, aviso_renovacao_mensal_dias: Number(e.target.value)}))} />
              <Input label="Avisar renovação anual X meses antes" type="number" min="1" value={config.aviso_renovacao_anual_meses} onChange={e => setConfig(c => ({...c, aviso_renovacao_anual_meses: Number(e.target.value)}))} />
            </div>
          </Card>
          <Button onClick={salvarAlertas} className="w-full justify-center">Salvar Alertas</Button>
        </>
      )}

      {}
      {tab === 'plataforma' && (
        <Card>
          <h2 className="font-bold text-gray-700 mb-2">Configurações da plataforma</h2>
          <p className="text-gray-400 text-sm">Em breve: fuso horário, idioma, integrações com WhatsApp e Google Calendar.</p>
        </Card>
      )}
    </div>
  );
}

// v134: mora dentro de Pendencias como "Historico de avisos" (embutido: sem titulo proprio).
// avisoResolvido(n): a pendencia do aviso ja' nao esta' aberta -> "✓ resolvido", sem botao de acao.
function Notificacoes({ professor, onNavigate, embutido = false, avisoResolvido = null }) {
  const [notifs, setNotifs] = React.useState([]);
  const [filtro, setFiltro] = React.useState('todos');
  const [novas, setNovas] = React.useState(new Set());   // ids que chegaram sem ler (destaque nesta visita)
  const [enviados, setEnviados] = React.useState(null);  // null = fechado; [] = o que foi mandado aos alunos

  React.useEffect(() => {
    // 01/10 (Fase 0, Etapa 1): mostra o que e' PARA o professor (coluna destinatario), inclusive
    // avisos sem aluno ligado (aulas sem confirmacao, resumo da semana, recompensa), e nao mais
    // o que ele mesmo mandou aos alunos. Ao abrir, marca como lido NO BANCO (antes o "lido" do
    // professor nunca era salvo). Banco sem a coluna nova: cai na consulta antiga.
    async function load() {
      let { data, error } = await supabase.from('notificacoes').select('*, alunos(id, nome)')
        .eq('professor_id', professor.id).eq('destinatario', 'professor')
        .order('created_at', { ascending: false }).limit(100);
      if (error) {
        const { data: ids } = await supabase.from('alunos').select('id').eq('professor_id', professor.id);
        if (!ids?.length) return;
        ({ data } = await supabase.from('notificacoes').select('*, alunos(id, nome)').in('aluno_id', ids.map(x => x.id)).order('created_at', { ascending: false }).limit(100));
        setNotifs(data || []);
        return;
      }
      // v152: pedido de renovacao (ajuste_contrato + ref contrato) so' fica "resolvido" quando o aluno ja' tem o
      // proximo pacote combinado ou o pacote do pedido ja' virou. Sem resposta do banco, fica como nao resolvido.
      const pedidosRen = (data || []).filter(n => n.tipo === 'ajuste_contrato' && n.ref_tipo === 'contrato' && n.ref_id && n.aluno_id);
      if (pedidosRen.length) {
        try {
          const [rRef, rProx] = await Promise.all([
            supabase.from('contratos').select('id, status').in('id', [...new Set(pedidosRen.map(n => n.ref_id))]),
            supabase.from('contratos').select('aluno_id').eq('professor_id', professor.id).eq('status', 'proximo')
              .in('aluno_id', [...new Set(pedidosRen.map(n => n.aluno_id))]),
          ]);
          if (!rRef.error && !rProx.error) {
            const virou = new Set((rRef.data || []).filter(c => c.status && c.status !== 'ativo').map(c => String(c.id)));
            const comProx = new Set((rProx.data || []).map(c => c.aluno_id));
            data = data.map(n => pedidosRen.includes(n) && (virou.has(String(n.ref_id)) || comProx.has(n.aluno_id)) ? { ...n, renovacao_resolvida: true } : n);
          }
        } catch {}
      }
      setNotifs(data || []);
      const naoLidas = (data || []).filter(n => !n.lida).map(n => n.id);
      setNovas(new Set(naoLidas));
      if (naoLidas.length) {
        await supabase.from('notificacoes').update({ lida: true }).in('id', naoLidas);
        window.dispatchEvent(new Event('plen:avisos-lidos'));
      }
    }
    load();
  }, [professor.id]);

  const tipos = {
    aula_cancelada:       { label: 'Aula Cancelada',   color: 'red',    emoji: 'circle-x', acao: false },
    credito_atualizado:   { label: 'Crédito',           color: 'green',  emoji: 'ticket', acao: false },
    renovacao:            { label: 'Renovação',         color: 'blue',   emoji: 'refresh', acao: false },
    plano_expirando:      { label: 'Plano Expirando',   color: 'yellow', emoji: 'hourglass', acao: true  },
    aceite_reajuste:      { label: 'Reajuste',          color: 'purple', emoji: 'trending-up', acao: false },
    contrato:             { label: 'Acordo',          color: 'indigo', emoji: 'file-text', acao: false },
    aula_corrigida:       { label: 'Aula corrigida',    color: 'blue',   emoji: 'pencil',        acao: false },
    cadastro_alterado:    { label: 'Cadastro',          color: 'blue',   emoji: 'pencil',        acao: false },
    reposicao_marcada:    { label: 'Reposição',         color: 'green',  emoji: 'calendar-plus', acao: false },
    recompensa:           { label: 'Recompensa',        color: 'green',  emoji: 'gift',          acao: false },
    remarcacao_solicitada:{ label: 'Reagendamento',     color: 'orange', emoji: 'refresh', acao: true  },
    reagendamento:        { label: 'Reagendamento',     color: 'orange', emoji: 'refresh', acao: true  },
    credito_baixo:        { label: 'Crédito Baixo',     color: 'red',    emoji: 'hourglass', acao: false },
    // o que o ALUNO manda ao professor (e os avisos automaticos do professor)
    cancelamento_aluno:       { label: 'Aula desmarcada',      color: 'red',    emoji: 'circle-x',       acao: false },
    cancelamento_solicitado:  { label: 'Aviso de falta',       color: 'orange', emoji: 'alert-triangle', acao: true, ir: 'aulas', botao: 'Ver a aula e decidir' },
    reagendamento_aceito:     { label: 'Remarcação aceita',    color: 'green',  emoji: 'check',          acao: false },
    // v136: voce remarcou e o aluno disse que nao consegue — o Inicio tem [Remarcar] [Desmarcar]
    remarcacao_recusada:      { label: 'Não consegue no novo horário', color: 'orange', emoji: 'calendar-x', acao: true, ir: 'dashboard', botao: 'Ver no Início' },
    aula_empurrada:           { label: 'Aula movida',          color: 'blue',   emoji: 'calendar',       acao: false },
    ajuste_contrato:          { label: 'Correção no acordo',   color: 'orange', emoji: 'pencil',         acao: true, ir: 'alunos', botao: 'Abrir alunos' },
    pausa_solicitada:         { label: 'Pedido de pausa',      color: 'orange', emoji: 'player-pause',   acao: true, ir: 'dashboard', botao: 'Ver no Início' },
    reajuste_aceito:          { label: 'Reajuste aceito',      color: 'green',  emoji: 'trending-up',    acao: false },
    reajuste_recusado:        { label: 'Reajuste recusado',    color: 'red',    emoji: 'trending-up',    acao: false },
    reajuste_refazer:         { label: 'Refazer reajuste',     color: 'orange', emoji: 'trending-up',    acao: true, ir: 'financeiro', botao: 'Propor de novo' },
    pagamento_confirmado:     { label: 'Pagamento confirmado', color: 'green',  emoji: 'circle-check',   acao: false },
    contrato_assinado:        { label: 'Acordo confirmado',    color: 'green',  emoji: 'file-check',     acao: false },
    pagamento_declarado_aluno:{ label: 'Pagamento informado',  color: 'green',  emoji: 'coin',           acao: true, ir: 'financeiro', botao: 'Conferir no Financeiro' },
    aula_proposta:            { label: 'Aula proposta',        color: 'orange', emoji: 'calendar-plus',  acao: true, ir: 'dashboard', botao: 'Responder no Início' },
    contestacao:              { label: 'Aula contestada',      color: 'red',    emoji: 'message-report', acao: true, ir: 'dashboard', botao: 'Ver no Início' },
    aula_confirmada_aluno:    { label: 'Aluno confirmou',      color: 'green',  emoji: 'check',          acao: true, ir: 'dashboard', botao: 'Fechar no Início' },
    falta_declarada_aluno:    { label: 'Aluno disse que não foi', color: 'orange', emoji: 'user-x',      acao: true, ir: 'dashboard', botao: 'Fechar no Início' },
    aula_divergencia:         { label: 'Registro diferente',   color: 'orange', emoji: 'arrows-diff',    acao: true, ir: 'dashboard', botao: 'Resolver no Início' },
    aula_fechada_prazo:       { label: 'Aula fechada',         color: 'gray',   emoji: 'lock',           acao: false },
    aulas_sem_confirmacao:    { label: 'Aulas sem confirmação',color: 'orange', emoji: 'clock-exclamation', acao: false, ir: 'dashboard', botao: 'Confirmar no Início' },
    resumo_semanal:           { label: 'Sua semana',           color: 'blue',   emoji: 'chart-bar',      acao: false },
    // v140: "o professor nao veio" (o aviso traz a aula: abre no Inicio, com "Nao houve" ja' aberto)
    aluno_esperando:          { label: 'Aluno esperando',      color: 'red',    emoji: 'user-exclamation', acao: true, ir: 'dashboard', botao: 'Responder' },
    prof_nao_veio:            { label: 'Aluno disse que você não veio', color: 'red', emoji: 'user-exclamation', acao: true, ir: 'dashboard', botao: 'Responder' },
    aula_devolvida:           { label: 'Aula fechada sem resposta', color: 'orange', emoji: 'arrow-back-up', acao: false, ir: 'alunos', botao: 'Ver em Alunos' },
    // v140 (Tema 3): lembretes do proprio professor (so' aviso)
    lembrete_aula_prof:       { label: 'Sua próxima aula',     color: 'blue',   emoji: 'alarm',          acao: false },
    agenda_resumo_prof:       { label: 'Sua agenda',           color: 'blue',   emoji: 'calendar',       acao: false },
    // v142: o aluno desfez o que tinha feito ("Bia desfez o aviso de que não iria à aula de …")
    acao_desfeita:            { label: 'Ação desfeita',        color: 'gray',   emoji: 'arrow-back-up',  acao: false },
    // v144: o aluno cancelou o pedido que tinha mandado / o acordo passou a "desmarcar livre" (so' aviso)
    pedido_cancelado:         { label: 'Pedido cancelado',  color: 'gray',   emoji: 'circle-x',       acao: false },
    acordo_desmarcar_livre:   { label: 'Acordo',            color: 'indigo', emoji: 'file-text',      acao: false },
    // v142 (revisao): entrou pelo link "Cadastrar varios de uma vez" — o "Completar" mora em Alunos
    aluno_entrou_link:        { label: 'Entrou pelo seu link', color: 'orange', emoji: 'user-plus',      acao: true, ir: 'alunos', botao: 'Completar em Alunos' },
    // v144: um professor entrou pelo seu convite (link de indicacao) — so' aviso
    prof_entrou_indicacao:    { label: 'Professor pelo seu convite', color: 'green', emoji: 'user-plus',   acao: false },
  };

  // Aviso diario do robo: so' o mais recente de cada tipo (a lista vem do mais novo ao mais velho).
  // v140: os lembretes do proprio professor (antes de cada aula / resumo da agenda) tambem — senao o
  // historico enche com um aviso por aula
  const ROBO_DIARIO = ['aulas_sem_confirmacao', 'resumo_semanal', 'lembrete_aula_prof', 'agenda_resumo_prof'];
  const jaTem = new Set();
  const lista = notifs.filter(n => !ROBO_DIARIO.includes(n.tipo) || (!jaTem.has(n.tipo) && jaTem.add(n.tipo)));
  // "Aguarda sua acao" so' no que chegou sem ler: o aviso nao sabe se o pedido ja' foi resolvido.
  // v134: com a lista de pendencias, o que ja' foi resolvido sai do "aguarda" e perde o botao.
  const desfeito = n => avisoDesfeitoSM(n, notifs);   // v142
  const resolvido = n => !desfeito(n) && !!(avisoResolvido && avisoResolvido(n));
  // dentro de Pendencias quem diz o que e' acao e' a "Sua vez": sem o filtro "Precisam de acao" e sem o
  // selo "Aguarda sua acao" (eram uma segunda conta, que contradizia o "Tudo em dia")
  // v152: 'ajuste_contrato' com ref contrato e' o pedido de renovacao do aluno (nao uma correcao no acordo)
  const pedeRenovar = n => n.tipo === 'ajuste_contrato' && n.ref_tipo === 'contrato' && !!n.aluno_id;
  const tipoDoAviso = n => pedeRenovar(n) ? { label: 'Quer renovar', color: 'blue', emoji: 'refresh', acao: true, ir: 'alunos', botao: 'Combinar próximo pacote' }
    : tipos[n.tipo] || { label: n.tipo, color: 'gray', emoji: 'bell', acao: false };
  const precisa = n => !embutido && !!tipoDoAviso(n).acao && novas.has(n.id) && !resolvido(n) && !desfeito(n);
  const naoPendentes = lista.filter(precisa).length;
  const visiveis = filtro === 'pendentes' ? lista.filter(precisa) : lista;
  async function verEnviados() {
    if (enviados) { setEnviados(null); return; }
    const { data } = await supabase.from('notificacoes').select('*, alunos(id, nome, user_id)')
      .eq('professor_id', professor.id).eq('destinatario', 'aluno').order('created_at', { ascending: false }).limit(60);
    setEnviados(data || []);
  }

  return (
    <div className={embutido ? '' : 'module-page'}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        {!embutido && (
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Notificações</h1>
            <p className="text-gray-500 mt-1">Avisos e solicitações dos alunos</p>
          </div>
        )}
        {!embutido && naoPendentes > 0 && (
          <div className="flex gap-2">
            <button onClick={() => { uso.acao('avisos.filtro', { filtro: 'todos' }); setFiltro('todos'); }} style={{ minHeight: '44px' }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filtro==='todos'?'bg-indigo-600 text-white':'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Todos</button>
            <button onClick={() => { uso.acao('avisos.filtro', { filtro: 'pendentes' }); setFiltro('pendentes'); }} style={{ minHeight: '44px' }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filtro==='pendentes'?'bg-orange-500 text-white':'bg-orange-50 text-orange-600 hover:bg-orange-100'}`}>Precisam de ação ({naoPendentes})</button>
          </div>
        )}
      </div>
      <div className={embutido ? 'mt-2' : 'mt-6'}>
      {visiveis.length === 0 ? (
        embutido ? <p className="text-sm text-gray-500" style={{ margin: '4px 2px 10px' }}>Nenhum aviso por enquanto.</p>
        : <Card className="text-center py-12"><div className="text-4xl mb-3"><Ico n="bell" /></div><h3 className="font-bold text-gray-700">Nenhuma notificação</h3></Card>
      ) : visiveis.map(n => {
        const t = tipoDoAviso(n);
        const precisaAcao = precisa(n);
        const foiDesfeito = desfeito(n);
        const feito = resolvido(n) || foiDesfeito;   // sem botao de acao nos dois casos
        return (
          <div key={n.id} className={`flex items-start gap-4 bg-white border rounded-2xl p-4 mb-3 shadow-sm ${precisaAcao ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100'}`}>
            <div className="flex-shrink-0 mt-0.5"><Ico n={t.emoji} s={22} /></div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                {novas.has(n.id) && <span title="Nova" style={{width:'8px', height:'8px', borderRadius:'50%', background:'#2F6BFF', display:'inline-block'}} />}
                <Badge color={t.color}>{t.label}</Badge>
                <span className="font-semibold text-gray-700 text-sm">{n.alunos?.nome}</span>
                {precisaAcao && <Badge color="orange">Aguarda sua ação</Badge>}
                {foiDesfeito ? <Badge color="gray"><Ico n="arrow-back-up" s={12} /> desfeito</Badge>
                  : feito ? <Badge color="green"><Ico n="check" s={12} /> resolvido</Badge> : null}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{n.mensagem}</p>
              <p className="text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleString('pt-BR')}</p>
              {!feito && (n.tipo === 'remarcacao_solicitada' || n.tipo === 'reagendamento') && (
                <button onClick={() => { uso.acao('avisos.abrir', { destino: 'agenda', tipo: n.tipo }); onNavigate && onNavigate('agenda'); }}
                  className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                  <Ico n="calendar" /> Abrir Calendário para reagendar
                </button>
              )}
              {!feito && n.tipo === 'plano_expirando' && (
                <button onClick={() => { uso.acao('avisos.abrir', { destino: 'alunos', tipo: n.tipo }); onNavigate && onNavigate('alunos'); }}
                  className="mt-3 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                  <Ico n="refresh" /> Renovar plano
                </button>
              )}
              {!feito && t.ir && t.botao && (
                <button onClick={() => { uso.acao('avisos.abrir', { destino: t.ir, tipo: pedeRenovar(n) ? 'pedido_renovacao' : n.tipo }); onNavigate && onNavigate(t.ir, n.tipo === 'reajuste_refazer' && n.aluno_id ? { reajuste_aluno_id: n.aluno_id }
                  : pedeRenovar(n) ? { renovar_aluno_id: n.aluno_id, renovar_ja_pagou: renovarJaPagouSM(n), t: Date.now() }
                  : n.tipo === 'cancelamento_solicitado' && n.ref_tipo === 'aula' && n.ref_id ? { aula_id: n.ref_id }
                  : (n.tipo === 'aluno_esperando' || n.tipo === 'prof_nao_veio') && n.ref_tipo === 'aula' && n.ref_id ? { aula_id: n.ref_id, abrir_aula: true, t: Date.now() }
                  : undefined); }}
                  className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                  {t.botao}
                </button>
              )}
            </div>
          </div>
        );
      })}
      <div style={{textAlign:'center', marginTop:'8px'}}>
        <button onClick={() => { if (!enviados) uso.acao('avisos.ver_enviados'); verEnviados(); }} style={{border:'none', background:'none', color:'#2F6BFF', fontWeight:'700', fontSize:'13px', cursor:'pointer', minHeight:'44px', padding:'12px 8px', fontFamily:'inherit'}}>
          {enviados ? 'Esconder o que foi enviado aos alunos' : 'Ver o que foi enviado aos alunos'}
        </button>
      </div>
      {enviados && (
        <div style={{marginTop:'8px'}}>
          {enviados.length === 0 && <p className="text-sm text-gray-400 text-center">Nada enviado ainda.</p>}
          {enviados.map(n => (
            <div key={n.id} className="bg-gray-50 border border-gray-100 rounded-2xl p-3 mb-2">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-semibold text-gray-700 text-sm">Para {n.alunos?.nome || 'aluno'}</span>
                {n.alunos && !n.alunos.user_id && <Badge color="gray">ainda não usa o app</Badge>}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{n.mensagem}</p>
              <p className="text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleString('pt-BR')}</p>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}

function ContratosModule({ professor }) {
  const [contratos, setContratos] = React.useState([]);
  const [filtroStatus, setFiltroStatus] = React.useState('');
  const [filtroVinculo, setFiltroVinculo] = React.useState('');
  const [verHistorico, setVerHistorico] = React.useState(false);
  const [modalVer, setModalVer] = React.useState(null);
  const [modalEnviar, setModalEnviar] = React.useState(null);
  const [enviando, setEnviando] = React.useState(false);
  React.useEffect(() => { if (modalVer) uso.janela('acordo_ver'); }, [!!modalVer]);
  React.useEffect(() => { if (modalEnviar) uso.janela('acordo_enviar'); }, [!!modalEnviar]);

  React.useEffect(() => { loadContratos(); }, []);

  async function loadContratos() {
    const { data } = await supabase.from('contratos')
      .select('*, alunos(id, nome, email, telefone, modalidade, tipo_vinculo), planos(nome, duracao_meses, aulas_por_semana)')
      .eq('professor_id', professor.id)
      .order('created_at', { ascending: false });
    setContratos(data || []);
  }

  async function marcarAceito(contratoId) {
    await supabase.from('contratos').update({ status_aceite: 'aceito', data_aceite: new Date().toISOString() }).eq('id', contratoId);
    uso.acao('acordo.marcar_aceito');
    loadContratos();
    setModalEnviar(null);
    toastSM('Acordo marcado como confirmado manualmente.');
  }

  async function enviarNotificacao(contrato) {
    setEnviando(true);
    await supabase.from('notificacoes').insert({
      aluno_id: contrato.aluno_id, professor_id: professor.id, tipo: 'contrato',
      mensagem: `Seu acordo (${contrato.planos?.nome || 'plano'}) está pronto para você conferir e confirmar no app.`
    });
    await supabase.from('contratos').update({ status_aceite: 'pendente' }).eq('id', contrato.id);
    uso.acao('acordo.enviar');
    setEnviando(false);
    toastSM('Notificação enviada ao aluno!');
    loadContratos();
    setModalEnviar(null);
  }

  function gerarTextoContrato(c) {
    const fmt = d => d ? new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—';
    const plano = c.planos || {};
    const aluno = c.alunos || {};
    const duracaoMeses = plano.duracao_meses || 1;
    const tipoPlano = duracaoMeses >= 12 ? 'anual' : duracaoMeses >= 6 ? 'semestral' : 'mensal';
    const multaPct = Number(c.multa_cancelamento_pct || 0);
    const multaClausula = tipoPlano !== 'mensal' && multaPct > 0
      ? `\n5. MULTA POR CANCELAMENTO ANTECIPADO\n   Em caso de cancelamento antes do término do plano ${tipoPlano}, será devida multa equivalente a ${multaPct}% (${multaPct === 10 ? 'dez' : multaPct === 5 ? 'cinco' : multaPct === 20 ? 'vinte' : String(multaPct)}) por cento do valor restante do contrato, conforme legislação vigente (CDC).\n`
      : tipoPlano !== 'mensal' ? `\n5. MULTA POR CANCELAMENTO ANTECIPADO\n   Não há multa por cancelamento antecipado prevista neste acordo.\n` : '';
    return `CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE PERSONAL TRAINER


PARTES:
  Professor: ${professor.nome}
  Aluno:     ${aluno.nome || '—'}
  Contato:   ${aluno.telefone || aluno.email || '—'}

OBJETO:
  Prestação de serviços de personal trainer na modalidade ${aluno.modalidade || 'Não definida'}, conforme plano contratado.

1. PLANO CONTRATADO
   Modalidade: ${plano.nome || '—'}
   Duração:    ${duracaoMeses} mês(es)
   Frequência: ${plano.aulas_por_semana || 2}x por semana
   Período:    ${fmt(c.data_inicio)} a ${fmt(c.data_fim)}
   Valor do pacote: R$ ${Number(c.preco_negociado || 0).toFixed(2).replace('.',',')}${c.taxa_academia > 0 ? `\n   Taxa academia por aula: R$ ${Number(c.taxa_academia).toFixed(2).replace('.',',')}` : ''}

2. REGRAS DE CANCELAMENTO DE AULA
${c.cancelamento_livre === true ? `   Não há prazo mínimo combinado: o aviso dado antes do horário da aula está no prazo.
   Aulas canceladas depois do horário da aula não serão repostas.` : `   O cancelamento de aulas deve ser comunicado com antecedência mínima conforme combinado.
   Aulas canceladas fora do prazo não serão repostas.`}
${c.reposicao_permitida !== false ? `
3. REPOSIÇÃO DE AULAS
   É permitida a reposição de até ${c.limite_reposicao || 1} aula(s) por plano, mediante aviso ${c.cancelamento_livre === true ? 'antes do horário da aula' : `prévio de ${c.antecedencia_cancelamento_horas || 24} horas`}.
` : `
3. REPOSIÇÃO DE AULAS
   Não está prevista reposição de aulas neste contrato.
`}
4. RENOVAÇÃO
   O contrato se renova automaticamente ao final do período, salvo comunicação prévia de ${duracaoMeses > 1 ? '30' : '7'} dias.
${multaClausula}
6. ACEITE
   Ao aceitar este contrato, o aluno concorda com todos os termos acima.

   Status atual: ${c.status_aceite === 'aceito'? 'ACEITO': c.status_aceite === 'recusado'? 'RECUSADO': 'AGUARDANDO ACEITE'}
   ${c.data_aceite ? `Data do aceite: ${new Date(c.data_aceite).toLocaleDateString('pt-BR')}` : ''}


Gerado pelo Plen em ${new Date().toLocaleDateString('pt-BR')}`;
  }

  const filtrados = contratos.filter(c => {
    if (!verHistorico && c.status !== 'ativo') return false;
    const modoC = c.modo || 'interno';
    if (filtroStatus) {
      if (modoC === 'interno') return false;
      if ((c.status_aceite || 'pendente') !== filtroStatus) return false;
    }
    if (filtroVinculo && modoC !== filtroVinculo) return false;
    return true;
  });

  const totalFormais = contratos.filter(c => (c.modo || 'interno') === 'formal').length;
  const totalAceitos = contratos.filter(c => c.status_aceite === 'aceito').length;
  const totalPendentes = contratos.filter(c => (c.modo || 'interno') === 'formal' && c.status_aceite !== 'aceito' && c.status === 'ativo').length;

  const statusAceite = (c) => {
    const modo = c.modo || 'interno';
    if (modo === 'interno') return { label: 'Interno', color: '#6E7480', bg: '#F0F1F4'};
    const s = c.status_aceite || 'pendente';
    if (s === 'aceito') return { label: 'Confirmado', color: '#0A6F4D', bg: '#DCF5EC'};
    if (s === 'recusado') return { label: 'Recusado', color: '#991B1B', bg: '#FEE2E2'};
    return { label: 'Aguard. confirmação', color: '#92400E', bg: '#FEF3C7'};
  };

  const fmtDate = d => d ? new Date(d+'T12:00:00').toLocaleDateString('pt-BR',{day:'2-digit',month:'short',year:'numeric'}) : '—';
  const duracaoLabel = m => m >= 12 ? '12 meses' : m >= 6 ? '6 meses' : '1 mês';

  return (
    <div className="module-page" style={{maxWidth:"1000px",margin:"0 auto"}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'24px'}}>
        <div>
          <h1 style={{fontSize:'24px',fontWeight:'800',color:'#16181D',margin:0}}>Acordos</h1>
          <p style={{fontSize:'14px',color:'#6E7480',marginTop:'4px'}}>Acordos e confirmações dos alunos</p>
        </div>
      </div>

      {}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'12px',marginBottom:'20px',overflowX:'auto'}}>
        {[
 { label: 'Acordos ativos', value: contratos.filter(c=>c.status==='ativo').length, icon: 'file-text', color: '#2F6BFF', bg: '#E7EDFF'},
 { label: 'Acordos formais', value: totalFormais, icon: 'writing-sign', color: '#2F6BFF', bg: '#E7EDFF'},
 { label: 'Aguard. confirmação', value: totalPendentes, icon: 'hourglass', color: '#D97706', bg: '#FEF3C7'},
        ].map((s,i) => (
          <div key={i} style={{background:'white',border:'1px solid #E2E4E9',borderRadius:'16px',padding:'18px 20px',display:'flex',alignItems:'center',gap:'14px'}}>
            <div style={{width:'44px',height:'44px',borderRadius:'12px',background:s.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Ico n={s.icon} s={20} /></div>
            <div>
              <div style={{fontSize:'26px',fontWeight:'800',color:s.color,lineHeight:1}}>{s.value}</div>
              <div style={{fontSize:'12px',color:'#6E7480',marginTop:'3px'}}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {}
      <div style={{display:'flex',gap:'8px',marginBottom:'12px',flexWrap:'wrap',alignItems:'center'}}>
        {!verHistorico && [
 {v:'',l:'Todos'},{v:'pendente',l:'Aguard. confirmação'},{v:'aceito',l:'Confirmados'},{v:'recusado',l:'Recusados'}
        ].map(f=>(
          <button key={f.v} onClick={()=>{ uso.acao('acordos.filtro', { status: f.v || 'todos' }); setFiltroStatus(f.v); }}
            style={{padding:'7px 14px',borderRadius:'20px',border:filtroStatus===f.v?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:filtroStatus===f.v?'#E7EDFF':'white',color:filtroStatus===f.v?'#2F6BFF':'#6E7480',fontSize:'12px',fontWeight:'600',cursor:'pointer'}}>
            {f.l}
          </button>
        ))}
        <div style={{marginLeft:'auto',display:'flex',gap:'8px',alignItems:'center'}}>
 {!verHistorico && [{v:'',l:'Todos'},{v:'formal',l:'Formais'},{v:'interno',l:'Internos'}].map(f=>(
            <button key={f.v} onClick={()=>{ uso.acao('acordos.filtro', { vinculo: f.v || 'todos' }); setFiltroVinculo(f.v); }}
              style={{padding:'7px 14px',borderRadius:'20px',border:filtroVinculo===f.v?'2px solid #2F6BFF':'1.5px solid #E2E4E9',background:filtroVinculo===f.v?'#E7EDFF':'white',color:filtroVinculo===f.v?'#2F6BFF':'#6E7480',fontSize:'12px',fontWeight:'600',cursor:'pointer'}}>
              {f.l}
            </button>
          ))}
          <button onClick={()=>{ if (!verHistorico) uso.acao('acordos.historico'); setVerHistorico(h=>!h); }}
            style={{padding:'7px 14px',borderRadius:'20px',border:'1.5px solid #E2E4E9',background:verHistorico?'#FEF3C7':'white',color:verHistorico?'#92400E':'#6E7480',fontSize:'12px',fontWeight:'600',cursor:'pointer'}}>
 {verHistorico ? 'Ocultar histórico': 'Ver histórico'}
          </button>
        </div>
      </div>
      {!verHistorico && <div style={{fontSize:'11px',color:'#9AA0AB',marginBottom:'16px'}}>Mostrando apenas acordos em vigor. Clique em "Ver histórico" para ver os anteriores.</div>}

      {}
      {filtrados.length === 0 ? (
        <div style={{background:'white',border:'1px solid #E2E4E9',borderRadius:'16px',padding:'48px',textAlign:'center'}}>
          <div style={{fontSize:'40px',marginBottom:'12px'}}><Ico n="file-text" /></div>
          <div style={{fontSize:'16px',fontWeight:'700',color:'#3D424C'}}>Nenhum acordo encontrado</div>
          <div style={{fontSize:'13px',color:'#9AA0AB',marginTop:'6px'}}>Crie um plano para um aluno para gerar um acordo automaticamente.</div>
        </div>
      ) : filtrados.map(c => {
        const st = statusAceite(c);
        return (
          <div key={c.id} style={{background:'white',border:'1px solid #E2E4E9',borderRadius:'16px',padding:'18px 20px',marginBottom:'10px',display:'flex',alignItems:'center',gap:'16px'}}>
            <div style={{width:'44px',height:'44px',borderRadius:'12px',background:(c.modo||'interno')==='formal'?'#E7EDFF':'#F0F1F4',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'20px',flexShrink:0}}>
 {(c.modo||'interno') === 'formal'? '': ''}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'4px'}}>
                <span style={{fontSize:'15px',fontWeight:'700',color:'#16181D'}}>{c.alunos?.nome || '—'}</span>
                <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'20px',background:st.bg,color:st.color}}>{st.label}</span>
                {c.status === 'ativo' && <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'20px',background:'#DCF5EC',color:'#0A6F4D'}}>Ativo</span>}
                {c.status === 'encerrado' && <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'20px',background:'#F0F1F4',color:'#6E7480'}}>Encerrado</span>}
              </div>
              <div style={{fontSize:'12px',color:'#6E7480',display:'flex',gap:'16px',flexWrap:'wrap'}}>
                <span> {c.planos?.nome || '—'} · {duracaoLabel(c.planos?.duracao_meses)}</span>
                <span><Ico n="coin" /> R$ {Number(c.preco_negociado || 0).toFixed(0)}/pacote</span>
                <span><Ico n="calendar" /> {fmtDate(c.data_inicio)} <Ico n="arrow-right" /> {fmtDate(c.data_fim)}</span>
                {c.alunos?.modalidade && <span><Ico n="run" /> {c.alunos.modalidade}</span>}
              </div>
              {c.data_aceite && <div style={{fontSize:'11px',color:'#0E9F6E',marginTop:'3px'}}>Aceito em {new Date(c.data_aceite).toLocaleDateString('pt-BR')}</div>}
            </div>
            <div style={{display:'flex',gap:'8px',flexShrink:0}}>
              <button onClick={()=>setModalVer(c)}
                style={{padding:'8px 14px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#3D424C',fontSize:'12px',fontWeight:'600',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}>
                <Ico n="eye" /> Ver
              </button>
              {(c.modo||'interno') === 'formal' && c.status_aceite !== 'aceito' && (
                <button onClick={()=>setModalEnviar(c)}
                  style={{padding:'8px 14px',borderRadius:'10px',border:'none',background:'#2F6BFF',color:'white',fontSize:'12px',fontWeight:'600',cursor:'pointer',display:'flex',alignItems:'center',gap:'5px'}}>
                  <Ico n="send" /> Reenviar
                </button>
              )}
            </div>
          </div>
        );
      })}

      {}
      {modalVer && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setModalVer(null)}>
          <div style={{background:'white',borderRadius:'20px',boxShadow:'0 24px 80px rgba(0,0,0,0.2)',width:'100%',maxWidth:'640px',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'20px 24px',borderBottom:'1px solid #E2E4E9',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:'17px',fontWeight:'800',color:'#16181D'}}><Ico n="file-text" /> Contrato — {modalVer.alunos?.nome}</div>
                <div style={{fontSize:'12px',color:'#6E7480',marginTop:'2px'}}>{modalVer.planos?.nome} · {fmtDate(modalVer.data_inicio)} <Ico n="arrow-right" /> {fmtDate(modalVer.data_fim)}</div>
              </div>
              <button onClick={()=>setModalVer(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#9AA0AB',fontSize:'22px',lineHeight:1}}><Ico n="x" /></button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'24px'}}>
              <pre style={{fontFamily:'monospace',fontSize:'12px',lineHeight:'1.7',color:'#3D424C',whiteSpace:'pre-wrap',background:'#F7F8FA',border:'1px solid #E2E4E9',borderRadius:'12px',padding:'20px',margin:0}}>
                {gerarTextoContrato(modalVer)}
              </pre>
            </div>
            <div style={{padding:'16px 24px',borderTop:'1px solid #E2E4E9',display:'flex',gap:'10px',justifyContent:'flex-end'}}>
              {(modalVer.modo||'interno') === 'formal' && modalVer.status_aceite !== 'aceito' && (
                <button onClick={()=>marcarAceito(modalVer.id)}
                  style={{padding:'10px 18px',borderRadius:'10px',border:'none',background:'#0E9F6E',color:'white',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="circle-check" c="#0E9F6E" /> Marcar como confirmado manualmente
                </button>
              )}
              <button onClick={()=>setModalVer(null)}
                style={{padding:'10px 18px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#3D424C',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}>
                Fechar
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {}
      {modalEnviar && (
        <Portal>
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.6)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setModalEnviar(null)}>
          <div style={{background:'white',borderRadius:'20px',boxShadow:'0 24px 80px rgba(0,0,0,0.2)',width:'100%',maxWidth:'440px',overflow:'hidden'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'24px'}}>
              <div style={{fontSize:'18px',fontWeight:'800',color:'#16181D',marginBottom:'8px'}}><Ico n="send" /> Enviar acordo</div>
              <div style={{fontSize:'13px',color:'#6E7480',marginBottom:'20px'}}>
                Envie uma notificação para <strong>{modalEnviar.alunos?.nome}</strong> solicitando que revise e aceite os termos do contrato na plataforma.
              </div>
              <div style={{background:'#E7EDFF',border:'1px solid #C2D4FF',borderRadius:'12px',padding:'14px',fontSize:'12px',color:'#1C43AE',marginBottom:'20px'}}>
                <Ico n="bulb" c="#D97706" /> Quando o módulo do aluno estiver ativo, ele poderá ler e aceitar diretamente pelo app. Por ora, você pode marcar o aceite manualmente após confirmação pessoal ou por WhatsApp.
              </div>
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={()=>enviarNotificacao(modalEnviar)} disabled={enviando}
                  style={{flex:1,padding:'11px',borderRadius:'10px',border:'none',background:'#2F6BFF',color:'white',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
 {enviando ? 'Enviando...': 'Enviar notificação'}
                </button>
                <button onClick={()=>marcarAceito(modalEnviar.id)}
                  style={{flex:1,padding:'11px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#0E9F6E',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="circle-check" c="#0E9F6E" /> Aceite manual
                </button>
              </div>
              <button onClick={()=>setModalEnviar(null)} style={{width:'100%',marginTop:'8px',padding:'10px',borderRadius:'10px',border:'1px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',cursor:'pointer'}}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

function Relatorios({ professor }) {
  return (
    <div style={{padding:'24px 20px',maxWidth:'600px',margin:'0 auto'}}>
      <div style={{marginBottom:'24px'}}>
        <h2 style={{fontSize:'22px',fontWeight:'700',color:'#1F2229',margin:'0 0 4px'}}>Relatórios</h2>
        <p style={{fontSize:'14px',color:'#6E7480',margin:0}}>Extraia dados e acompanhe a evolução do seu negócio.</p>
      </div>
      <div style={{background:'#F7F8FA',border:'2px dashed #C7CAD1',borderRadius:'16px',padding:'48px 24px',textAlign:'center'}}>
        <div style={{fontSize:'40px',marginBottom:'12px'}}><Ico n="chart-bar" /></div>
        <div style={{fontSize:'16px',fontWeight:'600',color:'#3D424C',marginBottom:'8px'}}>Em breve</div>
        <div style={{fontSize:'13px',color:'#9AA0AB',lineHeight:'1.6'}}>
          Aqui você poderá gerar relatórios de presença,<br/>
          financeiros, desempenho por aluno e muito mais.
        </div>
      </div>
    </div>
  );
}



/* ===== bloco 2 ===== */


// ---------------------------------------------------------------------------
// Primeiro acesso do professor.
//
// O que NAO esta' aqui e' tao importante quanto o que esta': nada de prazo de
// cancelamento, reajuste, plano ou chave Pix. Quem acabou de chegar nao parou para
// pensar nisso, e perguntar cedo trava a pessoa numa decisao que ela nao veio tomar.
// Sao tres telas, cada uma devolvendo algo — e a ultima deixa sair sem fazer nada.
// ---------------------------------------------------------------------------
function OnboardingProfessor({ professor, onProfessorAtualizado, onPronto }) {
  const [passo, setPasso] = React.useState(1);
  // v143: entrou pelo convite de um aluno que disse a modalidade ("Ele dá aula de quê?"): ja'
  // abre com ela marcada. "balé" vira o "Balé" da lista; o que nao esta' na lista entra como
  // modalidade propria. O professor pode desmarcar.
  const [mods, setMods] = React.useState(() => {
    const m = parseMods(professor.modalidades); if (m.length) return m;
    try {
      const x = String(JSON.parse(localStorage.getItem('propostaDoAluno') || 'null')?.modalidade || '').trim().slice(0, 40);
      if (!x) return [];
      const daLista = MODALIDADES_PROF.find(o => semAcentoSM(o) === semAcentoSM(x));
      return [daLista || x.charAt(0).toUpperCase() + x.slice(1)];
    } catch { return []; }
  });
  const [busca, setBusca] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [permAviso, setPermAviso] = React.useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'indisponivel');
  // v147: no app da loja o passo 2 voltou (aviso nativo). A permissao vem do aparelho, nao do navegador.
  const avisoNativo = APP_NATIVO && pushNativoDisponivel();
  React.useEffect(() => {
    if (!avisoNativo) return;
    pushNativo('checkPermissions').then(p => setPermAviso(p?.receive === 'granted' ? 'granted' : p?.receive === 'denied' ? 'denied' : 'default')).catch(() => {});
  }, []);
  // Central de uso: funil das boas-vindas (passos 1..3) e o pedido de aviso (push_ativar)
  const fOnb = useFluxoUso('onboarding_prof', passo, true);
  const [pedindoAviso, setPedindoAviso] = React.useState(false);
  const fPushOnb = useFluxoUso('push_ativar', 1, pedindoAviso);

  // Instalado na tela de inicio? No iPhone o aviso pelo navegador SO' funciona nesse modo — entao,
  // em vez de pedir uma permissao que o sistema vai negar, mando para o app da App Store (v141;
  // antes ensinava "Adicionar a Tela de Inicio").
  const instalado = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  // Entrou porque um aluno o convidou: ele ja' tem alguem na lista, entao "primeiro aluno"
  // soaria errado — o convite aqui e' para trazer os outros.
  const veioDeAlunoLS = (() => { try { return !!localStorage.getItem('propostaDoAluno'); } catch { return false; } })();
  // Fechamento v144 (revisao, achado 2): o pedido aceito tambem vem do BANCO — o professor que aceitou no navegador e
  // entrou pelo app da loja (ou outro aparelho) nao tem nada guardado aqui. Havendo pedido, o passo 3 abre DIRETO nele.
  const [pedidoBanco, setPedidoBanco] = React.useState(null);
  React.useEffect(() => {
    if (veioDeAlunoLS) return;
    supabase.rpc('professor_pedidos_a_completar').then(({ data, error }) => {
      const pd = !error && Array.isArray(data) && data[0] ? data[0] : null;
      if (!pd) return;
      setPedidoBanco(pd);
      uso.acao('onboarding.veio_de_aluno', { origem: 'banco' });
      const x = String((pd.proposta && pd.proposta.modalidade) || '').trim().slice(0, 40);
      if (x) setMods(prev => prev.length ? prev : [MODALIDADES_PROF.find(o => semAcentoSM(o) === semAcentoSM(x)) || x.charAt(0).toUpperCase() + x.slice(1)]);
    }, () => {});
  }, []);
  const veioDeAluno = veioDeAlunoLS || !!pedidoBanco;
  // v144: o passo 3 dizia "Você já entrou com um aluno na lista" — e o aluno ainda NAO estava (teste de 21/10)
  const alunoDoPedido = (() => { try { const n = String((pedidoBanco ? pedidoBanco.aluno_nome : JSON.parse(localStorage.getItem('propostaDoAluno') || 'null')?.aluno_nome) || '').trim();
    return n.includes('@') ? 'seu aluno' : primeiroNomeSM(n, 'seu aluno'); } catch { return 'seu aluno'; } })();
  const ehIOS = IOS_NAVEGADOR;   // inclui o iPad com iPadOS 13+ (que se apresenta como Mac)
  React.useEffect(() => { if (veioDeAlunoLS) uso.acao('onboarding.veio_de_aluno'); }, []);
  React.useEffect(() => { if (passo === 2 && ehIOS && !instalado) fOnb.barrar('ios_sem_tela_inicio'); }, [passo]);

  function toggle(m) {
    setMods(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  }

  async function salvarMods() {
    if (!mods.length) return;
    setSalvando(true);
    const { error: eMods } = await supabase.from('professores').update({ modalidades: JSON.stringify(mods) }).eq('id', professor.id);
    if (eMods) uso.erro('erro.onboarding', eMods.message);
    onProfessorAtualizado?.({ modalidades: JSON.stringify(mods) });
    setSalvando(false);
    // No app da loja o passo 2 (avisos pelo navegador, "coloque na tela de inicio") nao se
    // aplica — pula direto. Volta quando o push nativo estiver ligado.
    // v147 (Iago): o push nativo esta' ligado — o passo 2 volta no app (pedido pelo plugin).
    setPasso(APP_NATIVO && !avisoNativo ? 3 : 2);
  }

  async function pedirAviso() {
    if (avisoNativo) {
      setPedindoAviso(true);
      const r = await registrarPushNativo(professor?.user_id, { pedir: true });
      const res = resultadoPush(r);
      fPushOnb.fim(res, { via: 'nativo', origem: 'onboarding' });
      if (res === 'erro') uso.erro('erro.push', r && r.motivo);
      setPermAviso(r.ok ? 'granted' : r.motivo === 'denied' ? 'denied' : 'default');
      if (!r.ok && r.motivo !== 'denied') toastSM('Não deu para ativar agora. Dá para tentar de novo no Início.');
      setPedindoAviso(false);
      return;
    }
    if (typeof Notification === 'undefined') { pushEstadoUmaVez('sem_suporte'); setPasso(3); return; }
    setPedindoAviso(true);
    try {
      const r = await Notification.requestPermission();
      setPermAviso(r);
      fPushOnb.fim(resultadoPush({ ok: r === 'granted', motivo: r }), { via: 'web', origem: 'onboarding' });
      if (r === 'granted') inscreverPush(professor?.user_id).then(x => { if (!x || !x.ok) uso.erro('erro.push', x && x.motivo); });
    } catch (e) { fPushOnb.fim('erro', { via: 'web', origem: 'onboarding' }); setPermAviso('denied'); }
    setPedindoAviso(false);
  }

  async function terminar(irParaAluno) {
    setSalvando(true);
    const { error: eFim } = await supabase.from('professores').update({ onboarding_em: new Date().toISOString() }).eq('id', professor.id);
    if (eFim) uso.erro('erro.onboarding', eFim.message);
    onProfessorAtualizado?.({ onboarding_em: new Date().toISOString() });
    setSalvando(false);
    fOnb.fim(irParaAluno ? 'concluiu' : 'pulou');   // antes do onPronto, que desmonta esta tela
    onPronto?.({ irParaAluno, pedido: irParaAluno && !veioDeAlunoLS && pedidoBanco ? pedidoBanco.code : null });
  }

  // Ja' marcada uma das "outras" (ex.: Balé vindo do aluno): abre a lista inteira para ela aparecer.
  const [verTodas, setVerTodas] = React.useState(() => mods.some(m => MODALIDADES_OUTRAS.includes(m)));
  const semAcento = t => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const q = semAcento(busca.trim());
  // Busca sem acento: quem digita "muay tai" ou "danca" tem de achar do mesmo jeito.
  const achados = q ? MODALIDADES_PROF.filter(m => semAcento(m).includes(q)) : null;

  const caixa = {
    minHeight:'100vh', background:'linear-gradient(150deg,#16181D,#1C43AE,#2455D6)',
    display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
  };
  const cartao = {
    background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,.35)',
    padding:'32px 26px', width:'100%', maxWidth:'480px', maxHeight:'92vh', overflowY:'auto',
  };
  const btnPrim = {
    width:'100%', background:'#2F6BFF', color:'white', border:'none', borderRadius:'14px',
    padding:'16px', fontSize:'16px', fontWeight:'800', cursor:'pointer', marginTop:'18px',
  };
  const btnSec = {
    width:'100%', background:'white', color:'#6E7480', border:'1.5px solid #E2E4E9',
    borderRadius:'14px', padding:'14px', fontSize:'14px', fontWeight:'700', cursor:'pointer', marginTop:'10px',
  };

  return (
    <div style={caixa}>
      <div style={cartao}>
        {}
        <div style={{display:'flex', gap:'6px', marginBottom:'22px'}}>
          {[1,2,3].map(n => (
            <div key={n} style={{height:'4px', flex:1, borderRadius:'2px', background: n <= passo ? '#2F6BFF' : '#E2E4E9'}} />
          ))}
        </div>

        {passo === 1 && (
          <>
            <h1 style={{fontFamily:"'Sora',sans-serif", fontSize:'23px', fontWeight:'700', color:'#16181D', marginBottom:'6px'}}>
              Você dá aula de quê, {professor.nome?.split(' ')[0]}?
            </h1>
            <p style={{fontSize:'14px', color:'#6E7480', marginBottom:'16px', lineHeight:1.5}}>
              Pode marcar mais de uma. Serve para o app falar a sua língua no resto das telas.
            </p>
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar ou digitar a sua (ex.: balé)"
              style={{width:'100%', padding:'12px 14px', borderRadius:'12px', border:'1.5px solid #E2E4E9',
                      fontSize:'15px', outline:'none', boxSizing:'border-box', marginBottom:'16px'}} />
            {(() => {
              const chip = m => {
                const on = mods.includes(m);
                return (
                  <button key={m} onClick={() => toggle(m)}
                    style={{padding:'8px 14px', borderRadius:'20px', cursor:'pointer', fontSize:'13px',
                            border: on ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9',
                            background: on ? '#E7EDFF' : 'white',
                            color: on ? '#2F6BFF' : '#6E7480', fontWeight: on ? '700' : '500'}}>{m}</button>
                );
              };
              // Modalidade que nao esta' na lista (balé, vôlei de praia...): o professor usa o que digitou
              // (Marcus, 20/10: "se ele fosse professor de balé, não ia aparecer — e não tem como digitar").
              const proprias = mods.filter(m => !MODALIDADES_PROF.includes(m));
              if (achados) {
                const termo = busca.trim().replace(/\s+/g, ' ').slice(0, 40);
                const exato = MODALIDADES_PROF.concat(mods).some(m => semAcento(m) === q);
                const usar = !exato && termo.length >= 2 ? (
                  <button key="__usar" type="button"
                    onClick={() => { const v = termo.charAt(0).toUpperCase() + termo.slice(1); setMods(prev => prev.includes(v) ? prev : [...prev, v]); setBusca(''); uso.acao('onboarding.modalidade_propria'); }}
                    style={{padding:'8px 14px', minHeight:'40px', borderRadius:'20px', cursor:'pointer', fontSize:'13px', border:'1.5px dashed #2F6BFF',
                            background:'white', color:'#2F6BFF', fontWeight:'700'}}>+ Usar “{termo}”</button>
                ) : null;
                return (achados.length || usar)
                  ? <div style={{display:'flex', flexWrap:'wrap', gap:'7px'}}>{achados.map(chip)}{usar}</div>
                  : <p style={{fontSize:'13px', color:'#9AA0AB'}}>Digite pelo menos 2 letras.</p>;
              }
              return (
                <>
                  {proprias.length > 0 && <div style={{display:'flex', flexWrap:'wrap', gap:'7px', marginBottom:'7px'}}>{proprias.map(chip)}</div>}
                  <div style={{display:'flex', flexWrap:'wrap', gap:'7px'}}>{MODALIDADES_COMUNS.map(chip)}</div>
                  <div style={{display:'flex', alignItems:'center', gap:'10px', margin:'18px 0 12px'}}>
                    <div style={{height:'1px', flex:1, background:'#E2E4E9'}} />
                    <span style={{fontSize:'11px', fontWeight:'700', color:'#9AA0AB', whiteSpace:'nowrap'}}>
                      {MODALIDADES_OUTRAS.length} outras
                    </span>
                    <div style={{height:'1px', flex:1, background:'#E2E4E9'}} />
                  </div>
                  {verTodas ? (
                    <div style={{display:'flex', flexWrap:'wrap', gap:'7px'}}>{MODALIDADES_OUTRAS.map(chip)}</div>
                  ) : (
                    <button onClick={() => setVerTodas(true)}
                      style={{width:'100%', background:'white', color:'#2F6BFF', border:'1.5px solid #C2D4FF',
                              borderRadius:'12px', padding:'12px', fontSize:'14px', fontWeight:'700', cursor:'pointer'}}>
                      Ver todas as modalidades
                    </button>
                  )}
                </>
              );
            })()}
            <button onClick={salvarMods} disabled={!mods.length || salvando}
              style={{...btnPrim, opacity: mods.length ? 1 : .45, cursor: mods.length ? 'pointer' : 'not-allowed'}}>
              {salvando ? 'Salvando…' : mods.length ? `Continuar com ${mods.length}` : 'Escolha ao menos uma'}
            </button>
          </>
        )}

        {passo === 2 && (
          <>
            <h1 style={{fontFamily:"'Sora',sans-serif", fontSize:'23px', fontWeight:'700', color:'#16181D', marginBottom:'6px'}}>
              Gostaria de ativar as notificações?
            </h1>
            <p style={{fontSize:'14px', color:'#6E7480', marginBottom:'18px', lineHeight:1.55}}>
              O Plen te avisa quando um aluno pede para remarcar, quando alguém paga, e no
              fim do dia se sobrou aula sem confirmar. Só isso — nada de propaganda.
            </p>

            {ehIOS && !instalado ? (
              <div style={{background:'#E7EDFF', border:'1px solid #C2D4FF', borderRadius:'14px', padding:'16px'}}>
                <p style={{fontSize:'14px', fontWeight:'700', color:'#1C43AE', marginBottom:'8px'}}>
                  No iPhone, os avisos chegam pelo app do Plen
                </p>
                <p style={{fontSize:'13px', color:'#1C43AE', lineHeight:1.6, marginBottom:'12px'}}>
                  Baixe o Plen na App Store e entre com a mesma conta.
                </p>
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" onClick={() => uso.acao('app_store.abrir', { origem: 'onboarding' })}
                  style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', minHeight:'44px', padding:'11px 16px', borderRadius:'12px', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', textDecoration:'none'}}>
                  <Ico n={ICONE_APP_STORE} s={16} c="white" /> Baixar na App Store
                </a>
              </div>
            ) : permAviso === 'granted' ? (
              <div style={{background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'14px', padding:'16px', color:'#0A6F4D', fontSize:'14px', fontWeight:'700'}}>
                <Ico n="circle-check" s={15} /> Pronto. Os avisos vão chegar neste aparelho.
              </div>
            ) : permAviso === 'denied' ? (
              <div style={{background:'#F4F5F7', border:'1px solid #E2E4E9', borderRadius:'14px', padding:'16px', color:'#3D424C', fontSize:'13px', lineHeight:1.6}}>
                {avisoNativo
                  ? <>Os avisos estão desligados neste celular. Para ligar depois: <b>Ajustes</b> → <b>Plen</b> → <b>Notificações</b>. O Plen continua funcionando sem eles.</>
                  : <>Os avisos estão bloqueados neste aparelho. Dá para liberar depois nos ajustes
                    do navegador — e o Plen continua funcionando sem eles.</>}
              </div>
            ) : (
              <>
                <button onClick={pedirAviso} disabled={pedindoAviso} style={{...btnPrim, marginTop:0, opacity: pedindoAviso ? 0.7 : 1}}>
                  <Ico n="bell" s={16} c="white" /> {avisoNativo ? (pedindoAviso ? 'Aguarde…' : 'Ativar avisos') : 'Ativar notificações'}
                </button>
                {avisoNativo && <div style={{fontSize:'12.5px', color:'#6E7480', textAlign:'center', marginTop:'8px'}}>O iPhone vai perguntar: toque em “Permitir” (ou “Allow”).</div>}
              </>
            )}

            <button onClick={() => { if (avisoNativo && permAviso !== 'granted') adiarAvisoSM(); setPasso(3); }} style={btnSec}>
              {permAviso === 'granted' ? 'Continuar' : 'Deixar para depois'}
            </button>
          </>
        )}

        {passo === 3 && (
          <>
            <h1 style={{fontFamily:"'Sora',sans-serif", fontSize:'23px', fontWeight:'700', color:'#16181D', marginBottom:'6px'}}>
              {veioDeAluno ? `Agora, o combinado de ${alunoDoPedido}` : 'Vamos cadastrar o seu primeiro aluno?'}
            </h1>
            <p style={{fontSize:'14px', color:'#6E7480', marginBottom:'18px', lineHeight:1.55}}>
              {veioDeAluno
                ? <>{alunoDoPedido} já mandou os dias, o pacote e o pagamento. Abre preenchido: você confere,
                    ajusta o que precisar e cria — <strong>{alunoDoPedido} vê no app na hora</strong>, sem
                    mensagem de volta.</>
                : <>São seis perguntas curtas, uma por tela. No fim, <strong>todas as aulas dele já
                    entram na sua agenda sozinhas</strong> — você não vai precisar marcar nenhuma.
                    Leva menos de um minuto.</>}
            </p>
            <div style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'14px', padding:'14px 16px', fontSize:'13px', color:'#1C43AE', lineHeight:1.6}}>
              {veioDeAluno
                ? 'Depois, os outros alunos você cadastra em "Novo aluno" — leva menos de um minuto cada.'
                : 'Pode ser um aluno de verdade. Se não gostar, dá para apagar depois.'}
            </div>
            <button onClick={() => terminar(true)} disabled={salvando} style={btnPrim}>
              {salvando ? 'Abrindo…' : veioDeAluno ? 'Conferir o combinado' : 'Vamos lá'}
            </button>
            <button onClick={() => terminar(false)} disabled={salvando} style={btnSec}>
              {veioDeAluno ? 'Depois — fica guardado em Alunos' : 'Agora não, quero só dar uma olhada'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ProfessorApp({ user, professor }) {
  useTemaTela(false);
  const { setPerfil, setUser, setProfessor, conviteData, setConviteData } = useApp();
  const [tela, setTela] = React.useState('dashboard');
  // Trocar de aba abre a tela nova do topo (antes herdava a rolagem da aba anterior).
  React.useEffect(() => { try { window.scrollTo(0, 0); const m = document.querySelector('.app-main'); if (m) m.scrollTop = 0; } catch (_) {} }, [tela]);
  const [telaParams, setTelaParams] = React.useState({});
  const [pulouOnboarding, setPulouOnboarding] = React.useState(false);
  const [filtroFinanceiroAluno, setFiltroFinanceiroAluno] = React.useState('');

  async function logout() { await sairDaConta(); setUser(null); setPerfil(null); setProfessor(null); }

  function navegar(novaTela, params = {}) {
    setTela(novaTela);
    setTelaParams(params);
  }

  function irParaFinanceiro(alunoId) {
    setFiltroFinanceiroAluno(alunoId || '');
    navegar('financeiro');
  }

  React.useEffect(() => { if (!telaParams?.aluno_id) return; }, [tela]);

  React.useEffect(() => { if (tela !== 'financeiro') setFiltroFinanceiroAluno(''); }, [tela]);

  // Central de uso: tela atual. Financeiro e Configuracoes emitem o proprio codigo (com a aba).
  // v134: a aba 'notificacoes' agora e' Pendencias (a chave ficou por causa dos links antigos).
  const mostrandoOnboarding = !!(professor && !professor.onboarding_em && !pulouOnboarding);
  React.useEffect(() => {
    if (mostrandoOnboarding) uso.tela('prof.onboarding');
    else if (tela !== 'financeiro' && tela !== 'configuracoes') uso.tela(tela === 'notificacoes' ? 'prof.pendencias' : 'prof.' + tela);
  }, [tela, mostrandoOnboarding]);

  // v134: Pendencias — uma lista so' (do banco) para a tela e para o numero da aba (barra e menu).
  const pend = usePendenciasSM(!!professor?.id && !mostrandoOnboarding);
  const numPend = numeroPendSM(pend, 'professor');
  useNumeroIconeSM(pend, 'professor', !!professor?.id && !mostrandoOnboarding);   // v148: o mesmo numero no icone do app

  // v140: tocou no push de uma aula ("Seu aluno está esperando", "Aluno disse que você não veio", "Sua
  // próxima aula") — o App guardou "/?aula=<id>" em pendingAula (antes so' o app do aluno lia). Abre o
  // Inicio nessa aula (o Dashboard rola ate' ela e, se for "nao veio", ja' abre o "Nao houve"). Link
  // com mais de 12h e' ignorado. Uma vez por abertura.
  const pendAulaProfFeita = React.useRef(false);
  React.useEffect(() => {
    if (!professor?.id || mostrandoOnboarding || pendAulaProfFeita.current) return;
    let p = null;
    try { p = JSON.parse(localStorage.getItem('pendingAula') || 'null'); } catch {}
    if (!p || !p.aula) return;
    pendAulaProfFeita.current = true;
    try { localStorage.removeItem('pendingAula'); } catch {}
    if (Date.now() - Number(p.em || 0) > 12 * 3600 * 1000 || !UUID_RE.test(String(p.aula))) return;
    uso.acao('prof.aviso_aula_abrir', { lembrete: !!p.l });
    navegar('dashboard', { aula_id: p.aula, abrir_aula: true, t: Date.now() });
  }, [professor?.id, mostrandoOnboarding]);

  // Seguranca convites (01/10): professor que abre o link de um ALUNO ja' logado, ou que criou a
  // conta por e-mail na tela comum, tambem aceita o pedido. Antes so' quem entrava pelo Google
  // (TipoUsuario) aceitava — nos outros casos o pedido ficava esquecido e o aluno nunca recebia
  // o aviso do professor. Com proposta, o cadastro abre ja' preenchido na tela Alunos (no
  // primeiro acesso, o fim do onboarding ja' leva para la').
  React.useEffect(() => {
    if (conviteData?.tipo !== 'aluno_prof' || !conviteData._code) return;
    const cd = conviteData;
    (async () => {
      const r = await aceitarPedidoDoAluno(cd);
      if (r.ok) uso.acao('convite.pedido_aceito', { tem_proposta: !!r.temProposta }); else uso.bloqueio('pedido_prof', null, null, 'convite_usado_ou_vencido');
      if (!r.ok) toastSM('Esse convite já foi usado ou venceu. Peça ao aluno para mandar de novo.');
      else if (r.temProposta && (professor?.onboarding_em || pulouOnboarding)) navegar('alunos', { proposta: Date.now() });
      setConviteData(null);
    })();
  }, [conviteData?._code]);

  // v144: entrou pelo link de OUTRO professor (indicacao). O banco grava quem indicou — so' conta nova (24h), uma
  // vez, nunca a si mesmo — e avisa quem indicou. Professor antigo que toca no link: nada muda. O codigo guardado
  // so' sai do aparelho quando o banco responde (sem internet, tenta de novo na proxima abertura).
  React.useEffect(() => {
    if (conviteData?.tipo !== 'prof_prof' || !conviteData._code || !professor?.id) return;
    const cd = conviteData;
    (async () => {
      let r = null, err = null;
      try { const res = await supabase.rpc('professor_entrou_por_indicacao', { p_code: cd._code }); r = res.data; err = res.error; } catch (e) { err = e; }
      if (err) uso.erro('erro.indicacao', err.message || err);
      // Fecho v147 (revisao, achado 2): cada resposta diz o que aconteceu — antes so' o "proprio link" falava, e quem
      // colava o link de indicacao voltava ao mesmo Inicio sem saber se tinha dado certo.
      if (r && r.registrado) {
        uso.acao('indicacao.entrou');
        toastSM(`Pronto: ${primeiroNomeSM(r.indicador_nome || cd.pnome, 'quem te convidou')} fica sabendo que você entrou pelo convite.`);
      }
      else if (r && r.proprio) { uso.acao('indicacao.proprio_link'); toastSM('Esse é o seu link de convite. Mande para outros professores.'); }
      else if (r && r.ja_feito) { uso.acao('indicacao.ja_feito'); toastSM('Esse convite já está anotado na sua conta.'); }
      else if (r && r.conta_antiga) { uso.acao('indicacao.ja_professor'); toastSM('Você já tinha conta de professor no Plen: o convite não muda nada.'); }
      else if (r && r.erro) uso.bloqueio('indicacao', null, null, r.erro);
      if (r || (err && (err.code === 'PGRST202' || /professor_entrou_por_indicacao/.test(String(err.message || ''))))) { try { localStorage.removeItem('pendingInviteCode'); } catch {} }
      setConviteData(null);
    })();
  }, [conviteData?._code, professor?.id]);

  const telas = {
    dashboard: <Dashboard professor={professor} onNavigate={navegar} params={telaParams} />,
    planos: <Planos professor={professor} />,
    alunos: <Alunos professor={professor} onIrParaFinanceiro={irParaFinanceiro} onIrParaTurmas={() => navegar('turmas')} params={telaParams} />,
    turmas: <Turmas professor={professor} onNavigate={navegar} params={telaParams} />,
    contratos: <ContratosModule professor={professor} />,
    agenda: <Agenda professor={professor} />,
    aulas: <Aulas professor={professor} params={telaParams} />,
    financeiro: <Financeiro professor={professor} filtroInicialAluno={filtroFinanceiroAluno} params={telaParams} />,
    relatorios: <Relatorios professor={professor} />,
    notificacoes: <PendenciasProfessor professor={professor} onNavigate={(k, p) => navegar(k, p)} pend={pend} />,
    configuracoes: <Configuracoes professor={professor} />,
  };

  // Primeiro acesso: tres telas curtas antes do painel. Nao e' tutorial — e' o minimo
  // que o app precisa saber de quem chegou, feito de um jeito que devolve algo a cada passo.
  if (professor && !professor.onboarding_em && !pulouOnboarding) {
    return (
      <OnboardingProfessor
        professor={professor}
        onProfessorAtualizado={p => setProfessor(prev => ({ ...prev, ...p }))}
        onPronto={({ irParaAluno, pedido }) => {
          setPulouOnboarding(true);
          // fechamento v144: veio de um pedido guardado no banco — Alunos abre direto nele (preenchido)
          if (irParaAluno && pedido) navegar('alunos', { pedido, t: Date.now() });
          else setTela(irParaAluno ? 'alunos' : 'dashboard');
        }}
      />
    );
  }

  return (
    <div className="flex min-h-screen" style={{position:'relative'}}>
      <Sidebar ativa={tela} setAtiva={k => navegar(k)} professor={professor} onLogout={logout} pendencias={numPend} />
      <main className="app-main flex-1 overflow-y-auto bg-gray-50">
        <MobileTopBarProf professor={professor} onLogout={logout} onPerfil={() => navegar('configuracoes')} />
        {telas[tela]}
        {/* Atalho "+" do celular (Marcus, 29/09): cadastrar sem ter que ir ate Alunos. Fica
            DENTRO do painel para as janelas (que tambem vivem aqui) cobrirem o botao. Trocar
            de aba pelo menu passa por navegar() para limpar o pedido do atalho. */}
        <AtalhoNovo onNovoAluno={() => navegar('alunos', { novo: Date.now() })} onNovaTurma={() => navegar('turmas', { nova: Date.now() })} />
      </main>
      <div className="notif-bell-desktop"><NotificationBell professor={professor} onNavigate={navegar} pendencias={numPend} pendCarregando={!pend || pend.estado === 'carregando'} /></div>
      <BottomNavProf ativa={tela} setAtiva={k => navegar(k)} professorId={professor?.id} pendencias={numPend} />
    </div>
  );
}

// Botao redondo "+" acima do menu de baixo (so' celular, ver .plen-fab no index). Um toque
// abre duas opcoes; a janela do cadastro abre por cima da tela em que a pessoa estava.
function AtalhoNovo({ onNovoAluno, onNovaTurma }) {
  const [aberto, setAberto] = React.useState(false);
  const opc = (icone, texto, acao) => (
    <button onClick={() => { uso.acao(icone === 'user-plus' ? 'atalho.novo_aluno' : 'atalho.nova_turma'); setAberto(false); acao(); }}
      style={{display:'flex', alignItems:'center', gap:'12px', width:'100%', padding:'14px 16px', border:'none', background:'white', borderRadius:'14px', fontWeight:'700', fontSize:'15px', color:'#16181D', cursor:'pointer', textAlign:'left'}}>
      <span style={{width:'36px', height:'36px', borderRadius:'10px', background:'#E7EDFF', color:'#2F6BFF', display:'flex', alignItems:'center', justifyContent:'center'}}><Ico n={icone} s={18} /></span>{texto}
    </button>
  );
  return (
    <>
      <button className="plen-fab" aria-label="Adicionar" onClick={() => { uso.janela('atalho'); setAberto(true); }}><Ico n="plus" s={26} c="white" /></button>
      {aberto && (
        <Portal>
        <div className="plen-modal-wrap fixed inset-0 z-50 flex items-end p-4" style={{background:'rgba(22,24,29,.45)'}} onClick={() => setAberto(false)}>
          <div className="plen-modal" style={{background:'#F7F8FA', width:'100%', padding:'8px 12px calc(16px + env(safe-area-inset-bottom))', display:'grid', gap:'8px'}} onClick={e => e.stopPropagation()}>
            <div style={{fontSize:'11px', fontWeight:'800', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.06em', padding:'10px 6px 2px'}}>Adicionar</div>
            {opc('user-plus', 'Novo aluno', onNovoAluno)}
            {opc('users', 'Nova turma de treino', onNovaTurma)}
          </div>
        </div>
        </Portal>
      )}
    </>
  );
}

const EMOJIS_PERFIL = ['','','','','','','','','','','','','','','','','','','',''];

function ModalEditarPerfilAluno({ aluno, userId, onClose, onSalvo, onExcluirConta }) {
  const avatarKey = `avatar_${userId}`;
  const [emoji, setEmoji] = React.useState(() => localStorage.getItem(avatarKey) || '');
  const [nome, setNome] = React.useState(aluno?.nome || '');
  const [telefone, setTelefone] = React.useState(aluno?.telefone || '');
  const [salvando, setSalvando] = React.useState(false);
  const [confirmaExclusao, setConfirmaExclusao] = React.useState(false);
  React.useEffect(() => { uso.janela('aluno.perfil'); }, []);

  async function salvar() {
    if (!nome.trim()) return;
    setSalvando(true);
    uso.acao('aluno.perfil_salvar', { tem_tel: !!telefone.trim() });
    localStorage.setItem(avatarKey, emoji);
    // "Meu perfil" e' o cadastro do ALUNO — grava em perfis, que e' dele. A ficha que
    // cada professor mantem (alunos.nome) e' do professor: pode ser um apelido, e nao
    // muda quando o aluno edita o proprio nome. O telefone, sim, atualiza em todas as
    // fichas dele — e' contato, e o professor precisa do numero certo.
    await supabase.from('perfis').update({ nome: nome.trim() }).eq('id', userId);
    if (telefone.trim()) {
      await supabase.from('alunos').update({ telefone: telefone.trim() }).eq('user_id', userId);
    }
    setSalvando(false);
    onSalvo({ nome: nome.trim(), telefone: telefone.trim(), emoji });
    onClose();
  }

  return (
    <Portal>
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,display:'flex',alignItems:'flex-end',justifyContent:'center'}} onClick={onClose}>
      <div style={{position:'relative',background:'white',borderRadius:'24px 24px 0 0',width:'100%',maxWidth:'480px',padding:'24px 20px',
                   maxHeight:'82vh',overflowY:'auto',WebkitOverflowScrolling:'touch',
                   paddingBottom:'calc(96px + env(safe-area-inset-bottom))',
                   boxShadow:'0 -20px 60px rgba(0,0,0,0.2)'}} onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} aria-label="Fechar"
          style={{position:'absolute',top:'12px',right:'14px',width:'32px',height:'32px',borderRadius:'50%',
                  border:'none',background:'#F0F1F4',color:'#6E7480',fontSize:'17px',cursor:'pointer',lineHeight:1}}><Ico n="x" /></button>
        <div style={{width:'40px',height:'4px',background:'#E2E4E9',borderRadius:'2px',margin:'0 auto 20px'}}/>
        <h2 style={{fontSize:'17px',fontWeight:'800',color:'#16181D',marginBottom:'16px',textAlign:'center'}}>Editar Perfil</h2>

        {/* A grade de avatares saiu: os emojis foram removidos da marca e ficaram 20 quadrados vazios. O avatar e' a inicial no circulo azul. */}
        {}
        <p style={{fontSize:'12px',fontWeight:'600',color:'#9AA0AB',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>Nome</p>
        <input value={nome} onChange={e=>setNome(e.target.value)}
          style={{width:'100%',border:'1.5px solid #E2E4E9',borderRadius:'12px',padding:'12px 14px',fontSize:'15px',fontWeight:'600',color:'#16181D',outline:'none',boxSizing:'border-box',marginBottom:'14px'}} />

        {}
        <p style={{fontSize:'12px',fontWeight:'600',color:'#9AA0AB',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'6px'}}>Telefone</p>
        <input value={telefone} onChange={e=>setTelefone(mascaraTel(e.target.value))} type="tel"
          style={{width:'100%',border:'1.5px solid #E2E4E9',borderRadius:'12px',padding:'12px 14px',fontSize:'15px',color:'#16181D',outline:'none',boxSizing:'border-box',marginBottom:'20px'}} />

        <button onClick={salvar} disabled={salvando||!nome.trim()}
          style={{width:'100%',background:'linear-gradient(135deg,#2F6BFF,#2455D6)',color:'white',border:'none',borderRadius:'14px',padding:'14px',fontSize:'15px',fontWeight:'800',cursor:salvando||!nome.trim()?'not-allowed':'pointer',opacity:salvando||!nome.trim()?0.6:1}}>
 {salvando ? 'Salvando...': 'Salvar'}
        </button>

        {/* Central de uso: chave de recusa — grava sozinha (o Salvar acima exige o nome) */}
        <div style={{marginTop:'18px'}}><ChaveAjudarPlen /></div>
        <div style={{marginTop:'12px'}}><FalarComPlen quem="aluno" nome={nome} origem="perfil_aluno" /></div>
        <div style={{marginTop:'12px'}}><TrocarSenhaSM quem="aluno" /></div>

        {}
        <div style={{marginTop:'24px',paddingTop:'20px',borderTop:'1px solid #FEE2E2'}}>
          <p style={{fontSize:'11px',fontWeight:'700',color:'#9AA0AB',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'10px'}}>Zona de perigo — LGPD</p>
          {!confirmaExclusao ? (
            <button onClick={() => { uso.acao('aluno.excluir_conta_abrir'); setConfirmaExclusao(true); }}
              style={{width:'100%',padding:'11px',borderRadius:'12px',border:'1.5px solid #FECACA',background:'#FEF2F2',color:'#DC2626',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
              <Ico n="trash"/> Excluir minha conta e dados
            </button>
          ) : (
            <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:'12px',padding:'14px'}}>
              <p style={{fontSize:'13px',color:'#DC2626',fontWeight:'700',marginBottom:'8px'}}><Ico n="alert-triangle" c="#D97706" /> Tem certeza? Esta ação é irreversível.</p>
              <p style={{fontSize:'12px',color:'#7F1D1D',marginBottom:'12px',lineHeight:'1.5'}}>Todos os seus dados serão permanentemente removidos: perfil, aulas, pagamentos e acordos.</p>
              <div style={{display:'flex',gap:'8px'}}>
                <button onClick={onExcluirConta}
                  style={{flex:1,padding:'10px',borderRadius:'10px',border:'none',background:'#DC2626',color:'white',fontSize:'13px',fontWeight:'800',cursor:'pointer'}}>
                  Sim, excluir tudo
                </button>
                <button onClick={() => setConfirmaExclusao(false)}
                  style={{flex:1,padding:'10px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}

function MobileTopBarAluno({ aluno, userId, onLogout, onEditarPerfil }) {
  return (
    <div className="mobile-top-bar">
      <button onClick={onEditarPerfil}
        style={{display:'flex', alignItems:'center', gap:'10px', background:'none', border:'none', cursor:'pointer', padding:0}}>
        <div style={{width:'36px', height:'36px', background:'#2F6BFF', borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'15px', fontWeight:'700', color:'white', flexShrink:0}}>
          {(aluno?.nome || 'A').trim().charAt(0).toUpperCase()}
        </div>
        <div style={{textAlign:'left'}}>
          <div style={{fontSize:'11px', color:'#9AA0AB', fontWeight:'600', letterSpacing:'0.05em'}}>MEU PERFIL <Ico n="pencil" /></div>
          <div style={{fontSize:'14px', fontWeight:'700', color:'#16181D', lineHeight:'1.2'}}>{aluno?.nome?.split(' ')[0] || 'Aluno'}</div>
        </div>
      </button>
      <button onClick={onLogout} aria-label="Sair da conta"
        style={{display:'flex', alignItems:'center', gap:'6px', padding:'8px 12px', borderRadius:'12px', background:'#F0F1F4', border:'none', color:'#6E7480', fontSize:'12px', fontWeight:'700', cursor:'pointer'}}>
        <Icons.LogOut /> Sair
      </button>
    </div>
  );
}

function BottomNavAluno({ ativa, setAtiva, notifCount = 0 }) {
  const tabs = [
    { key:'inicio',       label:'Início',      ic:'home' },
    { key:'aulas',        label:'Aulas',       ic:'calendar' },
    { key:'plano',        label:'Meu Plano',   ic:'ticket' },
    { key:'financeiro',   label:'Financeiro',  ic:'wallet' },
    { key:'notificacoes', label:'Pendências',  ic:'checklist', badge: notifCount },   // v134: numero = "Sua vez"
  ];
  return (
    <div className="app-bottom-nav">
      {tabs.map(t => {
        const ativo = ativa === t.key;
        return (
          <button key={t.key} className="bnav-btn" onClick={() => setAtiva(t.key)}>
            <span className="bnav-icon">
              <Ico n={t.ic} s={21} c={ativo ? '#6E9BFF' : 'rgba(255,255,255,0.62)'} />
              {t.badge > 0 && <span className="bnav-badge">{t.badge}</span>}
            </span>
            <span className="bnav-label" style={{color: ativo ? '#6E9BFF' : 'rgba(255,255,255,0.65)'}}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function SidebarAluno({ ativa, setAtiva, aluno, onLogout, notifCount, onEditarPerfil }) {
  const menus = [
    { key:'inicio',        label:'Início',        icon:<Icons.Home /> },
    { key:'aulas',         label:'Minhas Aulas',  icon:<Icons.Calendar /> },
    { key:'plano',         label:'Meu Plano',     icon:<Icons.Package /> },
    { key:'financeiro',    label:'Financeiro',    icon:<Icons.DollarSign /> },
    { key:'contrato',      label:'Meu acordo',      icon:<Icons.FileText /> },
    { key:'notificacoes',  label:'Pendências',    icon:<Ico n="checklist" s={20} />, badge: notifCount || 0 },
  ];
  const iniciais = aluno?.nome ? aluno.nome.split(' ').slice(0,2).map(n=>n[0]).join('').toUpperCase() : '?';
  return (
    <div className="app-sidebar" style={{width:'232px', background:'#16181D', minHeight:'100vh', flexDirection:'column', flexShrink:0}}>
      <div style={{padding:'22px 18px 18px', borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
          <div style={{maxWidth:'34px',width:'100%', height:'34px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'17px', flexShrink:0, boxShadow:'0 4px 10px rgba(99,102,241,0.4)'}}><Ico n="trophy" /></div>
          <img src="/logo-branco.svg" alt="Plen" style={{height:'22px', display:'block'}} />
        </div>
      </div>
      <nav style={{flex:1, padding:'14px 10px', display:'flex', flexDirection:'column', gap:'1px'}}>
        {menus.map(m => {
          const isAtiva = ativa === m.key;
          return (
            <button key={m.key} onClick={() => setAtiva(m.key)}
              style={{width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'10px', border:'none', cursor:'pointer', textAlign:'left', fontSize:'13px', fontWeight: isAtiva ? '700' : '500', background: isAtiva ? 'rgba(99,102,241,0.18)' : 'transparent', color: isAtiva ? '#6E9BFF' : 'rgba(255,255,255,0.48)', transition:'all 0.12s'}}
              onMouseOver={e => { if(!isAtiva){e.currentTarget.style.background='rgba(255,255,255,0.05)';e.currentTarget.style.color='rgba(255,255,255,0.75)';}}}
              onMouseOut={e => { if(!isAtiva){e.currentTarget.style.background='transparent';e.currentTarget.style.color='rgba(255,255,255,0.48)';}}} >
              <span style={{color:isAtiva?'#6E9BFF':'rgba(255,255,255,0.32)',flexShrink:0,display:'flex'}}>{m.icon}</span>
              <span style={{flex:1}}>{m.label}</span>
              {m.badge > 0 && <span style={{background:'#EF4444',color:'white',borderRadius:'10px',fontSize:'10px',fontWeight:'800',padding:'1px 6px',minWidth:'18px',textAlign:'center'}}>{m.badge}</span>}
            </button>
          );
        })}
      </nav>
      <div style={{padding:'10px', borderTop:'1px solid rgba(255,255,255,0.06)'}}>
        {/* No computador a barra de cima some: o cartao do nome e' o caminho para "Meu perfil"
            (e para a chave "Ajudar a melhorar o Plen"). */}
        <button type="button" onClick={() => onEditarPerfil && onEditarPerfil()} style={{width:'100%', display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'10px', background:'rgba(255,255,255,0.04)', marginBottom:'4px', border:'none', cursor: onEditarPerfil ? 'pointer' : 'default', textAlign:'left'}}>
          <div style={{width:'30px', height:'30px', borderRadius:'8px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'800', color:'white', flexShrink:0}}>{iniciais}</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:'12px', fontWeight:'700', color:'rgba(255,255,255,0.85)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{aluno?.nome?.split(' ')[0]}</div>
            <div style={{fontSize:'10px', color:'rgba(255,255,255,0.32)'}}>{onEditarPerfil ? 'Meu perfil ›' : 'Aluno'}</div>
          </div>
        </button>
        <button onClick={onLogout}
          style={{width:'100%', display:'flex', alignItems:'center', gap:'8px', padding:'9px 12px', borderRadius:'8px', border:'none', background:'transparent', cursor:'pointer', fontSize:'12px', color:'rgba(255,255,255,0.32)', transition:'all 0.12s'}}
          onMouseOver={e=>{e.currentTarget.style.background='rgba(239,68,68,0.1)';e.currentTarget.style.color='#EF4444';}}
          onMouseOut={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='rgba(255,255,255,0.32)';}}>
          <Icons.LogOut /> Sair da conta
        </button>
      </div>
    </div>
  );
}

/// ── ALUNO SEM PROFESSOR: CADASTRAR O PROFESSOR (ou so' gerar o link) ─────────
// (v140: o ConviteProfInline, que so' gerava link sem o combinado, saiu — todas as portas do
// aluno abrem o assistente ModalAdicionarProfessor.)
function TelaSemProfessor({ user, meuNome, meuTelefone, onLogout, onEntrarSemProfessor, onConvidou }) {
  const [enviando, setEnviando] = React.useState(false);
  const [linkGerado, setLinkGerado] = React.useState('');
  const [nomeProfInput, setNomeProfInput] = React.useState('');
  // v140 (Marcus, 16/10: "o aluno tambem inclui professor, da mesma maneira"): o caminho principal
  // e' o MESMO assistente do "Adicionar professor" — agenda do celular, dias, horario de cada dia,
  // quanto paga. O professor recebe o combinado preenchido. O link solto (embaixo) chega sem nada.
  const [assistente, setAssistente] = React.useState(false);
  const criouRef = React.useRef(false);

  async function gerarConviteParaProfessor(nomeProfStr) {
    setEnviando(true);
    try {

      const { data: alunoExistL } = await supabase.from('alunos').select('id').eq('user_id', user.id).order('created_at').limit(1);
      let alunoId = alunoExistL && alunoExistL[0] && alunoExistL[0].id;
      if (!alunoId) {
        const { data: novoAluno } = await supabase.from('alunos').insert({ user_id: user.id, nome: user.email, professor_id: null }).select('id').single();
        alunoId = novoAluno?.id;
      }
      const code = await criarConvite({
        tipo: 'aluno_prof',
        aluno_id: alunoId,
        dados: { anome: user.email, nomeProfDestino: nomeProfStr },
      });
      const url = `${window.location.origin}${window.location.pathname}?c=${code}`;
      uso.acao('aluno.convidar_prof_gerar', { origem: 'sem_prof' });
      setLinkGerado(url);
      return url;
    } catch(e) { uso.erro('erro.convite_prof', e?.message || e); toastSM('Erro ao gerar convite: ' + e.message); }
    finally { setEnviando(false); }
  }

  async function convidarManual() {
    const url = await gerarConviteParaProfessor(nomeProfInput || 'Professor');
    if (!url) return;
    try { await navigator.clipboard.writeText(url); } catch {}
  }

  return (
    <div style={{minHeight:'100vh', background:'linear-gradient(150deg,#16181D,#16181D,#16181D)', display:'flex', alignItems:'center', justifyContent:'center', padding:'24px', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
      <div style={{background:'white', borderRadius:'28px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', padding:'40px 32px', width:'100%', maxWidth:'400px', textAlign:'center'}}>

        <div style={{width:'72px', height:'72px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', borderRadius:'24px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'36px', margin:'0 auto 20px'}}><Ico n="trophy" /></div>
        <h1 style={{fontSize:'22px', fontWeight:'800', color:'#16181D', marginBottom:'8px', letterSpacing:'-0.4px'}}>Bem-vindo ao Plen!</h1>
        <p style={{color:'#6E7480', fontSize:'14px', lineHeight:'1.6', marginBottom:'28px'}}>Seu perfil está pronto. Agora cadastre seu professor: você conta os dias e horários, e ele confere quando entrar.</p>

        {!linkGerado ? (<>
          {/* v147 (aluno do Cadu, 21/10): ja' cadastrado pelo professor, baixou o app e abriu direto — o convite
              colado liga a conta a' ficha que o professor fez, em vez de "Cadastrar meu professor" (ficha repetida). */}
          {APP_NATIVO && <div style={{marginBottom:'16px'}}><ColarConvite quem="aluno" /></div>}
          <button onClick={() => { uso.acao('aluno.add_prof_abrir', { origem: 'bem_vindo' }); setAssistente(true); }} disabled={enviando}
            style={{width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', padding:'16px', borderRadius:'16px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', fontSize:'15px', fontWeight:'700', cursor:'pointer', marginBottom:'12px', boxShadow:'0 4px 14px rgba(99,102,241,0.35)', opacity:enviando?0.6:1}}>
            <Ico n="user-plus" s={22} c="white" />
            Cadastrar meu professor
          </button>

          <div style={{display:'flex', alignItems:'center', gap:'10px', margin:'16px 0'}}>
            <div style={{flex:1, height:'1px', background:'#E2E4E9'}}/>
            <span style={{fontSize:'11px', color:'#9AA0AB', fontWeight:'600'}}>OU SÓ GERE O LINK</span>
            <div style={{flex:1, height:'1px', background:'#E2E4E9'}}/>
          </div>

          <input placeholder="Nome do professor (opcional)" value={nomeProfInput} onChange={e => setNomeProfInput(e.target.value)}
            style={{width:'100%', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'11px 14px', fontSize:'14px', marginBottom:'10px', outline:'none', boxSizing:'border-box'}} />
          <button onClick={convidarManual} disabled={enviando}
            style={{width:'100%', padding:'14px', borderRadius:'14px', background:'#F0F1F4', color:'#2F6BFF', border:'1.5px solid #C2D4FF', fontSize:'14px', fontWeight:'700', cursor:'pointer', opacity:enviando?0.6:1}}>
            <Ico n="link" /> Gerar link de convite
          </button>
        </>) : (
          <div style={{textAlign:'left'}}>
            <div style={{background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'14px', padding:'16px', marginBottom:'16px'}}>
              <div style={{fontSize:'12px', fontWeight:'700', color:'#0A6F4D', marginBottom:'8px'}}><Ico n="circle-check" c="#0E9F6E" /> Link gerado! Mande para o seu professor:</div>
              <div style={{background:'white', borderRadius:'10px', padding:'10px 12px', fontSize:'13px', fontFamily:'monospace', color:'#1F2229', wordBreak:'break-all', border:'1px solid #DCF5EC', marginBottom:'10px'}}>{linkGerado}</div>
              <div style={{display:'flex', gap:'8px'}}>
                <button onClick={() => { uso.acao('aluno.convite_prof_copiar'); copiarTextoSM(linkGerado, 'Link copiado.'); }}
                  style={{flex:1, padding:'10px', borderRadius:'10px', background:'#2F6BFF', color:'white', border:'none', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}><Ico n="clipboard-list" /> Copiar</button>
                <button onClick={() => {
                  const primeiro = (nomeProfInput || '').trim().split(' ')[0];
                  const msg = `Oi${primeiro ? ' ' + primeiro : ''}! 👋 Toque no link para entrar no Plen e já me encontrar lá:\n${blocoBaixarEConvite(linkGerado)}\n\nCódigo: *${linkGerado.split('?c=')[1]}*`;
                  compartilharOuWhats(`https://wa.me/?text=${encodeURIComponent(msg)}`, 'convite_prof');
                }}
                  style={{flex:1, padding:'10px', borderRadius:'10px', background:'#25D366', color:'white', border:'none', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}><Ico n="message-circle" /> WhatsApp</button>
              </div>
            </div>
            <button onClick={() => setLinkGerado('')} style={{width:'100%', padding:'10px', borderRadius:'10px', background:'transparent', color:'#9AA0AB', border:'none', fontSize:'13px', cursor:'pointer'}}>Gerar outro link</button>
          </div>
        )}

        {}
        {onEntrarSemProfessor && (
          <button onClick={() => { onEntrarSemProfessor(); }}
            style={{width:'100%', marginTop:'12px', padding:'11px', borderRadius:'12px', background:'transparent', color:'#2F6BFF', border:'1.5px solid #C2D4FF', fontSize:'13px', fontWeight:'600', cursor:'pointer'}}>
            Usar sem professor por enquanto <Ico n="arrow-right" />
          </button>
        )}

        <button onClick={onLogout} style={{width:'100%', marginTop:'12px', padding:'10px', borderRadius:'10px', background:'transparent', color:'#C7CAD1', border:'none', fontSize:'12px', cursor:'pointer'}}>Sair da conta</button>
      </div>
      {assistente && (
        <ModalAdicionarProfessor user={user} meuNome={meuNome} meuTelefone={meuTelefone || ''}
          onCriou={() => { criouRef.current = true; }}
          onClose={() => { setAssistente(false); if (criouRef.current) onConvidou?.(); }} />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// O ALUNO TRAZ O PROFESSOR.
//
// Este e' o laco que faz o Plen crescer sozinho: quem ja' usa com um professor quer
// controlar as outras aulas no mesmo lugar, e acaba levando o proximo professor junto.
//
// A regra que mantem tudo honesto: o aluno PROPOE, o professor CONFIRMA. O aluno
// descreve como sao as aulas dele — dias, horario, quanto paga — e isso viaja dentro do
// convite. Quando o professor entra, ele ve' a proposta preenchida e decide: aceita como
// esta' ou corrige. Nenhum numero entra no sistema sem o professor olhar, porque o
// dinheiro e' dele.
// ---------------------------------------------------------------------------
function ModalAdicionarProfessor({ user, meuNome, meuTelefone, onClose, onCriou }) {
  // v143 (Marcus, 20/10: "o MESMO processo que o professor faz, se eu, como aluno, to' adicionando
  // meu professor, eu tenho que conseguir fazer"): as perguntas e os numeros dos passos sao os do
  // "Novo aluno" do professor, sem a sala (3), que e' custo dele. 1 quem · 2 quanto · 4 dias e
  // duracao · 5 ja' comecou? · 6 ja' pagou? · 9 confira. No mensal somem 5 e 6, como no professor.
  // A diferenca: cada pergunta opcional tem "Nao sei — ele preenche".
  const PASSO_CONFIRA = 9;
  const [passo, setPasso] = React.useState(1);
  const [vistos, setVistos] = React.useState([]);              // passos ja' respondidos (Continuar / Nao sei)
  const [voltarResumo, setVoltarResumo] = React.useState(false); // veio de um toque numa linha da Confira
  const [nomeP, setNomeP] = React.useState('');
  const [modalidade, setModalidade] = React.useState('');
  const [outraMod, setOutraMod] = React.useState(false);   // modalidade fora da lista (balé...): digita
  const [telP, setTelP] = React.useState('');
  // Como o professor entra (v140, igual ao "Novo aluno" do professor): primeiro a ESCOLHA —
  // agenda do celular ou digitar —, campos so' depois. null = escolhendo; 'agenda'; 'digitar'.
  const [entrada, setEntrada] = React.useState(null);
  // O combinado — os mesmos campos do professor (ver "O COMBINADO"). Aqui comecam vazios: a forma
  // de pagar nao vem marcada (modo null), o vencimento e o "mes pago" podem ficar em branco.
  const [modo, setModo] = React.useState(null);             // 'aula' | 'pacote' | 'mes' | null
  const [valorAula, setValorAula] = React.useState('');
  const [valorPacote, setValorPacote] = React.useState('');
  const [valorMes, setValorMes] = React.useState('');
  const [qtdAulas, setQtdAulas] = React.useState('');
  const [dias, setDias] = React.useState([]);
  // Horario comeca VAZIO, como no "Novo aluno" do professor: 08:00 pronto virava dado que ninguem
  // conferiu (e, separando por dia, se repetia em todos os dias).
  const [hora, setHora] = React.useState('');
  const [horarios, setHorarios] = React.useState({});
  const [porDia, setPorDia] = React.useState(false);
  const [duracao, setDuracao] = React.useState(60);         // null = "Nao sei"
  const [novoPacote, setNovoPacote] = React.useState(null);
  const [feitas, setFeitas] = React.useState('');
  const [inicioCiclo, setInicioCiclo] = React.useState('');
  const [pago, setPago] = React.useState(null);
  const [dataPgto, setDataPgto] = React.useState('');
  const [diaVenc, setDiaVenc] = React.useState('');
  const [mesPago, setMesPago] = React.useState(null);
  const [prazoH, setPrazoH] = React.useState(0);            // v144: prazo para desmarcar (0 = livre, o padrao)
  const [salvando, setSalvando] = React.useState(false);
  const [erro, setErro] = React.useState('');
  const [pronto, setPronto] = React.useState(null);        // { url, texto, porDia, emBranco, comoPaga }
  // Central de uso: funil aluno_add_prof2 (v143; os numeros sao os do professor) — 1 quem, 2 quanto,
  // 4 dias, 5 ja' comecou, 6 ja' pagou, 9 confira, 10 convite pronto; termina ao mandar no WhatsApp
  // ou copiar. Fechar antes disso = saiu. (aluno_add_prof, com 2 = dias, e' o da versao antiga.)
  const fAdd = useFluxoUso('aluno_add_prof2', pronto ? 10 : passo, true);
  React.useEffect(() => { uso.janela('aluno.add_prof'); }, []);
  // Cada pergunta abre no topo (antes a Confira abria rolada, com o titulo fora da vista).
  const folhaRef = React.useRef(null);
  React.useEffect(() => { if (folhaRef.current) folhaRef.current.scrollTop = 0; }, [passo, !!pronto]);

  const a = { modo, setModo, valorAula, setValorAula, valorPacote, setValorPacote, valorMes, setValorMes,
              qtdAulas, setQtdAulas, dias, setDias, hora, setHora, horarios, setHorarios, porDia, setPorDia,
              duracao, setDuracao, novoPacote, setNovoPacote, feitas, setFeitas, inicioCiclo, setInicioCiclo,
              pago, setPago, dataPgto, setDataPgto, diaVenc, setDiaVenc, mesPago, setMesPago, setErro };
  const c = contaDoAcordo(a);
  // "balé" digitado em "Outra…" sai como "Balé" (grafia da lista do app) na Confira, no convite e na ficha.
  const modalidadeOk = modalidadeDoPedido(modalidade, []);

  // Quais perguntas existem (forma ainda nao escolhida conta como nao-mensal).
  const passosAluno = () => c.ehMensal ? [1, 2, 4, PASSO_CONFIRA] : [1, 2, 4, 5, 6, PASSO_CONFIRA];
  const lista = passosAluno();
  const TOTAL = lista.length - 1;
  const iPasso = lista.indexOf(passo);
  const numPergunta = passo === PASSO_CONFIRA ? TOTAL : (iPasso >= 0 ? iPasso + 1 : 1);
  // Para onde vai o Continuar. Voltando de uma linha da Confira: a primeira pergunta ainda nao
  // vista (ex.: trocou "Por mes" por "Pelo pacote" — faltam 5 e 6), senao a Confira.
  // Tambem e' pendente a pergunta ja' vista que ficou ERRADA com a mudanca (ex.: o pacote caiu de
  // 10 para 5 e as "8 feitas" passaram do total) — vai direto a ela, nao a Confira.
  function destino(p) {
    if (voltarResumo) {
      const vs = [...vistos, p];
      const errado = x => !!validarPassoAcordo(x, a, c, { voz: 'aluno', parcial: true });
      return lista.find(x => x >= 2 && x <= 8 && x !== p && (!vs.includes(x) || errado(x))) ?? PASSO_CONFIRA;
    }
    const i = lista.indexOf(p);
    return i >= 0 && i < lista.length - 1 ? lista[i + 1] : PASSO_CONFIRA;
  }
  function seguir(p) {
    const d = destino(p);
    setVistos(v => v.includes(p) ? v : [...v, p]);
    setErro('');
    if (d === PASSO_CONFIRA) setVoltarResumo(false);
    setPasso(d);
  }
  function voltar() {
    setErro('');
    if (voltarResumo && passo !== PASSO_CONFIRA) { setVoltarResumo(false); setPasso(PASSO_CONFIRA); return; }
    const i = lista.indexOf(passo);
    setPasso(i > 0 ? lista[i - 1] : 1);
  }
  // Continuar: a mesma validacao do professor. O botao nao fica apagado — mostra o motivo.
  function continuar() {
    if (passo === PASSO_CONFIRA) { enviar(); return; }
    if (passo === 1) {
      if (!nomeP.trim()) { fAdd.barrar('sem_nome'); setErro('Me diz o nome dele primeiro'); return; }
      seguir(1); return;
    }
    const v = validarPassoAcordo(passo, a, c, { voz: 'aluno' });
    if (v) { fAdd.barrar(v.codigo); setErro(v.msg); return; }
    seguir(passo);
  }
  // "Nao sei — ele preenche": segue com o que ja' respondeu certo; o vazio vai vazio. Resposta
  // ERRADA (201 aulas, 12 feitas num pacote de 10, data impossivel) continua travando.
  // Pergunta de um toque so' (Ja' pagou? / "Comeca agora"): o chip nao desmarca, entao o "Nao sei"
  // DESFAZ a escolha — senao ele prometia deixar para o professor e mandava o "ja' paguei".
  function naoSei() {
    let a2 = a;
    if (passo === 6) { a2 = { ...a, pago: null, dataPgto: '' }; setPago(null); setDataPgto(''); }
    if (passo === 5 && novoPacote === true) { a2 = { ...a, novoPacote: null }; setNovoPacote(null); }
    const v = validarPassoAcordo(passo, a2, contaDoAcordo(a2), { voz: 'aluno', parcial: true });
    if (v) { fAdd.barrar(v.codigo); setErro(v.msg); return; }
    uso.acao('aluno.add_prof_nao_sei', { passo: passo === 2 ? 'quanto' : passo === 5 ? 'comecou' : 'pagou' });
    seguir(passo);
  }
  // O link so' aparece quando ainda ha' o que deixar em branco. Pergunta ja' respondida por inteiro
  // com numeros (quanto, quantas feitas) nao tem "nao sei": e' so' apagar ou seguir.
  const mostraNaoSei = passo === 6 || (passo === 5 && novoPacote === true)
    || ([2, 5].includes(passo) && !!validarPassoAcordo(passo, a, c, { voz: 'aluno' }));

  // Estilos do passo 1 (iguais aos de antes).
  const inputCss = { width:'100%', padding:'14px', borderRadius:'12px', border:'1.5px solid #E2E4E9', fontSize:'16px', outline:'none', boxSizing:'border-box' };
  const chip = on => ({ padding:'11px 16px', borderRadius:'12px', fontSize:'15px', cursor:'pointer', fontWeight: on ? '800' : '600',
                        border: on ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: on ? '#E7EDFF' : 'white', color: on ? '#2F6BFF' : '#3D424C' });

  const temAgenda = temAgendaCelular();
  async function escolherDaAgenda() {
    const r = await escolherContatos({ origem: 'aluno_add_prof' });
    // Revisao v142 (o mesmo do professor): cancelar depois de ja' ter escolhido nao muda nada; escolher outro
    // troca o numero SEMPRE (contato sem numero = sem numero — antes o convite ia para o WhatsApp do anterior).
    const jaEscolheu = entrada === 'agenda';
    if (r.erro) { if (!jaEscolheu) setEntrada('digitar'); return; }
    const [ct] = r;
    if (!ct) { if (!jaEscolheu) setEntrada('vazia'); return; }   // fechou sem trazer ninguem: campo + dica (AvisoAgendaVazia)
    if (ct.nome) setNomeP(ct.nome);
    setTelP(ct.tel || '');
    setEntrada('agenda');
  }

  async function enviar() {
    if (salvando) return;
    // Rede de seguranca (normalmente nunca dispara): so' resposta errada trava aqui.
    for (const p of lista) {
      if (p < 2 || p > 8) continue;
      const v = validarPassoAcordo(p, a, c, { voz: 'aluno', parcial: true });
      if (v) { fAdd.barrar(v.codigo); setVoltarResumo(true); setPasso(p); setErro(v.msg); return; }
    }
    setSalvando(true); setErro('');
    // Ficha sem professor ainda — e' so' o lugar onde a proposta espera. v140: reaproveita a
    // ficha provisoria que ja' existir (cada convite novo criava mais uma = mais um chip
    // "Professor" no topo). O banco ja' sabe guardar varios pedidos na mesma ficha e so' a
    // apaga quando nenhum pedido depende dela (assumir_ficha_aluno).
    const { data: provL } = await supabase.from('alunos').select('id').eq('user_id', user.id).is('professor_id', null).order('created_at').limit(1);
    let fichaId = provL && provL[0] && provL[0].id;
    if (!fichaId) {
      const { data: fichaNova, error: eA } = await supabase.from('alunos').insert({
        professor_id: null, user_id: user.id, nome: meuNome || user.email,
        telefone: meuTelefone || '', email: user.email, modalidade: modalidadeOk,
        tipo_vinculo: 'vinculado', observacoes: '',
      }).select('id').single();
      if (eA) { fAdd.barrar('ficha_falhou'); uso.erro('erro.add_prof', eA.message); setSalvando(false); setErro('Não consegui preparar o convite: ' + eA.message); return; }
      fichaId = fichaNova.id;
    }

    // Proposta v2: tudo o que o aluno respondeu; o que ele nao sabe nao vai (o professor preenche).
    // Revisao v142 (achado 4): o WhatsApp do professor que veio da agenda viaja na proposta — ele confere no
    // resumo ("Seu WhatsApp (confira)"), igual ao numero do aluno no outro sentido.
    const proposta = propostaDoAcordo(a, c, { modalidade: modalidadeOk, aluno_nome: meuNome || user.email, aluno_telefone: meuTelefone || '', prof_telefone: telP, desmarcar_h: prazoH });
    let code;
    try {
      code = await criarConvite({
        tipo: 'aluno_prof', aluno_id: fichaId,
        dados: { anome: meuNome || user.email, nomeProfDestino: nomeP.trim() || 'Professor', proposta },
      });
    } catch (e) { fAdd.barrar('convite_falhou'); uso.erro('erro.convite_prof', e?.message || e); setSalvando(false); setErro('Não consegui gerar o convite. Tente de novo.'); return; }

    const url = `${window.location.origin}${window.location.pathname}?c=${code}`;
    const texto = textoWhatsConviteProf({ nomeProf: nomeP, proposta, url });
    setSalvando(false);
    setPronto({ url, texto, porDia: !!proposta.horarios, emBranco: linhasDaProposta(proposta, 'eu').nEmBranco, comoPaga: proposta.como_paga });
    onCriou?.();
  }

  const fone = whatsValidoSM(telP) ? digitosWhatsSM(telP) : '';   // o mesmo criterio do link (linkWhatsConviteSM)
  const wa = linkWhatsConviteSM(telP, pronto?.texto || '');
  const detalhesFim = () => ({ tem_fone: !!fone, como_paga: pronto?.comoPaga || 'nao_sei', horario_por_dia: !!pronto?.porDia, agenda: entrada === 'agenda', em_branco: pronto?.emBranco || 0 });

  let titulo = '', ajuda = '', conteudo = null, semRodape = false;

  if (passo === 1) {
    titulo = 'Quem é o professor?';
    // v142 (item C): so' o NOME, com o teclado aberto — igual ao "Novo aluno" do professor. O numero
    // saiu (contato salvo so' no WhatsApp nao aparece para nenhum app); a agenda e' um atalho pequeno.
    conteudo = (
      <>
        <CampoSoONome valor={nomeP} onValor={v => { setNomeP(v); if (erro) setErro(''); }} placeholder="Nome dele" estilo={inputCss}
          onEnter={continuar} entrada={entrada} tel={telP} textoAgenda="Veio da agenda. Confira o nome dele."
          onLimpar={() => { setTelP(''); if (entrada === 'agenda') setEntrada('digitar'); }}
          onTirarTel={() => { uso.acao('aluno.add_prof_tirar_whats'); setTelP(''); }}
          temAgenda={temAgenda} onAgenda={() => { uso.acao('aluno.add_prof_entrada', { via: entrada === 'agenda' ? 'agenda_outro' : 'agenda_depois' }); escolherDaAgenda(); }} />
        <div style={{fontSize:'13px', color:'#6E7480', margin:'16px 0 6px'}}>Ele dá aula de quê?</div>
        <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
          {MODALIDADES_COMUNS.map(m => (
            <button key={m} type="button" onClick={() => { setOutraMod(false); setModalidade(m); }}
              style={{...chip(!outraMod && modalidade === m), fontSize:'13px', padding:'9px 13px'}}>{m}</button>
          ))}
          {/* Fora da lista (balé, vôlei de praia...): "Outra…" abre o campo (Marcus, 20/10). */}
          <button type="button" onClick={() => { if (!outraMod) { setOutraMod(true); setModalidade(''); uso.acao('aluno.add_prof_modalidade_outra'); } }}
            style={{...chip(outraMod), fontSize:'13px', padding:'9px 13px'}}>Outra…</button>
        </div>
        {outraMod && (
          <input value={modalidade} onChange={e => setModalidade(e.target.value.slice(0, 40))} placeholder="Qual? Ex.: balé, vôlei de praia"
            autoFocus style={{...inputCss, marginTop:'10px', fontSize:'15px'}} />
        )}
      </>
    );
  }

  if (passo === 2) {
    ({ titulo, ajuda } = textosPassoAcordo(2, 'aluno', c));
    conteudo = (
      <>
        <CorpoQuanto voz="aluno" a={a} c={c} />
        {mostraNaoSei && <LinkNaoSei onClick={naoSei} />}
      </>
    );
  }

  if (passo === 4) {
    ({ titulo, ajuda } = textosPassoAcordo(4, 'aluno', c));
    conteudo = (
      <CorpoDias voz="aluno" a={a} c={c}
        onSeparou={() => uso.acao('aluno.add_prof_horario_por_dia', { dias: dias.length })}
        onJuntou={() => uso.acao('aluno.add_prof_mesmo_horario')} />
    );
  }

  if (passo === 5) {
    ({ titulo, ajuda } = textosPassoAcordo(5, 'aluno', c));
    conteudo = (
      <>
        <CorpoComecou voz="aluno" a={a} c={c} onSeguir={() => seguir(5)} />
        {mostraNaoSei && <LinkNaoSei onClick={naoSei} />}
      </>
    );
  }

  if (passo === 6) {
    ({ titulo, ajuda } = textosPassoAcordo(6, 'aluno', c));
    conteudo = (
      <>
        <CorpoPagou voz="aluno" a={a} c={c} />
        {mostraNaoSei && <LinkNaoSei onClick={naoSei} />}
      </>
    );
  }

  if (passo === PASSO_CONFIRA) {
    const primeiroProf = (nomeP.trim() || '').split(' ')[0];
    titulo = 'Confira o combinado';
    // O topo ja' diz "confira"; a ajuda ensina o que da' para fazer aqui (as linhas tem "›").
    ajuda = 'Toque numa linha para mudar.';
    const linhas = linhasDoAcordo(a, c, { voz: 'aluno', titulo: nomeP.trim() || 'Professor', modalidade: modalidadeOk, chuva: aoArLivre(modalidadeOk) });   // fecho v148: a linha da chuva
    const temVazio = linhas.some(l => l.vazio || l.pre);
    conteudo = (
      <>
        {/* Mensal: o dia de pagar e o "mes pago" se respondem aqui mesmo — em cima, para nao
            nascerem escondidos atras do rodape (a tabela embaixo ja' mostra o que foi escolhido). */}
        {c.ehMensal && <div style={{marginBottom: '16px'}}><BlocoMensalResumo voz="aluno" a={a} /></div>}
        <TabelaAcordo linhas={linhas}
          onTocar={l => { uso.acao('aluno.add_prof_mudar', { passo: l.passo }); setErro(''); setVoltarResumo(true); setPasso(l.passo); }} />
        {/* v144: a mesma pergunta do "Novo aluno" do professor — sem prazo combinado, desmarcar e' livre */}
        <PerguntaPrazoDesmarcar valor={prazoH} onValor={v => { uso.acao('aluno.add_prof_prazo_desmarcar', { h: v }); setPrazoH(v); }} />
        <div style={{background:'#F7F8FA', borderRadius:'12px', padding:'12px 14px', marginTop:'18px', fontSize:'13px', color:'#3D424C', lineHeight:1.6}}>
          <strong>{nomeP.trim() || 'Ele'}</strong>{' vai receber isso já preenchido e confere antes de valer. Nada é cobrado por aqui — o Plen só organiza.'}
          {temVazio ? ' O que está como “ele preenche”, ele completa quando aceitar.' : ''}
        </div>
      </>
    );
  }

  const rotulo = passo === PASSO_CONFIRA ? (salvando ? 'Preparando…' : 'Gerar convite')
    : destino(passo) === PASSO_CONFIRA ? 'Conferir o combinado' : 'Continuar';

  return (
    <Portal>
    {/* plen-modal-wrap: a janela sobe junto com o teclado do iPhone (--kb), como a do professor —
        senao o teclado cobria o Continuar ao digitar o valor ou as aulas feitas. */}
    <div className="plen-modal-wrap" style={{position:'fixed', inset:0, background:'rgba(22,24,29,.55)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:80}}>
      {/* Sem padding embaixo quando ha' rodape preso: o Chrome desconta o padding da janela do
          "sticky" e o conteudo aparecia rolando por baixo do botao. */}
      <div ref={folhaRef} style={{background:'white', borderRadius:'22px 22px 0 0', width:'100%', maxWidth:'480px', maxHeight:'calc(100dvh - var(--kb, 0px) - 48px)', overflowY:'auto', padding: (pronto || semRodape) ? '22px 20px 28px' : '22px 20px 0', position:'relative'}}>
        <button onClick={onClose} aria-label="Fechar"
          style={{position:'absolute', top:'14px', right:'16px', width:'32px', height:'32px', borderRadius:'50%', border:'none', background:'#F0F1F4', color:'#6E7480', cursor:'pointer'}}><Ico n="x" /></button>

        {pronto ? (
          <>
            <div style={{fontSize:'19px', fontWeight:'800', color:'#16181D', marginBottom:'6px'}}>Convite pronto</div>
            <div style={{fontSize:'13px', color:'#6E7480', marginBottom:'18px', lineHeight:1.55}}>
              {`Mande para ${nomeP.trim() || 'ele'}. Quando entrar, suas aulas${modalidadeOk ? ' de ' + modalidadeOk : ''} aparecem aqui ao lado das outras.`
                + (pronto.emBranco ? ' O que ficou em branco, ele completa quando aceitar.' : '')}
            </div>
            <a href={wa} target="_blank" rel="noopener noreferrer" onClick={() => { fAdd.fim('whatsapp', detalhesFim()); uso.acao('whatsapp.abrir', { motivo: 'convite_prof', com_numero: !!fone, via: 'link' }); onClose(); }}
              style={{display:'block', textAlign:'center', textDecoration:'none', width:'100%', padding:'16px', borderRadius:'14px',
                      background:'linear-gradient(135deg,#25D366,#128C7E)', color:'white', fontWeight:'800', fontSize:'15px'}}>
              Mandar convite no WhatsApp
            </a>
            <button onClick={() => { fAdd.fim('copiou', detalhesFim()); copiarTextoSM(pronto.url, 'Link copiado.', 'Não consegui copiar. Toque em "Mandar convite no WhatsApp".'); }}
              style={{width:'100%', marginTop:'10px', padding:'13px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontWeight:'700', fontSize:'14px', cursor:'pointer'}}>
              Copiar o link
            </button>
            {/* v142 (item C): o mesmo aviso do fim do "Novo aluno" do professor. */}
            <div style={{fontSize:'12px', color:'#6E7480', marginTop:'9px', lineHeight:1.5}}>
              {fone ? 'Vai uma mensagem pronta para o WhatsApp dele, com o combinado e o link.'
                : `O WhatsApp abre com a mensagem pronta — é só escolher ${(nomeP.trim() || 'ele').split(' ')[0]} lá.`}
            </div>
          </>
        ) : (
          <>
            {/* paddingRight: o X de fechar cobria o ultimo pedaco da barra */}
            <div style={{display:'flex', gap:'5px', marginBottom:'16px', paddingRight:'44px'}}>
              {Array.from({ length: TOTAL }, (_, k) => k + 1).map(i => <div key={i} style={{height:'4px', flex:1, borderRadius:'2px', background: i <= numPergunta ? '#2F6BFF' : '#E2E4E9'}} />)}
            </div>
            <div style={{fontSize:'12px', color:'#9AA0AB', marginBottom:'4px'}}>
              {passo === PASSO_CONFIRA ? 'Confira antes de mandar' : `Pergunta ${numPergunta} de ${TOTAL}`}
            </div>
            <div style={{fontSize:'19px', fontWeight:'800', color:'#16181D', marginBottom: ajuda ? '4px' : '16px', lineHeight:1.3}}>{titulo}</div>
            {ajuda && <div style={{fontSize:'13px', color:'#6E7480', margin:'0 0 16px', lineHeight:1.5}}>{ajuda}</div>}
            {conteudo}
            {erro && <div style={{fontSize:'13px', color:'#DC2626', marginTop:'10px', fontWeight:'600'}}>{erro}</div>}
            {/* Rodape preso embaixo da janela, igual ao "Novo aluno" do professor: com varios dias
                separados o Continuar nao some (Marcus, 29/09: o botao nunca fica escondido). */}
            {!semRodape && <div style={{display:'flex', gap:'8px', position:'sticky', bottom:0, background:'white',
                                        padding:'12px 20px calc(28px + env(safe-area-inset-bottom))', margin:'20px -20px 0', borderTop:'1px solid #F0F1F4'}}>
              {passo > 1 && (
                <button onClick={voltar}
                  style={{padding:'15px 18px', borderRadius:'13px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontWeight:'700', fontSize:'14px', cursor:'pointer'}}>
                  {voltarResumo && passo !== PASSO_CONFIRA ? 'Voltar ao combinado' : 'Voltar'}
                </button>
              )}
              <button onClick={continuar} disabled={salvando}
                style={{flex:1, padding:'15px', borderRadius:'13px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'15px',
                        cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? .6 : 1}}>
                {rotulo}
              </button>
            </div>}
          </>
        )}
      </div>
    </div>
    </Portal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TURMAS — aula em grupo (Fase 2G). Regras: a turma agrupa, a pessoa paga; mensal sem
// reposicao; sem avulso, sem limite de vagas. Banco: sql/turmas.sql.
// ═══════════════════════════════════════════════════════════════════════════════
const DIAS_T = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const brl = v => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
function descreverHorariosTurma(hs) {
  if (!hs || !hs.length) return 'sem horário';
  const porHora = {};
  hs.forEach(h => { const k = String(h.hora).slice(0, 5); (porHora[k] = porHora[k] || []).push(Number(h.dia_semana)); });
  return Object.entries(porHora).map(([hora, dias]) => dias.sort().map(d => DIAS_T[d]).join(', ') + ' às ' + hora.replace(':00', 'h').replace(':', 'h')).join(' · ');
}

// ---------------------------------------------------------------- criar / editar turma
function ModalTurma({ professor, turma, onClose, onSalvou }) {
  const [nome, setNome] = React.useState(turma?.nome || '');
  const [modalidade, setModalidade] = React.useState(turma?.modalidade || (parseMods(professor.modalidades)[0] || ''));
  const [preco, setPreco] = React.useState(turma?.preco_mensal ? String(turma.preco_mensal) : '');
  const [diaVenc, setDiaVenc] = React.useState(turma?.dia_vencimento ? String(turma.dia_vencimento) : '10');
  const [local, setLocal] = React.useState(turma?.local || '');
  const [dias, setDias] = React.useState([]);
  const [hora, setHora] = React.useState('');
  const [horarios, setHorarios] = React.useState({});  // {dia: 'HH:MM'} quando difere
  const [porDia, setPorDia] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => {
    if (!turma) return;
    supabase.from('turma_horarios').select('dia_semana, hora').eq('turma_id', turma.id).then(({ data }) => {
      const hs = data || [];
      const ds = [...new Set(hs.map(h => Number(h.dia_semana)))].sort();
      const map = {}; hs.forEach(h => { map[Number(h.dia_semana)] = String(h.hora).slice(0, 5); });
      const distintas = [...new Set(Object.values(map))];
      setDias(ds); setHorarios(map);
      if (distintas.length <= 1) { setHora(distintas[0] || ''); setPorDia(false); } else setPorDia(true);
    });
  }, []);

  const num = numDinheiro;
  const horaDe = d => porDia ? (horarios[d] || '') : hora;
  const pronto = nome.trim() && dias.length > 0 && dias.every(d => horaDe(d));
  // Central de uso: funil nova_turma (so' na criacao) — 1 abriu, 2 deu o nome, 3 escolheu os dias, 4 horario completo
  const fTurma = useFluxoUso('nova_turma', !nome.trim() ? 1 : !dias.length ? 2 : !pronto ? 3 : 4, !turma);

  async function salvar() {
    if (!pronto) { if (turma) uso.bloqueio('turma_editar', null, null, 'incompleta'); else fTurma.barrar('incompleta'); toastSM('Falta o nome ou algum horário.'); return; }
    setSalvando(true);
    try {
      const base = { professor_id: professor.id, nome: nome.trim(), modalidade: modalidade || null,
        preco_mensal: num(preco) || null, dia_vencimento: Number(diaVenc) || null, local: local.trim() || null };
      let id = turma?.id;
      if (id) {
        const { error } = await supabase.from('turmas').update(base).eq('id', id);
        if (error) throw error;
        await supabase.from('turma_horarios').delete().eq('turma_id', id);
      } else {
        const { data, error } = await supabase.from('turmas').insert(base).select('id').single();
        if (error) throw error;
        id = data.id;
      }
      const linhas = dias.map(d => ({ turma_id: id, dia_semana: d, hora: horaDe(d) }));
      const { error: eH } = await supabase.from('turma_horarios').insert(linhas);
      if (eH) throw eH;
      // Membros que ja' estao na turma: agenda deles acompanha os horarios novos.
      if (turma?.id) {
        await supabase.from('agenda_semanal').delete().eq('turma_id', id);
        const { data: membros } = await supabase.from('alunos').select('id').eq('turma_id', id);
        const ag = [];
        (membros || []).forEach(m => linhas.forEach(l => ag.push({ aluno_id: m.id, professor_id: professor.id, dia_semana: l.dia_semana, hora: l.hora, ativo: true, turma_id: id })));
        if (ag.length) await supabase.from('agenda_semanal').insert(ag);
        await supabase.from('aulas').delete().eq('turma_id', id).eq('status', 'agendada').gte('data', new Date().toLocaleDateString('sv-SE'));
        await supabase.rpc('repor_agenda_turmas');
      }
      if (turma) uso.acao('turma.editar_salvar'); else fTurma.fim('criou', { n_dias: dias.length, com_preco: num(preco) > 0 });
      toastSM(turma ? 'Turma atualizada.' : 'Turma criada. Agora adicione as pessoas.');
      onSalvou && onSalvou(id);
    } catch (e) { if (turma) uso.bloqueio('turma_editar', null, null, 'falha_salvar'); else fTurma.barrar('falha_salvar'); uso.erro('erro.turma_salvar', e?.message || e); toastSM('Não foi possível salvar: ' + (e.message || e)); }
    finally { setSalvando(false); }
  }

  const inp = { width:'100%', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'12px 14px', fontSize:'15px', color:'#16181D', outline:'none', boxSizing:'border-box', background:'white' };
  const lab = { fontSize:'11px', fontWeight:'700', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'6px', display:'block' };
  // Modalidade vem de LISTA, nunca digitada: "Bike", "bike" e "Biek" viravam tres coisas
  // diferentes e o dado ficava imprestavel para qualquer conta depois (Marcus, 29/09).
  // As do proprio professor vem primeiro; o resto da lista do app logo abaixo.
  const minhas = parseMods(professor.modalidades);
  const outras = MODALIDADES_PROF.filter(m => !minhas.includes(m));
  // turma antiga com texto livre: continua aparecendo para nao sumir da tela
  const foraDaLista = modalidade && !minhas.includes(modalidade) && !outras.includes(modalidade);

  return (
    <Modal open onClose={onClose} title={turma ? 'Editar turma de treino' : 'Nova turma de treino'} sujo={dias.length > 0} uso={turma ? 'turma_editar' : 'turma_nova'}
      footer={<Button onClick={salvar} disabled={salvando || !pronto} className="justify-center" style={{width:'100%'}}>{salvando ? 'Salvando…' : (turma ? 'Salvar' : 'Criar turma e adicionar pessoas')}</Button>}>
      <div style={{display:'grid', gap:'16px'}}>
        <div><span style={lab}>Nome da turma</span>
          <input style={inp} value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex.: Beach tennis manhã, Pedal de sábado" autoFocus /></div>
        <div><span style={lab}>Modalidade</span>
          <select style={inp} value={modalidade} onChange={e => setModalidade(e.target.value)}>
            <option value="">Escolha…</option>
            {minhas.length > 0 && <optgroup label="Suas modalidades">{minhas.map(m => <option key={m} value={m}>{m}</option>)}</optgroup>}
            <optgroup label={minhas.length > 0 ? 'Outras' : 'Modalidades'}>{outras.map(m => <option key={m} value={m}>{m}</option>)}</optgroup>
            {foraDaLista && <option value={modalidade}>{modalidade}</option>}
          </select></div>
        <div><span style={lab}>Local (opcional)</span>
          <input style={inp} value={local} onChange={e => setLocal(e.target.value)} placeholder="Arena, parque…" /></div>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px'}}>
          <div><span style={lab}>Mensalidade por pessoa</span>
            <input style={inp} inputMode="decimal" value={preco} onChange={e => setPreco(limparDinheiro(e.target.value))} onBlur={() => setPreco(v => formatarDinheiro(v))} placeholder="Ex.: 250,00" />
            <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'4px'}}>É só o padrão. Cada pessoa pode ter outro valor.</div></div>
          <div><span style={lab}>Vence todo dia</span>
            <input style={inp} inputMode="numeric" value={diaVenc} onChange={e => setDiaVenc(e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="10" /></div>
        </div>
        <div>
          <span style={lab}>Dias da semana</span>
          <div style={{display:'flex', gap:'6px', flexWrap:'wrap'}}>
            {DIAS_T.map((d, i) => { const on = dias.includes(i); return (
              <button key={i} type="button" onClick={() => setDias(ds => on ? ds.filter(x => x !== i) : [...ds, i].sort())}
                style={{padding:'9px 0', width:'44px', borderRadius:'10px', border:'1.5px solid ' + (on ? '#2F6BFF' : '#E2E4E9'), background: on ? '#2F6BFF' : 'white', color: on ? 'white' : '#3D424C', fontWeight:'700', fontSize:'13px', cursor:'pointer'}}>{d}</button>); })}
          </div>
        </div>
        {dias.length > 0 && (
          <div>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <span style={lab}>{porDia ? 'Horário de cada dia' : 'Horário'}</span>
              {dias.length > 1 && <button type="button" onClick={() => setPorDia(p => !p)} style={{border:'none', background:'none', color:'#2F6BFF', fontWeight:'700', fontSize:'12.5px', cursor:'pointer'}}>{porDia ? 'Mesmo horário todo dia' : 'Horário diferente por dia'}</button>}
            </div>
            {porDia ? dias.map(d => (
              <div key={d} style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px'}}>
                <span style={{width:'40px', fontWeight:'700', fontSize:'13px'}}>{DIAS_T[d]}</span>
                <input type="time" style={{...inp, flex:1, minWidth:0}} value={horarios[d] || ''} onChange={e => setHorarios(h => ({ ...h, [d]: e.target.value }))} />
              </div>
            )) : <input type="time" style={{...inp, maxWidth:'200px'}} value={hora} onChange={e => setHora(e.target.value)} />}
          </div>
        )}
        <div style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'12px 14px', fontSize:'13px', color:'#1C43AE'}}>
          Turma é <b>mensal</b>: horário fixo, quem falta não repõe, e cada pessoa paga a própria mensalidade pelo Pix.
        </div>
        {!pronto && <div style={{fontSize:'12.5px', color:'#6E7480'}}>Para continuar: nome, pelo menos um dia e o horário.</div>}
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------ adicionar pessoas (lote)
function ModalMembrosTurma({ professor, turma, onClose, onMudou }) {
  const [alunos, setAlunos] = React.useState([]);        // alunos do professor fora de turma
  // Pessoas novas: uma linha com campo de nome, de WhatsApp (com mascara) e de preco. Era uma
  // caixa de texto "Nome, WhatsApp" — o Cadu digitou sem virgula e o telefone virou parte do
  // nome ("Henrique 11 99324…"), 29/09.
  // O preco ja' nasce com a mensalidade da turma; o professor so' mexe se aquela pessoa paga
  // diferente (Marcus, 29/09 — antes o campo vinha vazio com o valor apagado de fundo).
  const precoPadrao = turma.preco_mensal ? formatarDinheiro(turma.preco_mensal) : '';
  const linhaVazia = () => ({ nome: '', tel: '', preco: precoPadrao });
  const [novos, setNovos] = React.useState([linhaVazia()]);
  const [precos, setPrecos] = React.useState({});         // aluno_id -> preco
  const [selecionados, setSelecionados] = React.useState(new Set());
  const [salvando, setSalvando] = React.useState(false);

  React.useEffect(() => {
    supabase.from('alunos').select('id, nome, telefone, turma_id').eq('professor_id', professor.id).is('turma_id', null).order('nome')
      .then(({ data }) => setAlunos((data || []).filter(a => !a.desvinculado_em)));
  }, []);

  function alternar(id) {
    setSelecionados(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    setPrecos(p => p[id] ? p : { ...p, [id]: precoPadrao });
  }
  // Valor igual ao da turma vai como null = "segue o padrao da turma".
  const precoOuPadrao = v => { const n = numDinheiro(v); return n && n !== Number(turma.preco_mensal) ? n : null; };

  // Agenda do celular: cada contato escolhido ocupa a primeira linha vazia (ou cria outra).
  // Se o telefone ja' e' de um aluno da lista de cima, marca ele em vez de criar repetido.
  async function daAgenda() {
    const lista = await escolherContatos({ varios: true, origem: 'turma', textoErro: 'Não consegui abrir a agenda. Digite o nome e o WhatsApp.' });
    if (!lista.length) return;
    const fim8 = t => String(t || '').replace(/\D/g, '').slice(-8);
    const novosC = [];
    for (const c of lista) {
      const ja = c.tel && alunos.find(a => fim8(a.telefone) && fim8(a.telefone) === fim8(c.tel));
      if (ja) { if (!selecionados.has(ja.id)) alternar(ja.id); toastSM(`${ja.nome} já é seu aluno — marquei lá em cima.`); continue; }
      novosC.push({ nome: c.nome, tel: c.tel, preco: precoPadrao });
    }
    if (!novosC.length) return;
    setNovos(ls => {
      const r = [...ls];
      for (const c of novosC) {
        const i = r.findIndex(l => !(l.nome || '').trim() && !(l.tel || '').trim());
        if (i >= 0) r[i] = c; else r.push(c);
      }
      return r;
    });
  }

  async function adicionar() {
    setSalvando(true);
    try {
      let n = 0;
      // 1) alunos que ja existem
      for (const id of selecionados) {
        const p = precoOuPadrao(precos[id]);
        const { error } = await supabase.rpc('turma_adicionar_membro', { p_turma: turma.id, p_aluno: id, p_preco: p, p_dia_venc: null });
        if (error) throw error; n++;
      }
      // 2) pessoas novas (linhas com nome preenchido)
      for (const l of novos) {
        const nomeRaw = (l.nome || '').trim();
        if (!nomeRaw) continue;
        const { data: a, error: eA } = await supabase.from('alunos').insert({
          professor_id: professor.id, nome: nomeRaw, telefone: l.tel ? mascaraTel(l.tel) : '', email: '',
          modalidade: turma.modalidade || '', observacoes: '' }).select('id').single();
        if (eA) throw eA;
        const p = precoOuPadrao(l.preco);
        const { error } = await supabase.rpc('turma_adicionar_membro', { p_turma: turma.id, p_aluno: a.id, p_preco: p, p_dia_venc: null });
        if (error) throw error; n++;
      }
      uso.acao('turma.membros_add', { n, novos: novos.filter(l => (l.nome || '').trim()).length });
      toastSM(n ? `${n} pessoa${n > 1 ? 's' : ''} na turma. Agora envie os convites.` : 'Ninguém selecionado.');
      onMudou && onMudou(); onClose();
    } catch (e) { uso.erro('erro.turma_membros', e?.message || e); toastSM('Não foi possível: ' + (e.message || e)); }
    finally { setSalvando(false); }
  }

  const inp = { width:'100%', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'12px 14px', fontSize:'15px', color:'#16181D', outline:'none', boxSizing:'border-box' };
  const temNovo = novos.some(l => (l.nome || '').trim());
  const mudarNovo = (i, campo, v) => setNovos(ls => ls.map((l, k) => k === i ? { ...l, [campo]: v } : l));
  const agenda = temAgendaCelular();
  return (
    <Modal open onClose={onClose} title={'Adicionar pessoas — ' + turma.nome} sujo={selecionados.size > 0 || temNovo} uso="turma_membros"
      footer={<Button onClick={adicionar} disabled={salvando || (selecionados.size === 0 && !temNovo)} className="justify-center" style={{width:'100%'}}>{salvando ? 'Adicionando…' : 'Adicionar à turma'}</Button>}>
      <div style={{display:'grid', gap:'16px'}}>
        {alunos.length > 0 && (
          <div>
            <div style={{fontSize:'11px', fontWeight:'700', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'8px'}}>Alunos que você já tem</div>
            <div style={{display:'grid', gap:'6px', maxHeight:'220px', overflowY:'auto'}}>
              {alunos.map(a => { const on = selecionados.has(a.id); return (
                <div key={a.id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'12px', border:'1.5px solid ' + (on ? '#2F6BFF' : '#E2E4E9'), background: on ? '#F7F9FF' : 'white'}}>
                  <button type="button" onClick={() => alternar(a.id)} style={{width:'22px', height:'22px', borderRadius:'7px', border:'1.5px solid ' + (on ? '#2F6BFF' : '#C9CDD5'), background: on ? '#2F6BFF' : 'white', color:'white', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0}}>{on && <Ico n="check" s={14} c="white" />}</button>
                  <span style={{flex:1, fontWeight:'700', fontSize:'14px'}}>{a.nome}</span>
                  {on && <input inputMode="decimal" placeholder={precoPadrao || 'R$'} value={precos[a.id] || ''} onChange={e => setPrecos(p => ({ ...p, [a.id]: limparDinheiro(e.target.value) }))} onBlur={() => setPrecos(p => ({ ...p, [a.id]: formatarDinheiro(p[a.id]) }))}
                    style={{width:'84px', border:'1.5px solid #E2E4E9', borderRadius:'8px', padding:'6px 8px', fontSize:'13px'}} />}
                </div>); })}
            </div>
          </div>
        )}
        <div>
          <div style={{fontSize:'11px', fontWeight:'700', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'8px'}}>Pessoas novas</div>
          {agenda && (
            <button type="button" onClick={daAgenda}
              style={{width:'100%', marginBottom:'10px', padding:'12px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'700', fontSize:'14px', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
              <Ico n="address-book" s={17} c="white" /> Buscar na agenda do celular
            </button>
          )}
          <div style={{display:'grid', gap:'10px'}}>
            {novos.map((l, i) => (
              <div key={i} style={{background:'#F7F8FA', borderRadius:'12px', padding:'10px', display:'grid', gap:'8px'}}>
                <input style={{...inp, background:'white'}} value={l.nome} onChange={e => mudarNovo(i, 'nome', e.target.value)} placeholder="Nome" />
                <div style={{display:'flex', gap:'8px'}}>
                  <input style={{...inp, background:'white', flex:1, minWidth:0}} inputMode="tel" value={l.tel} onChange={e => mudarNovo(i, 'tel', mascaraTel(e.target.value))} placeholder="WhatsApp (11 99999-1234)" />
                  <div style={{position:'relative', width:'118px', flexShrink:0}}>
                    <span style={{position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', fontSize:'13px', color:'#9AA0AB'}}>R$</span>
                    <input style={{...inp, background:'white', paddingLeft:'32px'}} inputMode="decimal" value={l.preco} onChange={e => mudarNovo(i, 'preco', limparDinheiro(e.target.value))} onBlur={() => mudarNovo(i, 'preco', formatarDinheiro(l.preco))} placeholder={precoPadrao || '0,00'} />
                  </div>
                </div>
                {novos.length > 1 && <button type="button" onClick={() => setNovos(ls => ls.filter((_, k) => k !== i))} style={{border:'none', background:'none', color:'#9AA0AB', fontSize:'12px', cursor:'pointer', justifySelf:'end'}}>tirar linha</button>}
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setNovos(ls => [...ls, linhaVazia()])} style={{marginTop:'8px', border:'1.5px dashed #C2D4FF', background:'#F7F9FF', color:'#2F6BFF', borderRadius:'12px', padding:'10px', width:'100%', fontWeight:'700', fontSize:'13px', cursor:'pointer'}}>+ Mais uma pessoa</button>
          <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'6px'}}>{turma.preco_mensal ? `Já vem com a mensalidade da turma (${brl(turma.preco_mensal)}). Mude só se essa pessoa paga diferente — dá para mudar depois também.` : 'Cada pessoa pode ter o seu valor; dá para mudar depois tocando nele.'}</div>
        </div>
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------- presenca de uma sessao
// v131 (Etapa 9): so' fecha quem esta' 'agendada'. Quem avisou que nao vem (aviso vigente) ja'
// vem desmarcado; quem cancelou aparece riscado e nao da' para marcar; quem ja' foi fechado
// (dada/falta) aparece com o que ficou, sem mudar. professor_fechar_aula sem "forcar" tambem nao
// grava nada em aula que nao esta' na agenda.
function ModalPresencaTurma({ professor, sessao, onClose, onFechou }) {
  // sessao = { sessao_id, turma_nome, data, hora, aulas: [{id, aluno_id, nome, status, aviso_aluno_em, desfeita_em}] }
  const abertas = sessao.aulas.filter(a => a.status === 'agendada');
  const [presentes, setPresentes] = React.useState(() => new Set(abertas.filter(a => !avisoVigenteAula(a)).map(a => a.aluno_id)));
  const [salvando, setSalvando] = React.useState(false);
  const trava = React.useRef(false);
  function alternar(id) { setPresentes(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  async function fechar() {
    if (trava.current) return;            // toque duplo nao fecha duas vezes
    trava.current = true; setSalvando(true);
    let ok = 0, rede = false;
    for (const a of abertas) {
      const res = await rpcAulaSM('professor_fechar_aula', { p_aula_id: a.id, p_desfecho: presentes.has(a.aluno_id) ? 'realizada' : 'falta', p_forcar: false });
      if (res.ok) ok++; else if (res.rede) rede = true;
    }
    trava.current = false; setSalvando(false);
    const faltas = abertas.filter(a => !presentes.has(a.aluno_id)).length;
    uso.acao('turma.presenca', { presentes: abertas.length - faltas, faltas, ok: ok === abertas.length });
    if (rede) uso.erro('erro.turma_presenca', 'rede');
    toastSM(ok === abertas.length ? 'Aula da turma fechada.' : `Fechei ${ok} de ${abertas.length}. Tente de novo para o resto.`);
    onFechou && onFechou(); onClose();
  }
  const nPres = abertas.filter(a => presentes.has(a.aluno_id)).length;
  const nFalta = abertas.length - nPres;
  return (
    <Modal open onClose={onClose} title={sessao.turma_nome} uso="turma_presenca"
      footer={<Button onClick={fechar} disabled={salvando || !abertas.length} className="justify-center" style={{width:'100%', minHeight:'44px'}}>{salvando ? 'Fechando…' : abertas.length ? 'Fechar aula' : 'Nada para fechar'}</Button>}>
      <div style={{fontSize:'13px', color:'#6E7480', marginBottom:'12px'}}>{fmtDataBR(sessao.data)} · {String(sessao.hora).slice(0, 5)} · desmarque quem faltou</div>
      <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'18px'}}>
        {sessao.aulas.map(a => {
          const aberta = a.status === 'agendada';
          const cancelada = String(a.status || '').startsWith('cancelada');
          const on = aberta ? presentes.has(a.aluno_id) : a.status === 'realizada';
          const avisou = aberta && avisoVigenteAula(a);
          const extra = cancelada ? (rotuloEspecialAula(a) ? ` · ${rotuloEspecialAula(a).toLowerCase()}` : ' · cancelou')
                      : a.status === 'realizada' ? ' · já fechada' : a.status === 'falta' ? ' · falta' : avisou ? ' · avisou' : '';
          return (
          <button key={a.id || a.aluno_id} type="button" disabled={!aberta} onClick={() => aberta && alternar(a.aluno_id)}
            title={cancelada ? 'Cancelou esta aula' : !aberta ? 'Já fechada' : undefined}
            style={{display:'flex', alignItems:'center', gap:'8px', minHeight:'44px', padding:'10px 14px', borderRadius:'999px',
                    border:'1.5px solid ' + (cancelada ? '#E2E4E9' : on ? '#0E9F6E' : '#E2E4E9'),
                    background: cancelada ? '#F7F8FA' : on ? '#E6F7F0' : 'white',
                    color: cancelada ? '#9AA0AB' : on ? '#067A52' : '#9AA0AB', fontWeight:'700', fontSize:'14px',
                    cursor: aberta ? 'pointer' : 'not-allowed', opacity: aberta ? 1 : 0.75,
                    textDecoration: cancelada || !on ? 'line-through' : 'none'}}>
            <Ico n={cancelada ? 'circle-off' : on ? 'check' : 'x'} s={14} /> {String(a.nome || 'Aluno').split(' ')[0]}{extra}
          </button>); })}
      </div>
      <div style={{fontSize:'13px', color:'#3D424C'}}><b>{nPres}</b> presente{nPres !== 1 ? 's' : ''} · <b>{nFalta}</b> falta{nFalta !== 1 ? 's' : ''}</div>
    </Modal>
  );
}

// Agrupa linhas de aulas (mesma sessao_id) em sessoes. Usado no Início e na tela Turmas.
function agruparSessoes(aulas, turmasPorId = {}) {
  const map = {};
  (aulas || []).forEach(a => {
    if (!a.sessao_id || a.status === 'remarcada') return;   // v131: a original de aula remarcada nao entra
    const s = map[a.sessao_id] || (map[a.sessao_id] = { sessao_id: a.sessao_id, turma_id: a.turma_id, turma_nome: turmasPorId[a.turma_id]?.nome || 'Turma', data: a.data, hora: a.hora, aulas: [] });
    s.aulas.push({ id: a.id, aluno_id: a.aluno_id, nome: a.nome || a.alunos?.nome || 'Aluno', status: a.status,
                   motivo_cancelamento: a.motivo_cancelamento, aviso_aluno_em: a.aviso_aluno_em, desfeita_em: a.desfeita_em,   // v131: aviso (presenca)
                   desfecho_prof: a.desfecho_prof, divergencia_em: a.divergencia_em });   // 02/10: ja' fechada pelo professor
  });
  return Object.values(map).sort((x, y) => (x.data + x.hora).localeCompare(y.data + y.hora));
}

// ------------------------------------------------------------------- tela Turmas
function Turmas({ professor, onNavigate, params }) {
  const [turmas, setTurmas] = React.useState([]);
  const [horarios, setHorarios] = React.useState({});     // turma_id -> [{dia_semana,hora}]
  const [membros, setMembros] = React.useState({});       // turma_id -> [aluno]
  const [pagMes, setPagMes] = React.useState({});         // aluno_id -> {status, valor}
  const [precoMembro, setPrecoMembro] = React.useState({}); // aluno_id -> {contrato_id, valor}
  const [sessoes, setSessoes] = React.useState([]);       // proximas/pendentes
  const [aberta, setAberta] = React.useState(null);       // turma_id expandida
  const [modalTurma, setModalTurma] = React.useState(null);   // null | 'nova' | turma
  const [modalMembros, setModalMembros] = React.useState(null);
  const [modalPresenca, setModalPresenca] = React.useState(null);
  const [linkConvite, setLinkConvite] = React.useState(null);
  const [carregando, setCarregando] = React.useState(true);
  const mesRef = new Date().toISOString().slice(0, 7);
  const hoje = new Date().toLocaleDateString('sv-SE');

  async function carregar() {
    const { data: ts } = await supabase.from('turmas').select('*').eq('professor_id', professor.id).eq('ativa', true).order('nome');
    const lista = ts || [];
    // v141: a lista de turmas so' entra na tela JUNTO com as pessoas, horarios e pagamentos (antes
    // entrava logo e cada cartao mostrava "0 pessoas" por ~2 s). Enquanto isso, o esqueleto abaixo.
    if (!lista.length) { setTurmas([]); setCarregando(false); return; }
    const ids = lista.map(t => t.id);
    const [{ data: hs }, { data: ms }, { data: au }] = await Promise.all([
      supabase.from('turma_horarios').select('turma_id, dia_semana, hora').in('turma_id', ids),
      supabase.from('alunos').select('id, nome, telefone, user_id, turma_id, convite_enviado_em').in('turma_id', ids).order('nome'),
      supabase.from('aulas').select('id, aluno_id, turma_id, sessao_id, data, hora, status, motivo_cancelamento, aviso_aluno_em, desfeita_em, desfecho_prof, divergencia_em, alunos(nome)').in('turma_id', ids)
        .gte('data', new Date(Date.now() - 7 * 864e5).toLocaleDateString('sv-SE')).lte('data', new Date(Date.now() + 7 * 864e5).toLocaleDateString('sv-SE')),
    ]);
    const H = {}; (hs || []).forEach(h => (H[h.turma_id] = H[h.turma_id] || []).push(h));
    const M = {}; (ms || []).forEach(m => (M[m.turma_id] = M[m.turma_id] || []).push(m));
    const alunoIds = (ms || []).map(m => m.id);
    const PM = {}, P = {};
    if (alunoIds.length) {
      // So' a mensalidade DA TURMA conta (pelo acordo com turma_id). Antes olhava qualquer
      // pagamento do aluno no mes — quem tinha aula individual paga aparecia como "pagou"
      // a turma sem ter pago (teste de 29/09).
      const { data: cts } = await supabase.from('contratos').select('id, aluno_id, valor_mensal').in('turma_id', ids).eq('status', 'ativo');
      (cts || []).forEach(c => { PM[c.aluno_id] = { contrato_id: c.id, valor: Number(c.valor_mensal || 0) }; });
      const ctIds = (cts || []).map(c => c.id);
      const { data: pg } = ctIds.length
        ? await supabase.from('pagamentos').select('aluno_id, contrato_id, status, valor').in('contrato_id', ctIds).eq('referencia_mes', mesRef).neq('status', 'cancelado')
        : { data: [] };
      (pg || []).forEach(p => { if (!P[p.aluno_id] || p.status === 'pago') P[p.aluno_id] = p; });
    }
    const porId = {}; lista.forEach(t => porId[t.id] = t);
    const todas = agruparSessoes((au || []).map(a => ({ ...a, nome: a.alunos?.nome })), porId);
    // pendentes (passadas com alguem em aberto) + as proximas 3
    // 02/10: aula ja' fechada pelo professor (desfecho_prof, sem divergencia) nao deixa a sessao pendente
    const pend = todas.filter(s => s.data <= hoje && s.aulas.some(a => (a.status === 'agendada' || a.status === 'em_andamento') && (!a.desfecho_prof || a.divergencia_em)));
    const prox = todas.filter(s => s.data > hoje).slice(0, 3);
    // Tudo de uma vez (o React junta estas trocas numa pintura so')
    setHorarios(H); setMembros(M); setPrecoMembro(PM); setPagMes(P);
    setSessoes([...pend.map(s => ({ ...s, pendente: true })), ...prox]);
    setTurmas(lista);
    setCarregando(false);
  }
  React.useEffect(() => { carregar(); }, [professor.id]);
  // Veio pelo atalho "+" do menu de baixo: ja' abre o formulario.
  React.useEffect(() => { if (params?.nova) setModalTurma('nova'); }, [params?.nova]);

  async function convidar(aluno) {
    let code;
    try { code = await criarConvite({ tipo: 'prof_aluno', professor_id: professor.id, aluno_id: aluno.id, dados: { pnome: professor.nome, anome: aluno.nome, telefone: aluno.telefone } }); }
    catch (e) { uso.bloqueio('convite_turma', null, null, 'falha_gerar_codigo'); uso.erro('erro.convite', e?.message || e); toastSM('Não consegui gerar o convite agora. Tente de novo.'); return; }
    uso.acao('convite.turma');
    const url = `${window.location.origin}${window.location.pathname}?c=${code}`;
    await supabase.from('alunos').update({ convite_enviado_em: new Date().toISOString() }).eq('id', aluno.id);
    const t = turmas.find(x => x.id === aluno.turma_id);
    const msg = `Oi ${aluno.nome.split(' ')[0]}! Sou ${professor.nome.split(' ')[0]}. Estou usando o Plen para organizar a turma ${t?.nome || ''} (${descreverHorariosTurma(horarios[aluno.turma_id])}). Toque no link para ver a agenda e os pagamentos:\n${blocoBaixarEConvite(url)}`;
    const fone = (aluno.telefone || '').replace(/\D/g, '');
    compartilharOuWhats(fone ? `https://wa.me/${fone.startsWith('55') ? fone : '55' + fone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, 'convite_turma');
    carregar();
  }
  async function remover(aluno) {
    if (!window.confirm(`Tirar ${aluno.nome} da turma? O histórico fica; as aulas futuras da turma somem da agenda dele.`)) return;
    const { error } = await supabase.rpc('turma_remover_membro', { p_turma: aluno.turma_id, p_aluno: aluno.id });
    if (error) { toastSM('Não foi possível: ' + error.message); return; }
    uso.acao('turma.remover_membro');
    toastSM('Pronto.'); carregar();
  }
  async function encerrarTurma(t) {
    if (!window.confirm(`Encerrar a turma ${t.nome}? As pessoas continuam como alunos, mas saem da turma.`)) return;
    uso.acao('turma.encerrar', { n: (membros[t.id] || []).length });
    for (const m of (membros[t.id] || [])) await supabase.rpc('turma_remover_membro', { p_turma: t.id, p_aluno: m.id });
    await supabase.from('turmas').update({ ativa: false }).eq('id', t.id);
    toastSM('Turma encerrada.'); carregar();
  }
  // Preco por pessoa (Marcus, 29/09): a turma tem um valor padrao, mas cada membro pode ter o
  // seu. Muda o acordo dele e a cobranca deste mes, se ainda estiver em aberto.
  async function mudarPreco(aluno) {
    const atual = precoMembro[aluno.id];
    if (!atual) { toastSM('Essa pessoa ainda não tem acordo na turma.'); return; }
    const resp = window.prompt(`Mensalidade de ${aluno.nome.split(' ')[0]} nesta turma (R$):`, formatarDinheiro(atual.valor));
    if (resp === null) return;
    const v = numDinheiro(resp);
    if (!(v > 0)) { uso.bloqueio('turma_preco', null, null, 'valor_invalido'); toastSM('Valor inválido.'); return; }
    const { error } = await supabase.from('contratos').update({ valor_mensal: v, preco_negociado: v }).eq('id', atual.contrato_id);
    if (error) { toastSM('Não foi possível: ' + error.message); return; }
    await supabase.from('pagamentos').update({ valor: v }).eq('contrato_id', atual.contrato_id).eq('referencia_mes', mesRef).eq('status', 'pendente');
    uso.acao('turma.preco');
    toastSM(`${aluno.nome.split(' ')[0]}: ${brl(v)} por mês.`); carregar();
  }
  function cobrar(aluno) {
    const p = pagMes[aluno.id];
    const fone = (aluno.telefone || '').replace(/\D/g, '');
    const mes = new Date(mesRef + '-15T12:00:00').toLocaleDateString('pt-BR', { month: 'long' });
    const msg = `Oi ${aluno.nome.split(' ')[0]}! Passando para lembrar da mensalidade de ${mes}${p?.valor ? ` (${brl(p.valor)})` : ''}. Pode pagar pelo Pix no app. Obrigado!`;
    compartilharOuWhats(fone ? `https://wa.me/${fone.startsWith('55') ? fone : '55' + fone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, 'cobranca_turma');
  }

  const chip = (t, cor, bg) => <span style={{fontSize:'11px', fontWeight:'800', color:cor, background:bg, borderRadius:'999px', padding:'3px 8px'}}>{t}</span>;

  return (
    <div className="module-page">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div><h1 className="text-2xl font-bold text-gray-800">Turmas de treino</h1><p className="text-gray-500 text-sm">Treino em grupo · cada pessoa paga a própria mensalidade</p></div>
        <button onClick={() => setModalTurma('nova')}
          style={{display:'flex', alignItems:'center', gap:'8px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', borderRadius:'12px', padding:'12px 20px', fontWeight:'800', fontSize:'14px', cursor:'pointer', whiteSpace:'nowrap'}}>
          <Ico n="users" s={16} c="white" /> Nova turma de treino
        </button>
      </div>

      {sessoes.length > 0 && (
        <Card className="mb-5">
          <div style={{fontSize:'11px', fontWeight:'800', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'10px'}}>Aulas da turma</div>
          <div style={{display:'grid', gap:'8px'}}>
            {sessoes.map(s => (
              <div key={s.sessao_id} style={{display:'flex', alignItems:'center', gap:'12px', padding:'10px 12px', borderRadius:'12px', background: s.pendente ? '#FEF2F2' : '#F7F8FA', border:'1px solid ' + (s.pendente ? '#FECACA' : '#E9EBEF')}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:'800', fontSize:'14px'}}>{s.turma_nome}</div>
                  <div style={{fontSize:'12.5px', color: s.pendente ? '#B91C1C' : '#6E7480'}}>{fmtDataBR(s.data)} · {String(s.hora).slice(0, 5)} · {s.aulas.length} pessoa{s.aulas.length !== 1 ? 's' : ''}{s.pendente ? ' · falta fechar' : ''}</div>
                </div>
                {s.pendente ? <Button onClick={() => setModalPresenca(s)} style={{padding:'8px 14px'}}>Presença</Button>
                  : chip('agendada', '#1C43AE', '#E7EDFF')}
              </div>
            ))}
          </div>
        </Card>
      )}

      {carregando && turmas.length === 0 && (
        <div style={{display:'grid', gap:'12px'}} aria-busy="true" aria-label="Carregando as turmas">
          {[0, 1].map(i => (
            <Card key={i}>
              <div className="sk-linha" style={{width:'45%', height:'14px'}} />
              <div className="sk-linha" style={{width:'70%', height:'11px', marginTop:'10px'}} />
            </Card>
          ))}
        </div>
      )}

      {!carregando && turmas.length === 0 && (
        <Card><div style={{textAlign:'center', padding:'28px 8px'}}>
          <div style={{fontSize:'40px', marginBottom:'8px'}}><Ico n="users" s={40} c="#C9CDD5" /></div>
          <div style={{fontWeight:'800', fontSize:'16px', marginBottom:'6px'}}>Nenhuma turma de treino ainda</div>
          <div style={{fontSize:'13.5px', color:'#6E7480', maxWidth:'360px', margin:'0 auto 16px'}}>Beach tennis, pedal, funcional: crie a turma com dias e horário, adicione as pessoas e cada uma recebe o próprio convite e a própria mensalidade.</div>
          <Button onClick={() => setModalTurma('nova')}>Criar a primeira turma de treino</Button>
        </div></Card>
      )}

      <div style={{display:'grid', gap:'12px'}}>
        {turmas.map(t => {
          const ms = membros[t.id] || [];
          const pagos = ms.filter(m => pagMes[m.id]?.status === 'pago').length;
          const devendo = ms.filter(m => pagMes[m.id] && pagMes[m.id].status !== 'pago');
          const open = aberta === t.id;
          return (
            <Card key={t.id} style={{padding:0}}>
              <div onClick={() => { if (!open) uso.acao('turma.expandir'); setAberta(open ? null : t.id); }} style={{display:'flex', alignItems:'center', gap:'12px', padding:'16px 18px', cursor:'pointer'}}>
                <div style={{width:'42px', height:'42px', borderRadius:'12px', background:'#E7EDFF', color:'#2F6BFF', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}><Ico n="users" s={20} /></div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:'800', fontSize:'15px'}}>{t.nome}</div>
                  <div style={{fontSize:'12.5px', color:'#6E7480'}}>{descreverHorariosTurma(horarios[t.id])}{t.local ? ' · ' + t.local : ''}</div>
                  <div style={{display:'flex', gap:'6px', marginTop:'6px', flexWrap:'wrap'}}>
                    {chip(`${ms.length} pessoa${ms.length !== 1 ? 's' : ''}`, '#3D424C', '#F0F1F4')}
                    {ms.length > 0 && chip(`${pagos} pagou`, '#067A52', '#E6F7F0')}
                    {devendo.length > 0 && chip(`${devendo.length} em aberto`, '#B45309', '#FEF3C7')}
                  </div>
                </div>
                <Ico n={open ? 'arrow-up' : 'arrow-down'} s={16} c="#9AA0AB" />
              </div>
              {open && (
                <div style={{borderTop:'1px solid #E9EBEF', padding:'12px 18px 16px'}}>
                  <div style={{display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'12px'}}>
                    <Button onClick={() => setModalMembros(t)} style={{padding:'9px 14px'}}><Ico n="user-plus" s={15} c="white" /> Adicionar pessoas</Button>
                    <button onClick={() => setModalTurma(t)} style={{border:'1.5px solid #E2E4E9', background:'white', borderRadius:'12px', padding:'9px 14px', fontWeight:'700', fontSize:'13px', cursor:'pointer'}}><Ico n="pencil" /> Editar</button>
                    {devendo.length > 0 && <button onClick={() => { uso.acao('turma.cobrar_lote', { n: devendo.length }); devendo.forEach((m, i) => setTimeout(() => cobrar(m), i * 400)); }} style={{border:'1.5px solid #FDE68A', background:'#FFFBEB', color:'#92400E', borderRadius:'12px', padding:'9px 14px', fontWeight:'700', fontSize:'13px', cursor:'pointer'}}><Ico n="brand-whatsapp" /> Cobrar {devendo.length} pendente{devendo.length > 1 ? 's' : ''}</button>}
                    <button onClick={() => encerrarTurma(t)} style={{border:'none', background:'none', color:'#DC2626', fontWeight:'700', fontSize:'13px', cursor:'pointer', marginLeft:'auto'}}>Encerrar turma</button>
                  </div>
                  {ms.length === 0 ? <div style={{fontSize:'13px', color:'#6E7480'}}>Ninguém na turma ainda.</div> : (
                    <div style={{display:'grid', gap:'6px'}}>
                      {ms.map(m => { const p = pagMes[m.id]; return (
                        <div key={m.id} style={{display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', borderRadius:'12px', background:'#F7F8FA'}}>
                          <div style={{width:'32px', height:'32px', borderRadius:'10px', background:'#E7EDFF', color:'#2F6BFF', fontWeight:'800', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>{m.nome[0]}</div>
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{fontWeight:'700', fontSize:'14px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{m.nome}</div>
                            <div style={{fontSize:'12px', color:'#6E7480', display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap'}}>
                              <button type="button" onClick={() => mudarPreco(m)} title="Mudar o valor desta pessoa"
                                style={{border:'none', background:'#F0F1F4', color:'#3D424C', borderRadius:'999px', padding:'2px 8px', fontSize:'11.5px', fontWeight:'800', cursor:'pointer'}}>
                                {precoMembro[m.id] ? brl(precoMembro[m.id].valor) + '/mês' : 'sem valor'} <Ico n="pencil" s={10} />
                              </button>
                              <span>{!m.user_id ? (m.convite_enviado_em ? 'convite enviado, ainda não entrou' : 'ainda não convidado') : (p ? (p.status === 'pago' ? `pagou ${brl(p.valor)}` : `em aberto · ${brl(p.valor)}`) : 'sem cobrança este mês')}</span>
                            </div>
                          </div>
                          {!m.user_id && <button onClick={() => convidar(m)} title="Convidar" style={{border:'none', background:'#E7EDFF', color:'#1C43AE', borderRadius:'10px', padding:'8px 10px', fontWeight:'700', fontSize:'12px', cursor:'pointer'}}><Ico n="send" /> Convidar</button>}
                          {m.user_id && p && p.status !== 'pago' && <button onClick={() => cobrar(m)} title="Cobrar" style={{border:'none', background:'#FEF3C7', color:'#92400E', borderRadius:'10px', padding:'8px 10px', fontWeight:'700', fontSize:'12px', cursor:'pointer'}}><Ico n="brand-whatsapp" /></button>}
                          <button onClick={() => remover(m)} title="Tirar da turma" style={{border:'none', background:'none', color:'#9AA0AB', cursor:'pointer', padding:'6px'}}><Ico n="x" /></button>
                        </div>); })}
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {modalTurma && <ModalTurma professor={professor} turma={modalTurma === 'nova' ? null : modalTurma} onClose={() => setModalTurma(null)}
        onSalvou={(id) => { setModalTurma(null); carregar().then(() => { setAberta(id); if (modalTurma === 'nova') setTimeout(() => setModalMembros({ id, nome: '', preco_mensal: null }), 0); }); }} />}
      {modalMembros && <ModalMembrosTurma professor={professor} turma={turmas.find(t => t.id === modalMembros.id) || modalMembros} onClose={() => setModalMembros(null)} onMudou={carregar} />}
      {modalPresenca && <ModalPresencaTurma professor={professor} sessao={modalPresenca} onClose={() => setModalPresenca(null)} onFechou={carregar} />}
    </div>
  );
}


function AlunoApp({ user }) {
  useTemaTela(false);
  const { perfil, setPerfil, setUser, setProfessor: setCtxProfessor, conviteData, setConviteData } = useApp();

  const [todosVinculos, setTodosVinculos] = React.useState([]);
  const [contextoIdx, setContextoIdx] = React.useState(0);
  const [seletorAberto, setSeletorAberto] = React.useState(false);

  const [semProfessorOk, setSemProfessorOk] = React.useState(
    () => localStorage.getItem('smSemProf_' + user.id) === '1'
  );

  const dados = todosVinculos[contextoIdx] || null;
  const prof  = dados?._professor || null;

  const [pendingConvite, setPendingConvite] = React.useState(conviteData?.pid ? conviteData : null);
  const [fichas, setFichas] = React.useState(null); // lista para "qual desses e voce?"
  const [avisoWpp, setAvisoWpp] = React.useState(null); // { texto } — fallback fora do app
  // O professor deste vinculo usa o app? Quem responde e' o servidor (Fase 0, Etapa 1):
  // prof?.user_id as vezes nao vem (depende de como o professor foi carregado) e a faixa
  // "mande no WhatsApp" aparecia para quem ja' recebe o aviso no celular. null = nao sei.
  const [profUsaApp, setProfUsaApp] = React.useState(null);
  React.useEffect(() => {
    const aid = dados?.id;
    if (!aid) { setProfUsaApp(null); return; }
    let vivo = true;
    supabase.rpc('aluno_professor_usa_app', { p_aluno_id: aid })
      .then(({ data, error }) => { if (vivo) setProfUsaApp(error ? null : (data?.tem_conta ?? null)); });
    return () => { vivo = false; };
  }, [dados?.id]);
  const [mostrarAntecipar, setMostrarAntecipar] = React.useState(false);
  const [aulas, setAulas] = React.useState([]);
  const [historico, setHistorico] = React.useState([]);
  // Turma: o aluno ve nome, horarios e os colegas. So' leitura.
  const [minhaTurma, setMinhaTurma] = React.useState(null);
  React.useEffect(() => {
    const tid = dados?.turma_id;
    if (!tid) { setMinhaTurma(null); return; }
    (async () => {
      const [{ data: t }, { data: hs }, { data: cs }] = await Promise.all([
        supabase.from('turmas').select('id, nome, modalidade, local').eq('id', tid).maybeSingle(),
        supabase.from('turma_horarios').select('dia_semana, hora').eq('turma_id', tid),
        supabase.from('alunos').select('id, nome').eq('turma_id', tid).order('nome'),
      ]);
      if (t) setMinhaTurma({ ...t, horarios: hs || [], colegas: (cs || []).filter(c => c.id !== dados?.id) });
    })();
  }, [dados?.turma_id]);
  // Feriados dos proximos 30 dias — para avisar o aluno quando uma aula dele cai num
  // (a aula continua marcada; ele decide se pede para remarcar). Marcus, 25/09.
  const [feriadosProx, setFeriadosProx] = React.useState([]);
  React.useEffect(() => {
    const hoje = new Date().toLocaleDateString('sv-SE');
    const ate = new Date(Date.now() + 30 * 86400000).toLocaleDateString('sv-SE');
    supabase.from('feriados').select('data, nome, abrangencia, cidade, professor_id').gte('data', hoje).lte('data', ate)
      .then(({ data }) => setFeriadosProx(data || [])).catch(() => {});
  }, []);
  // Aulas que ja' passaram e ninguem disse o que aconteceu. Antes elas caiam num limbo:
  // saiam de "proximas" (passaram) e nao entravam no historico (sem resposta). O aluno
  // via "2 restantes" por semanas sem saber que havia 4 aulas paradas.
  const [semResposta, setSemResposta] = React.useState([]);
  const [pagamentos, setPagamentos] = React.useState([]);
  const [tela, setTela] = React.useState('inicio');
  React.useEffect(() => { try { window.scrollTo(0, 0); const m = document.querySelector('.app-main'); if (m) m.scrollTop = 0; } catch (_) {} }, [tela]);
  const [loading, setLoading] = React.useState(true);
  const [acaoAula, setAcaoAula] = React.useState(null);
  const [reagendarMsg, setReagendarMsg] = React.useState('');
  const [motivoCancel, setMotivoCancel] = React.useState(null); // U21 — motivo escolhido pelo aluno
  const [obsCancel, setObsCancel] = React.useState('');
  const [reagendarData, setReagendarData] = React.useState('');
  const [reagendarHora, setReagendarHora] = React.useState('');
  const [solicitacoes, setSolicitacoes] = React.useState([]);   // so' pedidos DO ALUNO (sem remarcacao_prof)
  // v136: o professor remarcou e ja' vale — o aluno diz "Ok, combinado" ou "Nao consigo" (Inicio).
  // Vem 'pendente' (a faixa) e 'recusada' (o selo "Voce disse que nao consegue" na aula)
  const [remarcacoesProf, setRemarcacoesProf] = React.useState([]);
  // Outro professor te cadastrou. O link de convite continua existindo e funciona, mas
  // quem ja' esta' com o app aberto nao pode depender dele: o aviso vem sozinho. Quem
  // procura e' o ALUNO, dentro da conta dele — o professor nunca sabe se voce ja' usa o
  // Plen, entao o app nao vira maquina de descobrir quem esta' na plataforma.
  const [convitesProf, setConvitesProf] = React.useState([]);
  const [addProfessor, setAddProfessor] = React.useState(false);
  const addProfCriouRef = React.useRef(false);   // o assistente gerou convite: recarrega ao fechar
  // "Vou viajar": o aluno marca um periodo e manda UM pedido — e' o "trancamento" das
  // academias, com a linguagem mais leve da Netflix: pausar.
  const [pausa, setPausa] = React.useState(null);   // null | { inicio, fim }
  const [aulasPropostas, setAulasPropostas] = React.useState([]); // U26 — lancadas por mim, aguardando o professor
  const [novaAula, setNovaAula] = React.useState(null);           // null | { data, hora, obs }
  const [salvando, setSalvando] = React.useState(false);
  const [notifs, setNotifs] = React.useState([]);
  // v134: Pendencias do aluno (todas as fichas, de todos os professores) — numero da aba e a tela.
  const pend = usePendenciasSM(!!user?.id);
  useNumeroIconeSM(pend, 'aluno', !!user?.id);   // v148: o mesmo numero no icone do app
  const [contratoAssinado, setContratoAssinado] = React.useState(false);
  // v149 (proximo pacote): o acordo de RENOVACAO abre numa folha que se fecha (nao trava a tela); "Agora nao" esconde
  // o cartao do Inicio so' nesta visita. pixFila: o Pix da cobranca do pacote da fila aberto em Pagamentos.
  const [folhaAcordo, setFolhaAcordo] = React.useState(false);
  const [cartaoAcordoFechado, setCartaoAcordoFechado] = React.useState(null);
  const [pixFila, setPixFila] = React.useState(false);
  const [avisoRenovar, setAvisoRenovar] = React.useState(null);
  // Item D (v142): ao aceitar o convite do professor (o acordo, no portao), o aluno confirma o PROPRIO
  // WhatsApp — o professor pode ter cadastrado so' o nome. null = ainda nao mexeu (vale o da ficha).
  const [whatsAceite, setWhatsAceite] = React.useState(null);
  const [erroWhatsAceite, setErroWhatsAceite] = React.useState('');
  // Texto do acordo montado pelo BANCO (Etapa 4) + impressao digital (sha256) que vai no aceite.
  const [termos, setTermos] = React.useState(null);   // { contrato_id, texto, sha, inconsistente, carregando, erro }
  const termosReq = React.useRef(0);
  const [reajusteDesatualizado, setReajusteDesatualizado] = React.useState(null);   // id do acordo
  const [pedindoAjuste, setPedindoAjuste] = React.useState(false);
  const [textoAjuste, setTextoAjuste] = React.useState('');
  const [pixCopiado, setPixCopiado] = React.useState(false);
  const [pixCodeCopiado, setPixCodeCopiado] = React.useState(false);
  const [modalEditarPerfil, setModalEditarPerfil] = React.useState(false);
  const [convitesProfPendentes, setConvitesProfPendentes] = React.useState([]);
  // v144: todos os pedidos que o aluno mandou (tambem os ja' abertos pelo professor) — a ficha provisoria mostra o
  // combinado enquanto espera, e o acordo ajustado mostra o que mudou (dados.completado, gravado pelo banco)
  const [pedidosAluno, setPedidosAluno] = React.useState([]);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [avatarEmoji, setAvatarEmoji] = React.useState(() => localStorage.getItem(`avatar_${user.id}`) || '');

  const DIAS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

  const [creditosDB, setCreditosDB] = React.useState(null);

  async function loadContexto(alunoId) {
    if (!alunoId) return;
    // v131: "hoje" de Brasilia (o mesmo do banco). toISOString() dava o dia de Londres: depois
    // das 21h a aula de hoje saia de "proximas" e ia para "sem resposta".
    const hoje = hojeSP();
    const lidosEm = Date.now();
    supabase.from('aulas').select('*').eq('aluno_id', alunoId).eq('status', 'aguardando_confirmacao').gte('data', hoje).order('data').limit(20)
      .then(({ data }) => setAulasPropostas(data || []));
    const [{ data: proximas }, { data: passadasAbertas }, { data: hist }, { data: pags }, { data: nots }, { data: creds }, { data: solics }, { data: remarcs }, , ctsR] = await Promise.all([
      supabase.from('aulas').select('*').eq('aluno_id', alunoId).eq('status', 'agendada').gte('data', hoje).order('data').limit(200),
      supabase.from('aulas').select('*').eq('aluno_id', alunoId).eq('status', 'agendada').lt('data', hoje).order('data', { ascending: false }).limit(30),
      // Historico sem a original de uma aula remarcada (v131). Das futuras, so' saem as da pausa da
      // mensalidade (o banco ja' cancela o periodo todo); a aula futura ja' resolvida (cancelada no
      // prazo, devolvida, "conta a aula") continua aqui — o anel do pacote soma esta lista.
      // v155: 300 (eram 30) — pacote grande com canceladas cortava aulas feitas e o "Faltam N" ficava maior que o real
      supabase.from('aulas').select('*').eq('aluno_id', alunoId).not('status', 'in', '(agendada,aguardando_confirmacao,remarcada)').or(`data.lte.${hoje},motivo_cancelamento.is.null,motivo_cancelamento.neq.pausa`).order('data', { ascending: false }).limit(300),
      supabase.from('pagamentos').select('*').eq('aluno_id', alunoId).order('data_pagamento', { ascending: false }).limit(20),
      // So' o que e' PARA o aluno (Fase 0, Etapa 1): os avisos que ele mandou ao professor nao
      // aparecem mais na caixa dele nem no numero de nao lidos. Sem a coluna nova: consulta antiga.
      (async () => {
        const r = await supabase.from('notificacoes').select('*').eq('aluno_id', alunoId).eq('destinatario', 'aluno').order('created_at', { ascending: false }).limit(20);
        return r.error ? supabase.from('notificacoes').select('*').eq('aluno_id', alunoId).order('created_at', { ascending: false }).limit(20) : r;
      })(),
      supabase.from('creditos').select('total, usado').eq('aluno_id', alunoId).maybeSingle(),
      // Pedidos do aluno ("Meus pedidos", selo da aula, contraproposta). v136: a remarcacao do
      // PROFESSOR (remarcacao_prof) mora na mesma tabela mas nao e' pedido do aluno — vem a' parte,
      // so' as que esperam resposta ou que ele recusou (o app ainda confere se a aula esta' no horario).
      supabase.from('solicitacoes').select('*').eq('aluno_id', alunoId).neq('tipo', TIPO_REMARC_PROF_SM).order('criado_em', { ascending: false }).limit(30),
      supabase.from('solicitacoes').select('*').eq('aluno_id', alunoId).eq('tipo', TIPO_REMARC_PROF_SM).in('status', ['pendente', 'recusada']).order('criado_em', { ascending: false }).limit(20),
      carregarRegraNaoVeioSM(),      // v141 (B6): a regra nova vem do banco — pronta antes de desenhar as aulas
      // v155: os acordos tambem a cada atualizar — proximo pacote combinado e a virada aparecem sem fechar o app
      supabase.from('contratos').select('*, planos(*)').eq('aluno_id', alunoId).order('data_inicio', { ascending: false }).order('id'),
    ]);
    if (ctsR && !ctsR.error && Array.isArray(ctsR.data)) {
      const novos = ctsR.data;
      setTodosVinculos(prev => prev.map(v => {
        if (v.id !== alunoId) return v;
        const iguais = JSON.stringify(v.contratos || []) === JSON.stringify(novos);
        return iguais && v._contratosLidosEm ? { ...v, _contratosLidosEm: lidosEm } : { ...v, contratos: novos, _contratosLidosEm: lidosEm };
      }));
    }
    try {
      const { data: cv } = await supabase.rpc('convites_para_mim');
      setConvitesProf(cv || []);
    } catch { setConvitesProf([]); }
    // Aula de HOJE que o professor ja' registrou vai direto para a lista "confirme" do
    // aluno — senao so' apareceria amanha, e o prazo de 24h correria sem ele ver.
    const registradasHoje = (proximas || []).filter(a => a.desfecho_prof);
    setAulas((proximas || []).filter(a => !a.desfecho_prof));
    setHistorico(hist || []);
    setSemResposta([...registradasHoje, ...(passadasAbertas || [])]);
    setPagamentos(pags || []);
    setNotifs(nots || []);
    setCreditosDB(creds || null);
    setSolicitacoes((solics || []).filter(ehPedidoDoAlunoSM));
    setRemarcacoesProf(remarcs || []);
  }

  // O app do aluno carregava uma vez e ficava parado. O professor respondia, e na tela
  // dele nao mudava nada — parecia que o pedido tinha sumido no ar. Agora ele se atualiza
  // sempre que volta para a frente e a cada minuto com a tela aberta.
  React.useEffect(() => {
    const id = dados?.id;
    if (!id) return;
    const atualizar = () => { if (document.visibilityState === 'visible') loadContexto(id); };
    document.addEventListener('visibilitychange', atualizar);
    window.addEventListener('focus', atualizar);
    const t = setInterval(atualizar, 60000);
    return () => {
      document.removeEventListener('visibilitychange', atualizar);
      window.removeEventListener('focus', atualizar);
      clearInterval(t);
    };
  }, [dados?.id]);

  // O professor propos outro horario. Antes isso morria como texto: o aluno lia e nao
  // tinha o que tocar. Aceitar aqui move a aula de verdade e fecha o assunto.
  // Etapa 6 (v131): quem move a aula, fecha o pedido e avisa o professor e' o BANCO
  // (aluno_responder_contraproposta), lendo o dia e a hora do PEDIDO (nada vem do celular). Se o
  // horario ficou ocupado, o aluno e' impedido (decisao h) — sem ver o nome de ninguem.
  const travaContra = React.useRef(false);
  async function responderContraproposta(s, aceita) {
    if (travaContra.current) return;          // toque duplo nao responde duas vezes
    travaContra.current = true;
    setSalvando(true);
    const nomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
    let r = null, error = null;
    try {
      ({ data: r, error } = await supabase.rpc('aluno_responder_contraproposta', { p_solicitacao_id: s.id, p_aceita: !!aceita }));
    } catch (e) { error = e; }
    travaContra.current = false;
    setSalvando(false);
    if (error) {
      uso.erro('erro.aluno_contraproposta', error.message || String(error));
      toastSM('Não foi possível responder agora. Confira a internet e tente de novo.');
      return;
    }
    if (!r || r.erro || !r.ok) {
      const cod = (r && r.erro) || 'sem_resposta';
      uso.bloqueio('aluno_contraproposta', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      const txt = {
        horario_ocupado: `Esse horário está ocupado na agenda de ${nomeP}. Toque em "Não dá pra mim" para ${nomeP} propor outro.`,
        pedido_invalido: 'Este pedido não vale mais. A tela foi atualizada.',
        pedido_fechado: 'Este pedido já foi resolvido. A tela foi atualizada.',
        contraproposta_sem_data: `A proposta veio sem o dia novo. Peça a ${nomeP} para mandar de novo.`,
        aula_nao_agendada: 'Esta aula não está mais na agenda. A tela foi atualizada.',
        aula_com_registro: `${nomeP} já registrou o que aconteceu nesta aula — o horário não muda mais.`,
        data_invalida: `O dia proposto já passou. Peça outro horário a ${nomeP}.`,
        aluno_desvinculado: `Seu vínculo com ${nomeP} foi encerrado.`,
        sem_permissao: 'Este pedido não está na sua ficha.',
      }[cod] || 'Não foi possível responder agora. Tente de novo em instantes.';
      toastSM(txt);
      if (dados?.id) loadContexto(dados.id);
      return;
    }
    uso.acao('aluno.contraproposta', { aceitou: !!aceita, ja_feito: !!r.ja_feito });
    avisarPendenciasMudouSM();
    toastSM(r.ja_feito ? 'Você já tinha respondido este pedido.'
      : aceita ? 'Confirmado. Sua aula foi movida.'
      : 'Avisado. A aula segue no horário de sempre.');
    if (dados?.id) loadContexto(dados.id);
  }

  React.useEffect(() => {
    async function load() {
      setLoading(true);
      try {

        // NAO existe vinculo automatico por telefone. A funcao `vincular_por_telefone`
        // era chamada aqui e nunca existiu no banco — falhava em silencio e dava a
        // impressao de que bastava repetir o numero. Quem liga aluno e professor e' o
        // LINK DE CONVITE (`/?c=CODIGO` → aceitar_convite_aluno / assumir_ficha_aluno).
        // O telefone e' so' contato, para o botao de cobrar no WhatsApp.

        const { data: alunos } = await supabase
          .from('alunos')
          .select('*, contratos(*, planos(*)), professores!professor_id(*)')
          .eq('user_id', user.id)
          .order('created_at');
        if (!alunos || alunos.length === 0) { setLoading(false); return; }

        const enriquecidos = await Promise.all(alunos.map(async aluno => {
          try {
            let profData = aluno.professores;
            if (!profData && aluno.professor_id) {

              const { data: p } = await supabase.from('professores').select('*').eq('id', aluno.professor_id).maybeSingle();
              if (p) {
                profData = p;
              } else {

                const { data: pRpc } = await supabase.rpc('get_professor_do_aluno', { p_aluno_id: aluno.id });
                if (pRpc) profData = pRpc;
              }
            }
            return { ...aluno, _professor: profData };
          } catch(e) {
            console.warn('Erro ao enriquecer vínculo:', e);
            return { ...aluno, _professor: null };
          }
        }));

        const vistos = new Set();
        const unicos = enriquecidos.filter(v => {
          const chave = v.professor_id || v.id;
          if (vistos.has(chave)) return false;
          vistos.add(chave);
          return true;
        });

        const comProf = unicos.filter(v => v.professor_id);
        const semProf = unicos.filter(v => !v.professor_id);
        const ordenados = comProf.length > 0 ? [...comProf, ...semProf] : semProf;

        if (comProf.length > 0) localStorage.removeItem('smSemProf_' + user.id);

        setTodosVinculos(ordenados);
        await loadContexto(ordenados[0].id).catch(e => console.warn('Erro ao carregar contexto:', e));

        const alunoIds = unicos.map(v => v.id);
        if (alunoIds.length > 0) {
          const { data: convPend } = await supabase.from('convites')
            .select('*')
            .in('aluno_id', alunoIds)
            .eq('tipo', 'aluno_prof')
            .order('created_at', { ascending: false })
            .limit(30);
          const agoraIso = new Date().toISOString();
          setConvitesProfPendentes((convPend || []).filter(c => !c.usado_em && String(c.expires_at || '') > agoraIso));
          setPedidosAluno(convPend || []);
        }
      } catch(e) {
        console.error('Erro no carregamento do AlunoApp:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user.id, reloadKey]);

  React.useEffect(() => {
    if (todosVinculos.length === 0) return;
    const aluno = todosVinculos[contextoIdx];
    if (aluno) loadContexto(aluno.id);
    setContratoAssinado(false);   // v144: o "acabei de aceitar" e' do acordo da outra ficha (a lista ja' guarda o aceite)
  }, [contextoIdx]);

  // Etapa 5 (v131): tocou no lembrete (ou em outro aviso de aula) — o App guardou "/?aula=<id>&l=<aviso>"
  // como pendencia. Depois que a tela carregou: conta a abertura do lembrete no banco
  // (lembrete_aberto — e' assim que o Marcus mede quantos foram abertos) e abre a aula nas opcoes.
  // Uma vez so' por abertura (ref de trava); link com mais de 12h e' ignorado.
  const pendAulaFeita = React.useRef(false);
  React.useEffect(() => {
    if (loading || !dados?.id || pendAulaFeita.current) return;
    let pend = null;
    try { pend = JSON.parse(localStorage.getItem('pendingAula') || 'null'); } catch {}
    if (!pend || !pend.aula) return;
    pendAulaFeita.current = true;
    try { localStorage.removeItem('pendingAula'); } catch {}
    if (Date.now() - Number(pend.em || 0) > 12 * 3600 * 1000) return;
    (async () => {
      if (pend.l) {
        try {
          const { data: rl, error: el } = await supabase.rpc('lembrete_aberto', { p_notificacao_id: pend.l });
          if (el) uso.erro('erro.lembrete_aberto', el.message);
          else if (!rl || rl.erro || !rl.ok) uso.erro('erro.lembrete_aberto', (rl && rl.erro) || 'sem_resposta');
        } catch (e) { uso.erro('erro.lembrete_aberto', e?.message || String(e)); }
      }
      let a = null;
      try {
        const { data: au, error: ea } = await supabase.from('aulas').select('*').eq('id', pend.aula).maybeSingle();
        if (!ea) a = au || null;
      } catch {}
      // v143 (B): veio do botao [Nao vou] do aviso ("/?aula=<id>&acao=nao_vou")
      const naoVouAviso = pend.acao === 'nao_vou';
      uso.acao('aluno.aviso_aula_abrir', { lembrete: !!pend.l, achou: !!a, agendada: a?.status === 'agendada', ...(naoVouAviso ? { acao: 'nao_vou' } : {}) });
      if (!a) { setTela('aulas'); if (naoVouAviso) toastSM('Não consegui abrir esta aula agora. Confira em Aulas.'); return; }
      // aula de outro professor (o aluno tem mais de um): troca para o vinculo certo
      const idx = todosVinculos.findIndex(v => v.id === a.aluno_id);
      if (idx >= 0 && idx !== contextoIdx) setContextoIdx(idx);
      // v143 (B): [Nao vou] — o mesmo caminho do "Nao vou" do cartao (naoVouPresenca): a folha do aviso de
      // falta, no prazo ("cancelar") ou fora ("avisar"), com a consequencia ANTES de confirmar. Quem decide
      // o desenho certo e' a previa do banco (aula ja' comecou → "Esta aula já começou. O que aconteceu?").
      if (naoVouAviso) {
        if (a.status !== 'agendada') { setTela('aulas'); toastSM('Esta aula mudou. Veja como ficou em Aulas.'); return; }
        setTela('inicio');
        // ja' avisou: as opcoes da aula (a caixa amarela ja' diz "Você avisou … que não vai"; sem toast por cima)
        if (avisoVigenteAula(a)) { setAcaoAula({ aula: a, modo: 'opcoes', doAviso: true }); return; }
        naoVouPresenca(a, 'push_botao');
        return;
      }
      // v140: a pergunta "Tudo certo para a aula?" ainda aberta abre direto em [Vou] [Nao vou conseguir]
      if (a.status === 'agendada') { setTela('inicio'); setAcaoAula({ aula: a, modo: perguntaAbertaSM(a) ? 'presenca' : 'opcoes' }); }
      else setTela('aulas');   // ja' cancelada/fechada: a lista e o historico mostram como ficou
    })();
  }, [loading, dados?.id]);

  React.useEffect(() => {
    if (!conviteData?.pid) return;
    if (!dados) {

      setPendingConvite(conviteData);
      return;
    }
    // Seguranca convites (01/10): o convite e' de uma ficha que ja' e' minha (ou, no link geral,
    // ja' sou aluno desse professor) — nada a aceitar. Antes a tela "Convite recebido!" ficava
    // aberta e o "Aceitar" dava erro, porque o convite ja' tinha sido usado.
    const jaTenho = conviteData.aid
      ? todosVinculos.some(v => v.id === conviteData.aid)
      : todosVinculos.some(v => v.professor_id === conviteData.pid);
    if (jaTenho) {
      funilConvite.fim('ja_vinculado');
      try { localStorage.removeItem('pendingInviteCode'); } catch {}
      setPendingConvite(null); setConviteData(null);
      return;
    }
    if (!dados.professor_id || dados.professor_id !== conviteData.pid) {
      setPendingConvite(conviteData);
    }
  }, [conviteData, dados]);

  // Link geral (Seguranca convites, 01/10): null | 'pergunta' | 'novo' (v142: nome e WhatsApp; o 'ja_aluno' saiu)
  const [perguntaNovo, setPerguntaNovo] = React.useState(null);
  // v142 (itens D e E): quem entra pelo link geral ("Cadastrar varios de uma vez") deixa o proprio
  // WhatsApp — e' o que o professor ve' no "Esperando voce completar". A ficha nasce sem numero.
  const [whatsNovo, setWhatsNovo] = React.useState(null);
  const [erroWhatsNovo, setErroWhatsNovo] = React.useState('');
  const whatsNovoValor = () => whatsNovo !== null ? whatsNovo : (todosVinculos.map(v => v.telefone).find(x => whatsValidoSM(x)) || '');
  // Revisao v142 (achado 8): o texto do link promete "coloca seu nome" — agora tem o campo, ja' com o nome do
  // perfil (o banco le' o nome da ficha de la'). Sem nome no perfil, a ficha nascia com o comeco do e-mail.
  const [nomeNovo, setNomeNovo] = React.useState(null);
  const [erroNomeNovo, setErroNomeNovo] = React.useState('');
  const nomeNovoValor = () => nomeNovo !== null ? nomeNovo
    : String(perfil?.nome || user.user_metadata?.nome || user.user_metadata?.full_name || '').trim();
  // Revisao v142 (achado 2): "Sim, ja' me cadastrou" nao e' mais beco ("peca seu link pessoal"): entra do mesmo
  // jeito e o professor JUNTA com a ficha que ja' tinha (o aviso dele diz isso).
  const [jaCadastrado, setJaCadastrado] = React.useState(false);

  // Assume a ficha escolhida (ou a do convite) e recarrega tudo.
  async function assumirFicha(alunoId) {
    setSalvando(true);
    const { error } = await rpcAssumirFicha(pendingConvite._code, alunoId);
    if (error) { funilConvite.barrar('assumir_falhou'); uso.erro('erro.assumir_ficha', error.message); toastSM(''+ error.message); setSalvando(false); return; }
    funilConvite.passo(4, { modo: fichas && fichas.length ? 'escolheu_ficha' : 'ficha' }, true); uso.acao('aluno.convite_aceitar', { modo: 'ficha' }); uso.enviarAgora();
    localStorage.removeItem('pendingInvite');
    localStorage.removeItem('pendingInviteCode');
    setConviteData(null); setPendingConvite(null); setFichas(null);
    setSalvando(false);
    window.location.reload();
  }

  async function aceitarConvitePendente() {
    if (!pendingConvite) return;

    // Caminho novo: o codigo do convite manda. Se o convite aponta um aluno, assume aquele.
    if (pendingConvite._code) {
      if (pendingConvite.aid) return assumirFicha(pendingConvite.aid);

      // Link generico: pergunta "qual desses e voce?"
      setSalvando(true);
      const { data: lista, error: eL } = await supabase.rpc('convite_alunos_disponiveis', { p_code: pendingConvite._code });
      setSalvando(false);
      if (eL) { funilConvite.barrar('lista_fichas_falhou'); uso.erro('erro.convite_fichas', eL.message); toastSM(''+ eL.message); return; }
      // Seguranca convites (01/10): com UMA ficha na lista o app assumia sozinho, sem perguntar —
      // quem abrisse o link geral do professor caia na ficha de outra pessoa. Agora sempre pergunta.
      // E, com o banco novo, a lista do link geral so' traz a ficha com o e-mail desta conta.
      if (lista && lista.length > 0) { setFichas(lista); return; }
      // Seguranca convites (01/10): pelo link geral (o que vai para grupo) ninguem mais escolhe
      // ficha da lista — qualquer pessoa do grupo pegava a ficha de outra. Quem ja' e' aluno usa o
      // link pessoal; aqui so' entra aluno novo. Pergunta antes, para nao criar ficha repetida.
      if (pendingConvite._seguro) { setPerguntaNovo('pergunta'); return; }
      setPerguntaNovo('novo'); return;   // v142: antes de entrar, o WhatsApp (item D)
    }

    // Seguranca convites (01/10): convite sem codigo so' vinha do link antigo (?convite=...),
    // que trazia professor e ficha dentro do proprio link (dava para forjar). Nao vale mais.
    funilConvite.barrar('link_antigo'); funilConvite.sair('link_antigo');
    toastSM('Esse link de convite é antigo. Peça ao professor para mandar o link de novo.');
    localStorage.removeItem('pendingInvite');
    setConviteData(null);
    setPendingConvite(null);
  }

  // Link geral, aluno novo: cria a ficha do proprio aluno com esse professor.
  async function entrarComoAlunoNovo() {
    if (!pendingConvite?._code || salvando) return;
    const meuWhats = whatsNovoValor();
    const meuNomeNovo = nomeNovoValor().trim().slice(0, 80);
    if (!meuNomeNovo) {
      funilConvite.barrar('sem_nome');
      setErroNomeNovo(`Digite o seu nome — é como ${(pendingConvite.pnome || 'seu professor').split(' ')[0]} vai ver você na lista.`);
      return;
    }
    if (!whatsValidoSM(meuWhats)) {
      funilConvite.barrar(meuWhats.trim() ? 'whats_invalido' : 'sem_whats');
      setErroWhatsNovo(meuWhats.trim() ? 'Confira o número: DDD + número, ex.: (11) 99999-9999.'
        : `Digite o seu WhatsApp — ${(pendingConvite.pnome || 'seu professor').split(' ')[0]} usa para falar com você.`);
      return;
    }
    setSalvando(true);
    // O nome vai no perfil (o banco usa o nome do perfil na ficha nova). So' se mudou.
    if (meuNomeNovo !== String(perfil?.nome || '').trim()) {
      const { error: eP } = await supabase.from('perfis').update({ nome: meuNomeNovo }).eq('id', user.id);
      if (eP) uso.erro('erro.perfil_nome', eP.message);
      else { uso.acao('aluno.nome_link_geral', { tinha: !!String(perfil?.nome || '').trim() }); try { setPerfil && setPerfil(pf => pf ? { ...pf, nome: meuNomeNovo } : pf); } catch {} }
    }
    if (pendingConvite._seguro) {
      // Seguranca convites (01/10): o aluno nao cria mais ficha "de" um professor direto na
      // tabela (era assim que dava para mandar aviso a qualquer professor). Quem cria e' o banco,
      // conferindo o codigo do convite. So' entra aqui se o convite veio pela funcao nova
      // (ver_convite) — sinal de que o banco ja' tem a versao corrigida de aceitar_convite_aluno.
      const { data: r, error: eA } = await supabase.rpc('aceitar_convite_aluno', { p_code: pendingConvite._code });
      if (eA || !r || r.ok === false) { funilConvite.barrar('aceitar_falhou'); uso.erro('erro.aceitar_convite', eA?.message || r?.error || 'sem resposta'); toastSM('' + (eA?.message || r?.error || 'Não deu para aceitar o convite.')); setSalvando(false); return; }
      // O numero vai na ficha que o banco devolveu (o aluno so' troca o telefone da propria ficha).
      if (r.aluno_id) {
        const { error: eW } = await supabase.from('alunos').update({ telefone: meuWhats.trim() }).eq('id', r.aluno_id);
        if (eW) uso.erro('erro.whats_aluno', eW.message); else uso.acao('aluno.whats_confirmar', { origem: 'link_geral' });
        // Revisao v142 (achado 3): o professor fica sabendo (aviso no sininho e no celular dele) — antes so' via
        // se abrisse a tela Alunos. Uma vez por ficha; banco sem a v142b: segue sem o aviso.
        try {
          const { error: eAv } = await supabase.rpc('aluno_entrou_pelo_link', { p_aluno_id: r.aluno_id, p_ja_cadastrado: !!jaCadastrado });
          if (eAv && !semFuncaoBancoSM(eAv.message)) uso.erro('erro.aviso_entrou_link', eAv.message);
        } catch (e) { uso.erro('erro.aviso_entrou_link', e?.message || e); }
      }
    } else {
      // VOLTA: banco ainda sem o SQL novo — caminho antigo
      const meuNome = meuNomeNovo || (dados?.nome) || user.user_metadata?.nome || (user.email || '').split('@')[0];
      const { error: eI } = await supabase.from('alunos')
        .insert({ professor_id: pendingConvite.pid, user_id: user.id, nome: meuNome, email: user.email || '', telefone: meuWhats.trim() });
      if (eI) { funilConvite.barrar('aceitar_falhou'); uso.erro('erro.aceitar_convite', eI.message); toastSM(''+ eI.message); setSalvando(false); return; }
    }
    funilConvite.passo(4, { modo: 'novo' }, true); uso.acao('aluno.convite_aceitar', { modo: 'novo', ja_cadastrado: !!jaCadastrado }); uso.enviarAgora();
    localStorage.removeItem('pendingInviteCode');
    setConviteData(null); setPendingConvite(null); setSalvando(false);
    window.location.reload();
  }

  async function recusarConvitePendente() {
    funilConvite.sair('ignorou');
    localStorage.removeItem('pendingInvite');
    // Seguranca convites (01/10): sem isto o convite voltava a cada vez que o app abria.
    localStorage.removeItem('pendingInviteCode');
    setConviteData(null);
    setPendingConvite(null);
    setPerguntaNovo(null);
  }

  async function excluirContaAluno() {
    // LGPD: apagar todos os dados do aluno E o login (e-mail), e sair.
    // A função do banco faz tudo de uma vez; o caminho antigo abaixo fica de reserva.
    // A fila da central de uso sai ANTES: depois da exclusao o login deixa de valer.
    try { uso.evento('sessao.conta_excluida'); uso.enviarAgora(); } catch {}
    const { error: eRpc } = await supabase.rpc('excluir_minha_conta');
    if (!eRpc) {
      try { localStorage.clear(); } catch (_) {}
      await supabase.auth.signOut();
      setUser(null); setPerfil(null); setCtxProfessor(null);
      return;
    }
    console.warn('excluir_minha_conta indisponível, usando caminho antigo:', eRpc.message);
    const { data: meusAlunos } = await supabase.from('alunos').select('id').eq('user_id', user.id);
    const ids = (meusAlunos || []).map(a => a.id);
    if (ids.length > 0) {
      await supabase.from('notificacoes').delete().in('aluno_id', ids);
      await supabase.from('aulas').delete().in('aluno_id', ids);
      await supabase.from('creditos').delete().in('aluno_id', ids);
      await supabase.from('pagamentos').delete().in('aluno_id', ids);
      await supabase.from('contratos').delete().in('aluno_id', ids);
      await supabase.from('alunos').delete().eq('user_id', user.id);
    }
    await supabase.from('perfis').delete().eq('id', user.id);
    localStorage.clear();
    await supabase.auth.signOut();
    setUser(null); setPerfil(null); setCtxProfessor(null);
  }

  async function logout() {
    await sairDaConta();
    setUser(null); setPerfil(null); setCtxProfessor(null);
  }

  // v155: o acordo principal e' o individual (o da turma tambem e' 'ativo' e, mais novo, vinha primeiro na recarga)
  const contrato = dados?.contratos?.find(c => c.status === 'ativo' && !c.turma_id) || dados?.contratos?.find(c => c.status === 'ativo');
  // v149: pacote na fila (combinado, comeca depois da ultima aula do atual). O acordo a confirmar e' o dele quando
  // pendente; a tela cheia ("portao") fica so' para o PRIMEIRO acordo com o professor — renovacao vira cartao + folha.
  const proximoContrato = dados?.contratos?.find(c => c.status === 'proximo') || null;
  const primeiroAcordo = !(dados?.contratos || []).some(k => k.id !== contrato?.id && k.status !== 'proximo' && (k.status_aceite === 'aceito' || k.aceite_aluno));
  const acordoK = (contrato && contrato.status_aceite !== 'aceito' && !contratoAssinado) ? contrato
    : (proximoContrato && proximoContrato.status_aceite !== 'aceito') ? proximoContrato : null;
  // Mensalidade renovada segue no portao de antes (o cartao e a folha falam em pacote e aulas)
  // v152: o "ja' avisei que quero renovar" sobrevive a recarregar (guardado por pacote neste aparelho, com a data).
  // Revisao v152: vale 3 dias; quando o professor combina o proximo pacote, sai (se ele cancelar depois, o botao volta).
  React.useEffect(() => {
    if (!contrato?.id) return;
    const chave = 'plen_renovar_' + contrato.id;
    try {
      const [v, ts] = String(localStorage.getItem(chave) || '').split('|');
      // v155: o "ja' paguei o proximo" fica ate' ser ligado a' cobranca do proximo pacote (efeito abaixo)
      if (proximoContrato) { if (v !== 'pago') localStorage.removeItem(chave); setAvisoRenovar(null); return; }
      if (!v) return;
      // v155: "quero renovar" vale 3 dias; "ja' paguei o proximo" vale 30 (o Inicio lembra que avisou)
      if (!(Number(ts) > Date.now() - (v === 'pago' ? 30 : 3) * 24 * 3600 * 1000)) { localStorage.removeItem(chave); return; }
      setAvisoRenovar(a => a && a.contrato_id === contrato.id ? a : { contrato_id: contrato.id, jaPagou: v === 'pago' });
    } catch {}
  }, [contrato?.id, proximoContrato?.id]);
  const esquecerAvisoRenovar = () => {
    try { if (contrato?.id) localStorage.removeItem('plen_renovar_' + contrato.id); } catch {}
    setAvisoRenovar(null);
  };
  // v155: o aluno avisou "Ja' paguei o proximo" antes de o professor combinar; o professor combinou sem marcar "Ja' pagou".
  // O aviso vai para a cobranca do proximo pacote (o mesmo "Ja' paguei" de sempre) — o app nao cobra de novo.
  // Com fila: a cobranca do proximoContrato. Sem fila (renovou depois da ultima aula): o aviso ficou guardado no pacote
  // que encerrou; vai para a cobranca do pacote ativo novo nascida depois do aviso. A lembranca so' sai quando der certo.
  const avisoProximoRef = React.useRef('');
  const [avisoProximoFalhou, setAvisoProximoFalhou] = React.useState(null);   // { pgId, chave }
  React.useEffect(() => {
    if (!contrato?.id || !dados?.id) return;
    const lerChave = id => { try { return String(localStorage.getItem('plen_renovar_' + id) || '').split('|'); } catch { return []; } };
    const tirar = ch => { try { localStorage.removeItem(ch); } catch {} };
    let chave = '', pgP = null;
    if (proximoContrato?.id) {
      if (lerChave(contrato.id)[0] !== 'pago') return;
      chave = 'plen_renovar_' + contrato.id;
      pgP = (pagamentos || []).find(pg => pg.contrato_id === proximoContrato.id && pg.status !== 'cancelado');
    } else {
      for (const k of (dados.contratos || [])) {
        if (k.status !== 'encerrado' || k.id === contrato.id) continue;
        const [v, ts] = lerChave(k.id);
        if (v !== 'pago') continue;
        if (!(Number(ts) > Date.now() - 30 * 24 * 3600 * 1000)) { tirar('plen_renovar_' + k.id); continue; }
        // 10 min de folga: o relogio do aparelho pode estar adiantado
        const pg = (pagamentos || []).filter(x => x.contrato_id === contrato.id && x.status !== 'cancelado'
          && x.created_at && new Date(x.created_at).getTime() >= Number(ts) - 10 * 60 * 1000)
          .sort((x, y) => String(x.created_at).localeCompare(String(y.created_at)))[0];
        if (pg) { chave = 'plen_renovar_' + k.id; pgP = pg; break; }
      }
    }
    if (!chave || !pgP) return;
    if (pgP.status !== 'pendente' || pgP.declarado_pago_em) { tirar(chave); return; }
    if (avisoProximoRef.current === pgP.id) return;
    avisoProximoRef.current = pgP.id;
    (async () => {
      let r = null, error = null;
      try { ({ data: r, error } = await supabase.rpc('aluno_declarar_pagamento', { p_aluno_id: dados.id, p_pagamento_id: pgP.id })); } catch (e) { error = e; }
      if (error) { avisoProximoRef.current = ''; uso.erro('erro.ja_paguei_proximo', error.message || String(error)); return; }
      if (!r || r.erro || !r.ok) {
        // banco recusou: a lembranca fica e o aluno ve' o aviso com o "Ja' paguei" manual
        const cod = String((r && r.erro) || 'sem_resposta').replace(/ /g, '_');
        uso.bloqueio('aluno_ja_paguei', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'aviso_proximo');
        if (cod === 'ja_pago' || cod === 'cancelado') { tirar(chave); return; }
        setAvisoProximoFalhou({ pgId: pgP.id, chave });
        return;
      }
      tirar(chave);
      uso.acao('aluno.ja_paguei', { origem: proximoContrato?.id ? 'aviso_proximo' : 'aviso_proximo_sem_fila', n: 1, uma_cobranca: true });
      avisarPendenciasMudouSM();
      setPagamentos(prev => prev.map(p => p.id === pgP.id ? { ...p, declarado_pago_em: p.declarado_pago_em || new Date().toISOString() } : p));
    })();
  }, [contrato?.id, proximoContrato?.id, pagamentos, dados?.contratos]);
  // v155: aviso de "ja' paguei o proximo" que o banco nao aceitou — o aluno avisa de novo, na mao
  const notaAvisoFalhou = pg => (avisoProximoFalhou && pg && avisoProximoFalhou.pgId === pg.id && pg.status === 'pendente' && !pg.declarado_pago_em) ? (
    <div style={{background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'14px', padding:'12px 14px', fontSize:'13px', color:'#92400E', lineHeight:1.5, marginTop:'10px'}}>
      <Ico n="alert-triangle" c="#D97706" /> Você avisou que já pagou o próximo pacote, mas o aviso não chegou a {prof?.nome?.split(' ')[0] || 'seu professor'}.
      <button disabled={salvando} onClick={() => declararPagamentoAluno([pg.id])}
        style={{display:'block', width:'100%', marginTop:'8px', padding:'10px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontWeight:'800', fontSize:'13px', cursor: salvando ? 'not-allowed' : 'pointer'}}>
        {salvando ? 'Avisando…' : 'Já paguei — avisar de novo'}
      </button>
    </div>
  ) : null;
  // v155: o Pix do proximo pacote e o "Pagar adiantado" fecham ao sair de Pagamentos (nao voltam abertos)
  React.useEffect(() => { if (tela !== 'financeiro') { setPixFila(false); setMostrarAntecipar(false); } }, [tela]);
  const portaoAcordo = !!(acordoK && acordoK === contrato && (primeiroAcordo || contrato.tipo_cobranca === 'mensal'));
  // A cobranca do pacote da fila nunca e' "pagamento em aberto" (nem depois de vencer): o unico lugar de pagar e' o
  // bloco "Proximo pacote" em Pagamentos — evita dois Pix para a mesma divida e Pix antes do aceite (revisao v152).
  // v152: aula ja' marcada do proximo pacote (renovou antecipado) — Minhas Aulas separa com um divisor.
  const ehAulaDaFila = au => !!(proximoContrato && au && au.contrato_id === proximoContrato.id);
  // v152b (Marcus 29/10): no dia do vencimento (ultima aula do atual) a cobranca do proximo pacote ja' aceito volta
  // para "Pagamento em aberto" — no Inicio, com Pix e "Ja' paguei". Antes disso fica so' no bloco "Proximo pacote".
  // v155: cobranca de um pacote que o app ainda nao leu (combinado com o app aberto) conta como da fila ate' reler
  // os acordos (loadContexto). So' a nascida depois da ultima leitura: divida antiga de acordo fora da lista segue.
  const idsContratosAluno = new Set((dados?.contratos || []).map(k => k.id));
  const lidosEmContratos = dados?._contratosLidosEm || 0;
  const ehCobrancaDesconhecida = pg => !!(pg && pg.status === 'pendente' && pg.contrato_id && !idsContratosAluno.has(pg.contrato_id)
    && (!lidosEmContratos || !pg.created_at || new Date(pg.created_at).getTime() > lidosEmContratos));
  const ehCobrancaDaFila = pg => !!(pg && ((proximoContrato && pg.contrato_id === proximoContrato.id && pg.status === 'pendente'
    && !(proximoContrato.status_aceite === 'aceito' && pg.data_vencimento && String(pg.data_vencimento).slice(0, 10) <= hojeSP()))
    || ehCobrancaDesconhecida(pg)));
  // v156 (Marcus 29/10): a situacao do pagamento do PROXIMO pacote num selo colorido (o " · pago" no fim da linha
  // ficava escondido). Uma regra so' para o Inicio e Pagamentos: pago (verde) > a confirmar (cinza, pacote ainda nao
  // aceito) > avisou que pagou (azul) > vence hoje / venceu (vermelho) > vence dd/mm (ambar).
  const seloPgProximo = (pgP, k, curto) => {
    if (!k) return null;
    const vencP = pgP && pgP.data_vencimento ? String(pgP.data_vencimento).slice(0, 10) : '';
    if (pgP && pgP.status === 'pago') return { t: 'Pago', ic: 'circle-check', bg: '#DCF5EC', c: '#0A6F4D' };
    if (k.status_aceite !== 'aceito') return { t: 'A confirmar', ic: 'hourglass', bg: '#F0F1F4', c: '#3D424C' };
    if (!pgP) return null;
    if (pgP.declarado_pago_em) return { t: curto ? 'Você avisou' : `Você avisou que pagou · ${prof?.nome?.split(' ')[0] || 'o professor'} confirma`, ic: 'clock', bg: '#E7EDFF', c: '#1C43AE' };
    if (vencP && vencP <= hojeSP()) return { t: vencP === hojeSP() ? 'Vence hoje' : `Venceu ${fmtDiaMes(vencP)}`, ic: 'alert-circle', bg: '#FEE2E2', c: '#991B1B' };
    return { t: vencP ? `Vence ${fmtDiaMes(vencP)}` : 'Em aberto', ic: 'calendar-due', bg: '#FFFBEB', c: '#92400E' };
  };
  // dia (em Brasilia) de um data_pagamento: timestamptz vira o dia de Brasilia; 'AAAA-MM-DD' fica como esta'
  const diaPgSP = v => !v ? '' : /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? String(v) : diaSP(v);
  const SeloPg = ({ s }) => s ? (
    <span style={{display:'inline-flex', alignItems:'center', gap:'4px', background:s.bg, color:s.c, borderRadius:'20px', padding:'4px 10px', fontSize:'12px', fontWeight:'800', whiteSpace:'nowrap', flexShrink:0}}>
      <Ico n={s.ic} s={13} c={s.c} /> {s.t}
    </span>
  ) : null;
  // v153 (Marcus 29/10): pagar o proximo pacote ANTES do vencimento a um toque, daqui do Inicio
  // v155: com o pacote atual em aberto, o laranja la' de cima vem primeiro (um so' Pix)
  // v156: virou funcao — o cartao "Suas aulas" e o "Seu proximo pacote" (atual concluido) usam o mesmo botao
  const botaoPagarFilaInicio = pgP => (pgP && proximoContrato && proximoContrato.status_aceite === 'aceito' && ehCobrancaDaFila(pgP) && !pgP.declarado_pago_em && emAbertoAluno.length === 0) ? (
    <>
      <button type="button" onClick={() => { uso.acao('aluno.fila_pagar_inicio', { pix: temPixAluno }); setPixFila(temPixAluno); setTela('financeiro'); setTimeout(() => { try { document.getElementById('bloco-proximo-pacote')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }, 350); }}
        style={{width:'100%', marginTop:'10px', padding:'11px', borderRadius:'12px', border:'1.5px solid #C2D4FF', background:'#E7EDFF', color:'#2F6BFF', fontWeight:'800', fontSize:'13.5px', cursor:'pointer'}}>
        Pagar o próximo pacote adiantado
      </button>
      {!temPixAluno && (
        <div style={{fontSize:'12px', color:'#6E7480', marginTop:'6px', lineHeight:1.45}}>
          {(prof?.nome?.split(' ')[0] || 'Seu professor')} ainda não cadastrou o Pix: combine com {prof?.nome?.split(' ')[0] || 'ele'} como pagar e toque em "Já paguei".
        </div>
      )}
    </>
  ) : null;
  // v155: pacote novo ainda nao confirmado (fora do portao) nao vira "Pagamento em aberto": o cartao do acordo pede
  // "Confirme o pacote antes de pagar" — o mesmo que a pendencia e o banco dizem
  const ehCobrancaSemAceite = pg => !!(pg && pg.status === 'pendente' && acordoK && acordoK === contrato && !portaoAcordo && pg.contrato_id === contrato.id);
  const pagamentosForaDaFila = (pagamentos || []).filter(pg => !ehCobrancaDaFila(pg) && !ehCobrancaSemAceite(pg));
  // v155: aula extra gasta uma do pacote ATUAL (a ultima dele sai) — o aviso conta so' estas
  const aulasAtualExtra = (aulas || []).filter(x => !ehAulaDaFila(x));
  // v142 (revisao): ficha com professor, sem acordo nenhum (entrou pelo link geral) — esperando o professor completar
  const esperandoCompletarAluno = !!dados && !!dados.professor_id && !dados.desvinculado_em && Array.isArray(dados.contratos) && dados.contratos.length === 0;
  // v144 (Marcus 21/10): a ficha provisoria (sem professor) mostra o combinado que o aluno mandou enquanto o professor
  // nao completa — antes aparecia vazia ("Sem professor vinculado", "Nenhuma aula agendada") e o chip dizia "Professor".
  // Fechamento v144: todos os pedidos que a ficha espera (pode haver mais de um professor); cancelado ou completado sai.
  const pedidosDaFicha = id => (pedidosAluno || []).filter(c => c.aluno_id === id && !(c.dados && (c.dados.completado || c.dados.cancelado))
    && (c.usado_em || new Date(c.expires_at) > new Date()));
  const pedidoDaFicha = id => pedidosDaFicha(id)[0] || null;
  const nomeProfPedido = c => primeiroNomeSM(c && c.dados && c.dados.nomeProfDestino, 'Professor');
  const pedidosEsperando = dados && !dados.professor_id ? pedidosDaFicha(dados.id) : [];
  const pedidoEsperando = pedidosEsperando[0] || null;
  // Regra 2 (desfazer ate' o outro confirmar): o aluno cancela o pedido enquanto o professor nao completou
  const [cancelandoPedido, setCancelandoPedido] = React.useState(null);   // code do pedido na confirmacao
  const cancelarPedidoRef = React.useRef(false);
  async function cancelarPedido(c) {
    if (!c || cancelarPedidoRef.current) return;
    cancelarPedidoRef.current = true;
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('aluno_cancelar_pedido', { p_pedido: c.code })); } catch (e) { error = e; }
    cancelarPedidoRef.current = false;
    setCancelandoPedido(null);
    if (error || !data || data.ok === false) {
      const cod = error ? 'rede' : String((data && data.erro) || 'sem_resposta');
      if (cod === 'rede') uso.erro('erro.aluno_cancelar_pedido', (error && error.message) || String(error));
      else uso.bloqueio('aluno_cancelar_pedido', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      toastSM(cod === 'ja_completado' ? `${nomeProfPedido(c)} já completou o acordo — confira no app.`
        : 'Não foi possível cancelar agora. Confira a internet e tente de novo.');
      setReloadKey(k => k + 1);
      return;
    }
    uso.acao('aluno.cancelou_pedido', { aberto: !!c.usado_em, apagou: !!data.apagou_ficha });
    toastSM(`Pedido para ${nomeProfPedido(c)} cancelado.`);
    if (data.apagou_ficha && todosVinculos.length <= 1) { window.location.reload(); return; }
    if (data.apagou_ficha) setContextoIdx(0);
    setReloadKey(k => k + 1);
  }
  const plano = contrato?.planos;
  const aulasPorSemana = contrato?.aulas_por_semana || plano?.aulas_por_semana || 1;
  const duracaoMeses = plano?.duracao_meses || 1;

  // A tela "Meu Plano" lia do `planos` (a estrutura antiga, de mensalidade generica) e por
  // isso chamava pacote de "Plano Mensal" e mostrava 0 aulas por semana. Quem manda e' o
  // contrato. Se ele nao souber quantas por semana, conto os dias distintos da agenda —
  // melhor um numero certo deduzido do que um zero errado.
  const ehMensalAluno = contrato?.tipo_cobranca === 'mensal';
  // "Referente a..." da cobranca em aberto: mensalidade fala do mes; pacote fala do pacote.
  const referenteCobranca = lista => {
    // v155 (teste de 29/10): com o proximo pacote na tela (tambem R$ 1.200), "seu pacote de aulas" nao dizia qual — diz as datas
    if (!ehMensalAluno && lista.length === 1 && contrato && lista[0]?.contrato_id === contrato.id && contrato.data_inicio && contrato.data_fim)
      return `Pacote atual · ${ddmmSM(contrato.data_inicio)} a ${ddmmSM(contrato.data_fim)}`;
    if (!ehMensalAluno) return lista.length > 1 ? `${lista.length} cobranças do seu pacote` : 'Referente ao seu pacote de aulas';
    if (lista.length > 1) return `${lista.length} mensalidades em aberto`;
    const ref = lista[0]?.referencia_mes;
    return /^\d{4}-\d{2}$/.test(ref || '') ? `Mensalidade de ${nomeMesRefSM(ref)}` : 'Referente à sua mensalidade';
  };
  const diasDistintos = new Set((aulas || []).map(a => new Date(a.data + 'T12:00').getDay())).size;
  const porSemanaReal = Number(contrato?.aulas_por_semana) || diasDistintos || null;
  const tituloPlano = ehMensalAluno
    ? 'Mensalidade'
    : (Number(contrato?.total_aulas) ? `Pacote de ${contrato.total_aulas} aulas` : 'Pacote de aulas');

  const aulasAnteriores = contrato?.aulas_realizadas_anterior || 0;
  const realizadasCount = historico.filter(a => a.status === 'realizada').length;

  // formula unica do motor de creditos — identica a que o professor ve
  const aulasContratoAluno = contrato ? [...aulas, ...historico].filter(x => x.contrato_id === contrato.id) : [];
  const credAluno = calcularCreditos(contrato, aulasContratoAluno);
  const creditosUsados = credAluno.usado;
  const creditosDisponiveis = credAluno.restante;
  const creditosTotal = credAluno.total;
  const creditosPct = creditosTotal > 0 ? Math.min(1, creditosDisponiveis / creditosTotal) : 0;

  const mesAtual = hojeSP().slice(0, 7);
  // Linguagem de pacote: o que importa e' quanto ja foi pago deste pacote, nao "do mes".
  // So' os pagamentos DESTE contrato. Antes somava tudo da ficha, de qualquer epoca —
  // com dois pacotes o aluno via o dobro do que pagou. Contratos antigos (sem o vinculo
  // gravado) caem no criterio de data: pagamento a partir do inicio deste contrato.
  const pagosDoContrato = pagamentos.filter(p => {
    if (p.status !== 'pago') return false;
    if (!contrato) return true;
    if (p.contrato_id) return p.contrato_id === contrato.id;
    const ref = p.data_pagamento || p.data_vencimento || p.created_at;
    return !ref || String(ref).slice(0, 10) >= String(contrato.data_inicio).slice(0, 10);
  });
  const pagoMes = pagosDoContrato.reduce((s, p) => s + Number(p.valor || 0), 0);
  const ultimoPagamento = pagosDoContrato
    .slice()
    .sort((a, b) => String(b.data_pagamento || '').localeCompare(String(a.data_pagamento || '')))[0];
  // v156: o dia do pagamento em Brasilia (o mesmo de Pagamentos) — pago as 22h de 29/10 nao vira 30/10
  const dataUltimoPag = ultimoPagamento?.data_pagamento ? (fmtDiaMes(diaPgSP(ultimoPagamento.data_pagamento)) || null) : null;
  // v156: pacote novo que ainda NAO comecou: 0 usadas, 1a aula marcada no futuro e nenhuma aula dele antes de hoje
  // (de qualquer status — pacote em andamento com as aulas passadas desmarcadas ja' comecou) e inicio hoje ou depois
  const marcadasNaoComecou = contrato ? aulas.filter(x => x.contrato_id === contrato.id && x.status === 'agendada').map(x => String(x.data).slice(0, 10)).sort() : [];
  const primAulaNaoComecou = marcadasNaoComecou[0] || (contrato?.data_inicio ? String(contrato.data_inicio).slice(0, 10) : '');
  const pacoteNaoComecou = !!(contrato && !proximoContrato && credAluno.total > 0 && credAluno.usado === 0
    && primAulaNaoComecou && primAulaNaoComecou > hojeSP()
    && !aulasContratoAluno.some(x => x.data && String(x.data).slice(0, 10) < hojeSP())
    && !(contrato.data_inicio && String(contrato.data_inicio).slice(0, 10) < hojeSP()));

  const hojeStr = hojeSP();   // v131: dia de Brasilia (o mesmo do banco)
  // v135 (Marcus 08/10): "Proxima aula" e' a de DEPOIS de hoje. Antes era aulas[0] — a aula de hoje
  // aparecia duas vezes (no cartao "Hoje" e no "Proxima aula", as duas "aguardando confirmacao").
  const proximaAula = aulas.find(a => a.data > hojeStr) || null;
  const aulasHojeAluno = aulas.filter(a => a.data === hojeStr);
  const temAulaHoje = aulasHojeAluno.length > 0;
  // v136: so' a remarcacao que ainda vale (aula agendada no horario novo e que nao comecou)
  const remarcacoesValendo = remarcacoesProf.filter(s => s.status === 'pendente' && remarcacaoValendoSM(s, aulas.find(a => a.id === s.aula_id)));
  // ...e a que ele recusou, com a aula ainda nesse horario: o selo "Voce disse que nao consegue"
  // (sem isto a aula seguia "Aula confirmada" e parecia que o "Nao consigo" nao tinha valido)
  const naoConsigoDaAula = aulaId => remarcacoesProf.find(s => s.aula_id === aulaId && s.status === 'recusada'
    && remarcacaoValendoSM(s, aulas.find(a => a.id === aulaId))) || null;

  const dataFim = contrato?.data_fim ? new Date(contrato.data_fim + 'T12:00') : null;
  const hoje2 = new Date(); hoje2.setHours(0,0,0,0);
  const diasRestantes = dataFim ? Math.max(0, Math.ceil((dataFim - hoje2) / 86400000)) : null;

  // Sequencia de semanas com pelo menos uma aula dada (padrao Strava/Duolingo, mas em
  // fato: "4ª semana seguida"). v133 (Marcus 06/10): SO' conta quando a semana atual ja' teve
  // treino. Antes contava ate' a semana passada e dizia "2ª semana seguida treinando" no mesmo
  // dia em que o aluno tinha acabado de perder a aula da semana.
  const sequenciaSemanas = (() => {
    const chave = d => { const x = new Date(d + 'T12:00:00'); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x.toLocaleDateString('sv-SE'); };
    const semanas = new Set([...aulas, ...historico].filter(a => a.status === 'realizada' && a.data).map(a => chave(String(a.data).slice(0, 10))));
    const seg = new Date(); seg.setHours(12, 0, 0, 0); seg.setDate(seg.getDate() - ((seg.getDay() + 6) % 7));
    if (!semanas.has(seg.toLocaleDateString('sv-SE'))) return 0;
    let n = 1;
    const c = new Date(seg); c.setDate(c.getDate() - 7);
    while (semanas.has(c.toLocaleDateString('sv-SE'))) { n++; c.setDate(c.getDate() - 7); }
    return n;
  })();
  const totalFeitas = [...aulas, ...historico].filter(a => a.status === 'realizada').length;

  // Fatos do plano, em linhas — no lugar dos 4 quadrados (rotulo cortado, cor sem sentido,
  // e repetiam o que as bolinhas ja' mostram). Marcus, 25/09.
  const LinhaFato = ({ ic, rotulo, valor, alerta }) => (
    <div style={{display:'flex', alignItems:'center', gap:'10px', padding:'11px 0', borderTop:'1px solid #F0F1F4'}}>
      <Ico n={ic} s={18} c={alerta ? '#DC2626' : '#6E7480'} />
      <span style={{flex:1, fontSize:'14px', color:'#6E7480'}}>{rotulo}</span>
      <span style={{fontSize:'14.5px', fontWeight:'800', color: alerta ? '#DC2626' : '#16181D', textAlign:'right'}}>{valor}</span>
    </div>
  );

  // v133 (Marcus 06/10): aproveitamento do pacote — das aulas que ja' sairam do pacote, quantas
  // foram treino. Tocar abre a lista (details nativo: sem estado, sem hook depois de return).
  // Com aula perdida, a dica vem do PROPRIO acordo (antecedencia), nunca um numero inventado.
  const aprovPacote = ehMensalAluno ? null : aproveitamentoPacoteSM(contrato, aulasContratoAluno);
  const linhaAproveitamento = () => {
    const ap = aprovPacote;
    if (!ap) return null;
    return (
      <details style={{borderTop:'1px solid #F0F1F4'}} onToggle={e => { if (e.currentTarget.open) uso.acao('plano.aproveitamento'); }}>
        <summary style={{display:'flex', alignItems:'center', gap:'10px', padding:'11px 0', cursor:'pointer', listStyle:'none'}}>
          <Ico n="target" s={18} c="#6E7480" />
          <span style={{flex:1, fontSize:'14px', color:'#6E7480'}}>Aproveitamento</span>
          <span style={{fontSize:'14.5px', fontWeight:'800', color: corAproveitamentoSM(ap.pct), textAlign:'right'}}>
            {ap.pct}% · {ap.treinos} de {ap.usadas}
          </span>
          <Ico n="chevron-down" s={14} c="#9AA0AB" />
        </summary>
        <div style={{padding:'0 0 12px 28px'}}>
          <div style={{fontSize:'12.5px', color:'#6E7480', marginBottom:'8px', lineHeight:1.45}}>
            Das {ap.usadas} aulas que já saíram do pacote, {ap.treinos === 1 ? '1 foi treino' : `${ap.treinos} foram treino`}
            {ap.perdidas > 0 ? ` e ${ap.perdidas === 1 ? '1 foi perdida' : `${ap.perdidas} foram perdidas`}.` : '.'}
          </div>
          <div style={{fontSize:'12px', color:'#9AA0AB', marginBottom:'2px'}}>As datas de cada aula estão em "Ver as datas", logo acima.</div>
          {ap.perdidas > 0 && (
            <div style={{fontSize:'12.5px', color:'#3D424C', background:'#F7F8FA', borderRadius:'10px', padding:'8px 10px', marginTop:'8px', lineHeight:1.45}}>
              {antecedenciaH() > 0 ? `Para não perder aula: avise com ${antecedenciaH()}h de antecedência. É a regra do seu acordo.`
                : 'Para não perder aula: avise antes do horário da aula. Vocês não combinaram prazo.'}
            </div>
          )}
        </div>
      </details>
    );
  };


  const reajustePendente = contrato?.preco_reajuste_pendente;
  const precisaAceite = !!acordoK;
  React.useEffect(() => { if (precisaAceite && acordoK?.id) carregarTermos(acordoK.id); }, [acordoK?.id, precisaAceite]);
  const termosAtuais = termos && acordoK && termos.contrato_id === acordoK.id ? termos : null;

  // Cartao da proposta de reajuste (Inicio e Meu acordo). Mensalidade: compara com o valor_mensal
  // e diz "/mes" (antes dizia "/pacote" e comparava com um preco que podia estar velho).
  function cartaoReajuste() {
    if (!reajustePendente || !contrato) return null;
    const mensalR = contrato.tipo_cobranca === 'mensal';
    const atual = mensalR && Number(contrato.valor_mensal) > 0 ? Number(contrato.valor_mensal) : Number(contrato.preco_negociado || 0);
    const sufR = mensalR ? '/mês' : '/pacote';
    const desat = reajusteDesatualizado === contrato.id;
    return (
      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><span className="text-xl"><Ico n="cash-banknote" /></span><p className="font-bold text-yellow-800">Proposta de reajuste</p></div>
        <p className="text-sm text-yellow-700 mb-4">
          {prof?.nome?.split(' ')[0] || 'Seu professor'} propôs mudar {mensalR ? 'a mensalidade' : 'o valor do pacote'}: <strong>{fmtBRL(atual)}</strong> <Ico n="arrow-right" /> <strong>{fmtBRL(reajustePendente)}{sufR}</strong>
          {mensalR && <><br/><span className="text-xs">Se aceitar, o valor novo vale a partir da próxima mensalidade.</span></>}
        </p>
        {desat && (
          <div className="text-sm text-yellow-800 bg-white/70 rounded-xl p-3 mb-3">
            Esta proposta foi feita em cima de um valor antigo. Peça a {prof?.nome?.split(' ')[0] || 'seu professor'} para propor de novo — você ainda pode recusar.
          </div>
        )}
        <div className="flex gap-3">
          {!desat && <Button onClick={aceitarReajuste} disabled={salvando} variant="success" className="flex-1 justify-center"><Ico n="circle-check" c="white" /> Aceitar</Button>}
          <Button onClick={recusarReajuste} disabled={salvando} variant="danger" className="flex-1 justify-center"><Ico n="circle-x" c="#DC2626" /> Recusar</Button>
        </div>
      </div>
    );
  }

  // A aula ja' terminou? Uso a duracao do contrato (+15 min de folga) em vez de um
  // valor fixo: aula de 30 min e aula de 90 min nao acabam no mesmo tempo.
  function jaAconteceu(aula) {
    if (!aula || aula.status !== 'agendada') return false;
    const ini = inicioAulaSP(aula);   // v131: hora de Brasilia, qualquer que seja o fuso do celular
    if (!ini) return false;
    const fim = new Date(ini.getTime() + ((Number(contrato?.duracao_min) || 60) + 15) * 60000);
    return fim < new Date();
  }

  // "Nao fui": o aluno registra a falta pelo lado dele. Quem fecha continua sendo o
  // professor (e' ele quem decide se consome ou vira reposicao), mas a resposta do aluno
  // ja' aparece la' e tira a aula do limbo.
  // As duas respostas do aluno passam pela funcao do banco (aluno_responder_aula), que
  // compara com o que o professor registrou: se bate, a aula fecha na hora; se nao bate,
  // vira divergencia; se o professor ainda nao disse nada, ele e' avisado. (Consenso, 24/09.)
  // textoNaoFui (v131): o texto do "Não fui" registrado sozinho quando a aula ja' tinha comecado
  // v134 (Pendencias): origem ('pendencias') e nomeProf de fora — a lista traz aulas de TODOS os
  // professores, e o nome do professor do chip atual pode nao ser o desta aula.
  // v140: motivo do "nao aconteceu" — null/'eu_nao_fui' (falta minha) ou 'prof_nao_veio' (o professor
  // nao veio: so' 15 min depois do inicio; o professor e' avisado e, calado 24h, a aula volta ao
  // pacote). Sem motivo a chamada e' a de 2 parametros (vale no banco antigo e no novo).
  async function responderAulaAluno(aula, aconteceu, { textoNaoFui = null, origem = null, nomeProf: nomeProfFora = null, motivo = null } = {}) {
    const nomeP0 = nomeProfFora || prof?.nome?.split(' ')[0] || 'seu professor';
    const NomeP0 = nomeP0.charAt(0).toUpperCase() + nomeP0.slice(1);
    const naoVeio = !aconteceu && motivo === 'prof_nao_veio';
    setSalvando(true);
    const agora = new Date().toISOString();
    const params = { p_aula_id: aula.id, p_aconteceu: aconteceu };
    if (naoVeio) params.p_motivo = 'prof_nao_veio';
    let r = null, error = null;
    try { ({ data: r, error } = await supabase.rpc('aluno_responder_aula', params)); } catch (e) { error = e; }
    setSalvando(false);
    if (error) {
      if (semFuncaoBancoSM([error.code, error.message].join(' '))) {
        uso.bloqueio('aluno_aula_responder', null, null, 'sem_funcao');
        toastSM(`Esta versão ainda não consegue enviar isso. Atualize o app (feche e abra de novo) ou chame ${nomeP0} no WhatsApp.`);
      } else {
        uso.erro('erro.aluno_aula_responder', error.message || String(error));
        toastSM('Não foi possível registrar agora. Confira a internet e tente de novo.');
      }
      return null;
    }
    if (!r || r.erro || !r.ok) {
      const cod0 = String((r && r.erro) || 'sem_resposta');
      const cod = ({ 'aula nao encontrada': 'aula_nao_encontrada', 'sem permissao': 'sem_permissao' })[cod0] || cod0;
      uso.bloqueio('aluno_aula_responder', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      const libera = r && r.libera_em ? horaSP(r.libera_em) : '';
      toastSM(({
        // v156: "aconteceu" vale desde 30 min antes; "eu nao fui", so' depois do inicio
        // banco novo (C1) devolve libera_em: diz a hora exata; banco antigo (SQL ainda nao rodado) so' libera no horario
        aula_nao_comecou: aconteceu
          ? (libera ? `Dá para confirmar a partir das ${libera}.` : 'A aula ainda não começou no sistema. Tente de novo no horário marcado.')
          : 'A aula ainda não começou.',
        cedo_demais: `Espere${libera ? ' até ' + libera : ' uns minutos'}: atraso de alguns minutos acontece. Enquanto isso, chame ${nomeP0} no WhatsApp.`,
        avisou_falta: aula && aula.aviso_canal === 'professor_registrou'
          ? `${NomeP0} registrou que você avisou que não vinha. Se não foi isso, fale com ${nomeP0}.`
          : 'Você avisou que não ia a esta aula.',
        turma: `Em aula de turma, fale com ${nomeP0}.`,
        tarde_demais: `Já passou mais de 1 dia desta aula: para dizer que ${nomeP0} não veio, fale com ${nomeP0}.`,
        motivo_invalido: 'Escolha de novo o que aconteceu.',
        resposta_invalida: 'Escolha de novo o que aconteceu.',
        sem_permissao: 'Não encontramos esta aula na sua ficha.',
        aula_nao_encontrada: 'Não encontramos esta aula na sua ficha.',
      })[cod] || 'Não foi possível registrar agora. Tente de novo em instantes.');
      if (['avisou_falta', 'aula_nao_encontrada', 'sem_permissao'].includes(cod) && dados?.id) loadContexto(dados.id);
      return null;
    }
    // A aula ja' tinha sido desmarcada (robo de 24h, "Eu nao fui" do professor...) ou remarcada com a tela
    // desatualizada: o banco devolve so' o estado dela e NAO grava nada — nao dizer "Confirmado".
    if (!r.ja_feito && !['aguardando', 'divergencia', 'realizada', 'falta'].includes(r.estado)) {
      uso.bloqueio('aluno_aula_responder', null, null, 'aula_mudou');
      setAcaoAula(null);
      toastSM('Esta aula já foi fechada ou desmarcada. A tela foi atualizada.');
      if (dados?.id) loadContexto(dados.id);
      return null;
    }
    uso.acao('aluno.aula_responder', { resposta: aconteceu ? 'fui' : 'nao_fui', estado: r.estado, origem: origem || (acaoAula ? 'janela' : 'lista'),
      motivo: aconteceu ? 'aconteceu' : (naoVeio ? 'prof_nao_veio' : 'eu_nao_fui'), ja_feito: !!r.ja_feito });
    avisarPendenciasMudouSM();
    if (r.ja_feito) {
      setAcaoAula(null);
      toastSM('Você já tinha respondido isso.');
      if (naoVeio && avisoDoProfSM(aula) && dados?.id) loadContexto(dados.id);   // v141: o aviso pode ter caido
      return r.estado || 'aguardando';
    }
    const marca = aconteceu ? { confirmada_aluno_em: agora, falta_declarada_aluno_em: null, aluno_motivo_nao: null }
      : { falta_declarada_aluno_em: agora, confirmada_aluno_em: null, aluno_motivo_nao: naoVeio ? 'prof_nao_veio' : 'eu_nao_fui' };
    // v141 (B6): "nao avisei — ele nao veio" contra o aviso que o PROFESSOR registrou: sem decisao dele sobre o
    // aviso, o banco apagou o aviso — a tela tambem (senao o cartao seguia dizendo "Cadu registrou que voce
    // avisou" ate' a proxima recarga). Ja' decidido ("conta"), o aviso fica e a resposta vira desacordo.
    const avisoCaiu = naoVeio && avisoDoProfSM(aula) && !aula.aviso_decisao;
    if (avisoCaiu) Object.assign(marca, { aviso_aluno_em: null, aviso_canal: null, aviso_no_prazo: null, aviso_prazo_ate: null, motivo_cancelamento: null, cancelado_por: null });
    const fechou = r.estado === 'realizada' || r.estado === 'falta';
    if (fechou) {
      setSemResposta(prev => prev.filter(a => a.id !== aula.id));
      setAulas(prev => prev.filter(a => a.id !== aula.id));
    } else {
      const extra = r.estado === 'divergencia' ? { divergencia_em: agora } : {};
      setSemResposta(prev => prev.map(a => a.id === aula.id ? { ...a, ...marca, ...extra } : a));
      setAulas(prev => prev.map(a => a.id === aula.id ? { ...a, ...marca } : a));
    }
    setAcaoAula(null);
    const nomeProf = nomeProfFora || prof?.nome?.split(' ')[0] || 'seu professor';
    // aula de OUTRA ficha (Pendencias): a conta do pacote sairia com o acordo errado — frase simples
    const outraFicha = !!(aula.aluno_id && dados?.id && aula.aluno_id !== dados.id);
    // Progresso em FATO na hora certa (padrao Strava: marco concreto, nada de "Parabens!"):
    // "Aula 3 de 8 feita — faltam 5." / "4ª aula de setembro." + marcos 10, 25, 50, 100...
    // v142: "Feito · Desfazer" — A6 (aula sem registro) e A7 (discordou do registro): 'resposta'; A8 (concordou
    // e a aula fechou): 'concordou'. O professor e' avisado do desfazer so' quando a resposta o avisou.
    const alvoResp = { ref_tipo: 'aula', ref_id: aula.id, esperado: fechou ? 'concordou' : 'resposta' };
    const opResp = { papel: 'aluno', nomeOutro: nomeProf, curto: fechou ? 'sua confirmação da aula' : 'sua resposta sobre a aula' };
    if (fechou && r.estado === 'realizada' && !outraFicha) {
      try {
        const todas = [...aulas, ...historico].map(x => x.id === aula.id ? { ...x, status: 'realizada' } : x);
        const totalGeral = todas.filter(x => x.status === 'realizada').length;
        let fato;
        if (contrato && contrato.tipo_cobranca === 'mensal') {
          const mes = String(aula.data).slice(0, 7);
          const nMes = todas.filter(x => x.status === 'realizada' && String(x.data).slice(0, 7) === mes).length;
          fato = `${nMes}ª aula de ${new Date(aula.data + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'long' })} feita.`;
        } else if (contrato) {
          const c = calcularCreditos(contrato, todas.filter(x => !x.contrato_id || x.contrato_id === contrato.id));
          fato = c.total ? (c.restante === 0 ? `Pacote concluído — ${c.usado} de ${c.total} aulas feitas.` : `Aula ${c.usado} de ${c.total} feita — faltam ${c.restante}.`) : 'Aula fechada.';
        } else fato = 'Aula fechada.';
        const marco = [10, 25, 50, 75, 100, 150, 200, 300].includes(totalGeral) ? ` Sua ${totalGeral}ª aula com ${nomeProf}.` : '';
        toastDesfazerSM(fato + marco, alvoResp, opResp);
      } catch { toastDesfazerSM('Pronto — vocês dois concordaram. A aula está fechada.', alvoResp, opResp); }
      if (dados?.id) loadContexto(dados.id);
      return r.estado;
    }
    // v140: "o professor nao veio" — durante a aula ele recebe "Seu aluno está esperando"; depois, o
    // prazo de 24h (volta_ao_pacote_em). Mensalidade: fica registrado (sem reposicao automatica).
    let txtNaoVeio = null;
    if (naoVeio && r.estado !== 'divergencia' && !fechou) {
      const ini = inicioAulaSP(aula);
      const durV = (outraFicha ? 0 : Number(contrato?.duracao_min)) || 60;
      const duranteAula = !!ini && Date.now() < ini.getTime() + durV * 60000;
      const voltaEm = r.volta_ao_pacote_em ? new Date(r.volta_ao_pacote_em) : null;
      txtNaoVeio = duranteAula ? `Avisado. ${NomeP0} recebeu agora.`
        : r.pacote === false ? `Registrado. ${NomeP0} recebeu o aviso.`
        : voltaEm && !isNaN(voltaEm) ? `Registrado. ${NomeP0} tem até ${quandoSP(voltaEm)} para responder — sem resposta, a aula volta para o seu pacote.`
        : `Registrado. ${NomeP0} recebeu o aviso.`;
    }
    toastDesfazerSM(fechou ? 'Pronto — vocês dois concordaram. A aula está fechada.'
      : r.estado === 'divergencia' ? `Registrado. Ficou diferente do que ${nomeProf} marcou — a aula não sai do seu pacote até vocês acertarem.`
      : txtNaoVeio ? txtNaoVeio
      : aconteceu ? `Confirmado. ${nomeProf} foi avisado.` : (textoNaoFui || `Registrado. ${NomeP0} fecha com o motivo.`), alvoResp, opResp);
    // v141 (B6): o aviso registrado pelo professor mudou no banco — relê tudo para a tela bater com ele
    if ((fechou || (naoVeio && avisoDoProfSM(aula))) && dados?.id) loadContexto(dados.id);
    return r.estado;
  }

  async function declararFaltaAluno(aula) { return responderAulaAluno(aula, false); }

  // "Já paguei": o aluno avisa que fez o Pix. Quem dá baixa de verdade continua sendo o
  // professor — ele confere se o dinheiro caiu e confirma (mesmo padrao de
  // declararFaltaAluno acima: o aluno avisa, o professor fecha). Sem isto, o pagamento
  // ficava "pendente" tanto pra quem nunca pagou quanto pra quem ja' pagou e so' esta'
  // esperando o professor conferir — os dois apareciam do mesmo jeito pro professor.
  // Passa por uma funcao no banco (aluno_declarar_pagamento) em vez de gravar direto.
  // Duas razoes: o aluno nao tem — e nao pode ter — permissao de escrita em pagamentos
  // (senao mudaria o proprio valor ou se marcaria como pago); e a funcao so' carimba o
  // que ainda esta' em branco, entao apertar o botao duas vezes nao manda dois avisos.
  // Antes, o UPDATE direto batia na RLS, nao gravava e nao dava erro: o app achava que
  // tinha dado certo, mandava a notificacao, e no reload o botao voltava (relato do
  // Marcus em 23/09 — dava pra avisar o professor varias vezes).
  // Etapa 3: quando o aviso e' de UMA cobranca, ela vai junto (p_pagamento_id) — e' assim que vira
  // do link do WhatsApp. O banco avisa o professor certo de cada cobranca.
  async function declararPagamentoAluno(idsPendentes) {
    setSalvando(true);
    const agora = new Date().toISOString();
    // Revisao v142 (desfazer, achado 2): a referencia da faixa e' uma cobranca que ESTE toque carimbou (sem "Já
    // paguei" antes). A lista traz todas as em aberto — com a 1a ja' avisada antes, o "Desfazer" tirava o aviso
    // antigo e deixava o novo.
    const novasDoToque = (idsPendentes || []).filter(id => !((pagamentos || []).find(p => p.id === id) || {}).declarado_pago_em);
    const umaSo = (idsPendentes || []).length === 1 ? idsPendentes[0] : null;
    let r = null, error = null, semLote = false;
    try {
      // v155: com 2 ou mais, vao so' as cobrancas mostradas (p_ids) — sem id o banco marcava tambem as que a tela
      // esconde (pacote novo sem aceite, acordo que o app ainda nao leu)
      ({ data: r, error } = umaSo
        ? await supabase.rpc('aluno_declarar_pagamento', { p_aluno_id: dados.id, p_pagamento_id: umaSo })
        : await supabase.rpc('aluno_declarar_pagamentos', { p_aluno_id: dados.id, p_ids: idsPendentes }));
      // banco ainda sem a funcao nova: uma por vez (o "Desfazer" fica so' no aviso simples)
      if (!umaSo && error && (error.code === 'PGRST202' || /aluno_declarar_pagamentos/.test(error.message || ''))) {
        semLote = true; error = null; r = { ok: true, ja_avisado: true };
        for (const id of idsPendentes) {
          const x = await supabase.rpc('aluno_declarar_pagamento', { p_aluno_id: dados.id, p_pagamento_id: id });
          if (x.error) { error = x.error; break; }
          if (x.data?.erro || !x.data?.ok) { r = x.data; break; }
          if (!x.data.ja_avisado) r = { ok: true, ja_avisado: false };
        }
      }
    } catch (e) { error = e; }
    setSalvando(false);
    if (error) {
      uso.erro('erro.ja_paguei', error.message || String(error));
      toastSM('Não foi possível avisar agora. Tente de novo em instantes.');
      return;
    }
    if (r?.erro || !r?.ok) {
      const cod = String(r?.erro || 'sem_resposta').replace(/ /g, '_');
      uso.bloqueio('aluno_ja_paguei', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      if (cod === 'ja_pago') { toastSM('Esta cobrança já foi confirmada pelo seu professor. Não precisa avisar.'); loadContexto(dados.id); return; }
      if (cod === 'cancelado') { toastSM('Esta cobrança foi cancelada pelo seu professor. Não há nada a pagar nela.'); loadContexto(dados.id); return; }
      if (cod === 'sem_permissao') { toastSM('Não foi possível avisar: esta cobrança não está na sua ficha. Fale com seu professor.'); return; }
      toastSM('Não foi possível avisar agora. Tente de novo em instantes.');
      return;
    }
    uso.acao('aluno.ja_paguei', { ja_avisado: !!r?.ja_avisado, n: idsPendentes.length, uma_cobranca: !!umaSo });
    avisarPendenciasMudouSM();
    setPagamentos(prev => prev.map(p => idsPendentes.includes(p.id) ? { ...p, declarado_pago_em: p.declarado_pago_em || agora } : p));
    // v155: o "ja' paguei o proximo" que nao tinha chegado ao professor foi agora
    if (avisoProximoFalhou && idsPendentes.includes(avisoProximoFalhou.pgId)) {
      try { localStorage.removeItem(avisoProximoFalhou.chave); } catch {}
      setAvisoProximoFalhou(null);
    }
    // v142: "Feito · Desfazer" (A9) — vale para todas as cobrancas do mesmo toque (o banco acha o lote).
    if (semLote) toastSM('Pronto! Avisamos seu professor.');
    else if (r?.ja_avisado || !novasDoToque[0]) toastSM(r?.ja_avisado ? 'Seu professor já foi avisado.' : 'Pronto! Avisamos seu professor.');
    else toastDesfazerSM('Pronto! Avisamos seu professor.', { ref_tipo: 'pagamento', ref_id: novasDoToque[0], esperado: 'ja_paguei' },
      { papel: 'aluno', nomeOutro: prof?.nome, curto: '"Já paguei"' });
  }

  async function confirmarAulaAluno(aula) { return responderAulaAluno(aula, true); }

  // U26 — "combinei com o professor pelo WhatsApp": o aluno lanca, o professor confirma.
  // v131: o banco recusa horario ocupado do professor ou do aluno (por intervalo, codigo
  // 'horario_ocupado') e "a data ja' passou" pela data de Brasilia. O erro vem em frase sem
  // acento + codigo — a tela mostra a frase dela, nunca a do banco.
  const travaPropor = React.useRef(false);
  async function proporAula() {
    if (!novaAula?.data || !novaAula?.hora) { uso.bloqueio('aluno_propor_aula', null, null, 'sem_data_hora'); toastSM('Escolha o dia e a hora.'); return; }
    if (!dataValidaSM(novaAula.data)) { uso.bloqueio('aluno_propor_aula', null, null, 'data_invalida'); toastSM('Data inválida — confira o ano.'); return; }
    if (travaPropor.current) return;          // toque duplo nao lanca duas aulas
    travaPropor.current = true;
    setSalvando(true);
    let r = null, error = null;
    try {
      ({ data: r, error } = await supabase.rpc('aluno_propor_aula', { p_aluno_id: dados.id, p_data: novaAula.data, p_hora: novaAula.hora, p_obs: (novaAula.obs || '').trim() || null }));
    } catch (e) { error = e; }
    travaPropor.current = false;
    setSalvando(false);
    const nomeP = prof?.nome?.split(' ')[0] || 'seu professor';
    // v155: trava de aula repetida do banco nao e' falha de rede
    if (error && ehAulaRepetidaSM(error)) {
      uso.bloqueio('aluno_propor_aula', null, null, 'aula_mesmo_horario');
      toastSM('Você já tem aula nesse dia e horário. Não precisa lançar de novo.');
      return;
    }
    if (error) {
      uso.erro('erro.aluno_propor_aula', error.message || String(error));
      toastSM('Não foi possível lançar a aula agora. Confira a internet e tente de novo.');
      return;
    }
    if (!r || r.erro || !r.ok) {
      const cod = (r && r.codigo) || ({ 'esse horario nao esta livre': 'horario_ocupado', 'a data ja passou': 'data_passou',
        'sem permissao': 'sem_permissao', 'aluno sem professor': 'sem_professor' })[r && r.erro] || 'outro';
      uso.bloqueio('aluno_propor_aula', null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      toastSM({
        horario_ocupado: `Esse horário já está ocupado na agenda de ${nomeP}. Escolha outro.`,
        data_passou: 'Essa data já passou. Escolha outro dia.',
        sem_permissao: 'Não foi possível lançar: esta ficha não é sua.',
        sem_professor: 'Você ainda não tem professor vinculado.',
        aula_mesmo_horario: 'Você já tem aula nesse dia e horário. Não precisa lançar de novo.',
      }[cod] || 'Não foi possível lançar a aula agora. Tente de novo em instantes.');
      return;
    }
    const quando = new Date(novaAula.data + 'T12:00').toLocaleDateString('pt-BR', { weekday:'long', day:'numeric', month:'long' }) + ' às ' + novaAula.hora;
    setNovaAula(null);
    uso.acao('aluno.propor_aula_enviar');
    toastSM(`Aula adicionada. ${prof?.nome?.split(' ')[0] || 'O professor'} recebe o aviso e confirma.`);
    setAvisoWpp({ motivo: 'aula_proposta', texto: `Oi${prof?.nome ? ' ' + prof.nome.split(' ')[0] : ''}, adicionei no Plen a aula de ${quando} que combinamos. É só confirmar lá.` });
    loadContexto(dados.id);
  }

  // PALPITE do prazo (enquanto a previa do banco carrega). Mesma conta do banco (decisao e):
  // antecedencia do acordo; 0 ou vazia, a do plano; 0 ou vazia, 24h. Inicio em Brasilia.
  // Quem DECIDE e' o banco (aluno_avisar_falta) — isto so' escolhe o primeiro desenho da folha.
  // v144: acordo sem prazo combinado (cancelamento_livre) = 0h — avisou antes do horario da aula, no prazo.
  // Fecho v147 (revisao, achado 3): com a aula, vale o acordo DA AULA (aluno com dois professores: o [Nao vou] do aviso
  // chega antes de a troca de ficha valer, e o primeiro desenho da folha saia pelo acordo da ficha aberta).
  function acordoDaAula(aula) {
    const id = aula && aula.contrato_id;
    if (id && (!contrato || contrato.id !== id)) {
      for (const v of todosVinculos) { const k = (v.contratos || []).find(c => c.id === id); if (k) return k; }
    }
    return contrato;
  }
  function antecedenciaH(aula) {
    const k = aula ? acordoDaAula(aula) : contrato;
    if (k?.cancelamento_livre === true) return 0;
    const a = Number(k?.antecedencia_cancelamento_horas), b = Number(k?.planos?.antecedencia_cancelamento_horas);
    return a > 0 ? a : b > 0 ? b : 24;
  }
  function prazoPalpite(aula) {
    const ini = inicioAulaSP(aula);
    return ini ? new Date(ini.getTime() - antecedenciaH(aula) * 3600000) : null;
  }
  function podeCancelar(aula) {
    const p = prazoPalpite(aula);
    return !!p && Date.now() < p.getTime();
  }
  // v140: a consequencia do "Nao vou conseguir" ANTES de responder a pergunta do professor (pelo
  // palpite do prazo e o acordo carregado — quem decide de verdade e' o banco, no aviso de falta).
  // Fora do prazo vale o acordo: a aula conta e o professor pode devolver (decisao 3 = A).
  function consequenciaPresenca(aula) {
    const pp = prazoPalpite(aula);
    if (!pp) return '';
    const pz = quandoSP(pp);
    const kA = acordoDaAula(aula);
    const mensal = kA?.tipo_cobranca === 'mensal';
    const NomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
    if (podeCancelar(aula)) return mensal ? `Se não puder, avise até ${pz}.`
      : kA?.reposicao_permitida === false ? `Se não puder, avise até ${pz}: a aula não é descontada.`
      : `Se não puder, avise até ${pz}: a aula vai para o fim do pacote e não é descontada.`;
    return mensal ? `O prazo para desmarcar sem custo acabou ${pz}. Se você não for, fica como falta.`
      : `O prazo para desmarcar sem custo acabou ${pz}. Se você não for, a aula conta — ${NomeP} pode decidir devolver.`;
  }
  // [Nao vou] (com ou sem a pergunta do professor): o aviso de falta de sempre, no prazo ou fora — a
  // folha mostra a consequencia (pela previa do banco) ANTES de confirmar
  // v143 (B): origem 'push_botao' = o botao [Nao vou] do aviso no celular — a folha diz de que aula se trata
  // (doAviso), porque o aluno chega nela sem ter passado pelo cartao.
  function naoVouPresenca(aula, origem) {
    uso.acao('aluno.presenca_responder', { resposta: 'nao_vou', origem, sem_pergunta: !aula.presenca_pedida_em });
    setAcaoAula({ aula, modo: podeCancelar(aula) ? 'cancelar' : 'avisar', ...(origem === 'push_botao' ? { doAviso: true } : {}) });
  }
  // [✓ Vou] no cartao "Hoje" / "Proxima aula" / na folha: responde e marca na hora (o ✓ aparece sem
  // esperar a recarga). v142 (B): tambem sem a pergunta do professor; "Desfazer" na faixa e no cartao.
  const [vouBusy, setVouBusy] = React.useState(null);
  const tirarVouLocal = aulaId => setAulas(prev => prev.map(x => x.id === aulaId ? { ...x, presenca_vou_em: null } : x));
  // depois de um desfazer (faixa ou cartao), a tela recarrega do banco
  useAoDesfazerSM(() => { if (dados?.id) loadContexto(dados.id); });
  // v142 (B): o que o cartao da aula (Hoje / Proxima aula) mostra sobre o "Vou", antes do inicio:
  //  pergunta = o professor perguntou e o aluno ainda nao respondeu (v140);
  //  mostrar  = [✓ Vou] [Nao vou] — com a pergunta, ou sem ela na mesma janela (hoje/amanha, individual);
  //             nunca com pedido do aluno aberto, "Nao consigo" ou remarcacao do professor esperando;
  //  feito    = "Voce avisou que vai ✓" + "Desfazer" (A3: ate' a aula comecar).
  function presencaDoCartao(a, est) {
    const vazio = { pergunta: false, mostrar: false, feito: false };
    if (!a || a.status !== 'agendada' || est !== 'futura') return vazio;
    // v156: na meia hora antes, o principal e' "A aula comecou" — o [Vou] sai (o "Voce avisou que vai" fica)
    if (quaseComecandoSM(a)) return { pergunta: false, mostrar: false, feito: vouDesfazivelSM(a) };
    const pergunta = perguntaAbertaSM(a);
    const travada = !!pedidoDaAula(a.id) || !!naoConsigoDaAula(a.id) || remarcacoesValendo.some(s => s.aula_id === a.id);
    const acordo = contrato && contrato.id === a.contrato_id ? contrato : null;
    return { pergunta, mostrar: !travada && (pergunta || podeDizerVouSM(a, acordo)), feito: vouDesfazivelSM(a) };
  }
  const aoDesfazerVouCartao = aulaId => r => { if (r && r.ok) tirarVouLocal(aulaId); if (dados?.id) loadContexto(dados.id); };
  async function vouPresenca(aula, origem) {
    if (vouBusy) return null;
    setVouBusy(aula.id);
    let r = null;
    try {
      r = await responderPresencaAlunoSM(aula.id, { origem, nomeProf: prof?.nome?.split(' ')[0] || 'Seu professor',
        semPergunta: !aula.presenca_pedida_em, aoDesfazer: rd => { if (rd && rd.ok) tirarVouLocal(aula.id); } });
    } finally { setVouBusy(null); }
    if (r && r.ok) {
      if (r.data && !r.data.ja_mudou) setAulas(prev => prev.map(x => x.id === aula.id ? { ...x, presenca_vou_em: (r.data && r.data.vou_em) || new Date().toISOString() } : x));
      setAcaoAula(m => (m && m.aula && m.aula.id === aula.id ? null : m));
    }
    if (r && !r.rede && dados?.id) loadContexto(dados.id);
    return r;
  }
  // "O que aconteceu?" aberto (cartao Hoje / faixa "sem resposta"): chave = lugar + aula
  const [escolhaAberta, setEscolhaAberta] = React.useState(null);

  // Etapa 8 (v131): a PREVIA do "nao vou" vem do BANCO (aluno_avisar_falta com p_simular) — no
  // prazo ou fora, ate' quando ("Avise ate' qua 30/09 as 07:00"), o que acontece com a aula (fim do
  // pacote em DD/MM, reposicao, desconto em reais, nada) e a renovacao. E' o mesmo calculo que
  // vale de verdade. Pede de novo quando o motivo muda a regra (chuva/local fechado tem limite
  // no mes); feriado segue o caminho de antes (fora desta rodada) e usa a previa geral.
  const [previaAviso, setPreviaAviso] = React.useState(null);   // { aulaId, motivo, carregando, dados, erro }
  const previaReq = React.useRef(0);
  const travaAviso = React.useRef(false);
  // troca de folha feita pelo banco (nao por toque): a central de uso nao conta como janela aberta
  const modoAutoAviso = React.useRef(null);
  const emFolhaAviso = !!acaoAula && (acaoAula.modo === 'cancelar' || acaoAula.modo === 'avisar');
  const motivoPrevia = motivoCancel === 'clima' ? 'clima' : 'agenda';
  function aplicarPreviaAviso(aulaId, motivo, dadosP) {
    setPreviaAviso({ aulaId, motivo, carregando: false, dados: dadosP, erro: null });
    if (!dadosP || dadosP.ja_feito) return;
    // o banco manda: se o palpite errou (ou o prazo virou), a folha troca de desenho
    const modoCerto = dadosP.no_prazo ? 'cancelar' : 'avisar';
    setAcaoAula(m => {
      if (!(m && m.aula?.id === aulaId && (m.modo === 'cancelar' || m.modo === 'avisar') && m.modo !== modoCerto)) return m;
      modoAutoAviso.current = modoCerto;
      return { ...m, modo: modoCerto };
    });
    if (!dadosP.no_prazo) setMotivoCancel(mc => mc === 'feriado' ? null : mc);
  }
  async function carregarPreviaAviso(aulaId, motivo) {
    const n = ++previaReq.current;
    setPreviaAviso(p => ({ aulaId, motivo, carregando: true, dados: p && p.aulaId === aulaId ? p.dados : null, erro: null }));
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('aluno_avisar_falta', { p_aula_id: aulaId, p_motivo: motivo, p_simular: true })); } catch (e) { error = e; }
    if (n !== previaReq.current) return;      // chegou uma resposta mais nova
    if (error) {
      uso.erro('erro.aluno_previa_aviso', error.message || String(error));
      setPreviaAviso({ aulaId, motivo, carregando: false, dados: null, erro: 'rede' });
      return;
    }
    if (!data || data.erro || !data.ok) {
      setPreviaAviso({ aulaId, motivo, carregando: false, dados: null, erro: (data && data.erro) || 'sem_resposta' });
      return;
    }
    aplicarPreviaAviso(aulaId, motivo, data);
  }
  React.useEffect(() => {
    const aulaId = acaoAula?.aula?.id;
    if (!emFolhaAviso || !aulaId) { previaReq.current++; setPreviaAviso(null); return; }
    carregarPreviaAviso(aulaId, motivoPrevia);
  }, [emFolhaAviso, acaoAula?.aula?.id, motivoPrevia]);

  // Texto (de leigo) para cada recusa do banco. null = sem texto proprio.
  function textoErroAviso(cod) {
    const p = prof?.nome?.split(' ')[0] || 'seu professor';
    const P = prof?.nome?.split(' ')[0] || 'Seu professor';
    return ({
      nao_e_feriado: `Esse dia não está cadastrado como feriado para ${p}. Escolha outro motivo.`,
      feriado_fora_do_prazo: 'Feriado só vale dentro do prazo de aviso. Escolha outro motivo.',
      acordo_invalido: `Esta aula está ligada a um acordo que não é o seu. Fale com ${p} para corrigir.`,
      fila_ja_usada: `Esta aula já teve a compensação dela — fale com ${p}.`,
      reposicao_existente: `Esta aula já teve a compensação dela — fale com ${p}.`,
      professor_ja_registrou: `${P} já registrou o que aconteceu nesta aula. Confira na tela inicial.`,
      aula_com_registro: `${P} já registrou o que aconteceu nesta aula. Confira na tela inicial.`,
      aula_nao_agendada: 'Esta aula não está mais na agenda — pode ter sido cancelada ou remarcada.',
      aula_proposta: `Esta aula ainda espera ${p} confirmar.`,
      aluno_desvinculado: `Seu vínculo com ${p} foi encerrado — não dá para mexer nesta aula.`,
      sem_permissao: 'Não encontramos esta aula na sua ficha.',
      aula_nao_encontrada: 'Não encontramos esta aula na sua ficha.',
      motivo_invalido: 'Escolha o motivo de novo.',
    })[cod] || null;
  }
  // Quando a previa diz que nao da' para seguir: ja' avisou, a aula ja' comecou, ou uma recusa
  // do banco que nao depende do motivo. Devolve o miolo da folha (ou null = segue normal).
  function blocoPreviaBloqueia(aula) {
    const pa = previaAviso && previaAviso.aulaId === aula.id ? previaAviso : null;
    if (!pa) return null;
    const P1 = prof?.nome?.split(' ')[0] || 'Seu professor';
    const caixa = { background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'14px', padding:'12px 14px', marginBottom:'14px', fontSize:'13.5px', color:'#92400E', lineHeight:1.5 };
    const voltar = <Button variant="secondary" onClick={() => setAcaoAula(m => m ? { ...m, modo: 'opcoes' } : m)} className="flex-1 justify-center">Voltar</Button>;
    if (pa.dados && pa.dados.ja_feito) return (
      <>
        <div style={caixa}><Ico n="bell-ringing" s={14} c="#D97706" /> Você já avisou{pa.dados.aviso_em ? ` às ${horaSP(pa.dados.aviso_em)}` : ''} que não vai a esta aula. {P1} já recebeu o aviso.</div>
        <div className="flex gap-3">{voltar}</div>
      </>
    );
    // v140: a aula ja' comecou — no lugar de "Registrar que nao fui" (que gravava falta), a pergunta
    // "O que aconteceu?": [Eu nao fui] [Cadu nao veio] (o "nao veio" libera 15 min depois do inicio)
    if (pa.erro === 'aula_ja_comecou') {
      const ini = inicioAulaSP(aula);
      return (
        <>
          <div style={caixa}>Esta aula já começou. O que aconteceu?</div>
          <EscolhaNaoAconteceuSM nomeProf={P1} titulo={null} disabled={salvando}
            opcoes={aulaIndividualSM(aula) ? ['eu_nao_fui', 'nao_veio'] : ['eu_nao_fui']}
            liberaNaoVeioEm={ini ? new Date(ini.getTime() + 15 * 60000) : null}
            onEscolher={op => responderAulaAluno(aula, false, op === 'nao_veio'
              ? { motivo: 'prof_nao_veio', origem: 'folha_comecou' }
              : { origem: 'folha_comecou', textoNaoFui: `Registrado: você não foi. Pela regra, conta como falta. ${P1} fecha com o motivo.` })} />
          <div className="flex gap-3" style={{ marginTop: '10px' }}>{voltar}</div>
        </>
      );
    }
    const soMotivo = ['rede', 'sem_resposta', 'nao_e_feriado', 'feriado_fora_do_prazo', 'motivo_invalido'];
    const txt = pa.erro && !soMotivo.includes(pa.erro) ? textoErroAviso(pa.erro) : null;
    if (txt) return (
      <>
        <div style={caixa}>{txt}</div>
        <div className="flex gap-3">{voltar}</div>
      </>
    );
    return null;
  }
  function limparFolhaAviso() {
    setAcaoAula(null); setMotivoCancel(null); setObsCancel(''); setReagendarMsg(''); setPreviaAviso(null);
  }
  // Resultado do aviso no prazo, em uma frase (o que o banco gravou)
  function textoResultadoAviso(r) {
    const p = prof?.nome?.split(' ')[0] || 'seu professor';
    // v148: chuva no acordo ao ar livre
    if (r.chuva && r.beneficio === 'fila' && r.fila?.nova_data) return `Chuva não conta para ninguém: a aula foi para o fim do pacote, em ${fmtDataBR(r.fila.nova_data).slice(0, 5)}.`;
    if (r.chuva && r.beneficio === 'reposicao') return `Chuva não conta para ninguém: ficou uma reposição para combinar com ${p}.`;
    if (r.beneficio === 'fila' && r.fila?.nova_data) return `Você não perde a aula: ela foi para o fim do pacote, em ${fmtDataBR(r.fila.nova_data).slice(0, 5)}.`;
    if (r.beneficio === 'reposicao') return `Cancelada. Você não perde a aula — ficou uma reposição para combinar com ${p}.`;
    if (r.beneficio === 'desconto') return `Cancelada. ${fmtBRL(r.valor_credito)} vira desconto na próxima cobrança.`;
    if (r.beneficio === 'teto') return 'Cancelada. O limite de reposições por chuva ou local fechado deste mês já foi usado.';
    if (r.regras && (r.regras.tipo_cobranca === 'mensal' || r.regras.turma)) return 'Cancelada. Na mensalidade a aula não gera crédito.';
    return 'Cancelada.';
  }

  // "Nao vou poder ir" — no prazo (cancelar) e fora do prazo (avisar) viram UMA chamada ao banco
  // (aluno_avisar_falta). O app manda o no_prazo que o cartao MOSTROU (p_no_prazo_esperado): se o
  // prazo virou enquanto a pessoa lia, o banco nao grava nada e devolve o cartao novo. O banco faz
  // tudo de uma vez (status, fim do pacote, reposicao/desconto, pedido aberto, saldo, aviso ao
  // professor, registro) — sairam daqui o update direto, repor_aula_no_fim, o insert em
  // reposicoes e os avisos montados no celular. Depois NAO chama recalcularCreditos: usa `saldo`.
  async function avisarFalta(aula) {
    const noPrazoTela = acaoAula?.modo === 'cancelar';
    const fluxo = noPrazoTela ? 'aluno_cancelar' : 'aluno_avisar_falta';
    if (!motivoCancel) { uso.bloqueio(fluxo, null, null, 'sem_motivo'); toastSM('Escolha o motivo.'); return; }
    if (motivoCancel === 'feriado') return cancelarAulaFeriado(aula);   // caminho de antes (fora desta rodada)
    if (travaAviso.current) return;           // toque duplo nao avisa duas vezes
    travaAviso.current = true;
    setSalvando(true);
    const motivo = motivoCancel;
    const pv = previaAviso && previaAviso.aulaId === aula.id && previaAviso.dados && !previaAviso.dados.ja_feito ? previaAviso.dados : null;
    const esperado = pv ? !!pv.no_prazo : noPrazoTela;
    // o texto livre so' do que esta' na tela: "Conte em poucas palavras" (motivo Outro) e, fora
    // do prazo, "Quer explicar melhor?". Vai para a observacao da aula — nunca para o registro.
    const obs = [motivo === 'outro' ? obsCancel.trim() : '', noPrazoTela ? '' : reagendarMsg.trim()].filter(Boolean).join(' — ') || null;
    let r = null, error = null;
    try {
      ({ data: r, error } = await supabase.rpc('aluno_avisar_falta', {
        p_aula_id: aula.id, p_motivo: motivo, p_obs: obs, p_simular: false, p_no_prazo_esperado: esperado }));
    } catch (e) { error = e; }
    travaAviso.current = false;
    setSalvando(false);
    const P = prof?.nome?.split(' ')[0] || 'Seu professor';
    if (error) {
      uso.erro('erro.' + fluxo, error.message || String(error));   // erro.aluno_cancelar | erro.aluno_avisar_falta (as séries da v130)
      toastSM('Não foi possível avisar agora. Confira a internet e tente de novo.');
      return;
    }
    if (!r || r.erro || !r.ok) {
      const cod = (r && r.erro) || 'sem_resposta';
      uso.bloqueio(fluxo, null, null, /^[a-z_]{1,40}$/.test(cod) ? cod : 'outro');
      if (cod === 'prazo_mudou') {
        // nada foi gravado: o cartao novo aparece e a pessoa confirma de novo
        if (r.previa) aplicarPreviaAviso(aula.id, motivoPrevia, r.previa);
        else carregarPreviaAviso(aula.id, motivoPrevia);
        toastSM(r.no_prazo ? 'Você ainda está dentro do prazo. Confira o cartão e confirme de novo.'
          : 'O prazo para desmarcar acabou de passar. Confira o cartão e confirme de novo.');
        return;
      }
      if (cod === 'aula_ja_comecou') {
        // decisao d: aviso depois do inicio da aula nao e' mais aviso. v140: antes gravava "Nao fui"
        // sozinho; agora a folha pergunta "O que aconteceu?" ([Eu nao fui] [Cadu nao veio])
        setPreviaAviso({ aulaId: aula.id, motivo: motivoPrevia, carregando: false, dados: null, erro: 'aula_ja_comecou' });
        toastSM('A aula já começou. Diga o que aconteceu.');
        return;
      }
      if (cod === 'no_prazo_esperado_obrigatorio') {
        carregarPreviaAviso(aula.id, motivoPrevia);
        toastSM('Confira o cartão e confirme de novo.');
        return;
      }
      const txt = textoErroAviso(cod);
      if (cod === 'nao_e_feriado' || cod === 'feriado_fora_do_prazo' || cod === 'motivo_invalido') {
        setMotivoCancel(null);
        toastSM(txt);
        return;
      }
      if (['aula_nao_agendada', 'professor_ja_registrou', 'aula_com_registro', 'sem_permissao', 'aula_nao_encontrada', 'aula_proposta'].includes(cod)) {
        limparFolhaAviso();
        toastSM(txt);
        if (dados?.id) loadContexto(dados.id);
        return;
      }
      if (txt) {
        // acordo_invalido, fila_ja_usada, reposicao_existente, aluno_desvinculado: o texto fica na folha
        setPreviaAviso({ aulaId: aula.id, motivo: motivoPrevia, carregando: false, dados: null, erro: cod });
        toastSM(txt);
        return;
      }
      toastSM('Não foi possível avisar agora. Tente de novo em instantes.');
      return;
    }

    // ---- gravado pelo banco
    limparFolhaAviso();
    if (r.ja_feito) {
      toastSM(`Você já tinha avisado${r.aviso_em ? ' às ' + horaSP(r.aviso_em) : ''}. ${P} já sabe.`);
      if (dados?.id) loadContexto(dados.id);
      return;
    }
    // saldo do pacote calculado no servidor (null = mensalidade/turma: o anel nao muda)
    if (r.saldo && typeof r.saldo === 'object') setCreditosDB({ total: r.saldo.total, usado: r.saldo.usado });
    const fmtD = new Date(aula.data + 'T12:00').toLocaleDateString('pt-BR');
    if (r.no_prazo) {
      const res = { fila: 'fila', reposicao: 'reposicao', desconto: 'desconto', teto: 'teto' }[r.beneficio] || 'sem_credito';
      uso.acao('aluno.aula_cancelada', { motivo: motivoUso(motivo), resultado: res, chuva: !!r.chuva });
      setAulas(prev => prev.filter(a => a.id !== aula.id));
      // a aula cancelada vai para o historico (a lista recarrega logo abaixo)
      setHistorico(prev => [{ ...aula, status: r.status || 'cancelada_aluno', motivo_cancelamento: motivo, beneficio: r.beneficio }, ...prev]);
      // v142: "Feito · Desfazer" (A1) — ate' a aula comecar, se o horario continuar livre. Desfeito: some a
      // sugestao de avisar pelo WhatsApp (o texto do banco ja' diz "avise que vai").
      toastDesfazerSM(textoResultadoAviso(r), { ref_tipo: 'aula', ref_id: aula.id, esperado: 'cancelamento' },
        { papel: 'aluno', nomeOutro: prof?.nome, curto: 'cancelamento da aula', aoDesfazer: x => { if (x && x.ok) setAvisoWpp(null); } });
      // O sininho so' avisa quem abre o app. Ofereco o caminho que o professor de fato le'.
      setAvisoWpp({ motivo: 'cancelamento', texto: `Oi${prof?.nome ? ' ' + prof.nome.split(' ')[0] : ''}, preciso cancelar a aula de ${fmtD} às ${aula.hora?.slice(0,5)}.` });
    } else {
      uso.acao('aluno.aula_avisou_falta', { motivo: motivoUso(motivo) });
      const em = r.agora || new Date().toISOString();
      setAulas(prev => prev.map(a => a.id === aula.id
        ? { ...a, aviso_aluno_em: em, aviso_no_prazo: false, falta_declarada_aluno_em: a.falta_declarada_aluno_em || em, confirmada_aluno_em: null, motivo_cancelamento: motivo }
        : a));
      // v142: "Feito · Desfazer" (A2) — ate' a aula comecar e enquanto o professor nao decidir.
      toastDesfazerSM(r.consome ? `${P} foi avisado. Pela regra a aula conta — ele pode devolver por cortesia.` : `${P} foi avisado.`,
        { ref_tipo: 'aula', ref_id: aula.id, esperado: 'aviso' }, { papel: 'aluno', nomeOutro: prof?.nome, curto: 'aviso de que não vai' });
    }
    if (dados?.id) loadContexto(dados.id);
  }

  // U21 — feriado nao e' culpa de ninguem. FORA DESTA RODADA (o calendario de feriados esta' sendo
  // redesenhado): continua o caminho de antes, pela funcao do banco que ja' confere o feriado.
  async function cancelarAulaFeriado(aula) {
    setSalvando(true);
    const fmtD = new Date(aula.data + 'T12:00').toLocaleDateString('pt-BR');
    const { data: r, error } = await supabase.rpc('empurrar_aula_feriado', { p_aula_id: aula.id });
    if (error || r?.erro) { setSalvando(false); toastSM('Não foi possível: ' + (error?.message || r?.erro)); return; }
    uso.acao('aluno.aula_cancelada', { motivo: motivoUso('feriado'), resultado: r?.empurrada ? 'feriado_empurrada' : 'feriado_retirada' });
    await supabase.from('notificacoes').insert({ professor_id: prof?.id, aluno_id: dados.id, tipo: 'aula_empurrada',
      mensagem: `${dados.nome} avisou que a aula de ${fmtD} cai num feriado. ${r?.empurrada ? 'Ela foi para o fim do pacote.' : 'Ela saiu da agenda.'}` });
    setAulas(prev => prev.filter(a => a.id !== aula.id));
    setAcaoAula(null); setMotivoCancel(null); setObsCancel(''); setSalvando(false);
    // v142: "Feito · Desfazer" (A1, feriado) — ate' a aula comecar, se o horario continuar livre.
    toastDesfazerSM(r?.empurrada ? 'Aula empurrada para o fim do pacote. Você não perde nada.' : 'Aula retirada da agenda.',
      { ref_tipo: 'aula', ref_id: aula.id, esperado: 'cancelamento' }, { papel: 'aluno', nomeOutro: prof?.nome, curto: 'cancelamento por feriado' });
    if (dados?.id) loadContexto(dados.id);
  }

  async function solicitarReagendamento(aula) {
    if (reagendarData && !dataValidaSM(reagendarData)) { uso.bloqueio('aluno_reagendar', null, null, 'data_invalida'); toastSM('Data sugerida inválida — confira o ano.'); return; }
    if (!reagendarData && !reagendarMsg.trim()) { uso.bloqueio('aluno_reagendar', null, null, 'sem_data_nem_msg'); toastSM('Escolha um dia ou escreva o que precisa.'); return; }
    setSalvando(true);

    // De → Para, sempre nessa ordem. A mensagem antiga comecava pela aula de hoje e o
    // professor lia isso como se fosse a data nova.
    const de = `${new Date(aula.data+'T12:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})} às ${aula.hora?.slice(0,5)}`;
    const paraDia  = reagendarData ? new Date(reagendarData+'T12:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'}) : '';
    const paraHora = reagendarHora ? ` às ${reagendarHora}` : '';
    const para = paraDia ? `${paraDia}${paraHora}` : '';

    const { data: nova, error: eSolic } = await supabase.from('solicitacoes').insert({
      aluno_id: dados.id, professor_id: prof?.id, aula_id: aula.id,
      contrato_id: contrato?.id || null, tipo: 'reagendamento',
      data_sugerida: reagendarData || null,
      hora_sugerida: reagendarHora || null,
      mensagem: reagendarMsg.trim() || null,
      status: 'pendente',
    }).select().single();
    if (eSolic) { setSalvando(false); uso.erro('erro.aluno_reagendar', eSolic.message); toastSM('Não foi possível enviar: '+ eSolic.message); return; }
    uso.acao('aluno.reagendar_enviar', { com_data: !!reagendarData, com_msg: !!reagendarMsg.trim() });

    await supabase.from('notificacoes').insert({
      professor_id: prof?.id, aluno_id: dados.id, tipo: 'remarcacao_solicitada',
      mensagem: `${dados.nome} quer mudar a aula de ${de}${para ? ` para ${para}` : ''}.${reagendarMsg.trim() ? ` Observação: "${reagendarMsg.trim()}"` : ''}`,
    });

    const textoWpp =
      `Oi${prof?.nome ? ' ' + prof.nome.split(' ')[0] : ''}, pedi um reagendamento pelo Plen:\n\n` +
      `De: ${de}\n` +
      (para ? `Para: ${para}\n` : `Para: (sem dia definido — combinar)\n`) +
      (reagendarMsg.trim() ? `Obs: ${reagendarMsg.trim()}\n` : '') +
      `\nResponde aqui, é um toque: ${location.origin}/?solicitacao=${nova.id}` +
      `\n(se pedir para entrar, é a sua conta do Plen — depois o pedido abre sozinho)`;

    setSolicitacoes(prev => [nova, ...prev]);
    setReagendarMsg(''); setReagendarData(''); setReagendarHora('');
    setAcaoAula(null);
    setSalvando(false);
    toastSM('Pedido enviado. A aula fica aguardando confirmação.');
    setAvisoWpp({ motivo: 'reagendamento', texto: textoWpp });
  }

  // A aula precisa mostrar na cara que ha' um pedido em aberto. Antes ela seguia na
  // agenda como se nada tivesse sido pedido, e o aluno ficava sem saber se foi visto.
  // v136: a remarcacao do professor ('pendente' = esperando o aluno) nao e' pedido do aluno — a lista
  // ja' vem sem ela (loadContexto); o filtro aqui e' a garantia do selo "Aguardando o professor".
  function pedidoDaAula(aulaId) {
    return solicitacoes.find(s => s.aula_id === aulaId && s.status === 'pendente' && ehPedidoDoAlunoSM(s)) || null;
  }
  function SeloPedido({ aulaId, escuro }) {
    const p = pedidoDaAula(aulaId);
    if (!p) {
      // v136: o aluno disse "Nao consigo" na remarcacao do professor e a aula segue nesse horario
      if (!naoConsigoDaAula(aulaId)) return null;
      const nomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
      return (
        <span style={{display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 9px',borderRadius:'999px',
                      fontSize:'11px',fontWeight:'700',marginTop:'4px',lineHeight:1.35,
                      background: escuro ? 'rgba(255,255,255,.22)' : '#FFFBEB',
                      color: escuro ? 'white' : '#92400E',
                      border: escuro ? 'none' : '1px solid #FDE68A'}}>
          <Ico n="calendar-x" s={12} /> Você disse que não consegue · {nomeP} remarca ou desmarca
        </span>
      );
    }
    const para = p.data_sugerida
      ? `${new Date(p.data_sugerida+'T12:00').toLocaleDateString('pt-BR',{day:'numeric',month:'short'})}${p.hora_sugerida ? ' às ' + String(p.hora_sugerida).slice(0,5) : ''}`
      : 'novo dia a combinar';
    return (
      <span style={{display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 9px',borderRadius:'999px',
                    fontSize:'11px',fontWeight:'700',marginTop:'4px',
                    background: escuro ? 'rgba(255,255,255,.22)' : '#FFFBEB',
                    color: escuro ? 'white' : '#92400E',
                    border: escuro ? 'none' : '1px solid #FDE68A'}}>
        <Ico n="clock" s={12} /> Aguardando o professor · {para}
      </span>
    );
  }
  // Etapa 8 (v131): o aluno avisou que nao vai (fora do prazo — no prazo a aula sai da agenda).
  // A hora e' a OFICIAL, gravada pelo banco: "Você avisou às 22:00".
  // curto (v141, cartao Hoje): a linha de cima ja' diz "Cadu registrou que voce avisou que nao vinha" —
  // o selo fica so' com a hora ("Registrado às 22:00") em vez de repetir a frase inteira (2 linhas a 375 px).
  function SeloAviso({ aula, escuro, nomeProf, curto }) {
    const t = curto && avisoDoProfSM(aula) ? `Registrado ${quandoAvisoSM(aula)}`
      : textoAvisouAluno(aula, nomeProf || prof?.nome?.split(' ')[0] || 'O professor');
    if (!t) return null;
    return (
      <span style={{display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 9px',borderRadius:'999px',
                    fontSize:'11px',fontWeight:'700',marginTop:'4px',
                    background: escuro ? 'rgba(255,255,255,.22)' : '#FFFBEB',
                    color: escuro ? 'white' : '#92400E',
                    border: escuro ? 'none' : '1px solid #FDE68A'}}>
        <Ico n="bell-ringing" s={12} /> {t}
      </span>
    );
  }

  async function aceitarProfessorNovo(c) {
    setSalvando(true);
    const { error } = await rpcAssumirFicha(c.code, c.aluno_id);
    setSalvando(false);
    if (error) { uso.erro('erro.prof_novo_aceitar', error.message); toastSM('Não foi possível aceitar: ' + error.message); return; }
    uso.acao('aluno.prof_novo_aceitar'); uso.enviarAgora();
    toastSM(`Pronto. ${(c.professor_nome || 'Seu professor').split(' ')[0]} agora aparece aqui no app.`);
    window.location.reload();
  }

  async function recusarProfessorNovo(c) {
    uso.acao('aluno.prof_novo_recusar');
    setConvitesProf(prev => prev.filter(x => x.code !== c.code));
    toastSM('Tudo bem. Esse pedido não aparece mais.');
  }

  // v149: o pacote esta' acabando e ainda nao ha' proximo combinado — o aluno avisa que quer renovar (ou que ja' pagou o
  // proximo). Vai para o sininho do professor (tipo de mao dupla gravado pelo aluno -> destino professor) e pelo WhatsApp.
  async function avisarRenovacaoAluno(jaPagou) {
    if (!dados?.id || !prof?.id) return;
    const nP = prof?.nome?.split(' ')[0] || '';
    const valorTxt = contrato ? fmtBRL(contrato.preco_negociado || 0) : '';
    setSalvando(true);
    const { error } = await supabase.from('notificacoes').insert({
      professor_id: prof.id, aluno_id: dados.id, tipo: 'ajuste_contrato',
      mensagem: jaPagou ? `${dados.nome} avisou que já pagou o próximo pacote (${valorTxt}). Combine o próximo pacote na ficha dele e marque "Já pagou".`
        : `${dados.nome} quer renovar o pacote. Combine o próximo pacote na ficha dele.`,
      // v152: o pedido de renovacao leva o contrato — o professor abre direto "Combinar proximo pacote" daquele aluno
      // (tambem no "ja' paguei o proximo": o modal ja' abre com "Ja' pagou" marcado)
      ...(contrato?.id ? { ref_tipo: 'contrato', ref_id: contrato.id } : {}),
    });
    setSalvando(false);
    if (error) { uso.erro('erro.aviso_renovar', error.message); toastSM('Não foi possível avisar agora. Tente de novo em instantes.'); return; }
    uso.acao('aluno.avisar_renovacao', { ja_pagou: !!jaPagou });
    setAvisoRenovar({ contrato_id: contrato?.id, jaPagou: !!jaPagou });
    try { if (contrato?.id) localStorage.setItem('plen_renovar_' + contrato.id, (jaPagou ? 'pago' : 'renovar') + '|' + Date.now()); } catch {}
    toastSM(`Avisado. ${nP || 'Seu professor'} combina o próximo pacote e ele aparece aqui.`);
    setAvisoWpp({ motivo: 'renovar', texto: jaPagou
      ? `Oi${nP ? ' ' + nP : ''}, já paguei o próximo pacote (${valorTxt}). Pode combinar no Plen? Aí ele começa depois da minha última aula.`
      : `Oi${nP ? ' ' + nP : ''}, quero renovar meu pacote. Pode combinar o próximo no Plen?` });
  }

  async function pedirAjusteContrato() {
    const txt = textoAjuste.trim();
    if (!txt) return;
    setSalvando(true);
    const { error } = await supabase.from('solicitacoes').insert({
      aluno_id: dados.id, professor_id: prof?.id, contrato_id: (acordoK || contrato)?.id,
      tipo: 'ajuste_contrato', mensagem: txt, status: 'pendente',
    });
    if (error) { setSalvando(false); toastSM('Não foi possível enviar: ' + error.message); return; }
    await supabase.from('notificacoes').insert({
      professor_id: prof?.id, aluno_id: dados.id, tipo: 'ajuste_contrato',
      mensagem: `${dados.nome} pediu uma correção no acordo: "${txt}"`,
    });
    setSalvando(false); setPedindoAjuste(false); setTextoAjuste('');
    uso.acao('aluno.acordo_ajuste_enviar');
    toastSM('Enviado. Seu professor vai corrigir e mandar de novo.');
    // O sininho so' avisa quem abre o app; o WhatsApp e' o canal que o professor de fato le'.
    setAvisoWpp({ motivo: 'ajuste_acordo', texto:
      `Oi${prof?.nome ? ' ' + prof.nome.split(' ')[0] : ''}, recebi o acordo no Plen mas tem algo diferente do que a gente combinou:\n\n` +
      `${txt}\n\nDá uma olhada e me manda de novo? Aí eu aceito na hora.` });
  }

  async function pedirPausa() {
    if (!pausa?.inicio || !pausa?.fim || pausa.fim < pausa.inicio) { uso.bloqueio('aluno_pausa', null, null, 'datas_invalidas'); toastSM('Escolha o começo e o fim da viagem.'); return; }
    const afetadas = aulas.filter(a => a.data >= pausa.inicio && a.data <= pausa.fim);
    setSalvando(true);
    const fmt = d => new Date(d + 'T12:00').toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
    const { data: nova, error } = await supabase.from('solicitacoes').insert({
      aluno_id: dados.id, professor_id: prof?.id, contrato_id: contrato?.id || null,
      tipo: 'pausa', periodo_inicio: pausa.inicio, periodo_fim: pausa.fim,
      mensagem: `Vou pausar de ${fmt(pausa.inicio)} a ${fmt(pausa.fim)} — ${afetadas.length} aula${afetadas.length === 1 ? '' : 's'} nesse período.`,
      status: 'pendente',
    }).select().single();
    if (error) { setSalvando(false); toastSM('Não foi possível enviar: ' + error.message); return; }
    await supabase.from('notificacoes').insert({
      professor_id: prof?.id, aluno_id: dados.id, tipo: 'pausa_solicitada',
      mensagem: `${dados.nome} avisou que vai pausar de ${fmt(pausa.inicio)} a ${fmt(pausa.fim)} (${afetadas.length} aula${afetadas.length === 1 ? '' : 's'}).`,
    });
    setSolicitacoes(prev => [nova, ...prev]);
    setSalvando(false); setPausa(null);
    uso.acao('aluno.pausa_enviar', { n_aulas: afetadas.length });
    toastSM('Avisado. Seu professor decide como fica o pacote.');
    setAvisoWpp({ motivo: 'pausa', texto:
      `Oi${prof?.nome ? ' ' + prof.nome.split(' ')[0] : ''}, avisei pelo Plen: vou ficar fora de ${fmt(pausa.inicio)} a ${fmt(pausa.fim)} ` +
      `(${afetadas.length} aula${afetadas.length === 1 ? '' : 's'}). Pode pausar meu pacote nesse período?` });
  }

  async function desistirSolicitacao(s) {
    uso.acao('aluno.pedido_retirar', { tipo: s.tipo });
    await supabase.from('solicitacoes').update({ status: 'cancelada', respondida_em: new Date().toISOString() }).eq('id', s.id);
    setSolicitacoes(prev => prev.map(x => x.id === s.id ? { ...x, status: 'cancelada' } : x));
    toastSM('Pedido retirado.');
  }

  // Muda campos do acordo ativo so' na tela (depois que o banco confirmou).
  function atualizarContratoLocal(campos, idAcordo) {
    if (!contrato && !idAcordo) return;
    const cid = idAcordo || contrato.id;
    setTodosVinculos(prev => prev.map((v, i) => i !== contextoIdx ? v : ({
      ...v,
      contratos: (v.contratos || []).map(c => c.id === cid ? { ...c, ...campos } : c)
    })));
  }
  // Rele' o acordo ativo do banco (o professor pode ter mudado a proposta ou o valor).
  async function recarregarContrato() {
    if (!contrato) return;
    const { data, error } = await supabase.from('contratos').select('*, planos(*)').eq('id', contrato.id).maybeSingle();
    if (!error && data) atualizarContratoLocal(data);
  }

  // Etapa 4: o aluno so' diz sim/nao — o preco novo vem do BANCO (a proposta do professor) e o
  // app manda o valor que MOSTROU (p_valor_visto). Quem avisa o professor e' a funcao.
  async function responderReajuste(aceita) {
    if (!contrato || !reajustePendente) return;
    const nomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
    const sufR = contrato.tipo_cobranca === 'mensal' ? '/mês' : '/pacote';
    setSalvando(true);
    let data = null, error = null;
    try {
      ({ data, error } = await supabase.rpc('aluno_responder_reajuste',
        { p_contrato_id: contrato.id, p_aceita: !!aceita, p_valor_visto: Number(reajustePendente) }));
    } catch (e) { error = e; }
    setSalvando(false);
    if (error) { uso.erro('erro.reajuste_responder', error.message || String(error)); toastSM('Não foi possível responder agora. Tente de novo em instantes.'); return; }
    const cod = data?.erro;
    if (cod || !data?.ok) {
      const c = cod || 'sem_resposta';
      uso.bloqueio('aluno_reajuste', null, null, /^[a-z_]{1,40}$/.test(c) ? c : 'outro');
      if (c === 'reajuste_mudou') {
        const novo = Number(data.preco_reajuste_pendente);
        atualizarContratoLocal({ preco_reajuste_pendente: novo });
        toastSM(`${nomeP} mudou a proposta: o novo valor é ${fmtBRL(novo)}${sufR}. Confira e responda de novo.`);
        return;
      }
      if (c === 'reajuste_desatualizado') {
        setReajusteDesatualizado(contrato.id);
        recarregarContrato();
        toastSM(`Esta proposta foi feita em cima de um valor antigo${Number(data.valor_mensal) > 0 ? ` (hoje você paga ${fmtBRL(data.valor_mensal)}/mês)` : ''}. Peça a ${nomeP} para propor de novo — ou recuse.`);
        return;
      }
      if (c === 'sem_reajuste') {
        atualizarContratoLocal({ preco_reajuste_pendente: null });
        recarregarContrato();
        toastSM('Esta proposta não está mais valendo — ela já foi respondida ou retirada.');
        return;
      }
      if (c === 'valor_obrigatorio') { recarregarContrato(); toastSM('Não consegui conferir o valor proposto. A tela foi atualizada — responda de novo.'); return; }
      if (c === 'contrato_inativo') { toastSM('Este acordo não está mais ativo.'); recarregarContrato(); return; }
      if (c === 'sem_permissao') { toastSM('Não foi possível responder: este acordo não está na sua ficha.'); return; }
      toastSM('Não foi possível responder agora. Tente de novo em instantes.');
      return;
    }
    atualizarContratoLocal({ preco_negociado: data.preco_negociado, valor_mensal: data.valor_mensal, preco_reajuste_pendente: null });
    setReajusteDesatualizado(null);
    uso.acao('aluno.reajuste', { aceitou: !!aceita });
    avisarPendenciasMudouSM();
    toastSM(aceita ? 'Reajuste aceito! Obrigado.' : 'Reajuste recusado. Seu professor será notificado.');
  }
  function aceitarReajuste() { return responderReajuste(true); }
  function recusarReajuste() { return responderReajuste(false); }

  // Etapa 4: o texto do acordo vem do BANCO (termos_do_contrato_json) junto com a impressao
  // digital (sha256). O aceite manda essa impressao: se o professor mexeu no acordo enquanto o
  // aluno lia, o banco recusa e o app mostra o texto novo. (montarTermos, que montava no celular, saiu.)
  async function carregarTermos(cid) {
    const id = cid || contrato?.id;
    if (!id) return;
    const n = ++termosReq.current;
    setTermos(t => ({ ...(t && t.contrato_id === id ? t : {}), contrato_id: id, carregando: true, erro: null }));
    let data = null, error = null;
    try { ({ data, error } = await supabase.rpc('termos_do_contrato_json', { p_contrato_id: id })); } catch (e) { error = e; }
    if (n !== termosReq.current) return;
    if (error || !data || data.erro || !data.texto || !data.termos_sha256) {
      if (error) uso.erro('erro.acordo_termos', error.message || String(error));
      else uso.bloqueio('aluno_acordo', null, null, data?.erro === 'sem_permissao' ? 'sem_permissao' : 'sem_texto');
      setTermos({ contrato_id: id, carregando: false, erro: data?.erro || 'falha', texto: null, sha: null, inconsistente: false });
      return;
    }
    setTermos({ contrato_id: id, carregando: false, erro: null, texto: data.texto, sha: data.termos_sha256, inconsistente: !!data.acordo_inconsistente });
    if (data.aceito) {   // ja' aceito no banco (outro aparelho): libera a tela
      if (id === contrato?.id) setContratoAssinado(true);   // v149: o aceite do pacote da fila nao e' o do acordo atual
      setTodosVinculos(prev => prev.map(v => ({ ...v, contratos: (v.contratos || []).map(c => c.id === id ? { ...c, status_aceite: 'aceito', aceite_aluno: true } : c) })));
    }
  }

  // Revisao v142 (codigo, achado 5): trava de toque duplo — o telefone e' gravado ANTES do aceite e, nesse meio
  // tempo, "Aceito as condicoes" seguia ativo (dois aceites, dois "Acordo confirmado!").
  const aceitandoRef = React.useRef(false);
  async function assinarContrato(opcoes) {
    if (!(acordoK || contrato) || aceitandoRef.current) return;
    aceitandoRef.current = true;
    try { await assinarContratoMiolo(opcoes); } finally { aceitandoRef.current = false; }
  }
  async function assinarContratoMiolo(opcoes) {
    const comWhats = !!(opcoes && opcoes.comWhats);   // so' o portao mostra o campo "Seu WhatsApp"
    const alvoK = acordoK || contrato;   // v149: pode ser o acordo do pacote da fila
    const t = termos && termos.contrato_id === alvoK.id ? termos : null;
    if (!t || !t.sha) {
      toastSM(t?.carregando ? 'Carregando o texto do acordo…' : 'Não consegui carregar o texto do acordo. Tentando de novo…');
      if (!t?.carregando) carregarTermos(alvoK.id);
      return;
    }
    if (t.inconsistente) { toastSM('Este acordo ainda não pode ser confirmado: peça ao professor para revisar o valor.'); return; }
    if (comWhats) {
      const w = whatsDoAceite();
      if ((whatsAceiteObrigatorio || w.trim()) && !whatsValidoSM(w)) {
        uso.bloqueio('aluno_acordo', null, null, w.trim() ? 'whats_invalido' : 'sem_whats');
        setErroWhatsAceite(w.trim() ? 'Confira o número: DDD + número, ex.: (11) 99999-9999.' : `Digite o seu WhatsApp — ${prof?.nome?.split(' ')[0] || 'seu professor'} usa para falar com você.`);
        return;
      }
      // Grava na ficha DESTE professor (o aluno so' troca o telefone da propria ficha), so' se mudou.
      if (whatsValidoSM(w) && dados?.id && !mesmoWhatsSM(w, dados.telefone)) {
        setSalvando(true);   // o botao ja' fica "Confirmando…" enquanto o numero grava
        const { error: eW } = await supabase.from('alunos').update({ telefone: w.trim() }).eq('id', dados.id);
        if (eW) uso.erro('erro.whats_aluno', eW.message);
        else {
          uso.acao('aluno.whats_confirmar', { origem: 'acordo', tinha: !!digitosWhatsSM(dados.telefone) });
          setTodosVinculos(vs => vs.map(v => v.id === dados.id ? { ...v, telefone: w.trim() } : v));
        }
      }
    }
    setSalvando(true);
    let data = null, error = null;
    try {
      ({ data, error } = await supabase.rpc('aluno_aceitar_contrato', { p_contrato_id: alvoK.id, p_termos_sha256: t.sha }));
    } catch (e) { error = e; }
    setSalvando(false);
    if (error) { uso.erro('erro.acordo_aceitar', error.message || String(error)); toastSM('Não foi possível confirmar agora. Tente de novo em instantes.'); return; }
    const cod = data?.erro;
    if (cod || !data?.ok) {
      const c = cod || 'sem_resposta';
      uso.bloqueio('aluno_acordo', null, null, /^[a-z_]{1,40}$/.test(c) ? c : 'outro');
      if (c === 'termos_mudaram') {
        await carregarTermos(alvoK.id);
        toastSM(`${prof?.nome?.split(' ')[0] || 'Seu professor'} mudou o acordo enquanto você lia. Confira o texto novo e confirme de novo.`);
        return;
      }
      if (c === 'sha_obrigatorio') {
        await carregarTermos(alvoK.id);
        toastSM('Não consegui conferir o texto do acordo. Ele foi carregado de novo — confira e confirme.');
        return;
      }
      if (c === 'acordo_inconsistente') {
        setTermos(x => (x && x.contrato_id === alvoK.id) ? { ...x, inconsistente: true } : x);
        toastSM('As cobranças lançadas passam do total deste acordo. Peça ao professor para revisar o valor.');
        return;
      }
      if (c === 'contrato_inativo') { toastSM('Este acordo não está mais ativo. Seu professor pode ter trocado o plano — atualize a tela.'); return; }
      if (c === 'sem_permissao') { toastSM('Não foi possível confirmar: este acordo não está na sua ficha. Fale com seu professor.'); return; }
      toastSM('Não foi possível confirmar agora. Tente de novo em instantes.');
      return;
    }
    // A funcao ja' avisou o professor ('contrato_assinado') — aqui nao insere aviso.
    uso.acao('aluno.acordo_aceitar', { origem: telaUso === 'aluno.acordo' ? 'portao' : 'aba', ja_feito: !!data.ja_feito });
    avisarPendenciasMudouSM();
    const agoraISO = new Date().toISOString();
    if (alvoK.id === contrato?.id) setContratoAssinado(true);
    atualizarContratoLocal({ aceite_aluno: true, status_aceite: 'aceito', data_aceite: agoraISO, termos_aceitos: t.texto }, alvoK.id);
    setFolhaAcordo(false);
    toastSM(portaoAcordo ? 'Acordo confirmado! Bem-vindo.' : 'Pacote confirmado.');
  }

  function copiarPix() {
    const chave = prof?.pix_chave || '';
    if (!chave) return;
    navigator.clipboard.writeText(chave).then(() => { uso.acao('aluno.pix_copiar_chave'); setPixCopiado(true); setTimeout(() => setPixCopiado(false), 2500); }, (e) => uso.erro('erro.copiar', e?.message || e));
  }

  // "Como pagar" (src/regras/cobranca.js, Etapa 3): o QR cobra o valor EM ABERTO (nao o preco do
  // acordo — desconto de reposicao ou valor corrigido aparecem); sem cobranca em aberto, o valor do
  // proximo pagamento (mensalidade: valor_mensal). Leva o codigo da cobranca (txid).
  // v155: o que o aluno ja' avisou que pagou ("Ja' paguei") nao entra na soma, no QR nem no laranja — fica a' parte
  const emAbertoAluno = pagamentosForaDaFila.filter(pg => pg.status === 'pendente' && !pg.declarado_pago_em);
  const avisadasAluno = pagamentosForaDaFila.filter(pg => pg.status === 'pendente' && pg.declarado_pago_em);
  const chaveAbertas = emAbertoAluno.map(pg => pg.id + ':' + pg.valor).join('|');
  const pagar = React.useMemo(() => comoPagar(emAbertoAluno, prof, contrato),
    [chaveAbertas, prof?.pix_chave, prof?.pix_tipo, prof?.nome, contrato?.id, contrato?.tipo_cobranca, contrato?.valor_mensal, contrato?.preco_negociado, contrato?.forma_recebimento]);
  // v155: um so' teste de "tem Pix" para o Inicio e Pagamentos (sem chave: "combine com {prof} como pagar")
  const temPixAluno = pagar.forma === 'pix_direto' && !!pagar.chave;
  // BR Code Pix: payload copia-e-cola + QR em dataURL (gerados sob demanda)
  const brcodePix = React.useMemo(() => {
    if (!pagar?.brcode) return null;
    try {
      const qr = qrcode(0, 'M');
      qr.addData(pagar.brcode);
      qr.make();
      return { payload: pagar.brcode, dataURL: qr.createDataURL(6, 8) };
    } catch (e) { console.warn('qr pix:', e.message); return { payload: pagar.brcode, dataURL: null }; }
  }, [pagar?.brcode]);

  function copiarBRCode() {
    if (!brcodePix?.payload) return;
    navigator.clipboard.writeText(brcodePix.payload).then(() => { uso.acao('aluno.pix_copiar_codigo'); setPixCodeCopiado(true); setTimeout(() => setPixCodeCopiado(false), 2500); }, (e) => uso.erro('erro.copiar', e?.message || e));
  }

  async function marcarNotifsLidas() {
    const ids = notifs.filter(n => !n.lida).map(n => n.id);
    if (!ids.length) return;
    // Pela funcao do banco (Fase 0, Etapa 1): marca so' os avisos do proprio aluno e funciona
    // mesmo sem regra de UPDATE do aluno na tabela. Sem a funcao ainda: o UPDATE de antes.
    const { error } = await supabase.rpc('aluno_marcar_avisos_lidos', { p_ids: ids });
    if (error) await supabase.from('notificacoes').update({ lida: true }).in('id', ids);
    setNotifs(prev => prev.map(n => ({ ...n, lida: true })));
  }

  function CreditRing({ pct, disponivel, total }) {
    const r = 54, circ = 2 * Math.PI * r;

    const cor = pct > 0.5 ? '#2F6BFF' : pct > 0.25 ? '#E8971A' : '#EF4444';
    const isFull = pct >= 1;
    return (
      <div style={{ position:'relative', width:130, height:130, margin:'0 auto' }}>
        <svg width="130" height="130" viewBox="0 0 130 130">
          {}
          <circle cx="65" cy="65" r={r} fill="none" stroke="#E2E4E9" strokeWidth="12"/>
          {isFull ? (

            <circle cx="65" cy="65" r={r} fill="none" stroke={cor} strokeWidth="12"/>
          ) : (

            <circle cx="65" cy="65" r={r} fill="none" stroke={cor} strokeWidth="12"
              strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
              strokeDashoffset={circ * 0.25}
              strokeLinecap="round"
              style={{ transition:'stroke-dasharray .6s ease' }}/>
          )}
        </svg>
        <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <div style={{ fontSize:28, fontWeight:800, color:cor, lineHeight:1 }}>{disponivel}</div>
          <div style={{ fontSize:11, color:'#9AA0AB', marginTop:2 }}>de {total} aulas</div>
        </div>
      </div>
    );
  }

  // Central de uso: a tela do aluno na MESMA ordem dos returns abaixo (sem professor, convite,
  // portao do acordo, abas). Carregando = nada. Tambem fecha o funil do convite (chegou no app).
  // Item D: o numero que o campo mostra — o da ficha; sem ele, o de outra ficha do aluno (o mesmo dono).
  const whatsAceiteObrigatorio = !whatsValidoSM(dados?.telefone);
  // O campo aparece no PRIMEIRO acordo com este professor (o aceite do convite). Pacote renovado ja'
  // passou por aqui — so' volta a pedir se a ficha estiver sem numero.
  const pedeWhatsAceite = whatsAceiteObrigatorio
    || !(dados?.contratos || []).some(k => k.id !== (acordoK || contrato)?.id && (k.status_aceite === 'aceito' || k.aceite_aluno));
  function whatsDoAceite() {
    if (whatsAceite !== null) return whatsAceite;
    return dados?.telefone || (todosVinculos.map(v => v.telefone).find(x => whatsValidoSM(x)) || '');
  }
  const telaUso = loading ? null
    : (!dados && !pendingConvite?.pid && !semProfessorOk) ? 'aluno.sem_professor'
    : pendingConvite?.pid ? 'aluno.convite'
    : (acordoK && (portaoAcordo || folhaAcordo)) ? 'aluno.acordo'
    : 'aluno.' + (tela === 'notificacoes' ? 'pendencias' : tela);   // v134: a aba 'notificacoes' e' Pendencias
  React.useEffect(() => {
    if (!telaUso) return;
    uso.tela(telaUso);
    if (telaUso === 'aluno.convite') funilConvite.passo(2, null, true);
    if (telaUso === 'aluno.inicio' || telaUso === 'aluno.acordo') { const onde = telaUso === 'aluno.acordo' ? 'acordo' : 'inicio'; funilConvite.passo(5, { onde }, true); funilConvite.fim('concluiu', { onde }); }
  }, [telaUso]);
  // Janelas do aluno (todas sem <Modal>)
  React.useEffect(() => {
    if (!acaoAula?.modo) { modoAutoAviso.current = null; return; }
    if (modoAutoAviso.current && modoAutoAviso.current === acaoAula.modo) { modoAutoAviso.current = null; return; }   // troca automatica
    modoAutoAviso.current = null;
    uso.janela('aluno.aula_' + acaoAula.modo);
  }, [acaoAula?.modo]);
  React.useEffect(() => { if (novaAula) uso.acao('aluno.propor_aula_abrir'); }, [!!novaAula]);
  React.useEffect(() => { if (pausa) uso.acao('aluno.pausa_abrir'); }, [!!pausa]);
  React.useEffect(() => { if (pedindoAjuste) uso.acao('aluno.acordo_ajuste_abrir'); }, [pedindoAjuste]);

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(150deg,#16181D,#1C43AE,#2455D6)', fontFamily:"-apple-system,'Inter',sans-serif" }}>
      <div style={{ textAlign:'center', color:'white' }}>
        <div style={{ fontSize:48, marginBottom:16 }}><Ico n="trophy" /></div>
        <img src="/logo-cor.svg" alt="Plen" style={{height:'26px', marginBottom:'10px'}} />
        <div style={{ fontSize:14, opacity:0.7 }}>Carregando...</div>
      </div>
    </div>
  );

  if (!dados && !pendingConvite?.pid && !semProfessorOk) return (
    <TelaSemProfessor user={user} onLogout={logout}
      meuNome={(perfil?.nome || user.user_metadata?.nome || user.user_metadata?.full_name || '').trim()}
      onConvidou={() => setReloadKey(k => k + 1)}
      onEntrarSemProfessor={() => {
      uso.acao('aluno.usar_sem_professor'); uso.enviarAgora();
      localStorage.setItem('smSemProf_' + user.id, '1');
      window.location.reload();
    }} />
  );

  // O app chama o aluno pelo nome DELE (o do cadastro), nunca pelo nome que um professor
  // deu na ficha. Cada professor tem a propria ficha e pode escrever "Marcos", "Ze da
  // manha", o que quiser — e' controle dele. A saudacao nao pode mudar de contexto.
  const meuNomeReal = (perfil?.nome || user.user_metadata?.nome || user.user_metadata?.full_name || '').trim()
    || dados?.nome || user.email?.split('@')[0] || 'Aluno';
  const nome1 = meuNomeReal.split(' ')[0];
  const eu = dados ? { ...dados, nome: meuNomeReal } : dados;

  if (pendingConvite?.pid) return (
    <div style={{minHeight:'100vh', background:'linear-gradient(150deg,#16181D,#16181D,#16181D)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
      <div style={{background:'white', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', padding:'48px 40px', width:'100%', maxWidth:'420px', textAlign:'center'}}>
        <div style={{width:'64px', height:'64px', background:'linear-gradient(135deg,#2455D6,#2455D6)', borderRadius:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'30px', margin:'0 auto 20px'}}><Ico n="ball-tennis" /></div>
        <h1 style={{fontSize:'24px', fontWeight:'800', color:'#16181D', marginBottom:'10px', letterSpacing:'-0.5px'}}>Convite recebido!</h1>
        <p style={{color:'#6E7480', fontSize:'14px', marginBottom:'12px', lineHeight:'1.6'}}>O professor abaixo quer conectar suas aulas com você no Plen.</p>
        <div style={{background:'#E7EDFF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'14px 20px', marginBottom:'28px', display:'inline-block'}}>
          <span style={{fontWeight:'800', color:'#1C43AE', fontSize:'16px'}}><Ico n="barbell" /> {pendingConvite.pnome}</span>
        </div>
        {fichas && fichas.length > 0 ? (
          <div style={{marginBottom:'20px', textAlign:'left'}}>
            <p style={{color:'#3D424C', fontSize:'14px', fontWeight:'700', marginBottom:'10px'}}>Qual desses é você?</p>
            {fichas.map(f => (
              <button key={f.id} onClick={() => { uso.acao('aluno.convite_escolheu_ficha'); assumirFicha(f.id); }} disabled={salvando}
                style={{width:'100%', textAlign:'left', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'12px',
                        padding:'14px 16px', marginBottom:'8px', cursor:'pointer', fontSize:'15px', fontWeight:'700', color:'#16181D'}}>
                {f.nome || 'Sem nome'}{f.telefone ? <span style={{fontWeight:'400', color:'#9AA0AB', fontSize:'13px'}}> · {f.telefone}</span> : null}
              </button>
            ))}
            <p style={{color:'#9AA0AB', fontSize:'12px', marginTop:'6px'}}>Não achou seu nome? Peça ao professor para te cadastrar e enviar o convite de novo.</p>
          </div>
        ) : null}
        {/* Seguranca convites (01/10): link geral — so' aluno novo entra por ele. Quem ja' faz aula
            usa o link pessoal (assim ninguem do grupo pega a ficha de outra pessoa). */}
        {perguntaNovo === 'pergunta' && (
          <div style={{marginBottom:'16px', textAlign:'left'}}>
            <p style={{color:'#3D424C', fontSize:'14px', fontWeight:'700', marginBottom:'10px'}}>{(pendingConvite.pnome || 'Esse professor').split(' ')[0]} já tinha cadastrado você no Plen?</p>
            <button onClick={() => { setJaCadastrado(false); setPerguntaNovo('novo'); setErroWhatsNovo(''); setErroNomeNovo(''); }} disabled={salvando}
              style={{width:'100%', textAlign:'left', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'14px 16px', marginBottom:'8px', cursor:'pointer', fontSize:'15px', fontWeight:'700', color:'#16181D'}}>
              Não, quero entrar agora
            </button>
            <button onClick={() => { uso.acao('aluno.link_geral_ja_cadastrado'); setJaCadastrado(true); setPerguntaNovo('novo'); setErroWhatsNovo(''); setErroNomeNovo(''); }} disabled={salvando}
              style={{width:'100%', textAlign:'left', background:'white', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'14px 16px', marginBottom:'8px', cursor:'pointer', fontSize:'15px', fontWeight:'700', color:'#16181D'}}>
              Sim, já me cadastrou
            </button>
          </div>
        )}
        {perguntaNovo === 'novo' && (
          <div style={{marginBottom:'16px', textAlign:'left'}}>
            <p style={{color:'#3D424C', fontSize:'14px', lineHeight:1.55, marginBottom:'2px'}}>
              {jaCadastrado
                ? `Tudo bem: entre por aqui. ${(pendingConvite.pnome || 'Seu professor').split(' ')[0]} vê o seu nome e junta com o cadastro que já tinha feito — suas aulas e pagamentos aparecem aqui.`
                : `${(pendingConvite.pnome || 'Seu professor').split(' ')[0]} recebe o seu nome e o seu WhatsApp e completa o combinado de vocês. Depois você confere aqui.`}
            </p>
            <div style={{marginTop:'14px', textAlign:'left'}}>
              <label style={{display:'block', fontSize:'12px', fontWeight:'800', color:'#3D424C', marginBottom:'6px', letterSpacing:'.02em'}}>Seu nome</label>
              <input value={nomeNovoValor()} onChange={e => { setNomeNovo(e.target.value.slice(0, 80)); setErroNomeNovo(''); }} placeholder="Nome e sobrenome"
                autoCapitalize="words" autoComplete="name" aria-invalid={!!erroNomeNovo}
                style={{width:'100%', padding:'13px 14px', borderRadius:'12px', border:'1.5px solid ' + (erroNomeNovo ? '#DC2626' : '#E2E4E9'), fontSize:'16px', outline:'none', boxSizing:'border-box', background:'white', color:'#16181D'}} />
              {erroNomeNovo && <div style={{fontSize:'12px', color:'#DC2626', marginTop:'6px', lineHeight:1.45, fontWeight:700}}>{erroNomeNovo}</div>}
            </div>
            <CampoSeuWhats valor={whatsNovoValor()} onValor={v => { setWhatsNovo(v); setErroWhatsNovo(''); }} obrigatorio
              quem={(pendingConvite.pnome || 'Seu professor').split(' ')[0]} erro={erroWhatsNovo} />
            <button onClick={entrarComoAlunoNovo} disabled={salvando}
              style={{width:'100%', marginTop:'14px', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', borderRadius:'14px', padding:'15px', fontSize:'15px', fontWeight:'800', cursor:salvando?'not-allowed':'pointer', opacity:salvando?0.7:1}}>
              {salvando ? 'Entrando…' : 'Entrar'}
            </button>
            <button onClick={() => setPerguntaNovo('pergunta')} disabled={salvando}
              style={{display:'block', marginTop:'8px', minHeight:'44px', background:'transparent', border:'none', padding:0, color:'#2F6BFF', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
              Voltar
            </button>
          </div>
        )}
        {!perguntaNovo && <p style={{color:'#9AA0AB', fontSize:'13px', marginBottom:'28px'}}>Ao aceitar, suas aulas, créditos e pagamentos ficarão visíveis aqui.</p>}
        {!fichas && !perguntaNovo && <button onClick={aceitarConvitePendente} disabled={salvando}
          style={{width:'100%', background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', borderRadius:'14px', padding:'16px', fontSize:'16px', fontWeight:'800', cursor:salvando?'not-allowed':'pointer', opacity:salvando?0.7:1, marginBottom:'12px'}}>
 {salvando ? 'Vinculando…': 'Aceitar e vincular'}
        </button>}
        {/* revisao v142: 44 px e longe do "Voltar" do passo do WhatsApp (errar o toque jogava o convite fora) */}
        <button onClick={recusarConvitePendente}
          style={{width:'100%', minHeight:'44px', marginTop: perguntaNovo === 'novo' ? '16px' : 0, background:'transparent', color:'#9AA0AB', border:'none', fontSize:'13px', cursor:'pointer', padding:'8px'}}>
          Ignorar convite
        </button>
      </div>
    </div>
  );

  // PORTAO DE ENTRADA: enquanto o contrato nao for aceito, o aluno ve as condicoes primeiro.
  // O professor precisa saber que preencheu um CONTRATO; o aluno precisa concordar com ele.
  // v149: so' o PRIMEIRO acordo abre sozinho em tela cheia. Renovacao (pacote da fila ou pacote novo) abre quando o
  // aluno toca em "Ver e confirmar" no cartao do Inicio — e se fecha (caso Marcus 28/10: tampou a tela sem aviso).
  if (acordoK && (portaoAcordo || folhaAcordo)) {
    const DIAS_C = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
    const aulasDoAcordo = (aulas || []).filter(a => !a.contrato_id || a.contrato_id === acordoK.id);
    const linhaAgenda = (aulasDoAcordo.length > 0)
      ? Array.from(new Set(aulasDoAcordo.map(a => new Date(a.data + 'T12:00').getDay()))).sort().map(d => DIAS_C[d]).join(' · ')
      : null;
    const horaContrato = aulasDoAcordo[0] && aulasDoAcordo[0].hora ? String(aulasDoAcordo[0].hora).slice(0,5) : null;
    const renovacao = !portaoAcordo;
    const naFilaA = acordoK.status === 'proximo';
    const abertasAtual = naFilaA && contrato ? (aulas || []).filter(a => a.contrato_id === contrato.id && (a.status === 'agendada' || a.status === 'aguardando_confirmacao')) : [];
    const ultimaAtual = abertasAtual.map(a => a.data).sort().pop();
    const pgFila = (pagamentos || []).find(pg => pg.contrato_id === acordoK.id && pg.status !== 'cancelado');
    const linhaMuda = (rot, val) => (
      <div style={{display:'flex',gap:'10px',padding:'7px 0',borderTop:'1px solid #E2E4E9',fontSize:'13.5px',lineHeight:1.45}}>
        <span style={{minWidth:'104px',color:'#6E7480'}}>{rot}</span><span style={{color:'#16181D',fontWeight:'600'}}>{val}</span>
      </div>
    );
    // Etapa 4: o texto vem do BANCO (termos_do_contrato_json) — e' exatamente o que fica guardado
    // no aceite. Os dias da agenda ficam fora do texto (nao fazem parte do acordo aceito).
    const tA = termosAtuais;
    // v144: acordo que nasceu do PEDIDO deste aluno e o professor mudou algo — o banco guardou o que mudou
    // (antes -> depois). So' aparece enquanto o texto do acordo for o daquele momento (sha igual).
    const cpA = ((pedidosAluno || []).find(c => c.dados && c.dados.completado && c.dados.completado.contrato_id === acordoK.id
      && !c.dados.completado.repetido_de) || {}).dados?.completado;   // fechamento v144: o repetido nao traz as mudancas
    const mudancasA = cpA && !cpA.sem_proposta && Array.isArray(cpA.mudancas) && tA?.sha && (!cpA.termos_sha || cpA.termos_sha === tA.sha) ? cpA.mudancas : [];
    const outroVinculo = todosVinculos.length > 1 ? todosVinculos.findIndex((v, i) => i !== contextoIdx && !(v.contratos || []).some(k => k.status === 'ativo' && k.status_aceite !== 'aceito')) : -1;
    return (
      <div style={{minHeight:'100vh',background:'linear-gradient(150deg,#16181D,#1C43AE,#2455D6)',display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'}}>
        <div style={{background:'white',borderRadius:'22px',padding:'26px',maxWidth:'460px',width:'100%',maxHeight:'94vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
          <div style={{fontSize:'34px',marginBottom:'8px'}}><Ico n="note" /></div>
          <h1 style={{fontSize:'21px',fontWeight:'800',color:'#16181D',marginBottom:'6px',letterSpacing:'-0.4px'}}>
            {renovacao ? (naFilaA ? 'Seu próximo pacote' : 'Seu pacote novo') + ' com ' + (prof?.nome?.split(' ')[0] || 'o professor')
              : <>Como funciona o seu acordo com {prof?.nome?.split(' ')[0] || 'o professor'}</>}
          </h1>
          <p style={{color:'#6E7480',fontSize:'13px',marginBottom:'18px',lineHeight:1.6}}>
            {renovacao ? 'Primeiro o que muda; embaixo, o acordo completo.' : 'Confira o que foi combinado e confirme — leva 30 segundos. Depois, é só aproveitar as aulas.'}
          </p>
          {renovacao && (
            <div data-o-que-muda="1" style={{background:'#F4F7FF',border:'1.5px solid #C2D4FF',borderRadius:'14px',padding:'8px 14px 4px',marginBottom:'12px'}}>
              <p style={{fontSize:'13.5px',fontWeight:'800',color:'#1C43AE',margin:'4px 0 6px'}}>O que muda</p>
              {/* v155: o "faltam" e' o saldo do pacote (o mesmo das bolinhas); a data vem da agenda */}
              {naFilaA && (() => {
                const faltamM = credAluno.total > 0 ? credAluno.restante : abertasAtual.length;
                return linhaMuda('Pacote atual', faltamM > 0
                  ? `${ultimaAtual ? 'termina ' + diaEDataSM(ultimaAtual) + ' · ' : ''}falta${faltamM === 1 ? '' : 'm'} ${faltamM} aula${faltamM === 1 ? '' : 's'} — não muda nada`
                  : 'terminando — não muda nada');
              })()}
              {linhaMuda(naFilaA ? 'Próximo pacote' : 'Pacote novo', `começa ${diaEDataSM(acordoK.data_inicio)} e termina ${diaEDataSM(acordoK.data_fim)} · ${acordoK.total_aulas || '—'} aulas`)}
              {pgFila && linhaMuda('Pagamento', pgFila.status === 'pago' ? `${fmtBRL(pgFila.valor)} · já pago`
                : `${fmtBRL(pgFila.valor)} · vence ${fmtDiaMes(pgFila.data_vencimento)}${naFilaA ? ' · pode pagar antes' : ''}`)}
            </div>
          )}
          {mudancasA.length > 0 && (
            <div data-o-que-mudou="1" style={{background:'#FFFBEB',border:'1.5px solid #FDE68A',borderRadius:'14px',padding:'12px 14px',marginBottom:'12px'}}>
              <p style={{fontSize:'13.5px',fontWeight:'800',color:'#92400E',marginBottom:'4px'}}>
                O que {prof?.nome?.split(' ')[0] || 'o professor'} mudou no combinado que você mandou
              </p>
              {mudancasA.map((m, i) => (
                <div key={i} style={{fontSize:'13px',color:'#3D424C',lineHeight:1.5,padding:'5px 0',borderTop: i ? '1px solid #FDE68A' : 'none'}}>
                  <strong>{m.r}:</strong>{' '}
                  {m.a ? <><span style={{textDecoration:'line-through',color:'#9AA0AB'}}>{m.a}</span> → {m.d}</>
                    : <>{m.d} <span style={{color:'#92400E'}}>(você tinha deixado para ele)</span></>}
                </div>
              ))}
            </div>
          )}

          <div style={{background:'#F7F8FA',border:'1.5px solid #E2E4E9',borderRadius:'14px',padding:'4px 16px',marginBottom:'12px'}}>
            {tA?.texto ? <TextoDoAcordo texto={tA.texto} /> : (
              <div style={{padding:'16px 0',textAlign:'center',color:'#6E7480',fontSize:'13px'}}>
                {tA?.erro ? 'Não consegui carregar o acordo agora.' : 'Carregando o acordo…'}
              </div>
            )}
          </div>
          {linhaAgenda && (
            <p style={{color:'#6E7480',fontSize:'12px',marginBottom:'16px',lineHeight:1.5}}>
              Seus horários na agenda: <strong style={{color:'#3D424C'}}>{linhaAgenda}{horaContrato ? ' às ' + horaContrato : ''}</strong>
            </p>
          )}

          {tA?.inconsistente ? (
            // O banco recusa este aceite (cobrancas lancadas passam do total do texto): no lugar do
            // botao, o caminho e' pedir ao professor para revisar o valor.
            <div style={{background:'#FFFBEB',border:'1.5px solid #FDE68A',borderRadius:'14px',padding:'14px 16px',marginBottom:'10px',color:'#92400E',fontSize:'13.5px',lineHeight:1.55}}>
              <strong>Ainda não dá para confirmar.</strong> As cobranças lançadas passam do total deste acordo.
              Peça ao professor para revisar o valor do pacote — depois disso você confirma aqui. Use "Tem algo errado aqui" para avisar.
            </div>
          ) : tA?.erro ? (
            <button onClick={() => carregarTermos(acordoK.id)}
              style={{width:'100%',background:'white',color:'#2F6BFF',border:'1.5px solid #C2D4FF',borderRadius:'14px',
                      padding:'16px',fontSize:'15px',fontWeight:'800',cursor:'pointer',marginBottom:'10px'}}>
              Tentar de novo
            </button>
          ) : (
            <>
            {pedeWhatsAceite && (
              <div style={{marginBottom:'14px'}}>
                <CampoSeuWhats valor={whatsDoAceite()} onValor={v => { setWhatsAceite(v); setErroWhatsAceite(''); }}
                  obrigatorio={whatsAceiteObrigatorio} quem={prof?.nome?.split(' ')[0] || 'Seu professor'} erro={erroWhatsAceite} />
              </div>
            )}
            <button onClick={() => assinarContrato({ comWhats: pedeWhatsAceite })} disabled={salvando || !tA?.sha}
              style={{width:'100%',background:'linear-gradient(135deg,#2F6BFF,#2455D6)',color:'white',border:'none',borderRadius:'14px',
                      padding:'16px',fontSize:'16px',fontWeight:'800',cursor:(salvando || !tA?.sha)?'not-allowed':'pointer',opacity:(salvando || !tA?.sha)?0.7:1,marginBottom:'10px'}}>
              {salvando ? 'Registrando…' : !tA?.sha ? 'Carregando…' : 'Aceito as condições'}
            </button>
            </>
          )}

          {/* Aceitar nao pode ser a unica saida. O professor digita errado — troca 180 por
              200 no valor da aula — e o aluno ficava entre concordar com o numero errado ou
              travar no app sem caminho nenhum. Aqui ele aponta o que esta' errado, com as
              proprias palavras, e o pedido fica registrado dos dois lados. */}
          {!pedindoAjuste ? (
            <button onClick={() => setPedindoAjuste(true)}
              style={{width:'100%',background:'white',color:'#92400E',border:'1.5px solid #FDE68A',borderRadius:'14px',
                      padding:'14px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>
              Tem algo errado aqui
            </button>
          ) : (
            <div style={{background:'#FFFBEB',border:'1.5px solid #FDE68A',borderRadius:'14px',padding:'14px',textAlign:'left'}}>
              <p style={{fontSize:'13px',fontWeight:'700',color:'#92400E',marginBottom:'4px'}}>O que está diferente do combinado?</p>
              <p style={{fontSize:'12px',color:'#92400E',opacity:.85,marginBottom:'10px',lineHeight:1.5}}>
                Escreva do seu jeito. {prof?.nome ? prof.nome.split(' ')[0] : 'Seu professor'} recebe o aviso e corrige — aí o acordo volta pra você.
              </p>
              <textarea value={textoAjuste} onChange={e => setTextoAjuste(e.target.value)}
                placeholder="Ex: a aula é R$ 180, não R$ 200"
                style={{width:'100%',border:'1.5px solid #FDE68A',borderRadius:'10px',padding:'10px 12px',fontSize:'14px',
                        minHeight:'80px',resize:'none',outline:'none',boxSizing:'border-box',fontFamily:'inherit',background:'white'}} />
              <div style={{display:'flex',gap:'8px',marginTop:'10px'}}>
                <button onClick={() => { setPedindoAjuste(false); setTextoAjuste(''); }}
                  style={{padding:'11px 16px',borderRadius:'10px',border:'1.5px solid #E2E4E9',background:'white',color:'#6E7480',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  Voltar
                </button>
                <button onClick={pedirAjusteContrato} disabled={salvando || !textoAjuste.trim()}
                  style={{flex:1,padding:'11px',borderRadius:'10px',border:'none',background:'#D97706',color:'white',fontSize:'13px',fontWeight:'800',
                          cursor: textoAjuste.trim() ? 'pointer' : 'not-allowed', opacity: textoAjuste.trim() ? 1 : .5}}>
                  {salvando ? 'Enviando…' : 'Enviar correção'}
                </button>
              </div>
            </div>
          )}

          <p style={{color:'#9AA0AB',fontSize:'12px',textAlign:'center',lineHeight:1.6,marginTop:'12px'}}>
            {renovacao ? 'Confirmar não muda nenhuma data. Se algo estiver diferente do combinado, use "Tem algo errado aqui".'
              : 'Enquanto você não aceitar, nada é cobrado e nenhuma aula é descontada.'}
          </p>
          {renovacao && (
            <button onClick={() => { uso.acao('aluno.acordo_fechar_folha'); setFolhaAcordo(false); setPedindoAjuste(false); }}
              style={{display:'block',margin:'4px auto 0',minHeight:'44px',background:'transparent',border:'none',color:'#2F6BFF',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>
              Agora não — voltar ao app
            </button>
          )}
          {!renovacao && outroVinculo >= 0 && (
            <button onClick={() => { uso.acao('aluno.acordo_ver_depois'); setContextoIdx(outroVinculo); setTela('inicio'); }}
              style={{display:'block',margin:'4px auto 0',minHeight:'44px',background:'transparent',border:'none',color:'#2F6BFF',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
              Ver depois — ir para {todosVinculos[outroVinculo]?._professor?.nome?.split(' ')[0] || 'o outro professor'}
            </button>
          )}
        </div>
      </div>
    );
  }

  const handleNavAluno = (t) => { setTela(t); if (t === 'notificacoes') marcarNotifsLidas(); };
  // v134: o numero da aba e' o que esta' "Sua vez" (todas as fichas). Sem a lista do banco: nao lidos.
  const naoLidosAluno = notifs.filter(n=>!n.lida).length;
  const numPendAluno = numeroPendSM(pend, 'aluno');
  const notifCount = numPendAluno == null ? naoLidosAluno : numPendAluno;
  // Pendencias: tocar num item de outro professor troca o chip (como os botoes de cima) e abre a
  // tela onde se resolve. Ficha escondida pela regra de "um chip por professor": pelo professor.
  function irPendenciaAluno(item, destino) {
    uso.acao('pendencias.resolver', { tipo: tipoUsoPendSM(item.tipo), via: 'navegar' });
    let idx = todosVinculos.findIndex(v => v.id === item.aluno_id);
    if (idx < 0 && item.professor_id) idx = todosVinculos.findIndex(v => v.professor_id === item.professor_id);
    if (idx >= 0 && idx !== contextoIdx) setContextoIdx(idx);
    setTela(destino);
    // v155: confirmar o proximo pacote — rola ate' o bloco dele no Financeiro
    if (item.tipo === 'aceitar_acordo' && item.detalhe?.proximo) setTimeout(() => { try { document.getElementById('bloco-proximo-pacote')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }, 350);
  }
  async function responderAulaPendencia(item, aconteceu, motivo = null) {
    const est = await responderAulaAluno(aulaDoItemSM(item), aconteceu, { origem: 'pendencias', nomeProf: primeiroNomeSM(item.nome, 'seu professor'), motivo });
    if (est) uso.acao('pendencias.resolver', { tipo: tipoUsoPendSM(item.tipo), via: 'inline' });
    return est;
  }
  // v140: abre uma aula no Inicio, na folha certa — do push ou de Pendencias (que lista aulas de TODOS
  // os professores: troca para a ficha da aula). modo: 'presenca' | 'opcoes' | 'naovou' (o aviso de
  // falta: no prazo = cancelar, fora = avisar; a previa do banco corrige o desenho se o palpite errar).
  async function abrirAulaAluno(aulaId, modo = 'opcoes') {
    let a = null;
    try {
      const { data: au, error: ea } = await supabase.from('aulas').select('*').eq('id', aulaId).maybeSingle();
      if (!ea) a = au || null;
    } catch {}
    if (!a) { setTela('aulas'); toastSM('Não encontramos esta aula. A tela foi atualizada.'); return; }
    const idx = todosVinculos.findIndex(v => v.id === a.aluno_id);
    if (idx >= 0 && idx !== contextoIdx) setContextoIdx(idx);
    setTela('inicio');
    if (a.status !== 'agendada') { setTela('aulas'); return; }
    const m = modo === 'naovou' ? (podeCancelar(a) ? 'cancelar' : 'avisar') : modo === 'presenca' && !perguntaAbertaSM(a) ? 'opcoes' : modo;
    setAcaoAula({ aula: a, modo: m });
  }

  return (
    <div className="flex min-h-screen" style={{position:'relative'}}>
      <SidebarAluno ativa={tela} setAtiva={handleNavAluno} aluno={eu} onLogout={logout} notifCount={notifCount} onEditarPerfil={() => setModalEditarPerfil(true)} />

      <main className="app-main flex-1 overflow-y-auto bg-gray-50">
        <MobileTopBarAluno aluno={eu} userId={user.id} onLogout={logout} onEditarPerfil={() => setModalEditarPerfil(true)} />

      {}
      {todosVinculos.length > 0 && (
        <div style={{background:'white', borderBottom:'1px solid #E2E4E9', padding:'10px 16px'}}>
          <div style={{display:'flex', gap:'8px', overflowX:'auto', paddingBottom:'2px'}}>
            {todosVinculos.map((v, i) => {
              const ativo = i === contextoIdx;
              const pedV = !v.professor_id ? pedidosDaFicha(v.id) : [];   // fechamento v144: a provisoria mostra o(s) pedido(s) dela
              const modalidade = (pedV[0] && pedV[0].dados && pedV[0].dados.proposta && String(pedV[0].dados.proposta.modalidade || '').trim())
                || v.modalidade || v._professor?.modalidades?.split(',')[0]?.trim() || 'Esporte';
              const nomeProfStr = v._professor?.nome?.split(' ')[0] || (pedV.length ? nomeProfPedido(pedV[0]) + (pedV.length > 1 ? ` +${pedV.length - 1}` : '') : 'Professor');
              const m = modalidade.toLowerCase();
              const icoModalidade = m.includes('tenis') || m.includes('tênis') ? 'ball-tennis'
                : m.includes('personal') || m.includes('muscula') ? 'barbell'
                : m.includes('swim') || m.includes('nata') ? 'swimming'
                : m.includes('fute') ? 'ball-football'
                : m.includes('bike') || m.includes('cicl') ? 'bike'
                : m.includes('corrida') || m.includes('run') ? 'run'
                : 'ball-tennis';
              return (
                <button key={v.id} onClick={() => { if (i !== contextoIdx) uso.acao('aluno.trocar_professor', { n: todosVinculos.length }); setContextoIdx(i); setSeletorAberto(false); setTela('inicio'); }}
                  style={{display:'flex', alignItems:'center', gap:'6px', padding:'7px 14px', borderRadius:'20px', border: ativo ? '2px solid #2F6BFF' : '1.5px solid #E2E4E9', background: ativo ? '#E7EDFF' : 'white', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0, transition:'all 0.15s'}}>
                  <span style={{display:'flex'}}><Ico n={icoModalidade} s={15} /></span>
                  <span style={{fontSize:'12px', fontWeight: ativo ? '700' : '500', color: ativo ? '#2F6BFF' : '#6E7480'}}>
                    {modalidade} · {nomeProfStr}
                  </span>
                  {ativo && <span style={{width:'6px', height:'6px', borderRadius:'50%', background:'#2F6BFF', flexShrink:0}} />}
                </button>
              );
            })}
            {/* O aluno que ja' usa o Plen com um professor quer trazer os outros para o
                mesmo lugar — e, ao trazer, apresenta o app ao proximo professor. E' o laco
                que faz o produto crescer sozinho, e mora aqui porque e' aqui que ele
                pensa "quais sao os meus professores". */}
            <button onClick={() => { uso.acao('aluno.add_prof_abrir'); setAddProfessor(true); }}
              style={{display:'flex', alignItems:'center', gap:'6px', padding:'7px 14px', borderRadius:'20px',
                      border:'1.5px dashed #C2D4FF', background:'white', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0}}>
              <Ico n="plus" s={14} c="#2F6BFF" />
              <span style={{fontSize:'12px', fontWeight:'700', color:'#2F6BFF'}}>Adicionar professor</span>
            </button>
          </div>
        </div>
      )}

      {addProfessor && (
        <ModalAdicionarProfessor
          user={user} meuNome={meuNomeReal} meuTelefone={dados?.telefone}
          onCriou={() => { addProfCriouRef.current = true; }}
          onClose={() => {
            setAddProfessor(false);
            // Recarrega so' ao FECHAR (o "Convite pronto" precisa ficar na tela ate' la'): aparece o
            // "Convite enviado — aguardando confirmação" e a ficha que espera o professor.
            if (addProfCriouRef.current) { addProfCriouRef.current = false; setReloadKey(k => k + 1); }
          }} />
      )}

      {}
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">

        {}
        {tela === 'inicio' && (
          <div className="flex flex-col gap-4">
            <FaixaPush userId={user?.id} quem="aluno" texto="Receber lembrete antes da aula e avisos do professor?" />
            {convitesProf.map(c => {
              const primeiro = (c.professor_nome || 'Um professor').split(' ')[0];
              const mod = (() => { try { const v = JSON.parse(c.professor_modalidades || '[]'); return Array.isArray(v) && v[0] ? v[0] : ''; } catch { return ''; } })();
              const oQue = c.tipo_cobranca === 'mensal'
                ? `Mensalidade de R$ ${Number(c.valor_mensal || c.preco || 0).toLocaleString('pt-BR')}`
                : `Pacote de ${c.total_aulas || '?'} aulas · R$ ${Number(c.preco || 0).toLocaleString('pt-BR')}`;
              return (
                <div key={c.code} style={{background:'#16181D', borderRadius:'16px', padding:'18px', color:'white'}}>
                  <p style={{fontSize:'11px', fontWeight:'800', color:'#8FDFC4', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:'7px'}}>
                    <Ico n="user-plus" s={13} c="#8FDFC4" /> Novo professor
                  </p>
                  <p style={{fontSize:'17px', fontWeight:'700', lineHeight:1.35, marginBottom:'6px'}}>
                    {c.professor_nome || 'Um professor'} quer te adicionar como aluno{mod ? ` de ${mod.toLowerCase()}` : ''}.
                  </p>
                  <p style={{fontSize:'13px', opacity:.75, lineHeight:1.5}}>
                    {oQue}{c.agenda ? ` · ${c.agenda}` : ''}
                  </p>
                  <div style={{display:'flex', gap:'8px', marginTop:'14px', flexWrap:'wrap'}}>
                    <button disabled={salvando} onClick={() => aceitarProfessorNovo(c)}
                      style={{padding:'12px 20px', borderRadius:'12px', border:'none', background:'#0E9F6E', color:'white', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                      <Ico n="check" s={15} c="white" /> Sou eu, aceitar
                    </button>
                    <button disabled={salvando} onClick={() => recusarProfessorNovo(c)}
                      style={{padding:'12px 20px', borderRadius:'12px', border:'1.5px solid rgba(255,255,255,.25)', background:'transparent', color:'rgba(255,255,255,.8)', fontSize:'14px', fontWeight:'700', cursor:'pointer'}}>
                      Não conheço {primeiro}
                    </button>
                  </div>
                </div>
              );
            })}

            {(() => {
              // v144: o acordo de OUTRO professor espera a conferencia (ex.: ele completou o seu pedido mudando algo)
              const iA = todosVinculos.findIndex((v, k) => k !== contextoIdx && v.professor_id && !v.desvinculado_em
                && (v.contratos || []).some(x => x.status === 'ativo' && x.status_aceite !== 'aceito'));
              if (iA < 0) return null;
              const PA = primeiroNomeSM(todosVinculos[iA]._professor?.nome, 'Seu professor');
              return (
                <div data-acordo-outro="1" style={{background:'#E7EDFF', border:'2px solid #2F6BFF', borderRadius:'16px', padding:'16px'}}>
                  <p style={{fontSize:'12px', fontWeight:'800', color:'#1C43AE', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'6px'}}>
                    <Ico n="file-text" s={13} /> Precisa da sua resposta
                  </p>
                  <p style={{fontSize:'15px', fontWeight:'700', color:'#16181D', lineHeight:'1.4'}}>O acordo com {PA} está esperando você conferir.</p>
                  <button onClick={() => { uso.acao('aluno.acordo_outro_abrir'); setContextoIdx(iA); setTela('inicio'); }}
                    style={{marginTop:'12px', padding:'11px 18px', minHeight:'44px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                    Conferir o acordo
                  </button>
                </div>
              );
            })()}
            {solicitacoes.filter(s => s.status === 'contraproposta').map(s => (
              <div key={s.id} style={{background:'#E7EDFF', border:'2px solid #2F6BFF', borderRadius:'16px', padding:'16px'}}>
                <p style={{fontSize:'12px', fontWeight:'800', color:'#1C43AE', textTransform:'uppercase', letterSpacing:'.04em', marginBottom:'6px'}}>
                  <Ico n="clock" s={13} /> Precisa da sua resposta
                </p>
                <p style={{fontSize:'15px', fontWeight:'700', color:'#16181D', lineHeight:'1.4'}}>
                  {prof?.nome?.split(' ')[0] || 'Seu professor'} não consegue no dia que você pediu, mas propôs{' '}
                  {s.resposta_data
                    ? `${new Date(s.resposta_data+'T12:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})}${s.resposta_hora ? ' às ' + String(s.resposta_hora).slice(0,5) : ''}`
                    : 'outro horário'}.
                </p>
                <div style={{display:'flex', gap:'8px', marginTop:'12px', flexWrap:'wrap'}}>
                  <button disabled={salvando} onClick={() => responderContraproposta(s, true)}
                    style={{padding:'11px 18px', minHeight:'44px', borderRadius:'12px', border:'none', background:'#0E9F6E', color:'white', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                    <Ico n="check" s={15} /> Confirmar
                  </button>
                  <button disabled={salvando} onClick={() => responderContraproposta(s, false)}
                    style={{padding:'11px 18px', minHeight:'44px', borderRadius:'12px', border:'1.5px solid #FECACA', background:'white', color:'#991B1B', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                    Não dá pra mim
                  </button>
                </div>
              </div>
            ))}

            {dados?.desvinculado_em && (
              <div style={{background:'#F4F5F7', border:'1px solid #E2E4E9', borderRadius:'14px', padding:'14px 16px'}}>
                <p style={{fontSize:'13px', fontWeight:'700', color:'#16181D', marginBottom:'3px'}}>
                  <Ico n="info-circle" s={15} c="#6E7480" /> Vínculo encerrado em {new Date(dados.desvinculado_em).toLocaleDateString('pt-BR')}
                </p>
                <p style={{fontSize:'12px', color:'#6E7480', lineHeight:'1.5'}}>
                  Você não tem aulas agendadas com {prof?.nome?.split(' ')[0] || 'este professor'}, mas seu histórico de
                  aulas e pagamentos continua aqui, por completo.
                </p>
              </div>
            )}
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Olá, {nome1}! <Ico n="mood-smile" /></h1>
              <p className="text-sm text-gray-400 mt-1">{prof ? `Professor: ${prof.nome}` : pedidoEsperando ? `Professor: ${pedidoEsperando.dados?.nomeProfDestino || nomeProfPedido(pedidoEsperando)}` : 'Sem professor vinculado'}</p>
            </div>
            {/* Revisao v142 (achado 3): entrou pelo link geral e o professor ainda nao completou — antes so'
                aparecia "Nenhum plano ativo… Entre em contato com seu professor". */}
            {pedidosEsperando.map(pe => {
              const P1 = nomeProfPedido(pe);
              const temProposta = !!(pe.dados && pe.dados.proposta);
              const L = linhasDaProposta(temProposta ? pe.dados.proposta : {}, 'eu');
              // ja' tem acordo com esse professor (outra ficha): o pedido foi engano? / aberto ha' mais de 30 dias
              const jaTemAcordo = !!pe.professor_id && todosVinculos.some(v => v.professor_id === pe.professor_id && (v.contratos || []).some(k => k.status === 'ativo'));
              const velho = !!pe.usado_em && (Date.now() - new Date(pe.usado_em).getTime()) > 30 * 86400000;
              return (
                <div key={pe.id || pe.code} data-esperando-pedido="1" style={{background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'14px', padding:'14px 16px'}}>
                  <p style={{fontSize:'14px', fontWeight:'800', color:'#16181D', marginBottom:'3px'}}>
                    <Ico n="hourglass" s={15} c="#2F6BFF" /> Esperando {P1} conferir
                  </p>
                  <p style={{fontSize:'13px', color:'#3D424C', lineHeight:'1.5'}}>
                    {pe.usado_em ? `${P1} já abriu o seu convite.` : `O convite foi para ${P1}.`} Quando ele conferir, o acordo aparece aqui{temProposta ? '. O combinado que você mandou:' : '.'}
                  </p>
                  {(jaTemAcordo || velho) && (
                    <p style={{fontSize:'12.5px', color:'#92400E', lineHeight:'1.5', marginTop:'6px'}}>
                      {jaTemAcordo ? `Você já tem um acordo com ${P1}. Se este pedido foi engano, cancele.` : `Faz mais de 30 dias e ${P1} não conferiu. Se não for mais seguir, cancele.`}
                    </p>
                  )}
                  {temProposta && (
                    <div style={{background:'white', border:'1px solid #E2E4E9', borderRadius:'12px', padding:'10px 12px', marginTop:'10px', fontSize:'13px', color:'#3D424C', lineHeight:1.6}}>
                      <div style={{fontWeight:'700', color:'#16181D'}}>{(L.modalidade ? L.modalidade + ' · ' : '') + L.agenda}</div>
                      <div>{L.valor}</div>
                      {L.andamento && <div>{L.andamento}</div>}
                      {L.pagamento && <div>{L.pagamento}</div>}
                      {L.desmarcar && <div>{L.desmarcar}</div>}
                      {L.emBranco && <div style={{color:'#6E7480'}}>O que você deixou em branco, {P1} completa.</div>}
                    </div>
                  )}
                  {cancelandoPedido === pe.code ? (
                    <div data-cancelar-pedido="1" style={{marginTop:'10px', fontSize:'13px', color:'#3D424C'}}>
                      Cancelar o pedido para {P1}? {pe.usado_em ? `${P1} recebe um aviso.` : 'O link deixa de abrir.'}
                      <div style={{display:'flex', gap:'8px', marginTop:'8px'}}>
                        <button type="button" onClick={() => setCancelandoPedido(null)}
                          style={{flex:1, minHeight:'44px', borderRadius:'10px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontWeight:'700', fontSize:'14px', cursor:'pointer'}}>Não</button>
                        <button type="button" onClick={() => cancelarPedido(pe)}
                          style={{flex:1, minHeight:'44px', borderRadius:'10px', border:'none', background:'#DC2626', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer'}}>Sim, cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => { uso.acao('aluno.cancelar_pedido_abrir'); setCancelandoPedido(pe.code); }}
                      style={{display:'block', marginTop:'8px', minHeight:'44px', border:'none', background:'none', padding:0, color:'#6E7480', fontWeight:'700', fontSize:'13px', cursor:'pointer'}}>
                      Cancelar pedido
                    </button>
                  )}
                </div>
              );
            })}
            {esperandoCompletarAluno && (
              <div data-esperando-prof="1" style={{background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'14px', padding:'14px 16px'}}>
                <p style={{fontSize:'14px', fontWeight:'800', color:'#16181D', marginBottom:'3px'}}>
                  <Ico n="hourglass" s={15} c="#2F6BFF" /> {primeiroNomeSM(prof?.nome, 'Seu professor')} vai completar o combinado
                </p>
                <p style={{fontSize:'13px', color:'#3D424C', lineHeight:'1.5'}}>
                  Ele escolhe o pacote e os dias. Quando terminar, o acordo aparece aqui para você conferir e aceitar.
                </p>
              </div>
            )}

            {/* O empurrão pelo WhatsApp só faz sentido quando NÃO existe app do outro lado.
                Se o professor foi quem convidou e o aluno aceitou, ele obviamente usa o
                Plen: recebe a notificação e o push. Pedir para avisar de novo por fora,
                toda vez, além de feio, passa a impressão de que o app não funciona
                sozinho (apontado pelo Marcus em 23/09). Fica só para o professor que
                ainda não abriu conta — aí o aviso no app não chega a ninguém. */}
            {avisoWpp && (profUsaApp === false || (profUsaApp === null && !prof?.user_id)) && (
              <div style={{background:'#FFFBEB',border:'1.5px solid #FDE68A',borderRadius:'16px',padding:'16px 18px'}}>
                <div style={{fontSize:'14px',fontWeight:'800',color:'#92400E',marginBottom:'4px'}}><Ico n="alert-triangle" c="#D97706" /> Registrado — mas ele ainda não usa o Plen</div>
                <div style={{fontSize:'13px',color:'#B45309',marginBottom:'12px',lineHeight:1.6}}>
                  {prof?.nome?.split(' ')[0] || 'Seu professor'} ainda não abriu conta, então não vai
                  receber o aviso pelo app. Mande pelo WhatsApp para ele não perder.
                </div>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  <a href={`https://wa.me/${(prof?.telefone||'').replace(/\D/g,'') ? '55' + (prof.telefone||'').replace(/\D/g,'').replace(/^55/,'') : ''}?text=${encodeURIComponent(avisoWpp.texto)}`}
                     target="_blank" rel="noopener noreferrer" onClick={() => { uso.acao('whatsapp.abrir', { motivo: avisoWpp.motivo || 'outro', via: 'aviso_aluno', com_numero: !!(prof?.telefone || '').replace(/\D/g, '') }); setAvisoWpp(null); }}
                     style={{background:'#0E9F6E',color:'white',borderRadius:'12px',padding:'11px 18px',fontWeight:'800',fontSize:'14px',textDecoration:'none'}}>
                    <Ico n="message-circle" /> Avisar no WhatsApp
                  </a>
                  <button onClick={() => { uso.acao('aluno.whatsapp_dispensar', { motivo: avisoWpp.motivo || 'outro' }); setAvisoWpp(null); }}
                     style={{background:'transparent',border:'none',color:'#B45309',fontSize:'13px',fontWeight:'700',cursor:'pointer',padding:'11px 8px'}}>
                    Agora não
                  </button>
                </div>
              </div>
            )}

            {/* v149: acordo de renovacao a confirmar — cartao que se toca, nunca tela cheia */}
            {acordoK && !portaoAcordo && cartaoAcordoFechado !== acordoK.id && (() => {
              const naFilaC = acordoK.status === 'proximo';
              const pgC = (pagamentos || []).find(pg => pg.contrato_id === acordoK.id && pg.status !== 'cancelado');
              const nP = prof?.nome?.split(' ')[0] || 'Seu professor';
              return (
                <div style={{background:'#F4F7FF', border:'1.5px solid #C2D4FF', borderRadius:'18px', padding:'16px 18px'}}>
                  <div style={{fontSize:'15px', fontWeight:'800', color:'#1C43AE'}}><Ico n="calendar-plus" c="#2F6BFF" /> {naFilaC ? 'Seu próximo pacote está pronto' : 'Seu pacote novo está pronto'}</div>
                  <div style={{fontSize:'13.5px', color:'#3D424C', marginTop:'6px', lineHeight:1.5}}>
                    {`${nP} combinou ${acordoK.total_aulas || ''} aulas${pgC ? ' por ' + fmtBRL(pgC.valor) : ''}. `}
                    {naFilaC ? `Começa ${diaEDataSM(acordoK.data_inicio)} e termina ${diaEDataSM(acordoK.data_fim)}. O pacote atual segue até a última aula dele.` : `De ${fmtDiaMes(acordoK.data_inicio)} a ${fmtDiaMes(acordoK.data_fim)}.`}
                    {pgC && pgC.status === 'pendente' ? ' Confirme o pacote antes de pagar.' : ''}
                  </div>
                  <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                    <button onClick={() => { uso.acao('aluno.acordo_abrir_folha', { fila: naFilaC }); setFolhaAcordo(true); }}
                      style={{flex:1, padding:'12px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer'}}>
                      Ver e confirmar
                    </button>
                    <button onClick={() => { uso.acao('aluno.acordo_agora_nao'); setCartaoAcordoFechado(acordoK.id); }}
                      style={{padding:'12px 16px', borderRadius:'12px', border:'1.5px solid #C2D4FF', background:'white', color:'#1C43AE', fontWeight:'700', fontSize:'14px', cursor:'pointer'}}>
                      Agora não
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* PRIORIDADE 1: pagamento em aberto — o aluno precisa ver isso antes de tudo */}
            {(() => {
              // v155: so' o que ainda nao foi avisado; o ja' avisado vira a linha calma (nunca cobrar de novo)
              const emAberto = emAbertoAluno;
              const nPg = prof?.nome?.split(' ')[0] || 'seu professor';
              if (emAberto.length === 0) {
                if (avisadasAluno.length === 0) return null;
                const totalAv = avisadasAluno.reduce((s, pg) => s + Number(pg.valor || 0), 0);
                return (
                  <div onClick={() => { uso.acao('aluno.atalho_pagar', { avisado: true }); setTela('financeiro'); }}
                    style={{background:'#EFFAF5', border:'1.5px solid #B7E9D6', borderRadius:'16px', padding:'14px 16px', fontSize:'13.5px', color:'#0A6F4D', lineHeight:1.5, cursor:'pointer'}}>
                    <Ico n="circle-check" c="#0E9F6E" /> Você avisou que pagou {fmtBRL(totalAv)} · {nPg} confere
                  </div>
                );
              }
              const total = emAberto.reduce((s, pg) => s + Number(pg.valor || 0), 0);
              return (
                <div onClick={() => { uso.acao('aluno.atalho_pagar'); setTela('financeiro'); }}
                  style={{background:'linear-gradient(135deg,#B45309,#D97706)', borderRadius:'18px', padding:'18px 20px',
                          color:'white', cursor:'pointer', boxShadow:'0 8px 24px rgba(180,83,9,0.28)'}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap'}}>
                    <div>
                      <div style={{fontSize:'11px', fontWeight:'700', letterSpacing:'0.06em', textTransform:'uppercase', opacity:0.9}}>
                        <Ico n="credit-card" /> Pagamento em aberto
                      </div>
                      <div style={{fontSize:'26px', fontWeight:'800', marginTop:'4px', lineHeight:1.1}}>
                        R$ {total.toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2})}
                      </div>
                      <div style={{fontSize:'13px', opacity:0.92, marginTop:'4px'}}>
                        {referenteCobranca(emAberto)}
                      </div>
                      {avisadasAluno.length > 0 && (
                        <div style={{fontSize:'12px', opacity:0.92, marginTop:'2px'}}>
                          Não soma os {fmtBRL(avisadasAluno.reduce((s, pg) => s + Number(pg.valor || 0), 0))} que você avisou que pagou
                        </div>
                      )}
                    </div>
                    <div style={{background:'white', color:'#B45309', borderRadius:'12px', padding:'12px 18px',
                                 fontWeight:'800', fontSize:'14px', whiteSpace:'nowrap'}}>
                      {temPixAluno ? 'Pagar com Pix' : 'Ver como pagar'} <Ico n="arrow-right" />
                    </div>
                  </div>
                </div>
              );
            })()}

            {}
            {/* Aulas passadas sem resposta — o mesmo que a faixa vermelha do professor,
                do lado do aluno. Nenhuma aula fica invisivel enquanto ninguem responde. */}
            {semResposta.length > 0 && (() => {
              const nomeProf = prof?.nome?.split(' ')[0] || 'Seu professor';
              const paraConfirmar = semResposta.filter(a => a.desfecho_prof && !a.divergencia_em && !a.confirmada_aluno_em && !a.falta_declarada_aluno_em).length;
              return (
              <div style={{background: paraConfirmar ? '#E7EDFF' : '#FFFBEB', border: `2px solid ${paraConfirmar ? '#C2D4FF' : '#FDE68A'}`, borderRadius:'16px', padding:'14px 16px'}}>
                <p style={{fontSize:'13px', fontWeight:'800', color: paraConfirmar ? '#1C43AE' : '#92400E', marginBottom:'4px'}}>
                  <Ico n={paraConfirmar ? 'checks' : 'clock-exclamation'} s={15} c={paraConfirmar ? '#2F6BFF' : '#D97706'} />{' '}
                  {paraConfirmar
                    ? (paraConfirmar === 1 ? 'Confirme 1 aula' : `Confirme ${paraConfirmar} aulas`)
                    : (semResposta.length === 1 ? '1 aula sem resposta' : semResposta.length + ' aulas sem resposta')}
                </p>
                <p style={{fontSize:'12px', color: paraConfirmar ? '#3D5AC4' : '#92400E', marginBottom:'10px', lineHeight:1.5}}>
                  {paraConfirmar
                    ? `Uma aula só sai do seu pacote quando você e ${nomeProf} concordam. Confira o que ele registrou.`
                    : `${nomeProf} ainda não fechou estas aulas. Diga o que aconteceu — ajuda a manter seu saldo certo.`}
                </p>
                <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                  {semResposta.slice(0, 6).map(a => {
                    const d = new Date(a.data + 'T12:00');
                    const quando = d.toLocaleDateString('pt-BR', { weekday:'short', day:'numeric', month:'short' }).replace('.', '');
                    // v140: 'nao_veio' = o aluno disse que o professor nao veio (aluno_motivo_nao)
                    const nvS = naoVeioSM({ ...a, duracao_min: a.duracao_min || contrato?.duracao_min });
                    const jaDisse = a.confirmada_aluno_em ? 'fui' : nvS ? 'nao_veio' : a.falta_declarada_aluno_em ? 'nao_fui' : null;
                    const chEsc = 'sr:' + a.id;
                    const iniS = inicioAulaSP(a);
                    const liberaS = iniS ? new Date(iniS.getTime() + 15 * 60000) : null;
                    const individualS = aulaIndividualSM(a);
                    // v141: "nao veio" so' onde o banco aceita (aviso do proprio aluno trava; o registrado pelo professor, nao)
                    const nvOkS = individualS && !avisoTravaNaoVeioSM(a);
                    const escolher = op => { setEscolhaAberta(null);
                      if (op === 'fiz') return confirmarAulaAluno(a);
                      if (op === 'nao_veio') return responderAulaAluno(a, false, { motivo: 'prof_nao_veio', origem: 'sem_resposta' });
                      return responderAulaAluno(a, false, { origem: 'sem_resposta' }); };
                    // Prazo do consenso: 24h depois do fim da aula.
                    const prazo = new Date(`${a.data}T${String(a.hora || '00:00').slice(0,5)}:00`);
                    prazo.setMinutes(prazo.getMinutes() + (Number(contrato?.duracao_min) || 60) + 24 * 60);
                    // v141: aula REABERTA (contestacao) que o aluno confirmou: o robo espera 24h contadas da
                    // confirmacao (sql da v141) — o prazo do fim da aula ja' passou
                    if (a.desfeita_em && a.confirmada_aluno_em && new Date(a.confirmada_aluno_em) > new Date(a.desfeita_em)) {
                      const p24 = new Date(a.confirmada_aluno_em).getTime() + 24 * 3600000;
                      if (p24 > prazo.getTime()) prazo.setTime(p24);
                    }
                    const prazoTxt = prazo.toLocaleDateString('pt-BR', { weekday:'short', day:'numeric' }).replace('.', '') + ' às ' + prazo.toLocaleTimeString('pt-BR', { hour:'2-digit', minute:'2-digit' });
                    const ehFalta = a.desfecho_prof === 'falta';
                    return (
                      <div key={a.id} style={{background:'white', border:`1px solid ${a.desfecho_prof ? '#C2D4FF' : '#FDE68A'}`, borderRadius:'12px', padding:'10px 12px'}}>
                        <div style={{fontSize:'13px', fontWeight:'700', color:'#16181D'}}>{quando} · {String(a.hora || '').slice(0,5)}</div>
                        {a.divergencia_em ? (
                          <div style={{fontSize:'12px', color:'#991B1B', marginTop:'4px', fontWeight:'600', lineHeight:1.5}}>
                            <Ico n="alert-triangle" s={12} /> {a.aluno_motivo_nao === 'prof_nao_veio' ? `Você disse que ${nomeProf} não veio e ${nomeProf} registrou diferente.` : 'Vocês registraram diferente.'} A aula não sai do seu pacote até {nomeProf} decidir — conversem.
                          </div>
                        ) : a.desfecho_prof && !jaDisse ? (
                          <>
                            <div style={{fontSize:'12.5px', color:'#3D424C', marginTop:'4px', lineHeight:1.5}}>
                              {nomeProf} registrou como <strong>{ehFalta ? 'falta (conta como aula)' : 'aula dada'}</strong>. Está certo?
                            </div>
                            {escolhaAberta === chEsc ? (
                              // v140: discordar pergunta o que aconteceu — falta: [Fiz a aula] [Cadu nao veio];
                              // aula dada: [Eu nao fui] [Cadu nao veio]
                              <EscolhaNaoAconteceuSM nomeProf={nomeProf} disabled={salvando} liberaNaoVeioEm={liberaS} aulaRef={{ ...a, duracao_min: a.duracao_min || contrato?.duracao_min }}
                                opcoes={ehFalta ? (nvOkS ? ['fiz', 'nao_veio'] : ['fiz']) : (nvOkS ? ['eu_nao_fui', 'nao_veio'] : ['eu_nao_fui'])}
                                onEscolher={escolher} onVoltar={() => setEscolhaAberta(null)} />
                            ) : (
                            <div style={{display:'flex', gap:'7px', marginTop:'8px'}}>
                              <button disabled={salvando} onClick={() => ehFalta ? declararFaltaAluno(a) : confirmarAulaAluno(a)}
                                style={{flex:1, padding:'10px', minHeight:'44px', borderRadius:'10px', border:'none', background:'#2F6BFF', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                                {ehFalta ? 'Sim, faltei' : 'Sim, fiz a aula'}
                              </button>
                              <button disabled={salvando} onClick={() => setEscolhaAberta(chEsc)}
                                style={{padding:'10px 14px', minHeight:'44px', borderRadius:'10px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                                {ehFalta ? 'Não, eu fui' : 'Não aconteceu'}
                              </button>
                            </div>
                            )}
                            <div style={{fontSize:'11.5px', color:'#9AA0AB', marginTop:'6px'}}>
                              Se você não responder até {prazoTxt}, vale o registro de {nomeProf}.
                            </div>
                          </>
                        ) : jaDisse ? (
                          <div style={{fontSize:'12px', color: jaDisse === 'fui' ? '#0A6F4D' : jaDisse === 'nao_veio' ? '#991B1B' : '#92400E', marginTop:'4px', fontWeight:'600', lineHeight:1.5}}>
                            <Ico n={jaDisse === 'nao_veio' ? 'user-exclamation' : 'circle-check'} s={12} /> {jaDisse === 'fui'
                              ? `Você disse que fez a aula — se ${nomeProf} não responder até ${prazoTxt}, ela fecha como dada`
                              : jaDisse === 'nao_veio'
                                ? (ehMensalAluno ? `Você disse que ${nomeProf} não veio — fica registrado. Combine a reposição com ${nomeProf}.`
                                   : nvS.voltaEm > new Date() ? `Você disse que ${nomeProf} não veio — se ele não responder até ${quandoSP(nvS.voltaEm)}, a aula volta para o seu pacote`
                                   : `Você disse que ${nomeProf} não veio — ele não respondeu em 24h: a aula volta para o seu pacote na próxima hora`)
                              : 'Você disse que não foi — falta o professor fechar'}
                          </div>
                        ) : escolhaAberta === chEsc ? (
                          // v140: "Nao aconteceu" -> [Eu nao fui] [Cadu nao veio]
                          <EscolhaNaoAconteceuSM nomeProf={nomeProf} disabled={salvando} liberaNaoVeioEm={liberaS} aulaRef={{ ...a, duracao_min: a.duracao_min || contrato?.duracao_min }}
                            opcoes={nvOkS ? ['eu_nao_fui', 'nao_veio'] : ['eu_nao_fui']}
                            onEscolher={escolher} onVoltar={() => setEscolhaAberta(null)} />
                        ) : (
                          <div style={{display:'flex', gap:'7px', marginTop:'8px'}}>
                            <button disabled={salvando} onClick={() => confirmarAulaAluno(a)}
                              style={{flex:1, padding:'10px', minHeight:'44px', borderRadius:'10px', border:'none', background:'#0E9F6E', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                              Fiz a aula
                            </button>
                            <button disabled={salvando} onClick={() => setEscolhaAberta(chEsc)}
                              style={{padding:'10px 14px', minHeight:'44px', borderRadius:'10px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                              Não aconteceu
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {semResposta.length > 6 && (
                  <p style={{fontSize:'12px', color:'#92400E', marginTop:'8px'}}>…e mais {semResposta.length - 6}.</p>
                )}
              </div>
              );
            })()}

            {/* v140: "Cadu perguntou se você vai" — as aulas que nao sao de hoje (a de hoje fica no cartao "Hoje") */}
            <FaixaPresencaAlunoSM itens={aulas.filter(x => x.data !== hojeStr && perguntaAbertaSM(x))}
              nomeProf={prof?.nome?.split(' ')[0] || 'Seu professor'} prazoTexto={consequenciaPresenca}
              onNaoVou={a => naoVouPresenca(a, 'faixa')}
              onFeito={(a, res) => {
                if (res && res.ok && res.data && !res.data.ja_mudou) setAulas(prev => prev.map(x => x.id === a.id ? { ...x, presenca_vou_em: res.data.vou_em || new Date().toISOString() } : x));
                if (dados?.id) loadContexto(dados.id);
              }} />

            {/* v136: o professor remarcou (ja' vale) — o aluno diz se consegue. Acima do "Hoje". */}
            <FaixaRemarcacaoAlunoSM itens={remarcacoesValendo} nomeProf={prof?.nome?.split(' ')[0] || 'Seu professor'}
              onFeito={(s, res) => {
                if (res && res.ok) setRemarcacoesProf(prev => prev.filter(x => x.id !== s.id));
                if (dados?.id) loadContexto(dados.id);
              }} />

            {temAulaHoje ? (
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg">
                <p className="text-xs text-indigo-100 uppercase tracking-wider mb-2"><Ico n="calendar" /> Hoje</p>
                {aulasHojeAluno.map((a, i) => {
                  const d = new Date(a.data+'T12:00');
                  // v135 (Marcus 08/10): a aula das 11h nao aconteceu e o aluno nao tinha onde dizer isso —
                  // os botoes "Fiz a aula / Nao fui" so' existiam para aula de dia anterior. Agora a aula de
                  // HOJE que terminou sem registro do professor pergunta "como foi?" ali mesmo.
                  // v140: a duracao do acordo entra na conta (aula de 90 min nao "termina" com 60); o "Nao
                  // aconteceu" pergunta [Eu nao fui] / [Cadu nao veio]; durante a aula, "Chamar Cadu no
                  // WhatsApp" e, 15 min depois do inicio, "Cadu nao chegou?"; a pergunta "vai?" com
                  // [Vou] [Nao vou conseguir]; [Gerenciar] so' antes da aula.
                  // v142 (B): [✓ Vou] [Nao vou] tambem sem a pergunta; depois do "Vou", "Voce avisou que vai ✓"
                  // + "Desfazer"; com eles na tela, o botao de cima vira "Mais opcoes".
                  const aD = { ...a, duracao_min: a.duracao_min || contrato?.duracao_min };
                  // v149 (Marcus 28/10, 13:10): o aluno responde "Fiz a aula" assim que a aula termina — sem a folga de 15 min do professor
                  const est = estadoAulaSM(aD, new Date(), 0);
                  const nvH = naoVeioSM(aD);
                  const jaDisse = a.confirmada_aluno_em ? 'fez' : nvH ? 'nao_veio' : a.falta_declarada_aluno_em ? 'nao' : null;
                  const nomeP = prof?.nome?.split(' ')[0] || 'seu professor';
                  const NomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
                  const podeDizer = a.status === 'agendada' && !a.desfecho_prof && !jaDisse && !avisoVigenteAula(a);
                  const horaA = String(a.hora || '').slice(0, 5);
                  const individualH = aulaIndividualSM(a);
                  const iniH = inicioAulaSP(a);
                  const liberaH = iniH ? new Date(iniH.getTime() + 15 * 60000) : null;
                  const naoChegou = !!liberaH && liberaH <= new Date();
                  // v156 (Marcus 29/10): o professor chegou mais cedo — "A aula comecou" desde 30 min antes do horario
                  const quaseH = est === 'futura' && quaseComecandoSM(a);
                  const pergunta = perguntaAbertaSM(a);
                  const pc = jaDisse ? { pergunta: false, mostrar: false, feito: false } : presencaDoCartao(a, est);
                  const chEscH = 'hoje:' + a.id;
                  const foneProf = prof?.telefone || '';
                  const whatsProf = origemW => { uso.acao('aluno.prof_whats', { origem: origemW });
                    compartilharOuWhats(waLinkSM(foneProf, `Oi ${NomeP}! Estou no local da aula das ${horaA} te esperando.`), 'aluno_esperando'); };
                  const linkSt = {marginTop:'8px', minHeight:'44px', border:'none', background:'none', color:'white', fontSize:'13px', fontWeight:'700', textDecoration:'underline', cursor:'pointer', padding:0, opacity:0.95, display:'inline-flex', alignItems:'center', gap:'6px', fontFamily:'inherit'};
                  return (
                    <div key={a.id} className={i > 0 ? 'mt-3 pt-3 border-t border-white/20' : ''}>
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 rounded-xl px-4 py-3 text-center min-w-[56px]">
                        <p className="text-xs text-indigo-100">{DIAS[d.getDay()]}</p>
                        <p className="text-2xl font-bold">{d.getDate()}</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-xl font-bold">{a.hora?.slice(0,5)}</p>
                        <p className="text-sm text-indigo-100">
 {a.status === 'realizada'? 'Realizada'
                            : a.status === 'cancelada_aluno'|| a.status === 'cancelada_professor'? 'Cancelada'
                            : avisoVigenteAula(a) ? (avisoDoProfSM(a) ? `${NomeP} registrou que você avisou que não vinha` : 'Você avisou que não vai')
                            : jaDisse === 'nao_veio' ? `Você avisou ${quandoRelSM(nvH.em)} que ${nomeP} não chegou`
                            : jaDisse === 'fez' ? (est === 'agora' || est === 'futura' ? `Você confirmou a aula ✓ · ${nomeP} fecha` : `Você disse que fez a aula · ${nomeP} fecha`)
                            : jaDisse === 'nao' ? `Você disse que não aconteceu · ${nomeP} fecha com o motivo`
                            : est === 'aguardando' ? 'Terminou · como foi?'
                            : est === 'agora' ? 'Acontecendo agora'
                            : quaseH && podeDizer ? 'Começa em instantes'
                            : a.presenca_vou_em ? 'Você avisou que vai ✓'
                            : pergunta ? `${NomeP} perguntou se você vai`
                            : 'Aula marcada'}
                        </p>
                        <SeloPedido aulaId={a.id} escuro />
                        <SeloAviso aula={a} escuro nomeProf={NomeP} curto />
                      </div>
                      {a.status === 'agendada' && !jaDisse && est === 'futura' && (
                        <button onClick={() => setAcaoAula({ aula: a, modo: 'opcoes' })}
                          className="bg-white/20 hover:bg-white/30 text-white text-sm font-semibold px-3 py-2 rounded-xl transition-colors" style={{minHeight:'44px'}}>
                          {pc.mostrar || pc.feito || (quaseH && podeDizer && individualH) ? 'Mais opções' : 'Gerenciar'}
                        </button>
                      )}
                    </div>
                    <BlocoVouAlunoSM aula={a} nomeProf={NomeP} mostrar={pc.mostrar} feito={pc.feito}
                      consequencia={pc.pergunta ? consequenciaPresenca(a) : ''} busy={vouBusy}
                      onVou={() => vouPresenca(a, pc.pergunta ? 'hoje' : 'hoje_sem_pergunta')} onNaoVou={() => naoVouPresenca(a, 'hoje')}
                      onDesfeito={aoDesfazerVouCartao(a.id)} />
                    {/* v142 (A2, A6, A7): "Desfazer" do aviso de falta ou da resposta, enquanto o professor nao respondeu */}
                    {desfazerCartaoAlunoSM(a) && (
                      <BotaoDesfazerSM escuro alvo={{ ref_tipo: 'aula', ref_id: a.id, esperado: desfazerCartaoAlunoSM(a) }} papel="aluno" nomeOutro={NomeP}
                        onFeito={() => { if (dados?.id) loadContexto(dados.id); }} />
                    )}
                    {podeDizer && est === 'aguardando' && (escolhaAberta === chEscH && individualH ? (
                      <EscolhaNaoAconteceuSM escuro nomeProf={NomeP} disabled={salvando} liberaNaoVeioEm={liberaH}
                        onEscolher={op => { setEscolhaAberta(null); responderAulaAluno(a, false, op === 'nao_veio'
                          ? { motivo: 'prof_nao_veio', origem: 'hoje' }
                          : { origem: 'hoje', textoNaoFui: `Registrado. ${NomeP} fecha com o motivo — a aula só sai do seu pacote se foi falta sua.` }); }}
                        onVoltar={() => setEscolhaAberta(null)} />
                    ) : (
                      <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                        <button disabled={salvando} onClick={() => responderAulaAluno(a, true, { origem: 'hoje' })}
                          style={{flex:1, minHeight:'44px', borderRadius:'12px', border:'none', background:'white', color:'#1C43AE', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                          Fiz a aula
                        </button>
                        <button disabled={salvando} onClick={() => individualH ? setEscolhaAberta(chEscH)
                            : responderAulaAluno(a, false, { origem: 'hoje', textoNaoFui: `Registrado. ${NomeP} fecha com o motivo — a aula só sai do seu pacote se foi falta sua.` })}
                          style={{flex:1, minHeight:'44px', borderRadius:'12px', border:'1.5px solid rgba(255,255,255,0.6)', background:'transparent', color:'white', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                          Não aconteceu
                        </button>
                      </div>
                    ))}
                    {podeDizer && (est === 'agora' || quaseH) && individualH && (
                      <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start'}}>
                        {/* v150 (Marcus 28/10): durante a aula o aluno ja' confirma — o mesmo "fiz a aula" do fim.
                            v156: desde 30 min antes do horario (o banco aceita o "aconteceu" nessa janela) */}
                        <button disabled={salvando} onClick={() => responderAulaAluno(a, true, { origem: quaseH ? 'hoje_antes' : 'hoje_agora' })}
                          style={{marginTop:'12px', alignSelf:'stretch', minHeight:'44px', borderRadius:'12px', border:'none', background:'white', color:'#1C43AE', fontSize:'14px', fontWeight:'800', cursor:'pointer'}}>
                          <Ico n="circle-check" s={16} c="#1C43AE" /> A aula começou
                        </button>
                        {foneProf ? (
                          <button onClick={() => whatsProf('hoje_agora')} style={linkSt}>
                            <Ico n="brand-whatsapp" s={15} c="white" /> Chamar {NomeP} no WhatsApp
                          </button>
                        ) : null}
                        {naoChegou ? (
                          <button disabled={salvando} onClick={() => setAcaoAula({ aula: a, modo: 'nao_veio' })} style={{...linkSt, marginTop: foneProf ? '0' : '8px'}}>
                            <Ico n="user-exclamation" s={15} c="white" /> {NomeP} não chegou?
                          </button>
                        ) : null}
                      </div>
                    )}
                    {podeNaoVeioContraAvisoSM(aD) && (
                      // v141 (B6): o professor registrou que o aluno avisou — o aluno pode dizer que nao avisou e ele nao veio
                      <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start'}}>
                        <button disabled={salvando} onClick={() => { uso.acao('aluno.nao_veio_contra_aviso', { origem: 'hoje' }); setAcaoAula({ aula: a, modo: 'nao_veio' }); }} style={linkSt}>
                          <Ico n="user-exclamation" s={15} c="white" /> Não avisei — {NomeP} não veio
                        </button>
                      </div>
                    )}
                    {podeDizer && est === 'agora' && !individualH && (
                      // turma: como na v139
                      <button disabled={salvando}
                        onClick={() => {
                          if (!window.confirm(`Avisar ${nomeP} que a aula das ${horaA} não está acontecendo? Ele fecha com o motivo.`)) return;
                          responderAulaAluno(a, false, { origem: 'hoje_agora', textoNaoFui: `Avisado. ${nomeP} fecha com o motivo — a aula só sai do seu pacote se foi falta sua.` });
                        }}
                        style={{marginTop:'10px', minHeight:'44px', border:'none', background:'none', color:'white', fontSize:'13px', fontWeight:'700', textDecoration:'underline', cursor:'pointer', padding:0, opacity:0.95}}>
                        A aula não está acontecendo?
                      </button>
                    )}
                    {jaDisse === 'nao_veio' && nvH && nvH.pendente && (
                      <div style={{marginTop:'10px'}}>
                        <div style={{fontSize:'12.5px', color:'rgba(255,255,255,.92)', lineHeight:1.45}}>
                          {ehMensalAluno ? `Fica registrado que ele não veio. Combine a reposição com ${NomeP}.`
                            : textoVoltaNaoVeioSM(nvH.voltaEm, { aluno: true })}
                        </div>
                        <div style={{display:'flex', flexDirection:'column', alignItems:'flex-start'}}>
                          {foneProf ? (
                            <button onClick={() => whatsProf('hoje_nao_veio')} style={linkSt}>
                              <Ico n="brand-whatsapp" s={15} c="white" /> Chamar {NomeP} no WhatsApp
                            </button>
                          ) : null}
                          <button disabled={salvando} onClick={() => responderAulaAluno(a, true, { origem: 'hoje_chegou' })} style={{...linkSt, marginTop:'0'}}>
                            <Ico n="circle-check" s={15} c="white" /> Ele chegou, a aula aconteceu
                          </button>
                        </div>
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            ) : pedidoEsperando ? null : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
                <div className="bg-gray-100 rounded-xl p-3 text-2xl"><Ico n="calendar" /></div>
                <div>
                  <p className="font-semibold text-gray-700">Sem aula hoje</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {proximaAula
                      ? `Próxima: ${new Date(proximaAula.data+'T12:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})} às ${proximaAula.hora?.slice(0,5)}`
                      : 'Nenhuma aula agendada em breve'}
                  </p>
                </div>
              </div>
            )}

            {}
            {(() => {
              const valem = feriadosProx.filter(f => f.abrangencia === 'nacional' || f.professor_id === prof?.id
                || (f.cidade && prof?.cidade && f.cidade === prof.cidade));
              const naFeriado = aulas.filter(a => a.status === 'agendada' && valem.some(f => String(f.data).slice(0, 10) === String(a.data).slice(0, 10)))
                .sort((x, y) => String(x.data).localeCompare(String(y.data)));
              if (!naFeriado.length) return null;
              const a = naFeriado[0];
              const f = valem.find(v => String(v.data).slice(0, 10) === String(a.data).slice(0, 10));
              const quando = new Date(a.data + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
              return (
                <div style={{background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'16px', padding:'14px 16px'}}>
                  <p style={{fontSize:'14px', fontWeight:'700', color:'#16181D', lineHeight:1.45}}>
                    <Ico n="calendar-off" s={15} c="#D97706" /> {quando.charAt(0).toUpperCase() + quando.slice(1)} é feriado ({f?.nome}).
                  </p>
                  <p style={{fontSize:'13px', color:'#6E7480', marginTop:'4px', lineHeight:1.5}}>
                    Sua aula das {String(a.hora || '').slice(0, 5)} continua marcada. Se não puder ir, peça para remarcar.
                  </p>
                  <button onClick={() => setAcaoAula({ aula: a, modo: 'opcoes' })}
                    style={{marginTop:'10px', padding:'10px 14px', borderRadius:'10px', border:'1.5px solid #E2E4E9', background:'white', color:'#16181D', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>
                    Gerenciar esta aula
                  </button>
                </div>
              );
            })()}

            {/* Mensalidade nao tem saldo de aulas — o anel de creditos mostraria "0 de 0",
                que nao quer dizer nada. Cada tipo de cobranca fala a sua propria lingua.
                v142 (revisao): sem acordo ainda (esperando o professor completar) o cartao nao aparece —
                mostrava "Pacote pago: em aberto" de um pacote que nao existe. */}
            {!esperandoCompletarAluno && !pedidoEsperando && <Card>
              {ehMensalAluno ? (
                <>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">{minhaTurma ? 'Sua turma' : 'Sua mensalidade'}</p>
                  {minhaTurma && (
                    <div style={{background:'#F7F9FF', border:'1px solid #C2D4FF', borderRadius:'12px', padding:'12px 14px', marginBottom:'14px'}}>
                      <div style={{fontWeight:'800', fontSize:'15px'}}>{minhaTurma.nome}</div>
                      <div style={{fontSize:'12.5px', color:'#3D424C', marginTop:'2px'}}>{descreverHorariosTurma(minhaTurma.horarios)}{minhaTurma.local ? ' · ' + minhaTurma.local : ''}</div>
                      {minhaTurma.colegas.length > 0 && (
                        <div style={{display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'10px'}}>
                          {minhaTurma.colegas.map(c => <span key={c.id} style={{fontSize:'12px', fontWeight:'700', background:'white', border:'1px solid #E2E4E9', borderRadius:'999px', padding:'4px 10px'}}>{c.nome.split(' ')[0]}</span>)}
                        </div>
                      )}
                      <div style={{fontSize:'11.5px', color:'#6E7480', marginTop:'10px'}}>Turma tem horário fixo: se faltar, não há reposição. A mensalidade é sua, paga direto ao professor.</div>
                    </div>
                  )}
                  <div style={{marginBottom:'18px'}}>
                    <PontosAulas contrato={contrato} aulas={[...aulas, ...historico]} tamanho={16} />
                  </div>
                  <div>
                    <LinhaFato ic="calendar-dollar" rotulo="Mensalidade" valor={`${fmtBRL(Number(contrato?.valor_mensal || contrato?.preco_negociado || 0))} · dia ${contrato?.dia_vencimento || '—'}`} />
                    {/* Mensalidade: o ULTIMO pagamento (antes somava todos os meses do acordo). */}
                    <LinhaFato ic="receipt" rotulo="Último pagamento" valor={ultimoPagamento ? fmtBRL(ultimoPagamento.valor) + (dataUltimoPag ? ` em ${dataUltimoPag.slice(0, 5)}` : '') : '—'} />
                    {sequenciaSemanas >= 2 && <LinhaFato ic="flame" rotulo="Sequência" valor={`${sequenciaSemanas}ª semana seguida treinando`} />}
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    // v156 (Marcus 29/10): o pacote atual acabou (saldo 0, nenhuma aula aberta) e o proximo ja' esta' na fila
                    // (o robo vira em ate' 10 min, ou espera a data de inicio): o destaque vai para o PROXIMO — o concluido
                    // vira uma linha discreta que abre as bolinhas e as datas dele.
                    const abertasK = proximoContrato && contrato ? aulas.filter(x => x.contrato_id === contrato.id && (x.status === 'agendada' || x.status === 'aguardando_confirmacao')) : [];
                    if (!(proximoContrato && credAluno.total > 0 && credAluno.restante === 0 && abertasK.length === 0)) return null;
                    const aulasProx = [...aulas, ...historico].filter(x => x.contrato_id === proximoContrato.id);
                    const pgP = (pagamentos || []).find(pg => pg.contrato_id === proximoContrato.id && pg.status !== 'cancelado');
                    const nTot = Number(proximoContrato.total_aulas) || 0;
                    return (
                      <>
                        <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Seu próximo pacote</p>
                        <PontosAulas contrato={proximoContrato} aulas={aulasProx} tamanho={16} soPontos />
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px', marginTop:'12px'}}>
                          <div style={{minWidth:0}}>
                            <div style={{fontSize:'17px', fontWeight:800, color:'#16181D'}}>Começa {diaEDataSM(proximoContrato.data_inicio)}</div>
                            <div style={{fontSize:'13px', color:'#6E7480', marginTop:'2px'}}>{nTot ? `${nTot} aulas` : 'Pacote'}{proximoContrato.data_fim ? ` até ${fmtDiaMes(proximoContrato.data_fim)}` : ''}{pgP && pgP.status !== 'pago' ? ` · ${fmtBRL(pgP.valor)}` : ''}</div>
                          </div>
                          <SeloPg s={seloPgProximo(pgP, proximoContrato, true)} />
                        </div>
                        {botaoPagarFilaInicio(pgP)}
                        <details style={{marginTop:'14px', borderTop:'1px solid #F0F1F4', paddingTop:'4px'}} onToggle={e => { if (e.currentTarget.open) uso.acao('aluno.pacote_anterior_abrir'); }}>
                          <summary style={{cursor:'pointer', listStyle:'none', display:'flex', alignItems:'center', gap:'8px', minHeight:'40px', fontSize:'13.5px', color:'#3D424C'}}>
                            <Ico n="circle-check" s={17} c="#0E9F6E" />
                            <span style={{flex:1}}>Pacote anterior concluído · {credAluno.usado} de {credAluno.total}</span>
                            <Ico n="chevron-down" s={14} c="#9AA0AB" />
                          </summary>
                          <div style={{padding:'6px 0 2px'}}>
                            <PontosAulas contrato={contrato} aulas={aulasContratoAluno} tamanho={12} comDatas />
                            <LinhaFato ic="receipt" rotulo="Pacote pago" valor={dataUltimoPag ? `${fmtBRL(pagoMes)} em ${dataUltimoPag.slice(0, 5)}` : (pagoMes ? fmtBRL(pagoMes) : 'em aberto')} />
                            {linhaAproveitamento()}
                          </div>
                        </details>
                      </>
                    );
                  })() || (
                  <>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Suas aulas</p>
                  <PontosAulas contrato={contrato} aulas={aulasContratoAluno} tamanho={16} comDatas proximoInicio={proximoContrato?.data_inicio || null} semPrevisao={pacoteNaoComecou} />
                  <div style={{marginTop:'14px'}}>
                    {(() => {
                      // v156: pacote novo que ainda nao comecou (0 usadas, 1a aula no futuro): "Comeca ter 03/11" + o selo do
                      // pagamento dele, no lugar de "Pacote pago: em aberto"
                      const primK = primAulaNaoComecou;
                      const naoComecou = pacoteNaoComecou;
                      if (!naoComecou) return <LinhaFato ic="receipt" rotulo="Pacote pago" valor={dataUltimoPag ? `${fmtBRL(pagoMes)} em ${dataUltimoPag.slice(0, 5)}` : (pagoMes ? fmtBRL(pagoMes) : 'em aberto')} />;
                      const pgK = (pagamentos || []).find(pg => pg.contrato_id === contrato.id && pg.status !== 'cancelado');
                      return (
                        <div style={{display:'flex', alignItems:'center', gap:'10px', padding:'11px 0', borderTop:'1px solid #F0F1F4'}}>
                          <Ico n="calendar-event" s={18} c="#2F6BFF" />
                          <div style={{flex:1, minWidth:0}}>
                            <div style={{fontSize:'14.5px', fontWeight:800, color:'#16181D'}}>Começa {diaEDataSM(primK)}</div>
                            <div style={{fontSize:'12.5px', color:'#6E7480', marginTop:'1px'}}>{credAluno.total} aulas{contrato.data_fim ? ` até ${fmtDiaMes(contrato.data_fim)}` : ''}</div>
                          </div>
                          <SeloPg s={pgK ? seloPgProximo(pgK, contrato, true) : (pagoMes ? seloPgProximo({ status: 'pago' }, contrato, true) : null)} />
                        </div>
                      );
                    })()}
                    {proximoContrato ? (() => {
                      // v149: os dois pacotes juntos — o atual (o que falta) e o da fila (quando comeca e como esta' o pagamento)
                      // v155: aula do atual que cai no periodo do proximo entra nele (a virada leva) — nao conta aqui
                      const abertasA = aulas.filter(x => x.contrato_id === contrato.id && (x.status === 'agendada' || x.status === 'aguardando_confirmacao')
                        && !(proximoContrato.data_inicio && x.data >= String(proximoContrato.data_inicio).slice(0, 10)));
                      const ultA = abertasA.map(x => x.data).sort().pop();
                      // v155: o "faltam" e' o mesmo numero das bolinhas logo acima (saldo do pacote); a data vem da agenda
                      const faltamA = credAluno.total > 0 ? credAluno.restante : abertasA.length;
                      const pgP = (pagamentos || []).find(pg => pg.contrato_id === proximoContrato.id && pg.status !== 'cancelado');
                      // v152: a cobranca vence no dia da ultima aula do atual — no dia ou depois, diz "vence hoje"/"venceu"
                      return (
                        <>
                          <LinhaFato ic="calendar-event" rotulo="Pacote atual" valor={faltamA > 0 ? `${ultA ? 'termina ' + diaEDataSM(ultA) + ' · ' : ''}falta${faltamA === 1 ? '' : 'm'} ${faltamA}` : 'terminando'} />
                          {/* v156 (Marcus 29/10): a situacao do pagamento num selo a' direita — verde pago, azul avisou, ambar vence, vermelho venceu */}
                          <div style={{display:'flex', alignItems:'center', gap:'10px', padding:'11px 0', borderTop:'1px solid #F0F1F4'}}>
                            <Ico n="calendar-plus" s={18} c="#6E7480" />
                            <div style={{flex:1, minWidth:0}}>
                              <div style={{fontSize:'14px', color:'#6E7480'}}>Próximo pacote</div>
                              <div style={{fontSize:'14.5px', fontWeight:800, color:'#16181D'}}>começa {diaEDataSM(proximoContrato.data_inicio)}{pgP && pgP.status !== 'pago' ? ` · ${fmtBRL(pgP.valor)}` : ''}</div>
                            </div>
                            <SeloPg s={seloPgProximo(pgP, proximoContrato, true)} />
                          </div>
                          {botaoPagarFilaInicio(pgP)}
                        </>
                      );
                    })() : (
                    <>
                    {!pacoteNaoComecou && <LinhaFato ic="refresh" rotulo="Previsão de renovação" valor={dataFim ? dataFim.toLocaleDateString('pt-BR').slice(0, 5) + (diasRestantes !== null && diasRestantes <= 7 ? ` · ${diasRestantes} dia${diasRestantes !== 1 ? 's' : ''}` : '') : '—'}
                      alerta={diasRestantes !== null && diasRestantes <= 7} />}
                    {(() => {
                      // v152: falta 1 aula (ou acabou) e nada combinado — o aluno pede a renovacao daqui mesmo
                      // v155: tambem pacote sem total de aulas ou de turma (so' o link discreto) e, com qualquer numero de
                      // aulas, "Pagar o proximo pacote adiantado" (Pix + "Ja' paguei o proximo" em Pagamentos)
                      if (!contrato || contrato.tipo_cobranca === 'mensal' || acordoK) return null;
                      const saldoConhecido = !contrato.turma_id && !!creditosDB && creditosDB.total > 0;
                      const restR = saldoConhecido ? Math.max(0, (creditosDB.total || 0) - (creditosDB.usado || 0)) : null;
                      const nP = prof?.nome?.split(' ')[0] || 'seu professor';
                      const irPagarProximo = () => {
                        uso.acao('aluno.proximo_pagar_inicio', { pix: temPixAluno, restantes: restR });
                        setMostrarAntecipar(true); setTela('financeiro');
                        setTimeout(() => { try { document.getElementById('bloco-pagar-proximo')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {} }, 350);
                      };
                      // com o pacote atual em aberto, o laranja la' de cima vem primeiro
                      const botaoPagar = emAbertoAluno.length > 0 ? null : (
                        <button type="button" onClick={irPagarProximo}
                          style={{width:'100%', marginTop:'10px', padding:'11px', borderRadius:'12px', border:'1.5px solid #C2D4FF', background:'#E7EDFF', color:'#2F6BFF', fontWeight:'800', fontSize:'13.5px', cursor:'pointer'}}>
                          Pagar o próximo pacote adiantado
                        </button>
                      );
                      if (avisoRenovar && avisoRenovar.contrato_id === contrato.id) return (
                        <>
                          <div style={{marginTop:'12px', background:'#EFFAF5', border:'1.5px solid #B7E9D6', borderRadius:'14px', padding:'14px 16px', fontSize:'13.5px', color:'#0A6F4D', lineHeight:1.5}}>
                            <Ico n="circle-check" c="#0E9F6E" /> {avisoRenovar.jaPagou ? `Você avisou ${nP} que já pagou o próximo pacote.` : `Você avisou ${nP}.`} Ele combina o próximo pacote e você confirma aqui.
                            {' '}<button type="button" onClick={esquecerAvisoRenovar} style={{background:'none', border:'none', padding:0, color:'#0A6F4D', fontWeight:'800', textDecoration:'underline', cursor:'pointer', fontSize:'13px'}}>Avisar de novo</button>
                          </div>
                          {!avisoRenovar.jaPagou && botaoPagar}
                        </>
                      );
                      // v153 (Marcus 29/10): renovar antes do fim tambem e' facil — com mais de 1 aula (ou sem saldo conhecido), um link discreto
                      if (restR === null || restR > 1) return (
                        <>
                          <button type="button" disabled={salvando} onClick={() => avisarRenovacaoAluno(false)}
                            style={{width:'100%', marginTop:'10px', padding:'11px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#2F6BFF', fontWeight:'800', fontSize:'13.5px', cursor:'pointer'}}>
                            Já quero renovar o pacote
                          </button>
                          {botaoPagar}
                        </>
                      );
                      const ultR = aulas.filter(x => x.contrato_id === contrato.id && (x.status === 'agendada' || x.status === 'aguardando_confirmacao')).map(x => x.data).sort().pop();
                      return (
                        <div style={{marginTop:'12px', background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'14px', padding:'14px 16px'}}>
                          <div style={{fontWeight:'800', fontSize:'15px', color:'#16181D'}}>{restR === 1 ? 'Falta 1 aula no seu pacote' : 'Seu pacote terminou'}</div>
                          {ultR && <div style={{fontSize:'13px', color:'#3D424C', marginTop:'2px'}}>Termina {diaEDataSM(ultR)}</div>}
                          <button disabled={salvando} onClick={() => avisarRenovacaoAluno(false)}
                            style={{width:'100%', marginTop:'12px', padding:'12px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer'}}>
                            Quero renovar
                          </button>
                          {botaoPagar}
                        </div>
                      );
                    })()}
                    </>
                    )}
                    {linhaAproveitamento()}
                    {sequenciaSemanas >= 2 && <LinhaFato ic="flame" rotulo="Sequência" valor={`${sequenciaSemanas}ª semana seguida treinando`} />}
                  </div>
                  </>
                  )}
                </>
              )}
            </Card>}

            {}
            {proximaAula ? (() => {
              // v142 (B): aula de amanha — [✓ Vou] [Nao vou] tambem aqui (sem a pergunta do professor; com a
              // pergunta, quem responde e' a faixa "Cadu perguntou se você vai", la' em cima), "Voce avisou
              // que vai ✓" + "Desfazer" e "Mais opcoes" no lugar de "Gerenciar".
              const NomePx = prof?.nome?.split(' ')[0] || 'Seu professor';
              const pcx = presencaDoCartao(proximaAula, estadoAulaSM({ ...proximaAula, duracao_min: proximaAula.duracao_min || contrato?.duracao_min }));
              const mostrarX = pcx.mostrar && !pcx.pergunta;
              const linhaX = proximaAula.status === 'agendada' && !avisoVigenteAula(proximaAula)
                ? (pcx.feito ? 'Você avisou que vai ✓' : pcx.pergunta ? `${NomePx} perguntou se você vai` : null) : null;
              return (
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg">
                <p className="text-xs text-indigo-100 uppercase tracking-wider mb-3">Próxima aula</p>
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 rounded-xl px-4 py-3 text-center min-w-[56px]">
                    <p className="text-xs text-indigo-100">{DIAS[new Date(proximaAula.data+'T12:00').getDay()]}</p>
                    <p className="text-2xl font-bold">{new Date(proximaAula.data+'T12:00').getDate()}</p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xl font-bold">{proximaAula.hora?.slice(0,5)}</p>
                    <p className="text-sm text-indigo-100">{linhaX || new Date(proximaAula.data+'T12:00').toLocaleDateString('pt-BR',{month:'long',year:'numeric'})}</p>
                    <SeloPedido aulaId={proximaAula.id} escuro />
                    <SeloAviso aula={proximaAula} escuro />
                  </div>
                  <button onClick={() => setAcaoAula({ aula: proximaAula, modo: 'opcoes' })}
                    className="bg-white/20 hover:bg-white/30 text-white text-sm font-semibold px-3 py-2 rounded-xl transition-colors" style={{minHeight:'44px'}}>
                    {mostrarX || pcx.feito ? 'Mais opções' : 'Gerenciar'}
                  </button>
                </div>
                <BlocoVouAlunoSM aula={proximaAula} nomeProf={NomePx} mostrar={mostrarX} feito={pcx.feito} consequencia="" busy={vouBusy}
                  onVou={() => vouPresenca(proximaAula, 'proxima_sem_pergunta')} onNaoVou={() => naoVouPresenca(proximaAula, 'proxima')}
                  onDesfeito={aoDesfazerVouCartao(proximaAula.id)} />
              </div>
              );
            })() : pedidoEsperando ? null : (
              <Card className="text-center py-8">
                <div className="text-3xl mb-2"><Ico n="calendar" /></div>
                <p className="text-gray-500 text-sm">Nenhuma aula agendada</p>
              </Card>
            )}
            {/* 21/10 (Marcus): o caminho para a aula extra nao era obvio (so' na aba Aulas). Atalho logo abaixo
                do cartao da proxima aula — abre a aba Aulas ja' no formulario. */}
            {prof && (
              <button onClick={() => { uso.acao('aluno.aula_extra_atalho'); setTela('aulas'); setNovaAula({ data: '', hora: '', obs: '' });
                                       setTimeout(() => { try { document.getElementById('form-aula-extra')?.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {} }, 120); }}
                style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', width:'100%', minHeight:'44px', padding:'10px 14px', borderRadius:'14px',
                        border:'1.5px dashed #C2D4FF', background:'white', color:'#2F6BFF', fontSize:'13.5px', fontWeight:'700', cursor:'pointer'}}>
                <Ico n="calendar-plus" s={16} c="#2F6BFF" /> Marcar aula extra
              </button>
            )}

            {}
            {reajustePendente && cartaoReajuste()}
            {/* v147 (revisao): quem ja' mandou pedido a um professor ou tocou em "Usar sem professor" nao passa mais
                pelo "Bem-vindo" — o "Colar convite" tambem fica aqui enquanto nenhuma ficha tiver professor. */}
            {APP_NATIVO && !prof && !todosVinculos.some(v => v.professor_id) && <ColarConvite quem="aluno" onde="inicio_aluno" />}

            {}
            {}
            {!prof && pedidoEsperando ? (
              <button onClick={() => { uso.acao('aluno.convidar_prof_abrir', { origem: 'esperando' }); setAddProfessor(true); }}
                style={{width:'100%',padding:'10px',borderRadius:'12px',background:'#F0F1F4',border:'1px solid #E2E4E9',color:'#6E7480',fontSize:'12px',fontWeight:'600',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                <Ico n="plus" /> Adicionar outro professor
              </button>
            ) : !prof && convitesProfPendentes.length > 0 ? (
              <div style={{background:'#FFFBEB',border:'1.5px solid #FDE68A',borderRadius:'16px',padding:'14px 16px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'8px'}}>
                  <span style={{fontSize:'20px'}}><Ico n="hourglass" c="#D97706" /></span>
                  <div>
                    <p style={{fontSize:'13px',fontWeight:'700',color:'#92400E',margin:0}}>Convite enviado — aguardando confirmação</p>
                    {convitesProfPendentes.map(c => (
                      <p key={c.id} style={{fontSize:'12px',color:'#B45309',margin:'2px 0 0'}}>
                        {c.dados?.nomeProfDestino || 'Professor'} · expira {new Date(c.expires_at).toLocaleDateString('pt-BR')}
                      </p>
                    ))}
                  </div>
                </div>
                <button onClick={() => { uso.acao('aluno.convidar_prof_abrir', { origem: 'pendente' }); setAddProfessor(true); }}
                  style={{width:'100%',padding:'8px',borderRadius:'10px',background:'#FEF3C7',border:'1px solid #FDE68A',color:'#92400E',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="plus" /> Convidar outro professor
                </button>
              </div>
            ) : !prof ? (
              <div style={{background:'#F7F8FA',border:'1.5px dashed #C7CAD1',borderRadius:'16px',padding:'14px 16px'}}>
                <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'8px'}}>
                  <span style={{fontSize:'20px'}}><Ico n="link" /></span>
                  <div>
                    <p style={{fontSize:'13px',fontWeight:'700',color:'#3D424C',margin:0}}>Sem professor vinculado</p>
                    <p style={{fontSize:'12px',color:'#9AA0AB',margin:'2px 0 0'}}>Use o app de forma independente ou convide seu professor.</p>
                  </div>
                </div>
                <button onClick={() => { uso.acao('aluno.convidar_prof_abrir', { origem: 'sem_prof' }); setAddProfessor(true); }}
                  style={{width:'100%',padding:'8px',borderRadius:'10px',background:'#E7EDFF',border:'1px solid #C2D4FF',color:'#2F6BFF',fontSize:'12px',fontWeight:'700',cursor:'pointer'}}>
                  <Ico n="plus" /> Convidar professor
                </button>
              </div>
            ) : (
              <button onClick={() => { uso.acao('aluno.convidar_prof_abrir', { origem: 'outro' }); setAddProfessor(true); }}
                style={{width:'100%',padding:'10px',borderRadius:'12px',background:'#F0F1F4',border:'1px solid #E2E4E9',color:'#6E7480',fontSize:'12px',fontWeight:'600',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                <Ico n="plus" /> Adicionar outro professor
              </button>
            )}

            {}
            {contrato && (
              <Card>
                <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3">Plano Ativo</p>
                <p className="text-lg font-bold text-gray-800">{plano?.nome || 'Plano personalizado'}</p>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">{ehMensalAluno ? 'Mensalidade' : 'Valor do pacote'}</p>
                    <p className="text-base font-bold text-gray-800 mt-1">{fmtBRL(ehMensalAluno && Number(contrato.valor_mensal) > 0 ? contrato.valor_mensal : contrato.preco_negociado)}{ehMensalAluno ? '/mês' : ''}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400">{proximoContrato ? 'Próximo pacote começa' : 'Previsão de renovação'}</p>
                    <p className="text-base font-bold text-gray-800 mt-1">{proximoContrato ? (proximoContrato.data_inicio ? fmtDataBR(proximoContrato.data_inicio) : '—') : dataFim ? dataFim.toLocaleDateString('pt-BR') : '—'}</p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        )}

        {}
        {tela === 'aulas' && (
          <div className="flex flex-col gap-4">
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Minhas Aulas</h1>
              <p className="text-sm text-gray-400 mt-1">{aulas.length} aula{aulas.length !== 1?'s':''} agendada{aulas.length !== 1?'s':''}
                {/* v136 (Marcus 09/10): o historico ficava no fim da tela e ninguem achava */}
                {historico.length > 0 && (
                  <button type="button" onClick={() => { uso.acao('aulas.ir_historico'); document.getElementById('historico-aulas-aluno')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    style={{marginLeft:'8px', border:'none', background:'none', padding:0, color:'#2F6BFF', fontWeight:700, fontSize:'13px', cursor:'pointer', minHeight:'32px'}}>
                    · Ver histórico ↓
                  </button>
                )}
              </p>
              {/* O Cadu reparou que a agenda nao dizia se aquelas aulas ja' estao pagas —
                  e' a primeira duvida de quem abre a tela ("essas aulas sao minhas mesmo?").
                  A cobranca fica no Financeiro; aqui vai so' o selo, que e' a resposta. */}
              {contrato && (() => {
                const emAberto = pagamentosForaDaFila.filter(p => p.status === 'pendente');
                const avisado = emAberto.length > 0 && emAberto.every(p => p.declarado_pago_em);
                // v152: o selo fala do pacote ATUAL. Com o proximo ja' marcado na lista, "Aulas pagas"
                // cobriria aulas cuja cobranca ainda esta' aberta — diz "Pacote atual pago".
                const nAtuais = aulas.filter(x => !ehAulaDaFila(x)).length;
                // v155: pacote novo sem aceite nao e' "pago" nem "em aberto": falta confirmar
                const est = emAberto.length === 0 && (pagamentos || []).some(ehCobrancaSemAceite)
                  ? { t: 'Confirme o pacote antes de pagar', ic: 'writing-sign', bg: '#E7EDFF', bd: '#C2D4FF', c: '#1C43AE' }
                  : emAberto.length === 0
                  ? { t: nAtuais < aulas.length ? 'Pacote atual pago' : aulas.length === 1 ? 'Aula paga' : 'Aulas pagas', ic: 'circle-check', bg: '#DCF5EC', bd: '#8FDFC4', c: '#0A6F4D' }
                  : avisado
                    ? { t: 'Pagamento aguardando confirmação', ic: 'clock', bg: '#E7EDFF', bd: '#C2D4FF', c: '#1C43AE' }
                    : { t: 'Pagamento em aberto', ic: 'alert-triangle', bg: '#FFFBEB', bd: '#FDE68A', c: '#92400E' };
                return (
                  <span style={{display:'inline-flex', alignItems:'center', gap:'5px', marginTop:'8px',
                                background:est.bg, border:`1.5px solid ${est.bd}`, color:est.c,
                                borderRadius:'20px', padding:'4px 11px', fontSize:'12px', fontWeight:'800'}}>
                    <Ico n={est.ic} s={13} c={est.c} /> {est.t}
                  </span>
                );
              })()}
            </div>
            {/* U26 — aula combinada fora do app. O aluno lanca, fica em cinza pontilhado ate'
                o professor confirmar; nunca conta no saldo antes disso. */}
            {aulasPropostas.length > 0 && (
              <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                {aulasPropostas.map(a => (
                  <div key={a.id} style={{background:'#F7F8FA', border:'1.5px dashed #C7CAD1', borderRadius:'14px', padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px'}}>
                    <Ico n="clock" s={18} c="#9AA0AB" />
                    <div style={{flex:1}}>
                      <div style={{fontSize:'13.5px', fontWeight:'700', color:'#3D424C'}}>
                        {new Date(a.data + 'T12:00').toLocaleDateString('pt-BR', { weekday:'short', day:'numeric', month:'short' }).replace('.', '')} às {String(a.hora||'').slice(0,5)}
                      </div>
                      <div style={{fontSize:'11.5px', color:'#9AA0AB', marginTop:'1px'}}>lançada por você · aguardando {prof?.nome?.split(' ')[0] || 'o professor'} confirmar</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!novaAula ? (
              <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', gap:'8px'}}>
                <button onClick={() => setNovaAula({ data: '', hora: '', obs: '' })}
                  style={{display:'flex', alignItems:'center', gap:'8px', flex:1, minWidth:'200px', padding:'12px 14px', borderRadius:'14px',
                          border:'1.5px dashed #C2D4FF', background:'white', color:'#2F6BFF', fontSize:'13px', fontWeight:'700', cursor:'pointer', textAlign:'left'}}>
                  <Ico n="calendar-plus" s={16} c="#2F6BFF" /> Marcar aula extra
                </button>
                <Ajuda texto={<>É para a aula que vocês combinaram por fora — no WhatsApp, no fim do treino. Você lança o dia e a hora aqui, {prof?.nome?.split(' ')[0] || 'seu professor'} recebe o aviso e confirma. <strong>Enquanto ele não confirmar, ela não entra na agenda nem desconta do seu pacote.</strong></>} />
              </div>
            ) : (
              <div id="form-aula-extra" style={{background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'14px', padding:'14px'}}>
                <p style={{fontSize:'13px', fontWeight:'700', color:'#1C43AE', marginBottom:'4px'}}>Combinou uma aula a mais com {prof?.nome?.split(' ')[0] || 'o professor'}?</p>
                <p style={{fontSize:'12px', color:'#6E7480', marginBottom:'10px'}}>Lance aqui. {prof?.nome?.split(' ')[0] || 'O professor'} recebe o aviso e confirma com um toque.</p>
                <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                  <CampoDataHoraSM tipo="date" rotulo="Dia" vazio="Escolher o dia" value={novaAula.data} min={hojeSP()}
                    onChange={v => setNovaAula(n => ({ ...n, data: v }))} style={{flex:1, minWidth:'150px'}} />
                  <CampoDataHoraSM tipo="time" rotulo="Horário" vazio="Escolher" value={novaAula.hora}
                    onChange={v => setNovaAula(n => ({ ...n, hora: v }))} style={{width:'140px'}} />
                </div>
                <input value={novaAula.obs} onChange={e => setNovaAula(n => ({ ...n, obs: e.target.value }))} placeholder="Ex.: combinamos pelo WhatsApp (opcional)"
                  style={{width:'100%', marginTop:'8px', padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #E2E4E9', fontSize:'13px', boxSizing:'border-box', background:'white'}} />
                {/* O outro lado da fila: marcar uma aula a mais gasta um credito, entao a
                    ultima sai e a renovacao ANTECIPA. Mexer no dia em que a pessoa paga sem
                    avisar antes seria o tipo de surpresa que quebra a confianca no app. */}
                {/* v155: so' as aulas do pacote atual — as do proximo pacote (ja' na agenda) nao saem */}
                {contrato?.tipo_cobranca !== 'mensal' && aulasAtualExtra.length > 1 && (
                  <div style={{background:'#FFFBEB', border:'1.5px solid #FDE68A', borderRadius:'12px', padding:'10px 12px', marginTop:'10px'}}>
                    <div style={{fontSize:'12.5px', fontWeight:'800', color:'#92400E'}}>
                      <Ico n="alert-triangle" s={13} c="#D97706" /> {proximoContrato ? 'Muda o fim do pacote atual' : 'Muda a data da sua renovação'}
                    </div>
                    <div style={{fontSize:'12px', color:'#B45309', marginTop:'4px', lineHeight:1.5}}>
                      Esta aula usa uma do seu pacote{proximoContrato ? ' atual' : ''}. Quando {prof?.nome?.split(' ')[0] || 'seu professor'} confirmar,
                      a última {proximoContrato ? 'dele' : 'da agenda'} ({fmtDataBR(aulasAtualExtra[aulasAtualExtra.length - 1]?.data)}) sai e {proximoContrato ? 'o pacote atual termina' : 'sua previsão de renovação passa para'}
                      {' '}<strong>{fmtDataBR(aulasAtualExtra[aulasAtualExtra.length - 2]?.data)}</strong>.{proximoContrato ? ' O próximo pacote não muda.' : ''} Você não paga nada a mais —
                      só usa antes uma aula que já era sua.
                    </div>
                  </div>
                )}
                <div style={{display:'flex', gap:'8px', marginTop:'10px'}}>
                  <Button variant="secondary" onClick={() => setNovaAula(null)} className="flex-1 justify-center">Cancelar</Button>
                  <Button onClick={proporAula} disabled={salvando || !novaAula.data || !novaAula.hora} className="flex-1 justify-center">{salvando ? 'Enviando…' : 'Adicionar e avisar'}</Button>
                </div>
              </div>
            )}
            {/* "Vou viajar" — o pedido em massa. Como "gerenciar reserva" de companhia
                aerea: marca o periodo, o app conta as aulas, manda um pedido so'. */}
            {aulas.length > 0 && (
              !pausa ? (
                <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', gap:'8px'}}>
                  <button onClick={() => setPausa({ inicio: '', fim: '' })}
                    style={{display:'flex', alignItems:'center', gap:'8px', flex:1, minWidth:'200px', padding:'12px 14px', borderRadius:'14px',
                            border:'1.5px dashed #C2D4FF', background:'white', color:'#2F6BFF', fontSize:'13px', fontWeight:'700', cursor:'pointer', textAlign:'left'}}>
                    <Ico n="plane-departure" s={16} c="#2F6BFF" /> Vou viajar ou pausar por um tempo
                  </button>
                  <Ajuda texto={<>Para quando você vai sumir alguns dias — viagem, cirurgia, trabalho. Em vez de desmarcar aula por aula, você marca o período e {prof?.nome?.split(' ')[0] || 'seu professor'} recebe <strong>um pedido só</strong>. {contrato?.tipo_cobranca === 'mensal'
                    ? <>Se ele aceitar, as aulas do período ficam canceladas e o valor do mês não muda.</>
                    : <>Se ele aceitar, as aulas do período saem da agenda e o seu pacote não anda nesse tempo — o que você pagou continua valendo, só fica para depois.</>}</>} />
                </div>
              ) : (
                <div style={{background:'#F7F9FF', border:'1.5px solid #C2D4FF', borderRadius:'14px', padding:'14px'}}>
                  <p style={{fontSize:'13px', fontWeight:'700', color:'#1C43AE', marginBottom:'10px'}}>Em que período você não vai poder?</p>
                  <div style={{display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap'}}>
                    <input type="date" value={pausa.inicio} min={hojeSP()}
                      onChange={e => setPausa(p => ({ ...p, inicio: e.target.value }))}
                      style={{padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #E2E4E9', fontSize:'14px', background:'white'}} />
                    <span style={{fontSize:'13px', color:'#6E7480'}}>até</span>
                    <input type="date" value={pausa.fim} min={pausa.inicio || undefined}
                      onChange={e => setPausa(p => ({ ...p, fim: e.target.value }))}
                      style={{padding:'10px 12px', borderRadius:'10px', border:'1.5px solid #E2E4E9', fontSize:'14px', background:'white'}} />
                  </div>
                  {pausa.inicio && pausa.fim && pausa.fim >= pausa.inicio ? (
                    <p style={{fontSize:'12.5px', color:'#3D424C', marginTop:'10px', lineHeight:1.5}}>
                      {(() => { const n = aulas.filter(a => a.data >= pausa.inicio && a.data <= pausa.fim).length;
                        return n === 0 ? 'Nenhuma aula cai nesse período.'
                          : <><strong>{n} aula{n === 1 ? '' : 's'}</strong> nesse período. {prof?.nome?.split(' ')[0] || 'Seu professor'} recebe um pedido só e decide se pausa{contrato?.tipo_cobranca === 'mensal' ? ' — na mensalidade, as aulas do período ficam canceladas e o valor do mês não muda.' : ' o pacote — o saldo não anda e a renovação desloca.'}</>; })()}
                    </p>
                  ) : null}
                  <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                    <button onClick={() => setPausa(null)}
                      style={{padding:'11px 16px', borderRadius:'10px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'13px', fontWeight:'700', cursor:'pointer'}}>Cancelar</button>
                    <button onClick={pedirPausa} disabled={salvando || !pausa.inicio || !pausa.fim || pausa.fim < pausa.inicio}
                      style={{flex:1, padding:'11px', borderRadius:'10px', border:'none', background:'#2F6BFF', color:'white', fontSize:'13px', fontWeight:'800', cursor:'pointer',
                              opacity: (pausa.inicio && pausa.fim && pausa.fim >= pausa.inicio) ? 1 : .5}}>
                      {salvando ? 'Enviando…' : 'Avisar o professor'}
                    </button>
                  </div>
                </div>
              )
            )}

            {aulas.length === 0 ? (
              <Card className="text-center py-10"><div className="text-4xl mb-3"><Ico n="calendar" /></div><p className="text-gray-500 text-sm">Nenhuma aula agendada</p></Card>
            ) : aulas.map((a, idx) => {
              const d = new Date(a.data+'T12:00');
              // Agrupamento por semana, como a tela "Proximas" do Mindbody e do Gympass.
              const hojeD = new Date(); hojeD.setHours(0,0,0,0);
              const semanaDe = dt => { const x = new Date(dt); x.setHours(0,0,0,0); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); return x.getTime(); };
              const s0 = semanaDe(hojeD), sA = semanaDe(d);
              const rotuloSemana = sA === s0 ? 'Esta semana' : sA === s0 + 7*86400000 ? 'Semana que vem'
                : d.toLocaleDateString('pt-BR', { month: 'long' }).replace(/^./, c => c.toUpperCase());
              const anterior = idx > 0 ? aulas[idx - 1] : null;
              // v152: renovou antecipado — as aulas do proximo pacote ficam abaixo de um divisor.
              // v155: um divisor so' (na 1a aula da fila); aula do atual que fica depois dele leva o selo "pacote atual"
              const idxFila = proximoContrato ? aulas.findIndex(x => ehAulaDaFila(x)) : -1;
              const inicioFila = idxFila >= 0 && idx === idxFila;
              // (no periodo do proximo, a virada leva a aula para ele — ai' nao tem selo)
              // (so' aula do pacote atual: aula de turma ou de outro acordo nao leva o selo)
              const atualDepoisFila = idxFila >= 0 && idx > idxFila && !ehAulaDaFila(a)
                && !!contrato && a.contrato_id === contrato.id && !a.turma_id
                && !(proximoContrato.data_inicio && a.data >= String(proximoContrato.data_inicio).slice(0, 10));
              const novaSemana = inicioFila || !anterior || semanaDe(new Date(anterior.data + 'T12:00')) !== sA;
              return (
                <React.Fragment key={a.id}>
                {inicioFila && (
                  <div style={{display:'flex', alignItems:'center', gap:'8px', margin: idx ? '14px 0 0' : '0'}}>
                    <span style={{flex:1, height:'1px', background:'#C2D4FF'}} />
                    <span style={{fontSize:'12px', fontWeight:'800', color:'#1C43AE'}}>Próximo pacote · começa {fmtDiaMes(proximoContrato?.data_inicio || a.data)}</span>
                    <span style={{flex:1, height:'1px', background:'#C2D4FF'}} />
                  </div>
                )}
                {novaSemana && (
                  <p style={{fontSize:'11px', fontWeight:'800', color:'#9AA0AB', textTransform:'uppercase', letterSpacing:'.05em', margin: idx ? '10px 0 0' : '0'}}>{rotuloSemana}</p>
                )}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
                  <div className="bg-indigo-50 rounded-xl px-3 py-2 text-center min-w-[52px]">
                    <p className="text-xs text-indigo-500 font-semibold">{DIAS[d.getDay()]}</p>
                    <p className="text-xl font-bold text-indigo-700">{d.getDate()}</p>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-800">{a.hora?.slice(0,5)}</p>
                    {atualDepoisFila && <span style={{display:'inline-block', fontSize:'11px', fontWeight:'700', color:'#6E7480', background:'#F0F1F4', borderRadius:'999px', padding:'2px 8px', marginTop:'2px'}}>pacote atual</span>}
                    {/* Aula marcada e' uma boa noticia — a linha de baixo diz isso e mais nada.
                        Antes ela trazia o prazo de aviso em toda aula ("avise ate' seg 12h",
                        "fora do prazo — se faltar, a aula conta"): lido de relance parecia que
                        havia problema com a aula. O prazo continua existindo, mas no lugar onde
                        ele importa — dentro de "Gerenciar", na hora de desmarcar. */}
                    {/* v140: "Aula confirmada" virou "Aula marcada" (confirmada parecia que alguem ja' tinha dito que vem);
                        com a pergunta do professor: "Cadu perguntou se você vai · responder"; com o "Vou": "Você avisou que vai" (v142) */}
                    {perguntaAbertaSM(a) && !pedidoDaAula(a.id) && !naoConsigoDaAula(a.id) ? (
                      <button type="button" onClick={() => setAcaoAula({ aula: a, modo: 'presenca' })}
                        style={{border:'none', background:'none', padding:0, minHeight:'44px', cursor:'pointer', fontFamily:'inherit', fontSize:'12px', fontWeight:'700', color:'#1C43AE', textAlign:'left', display:'inline-flex', alignItems:'center', gap:'4px'}}>
                        <Ico n="calendar-question" s={13} c="#2F6BFF" /> {prof?.nome?.split(' ')[0] || 'Seu professor'} perguntou se você vai · <span style={{textDecoration:'underline'}}>responder</span>
                      </button>
                    ) : (
                    <p className="text-xs mt-0.5" style={{color: pedidoDaAula(a.id) || avisoVigenteAula(a) || naoConsigoDaAula(a.id) ? '#9AA0AB' : '#0A6F4D'}}>
                      {pedidoDaAula(a.id) || avisoVigenteAula(a) || naoConsigoDaAula(a.id)
                        ? d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
                        : a.presenca_vou_em ? <><Ico n="circle-check" s={12} c="#0E9F6E" /> Você avisou que vai</>
                        : <><Ico n="circle-check" s={12} c="#0E9F6E" /> Aula marcada</>}
                    </p>
                    )}
                    <SeloPedido aulaId={a.id} />
                    <SeloAviso aula={a} />
                  </div>
                  {a.status === 'agendada' ? (
                    <button onClick={() => setAcaoAula({ aula: a, modo: 'opcoes' })}
                      style={{display:'inline-flex', alignItems:'center', gap:'5px', padding:'8px 12px', minHeight:'44px', borderRadius:'11px',
                              border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontSize:'12.5px',
                              fontWeight:'700', cursor:'pointer', flexShrink:0}}>
                      <Ico n="settings" s={14} c="#6E7480" /> Gerenciar
                    </button>
                  ) : (
                    <Badge color={a.status === 'realizada' ? 'green' : a.status === 'cancelada_aluno' || a.status === 'cancelada_professor' ? 'red' : 'gray'}>
 {a.status === 'realizada'? '': a.status === 'cancelada_aluno'|| a.status === 'cancelada_professor'? '': a.status}
                    </Badge>
                  )}
                </div>
                </React.Fragment>
              );
            })}

            {}
            {solicitacoes.length > 0 ? (
              <Card className="mt-2">
                <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3">Meus pedidos</p>
                <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                  {solicitacoes.map(s => {
                    const est = {
                      pendente:      { l:'Aguardando resposta', c:'#92400E', bg:'#FFFBEB', b:'#FDE68A' },
                      aceita:        { l:'Aceito',              c:'#0A6F4D', bg:'#EFFAF5', b:'#B7E9D6' },
                      recusada:      { l:'Recusado',            c:'#991B1B', bg:'#FEF2F2', b:'#FECACA' },
                      contraproposta:{ l:'Professor propôs outro horário', c:'#1C43AE', bg:'#E7EDFF', b:'#C2D4FF' },
                      cancelada:     { l:'Retirado por você',   c:'#6E7480', bg:'#F4F5F7', b:'#E2E4E9' },
                    }[s.status] || { l:s.status, c:'#6E7480', bg:'#F4F5F7', b:'#E2E4E9' };
                    const alvo = s.data_sugerida
                      ? `${new Date(s.data_sugerida+'T12:00').toLocaleDateString('pt-BR',{day:'numeric',month:'short'})}${s.hora_sugerida ? ' às ' + String(s.hora_sugerida).slice(0,5) : ''}`
                      : 'dia a combinar';
                    return (
                      <div key={s.id} style={{border:`1px solid ${est.b}`,background:est.bg,borderRadius:'14px',padding:'12px 14px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'8px'}}>
                          <div style={{flex:1}}>
                            <p style={{fontSize:'13px',fontWeight:'700',color:'#16181D'}}>
                              {s.tipo === 'reagendamento' ? `Pedi para remarcar — ${alvo}`
                               : s.tipo === 'cancelamento' ? `Avisei que não ia — ${alvo}`
                               : s.tipo === 'pausa' ? `Pausa de ${s.periodo_inicio ? new Date(s.periodo_inicio+'T12:00').toLocaleDateString('pt-BR',{day:'numeric',month:'short'}) : '?'} a ${s.periodo_fim ? new Date(s.periodo_fim+'T12:00').toLocaleDateString('pt-BR',{day:'numeric',month:'short'}) : '?'}`
                               : s.tipo === 'ajuste_contrato' ? 'Pedi uma correção no acordo'
                               : `Pedi um ajuste — ${alvo}`}
                            </p>
                            {s.mensagem ? <p style={{fontSize:'12px',color:'#3D424C',marginTop:'3px'}}>"{s.mensagem}"</p> : null}
                            <p style={{fontSize:'11px',color:'#9AA0AB',marginTop:'4px'}}>
                              {new Date(s.criado_em).toLocaleDateString('pt-BR',{day:'numeric',month:'short'})} às {new Date(s.criado_em).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}
                            </p>
                          </div>
                          <span style={{fontSize:'11px',fontWeight:'700',color:est.c,whiteSpace:'nowrap'}}>{est.l}</span>
                        </div>
                        {s.resposta ? (
                          <p style={{fontSize:'12px',color:est.c,marginTop:'8px',paddingTop:'8px',borderTop:`1px solid ${est.b}`}}>
                            <strong>{prof?.nome?.split(' ')[0] || 'Professor'}:</strong> {s.resposta}
                          </p>
                        ) : null}
                        {s.status === 'contraproposta' ? (
                          <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap'}}>
                            <button disabled={salvando} onClick={() => responderContraproposta(s, true)}
                              style={{padding:'8px 14px',minHeight:'44px',borderRadius:'10px',border:'none',background:'#0E9F6E',color:'white',fontSize:'12px',fontWeight:'800',cursor:'pointer'}}>
                              <Ico n="check" s={13} /> Confirmar este horário
                            </button>
                            <button disabled={salvando} onClick={() => responderContraproposta(s, false)}
                              style={{padding:'8px 14px',minHeight:'44px',borderRadius:'10px',border:'1.5px solid #FECACA',background:'white',color:'#991B1B',fontSize:'12px',fontWeight:'800',cursor:'pointer'}}>
                              Não dá pra mim
                            </button>
                          </div>
                        ) : null}
                        {s.status === 'pendente' ? (
                          <button onClick={() => desistirSolicitacao(s)}
                            style={{marginTop:'8px',fontSize:'11px',fontWeight:'700',color:'#6E7480',background:'none',border:'none',padding:'0',cursor:'pointer',textDecoration:'underline'}}>
                            Retirar pedido
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ) : null}

            <Card className="mt-2">
              <p id="historico-aulas-aluno" className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4" style={{scrollMarginTop:'80px'}}>Histórico</p>
              {historico.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">Sem histórico ainda</p>
              ) : historico.slice(0,15).map(a => {
                const d = new Date(a.data+'T12:00');
                const sMap = { realizada:'green', cancelada_aluno:'yellow', cancelada_professor:'purple', cancelada_forca_maior:'gray', falta:'red' };
                const sLabel = { realizada:'Treino', cancelada_aluno:'Cancelada', cancelada_professor:'Canc. Prof.', cancelada_forca_maior:'Imprevisto', falta:'Perdida' };
                // feriado / pausa dizem o que foi (v131); status desconhecido nunca aparece em codigo
                // v140: o aluno disse que o professor nao veio — "Prof. não veio" (o professor confirmou ou ficou
                // calado 24h) ou o rotulo + " · em desacordo" (o professor fechou diferente: dada, falta ou
                // "o aluno avisou" — decisao 2 = A)
                const nvHist = a.aluno_motivo_nao === 'prof_nao_veio';
                const rotuloBase = rotuloEspecialAula(a) || sLabel[a.status] || (String(a.status || '').startsWith('cancelada') ? 'Cancelada' : 'Encerrada');
                const rotuloH = nvHist && a.status === 'cancelada_professor' ? 'Prof. não veio'
                  : nvHist && ['realizada', 'falta', 'cancelada_aluno'].includes(a.status) ? `${rotuloBase} · em desacordo`
                  : rotuloBase;
                // Fechada pelo robo dos 7 dias: o aluno tem 48h para contestar. Depois disso
                // a aula e' definitiva — e' o mesmo prazo do "reportar problema" do Airbnb.
                const fechadaPrazo = a.fechada_por_prazo_em ? new Date(a.fechada_por_prazo_em) : null;
                const podeContestar = fechadaPrazo && (Date.now() - fechadaPrazo.getTime()) < 48 * 3600 * 1000;
                return (
                  <div key={a.id} className="pb-3 mb-3 border-b border-gray-50 last:border-0 last:pb-0 last:mb-0">
                    <div className="flex items-center gap-3">
                      <div className="text-center min-w-[44px]">
                        <p className="text-xs text-gray-400">{DIAS[d.getDay()]}</p>
                        <p className="font-bold text-gray-600 text-sm">{d.getDate()}/{d.getMonth()+1}</p>
                      </div>
                      <p className="flex-1 text-sm text-gray-500">{a.hora?.slice(0,5)}</p>
                      <Badge color={rotuloEspecialAula(a) ? 'gray' : (sMap[a.status]||'gray')}>{rotuloH}</Badge>
                    </div>
                    {/* v142 (A1): a aula que VOCE desmarcou (no prazo ou por feriado) volta para a agenda ate' comecar,
                        se o horario continuar livre. A aula desmarcada sai da lista de cima — por isso o botao mora aqui. */}
                    {cancelamentoDesfazivelAlunoSM(a) && (
                      <div style={{marginLeft:'56px', display:'flex', flexDirection:'column'}}>
                        <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: a.id, esperado: 'cancelamento' }} papel="aluno" nomeOutro={prof?.nome}
                          onFeito={() => { if (dados?.id) loadContexto(dados.id); }} />
                      </div>
                    )}
                    {fechadaPrazo && (
                      <div style={{fontSize:'11.5px', color:'#92400E', marginTop:'6px', marginLeft:'56px', lineHeight:1.5}}>
                        Fechada automaticamente: um lado registrou e o outro não respondeu em 24h.
                        {podeContestar ? (
                          <button onClick={async () => {
                              const motivo = window.prompt('O que aconteceu de verdade nessa aula?');
                              if (motivo === null) return;
                              const { data, error } = await supabase.rpc('contestar_fechamento', { p_aula_id: a.id, p_motivo: motivo });
                              if (error || data?.erro) { toastSM('Não deu para contestar: ' + (error?.message || data.erro)); return; }
                              uso.acao('aluno.contestar');
                              toastSM('Aula reaberta. Combine com o professor o que aconteceu.');
                              loadContexto(dados.id);
                            }}
                            style={{marginLeft:'6px', background:'none', border:'none', color:'#D97706', fontWeight:'700', fontSize:'11.5px', cursor:'pointer', textDecoration:'underline', padding:0}}>
                            Contestar
                          </button>
                        ) : ' Prazo de contestação encerrado.'}
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {}
        {tela === 'plano' && (
          <div className="flex flex-col gap-4">
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Meu Plano</h1>
              <p className="text-sm text-gray-400 mt-1">Detalhes do seu plano ativo</p>
            </div>

            {!contrato ? (
              <Card className="text-center py-10">
                <div className="text-4xl mb-3"><Ico n="clipboard-list" /></div>
                <p className="text-gray-500 text-sm">Nenhum plano ativo no momento</p>
                <p className="text-xs text-gray-400 mt-1">{esperandoCompletarAluno
                  ? `${primeiroNomeSM(prof?.nome, 'Seu professor')} vai completar o combinado. Você confere aqui.`
                  : pedidoEsperando ? `Esperando ${nomeProfPedido(pedidoEsperando)} conferir o combinado que você mandou (está no Início). Você confere aqui.`
                  : 'Entre em contato com seu professor para ativar um plano.'}</p>
              </Card>
            ) : (
              <>
                {}
                <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                  <p className="text-xs text-indigo-100 uppercase tracking-wider mb-1">Plano Ativo</p>
                  <h2 className="text-2xl font-bold mb-1">{tituloPlano}</h2>
                  <p className="text-indigo-100 text-sm">Professor: {prof?.nome || '—'}</p>
                </div>

                {}
                <Card>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Configuração</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-400 mb-1">Aulas por semana</p>
                      <p className="text-2xl font-bold text-gray-800">{porSemanaReal ?? '—'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-400 mb-1">{ehMensalAluno ? 'Cobrança' : 'Total de aulas'}</p>
                      {ehMensalAluno ? (
                        <p className="text-2xl font-bold text-gray-800">Mensal<span className="text-sm font-normal text-gray-400 ml-1">{contrato?.dia_vencimento ? `· dia ${contrato.dia_vencimento}` : ''}</span></p>
                      ) : (
                        <p className="text-2xl font-bold text-gray-800">{Number(contrato?.total_aulas) || '—'}<span className="text-sm font-normal text-gray-400 ml-1">aulas</span></p>
                      )}
                    </div>
                    <div className="bg-indigo-50 rounded-xl p-4">
                      <p className="text-xs text-indigo-400 mb-1">{ehMensalAluno ? 'Valor por mês' : 'Valor do pacote'}</p>
                      <p className="text-2xl font-bold text-indigo-700">{fmtBRL(ehMensalAluno ? (contrato.valor_mensal || contrato.preco_negociado) : contrato.preco_negociado || 0)}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-xs text-green-500 mb-1">{antecedenciaH() > 0 ? 'Cancelamento c/ aviso de' : 'Desmarcar'}</p>
                      {antecedenciaH() > 0
                        ? <p className="text-2xl font-bold text-green-700">{antecedenciaH()}<span className="text-sm font-normal text-green-400 ml-1">hrs</span></p>
                        : <p className="text-2xl font-bold text-green-700">Livre<span className="text-sm font-normal text-green-400 ml-1">sem prazo</span></p>}
                    </div>
                  </div>
                </Card>

                {}
                <Card>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Período do acordo</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs text-gray-400 mb-1">Início</p>
                      <p className="text-base font-bold text-gray-800">
                        {contrato.data_inicio ? new Date(contrato.data_inicio + 'T12:00').toLocaleDateString('pt-BR') : '—'}
                      </p>
                    </div>
                    <div className={`rounded-xl p-4 ${!proximoContrato && diasRestantes !== null && diasRestantes <= 7 ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <p className={`text-xs mb-1 ${!proximoContrato && diasRestantes !== null && diasRestantes <= 7 ? 'text-red-400' : 'text-gray-400'}`}>Término</p>
                      <p className={`text-base font-bold ${!proximoContrato && diasRestantes !== null && diasRestantes <= 7 ? 'text-red-700' : 'text-gray-800'}`}>
                        {dataFim ? dataFim.toLocaleDateString('pt-BR') : '—'}
                      </p>
                    </div>
                  </div>
                  {proximoContrato ? (
                    <div className="mt-3 rounded-xl p-3 flex items-center gap-2 bg-green-50 border border-green-200">
                      <p className="text-sm font-medium text-green-700">
                        Próximo pacote começa {proximoContrato.data_inicio ? fmtDataBR(proximoContrato.data_inicio) : 'depois da sua última aula'}
                      </p>
                    </div>
                  ) : diasRestantes !== null && (
                    <div className={`mt-3 rounded-xl p-3 flex items-center gap-2 ${diasRestantes <= 7 ? 'bg-red-50 border border-red-200' : diasRestantes <= 30 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                      <span>{diasRestantes <= 7 ? '': diasRestantes <= 30 ? '': ''}</span>
                      <p className={`text-sm font-medium ${diasRestantes <= 7 ? 'text-red-700' : diasRestantes <= 30 ? 'text-yellow-700' : 'text-green-700'}`}>
                        {diasRestantes === 0 ? 'Plano encerra hoje!' : `${diasRestantes} dia${diasRestantes !== 1 ? 's' : ''} restante${diasRestantes !== 1 ? 's' : ''}`}
                      </p>
                    </div>
                  )}
                </Card>

                {}
                {ehMensalAluno ? null : (
                <Card>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Progresso das Aulas</p>
                  <PontosAulas contrato={contrato} aulas={aulasContratoAluno} tamanho={16} comDatas proximoInicio={proximoContrato?.data_inicio || null} />
                  <div className="grid grid-cols-2 gap-3 mt-5">
                    <div className="bg-indigo-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-indigo-700">{creditosDisponiveis}</p>
                      <p className="text-xs text-indigo-400 mt-1">Agendadas</p>
                    </div>
                    <div className="bg-green-50 rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">{creditosUsados}</p>
                      <p className="text-xs text-green-400 mt-1">Realizadas</p>
                    </div>
                  </div>
                  {}
                  {historico.length > 0 && (() => {
                    const total = historico.filter(a => !rotuloEspecialAula(a)).length || 1;
                    // pausa / feriado (v131) nao contam como cancelamento de ninguem
                    const cancProf = historico.filter(a => a.status === 'cancelada_professor' && !rotuloEspecialAula(a)).length;
                    const cancAluno = historico.filter(a => a.status === 'cancelada_aluno' && !rotuloEspecialAula(a)).length;
                    const faltou = historico.filter(a => a.status === 'falta').length;
                    const pctProf = Math.round(cancProf / total * 100);
                    const pctAluno = Math.round((cancAluno + faltou) / total * 100);
                    return (
                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Cancelamentos</p>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Canceladas pelo professor</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-2 bg-red-400 rounded-full" style={{width: `${pctProf}%`}}></div>
                              </div>
                              <span className="text-xs font-bold text-red-600 w-16 text-right">{cancProf} aula{cancProf !== 1 ? 's' : ''} ({pctProf}%)</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">Canceladas / faltou (você)</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-2 bg-yellow-400 rounded-full" style={{width: `${pctAluno}%`}}></div>
                              </div>
                              <span className="text-xs font-bold text-yellow-600 w-16 text-right">{cancAluno + faltou} aula{(cancAluno + faltou) !== 1 ? 's' : ''} ({pctAluno}%)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </Card>
                )}

                {}
                {contrato?.reposicao_permitida ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-start gap-3">
                    <span className="text-xl"><Ico n="refresh" /></span>
                    <div>
                      <p className="font-semibold text-blue-800 text-sm">Reposição permitida</p>
                      <p className="text-xs text-blue-500 mt-0.5">Até {contrato.limite_reposicao || 1} reposição(ões) no plano. Consulte seu professor para agendar.</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex items-start gap-3">
                    <span className="text-xl"><Ico n="info-circle" /></span>
                    <div>
                      <p className="font-semibold text-gray-600 text-sm">Sem reposição</p>
                      <p className="text-xs text-gray-400 mt-0.5">Este plano não inclui reposição de aulas canceladas.</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {}
        {tela === 'financeiro' && (
          <div className="flex flex-col gap-4">
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Financeiro</h1>
              <p className="text-sm text-gray-400 mt-1">Pagamentos e dados do plano</p>
            </div>
            {(() => {
              // O Financeiro do aluno e' um EXTRATO, nao uma caixa registradora.
              // Pix so' aparece quando ha' cobranca em aberto — ou se o aluno escolher antecipar.
              const emAberto = emAbertoAluno;
              const totalAberto = emAberto.reduce((t, pg) => t + Number(pg.valor || 0), 0);
              // Como pagar vem de comoPagar (Etapa 3): hoje Pix direto na chave do professor.
              const temPix = temPixAluno;

              const blocoPix = (titulo, valorTexto) => (
                <div className="bg-gradient-to-br from-green-800 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                  <div className="flex items-center gap-2 mb-4"><span className="text-2xl"><Ico n="bolt" /></span><p className="text-lg font-bold">{titulo}</p></div>
                  <div className="bg-white/15 rounded-xl p-4 mb-4">
                    <p className="text-xs text-green-100 mb-1">{pagar.tipo_chave ? String(pagar.tipo_chave).toUpperCase() : 'CHAVE PIX'}</p>
                    <p className="font-bold text-base" style={{wordBreak:'break-all'}}>{pagar.chave}</p>
                  </div>
                  <p className="text-sm text-green-100 mb-4">{valorTexto}</p>
                  {brcodePix?.dataURL && (
                    <div className="bg-white rounded-xl p-3 mb-4 flex flex-col items-center">
                      <img src={brcodePix.dataURL} alt="QR Code Pix" style={{width:'190px',height:'190px',imageRendering:'pixelated'}} />
                      <p className="text-[11px] text-gray-500 mt-1 text-center">Aponte a câmera do app do seu banco</p>
                    </div>
                  )}
                  {brcodePix?.payload && (
                    <button onClick={copiarBRCode}
                      className={`w-full rounded-xl py-3 font-bold transition-all mb-2 ${pixCodeCopiado?'bg-white text-green-700':'bg-white/90 text-green-800 hover:bg-white'}`}>
 {pixCodeCopiado ? 'Código copiado! Cole no app do banco': 'Copiar código Pix (copia e cola)'}
                    </button>
                  )}
                  <button onClick={copiarPix}
                    className={`w-full border-2 border-white/40 rounded-xl py-3 font-bold text-white transition-all ${pixCopiado?'bg-white/30':'bg-white/15 hover:bg-white/25'}`}>
 {pixCopiado ? 'Chave copiada!': 'Copiar apenas a chave'}
                  </button>
                </div>
              );

              // CASO 1 - COM cobranca em aberto (v155: so' as ainda nao avisadas; as avisadas ficam numa linha a' parte)
              const nPf = prof?.nome?.split(' ')[0] || 'seu professor';
              const totalAvisado = avisadasAluno.reduce((t, pg) => t + Number(pg.valor || 0), 0);
              if (emAberto.length > 0) {
                return (
                  <>
                    <div style={{background:'linear-gradient(135deg,#B45309,#D97706)',borderRadius:'18px',padding:'20px',color:'white'}}>
                      <div style={{fontSize:'11px',fontWeight:'700',letterSpacing:'0.06em',textTransform:'uppercase',opacity:0.9}}><Ico n="credit-card" /> Pagamento em aberto</div>
                      <div style={{fontSize:'30px',fontWeight:'800',marginTop:'6px',lineHeight:1.1}}>
                        R$ {totalAberto.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}
                      </div>
                      <div style={{fontSize:'13px',opacity:0.92,marginTop:'4px'}}>
                        {referenteCobranca(emAberto)}
                      </div>
                    </div>
                    {notaAvisoFalhou(emAberto.find(pg => avisoProximoFalhou && pg.id === avisoProximoFalhou.pgId))}
                    {avisadasAluno.length > 0 && (
                      <div style={{background:'#EFFAF5', border:'1.5px solid #B7E9D6', borderRadius:'14px', padding:'12px 16px', fontSize:'13px', color:'#0A6F4D', lineHeight:1.5}}>
                        <Ico n="circle-check" c="#0E9F6E" /> {fmtBRL(totalAvisado)} · você avisou que pagou. {nPf.charAt(0).toUpperCase() + nPf.slice(1)} confere — não entra no valor acima.
                      </div>
                    )}
                    {temPix
                      ? blocoPix('Pagar via Pix', `Valor a pagar: ${fmtBRL(pagar.valor)}`)
                      : <Card className="text-center py-8">
                          <div className="text-3xl mb-3"><Ico n="credit-card" /></div>
                          <p className="font-bold text-gray-700 mb-2">Como pagar</p>
                          <p className="text-sm text-gray-400">{pagar.forma === 'plen' ? 'O pagamento deste acordo é feito pelo Plen — em breve, aqui.' : `Combine com ${nPf} como pagar e toque em "Já paguei".`}</p>
                        </Card>}
                    {/* Cinza de propósito: verde aqui parecia "já avisei" e a pessoa não
                        tocava — ou tocava de novo achando que não tinha ido. Botão neutro =
                        ação pendente; o verde só aparece depois, no aviso confirmado. */}
                    <button disabled={salvando} onClick={() => declararPagamentoAluno(emAberto.map(p => p.id))}
                      style={{width:'100%', padding:'14px', borderRadius:'13px', border:'1.5px solid #E2E4E9', background:'white',
                              color:'#3D424C', fontWeight:'800', fontSize:'14px', cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.6 : 1}}>
                      <Ico n="send" s={15} /> {salvando ? 'Avisando…' : 'Já paguei — avisar meu professor'}
                    </button>
                    <div style={{fontSize:'12px', color:'#9AA0AB', textAlign:'center'}}>
                      Isso não confirma o pagamento sozinho — seu professor confere o Pix e dá baixa.
                    </div>
                  </>
                );
              }

              // v155: pacote novo ainda nao confirmado — nada a pagar antes do aceite
              const semAceite = (pagamentos || []).filter(ehCobrancaSemAceite);
              if (semAceite.length > 0) {
                return (
                  <Card>
                    <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">Pacote novo</p>
                    <p className="text-sm text-gray-600" style={{lineHeight:1.5}}>
                      {nPf.charAt(0).toUpperCase() + nPf.slice(1)} combinou um pacote novo de {fmtBRL(semAceite.reduce((t, pg) => t + Number(pg.valor || 0), 0))}. Confirme o pacote antes de pagar.
                    </p>
                    {notaAvisoFalhou(semAceite.find(pg => avisoProximoFalhou && pg.id === avisoProximoFalhou.pgId))}
                    <button onClick={() => { uso.acao('aluno.acordo_abrir_folha', { fila: false, origem: 'financeiro' }); setFolhaAcordo(true); }}
                      style={{width:'100%', marginTop:'12px', padding:'12px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer'}}>
                      Ver e confirmar
                    </button>
                  </Card>
                );
              }

              // Ja' avisou que pagou tudo? Entao a bola esta' com o professor — mostra uma
              // tela mais calma em vez do banner laranja "urgente" e do Pix de novo
              // (o aluno ja' fez a parte dele; ficar oferecendo pra pagar de novo confunde).
              if (avisadasAluno.length > 0) {
                const quando = avisadasAluno.map(p => p.declarado_pago_em).filter(Boolean).sort()[0];
                const quandoTxt = quando
                  ? new Date(quando).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }) +
                    ' às ' + new Date(quando).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                  : null;
                return (
                  <div style={{background:'#EFFAF5', border:'1.5px solid #B7E9D6', borderRadius:'18px', padding:'20px'}}>
                    <div style={{fontSize:'15px', fontWeight:'800', color:'#0A6F4D'}}>
                      <Ico n="circle-check" c="#0E9F6E" /> Aviso enviado ao professor
                    </div>
                    <div style={{fontSize:'13px', color:'#0C8A5F', marginTop:'6px', lineHeight:1.5}}>
                      Você avisou que pagou R$ {totalAvisado.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}
                      {quandoTxt ? ` em ${quandoTxt}` : ''}. Agora é com {nPf}:
                      assim que ele conferir o Pix e dar baixa, esta cobrança sai daqui.
                    </div>
                    {/* v142 (A9): o ultimo "Ja' paguei" (o banco desfaz o lote do mesmo toque) */}
                    <BotaoDesfazerSM alvo={{ ref_tipo: 'pagamento', ref_id: avisadasAluno.slice().sort((x, y) => String(y.declarado_pago_em).localeCompare(String(x.declarado_pago_em)))[0].id, esperado: 'ja_paguei' }}
                      papel="aluno" nomeOutro={prof?.nome} onFeito={() => { if (dados?.id) loadContexto(dados.id); }} />
                  </div>
                );
              }

              // CASO 2 - SEM cobranca em aberto, em dia
              // v155: sem proximo pacote, "Pagar o proximo adiantado" vale com qualquer numero de aulas (e sem total):
              // valor do pacote atual, o Pix (se houver) e um so' "Ja' paguei o proximo" (aviso ao professor)
              const pacoteSemProx = !!(contrato && !proximoContrato && contrato.tipo_cobranca !== 'mensal' && !acordoK);
              const restF = creditosDB && creditosDB.total > 0 ? Math.max(0, (creditosDB.total || 0) - (creditosDB.usado || 0)) : null;
              const avisouF = avisoRenovar && contrato && avisoRenovar.contrato_id === contrato.id ? avisoRenovar : null;
              const NPf = nPf.charAt(0).toUpperCase() + nPf.slice(1);
              return (
                <>
                  <div style={{background:'#DCF5EC',border:'1.5px solid #8FDFC4',borderRadius:'18px',padding:'18px 20px'}}>
                    <div style={{fontSize:'15px',fontWeight:'800',color:'#0A6F4D'}}><Ico n="circle-check" c="#0E9F6E" /> {ehMensalAluno ? 'Mensalidade em dia' : 'Pacote em dia'}</div>
                    <div style={{fontSize:'13px',color:'#0C8A5F',marginTop:'4px'}}>Você não tem nada a pagar agora.</div>
                  </div>

                  {contrato && !proximoContrato && (
                    <Card>
                      {/* "Próximo pagamento" soava como boleto a vencer — e nao e' isso: no
                          pacote nao existe data de vencimento, existe o dia em que as aulas
                          acabam e vocês combinam o proximo. Na mensalidade, ai' sim, e' uma
                          data fixa todo mes. O titulo agora diz qual dos dois e'. */}
                      <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">
                        {contrato.tipo_cobranca === 'mensal' ? 'Próxima mensalidade' : 'Próxima renovação'}
                      </p>
                      <p className="text-2xl font-bold text-gray-800">{fmtBRL(contrato.valor_mensal || contrato.preco_negociado || 0)}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {contrato.tipo_cobranca === 'mensal'
                          ? <>Todo dia <strong>{contrato.dia_vencimento || '—'}</strong> do mês</>
                          : <>Prevista para <strong>{dataFim ? dataFim.toLocaleDateString('pt-BR') : '—'}</strong>
                              {restF !== null ? ` — quando terminarem as ${restF} aulas restantes` : ''}</>}
                      </p>
                      {contrato.tipo_cobranca !== 'mensal' && (
                        <p className="text-xs text-gray-400 mt-2">
                          É uma previsão, não uma cobrança: a data anda junto com as suas aulas.
                          Se alguma for remarcada ou cancelada, ela muda também.
                        </p>
                      )}
                      {temPix && contrato.tipo_cobranca === 'mensal' && (
                        <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', gap:'8px', marginTop:'14px'}}>
                          {/* "Pagar adiantado" (era "Antecipar pagamento"): "antecipacao" fica reservada
                              para os recebiveis do professor (Fase 3). */}
                          <button onClick={() => { if (!mostrarAntecipar) uso.acao('aluno.antecipar_abrir'); setMostrarAntecipar(v => !v); }}
                            style={{flex:1, minWidth:'180px', padding:'12px',borderRadius:'12px',border:'1.5px solid #C2D4FF',
                                    background:'#E7EDFF',color:'#2F6BFF',fontWeight:'800',fontSize:'13px',cursor:'pointer'}}>
                            {mostrarAntecipar ? 'Fechar' : 'Pagar adiantado'}
                          </button>
                          <Ajuda texto={<>Mostra a chave Pix e o QR de {nPf} para você pagar antes da hora, se quiser — útil para quem gosta de deixar resolvido. <strong>Não é obrigatório e não adianta a data:</strong> depois de pagar, mande o comprovante para {nPf} (pelo WhatsApp, por exemplo) — ele dá a baixa quando lançar a cobrança.</>} />
                        </div>
                      )}
                    </Card>
                  )}

                  {pacoteSemProx && (
                    <Card>
                      <div id="bloco-pagar-proximo" style={{scrollMarginTop:'80px'}} />
                      <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">Próximo pacote</p>
                      {avisouF ? (
                        <div style={{background:'#EFFAF5', border:'1.5px solid #B7E9D6', borderRadius:'14px', padding:'12px 14px', fontSize:'13.5px', color:'#0A6F4D', lineHeight:1.5}}>
                          <Ico n="circle-check" c="#0E9F6E" /> {avisouF.jaPagou ? 'Você avisou que já pagou o próximo pacote.' : 'Você avisou que quer renovar.'} Quando {nPf} combinar, o próximo pacote aparece aqui.
                          {' '}<button type="button" onClick={esquecerAvisoRenovar} style={{background:'none', border:'none', padding:0, color:'#0A6F4D', fontWeight:'800', textDecoration:'underline', cursor:'pointer', fontSize:'13px'}}>Avisar de novo</button>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600" style={{lineHeight:1.5}}>
                          {restF !== null && restF <= 2 ? `Seu pacote está acabando. Avise ${nPf}` : `Quer garantir o próximo pacote? Avise ${nPf}`} — o próximo começa depois da sua última aula, nenhuma se perde.
                        </p>
                      )}
                      {!(avisouF && avisouF.jaPagou) && (
                        <div style={{display:'flex', gap:'8px', marginTop:'12px', flexWrap:'wrap'}}>
                          {!avisouF && (
                            <button disabled={salvando} onClick={() => avisarRenovacaoAluno(false)}
                              style={{flex:1, minWidth:'140px', padding:'12px', borderRadius:'12px', border:'1.5px solid #C2D4FF', background:'#E7EDFF', color:'#2F6BFF', fontWeight:'800', fontSize:'13px', cursor:'pointer'}}>
                              Quero renovar
                            </button>
                          )}
                          <button onClick={() => { if (!mostrarAntecipar) uso.acao('aluno.antecipar_abrir', { proximo: true }); setMostrarAntecipar(v => !v); }}
                            style={{flex:1, minWidth:'140px', padding:'12px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontWeight:'800', fontSize:'13px', cursor:'pointer'}}>
                            {mostrarAntecipar ? 'Fechar' : 'Pagar o próximo adiantado'}
                          </button>
                        </div>
                      )}
                      {mostrarAntecipar && !(avisouF && avisouF.jaPagou) && !temPix && (
                        <p className="text-xs text-gray-500 mt-3" style={{lineHeight:1.5}}>
                          {NPf} ainda não cadastrou a chave Pix no Plen: combine com {nPf} como pagar e toque em "Já paguei o próximo".
                        </p>
                      )}
                    </Card>
                  )}

                  {mostrarAntecipar && temPix && (pacoteSemProx ? !(avisoRenovar && avisoRenovar.contrato_id === contrato.id && avisoRenovar.jaPagou) : contrato?.tipo_cobranca === 'mensal')
                    && blocoPix(pacoteSemProx ? 'Pagar o próximo pacote' : 'Pagar adiantado', contrato?.tipo_cobranca === 'mensal'
                    ? `Valor da próxima mensalidade: ${fmtBRL(pagar.valor)}`
                    : pagar.valor > 0 ? `Valor do próximo pacote (o mesmo do atual): ${fmtBRL(pagar.valor)}` : `Combine o valor com ${nPf}.`)}

                  {mostrarAntecipar && pacoteSemProx && !(avisouF && avisouF.jaPagou) && (
                    <>
                      <button disabled={salvando} onClick={async () => { await avisarRenovacaoAluno(true); setMostrarAntecipar(false); }}
                        style={{width:'100%', padding:'14px', borderRadius:'13px', border:'1.5px solid #E2E4E9', background:'white',
                                color:'#3D424C', fontWeight:'800', fontSize:'14px', cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.6 : 1}}>
                        <Ico n="send" s={15} /> {salvando ? 'Avisando…' : `Já paguei o próximo — avisar ${nPf}`}
                      </button>
                      <div style={{fontSize:'12px', color:'#9AA0AB', textAlign:'center'}}>
                        {NPf} combina o próximo pacote e dá baixa. Pagar antes não muda nenhuma data.
                      </div>
                    </>
                  )}
                </>
              );
            })()}

            {/* v149: o pacote da fila — quando comeca, quanto, e pagar antes / avisar que pagou */}
            {proximoContrato && (() => {
              const pgP = (pagamentos || []).find(pg => pg.contrato_id === proximoContrato.id && pg.status !== 'cancelado');
              const nP = prof?.nome?.split(' ')[0] || 'seu professor';
              const aceito = proximoContrato.status_aceite === 'aceito';
              // v152: vence no dia da ultima aula do atual — vencida (ou vencendo hoje) continua pagavel aqui
              const vencP = pgP && pgP.data_vencimento ? String(pgP.data_vencimento).slice(0, 10) : '';
              const vencidaP = !!(vencP && vencP <= hojeSP());
              const podePagar = !!(pgP && ehCobrancaDaFila(pgP) && !pgP.declarado_pago_em);
              const pagarP = pgP ? comoPagar([pgP], prof, proximoContrato) : null;
              // v155: um so' Pix por tela — com o pacote atual em aberto, o Pix la' de cima vem primeiro
              const temPixP = !!(pagarP && pagarP.forma === 'pix_direto' && pagarP.chave) && emAbertoAluno.length === 0;
              let qrP = null;
              if (pixFila && temPixP && pagarP.brcode) {
                try { const q = qrcode(0, 'M'); q.addData(pagarP.brcode); q.make(); qrP = q.createDataURL(6, 8); } catch (e) { qrP = null; }
              }
              // v156: o mesmo selo do Inicio (helper seloPgProximo) — com icone; "Vence em" aqui, com espaco.
              // Pacote ainda nao aceito: cinza "A confirmar", igual ao Inicio (nada de ambar/vermelho antes do aceite)
              const selo0 = pgP ? seloPgProximo(pgP, proximoContrato) : null;
              const selo = selo0 && aceito && pgP.status === 'pendente' && !pgP.declarado_pago_em && !vencidaP && pgP.data_vencimento ? { ...selo0, t: `Vence em ${fmtDiaMes(pgP.data_vencimento)}` } : selo0;
              const dPgP = pgP && pgP.status === 'pago' ? diaPgSP(pgP.data_pagamento) : '';
              return (
                <>
                  <div id="bloco-proximo-pacote" style={{scrollMarginTop:'80px'}} />
                  <Card>
                    <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-2">Próximo pacote</p>
                    {(() => {
                      // v150 (Marcus 28/10): as duas datas, sem frase — o atual termina na ultima aula dele; o proximo comeca na 1a
                      const ultF = contrato ? (aulas || []).filter(x => x.contrato_id === contrato.id && (x.status === 'agendada' || x.status === 'aguardando_confirmacao')
                        && !(proximoContrato.data_inicio && x.data >= String(proximoContrato.data_inicio).slice(0, 10))).map(x => x.data).sort().pop() : null;
                      return (
                        <div className="text-sm text-gray-600" style={{lineHeight:1.6}}>
                          {ultF && <div>Pacote atual termina <strong>{diaEDataSM(ultF)}</strong></div>}
                          <div>Próximo pacote começa <strong>{diaEDataSM(proximoContrato.data_inicio)}</strong> · {proximoContrato.total_aulas || ''} aulas até {fmtDiaMes(proximoContrato.data_fim)}</div>
                        </div>
                      );
                    })()}
                    {pgP && (
                      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px', marginTop:'10px', flexWrap:'wrap'}}>
                        <span className="text-2xl font-bold text-gray-800">{fmtBRL(pgP.valor)}</span>
                        {selo && (
                          <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'3px'}}>
                            <SeloPg s={selo} />
                            {dPgP && <span style={{fontSize:'12px', color:'#0A6F4D', fontWeight:600}}>Pago em {fmtDiaMes(dPgP)}</span>}
                          </div>
                        )}
                      </div>
                    )}
                    {notaAvisoFalhou(pgP)}
                    {!aceito ? (
                      <>
                        <p className="text-xs text-gray-500 mt-3">Confirme o pacote antes de pagar.</p>
                        <button onClick={() => { uso.acao('aluno.acordo_abrir_folha', { fila: true, origem: 'financeiro' }); setFolhaAcordo(true); }}
                          style={{width:'100%', marginTop:'8px', padding:'12px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontWeight:'800', fontSize:'14px', cursor:'pointer'}}>
                          Ver e confirmar
                        </button>
                      </>
                    ) : podePagar ? (
                      <>
                        <div style={{display:'flex', gap:'8px', marginTop:'12px', flexWrap:'wrap'}}>
                          {temPixP && (
                            <button onClick={() => { if (!pixFila) uso.acao('aluno.fila_pix_abrir'); setPixFila(v => !v); }}
                              style={{flex:1, minWidth:'140px', padding:'12px', borderRadius:'12px', border:'1.5px solid #C2D4FF', background:'#E7EDFF', color:'#2F6BFF', fontWeight:'800', fontSize:'13px', cursor:'pointer'}}>
                              {pixFila ? 'Fechar Pix' : vencidaP ? 'Pagar com Pix' : 'Pagar adiantado'}
                            </button>
                          )}
                          {!(pixFila && temPixP) && (
                            <button disabled={salvando} onClick={() => declararPagamentoAluno([pgP.id])}
                              style={{flex:1, minWidth:'140px', padding:'12px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontWeight:'800', fontSize:'13px', cursor: salvando ? 'not-allowed' : 'pointer'}}>
                              {salvando ? 'Avisando…' : 'Já paguei'}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">{temPixP ? '' : emAbertoAluno.length > 0 ? 'Primeiro o pacote atual: o Pix dele está aqui em cima. Se já pagou o próximo, toque em "Já paguei". ' : `${nP.charAt(0).toUpperCase() + nP.slice(1)} ainda não cadastrou a chave Pix no Plen — combine com ${nP} como pagar e toque em "Já paguei". `}{vencidaP ? 'Vence no dia da última aula do pacote atual.' : 'Pagar antes não muda nenhuma data.'} {nP.charAt(0).toUpperCase() + nP.slice(1)} confirma quando o pagamento cair.</p>
                      </>
                    ) : (pgP && pgP.status === 'pendente' && !pgP.declarado_pago_em && (
                      <p className="text-xs text-gray-500 mt-3">{vencP === hojeSP() ? 'Vence hoje' : 'Já venceu'}: o pagamento está em "Pagamento em aberto", aqui em cima, {temPixAluno ? 'com o Pix e o "Já paguei".' : `com o "Já paguei" — combine com ${nP} como pagar e toque em "Já paguei".`}</p>
                    ))}
                  </Card>
                  {pixFila && podePagar && temPixP && (
                    <div className="bg-gradient-to-br from-green-800 to-green-600 rounded-2xl p-6 text-white shadow-lg">
                      <div className="flex items-center gap-2 mb-4"><span className="text-2xl"><Ico n="bolt" /></span><p className="text-lg font-bold">Pagar o próximo pacote</p></div>
                      <div className="bg-white/15 rounded-xl p-4 mb-4">
                        <p className="text-xs text-green-100 mb-1">{pagarP.tipo_chave ? String(pagarP.tipo_chave).toUpperCase() : 'CHAVE PIX'}</p>
                        <p className="font-bold text-base" style={{wordBreak:'break-all'}}>{pagarP.chave}</p>
                      </div>
                      <p className="text-sm text-green-100 mb-4">{`Valor: ${fmtBRL(pagarP.valor)}`}</p>
                      {qrP && (
                        <div className="bg-white rounded-xl p-3 mb-4 flex flex-col items-center">
                          <img src={qrP} alt="QR Code Pix" style={{width:'190px',height:'190px',imageRendering:'pixelated'}} />
                          <p className="text-[11px] text-gray-500 mt-1 text-center">Aponte a câmera do app do seu banco</p>
                        </div>
                      )}
                      {pagarP.brcode && (
                        <button onClick={() => navigator.clipboard.writeText(pagarP.brcode).then(() => { uso.acao('aluno.pix_copiar_codigo', { fila: true }); toastSM('Código copiado! Cole no app do banco'); }, () => {})}
                          className="w-full rounded-xl py-3 font-bold transition-all mb-2 bg-white/90 text-green-800 hover:bg-white">
                          Copiar código Pix (copia e cola)
                        </button>
                      )}
                      <button onClick={() => declararPagamentoAluno([pgP.id])} disabled={salvando}
                        className="w-full border-2 border-white/40 rounded-xl py-3 font-bold text-white bg-white/15 hover:bg-white/25">
                        Já paguei — avisar {nP}
                      </button>
                    </div>
                  )}
                </>
              );
            })()}

            <Card>
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Histórico de Pagamentos</p>
              {pagamentos.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-4">Sem pagamentos registrados</p>
              ) : [...pagamentos].sort((x, y) => {
                // v155 (teste de 29/10): o mais novo em cima e os cancelados no fim — antes vinham misturados, com um
                // "Pacote de 05/11 a 01/12 · Cancelado" logo acima do mesmo pacote pago
                const can = p => p.status === 'cancelado' ? 1 : 0;
                const ini = p => String(p.periodo_inicio || (dados?.contratos || []).find(k => k.id === p.contrato_id)?.data_inicio || p.data_vencimento || '');
                return can(x) - can(y) || ini(y).localeCompare(ini(x));
              }).map(p => {
                // v156 (Marcus 29/10): cada linha diz O QUE foi pago (em cima) e QUANDO/COMO (embaixo), valor e selo a' direita.
                // A data e' o dia em Brasilia (data_pagamento e' timestamptz; so' 'AAAA-MM-DD' fica como esta').
                const kP = (dados?.contratos || []).find(k => k.id === p.contrato_id) || null;
                const dPg = diaPgSP(p.data_pagamento);
                const pIni = p.periodo_inicio || kP?.data_inicio || null;
                const pFim = p.periodo_fim || kP?.data_fim || null;
                const mesRef = /^\d{4}-\d{2}/.test(p.referencia_mes || '') ? String(p.referencia_mes).slice(0, 7) : '';
                const ehMensalP = p.tipo === 'mensalidade' || kP?.tipo_cobranca === 'mensal';
                const antes = ehPagoAntesDoCadastro(p) && !p.periodo_inicio;
                const oQue = antes ? 'Pago antes de usar o Plen'
                  : p.tipo === 'avulsa' ? 'Aula avulsa'
                  : ehMensalP ? (mesRef ? 'Mensalidade de ' + new Date(mesRef + '-15T12:00:00').toLocaleDateString('pt-BR', mesRef.slice(0, 4) === hojeSP().slice(0, 4) ? { month: 'long' } : { month: 'long', year: 'numeric' }) : 'Mensalidade')
                  : (pIni && pFim) ? `Pacote de ${fmtDiaMes(pIni)} a ${fmtDiaMes(pFim)}`
                  : 'Pacote';
                const forma = formaDoPagamento(p);
                const nP = prof?.nome?.split(' ')[0] || 'o professor';
                const quando = p.status === 'pago' ? (dPg ? `Pago em ${fmtDiaMes(dPg)}${forma !== '—' && !antes ? ' · ' + forma : ''}` : antes ? '' : 'Pago')
                  : p.status === 'cancelado' ? 'Cancelado'
                  : p.declarado_pago_em ? `Você avisou em ${fmtDiaMes(diaSP(p.declarado_pago_em))} · ${nP} confirma`
                  : p.data_vencimento ? `Vence em ${fmtDiaMes(p.data_vencimento)}` : 'Em aberto';
                const selo = p.status === 'pago' ? { t: 'Pago', ic: 'circle-check', bg: '#DCF5EC', c: '#0A6F4D' }
                  : p.status === 'cancelado' ? { t: 'Cancelado', ic: 'circle-x', bg: '#F0F1F4', c: '#6E7480' }
                  : p.declarado_pago_em ? { t: 'Avisado', ic: 'clock', bg: '#E7EDFF', c: '#1C43AE' }
                  : (p.data_vencimento && String(p.data_vencimento).slice(0, 10) <= hojeSP()) ? { t: String(p.data_vencimento).slice(0, 10) === hojeSP() ? 'Vence hoje' : 'Venceu', ic: 'alert-circle', bg: '#FEE2E2', c: '#991B1B' }
                  : { t: 'Pendente', ic: 'calendar-due', bg: '#FFFBEB', c: '#92400E' };
                const cancel = p.status === 'cancelado';
                return (
                  <div key={p.id} className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-50 last:border-0 last:pb-0 last:mb-0">
                    <div className="bg-green-50 rounded-xl p-2.5 text-lg"><Ico n="coin" /></div>
                    <div style={{flex:1, minWidth:0}}>
                      <p style={{fontSize:'14px', fontWeight:700, color: cancel ? '#9AA0AB' : '#16181D', lineHeight:1.3, textDecoration: cancel ? 'line-through' : 'none'}}>{oQue}</p>
                      {quando && <p style={{fontSize:'12.5px', color: p.status === 'pago' ? '#0A6F4D' : '#6E7480', marginTop:'2px', fontWeight: p.status === 'pago' ? 600 : 400}}>{quando}</p>}
                    </div>
                    <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'4px', flexShrink:0}}>
                      <span className="num" style={{fontWeight:800, fontSize:'15px', color: cancel ? '#9AA0AB' : '#16181D'}}>{fmtBRL(p.valor)}</span>
                      <SeloPg s={selo} />
                    </div>
                  </div>
                );
              })}
            </Card>
          </div>
        )}

        {}
        {tela === 'contrato' && (
          <div className="flex flex-col gap-4">
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-900">Meu acordo</h1>
              <p className="text-sm text-gray-400 mt-1">O que foi combinado com seu professor</p>
            </div>
            {!contrato ? (
              <Card className="text-center py-10"><div className="text-4xl mb-3"><Ico n="clipboard-list" /></div><p className="text-gray-500 text-sm">Nenhum acordo em vigor</p></Card>
            ) : (
              <>
                {(contrato.modo || 'interno') === 'formal' ? (
                  contrato.status_aceite !== 'aceito' && !contratoAssinado ? (
                    <div className="bg-gradient-to-br from-indigo-900 to-indigo-600 rounded-2xl p-6 text-white shadow-lg">
                      <div className="text-2xl mb-3"><Ico n="writing-sign" /></div>
                      <p className="text-lg font-bold mb-2">Confirmar o acordo</p>
                      <p className="text-sm text-indigo-200 mb-5">Ao confirmar, as condições combinadas com {prof?.nome || 'seu professor'} passam a valer para as suas aulas.</p>
                      <div className="bg-white/10 rounded-xl px-4 py-1 mb-5 text-sm">
                        {termosAtuais?.texto
                          ? <TextoDoAcordo texto={termosAtuais.texto} escuro />
                          : <p className="text-indigo-200 py-3">{termosAtuais?.erro ? 'Não consegui carregar o acordo.' : 'Carregando o acordo…'}</p>}
                      </div>
                      {termosAtuais?.inconsistente ? (
                        <div className="bg-white/15 rounded-xl p-4 text-sm font-semibold">
                          Ainda não dá para confirmar: as cobranças lançadas passam do total deste acordo. Peça ao professor para revisar o valor do pacote.
                        </div>
                      ) : termosAtuais?.erro ? (
                        <button onClick={() => carregarTermos((acordoK || contrato).id)}
                          className="w-full bg-white text-indigo-700 font-bold py-3 rounded-xl hover:bg-indigo-50 transition-colors">
                          Tentar de novo
                        </button>
                      ) : (
                        <button onClick={() => assinarContrato()} disabled={salvando || !termosAtuais?.sha}
                          className="w-full bg-white text-indigo-700 font-bold py-3 rounded-xl hover:bg-indigo-50 transition-colors" style={{opacity: (salvando || !termosAtuais?.sha) ? 0.7 : 1}}>
                          {salvando ? 'Confirmando…' : 'Confirmo o acordo'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center gap-4">
                      <span className="text-3xl"><Ico n="circle-check" c="#0E9F6E" /></span>
                      <div><p className="font-bold text-green-800">Acordo confirmado</p><p className="text-sm text-green-600 mt-0.5">Você confirmou as condições do seu plano.</p></div>
                    </div>
                  )
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
                    <span className="text-3xl"><Ico n="clipboard-list" /></span>
                    <div><p className="font-bold text-slate-700">Plano ativo</p><p className="text-sm text-slate-500 mt-0.5">Seu professor gerencia os detalhes internamente.</p></div>
                  </div>
                )}

                <Card>
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">O que foi combinado</p>
                  {[['Plano',plano?.nome||'—'],['Aulas/semana',`${aulasPorSemana}x`],(ehMensalAluno ? null : ['Pacote',`${contrato.total_aulas || 0} aulas`]),(ehMensalAluno ? ['Mensalidade', fmtBRL(Number(contrato.valor_mensal) > 0 ? contrato.valor_mensal : contrato.preco_negociado) + '/mês'] : ['Valor do pacote', fmtBRL(contrato.preco_negociado)]),['Início',new Date(contrato.data_inicio+'T12:00').toLocaleDateString('pt-BR')],['Término',dataFim?dataFim.toLocaleDateString('pt-BR'):'—'],['Professor',prof?.nome||'—']].filter(Boolean).map(([k,v])=>(
                    <div key={k} className="flex justify-between items-center pb-3 mb-3 border-b border-gray-50 last:border-0 last:pb-0 last:mb-0">
                      <span className="text-sm text-gray-400">{k}</span>
                      <span className="text-sm font-semibold text-gray-800">{v}</span>
                    </div>
                  ))}
                </Card>

                {reajustePendente && cartaoReajuste()}
              </>
            )}
          </div>
        )}
        {}
        {tela === 'notificacoes' && (
          <PendenciasAluno pend={pend} onIr={irPendenciaAluno} onResponderAula={responderAulaPendencia}
            onRespondeuRemarcacao={() => { if (dados?.id) loadContexto(dados.id); }}
            onAbrirAula={(item, modo) => abrirAulaAluno((item.detalhe && item.detalhe.aula_id) || item.ref_id, modo)}
            historico={   /* a lista de avisos de antes, agora como "Historico de avisos" */
              <div className="flex flex-col gap-3">
                {notifs.length === 0 ? (
                  <p className="text-sm text-gray-500" style={{ margin: '4px 2px 10px' }}>Nenhum aviso por enquanto.</p>
                ) : notifs.map(n => {
                  const tiposMap = {
                    renovacao:             { emoji:'refresh', cor:'blue',   label:'Renovação'},
                    plano_expirando:       { emoji:'hourglass', cor:'yellow', label:'Plano Expirando'},
                    credito_baixo:         { emoji:'hourglass', cor:'red',   label:'Crédito Baixo'},
                    aceite_reajuste:       { emoji:'trending-up', cor:'purple', label:'Proposta de Reajuste'},
                    contrato:              { emoji:'file-text', cor:'indigo', label:'Acordo'},
                    contrato_assinado:     { emoji:'file-check', cor:'green', label:'Acordo confirmado'},   // v144: o professor confirmou o seu pedido
                    aula_cancelada:        { emoji:'circle-x', cor:'red',   label:'Aula Cancelada'},
                    credito_atualizado:    { emoji:'ticket', cor:'green', label:'Crédito Atualizado'},
                    reposicao_disponivel:  { emoji:'calendar-plus', cor:'blue',  label:'Reposição disponível'},
                    cobranca_lembrete:     { emoji:'credit-card', cor:'orange', label:'Pagamento em aberto'},
                    aula_corrigida:        { emoji:'pencil', cor:'blue',   label:'Aula corrigida'},
                    cadastro_alterado:     { emoji:'pencil', cor:'blue',   label:'Cadastro atualizado'},
                    reposicao_marcada:     { emoji:'calendar-plus', cor:'green', label:'Reposição marcada'},
                    recompensa:            { emoji:'gift', cor:'green',  label:'Recompensa'},
                    pagamento_confirmado:  { emoji:'circle-check', cor:'green', label:'Pagamento confirmado'},
                    // v131 (Etapas 5–9): avisos que o banco manda ao aluno
                    lembrete_aula:         { emoji:'alarm', cor:'blue',   label:'Lembrete de aula'},
                    cancelamento_professor:{ emoji:'circle-x', cor:'red', label:'Aula cancelada'},
                    aula_empurrada:        { emoji:'arrow-right', cor:'blue', label:'Aula movida'},
                    aula_decidida:         { emoji:'clipboard-check', cor:'gray', label:'Aula registrada'},
                    aula_fechada_prazo:    { emoji:'lock', cor:'gray',    label:'Aula fechada'},
                    reagendamento:         { emoji:'calendar-event', cor:'blue', label:'Aula remarcada'},
                    reagendamento_aceito:  { emoji:'calendar-check', cor:'green', label:'Remarcação aceita'},
                    // v140
                    presenca_pedida:       { emoji:'calendar-question', cor:'blue', label:'Pergunta do professor'},
                    aula_devolvida:        { emoji:'arrow-back-up', cor:'green', label:'Professor não veio'},
                    aula_divergencia:      { emoji:'arrows-diff', cor:'orange', label:'Registro diferente'},
                    // v142: o professor desfez o que tinha feito
                    acao_desfeita:         { emoji:'arrow-back-up', cor:'gray', label:'Ação desfeita'},
                  };
                  const t = tiposMap[n.tipo] || { emoji:'bell', cor:'gray', label:'Aviso'};
                  const data = new Date(n.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
                  // v134: aviso que pedia uma acao ja' feita (a pendencia nao esta' mais aberta)
                  const desfeitoN = avisoDesfeitoSM(n, notifs);   // v142
                  const feito = !desfeitoN && pend.estado === 'ok' && avisoResolvidoSM(n,
                    [...((pend.aluno && pend.aluno.sua_vez) || []), ...((pend.aluno && pend.aluno.esperando) || []).filter(i => i.tipo === 'aula_sem_registro')],
                    FAMILIA_AVISO_ALUNO_SM);
                  return (
                    <div key={n.id} className={`bg-white rounded-2xl border p-4 shadow-sm flex items-start gap-3 ${!n.lida ? 'border-indigo-200' : 'border-gray-100'}`}>
                      <div className="flex-shrink-0 mt-0.5"><Ico n={t.emoji} s={22} /></div>
                      <div className="flex-1" style={{ minWidth: 0 }}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge color={t.cor}>{t.label}</Badge>
                          {!n.lida && <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0"/>}
                          {desfeitoN ? <Badge color="gray"><Ico n="arrow-back-up" s={12} /> desfeito</Badge>
                            : feito ? <Badge color="green"><Ico n="check" s={12} /> resolvido</Badge> : null}
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed" style={{ overflowWrap: 'anywhere' }}>{n.mensagem}</p>
                        <p className="text-xs text-gray-400 mt-1">{data}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            } />
        )}

      </div>

      </main>

      {}
      {acaoAula && (
        <Portal>
        <div style={{position:'fixed',inset:0,zIndex:120,display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
          <div onClick={() => setAcaoAula(null)} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)'}}/>
          {/* altura limitada + rolagem propria + espaco da barra inferior e da area segura do iPhone */}
          <div style={{position:'relative',background:'white',borderRadius:'24px 24px 0 0',padding:'22px 20px',
                       maxHeight:'82vh',overflowY:'auto',WebkitOverflowScrolling:'touch',
                       paddingBottom:'calc(96px + env(safe-area-inset-bottom))',
                       boxShadow:'0 -20px 60px rgba(0,0,0,0.25)'}}>
            <button onClick={() => setAcaoAula(null)} aria-label="Fechar"
              style={{position:'absolute',top:'12px',right:'14px',width:'32px',height:'32px',borderRadius:'50%',
                      border:'none',background:'#F0F1F4',color:'#6E7480',fontSize:'17px',cursor:'pointer',lineHeight:1}}><Ico n="x" /></button>
            <div style={{width:'40px',height:'4px',background:'#E2E4E9',borderRadius:'2px',margin:'0 auto 20px'}}/>
            {acaoAula.modo === 'opcoes' && (
              <>
                <p className="font-bold text-gray-800 text-base mb-1">Aula {new Date(acaoAula.aula.data+'T12:00').toLocaleDateString('pt-BR',{weekday:'short',day:'numeric',month:'short'})} às {acaoAula.aula.hora?.slice(0,5)}</p>
                <p className="text-sm text-gray-400 mb-5">O que você gostaria de fazer?</p>

                {(() => {
                  // v156 (Marcus 29/10): de 30 min antes do horario ate' o fim, o principal e' "A aula comecou" (o
                  // professor chegou mais cedo) — o aviso de falta e o reagendar vem depois. Depois do fim, o
                  // "Confirmar que a aula aconteceu" la' embaixo. Turma: como antes (so' aula individual).
                  const aF = acaoAula.aula;
                  const iniF = inicioAulaSP(aF);
                  const naJanela = !!iniF && aF.status === 'agendada' && Date.now() >= iniF.getTime() - JANELA_COMECOU_MIN * 60000 && !jaAconteceu(aF);
                  if (!naJanela || aF.confirmada_aluno_em || aF.falta_declarada_aluno_em || aF.desfecho_prof || avisoVigenteAula(aF) || !aulaIndividualSM(aF)) return null;
                  return (
                    <button onClick={() => responderAulaAluno(aF, true, { origem: Date.now() < iniF.getTime() ? 'folha_antes' : 'folha_agora' })} disabled={salvando}
                      style={{width:'100%',background:'#2F6BFF',color:'white',fontWeight:'800',textAlign:'left',padding:'14px 20px',borderRadius:'16px',marginBottom:'10px',border:'none',cursor: salvando ? 'not-allowed' : 'pointer',display:'flex',alignItems:'center',gap:'12px'}}>
                      <span style={{fontSize:'22px'}}><Ico n="circle-check" c="white" /></span>
                      <div>
                        <div style={{fontSize:'14px',fontWeight:'800'}}>A aula começou</div>
                        <div style={{fontSize:'11px',fontWeight:'400',color:'#E7EDFF',marginTop:'2px'}}>{prof?.nome?.split(' ')[0] || 'Seu professor'} chegou? Confirme — ele fecha com um toque</div>
                      </div>
                    </button>
                  );
                })()}

                {/* v140: a pergunta do professor ainda aberta — responder primeiro */}
                {perguntaAbertaSM(acaoAula.aula) ? (
                  <button onClick={() => setAcaoAula({ ...acaoAula, modo: 'presenca' })}
                    style={{width:'100%',background:'#E7EDFF',color:'#1C43AE',fontWeight:'700',textAlign:'left',padding:'14px 20px',borderRadius:'16px',marginBottom:'10px',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:'12px'}}>
                    <span style={{fontSize:'22px'}}><Ico n="calendar-question" /></span>
                    <div>
                      <div style={{fontSize:'14px',fontWeight:'700'}}>{prof?.nome?.split(' ')[0] || 'Seu professor'} quer saber se você vai</div>
                      <div style={{fontSize:'11px',fontWeight:'400',color:'#2F6BFF',marginTop:'2px'}}>Responda Vou ou Não vou</div>
                    </div>
                  </button>
                ) : null}

                {/* A aula ja' passou e ninguem confirmou. Ate' aqui o aluno so' podia
                    desmarcar ou remarcar — nao tinha como dizer "aconteceu". A confirmacao
                    dele NAO consome credito sozinha: quem fecha a conta e' o professor.
                    Isto so' adianta o trabalho e tira a aula do limbo. */}
                {jaAconteceu(acaoAula.aula) && !acaoAula.aula.confirmada_aluno_em ? (
                  <button onClick={() => confirmarAulaAluno(acaoAula.aula)} disabled={salvando}
                    style={{width:'100%',background:'#EFFAF5',color:'#0A6F4D',fontWeight:'700',textAlign:'left',padding:'14px 20px',borderRadius:'16px',marginBottom:'10px',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:'12px'}}>
                    <span style={{fontSize:'22px'}}><Ico n="circle-check" /></span>
                    <div>
                      <div style={{fontSize:'14px',fontWeight:'700'}}>Confirmar que a aula aconteceu</div>
                      <div style={{fontSize:'11px',fontWeight:'400',color:'#0E9F6E',marginTop:'2px'}}>Seu professor recebe o aviso e fecha com um toque</div>
                    </div>
                  </button>
                ) : null}

                {acaoAula.aula.confirmada_aluno_em ? (
                  <div style={{background:'#EFFAF5',color:'#0A6F4D',padding:'12px 16px',borderRadius:'14px',marginBottom:'10px',fontSize:'13px',fontWeight:'600'}}>
                    <Ico n="circle-check" s={14} /> Você já confirmou. Falta o professor fechar.
                  </div>
                ) : null}

                {/* v131: se o aluno ja' avisou (hora oficial do banco), a folha diz isso em vez de
                    oferecer de novo. O botao abre o cartao pelo PALPITE do prazo; a previa do banco
                    corrige o desenho em seguida, se for o caso. */}
                {avisoVigenteAula(acaoAula.aula) ? (
                  <div style={{background:'#FFFBEB',color:'#92400E',padding:'12px 16px',borderRadius:'14px',marginBottom:'10px',fontSize:'13px',fontWeight:'600',lineHeight:1.5}}>
                    <Ico n="bell-ringing" s={14} /> {textoAvisouAluno(acaoAula.aula, prof?.nome?.split(' ')[0] || 'Seu professor')} {avisoDoProfSM(acaoAula.aula) ? 'que não vinha' : 'que não vai'} a esta aula. {avisoDoProfSM(acaoAula.aula)
                      ? `Se não foi isso, fale com ${prof?.nome?.split(' ')[0] || 'seu professor'}.`
                      : `${prof?.nome?.split(' ')[0] || 'Seu professor'} já recebeu o aviso.`}
                    {podeNaoVeioContraAvisoSM({ ...acaoAula.aula, duracao_min: acaoAula.aula.duracao_min || contrato?.duracao_min }) ? (
                      <button onClick={() => { uso.acao('aluno.nao_veio_contra_aviso', { origem: 'folha' }); setAcaoAula({ ...acaoAula, modo: 'nao_veio' }); }}
                        style={{display:'flex', alignItems:'center', gap:'6px', marginTop:'6px', minHeight:'44px', border:'none', background:'none', padding:0, color:'#991B1B', fontSize:'13px', fontWeight:'700', textDecoration:'underline', cursor:'pointer', fontFamily:'inherit'}}>
                        <Ico n="user-exclamation" s={14} /> Não avisei — {prof?.nome?.split(' ')[0] || 'o professor'} não veio
                      </button>
                    ) : null}
                    {/* v142 (A2): so' o aviso do proprio aluno (o registrado pelo professor, nao), antes de a aula comecar */}
                    {desfazerCartaoAlunoSM(acaoAula.aula) === 'aviso' ? (
                      <div style={{display:'flex', flexDirection:'column'}}>
                        <BotaoDesfazerSM alvo={{ ref_tipo: 'aula', ref_id: acaoAula.aula.id, esperado: 'aviso' }} papel="aluno" nomeOutro={prof?.nome}
                          onFeito={() => { setAcaoAula(null); if (dados?.id) loadContexto(dados.id); }} />
                      </div>
                    ) : null}
                  </div>
                ) : (
                <button onClick={() => setAcaoAula({...acaoAula, modo: podeCancelar(acaoAula.aula) ? 'cancelar' : 'avisar'})}
                  style={{width:'100%', background: podeCancelar(acaoAula.aula) ? '#FEF2F2' : '#FFFBEB', color: podeCancelar(acaoAula.aula) ? '#991B1B' : '#92400E', fontWeight:'700', textAlign:'left', padding:'14px 20px', borderRadius:'16px', marginBottom:'10px', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:'12px'}}>
                  <span style={{fontSize:'22px'}}><Ico n={podeCancelar(acaoAula.aula) ? 'circle-x' : 'alert-triangle'} /></span>
                  <div>
                    <div style={{fontSize:'14px',fontWeight:'700'}}>{podeCancelar(acaoAula.aula) ? 'Cancelar esta aula' : 'Avisar que não vou comparecer'}</div>
                    <div style={{fontSize:'11px',fontWeight:'400',marginTop:'2px',color: podeCancelar(acaoAula.aula) ? '#DC2626' : '#B45309'}}>
                      {podeCancelar(acaoAula.aula) ? 'Dentro do prazo — pode ter direito a reposição' : 'Fora do prazo de aviso — sem reposição garantida'}
                    </div>
                  </div>
                </button>
                )}

                {}
                <button onClick={() => setAcaoAula({...acaoAula,modo:'reagendar'})}
                  style={{width:'100%',background:'#E7EDFF',color:'#2455D6',fontWeight:'700',textAlign:'left',padding:'14px 20px',borderRadius:'16px',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:'12px'}}>
                  <span style={{fontSize:'22px'}}><Ico n="refresh" /></span>
                  <div>
                    <div style={{fontSize:'14px',fontWeight:'700'}}>Solicitar reagendamento</div>
                    <div style={{fontSize:'11px',fontWeight:'400',color:'#2F6BFF',marginTop:'2px'}}>Sugira uma nova data ao professor</div>
                  </div>
                </button>
              </>
            )}
            {/* v140: "Tudo certo para a aula?" — o professor perguntou se o aluno vem. [Vou] responde
                (sem mexer em credito); [Nao vou conseguir] e' o aviso de falta de sempre, com o prazo ANTES. */}
            {acaoAula.modo === 'presenca' && (() => {
              const aulaF = acaoAula.aula;
              const NomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
              const qd = hojeOuAmanhaSM(aulaF.data);
              const aberta = perguntaAbertaSM(aulaF);
              return (
                <>
                  <p className="font-bold text-gray-800 text-base mb-1"><Ico n="calendar-question" c="#2F6BFF" /> {NomeP} quer saber se você vai</p>
                  <p className="text-sm text-gray-500 mb-4">{qd ? qd.charAt(0).toUpperCase() + qd.slice(1) + ', ' : ''}{minuscSM(quandoSP(inicioAulaSP(aulaF) || new Date()))}</p>
                  {!aberta ? (
                    <div style={{background:'#F7F8FA', borderRadius:'14px', padding:'12px 14px', fontSize:'13.5px', color:'#3D424C', lineHeight:1.5, marginBottom:'14px'}}>
                      {aulaF.presenca_vou_em ? `Você já avisou que vai ✓ ${NomeP} vê isso na agenda.` : 'Não dá mais para responder por aqui: a aula mudou, foi desmarcada ou já começou.'}
                    </div>
                  ) : (
                    <>
                      {consequenciaPresenca(aulaF) ? (
                        <div style={{background: podeCancelar(aulaF) ? '#EFFAF5' : '#FFFBEB', border: `1.5px solid ${podeCancelar(aulaF) ? '#B7E9D6' : '#FDE68A'}`, borderRadius:'14px', padding:'12px 14px',
                                     fontSize:'13px', color: podeCancelar(aulaF) ? '#0A5B40' : '#92400E', lineHeight:1.5, marginBottom:'14px'}}>
                          <Ico n="clock" s={13} /> {consequenciaPresenca(aulaF)}
                        </div>
                      ) : null}
                      <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                        <button disabled={!!vouBusy} onClick={() => vouPresenca(aulaF, 'folha')}
                          style={{minHeight:'48px', borderRadius:'14px', border:'none', background:'#2F6BFF', color:'white', fontSize:'15px', fontWeight:'800', cursor:'pointer', opacity: vouBusy ? 0.6 : 1}}>
                          {vouBusy === aulaF.id ? 'Salvando…' : <><Ico n="check" s={16} c="white" /> Vou</>}
                        </button>
                        <button disabled={!!vouBusy} onClick={() => naoVouPresenca(aulaF, 'folha')}
                          style={{minHeight:'48px', borderRadius:'14px', border:'1.5px solid #E2E4E9', background:'white', color:'#3D424C', fontSize:'15px', fontWeight:'800', cursor:'pointer'}}>
                          Não vou
                        </button>
                      </div>
                    </>
                  )}
                  <button onClick={() => setAcaoAula({ ...acaoAula, modo: aberta ? 'reagendar' : 'opcoes' })}
                    style={{marginTop:'10px', width:'100%', minHeight:'44px', border:'none', background:'none', color:'#2F6BFF', fontSize:'13.5px', fontWeight:'700', cursor:'pointer', textDecoration:'underline'}}>
                    {aberta ? 'Prefiro outro horário' : 'Ver as opções da aula'}
                  </button>
                </>
              );
            })()}
            {/* v140: "Cadu não chegou?" — 15 min depois do inicio, o aluno no local avisa o professor na hora */}
            {acaoAula.modo === 'nao_veio' && (() => {
              const aulaF = acaoAula.aula;
              const NomeP = prof?.nome?.split(' ')[0] || 'Seu professor';
              const ini = inicioAulaSP(aulaF);
              const fimF = ini ? new Date(ini.getTime() + ((Number(contrato?.duracao_min) || 60) * 60000)) : new Date();
              const volta = new Date(Math.max(fimF.getTime(), Date.now()) + 24 * 3600000);
              return (
                <>
                  <p className="font-bold text-gray-800 text-base mb-2"><Ico n="user-exclamation" c="#DC2626" /> {NomeP} não chegou?</p>
                  <p className="text-sm text-gray-600 mb-3" style={{lineHeight:1.5}}>{avisoDoProfSM(aulaF)
                    ? (aulaF.aviso_decisao
                      // v141: o professor ja' decidiu sobre o aviso — o banco mantem o aviso e registra o desacordo
                      ? `${NomeP} registrou que você avisou que não vinha e já decidiu sobre a aula. Se você não avisou e ele não veio, conte aqui: fica registrado que vocês discordam e ${NomeP} recebe o aviso na hora.`
                      : `${NomeP} registrou que você avisou que não vinha. Se você não avisou e ele não veio, conte aqui: o aviso registrado deixa de valer e ${NomeP} recebe o aviso na hora.`)
                    : `Vamos avisar ${NomeP} agora que você está esperando no local.`}</p>
                  <div style={{background:'#F7F8FA', borderRadius:'14px', padding:'12px 14px', fontSize:'13px', color:'#3D424C', lineHeight:1.5, marginBottom:'16px'}}>
                    {avisoDoProfSM(aulaF) && aulaF.aviso_decisao
                      ? `A aula não sai do seu pacote até ${NomeP} decidir — conversem.`
                      : ehMensalAluno
                      ? `Fica registrado que ele não veio. Na mensalidade não há reposição automática — combine com ${NomeP}.`
                      : `Se a aula não acontecer por causa dele, ela não sai do seu pacote. Se ele não responder até ${quandoSP(volta)}, ela volta sozinha para o fim do pacote.`}
                  </div>
                  <div className="flex gap-3">
                    <Button variant="secondary" onClick={() => setAcaoAula(null)} className="flex-1 justify-center">Voltar</Button>
                    <button disabled={salvando} onClick={() => responderAulaAluno(aulaF, false, { motivo: 'prof_nao_veio', origem: 'hoje_agora' })}
                      style={{flex:1, minHeight:'44px', borderRadius:'12px', border:'none', background:'#DC2626', color:'white', fontSize:'14px', fontWeight:'800', cursor: salvando ? 'not-allowed' : 'pointer', opacity: salvando ? 0.6 : 1}}>
                      {salvando ? 'Avisando…' : `Avisar ${NomeP}`}
                    </button>
                  </div>
                </>
              );
            })()}
            {acaoAula.modo === 'cancelar' && (() => {
              // Etapa 8 (v131): o cartao vem da PREVIA do banco (aluno_avisar_falta, p_simular) —
              // ate' quando, o que acontece com a aula e a renovacao. Enquanto carrega: o palpite.
              const aulaF = acaoAula.aula;
              const pa = previaAviso && previaAviso.aulaId === aulaF.id ? previaAviso : null;
              const pv = pa && pa.dados && !pa.dados.ja_feito ? pa.dados : null;
              const travou = blocoPreviaBloqueia(aulaF);
              const p1 = prof?.nome?.split(' ')[0] || 'o professor';
              const pp = prazoPalpite(aulaF);
              const prazoTxt = (pv && pv.prazo_texto) || (pp ? quandoSP(pp) : '');
              const mensalPv = pv && pv.regras && (pv.regras.tipo_cobranca === 'mensal' || pv.regras.turma);
              const nT = Number(pv && pv.teto) || 1;
              return (
              <>
                <p className="font-bold text-red-600 text-base mb-2"><Ico n="circle-x" c="#DC2626" /> Cancelar esta aula?</p>
                {acaoAula.doAviso ? <LinhaAulaDoAvisoSM aula={aulaF} /> : null}
                {travou || (<>
                {prazoTxt ? (
                  <div style={{display:'inline-flex', alignItems:'center', gap:'6px', background:'#EFFAF5', border:'1px solid #B7E9D6', color:'#0A6F4D',
                               borderRadius:'999px', padding:'5px 11px', fontSize:'12.5px', fontWeight:'700', marginBottom:'10px'}}>
                    <Ico n="clock" s={13} c="#0E9F6E" /> {pv && pv.chuva ? <>Chuva vale até o início: {prazoTxt}</> : <>Avise até {prazoTxt}</>}
                  </div>
                ) : null}
                <p className="text-sm text-gray-500 mb-5">
                  {!pv ? (pa && pa.carregando
                      ? <>Você está dentro do prazo. Conferindo o que acontece com a aula…</>
                      : <>Você está dentro do prazo de aviso. Não consegui conferir agora o que acontece com a aula — o app confere de novo quando você confirmar.</>)
                    : pv.chuva && pv.beneficio === 'fila' ? <>Chuva não conta para ninguém: <strong style={{color:'#0A6F4D'}}>você não perde a aula</strong>.</>
                    : pv.chuva && pv.beneficio === 'reposicao' ? <>Chuva não conta para ninguém: <strong style={{color:'#0A6F4D'}}>você não perde a aula</strong> — ela vira uma reposição para combinar com {p1}{pv.validade ? `, válida até ${fmtDataBR(pv.validade).slice(0, 5)}` : ''}.</>
                    : pv.beneficio === 'fila' ? <>Você está dentro do prazo e <strong style={{color:'#0A6F4D'}}>não perde a aula</strong>.</>
                    : pv.beneficio === 'reposicao' ? <>Você está dentro do prazo. <strong style={{color:'#0A6F4D'}}>Não perde a aula</strong> — ela vira uma reposição para combinar com {p1}{pv.validade ? `, válida até ${fmtDataBR(pv.validade).slice(0, 5)}` : ''}.</>
                    : pv.beneficio === 'desconto' ? <>Você está dentro do prazo. O valor da aula (<strong>{fmtBRL(pv.valor_credito)}</strong>) vira desconto na próxima cobrança.</>
                    : pv.beneficio === 'teto' ? <>Você está dentro do prazo e a aula não é descontada do pacote, mas o limite de {nT} reposiç{nT > 1 ? 'ões' : 'ão'} por chuva ou local fechado deste mês já foi usado.</>
                    : mensalPv ? <>Você está dentro do prazo. Na mensalidade a aula não gera crédito — {p1} pode oferecer uma reposição por cortesia.</>
                    : <>Você está dentro do prazo, mas este acordo não prevê reposição. A aula será cancelada sem crédito.</>}
                </p>
                {/* As duas datas ANTES de confirmar. Mexer na agenda mexe no dia em que a
                    pessoa vai pagar de novo — ela tem que saber disso na hora de decidir,
                    nao depois. E' a peca que o Marcus apontou como a mais importante. */}
                {pv && pv.beneficio === 'fila' && pv.fila && pv.fila.nova_data && (
                  <div style={{background:'#EFFAF5', border:'1.5px solid #B7E9D6', borderRadius:'14px', padding:'12px 14px', marginBottom:'14px'}}>
                    <div style={{fontSize:'13px', fontWeight:'800', color:'#0A6F4D'}}>
                      <Ico n="arrow-right" s={13} c="#0E9F6E" /> Sua aula vai para {fmtDataBR(pv.fila.nova_data)}
                    </div>
                    <div style={{fontSize:'12.5px', color:'#0C8A5F', marginTop:'4px', lineHeight:1.5}}>
                      {pv.chuva ? 'Chuva não queima nenhuma aula do pacote.' : 'Você comprou um pacote de aulas — desmarcar no prazo não queima nenhuma.'}
                      {pv.fila.no_proximo ? <> Ela entra no seu próximo pacote, como uma aula a mais nele. O dia de pagar não muda.</>
                        : pv.fila.proximo_pacote ? <> Ela fica antes do seu próximo pacote começar.</>
                        : <> Ela vai para o fim
                      do pacote{pv.fila.renovacao_antes && pv.fila.renovacao_depois && pv.fila.renovacao_antes !== pv.fila.renovacao_depois
                        ? <>, e por isso a sua previsão de renovação passa de <strong>{fmtDataBR(pv.fila.renovacao_antes)}</strong> para <strong>{fmtDataBR(pv.fila.renovacao_depois)}</strong></>
                        : null}.</>}
                      {!pv.fila.proximo_pacote && <> Se quiser repor antes, combine uma aula extra com {prof?.nome?.split(' ')[0] || 'seu professor'} — aí a data volta.</>}
                    </div>
                  </div>
                )}
                {pv && pv.chuva_livre && !pv.chuva && pv.beneficio !== 'fila' ? (
                  <div style={{background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'10px 12px', fontSize:'13px', color:'#0A5B40', lineHeight:1.5, marginBottom:'12px'}}>
                    <Ico n="cloud-rain" s={13} c="#0E9F6E" /> É por causa da chuva? Escolha <strong>Chuva ou local fechado</strong>: chuva não conta para ninguém{mensalPv ? ' e vira uma reposição a combinar' : ' e a aula vai para o fim do pacote'}.
                  </div>
                ) : null}
                <div style={{marginBottom:'14px'}}>
                  <MotivoPicker valor={motivoCancel} onChange={setMotivoCancel} obs={obsCancel} onObs={setObsCancel} />
                  {motivoCancel === 'feriado' && <p style={{fontSize:'12px',color:'#0A6F4D',marginTop:'8px'}}>Feriado não é culpa de ninguém: a aula vai para o fim do pacote e você não perde nada.</p>}
                </div>
                <div className="flex gap-3">
                  <Button variant="secondary" onClick={() => setAcaoAula({...acaoAula,modo:'opcoes'})} className="flex-1 justify-center">Voltar</Button>
                  <Button variant="danger" onClick={() => avisarFalta(aulaF)} disabled={salvando || !motivoCancel || !!(pa && pa.carregando)} className="flex-1 justify-center">{salvando?'Cancelando…':'Confirmar cancelamento'}</Button>
                </div>
                </>)}
              </>
              );
            })()}
            {acaoAula.modo === 'avisar' && (() => {
              const aulaF = acaoAula.aula;
              const pa = previaAviso && previaAviso.aulaId === aulaF.id ? previaAviso : null;
              const pv = pa && pa.dados && !pa.dados.ja_feito ? pa.dados : null;
              const travou = blocoPreviaBloqueia(aulaF);
              const p1 = prof?.nome?.split(' ')[0] || 'o professor';
              return (
              <>
                <p className="font-bold text-yellow-700 text-base mb-2"><Ico n="alert-triangle" c="#D97706" /> Avisar que não compareço</p>
                {acaoAula.doAviso ? <LinhaAulaDoAvisoSM aula={aulaF} /> : null}
                {travou || (<>
                <p className="text-sm text-gray-500 mb-4">
                  {pv && pv.prazo_texto
                    ? <>O prazo para desmarcar sem custo era até <strong>{pv.prazo_texto}</strong>.
                        {pv.consome ? ' Pela regra do acordo, a aula conta: sai 1 aula do seu pacote.' : ' Pela regra do acordo, a aula fica como falta.'}
                        {pv.pode_devolver ? ` Avise mesmo assim: ${p1} pode devolver a aula por cortesia.` : ` Avise mesmo assim: ${p1} fica sabendo na hora.`}</>
                    : <>Fora do prazo mínimo de aviso — pela regra do acordo, a aula conta. Avise mesmo assim: {p1} pode devolver a aula se entender o motivo.</>}
                </p>
                {pv && pv.chuva_livre ? (
                  <div style={{background:'#EFFAF5', border:'1px solid #B7E9D6', borderRadius:'12px', padding:'10px 12px', fontSize:'13px', color:'#0A5B40', lineHeight:1.5, marginBottom:'12px'}}>
                    <Ico n="cloud-rain" s={13} c="#0E9F6E" /> É por causa da chuva? Escolha <strong>Chuva ou local fechado</strong>: até o início da aula, chuva não conta para ninguém.
                  </div>
                ) : null}
                <div style={{marginBottom:'12px'}}><MotivoPicker valor={motivoCancel} onChange={setMotivoCancel} obs={obsCancel} onObs={setObsCancel} excluir={['feriado']} /></div>
                <textarea value={reagendarMsg} onChange={e => setReagendarMsg(e.target.value)} placeholder="Quer explicar melhor? (opcional)"
                  className="w-full border-2 border-gray-200 rounded-xl p-3 text-sm min-h-[60px] resize-none outline-none focus:border-yellow-400"/>
                <div className="flex gap-3 mt-4">
                  <Button variant="secondary" onClick={() => setAcaoAula({...acaoAula,modo:'opcoes'})} className="flex-1 justify-center">Voltar</Button>
                  <button onClick={() => avisarFalta(aulaF)} disabled={salvando || !!(pa && pa.carregando)}
                    style={{flex:1,padding:'10px',minHeight:'44px',borderRadius:'12px',border:'none',background:'#E8971A',color:'white',fontSize:'13px',fontWeight:'700',cursor:salvando?'not-allowed':'pointer',opacity:(pa && pa.carregando) ? .6 : 1}}>
                    {salvando?'Enviando…':'Avisar professor'}
                  </button>
                </div>
                </>)}
              </>
              );
            })()}
            {acaoAula.modo === 'reagendar' && (
              <>
                <p className="font-bold text-indigo-600 text-base mb-2"><Ico n="refresh" /> Solicitar reagendamento</p>
                <p className="text-sm text-gray-500 mb-1">Aula atual:</p>
                <p style={{fontSize:'14px',fontWeight:'700',color:'#16181D',marginBottom:'14px'}}>
                  {new Date(acaoAula.aula.data+'T12:00').toLocaleDateString('pt-BR',{weekday:'long',day:'numeric',month:'long'})} às {acaoAula.aula.hora?.slice(0,5)}
                </p>
                <p style={{fontSize:'12px',fontWeight:'600',color:'#9AA0AB',marginBottom:'6px'}}>Para que dia?</p>
                <input type="date" value={reagendarData} onChange={e => setReagendarData(e.target.value)}
                  min={hojeSP()}
                  style={{width:'100%',border:'1.5px solid #C2D4FF',borderRadius:'12px',padding:'10px 14px',fontSize:'14px',color:'#16181D',outline:'none',boxSizing:'border-box',marginBottom:'12px',background:'white'}} />
                <p style={{fontSize:'12px',fontWeight:'600',color:'#9AA0AB',marginBottom:'6px'}}>A que horas?</p>
                <input type="time" value={reagendarHora} onChange={e => setReagendarHora(e.target.value)}
                  style={{width:'100%',border:'1.5px solid #C2D4FF',borderRadius:'12px',padding:'10px 14px',fontSize:'14px',color:'#16181D',outline:'none',boxSizing:'border-box',marginBottom:'6px',background:'white'}} />
                <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'12px'}}>
                  {['07:00','09:00','12:00','18:00','19:00'].map(h => (
                    <button key={h} onClick={() => setReagendarHora(h)}
                      style={{padding:'6px 12px',borderRadius:'999px',fontSize:'12px',fontWeight:'700',cursor:'pointer',
                              border: reagendarHora===h ? '1.5px solid #2F6BFF' : '1.5px solid #E2E4E9',
                              background: reagendarHora===h ? '#E7EDFF' : 'white',
                              color: reagendarHora===h ? '#2F6BFF' : '#6E7480'}}>{h}</button>
                  ))}
                </div>
                <p style={{fontSize:'12px',fontWeight:'600',color:'#9AA0AB',marginBottom:'6px'}}>Observação (opcional)</p>
                <textarea value={reagendarMsg} onChange={e => setReagendarMsg(e.target.value)} placeholder="Ex: Posso às quartas após as 18h..."
                  style={{width:'100%',border:'1.5px solid #E2E4E9',borderRadius:'12px',padding:'10px 14px',fontSize:'14px',color:'#16181D',outline:'none',resize:'none',minHeight:'70px',boxSizing:'border-box',fontFamily:'inherit'}}/>
                <div className="flex gap-3 mt-4">
                  <Button variant="secondary" onClick={() => setAcaoAula({...acaoAula,modo:'opcoes'})} className="flex-1 justify-center">Voltar</Button>
                  <Button variant="primary" onClick={() => solicitarReagendamento(acaoAula.aula)} disabled={salvando} className="flex-1 justify-center">{salvando?'Enviando…':'Enviar solicitação'}</Button>
                </div>
              </>
            )}
          </div>
        </div>
        </Portal>
      )}
      <BottomNavAluno ativa={tela} setAtiva={handleNavAluno} notifCount={notifCount} />

      {}
      {modalEditarPerfil && (
        <ModalEditarPerfilAluno
          aluno={eu}
          userId={user.id}
          onClose={() => setModalEditarPerfil(false)}
          onSalvo={({ nome, telefone, emoji }) => {
            setAvatarEmoji(emoji);
            setPerfil(prev => prev ? { ...prev, nome } : prev);
            if (telefone) setTodosVinculos(prev => prev.map(v => ({ ...v, telefone })));
          }}
          onExcluirConta={excluirContaAluno}
        />
      )}

    </div>
  );
}

// v151 — "Trocar senha" no perfil (professor e aluno), para quem ja' esta' dentro do app. Antes o unico caminho era
// sair e pedir o e-mail do "Esqueci minha senha". Fechado, e' uma linha so'; aberto, os dois campos e Salvar.
function TrocarSenhaSM({ quem }) {
  const [aberto, setAberto] = React.useState(false);
  const [senha, setSenha] = React.useState('');
  const [confirma, setConfirma] = React.useState('');
  const [salvando, setSalvando] = React.useState(false);
  const [erro, setErro] = React.useState('');
  const [ok, setOk] = React.useState(false);
  const campo = {width:'100%', border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'12px 14px', fontSize:'15px', color:'#16181D', outline:'none', boxSizing:'border-box', background:'white'};

  async function salvar() {
    if (senha.length < 6) { setErro('A senha precisa ter pelo menos 6 caracteres.'); return; }
    if (senha !== confirma) { setErro('As duas senhas estão diferentes.'); return; }
    setSalvando(true); setErro('');
    const { error } = await supabase.auth.updateUser({ password: senha });
    setSalvando(false);
    if (error) {
      uso.bloqueio('senha_trocar', null, null, error.code || 'salvar_falhou');
      setErro(error.code === 'same_password' ? 'Essa já é a sua senha. Escolha outra.'
        : error.code === 'weak_password' ? 'Essa senha é fraca demais ou já vazou na internet. Escolha outra, mais longa.'
        : 'Não deu para salvar. Tente de novo.');
      return;
    }
    uso.acao('conta.senha_trocada', { quem });
    setOk(true); setAberto(false); setSenha(''); setConfirma('');
  }

  if (!aberto) return (
    <div>
      <button type="button" onClick={() => { uso.acao('conta.trocar_senha_abrir', { quem }); setAberto(true); setOk(false); setErro(''); }}
        style={{width:'100%', display:'flex', alignItems:'center', gap:'8px', padding:'12px 14px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#16181D', fontSize:'14px', fontWeight:'700', cursor:'pointer', textAlign:'left'}}>
        <Ico n="lock" /> <span style={{flex:1}}>Trocar senha</span> <span style={{color:'#9AA0AB'}}>›</span>
      </button>
      {ok && <p style={{fontSize:'13px', color:'#0E9F6E', fontWeight:'600', margin:'8px 2px 0'}}><Ico n="circle-check" c="#0E9F6E" /> Senha trocada. Use a nova na próxima vez que entrar.</p>}
    </div>
  );
  return (
    <div style={{border:'1.5px solid #E2E4E9', borderRadius:'12px', padding:'14px', background:'white', display:'flex', flexDirection:'column', gap:'10px'}}>
      <div style={{fontSize:'14px', fontWeight:'800', color:'#16181D'}}>Trocar senha</div>
      <input type="password" autoComplete="new-password" placeholder="Nova senha (mínimo 6 caracteres)" value={senha} onChange={e => setSenha(e.target.value)} style={campo} />
      <input type="password" autoComplete="new-password" placeholder="Repita a nova senha" value={confirma} onChange={e => setConfirma(e.target.value)} style={campo} />
      {erro && <p style={{color:'#DC2626', fontSize:'12.5px', margin:0}}>{erro}</p>}
      <div style={{display:'flex', gap:'8px'}}>
        <button type="button" onClick={salvar} disabled={salvando}
          style={{flex:1, padding:'12px', borderRadius:'12px', border:'none', background:'#2F6BFF', color:'white', fontSize:'14px', fontWeight:'800', cursor:salvando?'not-allowed':'pointer', opacity:salvando?0.7:1}}>
          {salvando ? 'Salvando…' : 'Salvar nova senha'}
        </button>
        <button type="button" onClick={() => { setAberto(false); setSenha(''); setConfirma(''); setErro(''); }}
          style={{padding:'12px 14px', borderRadius:'12px', border:'1.5px solid #E2E4E9', background:'white', color:'#6E7480', fontSize:'14px', fontWeight:'600', cursor:'pointer'}}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// v151: depois de salvar, entra direto no app (antes voltava ao login e pedia a senha que a pessoa acabou de criar).
// "Voltar" para quem abriu um link velho: sem ele a pessoa ficava presa nesta tela.
function RedefinirSenha({ onConcluido, onDesistir }) {
  const [emailConta, setEmailConta] = React.useState('');
  React.useEffect(() => { supabase.auth.getSession().then(r => { const u = r?.data?.session?.user; if (u && (!LINK_SENHA_CONTA_SM || u.id === LINK_SENHA_CONTA_SM)) setEmailConta(u.email || ''); }).catch(() => {}); }, []);
  const [senha, setSenha] = React.useState('');
  const [confirma, setConfirma] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [erro, setErro] = React.useState('');
  const [ok, setOk] = React.useState(false);
  React.useEffect(() => { uso.area('entrada'); uso.tela('entrada.redefinir_senha'); }, []);

  async function salvar() {
    if (senha.length < 6) { uso.bloqueio('senha_redefinir', null, null, 'senha_curta'); setErro('A senha deve ter pelo menos 6 caracteres.'); return; }
    if (senha !== confirma) { uso.bloqueio('senha_redefinir', null, null, 'senhas_diferentes'); setErro('As duas senhas estão diferentes.'); return; }
    setLoading(true); setErro('');
    const { data: sess } = await supabase.auth.getSession();
    if (LINK_SENHA_CONTA_SM && sess?.session?.user?.id !== LINK_SENHA_CONTA_SM) {
      setLoading(false); uso.bloqueio('senha_redefinir', null, null, 'link_de_outra_sessao');
      setErro('O link venceu. Toque em "Voltar" e, se precisar, peça outro em "Esqueci minha senha".'); return;
    }
    const { error } = await supabase.auth.updateUser({ password: senha });
    setLoading(false);
    if (error) {
      uso.bloqueio('senha_redefinir', null, null, error.code || error.name || 'salvar_falhou');
      setErro(error.code === 'same_password' ? 'Essa já é a sua senha. Escolha outra.'
        : error.code === 'weak_password' ? 'Essa senha é fraca demais ou já vazou na internet. Escolha outra, mais longa.'
        : /session|jwt|expired|missing/i.test(error.message || '') ? 'O link venceu. Toque em "Voltar" e, se precisar, peça outro em "Esqueci minha senha".'
        : 'Não deu para salvar. Tente de novo.');
    }
    else { uso.acao('entrada.senha_redefinida'); setOk(true); setTimeout(() => onConcluido(), APP_NATIVO ? 1800 : 6000); }
  }

  return (
    <div style={{minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 16px', boxSizing:'border-box', background:'linear-gradient(150deg,#16181D,#16181D,#16181D)', fontFamily:"-apple-system,BlinkMacSystemFont,'Inter',sans-serif"}}>
      {/* v151: o cartao era branco e os campos (AuthInput, feitos para a tela escura) ficavam branco no branco — a
          pessoa nao via onde digitar a senha nova (Cadu, 28/10). Agora o cartao e' escuro como a tela de entrada. */}
      <div style={{background:'#20232B', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'24px', boxShadow:'0 30px 80px rgba(0,0,0,0.4)', padding:'40px 24px', width:'100%', maxWidth:'400px', boxSizing:'border-box', textAlign:'center'}}>
        <h2 style={{fontSize:'22px', fontWeight:'800', color:'white', marginBottom:'8px', letterSpacing:'-0.4px'}}>
          {ok ? 'Senha atualizada!' : 'Criar nova senha'}
        </h2>
        <p style={{fontSize:'13px', color:'rgba(255,255,255,0.6)', marginBottom:'24px'}}>
          {ok ? (APP_NATIVO ? 'Entrando no app…' : 'Senha salva. Se você usa o app do Plen no celular, abra o app e entre com a senha nova.')
            : emailConta ? <>Nova senha para <b style={{color:'white'}}>{emailConta}</b></> : 'Escolha a sua senha nova.'}
        </p>
        {!ok && (
          <div style={{display:'flex', flexDirection:'column', gap:'12px', textAlign:'left'}}>
            <AuthInput label="Nova senha" type="password" placeholder="Mínimo 6 caracteres" value={senha} onChange={e => setSenha(e.target.value)} />
            <AuthInput label="Confirmar senha" type="password" placeholder="Repita a senha" value={confirma} onChange={e => setConfirma(e.target.value)} />
            {erro && <p style={{color:'#FCA5A5', fontSize:'12.5px', margin:0, lineHeight:1.45}}>{erro}</p>}
            <button onClick={salvar} disabled={loading}
              style={{background:'linear-gradient(135deg,#2F6BFF,#2455D6)', color:'white', border:'none', borderRadius:'12px', padding:'14px', fontSize:'15px', fontWeight:'700', cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1, marginTop:'4px', boxShadow:'0 4px 14px rgba(99,102,241,0.35)'}}>
 {loading ? 'Salvando...': 'Salvar nova senha'}
            </button>
            {onDesistir && (
              <button type="button" onClick={onDesistir}
                style={{background:'none', border:'none', color:'rgba(255,255,255,0.6)', fontSize:'13px', fontWeight:'600', cursor:'pointer', padding:'6px', marginTop:'2px'}}>
                Voltar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = React.useState(null);
  const [perfil, setPerfil] = React.useState(null);
  const [professor, setProfessor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [modoRecuperacao, setModoRecuperacao] = React.useState(!!LINK_SENHA_CONTA_SM);   // v151
  React.useEffect(() => { if (LINK_SENHA_SM === 'link_invalido') { try { window.history.replaceState({}, '', window.location.pathname + window.location.search); } catch {} } }, []);
  const [conviteData, setConviteData] = React.useState(null);
  // v147: link de convite aberto AGORA no iPhone, pelo navegador (sem o app) e sem login: antes do cadastro,
  // a pagina "Baixar o Plen" (ConviteBaixarApp). Some se o convite nao valer, se a pessoa ja' estiver logada
  // ou se tocar em "Continuar no navegador" (vale ate' fechar a aba). Lido antes de o efeito limpar o ?c=.
  const [linkIOS, setLinkIOS] = React.useState(() => {
    try {
      const c = new URLSearchParams(window.location.search).get('c') || '';
      return IOS_NAVEGADOR && /^[A-Za-z0-9]{4,16}$/.test(c) && !sessionStorage.getItem('plen_convite_navegador') ? c.toUpperCase() : '';
    } catch { return ''; }
  });
  // Logado (ou entrou agora) = a pagina nao volta nesta aba, nem depois de sair da conta. E se o banco nao
  // responder em 8 s, segue o caminho de antes (a tela de entrada mostra o convite quando ele chegar).
  React.useEffect(() => { if (user) setLinkIOS(''); }, [user]);
  React.useEffect(() => {
    if (!linkIOS || conviteData) return;
    const t = setTimeout(() => setLinkIOS(''), 8000);
    return () => clearTimeout(t);
  }, [linkIOS, !!conviteData]);

  React.useEffect(() => {
    async function resolveInvite() {
      const params = new URLSearchParams(window.location.search);

      // Etapa 5 (v131): tocou num aviso de aula (push) — "/?aula=<id>&l=<id do aviso>". Fica
      // guardado como pendencia: o app do aluno, depois de carregar, abre essa aula nas opcoes
      // e conta a abertura do lembrete (lembrete_aberto). So' ids validos entram.
      // v143 (B): o botao [Nao vou] do aviso traz "&acao=nao_vou" — o app do aluno abre direto o aviso de
      // falta dessa aula (com a consequencia do prazo). So' esse valor e' aceito; qualquer outro e' ignorado.
      const aulaLink = params.get('aula');
      if (aulaLink && UUID_RE.test(aulaLink)) {
        const l = params.get('l');
        const acaoLink = params.get('acao') === 'nao_vou' ? 'nao_vou' : null;
        try { localStorage.setItem('pendingAula', JSON.stringify({ aula: aulaLink, l: l && UUID_RE.test(l) ? l : null, acao: acaoLink, em: Date.now() })); } catch {}
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
      }

      // O link do WhatsApp abre no navegador interno do WhatsApp, que nao tem a sessao
      // do app — o professor cai no login e o destino se perdia. Guardo antes de logar.
      const solic = params.get('solicitacao');
      if (solic) {
        uso.acao('entrada.link_solicitacao');
        localStorage.setItem('pendingSolicitacao', solic);
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
      }

      const shortCode = params.get('c') || localStorage.getItem('pendingInviteCode');
      const legacyToken = params.get('convite') || localStorage.getItem('pendingInvite');

      if (shortCode) {
        localStorage.setItem('pendingInviteCode', shortCode);
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
        const convite = await resolverConvite(shortCode);
        if (!convite) setLinkIOS('');   // v147: convite que nao vale nao mostra a pagina "Baixar o Plen"
        // Central de uso: link de convite aberto AGORA (o codigo guardado de antes nao conta)
        if (params.get('c')) {
          try {
            if (convite && convite.tipo === 'aluno_prof') uso.acao('entrada.link_pedido_prof', { valido: true });
            else if (convite && convite.tipo === 'prof_prof') uso.acao('entrada.link_indicacao', { valido: true });   // v144
            else {
              funilConvite.passo(1, { tipo: convite ? (convite.aluno_id ? 'pessoal' : 'geral') : 'invalido' });
              if (!convite) { funilConvite.barrar(resolverConvite.motivo || 'convite_invalido'); funilConvite.sair(resolverConvite.motivo || 'convite_invalido'); }
            }
          } catch {}
        }
        if (convite && convite.tipo === 'prof_prof') {
          // v144: convite de PROFESSOR para professor (link de indicacao). Vai SEM "pid": no app inteiro pid quer
          // dizer "convite do professor para o aluno" (ficha, boas-vindas de aluno...). Quem convidou fica em
          // indicador_id/pnome; a conta nasce de professor (AuthPage/TipoUsuario) e o ProfessorApp grava quem
          // indicou (professor_entrou_por_indicacao).
          setConviteData({ _code: shortCode, tipo: 'prof_prof', pnome: convite.dados?.pnome || '', indicador_id: convite.professor_id, _seguro: !!convite._seguro });
        } else if (convite) {
          setConviteData({
            _code: shortCode,
            tipo: convite.tipo,
            pid: convite.professor_id,
            pnome: convite.dados?.pnome || '',
            aid: convite.aluno_id,
            anome: convite.dados?.anome || '',
            proposta: convite.dados?.proposta || null,
            ...convite.dados,
            _seguro: !!convite._seguro,
          });
        }
      } else if (legacyToken) {
        // Seguranca convites (01/10): o link antigo (?convite=...) trazia professor e ficha
        // dentro do proprio link — qualquer um podia forjar. O app nao gera mais esse link;
        // agora ele e' so' descartado (o aluno pede ao professor um link novo, ?c=CODIGO).
        try { localStorage.removeItem('pendingInvite'); } catch {}
      }
    }
    resolveInvite().catch(() => setLinkIOS(''));   // v147: falhou = sem a pagina "Baixar o Plen"
  }, []);

  React.useEffect(() => {
    async function carregarSessao(session) {
      try {
        if (!session) { try { sessionStorage.removeItem('plen_uso_google'); } catch {} setUser(null); setPerfil(null); setProfessor(null); return; }
        setUser(session.user);
        // v151: "Bem-vindo de volta" e sem o quadro de convite tambem para quem entrou pelo link de senha ou criou a conta aqui
        try { if (session.user.email) localStorage.setItem('plenUltimoEmail', session.user.email); } catch {}
        // Central de uso: volta do Google (a marca foi gravada em handleGoogleLogin). Conta
        // criada ha' menos de 10 min = cadastro; senao, login. A marca sai na hora: carregarSessao
        // roda de novo a cada renovacao do token e nao pode contar duas vezes.
        try {
          if (sessionStorage.getItem('plen_uso_google')) {
            sessionStorage.removeItem('plen_uso_google');
            const contaNova = Date.now() - new Date(session.user.created_at).getTime() < 10 * 60 * 1000;
            uso.evento(contaNova ? 'sessao.cadastro' : 'sessao.login', { detalhes: { metodo: 'google' } });
            if (contaNova) funilCadastro.passo(3, { metodo: 'google' });
            funilConvite.passo(3, { conta: contaNova ? 'criou' : 'entrou' }, true);
          }
        } catch {}
        const { data: p } = await supabase.from('perfis').select('*').eq('id', session.user.id).single();
        setPerfil(p || null);
        if (p && p.tipo === 'professor') {
          const { data: prof } = await supabase.from('professores').select('*').eq('user_id', session.user.id).single();
          if (prof) setProfessor(prof); // only update if got data; avoid overwriting valid state
        }
      } catch(err) { console.error('Erro auth:', err); } finally { setLoading(false); }
    }
    supabase.auth.getSession().then(function(res) { carregarSessao(res && res.data && res.data.session ? res.data.session : null); });
    var listener = supabase.auth.onAuthStateChange(function(_event, session) {
      if (_event === 'PASSWORD_RECOVERY') { setModoRecuperacao(true); setLoading(false); return; }
      // SIGNED_OUT: limpar estado explicitamente sem re-query
      if (_event === 'SIGNED_OUT') { fimRecuperacaoSM(); setUser(null); setPerfil(null); setProfessor(null); setLoading(false); return; }
      // TOKEN_REFRESHED: renovar sessao
      if (_event === 'TOKEN_REFRESHED') { uso.remontagem(); setLoading(true); carregarSessao(session); }
      // SIGNED_IN nao tratado aqui: getSession() ja carrega na inicializacao;
      // handleLogin/handleRegister ja setam estado manualmente. Tratar SIGNED_IN
      // causaria double-call que sobrescreve professor com null (bug da tela branca).
    });
    return function() { try { if (listener && listener.data && listener.data.subscription) { listener.data.subscription.unsubscribe(); } } catch(e) {} };
  }, []);

  // Central de uso: em qual parte do app a pessoa esta' (area) e o fim do funil de cadastro.
  // Espera o carregamento: senao o app.abrir de quem ja' esta' logado sairia como 'entrada'.
  React.useEffect(() => {
    try {
      if (loading) return;
      if (naRotaPainel()) { uso.area('painel'); if (user) uso.tela('painel.inicio'); return; }
      if (modoRecuperacao || !user || !perfil) { uso.area('entrada'); return; }
      const papel = perfil.tipo === 'professor' ? 'professor' : perfil.tipo === 'aluno' ? 'aluno' : null;
      if (!papel) return;
      uso.area(papel);
      if (papel === 'professor' && !professor) { uso.tela('prof.erro_perfil'); uso.erro('erro.perfil_prof', 'perfil de professor sem cadastro'); return; }
      // chegou no app: conta criada nesta sessao (passo >= 3) conclui; quem so' abriu o cadastro e entrou com conta antiga, sai
      const pCad = funilCadastro.passoAtual();
      if (pCad >= 3) { funilCadastro.passo(5, { papel }, true); funilCadastro.fim('concluiu', { papel }); }
      else if (pCad > 0) funilCadastro.sair('ja_tinha_conta');
    } catch {}
  }, [loading, !!user, perfil?.tipo, !!professor, modoRecuperacao]);

  // Enquanto confere a sessao, continua o MESMO esqueleto que ja' estava na tela desde
  // o index.html. Antes entrava aqui uma tela azul escrita "Carregando", o que dava dois
  // saltos visuais: esqueleto → tela azul → app. Agora e' um so': esqueleto → app.
  if (loading) return <EsqueletoCarregando />;

  if (modoRecuperacao) return (
    <RedefinirSenha onConcluido={() => { fimRecuperacaoSM(); try { window.location.replace('/'); } catch { setModoRecuperacao(false); } }}
      onDesistir={() => { fimRecuperacaoSM(); setModoRecuperacao(false); try { window.history.replaceState({}, '', '/'); } catch {} }} />
  );

  // /painel — o painel de acompanhamento numa pagina propria, de tela cheia.
  //
  // Por que fora do app do professor: o app e' desenhado para o celular (coluna estreita,
  // barra embaixo, menu lateral). Tabela de funil com 9 colunas nao cabe ali — e esta
  // analise se faz sentada, numa tela grande, com a pagina aberta o dia todo. Aqui e' uma
  // URL que o Marcus deixa fixada no navegador, sem passar pelo app de ninguem.
  //
  // A porta e' a mesma (mesmo login), mas quem decide o acesso e' o BANCO: a funcao
  // metricas_piloto() confere o e-mail e recusa qualquer outro. Abrir a URL nao vaza nada.
  if (typeof window !== 'undefined' && window.location.pathname.replace(/\/$/, '') === '/painel') {
    return (
      <AppContext.Provider value={{ user, setUser, perfil, setPerfil, professor, setProfessor, conviteData: null, setConviteData }}>
        {!user ? <AuthPage /> : (
          <div style={{minHeight:'100vh', background:'#F7F8FA', padding:'24px 16px 60px'}}>
            <div style={{maxWidth:'1280px', margin:'0 auto'}}>
              <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', justifyContent:'space-between', gap:'12px 16px', marginBottom:'14px'}}>
                <div style={{display:'flex', alignItems:'center', gap:'12px', minWidth:0}}>
                  <img src="/logo-cor.svg" alt="Plen" style={{height:'26px', width:'auto', display:'block'}} onError={e => { e.currentTarget.style.display = 'none'; }} />
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:'19px', fontWeight:'800', color:'#16181D'}}>Painel do Plen</div>
                  </div>
                </div>
                <a href="/" style={{fontSize:'13px', fontWeight:'700', color:'#2F6BFF', textDecoration:'none', display:'inline-flex', alignItems:'center', minHeight:'44px'}}>Ir para o app →</a>
              </div>
              <PainelPiloto />
            </div>
          </div>
        )}
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={{ user, setUser, perfil, setPerfil, professor, setProfessor, conviteData, setConviteData }}>
      {!user && (linkIOS
        ? (conviteData
          ? <ConviteBaixarApp convite={conviteData} code={conviteData._code || linkIOS} onNavegador={() => setLinkIOS('')} />
          : <EsqueletoCarregando />)
        : <AuthPage />)}
      {user && !perfil && <TipoUsuario user={user} />}
      {user && perfil?.tipo === 'professor' && professor && <ProfessorApp user={user} professor={professor} />}
      {user && perfil?.tipo === 'professor' && !professor && (
        <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',background:'linear-gradient(135deg,#2F6BFF,#2455D6)'}}>
          <div style={{color:'white',textAlign:'center',padding:'2rem'}}>
            <div style={{fontSize:'1.5rem',fontWeight:'bold',marginBottom:'1rem'}}><Ico n="alert-triangle" c="#D97706" /> Erro ao carregar perfil do professor</div>
            <div style={{marginBottom:'1.5rem',opacity:0.8}}>Não foi possível acessar os dados. Faça login novamente.</div>
            <button onClick={async function(){ await sairDaConta(); setUser(null); setPerfil(null); setProfessor(null); }}
              style={{padding:'0.75rem 1.5rem',background:'white',color:'#2F6BFF',border:'none',borderRadius:'8px',fontWeight:'bold',cursor:'pointer',fontSize:'1rem'}}>
              Voltar ao login
            </button>
          </div>
        </div>
      )}
      {user && perfil?.tipo === 'aluno' && (conviteData?.tipo === 'prof_prof' ? <IndicacaoParaAlunoSM /> : <AlunoApp user={user} />)}
    </AppContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Rede indisponivel (Supabase pausado, offline, etc.): antes disso o app
// simplesmente mostrava tela vazia, sem nenhuma explicacao.
// ---------------------------------------------------------------------------
function TelaErro({ titulo, mensagem, detalhe, onTentarNovamente }) {
  return (
    <div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:'24px',
                 background:'linear-gradient(135deg,#2F6BFF,#2455D6)',fontFamily:'system-ui,-apple-system,sans-serif'}}>
      <div style={{background:'white',borderRadius:'20px',padding:'32px',maxWidth:'420px',width:'100%',
                   boxShadow:'0 20px 60px rgba(0,0,0,0.25)',textAlign:'center'}}>
        <div style={{fontSize:'44px',marginBottom:'12px'}}>{'\u26A0\uFE0F'}</div>
        <h1 style={{fontSize:'19px',fontWeight:'800',color:'#16181D',margin:'0 0 10px'}}>{titulo}</h1>
        <p style={{fontSize:'14px',color:'#3D424C',lineHeight:1.55,margin:'0 0 22px'}}>{mensagem}</p>
        <button onClick={onTentarNovamente}
          style={{width:'100%',padding:'13px',borderRadius:'12px',border:'none',cursor:'pointer',
                  background:'linear-gradient(135deg,#2F6BFF,#2455D6)',color:'white',fontSize:'15px',fontWeight:'700'}}>
          Tentar novamente
        </button>
        {detalhe && (
          <details style={{marginTop:'16px',textAlign:'left'}}>
            <summary style={{fontSize:'12px',color:'#9AA0AB',cursor:'pointer'}}>Detalhes tecnicos</summary>
            <pre style={{fontSize:'11px',color:'#6E7480',whiteSpace:'pre-wrap',wordBreak:'break-word',marginTop:'8px'}}>{detalhe}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error Boundary: impede que um erro em qualquer tela derrube o app inteiro
// para tela branca. Reporta ao Sentry quando disponivel.
// ---------------------------------------------------------------------------
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { erro: null }; }
  static getDerivedStateFromError(erro) { return { erro }; }
  componentDidCatch(erro, info) {
    try { const m = String((erro && erro.message) || erro || ''); uso.erro(/Failed to fetch|NetworkError|Load failed/i.test(m) ? 'erro.rede' : 'erro.tela_branca', m); uso.enviarAgora(); } catch (e) {}
    try {
      if (window.Sentry && window.Sentry.captureException) {
        window.Sentry.captureException(erro, { extra: { componentStack: info && info.componentStack } });
      }
    } catch (e) {}
    console.error('Erro capturado pelo ErrorBoundary:', erro, info);
  }
  render() {
    if (this.state.erro) {
      const msg = String(this.state.erro && this.state.erro.message || '');
      const ehRede = /Failed to fetch|NetworkError|Load failed/i.test(msg);
      return (
        <TelaErro
          titulo={ehRede ? 'Sem conexao com o servidor' : 'Algo deu errado'}
          mensagem={ehRede
            ? 'Nao conseguimos falar com o servidor agora. Verifique sua internet e tente de novo. Se persistir, o servico pode estar temporariamente indisponivel.'
            : 'Encontramos um problema inesperado nesta tela. A equipe ja foi avisada automaticamente.'}
          detalhe={msg}
          onTentarNovamente={() => { try { uso.acao('app.erro_recarregar'); uso.enviarAgora(); } catch (e) {} window.location.reload(); }}
        />
      );
    }
    return this.props.children;
  }
}

// Erros de rede que acontecem fora do React (promessas rejeitadas)
window.addEventListener('unhandledrejection', function (ev) {
  const msg = String(ev && ev.reason && ev.reason.message || ev && ev.reason || '');
  if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) {
    console.warn('Falha de rede nao tratada:', msg);
    try { if (window.Sentry && window.Sentry.captureMessage) window.Sentry.captureMessage('Falha de rede: ' + msg); } catch (e) {}
  }
});

const root = createRoot(document.getElementById('root'));
root.render(<ErrorBoundary><App /></ErrorBoundary>);
